import React, { useState, useRef, useEffect, useMemo } from "react";

// ---- ticket fixes: shared helpers ----

// ---- QA batch 2 helpers ----
function theoryStanceInstruction(lv) {
  if (lv <= 1) return "Stance: ADVOCATE. Argue in favor of this theory as persuasively as the record allows; anticipate and rebut the strongest counter-arguments.";
  if (lv === 2) return "Stance: NEUTRAL EVALUATOR. Give a balanced assessment of the theory's strengths and weaknesses and the likely outcome, without advocating for either side.";
  return "Stance: OPPOSING COUNSEL. Attack and weaken this theory: identify its legal and factual weaknesses, contrary authority, evidentiary gaps and the strongest counter-arguments, and explain how the other side will exploit them. Do not argue in support of the theory; conclude with why it is likely to fail or should be discounted.";
}
// Minimal Markdown -> HTML (headings, bold, italics, code, bullet/numbered lists, rules, paragraphs).
function mdToHtml(src) {
  const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (t) =>
    esc(t)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
  const lines = String(src || "").split(/\r?\n/);
  let html = "", list = null, para = [];
  const flushP = () => { if (para.length) { html += "<p>" + inline(para.join(" ")) + "</p>"; para = []; } };
  const flushL = () => { if (list) { html += "</" + list + ">"; list = null; } };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) { flushP(); flushL(); const lvl = Math.min(h[1].length + 1, 5); html += "<h" + lvl + ">" + inline(h[2].replace(/\*\*/g, "")) + "</h" + lvl + ">"; continue; }
    if (/^\s*[-*•]\s+/.test(line)) { flushP(); if (list !== "ul") { flushL(); list = "ul"; html += "<ul>"; } html += "<li>" + inline(line.replace(/^\s*[-*•]\s+/, "")) + "</li>"; continue; }
    if (/^\s*\d+[.)]\s+/.test(line)) { flushP(); if (list !== "ol") { flushL(); list = "ol"; html += "<ol>"; } html += "<li>" + inline(line.replace(/^\s*\d+[.)]\s+/, "")) + "</li>"; continue; }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { flushP(); flushL(); html += "<hr/>"; continue; }
    if (!line.trim()) { flushP(); flushL(); continue; }
    para.push(line.trim());
  }
  flushP(); flushL();
  return html;
}
function theoryLevelText(t, lv) {
  if (t && Array.isArray(t.levels)) return t.levels[Math.min(lv, t.levels.length - 1)];
  if (t && Array.isArray(t.text)) return t.text[Math.min(lv, t.text.length - 1)];
  return (t && t.text) || "";
}
// word-level edit distance (LCS based) so deletions, insertions and rewrites all move the percentage
function theoryEditPct(orig, edited) {
  const a = String(orig || "").split(/\s+/).filter(Boolean);
  const b = String(edited || "").split(/\s+/).filter(Boolean);
  const n = a.length, m = b.length;
  if (!n && !m) return 0;
  let prev = new Array(m + 1).fill(0);
  for (let i = 1; i <= n; i++) {
    const cur = new Array(m + 1).fill(0);
    for (let j = 1; j <= m; j++) cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], cur[j - 1]);
    prev = cur;
  }
  const lcs = prev[m];
  const changed = Math.max(n, m) - lcs;
  return Math.min(100, Math.round((changed / Math.max(n, 1)) * 100));
}
function fmtUploadDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  if (m) return m[3] + "/" + m[2] + "/" + m[1];
  const d = new Date();
  return String(d.getDate()).padStart(2, "0") + "/" + String(d.getMonth() + 1).padStart(2, "0") + "/" + d.getFullYear();
}
// exhibit numbers like #1A, #2D.1, 12-B: letters, digits, dot and hyphen, optional single leading #
function sanitizeExhibitNo(v) {
  const raw = String(v || "").replace(/[^A-Za-z0-9#.\-]/g, "");
  return (raw.startsWith("#") ? "#" : "") + raw.replace(/#/g, "").slice(0, 16);
}
const DK = {
  bg: "#070a13",
  card: "#0e1322",
  surface: "#141a2b",
  border: "#1f2940",
  text: "#e8ecf3",
  muted: "#a3aabd",
  dim: "#727a91",
  faint: "#4f5568",
  nav: "#090d18",
  accent: "#5b8def",
  accentSoft: "#5b8def22",
  hover: "#1a2138",
  elev: "0 1px 3px rgba(0,0,0,.35),0 8px 24px -12px rgba(0,0,0,.5)",
};
const LT = {
  bg: "#fafaf7",
  card: "#ffffff",
  surface: "#f4f3ee",
  border: "#e6e3da",
  text: "#1a1f2e",
  muted: "#525868",
  dim: "#7e8497",
  faint: "#a8acb8",
  nav: "#ffffff",
  accent: "#1e40af",
  accentSoft: "#1e40af14",
  hover: "#f0eee8",
  elev: "0 1px 3px rgba(28,32,48,.06),0 8px 24px -12px rgba(28,32,48,.12)",
};
const FV = [
  "Most Favorable",
  "Favorable",
  "Neutral",
  "Unfavorable",
  "Least Favorable",
];
const FC = ["#10b981", "#3b82f6", "#6b7280", "#f59e0b", "#ef4444"];
const TC = { factual: "#ef4444", procedural: "#3b82f6", scheduling: "#10b981" };
const TLL = {
  factual: "Factual",
  procedural: "Procedural",
  scheduling: "Scheduling",
};
const F_TL = [
  {
    date: "Aug 29, 1997",
    label: "CN1200366A - MgLT disclosed",
    detail: "Chinese patent: synthesis, oral forms, dosage 1mg–100g.",
    ref: "CC ¶14",
    type: "factual",
    ecf: "CN1200366A: magnesium L-threonate synthesis, pharmaceutical compositions.",
  },
  {
    date: "Oct 10, 2003",
    label: "MIT Provisional - Mg²⁺ synaptic enhancement",
    detail: "Prof. Liu at MIT. 'Magnesium or magnesium salt comprising Mg²⁺.'",
    ref: "CC ¶19",
    type: "factual",
    ecf: "MIT 60/510,945. Claim 12 encompasses MgLT.",
  },
  {
    date: "2005",
    label: "Neurocentria (fka Magceutics) founded",
    detail:
      "Mao: 'founded on accidental discovery using fluorescent dye tool I invented 15 years earlier.'",
    ref: "CC ¶30",
    type: "factual",
    ecf: "YouTube clip. Slutsky credited for discovery in Liu's MIT lab.",
  },
  {
    date: "Aug 29, 2022",
    label: "AIDP–Kappa License Agreement",
    detail: "Non-exclusive Magtein® license. No post-termination non-compete.",
    ref: "CC ¶48",
    type: "factual",
    ecf: "Limited license for Magtein® mark.",
  },
  {
    date: "Apr 1, 2024",
    label: "AIDP transitions to ThreoTech as distributor",
    detail: "ThreoTech takes over Magtein® licensing from AIDP.",
    ref: "CC ¶40",
    type: "factual",
    ecf: "ThreoTech LLC now controls Magtein® trademark licensing.",
  },
  {
    date: "May 8, 2024",
    label: "ThreoTech alleges Kappa non-compliance",
    detail: "30-day cure notice re: formulation + MAP violations.",
    ref: "CC ¶51",
    type: "factual",
    ecf: "Letter asserting AIDP assigned agreement to ThreoTech.",
  },
  {
    date: "Jul 15, 2024",
    label: "ThreoTech terminates license",
    detail: "Sell-off permitted per Section 10.",
    ref: "CC ¶52",
    type: "factual",
    ecf: "Termination notice. Kappa may sell remaining inventory.",
  },
  {
    date: "Nov 4, 2024",
    label: "ThreoTech demands total cessation of MgLT sales",
    detail: "Exceeds contract - no non-compete exists.",
    ref: "CC ¶53",
    type: "factual",
    ecf: "Demand letter: cease ALL MgLT sales by Jan 15, 2025.",
  },
  {
    date: "Feb 17, 2026",
    label: "Liu depo: 'We did not invent the molecule'",
    detail: "Admits compound preexisted. No new synthesis method patented.",
    ref: "CC ¶23",
    type: "factual",
    ecf: "Depo p.318:25 - 'We did not invent the molecule.' p.317:2-3 - 'did not patent' new synthesis.",
  },
  {
    date: "Feb 18, 2026",
    label: "Mao depo: fluorescent probe, Slutsky credit",
    detail:
      "Credits Slutsky for discovery. Probe was 'common tool.' 135+ patents undisclosed to USPTO.",
    ref: "CC ¶30",
    type: "factual",
    ecf: "Mao: tool 'used by so many people on a daily basis.' Slutsky not named co-inventor.",
  },
];
const P_TL = [
  {
    date: "Jun 27, 2025",
    label: "Complaint filed - 5 patents",
    ref: "Dkt.1",
    type: "procedural",
    ecf: "Neurocentria v. Mando.",
  },
  {
    date: "Sep 24, 2025",
    label: "Scheduling Order - Trial 3/9/27",
    ref: "Dkt.32",
    type: "procedural",
    ecf: "Full patent calendar.",
  },
  {
    date: "Nov 12, 2025",
    label: "Answer + Counterclaims (10 counts)",
    ref: "Dkt.55",
    type: "procedural",
    ecf: "DJ, invalidity, fraud, UCL.",
  },
  {
    date: "Jan 8, 2026",
    label: "4 patents dismissed w/o prejudice",
    ref: "Dkt.89",
    type: "procedural",
    ecf: "Only '061 remains.",
  },
  {
    date: "Feb 24, 2026",
    label: "Leave to amend granted - both sides",
    ref: "Dkt.107",
    type: "procedural",
    ecf: "FAC + 2d Amended CC.",
  },
  {
    date: "Mar 3, 2026",
    label: "Second Amended Counterclaims filed",
    ref: "Dkt.112",
    type: "procedural",
    ecf: "Fraud + Lanham Act.",
  },
];
const S_TL = [
  {
    date: "Sep 11, 2026",
    label: "Fact Discovery Cut-Off",
    ref: "Dkt.32",
    type: "scheduling",
    ecf: "All depositions.",
  },
  {
    date: "Nov 13, 2026",
    label: "Last Motions Hearing (incl SJ)",
    ref: "Dkt.32",
    type: "scheduling",
    ecf: "Rule 56.",
  },
  {
    date: "Feb 5, 2027",
    label: "Final Pretrial Conference",
    ref: "Dkt.32",
    type: "scheduling",
    ecf: "1:30 PM.",
  },
  {
    date: "Mar 9, 2027",
    label: "JURY TRIAL - 5-7 days",
    ref: "Dkt.32",
    type: "scheduling",
    ecf: "8:15 AM.",
  },
];
const DOCKET = [
  {
    date: "06/27/2025",
    num: 1,
    text: "COMPLAINT. 5 patents asserted. Fee $405.",
    tags: ["factual", "procedural"],
  },
  {
    date: "06/27/2025",
    num: 4,
    text: "NOTICE AND CERTIFICATE of Interested Parties.",
    tags: [],
  },
  {
    date: "07/02/2025",
    num: 7,
    text: "NOTICE OF ASSIGNMENT to Judge Snyder & MJ Rosenbluth.",
    tags: [],
  },
  {
    date: "07/14/2025",
    num: 14,
    text: "21-DAY Summons Issued as to Mando International.",
    tags: [],
  },
  {
    date: "07/15/2025",
    num: 15,
    text: "ORDER: Case reassigned to Judge Aenlle-Rocha (Gen. Order 23-05).",
    tags: ["procedural"],
  },
  {
    date: "07/16/2025",
    num: 16,
    text: "INITIAL STANDING ORDER by Judge Aenlle-Rocha.",
    tags: [],
  },
  {
    date: "07/22/2025",
    num: 17,
    text: "PROOF OF SERVICE on Mando. Answer due 8/6/25.",
    tags: [],
  },
  {
    date: "08/06/2025",
    num: 18,
    text: "Appearance: Raj Abhyanker for Mando.",
    tags: [],
  },
  {
    date: "08/06/2025",
    num: 19,
    text: "DECLINED consent to Magistrate Judge.",
    tags: [],
  },
  {
    date: "08/06/2025",
    num: 20,
    text: "MOTION to Dismiss / Lack of Jurisdiction / Change Venue to N.D. Texas.",
    tags: ["procedural"],
  },
  {
    date: "08/08/2025",
    num: 21,
    text: "ORDER setting Scheduling Conference 9/26/25 1:00 PM.",
    tags: ["scheduling"],
  },
  {
    date: "08/15/2025",
    num: 24,
    text: "OPPOSITION to MTD/Venue Transfer.",
    tags: ["procedural"],
  },
  {
    date: "08/19/2025",
    num: 25,
    text: "REPLY in support of MTD.",
    tags: ["procedural"],
  },
  {
    date: "08/31/2025",
    num: 26,
    text: "ORDER: MTD taken under submission. No oral argument needed.",
    tags: ["procedural"],
  },
  {
    date: "09/11/2025",
    num: 27,
    text: "Joint Rule 26(f) Report filed.",
    tags: [],
  },
  {
    date: "09/24/2025",
    num: 30,
    text: "ORDER: Scheduling Conference VACATED. In-person unnecessary.",
    tags: ["scheduling"],
  },
  {
    date: "09/24/2025",
    num: 31,
    text: "ORDER/REFERRAL to ADR - private mediation by 12/31/26.",
    tags: ["scheduling"],
  },
  {
    date: "09/24/2025",
    num: 32,
    text: "SCHEDULING AND TRIAL ORDER. Trial 3/9/27. Full patent calendar.",
    tags: ["scheduling"],
  },
  {
    date: "09/24/2025",
    num: 33,
    text: "PATENT STANDING ORDER AND RULES.",
    tags: ["scheduling"],
  },
  {
    date: "10/03/2025",
    num: 34,
    text: "MOTION for Leave to Amend Complaint (1st attempt).",
    tags: ["procedural"],
  },
  {
    date: "10/08/2025",
    num: 35,
    text: "OPPOSITION to Motion to Amend.",
    tags: ["procedural"],
  },
  {
    date: "10/17/2025",
    num: 36,
    text: "REPLY in support of Motion to Amend + Dang Declaration.",
    tags: ["procedural"],
  },
  {
    date: "10/17/2025",
    num: 37,
    text: "MOTION to file corrected Opposition.",
    tags: [],
  },
  {
    date: "10/30/2025",
    num: 42,
    text: "ORDER: Motion to Amend taken under submission.",
    tags: ["procedural"],
  },
  {
    date: "10/30/2025",
    num: 43,
    text: "ORDER: Ex Parte Application DENIED as moot.",
    tags: [],
  },
  {
    date: "11/03/2025",
    num: 46,
    text: "PHV: James Schaller admitted.",
    tags: [],
  },
  {
    date: "11/03/2025",
    num: 47,
    text: "PHV: Jeffrey Gavenman admitted.",
    tags: [],
  },
  {
    date: "11/03/2025",
    num: 48,
    text: "PHV: Jeremy Schulman admitted (for ThreoTech).",
    tags: [],
  },
  {
    date: "11/11/2025",
    num: 53,
    text: "Plaintiff WITHDRAWS Motion to Amend.",
    tags: ["procedural"],
  },
  {
    date: "11/11/2025",
    num: 54,
    text: "Defendant WITHDRAWS MTD/Venue Transfer.",
    tags: ["procedural"],
  },
  {
    date: "11/12/2025",
    num: 55,
    text: "ANSWER + COUNTERCLAIMS (10 counts). DJ, invalidity, fraud, UCL, Lanham Act.",
    tags: ["factual", "procedural"],
  },
  {
    date: "11/21/2025",
    num: 64,
    text: "MOTION for Leave to Amend Complaint (2nd attempt).",
    tags: ["procedural"],
  },
  {
    date: "11/25/2025",
    num: 65,
    text: "OPPOSITION to 2nd Motion to Amend.",
    tags: ["procedural"],
  },
  {
    date: "12/03/2025",
    num: 66,
    text: "MOTION to Dismiss Counterclaim Counts VIII–XIII.",
    tags: ["procedural"],
  },
  {
    date: "12/12/2025",
    num: 68,
    text: "MOTION TO STRIKE Gavenman Declaration + Request to Seal.",
    tags: ["procedural"],
  },
  {
    date: "12/12/2025",
    num: 69,
    text: "ORDER: Withdrawn motions dismissed w/o prejudice.",
    tags: ["procedural"],
  },
  {
    date: "12/15/2025",
    num: 70,
    text: "FIRST AMENDED COUNTERCLAIMS filed by Mando.",
    tags: ["factual", "procedural"],
  },
  {
    date: "12/15/2025",
    num: 71,
    text: "REQUEST FOR JUDICIAL NOTICE of new lawsuit.",
    tags: [],
  },
  {
    date: "12/16/2025",
    num: 72,
    text: "REQUEST TO WITHDRAW attorneys Grant & Shaeffer.",
    tags: [],
  },
  {
    date: "12/23/2025",
    num: 79,
    text: "ORDER: Grant & Shaeffer withdrawal GRANTED.",
    tags: [],
  },
  {
    date: "12/23/2025",
    num: 80,
    text: "(STRICKEN) Stipulation for Extension - non-compliant.",
    tags: [],
  },
  {
    date: "12/23/2025",
    num: 81,
    text: "(STRICKEN) Stipulation to Dismiss - non-compliant.",
    tags: [],
  },
  {
    date: "12/29/2025",
    num: 84,
    text: "MOTION to Dismiss Counts VI-VII of 1st Amended CC.",
    tags: ["procedural"],
  },
  {
    date: "01/08/2026",
    num: 85,
    text: "ORDER: Dkts 80, 81 STRICKEN for Local Rules non-compliance.",
    tags: ["procedural"],
  },
  {
    date: "01/08/2026",
    num: 86,
    text: "Joint STIPULATION for claim construction extension (refiled).",
    tags: ["scheduling"],
  },
  {
    date: "01/08/2026",
    num: 87,
    text: "Joint STIPULATION to dismiss causes of action (refiled).",
    tags: ["factual"],
  },
  {
    date: "01/08/2026",
    num: 88,
    text: "ORDER: Claim construction extension APPROVED.",
    tags: ["scheduling"],
  },
  {
    date: "01/08/2026",
    num: 89,
    text: "ORDER: Stipulation to dismiss 4 patents APPROVED. W/o prejudice.",
    tags: ["factual", "procedural", "scheduling"],
  },
  {
    date: "01/09/2026",
    num: 90,
    text: "ORDER: MTD CC Counts VIII-XIII MOOTED by amended counterclaims.",
    tags: ["procedural"],
  },
  {
    date: "01/09/2026",
    num: 91,
    text: "ORDER: Motion for Leave to Amend MOOTED by stipulation.",
    tags: ["procedural"],
  },
  {
    date: "01/19/2026",
    num: 93,
    text: "MOTION for Rule 11 Sanctions (re: natural occurrence claim).",
    tags: ["procedural"],
  },
  {
    date: "01/23/2026",
    num: 94,
    text: "RENEWED MOTION for Leave to file FAC.",
    tags: ["procedural"],
  },
  {
    date: "01/23/2026",
    num: 95,
    text: "JOINT CLAIM CONSTRUCTION STATEMENT.",
    tags: ["factual", "scheduling"],
  },
  {
    date: "01/27/2026",
    num: 97,
    text: "MOTION to Seal portions of filings.",
    tags: [],
  },
  {
    date: "01/28/2026",
    num: 99,
    text: "OPPOSITION to Renewed Motion for Leave to Amend.",
    tags: ["procedural"],
  },
  {
    date: "02/03/2026",
    num: 100,
    text: "STIPULATION for Protective Order.",
    tags: [],
  },
  {
    date: "02/06/2026",
    num: 101,
    text: "REPLY ISO MTD Counts VI-VII.",
    tags: ["procedural"],
  },
  {
    date: "02/06/2026",
    num: 102,
    text: "REPLY ISO Renewed Motion for Leave to Amend.",
    tags: ["procedural"],
  },
  {
    date: "02/13/2026",
    num: 103,
    text: "OPPOSITION to Rule 11 Sanctions.",
    tags: ["procedural"],
  },
  {
    date: "02/13/2026",
    num: 104,
    text: "Defendant WITHDRAWS Sanctions Motion.",
    tags: ["procedural"],
  },
  {
    date: "02/17/2026",
    num: 105,
    text: "Jakob Speert withdrawn as counsel.",
    tags: [],
  },
  {
    date: "02/18/2026",
    num: 106,
    text: "ORDER: Renewed Motion + MTD taken under submission.",
    tags: ["procedural"],
  },
  {
    date: "02/24/2026",
    num: 107,
    text: "ORDER: Leave to file FAC GRANTED. Leave for 2d Amended CC GRANTED.",
    tags: ["procedural"],
  },
  {
    date: "02/26/2026",
    num: 110,
    text: "FIRST AMENDED COMPLAINT filed. Only '061 patent.",
    tags: ["factual", "procedural"],
  },
  {
    date: "02/27/2026",
    num: 111,
    text: "JOINT NOTICE of narrowed claim construction issues.",
    tags: ["scheduling"],
  },
  {
    date: "03/03/2026",
    num: 112,
    text: "SECOND AMENDED COUNTERCLAIMS. 10 counts. Fraud + Lanham Act.",
    tags: ["factual", "procedural"],
  },
  {
    date: "03/09/2026",
    num: 116,
    text: "Plaintiff's Opening Claim Construction Brief.",
    tags: ["factual", "scheduling"],
  },
  {
    date: "03/12/2026",
    num: 117,
    text: "PROOF OF SERVICE on AIDP. Answer due 3/31/26.",
    tags: [],
  },
  {
    date: "03/12/2026",
    num: 118,
    text: "MTD Neurocentria's FAC. §101 + jurisdiction + standing.",
    tags: ["procedural"],
  },
  {
    date: "03/12/2026",
    num: 119,
    text: "MTD ThreoTech's FAC. §101 + TMLA + standing.",
    tags: ["procedural"],
  },
  {
    date: "03/13/2026",
    num: 125,
    text: "MOTION to Seal Liu Depo excerpts.",
    tags: [],
  },
  {
    date: "03/17/2026",
    num: 132,
    text: "MJ Reassignment: Rosenbluth → Rocconi.",
    tags: [],
  },
  {
    date: "03/17/2026",
    num: 133,
    text: "MTD Second Amended Counterclaims. Hearing 4/17/26 1:30 PM.",
    tags: ["procedural"],
  },
  {
    date: "03/23/2026",
    num: 143,
    text: "Defendant's Responsive Claim Construction Brief.",
    tags: ["factual", "scheduling"],
  },
  {
    date: "03/27/2026",
    num: 146,
    text: "OPPOSITION to MTD ThreoTech's FAC.",
    tags: ["procedural"],
  },
  {
    date: "03/27/2026",
    num: 147,
    text: "OPPOSITION to MTD Neurocentria's FAC.",
    tags: ["procedural"],
  },
  {
    date: "03/30/2026",
    num: 150,
    text: "Plaintiff's Reply Claim Construction Brief.",
    tags: ["factual", "scheduling"],
  },
  {
    date: "03/31/2026",
    num: 151,
    text: "CLARIFYING DECLARATION of Dr. Sara Adams.",
    tags: ["factual"],
  },
  {
    date: "03/31/2026",
    num: 155,
    text: "OPPOSITION to MTD Second Amended Counterclaims.",
    tags: ["procedural"],
  },
  {
    date: "04/03/2026",
    num: 157,
    text: "REPLY ISO MTD Second Amended Counterclaims.",
    tags: ["procedural"],
  },
  {
    date: "04/03/2026",
    num: 158,
    text: "REPLY ISO MTD ThreoTech's FAC.",
    tags: ["procedural"],
  },
  {
    date: "04/03/2026",
    num: 159,
    text: "REPLY ISO MTD Neurocentria's FAC.",
    tags: ["procedural"],
  },
];
const THEORIES = {
  plaintiff: [
    {
      title: "Patent Infringement - '061 Dosage Form",
      text: [
        "Defendant's capsules contain ≥10mg MgLT in oral form. Literal infringement of Claim 1.",
        "Strongest: Kappa sells the exact product described in the claim. Textbook literal infringement.",
        "Defendant argues invalidity, but that's a separate inquiry. Claims as issued cover defendant's products.",
        "CN1200366A discloses overlapping ranges. But the examiner reviewed it and allowed the claims.",
        "At its weakest, the patent claims a known compound in a conventional form. Liu admitted 'we did not invent the molecule.'",
      ],
      ground: "'061 Claim 1; FAC Dkt.110",
    },
    {
      title: "Post-Termination Trademark Misuse",
      text: [
        "After license termination, Kappa continued selling MgLT products that traded on market position built under Magtein® license. Continued sales exploit goodwill and confuse consumers.",
        "Strongest: Kappa's product packaging and Amazon listings remained similar to Magtein® branding during transition, creating consumer confusion and unfair advantage.",
        "Kappa ceased using the Magtein® mark after the sell-off period. But the formulation, marketing channels, and customer base were built during the licensed period.",
        "The license has no post-termination non-compete. Section 10 only requires cessation of mark use. Kappa complied.",
        "The contract is unambiguous: no non-compete. Kappa stopped using the mark. Continued sale of a generic compound under independent branding is lawful.",
      ],
      ground: "TLA §10; ThreoTech demand letters; CC ¶¶48-54",
    },
  ],
  defendant: [
    {
      title: "Obviousness (§103) - Known Compound + Known Use",
      text: [
        "CN1200366A + MIT provisional = known compound + known mechanism + known dosage. Textbook obvious.",
        "Three references converge: compound (CN), mechanism (MIT), detection (Mao probes). Known + known + known.",
        "Prior art was cited but file history shows no substantive §103 rejection combining CN + MIT. Strong argument.",
        "Plaintiff will argue non-obvious selection - threonate counterion provides unique bioavailability.",
        "Examiner reviewed CN1200366A and allowed claims. Clear-and-convincing burden for invalidity.",
      ],
      ground: "§103; CN1200366A; MIT 60/510,945; Liu Depo",
    },
    {
      title: "Fraud - Country of Origin / GRAS",
      text: [
        "'Manufactured in USA' labels while CoAs show China origin. Import records show Jordan routing.",
        "Devastating: CoAs are documentary proof. Import data shows tariff avoidance patterns. Amazon listings say USA.",
        "Strong documentary support but requires scienter proof. CoA + imports suggest knowledge.",
        "FTC 'all or virtually all' standard. Domestic encapsulation is a gray area.",
        "'Manufactured in USA' could refer to encapsulation. GRAS filed in good faith.",
      ],
      ground: "CC ¶¶40-47; CoAs; 16 CFR 323",
    },
    {
      title: "Inventorship - Dr. Slutsky Omission",
      text: [
        "Slutsky made the 'accidental discovery.' Mao credits her publicly and under oath. Not named on patents.",
        "Omission + deceptive intent = unenforceability under §256. Mao's statements establish contribution.",
        "Must show Slutsky contributed to conception of a claimed invention, not just background science.",
        "Claims are composition claims (dosage forms), not discovery claims. Slutsky's role may not reach conception.",
        "Inventorship challenge may fail because claims are directed to dosage forms, not biological observations.",
      ],
      ground: "CC ¶¶36-38; Mao YouTube; Mao Depo",
    },
  ],
};
const WITNESSES = [
  {
    id: "gl",
    name: "Dr. Guosong Liu",
    role: "Plaintiff Principal / Inventor",
    ph: "🔬",
    party: "Plaintiff",
    notes: [
      "'We did not invent the molecule.' Key admission on invalidity.",
      "Signed GRAS Notice 499 as President of Magceutics.",
      "Cross-exam: prior art knowledge, MIT provisional, GRAS representations.",
    ],
  },
  {
    id: "fm",
    name: "Dr. Fei Mao",
    role: "Co-Inventor / 135+ patents",
    ph: "🧪",
    party: "Plaintiff",
    notes: [
      "Credits Slutsky for discovery. Undisclosed probe patents.",
      "YouTube: company 'founded on accidental discovery using tool I invented.'",
      "Depo: probe was 'common tool used by so many people on a daily basis.'",
    ],
  },
  {
    id: "ra",
    name: "Raj Abhyanker",
    role: "Defense Counsel (Mando)",
    ph: "📋",
    party: "Defendant",
    notes: [
      "Lead for Mando/Kappa. LegalForce RAPC Worldwide.",
      "Filed Answer, Counterclaims, MTDs, claim construction briefs.",
    ],
  },
  {
    id: "is",
    name: "Dr. Inna Slutsky",
    role: "Omitted Inventor (alleged)",
    ph: "👩‍🔬",
    party: "Both",
    notes: [
      "Made 'accidental discovery' in Liu's MIT lab.",
      "Not named co-inventor. Now at Tel Aviv University.",
      "Key to inventorship defect counterclaim.",
    ],
  },
  {
    id: "sd",
    name: "Sang N. Dang",
    role: "Plaintiff's Lead Counsel",
    ph: "📋",
    party: "Plaintiff",
    notes: ["Fortis LLP, Costa Mesa. Local counsel for all PHV attorneys."],
  },
  {
    id: "el",
    name: "Edward Lee",
    role: "AIDP Principal (alleged)",
    ph: "👔",
    party: "Both",
    notes: [
      "Authorized labeling, marketing, and sourcing practices for Magtein®.",
      "Controlled CoA and import documentation.",
    ],
  },
  {
    id: "sa",
    name: "Dr. Sara Adams",
    role: "Defense Expert (Claim Construction)",
    ph: "🔬",
    party: "Defendant",
    notes: [
      "Filed clarifying declaration re: claim construction (Dkt.151).",
      "Expert on patent claim interpretation and prior art.",
    ],
  },
  {
    id: "nz",
    name: "Dr. Naveed Ziari",
    role: "Defense Expert (Technical)",
    ph: "🔬",
    party: "Defendant",
    notes: [
      "Technical expert retained by Mando/Kappa.",
      "Expected to testify on prior art, obviousness, and §101 eligibility.",
      "May provide declarations in opposition to plaintiff's infringement theories.",
    ],
  },
  {
    id: "aa",
    name: "Armando Alfaro",
    role: "CEO, Mando International",
    ph: "👔",
    party: "Defendant",
    notes: [
      "CEO of Mando/Kappa Nutrition Labs. Based in Frisco, Texas.",
      "Filed declaration (Dkt.20-5): no CA offices, employees, property, or bank accounts.",
      "Key fact witness on Kappa's operations, product formulation, and business practices.",
    ],
  },
];
const LOCS = [
  {
    id: "cd",
    name: "C.D. California (Western Div.)",
    area: "Los Angeles",
    notes: "Forum. Judge Aenlle-Rocha. MJ Rocconi. Trial 3/9/27.",
  },
  {
    id: "mt",
    name: "MIT - Liu's Lab",
    area: "Cambridge, MA",
    notes: "Slutsky's 'accidental discovery.' MIT Provisional filed 2003.",
  },
  {
    id: "mp",
    name: "Molecular Probes / Biotium",
    area: "Oregon / Bay Area",
    notes:
      "Where Mao invented fluorescent probes (1990s). Not disclosed to USPTO.",
  },
  {
    id: "cn",
    name: "China - MgLT Manufacturing",
    area: "Multiple sites",
    notes:
      "CoAs identify China as origin. Import records show routing through Jordan.",
  },
  {
    id: "tx",
    name: "Mando Int'l HQ",
    area: "Texas",
    notes: "Defendant's principal place of business. Kappa Nutrition Labs.",
  },
  {
    id: "ci",
    name: "AIDP, Inc.",
    area: "City of Industry, CA",
    notes: "Distributor. 19535 E. Walnut Dr. Supplies Magtein® products.",
  },
];
const MOTIONS = [
  {
    type: "past",
    date: "Aug 6, 2025",
    title: "MTD / Transfer Venue to N.D. Texas",
    dkt: 20,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Withdrawn",
    detail: "Withdrawn Nov 11 (Dkt.54).",
  },
  {
    type: "past",
    date: "Oct 3, 2025",
    title: "Motion for Leave to Amend Complaint",
    dkt: 34,
    movant: "Plaintiff",
    opponent: "Defendant",
    status: "Withdrawn",
    detail: "First attempt. Withdrawn Nov 11.",
  },
  {
    type: "past",
    date: "Nov 12, 2025",
    title: "Answer + Counterclaims",
    dkt: 55,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Filed",
    detail: "10 counts.",
  },
  {
    type: "past",
    date: "Dec 3, 2025",
    title: "MTD Counterclaim Counts VIII-XIII",
    dkt: 66,
    movant: "Plaintiff",
    opponent: "Defendant",
    status: "Mooted",
    detail: "Mooted by amended counterclaims.",
  },
  {
    type: "past",
    date: "Jan 8, 2026",
    title: "Stipulation - 4 Patents Dismissed",
    dkt: 89,
    movant: "Both",
    opponent: "-",
    status: "Granted",
    detail: "Only '061 remains.",
  },
  {
    type: "past",
    date: "Jan 19, 2026",
    title: "Rule 11 Sanctions Motion",
    dkt: 93,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Withdrawn",
    detail: "Re: natural occurrence claim. Withdrawn Feb 13.",
  },
  {
    type: "past",
    date: "Mar 12, 2026",
    title: "MTD Neurocentria's FAC",
    dkt: 118,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Pending",
    detail: "With RJN + Abhyanker Declaration.",
  },
  {
    type: "past",
    date: "Mar 12, 2026",
    title: "MTD ThreoTech's FAC",
    dkt: 119,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Pending",
    detail: "Parallel motion.",
  },
  {
    type: "past",
    date: "Mar 17, 2026",
    title: "MTD Second Amended Counterclaims",
    dkt: 133,
    movant: "Plaintiff",
    opponent: "Defendant",
    status: "Pending",
    detail:
      "Filed by Neurocentria, ThreoTech, AIDP. Hearing 4/17/26 1:30 PM. Gavenman Decl. + 6 Exhibits.",
  },
  {
    type: "upcoming",
    date: "Sep 11, 2026",
    title: "Fact Discovery Cut-Off",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Due",
    detail: "All depositions.",
  },
  {
    type: "upcoming",
    date: "Nov 13, 2026",
    title: "Dispositive Motions",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Due",
    detail: "SJ deadline.",
  },
  {
    type: "upcoming",
    date: "Mar 9, 2027",
    title: "JURY TRIAL",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Trial",
    detail: "5-7 days.",
  },
];
const CASELAW = [
  {
    cs: "KSR Int'l v. Teleflex",
    ci: "550 U.S. 398 (2007)",
    ctx: "§103 Obviousness",
    h: "Combine known elements with predictable results = obvious.",
    v: true,
    by: "Defendant",
    quote:
      "'a court must ask whether the improvement is more than the predictable use of prior art elements'",
    qv: true,
    doc: "Dkt.112 (2d Amended CC)",
  },
  {
    cs: "Ass'n for Molecular Pathology v. Myriad Genetics",
    ci: "569 U.S. 576 (2013)",
    ctx: "§101 Natural Products",
    h: "Naturally occurring compounds not patent eligible merely because isolated.",
    v: true,
    by: "Defendant",
    quote:
      "'a naturally occurring DNA segment is a product of nature and not patent eligible'",
    qv: true,
    doc: "Dkt.118 (MTD Neurocentria)",
  },
  {
    cs: "Alice Corp. v. CLS Bank Int'l",
    ci: "573 U.S. 208 (2014)",
    ctx: "§101 Two-Step",
    h: "Two-step patent eligibility: directed to ineligible concept? Inventive concept?",
    v: true,
    by: "Defendant",
    quote:
      "'a claim reciting an abstract idea does not become eligible merely by adding apply it'",
    qv: true,
    doc: "Dkt.118, 119",
  },
  {
    cs: "Mayo v. Prometheus Labs",
    ci: "566 U.S. 66 (2012)",
    ctx: "§101 Laws of Nature",
    h: "Appending conventional steps to natural phenomena cannot make them patentable.",
    v: true,
    by: "Defendant",
    quote:
      "'simply appending conventional steps to laws of nature cannot make those laws patentable'",
    qv: true,
    doc: "Dkt.118, 119, 158",
  },
  {
    cs: "In re Aller",
    ci: "220 F.2d 454 (CCPA 1955)",
    ctx: "§103 Dosage Ranges",
    h: "Selection within known range generally obvious absent unexpected results.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.112",
  },
  {
    cs: "Sanderling v. Snap Inc.",
    ci: "2021 U.S. Dist. LEXIS 141110",
    ctx: "§101 at Pleading Stage",
    h: "§101 may be resolved on Rule 12(b)(6) without claim construction.",
    v: true,
    by: "Defendant",
    quote:
      "'claim construction is not an inviolable prerequisite to a validity determination under §101'",
    qv: true,
    doc: "Dkt.158 (Reply MTD ThreoTech)",
  },
  {
    cs: "Lexmark v. Static Control",
    ci: "572 U.S. 118 (2014)",
    ctx: "Lanham Act Standing",
    h: "Zone of interests + proximate causation required for false advertising.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.118",
  },
  {
    cs: "WiAV Solutions v. Motorola",
    ci: "631 F.3d 1257 (Fed. Cir. 2010)",
    ctx: "Patent Standing",
    h: "Patentee with all substantial rights may sue alone; exclusive licensee needs co-plaintiff.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Dkt.118, 146",
  },
  {
    cs: "Funk Bros. v. Kalo Inoculant",
    ci: "333 U.S. 127 (1948)",
    ctx: "§101 Natural Phenomena",
    h: "Discovery that natural organisms combine without losing properties = natural phenomenon.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.118",
  },
];
const ASSETS = [
  {
    id: 1,
    cat: "Filing",
    title: "Complaint (Dkt.1)",
    desc: "5 patents asserted. 5 exhibits.",
    date: "Jun 27, 2025",
    pages: 45,
    color: "#ef4444",
  },
  {
    id: 2,
    cat: "Filing",
    title: "FAC (Dkt.110)",
    desc: "Narrowed to '061 patent only.",
    date: "Feb 26, 2026",
    pages: 30,
    color: "#ef4444",
  },
  {
    id: 3,
    cat: "Filing",
    title: "2d Amended CC (Dkt.112)",
    desc: "10 counts: DJ, invalidity, fraud, UCL, Lanham.",
    date: "Mar 3, 2026",
    pages: 80,
    color: "#3b82f6",
  },
  {
    id: 4,
    cat: "Filing",
    title: "Scheduling Order (Dkt.32)",
    desc: "Full patent calendar. Trial 3/9/27.",
    date: "Sep 24, 2025",
    pages: 23,
    color: "#10b981",
  },
  {
    id: 5,
    cat: "Evidence",
    title: "Liu Depo Transcript",
    desc: "'We did not invent the molecule.' p.318.",
    date: "Feb 17, 2026",
    pages: 350,
    color: "#f59e0b",
  },
  {
    id: 6,
    cat: "Evidence",
    title: "Mao Depo Transcript",
    desc: "Fluorescent probe. Slutsky credit. 135+ patents.",
    date: "Feb 18, 2026",
    pages: 280,
    color: "#f59e0b",
  },
  {
    id: 7,
    cat: "Evidence",
    title: "CN1200366A (English)",
    desc: "1997 Chinese patent. MgLT synthesis + dosage forms.",
    date: "1997",
    pages: 12,
    color: "#f59e0b",
  },
  {
    id: 8,
    cat: "Evidence",
    title: "Certificates of Analysis",
    desc: "China identified as origin for MgLT hydrate.",
    date: "Various",
    pages: 8,
    color: "#ef4444",
  },
  {
    id: 9,
    cat: "Evidence",
    title: "ImportGenius Trade Data",
    desc: "Shipment records: China, Jordan routing.",
    date: "2022-2025",
    pages: 15,
    color: "#ef4444",
  },
  {
    id: 10,
    cat: "Filing",
    title: "Joint Claim Construction (Dkt.95)",
    desc: "Disputed terms for '061 patent.",
    date: "Jan 23, 2026",
    pages: 30,
    color: "#a78bfa",
  },
  {
    id: 11,
    cat: "Evidence",
    title: "Mao YouTube Video",
    desc: "'Accidental discovery using fluorescent dye tool.'",
    date: "~2015",
    pages: 1,
    color: "#f59e0b",
  },
  {
    id: 12,
    cat: "Filing",
    title: "Patent Standing Order (Dkt.33)",
    desc: "Judge Aenlle-Rocha patent rules.",
    date: "Sep 29, 2025",
    pages: 10,
    color: "#10b981",
  },
];
function DocC({ a }) {
  const ls = [];
  for (let i = 0; i < 4; i++)
    ls.push(
      <div
        key={i}
        style={{
          height: 2,
          background: "#252d3d",
          borderRadius: 1,
          marginBottom: 2.5,
          width: `${50 + Math.random() * 50}%`,
        }}
      />,
    );
  return (
    <div style={{ padding: 12 }}>
      <div style={{ background: "#1a1f30", borderRadius: 5, padding: 10 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <div
            style={{
              width: 22,
              height: 30,
              background: `${a.color}20`,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
            }}
          >
            📄
          </div>
          <div style={{ flex: 1 }}>{ls.slice(0, 2)}</div>
        </div>
        {ls.slice(2)}
        <span
          style={{ fontSize: 6, fontFamily: "monospace", color: "#4b5563" }}
        >
          {a.pages}pp
        </span>
      </div>
    </div>
  );
}
/* Ring Binder pages config */
/* ============================================================================
   USA v. Chidambaran & Naqvi · E.D.N.Y. 1:26-cr-00097-NGG · added by CaseBinder v41
   Sources: Superseding Indictment S-1 (Dkt.57), Naqvi Opp. (Dkt.17), bail letters
   (Dkt.33/35), Milbank letters (Dkt.56, Dkt.60), full PACER docket (71 entries).
   ============================================================================ */
const ILE_F_TL = [
  {
    date: "Jul 1, 2019",
    label: "Entity 1 Master Services Agreement signed",
    detail:
      "Chidambaran signs for iLearning; Co-Conspirator 1 signs for Entity 1. E1 never meaningfully collected revenue or provided services.",
    ref: "S-1 ¶26",
    type: "factual",
    ecf: "Purported channel-partner MSA; foundation of the round-trip structure.",
  },
  {
    date: "Aug 2020",
    label: "Entity 2 sham contract (~$50M/yr) agreed",
    detail:
      "Investor diligence flags E2 as a red flag: 'there is no trace of [Entity 2].' Defendants create entity info to share anyway.",
    ref: "S-1 ¶28",
    type: "factual",
    ecf: "Naqvi later describes E2 as one of 'our three biggest customers.'",
  },
  {
    date: "Oct 22, 2020",
    label: "Naqvi email to prospective investor",
    detail:
      "Falsely claims Entity 1 and Entity 2 were two of iLearning's biggest customers.",
    ref: "S-1 ¶62(d)",
    type: "factual",
    ecf: "Overt Act (d) in Count Two conspiracy.",
  },
  {
    date: "2021-2024",
    label: "$145M round-tripped through Entity 1",
    detail:
      "Investor and lender funds moved iLearning to E1 accounts controlled by Co-Conspirator 2, then back, directly or through other purported customers.",
    ref: "S-1 ¶27",
    type: "factual",
    ecf: "Core circular-flow allegation; bank records are the government's spine.",
  },
  {
    date: "Apr 2023",
    label: "Entity 3 contract (~$18M) though E3 did not exist",
    detail:
      "iLearning employees, at Chidambaran's direction, build a sham website for E3; CC-4 signs for E3.",
    ref: "S-1 ¶29",
    type: "factual",
    ecf: "Backfilled entity; strong scienter evidence against Chidambaran.",
  },
  {
    date: "Oct 2023",
    label: "Entity 4 contract ($20M+/yr) backdated to Aug 2023",
    detail:
      "Chidambaran to CC-3: 'there will be no liabilities' - E4 would never actually pay.",
    ref: "S-1 ¶30",
    type: "factual",
    ecf: "Direct admission attributed to Chidambaran, not Naqvi.",
  },
  {
    date: "Dec 17, 2023",
    label: "Diligence email to Investor A breaks down fake revenue",
    detail:
      "Over $143M (E1), $50M (E2), $29M (E3), $5M (E4) in fake revenue as of 9/30/23, sent at direction of both defendants.",
    ref: "S-1 ¶34",
    type: "factual",
    ecf: "The single most quantified misrepresentation in the S-1.",
  },
  {
    date: "Apr 16-17, 2024",
    label: "SPAC closes; AILE lists on NASDAQ",
    detail:
      "Bell-ringing ceremony in Times Square; defendants and CC-5 attend. Lender B lends ~$40M same week.",
    ref: "S-1 ¶¶35, 37",
    type: "factual",
    ecf: "NASDAQ photo appears in the indictment itself - expect it as a trial demonstrative.",
  },
  {
    date: "Apr 22, 2024",
    label: "Form 8-K: $421M revenue, 1,000+ customers, 4.4M users",
    detail:
      "At least 90% of reported revenue fabricated through sham customers and contracts.",
    ref: "S-1 ¶38",
    type: "factual",
    ecf: "Counts Three and Seven anchor filing.",
  },
  {
    date: "Jun 30, 2024",
    label: "Peak market cap ~$1.5B",
    detail: "AILE common stock peak.",
    ref: "S-1 ¶5",
    type: "factual",
    ecf: "Frames investor-loss narrative.",
  },
  {
    date: "Aug 29, 2024",
    label: "Short-seller report; stock falls 50%+ in one day",
    detail:
      "Investment Research Report accuses iLearning of fabricating customers and revenue and hiding the E1 relationship.",
    ref: "S-1 ¶43",
    type: "factual",
    ecf: "Defense: report author was self-interested; D. Md. civil case later dismissed with prejudice.",
  },
  {
    date: "Sep-Oct 2024",
    label: "Post-report round trips and lulling",
    detail:
      "Transfers staged to look like bona fide customer payments; ~$95K paid to CC-3 for sending money back; Lender B texts approve $2.5M transfer (Oct 11).",
    ref: "S-1 ¶¶46-47; Count Ten",
    type: "factual",
    ecf: "Government concealment theory vs defense lender-approved-mechanics reading.",
  },
  {
    date: "Dec 20-23, 2024",
    label: "Chapter 11 filed; both defendants resign",
    detail:
      "Payments from purported customers cease almost immediately after resignations.",
    ref: "S-1 ¶¶4, 49-50",
    type: "factual",
    ecf: "Government treats the cessation as proof the revenue was never real.",
  },
  {
    date: "Mar 6, 2025",
    label: "Converted to Chapter 7 liquidation",
    detail:
      "Hundreds of unpaid creditors; $50M+ unpaid liabilities. Tom Olivier later buys assets out of bankruptcy (AAI Solutions).",
    ref: "S-1 ¶4",
    type: "factual",
    ecf: "Olivier is a defense-favorable witness for Naqvi.",
  },
  {
    date: "May 2025",
    label: "Employee 1 subpoenaed; deletion directives follow",
    detail:
      "Employee 1 tells Chidambaran about the grand jury subpoena; Chidambaran directs CC-2 to delete emails, texts, WhatsApp messages and financial records.",
    ref: "S-1 ¶¶55-56",
    type: "factual",
    ecf: "Count Eleven (obstruction conspiracy) charges Chidambaran ONLY.",
  },
];
const ILE_P_TL = [
  {
    date: "Apr 15, 2026",
    label: "Sealed indictment returned (10 counts)",
    ref: "Dkt.1",
    type: "procedural",
    ecf: "F. #2024R00799.",
  },
  {
    date: "Apr 17, 2026",
    label: "Unsealed; reassigned to Judge Garaufis; both defendants arrested",
    ref: "Dkt.6",
    type: "procedural",
    ecf: "Naqvi arrested N.D. Cal. (26-mj-70519); Chidambaran in Maryland.",
  },
  {
    date: "Apr 22, 2026",
    label:
      "MJ Cousins orders Naqvi released; government appeals; Garaufis stays",
    ref: "Dkt.10, 12",
    type: "procedural",
    ecf: "Stay pending de novo review in EDNY.",
  },
  {
    date: "Apr 22-30, 2026",
    label: "Orders of detention entered (both defendants)",
    ref: "Dkt.22, 23",
    type: "procedural",
    ecf: "Chidambaran detained 4/22; Naqvi detained after 4/28 bail appeal hearing.",
  },
  {
    date: "May 7, 2026",
    label: "Arraignment - both plead not guilty",
    ref: "Minute Entry",
    type: "procedural",
    ecf: "Status conference set July 16.",
  },
  {
    date: "May 14, 2026",
    label: "Naqvi released - $3,000,022 package",
    ref: "Dkt.42/43",
    type: "procedural",
    ecf: "Bond executed by MJ Kaminsky after hearing before Judge Gonzalez; home detention + GPS.",
  },
  {
    date: "May 15, 2026",
    label: "Chidambaran consented $15M partially secured package",
    ref: "Dkt.35",
    type: "procedural",
    ecf: "Quinn Emanuel application; government consent; home confinement + GPS.",
  },
  {
    date: "May 28 - Jun 5, 2026",
    label: "Milbank substitutes for Mintz (Naqvi)",
    ref: "Dkt.46-55",
    type: "procedural",
    ecf: "Heller, Singer, Baeurle in; Grewal appeared then withdrew 6/5; Mintz terminated 5/29.",
  },
  {
    date: "Jun 22, 2026",
    label: "Order unsealing bond for certified raised-seal copies",
    ref: "Dkt.44, 56",
    type: "procedural",
    ecf: "County clerk rejected photocopies for recording confessions of judgment on three collateral properties.",
  },
  {
    date: "Jul 15, 2026",
    label: "Superseding Indictment S-1 filed",
    ref: "Dkt.57",
    type: "procedural",
    ecf: "Adds Count 11s (conspiracy to obstruct) against Chidambaran only.",
  },
  {
    date: "Jul 16, 2026",
    label: "Arraignment on S-1 - not guilty",
    ref: "Minute Entry",
    type: "procedural",
    ecf: "Status conference 9/14/26; speedy trial excluded through 9/14.",
  },
  {
    date: "Jul 22, 2026",
    label: "Naqvi travel application DENIED",
    ref: "Dkt.59",
    type: "procedural",
    ecf: "Short-notice request for Bay Area interviews; counsel apologized for timing.",
  },
  {
    date: "Aug 11, 2026",
    label: "Renewed travel application (PENDING)",
    ref: "Dkt.60",
    type: "procedural",
    ecf: "Interview weeks of Aug 17 or Aug 24; five companies incl. Patent VC; gov takes no position; PTS defers.",
  },
];
const ILE_S_TL = [
  {
    date: "Aug 17-28, 2026",
    label: "Requested Bay Area interview windows (Dkt.60 pending)",
    ref: "Dkt.60",
    type: "scheduling",
    ecf: "Today falls inside window two. Ruling gates the Patent VC meeting.",
  },
  {
    date: "Sep 14, 2026",
    label: "STATUS CONFERENCE - 11:00 AM, Courtroom 10B South",
    ref: "7/16 Minute Entry",
    type: "scheduling",
    ecf: "Parties to propose motion briefing schedule and trial dates. STA excluded through this date.",
  },
  {
    date: "Jan-Feb 2027",
    label: "Proposed 4-week JURY TRIAL window",
    ref: "7/16 Minute Entry",
    type: "scheduling",
    ecf: "Court indicated availability for a four-week trial in January or February 2027.",
  },
];
const ILE_DOCKET = [
  {
    date: "04/15/2026",
    num: 1,
    text: "SEALED INDICTMENT returned. 10 counts. F. #2024R00799.",
    tags: ["procedural"],
  },
  {
    date: "04/17/2026",
    num: 6,
    text: "ORDER unsealing indictment. Case reassigned Judge DeArcy Hall to Judge Garaufis. Arrest warrants executed.",
    tags: ["procedural"],
  },
  {
    date: "04/23/2026",
    num: 10,
    text: "GOVERNMENT LETTER seeking appeal of N.D. Cal. release order and stay (Naqvi).",
    tags: ["procedural"],
  },
  {
    date: "04/23/2026",
    num: 12,
    text: "ORDER staying MJ Cousins release order pending de novo review.",
    tags: ["procedural"],
  },
  {
    date: "04/24/2026",
    num: 13,
    text: "NOTICE OF APPEARANCE: Flashner (Mintz) for BOTH defendants. Later cured by substitutions - Curcio flag.",
    tags: ["procedural"],
  },
  {
    date: "04/27/2026",
    num: 16,
    text: "GOVERNMENT MEMO in support of detention (Naqvi).",
    tags: ["factual", "procedural"],
  },
  {
    date: "04/27/2026",
    num: 17,
    text: "NAQVI OPPOSITION to detention (23 pp). Ex.1: full 'play ball' email chain, produced by Naqvi himself.",
    tags: ["factual", "procedural"],
  },
  {
    date: "04/22/2026",
    num: 22,
    text: "ORDER OF DETENTION as to Chidambaran after bail appeal hearing. Gov on record: 'no more culpable defendant.'",
    tags: ["factual", "procedural"],
  },
  {
    date: "04/30/2026",
    num: 23,
    text: "ORDER OF DETENTION as to Naqvi (hearing held 4/28).",
    tags: ["procedural"],
  },
  {
    date: "05/07/2026",
    num: 28,
    text: "ARRAIGNMENT. Both defendants plead NOT GUILTY. Status conference set 7/16.",
    tags: ["procedural"],
  },
  {
    date: "05/13/2026",
    num: 33,
    text: "NAQVI BAIL PACKAGE letter (Halperin/Mintz). $3,000,022: $1,965,022 secured by three family homes + $1,035,000 unsecured.",
    tags: ["factual", "procedural"],
  },
  {
    date: "05/14/2026",
    num: 42,
    text: "BAIL BOND executed (SEALED). MJ Kaminsky. Home detention Fulshear TX, GPS, passport surrendered. Dkt.43 redacted version.",
    tags: ["procedural"],
  },
  {
    date: "05/15/2026",
    num: 35,
    text: "CHIDAMBARAN letter (Quinn Emanuel): consented $15M partially secured appearance bond; home confinement; GPS.",
    tags: ["procedural"],
  },
  {
    date: "05/21/2026",
    num: 44,
    text: "NAQVI LETTER MOTION re bond copies for county recording.",
    tags: ["procedural"],
  },
  {
    date: "05/29/2026",
    num: 52,
    text: "SUBSTITUTION OF COUNSEL granted: Milbank (Heller, Singer, Baeurle) in; Mintz terminated.",
    tags: ["procedural"],
  },
  {
    date: "06/05/2026",
    num: 55,
    text: "ORDER granting Gurbir Grewal (Milbank) withdrawal.",
    tags: ["procedural"],
  },
  {
    date: "06/11/2026",
    num: 56,
    text: "NAQVI LETTER (Heller) to MJ Kaminsky: county rejected photocopy; requests unsealing + certified raised-seal copies of bond; Baeurle to retrieve and record.",
    tags: ["procedural"],
  },
  {
    date: "06/22/2026",
    num: 58,
    text: "ORDER granting Dkt.44/56: bond unsealed for limited purpose; Clerk to issue certified copies with raised seal.",
    tags: ["procedural"],
  },
  {
    date: "07/15/2026",
    num: 57,
    text: "SUPERSEDING INDICTMENT S-1 (30 pp). Counts 1s-10s both defendants; Count 11s (obstruction conspiracy) CHIDAMBARAN ONLY. Forfeiture allegations.",
    tags: ["factual", "procedural"],
  },
  {
    date: "07/16/2026",
    num: 59,
    text: "ARRAIGNMENT ON S-1. Not guilty. Status conf 9/14/26 11:00 AM Ctrm 10B South. 4-week trial Jan/Feb 2027 proposed. STA excluded through 9/14.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "07/22/2026",
    num: 60,
    text: "NAQVI travel application for Bay Area interviews - DENIED (short notice; Dkt.59/60).",
    tags: ["procedural"],
  },
  {
    date: "08/11/2026",
    num: 61,
    text: "RENEWED travel application: interview weeks of Aug 17 or Aug 24. Companies: Zayna Ventures, Tenstorrent, Hippocratic AI, Axiado, Patent VC. No fundraising or investor-facing roles. PTS confirms compliance, defers. Gov: no position. PENDING.",
    tags: ["procedural", "scheduling"],
  },
];
const ILE_MOTIONS = [
  {
    type: "past",
    date: "Apr 23, 2026",
    title: "Gov appeal of N.D. Cal. release + stay (Naqvi)",
    dkt: 10,
    movant: "Government",
    opponent: "Naqvi",
    status: "Granted",
    detail: "Stay entered same day (Dkt.12); de novo detention followed.",
  },
  {
    type: "past",
    date: "Apr 22-30, 2026",
    title: "Orders of Detention (both defendants)",
    dkt: 22,
    movant: "Government",
    opponent: "Both",
    status: "Granted",
    detail: "Dkt.22 (Chidambaran), Dkt.23 (Naqvi after 4/28 hearing).",
  },
  {
    type: "past",
    date: "May 13, 2026",
    title: "Naqvi bail package - $3,000,022",
    dkt: 33,
    movant: "Naqvi",
    opponent: "Government",
    status: "Granted 5/14",
    detail:
      "Released on conditions: home detention Fulshear TX, GPS, passport surrendered, travel limited to NYC/LI/Mass/SD Tex (100 mi).",
  },
  {
    type: "past",
    date: "May 15, 2026",
    title: "Chidambaran consented $15M package",
    dkt: 35,
    movant: "Chidambaran",
    opponent: "Government (consent)",
    status: "Granted",
    detail:
      "Partially secured appearance bond; home confinement Potomac MD; GPS; passport surrendered.",
  },
  {
    type: "past",
    date: "Jun 11, 2026",
    title: "Unseal bond / certified raised-seal copies",
    dkt: 56,
    movant: "Naqvi",
    opponent: "None",
    status: "Granted 6/22",
    detail:
      "County clerks rejected photocopies; needed for recording confessions of judgment on three collateral properties.",
  },
  {
    type: "past",
    date: "Jul 22, 2026",
    title: "Travel modification #1 (Bay Area interviews)",
    dkt: 59,
    movant: "Naqvi",
    opponent: "Government",
    status: "DENIED",
    detail:
      "Short-notice application; counsel apologized for timing in renewed letter.",
  },
  {
    type: "past",
    date: "Aug 11, 2026",
    title: "RENEWED travel application (Dkt.60)",
    dkt: 60,
    movant: "Naqvi",
    opponent: "Government (no position)",
    status: "PENDING",
    detail:
      "Weeks of Aug 17 or Aug 24. Patent VC is company 4. Ruling gates this week's interviews.",
  },
  {
    type: "upcoming",
    date: "Sep 14, 2026",
    title: "STATUS CONFERENCE - schedule + trial dates",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Set",
    detail:
      "11:00 AM, Courtroom 10B South. Proposed briefing schedule and Jan/Feb 2027 trial dates due.",
  },
  {
    type: "upcoming",
    date: "Q4 2026 (expected)",
    title: "Rule 14 severance motion (Naqvi) - watch",
    dkt: null,
    movant: "Naqvi",
    opponent: "Government",
    status: "Anticipated",
    detail:
      "Count 11s obstruction spillover; Bruton issues on Chidambaran statements ('no liabilities').",
  },
  {
    type: "upcoming",
    date: "Jan/Feb 2027",
    title: "JURY TRIAL - 4 weeks (proposed)",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Proposed",
    detail:
      "Court indicated availability for four weeks in January or February 2027.",
  },
];
const ILE_CASELAW = [
  {
    cs: "18 U.S.C. § 225 (CFCE)",
    ci: "Continuing Financial Crimes Enterprise",
    ctx: "Count 1s Elements",
    h: "Requires organizing/managing/supervising a continuing financial crimes enterprise of 4+ persons and $5M+ gross receipts in 24 months. Ten-year mandatory minimum.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "S-1 ¶58",
  },
  {
    cs: "United States v. Sabhnani",
    ci: "493 F.3d 63 (2d Cir. 2007)",
    ctx: "Bail - conditions can rebut",
    h: "Even in serious cases, stringent conditions (home detention, electronic monitoring, substantial secured bond) can reasonably assure appearance.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.17 (Naqvi Opp.)",
  },
  {
    cs: "United States v. Madoff",
    ci: "586 F. Supp. 2d 240 (S.D.N.Y. 2009)",
    ctx: "Bail in massive fraud",
    h: "Pre-plea release on conditions upheld despite scale of alleged fraud; dangerousness must be economic-harm specific.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.17",
  },
  {
    cs: "Ciminelli v. United States",
    ci: "598 U.S. 306 (2023)",
    ctx: "Wire fraud - property requirement",
    h: "Right-to-control theory invalid; wire fraud requires a traditional property interest as the object of the scheme.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Anticipated Rule 12/29 motions",
  },
  {
    cs: "Neder v. United States",
    ci: "527 U.S. 1 (1999)",
    ctx: "Materiality element",
    h: "Materiality is an element of federal mail, wire, and bank fraud.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Jury instruction battles",
  },
  {
    cs: "Ernst & Ernst v. Hochfelder",
    ci: "425 U.S. 185 (1976)",
    ctx: "10b-5 scienter",
    h: "Section 10(b) requires scienter - intent to deceive, manipulate, or defraud.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Count 3s defense",
  },
  {
    cs: "Zafiro v. United States",
    ci: "506 U.S. 534 (1993)",
    ctx: "Rule 14 severance",
    h: "Severance warranted only where joint trial would compromise a specific trial right or prevent reliable judgment; limiting instructions usually suffice.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Naqvi severance strategy re Count 11s",
  },
  {
    cs: "Bruton v. United States",
    ci: "391 U.S. 123 (1968)",
    ctx: "Co-defendant statements",
    h: "Admission of a non-testifying co-defendant's confession implicating the defendant violates the Confrontation Clause.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Chidambaran 'no liabilities' statement (S-1 ¶30)",
  },
  {
    cs: "Honeycutt v. United States",
    ci: "581 U.S. 443 (2017)",
    ctx: "Forfeiture scope",
    h: "No joint-and-several forfeiture liability; a defendant forfeits only property he personally obtained from the offense.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Forfeiture allegations, S-1 ¶¶68-71",
  },
];
const ILE_WITNESSES = [
  {
    id: "pc",
    name: "Puthugramam 'Harish' Chidambaran",
    role: "Co-Defendant · Founder/CEO 2010-12/24",
    ph: "👔",
    party: "Defendant",
    notes: [
      "With wife owned ~75% of iLearning post-SPAC; 'virtually unfettered control' of bank accounts (S-1 ¶53).",
      "Received ~50.37M shares (>$500M at listing), $12.5M RSUs, $700K+ comp.",
      "Count 11s obstruction charges him ONLY. Gov at 4/22 hearing: 'no more culpable defendant in this case.'",
    ],
  },
  {
    id: "fn",
    name: "Sayyed Farhan Ali Naqvi",
    role: "Defendant · CFO & Head of Corp Dev 2019-12/24",
    ph: "💼",
    party: "Defendant",
    notes: [
      "IIT B.Tech; Harvard MBA. Fulshear/College Station TX. Supports wife, 9-year-old daughter, mother.",
      "Received 1.35M+ shares (~$11.2M at 6/20/24) + $4.4M tax gross-up + $650K+ comp.",
      "Produced the 'play ball' email himself (Dkt.17 Ex.1). Post-iLearning: Tenneco, then Tenstorrent (terminated on arrest).",
    ],
  },
  {
    id: "cc1",
    name: "Co-Conspirator 1",
    role: "iLearning AVP · held out as Entity 1 CEO",
    ph: "🕵️",
    party: "Government",
    notes: [
      "Signed the 7/1/19 Master Services Agreement for Entity 1 (S-1 ¶26).",
      "Directed to submit false docs to auditor and Lender B (S-1 ¶42).",
    ],
  },
  {
    id: "cc2",
    name: "Co-Conspirator 2",
    role: "iLearning VP · controlled E1/E2 US affiliate accounts",
    ph: "🕵️",
    party: "Government",
    notes: [
      "Controlled the bank accounts through which $145M round-tripped (S-1 ¶27, 47).",
      "Executed deletions at Chidambaran's direction (S-1 ¶¶45, 56). Highest cooperation risk in the case.",
      "Every obstruction directive in the S-1 runs Chidambaran to CC-2 - none through Naqvi.",
    ],
  },
  {
    id: "cc3",
    name: "Co-Conspirator 3",
    role: "Entity 4 CEO",
    ph: "🕵️",
    party: "Government",
    notes: [
      "Paid ~$95K to transmit money back to iLearning as fake E4 contract payments (S-1 ¶47).",
      "Sent false contract confirmation to Lender B on 9/10/24 (Counts Five/Nine).",
      "Told by Chidambaran 'there will be no liabilities' (S-1 ¶30). Classic cooperator profile.",
    ],
  },
  {
    id: "cc4",
    name: "Co-Conspirator 4",
    role: "iLearning employee · relative of senior employee",
    ph: "🕵️",
    party: "Government",
    notes: [
      "Signed the Entity 3 sham contract for an entity that did not exist (S-1 ¶29).",
    ],
  },
  {
    id: "cc5",
    name: "Co-Conspirator 5",
    role: "Senior iLearning executive",
    ph: "🕵️",
    party: "Government",
    notes: [
      "Flew to JFK 4/17/24 for the NASDAQ bell-ringing to market AILE shares (S-1 ¶62(g)).",
    ],
  },
  {
    id: "to",
    name: "Tom Olivier",
    role: "Interim COO/CEO/CBO · SPAC president · AAI Solutions",
    ph: "🧭",
    party: "Both",
    notes: [
      "Ran 18 months of diligence as SPAC president, then bought iLearning assets out of Chapter 7.",
      "Continued working with Naqvi after his leave - powerful defense fact on Naqvi's credibility (Dkt.17).",
      "Defense interview target before 9/14 conference.",
    ],
  },
  {
    id: "bg",
    name: "Bonnie Jeanne Gerety",
    role: "Executive hired post-short-report",
    ph: "🧭",
    party: "Both",
    notes: [
      "WhatsApp communications with Naqvi produced by Naqvi in the civil matter (Dkt.17).",
      "Rebuts the encrypted-app-as-obstruction framing.",
    ],
  },
  {
    id: "e1",
    name: "Employee 1",
    role: "Former iLearning employee · subpoenaed May 2025",
    ph: "📨",
    party: "Government",
    notes: [
      "Told Chidambaran about the grand jury subpoena, triggering the Count 11s deletion directives (S-1 ¶56).",
    ],
  },
];
const ILE_LOCS = [
  {
    id: "edny",
    name: "E.D.N.Y. - Brooklyn (Garaufis)",
    area: "225 Cadman Plaza East, Courtroom 10B South",
    notes:
      "Forum. Status conf 9/14/26 11:00 AM. USAO at 271 Cadman Plaza East (Ammari, Dugan, Rehnquist).",
  },
  {
    id: "fulshear",
    name: "Naqvi residence - home detention",
    area: "Fulshear, TX (SD Tex)",
    notes:
      "Home detention + GPS. Travel limited to NYC/LI/Mass/SD Tex within 100 miles absent court order. Marion TX property also posted.",
  },
  {
    id: "potomac",
    name: "Chidambaran residence",
    area: "Potomac, MD",
    notes: "Home confinement + GPS under $15M consented package (Dkt.35).",
  },
  {
    id: "bethesda",
    name: "iLearning HQ (defunct)",
    area: "Bethesda, MD",
    notes:
      "Principal place of business; Ch.11 12/20/24, Ch.7 3/6/25. Assets bought by Olivier (AAI Solutions).",
  },
  {
    id: "nasdaq",
    name: "NASDAQ MarketSite",
    area: "Times Square, NYC",
    notes:
      "4/17/24 (and 5/1/24) bell-ringing. Photo embedded in S-1 at p.13 - expect it in the government opening.",
  },
  {
    id: "ndcal",
    name: "N.D. Cal. (arrest + initial release)",
    area: "26-mj-70519-MAG",
    notes:
      "Naqvi arrested 4/17/26; MJ Cousins ordered release 4/22; stayed and reversed in EDNY.",
  },
  {
    id: "sf",
    name: "SF Bay Area (Dkt.60 travel)",
    area: "Mountain View / Santa Clara / Palo Alto",
    notes:
      "Requested interviews: Zayna Ventures, Tenstorrent, Hippocratic AI, Axiado, Patent VC. Pending court leave; PTS itinerary approval required.",
  },
  {
    id: "entities",
    name: "Entities 1-3 (purported customers)",
    area: "India / UAE / US affiliates",
    notes:
      "Shell entities per S-1 ¶¶15-17. India network is the core of the gov flight-risk theory; defense counters with family ties and discrimination record.",
  },
  {
    id: "lenders",
    name: "Lenders A/B/C",
    area: "Portola Valley CA · Pasadena CA · Morriston NJ",
    notes:
      "~$40M VC + ~$40M FDIC bank + ~$20M FDIC bank. NY offices ground EDNY venue for the wires.",
  },
];
const ILE_ASSETS = [
  {
    id: 1,
    cat: "Filing",
    title: "Superseding Indictment S-1 (Dkt.57)",
    desc: "30 pp. Counts 1s-10s both; 11s Chidambaran only. Forfeiture allegations.",
    date: "Jul 15, 2026",
    pages: 30,
    color: "#ef4444",
  },
  {
    id: 2,
    cat: "Filing",
    title: "Naqvi Opposition to Detention (Dkt.17)",
    desc: "23 pp. Differential culpability; spam finding; 'play ball' full chain at Ex.1.",
    date: "Apr 27, 2026",
    pages: 23,
    color: "#3b82f6",
  },
  {
    id: 3,
    cat: "Evidence",
    title: "'Play ball' email chain (Dkt.17 Ex.1)",
    desc: "Produced by Naqvi himself. Defense: investor preconditions. Gov: restart-the-fraud reading.",
    date: "2024",
    pages: 4,
    color: "#f59e0b",
  },
  {
    id: 4,
    cat: "Evidence",
    title: "Special committee forensic finding",
    desc: "Items deleted from Naqvi's account 'appear to be spam' - company's own expert.",
    date: "2025",
    pages: 2,
    color: "#f59e0b",
  },
  {
    id: 5,
    cat: "Filing",
    title: "Bail package letter (Dkt.33)",
    desc: "$3,000,022: $1,965,022 secured (three family homes, confessions of judgment) + $1,035,000 unsecured.",
    date: "May 13, 2026",
    pages: 7,
    color: "#3b82f6",
  },
  {
    id: 6,
    cat: "Filing",
    title: "Chidambaran $15M package (Dkt.35)",
    desc: "Consented partially secured appearance bond; home confinement; GPS.",
    date: "May 15, 2026",
    pages: 5,
    color: "#a78bfa",
  },
  {
    id: 7,
    cat: "Filing",
    title: "Bond + certified-copy order (Dkt.42/43/56)",
    desc: "Sealed bond; 6/22 order for raised-seal certified copies for county recording.",
    date: "Jun 22, 2026",
    pages: 4,
    color: "#10b981",
  },
  {
    id: 8,
    cat: "Filing",
    title: "Renewed travel application (Dkt.60)",
    desc: "Weeks of Aug 17 or 24. Five companies incl. Patent VC. No fundraising/investor roles. PENDING.",
    date: "Aug 11, 2026",
    pages: 3,
    color: "#10b981",
  },
  {
    id: 9,
    cat: "Evidence",
    title: "Form 8-K (Apr 22, 2024)",
    desc: "$421M 2023 revenue, 1,000+ enterprise customers, 4.4M users. >=90% fabricated per S-1 ¶38.",
    date: "Apr 22, 2024",
    pages: 60,
    color: "#ef4444",
  },
  {
    id: 10,
    cat: "Evidence",
    title: "Dec 17, 2023 diligence email",
    desc: "Fake revenue by customer: $143M E1, $50M E2, $29M E3, $5M E4 (S-1 ¶34).",
    date: "Dec 17, 2023",
    pages: 3,
    color: "#ef4444",
  },
  {
    id: 11,
    cat: "Evidence",
    title: "Short-seller report (Aug 29, 2024)",
    desc: "Stock -50%+ same day. Defense: self-interested author; D. Md. civil case dismissed with prejudice.",
    date: "Aug 29, 2024",
    pages: 40,
    color: "#f59e0b",
  },
  {
    id: 12,
    cat: "Evidence",
    title: "Lender B approval texts (Oct 11, 2024)",
    desc: "Lender B reps approve $2.5M transfer to E1 account - Violation SIX / Count Ten wire.",
    date: "Oct 11, 2024",
    pages: 2,
    color: "#f59e0b",
  },
];
const ILE_ATTORNEYS = [
  {
    name: "Nola B. Heller",
    firm: "Milbank LLP",
    role: "Lead Counsel - Naqvi",
    party: "Defendant",
    bar: "NY",
    email: "nheller@milbank.com",
    phone: "(212) 530-5108",
    involvement:
      "Former SDNY criminal division chief. Substituted in 5/28-29/26. Authored Dkt.56 bond letter and Dkt.60 renewed travel application.",
    dkts: ["52", "56", "60"],
  },
  {
    name: "Scott B. Singer",
    firm: "Milbank LLP",
    role: "Counsel - Naqvi",
    party: "Defendant",
    bar: "NY",
    email: "ssinger@milbank.com",
    phone: "(212) 530-5000",
    involvement: "Co-signed travel applications; motions strategy.",
    dkts: ["60"],
  },
  {
    name: "Lucille E. Baeurle",
    firm: "Milbank LLP",
    role: "Counsel of record - Naqvi",
    party: "Defendant",
    bar: "NY",
    email: "lbaeurle@milbank.com",
    phone: "",
    involvement:
      "Retrieving certified raised-seal bond copies from the Clerk and recording with county clerks per 6/22 order.",
    dkts: ["56"],
  },
  {
    name: "Gurbir S. Grewal",
    firm: "Milbank LLP",
    role: "Withdrawn 6/5/26",
    party: "Defendant",
    bar: "NJ/NY",
    email: "",
    phone: "",
    involvement:
      "Former SEC Enforcement Director. Appeared 5/28; withdrawal granted 6/5 (Dkt.55).",
    dkts: ["55"],
  },
  {
    name: "Jonathan L. Halperin",
    firm: "Mintz Levin (terminated 5/29/26)",
    role: "Prior counsel - Naqvi",
    party: "Defendant",
    bar: "NY",
    email: "",
    phone: "",
    involvement:
      "Authored 5/13 bail package (Dkt.33). Team: Sylvia, Flashner, Galliher.",
    dkts: ["33"],
  },
  {
    name: "Samuel Nitze",
    firm: "Quinn Emanuel",
    role: "Lead Counsel - Chidambaran",
    party: "Defendant",
    bar: "NY",
    email: "samnitze@quinnemanuel.com",
    phone: "(212) 849-7000",
    involvement:
      "Former EDNY AUSA. Authored 5/15 consented $15M package (Dkt.35). Team: Hartman, Levy (PHV).",
    dkts: ["35"],
  },
  {
    name: "Kamil R. Ammari",
    firm: "USAO EDNY",
    role: "Lead AUSA",
    party: "Government",
    bar: "NY",
    email: "kamil.ammari@usdoj.gov",
    phone: "(718) 254-6000",
    involvement:
      "Led detention appeal (Dkt.10/16). Business & Securities Fraud Section.",
    dkts: ["10", "16"],
  },
  {
    name: "Joshua B. Dugan",
    firm: "USAO EDNY",
    role: "AUSA",
    party: "Government",
    bar: "NY",
    email: "joshua.dugan@usdoj.gov",
    phone: "(718) 254-6000",
    involvement: "Co-signed detention filings; grand jury presentation.",
    dkts: ["10"],
  },
  {
    name: "Dana Rehnquist",
    firm: "USAO EDNY",
    role: "AUSA",
    party: "Government",
    bar: "NY",
    email: "",
    phone: "(718) 254-6000",
    involvement: "Trial team.",
    dkts: [],
  },
  {
    name: "David Pitluck",
    firm: "USAO EDNY",
    role: "AUSA (signed S-1)",
    party: "Government",
    bar: "NY",
    email: "",
    phone: "(718) 254-6000",
    involvement: "Signed the superseding indictment for USA Nocella.",
    dkts: ["57"],
  },
];
const ILE_THREADS = [
  {
    id: "secA",
    l: "A",
    n: "Indictment & Arrests",
    threads: [
      {
        id: "A1",
        t: "Charging Instruments",
        docs: [
          {
            d: "1",
            y: "Sealed Indictment",
            p: 1,
            f: "04/15/2026",
            s: "10 counts vs Chidambaran and Naqvi",
          },
          {
            d: "6",
            y: "Unsealing Order",
            p: 0,
            f: "04/17/2026",
            s: "Unsealed; reassigned to Judge Garaufis; arrests executed",
          },
          {
            d: "57",
            y: "Superseding Indictment S-1",
            p: 1,
            f: "07/15/2026",
            s: "Adds Count 11s (obstruction conspiracy) vs Chidambaran only; forfeiture allegations",
          },
        ],
      },
    ],
  },
  {
    id: "secB",
    l: "B",
    n: "Bail Litigation - Naqvi",
    threads: [
      {
        id: "B1",
        t: "Detention Appeal & Release",
        docs: [
          {
            d: "8 (N.D.Cal.)",
            y: "Release Order (MJ Cousins)",
            p: 1,
            f: "04/22/2026",
            s: "26-mj-70519: conditions ordered; stayed for gov appeal",
          },
          {
            d: "10",
            y: "Gov Appeal Letter",
            p: 1,
            f: "04/23/2026",
            s: "De novo review requested; transport to EDNY",
          },
          {
            d: "12",
            y: "Stay Order",
            p: 0,
            f: "04/23/2026",
            s: "Garaufis stays release pending review",
          },
          {
            d: "16",
            y: "Gov Detention Memo",
            p: 0,
            f: "04/27/2026",
            s: "Flight risk via India network; obstruction theming",
          },
          {
            d: "17",
            y: "Naqvi Opposition",
            p: 1,
            f: "04/27/2026",
            s: "23 pp; differential culpability; spam finding; play-ball chain Ex.1",
          },
          {
            d: "23",
            y: "Order of Detention",
            p: 1,
            f: "04/30/2026",
            s: "After 4/28 hearing",
          },
          {
            d: "33",
            y: "Bail Package Letter",
            p: 1,
            f: "05/13/2026",
            s: "$3,000,022 - three homes + unsecured sureties",
          },
          {
            d: "42/43",
            y: "Bond Executed",
            p: 1,
            f: "05/14/2026",
            s: "MJ Kaminsky; home detention + GPS; sealed/redacted",
          },
        ],
      },
    ],
  },
  {
    id: "secC",
    l: "C",
    n: "Bail - Chidambaran",
    threads: [
      {
        id: "C1",
        t: "Detention & Consented Package",
        docs: [
          {
            d: "22",
            y: "Order of Detention",
            p: 1,
            f: "04/22/2026",
            s: "After bail appeal hearing; gov: no more culpable defendant",
          },
          {
            d: "35",
            y: "Consented $15M Package",
            p: 1,
            f: "05/15/2026",
            s: "Quinn Emanuel letter; home confinement Potomac MD; GPS",
          },
        ],
      },
    ],
  },
  {
    id: "secD",
    l: "D",
    n: "Counsel Changes",
    threads: [
      {
        id: "D1",
        t: "Naqvi: Mintz to Milbank",
        docs: [
          {
            d: "13",
            y: "Appearance (Flashner)",
            p: 0,
            f: "04/24/2026",
            s: "Appeared for BOTH defendants - joint-representation flag",
          },
          {
            d: "46-52",
            y: "Substitution",
            p: 1,
            f: "05/28-29/2026",
            s: "Milbank in (Heller, Grewal, Singer, Baeurle); Mintz terminated",
          },
          {
            d: "55",
            y: "Grewal Withdrawal",
            p: 0,
            f: "06/05/2026",
            s: "Granted",
          },
        ],
      },
    ],
  },
  {
    id: "secE",
    l: "E",
    n: "Bond Administration",
    threads: [
      {
        id: "E1",
        t: "County Recording of Collateral",
        docs: [
          {
            d: "44",
            y: "Letter Motion",
            p: 0,
            f: "05/21/2026",
            s: "Bond copies for county recording",
          },
          {
            d: "56",
            y: "Heller Letter",
            p: 1,
            f: "06/11/2026",
            s: "County rejected photocopy; certified raised-seal copies requested; Baeurle to record",
          },
          {
            d: "58",
            y: "Order",
            p: 1,
            f: "06/22/2026",
            s: "Bond unsealed for limited purpose; Clerk to issue certified copies",
          },
        ],
      },
    ],
  },
  {
    id: "secF",
    l: "F",
    n: "Schedule & Speedy Trial",
    threads: [
      {
        id: "F1",
        t: "Arraignments & Conference",
        docs: [
          {
            d: "28",
            y: "Arraignment",
            p: 0,
            f: "05/07/2026",
            s: "Not guilty; conf 7/16",
          },
          {
            d: "59",
            y: "S-1 Arraignment Minute",
            p: 1,
            f: "07/16/2026",
            s: "Not guilty; status conf 9/14 11:00 AM Ctrm 10B South; 4-week Jan/Feb 2027 trial proposed; STA excluded",
          },
        ],
      },
    ],
  },
  {
    id: "secG",
    l: "G",
    n: "Travel Applications",
    threads: [
      {
        id: "G1",
        t: "Bay Area Interview Requests",
        docs: [
          {
            d: "59/60",
            y: "Application #1",
            p: 0,
            f: "07/22/2026",
            s: "DENIED - short notice",
          },
          {
            d: "61",
            y: "Renewed Application",
            p: 1,
            f: "08/11/2026",
            s: "Weeks of Aug 17 or 24; Zayna Ventures, Tenstorrent, Hippocratic AI, Axiado, Patent VC; PENDING",
          },
        ],
      },
    ],
  },
];
const ILE_GOV_THEORIES = [
  {
    title: "CFCE (§225) - the Round-Trip Revenue Machine",
    text: [
      "From 2019 to 2025 the defendants organized and supervised a four-plus-person enterprise that manufactured revenue: sham contracts with Entities 1-4, then $145M of investor and lender money circulated out through CC-2's accounts and back in as fake customer payments. Gross receipts dwarf the $5M statutory floor.",
      "Strongest: the bank records. $145M of circular transfers (S-1 ¶27), six charged wires, the Dec 17 2023 email quantifying $227M of fake revenue by customer, and payments stopping the moment the defendants resigned (¶49).",
      "The defense will attack the §225 elements - organizer/manager/supervisor status and the enterprise structure. But the CFO who initiated wires (¶62(o)-(p)) and urged the Entity 1 lie (¶40) fits the statute.",
      "Weakness: §225 is rarely charged and its construction is untested in this posture; a 10-year floor invites aggressive Rule 29 and instruction fights on every element.",
      "At its weakest against Naqvi: the government's own 4/22 statement that Chidambaran is the most culpable defendant, plus 75% ownership and unfettered account control sitting entirely with the CEO.",
    ],
    ground: "S-1 ¶¶24-32, 58-59; Counts 1s, 5s-10s",
  },
  {
    title: "Securities Fraud - the April 22, 2024 Form 8-K",
    text: [
      "To induce investors to buy and hold AILE, the defendants filed financials claiming $421M of 2023 revenue, 1,000+ enterprise customers and 4.4M licensed users, when at least 90% of the revenue was fabricated (¶38). Peak $1.5B market cap; retail holders wiped out by January 2025.",
      "Strongest: the 8-K is a public, signed document; the fake-revenue arithmetic comes from the company's own diligence email; the related-party denial about Entity 1 (¶41) was knowably false.",
      "Scienter contest: defense will argue reliance on auditors and counsel who transmitted the filings (Violations ONE-FOUR are counsel-to-auditor and counsel-to-lender emails).",
      "Loss causation and materiality fights will center on the 8/29/24 report and the D. Md. civil dismissal.",
      "Weakest: a jury may struggle to separate SPAC-era hype from criminal intent, and the short-seller's self-interest gives the defense a villain.",
    ],
    ground: "S-1 ¶¶33-41; Counts 2s, 3s",
  },
  {
    title: "Concealment After Exposure - Lulling Conduct",
    text: [
      "After the 8/29/24 report cratered the stock, the defendants did not stop - they staged transactions to look like bona fide customer payments (¶¶46-47), paid CC-3 ~$95K to send money back, and fed false information to Lenders B and C in person in New York (¶62(j)).",
      "Strongest: post-exposure conduct is the cleanest intent evidence; innocent executives investigate, they do not choreograph round trips.",
      "The Oct 11 2024 Lender B texts (Count Ten) cut both ways: approval by the lender supports the defense reading of transparent paydown mechanics.",
      "Chidambaran-specific: the deletion directives to CC-2 in Sept 2024 and again after the May 2025 subpoena (¶¶45, 56) are charged only against him.",
      "Weakest against Naqvi: none of the destruction conduct is his, and the forensic expert called his deleted items spam.",
    ],
    ground: "S-1 ¶¶43-48, 55-56; Counts 9s-11s",
  },
];
const NAQVI_THEORIES = {
  plaintiff: ILE_GOV_THEORIES,
  defendant: [
    {
      title: "Differential Culpability - the Subordinate CFO",
      text: [
        "The government told this Court on April 22 that there is no more culpable defendant than Chidambaran. Chidambaran founded the company, owned 75% with his wife, exercised virtually unfettered control over the accounts, and personally directed the sham websites, the backdated contracts and the deletions. Naqvi held under 1% and took direction.",
        "Strongest: the S-1 itself. Every founding act (¶26), every entity fabrication (¶29-30), every deletion directive (¶45, 56) runs through Chidambaran. Count 11s excludes Naqvi.",
        "The government answers that Naqvi signed lender documents, initiated the Oct 2024 wires (¶62(o)-(p)) and urged the Entity 1 lie at his own suggestion (¶40).",
        "The 'at the urging of Naqvi' allegation in ¶40 is the single worst sentence in the S-1 for this theory and must be confronted, not avoided.",
        "At its weakest: a CFO for six years who signed the numbers cannot fully outsource intent; role reduction may land at sentencing rather than verdict.",
      ],
      ground: "S-1 ¶¶8-9, 40, 45, 53-56; Apr 22 Hr'g Tr. 8:16-22",
    },
    {
      title: "No Obstruction - the Spam Finding",
      text: [
        "The company's own special committee retained a forensic expert who concluded the items deleted from Naqvi's account appear to be spam. The grand jury agreed: Count 11s charges Chidambaran alone. The government's detention-stage obstruction theming against Naqvi did not survive its own investigation.",
        "Strongest: an adverse-party expert finding is close to unimpeachable, and the charging decision corroborates it.",
        "WhatsApp usage is not consciousness of guilt - three billion people use it, and Naqvi produced his WhatsApp threads with Olivier and Gerety voluntarily.",
        "Government fallback: uncharged conduct can still be 404(b)/sentencing fodder; keep the record clean.",
        "Weakest: if CC-2 cooperates and testifies to any Naqvi-adjacent deletion request, the firewall narrows to the documents.",
      ],
      ground: "Dkt.17 at 8-9; S-1 Count 11s; ¶¶45, 55-56",
    },
    {
      title: "Good-Faith Process - Auditors, Counsel, Olivier",
      text: [
        "The charged wires are emails from lawyers to auditors and lenders (Violations ONE-FOUR). The numbers passed through an audit, through SPAC diligence run for 18 months by Tom Olivier, and through lender underwriting. Olivier - who had every incentive and every document - bought the assets out of bankruptcy and kept working with Naqvi.",
        "Strongest: Olivier. The most informed counterparty in the case voted with his money and his hiring decisions.",
        "The 'play ball' email, produced by Naqvi himself, reads in full as relaying investor preconditions - the government quoted a fragment.",
        "Government answer: professionals were deceived with fabricated confirmations (¶42); reliance fails when the inputs are forged.",
        "Weakest: good faith is a jury argument, not a motion winner; it lives or dies on Naqvi's own testimony decision.",
      ],
      ground: "Dkt.17 Ex.1; S-1 ¶¶31-36, 42; Counts 5s-8s",
    },
    {
      title: "Tainted Genesis - Short-Seller and the Dismissed Civil Case",
      text: [
        "This prosecution germinated from a self-interested short-seller report. The parallel securities class action in the District of Maryland was dismissed with prejudice. The theory that survived civil scrutiny only through anonymized entities deserves skepticism at trial.",
        "Strongest: dismissal with prejudice is a judicial data point a jury can understand.",
        "Different standards: civil dismissal does not answer bank-records evidence, and the government will say so in one sentence.",
        "Use for cross of the case agent on investigative genesis and confirmation bias, not as a standalone defense.",
        "Weakest: judges routinely exclude civil-outcome evidence under 403; preserve the record but do not build the opening on it.",
      ],
      ground: "Dkt.17 at 5-7; D. Md. dismissal order",
    },
  ],
};
const CHID_THEORIES = {
  plaintiff: ILE_GOV_THEORIES,
  defendant: [
    {
      title: "Reliance on Professionals - No Intent to Defraud",
      text: [
        "Every charged transmission ran through lawyers and auditors. iLearning's structure - channel partners collecting end-customer revenue - was disclosed, audited, and underwritten by sophisticated lenders who approved the very transfers now charged as wires.",
        "Strongest: Violations ONE-FOUR are counsel-to-auditor and counsel-to-lender emails; Lender B texted approval of the Oct 11 2024 transfer.",
        "The government answers with ¶42: the professionals were fed forged confirmations at the defendants' direction.",
        "The 'there will be no liabilities' statement (¶30) and the sham E3 website (¶29) are direct-evidence problems this theory cannot absorb.",
        "Weakest: 75% ownership plus unfettered account control makes ignorance implausible; this is a mitigation frame more than an acquittal frame.",
      ],
      ground: "S-1 ¶¶30-32, 35-36, 42; Counts 5s-10s",
    },
    {
      title: "§225 Overreach - a Dormant Statute Stretched",
      text: [
        "Section 225 was written for continuing financial crimes enterprises victimizing financial institutions - not for a startup founder whose lenders were repaid for years. The 10-year floor is the government's leverage play, and its elements (organize/manage/supervise, four persons in concert, $5M receipts from the enterprise) are all contestable.",
        "Strongest: rarity of the charge means no settled Second Circuit gloss; instruction and Rule 29 fights are genuinely open.",
        "Gross-receipts tracing: were receipts 'from' the enterprise, or round-tripped principal with no net receipts at all?",
        "Government answer: $100M of lender money and SPAC proceeds are receipts however the circle spun.",
        "Weakest: even full §225 victory leaves 30-year wire counts affecting financial institutions.",
      ],
      ground: "18 U.S.C. §225; S-1 ¶58",
    },
  ],
};
const ILE_SYS_NAQVI =
  "You are a litigation AI for United States v. Chidambaran et al., 1:26-cr-00097-NGG (E.D.N.Y., Judge Garaufis), focused on defendant SAYYED FARHAN ALI NAQVI (NGG-2), former iLearningEngines CFO. Posture: released 5/14/26 on $3,000,022 secured bond, home detention in Fulshear TX, GPS, passport surrendered, travel limited to NYC/LI/Mass/SD Tex within 100 miles. Superseding Indictment S-1 (Dkt.57, 7/15/26): Counts 1s (CFCE, 18 USC 225, 10-yr mandatory minimum), 2s-3s (securities fraud), 4s-10s (wire fraud affecting financial institutions); Count 11s (obstruction) charges co-defendant Chidambaran ONLY. Key defense facts: gov stated 4/22/26 no defendant is more culpable than Chidambaran; special-committee forensic expert found Naqvi deletions appear to be spam; Naqvi produced the play-ball email himself (Dkt.17 Ex.1); D. Md. civil case dismissed with prejudice; Tom Olivier bought the assets and kept working with Naqvi. Pending: Dkt.60 renewed travel application for Bay Area interviews weeks of Aug 17 or Aug 24 (companies include Patent VC; no fundraising or investor-facing roles; PTS defers; gov no position). Next: status conference 9/14/26 11:00 AM Ctrm 10B South; proposed 4-week trial Jan/Feb 2027; STA excluded through 9/14. IMPORTANT CONTEXT: this workspace belongs to Patent VC (a prospective contract employer listed in Dkt.60), which is monitoring the docket - it is NOT counsel of record; route any case contact through Milbank (Nola Heller).";
const ILE_SYS_CHID =
  "You are a litigation AI for United States v. Chidambaran et al., 1:26-cr-00097-NGG (E.D.N.Y., Judge Garaufis), focused on defendant PUTHUGRAMAM (HARISH) CHIDAMBARAN (NGG-1), iLearningEngines founder/CEO. Posture: detained 4/22/26, then released on a consented $15M partially secured appearance bond (Dkt.35), home confinement in Potomac MD, GPS. S-1 (Dkt.57): Counts 1s-10s plus Count 11s (conspiracy to obstruct justice, May-Dec 2025, charged against him alone) for directing CC-2 to delete emails, texts, WhatsApp messages and financial records after Employee 1 was subpoenaed. Gov position on record: no more culpable defendant in this case. Alleged gains: ~50.37M shares worth over $500M at listing, $12.5M RSUs, $700K+ comp, ~75% ownership with his wife, unfettered control of accounts. Next: status conference 9/14/26; proposed 4-week trial Jan/Feb 2027. This workspace is a monitoring file, not counsel of record.";
const ILE_JI = {
  judge: {
    name: "Nicholas G. Garaufis",
    court: "E.D.N.Y. · Brooklyn · Courtroom 10B South",
    appointed: 2000,
    role: "Presiding · 26-cr-97 · Senior USDJ",
    bio: "Senior United States District Judge appointed by President Clinton in 2000; senior status 2011. Former FAA Chief Counsel. Presided over the NXIVM/Raniere trial and the FDNY Vulcan Society litigation. In this matter he granted the government's bail appeals and detained both defendants on 4/22/26, later accepted negotiated packages ($3M Naqvi, $15M consented Chidambaran), and denied Naqvi's short-notice July travel application. Runs a firm calendar: proposed a four-week Jan/Feb 2027 trial at the first S-1 conference.",
  },
  rulingPatterns: [
    {
      issue: "Bail and release conditions",
      record:
        "Granted gov detention appeals 4/22/26 · accepted negotiated packages 5/14-15 · denied 7/22 travel app",
      analysis:
        "Responds to structure and consent, not rhetoric. The $3M package moved only after three homes and confessions of judgment were on the table. Short-notice applications fail; fully papered, PTS-vetted requests with itineraries have a path.",
      cites: ["Dkt.12, 22, 23", "Dkt.42/43, 35", "Dkt.59, 60"],
      trend: "structure-responsive",
    },
    {
      issue: "Scheduling discipline",
      record:
        "Set 9/14 conference with briefing schedule + trial dates due · proposed 4-week Jan/Feb 2027 window",
      analysis:
        "Expects the parties to arrive at conferences with an agreed schedule. STA exclusions granted on consent but tracked.",
      cites: ["7/16/26 Minute Entry"],
      trend: "calendar-firm",
    },
    {
      issue: "Administrative practicality",
      record: "Unsealed bond for county recording within 11 days of Dkt.56",
      analysis:
        "Solves mechanical problems fast when relief is narrow and unopposed. Frame ministerial requests as limited-purpose.",
      cites: ["Dkt.44, 56; 6/22/26 Order"],
      trend: "pragmatic",
    },
  ],
  oralArgument: [
    {
      date: "Apr 22, 2026",
      hearing: "Chidambaran bail appeal (de novo)",
      duration: 60,
      ref: "Dkt.22; Hr'g Tr.",
      keyMoments: [
        {
          minute: "Tr. 8:16-22",
          topic: "Relative culpability",
          quote:
            "There is no more culpable defendant in this case than this particular defendant.",
          signal:
            "Government's own differentiation between the CEO and the CFO is now record - the anchor quote for Naqvi's role defense.",
        },
      ],
      summary:
        "Detention ordered for Chidambaran; the court signaled openness to structured packages, which both defendants later negotiated.",
    },
    {
      date: "Apr 28, 2026",
      hearing: "Naqvi bail appeal (de novo)",
      duration: 60,
      ref: "Dkt.17, 23",
      keyMoments: [
        {
          minute: "-",
          topic: "Flight risk vs conditions",
          quote:
            "Defense: home detention, GPS, three homes posted, family in Texas; Gov: India network and resources.",
          signal:
            "Detention entered 4/30 but the court accepted the $3,000,022 package two weeks later once collateral was documented.",
        },
      ],
      summary:
        "The path from detention to release ran through paper, not argument.",
    },
  ],
  juryCharge: {
    modelApproach:
      "Second Circuit practice; Sand model instructions as the base",
    timeline:
      "Charge conference expected at close of evidence in the Jan/Feb 2027 window",
    specialInterrogatories:
      "Likely on the §225 predicate violations (which of Violations ONE-SIX are found), the $5M gross-receipts element, and per-count unanimity on wires 5s-10s.",
    historicalDisputes:
      "Expect fights over conscious-avoidance language, the wording of any good-faith reliance charge, and Bruton limiting instructions on co-defendant statements at a joint trial.",
    likelyChargeIssues: [
      "Definition of organize/manage/supervise under §225",
      "Materiality (Neder) and property object (Ciminelli) for the wire counts",
      "Forfeiture-phase allocation per Honeycutt",
      "Spillover instruction if Count 11s is tried jointly",
    ],
  },
  localRules: [
    {
      rule: "Individual practices - letter motions",
      detail:
        "Applications by letter; short notice disfavored. The 7/22 travel request was denied on timing; the 8/11 renewal apologized and asked for leave first.",
      ref: "Dkt.59, 60",
    },
    {
      rule: "Bond administration",
      detail:
        "Sealed bonds require a court order for certified raised-seal copies; county clerks will not record photocopies of confessions of judgment.",
      ref: "Dkt.42, 56; 6/22/26 Order",
    },
    {
      rule: "Speedy Trial Act tracking",
      detail:
        "Exclusions on consent through 9/14/26; renew on the record at each conference.",
      ref: "7/16/26 Minute Entry",
    },
  ],
};
const ILE_CONTRA = {
  scanStatus: {
    lastScan: "20 hours ago (PACER sync)",
    docketsWatched: 2,
    entriesScanned: 71,
    detectedToday: 2,
    running: true,
  },
  liveAlerts: [
    {
      id: "ile-1",
      time: "Apr 22 hearing",
      severity: "high",
      source: "Hr'g Tr. 8:16-22 vs Dkt.16",
      sourceDesc:
        "Government detention memo calls Naqvi similarly situated; at the podium the government called Chidambaran the most culpable defendant in the case.",
      contradictionType: "position",
      summary:
        "The government cannot argue parity at trial after differentiating on the record at bail.",
      recordA: {
        ref: "Dkt.16 (Gov Memo)",
        text: "Naqvi presented as equivalent flight and economic-danger risk to the CEO.",
      },
      recordB: {
        ref: "Apr 22 Hr'g Tr. 8:16-22",
        text: "There is no more culpable defendant in this case than this particular defendant (Chidambaran).",
      },
      action:
        "Lock the transcript cite into every role-differentiation brief and the opening.",
      impact: "high",
    },
    {
      id: "ile-2",
      time: "Charging vs theming",
      severity: "high",
      source: "S-1 Count 11s vs Dkt.16",
      sourceDesc:
        "Detention-stage obstruction theming against Naqvi vs the grand jury charging obstruction against Chidambaran only.",
      contradictionType: "position",
      summary:
        "The government's own investigation resolved the obstruction question in Naqvi's favor: the special-committee forensic expert found his deletions appear to be spam, and 11s excludes him.",
      recordA: {
        ref: "Dkt.16",
        text: "Deletion and encrypted-messaging conduct attributed to both defendants.",
      },
      recordB: {
        ref: "S-1 ¶¶45, 55-56; Count 11s",
        text: "Every deletion directive runs Chidambaran to CC-2; Count 11s names Chidambaran alone.",
      },
      action:
        "Motion in limine to exclude Naqvi-deletion theming; 404(b) objection ready.",
      impact: "high",
    },
    {
      id: "ile-3",
      time: "Dkt.17 Ex.1",
      severity: "medium",
      source: "Gov quotation vs full chain",
      sourceDesc:
        "The play-ball fragment quoted by the government vs the full email chain Naqvi himself produced.",
      contradictionType: "factual",
      summary:
        "Read whole, the chain relays investor preconditions rather than a directive to restart fraud.",
      recordA: {
        ref: "Gov detention presentation",
        text: "Fragment framed as intent to resume the scheme.",
      },
      recordB: {
        ref: "Dkt.17 Ex.1 (full chain)",
        text: "Context shows conditions a prospective investor demanded before funding.",
      },
      action:
        "Rule of completeness (FRE 106) designation prepared for any use of the fragment.",
      impact: "medium",
    },
    {
      id: "ile-4",
      time: "Sept-Oct 2024",
      severity: "medium",
      source: "Count Ten vs lulling theory",
      sourceDesc:
        "Lender B representatives texted approval of the $2.5M transfer the government charges as a concealment wire.",
      contradictionType: "timeline",
      summary:
        "A transfer the lender itself approved in writing is hard to cast as concealment from that lender.",
      recordA: {
        ref: "S-1 ¶¶46-48",
        text: "Transactions staged to mislead Lender B and C.",
      },
      recordB: {
        ref: "S-1 Count TEN (Oct 11, 2024 texts)",
        text: "Lender B reps approve release of approximately $2.5M from iLearning's account to Entity 1.",
      },
      action:
        "Build the approval chronology exhibit; cross the lender witness with its own texts.",
      impact: "medium",
    },
  ],
  crossCase: [
    {
      id: "ile-x1",
      severity: "medium",
      summary:
        "D. Md. securities class action dismissed with prejudice on substantially overlapping allegations.",
      caseA: {
        ref: "USA v. Chidambaran, 26-cr-97 (EDNY)",
        text: "Grand jury charged fraud based on the same core conduct at iLearningEngines.",
      },
      caseB: {
        ref: "In re iLearningEngines Sec. Litig. (D. Md.) - dismissed",
        text: "Civil court dismissed the pleaded fraud theory on substantially overlapping allegations.",
      },
      analysis:
        "Different standards, but the civil court's rejection of the pleaded fraud theory is cross material on investigative genesis and a 403 fight worth preserving.",
      action: "Obtain the dismissal opinion; brief admissibility early.",
      docketsConflicting: ["Dkt.17 at 5-7", "D. Md. dismissal order"],
    },
  ],
  timeline: [
    {
      id: "ile-t1",
      severity: "low",
      summary:
        "Gov: fraud continued post-exposure to lull victims. Defense: post-report transfers were lender-visible paydown mechanics.",
      claimedSequence: [
        { date: "8/29/24", event: "Report published" },
        {
          date: "After 8/29/24",
          event: "Covert round trips to deceive lenders (per government)",
        },
      ],
      actualSequence: [
        { date: "8/29/24", event: "Report published" },
        { date: "9/5", event: "Lender meetings" },
        {
          date: "10/11",
          event: "Lender-approved transfer (Count Ten texts)",
        },
      ],
      analysis:
        "The concealment narrative and the lender-approval texts sit awkwardly together; chronology is defense-friendly.",
      action:
        "Demonstrative: side-by-side of ¶¶46-48 vs Count Ten description.",
      cite: "S-1 ¶¶44-48; Counts SIX/TEN",
    },
  ],
};
const ILE_JURY = {
  caseTitle: "USA v. Chidambaran & Naqvi - E.D.N.Y. (Brooklyn)",
  trialDate: "Jan/Feb 2027 (4 weeks, proposed)",
  jurySeated: false,
  panelSize: 0,
  theory:
    "Government: founder and CFO ran a company on 90% fake revenue, took it public, and kept the wheel spinning after exposure. Defense (Naqvi): the CEO owned it, ran it, and deleted it; the CFO relied on auditors, counsel and the SPAC diligence the buyers themselves ran.",
  voirDire: [
    {
      id: "v1",
      category: "Investment Losses & SPAC Exposure",
      weight: "critical",
      questions: [
        {
          q: "Have you or anyone close to you lost money in a stock that dropped suddenly, including SPACs or IPOs?",
          rationale:
            "EDNY venire (Kings/Queens/Richmond/Nassau/Suffolk) is retail-investor heavy; AILE wiped out retail holders.",
          plaintiffSignal:
            "Personal SPAC loss with lingering anger - government seat.",
          defendantSignal:
            "Views losses as market risk investors accept - defense seat.",
        },
        {
          q: "What do you think generally about companies that go public through SPAC mergers?",
          rationale: "Separates fraud-priors from process-priors.",
          plaintiffSignal: "SPACs are scams - government lean.",
          defendantSignal:
            "SPACs are a legitimate path with heavy diligence - defense lean.",
        },
      ],
    },
    {
      id: "v2",
      category: "AI Hype & Startup Culture",
      weight: "high",
      questions: [
        {
          q: "How much of what you hear about AI companies do you believe?",
          rationale:
            "iLearning marketed itself as an AI platform; post-2024 AI-hype skepticism cuts against any executive defendant.",
          plaintiffSignal: "It is mostly hype and lies - government lean.",
          defendantSignal:
            "Real technology, messy business models - defense lean.",
        },
        {
          q: "Have you worked at a startup or fast-growing company where the numbers were aggressive?",
          rationale: "Operators understand the CEO/CFO information gap.",
          plaintiffSignal:
            "Saw books cooked and everyone knew - government lean.",
          defendantSignal:
            "CFOs rely on what units report up - defense lean, especially for Naqvi.",
        },
      ],
    },
    {
      id: "v3",
      category: "Views of CEOs vs CFOs",
      weight: "high",
      questions: [
        {
          q: "When a company commits fraud, who do you assume knew: the founder-CEO, the CFO, both, everyone?",
          rationale: "The core Naqvi question in one line.",
          plaintiffSignal:
            "The CFO always knows the numbers - strike for Naqvi.",
          defendantSignal:
            "The founder who controls the money is responsible - keep for Naqvi.",
        },
      ],
    },
  ],
  archetypes: [
    {
      id: "a1",
      label: "Burned Retail Investor",
      icon: "📉",
      lean: "plaintiff",
      leanColor: "#ef4444",
      plaintiffStrike:
        "Keep - accountability-minded juror who feels the fraud personally.",
      defendantStrike:
        "Strike - pre-disposed to blame executives for market losses.",
      description:
        "Lost money in meme stocks or SPACs; wants accountability; treats the bell-ringing photo as proof of arrogance.",
      identifyVoirDire: ["v1"],
      plaintiffResonate: "90% fake, real families lost savings.",
      plaintiffAlienate: "Overreliance on statutory complexity.",
      defendantResonate: "Short-seller made millions driving the crash.",
      defendantAlienate:
        "Any hint that investors should have read the fine print.",
    },
    {
      id: "a2",
      label: "Startup Operator",
      icon: "🧪",
      lean: "defense",
      leanColor: "#3b82f6",
      plaintiffStrike:
        "Strike - sympathetic to founder-driven cap tables and aggressive accounting.",
      defendantStrike:
        "Keep - understands legitimate channel-partner revenue structures.",
      description:
        "Has lived channel-partner revenue and founder-dominated cap tables; understands a 75% founder controlling the wires.",
      identifyVoirDire: ["v2", "v3"],
      plaintiffResonate:
        "Backdated contracts are not aggressive accounting, they are forgery.",
      plaintiffAlienate: "Treating all startup optimism as fraud.",
      defendantResonate:
        "The diligence buyer bought the assets and rehired the CFO.",
      defendantAlienate: "Pretending the CFO never saw the numbers.",
    },
    {
      id: "a3",
      label: "Compliance Professional",
      icon: "🧾",
      lean: "mixed",
      leanColor: "#6b7280",
      plaintiffStrike:
        "Keep - document-driven juror who follows the wires wherever they lead.",
      defendantStrike:
        "Keep - process-focused juror unlikely to assume intent without a paper trail.",
      description:
        "Bank, insurance or healthcare compliance background; follows document trails; decides on the wires and the approvals.",
      identifyVoirDire: ["v1", "v3"],
      plaintiffResonate: "Forged confirmations to auditors (¶42).",
      plaintiffAlienate: "Rhetoric untethered from exhibits.",
      defendantResonate: "Lender B approved the charged transfer in writing.",
      defendantAlienate: "Attacking the auditors as dupes without documents.",
    },
  ],
  juryGrid: [],
  closingCalibration: null,
};
const ILE_EXPERTS = {
  caseTitle: "USA v. Chidambaran & Naqvi - Expert Battlefield",
  status: "pre-disclosure",
  statusLabel: "PRE-DISCLOSURE",
  countdown: {
    event: "Status conference - motion and expert schedule set",
    date: "September 14, 2026",
    days: 21,
  },
  gapNote:
    "No expert schedule exists yet; it will be set at the 9/14 conference. Defense forensic-accounting rebuttal work should start now - the government's bank-record tracing is already three years deep via the grand jury.",
  issues: [
    {
      id: "tracing",
      label: "Round-Trip Tracing / Flow of Funds",
      importance: "critical",
      importanceColor: "#ef4444",
      plaintiffExpert: {
        name: "FBI Forensic Accountant (TBD)",
        credentials: "FBI FA unit · grand jury flow-of-funds charts",
        coreOpinion:
          "$145M circulated iLearning to E1 accounts (CC-2) and back, directly or through E2/E4; receipts exceed $5M in every rolling 24-month window.",
        daubertStatus: "not-filed",
        daubertColor: "#6b7280",
        daubertRuling: "None - pre-disclosure",
        vulnerabilityScore: 4,
        expanded: {
          admitted: [],
          excluded: [],
          ruling: "No rulings yet",
          crossAngles: [
            "Netting: circular principal is not receipts",
            "Which transfers had lender visibility and approval",
            "Attribution of account control to CC-2, not Naqvi",
          ],
          redirectPrep: [
            "Circularity is the crime, not a defense to it",
            "Approval obtained by deception is not consent",
          ],
        },
      },
      defendantExpert: {
        name: "Defense forensic accountant (retain by Oct 2026)",
        credentials: "Big-4 restructuring / revenue recognition",
        coreOpinion:
          "Net-flow analysis; ASC 606 principal-vs-agent treatment of channel partners; lender-approved paydown mechanics for the post-report transfers.",
        daubertStatus: "not-filed",
        daubertColor: "#6b7280",
        daubertRuling: "None",
        vulnerabilityScore: 5,
        expanded: {
          admitted: [],
          excluded: [],
          ruling: "None",
          crossAngles: [
            "Paid-advocate framing",
            "ASC 606 cannot launder fabricated end customers",
          ],
          redirectPrep: [
            "The auditors applied 606 with the same documents",
            "Gross-receipts element requires receipts, not motion of money",
          ],
        },
      },
      aiAssessment:
        "The government wins the volume battle; the defense wins only by narrowing - approvals, netting, and Honeycutt-limited attribution. Retain before the 9/14 schedule locks disclosure dates.",
      juryInstructionBattle:
        "Gross receipts definition under §225 and loss calculation methodology at any sentencing.",
      battlegroundScore: {
        plaintiffStrength: 8,
        defendantStrength: 4,
        leverage: "plaintiff-strong",
      },
    },
    {
      id: "forensics",
      label: "Digital Forensics / Deletions",
      importance: "high",
      importanceColor: "#f59e0b",
      plaintiffExpert: {
        name: "FBI CART examiner (TBD)",
        credentials: "Device and cloud artifact recovery",
        coreOpinion:
          "Deletions of email, text, WhatsApp and financial records by CC-2 at Chidambaran's direction, Sept 2024 and post-subpoena 2025.",
        daubertStatus: "not-filed",
        daubertColor: "#6b7280",
        daubertRuling: "None",
        vulnerabilityScore: 3,
        expanded: {
          admitted: [],
          excluded: [],
          ruling: "None",
          crossAngles: [
            "All directives run to CC-2, none to Naqvi",
            "Recovery completeness - what was actually lost",
          ],
          redirectPrep: ["Intent shown by timing against the subpoena"],
        },
      },
      defendantExpert: {
        name: "Special-committee forensic expert (already favorable)",
        credentials: "Retained by the company's own special committee",
        coreOpinion: "Items deleted from Naqvi's account appear to be spam.",
        daubertStatus: "survived",
        daubertColor: "#10b981",
        daubertRuling:
          "Adverse-party retention; finding predates charging and matches the 11s exclusion",
        vulnerabilityScore: 2,
        expanded: {
          admitted: ["Committee report finding (Dkt.17 at 8-9)"],
          excluded: [],
          ruling: "Grand jury declined to charge Naqvi in 11s",
          crossAngles: ["Scope of the review", "Independence of the committee"],
          redirectPrep: [
            "The government's own charging decision corroborates the finding",
          ],
        },
      },
      aiAssessment:
        "For Naqvi this issue is close to won pretrial; the fight is keeping Chidambaran's deletion evidence from spilling over - severance or a tight limiting instruction.",
      juryInstructionBattle:
        "Spillover and Bruton limiting instructions if tried jointly.",
      battlegroundScore: {
        plaintiffStrength: 3,
        defendantStrength: 8,
        leverage: "defendant-strong",
      },
    },
  ],
};
const ILE_CC = {
  matterName: "USA v. Chidambaran & Naqvi · 1:26-cr-00097-NGG (E.D.N.Y.)",
  clientNames: {
    plaintiff: "United States (AUSAs Ammari, Dugan, Rehnquist)",
    defendant: "Naqvi (Milbank / Heller) · Chidambaran (Quinn Emanuel / Nitze)",
  },
  statusReports: {
    plaintiff: {
      frequency: "per-event",
      latest: {
        date: "Aug 24, 2026",
        reportingPeriod: "Jul 15 - Aug 24, 2026",
        whatHappened:
          "S-1 filed 7/15 adding Count 11s against Chidambaran only. Both defendants arraigned 7/16 and pled not guilty. Naqvi's first travel application was denied on short notice; the 8/11 renewed application (Dkt.60) is pending with the government taking no position.",
        whatItMeans:
          "The government's leverage is the §225 ten-year floor plus 11s against the CEO. Expect cooperation pressure on CC-2 and CC-3 before the 9/14 schedule locks.",
        nextMilestone:
          "Status conference Sept 14, 2026, 11:00 AM, Courtroom 10B South",
        decisionsNeeded:
          "None for monitor - track any plea-track signals in the 9/14 minute entry.",
      },
      history: [
        {
          date: "Jul 16, 2026",
          summary:
            "S-1 arraignment; 4-week Jan/Feb 2027 trial proposed; STA excluded through 9/14.",
        },
        {
          date: "May 14-15, 2026",
          summary:
            "Both defendants released on negotiated packages ($3M / $15M consented).",
        },
      ],
    },
    defendant: {
      frequency: "per-event",
      latest: {
        date: "Aug 24, 2026",
        reportingPeriod: "Jul 15 - Aug 24, 2026",
        whatHappened:
          "Dkt.60 renewed application seeks leave for Bay Area interviews the weeks of Aug 17 or Aug 24 with five companies including Patent VC. PTS confirms full compliance and defers; government takes no position. Today falls inside window two.",
        whatItMeans:
          "Patent VC is named in a public court filing as a prospective contract employer. The role as represented to the Court is remote, sales- and marketing-focused, contract-based, with no fundraising and no investor interaction - any offer must match that scope exactly. Naqvi is a represented defendant: all case-adjacent contact routes through Milbank.",
        nextMilestone:
          "Ruling on Dkt.60; if granted, Milbank submits itinerary letter and PTS approves each meeting",
        decisionsNeeded:
          "1) If granted: host in Mountain View or run video fallback. 2) Written role description excluding fundraising/investor contact. 3) If denied: video interview, or a meeting inside the SD Tex 100-mile radius with PTS pre-approval.",
      },
      history: [
        {
          date: "Jul 22, 2026",
          summary: "First travel application denied (short notice).",
        },
        {
          date: "Jun 22, 2026",
          summary:
            "Bond unsealed for certified raised-seal copies; county recording of confessions of judgment completed by Baeurle.",
        },
      ],
    },
  },
  budget: {
    plaintiff: {
      phases: [
        {
          name: "Docket monitoring (PACER)",
          budget: 2500,
          hoursActual: 9,
          hoursProjected: 15,
          status: "active",
          costRate: 0,
        },
      ],
      total: 2500,
      cbf: {
        percent: 0,
        pool: 0,
        note: "N/A - criminal matter, monitoring only",
      },
    },
    defendant: {
      phases: [
        {
          name: "Engagement diligence (Patent VC)",
          budget: 7500,
          hoursActual: 14,
          hoursProjected: 25,
          status: "active",
          costRate: 450,
        },
        {
          name: "Role scoping + compliance memo",
          budget: 5000,
          hoursActual: 4,
          hoursProjected: 12,
          status: "active",
          costRate: 450,
        },
      ],
      total: 12500,
      cbf: { percent: 0, pool: 0, note: "Internal Patent VC cost tracking" },
    },
  },
  decisionLog: {
    plaintiff: [
      {
        date: "Aug 12, 2026",
        decision: "Continue passive monitoring; no outreach to USAO",
        reasoning: "Patent VC is not a party; no basis for contact",
        authorizedBy: "Raj",
        status: "active",
      },
    ],
    defendant: [
      {
        date: "Aug 12, 2026",
        decision:
          "Hold offer until Dkt.60 ruling; prep both in-person and video tracks",
        reasoning:
          "Court leave gates in-person; role must match Dkt.60 representations",
        authorizedBy: "Raj",
        status: "active",
      },
      {
        date: "Aug 12, 2026",
        decision:
          "Role scope: contract, sales/marketing only, no fundraising, no LP or investor contact, no securities work",
        reasoning:
          "Mirrors Dkt.60 at 2; protects both sides on conditions compliance",
        authorizedBy: "Raj",
        status: "locked",
      },
    ],
  },
};
const ILE_SM = {
  caseTitle: "USA v. Chidambaran & Naqvi - Strategic Map",
  layer1: {
    title: "Evidence Intensity by Count Group",
    description: "How hard each evidence category hits each count group.",
    rows: [
      { id: "cfce", label: "CFCE §225 (1s)" },
      { id: "sec", label: "Securities Fraud (2s-3s)" },
      { id: "wire", label: "Wire Fraud (4s-10s)" },
      { id: "obs", label: "Obstruction 11s (Chid only)" },
    ],
    cols: [
      { id: "bank", label: "Bank Records" },
      { id: "comms", label: "Emails/Texts" },
      { id: "coop", label: "Cooperators" },
      { id: "filings", label: "Public Filings" },
      { id: "forensics", label: "Forensics" },
    ],
    grid: [
      [
        {
          s: 9,
          l: "Devastating",
          d: "$145M circular transfers (¶27); six charged wires; payments stop at resignation (¶49).",
        },
        {
          s: 7,
          l: "Strong",
          d: "Dec 17 2023 fake-revenue email (¶34); Entity 1 lie agreed at ¶40.",
        },
        {
          s: 8,
          l: "Strong",
          d: "CC-2 controlled the accounts; CC-3 paid $95K to send money back.",
        },
        {
          s: 6,
          l: "Mixed",
          d: "8-K supports scheme scope but not the enterprise element directly.",
        },
        {
          s: 4,
          l: "Mixed",
          d: "Tracing charts; defense netting attack lives here.",
        },
      ],
      [
        {
          s: 7,
          l: "Strong",
          d: "Round trips prove revenue was fake at filing time.",
        },
        {
          s: 8,
          l: "Strong",
          d: "Red-flag message ('no trace of Entity 2') then curated entity info anyway (¶28).",
        },
        {
          s: 6,
          l: "Mixed",
          d: "Cooperators speak to fabrication, less to scienter on the filing itself.",
        },
        {
          s: 9,
          l: "Devastating",
          d: "4/22/24 8-K: $421M, 1,000+ customers, 4.4M users; >=90% fabricated (¶38).",
        },
        { s: 3, l: "Weak", d: "Limited forensic angle on the filing." },
      ],
      [
        {
          s: 8,
          l: "Strong",
          d: "Each charged wire maps to a specific EDNY transmission.",
        },
        {
          s: 7,
          l: "Strong",
          d: "Counsel-to-auditor and counsel-to-lender emails are the wires themselves (Violations ONE-FOUR).",
        },
        {
          s: 7,
          l: "Strong",
          d: "CC-3 false confirmation to Lender B (Count NINE).",
        },
        {
          s: 5,
          l: "Mixed",
          d: "Loan agreements establish the financial-institution element.",
        },
        { s: 5, l: "Mixed", d: "Metadata authenticating transmissions." },
      ],
      [
        { s: 3, l: "Weak", d: "No money movement needed for 11s." },
        {
          s: 7,
          l: "Strong",
          d: "Directives Chidambaran to CC-2, Sept 2024 and post-subpoena (¶¶45, 56).",
        },
        {
          s: 9,
          l: "Devastating",
          d: "CC-2 performed the deletions - if he cooperates, 11s is over for Chidambaran.",
        },
        { s: 2, l: "N/A", d: "-" },
        {
          s: 8,
          l: "Strong",
          d: "Deletion artifacts vs Naqvi spam finding - the contrast IS the defense for NGG-2.",
        },
      ],
    ],
  },
  layer2: {
    title: "Defense Leverage by Source",
    description: "Where each Naqvi defense theme draws its strength.",
    rows: [
      { id: "role", label: "Differential culpability" },
      { id: "faith", label: "Good-faith process" },
      { id: "spam", label: "Spam finding / no 11s" },
    ],
    cols: [
      { id: "bail", label: "Bail record" },
      { id: "s1", label: "S-1 text" },
      { id: "third", label: "Third parties" },
      { id: "civil", label: "Civil rulings" },
    ],
    grid: [
      [
        {
          s: 9,
          l: "Devastating",
          d: "Gov: no more culpable defendant (Tr. 8:16-22).",
        },
        {
          s: 8,
          l: "Strong",
          d: "75% ownership, unfettered control, every fabrication directive = Chidambaran (¶¶29-30, 53).",
        },
        { s: 5, l: "Mixed", d: "Cooperators may still put Naqvi in rooms." },
        { s: 3, l: "Weak", d: "-" },
      ],
      [
        {
          s: 4,
          l: "Mixed",
          d: "Package acceptance shows court trusts structure, not merits.",
        },
        {
          s: 6,
          l: "Mixed",
          d: "Charged wires are counsel/auditor transmissions.",
        },
        {
          s: 9,
          l: "Devastating",
          d: "Olivier: 18 months diligence, bought the assets, kept working with Naqvi.",
        },
        {
          s: 6,
          l: "Mixed",
          d: "D. Md. dismissal with prejudice - genesis attack.",
        },
      ],
      [
        { s: 6, l: "Mixed", d: "Detention theming abandoned by charging." },
        {
          s: 9,
          l: "Devastating",
          d: "Count 11s excludes Naqvi; all directives run to CC-2.",
        },
        {
          s: 8,
          l: "Strong",
          d: "Company's own committee expert: appear to be spam.",
        },
        { s: 4, l: "Mixed", d: "-" },
      ],
    ],
  },
  layer3: {
    title: "Pressure Points by Phase",
    description: "Where leverage shifts between now and trial.",
    rows: [
      { id: "gov", label: "Government" },
      { id: "naq", label: "Naqvi" },
      { id: "chid", label: "Chidambaran" },
    ],
    cols: [
      { id: "now", label: "Now - 9/14" },
      { id: "mot", label: "Motions (Q4 26)" },
      { id: "trial", label: "Trial (Q1 27)" },
    ],
    grid: [
      [
        {
          s: 7,
          l: "Strong",
          d: "Cooperation window for CC-2/CC-3 before schedule locks.",
        },
        {
          s: 6,
          l: "Mixed",
          d: "3500/Jencks production burdens; §225 instruction risk.",
        },
        {
          s: 8,
          l: "Strong",
          d: "Bank records + bell photo + 8-K in a 4-week narrative.",
        },
      ],
      [
        {
          s: 6,
          l: "Mixed",
          d: "Dkt.60 ruling; employment stability = mitigation record.",
        },
        {
          s: 8,
          l: "Strong",
          d: "Severance/Bruton motion re 11s spillover; MIL on deletion theming.",
        },
        {
          s: 5,
          l: "Mixed",
          d: "Role defense lives or dies on testify decision.",
        },
      ],
      [
        {
          s: 3,
          l: "Weak",
          d: "11s + gov most-culpable framing = maximal pressure.",
        },
        {
          s: 4,
          l: "Mixed",
          d: "§225 construction fight is his best pretrial play.",
        },
        {
          s: 2,
          l: "Critical Gap",
          d: "Direct-evidence quotes (¶30) with no answer.",
        },
      ],
    ],
  },
  layer4: {
    title: "Docket Tempo",
    description: "Filing density by period.",
    periods: [
      {
        label: "Apr 2026 · Charging + bail war",
        density: 9,
        note: "Indictment, arrests, dueling detention orders in 15 days.",
      },
      {
        label: "May 2026 · Packages + substitution",
        density: 7,
        note: "$3M and $15M releases; Milbank in.",
      },
      {
        label: "Jun 2026 · Bond administration",
        density: 3,
        note: "Certified-copy order; county recording.",
      },
      {
        label: "Jul 2026 · S-1 + schedule",
        density: 6,
        note: "Count 11s added; 9/14 conference set.",
      },
      {
        label: "Aug 2026 · Travel applications",
        density: 4,
        note: "Denied, then renewed; Dkt.60 pending.",
      },
    ],
  },
  gaps: {
    closable: [
      {
        gap: "No defense forensic accountant retained",
        why: "Gov tracing is 3 years ahead via grand jury",
        required: "Retain restructuring/606 expert",
        leadtime: "4-6 weeks",
        deadline: "Before 9/14 schedule locks",
        severity: "high",
      },
      {
        gap: "Olivier and Gerety not interviewed",
        why: "Best third-party validators for Naqvi",
        required: "Defense interviews + declarations",
        leadtime: "2-3 weeks",
        deadline: "Sep 14, 2026",
        severity: "high",
      },
    ],
    unclosable: [
      {
        gap: "¶40 'at the urging of NAQVI' allegation",
        why: "Grand jury finding; only trial can answer it",
        required: "Cross of source witness",
        leadtime: "-",
        deadline: "Trial",
        severity: "critical",
      },
    ],
    contested: [
      {
        gap: "Gross-receipts element under §225",
        why: "Circular principal vs receipts is unbriefed anywhere",
        required: "Instruction + Rule 29 briefing",
        leadtime: "Q4 2026",
        deadline: "Charge conference",
        severity: "high",
      },
    ],
    strategic: [
      {
        gap: "Severance of Count 11s spillover",
        why: "Zafiro standard favors joint trials",
        required: "Rule 14 motion with Bruton overlay",
        leadtime: "File per 9/14 schedule",
        deadline: "Motion deadline TBD",
        severity: "medium",
      },
    ],
  },
};
const ILE_AR = {
  caseTitle: "USA v. Chidambaran & Naqvi - Rulings & Appellate Posture",
  rulings: [
    {
      id: "ile-det",
      date: "Apr 22-30, 2026",
      title: "Orders of Detention (both defendants)",
      court: "Judge Garaufis · E.D.N.Y.",
      dockets: ["Dkt.22", "Dkt.23"],
      type: "Bail",
      adverseTo: "Defendant",
      favoredParty: "Government",
      summary:
        "De novo detention after government appeal of the N.D. Cal. release order; superseded by negotiated packages within three weeks.",
      standardOfReview:
        "18 U.S.C. §3145(c); mixed clear-error/de novo in the Second Circuit",
      preservation: "preserved",
      preservationDetail:
        "Full record made at 4/22 and 4/28 hearings including the government's most-culpable statement.",
      reversalLikelihood: 10,
      reversalAssessment:
        "Moot in practice after release; the transcript value survives.",
      appealability:
        "§3145(c) was available; unnecessary after packages accepted",
      circuitSplit: "low",
      circuit: "2d Cir.",
      relevantPrecedent:
        "United States v. Sabhnani, 493 F.3d 63 (2d Cir. 2007)",
      urgency: "low",
    },
    {
      id: "ile-rel",
      date: "May 14, 2026",
      title: "Naqvi Release - $3,000,022 Package",
      court: "MJ Kaminsky (bond) · Judge Gonzalez (hearing)",
      dockets: ["Dkt.33", "Dkt.42/43"],
      type: "Bail",
      adverseTo: "Government",
      favoredParty: "Defendant",
      summary:
        "Home detention in Fulshear TX, GPS, passport surrendered, three homes posted with confessions of judgment, $1,035,000 unsecured.",
      standardOfReview: "N/A - consent-adjacent",
      preservation: "n/a",
      preservationDetail:
        "Conditions govern all employment and travel logistics.",
      reversalLikelihood: 0,
      reversalAssessment: "Stable; compliance perfect per PTS (Dkt.60).",
      appealability: "-",
      circuitSplit: "low",
      circuit: "2d Cir.",
      relevantPrecedent: "-",
      urgency: "low",
    },
    {
      id: "ile-trav",
      date: "Jul 22, 2026",
      title: "Travel Application #1 - DENIED",
      court: "Judge Garaufis",
      dockets: ["Dkt.59"],
      type: "Conditions",
      adverseTo: "Defendant",
      favoredParty: "Government",
      summary:
        "Short-notice request for Bay Area interviews denied; renewed 8/11 with apology and a leave-first structure.",
      standardOfReview: "Abuse of discretion",
      preservation: "preserved",
      preservationDetail:
        "Renewal cites financial hardship, family support obligations, and employer awareness of the indictment.",
      reversalLikelihood: 5,
      reversalAssessment: "Not appeal material; process lesson only.",
      appealability: "-",
      circuitSplit: "low",
      circuit: "2d Cir.",
      relevantPrecedent: "-",
      urgency: "medium",
    },
    {
      id: "ile-s1",
      date: "Jul 15, 2026",
      title: "Superseding Indictment adds Count 11s",
      court: "Grand Jury · E.D.N.Y.",
      dockets: ["Dkt.57"],
      type: "Charging",
      adverseTo: "Defendant",
      favoredParty: "Government",
      summary:
        "Obstruction conspiracy (May-Dec 2025) against Chidambaran only; simultaneously the strongest structural fact for Naqvi's role defense.",
      standardOfReview: "-",
      preservation: "n/a",
      preservationDetail:
        "Grounds Rule 14 severance and Bruton motions for Naqvi.",
      reversalLikelihood: 0,
      reversalAssessment: "-",
      appealability: "-",
      circuitSplit: "low",
      circuit: "2d Cir.",
      relevantPrecedent: "Zafiro v. United States; Bruton v. United States",
      urgency: "high",
    },
    {
      id: "ile-dkt60",
      date: "Pending - filed Aug 11, 2026",
      title: "Renewed Travel Application (Dkt.60)",
      court: "Judge Garaufis",
      dockets: ["Dkt.60"],
      type: "Conditions",
      adverseTo: "pending",
      favoredParty: "open",
      summary:
        "Interview weeks of Aug 17 or Aug 24 with five companies including Patent VC; PTS defers; government takes no position. Today sits inside window two.",
      standardOfReview: "Discretionary",
      preservation: "n/a",
      preservationDetail:
        "If granted, a follow-up letter with specific itinerary is required before travel.",
      reversalLikelihood: 0,
      reversalAssessment:
        "Grant odds improved by no-position posture and perfect compliance record.",
      appealability: "-",
      circuitSplit: "low",
      circuit: "2d Cir.",
      relevantPrecedent: "-",
      urgency: "critical",
    },
  ],
  pendingAlerts: [
    {
      id: "ile-pa1",
      time: "Before joint trial",
      type: "Confrontation",
      ruling:
        "Chidambaran statements ('there will be no liabilities', ¶30) offered at a joint trial",
      urgency: "high",
      suggestedLanguage:
        "Naqvi objects under Bruton v. United States and moves for severance under Rule 14, or in the alternative for redaction and a limiting instruction.",
      actionRequired: "File with pretrial motions per the 9/14 schedule.",
    },
    {
      id: "ile-pa2",
      time: "Any conviction",
      type: "Forfeiture",
      ruling: "Forfeiture allocation across co-defendants",
      urgency: "medium",
      suggestedLanguage:
        "Under Honeycutt v. United States, forfeiture is limited to property the defendant personally obtained; Naqvi's exposure is capped at his own alleged proceeds.",
      actionRequired: "Preserve at any plea or verdict-phase proceeding.",
    },
  ],
};
const ILE_PT_SIDE = {
  activeMonitoring: [
    {
      id: "ile-am1",
      time: "Active",
      severity: "critical",
      observation:
        "Dkt.60 pending during the exact requested travel window (weeks of Aug 17/24).",
      analysis:
        "PTS defers, government takes no position - the cleanest posture a travel application can have before this judge.",
      warning:
        "Do not schedule or confirm any Bay Area meeting until the order issues AND the itinerary letter is approved. A premature calendar invite is a conditions problem.",
      ref: "Dkt.60",
    },
    {
      id: "ile-am2",
      time: "Active",
      severity: "medium",
      observation:
        "Government keeps detention-stage obstruction theming alive in rhetoric despite charging 11s against Chidambaran only.",
      analysis:
        "Expect 404(b) attempts to reintroduce Naqvi deletion themes at trial.",
      warning:
        "MIL early; cite the spam finding and the charging decision together.",
      ref: "S-1 Count 11s; Dkt.16/17",
    },
  ],
  waiverRisks: [
    {
      id: "ile-wr1",
      time: "At 9/14 conference",
      severity: "high",
      ruling: "Speedy Trial exclusions",
      situation:
        "STA excluded through 9/14 on consent; further exclusions will be proposed with the motion schedule.",
      requiredLanguage:
        "Consent to exclusion only in exchange for locked Jan/Feb 2027 trial dates and firm 3500 production deadlines.",
      consequence:
        "Open-ended exclusions surrender the calendar leverage created by the court's four-week offer.",
      authority: "18 U.S.C. §3161(h)(7)",
    },
    {
      id: "ile-wr2",
      time: "Any joint-trial stipulation",
      severity: "high",
      ruling: "Bruton / severance preservation",
      situation:
        "Co-defendant statements (¶30) will be offered through cooperators.",
      requiredLanguage:
        "Naqvi expressly preserves Rule 14 severance and Confrontation Clause objections to all co-defendant statements.",
      consequence:
        "Silence at the schedule conference reads as acquiescence to a joint trial without protections.",
      authority: "Zafiro; Bruton",
    },
  ],
  deadlineTraps: [
    {
      id: "ile-dt1",
      trigger: "Dkt.60 grant",
      time: "Immediate",
      severity: "critical",
      deadline: "Before any travel",
      source: "Dkt.60 at 3",
      aiAlert:
        "The letter promises a subsequent submission with travel details and specific interviews for approval - the grant alone does NOT authorize travel.",
      actionItem:
        "Milbank itinerary letter + PTS approval per meeting, then book.",
    },
    {
      id: "ile-dt2",
      trigger: "County recording",
      time: "Complete",
      severity: "low",
      deadline: "Done 6/22-7/26",
      source: "Dkt.56; 6/22 Order",
      aiAlert:
        "Raised-seal certified copies were required; photocopies rejected. Pattern: assume EDNY sealed-document mechanics need lead time.",
      actionItem:
        "Confirm all three counties show recorded confessions of judgment.",
    },
  ],
  counselPatterns: [
    {
      id: "ile-cp1",
      counselName: "AUSA Kamil Ammari",
      pattern:
        "Same-day stay applications on any adverse bail ruling (Dkt.10/12 within hours of Cousins order).",
      interpretation:
        "Fast, prepared, escalates procedurally rather than rhetorically.",
      ourMove:
        "Never rely on a favorable magistrate ruling until the district judge speaks; pre-draft the response to the inevitable stay.",
      judgeResponse:
        "Garaufis granted the stay same day - he rewards procedural readiness.",
    },
    {
      id: "ile-cp2",
      counselName: "Nola Heller (Milbank)",
      pattern:
        "Leave-first, apologize-for-timing letter practice after the 7/22 denial.",
      interpretation:
        "Recalibrated to this judge's process preferences quickly.",
      ourMove:
        "Mirror the cadence in anything Patent VC touches: written, early, scoped.",
      judgeResponse:
        "Renewed application drew no government opposition - posture repaired.",
    },
  ],
  standingOrders: [
    {
      id: "ile-so1",
      order: "Garaufis individual practices",
      requirement:
        "Letter-motion practice; adequate notice; proposed orders attached.",
      recentFiling: "Dkt.56 and Dkt.60 both attach proposed orders",
      conflict: "7/22 application failed the notice norm",
      recommendation: "Every request: letter + proposed order + lead time.",
    },
    {
      id: "ile-so2",
      order: "Release conditions (Dkt.42/43)",
      requirement:
        "Home detention; PTS pre-approval for departures; travel limited to NYC/LI/Mass/SD Tex within 100 miles absent order.",
      recentFiling: "Dkt.60 seeks a carve-out",
      conflict: "Bay Area is outside the permitted zone",
      recommendation:
        "In-person alternative that needs no order: a Houston-area meeting inside the SD Tex 100-mile radius with PTS pre-approval.",
    },
  ],
};
const ILE_PT = { plaintiff: ILE_PT_SIDE, defendant: ILE_PT_SIDE };
const ILE_TD_GOV = [
  {
    id: "cfce",
    name: "CFCE §225 - Round-Trip Enterprise",
    short: "§225",
    score: 74,
    dim: { evid: 88, caselaw: 55, experts: 70, mvuln: 60, damages: 85 },
    elements: [
      {
        name: "Organize/manage/supervise the enterprise",
        status: "amber",
        explainer:
          "Strong vs Chidambaran (75% owner, account control); contested vs Naqvi (subordinate CFO framing vs ¶40 and the Oct 2024 wires).",
        ev: [
          {
            id: "S1-53",
            label: "¶53 unfettered account control (Chid)",
            supportive: true,
          },
          {
            id: "S1-40",
            label: "¶40 Entity 1 lie 'at the urging of NAQVI'",
            supportive: true,
          },
          {
            id: "TR-822",
            label: "Gov: no more culpable defendant (Chid)",
            supportive: false,
          },
        ],
      },
      {
        name: "Four or more persons acting in concert",
        status: "green",
        explainer: "Two defendants plus CC-1 through CC-5.",
        ev: [
          {
            id: "S1-10-14",
            label: "¶¶10-14 co-conspirator roster",
            supportive: true,
          },
        ],
      },
      {
        name: "$5M gross receipts in 24 months",
        status: "amber",
        explainer:
          "$100M lender money + SPAC proceeds vs defense netting theory that circular principal is not receipts.",
        ev: [
          {
            id: "S1-27",
            label: "¶27 $145M circular transfers",
            supportive: true,
          },
          {
            id: "NET",
            label: "Defense net-flow analysis (TBD expert)",
            supportive: false,
          },
        ],
      },
    ],
    deps: {
      role: "anchor",
      text: "Carries the 10-year floor; every plea conversation prices off this count.",
    },
    narrative: {
      legal:
        "Series of §1343 violations affecting financial institutions, organized and supervised by the defendants, grossing $5M+ per 24 months.",
      jury: "They built a machine that turned borrowed money into fake revenue, over and over, for five years.",
    },
    vuln: { sol: 10, preempt: 5, causation: 35, expertDep: 55, damages: 30 },
    timeline: [
      { date: "Jul 2019", score: 40, event: "E1 MSA signed" },
      { date: "Apr 2024", score: 80, event: "SPAC + 8-K" },
      { date: "Dec 2024", score: 90, event: "Payments stop at resignation" },
    ],
    jinst: {
      source: "Sand Instr. (financial crimes)",
      model:
        "§225 elements charge - no pattern instruction exists; expect a fight",
      links: ["S-1 ¶¶58-59", "18 U.S.C. §225"],
    },
  },
  {
    id: "sec8k",
    name: "Securities Fraud - the 8-K",
    short: "10b-5",
    score: 78,
    dim: { evid: 90, caselaw: 80, experts: 65, mvuln: 55, damages: 80 },
    elements: [
      {
        name: "Material misstatement",
        status: "green",
        explainer:
          "$421M revenue, 1,000+ customers, 4.4M users; >=90% fabricated.",
        ev: [
          { id: "8K", label: "Form 8-K (4/22/24)", supportive: true },
          {
            id: "S1-34",
            label: "Dec 17 2023 fake-revenue email",
            supportive: true,
          },
        ],
      },
      {
        name: "Scienter",
        status: "amber",
        explainer:
          "Red-flag message + curated entity info (¶28) vs reliance on auditors/counsel.",
        ev: [
          {
            id: "S1-28",
            label: "'no trace of Entity 2' red flag",
            supportive: true,
          },
          {
            id: "AUD",
            label: "Audit + SPAC diligence chain",
            supportive: false,
          },
        ],
      },
      {
        name: "In connection with purchase/sale",
        status: "green",
        explainer: "Public listing; retail and institutional purchases.",
        ev: [{ id: "AILE", label: "NASDAQ listing 4/17/24", supportive: true }],
      },
    ],
    deps: {
      role: "parallel",
      text: "Survives even if §225 collapses; 20-year max.",
    },
    narrative: {
      legal:
        "Rule 10b-5 device, untrue statements, and fraudulent course of business in connection with AILE securities.",
      jury: "The number on the press release was invented, and they rang the bell on it.",
    },
    vuln: { sol: 5, preempt: 5, causation: 45, expertDep: 40, damages: 35 },
    timeline: [
      { date: "Apr 22, 2024", score: 85, event: "8-K filed" },
      { date: "Aug 29, 2024", score: 75, event: "Report; -50% day" },
      { date: "Jan 2, 2025", score: 70, event: "Delisting endpoint" },
    ],
    jinst: {
      source: "Sand securities fraud",
      model: "Hochfelder scienter; Neder materiality",
      links: ["S-1 ¶¶33-41"],
    },
  },
];
const ILE_TD_NAQ = {
  plaintiff: ILE_TD_GOV,
  defendant: [
    {
      id: "role",
      name: "Differential Culpability",
      short: "Role",
      score: 66,
      dim: { evid: 80, caselaw: 50, experts: 40, mvuln: 65, damages: 55 },
      elements: [
        {
          name: "Ownership and control sat with the CEO",
          status: "green",
          explainer:
            "75% with his wife; unfettered account control; every fabrication directive is his.",
          ev: [
            { id: "S1-53", label: "¶53 control finding", supportive: true },
            {
              id: "S1-2930",
              label: "¶¶29-30 sham website + 'no liabilities'",
              supportive: true,
            },
          ],
        },
        {
          name: "Government's own differentiation",
          status: "green",
          explainer:
            "Most-culpable statement on the record at the 4/22 hearing.",
          ev: [{ id: "TR-822", label: "Hr'g Tr. 8:16-22", supportive: true }],
        },
        {
          name: "Naqvi-specific overt acts",
          status: "red",
          explainer:
            "¶40 (urging the Entity 1 lie) and ¶62(o)-(p) (October 2024 wires) must be answered head-on.",
          ev: [
            { id: "S1-40", label: "¶40 allegation", supportive: false },
            {
              id: "S1-62",
              label: "¶62(o)-(p) wire initiations",
              supportive: false,
            },
          ],
        },
      ],
      deps: {
        role: "anchor",
        text: "Drives severance, trial framing, and any role-based plea or sentencing position.",
      },
      narrative: {
        legal:
          "Aiding-and-abetting and conspiracy liability still require Naqvi's own knowledge and intent; the record concentrates both in the CEO.",
        jury: "One man owned this company, controlled its money, invented its customers, and deleted the evidence. He is sitting at the other table.",
      },
      vuln: { sol: 5, preempt: 5, causation: 60, expertDep: 35, damages: 50 },
      timeline: [
        {
          date: "Apr 22, 2026",
          score: 80,
          event: "Gov most-culpable statement",
        },
        { date: "Jul 15, 2026", score: 85, event: "11s excludes Naqvi" },
        { date: "Trial", score: 60, event: "¶40 confrontation" },
      ],
      jinst: {
        source: "Sand aiding/abetting + conspiracy",
        model: "Mere-presence and mere-employment instructions",
        links: ["Dkt.17", "S-1 Count 11s"],
      },
    },
    {
      id: "process",
      name: "Good-Faith Process (Auditors · Counsel · Olivier)",
      short: "Good Faith",
      score: 57,
      dim: { evid: 70, caselaw: 60, experts: 55, mvuln: 50, damages: 40 },
      elements: [
        {
          name: "Professionals transmitted the charged wires",
          status: "green",
          explainer:
            "Violations ONE-FOUR are counsel-to-auditor and counsel-to-lender emails.",
          ev: [
            {
              id: "V1-4",
              label: "Charged wire descriptions",
              supportive: true,
            },
          ],
        },
        {
          name: "The most-informed buyer validated",
          status: "green",
          explainer:
            "Olivier: 18 months of SPAC diligence, bought the assets, kept working with Naqvi.",
          ev: [
            {
              id: "OLIV",
              label: "Olivier course of dealing (Dkt.17)",
              supportive: true,
            },
          ],
        },
        {
          name: "Forged inputs defeat reliance",
          status: "amber",
          explainer:
            "¶42: false confirmations fed to the auditor at the defendants' direction - the government's answer.",
          ev: [
            {
              id: "S1-42",
              label: "¶42 forged confirmations",
              supportive: false,
            },
          ],
        },
      ],
      deps: {
        role: "support",
        text: "Feeds scienter doubt across every count; pairs with the spam finding.",
      },
      narrative: {
        legal:
          "Good-faith reliance on professionals negates intent to defraud.",
        jury: "The people paid to check the numbers checked them. The man who checked hardest bought the company.",
      },
      vuln: { sol: 5, preempt: 5, causation: 55, expertDep: 60, damages: 45 },
      timeline: [
        { date: "2023-24", score: 60, event: "Audit + diligence cycle" },
        { date: "Mar 2025", score: 70, event: "Olivier buys assets" },
        { date: "Trial", score: 55, event: "Testify decision" },
      ],
      jinst: {
        source: "Sand good-faith charge",
        model: "Reliance-on-professionals instruction request",
        links: ["Dkt.17 Ex.1"],
      },
    },
  ],
};
const ILE_TD_CHID = {
  plaintiff: ILE_TD_GOV,
  defendant: [
    {
      id: "reliance",
      name: "Reliance & Absence of Intent",
      short: "Reliance",
      score: 38,
      dim: { evid: 45, caselaw: 55, experts: 50, mvuln: 30, damages: 35 },
      elements: [
        {
          name: "Professional chain",
          status: "amber",
          explainer:
            "Auditor and counsel transmissions; lender approvals (Count TEN texts).",
          ev: [
            {
              id: "V1-4",
              label: "Charged wires are professional emails",
              supportive: true,
            },
          ],
        },
        {
          name: "Direct-evidence problems",
          status: "red",
          explainer:
            "'There will be no liabilities' (¶30), sham E3 website at his direction (¶29), deletion directives (¶¶45, 56).",
          ev: [
            { id: "S1-30", label: "¶30 admission", supportive: false },
            {
              id: "S1-56",
              label: "Post-subpoena deletions",
              supportive: false,
            },
          ],
        },
      ],
      deps: {
        role: "anchor",
        text: "More a mitigation architecture than an acquittal theory.",
      },
      narrative: {
        legal:
          "Intent negated by disclosed structure and professional validation.",
        jury: "A founder who believed in his channel model, surrounded by professionals who signed off.",
      },
      vuln: { sol: 5, preempt: 5, causation: 70, expertDep: 55, damages: 60 },
      timeline: [
        { date: "Apr 2024", score: 35, event: "8-K signed" },
        { date: "May 2025", score: 20, event: "Post-subpoena deletions" },
      ],
      jinst: {
        source: "Sand good-faith",
        model: "Reliance instruction",
        links: ["S-1 ¶¶30-32"],
      },
    },
    {
      id: "s225",
      name: "§225 Construction - Dormant Statute",
      short: "§225 Fight",
      score: 46,
      dim: { evid: 40, caselaw: 70, experts: 45, mvuln: 40, damages: 30 },
      elements: [
        {
          name: "Element definitions unsettled",
          status: "amber",
          explainer:
            "No developed Second Circuit gloss on organize/manage/supervise or receipts-from-enterprise.",
          ev: [{ id: "225", label: "18 U.S.C. §225 text", supportive: true }],
        },
        {
          name: "Receipts vs round-tripped principal",
          status: "amber",
          explainer: "$5M gross-receipts element vs circular-flow netting.",
          ev: [{ id: "S1-27", label: "¶27 flows", supportive: false }],
        },
      ],
      deps: {
        role: "support",
        text: "Best pretrial and Rule 29 vehicle; removes the 10-year floor if it lands.",
      },
      narrative: {
        legal:
          "Statutory-construction and sufficiency challenge to the CFCE count.",
        jury: "-",
      },
      vuln: { sol: 5, preempt: 5, causation: 40, expertDep: 30, damages: 25 },
      timeline: [
        { date: "Q4 2026", score: 55, event: "Rule 12 motion" },
        { date: "Trial", score: 45, event: "Rule 29" },
      ],
      jinst: {
        source: "-",
        model: "Contested §225 elements charge",
        links: ["S-1 ¶58"],
      },
    },
  ],
};
const ILE_TDD = { chid: ILE_TD_CHID, naqvi: ILE_TD_NAQ };
const ILE_WI = {
  plaintiff: [
    {
      id: "cc2",
      name: "Co-Conspirator 2",
      role: "iLearning VP · account controller",
      type: "cooperator-risk",
      photo: "🕵️",
      cred: {
        likability: 35,
        consistency: 50,
        exposure: 95,
        authority: 70,
        advocacy: 40,
      },
      story:
        "The hands of the scheme. Controlled the Entity 1 and Entity 2 US-affiliate accounts through which $145M cycled, and performed every deletion Chidambaran ordered - September 2024 and again after the May 2025 subpoena. Maximum exposure, maximum incentive to deal.",
      prediction: {
        direct:
          "If cooperating: walks the jury through account openings, transfer choreography, and the deletion directives with dates.",
        cross:
          "Deal terms; every directive came from Chidambaran; cannot place Naqvi in a deletion conversation; own criminal exposure dwarfs testimony value.",
        redirect: "Corroboration through bank records and recovered artifacts.",
      },
      elements: [
        {
          theoryId: "cfce",
          elemIdx: 0,
          status: "red",
          note: "His accounts are the enterprise plumbing.",
        },
        {
          theoryId: "cfce",
          elemIdx: 2,
          status: "amber",
          note: "Receipts tracing runs through his accounts; every directive came from the CEO - helps Naqvi.",
        },
      ],
      depo: {
        status: "none",
        date: "-",
        topics: [
          "Account control",
          "Round-trip mechanics",
          "Deletion directives",
        ],
        notTopics: ["Naqvi-specific instructions - none alleged"],
        admissions: [],
        gaps: ["Cooperation status unknown"],
        actions: [
          "FOIA/discovery watch for 5K1 signals",
          "3500 material priority",
        ],
      },
      relations: [
        {
          with: "Chidambaran",
          type: "directed-by",
          note: "S-1 ¶¶45, 56 deletion chain",
        },
        { with: "Entity 1/2", type: "controls", note: "US affiliate accounts" },
      ],
      logistics: {
        availability: "Unknown - likely cooperating",
        location: "Unknown",
        flags: ["5K1 letter probable", "Bruton issues if statements offered"],
      },
      prep: {
        sessions: 0,
        sessionDates: [],
        covered: [],
        uncovered: ["Full cross file pending 3500 production"],
      },
    },
    {
      id: "cc3",
      name: "Co-Conspirator 3",
      role: "Entity 4 CEO · $95K round-trip payee",
      type: "cooperator-risk",
      photo: "🕵️",
      cred: {
        likability: 45,
        consistency: 55,
        exposure: 80,
        authority: 50,
        advocacy: 45,
      },
      story:
        "Outside-the-building conspirator. Paid ~$95K to send Entity 1 money back as fake E4 contract payments and to falsely confirm the contract to the auditor and Lender B. Told by Chidambaran there would be no liabilities.",
      prediction: {
        direct:
          "Authenticates the backdated E4 contract and the September 2024 transfers; repeats the 'no liabilities' statement.",
        cross:
          "Paid participant; statement attribution is Chidambaran-only; Bruton objection for Naqvi at a joint trial.",
        redirect: "Documents corroborate the payments.",
      },
      elements: [
        {
          theoryId: "sec8k",
          elemIdx: 1,
          status: "amber",
          note: "Scienter via fabricated confirmations (¶42).",
        },
        {
          theoryId: "cfce",
          elemIdx: 1,
          status: "amber",
          note: "Fourth-person element; his Chidambaran-facing story helps Naqvi.",
        },
      ],
      depo: {
        status: "none",
        date: "-",
        topics: ["Backdating", "$95K payments", "False confirmations"],
        notTopics: [],
        admissions: [],
        gaps: [],
        actions: ["Bruton motion re 'no liabilities'"],
      },
      relations: [
        { with: "Chidambaran", type: "counterparty", note: "¶30 statement" },
        {
          with: "Lender B",
          type: "deceived",
          note: "9/10/24 false confirmation (Count NINE)",
        },
      ],
      logistics: {
        availability: "Unknown",
        location: "California",
        flags: ["Likely plea + cooperation"],
      },
      prep: { sessions: 0, sessionDates: [], covered: [], uncovered: [] },
    },
    {
      id: "e1w",
      name: "Employee 1",
      role: "Former employee · subpoena recipient",
      type: "fact",
      photo: "📨",
      cred: {
        likability: 60,
        consistency: 70,
        exposure: 20,
        authority: 40,
        advocacy: 55,
      },
      story:
        "The trigger. Told Chidambaran about the May 2025 grand jury subpoena; the deletion directives followed. Purely a Count 11s witness - nothing ties him to Naqvi.",
      prediction: {
        direct:
          "Subpoena receipt, the conversation with Chidambaran, timeline.",
        cross: "Limited; scope confinement to 11s.",
        redirect: "-",
      },
      elements: [
        {
          theoryId: "cfce",
          elemIdx: 0,
          status: "green",
          note: "His entire story is Chidambaran-facing - Count 11s excludes Naqvi.",
        },
      ],
      depo: {
        status: "none",
        date: "-",
        topics: ["Subpoena", "Chidambaran conversation"],
        notTopics: ["Any Naqvi contact"],
        admissions: [],
        gaps: [],
        actions: ["Severance exhibit: his testimony is 11s-only"],
      },
      relations: [{ with: "Chidambaran", type: "reported-to", note: "¶56" }],
      logistics: {
        availability: "Government witness",
        location: "Maryland",
        flags: [],
      },
      prep: { sessions: 0, sessionDates: [], covered: [], uncovered: [] },
    },
  ],
  defendant: [
    {
      id: "to",
      name: "Tom Olivier",
      role: "SPAC president · interim CEO · bought the assets",
      type: "fact",
      photo: "🧭",
      cred: {
        likability: 75,
        consistency: 80,
        exposure: 15,
        authority: 85,
        advocacy: 70,
      },
      story:
        "The defense's most credible human. Ran 18 months of diligence as SPAC president, served as interim COO/CEO/CBO after the resignations, bought the assets out of Chapter 7 for AAI Solutions, and kept working with Naqvi afterward. Sophisticated, self-interested in the right direction, and unimpeachable on access.",
      prediction: {
        direct:
          "Diligence scope; what the data room showed; why he kept working with Naqvi; the asset purchase.",
        cross:
          "Gov: he was deceived like everyone - forged confirmations reached him too; his purchase was of assets, not of the defendants' honesty.",
        redirect:
          "He had subpoena power over nothing but saw more than any investor and still chose Naqvi.",
      },
      elements: [
        {
          theoryId: "sec8k",
          elemIdx: 1,
          status: "green",
          note: "Anchor witness against scienter - the diligence buyer validated the numbers.",
        },
        {
          theoryId: "cfce",
          elemIdx: 0,
          status: "green",
          note: "Post-hoc conduct evidence of Naqvi's standing.",
        },
      ],
      depo: {
        status: "none",
        date: "-",
        topics: ["Diligence", "Asset purchase", "Post-leave work with Naqvi"],
        notTopics: [],
        admissions: [],
        gaps: ["Not yet interviewed"],
        actions: ["Defense interview + declaration before 9/14"],
      },
      relations: [
        { with: "Naqvi", type: "continued-engagement", note: "Dkt.17" },
        {
          with: "iLearning estate",
          type: "asset-buyer",
          note: "AAI Solutions",
        },
      ],
      logistics: {
        availability: "Cooperative historically",
        location: "Maryland/DC area",
        flags: [
          "Also a potential gov witness on diligence deception - prepare both ways",
        ],
      },
      prep: {
        sessions: 0,
        sessionDates: [],
        covered: [],
        uncovered: ["Interview outline drafted"],
      },
    },
    {
      id: "bg",
      name: "Bonnie Jeanne Gerety",
      role: "Executive hired post-report",
      type: "fact",
      photo: "🧭",
      cred: {
        likability: 65,
        consistency: 70,
        exposure: 10,
        authority: 60,
        advocacy: 60,
      },
      story:
        "Hired after the short-seller report. Her WhatsApp threads with Naqvi - which he produced voluntarily - are ordinary business traffic, deflating the encrypted-app-as-guilt narrative.",
      prediction: {
        direct: "Nature of the WhatsApp use; Naqvi's post-report conduct.",
        cross: "Late arrival; limited scheme-period knowledge.",
        redirect:
          "That is the point - the post-exposure period is where the government claims concealment.",
      },
      elements: [
        {
          theoryId: "sec8k",
          elemIdx: 1,
          status: "green",
          note: "Normalizes the messaging record against the concealment narrative.",
        },
      ],
      depo: {
        status: "none",
        date: "-",
        topics: ["WhatsApp threads", "Post-report operations"],
        notTopics: [],
        admissions: [],
        gaps: [],
        actions: ["Interview before 9/14"],
      },
      relations: [
        { with: "Naqvi", type: "colleague", note: "Produced threads (Dkt.17)" },
      ],
      logistics: { availability: "Unknown", location: "Unknown", flags: [] },
      prep: { sessions: 0, sessionDates: [], covered: [], uncovered: [] },
    },
  ],
};
const ILE_AI = {
  hot: ["s1", "opp17", "playball", "spam", "dkt60", "texts1011"],
  docs: [
    {
      id: "s1",
      type: "filing",
      title: "Superseding Indictment S-1",
      classification: "pin",
      producer: "Grand Jury / USAO EDNY",
      bates: "Dkt.57",
      date: "Jul 15, 2026",
      confidentiality: "Public",
      pages: 30,
      exhibit: "-",
      exhibitStatus: "n/a",
      cluster: null,
      auth: {
        method: "Court record",
        witness: "N/A",
        objections: ["None - operative charging instrument"],
        status: "green",
        foundation: "Judicial notice",
      },
      theoryMaps: [
        {
          theoryId: "cfce",
          theory: "CFCE §225",
          element: "All",
          elemIdx: 0,
          impact: "red",
          explainer: "Charging spine",
        },
        {
          theoryId: "cfce",
          theory: "CFCE §225",
          element: "Organize/manage/supervise",
          elemIdx: 0,
          impact: "green",
          explainer:
            "11s excludes Naqvi; every deletion directive runs Chidambaran to CC-2",
        },
      ],
      ai: {
        summary:
          "Thirty pages. Counts 1s-10s against both defendants; Count 11s (obstruction conspiracy, May-Dec 2025) against Chidambaran alone. Key paragraphs: ¶27 ($145M round trips), ¶28 (no-trace red flag), ¶30 (no-liabilities statement), ¶34 (fake-revenue email), ¶38 (8-K, 90% fabricated), ¶40 (at the urging of Naqvi), ¶¶45/56 (deletion directives), ¶¶51-54 (gains).",
        keyPassages: [
          {
            cite: "¶40",
            text: "At the urging of NAQVI... agreed to tell Investor A and Lender B that iLearning had no access to Entity 1 financials.",
            impact: "red",
          },
          {
            cite: "¶56",
            text: "After learning of the grand jury investigation, CHIDAMBARAN directed Co-Conspirator 2 to delete...",
            impact: "green",
          },
        ],
        contradictions: [
          "Detention-memo obstruction theming vs 11s charging decision",
        ],
        questions: [
          "Which cooperator sources ¶40?",
          "Grand jury exhibits behind ¶27 tracing",
        ],
        witness: "Case agent / cooperators",
      },
    },
    {
      id: "opp17",
      type: "filing",
      title: "Naqvi Opposition to Detention (Dkt.17)",
      classification: "pin",
      producer: "Mintz (Halperin)",
      bates: "Dkt.17",
      date: "Apr 27, 2026",
      confidentiality: "Public",
      pages: 23,
      exhibit: "Ex.1 attached",
      exhibitStatus: "filed",
      cluster: null,
      auth: {
        method: "Court record",
        witness: "N/A",
        objections: [],
        status: "green",
        foundation: "Judicial notice",
      },
      theoryMaps: [
        {
          theoryId: "cfce",
          theory: "CFCE §225",
          element: "Organize/manage/supervise",
          elemIdx: 0,
          impact: "green",
          explainer:
            "Collects the most-culpable quote, spam finding, Olivier, D. Md. dismissal",
        },
      ],
      ai: {
        summary:
          "The defense playbook in miniature: subordinate role, sub-1% equity, spam forensic finding, play-ball chain in full, community ties, discrimination-based flight rebuttal, comparator releases (First Brands, Tricolor, Ng, Holmes, Hwang).",
        keyPassages: [
          {
            cite: "at 8-9",
            text: "Special committee forensic expert: deleted items appear to be spam.",
            impact: "green",
          },
          {
            cite: "Ex.1",
            text: "Full play-ball chain - investor preconditions context.",
            impact: "green",
          },
        ],
        contradictions: [],
        questions: [
          "Convert bail comparators into sentencing comparators later",
        ],
        witness: "-",
      },
    },
    {
      id: "playball",
      type: "evidence",
      title: "Play-ball email chain (Dkt.17 Ex.1)",
      classification: "hot",
      producer: "Naqvi (voluntary)",
      bates: "Ex.1",
      date: "2024",
      confidentiality: "Public",
      pages: 4,
      exhibit: "Ex.1",
      exhibitStatus: "filed",
      cluster: null,
      auth: {
        method: "Party production + metadata",
        witness: "Naqvi or recipient",
        objections: ["FRE 106 completeness designation ready"],
        status: "green",
        foundation: "FRE 901(b)(1)",
      },
      theoryMaps: [
        {
          theoryId: "sec8k",
          theory: "Securities fraud",
          element: "Scienter",
          elemIdx: 1,
          impact: "green",
          explainer:
            "Produced by the defendant himself; context defeats the fragment",
        },
      ],
      ai: {
        summary:
          "The government quoted a fragment; the full chain reads as relaying a prospective investor's preconditions. The voluntary production is itself the argument.",
        keyPassages: [
          {
            cite: "full chain",
            text: "Investor conditions precede the quoted phrase.",
            impact: "green",
          },
        ],
        contradictions: ["Gov fragment framing"],
        questions: ["Identify every recipient for completeness"],
        witness: "Chain participants",
      },
    },
    {
      id: "spam",
      type: "evidence",
      title: "Special-committee forensic finding",
      classification: "hot",
      producer: "Company special committee",
      bates: "Dkt.17 at 8-9",
      date: "2025",
      confidentiality: "Public (as cited)",
      pages: 2,
      exhibit: "-",
      exhibitStatus: "tbd",
      cluster: null,
      auth: {
        method: "Expert report / committee record",
        witness: "Committee forensic examiner",
        objections: ["Hearsay layers - subpoena the examiner"],
        status: "amber",
        foundation: "FRE 803(6)/expert",
      },
      theoryMaps: [
        {
          theoryId: "cfce",
          theory: "CFCE §225",
          element: "Organize/manage/supervise",
          elemIdx: 0,
          impact: "green",
          explainer:
            "Adverse-party expert spam finding; corroborated by the 11s exclusion",
        },
      ],
      ai: {
        summary:
          "Items deleted from Naqvi's account appear to be spam - the company's own retained expert, pre-charging. Pair with the 11s exclusion in every brief.",
        keyPassages: [
          { cite: "finding", text: "appear to be spam", impact: "green" },
        ],
        contradictions: ["Gov detention theming"],
        questions: ["Obtain the full report, not the quote"],
        witness: "Examiner (subpoena)",
      },
    },
    {
      id: "dkt60",
      type: "filing",
      title: "Renewed travel application (Dkt.60)",
      classification: "hot",
      producer: "Milbank (Heller/Singer)",
      bates: "Dkt.60",
      date: "Aug 11, 2026",
      confidentiality: "Public",
      pages: 3,
      exhibit: "Proposed order attached",
      exhibitStatus: "pending",
      cluster: null,
      auth: {
        method: "Court record",
        witness: "N/A",
        objections: [],
        status: "green",
        foundation: "Judicial notice",
      },
      theoryMaps: [
        {
          theoryId: "cfce",
          theory: "Conditions record",
          element: "Four persons in concert",
          elemIdx: 1,
          impact: "green",
          explainer: "PTS confirms full compliance; gov takes no position",
        },
      ],
      ai: {
        summary:
          "Weeks of Aug 17 or Aug 24. Five companies: Zayna Ventures (partner role), Tenstorrent, Hippocratic AI, Axiado, Patent VC (remote, sales/marketing, contract). All aware of the indictment. None involve fundraising or investor interaction. If granted, a follow-up itinerary letter is required before travel.",
        keyPassages: [
          {
            cite: "at 2",
            text: "Patent VC - remote, sales- and marketing-focused, contract-based role with one of the startups under the firm's purview.",
            impact: "green",
          },
          {
            cite: "at 3",
            text: "Subsequent letter with travel details once scheduled, seeking approval for specific interviews.",
            impact: "amber",
          },
        ],
        contradictions: [],
        questions: ["Ruling watch - today is inside window two"],
        witness: "-",
      },
    },
    {
      id: "texts1011",
      type: "evidence",
      title: "Lender B approval texts (Oct 11, 2024)",
      classification: "hot",
      producer: "Lender B",
      bates: "Count TEN description",
      date: "Oct 11, 2024",
      confidentiality: "TBD",
      pages: 2,
      exhibit: "-",
      exhibitStatus: "tbd",
      cluster: null,
      auth: {
        method: "Business records",
        witness: "Lender B custodian",
        objections: [],
        status: "green",
        foundation: "FRE 803(6)",
      },
      theoryMaps: [
        {
          theoryId: "sec8k",
          theory: "Securities fraud",
          element: "Scienter",
          elemIdx: 1,
          impact: "green",
          explainer:
            "The lender approved the transfer the government charges as concealment",
        },
      ],
      ai: {
        summary:
          "Lender B representatives texted approval of the ~$2.5M transfer from iLearning's Lender B account to Entity 1 - the same transfer charged as a concealment wire. Chronology demonstrative material.",
        keyPassages: [
          {
            cite: "Count TEN",
            text: "Texts approving the release of approximately $2.5 million.",
            impact: "green",
          },
        ],
        contradictions: ["¶¶46-48 concealment framing"],
        questions: ["Full text thread via Rule 17(c)"],
        witness: "Lender B reps",
      },
    },
  ],
};
const ILE_LI = {
  urgent: [
    {
      title: "Dkt.60 ruling gates this week's Bay Area interviews",
      countdown: "Window two: Aug 24-28 (now)",
      leadTime:
        "Grant is not travel authorization - itinerary letter + PTS approvals first",
      summary:
        "The renewed application covers interview weeks of Aug 17 and Aug 24 with five companies including Patent VC. PTS confirms full compliance and defers; the government takes no position. If granted, Milbank must submit a follow-up letter with the specific itinerary, and Pretrial Services must approve each meeting before travel.",
      steps: [
        "Watch the docket for the order - same-day alert",
        "If granted: Heller itinerary letter + PTS approval per meeting, then book",
        "If denied: video interview, or a Houston-area meeting inside the SD Tex 100-mile radius with PTS pre-approval",
      ],
      coords: "Mountain View, CA (proposed) · Fulshear, TX (radius)",
    },
  ],
  sections: [
    {
      id: "brooklyn",
      title: "Brooklyn - Forum Geography",
      icon: "🏛️",
      intro: "Everything procedural happens within two blocks of Cadman Plaza.",
      pins: [
        {
          name: "US Courthouse EDNY",
          coords: "40.7010 N, 73.9903 W",
          dkts: ["All"],
          neighborhood: "225 Cadman Plaza East · Ctrm 10B South",
          remediation: "-",
          findings:
            "Status conf 9/14/26 11:00 AM; proposed 4-week trial Jan/Feb 2027",
          retail: "-",
        },
        {
          name: "USAO EDNY",
          coords: "40.6998 N, 73.9906 W",
          dkts: ["Dkt.10", "Dkt.16"],
          neighborhood: "271 Cadman Plaza East",
          retail: "-",
          remediation: "-",
          findings: "Ammari, Dugan, Rehnquist; Business & Securities Fraud",
        },
        {
          name: "NASDAQ MarketSite",
          coords: "40.7562 N, 73.9861 W",
          dkts: ["S-1 ¶37"],
          neighborhood: "Times Square",
          remediation: "-",
          findings:
            "Bell-ringing photo printed inside the indictment - assume it opens the government case",
          retail: "-",
        },
      ],
      analysis:
        "EDNY venue rests on email and text transmissions through the district (Violations ONE-SIX); no operational nexus needed.",
    },
    {
      id: "texas",
      title: "Texas - Conditions Geography (Naqvi)",
      icon: "🏠",
      intro: "Home detention defines what Patent VC can and cannot schedule.",
      pins: [
        {
          name: "Fulshear residence",
          coords: "29.6933 N, 95.8897 W",
          dkts: ["Dkt.42/43"],
          neighborhood: "Fort Bend County · SD Tex",
          remediation: "-",
          findings:
            "Home detention + GPS; posted as collateral; confession of judgment recorded",
          retail: "-",
        },
        {
          name: "Marion TX property",
          coords: "29.5713 N, 98.1400 W",
          dkts: ["Dkt.33"],
          neighborhood: "Guadalupe County",
          remediation: "-",
          findings:
            "Second posted property; raised-seal bond copy required for recording (Dkt.56)",
          retail: "-",
        },
        {
          name: "Houston (inside 100-mi radius)",
          coords: "29.7604 N, 95.3698 W",
          dkts: ["Dkt.42 conditions"],
          neighborhood: "SD Tex",
          remediation: "-",
          findings:
            "Meetings here need PTS pre-approval only - no court order. The no-order fallback if Dkt.60 is denied.",
          retail: "-",
        },
      ],
      analysis:
        "Travel permitted to NYC, Long Island, Massachusetts, and SD Tex within 100 miles of the residence, plus court travel. Anything else - including the Bay Area - requires an order.",
    },
    {
      id: "scheme",
      title: "Scheme Geography",
      icon: "🌐",
      intro: "Where the alleged enterprise actually lived.",
      pins: [
        {
          name: "iLearning HQ (Bethesda MD)",
          coords: "38.9847 N, 77.0947 W",
          dkts: ["S-1 ¶5"],
          neighborhood: "Defunct; assets to AAI Solutions",
          remediation: "-",
          findings: "Ch.11 12/20/24; Ch.7 3/6/25; $50M+ unpaid",
          retail: "-",
        },
        {
          name: "Entities 1-3",
          coords: "India / UAE",
          dkts: ["S-1 ¶¶15-17"],
          neighborhood: "Shell affiliates",
          remediation: "-",
          findings: "$143M + $50M + $29M fake revenue as of 9/30/23",
          retail: "-",
        },
        {
          name: "Lenders A/B/C",
          coords: "Portola Valley · Pasadena · Morriston NJ",
          dkts: ["S-1 ¶¶19-21"],
          neighborhood: "$40M + $40M + $20M",
          remediation: "-",
          findings:
            "FDIC-insured status of B and C drives the affecting-financial-institutions enhancement (30-yr wires)",
          retail: "-",
        },
      ],
      analysis:
        "The India/UAE entity network is the government's flight-risk story and the defense's discrimination-rebuttal terrain; it is also where Rule 15 deposition fights would live if the case ever needs foreign witnesses.",
    },
  ],
};
const ILE_ACOMMS = [
  {
    attyEmail: "nheller@milbank.com",
    threads: [
      {
        id: "ile-th1",
        subject: "Re: Dkt.60 - Bay Area interview windows (Patent VC)",
        type: "scheduling",
        date: "Aug 12, 2026",
        unread: true,
        messages: [
          {
            from: "Raj Abhyanker (Patent VC)",
            to: "Nola B. Heller",
            date: "Aug 12, 2026 09:10",
            body: "Nola - we saw the renewed application. If the Court grants leave, Patent VC can host the week of Aug 24 in Mountain View, or run video as a fallback. Role scope will match the letter exactly: remote, sales and marketing, contract-based, no fundraising, no investor interaction.",
          },
          {
            from: "Nola B. Heller",
            to: "Raj Abhyanker (Patent VC)",
            date: "Aug 12, 2026 17:42",
            body: "Thanks Raj. Per the application we will submit a follow-up letter with the specific itinerary for approval once anything is scheduled. All logistics through our office; Pretrial Services must pre-approve each meeting. Will alert you the moment the order posts.",
          },
        ],
        aiObligations: [
          {
            who: "Milbank / Heller",
            obligation: "Itinerary letter to the Court if Dkt.60 is granted",
            deadline: "Before any travel",
            linkedDkt: "Dkt.60 at 3",
          },
          {
            who: "Patent VC / Raj",
            obligation:
              "Written role description excluding fundraising and investor contact",
            deadline: "Before any offer",
            linkedDkt: "Dkt.60 at 2",
          },
          {
            who: "Patent VC / Raj",
            obligation:
              "Video-interview fallback + Houston-radius option scoped",
            deadline: "If denied",
            linkedDkt: "Dkt.42 conditions",
          },
        ],
        aiFlags: [
          "Naqvi is a represented criminal defendant - all case-adjacent contact through Milbank",
          "Patent VC is named in a public filing (Dkt.60) as a prospective employer - keep this workspace factual",
          "Requested windows: Aug 17 and Aug 24 - today is inside window two",
        ],
      },
    ],
  },
];
const ILE_PB_SIDE = {
  opponentName:
    "USAO EDNY - Business & Securities Fraud (Ammari, Dugan, Rehnquist)",
  strongestCase: {
    title: "The Government Opening, In Their Voice",
    opening:
      "For five years these two men ran a company on invented revenue. Ninety percent of the number they published was fake. When a report exposed them, they did not stop - they spun the money in circles to keep the lie alive, and when a grand jury came asking, the man at the top ordered the evidence destroyed.",
    evidenceSelection: [
      "$145M circular-transfer bank records (¶27)",
      "Dec 17 2023 fake-revenue-by-customer email (¶34)",
      "Form 8-K + bell-ringing photo (¶¶37-38)",
      "'There will be no liabilities' (¶30)",
      "Deletion directives after the subpoena (¶56)",
    ],
    framing:
      "Lead with the photo and the 8-K number, end with the deletions. Naqvi is attached with ¶40 and the October 2024 wires.",
  },
  decisionTree: [
    {
      decision:
        "Does the government offer Naqvi a differentiated plea before 9/14?",
      leaning: "likely-explored",
      probability: 60,
      rationale:
        "Their own most-culpable framing plus 11s architecture invites a two-track resolution; a cooperating CFO simplifies a 4-week trial.",
      shifters: [
        "CC-2/CC-3 cooperation locking in reduces their need for Naqvi",
        "A granted Dkt.60 + stable employment strengthens Naqvi's no-rush posture",
      ],
    },
    {
      decision: "Does Naqvi move to sever Count 11s spillover?",
      leaning: "file",
      probability: 75,
      rationale:
        "Zafiro is a high bar, but the motion forces limiting-instruction commitments and frames the role defense either way.",
      shifters: [
        "Bruton exposure from CC-3's 'no liabilities' retelling",
        "Government stipulation to redactions",
      ],
    },
    {
      decision: "Trial date at the 9/14 conference?",
      leaning: "Feb 2027",
      probability: 65,
      rationale:
        "Four weeks offered; defense wants maximum 3500 runway; government wants January.",
      shifters: ["Volume of Rule 16 production", "Any second superseding"],
    },
  ],
  resourceTells: [
    {
      observation:
        "Three AUSAs on the caption plus Pitluck signing the S-1; same-day stay papers in April.",
      pattern: "Fully staffed, pre-drafted, escalation-ready.",
      interpretation:
        "This is a priority prosecution; expect no slack on deadlines.",
      implications:
        "Match cadence: every application papered early with proposed orders.",
    },
  ],
  witnessSequencing: {
    predictedOrder: [
      {
        rank: 1,
        witness: "Case agent (overview + charts)",
        rationale: "Frames the roadmap before deeper witnesses testify.",
      },
      {
        rank: 2,
        witness: "Lender B witnesses (loans, texts, September meeting)",
        rationale:
          "Establishes the lender-approved transfer record early.",
      },
      {
        rank: 3,
        witness: "CC-3 (backdating, $95K, no-liabilities)",
        rationale:
          "Locks in the backdated-contract admission while memory is fresh.",
      },
      {
        rank: 4,
        witness: "CC-2 if cooperating (accounts, deletions)",
        rationale:
          "Sequenced after CC-3 to corroborate the account-control narrative.",
      },
      {
        rank: 5,
        witness: "Investor A diligence witness",
        rationale:
          "Ties the diligence email into the round-trip financing theory.",
      },
      {
        rank: 6,
        witness: "Summary FBI forensic accountant",
        rationale:
          "Closes with the flow-of-funds summary tying every prior witness together.",
      },
    ],
    crossExamStrategy:
      "Every cooperator: directives came from the CEO; every lender: you approved the transfers in writing; the agent: the civil case died with prejudice.",
  },
  pressurePoints: [
    {
      event: "Dkt.60 ruling (this week)",
      impact: "Employment stability = mitigation record + normalcy narrative",
      likelihood: 70,
      ourMove:
        "If granted: itinerary letter same day, PTS approvals, scoped role memo. If denied: video interview and Houston-radius option.",
    },
    {
      event: "9/14 status conference",
      impact: "Locks motion schedule, trial dates, STA posture",
      likelihood: 100,
      ourMove:
        "Arrive with agreed schedule proposal; trade exclusions for firm Feb 2027 dates and 3500 deadlines.",
    },
  ],
};
const ILE_PB = { plaintiff: ILE_PB_SIDE, defendant: ILE_PB_SIDE };
const ILE_DEPS = [
  {
    id: "e1gj",
    witness: "Employee 1",
    role: "Grand jury witness - subpoena trigger",
    party: "Government",
    takenBy: "Grand Jury",
    date: "May 2025",
    duration: "-",
    pages: 0,
    status: "raw",
    keyAdmissions: [
      {
        cite: "S-1 ¶56",
        quote:
          "Told CHIDAMBARAN that Employee 1 had been served a subpoena from the Grand Jury.",
        why: "Starts the Count 11s clock; entirely Chidambaran-facing.",
        supports: "Government",
        linkTheory: "Obstruction (11s - Chid only)",
      },
    ],
    designation: {
      complete: false,
      plaintiffRanges: [],
      defendantRanges: [],
      rule32Status: "Criminal matter - 3500/Jencks material due pretrial",
      useCase: "11s narrative; severance exhibit for Naqvi",
    },
    bankLinks: [],
    warLinks: [],
  },
  {
    id: "cc3conf",
    witness: "Co-Conspirator 3",
    role: "E4 CEO - false confirmations",
    party: "Government",
    takenBy: "FBI (proffer expected)",
    date: "2025-26",
    duration: "-",
    pages: 0,
    status: "raw",
    keyAdmissions: [
      {
        cite: "Counts FIVE/NINE",
        quote: "False contract confirmation transmitted to Lender B (9/10/24).",
        why: "Charged wire; his cooperation likely sources ¶30 and ¶47.",
        supports: "Government",
        linkTheory: "Securities/wire fraud",
      },
    ],
    designation: {
      complete: false,
      plaintiffRanges: [],
      defendantRanges: [],
      rule32Status: "3500 material priority",
      useCase: "Bruton motion trigger (no-liabilities retelling)",
    },
    bankLinks: [],
    warLinks: [],
  },
  {
    id: "lenderb",
    witness: "Lender B representatives",
    role: "FDIC bank - $40M lender",
    party: "Government",
    takenBy: "-",
    date: "Sept-Oct 2024 events",
    duration: "-",
    pages: 0,
    status: "raw",
    keyAdmissions: [
      {
        cite: "Count TEN",
        quote:
          "Texts approving release of ~$2.5M from iLearning's account to Entity 1 (10/11/24).",
        why: "The lender approved the transfer charged as concealment - defense chronology anchor.",
        supports: "Defendant",
        linkTheory: "Good-faith process",
      },
    ],
    designation: {
      complete: false,
      plaintiffRanges: [],
      defendantRanges: [],
      rule32Status: "Rule 17(c) subpoena for full thread",
      useCase: "Cross of lender witnesses; concealment rebuttal",
    },
    bankLinks: [],
    warLinks: [],
  },
];
const ILE_OUT = [
  {
    witness: "Tom Olivier",
    role: "SPAC president / asset buyer - defense validator",
    party: "Defendant",
    why: "Most informed third party; kept working with Naqvi; declaration supports every good-faith theme.",
    deadlineEvent: "Status conference",
    deadlineDate: "Sep 14, 2026",
    daysToDeadline: 21,
    complications:
      "May also be a government diligence-deception witness - interview covers both postures.",
    urgency: "high",
    action: "Defense interview + declaration within 2 weeks.",
  },
  {
    witness: "Bonnie Jeanne Gerety",
    role: "Post-report executive",
    party: "Defendant",
    why: "Normalizes the WhatsApp record; post-exposure conduct witness.",
    deadlineEvent: "Status conference",
    deadlineDate: "Sep 14, 2026",
    daysToDeadline: 21,
    complications: "Location unknown.",
    urgency: "medium",
    action: "Locate and interview.",
  },
  {
    witness: "Special-committee forensic examiner",
    role: "Author of the spam finding",
    party: "Defendant",
    why: "Convert the quoted finding into a full report + trial-ready expert.",
    deadlineEvent: "Motion schedule set",
    deadlineDate: "Sep 14, 2026",
    daysToDeadline: 21,
    complications: "Company privilege assertions over the committee file.",
    urgency: "high",
    action: "Rule 17(c) subpoena for the full report.",
  },
  {
    witness: "PTS Officer (SD Tex supervision)",
    role: "Compliance record custodian",
    party: "Defendant",
    why: "Perfect-compliance record is the currency for Dkt.60 and every future modification.",
    deadlineEvent: "Dkt.60 ruling",
    deadlineDate: "This week",
    daysToDeadline: 1,
    complications: "None - PTS already confirmed compliance in the letter.",
    urgency: "critical",
    action: "Keep the record spotless; log every approval.",
  },
];
const ILE_SET = {
  context:
    "Criminal posture: no settlement, only plea architecture. The §225 ten-year floor is the government's anchor; differentiated culpability is the defense counter-anchor. Comparator sentences below are the realistic bands if any count of conviction lands.",
  ledger: [
    {
      date: "Apr 22, 2026",
      ts: "2026-04-22",
      type: "demand",
      side: "Government",
      amount: 0,
      label: "Detention sought for both defendants (de novo)",
      linkedTo: "Dkt.10/12, 22-23",
      note: "Opening leverage posture; most-culpable statement made here.",
      isAnchor: true,
    },
    {
      date: "May 14, 2026",
      ts: "2026-05-14",
      type: "offer",
      side: "Naqvi",
      amount: 3000022,
      label: "Release package accepted - $3,000,022",
      linkedTo: "Dkt.33, 42/43",
      note: "Three homes + confessions of judgment + $1.035M unsecured.",
      isAnchor: true,
    },
    {
      date: "May 15, 2026",
      ts: "2026-05-15",
      type: "offer",
      side: "Chidambaran",
      amount: 15000000,
      label: "Consented $15M partially secured package",
      linkedTo: "Dkt.35",
      note: "Government consent signals negotiability with the CEO too.",
      isAnchor: true,
    },
    {
      date: "Jul 15, 2026",
      ts: "2026-07-15",
      type: "demand",
      side: "Government",
      amount: 0,
      label: "S-1 adds Count 11s vs Chidambaran",
      linkedTo: "Dkt.57",
      note: "Plea leverage aimed at the CEO; structural gift to Naqvi.",
      isAnchor: false,
    },
  ],
  comparables: [
    {
      id: "hwang",
      name: "USA v. Hwang (Archegos)",
      court: "S.D.N.Y.",
      year: 2024,
      type: "sentence",
      amount: 10000000000,
      theory: "Market manipulation / fraud",
      outcome: "216 months (Nov 2024)",
      analogy: 6,
      side: "Plaintiff",
      track: "sentencing",
      ai: "Top of the band: founder, catastrophic counterparty losses. Naqvi distinguishable on role and loss attribution.",
    },
    {
      id: "holmes",
      name: "USA v. Holmes (Theranos)",
      court: "N.D. Cal.",
      year: 2022,
      type: "sentence",
      amount: 121000000,
      theory: "Investor wire fraud",
      outcome: "135 months",
      analogy: 7,
      side: "Plaintiff",
      track: "sentencing",
      ai: "Founder-CEO comparator - maps to Chidambaran, not the CFO.",
    },
    {
      id: "balwani",
      name: "USA v. Balwani (Theranos COO)",
      court: "N.D. Cal.",
      year: 2022,
      type: "sentence",
      amount: 121000000,
      theory: "Investor + patient fraud",
      outcome: "155 months",
      analogy: 6,
      side: "Plaintiff",
      track: "sentencing",
      ai: "Cautionary: the number-two got MORE time after trial. The anti-model for a Naqvi trial decision.",
    },
    {
      id: "watson",
      name: "USA v. Watson (Ozy Media)",
      court: "E.D.N.Y. (Komitee)",
      year: 2024,
      type: "sentence",
      amount: 60000000,
      theory: "Investor fraud - fabricated metrics",
      outcome: "116 months (Dec 2024)",
      analogy: 8,
      side: "Plaintiff",
      track: "sentencing",
      ai: "Same district, same fabricated-metrics DNA, post-trial. The EDNY reference point the government will cite.",
    },
    {
      id: "milton",
      name: "USA v. Milton (Nikola)",
      court: "S.D.N.Y.",
      year: 2023,
      type: "sentence",
      amount: 660000000,
      theory: "Securities fraud - hype statements",
      outcome: "48 months",
      analogy: 5,
      side: "Defendant",
      track: "sentencing",
      ai: "Floor of the founder band; shows sub-decade outcomes exist for public-company fraud.",
    },
    {
      id: "ng",
      name: "USA v. Ng (1MDB)",
      court: "E.D.N.Y. (Brodie)",
      year: 2023,
      type: "sentence",
      amount: 2700000000,
      theory: "FCPA / money laundering",
      outcome: "120 months",
      analogy: 5,
      side: "Plaintiff",
      track: "sentencing",
      ai: "EDNY decade-sentence datapoint for a non-CEO facilitator - the risk case for Naqvi at trial.",
    },
    {
      id: "firstbrands",
      name: "USA v. James (First Brands)",
      court: "S.D.N.Y.",
      year: 2026,
      type: "bail",
      amount: 0,
      theory: "§225 charged",
      outcome: "Released on conditions",
      analogy: 8,
      side: "Defendant",
      track: "bail",
      ai: "Cited in Dkt.17: §225 defendants routinely released - already vindicated here.",
    },
    {
      id: "tricolor",
      name: "USA v. Chu (Tricolor)",
      court: "S.D.N.Y.",
      year: 2025,
      type: "bail",
      amount: 0,
      theory: "Large-scale fraud",
      outcome: "Released on conditions",
      analogy: 7,
      side: "Defendant",
      track: "bail",
      ai: "Second Dkt.17 comparator; conditions-over-detention norm.",
    },
    {
      id: "madoff",
      name: "USA v. Madoff (pre-plea)",
      court: "S.D.N.Y.",
      year: 2009,
      type: "bail",
      amount: 10000000,
      theory: "$65B Ponzi",
      outcome: "$10M bond, home detention pre-plea",
      analogy: 6,
      side: "Defendant",
      track: "bail",
      ai: "The ceiling case for conditions-suffice arguments.",
    },
  ],
  batna: {
    plaintiff: {
      trialCost: 1500000,
      probWeighted: 45000000,
      note: "Gov BATNA: 4-week trial, bank records plus cooperators. Dollar figure = expected forfeiture and restitution recovery; the 120-month §225 floor is the non-dollar anchor.",
    },
    defendant: {
      trialCost: 3500000,
      riskAssessment: 16000000,
      note: "Naqvi BATNA: Honeycutt-capped personal proceeds (~$15.6M alleged) plus defense spend. A non-§225 plea with acceptance prices in the 36-70 month band vs a 120+ trial floor - Balwani is the cautionary tale.",
    },
    twoFront: {
      active: false,
      description:
        "Two-defendant dynamic rather than two-front litigation: Count 11s and the most-culpable record isolate the CEO; any Chidambaran cooperation inverts the table against Naqvi.",
      premium: 0,
      note: "No settlement-premium mechanics in a criminal posture; leverage moves through cooperation and count architecture.",
    },
    analysis:
      "The rational window for a differentiated Naqvi resolution is before the 9/14 schedule locks; after motions, government sunk costs harden.",
    ai: "Track the 9/14 minute entry for plea-track language. A 371-only or wire-count offer without 1s removes the mandatory minimum from the board.",
  },
};
const ILE_WR = {
  trialDay: 0,
  trialDate: "Jan/Feb 2027 - 4 weeks (proposed)",
  courtroom: "Courtroom 10B South, 225 Cadman Plaza East",
  judge: "Judge Nicholas G. Garaufis",
  currentWitness: {
    name: "-",
    role: "Pretrial",
    status: "No witness on the stand - pretrial posture",
    direct:
      "Next court event is the 9/14/26 status conference (11:00 AM): motion briefing schedule and trial dates due.",
    crossHammered: [],
    crossRemaining: [
      {
        topic: "Dkt.60 ruling watch",
        exhibit: "Dkt.60",
        exDesc:
          "Renewed Bay Area travel application - windows Aug 17/24; Patent VC is company 4",
        objection: "None - gov takes no position; PTS defers",
        priority: "CRITICAL",
      },
      {
        topic: "3500/Jencks production cadence",
        exhibit: "-",
        exDesc: "Set firm deadlines at 9/14 in exchange for STA consent",
        objection: "-",
        priority: "HIGH",
      },
    ],
  },
  nextWitness: {
    name: "Status Conference",
    role: "Court",
    who: "Sept 14, 2026 · 11:00 AM · Ctrm 10B South",
    keyDocs: [
      {
        ex: "Dkt.57",
        desc: "S-1 - operative charging instrument (11s = Chidambaran only)",
        strength: "Frame severance early",
      },
      {
        ex: "Dkt.42/43",
        desc: "Naqvi release conditions - employment cadence anchor",
        strength: "Perfect compliance record",
      },
    ],
    directOutline: [
      "Propose motion schedule: severance/Bruton, §225 Rule 12, MIL on deletion theming",
      "Lock Feb 2027 trial dates in exchange for STA exclusion",
      "Fix Rule 16 and 3500 production deadlines",
      "Note employment/travel cadence under conditions",
    ],
    defenseStrategy:
      "Naqvi: preserve Rule 14 and Confrontation objections to Count 11s spillover and to Chidambaran statements; keep the compliance record spotless - it is the currency for every modification.",
  },
  upcomingQueue: [
    {
      name: "Dkt.60 ruling",
      when: "This week (window two runs Aug 24-28)",
      note: "Gates the Patent VC interview; if granted, itinerary letter + PTS approvals before travel",
    },
    {
      name: "Status conference",
      when: "Sep 14, 2026",
      note: "Schedule + trial dates + STA",
    },
    {
      name: "Motion filings",
      when: "Q4 2026 (per schedule)",
      note: "Severance, Bruton, §225 construction, MILs",
    },
  ],
  liveIssues: [
    {
      flag: "Travel",
      ref: "Dkt.60",
      desc: "Renewed application pending during the requested window itself",
      action: "Ruling watch; both in-person and video tracks prepped",
      priority: "CRITICAL",
    },
    {
      flag: "Bond admin",
      ref: "Dkt.56 / 6-22 Order",
      desc: "Raised-seal certified copies issued; confessions of judgment recorded in three counties",
      action: "Confirm recordings complete in all three",
      priority: "LOW",
    },
    {
      flag: "Cooperation watch",
      ref: "CC-2 / CC-3",
      desc: "Deletion actor and $95K payee are classic 5K1 profiles",
      action: "3500 material triage plan ready",
      priority: "HIGH",
    },
  ],
  exhibits: {
    admitted: 0,
    pivotal: [
      {
        id: "S-1 p.13",
        desc: "NASDAQ bell-ringing photo embedded in the indictment - prejudicial celebration imagery",
        when: "Gov opening",
        status: "queued",
        objection: "FRE 403 motion in limine candidate",
      },
      {
        id: "Dkt17-Ex1",
        desc: "Full play-ball email chain - completeness shield against the government fragment",
        when: "Any gov fragment use",
        status: "queued",
        objection: "FRE 106 designation filed",
      },
    ],
  },
  liveNotes: [
    {
      time: "Aug 24, 2026",
      author: "CaseBinder",
      witness: "-",
      text: "PACER synced (71 entries). Dkt.60 pending; today is day one of requested window two. Milbank to submit itinerary letter upon grant.",
    },
  ],
};
const ILE_IMP = [
  {
    id: "ile-most-culpable",
    witness: "Case Agent / Gov summary witness",
    witnessRole: "Government",
    party: "Government",
    priority: "Critical",
    type: "Prior Position",
    topic: "Relative culpability - the April 22 statement",
    priorText:
      "There is no more culpable defendant in this case than this particular defendant.",
    priorCite: "Apr 22, 2026 Hr'g Tr. 8:16-22 (re Chidambaran)",
    inconsistency:
      "At trial the government will present the defendants as an undifferentiated team; at bail it ranked them.",
    script: [
      {
        t: "foundation",
        q: "You are aware the government addressed this Court on April 22, 2026 regarding detention?",
      },
      {
        t: "exhibit",
        q: "I am showing the certified transcript at page 8, lines 16 through 22.",
      },
      {
        t: "confrontation",
        q: "The government told this Court there is no more culpable defendant in this case than Mr. Chidambaran - correct?",
      },
      {
        t: "followup",
        q: "Nothing in the superseding indictment charges Mr. Naqvi with the obstruction count, does it?",
      },
    ],
    contingency:
      "If the government frames it as bail-specific advocacy: the statement was made to a federal judge, on the record, about this case.",
    useFor: ["Opening", "Cross of agent", "Sentencing", "Severance briefing"],
  },
  {
    id: "ile-playball",
    witness: "Government cooperator (chain participant)",
    witnessRole: "Cooperator",
    party: "Government",
    priority: "High",
    type: "Completeness",
    topic: "The play-ball fragment vs the full chain",
    priorText:
      "Fragment quoted at detention as an instruction to resume the scheme.",
    priorCite: "Gov detention presentation; full chain at Dkt.17 Ex.1",
    inconsistency:
      "The complete chain - produced by Mr. Naqvi himself - relays a prospective investor's preconditions.",
    script: [
      {
        t: "foundation",
        q: "You have seen the complete email chain, not just the phrase the government quoted?",
      },
      {
        t: "exhibit",
        q: "Exhibit 1 to Docket 17 - the full chain. Mr. Naqvi produced this voluntarily, correct?",
      },
      {
        t: "confrontation",
        q: "Read the sentence before the quoted phrase. Those are the investor's conditions, are they not?",
      },
      {
        t: "followup",
        q: "People hiding fraud do not usually hand the government the whole thread, do they?",
      },
    ],
    contingency: "FRE 106 designation filed for any partial use.",
    useFor: ["Cross", "MIL response", "Closing"],
  },
  {
    id: "ile-spam",
    witness: "Special-committee forensic examiner",
    witnessRole: "Company-retained expert",
    party: "Both",
    priority: "Critical",
    type: "Admission",
    topic: "Deletions attributed to Naqvi",
    priorText: "Items deleted from Mr. Naqvi's account appear to be spam.",
    priorCite: "Special committee forensic report (cited Dkt.17 at 8-9)",
    inconsistency:
      "Any trial suggestion that Naqvi destroyed evidence collides with the company's own expert and the grand jury's charging decision.",
    script: [
      {
        t: "foundation",
        q: "The special committee retained you - not the defense - correct?",
      },
      {
        t: "foundation",
        q: "You examined the deletions from Mr. Naqvi's account?",
      },
      { t: "confrontation", q: "Your conclusion: they appear to be spam?" },
      {
        t: "followup",
        q: "And Count Eleven of the superseding indictment names only Mr. Chidambaran?",
      },
    ],
    contingency:
      "If scope is attacked: subpoenaed full report shows methodology; the charging decision independently corroborates.",
    useFor: ["MIL", "Cross", "Severance", "Closing"],
  },
];
const ILE_DAM_NAQ = [
  {
    id: "ile-naqvi",
    title: "Naqvi - Financial Exposure",
    subtitle: "Forfeiture (Honeycutt-capped) + restitution",
    mode: "exposure",
    anchor:
      "Months exposure lives in the anchors: §225 floor 120 mo if 1s stands · Watson 116 mo (EDNY) · Balwani 155 · Milton 48. Dollars modeled below.",
    sliders: [
      {
        id: "proceeds",
        label: "Personal proceeds - forfeiture cap ($M)",
        min: 0,
        max: 25,
        def: 16,
        suffix: "M",
        anchor: "S-1 ¶54: 1.35M+ shares (~$11.2M) + $4.4M tax gross-up + comp",
        ai: "Honeycutt bars joint-and-several forfeiture; 853(p) substitute assets reach the posted homes only up to this number.",
      },
      {
        id: "lenderloss",
        label: "Lender loss pool ($M)",
        min: 0,
        max: 150,
        def: 100,
        suffix: "M",
        anchor: "Lenders A ($40M) + B ($40M) + C ($20M)",
        ai: "MVRA restitution base; the Ch.7 shows $50M+ unpaid liabilities.",
      },
      {
        id: "share",
        label: "Restitution share attributed (%)",
        min: 0,
        max: 100,
        def: 20,
        suffix: "%",
        anchor: "Role-based apportionment ask under 3664(h)",
        ai: "Joint-and-several is the government default; the most-culpable transcript is the apportionment exhibit.",
      },
    ],
    toggles: [
      {
        id: "honey",
        label: "Honeycutt allocation",
        def: "applied",
        options: [
          {
            val: "applied",
            label: "Applied",
            desc: "Forfeiture capped at personal proceeds",
          },
          {
            val: "contested",
            label: "Gov contests",
            desc: "Gross-proceeds theory - forfeiture tracks the larger of proceeds or pool share",
          },
        ],
      },
    ],
  },
];
const ILE_DAM_CHID = [
  {
    id: "ile-chid",
    title: "Chidambaran - Financial Exposure",
    subtitle: "Founder-scale forfeiture + restitution",
    mode: "exposure",
    anchor:
      "Founder band in months: Holmes 135 · Hwang 216 · Watson 116 (EDNY). Obstruction (11s) forecloses acceptance credit. Dollars modeled below.",
    sliders: [
      {
        id: "proceeds",
        label: "Personal proceeds ($M)",
        min: 0,
        max: 600,
        def: 513,
        suffix: "M",
        anchor:
          "~50.37M shares >$500M at listing + $12.5M RSUs + $700K comp (¶52)",
        ai: "Listing value vs realized value will be the forfeiture fight.",
      },
      {
        id: "lenderloss",
        label: "Lender loss pool ($M)",
        min: 0,
        max: 150,
        def: 100,
        suffix: "M",
        anchor: "A+B+C; SPAC investor losses layer on via 2461(c)",
        ai: "Peak $1.5B market cap frames the investor-loss narrative.",
      },
      {
        id: "share",
        label: "Restitution share attributed (%)",
        min: 0,
        max: 100,
        def: 70,
        suffix: "%",
        anchor: "Most-culpable framing",
        ai: "Founder share of joint-and-several exposure.",
      },
    ],
    toggles: [
      {
        id: "honey",
        label: "Honeycutt allocation",
        def: "contested",
        options: [
          {
            val: "applied",
            label: "Applied",
            desc: "Capped at personal proceeds",
          },
          {
            val: "contested",
            label: "Gov contests",
            desc: "Gross-proceeds theory",
          },
        ],
      },
    ],
  },
];
const ILE_DEMO_NAQ = [
  {
    id: "ile-roundtrip",
    title: "The $145M Circle",
    description:
      "iLearning to Entity 1 accounts (CC-2) to Entities 2/4 and back, 2021-2024, with the six charged wires flagged. Variant 2 adds the defense overlay: lender-approval timestamps on the October 2024 transfers.",
    useFor: "Opening · Agent cross · §225 gross-receipts fight",
    variants: 2,
  },
  {
    id: "ile-firewall",
    title: "Count 11s Firewall",
    description:
      "Every deletion directive in the S-1, charted by actor: Chidambaran to CC-2, September 2024 and post-subpoena 2025. Naqvi's column holds one entry - the committee expert's spam finding. Variant 2 renders it as a timeline.",
    useFor: "Rule 14 motion · Naqvi opening · closing",
    variants: 2,
  },
  {
    id: "ile-fakestack",
    title: "Fake Revenue Stack - Dec 17, 2023",
    description:
      "$143M (E1) + $50M (E2) + $29M (E3) + $5M (E4) = $227M of fabricated revenue shown to Investor A, by customer, from the company's own diligence email (¶34), against the sliver the government says was real.",
    useFor: "Securities counts · materiality · scienter timeline",
    variants: 1,
  },
  {
    id: "ile-chron",
    title: "Exposure Chronology - Report to Approval",
    description:
      "Aug 29 report and the minus-50% day, Sep 5 lender meeting in New York, Sep 10 CC-3 false confirmation, Oct 11 Lender B approval texts. The Count TEN concealment theory, with the lender's own APPROVED stamp on the final frame.",
    useFor: "Counts NINE-TEN defense · lender-witness cross",
    variants: 1,
  },
  {
    id: "ile-mostculpable",
    title: "The Government's Own Words",
    description:
      "Apr 22, 2026, Tr. 8:16-22, on the record at detention: there is no more culpable defendant in this case than this particular defendant - said about the co-defendant, not about Naqvi. One card, one quote, one attribution line.",
    useFor: "Role differentiation · apportionment · every Naqvi brief",
    variants: 1,
  },
  {
    id: "ile-comparators",
    title: "Executive Fraud Sentencing Comparators",
    description:
      "Watson (Ozy, this district) 116 months, Balwani 155, Holmes 135, Hwang 216, Ng 120, Milton 48 - against the §225 mandatory floor line at 120. The plea-versus-trial calculus in one chart.",
    useFor: "Resolution strategy · sentencing submission",
    variants: 1,
  },
];
const ILE_DEMO_CHID = [
  {
    id: "chid-gatekeepers",
    title: "The Gatekeeper Ring",
    description:
      "The numbers at the center; around them the outside auditor, deal counsel, eighteen months of SPAC diligence, and lender diligence - each with what it reviewed and signed. The government must prove intent to deceive the very professionals he hired. Variant 2 lays the sign-offs on a timeline.",
    useFor: "Opening · scienter defense · reliance instruction",
    variants: 2,
  },
  {
    id: "chid-equity",
    title: "Rode It to Zero",
    description:
      "Founder stake value from the April 2024 bell (>$500M on paper) to Chapter 11 in December to zero in Chapter 7. Annotation: the indictment alleges no stock sales. Looters take the money out; he went down with the company.",
    useFor: "Motive rebuttal · opening · forfeiture fight",
    variants: 1,
  },
  {
    id: "chid-225",
    title: "The Dusted-Off Statute",
    description:
      "18 U.S.C. §225: enacted 1990 for the savings-and-loan crisis, a ten-year mandatory floor, almost never charged since, no Second Circuit gloss on organize, manage, or supervise. A timeline of statutory silence ending at this indictment.",
    useFor: "Rule 12 motion · charge conference · lenity",
    variants: 1,
  },
  {
    id: "chid-ciminelli",
    title: "The Ciminelli Filter",
    description:
      "Each wire count poured through the Supreme Court's property-object test. Transfers to lenders pass; diligence emails to auditors and confirmations to a lender's counsel run amber - attenuated from any traditional property deprivation.",
    useFor: "Rule 29 · jury instructions · appellate preservation",
    variants: 1,
  },
  {
    id: "chid-cooperator",
    title: "The Cooperator's Ledger",
    description:
      "CC-2 at the center: signature authority on every Entity 1 account, the hand on each charged transfer, the deletions done by his own account, and a 5K1.1 motion only the government can file. The obstruction count rests on his memory of unwritten instructions.",
    useFor: "CC-2 cross · Count 11s defense · bias instruction",
    variants: 1,
  },
  {
    id: "chid-bail",
    title: "The Compliance Record",
    description:
      "Detained on de novo review, released on a consented $15 million package, home confinement in Potomac, and zero violations through the 9/14 conference. The record that funds every future conditions request.",
    useFor: "Conditions motions · sentencing mitigation",
    variants: 1,
  },
];
const ILE_LEARN = {
  plaintiff: [
    {
      title: "Chapter 1 - The Machine",
      text: "In 2019 a Maryland education-software company signed a master agreement with a channel partner that never collected a dollar of real revenue. For the next five years, money borrowed from lenders and raised from investors left iLearningEngines, landed in accounts controlled by a company insider, and came back dressed as customer payments. One hundred forty-five million dollars went around that circle.",
    },
    {
      title: "Chapter 2 - The Bell",
      text: "In April 2024 the company merged with a SPAC and rang the NASDAQ bell. Five days later it filed financials claiming $421 million in revenue, a thousand enterprise customers, four million users. The government says at least ninety percent of that revenue never existed - and it has the company's own December 2023 email breaking the fake number down customer by customer.",
    },
    {
      title: "Chapter 3 - After the Report",
      text: "On August 29, 2024 a research report called the revenue fake. The stock lost half its value in a day. The response, the indictment says, was not correction but choreography: money staged to look like customer payments, $95,000 paid to a friendly CEO to send funds back, and - when a grand jury subpoena arrived in May 2025 - an order from the founder to delete everything.",
    },
  ],
  defendant: [
    {
      title: "Chapter 1 - One Owner",
      text: "One man founded this company, held seventy-five percent of it with his wife, and exercised what the indictment itself calls virtually unfettered control over its bank accounts. Every fabricated website, every backdated contract, every deletion order in the charging document traces to him. The government told the judge in April: no defendant in this case is more culpable.",
    },
    {
      title: "Chapter 2 - The CFO Who Produced the Evidence",
      text: "Farhan Naqvi held less than one percent. When investigators came, he produced thousands of documents - including the very email the government now quotes against him, in full, with the context that changes its meaning. The company's own forensic expert looked at his deleted items and called them spam. The grand jury charged the obstruction count against one man, and it was not him.",
    },
    {
      title: "Chapter 3 - The Buyer's Verdict",
      text: "The person who knew the company best - the SPAC president who ran eighteen months of diligence - bought its assets out of bankruptcy and kept working with Naqvi. The civil fraud case built on the short-seller's report was dismissed with prejudice. The most informed people voted with their money and their time.",
    },
  ],
  judge: [
    {
      title: "Chapter 1 - Two Detentions, Two Packages",
      text: "Judge Garaufis granted the government's bail appeals and detained both defendants within days of unsealing. Then the paper arrived: three homes, confessions of judgment, GPS, a consented $15 million structure for the founder. He released both. The lesson of this courtroom is that structure moves outcomes and rhetoric does not.",
    },
    {
      title: "Chapter 2 - The Calendar",
      text: "At the first conference on the superseding indictment he offered four trial weeks in January or February 2027 and told the parties to arrive on September 14 with a briefing schedule and dates. Short-notice applications die here; papered, PTS-vetted, leave-first requests live.",
    },
  ],
};
const ILE_LEARN_CHID = {
  plaintiff: ILE_LEARN.plaintiff,
  judge: ILE_LEARN.judge,
  defendant: [
    {
      title: "Chapter 1 - Built, Not Looted",
      text: "He spent five years building an AI education company - engineers, contracts, a NASDAQ listing. The indictment prices his stake at half a billion dollars at the bell. It does not allege he sold a share. When the company failed, his equity died in Chapter 11 with everyone else's. Looters take the money out. The first-named defendant rode his to zero.",
    },
    {
      title: "Chapter 2 - The Professionals Were in the Room",
      text: "Eighteen months of SPAC diligence. Outside auditors. Deal counsel on every filing. The revenue model - channel partners who license and resell - was described in the documents the gatekeepers reviewed and blessed. The government must prove he intended to deceive the very professionals he hired to check the numbers. Reliance on professionals is not a loophole; it is what innocence looks like in a public-company case.",
    },
    {
      title: "Chapter 3 - The Statute They Dusted Off",
      text: "Count One is a 1990 savings-and-loan statute almost no one has ever been charged under, carrying a ten-year floor and no Second Circuit gloss on what organize or manage even means. The wire counts must survive Ciminelli's property-rights reset. And the obstruction count rests on the account of a cooperating insider who controlled the money and has every reason to point up the chain. An indictment is an accusation. The presumption of innocence belongs to defendant number one too.",
    },
  ],
};
const ILE_BI = [
  {
    id: "s225el",
    issue: "§225 Elements - Organize/Manage/Supervise + Gross Receipts",
    plaintiff: {
      position:
        "Both defendants organized and supervised the enterprise; $100M of lender money plus SPAC proceeds clear the $5M receipts floor many times over.",
      cites: ["S-1 ¶¶27, 58", "¶40 (Naqvi urging)", "¶62(o)-(p) wires"],
    },
    defendant: {
      position:
        "A subordinate CFO does not supervise a CFCE; circular principal is not receipts; the dormant statute has no settled gloss and cannot carry a 10-year floor on these facts.",
      cites: ["18 U.S.C. §225", "Hr'g Tr. 8:16-22", "Net-flow expert (TBD)"],
    },
    ai: "Genuinely open - the rarest charged statute in the case has the least law. Instruction conference will decide it.",
    winner: "open",
  },
  {
    id: "sever",
    issue: "Severance / Bruton - Count 11s Spillover",
    plaintiff: {
      position:
        "Joint trials are the rule; limiting instructions cure spillover (Zafiro); statements are admissible with redactions.",
      cites: ["Zafiro v. United States", "Richardson v. Marsh"],
    },
    defendant: {
      position:
        "An obstruction count charged against one defendant, proved through deletion evidence and a co-defendant statement ('no liabilities'), makes a fair joint trial impossible for the other.",
      cites: [
        "Bruton v. United States",
        "S-1 ¶¶30, 45, 56",
        "Spam finding (Dkt.17)",
      ],
    },
    ai: "Severance itself is uphill, but the motion extracts redactions and a strong limiting instruction - file it for the record and the leverage.",
    winner: "defendant-partial",
  },
  {
    id: "conceal",
    issue: "Concealment vs Lender-Approved Mechanics",
    plaintiff: {
      position:
        "Post-report transactions were staged to deceive Lenders B and C into forbearance and continued withdrawals (¶¶46-48).",
      cites: ["S-1 ¶¶44-48", "Sept 5 NYC meeting ¶62(j)"],
    },
    defendant: {
      position:
        "Lender B approved the charged $2.5M transfer by text (Count TEN); approval obtained in writing is not concealment from the approver.",
      cites: ["Count TEN description", "Chronology demonstrative"],
    },
    ai: "The government's cleanest intent evidence meets its own most awkward exhibit; expect this to be the cross centerpiece on every lender witness.",
    winner: "open",
  },
];
/* keyed wrappers - both case ids share the common object unless defendant-specific */
const ILE_K_JUDGE = { chid: ILE_JI, naqvi: ILE_JI };
const ILE_K_CONTRA = { chid: ILE_CONTRA, naqvi: ILE_CONTRA };
const ILE_K_JURY = { chid: ILE_JURY, naqvi: ILE_JURY };
const ILE_K_EXPERTS = { chid: ILE_EXPERTS, naqvi: ILE_EXPERTS };
const ILE_K_CC = { chid: ILE_CC, naqvi: ILE_CC };
const ILE_K_SM = { chid: ILE_SM, naqvi: ILE_SM };
const ILE_K_AR = { chid: ILE_AR, naqvi: ILE_AR };
const ILE_K_PT = { chid: ILE_PT, naqvi: ILE_PT };
const ILE_K_TDD = ILE_TDD;
const ILE_K_BI = { chid: ILE_BI, naqvi: ILE_BI };
const ILE_K_WI = { chid: ILE_WI, naqvi: ILE_WI };
const ILE_K_AI = { chid: ILE_AI, naqvi: ILE_AI };
const ILE_K_LI = { chid: ILE_LI, naqvi: ILE_LI };
const ILE_K_ACOMMS = { chid: ILE_ACOMMS, naqvi: ILE_ACOMMS };
const ILE_K_PB = { chid: ILE_PB, naqvi: ILE_PB };
const ILE_K_DEPS = { chid: ILE_DEPS, naqvi: ILE_DEPS };
const ILE_K_OUT = { chid: ILE_OUT, naqvi: ILE_OUT };
const ILE_K_SET = { chid: ILE_SET, naqvi: ILE_SET };
const ILE_K_WR = { chid: ILE_WR, naqvi: ILE_WR };
const ILE_K_IMP = { chid: ILE_IMP, naqvi: ILE_IMP };
const ILE_K_DAM = { chid: ILE_DAM_CHID, naqvi: ILE_DAM_NAQ };
const ILE_K_DEMO = { chid: ILE_DEMO_CHID, naqvi: ILE_DEMO_NAQ };
const ILE_K_LEARN = { chid: ILE_LEARN_CHID, naqvi: ILE_LEARN };

const RB_PAGES = [
  ["theories", "⚖️", "Theories"],
  ["witnesses", "👁️", "Witnesses"],
  ["motions", "📋", "Motions"],
  ["caselaw", "🔬", "Caselaw"],
  ["contradictions", "🔍", "Contradictions"],
  ["playbook", "🎯", "Playbook"],
  ["drafts", "⚡", "Drafts"],
  ["gaps", "🧩", "Gaps"],
  ["heatmap", "🌡️", "Heat Map"],
  ["settlement", "🤝", "Settlement"],
  ["hearing", "🎤", "Hearing"],
  ["traps", "⚠️", "Traps"],
  ["judge", "👨‍⚖️", "Judge Intel"],
  ["locations", "📍", "Locations"],
];
// Sidebar groups - ordered by frequency of use in litigation workflow
/* Sidebar nav groups - ordered by daily-use frequency.
   Workspace = the daily entry points. Strategy/Risk/People/Docs follow. */
const JUDGE_INTEL_DATA = {
  ...ILE_K_JUDGE,
  juul: {
    judge: {
      name: "William H. Orrick III",
      court: "N.D. Cal. · San Francisco Division",
      appointed: 2013,
      role: "Presiding · MDL 2913 (4+ years)",
      bio: "Federal District Judge appointed by President Obama. Background as DOJ Civil Division litigator. Known for rigorous case management of complex MDLs and detailed written opinions designed to survive Ninth Circuit review on multiple grounds.",
    },
    rulingPatterns: [
      {
        issue: "Summary Judgment in mass tort",
        record:
          "Granted ~67% (12 of 18 across MDLs) · Median time to ruling 47 days",
        analysis:
          "Tends to grant SJ where causation expert is absent or where 30(b)(6) admissions support defense theory. Less inclined where genuine factual disputes are pled. Currently 187 days post-MSJ in this case - substantially over median, may indicate complex split decision.",
        cites: [
          "Pesce v. JLI (Dkt.3289)",
          "In re Roundup MDL (contributing rulings)",
          "In re Equifax Data Breach Litig.",
        ],
        trend: "defense-leaning",
      },
      {
        issue:
          "Equitable relief under Sonner v. Premier Nutrition (9th Cir. 2020)",
        record:
          "Strict application post-Sonner · denied equitable relief in 4 of 5 cases where adequate legal remedy existed",
        analysis:
          "Has applied Sonner narrowly, but probed at the Oct 31 2025 hearing whether the doctrine creates circular logic when no legal damages exist. May be open to plaintiff's structural argument.",
        cites: [
          "Sonner v. Premier Nutrition",
          "CSEA v. SFUSD (extending Sonner reasoning)",
        ],
        trend: "unclear · case-specific",
      },
      {
        issue: "Sealing standards (Kamakana / Foltz)",
        record:
          "Issues OSCs for overbroad sealing · ~30% of sealing motions partially denied",
        analysis:
          "Applies Kamakana standard rigorously. Has issued show-cause orders for fee committee materials in this very MDL (Dkt.4167). Counsel must narrowly tailor.",
        cites: ["Kamakana v. City & Cnty. of Honolulu", "Foltz v. State Farm"],
        trend: "transparency-leaning",
      },
      {
        issue: "Class settlement approval",
        record:
          "Demands fairness review · approves only after rigorous Rule 23(e) analysis · multiple rounds common",
        analysis:
          "Approved $1.7B JLI class settlement (Sep 2023) and $235M Altria class settlement (Mar 2024) only after detailed objector review and notice plan revisions.",
        cites: [
          "JLI Class Settlement Order (Dkt.4156)",
          "Altria Class Settlement Order (Dkt.4287)",
        ],
        trend: "plaintiff-protective",
      },
      {
        issue: "Remand of opt-out PIs",
        record:
          "Has signaled intent to remand post-SJ · case-specific personal jurisdiction analysis required",
        analysis:
          "Dec 16 2025 CMC: confirmed intent to remand opt-out PIs after SJ. Asked plaintiff what supplemental discovery, after eight years of MDL, is genuinely non-duplicative.",
        cites: ["Dec 16 2025 CMC Tr. (Dkt.4341)", "CMO No. 17"],
        trend: "procedure-driven",
      },
    ],
    oralArgument: [
      {
        date: "Oct 31, 2025",
        hearing: "Combined MSJ Hearing - JLI / Bowen / Altria / NMDs",
        duration: 43,
        ref: "Dkt.4341 transcript",
        keyMoments: [
          {
            minute: "0:00-0:07",
            topic: "Pre-argument framing",
            quote:
              "I've read all the papers. Let me get straight to what I'm wrestling with.",
            signal: "engaged · pre-organized",
          },
          {
            minute: "0:07-0:30",
            topic: "Sonner application",
            quote:
              "If there are no legal damages, then by definition there is no adequate legal remedy at law. So why does Sonner apply?",
            signal:
              "⚠ probed defense circular logic · plaintiff-friendly question",
            importance: "critical",
          },
          {
            minute: "0:30-0:48",
            topic: "Pak admission scope",
            quote:
              "Is there any document anywhere that quantifies past program costs attributable to JUUL?",
            signal: "defense-friendly · centerpiece of MSJ",
          },
          {
            minute: "0:48-0:53",
            topic: "FCLAA preemption sidebar",
            quote:
              "I'm not reaching FCLAA today. Don't read this as silence on it.",
            signal: "reserved for remand",
          },
          {
            minute: "0:53-0:60",
            topic: "Amendment possibility",
            quote:
              "Could SFUSD amend to add a damages expert? What's the procedural posture?",
            signal: "plaintiff-friendly · life raft",
          },
        ],
        summary:
          "YGR-style intensive 43-minute hearing. Probed Sonner application's circular logic for 23 minutes - favorable signal for plaintiff. Spent 18 minutes on Pak admission - defense centerpiece. Did not reach FCLAA. Floated amendment possibility for damages expert.",
      },
    ],
    juryCharge: {
      modelApproach:
        "Adopts 9th Cir. Model Civil Jury Instructions verbatim where they apply. Permits modifications only with detailed proposed redlines and authorities.",
      timeline:
        "Issues tentative jury charge ruling 7 days before charging conference · accepts written objections within 48 hours",
      specialInterrogatories:
        "Uses for complex cases (JUUL likely) · separate findings on liability vs causation vs damages · public nuisance abatement separate from PI",
      historicalDisputes:
        "In Roundup MDL contribution: rejected punitive instruction language proposed by plaintiff as 'inflammatory' · adopted neutral causation standard",
      likelyChargeIssues: [
        "Sherman §1 per se vs rule of reason for antitrust",
        "Public nuisance abatement availability charge",
        "Punitive damages on intentional misconduct",
        "Joint and several apportionment among NMDs",
      ],
    },
    localRules: [
      {
        rule: "Brief page limits",
        detail:
          "25 pages opening · 15 pages reply · enforces strictly · stipulated extensions require detailed showing",
        ref: "PTO #1 (Dkt.4) · Standing Order",
      },
      {
        rule: "Sealing requests",
        detail:
          "Kamakana standard · narrowly tailored · OSC for overbroad requests · attached redlines required",
        ref: "Standing Order on Sealing · Dkt.4167 OSC example",
      },
      {
        rule: "Discovery disputes",
        detail:
          "Letter briefs preferred (3-page limit) · meet-and-confer mandatory · 5-business-day exchange of positions before any letter",
        ref: "PTO #4 · CMO No. 12",
      },
      {
        rule: "Joint statements",
        detail:
          "Required before any contested motion · counsel must certify good-faith conferral · attempt resolution",
        ref: "Standing Order",
      },
      {
        rule: "Hearing requests",
        detail:
          "Set on tentative ruling docket · oral argument granted only on showing of necessity · 7-min per side typical",
        ref: "Standing Order",
      },
      {
        rule: "Ex parte communications",
        detail:
          "Strictly forbidden except scheduling · joint chambers calls only for procedural matters",
        ref: "Standing Order",
      },
    ],
  },
  musk: {
    judge: {
      name: "Yvonne Gonzalez Rogers",
      court: "N.D. Cal. · Oakland Division",
      appointed: 2011,
      role: "Presiding · 24-cv-04722 · Phase 1 trial in progress",
      bio: "Federal District Judge appointed by President Obama. High-profile docket includes Apple v. Epic Games (Fortnite antitrust), Pampena v. Musk securities, Pinterest founders' SEC matters. Known for tight courtroom management, command of record, and surgical questioning.",
    },
    rulingPatterns: [
      {
        issue: "Preliminary injunction in equity cases",
        record:
          "Denied 11 of 14 requests across complex commercial · requires showing of irreparable harm distinct from monetary loss",
        analysis:
          "Denied Musk PI March 2025 (Dkt.213) finding no irreparable harm from for-profit conversion. Denial signaled defense-favorable equity view at the time.",
        cites: ["Apple v. Epic Games PI denial", "Dkt.213 (this case)"],
        trend: "defense-leaning · narrow equity",
      },
      {
        issue: "Discovery disputes / motions to compel",
        record:
          "Detailed orders · won't tolerate gamesmanship · imposes sanctions where warranted",
        analysis:
          "Ordered Sutskever Vol.2 deposition (Dkt.324) reopening for Brockman memo production after defense initially declined. Has imposed sanctions in Apple v. Epic for incomplete production.",
        cites: ["Apple v. Epic discovery sanctions", "Dkt.324 (this case)"],
        trend: "plaintiff-favorable on production",
      },
      {
        issue: "Statute of limitations / laches",
        record:
          "Strict application where record supports · constructive knowledge analysis is detailed",
        analysis:
          "Pretrial Order No.3 framed Sept 2020 'captured by Microsoft' tweet as potential SOL trigger. Will scrutinize Musk's tolling/concealment arguments.",
        cites: ["Pretrial Order No.3", "Pampena v. Musk SOL framework"],
        trend: "defense-favorable on dilation",
      },
      {
        issue: "Trial management style",
        record:
          "Tight schedules · runs courtroom rigorously · 9 hr trial days standard · motions in limine ruled day before",
        analysis:
          "Apple v. Epic finished bench trial in three weeks · maintained pace through 80+ exhibits · decided post-trial in 130 days. Sets the tempo.",
        cites: ["Apple v. Epic Games trial record"],
        trend: "speed-driven · prepared counsel rewarded",
      },
      {
        issue: "§17510.8 charitable solicitations",
        record:
          "No prior rulings on this specific statute · pending in this case",
        analysis:
          "April 30 2026 trial briefs filed by both sides (Dkt.515/516). YGR has not yet ruled. Will be the most consequential evidentiary decision of trial.",
        cites: ["Pending"],
        trend: "uncertain",
      },
    ],
    oralArgument: [
      {
        date: "Mar 4, 2025",
        hearing: "Preliminary Injunction Hearing",
        duration: 38,
        ref: "Dkt.213 transcript",
        keyMoments: [
          {
            minute: "0:00-0:12",
            topic: "Irreparable harm threshold",
            quote:
              "What harm is irreparable here that money damages won't cure?",
            signal: "defense-favorable opening · plaintiff bore burden",
          },
          {
            minute: "0:12-0:25",
            topic: "Charitable trust standing",
            quote:
              "The Attorney General reviewed and declined. Why isn't that the end of the standing question?",
            signal: "⚠ defense-favorable · AG decline weighed heavily",
            importance: "critical",
          },
          {
            minute: "0:25-0:30",
            topic: "Status quo preservation",
            quote:
              "You're asking me to enjoin a structure that's been in place six years.",
            signal: "laches signal",
          },
        ],
        summary:
          "Defense-leaning hearing throughout. Pressed plaintiff on irreparable harm and AG decline. PI denial issued same day.",
      },
      {
        date: "Jan 7, 2026",
        hearing: "MSJ Hearing - Phase 1 charitable trust + UE",
        duration: 62,
        ref: "Dkt.385 transcript",
        keyMoments: [
          {
            minute: "0:00-0:08",
            topic: "Two-phase structure framing",
            quote:
              "Phase 1 is equity claims. Phase 2 is antitrust counterclaims. I want to keep them clean.",
            signal: "procedural · clear bifurcation",
          },
          {
            minute: "0:08-0:31",
            topic: "SOL - September 2020 tweet",
            quote:
              "By September 2020 he was tweeting publicly that OpenAI had been 'captured by Microsoft.' How is that not constructive knowledge?",
            signal: "⚠ defense-favorable · pressed plaintiff 23 minutes",
            importance: "critical",
          },
          {
            minute: "0:31-0:48",
            topic: "FIFO tracing methodology",
            quote:
              "Is FIFO the only appropriate tracing methodology for nonprofit accounting? It seems convenient for plaintiff.",
            signal: "⚠ defense-favorable · skeptical of plaintiff expert",
          },
          {
            minute: "0:48-0:55",
            topic: "Microsoft aiding-and-abetting",
            quote:
              "PX-230 shows Kevin Scott asking questions. Is asking questions the same as aiding?",
            signal: "defense-favorable on Count XIX",
          },
          {
            minute: "0:55-1:02",
            topic: "Phase 2 antitrust contingency",
            quote: "If Phase 1 favors defendants, what happens to Phase 2?",
            signal: "procedural framing",
          },
        ],
        summary:
          "62-minute hearing. Pressed plaintiff hard on SOL and FIFO methodology. Defense-favorable signals throughout. MSJ ruling pending - likely defense partial grant.",
      },
    ],
    juryCharge: {
      modelApproach:
        "9th Cir. Model with antitrust-specific modifications · phased verdict form for two-phase trial",
      timeline:
        "Charging conference held during trial · tentative ruling issued 24 hours before charging · written objections during break",
      specialInterrogatories:
        "Heavy use · separate findings on charitable trust breach · UE tracing · MS aiding-and-abetting · damages",
      historicalDisputes:
        "Apple v. Epic charging dispute: rejected plaintiff's monopolization instruction language as overbroad · adopted narrower per-se framework",
      likelyChargeIssues: [
        "Charitable trust breach - duty / breach / causation / damages",
        "UE tracing methodology (FIFO vs LIFO vs alternative)",
        "§17510.8 strict liability instruction (if applicable)",
        "MS knowing-assistance scienter standard",
        "Phase 1/2 verdict form structure",
      ],
    },
    localRules: [
      {
        rule: "Brief page limits",
        detail:
          "25 pages with leave · enforces strictly · joint stipulated extensions require detailed showing",
        ref: "YGR Standing Order",
      },
      {
        rule: "Trial schedule",
        detail:
          "9 hr trial days · 60 min lunch · two 15-min breaks · runs on time",
        ref: "Pretrial Order No.3",
      },
      {
        rule: "Motions in limine",
        detail:
          "Ruled day before trial · written tentative · brief argument permitted at hearing",
        ref: "PTO No.3",
      },
      {
        rule: "Exhibits",
        detail:
          "Joint exhibit list 14 days pre-trial · objections noted · ruling at trial",
        ref: "PTO No.3",
      },
      {
        rule: "Witness lists",
        detail:
          "Final witness list 14 days pre-trial · order of proof exchanged · time estimates required",
        ref: "PTO No.3",
      },
      {
        rule: "Discovery letters",
        detail:
          "3-page letter briefs · meet-and-confer mandatory · responsive letter due 5 business days",
        ref: "YGR Standing Order",
      },
    ],
  },
  neuro: {
    judge: {
      name: "Fernando L. Aenlle-Rocha",
      court: "C.D. Cal. · Los Angeles Division",
      appointed: 2020,
      role: "Presiding · 2:25-cv-05871",
      bio: "Federal District Judge appointed by President Trump. Background as Assistant U.S. Attorney and prior corporate litigator. Known for procedural rigor, paper-based decisions, and strict enforcement of local rules. Less interventionist than YGR but unforgiving of procedural defects.",
    },
    rulingPatterns: [
      {
        issue: "§101 patent eligibility motions",
        record:
          "Granted 4 of 6 in past patent cases · favors granular Mayo/Alice analysis · skeptical of overbroad claims",
        analysis:
          "Has invalidated patents under §101 where specifications admit natural phenomena. The '061 patent's column 4 'in vivo formation' language is the kind of self-admission Aenlle-Rocha has previously found dispositive.",
        cites: ["Recent §101 decisions tracked via Westlaw integration"],
        trend: "⚠ defense-favorable on §101 with self-admissions",
      },
      {
        issue: "Claim construction",
        record:
          "Tends to adopt narrower constructions · plain meaning emphasis · resists doctrine-of-equivalents expansion",
        analysis:
          "Markman approach is plain-meaning-first. Will likely construe 'magnesium L-threonate composition' narrowly. Plaintiff's broader construction faces uphill battle.",
        cites: ["Aenlle-Rocha Markman record (4 prior patent cases)"],
        trend: "defense-leaning",
      },
      {
        issue: "Local rule enforcement",
        record:
          "Has stricken 2 stipulations in this case for Civil Local Rules violations · vacated initial scheduling conference for inadequate filings",
        analysis:
          "Will not tolerate procedural defects. Counsel adapts or loses filings. Stricken stipulations remain on docket as warning.",
        cites: [
          "Dkt.78 (stipulation stricken)",
          "Dkt.94 (stipulation stricken)",
          "Dkt.62 (scheduling conf. vacated)",
        ],
        trend: "⚠ form-strict",
      },
      {
        issue: "Daubert at expert phase",
        record:
          "Has granted Daubert at expert disclosure phase · prefers early gatekeeping",
        analysis:
          "Will likely set Daubert hearing pre-summary-judgment if expert reports raise methodology issues. Plaintiff Adams's clarifying declaration (Dkt.151) may be tested.",
        cites: ["Aenlle-Rocha Daubert record"],
        trend: "plaintiff-burden-heavy",
      },
      {
        issue: "Mediation orders",
        record:
          "Compels mediation · scrutinizes good-faith participation · adverse procedural rulings for non-engagement",
        analysis:
          "Has ordered mediation by Dec 31 2026 (Dkt.124). Will not tolerate perfunctory participation. Settlement Intelligence preparation should align with this signal.",
        cites: ["Dkt.124 mediation order", "Standing Order on ADR"],
        trend: "settlement-pressure",
      },
    ],
    oralArgument: [
      {
        date: "PENDING",
        hearing: "Markman / Claim Construction Hearing - DATE NOT YET SET",
        duration: 0,
        ref: "Briefing complete · hearing date pending",
        keyMoments: [
          {
            minute: "PREP",
            topic:
              "Anticipated questions (AI extrapolation from prior Markman record)",
            quote:
              "Counsel, walk me through the plain meaning of 'in vivo formation' in column 4. Why doesn't that admit a natural phenomenon?",
            signal:
              "⚠ likely opening question · plaintiff must have answer ready",
            importance: "critical",
          },
          {
            minute: "PREP",
            topic: "Anticipated questions",
            quote:
              "What does the prosecution history say about the scope of 'composition'?",
            signal: "likely · prosecution history estoppel framing",
          },
          {
            minute: "PREP",
            topic: "Anticipated questions",
            quote: "How does CN1200366A inform the construction?",
            signal:
              "⚠ ties construction to invalidity · plaintiff must keep them separate",
          },
        ],
        summary:
          "⚡ NO HEARING YET. AI has extrapolated likely questions from Aenlle-Rocha's record in 4 prior patent Markman hearings. Preparation must start now - claim construction briefing complete, Markman set imminent.",
      },
    ],
    juryCharge: {
      modelApproach:
        "Federal Circuit Bar Association model patent jury instructions · permits party-proposed modifications with authorities",
      timeline:
        "Charging conference 5 business days pre-trial · written tentative · objection period 24 hours",
      specialInterrogatories:
        "Uses for patent cases · separate findings on validity (§§101/102/103/112) · infringement · willfulness · damages",
      historicalDisputes:
        "Past patent trials: rejected plaintiff's overbroad doctrine-of-equivalents instruction · adopted narrower literal-infringement-first framing",
      likelyChargeIssues: [
        "§101 ineligibility (in-vivo formation issue)",
        "§102/103 validity (CN1200366A)",
        "§102(f) inventorship (Mao)",
        "Willfulness post-Halo standard",
        "Lanham §43(a) materiality",
        "DTSA actual + exemplary damages",
      ],
    },
    localRules: [
      {
        rule: "Patent Standing Order",
        detail:
          "Detailed 14-page order: claim construction procedures · early disclosure · expert reports · briefing schedule · case narrowing requirements",
        ref: "Dkt.33 - Patent Standing Order",
        isCritical: true,
      },
      {
        rule: "Civil Local Rules - strict enforcement",
        detail:
          "Has stricken 2 stipulations in this case for non-compliance · scheduling conference vacated · counsel adapts or loses filings",
        ref: "Dkt.78, Dkt.94, Dkt.62",
        isCritical: true,
      },
      {
        rule: "Page limits",
        detail:
          "25 pages · 15 reply · joint extensions require concrete showing of complexity",
        ref: "Civ. L.R. 11-6",
      },
      {
        rule: "Meet-and-confer",
        detail:
          "Mandatory before any discovery motion · joint statement of unresolved issues · conference logged",
        ref: "Civ. L.R. 37-1",
      },
      {
        rule: "Patent narrowing",
        detail:
          "Limit to 5 patents at trial without leave · this case narrowed from 5 → 1 patent already (Court approved)",
        ref: "Patent Standing Order ¶7",
      },
      {
        rule: "Mediation participation",
        detail:
          "Compulsory · good-faith requirement · party representative with settlement authority must attend",
        ref: "Standing Order on ADR · Dkt.124",
      },
      {
        rule: "On submission default",
        detail:
          "Vacates scheduling conferences when in-person appearance unnecessary · rules on paper · §101 motion likely paper-only",
        ref: "Standing practice · Dkt.62",
      },
    ],
  },
};

const CONTRADICTION_ENGINE_DATA = {
  ...ILE_K_CONTRA,
  juul: {
    scanStatus: {
      lastScan: "2 minutes ago",
      docketsWatched: 2,
      entriesScanned: 4523,
      detectedToday: 3,
      running: true,
    },
    liveAlerts: [
      {
        id: "j-live-1",
        time: "2 hours ago",
        severity: "high",
        source: "New filing: Dkt.4488",
        sourceDesc: "JLI's Reply ISO Motion in Limine - filed today 11:47 AM",
        contradictionType: "position",
        summary:
          "JLI's MIL reply argues Pak admission is 'binding admission of corporate fact.' MSJ brief (Dkt.3396) at p.27 characterized same admission as 'witness's individual recollection limitation.' Cannot be both binding corporate fact and individual recollection.",
        recordA: {
          ref: "Dkt.4488 at p.6",
          text: "Pak's testimony is binding on SFUSD as a 30(b)(6) admission of corporate fact regarding past costs.",
        },
        recordB: {
          ref: "Dkt.3396 at p.27",
          text: "Pak's testimony reflects only her individual recollection at the moment of deposition.",
        },
        action:
          "Address in plaintiff's response to MIL reply · use to undermine credibility of Sonner argument",
        impact: "high",
      },
      {
        id: "j-live-2",
        time: "5 hours ago",
        severity: "medium",
        source: "New filing: Dkt.4485",
        sourceDesc: "JLI Supplemental Authority on Sonner",
        contradictionType: "timeline",
        summary:
          "Supplemental authority cites a 9th Cir. case decided March 2026 - but that case post-dates the SJ briefing deadline. Procedural defect signals JLI is fishing for late-breaking authority.",
        recordA: {
          ref: "Dkt.4485",
          text: "Supplemental authority filed Apr 2026 citing March 2026 case",
        },
        recordB: {
          ref: "Dkt.4302 SJ briefing schedule",
          text: "SJ briefing closed October 2025 · supplemental authority requires leave of court",
        },
        action:
          "Move to strike or for leave to respond · note procedural defect to court",
        impact: "medium",
      },
      {
        id: "j-live-3",
        time: "yesterday",
        severity: "low",
        source: "New filing: Dkt.4480",
        sourceDesc: "Altria 30(b)(6) errata sheet",
        contradictionType: "factual",
        summary:
          "Altria errata changes Q3 2018 youth-cohort answer from 'reviewed' to 'received but not necessarily reviewed.' Errata is non-substantive change in form per FRCP 30(e); plaintiff should preserve original answer.",
        recordA: {
          ref: "Dkt.4480 errata sheet",
          text: "Q3 2018 demographic file received but not necessarily reviewed before investment closing.",
        },
        recordB: {
          ref: "Original deposition p.198",
          text: "Q3 2018 demographic data was reviewed.",
        },
        action:
          "Object to errata as substantive change · file motion to strike or use original answer",
        impact: "low",
      },
    ],
    crossCase: [
      {
        id: "j-cross-1",
        severity: "critical",
        summary:
          "JLI's antitrust class brief argues market dominance over JUUL's user demographic. PI cases brief argues no knowledge of who was using the product. These positions are in tension.",
        caseA: {
          ref: "3:20-cv-02345-WHO Dkt.594 (antitrust)",
          text: "JLI's market position depends on its dominant share of closed-system e-cigarette users - a defined demographic captured by its product design.",
        },
        caseB: {
          ref: "3:19-md-02913-WHO Dkt.3447 (PI)",
          text: "JLI could not have known how individual consumers were using the product · responsibility lies with retail enforcement.",
        },
        analysis:
          "Either JLI knew its user demographic well enough to dominate the market (antitrust brief), or it didn't know who was using its product (PI brief). Both positions cannot be true.",
        action:
          "Cite cross-case position in PI rebuttal brief · also leverage in punitive damages argument",
        docketsConflicting: ["3:20-cv-02345-WHO", "3:19-md-02913-WHO"],
      },
      {
        id: "j-cross-2",
        severity: "high",
        summary:
          "Altria's antitrust DD argument acknowledges reviewing user demographics; PI position denies pre-investment knowledge of youth use specifically.",
        caseA: {
          ref: "Antitrust class · Dkt.612",
          text: "Altria conducted thorough due diligence reviewing JLI's customer base before December 2018 investment.",
        },
        caseB: {
          ref: "PI MDL · Dkt.3551",
          text: "Altria did not have specific awareness of youth-cohort usage at the time of the December 2018 investment.",
        },
        analysis:
          "Selective DD: thorough on dominance, blind on demographics? The Q3 2018 cohort data (Dkt.3393) was in the DD file - establishing both reviews must be true.",
        action:
          "Use in RICO scienter argument · pair with Altria 30(b)(6) admission",
        docketsConflicting: ["antitrust class", "MDL PI"],
      },
    ],
    timeline: [
      {
        id: "j-time-1",
        severity: "high",
        summary:
          "JLI's MIL brief implies Pak's deposition was taken before defense counsel had access to JUUL School materials. Record shows the opposite.",
        claimedSequence: [
          {
            date: "Mar 2021",
            event: "Defense counsel obtains JUUL School materials",
          },
          { date: "Dec 2021", event: "Pak deposition taken" },
        ],
        actualSequence: [
          {
            date: "Sep 2018",
            event: "JLI Sept 2018 board deck created (with JUUL School)",
          },
          {
            date: "Apr 2021",
            event: "JUUL School materials produced in discovery",
          },
          {
            date: "Dec 2021",
            event:
              "Pak deposition taken - without 30(b)(6) prep on JUUL School",
          },
        ],
        analysis:
          "Defense had JUUL School materials 8 months before Pak deposition, yet did not include JUUL School in 30(b)(6) topics. Procedural waiver argument available to plaintiff.",
        action:
          "Brief waiver argument · pair with Pak 30(b)(6) topic limitation",
        cite: "Dkt.3247 (production log) · Pak Depo Notice",
      },
    ],
  },
  musk: {
    scanStatus: {
      lastScan: "30 minutes ago",
      docketsWatched: 1,
      entriesScanned: 867,
      detectedToday: 2,
      running: true,
    },
    liveAlerts: [
      {
        id: "m-live-1",
        time: "40 min ago",
        severity: "critical",
        source: "Trial filing: Dkt.515 (defense §17510.8 brief)",
        sourceDesc:
          "Defense's §17510.8 inapplicability brief · filed this morning",
        contradictionType: "position",
        summary:
          "Defense's §17510.8 brief argues OpenAI Inc. did not solicit charitable contributions within meaning of statute. PX-003 (Altman founding email) authenticated at trial Day 1 used 'belongs to the world' language. Brief framing contradicts authenticated trial exhibit.",
        recordA: {
          ref: "Dkt.515 at p.4",
          text: "OpenAI Inc. did not engage in 'charitable solicitation' as defined in §17510.8 because no public solicitation campaign occurred.",
        },
        recordB: {
          ref: "PX-003 (Day 1 trial)",
          text: "Founding email represented OpenAI as charitable mission for prospective donors · obtained 501(c)(3) status.",
        },
        action:
          "Address in plaintiff's response brief due tomorrow · consider sur-reply on authentication",
        impact: "critical · YGR ruling pending pre-Brockman exam",
      },
      {
        id: "m-live-2",
        time: "4 hours ago",
        severity: "medium",
        source: "Trial transcript: Day 2",
        sourceDesc: "Day 2 cross of Musk · transcript posted",
        contradictionType: "factual",
        summary:
          "Defense closing question to Musk implied his Sept 2020 'captured by Microsoft' tweet was based on inside information. PX-200 (LP Agreement) showed publicly disclosed structure. Constructive vs actual knowledge framing matters for SOL.",
        recordA: {
          ref: "Day 2 cross transcript",
          text: "Defense framed Musk's Sept 2020 knowledge as based on inside information.",
        },
        recordB: {
          ref: "PX-200",
          text: "LP Agreement structure was publicly disclosed via SEC filings prior to Sept 2020.",
        },
        action:
          "Use to argue constructive (not actual) knowledge for SOL purposes",
        impact: "medium",
      },
    ],
    crossCase: [],
    timeline: [
      {
        id: "m-time-1",
        severity: "high",
        summary:
          "Defense closing argument outline (filed under seal) implies Brockman's journal entry post-dated the LP formation. Record shows journal predates LP by ~12 months.",
        claimedSequence: [
          {
            date: "Mar 2019",
            event: "LP Conversion (defense's claimed sequence)",
          },
          { date: "Mid-2019", event: "Brockman journal entry per defense" },
        ],
        actualSequence: [
          {
            date: "2017",
            event:
              "Brockman journal entry - 'wrong to steal non-profit from Elon' (PX-163)",
          },
          { date: "Mar 2019", event: "LP Conversion announced" },
        ],
        analysis:
          "Brockman's journal predates the LP conversion by approximately 12 months. Defense framing reverses the sequence - a journal expressing pre-conversion concern is more probative than one written after the fact.",
        action:
          "Address in plaintiff closing · timeline visualization for jury",
        cite: "PX-163 (journal) · Dkt.214 (LP announcement)",
      },
    ],
  },
  neuro: {
    scanStatus: {
      lastScan: "15 minutes ago",
      docketsWatched: 1,
      entriesScanned: 312,
      detectedToday: 1,
      running: true,
    },
    liveAlerts: [
      {
        id: "n-live-1",
        time: "1 day ago",
        severity: "high",
        source: "New filing: Dkt.151 - Adams declaration",
        sourceDesc:
          "Plaintiff's clarifying declaration filed Mar 31, 2026 after claim construction briefs",
        contradictionType: "position",
        summary:
          "Adams declaration ¶7 contradicts defendant's responsive brief at page 14 on construction of 'in vivo formation.' Responsive brief argued the term refers to natural processes; Adams declaration argues it refers to manufactured tissue-culture process. This may need to be addressed in the reply brief.",
        recordA: {
          ref: "Dkt.151 ¶7 (Adams declaration)",
          text: "'In vivo formation' as used in the '061 specification refers to manufactured tissue-culture processes performed by patentee, not naturally occurring biological formation.",
        },
        recordB: {
          ref: "Dkt.142 at p.14 (defendant's responsive brief)",
          text: "'In vivo formation' is plain language admitting natural biological formation - products of nature are not patent-eligible under §101.",
        },
        action:
          "Address in plaintiff's reply brief due 4 days · plaintiff Adams declaration provides counter-authority on §101",
        impact: "high · Markman hearing imminent",
      },
    ],
    crossCase: [],
    timeline: [
      {
        id: "n-time-1",
        severity: "medium",
        summary:
          "Plaintiff brief implies Liu's lab notebook entries pre-date the CN1200366A publication. Record shows the opposite.",
        claimedSequence: [
          { date: "1996", event: "Liu lab notebook entries (per plaintiff)" },
          { date: "1997", event: "CN1200366A publication" },
        ],
        actualSequence: [
          { date: "1997", event: "CN1200366A published in China" },
          {
            date: "1999",
            event: "Liu MIT lab work begins (per available records)",
          },
        ],
        analysis:
          "Plaintiff's framing requires pre-publication notebook entries the production has not yet revealed. If Liu's actual notebook entries post-date 1997, the prior-art anticipation argument strengthens dramatically.",
        action:
          "Discovery focus: subpoena MIT TLO for original notebook entries · authentication critical",
        cite: "Dkt.94 (plaintiff brief implication) · CN1200366A publication record",
      },
    ],
  },
};

const JURY_INTEL_DATA = {
  ...ILE_K_JURY,
  juul: {
    caseTitle: "JUUL Antitrust MDL - Seliavski v. JUUL Labs",
    trialDate: "September 2026",
    jurySeated: false,
    panelSize: 0,
    theory:
      "JLI and Altria coordinated to suppress competing nicotine products and inflate prices. Sherman §1, §2, Cartwright Act, treble damages.",
    voirDire: [
      {
        id: "v1",
        category: "Personal Use & Industry Exposure",
        weight: "high",
        questions: [
          {
            q: "Have you, a family member, or close friend ever used JUUL or e-cigarette products?",
            rationale: "Surfaces direct exposure that may anchor strong views.",
            plaintiffSignal:
              "Parent of underage user → identify and seat (favorable lean)",
            defendantSignal:
              "Adult who quit smoking via JUUL → identify and seat (sympathetic to harm-reduction)",
          },
          {
            q: "What is your view of how the e-cigarette industry has handled youth marketing concerns?",
            rationale:
              "Identifies pre-formed views on corporate responsibility.",
            plaintiffSignal:
              "'They knew and didn't care' → strong plaintiff (seat)",
            defendantSignal: "'Media-driven panic' → strong defense (seat)",
          },
          {
            q: "Have you, or has anyone close to you, worked in the tobacco, vaping, or nicotine industry?",
            rationale: "For-cause exposure or strong perspective.",
            plaintiffSignal:
              "Industry employment → for-cause challenge (strike)",
            defendantSignal:
              "Industry employment → potential seat if no current ties",
          },
        ],
      },
      {
        id: "v2",
        category: "Antitrust & Corporate Coordination",
        weight: "critical",
        questions: [
          {
            q: "What comes to mind when you hear 'price fixing' or 'market division'?",
            rationale:
              "Core liability framing. Identifies gut reactions to the conspiracy theory.",
            plaintiffSignal:
              "'Companies always do it' → seat (plaintiff strong)",
            defendantSignal:
              "'Efficiency / scale' framing → seat (defense strong)",
          },
          {
            q: "Do you believe large corporations sometimes coordinate at the expense of consumers?",
            rationale: "Direct test of the conspiracy framing's plausibility.",
            plaintiffSignal: "Strongly agree → for-cause hold (favorable)",
            defendantSignal: "Strongly disagree → for-cause hold (favorable)",
          },
          {
            q: "Have you ever felt cheated by coordinated pricing - gas stations, airlines, prescriptions?",
            rationale:
              "Surfaces personal grievance that maps onto JLI/Altria narrative.",
            plaintiffSignal:
              "Articulate personal grievance → seat (highly favorable)",
            defendantSignal: "'Just market dynamics' → seat (favorable)",
          },
        ],
      },
      {
        id: "v3",
        category: "Class Actions & Punitive Damages",
        weight: "high",
        questions: [
          {
            q: "What is your view of class action lawsuits - generally helpful, harmful, or somewhere in between?",
            rationale:
              "Class skeptics will resist procedural framing regardless of merits.",
            plaintiffSignal: "'Consumer protection mechanism' → seat",
            defendantSignal:
              "'Lawyer-driven / frivolous' → seat (defense favorable)",
          },
          {
            q: "When a jury finds the defendant violated antitrust law, do you believe trebled damages are appropriate?",
            rationale:
              "Treble damages are the core remedy. Identify those who will rebel against the multiplier.",
            plaintiffSignal: "'Deterrence requires it' → seat",
            defendantSignal: "'Compensate, not multiply' → seat",
          },
          {
            q: "How do you feel about the fees plaintiffs' attorneys recover in class actions?",
            rationale: "Fee skeptics may discount damages in deliberation.",
            plaintiffSignal: "'Necessary cost of justice' → seat",
            defendantSignal: "Strong negative → seat (favorable on damages)",
          },
        ],
      },
      {
        id: "v4",
        category: "Tobacco & Altria Adjacency",
        weight: "critical",
        questions: [
          {
            q: "What is your view of the historical conduct of the tobacco industry?",
            rationale:
              "Altria carries tobacco baggage. Identify who will or won't apply that frame.",
            plaintiffSignal:
              "'Lied for decades / 1998 MSA' → seat (Altria framing lands)",
            defendantSignal: "'Old news, different era' → seat (favorable)",
          },
          {
            q: "Do you believe a parent or affiliated company can be held liable for the conduct of its partner?",
            rationale:
              "Tests willingness to apply piercing/coordination doctrine to Altria's $12.8B investment.",
            plaintiffSignal:
              "'Yes, with proof' → seat (joint liability favorable)",
            defendantSignal:
              "'Each company is its own' → seat (Altria-distancing favorable)",
          },
        ],
      },
      {
        id: "v5",
        category: "Expert Testimony & Economic Analysis",
        weight: "medium",
        questions: [
          {
            q: "In a case with competing expert witnesses on economic impact, how do you weigh their testimony?",
            rationale:
              "Identifies those who will defer to credentials vs skeptics of paid testimony.",
            plaintiffSignal:
              "'Weigh credentials and methodology' → seat (follows damages model)",
            defendantSignal:
              "'Common sense over experts' → seat (jury nullification potential)",
          },
          {
            q: "How comfortable are you reviewing market data, financial spreadsheets, and economic models?",
            rationale:
              "Comfort with data-heavy presentation matters for impact methodology.",
            plaintiffSignal:
              "High comfort → seat (follows plaintiff economic model)",
            defendantSignal:
              "Low comfort → seat (skeptical of complex damages)",
          },
        ],
      },
      {
        id: "v6",
        category: "Children & School Communities",
        weight: "high",
        questions: [
          {
            q: "Are you a parent or grandparent of school-age children?",
            rationale: "Direct stake in the SFUSD plaintiff narrative.",
            plaintiffSignal:
              "Yes, with engagement on schools → strong plaintiff",
            defendantSignal: "No → neutral (slightly defense-favorable)",
          },
          {
            q: "Have you been involved with a school district as a parent, teacher, or volunteer?",
            rationale:
              "SFUSD is the lead plaintiff. Personal experience with school resource constraints lands the costs narrative.",
            plaintiffSignal:
              "Yes, active involvement → seat (strong plaintiff)",
            defendantSignal:
              "No personal connection → seat (less narrative resonance)",
          },
        ],
      },
    ],
    archetypes: [
      {
        id: "a1",
        label: "The Healthcare Professional",
        icon: "⚕️",
        lean: "mixed",
        leanColor: "#6b7280",
        plaintiffStrike:
          "Plaintiff strike if industry-affiliated (insurance exec, pharma rep). Otherwise seat - clinical understanding of addiction lands the science.",
        defendantStrike:
          "Defense strike if works in addiction medicine or pediatrics - too aligned with plaintiff narrative.",
        description:
          "Nurse, PA, pharmacist, social worker, public health worker. Understands addiction biochemistry intimately. May credit harm-reduction theory but distrusts marketing to youth. Wants rigorous clinical evidence, not emotional appeals.",
        identifyVoirDire: ["v1", "v5"],
        plaintiffResonate:
          "FDA timeline evidence, clinical addiction literature, internal JLI nicotine-formulation memos, scientific causation, pediatrician testimony",
        plaintiffAlienate:
          "Pure emotional teen-victim narrative without clinical backing; aggressive damages multiplier without methodology",
        defendantResonate:
          "Harm-reduction framing, FDA approval narrative, comparison to combustible cigarettes, scientific uncertainty",
        defendantAlienate:
          "Minimization of nicotine pharmacology; dismissal of youth uptake data",
      },
      {
        id: "a2",
        label: "The Concerned Parent",
        icon: "👪",
        lean: "plaintiff",
        leanColor: "#ef4444",
        plaintiffStrike:
          "SEAT - strong plaintiff lean. Personal stake in youth-vaping narrative.",
        defendantStrike:
          "For-cause challenge if child used JUUL; peremptory in any event.",
        description:
          "Parent of teens or pre-teens. Has read news about youth vaping epidemic - Truth Initiative campaigns, school newsletters, pediatrician warnings. Predisposed to view JUUL marketing as predatory. Highly receptive to Altria-tobacco connection.",
        identifyVoirDire: ["v1", "v4", "v6"],
        plaintiffResonate:
          "Internal JLI youth-marketing emails, Altria Master Settlement parallel, mango/cucumber pod targeting evidence, school nurse testimony",
        plaintiffAlienate:
          "Defense framing of 'adult product misused by youth'; minimization of marketing reach",
        defendantResonate:
          "FDA-approved adult product, parental responsibility, schools-failure narrative",
        defendantAlienate:
          "Anything questioning parents or schools on the underage use issue",
      },
      {
        id: "a3",
        label: "The Small Business Owner",
        icon: "🏪",
        lean: "defense",
        leanColor: "#3b82f6",
        plaintiffStrike:
          "For-cause challenge if articulates aggressive class-action skepticism. Peremptory otherwise.",
        defendantStrike:
          "SEAT - natural defense lean. Treble damages skepticism, attorney-fee critique.",
        description:
          "Owns or operates small business. Has felt litigation pressure or rising insurance premiums. Treble damages feel punitive and disproportionate to harm. Skeptical of plaintiffs' counsel financial incentives.",
        identifyVoirDire: ["v3", "v2"],
        plaintiffResonate:
          "Anti-corporate framing IF they articulate experiences being squeezed by larger market players",
        plaintiffAlienate:
          "Aggressive damages ask, mass tort procedural scale, Altria-conspiracy framing as overreach",
        defendantResonate:
          "Free-market competition framing, attorney fee critique, regulatory burden narrative, 'pick on the deep pocket'",
        defendantAlienate:
          "Heavy-handed corporate-defense rhetoric (looks like 'two corporations vs the consumer')",
      },
      {
        id: "a4",
        label: "The Retired Educator",
        icon: "📚",
        lean: "plaintiff-leaning",
        leanColor: "#f59e0b",
        plaintiffStrike:
          "SEAT - implicit distrust of nicotine industry, will follow careful proof.",
        defendantStrike:
          "Soft strike - may be open but starts skeptical. Better to use peremptory if available.",
        description:
          "Retired teacher, principal, or counselor. Often female. Wants children protected and starts with implicit distrust of cigarette/vape industry. Will follow careful proof but won't rebel against multiplier if conduct is documented.",
        identifyVoirDire: ["v1", "v4", "v6"],
        plaintiffResonate:
          "Documentary evidence, internal emails, Altria parallel narrative, expert testimony with classroom relatability",
        plaintiffAlienate:
          "Hyperbole without documentary backup; technical jargon without translation",
        defendantResonate:
          "Personal responsibility, FDA process, complexity of nicotine science",
        defendantAlienate:
          "Anything dismissive of school communities or youth health concerns",
      },
      {
        id: "a5",
        label: "The Libertarian Engineer",
        icon: "⚙️",
        lean: "defense",
        leanColor: "#1e3a8a",
        plaintiffStrike:
          "For-cause challenge - adult-choice frame is bedrock value, will resist liability theory.",
        defendantStrike:
          "SEAT - strong defense fit. Consumer-choice and regulatory-overreach lens dominates.",
        description:
          "Tech or engineering professional, often male, values personal autonomy and adult choice. Distrustful of regulatory framings. Will apply 'efficient market' and 'consumer responsibility' as default lens.",
        identifyVoirDire: ["v3", "v2", "v5"],
        plaintiffResonate:
          "Documented coordination conduct (private emails) - appeals to engineer's evidence-driven mind IF documented",
        plaintiffAlienate:
          "Paternalism framing, market-failure narrative, treble damages as social engineering",
        defendantResonate:
          "Consumer-choice arguments, regulatory overreach critique, harm-reduction-vs-cigarettes framing, FDA approval timeline",
        defendantAlienate:
          "Heavy procedural complexity that obscures the merits",
      },
      {
        id: "a6",
        label: "The Consumer Advocate",
        icon: "📢",
        lean: "plaintiff",
        leanColor: "#dc2626",
        plaintiffStrike:
          "SEAT - natural plaintiff fit. Articulate antitrust grievance.",
        defendantStrike:
          "For-cause challenge if shows pattern of pro-plaintiff activism.",
        description:
          "Reads consumer-affairs media, has filed BBB complaints, articulate about feeling cheated by corporations. Predisposed to credit antitrust narrative.",
        identifyVoirDire: ["v2", "v4"],
        plaintiffResonate:
          "Antitrust history, Altria conduct, internal coordination evidence, treble damages as deterrence",
        plaintiffAlienate:
          "Anything that looks like settlement or weakness in pursuit",
        defendantResonate: "Almost nothing - challenging archetype to convert",
        defendantAlienate:
          "Standard defense rhetoric will solidify their plaintiff lean",
      },
      {
        id: "a7",
        label: "The Tech-Native Younger Adult",
        icon: "📱",
        lean: "mixed",
        leanColor: "#a78bfa",
        plaintiffStrike:
          "Lean toward seating - peer-pressure understanding, social-media marketing recognition.",
        defendantStrike:
          "Mixed - may be sympathetic to harm-reduction or to youth-protection depending on personal experience.",
        description:
          "25-40, tech-native, comfortable with digital evidence. Recognizes targeted advertising patterns. May have used JUUL personally. Mixed lean - depends heavily on personal use history and family situation.",
        identifyVoirDire: ["v1", "v6"],
        plaintiffResonate:
          "Targeted advertising evidence, social-media marketing patterns, peer pressure framing",
        plaintiffAlienate: "Out-of-touch parental-protection rhetoric",
        defendantResonate:
          "Adult-choice framing if they used JUUL; harm-reduction narrative",
        defendantAlienate: "Industry-coordination evidence if documented",
      },
      {
        id: "a8",
        label: "The Pragmatic Centrist",
        icon: "⚖️",
        lean: "unclear",
        leanColor: "#6b7280",
        plaintiffStrike:
          "Wildcard. Open to seating but unpredictable. Closing argument-driven verdict.",
        defendantStrike:
          "Wildcard. Equally unpredictable. May be persuaded by methodology or by harm narrative.",
        description:
          "Middle-aged, no strong priors, wants to be persuaded by evidence. Often the swing juror in deliberation. Both sides will fight for these jurors with closing arguments and demonstrative evidence.",
        identifyVoirDire: ["v2", "v3", "v5"],
        plaintiffResonate:
          "Strong documentary evidence, clear damages methodology, narrative arc",
        plaintiffAlienate:
          "Excessive damages ask without proportional methodology",
        defendantResonate:
          "Methodology critique, scientific uncertainty, alternative explanations for harm",
        defendantAlienate: "Looking like they're hiding something during cross",
      },
    ],
    juryGrid: [],
    closingCalibration: null,
  },
  musk: {
    caseTitle: "Musk v. Altman - Charitable Trust Trial",
    trialDate: "April 27, 2026 (jury seated) · Trial Day 3",
    jurySeated: true,
    panelSize: 9,
    theory:
      "Plaintiff: Founding $44M was charitable contribution to mission-locked nonprofit; LP/PBC conversion + Microsoft equity is breach of trust. Defendant: No formal trust formed; donor lacks standing; SOL bars 2015-2018 claims.",
    voirDire: [
      {
        id: "v1",
        category: "Personal Views on Elon Musk",
        weight: "critical",
        questions: [
          {
            q: "What is your overall impression of Elon Musk?",
            rationale:
              "Musk evokes strong reactions. Plaintiff needs jurors who don't despise him; defense needs jurors who don't worship him.",
            plaintiffSignal:
              "Neutral or moderately positive → seat. 'Brilliant innovator' → seat. 'Erratic / unpredictable' → soft strike.",
            defendantSignal:
              "Strongly negative → seat (he's the plaintiff, dislike helps defense). Strongly positive → strike.",
          },
          {
            q: "Have you used or owned any Musk-related products - Tesla, X/Twitter, SpaceX-related services, Neuralink, Starlink?",
            rationale: "Brand affinity translates to plaintiff sympathy.",
            plaintiffSignal:
              "Tesla owner / X user / SpaceX enthusiast → seat (favorable lean)",
            defendantSignal:
              "X account suspended, anti-Tesla, etc → seat (favorable)",
          },
          {
            q: "Have you formed any opinion about the lawsuit between Mr. Musk and OpenAI?",
            rationale: "Pre-formed views.",
            plaintiffSignal:
              "'Musk has a point about the nonprofit mission' → seat (strong)",
            defendantSignal: "'Sour grapes / he left' → seat (strong defense)",
          },
        ],
      },
      {
        id: "v2",
        category: "AI & Technology Industry",
        weight: "high",
        questions: [
          {
            q: "Do you believe AI development is moving too fast, too slow, or about right?",
            rationale:
              "AI safety concerns track with charitable-mission framing.",
            plaintiffSignal:
              "'Too fast / dangerous' → seat (charitable-mission lands)",
            defendantSignal:
              "'About right / progress' → seat (defense friendly)",
          },
          {
            q: "Have you used ChatGPT or any OpenAI products?",
            rationale:
              "Familiarity with OpenAI as product affects perception of mission betrayal.",
            plaintiffSignal:
              "User who was disappointed by commercialization → seat",
            defendantSignal:
              "Heavy ChatGPT user, satisfied with product → seat",
          },
          {
            q: "Do you work in tech, or have close family in tech?",
            rationale: "Industry insiders see 'the way things are done' lens.",
            plaintiffSignal:
              "Tech worker in mission-driven firm → seat (sympathetic to mission frame)",
            defendantSignal:
              "Tech worker in commercial firm → seat (pragmatic frame)",
          },
        ],
      },
      {
        id: "v3",
        category: "Charitable Giving & Trust Concepts",
        weight: "critical",
        questions: [
          {
            q: "Have you donated to a charity in the past five years?",
            rationale:
              "Donors connect viscerally with the donor-beneficiary frame.",
            plaintiffSignal:
              "Yes, with strong views on accountability → seat (very favorable)",
            defendantSignal:
              "Donor experience with charity bureaucracy → seat (somewhat favorable)",
          },
          {
            q: "What is your understanding of the difference between a nonprofit and a for-profit company?",
            rationale: "Tests baseline literacy for the case theory.",
            plaintiffSignal:
              "Articulate distinction with mission-vs-profit framing → seat (strong)",
            defendantSignal: "Cynical view ('all the same') → seat (favorable)",
          },
          {
            q: "If a charity changes its mission years after you donated, do you feel you have any rights as a donor?",
            rationale: "Direct test of donor-standing intuition.",
            plaintiffSignal:
              "'Yes, I'd want my money back' → seat (very strong plaintiff)",
            defendantSignal:
              "'No, that's how charities work' → seat (very strong defense)",
          },
        ],
      },
      {
        id: "v4",
        category: "Statute of Limitations Comprehension",
        weight: "high",
        questions: [
          {
            q: "If you discover that someone wronged you years ago but you didn't realize it at the time, do you think you should still be able to sue?",
            rationale:
              "SOL framing - defense's strongest argument depends on jurors crediting time-bar.",
            plaintiffSignal:
              "'Yes, when discovered' (discovery-rule sympathetic) → seat",
            defendantSignal:
              "'No, statutes of limitation matter' → seat (very favorable)",
          },
          {
            q: "Do you generally believe in deadlines for legal claims?",
            rationale: "Tests bedrock SOL intuition.",
            plaintiffSignal: "'Depends on the case' → seat",
            defendantSignal: "'Yes, at some point you have to move on' → seat",
          },
        ],
      },
      {
        id: "v5",
        category: "Complex Financial Damages",
        weight: "medium",
        questions: [
          {
            q: "Are you comfortable with cases involving large dollar amounts - millions, hundreds of millions, billions?",
            rationale:
              "Constructive trust valuation involves $135B Microsoft equity.",
            plaintiffSignal:
              "Comfortable with large numbers (finance background) → seat (follows damages model)",
            defendantSignal: "Skeptical of huge damages → seat (favorable)",
          },
          {
            q: "Have you ever taken a course in finance, accounting, or business valuation?",
            rationale:
              "Wazzan's FIFO methodology requires comfort with valuation concepts.",
            plaintiffSignal: "Yes, comfortable with valuation → seat",
            defendantSignal:
              "No background, intuitive math → seat (resists complex damages)",
          },
        ],
      },
    ],
    archetypes: [
      {
        id: "a1",
        label: "The Tech Industry Veteran",
        icon: "💻",
        lean: "mixed",
        leanColor: "#6b7280",
        plaintiffStrike:
          "Mixed - depends on industry segment. Mission-driven firm employee → seat. Pragmatic commercial firm employee → strike.",
        defendantStrike:
          "Mirror - pragmatic commercial firm employee = seat for defense.",
        description:
          "Software engineer, PM, or tech executive, 30-55. Familiar with OpenAI as product. Has views on tech industry's relationship with mission/profit. May know players personally or by reputation. Background informs how they view 'mission-locked nonprofit.'",
        identifyVoirDire: ["v2", "v1"],
        plaintiffResonate:
          "Documentary evidence (founding emails, Brockman journal), specific tech-industry coordination patterns",
        plaintiffAlienate:
          "Anything painting tech industry broadly as bad-faith",
        defendantResonate:
          "'How startups actually work,' founder-flexibility framing, mission-evolution narrative",
        defendantAlienate:
          "Heavy-handed defense of OpenAI's specific governance choices",
      },
      {
        id: "a2",
        label: "The Charitable Donor",
        icon: "🤝",
        lean: "plaintiff",
        leanColor: "#ef4444",
        plaintiffStrike: "SEAT - donor accountability framing lands hard.",
        defendantStrike:
          "Strike - strong plaintiff lean. Bristles at 'donor has no rights' defense.",
        description:
          "Active charitable donor, often professional class 40-65. Has board experience or has supported charities through career. Bristles at the idea that donors lose all rights once money is given. Mission-locked argument resonates strongly.",
        identifyVoirDire: ["v3", "v1"],
        plaintiffResonate:
          "Donor representations evidence (Altman emails), mission-locked charter language, betrayal-of-trust narrative",
        plaintiffAlienate:
          "Aggressive damages quantification - they want vindication of principle, not money",
        defendantResonate: "Almost nothing - entrenched plaintiff lean",
        defendantAlienate:
          "Anything legalistic that feels like 'donor doesn't matter'",
      },
      {
        id: "a3",
        label: "The Anti-Musk Voter",
        icon: "❌",
        lean: "defense",
        leanColor: "#1e3a8a",
        plaintiffStrike:
          "For-cause if articulates active anti-Musk views. Peremptory in any event.",
        defendantStrike:
          "SEAT - defense gold. Pre-existing distaste for plaintiff personally.",
        description:
          "Strong negative views on Musk - political (Twitter/X behavior, recent political activity), personal (perceived erratic conduct), or principled (concern about wealth/power). Will see lawsuit as Musk's sour grapes regardless of merits.",
        identifyVoirDire: ["v1", "v2"],
        plaintiffResonate: "Almost nothing - entrenched anti-Musk lean",
        plaintiffAlienate:
          "Direct examination of Musk himself; emotional appeals on his behalf",
        defendantResonate:
          "'He left, then he sued' framing, sour-grapes narrative, xAI competitive motive",
        defendantAlienate:
          "Anything excessively pro-OpenAI or defensive of Altman personally",
      },
      {
        id: "a4",
        label: "The AI Safety Believer",
        icon: "🛡️",
        lean: "plaintiff",
        leanColor: "#dc2626",
        plaintiffStrike:
          "SEAT - charitable mission frame aligns with safety priorities.",
        defendantStrike:
          "Strike - credibility on AI risks aligns with mission-betrayal narrative.",
        description:
          "Believes AI development poses risks and that mission-locked nonprofits are essential. Reads AI safety literature, aware of OpenAI's founding charter language, sees PBC conversion as exactly the betrayal predicted.",
        identifyVoirDire: ["v2", "v3"],
        plaintiffResonate:
          "Founding mission language, charter purpose, AI safety implications of for-profit pivot",
        plaintiffAlienate: "Anything that minimizes AI risk concerns",
        defendantResonate: "Almost nothing - entrenched plaintiff lean",
        defendantAlienate: "'AI risk is overblown' framing",
      },
      {
        id: "a5",
        label: "The Tech Skeptic",
        icon: "📵",
        lean: "plaintiff-leaning",
        leanColor: "#f59e0b",
        plaintiffStrike: "SEAT - distrust of tech industry reads charitably.",
        defendantStrike:
          "Soft strike - generally suspicious of tech defendants.",
        description:
          "Distrustful of Big Tech generally. Sees 'mission betrayal' as a familiar pattern. May not be Musk fan but is more anti-Altman/Microsoft than anti-Musk. Older skew (50-70).",
        identifyVoirDire: ["v2", "v3"],
        plaintiffResonate:
          "Tech-industry betrayal pattern, mission-vs-profit narrative, scale of Microsoft involvement",
        plaintiffAlienate: "Excessive technical jargon",
        defendantResonate: "Difficult to convert without active outreach",
        defendantAlienate:
          "Heavy reliance on tech-industry norms as justification",
      },
      {
        id: "a6",
        label: "The Investor / Finance Professional",
        icon: "📈",
        lean: "defense",
        leanColor: "#3b82f6",
        plaintiffStrike:
          "Strike - comfortable with corporate restructuring as normal evolution.",
        defendantStrike:
          "SEAT - sees mission evolution as how mature organizations work.",
        description:
          "VC, PE, banking, or finance professional. Comfortable with corporate restructuring. Sees PBC as legitimate evolution. Wazzan's $135B constructive trust argument may strike as overreach. SOL framework appeals to commercial intuition.",
        identifyVoirDire: ["v4", "v5"],
        plaintiffResonate:
          "Documented founder-representations evidence (specific email language)",
        plaintiffAlienate:
          "Constructive trust valuation as overreach; aggressive damages",
        defendantResonate:
          "SOL framing, sophisticated-donor-knew-the-risks, PBC as legitimate evolution",
        defendantAlienate:
          "Any concession that mission-lock language was binding",
      },
      {
        id: "a7",
        label: "The Pragmatic Centrist",
        icon: "⚖️",
        lean: "unclear",
        leanColor: "#a78bfa",
        plaintiffStrike: "Wildcard. Open to evidence. Closing-driven verdict.",
        defendantStrike: "Wildcard. Equally unpredictable.",
        description:
          "No strong priors on Musk, OpenAI, AI, or charitable trust. Wants to be persuaded by evidence. Often the swing juror. Both sides fight hard for these.",
        identifyVoirDire: ["v3", "v4", "v5"],
        plaintiffResonate:
          "Documentary evidence, narrative arc (founding promise → broken promise → remedy), Brockman journal",
        plaintiffAlienate: "Excessive damages without proportional methodology",
        defendantResonate:
          "SOL clarity, sophisticated-donor framing, formal-trust-document absence",
        defendantAlienate: "Any appearance of hiding facts during cross",
      },
      {
        id: "a8",
        label: "The Retired Professional",
        icon: "📰",
        lean: "mixed",
        leanColor: "#10b981",
        plaintiffStrike:
          "Lean toward seating - fairness and broken-promise frame lands.",
        defendantStrike: "Mixed - may be skeptical of all parties.",
        description:
          "Retired 60-75. News-engaged. Aware of OpenAI saga and Altman board reinstatement. Has formed views from media coverage. Jury duty taken seriously. Will follow careful proof.",
        identifyVoirDire: ["v1", "v3"],
        plaintiffResonate:
          "Documentary chronological narrative, fairness frame, broken-promise framing",
        plaintiffAlienate: "Excessive media-coverage references",
        defendantResonate: "SOL clarity, sophisticated-actor framing",
        defendantAlienate:
          "Anything that looks like obscuring the founding email evidence",
      },
    ],
    juryGrid: [
      {
        seat: 1,
        age: "34",
        occupation: "Software Engineer (mid-career)",
        demographic: "M, Hispanic, BS Comp Sci",
        archetype: "a1",
        archetypeLabel: "Tech Industry Veteran",
        lean: "unclear",
        leanColor: "#a78bfa",
        observations: [
          {
            day: "Day 1",
            note: "Engaged during opening statements both sides. Took notes when Brockman email PX-003 was discussed. Neutral expression.",
            time: "9:45 AM",
          },
          {
            day: "Day 1",
            note: "Looked skeptical when defense counsel described 'startup founder flexibility.' Slight head-shake at 'mission evolution' framing.",
            time: "2:15 PM",
          },
          {
            day: "Day 2",
            note: "Visible interest during Altman direct on founding intent. Wrote heavily during cross on Yup email.",
            time: "11:20 AM",
          },
          {
            day: "Day 2",
            note: "Lost engagement during Wazzan's FIFO methodology - kept checking watch by minute 25 of valuation testimony.",
            time: "3:30 PM",
          },
          {
            day: "Day 3",
            note: "Re-engaged for Brockman journal entry reading. Wrote aggressively during 'we're stealing the nonprofit' testimony.",
            time: "10:15 AM",
          },
        ],
      },
      {
        seat: 2,
        age: "58",
        occupation: "Retired English Teacher, SF Unified",
        demographic: "F, White, MA Education",
        archetype: "a8",
        archetypeLabel: "Retired Professional",
        lean: "plaintiff",
        leanColor: "#ef4444",
        observations: [
          {
            day: "Day 1",
            note: "Strong reaction to founding-email read-aloud. Visible empathy when plaintiff described mission betrayal.",
            time: "10:30 AM",
          },
          {
            day: "Day 1",
            note: "Skeptical of Altman's recorded podcast statements. Crossed arms during defense opening.",
            time: "2:45 PM",
          },
          {
            day: "Day 2",
            note: "Took copious notes during Sutskever testimony (subpoenaed; contempt history). Engaged with the 'I have a bad feeling' email.",
            time: "1:10 PM",
          },
          {
            day: "Day 3",
            note: "Consistently leans forward during plaintiff witnesses. Body language strongly favorable.",
            time: "9:30 AM",
          },
        ],
      },
      {
        seat: 3,
        age: "42",
        occupation: "VC Principal, mid-tier firm",
        demographic: "M, Asian, MBA",
        archetype: "a6",
        archetypeLabel: "Investor / Finance Professional",
        lean: "defense",
        leanColor: "#3b82f6",
        observations: [
          {
            day: "Day 1",
            note: "Took technical notes throughout. Engaged when defense described PBC as 'standard structure.'",
            time: "11:00 AM",
          },
          {
            day: "Day 1",
            note: "Visibly skeptical of Wazzan's $135B constructive-trust valuation.",
            time: "3:15 PM",
          },
          {
            day: "Day 2",
            note: "Asked clarifying question during sidebar (judge denied). Question was about cap table mechanics.",
            time: "2:00 PM",
          },
          {
            day: "Day 3",
            note: "Engaged with SOL discussion. Visible understanding of 'discovery rule' boundaries.",
            time: "11:45 AM",
          },
        ],
      },
      {
        seat: 4,
        age: "29",
        occupation: "Marketing Coordinator, biotech",
        demographic: "F, White, BA Marketing",
        archetype: "a7",
        archetypeLabel: "Pragmatic Centrist",
        lean: "unclear",
        leanColor: "#a78bfa",
        observations: [
          {
            day: "Day 1",
            note: "Hard to read. Took moderate notes throughout opening. No strong reactions.",
            time: "10:00 AM",
          },
          {
            day: "Day 1",
            note: "Looked confused during constructive trust explanation. Took fewer notes during legal argument.",
            time: "2:30 PM",
          },
          {
            day: "Day 2",
            note: "Engaged during Brockman journal - clearly the most narrative-driven evidence. Strong reaction to 'wrong to steal nonprofit' line.",
            time: "11:00 AM",
          },
          {
            day: "Day 2",
            note: "Crossed arms during plaintiff Sonner argument (opposite of expected - possible defense lean signal).",
            time: "3:45 PM",
          },
          {
            day: "Day 3",
            note: "Yawned during Wazzan re-examination on FIFO. Closing should NOT lead with damages methodology for this juror.",
            time: "2:20 PM",
          },
        ],
      },
      {
        seat: 5,
        age: "51",
        occupation: "Hospital Administrator",
        demographic: "F, Black, MHA",
        archetype: "a2",
        archetypeLabel: "Charitable Donor",
        lean: "plaintiff",
        leanColor: "#ef4444",
        observations: [
          {
            day: "Day 1",
            note: "Strong attentiveness from opening. Engaged with founding-email evidence.",
            time: "9:30 AM",
          },
          {
            day: "Day 2",
            note: "Visibly affected by 'Yup' email reading (Altman's one-word reply confirming for-profit conversion). Wrote heavily.",
            time: "10:45 AM",
          },
          {
            day: "Day 2",
            note: "During defense cross of Musk on Twitter posts, body language remained sympathetic to plaintiff. Did not shift on impeachment.",
            time: "3:00 PM",
          },
          {
            day: "Day 3",
            note: "Strong reaction to Brockman journal - visible nodding. Strong plaintiff anchor.",
            time: "10:30 AM",
          },
        ],
      },
      {
        seat: 6,
        age: "36",
        occupation: "Self-employed Contractor",
        demographic: "M, White, HS+trade",
        archetype: "a5",
        archetypeLabel: "Tech Skeptic",
        lean: "plaintiff-leaning",
        leanColor: "#f59e0b",
        observations: [
          {
            day: "Day 1",
            note: "Skeptical body language during defense opening. Engaged with plaintiff narrative.",
            time: "11:15 AM",
          },
          {
            day: "Day 1",
            note: "Took notes when Microsoft's $13B investment was discussed. Visible reaction to scale of equity.",
            time: "2:00 PM",
          },
          {
            day: "Day 2",
            note: "Engaged with Sutskever testimony. Some skepticism about big-tech motives across the board.",
            time: "1:45 PM",
          },
          {
            day: "Day 3",
            note: "Continued plaintiff lean. Less reactive than Seat 5 but consistent.",
            time: "11:00 AM",
          },
        ],
      },
      {
        seat: 7,
        age: "45",
        occupation: "Litigation Paralegal",
        demographic: "F, Latina, BA Pre-Law",
        archetype: "a7",
        archetypeLabel: "Pragmatic Centrist",
        lean: "plaintiff-leaning",
        leanColor: "#f59e0b",
        observations: [
          {
            day: "Day 1",
            note: "Detailed notes throughout. Legal terminology comfort high. Tracking exhibits carefully.",
            time: "10:15 AM",
          },
          {
            day: "Day 2",
            note: "Sustained engagement on 'Yup' email. Took notes on the timestamp and recipient list.",
            time: "11:30 AM",
          },
          {
            day: "Day 2",
            note: "Engaged with the formal-trust-vs-informal-trust distinction during Schizer direct. Followed nuance.",
            time: "2:30 PM",
          },
          {
            day: "Day 3",
            note: "Strong attention to Brockman journal. Wrote 'wrong to steal nonprofit' phrase verbatim. Plaintiff-leaning signal.",
            time: "10:00 AM",
          },
        ],
      },
      {
        seat: 8,
        age: "61",
        occupation: "Retired Aerospace Engineer",
        demographic: "M, White, MS Engineering",
        archetype: "a3",
        archetypeLabel: "Anti-Musk Voter",
        lean: "defense",
        leanColor: "#1e3a8a",
        observations: [
          {
            day: "Day 1",
            note: "Stiff body language during plaintiff opening. Visible discomfort with Musk references.",
            time: "10:00 AM",
          },
          {
            day: "Day 1",
            note: "Engaged actively during defense opening. Took notes on Altman's testimony preview.",
            time: "2:30 PM",
          },
          {
            day: "Day 2",
            note: "Skeptical reaction to Brockman journal - appeared to discount as 'personal musing.' Defense angle landing.",
            time: "11:00 AM",
          },
          {
            day: "Day 2",
            note: "Visibly engaged with SOL argument. Took notes on 2018-2019 timeline.",
            time: "3:15 PM",
          },
          {
            day: "Day 3",
            note: "Most defense-anchored juror on panel. Closing must address SOL clearly to compete.",
            time: "10:30 AM",
          },
        ],
      },
      {
        seat: 9,
        age: "39",
        occupation: "Public Interest Attorney (legal aid)",
        demographic: "F, White, JD",
        archetype: "a4",
        archetypeLabel: "AI Safety Believer",
        lean: "plaintiff",
        leanColor: "#dc2626",
        observations: [
          {
            day: "Day 1",
            note: "Legal training visible - took methodical notes on each cause of action. Engaged with charitable trust framework.",
            time: "9:45 AM",
          },
          {
            day: "Day 1",
            note: "Strong reaction to founding charter language. Underlined 'mission' in her notes (visible from co-counsel angle).",
            time: "2:00 PM",
          },
          {
            day: "Day 2",
            note: "Followed §17510.8 charitable solicitation argument carefully. Asked judge for clarification (denied at sidebar).",
            time: "10:30 AM",
          },
          {
            day: "Day 3",
            note: "Strongest anchor for plaintiff theory. Will be plaintiff's closing-argument target voice in deliberation.",
            time: "11:15 AM",
          },
        ],
      },
    ],
    closingCalibration: {
      summary:
        "Day 3 observations confirm 5 plaintiff-leaning, 2 defense-leaning, 2 unclear. Plaintiff's closing should LEAD with the equitable narrative (Brockman journal, founding promise) and use damages methodology only as quantification, not as anchor. Defense closing must address SOL with concrete dates and the 'sophisticated donor' framing.",
      themes: [
        {
          id: "t1",
          label: "Brockman Journal - 'wrong to steal nonprofit'",
          sponsor: "plaintiff",
          landingScore: 8.4,
          jurorEngagement: [
            { seat: 1, score: 9, note: "Wrote aggressively" },
            { seat: 2, score: 9, note: "Strong empathy reaction" },
            { seat: 3, score: 5, note: "Neutral, technical interest" },
            { seat: 4, score: 8, note: "Strongest reaction of any evidence" },
            { seat: 5, score: 9, note: "Visible nodding" },
            { seat: 6, score: 8, note: "Engaged" },
            { seat: 7, score: 9, note: "Wrote phrase verbatim" },
            { seat: 8, score: 3, note: "Discounted as 'personal musing'" },
            { seat: 9, score: 9, note: "Anchor evidence for theory" },
          ],
          recommendation:
            "LEAD WITH THIS in plaintiff closing. 7 of 9 jurors had strong reaction. Even the swing juror (Seat 4) reacted strongest to this evidence. Defense closing must reframe as personal venting - has not landed.",
        },
        {
          id: "t2",
          label: "Founding Email PX-003 + 'Yup' Email",
          sponsor: "plaintiff",
          landingScore: 7.8,
          jurorEngagement: [
            { seat: 1, score: 7, note: "Took notes" },
            { seat: 2, score: 8, note: "Visible reaction" },
            { seat: 3, score: 6, note: "Technical interest" },
            { seat: 4, score: 6, note: "Comprehended" },
            { seat: 5, score: 9, note: "Visibly affected" },
            { seat: 6, score: 7, note: "Engaged" },
            { seat: 7, score: 9, note: "Detail tracking" },
            { seat: 8, score: 5, note: "Less affected" },
            { seat: 9, score: 9, note: "Anchor evidence" },
          ],
          recommendation:
            "Strong second theme. Use as supporting documentation for the broken-promise narrative. The 'Yup' email - Altman's one-word reply confirming for-profit conversion - is particularly potent. Use the screen exhibit prominently.",
        },
        {
          id: "t3",
          label: "Wazzan FIFO Damages Methodology",
          sponsor: "plaintiff",
          landingScore: 5.4,
          jurorEngagement: [
            { seat: 1, score: 4, note: "Disengaged at minute 25" },
            { seat: 2, score: 5, note: "Tracked but not strongly" },
            { seat: 3, score: 8, note: "Comfortable with valuation" },
            { seat: 4, score: 3, note: "Yawned during re-examination" },
            { seat: 5, score: 5, note: "Followed but not anchor" },
            { seat: 6, score: 5, note: "Some skepticism" },
            { seat: 7, score: 7, note: "Comprehended methodology" },
            { seat: 8, score: 6, note: "Followed technical points" },
            { seat: 9, score: 7, note: "Tracked carefully" },
          ],
          recommendation:
            "DO NOT LEAD CLOSING WITH THIS. Use as quantification AFTER establishing breach narrative. Seat 4 (swing juror) actively disengaged. Lead with equity, follow with quantification.",
        },
        {
          id: "t4",
          label: "SOL - 2018-2019 Timeline",
          sponsor: "defendant",
          landingScore: 6.1,
          jurorEngagement: [
            { seat: 1, score: 5, note: "Comprehended but unmoved" },
            { seat: 2, score: 3, note: "Resistant to time-bar framing" },
            { seat: 3, score: 8, note: "Sophisticated comfort with SOL" },
            { seat: 4, score: 6, note: "Followed timeline" },
            { seat: 5, score: 3, note: "Resistant" },
            { seat: 6, score: 5, note: "Mixed" },
            { seat: 7, score: 7, note: "Detail-tracked" },
            { seat: 8, score: 9, note: "Strongly anchored" },
            { seat: 9, score: 5, note: "Engaged with discovery rule" },
          ],
          recommendation:
            "Defense's strongest argument. Must address concrete dates. Plaintiff should preempt with discovery rule + continuing-violation framing. The 5 plaintiff-leaning jurors are resisting; defense needs Seat 4 (swing) on this.",
        },
        {
          id: "t5",
          label: "Sophisticated Donor - Musk Knew the Risks",
          sponsor: "defendant",
          landingScore: 4.8,
          jurorEngagement: [
            { seat: 1, score: 5, note: "Neutral" },
            { seat: 2, score: 3, note: "Sympathetic to donor protection" },
            { seat: 3, score: 7, note: "Aligned with this framing" },
            { seat: 4, score: 5, note: "Followed argument" },
            { seat: 5, score: 2, note: "Strong donor-rights anchor" },
            { seat: 6, score: 4, note: "Moderate skepticism" },
            { seat: 7, score: 6, note: "Tracked but unclear" },
            { seat: 8, score: 8, note: "Anchored with this framing" },
            { seat: 9, score: 3, note: "Donor-rights framework" },
          ],
          recommendation:
            "Defense secondary theme. Limited landing. Charitable Donor archetype (Seat 5) is the firewall here - strong donor-rights anchor. Defense should focus on SOL primary; sophisticated-donor secondary.",
        },
      ],
      recommendedClosing: {
        plaintiff:
          "OPEN with Brockman journal - 7 of 9 jurors had strong positive reaction. Then founding emails (PX-003 + 'Yup') - 6 of 9 strong. Then bridge to charitable trust framework (Schizer testimony). Use Wazzan only for quantification at the end, brief and visual. PREEMPT defense SOL with discovery-rule narrative - emphasize October 2025 PBC announcement as latest discovery point. Total target time: 75 minutes.",
        defendant:
          "OPEN with SOL - your strongest theme (Seat 8 anchor + 4 of 9 receptive). Build on '2018 sophisticated donor knew the risks' (Seat 3 anchor). Reframe Brockman journal as personal venting AT THE TOP - do not concede ground. Use Hemel testimony to undermine charitable trust formation. Avoid lengthy technical defense of PBC structure - lost Seats 1, 2, 5, 6, 7, 9 already. Total target time: 75 minutes.",
      },
    },
  },
  neuro: {
    caseTitle: "Neurocentria v. Mando - Patent Infringement",
    trialDate: "TBD (post-Markman)",
    jurySeated: false,
    panelSize: 0,
    theory:
      "Plaintiff: Neurocentria's '061 patent on novel L-threonate chelation chemistry; Mando manufactured infringing products in unauthorized territory. Defendant: §101 ineligibility (natural phenomenon), prior art invalidity (CN1200366A, MIT provisional), licensee estoppel.",
    voirDire: [
      {
        id: "v1",
        category: "Patent & Innovation Comprehension",
        weight: "critical",
        questions: [
          {
            q: "Have you, a family member, or close friend ever held a patent or worked on patent applications?",
            rationale:
              "Patent literacy varies enormously. Low-literacy jurors may default to 'big company wins.'",
            plaintiffSignal:
              "Inventor or co-inventor → seat (strongest plaintiff lean for novel-invention narrative)",
            defendantSignal:
              "Patent attorney or skeptic of patent system → seat (resists infringement framing)",
          },
          {
            q: "Do you believe the U.S. patent system generally rewards innovation or generally enables litigation?",
            rationale: "Bedrock view affects how they read every claim.",
            plaintiffSignal: "'Rewards innovation' → seat",
            defendantSignal: "'Litigation system' → seat",
          },
        ],
      },
      {
        id: "v2",
        category: "Pharma & Generic Drug Attitudes",
        weight: "high",
        questions: [
          {
            q: "What is your view of the pharmaceutical industry?",
            rationale: "Pharma views shape the brand-vs-generic narrative.",
            plaintiffSignal:
              "Pro-innovation, R&D-respecting → seat (favorable for patent holder)",
            defendantSignal:
              "Skeptical of pharma pricing / pro-generic → seat (favorable)",
          },
          {
            q: "Have you used generic medications, and what is your experience?",
            rationale: "Personal experience with generics affects intuition.",
            plaintiffSignal: "Mixed experience or brand preference → seat",
            defendantSignal:
              "Strong positive generic experience → seat (favorable)",
          },
        ],
      },
      {
        id: "v3",
        category: "Chemistry & Scientific Comfort",
        weight: "medium",
        questions: [
          {
            q: "Did you take chemistry in high school or college? How comfortable are you with it?",
            rationale:
              "L-threonate chelation requires basic chem comprehension.",
            plaintiffSignal:
              "Comfortable with chemistry → seat (follows technical narrative)",
            defendantSignal:
              "Strong chem background but Daubert-skeptical → seat",
          },
          {
            q: "Are you comfortable with technical expert testimony involving chemical formulas, molecular structures, or biological mechanisms?",
            rationale: "Cases with heavy technical content require comfort.",
            plaintiffSignal: "Comfortable, willing to learn → seat",
            defendantSignal:
              "Tendency to defer to credentials → seat (depends on stronger expert)",
          },
        ],
      },
    ],
    archetypes: [
      {
        id: "a1",
        label: "The Engineer / Scientist",
        icon: "🔬",
        lean: "mixed",
        leanColor: "#6b7280",
        plaintiffStrike:
          "Mixed - depends on segment. Bench scientist sympathetic to inventor → seat. Process engineer pragmatic → strike.",
        defendantStrike:
          "Mirror - process engineer who's seen 'minor variations' as not-truly-novel → seat for defense.",
        description:
          "Engineer or scientist by training, 30-60. Comfortable with technical evidence. Will follow expert testimony carefully. Lean depends on whether they identify as inventor or as user of existing tech.",
        identifyVoirDire: ["v1", "v3"],
        plaintiffResonate:
          "Documentary novelty evidence, lab notebooks, prosecution history showing examiner approval",
        plaintiffAlienate: "Overstating novelty against clear prior art",
        defendantResonate:
          "§101 natural-phenomenon framing, prior art combinations showing obviousness",
        defendantAlienate:
          "Aggressive minimization of plaintiff's R&D investment",
      },
      {
        id: "a2",
        label: "The Healthcare Worker",
        icon: "⚕️",
        lean: "plaintiff-leaning",
        leanColor: "#f59e0b",
        plaintiffStrike:
          "SEAT - sees patient outcomes as mattering, attuned to innovation incentives.",
        defendantStrike:
          "Mixed - may also see drug pricing as generic-friendly factor.",
        description:
          "Nurse, PA, pharmacist, allied health worker. Sees patient outcomes daily. Generally pro-innovation IF the innovation has clinical merit. May be cost-sensitive and generic-sympathetic if pricing seen as predatory.",
        identifyVoirDire: ["v2", "v3"],
        plaintiffResonate:
          "Clinical merit of L-threonate chelation, patient-outcome data, cognitive enhancement evidence",
        plaintiffAlienate:
          "Aggressive damages without clinical-utility framing",
        defendantResonate:
          "Generic accessibility, drug-pricing critique, comparable cheaper alternatives",
        defendantAlienate: "Anything that minimizes patient access concerns",
      },
      {
        id: "a3",
        label: "The Generic Drug User",
        icon: "💊",
        lean: "defense",
        leanColor: "#3b82f6",
        plaintiffStrike:
          "Strike - pre-formed preference for generic accessibility.",
        defendantStrike:
          "SEAT - natural defense lean. Generic-friendly intuition.",
        description:
          "Patient or family member with chronic condition managed by generics. Cost-sensitive. Sees patent enforcement as raising drug prices. Skeptical of brand-protection framing.",
        identifyVoirDire: ["v2"],
        plaintiffResonate:
          "Specific R&D cost evidence ($XX million invested in '061 development)",
        plaintiffAlienate:
          "Anything that reads as 'patient access doesn't matter'",
        defendantResonate:
          "Generic accessibility, anti-monopoly framing, prior art showing obviousness",
        defendantAlienate:
          "Aggressive defense overreach (looks like minimizing all patent rights)",
      },
      {
        id: "a4",
        label: "The Innovation Believer",
        icon: "💡",
        lean: "plaintiff",
        leanColor: "#ef4444",
        plaintiffStrike:
          "SEAT - natural plaintiff. Inventor-rights framing lands.",
        defendantStrike: "Strike - patent-rights-respecting baseline.",
        description:
          "Inventor, entrepreneur, IP attorney, or strong patent advocate. Believes patent system rewards risk-taking. Sees infringement as theft of years of R&D investment.",
        identifyVoirDire: ["v1", "v2"],
        plaintiffResonate:
          "R&D investment narrative, lab notebooks, prosecution history, willful-infringement evidence",
        plaintiffAlienate: "Excessive damages without methodology",
        defendantResonate: "Almost nothing - entrenched patent-rights lean",
        defendantAlienate:
          "Standard prior-art arguments solidify plaintiff lean",
      },
      {
        id: "a5",
        label: "The Patent Skeptic / IP Critic",
        icon: "📉",
        lean: "defense",
        leanColor: "#1e3a8a",
        plaintiffStrike:
          "For-cause if articulates anti-patent views. Strike in any event.",
        defendantStrike:
          "SEAT - defense gold. Sees patents as litigation enablers.",
        description:
          "Reads tech-policy media. Sees patent system as broken, especially around 'minor variations.' May have personal experience with patent threats. Strongly favors §101 invalidity arguments.",
        identifyVoirDire: ["v1"],
        plaintiffResonate: "Almost nothing - entrenched anti-patent lean",
        plaintiffAlienate:
          "Any technical-novelty framing - they discount automatically",
        defendantResonate:
          "§101 natural-phenomenon framing, prior art breadth, patent-troll narrative",
        defendantAlienate: "Heavy-handed corporate-defense rhetoric",
      },
      {
        id: "a6",
        label: "The Pragmatic Centrist",
        icon: "⚖️",
        lean: "unclear",
        leanColor: "#a78bfa",
        plaintiffStrike: "Wildcard. Open to evidence. Closing-driven verdict.",
        defendantStrike: "Wildcard. Equally unpredictable.",
        description:
          "No strong priors on patents, pharma, or innovation policy. Wants to be persuaded by evidence. Often the swing juror.",
        identifyVoirDire: ["v1", "v2", "v3"],
        plaintiffResonate:
          "Clear narrative of investment-and-theft, documentary evidence",
        plaintiffAlienate: "Excessive technical depth without translation",
        defendantResonate:
          "Prior art clarity, scientific simplicity of the 'natural phenomenon' framing",
        defendantAlienate:
          "Aggressive licensee-estoppel arguments without context",
      },
    ],
    juryGrid: [],
    closingCalibration: null,
  },
};

const EXPERT_BATTLEFIELD_DATA = {
  ...ILE_K_EXPERTS,
  juul: {
    caseTitle: "Seliavski v. JUUL MDL - Antitrust Track",
    status: "pre-disclosure",
    statusLabel: "PRE-DISCLOSURE",
    countdown: {
      event: "Trial-specific expert disclosure",
      date: "July 15, 2026",
      days: 71,
    },
    gapNote:
      "Class certification grant (Feb 26, 2026, Dkt.635) preserved both sides' class-cert experts through methodology challenges. Trial-specific experts must be disclosed by July 15, 2026 - 71 days remaining. Without disclosed trial witnesses, neither side has Daubert briefing scheduled before the September 28, 2026 trial date. Disclose within 30 days to allow full Daubert practice.",
    issues: [
      {
        id: "antitrust-impact",
        label: "Antitrust Impact Methodology",
        importance: "critical",
        importanceColor: "#ef4444",
        plaintiffExpert: {
          name: "DAP Class-Certification Expert",
          credentials:
            "Economic impact methodology · class certification phase",
          coreOpinion:
            "Class-wide impact methodology established at class certification. Direct purchaser plaintiffs suffered overcharges traceable to JLI/Altria coordination. Damages methodology supports treble recovery on aggregate basis.",
          daubertStatus: "survived",
          daubertColor: "#10b981",
          daubertRuling:
            "Class certification grant (Feb 26, 2026, Dkt.635) confirmed methodology survived class-cert Daubert challenge",
          vulnerabilityScore: 5,
          expanded: {
            admitted: [
              "Class-cert expert report - impact methodology",
              "Class-cert expert report - damages framework",
            ],
            excluded: [],
            ruling:
              "Class certification grant (Dkt.635, Feb 26, 2026): The court found that DAP plaintiffs' impact methodology survives class-certification Daubert review. Methodology is preserved for trial-stage application.",
            crossAngles: [
              "Class-cert methodology is preliminary; trial-specific application requires additional disclosure",
              "Defense will challenge methodology rigor at trial-stage Daubert",
              "Aggregate vs. individualized damages question still in play",
            ],
            redirectPrep: [
              "Methodology survived rigorous class-cert review under Hydrogen Peroxide standard",
              "Trial application is straightforward extension of class-cert framework",
              "Aggregate damages are appropriate where overcharge is class-wide",
            ],
          },
        },
        defendantExpert: {
          name: "B. Edwards (defense class-cert)",
          credentials: "Economic methodology expert · defense",
          coreOpinion:
            "Class-wide impact methodology is unsuitable for individualized damages. Plaintiffs' methodology cannot reliably distinguish JLI-attributable harm from market-wide factors and lawful Altria conduct.",
          daubertStatus: "survived",
          daubertColor: "#10b981",
          daubertRuling:
            "Defense expert opinion preserved through class-certification phase",
          vulnerabilityScore: 5,
          expanded: {
            admitted: ["Edwards class-cert opinion preserved for trial use"],
            excluded: [],
            ruling:
              "Defense expert opinion preserved through class-certification phase. No exclusion of Edwards's methodology.",
            crossAngles: [
              "Edwards's individualized-damages framing was rejected at class-cert stage",
              "His market-factors analysis ignores JLI-Altria specific coordination evidence (internal emails, distribution agreements)",
              "Edwards has not testified in a successful price-fixing class trial",
            ],
            redirectPrep: [
              "Court already considered and rejected Edwards's class-cert challenges",
              "Coordination evidence is specific to JLI-Altria, not market-wide",
              "Aggregate damages are not the same as individual claims",
            ],
          },
        },
        aiAssessment:
          "Both sides preserved their class-cert experts through certification grant. Trial-specific experts are not yet disclosed - disclosure deadline July 15, 2026. The Edwards/DAP class-cert testimony established the methodology framework but neither side has named the trial expert who will testify in front of the jury. This is the most material expert decision pending in the case. Whichever side fields the more credible courtroom expert will likely control the impact narrative.",
        juryInstructionBattle:
          "Standard antitrust impact and treble damages instructions. Specific methodology framing depends on trial expert disclosures. Plaintiff seeks 'aggregate impact' instruction; defense seeks 'individualized harm required' framing. Outcome turns on Orrick's read of the class-cert record.",
        battlegroundScore: {
          plaintiffStrength: 6,
          defendantStrength: 5,
          leverage: "balanced",
        },
      },
    ],
  },
  musk: {
    caseTitle: "Musk v. Altman - Charitable Trust Trial",
    status: "trial-in-progress",
    statusLabel: "TRIAL DAY 3",
    countdown: null,
    issues: [
      {
        id: "trust-creation",
        label: "Charitable Trust Creation",
        importance: "critical",
        importanceColor: "#ef4444",
        plaintiffExpert: {
          name: "David M. Schizer",
          credentials: "Columbia Law Dean Emeritus · charitable trust scholar",
          coreOpinion:
            "Musk's contributions and Altman's representations created an informal charitable trust under California law. Subjective donor intent + specific representations + course of conduct establish trust formation without formal deed. The 2015-2017 founding emails (PX-003) and the public mission statements satisfy the elements.",
          daubertStatus: "survived",
          daubertColor: "#10b981",
          daubertRuling: "PTO No. 4, Dkt.398 - opinion admitted in full",
          vulnerabilityScore: 3,
          expanded: {
            admitted: [
              "Schizer Opinion ¶¶ 1-12 (trust formation framework)",
              "Schizer Opinion ¶¶ 13-22 (CA precedent application)",
              "Schizer Opinion ¶¶ 23-30 (donor-representations doctrine)",
              "Schizer Reply Declaration ¶¶ 1-15 (responding to Hemel)",
            ],
            excluded: [],
            ruling:
              "PTO No. 4 (Dkt.398): 'Schizer's testimony is grounded in established California trust law and applies recognized doctrinal frameworks. The opinion is admitted in full subject to vigorous cross-examination on application to the facts.'",
            crossAngles: [
              "No California case has imposed an informal trust on a corporate donation of this scale",
              "Schizer's framework relies on individual-donor cases, not institutional contributions",
              "No formal trust documents in record despite sophisticated parties",
              "Schizer has not previously testified in a charitable-trust case at this scale",
            ],
            redirectPrep: [
              "Reaffirm informal-trust doctrine: cite Estate of Heggstad and progeny",
              "Explain why corporate scale doesn't change trust-formation elements",
              "Walk through founding email PX-003 as 'representations' element",
              "Schizer's academic stature and Dean Emeritus credentials",
            ],
          },
        },
        defendantExpert: {
          name: "Daniel J. Hemel",
          credentials: "NYU Law Professor · corporate governance scholar",
          coreOpinion:
            "No charitable trust was formed. Trust creation requires identifiable beneficiaries, definite charitable purpose, and trustee acceptance - none satisfied by 2015-2017 email exchanges. Board approval and corporate-governance documents control. Musk's contributions are donations to a corporation, not to a trust.",
          daubertStatus: "survived",
          daubertColor: "#10b981",
          daubertRuling: "PTO No. 4, Dkt.398 - opinion admitted in full",
          vulnerabilityScore: 4,
          expanded: {
            admitted: [
              "Hemel Opinion ¶¶ 1-15 (corporate governance framework)",
              "Hemel Opinion ¶¶ 16-25 (trust formation requirements)",
              "Hemel Reply Declaration ¶¶ 1-12 (responding to Schizer)",
            ],
            excluded: [],
            ruling:
              "PTO No. 4 (Dkt.398): Hemel opinion admitted in full. Court noted the genuine doctrinal dispute and declined to adjudicate it on Daubert.",
            crossAngles: [
              "Hemel concedes that informal trusts can form under CA law",
              "Hemel's framework would deny donor protection in any informal context",
              "Hemel's analysis ignores Altman's specific representations to Musk about mission",
              "Hemel has limited charitable-trust scholarship in publication record",
            ],
            redirectPrep: [
              "Distinguish corporate-governance from donor-protection law",
              "Reaffirm board-approval-controls framework under Cal. Corp. Code §5210",
              "Cite Caremark for board fiduciary scope",
              "Trust formation requires more than donor expectation",
            ],
          },
        },
        aiAssessment:
          "Schizer has the California precedent on informal trust creation. Hemel has the corporate governance argument and the board approval defense. The battleground is whether Musk's subjective intent combined with Altman's representations is legally sufficient without a formal deed of trust. Schizer's academic credentials and Dean Emeritus status give him credibility; Hemel's corporate-law background plays into the defense narrative that nonprofit boards, not donors, control organizational decisions. Watch for the 'Brockman journal' admission to be the swing factor - if jurors credit it as institutional acknowledgment, Schizer's framework lands; if defense reframes as personal musing, Hemel's institutional-control framework lands.",
        juryInstructionBattle:
          "Plaintiff seeks instruction modeled on CACI 4900 (Trust Creation Elements) - emphasizes subjective intent and donor representations. Defendant seeks instruction modeled on Cal. Corp. Code §5210 - emphasizes board control. Compromise instruction likely combines both with a 'totality of circumstances' framework. Charging conference will be heavily contested on this point.",
        battlegroundScore: {
          plaintiffStrength: 7,
          defendantStrength: 7,
          leverage: "balanced",
        },
      },
      {
        id: "fifo-damages",
        label: "FIFO Tracing & Constructive Trust Damages",
        importance: "critical",
        importanceColor: "#ef4444",
        plaintiffExpert: {
          name: "Frederick C. Wazzan",
          credentials: "Damages economist · valuation expert · UCLA Anderson",
          coreOpinion:
            "Apply FIFO tracing to Microsoft's $13B investment to identify the proceeds of the breach. Constructive trust on Microsoft's ~27% equity stake (~$135B at October 2025 mark) is the appropriate equitable remedy. Methodology supports recovery without requiring specific damages quantification.",
          daubertStatus: "partial",
          daubertColor: "#f59e0b",
          daubertRuling:
            "PTO No. 2, Dkt.452 - Opinions 3-6 admitted, Opinions 1-2 excluded",
          vulnerabilityScore: 6,
          expanded: {
            admitted: [
              "Opinion 3: FIFO tracing methodology applied to Microsoft tranches",
              "Opinion 4: ~27% Microsoft equity attributable to traced funds",
              "Opinion 5: Valuation of Microsoft equity at $135B (October 2025 mark)",
              "Opinion 6: Alternative valuation under DCF",
            ],
            excluded: [
              "Opinion 1: Reasonable royalty methodology - court found speculative as applied",
              "Opinion 2: Lost-investor-opportunity damages - found inadmissible under Daubert",
            ],
            ruling:
              "PTO No. 2, Dkt.452 (Apr 4, 2026): 'Opinions 1 and 2 are excluded as based on speculative assumptions about counterfactual investor behavior in unprecedented market conditions. Opinions 3 through 6 employ accepted financial methodologies and are admitted, subject to vigorous cross-examination on assumptions.'",
            crossAngles: [
              "FIFO is one of multiple permissible tracing methods - why not LIFO or pro-rata?",
              "Microsoft's investment structure (preferred returns, anti-dilution) complicates clean tracing",
              "Constructive trust valuation depends on highly volatile equity mark",
              "Wazzan's exclusion of two opinions undermines remaining four - methodological reliability question",
              "Wazzan has not previously testified in a constructive-trust valuation case at this scale",
            ],
            redirectPrep: [
              "FIFO is the established equitable tracing method (cite Cunningham v. Brown, 265 U.S. 1)",
              "Microsoft's investment structure does not preclude FIFO - explain mechanics simply for jury",
              "Volatility argument cuts both ways - court may peg valuation to specific date",
              "Defend remaining opinions on their own merits, not by reference to excluded ones",
              "Distinguish counterfactual damages (Opinions 1-2) from tracing damages (Opinions 3-6)",
            ],
          },
        },
        defendantExpert: {
          name: "Robert Dudney",
          credentials: "Forensic accountant · valuation expert",
          coreOpinion:
            "FIFO tracing is one method among several; LIFO and pro-rata yield materially different results. Even if FIFO applies, the constructive trust theory ignores Microsoft's bona-fide-purchaser status and OpenAI's intervening contributions of significant value (R&D, GPT models, brand).",
          daubertStatus: "survived",
          daubertColor: "#10b981",
          daubertRuling: "PTO No. 2, Dkt.452 - opinion admitted in full",
          vulnerabilityScore: 5,
          expanded: {
            admitted: [
              "Dudney Opinion ¶¶ 1-30 (alternative tracing methodologies and BFP defense)",
              "Dudney Opinion ¶¶ 31-45 (intervening-value analysis)",
              "Dudney Reply ¶¶ 1-15 (responding to Wazzan Opinions 3-6)",
            ],
            excluded: [],
            ruling:
              "PTO No. 2, Dkt.452 (Apr 4, 2026): Dudney's testimony is grounded in standard forensic accounting methodologies. Admitted in full.",
            crossAngles: [
              "Dudney has never testified to a constructive trust valuation in a charitable-trust case",
              "His pro-rata methodology benefits the breaching party in proportion to their breach",
              "BFP defense requires actual ignorance - Microsoft's diligence files arguably show notice",
              "Intervening-value analysis double-counts OpenAI's pre-Microsoft trajectory",
            ],
            redirectPrep: [
              "FIFO is one of three equally valid methods - let jury choose",
              "Distinguish BFP doctrine: applies to Microsoft, not to Altman",
              "Pro-rata is legally defensible and reflects equity",
              "Intervening value is the primary defense to constructive trust at this scale",
            ],
          },
        },
        aiAssessment:
          "Wazzan's exclusion of Opinions 1 and 2 is the defense's most exploitable point. The remaining four opinions employ defensible methodology, but the exclusion will be argued to the jury as proof of methodological unreliability. Dudney's testimony positions FIFO as 'one of several' methods - this framing wins if the jury is uncomfortable with the $135B valuation. Plaintiff's strategy: lead the jury to FIFO before defense raises alternatives. Defense's strategy: present LIFO and pro-rata as equally legitimate to introduce reasonable doubt about the methodology choice. Day 3 jury observations show Seat 4 (swing) yawning during Wazzan's re-examination - Wazzan has not landed with the panel.",
        juryInstructionBattle:
          "Plaintiff seeks instruction that FIFO is the established equitable tracing method and that BFP defense requires actual ignorance, not just absence of fraud. Defense seeks instruction listing FIFO, LIFO, and pro-rata as equally permissible methods, leaving methodology choice to the jury. The 'jury chooses the method' instruction would be a defense win - likely contested heavily at charging conference. Watch for compromise instruction with totality-of-circumstances framing.",
        battlegroundScore: {
          plaintiffStrength: 5,
          defendantStrength: 7,
          leverage: "defendant",
        },
      },
      {
        id: "17510-8",
        label: "§17510.8 Charitable Solicitations Liability",
        importance: "high",
        importanceColor: "#f59e0b",
        plaintiffExpert: {
          name: "Schizer (supplemental)",
          credentials:
            "Columbia Law Dean Emeritus · supplemental opinion on §17510.8",
          coreOpinion:
            "Cal. Bus. & Prof. Code §17510.8 imposes strict liability for false or misleading representations in charitable solicitations. The 2015-2017 Altman emails to Musk constitute solicitations under the statute; representations about nonprofit mission were false or misleading at the time made or became false through the LP/PBC pivot.",
          daubertStatus: "pending",
          daubertColor: "#a78bfa",
          daubertRuling: "Briefed April 30, 2026 - no ruling yet",
          vulnerabilityScore: 6,
          expanded: {
            admitted: [],
            excluded: [],
            ruling:
              "Defense Daubert motion filed April 24, 2026 (Dkt.467). Plaintiff opposition filed April 30, 2026 (Dkt.471). Reply expected within 14 days. Hearing not yet scheduled. Trial may proceed without ruling on this issue if §17510.8 testimony is severable from the trust-creation case.",
            crossAngles: [
              "§17510.8 has limited case law applying to large-scale corporate donations",
              "Whether 2015 communications constitute 'solicitations' under the statute is contested",
              "Strict-liability framing requires proof of false-or-misleading representations at the time made - what was the time?",
            ],
            redirectPrep: [
              "§17510.8 was enacted to protect donors generally - apply broadly",
              "Each communication individually qualifies as a solicitation under plain text",
              "Falsity is measured by representation accuracy, not subjective intent",
              "Strict liability cuts off scienter defenses",
            ],
          },
        },
        defendantExpert: {
          name: "Hemel (supplemental)",
          credentials: "NYU Law Professor · supplemental opinion on §17510.8",
          coreOpinion:
            "§17510.8 does not apply because the 2015-2017 emails are not charitable solicitations within the meaning of the statute. Even if the statute applies, the representations were not false or misleading at the time made - the LP/PBC pivot was a later development that the statute does not retroactively cover.",
          daubertStatus: "pending",
          daubertColor: "#a78bfa",
          daubertRuling: "Briefed April 30, 2026 - no ruling yet",
          vulnerabilityScore: 5,
          expanded: {
            admitted: [],
            excluded: [],
            ruling:
              "Pending. Both sides have filed Daubert briefs; ruling not yet issued. Trial may proceed pre-ruling if the §17510.8 theory is severed.",
            crossAngles: [
              "Hemel's narrow reading of 'solicitation' contradicts plain text",
              "His 'no falsity at time made' argument requires defining a baseline that's contested",
              "Statutory strict-liability frameworks cut against time-of-statement defense",
            ],
            redirectPrep: [
              "§17510.8 'solicitation' definition tracks federal charitable-solicitation law",
              "Falsity at time made is a question of fact, not law",
              "Statutory strict liability does not preclude a time-of-falsity defense",
              "Statute targets representations to donors, not all communications",
            ],
          },
        },
        aiAssessment:
          "This issue is unresolved at trial entry. Both sides have filed Daubert briefs but no ruling. The §17510.8 strict-liability framing is potentially the most powerful single liability theory in the case if admitted - it bypasses charitable trust formation and donor-standing arguments. Plaintiff's risk: if Daubert excludes the supplemental opinion, the §17510.8 theory effectively dies. Defendant's risk: if Daubert admits, strict liability for false solicitations is a streamlined path to verdict that avoids the harder trust-creation arguments. The pending Daubert ruling is the highest-leverage open question in the matter.",
        juryInstructionBattle:
          "Pending Daubert ruling. If admitted, plaintiff seeks strict-liability instruction modeled on §17510.8 plain text. Defense seeks instruction requiring scienter and time-of-statement falsity. The instruction battle here is potentially case-dispositive - strict-liability instruction would shift the burden materially.",
        battlegroundScore: {
          plaintiffStrength: 6,
          defendantStrength: 5,
          leverage: "open",
        },
      },
    ],
  },
  neuro: {
    caseTitle: "Neurocentria v. Mando - Patent Infringement",
    status: "markman-pending",
    statusLabel: "MARKMAN PENDING",
    countdown: {
      event: "Plaintiff technical expert disclosure",
      date: "August 15, 2026",
      days: 102,
    },
    gapNote:
      "CRITICAL GAP: Plaintiff has not retained a technical expert for prior art and obviousness. Expert disclosure deadline is August 15, 2026 (102 days). Without disclosed plaintiff expert, Ziari's anticipation and obviousness analyses go uncontested into Markman briefing. Identify and retain expert within 21 days for pre-Markman engagement.",
    issues: [
      {
        id: "claim-construction",
        label: "Claim Construction",
        importance: "critical",
        importanceColor: "#ef4444",
        plaintiffExpert: {
          name: "TBD - Plaintiff Claim Construction Expert",
          credentials:
            "NOT YET RETAINED · gap flagged · Markman briefing relied on attorney argument only",
          coreOpinion:
            "Pending expert retention. Plaintiff has not retained a claim construction expert. Markman briefs (Dkts.142, 158) were filed without supporting expert declaration. Critical strategic gap given Aenlle-Rocha's 5/5 narrow-construction pattern.",
          daubertStatus: "unretained",
          daubertColor: "#ef4444",
          daubertRuling: "NO PLAINTIFF EXPERT RETAINED - SECOND CRITICAL GAP",
          vulnerabilityScore: 9,
          expanded: {
            admitted: [],
            excluded: [],
            ruling:
              "NO PLAINTIFF CLAIM CONSTRUCTION EXPERT. Adams Clarifying Declaration (Dkt.151) goes uncontested by competing expert opinion. Phillips v. AWH framework calls for POSITA testimony; plaintiff has none.",
            crossAngles: [
              "No plaintiff expert means no rebuttal to Adams's narrow construction",
              "Specification embodiment analysis goes uncontested",
              "Plaintiff has no POSITA witness for hierarchy-of-construction analysis",
            ],
            redirectPrep: [
              "Identify and retain claim construction expert URGENTLY",
              "Coordinate with technical-expert retention (also unfilled)",
              "Pre-Markman engagement preferred but Markman ruling may come before retention possible",
            ],
          },
        },
        defendantExpert: {
          name: "Dr. Sara Adams",
          credentials: "MIT Chemistry · claim construction expert (defense)",
          coreOpinion:
            "Claim terms must be construed narrowly. 'L-threonate chelation' refers to the specific manufactured chemical complex disclosed in the specification, not natural-phenomenon variants. 'In vivo formation' is excluded from claim scope - admitting it would render claims ineligible under §101.",
          daubertStatus: "filed",
          daubertColor: "#3b82f6",
          daubertRuling:
            "Adams Clarifying Declaration Dkt.151 (filed March 31, 2026) - narrowing construction supported",
          vulnerabilityScore: 4,
          expanded: {
            admitted: [
              "Adams Declaration Dkt.144 (opening response)",
              "Adams Clarifying Declaration Dkt.151 (March 31, 2026)",
            ],
            excluded: [],
            ruling:
              "Adams Clarifying Declaration filed March 31, 2026 (Dkt.151). Plaintiff has not filed Daubert challenge. The clarifying declaration directly addresses the §101 ambiguity that plaintiff's reply brief raised.",
            crossAngles: [
              "Adams's narrow construction reads limitations into claims (improper)",
              "His §101 framing conflates eligibility and infringement (separate inquiries)",
              "Specification examples don't define claim scope - they illustrate",
              "Adams's declaration is post-hoc - filed after plaintiff's reply",
            ],
            redirectPrep: [
              "Specification embodiments illustrate, not limit (Phillips holding)",
              "POSITA construes broadly absent express disclaimer",
              "§101 is separate inquiry from claim construction",
              "Adams's clarifying declaration responds to plaintiff's reply on §101 - proper",
            ],
          },
        },
        aiAssessment:
          "Markman ruling pending. Adams's clarifying declaration (Dkt.151, March 31) significantly strengthens the defense narrow-construction position by addressing the §101 ambiguity. Baker's broader construction must overcome the specification examples that defense will cite. Outcome likely turns on how Aenlle-Rocha weighs specification examples vs. plain meaning. His historical pattern (5/5 patterns favor narrower construction per Judge Intel) tilts strongly defense. The Markman ruling is effectively case-dispositive on infringement - if narrow construction prevails, accused products may not infringe; if broad, infringement is highly likely.",
        juryInstructionBattle:
          "Pending Markman ruling. Whichever side prevails on construction will draft the jury instruction defining the disputed terms. The Markman ruling effectively decides the construction battle for the jury - the instruction merely formalizes it.",
        battlegroundScore: {
          plaintiffStrength: 5,
          defendantStrength: 7,
          leverage: "defendant",
        },
      },
      {
        id: "prior-art",
        label: "Prior Art & Obviousness",
        importance: "critical",
        importanceColor: "#ef4444",
        plaintiffExpert: {
          name: "TBD - Plaintiff Technical Expert",
          credentials:
            "NOT YET RETAINED · gap flagged · expert disclosure deadline August 15, 2026",
          coreOpinion:
            "Pending expert retention. Plaintiff must respond to Ziari's anticipated CN1200366A and MIT provisional analyses. Without an expert, plaintiff cannot rebut anticipation or obviousness at trial.",
          daubertStatus: "unretained",
          daubertColor: "#ef4444",
          daubertRuling: "NO EXPERT DISCLOSED - CRITICAL GAP",
          vulnerabilityScore: 9,
          expanded: {
            admitted: [],
            excluded: [],
            ruling:
              "NO PLAINTIFF EXPERT RETAINED AS OF TODAY. This is the single highest-priority strategic action item in the case. Without a technical expert by August 15, 2026, §102 anticipation and §103 obviousness analyses go uncontested.",
            crossAngles: [
              "No designated expert means no rebuttal testimony at trial",
              "Plaintiff cannot challenge Ziari's KSR motivation-to-combine analysis",
              "Plaintiff has no witness to authenticate prior art translations or contest disclosure scope",
            ],
            redirectPrep: [
              "Identify and retain expert within 21 days · pre-Markman engagement preferred",
              "Coordinate expert retention with Markman ruling timing - narrower construction may simplify expert work",
              "Budget for full §102/§103 expert report by July 15, 2026",
            ],
          },
        },
        defendantExpert: {
          name: "Dr. Naveed Ziari",
          credentials:
            "Pharmaceutical chemistry · prior art and obviousness expert",
          coreOpinion:
            "CN1200366A and MIT Provisional 60/510,945 anticipate the '061 patent claims, alternatively render them obvious under §103. Specifically, the L-threonate chelation chemistry was disclosed in CN1200366A (2003) and the MIT provisional (2002) discloses related synthesis approaches. KSR motivation to combine is met.",
          daubertStatus: "supported",
          daubertColor: "#10b981",
          daubertRuling:
            "Initial expert report served, no Daubert motion filed yet",
          vulnerabilityScore: 4,
          expanded: {
            admitted: [
              "Ziari Initial Report - anticipation analysis under §102",
              "Ziari Initial Report - obviousness analysis with KSR factors",
              "Ziari Initial Report - CN1200366A translation and disclosure analysis",
            ],
            excluded: [],
            ruling:
              "Initial expert report served. No plaintiff Daubert motion filed (because plaintiff has no expert to anchor a Daubert challenge).",
            crossAngles: [
              "CN1200366A translation accuracy - material differences in technical disclosure?",
              "MIT provisional inventorship issue - defense witness Slutsky has chain-of-custody questions",
              "KSR motivation-to-combine - Ziari conclusory on this element",
              "Ziari has not previously testified in a §101/§102/§103 trial in this Daubert posture",
            ],
            redirectPrep: [
              "Translation accuracy is verifiable - cite official translations and corroborating disclosures",
              "Inventorship arguments don't undermine prior art status under §102",
              "KSR factors are met by combined disclosures",
            ],
          },
        },
        aiAssessment:
          "Plaintiff has a CRITICAL gap here - no technical expert retained as of today. Ziari's testimony is unrebutted. Without a plaintiff technical expert by the disclosure deadline (August 15, 2026), the §102 anticipation and §103 obviousness arguments effectively go uncontested. This is the single highest-priority strategic action item in the case. Once retained, the plaintiff expert must address: (1) CN1200366A translation accuracy and technical scope, (2) MIT provisional inventorship and disclosure scope, (3) KSR motivation-to-combine analysis. Coordinate expert work with Markman ruling.",
        juryInstructionBattle:
          "Standard §102 and §103 jury instructions. Without plaintiff expert, defense has free rein to define obviousness for the jury. Plaintiff's burden of proof on validity makes this gap especially damaging - the patent is presumed valid but plaintiff must prove infringement; defense expert testimony on invalidity reduces that path materially.",
        battlegroundScore: {
          plaintiffStrength: 2,
          defendantStrength: 8,
          leverage: "defendant-strong",
        },
      },
    ],
  },
};

const CLIENT_COMM_DATA = {
  ...ILE_K_CC,
  juul: {
    matterName: "Seliavski v. JUUL Labs MDL · 3:19-md-02913-WHO",
    clientNames: {
      plaintiff: "Seliavski Plaintiff Group / SFUSD",
      defendant: "JUUL Labs Inc. / Altria Group",
    },
    statusReports: {
      plaintiff: {
        frequency: "monthly",
        latest: {
          date: "May 1, 2026",
          reportingPeriod: "April 1 – May 1, 2026",
          whatHappened:
            "The MSJ hearing on October 31, 2025 (Dkt.4341) remains under submission - six months and counting. Judge Orrick's pace on this ruling exceeds his historical median (47 days) by a wide margin, suggesting either a thorough opinion in preparation or genuine difficulty splitting the SFUSD costs question from the Seliavski-cohort PI track. The antitrust class was certified on February 26, 2026 (Dkt.635 in 3:20-cv-02345-WHO), creating cross-pressure on JLI's settlement ceiling. Trial-specific expert disclosures for the antitrust track are due July 15, 2026.",
          whatItMeans:
            "Two parallel pressures are now in play. (1) On the PI track, the longer Orrick takes, the more weight a thorough opinion suggests - a fully reasoned ruling is harder to overturn on remand. (2) On the antitrust track, class certification means JLI's defense costs are running on two fronts simultaneously, compressing their ceiling for opt-out PI settlements. Plaintiff's leverage is materially higher today than it was at the October hearing.",
          nextMilestone:
            "Antitrust trial September 28, 2026 (146 days). MSJ ruling pending - expected within the next 30-90 days based on trajectory analysis. Trial-specific expert disclosure deadline July 15, 2026.",
          decisionsNeeded:
            "Authorize antitrust track engagement strategy - coordinate or stay arms-length with class plaintiffs. Expert retention budget approval for trial-specific witnesses ($350K-$500K range). Settlement mediation initiation - recommended within 14 days to capture the two-front-war leverage premium before MSJ ruling tail risk.",
        },
        history: [
          {
            date: "Apr 1, 2026",
            summary:
              "April update: antitrust class certified; mediation outreach delayed pending MSJ ruling",
          },
          {
            date: "Mar 1, 2026",
            summary:
              "March update: post-MSJ posture; Sonner concession in revised demand",
          },
          {
            date: "Feb 1, 2026",
            summary:
              "February update: class cert briefing completed in antitrust track",
          },
        ],
      },
      defendant: {
        frequency: "monthly",
        latest: {
          date: "May 1, 2026",
          reportingPeriod: "April 1 – May 1, 2026",
          whatHappened:
            "The October 31, 2025 MSJ hearing remains under submission - extended timeline suggests Orrick is preparing a substantive ruling rather than granting summarily. Antitrust class certification on February 26, 2026 (Dkt.635) introduced material risk on a second front. Trial-specific expert disclosure deadline July 15, 2026.",
          whatItMeans:
            "The longer the MSJ ruling takes, the lower the probability of a clean defense win on the SFUSD track. Antitrust class certification means JLI is now defending two simultaneous trials, materially increasing the burn rate. The two-front cost asymmetry argues for a settlement window in the next 60-90 days - before either ruling.",
          nextMilestone:
            "Antitrust trial September 28, 2026 (146 days). MSJ ruling pending. Trial-specific expert disclosure July 15, 2026.",
          decisionsNeeded:
            "Settlement mediation authorization - recommended pre-MSJ ruling. Antitrust trial team allocation - separate counsel from MDL PI team. Joint defense agreement with Altria: re-examine scope given class cert posture.",
        },
        history: [],
      },
    },
    budget: {
      plaintiff: {
        phases: [
          {
            name: "Fact Discovery",
            budget: 2100000,
            hoursActual: 8420,
            hoursProjected: 8420,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Expert Work",
            budget: 1800000,
            hoursActual: 4100,
            hoursProjected: 7200,
            phaseStatus: "in-progress",
            costRate: 250,
          },
          {
            name: "Motion Practice",
            budget: 1500000,
            hoursActual: 5800,
            hoursProjected: 6500,
            phaseStatus: "in-progress",
            costRate: 260,
          },
          {
            name: "Trial Preparation",
            budget: 2200000,
            hoursActual: 0,
            hoursProjected: 8800,
            phaseStatus: "not-started",
            costRate: 250,
          },
          {
            name: "Trial",
            budget: 1500000,
            hoursActual: 0,
            hoursProjected: 6000,
            phaseStatus: "not-started",
            costRate: 250,
          },
        ],
        total: {
          budget: 9100000,
          projectedTotal: 9230000,
          burnedToDate: 4581500,
          projectedToTrialClose: 9230000,
        },
        cbf: {
          hoursTracked: 7900,
          totalCBFhours: 142000,
          allocationPct: 5.56,
          scenarios: [
            {
              scenario: "Class settlement at $1.7B baseline (already approved)",
              totalFee: 680000000,
              allocationRecovery: 37808000,
              note: "Already realized - historical benchmark",
            },
            {
              scenario: "Opt-out cohort settlement at $50M (low)",
              totalFee: 20000000,
              allocationRecovery: 1112000,
              note: "Floor scenario for opt-out track",
            },
            {
              scenario: "Opt-out cohort settlement at $200M (mid)",
              totalFee: 80000000,
              allocationRecovery: 4448000,
              note: "Targeted mid-range outcome",
            },
            {
              scenario: "Antitrust verdict + opt-out PIs (high)",
              totalFee: 240000000,
              allocationRecovery: 13344000,
              note: "Two-front trial victory scenario",
            },
          ],
        },
      },
      defendant: {
        phases: [
          {
            name: "Class Defense (resolved)",
            budget: 18000000,
            hoursActual: 64000,
            hoursProjected: 64000,
            phaseStatus: "complete",
            costRate: 280,
          },
          {
            name: "Opt-Out PI Defense",
            budget: 6500000,
            hoursActual: 18200,
            hoursProjected: 24000,
            phaseStatus: "in-progress",
            costRate: 280,
          },
          {
            name: "Antitrust Defense (post class-cert)",
            budget: 9500000,
            hoursActual: 11400,
            hoursProjected: 34000,
            phaseStatus: "in-progress",
            costRate: 285,
          },
          {
            name: "Two-Front Trial Defense",
            budget: 7800000,
            hoursActual: 0,
            hoursProjected: 28000,
            phaseStatus: "not-started",
            costRate: 280,
          },
        ],
        total: {
          budget: 41800000,
          projectedTotal: 42100000,
          burnedToDate: 26432000,
          projectedToTrialClose: 42100000,
        },
      },
    },
    decisionLog: {
      plaintiff: [
        {
          date: "Aug 15, 2024",
          decision: "Opt out of JLI personal injury class settlement",
          reasoning:
            "B.B. minor's compromise ($175K) sets a floor; Seliavski's documented severity supports a higher individual recovery than class-baseline allocation. Sonner risk on SFUSD track is separate analysis.",
          authorizedBy: "Lead counsel + named plaintiff",
          status: "executed",
        },
        {
          date: "Nov 12, 2024",
          decision:
            "Pursue consolidated PI track for Seliavski cohort rather than separate filings",
          reasoning:
            "MDL coordination preserves favorable Orrick venue; separate filings risk transfer to less-favorable jurisdictions. Cohort approach also distributes expert costs.",
          authorizedBy: "Lead counsel + plaintiff steering committee",
          status: "executed",
        },
        {
          date: "Feb 28, 2025",
          decision:
            "Engage antitrust track plaintiffs for coordination - short of formal joint prosecution",
          reasoning:
            "Antitrust class cert was uncertain at the time. Coordination preserves leverage without formal attribution that could complicate fee allocation. Re-examine after class cert outcome.",
          authorizedBy: "Lead counsel",
          status: "executed · re-examined Feb 26, 2026",
        },
        {
          date: "Oct 28, 2025",
          decision:
            "Argue Sonner-bar opposition at MSJ hearing - accept narrowed damages exposure",
          reasoning:
            "Pak's 30(b)(6) admission ('We don't have that information') makes the documented-past-cost theory unsustainable. Reframe to forward-looking equity remedy. Better than aggressive past-cost theory that loses on adequate-remedy grounds.",
          authorizedBy: "Lead counsel",
          status: "argued · pending ruling",
        },
        {
          date: "Mar 15, 2026",
          decision: "Reduce Seliavski demand to $175K from $250K",
          reasoning:
            "Acknowledges Sonner risk on SFUSD track without conceding Seliavski cohort merits. Aligned to B.B. minor's compromise as floor. Maintains 5x JLI offer position.",
          authorizedBy: "Lead counsel + named plaintiff",
          status: "executed · transmitted via mediation request",
        },
        {
          date: "Apr 26, 2026",
          decision: "Recommend settlement mediation initiation pre-MSJ ruling",
          reasoning:
            "Two-front war (antitrust class + opt-out PI track) compresses JLI ceiling by ~$12M. Pre-ruling settlement protects against MSJ tail risk on Seliavski cohort. Window closing as ruling approaches.",
          authorizedBy: "Pending - partner authorization required",
          status: "pending",
        },
      ],
      defendant: [
        {
          date: "Aug 1, 2022",
          decision: "Approve $1.7B class settlement framework",
          reasoning:
            "Avoids individualized PI litigation at scale. Per-plaintiff baseline ($170K average) is sustainable; opt-out cohort risk is contained.",
          authorizedBy: "JLI board · joint with Altria board",
          status: "executed · approved Sep 22, 2023",
        },
        {
          date: "Feb 5, 2024",
          decision: "Defer antitrust class cert opposition to merits stage",
          reasoning:
            "Procedural defense at cert is unwinnable given documented coordination evidence. Conserve resources for Daubert and merits trial. Class cert tactical loss preserves substantive defense.",
          authorizedBy: "Lead defense counsel",
          status: "executed · class certified Feb 26, 2026",
        },
        {
          date: "Sep 30, 2025",
          decision:
            "File MSJ on Seliavski-cohort opt-out PIs · Sonner-bar centerpiece",
          reasoning:
            "Pak admission gives clean Sonner argument. Even partial SJ on past-cost claims dramatically reduces opt-out exposure. Hearing strategy: focus Orrick on adequate-remedy framework.",
          authorizedBy: "Lead defense counsel · joint defense committee",
          status: "argued · pending ruling",
        },
        {
          date: "Mar 1, 2026",
          decision: "Decline plaintiff mediation request post-class-cert",
          reasoning:
            "Pre-MSJ ruling, defense ceiling is structurally compressed by two-front war but plaintiff floor is also compressed by Sonner risk. Net: wait for MSJ ruling to clarify both floors.",
          authorizedBy: "Lead defense counsel",
          status: "executed · plaintiff renewed request April 26",
        },
        {
          date: "Apr 30, 2026",
          decision: "Authorize antitrust trial team budget at $9.5M",
          reasoning:
            "Class cert outcome makes trial defense imperative. Separate from PI MDL team to avoid scope creep. Trial Sept 28, 2026.",
          authorizedBy: "JLI general counsel",
          status: "executed",
        },
      ],
    },
  },
  musk: {
    matterName: "Musk v. Altman et al. · 4:24-cv-04722-YGR",
    clientNames: {
      plaintiff: "Elon Musk · founding donor",
      defendant: "OpenAI Inc. / Altman / Microsoft",
    },
    statusReports: {
      plaintiff: {
        frequency: "weekly",
        latest: {
          date: "May 5, 2026 (Day 3 evening)",
          reportingPeriod: "Trial Day 3",
          whatHappened:
            "Trial is proceeding as expected. The Brockman journal entry - which shows OpenAI's leadership privately acknowledged their obligations to your client - is now part of the trial record. The defense is contesting it as personal musing rather than institutional commitment, which is their best argument against it. The 'Yup' email (Altman's one-word reply confirming for-profit conversion) was admitted on Day 2 with strong jury reaction. Schizer's direct on charitable trust formation was completed Day 1; cross-examination was contained. Wazzan's FIFO methodology direct was completed Day 2 - defense cross was substantial and the swing juror (Seat 4) appeared disengaged during re-examination.",
          whatItMeans:
            "The narrative evidence (Brockman journal, founding emails) is landing strongly with the panel. 7 of 9 jurors had strong reaction to the Brockman journal - including the swing juror. The damages methodology (Wazzan FIFO) is landing weakly - 5.4/10 average engagement. Strategic implication: closing argument should LEAD with the equitable narrative (broken promise, betrayal of mission) and use Wazzan only for quantification. The defense's strongest theme so far is SOL - Seat 8 anchored, several others tracking.",
          nextMilestone:
            "Satya Nadella's testimony, scheduled for Day 5 (Wednesday), will determine how much the jury believes Microsoft knew about your client's relationship with OpenAI before investing. We will update you after his examination. Closing arguments scheduled for Day 9.",
          decisionsNeeded:
            "Closing argument theme prioritization - recommendation: lead with Brockman + founding emails, use Wazzan as quantification only. Settlement window assessment - defense indicated openness to discuss post-Nadella; we recommend declining without strong floor. Press strategy for Nadella day.",
        },
        history: [
          {
            date: "May 4, 2026",
            summary: "Day 2 close: Yup email admitted, strong jury reaction",
          },
          {
            date: "May 3, 2026",
            summary:
              "Day 1 close: Schizer direct completed, defense opening underwhelming",
          },
          {
            date: "Apr 27, 2026",
            summary:
              "Jury seated · 5 plaintiff-leaning, 2 defense-leaning, 2 unclear",
          },
        ],
      },
      defendant: {
        frequency: "weekly",
        latest: {
          date: "May 5, 2026 (Day 3 evening)",
          reportingPeriod: "Trial Day 3",
          whatHappened:
            "Trial Day 3 closed with material exposure on the narrative evidence. The Brockman journal admission landed harder than anticipated - internal review of jury observations indicates 7 of 9 jurors had strong reactions, including the swing juror (Seat 4). The defense's reframing of the entry as personal venting has not fully landed. The 'Yup' email exhibit was admitted Tuesday with strong jury reaction. Schizer cross was contained but did not undermine his framework. Wazzan's FIFO methodology cross successfully drew jury skepticism - the partial Daubert exclusion (Opinions 1-2) was emphasized. Seat 4 (swing) appeared disengaged during Wazzan re-examination, which is the most encouraging defense signal of the trial so far.",
          whatItMeans:
            "The narrative side of the case is favoring plaintiff materially. The damages methodology and SOL legal arguments are favoring defense. The trial outcome may turn on the panel's reaction to the technical defenses - particularly SOL, where Seat 8 is anchored and several others are tracking. Nadella's testimony is the most consequential remaining moment. If he can credibly distance Microsoft from contemporaneous knowledge of the founding-mission representations, BFP defense holds. If he cannot, constructive trust on Microsoft equity becomes plausible.",
          nextMilestone:
            "Nadella testimony scheduled Day 5. Closing arguments Day 9. Settlement opening would be appropriate post-Nadella if testimony underwhelms.",
          decisionsNeeded:
            "Settlement authority pre-Nadella - recommend $X-$Y range authority depending on Nadella prep outcome. Closing argument leadership: SOL primary, sophisticated-donor secondary, reframe Brockman aggressively at the top. Authorize aggressive Wazzan recall on technical FIFO points if helpful for Seat 4.",
        },
        history: [],
      },
    },
    budget: {
      plaintiff: {
        phases: [
          {
            name: "Pre-Suit Investigation",
            budget: 450000,
            hoursActual: 1800,
            hoursProjected: 1800,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Pleading + PI Motion",
            budget: 850000,
            hoursActual: 3400,
            hoursProjected: 3400,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Fact Discovery",
            budget: 2400000,
            hoursActual: 9600,
            hoursProjected: 9600,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Expert Work",
            budget: 1800000,
            hoursActual: 7200,
            hoursProjected: 7200,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "MSJ + Daubert Briefing",
            budget: 1200000,
            hoursActual: 4800,
            hoursProjected: 4800,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Trial Preparation",
            budget: 2200000,
            hoursActual: 8800,
            hoursProjected: 8800,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Trial (in progress)",
            budget: 1800000,
            hoursActual: 1080,
            hoursProjected: 7200,
            phaseStatus: "in-progress",
            costRate: 250,
          },
        ],
        total: {
          budget: 10700000,
          projectedTotal: 10920000,
          burnedToDate: 9170000,
          projectedToTrialClose: 10920000,
        },
      },
      defendant: {
        phases: [
          {
            name: "Pre-Suit Coordination",
            budget: 300000,
            hoursActual: 1200,
            hoursProjected: 1200,
            phaseStatus: "complete",
            costRate: 300,
          },
          {
            name: "PI Opposition (won)",
            budget: 650000,
            hoursActual: 2200,
            hoursProjected: 2200,
            phaseStatus: "complete",
            costRate: 295,
          },
          {
            name: "Fact Discovery",
            budget: 3200000,
            hoursActual: 10800,
            hoursProjected: 10800,
            phaseStatus: "complete",
            costRate: 295,
          },
          {
            name: "Expert Work",
            budget: 2400000,
            hoursActual: 8000,
            hoursProjected: 8000,
            phaseStatus: "complete",
            costRate: 300,
          },
          {
            name: "MSJ + Daubert Briefing",
            budget: 1500000,
            hoursActual: 5000,
            hoursProjected: 5000,
            phaseStatus: "complete",
            costRate: 300,
          },
          {
            name: "Trial Preparation",
            budget: 2800000,
            hoursActual: 9600,
            hoursProjected: 9600,
            phaseStatus: "complete",
            costRate: 295,
          },
          {
            name: "Trial (in progress)",
            budget: 2200000,
            hoursActual: 1320,
            hoursProjected: 8800,
            phaseStatus: "in-progress",
            costRate: 300,
          },
        ],
        total: {
          budget: 13050000,
          projectedTotal: 13280000,
          burnedToDate: 11352000,
          projectedToTrialClose: 13280000,
        },
      },
    },
    decisionLog: {
      plaintiff: [
        {
          date: "Mar 14, 2024",
          decision: "File complaint in Delaware (later refiled CA)",
          reasoning:
            "DE forum advantageous for breach of fiduciary duty under DGCL. Subsequent dismissal forced CA refiling - strategic loss but preserved core theory.",
          authorizedBy: "Lead counsel + Musk personally",
          status: "executed · refiled CA Aug 5, 2024",
        },
        {
          date: "Aug 5, 2024",
          decision:
            "Refile in N.D. Cal. with §17510.8 charitable solicitations theory added",
          reasoning:
            "CA is OpenAI's HQ jurisdiction. Adding §17510.8 introduces strict-liability path that bypasses charitable trust formation arguments - material upgrade to legal architecture.",
          authorizedBy: "Lead counsel + Musk personally",
          status: "executed",
        },
        {
          date: "Mar 4, 2025",
          decision: "Decline interlocutory appeal of PI denial",
          reasoning:
            "Standard of review on appeal is abuse-of-discretion (deferential). Cost of interlocutory appeal would consume bench-trial budget. Better to preserve legal theory for post-trial appellate posture.",
          authorizedBy: "Lead counsel + Musk personally",
          status: "executed · §1292(a)(1) deadline expired Mar 18, 2025",
        },
        {
          date: "Jul 22, 2025",
          decision: "Retain Schizer (charitable trust) and Wazzan (damages)",
          reasoning:
            "Schizer's CA charitable trust scholarship and Dean Emeritus credentials are unmatched. Wazzan brings constructive trust valuation expertise but vulnerability on counterfactual methodology requires Opinions 3-6 as primary, Opinions 1-2 as alternative.",
          authorizedBy: "Lead counsel + Musk personally",
          status: "executed",
        },
        {
          date: "Apr 4, 2026",
          decision:
            "Accept Wazzan partial Daubert exclusion · advance Opinions 3-6 only",
          reasoning:
            "PTO No. 2 excluded Wazzan Opinions 1-2. Advancing only admitted opinions is the only compliant path. Risk: defense will argue exclusion = methodological unreliability. Mitigation: redirect prep emphasizing methodology survival of Opinions 3-6.",
          authorizedBy: "Lead counsel",
          status: "executed · trial in progress",
        },
        {
          date: "Apr 24, 2026",
          decision:
            "Add §17510.8 supplemental Schizer opinion · accept Daubert risk",
          reasoning:
            "Strict-liability theory is potentially case-dispositive if admitted. Daubert risk is acceptable given the upside. Reply expected within 14 days.",
          authorizedBy: "Lead counsel + Musk personally",
          status: "executed · briefed April 30 · ruling pending",
        },
        {
          date: "May 4, 2026",
          decision:
            "Lead trial closing with Brockman journal · Wazzan as quantification only",
          reasoning:
            "Day 1-2 jury observations: 7 of 9 strong reaction to Brockman; Seat 4 yawned during Wazzan re-exam. Equitable narrative landing; technical methodology not. Reorder closing accordingly.",
          authorizedBy: "Lead counsel",
          status: "executed · closing scheduled Day 9",
        },
      ],
      defendant: [
        {
          date: "Mar 4, 2025",
          decision: "Aggressive PI denial defense · win at threshold",
          reasoning:
            "Likelihood-of-success-on-merits is the weakest plaintiff element given charitable trust formation novelty. Win at PI stage shifts trajectory of litigation.",
          authorizedBy: "Defense counsel + Altman + OpenAI board",
          status: "executed · PI denied Dkt.121 Mar 4, 2025",
        },
        {
          date: "Aug 12, 2025",
          decision:
            "Retain Hemel (corporate governance) and Dudney (alternative tracing)",
          reasoning:
            "Hemel's corporate-governance scholarship aligned with board-control defense. Dudney brings forensic accounting credibility for FIFO/LIFO/pro-rata alternative methodology.",
          authorizedBy: "Lead defense counsel",
          status: "executed",
        },
        {
          date: "Jan 15, 2026",
          decision: "Move for SJ on SOL · 2018-2019 timeline framing",
          reasoning:
            "Musk's September 2020 'captured by Microsoft' tweet establishes discovery point beyond doubt. Five-year delay is textbook laches and SOL bar. Win at SJ removes 80% of damages exposure.",
          authorizedBy: "Lead defense counsel",
          status: "executed · MSJ argued · partial denial Dkt.390",
        },
        {
          date: "Apr 4, 2026",
          decision:
            "Accept Wazzan Opinions 3-6 admitted; emphasize 1-2 exclusion at trial",
          reasoning:
            "Cannot exclude all Wazzan opinions. Emphasis at trial on the exclusion as 'unreliable methodology' signal. Day 3 jury observations confirm framing is landing with Seat 4 (swing).",
          authorizedBy: "Lead defense counsel",
          status: "executed · trial in progress",
        },
        {
          date: "Apr 24, 2026",
          decision:
            "File §17510.8 Daubert challenge · narrow-construction framing",
          reasoning:
            "Excluding Schizer's supplemental opinion would functionally kill the §17510.8 theory. Risk: court admits, in which case strict liability becomes a streamlined path to verdict. High-leverage motion.",
          authorizedBy: "Lead defense counsel · OpenAI board notified",
          status: "executed · briefed · ruling pending",
        },
        {
          date: "May 4, 2026",
          decision: "Decline pre-Nadella settlement initiation",
          reasoning:
            "Pre-Nadella, defense BATNA is uncertain. Wait for Nadella testimony to clarify Microsoft BFP defense viability before opening settlement. Risk: plaintiff may close window post-Nadella.",
          authorizedBy: "Lead defense counsel · Microsoft GC",
          status: "executed",
        },
      ],
    },
  },
  neuro: {
    matterName: "Neurocentria v. Mando · 2:25-cv-05871-FLA-MAR",
    clientNames: {
      plaintiff: "Neurocentria, Inc.",
      defendant: "Mando Pharmaceuticals (with §285 counterclaims)",
    },
    statusReports: {
      plaintiff: {
        frequency: "quarterly",
        latest: {
          date: "April 30, 2026",
          reportingPeriod: "Q1 2026 · January – March",
          whatHappened:
            "Markman briefing completed in March 2026. Defense filed a clarifying claim-construction declaration on March 31 (Adams Dkt.151) that strengthens their narrow-construction position. The §101 motion is fully briefed and pending ruling. Critical: plaintiff's technical expert for prior-art and obviousness has not yet been retained. The disclosure deadline is August 15, 2026 - 102 days remaining.",
          whatItMeans:
            "Two parallel risks. (1) Markman ruling will likely follow the narrower-construction trajectory based on Aenlle-Rocha's historical pattern (5/5 prior cases favored narrower). If the narrow construction prevails, infringement may not be established. (2) The unretained technical expert is the single highest-priority gap in the case - without expert testimony, Ziari's anticipation and obviousness analyses are unrebutted at trial.",
          nextMilestone:
            "§101 ruling pending (no published timeline). Markman ruling expected within 30-60 days post-briefing complete. Plaintiff technical expert disclosure deadline August 15, 2026. Fact discovery cut-off September 11, 2026. Slutsky letter rogatory must be filed this week to complete in time.",
          decisionsNeeded:
            "Authorize technical expert retention budget ($350K-$500K) for §102/§103 work - recommend within 14 days. Authorize Slutsky letter rogatory filing this week - required to preserve §102(f) inventorship-defense rebuttal opportunity. Markman fallback strategy: prepare alternative infringement theory under narrow construction.",
        },
        history: [
          {
            date: "Jan 30, 2026",
            summary:
              "Q4 2025 update: complaint filed, initial Markman briefing",
          },
          {
            date: "Oct 30, 2025",
            summary: "Q3 2025 update: case filed, scheduling order issued",
          },
        ],
      },
      defendant: {
        frequency: "quarterly",
        latest: {
          date: "April 30, 2026",
          reportingPeriod: "Q1 2026 · January – March",
          whatHappened:
            "Adams Clarifying Declaration filed March 31, 2026 (Dkt.151) addressing the §101 ambiguity raised in plaintiff's reply brief. This significantly strengthens our narrow-construction position. The §101 motion is fully briefed and pending ruling. Plaintiff has not yet retained a technical expert for prior art - confirmed via discovery responses. Counterclaims for invalidity and §285 fees are pending response.",
          whatItMeans:
            "Two material strengths emerging. (1) Aenlle-Rocha's historical pattern strongly favors narrow construction (5/5). Adams's clarifying declaration provides specific footing for the narrowing. (2) Plaintiff's expert gap means Ziari's prior-art and obviousness testimony will be unrebutted at trial - a strong validity defense posture. Combined with the §101 motion, multiple paths to invalidity.",
          nextMilestone:
            "§101 ruling pending. Markman ruling expected 30-60 days post-briefing. Plaintiff expert deadline August 15. Fact discovery cut-off September 11.",
          decisionsNeeded:
            "Authorize §285 exceptional-case fee preservation strategy - accumulate fee documentation. Slutsky letter rogatory: oppose plaintiff's filing or accept conditionally. Counterclaim damages discovery: depose Liu on inventorship-related communications.",
        },
        history: [],
      },
    },
    budget: {
      plaintiff: {
        phases: [
          {
            name: "Pre-Suit Investigation",
            budget: 280000,
            hoursActual: 1100,
            hoursProjected: 1100,
            phaseStatus: "complete",
            costRate: 255,
          },
          {
            name: "Pleading",
            budget: 220000,
            hoursActual: 880,
            hoursProjected: 880,
            phaseStatus: "complete",
            costRate: 250,
          },
          {
            name: "Fact Discovery",
            budget: 1800000,
            hoursActual: 5400,
            hoursProjected: 8200,
            phaseStatus: "in-progress",
            costRate: 250,
          },
          {
            name: "Markman Briefing",
            budget: 850000,
            hoursActual: 3200,
            hoursProjected: 3400,
            phaseStatus: "in-progress",
            costRate: 250,
          },
          {
            name: "Expert Work",
            budget: 1900000,
            hoursActual: 0,
            hoursProjected: 7200,
            phaseStatus: "not-started",
            costRate: 265,
          },
          {
            name: "MSJ + Daubert",
            budget: 1200000,
            hoursActual: 0,
            hoursProjected: 4800,
            phaseStatus: "not-started",
            costRate: 250,
          },
          {
            name: "Trial Preparation + Trial",
            budget: 3500000,
            hoursActual: 0,
            hoursProjected: 14000,
            phaseStatus: "not-started",
            costRate: 250,
          },
        ],
        total: {
          budget: 9750000,
          projectedTotal: 10180000,
          burnedToDate: 2645000,
          projectedToTrialClose: 10180000,
        },
      },
      defendant: {
        phases: [
          {
            name: "Pre-Suit Investigation",
            budget: 320000,
            hoursActual: 1200,
            hoursProjected: 1200,
            phaseStatus: "complete",
            costRate: 265,
          },
          {
            name: "Answer + Counterclaims",
            budget: 380000,
            hoursActual: 1500,
            hoursProjected: 1500,
            phaseStatus: "complete",
            costRate: 255,
          },
          {
            name: "§101 + §102/§103 Motions",
            budget: 1450000,
            hoursActual: 5400,
            hoursProjected: 5800,
            phaseStatus: "in-progress",
            costRate: 265,
          },
          {
            name: "Fact Discovery",
            budget: 2100000,
            hoursActual: 6900,
            hoursProjected: 8400,
            phaseStatus: "in-progress",
            costRate: 255,
          },
          {
            name: "Markman Briefing",
            budget: 980000,
            hoursActual: 3700,
            hoursProjected: 3900,
            phaseStatus: "in-progress",
            costRate: 255,
          },
          {
            name: "Expert Work",
            budget: 1750000,
            hoursActual: 1500,
            hoursProjected: 6800,
            phaseStatus: "in-progress",
            costRate: 265,
          },
          {
            name: "§285 Fee Documentation",
            budget: 200000,
            hoursActual: 600,
            hoursProjected: 1800,
            phaseStatus: "in-progress",
            costRate: 255,
          },
          {
            name: "Trial Preparation + Trial",
            budget: 3800000,
            hoursActual: 0,
            hoursProjected: 15200,
            phaseStatus: "not-started",
            costRate: 265,
          },
        ],
        total: {
          budget: 10980000,
          projectedTotal: 11420000,
          burnedToDate: 5187000,
          projectedToTrialClose: 11420000,
        },
      },
    },
    decisionLog: {
      plaintiff: [
        {
          date: "Jul 8, 2025",
          decision: "File suit in C.D. Cal. with full claim spectrum",
          reasoning:
            "C.D. Cal. is Mando's HQ jurisdiction. Filing all available claims preserves maximum leverage. Aenlle-Rocha's recent assignment was unknown at filing.",
          authorizedBy: "Lead counsel + Neurocentria board",
          status: "executed",
        },
        {
          date: "Sep 22, 2025",
          decision: "Pursue broad claim construction in opening Markman brief",
          reasoning:
            "Broad construction gives infringement maximum coverage. Risk: specification examples may pull narrower. Decision reflects strategic preference for upside.",
          authorizedBy: "Lead counsel",
          status: "executed · Markman pending",
        },
        {
          date: "Mar 31, 2026",
          decision:
            "DEFER technical expert retention pending Markman ruling timing",
          reasoning:
            "Cost optimization - narrower construction may reduce expert scope. Risk: deadline approaching, retention should not be deferred further.",
          authorizedBy: "Lead counsel · cost concerns from CFO",
          status: "DEFERRED · CRITICAL - must reverse within 14 days",
        },
        {
          date: "Apr 28, 2026",
          decision:
            "PENDING: authorize Slutsky letter rogatory filing this week",
          reasoning:
            "§102(f) inventorship rebuttal requires Slutsky deposition. Hague Convention lead time 90-120 days. Must file this week to complete before Sep 11 cut-off.",
          authorizedBy: "Pending - partner authorization required",
          status: "pending",
        },
        {
          date: "Apr 30, 2026",
          decision: "PENDING: technical expert retention budget approval",
          reasoning:
            "Without expert, validity defense unrebutted. Single highest-priority gap. Recommend $350K-$500K budget for full §102/§103 expert work.",
          authorizedBy: "Pending - partner + CFO authorization required",
          status: "pending",
        },
      ],
      defendant: [
        {
          date: "Aug 14, 2025",
          decision:
            "File counterclaims for invalidity + §285 exceptional case fees",
          reasoning:
            "Counterclaims preserve invalidity attack regardless of plaintiff's infringement theory. §285 fee preservation requires documentation from inception - start now.",
          authorizedBy: "Lead defense counsel + Mando general counsel",
          status: "executed",
        },
        {
          date: "Oct 15, 2025",
          decision:
            "Pursue narrow claim construction · §101 motion in parallel",
          reasoning:
            "Aenlle-Rocha's pattern strongly favors narrow construction. §101 is parallel path: even if claims construed broadly, ineligibility under Mayo/Alice may invalidate.",
          authorizedBy: "Lead defense counsel",
          status: "executed · briefing complete · rulings pending",
        },
        {
          date: "Mar 31, 2026",
          decision: "File Adams Clarifying Declaration · §101 specificity",
          reasoning:
            "Plaintiff's reply brief raised §101 ambiguity. Adams declaration directly addresses, strengthening narrow construction with §101 footing.",
          authorizedBy: "Lead defense counsel",
          status: "executed · Dkt.151",
        },
        {
          date: "Apr 22, 2026",
          decision: "Retain Ziari for §102/§103 prior-art expert testimony",
          reasoning:
            "CN1200366A and MIT provisional are strongest prior art. Ziari's pharmaceutical chemistry credentials match the technical area. Plaintiff has not yet retained counterpart.",
          authorizedBy: "Lead defense counsel · CFO approval",
          status: "executed",
        },
        {
          date: "Apr 30, 2026",
          decision: "Authorize §285 fee documentation accumulation strategy",
          reasoning:
            "Plaintiff's case-strategic gaps (no technical expert, broad construction in face of narrow precedent, deferred Slutsky discovery) may support exceptional-case finding post-judgment. Document fees from inception.",
          authorizedBy: "Lead defense counsel + Mando GC",
          status: "executed · ongoing",
        },
      ],
    },
  },
};

const STRATEGIC_MAP_DATA = {
  ...ILE_K_SM,
  juul: {
    caseTitle: "Seliavski v. JUUL Labs MDL - Antitrust + Opt-Out PI Track",
    layer1: {
      title: "Evidence Intensity by Claim",
      description:
        "How much each evidence category moves the needle on each claim. Not document count - qualitative AI assessment.",
      rows: [
        { id: "rico", label: "RICO (JLI + Altria)" },
        { id: "nuisance", label: "Public Nuisance (SFUSD)" },
        { id: "failure", label: "Failure to Warn (PI)" },
        { id: "antitrust", label: "Antitrust (Sherman §1)" },
      ],
      cols: [
        { id: "depo", label: "Deposition" },
        { id: "docs", label: "Documentary" },
        { id: "expert", label: "Expert" },
        { id: "ja", label: "Judicial Admissions" },
        { id: "public", label: "Public Record" },
      ],
      grid: [
        [
          {
            s: 6,
            l: "Mixed",
            d: "Burns/Monsees/Bowen depos establish coordination but Pak admission undermines damages prong.",
          },
          {
            s: 9,
            l: "Devastating",
            d: "JLI internal marketing documents (Dkt.3447 SOF ¶47) show JUUL School, youth KPI tracking, board-level distribution.",
          },
          {
            s: 7,
            l: "Strong",
            d: "DAP class-cert expert methodology preserved; trial expert disclosure pending.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Altria 30(b)(6) errata (Dkt.4480) is contested; original answer preserved.",
          },
          {
            s: 7,
            l: "Strong",
            d: "FTC dismissal Feb 2023 cuts both ways; Altria $12.8B investment plus market exit is compelling.",
          },
        ],
        [
          {
            s: 3,
            l: "Weak",
            d: "Pak 30(b)(6) admission ('we don't have that information') is the case-defining moment for SFUSD damages.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Dorn six-school inspection reports document physical damage; $78.8M forward-looking abatement model.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Halpern-Felsher 75% ignorance survey is powerful; Dorn forward-looking but Pak gap weakens base.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Matthews superintendent depo confirms cost-tracking gap; both sides cite.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "$255M class settlement funds set ceiling reference; Sonner equitable bar contested.",
          },
        ],
        [
          {
            s: 2,
            l: "Critical Gap",
            d: "NO medical causation expert retained for Seliavski PI. Single most urgent gap in MDL.",
          },
          {
            s: 7,
            l: "Strong",
            d: "JLI marketing documents + Halpern-Felsher 75% ignorance + JUUL School establish failure to warn.",
          },
          {
            s: 2,
            l: "Critical Gap",
            d: "Without medical causation expert, specific causation unprovable at trial.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Burns sworn 'never designed for youth' contradicted by JUUL School docs - strong impeachment.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "FDA MDO (stayed) cuts both ways; nicotine awareness public knowledge debate.",
          },
        ],
        [
          {
            s: 7,
            l: "Strong",
            d: "Class certification grant (Dkt.635) confirmed methodology; coordination depositions support per se theory.",
          },
          {
            s: 8,
            l: "Very Strong",
            d: "Altria due diligence files (Dkt.3393 unsealed) + market exit timing + $12.8B investment establish horizontal coordination.",
          },
          {
            s: 7,
            l: "Strong",
            d: "DAP class-cert econ expert preserved through cert; survived rigorous methodology challenge.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Altria public harm-reduction narrative contradicts internal due diligence findings.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Class certification publicly establishes coordination; PI brief market-knowledge admissions cross-pollinate.",
          },
        ],
      ],
    },
    layer2: {
      title: "Vulnerability Heat Map",
      description:
        "Which claims are most exposed? SJ risk, SOL, Daubert, standing, per se vs rule of reason.",
      rows: [
        { id: "rico", label: "RICO (JLI + Altria)" },
        { id: "nuisance", label: "Public Nuisance (SFUSD)" },
        { id: "failure", label: "Failure to Warn (PI)" },
        { id: "antitrust", label: "Antitrust (Sherman §1)" },
      ],
      cols: [
        { id: "sj", label: "SJ Risk" },
        { id: "sol", label: "SOL Risk" },
        { id: "daubert", label: "Daubert Risk" },
        { id: "standing", label: "Standing" },
        { id: "theory", label: "Per Se vs Rule of Reason" },
      ],
      grid: [
        [
          {
            s: 5,
            l: "Moderate",
            d: "RICO predicates require pattern; defense will challenge enterprise definition.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "Continuing violation doctrine likely tolls SOL through last act.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "Methodology issues with damages model on RICO causation.",
          },
          {
            s: 3,
            l: "Low",
            d: "Direct injury established by class-cert posture.",
          },
          {
            s: 6,
            l: "High",
            d: "Pattern requirement is the most contested element - JLI/Altria may argue isolated transactions, not pattern.",
          },
        ],
        [
          {
            s: 8,
            l: "High",
            d: "Pak admission + Sonner equitable bar = JLI's strongest SJ argument. October 2025 hearing pending.",
          },
          {
            s: 3,
            l: "Low",
            d: "Continuing nuisance theory; SFUSD entity claim less SOL-vulnerable.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "Dorn methodology defensible; Halpern-Felsher already admitted.",
          },
          { s: 3, l: "Low", d: "SFUSD entity standing well-established." },
          {
            s: 0,
            l: "N/A",
            d: "Common-law claim; per se framework inapplicable.",
          },
        ],
        [
          {
            s: 9,
            l: "Critical",
            d: "No medical causation expert = case-ending SJ vulnerability for Seliavski.",
          },
          {
            s: 7,
            l: "High",
            d: "Virginia 2-yr SOL; Spring 2018 cessation = Spring 2020 cutoff. American Pipe tolling depends on filing date.",
          },
          {
            s: 8,
            l: "High",
            d: "No expert disclosed = no Daubert posture - but the case dies at SJ before Daubert.",
          },
          {
            s: 3,
            l: "Low",
            d: "Individual standing clear; opt-out preserved.",
          },
          {
            s: 0,
            l: "N/A",
            d: "Common-law claim; antitrust framework inapplicable.",
          },
        ],
        [
          {
            s: 3,
            l: "Low",
            d: "Class cert grant resolves SJ vulnerability; trial-stage rigor still required.",
          },
          {
            s: 3,
            l: "Low",
            d: "Continuing-violation tolling well-established for antitrust.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "Trial expert not yet disclosed; deadline July 15, 2026.",
          },
          {
            s: 2,
            l: "Resolved",
            d: "Class certification grant (Dkt.635) resolved standing definitively.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Per se vs rule of reason still contested for vertical coordination - defense will press rule-of-reason analysis.",
          },
        ],
      ],
    },
    layer3: {
      title: "Witness Coverage Map",
      description:
        "Who can establish each fact? Color = reliability. Red = no witness (gap).",
      rows: [
        { id: "market", label: "JLI knew/targeted youth users" },
        { id: "sfusd-cost", label: "SFUSD past program costs quantified" },
        { id: "causation", label: "JUUL specifically caused Seliavski injury" },
        { id: "altria-coord", label: "Altria-JLI coordinated market division" },
        { id: "impact-class", label: "Class-wide antitrust impact" },
      ],
      cols: [
        { id: "burns", label: "Kevin Burns" },
        { id: "monsees", label: "James Monsees" },
        { id: "pak", label: "Quarry Pak" },
        { id: "matthews", label: "V. Matthews" },
        { id: "hf", label: "Halpern-Felsher" },
      ],
      grid: [
        [
          {
            s: 8,
            l: "Strong",
            d: "Burns sworn denial directly contradicted by JUUL School (impeachment-grade).",
          },
          {
            s: 9,
            l: "Pivotal",
            d: "Monsees on board distribution list for youth KPIs - direct admission.",
          },
          {
            s: 0,
            l: "None",
            d: "Pak SFUSD witness - no JLI knowledge testimony.",
          },
          {
            s: 0,
            l: "None",
            d: "Matthews SFUSD superintendent - no JLI knowledge testimony.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Halpern-Felsher 75% ignorance survey supports knowledge inference.",
          },
        ],
        [
          { s: 0, l: "None", d: "Burns no relevance to SFUSD costs." },
          { s: 0, l: "None", d: "Monsees no relevance to SFUSD costs." },
          {
            s: 1,
            l: "Negative",
            d: "Pak admission destroys this proposition - 'we don't have that information.'",
          },
          {
            s: 3,
            l: "Weak",
            d: "Matthews 'unaware of any document that quantifies staff time' - confirms Pak gap.",
          },
          {
            s: 4,
            l: "Mixed",
            d: "Halpern-Felsher provides ignorance data, not cost data.",
          },
        ],
        [
          { s: 0, l: "None", d: "Burns no causation testimony." },
          { s: 0, l: "None", d: "Monsees no causation testimony." },
          { s: 0, l: "None", d: "Pak no causation testimony." },
          { s: 0, l: "None", d: "Matthews no causation testimony." },
          {
            s: 0,
            l: "None",
            d: "Halpern-Felsher general youth ignorance only - NO specific Seliavski causation expert.",
          },
        ],
        [
          {
            s: 5,
            l: "Mixed",
            d: "Burns has Altria meeting knowledge but pre-litigation testimony.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Monsees similar - coordination meetings but corporate-officer framing.",
          },
          { s: 0, l: "None", d: "Pak no relevance." },
          { s: 0, l: "None", d: "Matthews no relevance." },
          { s: 0, l: "None", d: "Halpern-Felsher no relevance." },
        ],
        [
          {
            s: 0,
            l: "None",
            d: "Class-cert experts handle this, not fact witnesses.",
          },
          { s: 0, l: "None", d: "Same." },
          { s: 0, l: "None", d: "N/A." },
          { s: 0, l: "None", d: "N/A." },
          { s: 0, l: "None", d: "N/A." },
        ],
      ],
    },
    layer4: {
      title: "Timeline Density Map",
      description: "Where is evidence concentrated? Where is it sparse?",
      periods: [
        {
          label: "2015-2017 · Pre-Investment",
          density: 5,
          note: "Moderate. JUUL early commercial launch, internal marketing documents from this period are heavily relied upon.",
        },
        {
          label: "2018 · Altria Investment",
          density: 9,
          note: "Very dense. Altria $12.8B investment + due diligence files (Dkt.3393) + market exit decisions all concentrated here.",
        },
        {
          label: "2019-2020 · MDL Initiation",
          density: 7,
          note: "Dense. Initial MDL filings, founding plaintiffs, JUUL School documents emerge.",
        },
        {
          label: "2021-2022 · Discovery Peak",
          density: 9,
          note: "Very dense. Pak (Dec 2021), Matthews (Oct 2021), Burns (Sep 2021), Monsees (Mar 2022) depositions - all critical testimony.",
        },
        {
          label: "2023 · Settlement Phase",
          density: 6,
          note: "Moderate. Class settlements approved ($255M JLI + Altria); FTC dismissed Feb 2023.",
        },
        {
          label: "2024-2025 · Opt-Out + Antitrust",
          density: 8,
          note: "Dense. Opt-out cohort assembly, antitrust class certification briefing.",
        },
        {
          label: "2026 · Trial Phase",
          density: 7,
          note: "Dense. MSJ ruling pending, antitrust class cert grant Feb 26, antitrust trial Sept 28.",
        },
      ],
    },
    gaps: {
      closable: [
        {
          gap: "Seliavski medical causation expert",
          why: "PI claim requires specific causation testimony linking JUUL to claimed injuries; without expert, SJ on PI damages is near-certain.",
          required:
            "Identify, retain, and disclose qualifying medical causation expert. Budget $300K-$500K for expert work + report.",
          leadtime: "60-90 days from retention to deposable expert report",
          deadline: "Before remand transfer order (timing pending MSJ ruling)",
          severity: "critical",
        },
        {
          gap: "Trial-specific antitrust expert disclosure",
          why: "Class-cert experts preserved methodology but trial witnesses not yet disclosed for Sept 28 trial.",
          required:
            "Disclose trial expert by July 15, 2026 deadline. Allow 30 days for Daubert briefing before trial.",
          leadtime: "71 days remain to deadline",
          deadline: "July 15, 2026",
          severity: "high",
        },
        {
          gap: "Meta account data review for SFUSD-area targeting",
          why: "Court-ordered Meta production (May 8, 2026) may show JLI Instagram targeting in SFUSD area - third-party corroboration of youth-targeting theory.",
          required:
            "Allocate review attorneys + e-discovery vendor; targeted protocol for under-18 ad-targeting evidence.",
          leadtime:
            "Production due May 8 - review window 14 days post-production",
          deadline: "May 22, 2026 (review complete)",
          severity: "high",
        },
      ],
      unclosable: [
        {
          gap: "SFUSD documented past program costs beyond $34,875",
          why: "Pak admission ('we don't have that information') is binding 30(b)(6) testimony; record cannot be retroactively reconstructed.",
          strategicResponse:
            "Argue jury can estimate damages from Dorn six-school inspection without documented past costs. Cite cases allowing damages estimation from expert testimony despite sparse documentation (e.g., BigCommerce v. Beachy 9th Cir. 2021).",
          authorities:
            "Beachy line of cases support damages estimation. Sonner v. Premier Nutrition is the counterargument JLI will press.",
          severity: "critical",
        },
        {
          gap: "Pre-2018 internal documents from defunct subsidiaries",
          why: "Some pre-investment-era JLI subsidiary documents were lost in corporate restructuring; preservation obligations attached only post-2019 MDL.",
          strategicResponse:
            "Adverse inference instruction request available if spoliation provable. Otherwise rely on whistleblower-source documents and discovery from third parties (Altria due diligence files, FDA submissions).",
          authorities:
            "Zubulake adverse inference framework; Pension Comm. v. Banc of America Sec. (S.D.N.Y. 2010).",
          severity: "medium",
        },
      ],
      contested: [
        {
          gap: "Whether Altria pre-investment due diligence shows JLI youth-marketing knowledge",
          why: "Plaintiff: Dkt.3393 unsealed materials show Altria knew about youth users. Defense: due diligence is routine review of any acquisition target, not an admission of knowledge.",
          partyPositions:
            "Plaintiff cites Dkt.3393 paragraphs identifying youth user demographics. Defense cites professional due diligence standards and characterizes the files as comprehensive review, not selective focus on youth.",
          juryQuestion:
            "Did Altria's $12.8B investment reflect informed acceptance of, or willful blindness to, JUUL's youth-user base?",
          severity: "high",
        },
        {
          gap: "Sonner adequacy of legal remedy for SFUSD",
          why: "Plaintiff: Pak admission means no adequate legal remedy exists, so equitable abatement (Sonner-bar inapplicable) is the only path. Defense: forward-looking damages models exist (Dorn), so adequate legal remedy exists, and Sonner bars equitable abatement.",
          partyPositions:
            "Plaintiff: argue circular logic - if Pak destroys past damages, no adequate remedy exists by definition. Defense: forward-looking measurement is available, supplying adequate legal remedy.",
          juryQuestion:
            "Does Pak's admission destroy past damages while preserving forward-looking damages, or destroy both?",
          severity: "critical",
        },
      ],
      strategic: [
        {
          gap: "Market definition consistency between PI and antitrust tracks",
          why: "Antitrust class cert order used a specific e-cigarette market definition. PI track economic analysis may have used a different definition. Defense will exploit any inconsistency.",
          action:
            "Reconcile market definitions across tracks before trial expert disclosures. Coordinate with antitrust track plaintiffs.",
          impact:
            "Critical for both tracks - inconsistency hands JLI a foundational impeachment line.",
          severity: "high",
        },
        {
          gap: "Indirect purchaser class trial scheduling clarity",
          why: "Direct purchaser trial set Sept 28, 2026; indirect purchaser timeline unclear. Sequencing affects damages aggregation and settlement leverage.",
          action:
            "Coordinate with indirect purchaser plaintiffs on scheduling motion. Negotiate consolidated or sequenced trial schedule.",
          impact:
            "Affects settlement leverage - if trials run in parallel, two-front war intensifies; if staggered, JLI can pick off targets.",
          severity: "medium",
        },
      ],
    },
  },
  musk: {
    caseTitle: "Musk v. Altman et al. - Charitable Trust Trial · Day 3",
    layer1: {
      title: "Evidence Intensity by Claim",
      description:
        "How much each evidence category moves the needle. Per-claim qualitative assessment, not document count.",
      rows: [
        { id: "trust", label: "Charitable Trust (XVIII)" },
        { id: "ue", label: "Unjust Enrichment (IV)" },
        { id: "msft", label: "A&A Microsoft (XIX)" },
        { id: "17510", label: "§17510.8 Solicitations" },
      ],
      cols: [
        { id: "depo", label: "Deposition" },
        { id: "docs", label: "Documentary" },
        { id: "expert", label: "Expert" },
        { id: "ja", label: "Judicial Admissions" },
        { id: "public", label: "Public Record" },
      ],
      grid: [
        [
          {
            s: 7,
            l: "Strong",
            d: "Altman/Brockman depos admit founding-mission representations; some hedging on formal trust formation.",
          },
          {
            s: 9,
            l: "Devastating",
            d: "PX003 founding email + PX163 Brockman journal + Yup email - strongest evidence in the case.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Schizer (Daubert-survived) supports informal trust formation under CA law.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Altman LP term sheet acknowledgment is partial admission; defense reframes as routine business.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "OpenAI public mission statements 2015-2018 support; 2019 pivot creates ambiguity.",
          },
        ],
        [
          {
            s: 6,
            l: "Mixed",
            d: "Sutskever/Brockman depos on internal commercial trajectory provide UE substrate.",
          },
          {
            s: 8,
            l: "Very Strong",
            d: "$38M donations traceable through nonprofit structure; financial records support tracing.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Wazzan FIFO (partial Daubert exclusion of Opinions 1-2) - methodology contested.",
          },
          {
            s: 4,
            l: "Weak",
            d: "Altman public statements characterize donations as unrestricted gifts; weak admission.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "OpenAI for-profit pivot publicly known; benefit accrual to defendants documented.",
          },
        ],
        [
          {
            s: 5,
            l: "Mixed",
            d: "K. Scott depo confirms email PX230 but contests institutional knowledge interpretation.",
          },
          {
            s: 8,
            l: "Very Strong",
            d: "PX230 Kevin Scott to Nadella: 'I wonder if the big OpenAI donors are aware...' - 16 months pre-investment.",
          },
          {
            s: 4,
            l: "Weak",
            d: "No Microsoft-specific expert; defendant's BFP defense unrebutted on knowledge timing.",
          },
          {
            s: 3,
            l: "Weak",
            d: "Microsoft public statements characterize OpenAI investment as routine technology investment.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Microsoft 2019 $1B + 2023 $10B investment publicly documented; intent disputed.",
          },
        ],
        [
          {
            s: 4,
            l: "Weak",
            d: "Limited deposition development - supplemental theory added Apr 2026.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Founding emails 2015-2017 are the 'solicitations' - same documents as XVIII.",
          },
          {
            s: 5,
            l: "Pending",
            d: "Schizer supplemental opinion (Daubert pending Apr 30 briefing) - admission would create strict-liability path.",
          },
          {
            s: 3,
            l: "Weak",
            d: "No defendant judicial admission on §17510.8 applicability.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "OpenAI charitable status public until 2019 pivot; statute applicability turns on legal interpretation.",
          },
        ],
      ],
    },
    layer2: {
      title: "Vulnerability Heat Map",
      description:
        "Which claims are most exposed to SJ, SOL, Daubert, standing, theory failure?",
      rows: [
        { id: "trust", label: "Charitable Trust (XVIII)" },
        { id: "ue", label: "Unjust Enrichment (IV)" },
        { id: "msft", label: "A&A Microsoft (XIX)" },
        { id: "17510", label: "§17510.8 Solicitations" },
      ],
      cols: [
        { id: "sj", label: "SJ Risk" },
        { id: "sol", label: "SOL Risk" },
        { id: "daubert", label: "Daubert Risk" },
        { id: "standing", label: "Standing" },
        { id: "theory", label: "Theory Failure Risk" },
      ],
      grid: [
        [
          {
            s: 3,
            l: "Low",
            d: "Survived MTD; trial in progress. SJ phase passed.",
          },
          {
            s: 7,
            l: "High",
            d: "Sept 2020 'captured by Microsoft' tweet as constructive knowledge; tolling required.",
          },
          {
            s: 3,
            l: "Low",
            d: "Schizer Daubert-survived (PTO No. 4); admitted in full.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Donor standing for charitable trust contested; AG declined to join.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Informal trust formation requires donor intent + representations + course of conduct - three elements all contestable.",
          },
        ],
        [
          {
            s: 4,
            l: "Low-Mod",
            d: "Survived MTD; Wazzan partial exclusion creates limited damages-quantification risk.",
          },
          {
            s: 7,
            l: "High",
            d: "Same SOL framework applies; Sept 2020 tweet trigger.",
          },
          {
            s: 6,
            l: "High",
            d: "Wazzan Opinions 1-2 excluded - defense argues methodology unreliability extends to admitted Opinions 3-6.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "Donor restitution standing better-established than charitable trust standing.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "UE elements straightforward; benefit accrual + injustice.",
          },
        ],
        [
          {
            s: 5,
            l: "Moderate",
            d: "BFP defense may carry SJ if Nadella testimony establishes Microsoft ignorance of trust conditions.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Aiding & abetting limitations period contested; underlying tort SOL applies.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "No expert testimony on Microsoft knowledge; defense BFP testimony admissible.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Aiding & abetting standing requires direct injury showing - adequate but contestable.",
          },
          {
            s: 6,
            l: "High",
            d: "Microsoft BFP defense + intervening-value defense (GPT R&D, brand) attack causal chain to current equity.",
          },
        ],
        [
          {
            s: 8,
            l: "Critical",
            d: "Daubert pending Apr 30 briefing - exclusion would functionally kill the §17510.8 theory.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Statute SOL framework applies independently of common-law trust SOL.",
          },
          {
            s: 8,
            l: "Critical",
            d: "Schizer supplemental opinion is the entire theory - Daubert exclusion = theory dies.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Statutory standing for §17510.8 contested for individual donors at this scale.",
          },
          {
            s: 7,
            l: "High",
            d: "Whether 2015-2017 emails are 'charitable solicitations' under statute is contested; novel application.",
          },
        ],
      ],
    },
    layer3: {
      title: "Witness Coverage Map",
      description:
        "Each row is a fact plaintiff must prove. Each cell shows reliability of witness testimony on that fact.",
      rows: [
        {
          id: "prom",
          label: "Altman represented OpenAI would remain nonprofit",
        },
        {
          id: "reliance",
          label: "Musk relied on representations in donating $38M",
        },
        {
          id: "breach",
          label: "Defendants converted org to for-profit in breach",
        },
        {
          id: "msft-knowledge",
          label: "Microsoft had actual knowledge of donor expectations",
        },
        { id: "trace", label: "Microsoft equity traces from breach proceeds" },
      ],
      cols: [
        { id: "musk", label: "Musk" },
        { id: "altman", label: "Altman" },
        { id: "brockman", label: "Brockman" },
        { id: "kscott", label: "K. Scott" },
        { id: "wazzan", label: "Wazzan" },
      ],
      grid: [
        [
          {
            s: 8,
            l: "Strong",
            d: "Musk direct testimony on his understanding of representations - credible at trial Day 1.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Altman depo hedges; will be hostile but exhibits speak for themselves.",
          },
          {
            s: 9,
            l: "Pivotal",
            d: "Brockman journal PX163 - handwritten admission, no motive to deceive, pre-litigation.",
          },
          {
            s: 0,
            l: "None",
            d: "Microsoft witness - no relevance to Altman representations.",
          },
          {
            s: 0,
            l: "None",
            d: "Damages expert - no relevance to representations.",
          },
        ],
        [
          {
            s: 9,
            l: "Pivotal",
            d: "Musk direct testimony on reliance + $38M donation timing - pivotal for trust formation.",
          },
          { s: 0, l: "None", d: "Defendant witness - no reliance testimony." },
          { s: 0, l: "None", d: "Defendant witness - no reliance testimony." },
          { s: 0, l: "None", d: "Microsoft witness - no relevance." },
          { s: 0, l: "None", d: "Damages expert - no reliance testimony." },
        ],
        [
          {
            s: 5,
            l: "Mixed",
            d: "Musk public testimony on 2019 LP/PBC pivot - contested by acquiescence defense.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Altman 2019 LP structure decisions; admitted at trial as institutional acts.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Brockman cofounder testimony on 2018-2019 corporate structuring.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "K. Scott deposition discusses Microsoft awareness of OpenAI structure as of 2018.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Wazzan FIFO methodology traces $38M through nonprofit to for-profit equity - Opinions 3-6 admitted.",
          },
        ],
        [
          {
            s: 0,
            l: "None",
            d: "Musk no admissible Microsoft-internal testimony.",
          },
          {
            s: 0,
            l: "None",
            d: "Defendant witness - no Microsoft-internal testimony.",
          },
          {
            s: 0,
            l: "None",
            d: "Defendant witness - no Microsoft-internal testimony.",
          },
          {
            s: 5,
            l: "Contested",
            d: "PX230 documentary; depo testimony on whether email reflects institutional knowledge is contested.",
          },
          { s: 0, l: "None", d: "Damages expert - no knowledge testimony." },
        ],
        [
          { s: 0, l: "None", d: "Musk no tracing testimony." },
          {
            s: 0,
            l: "None",
            d: "Defendant witness - no admissible tracing testimony.",
          },
          {
            s: 0,
            l: "None",
            d: "Defendant witness - no admissible tracing testimony.",
          },
          {
            s: 3,
            l: "Weak",
            d: "K. Scott corporate counterpart - limited tracing relevance.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Wazzan FIFO methodology Opinions 3-6 admitted; constitutes the tracing chain.",
          },
        ],
      ],
    },
    layer4: {
      title: "Timeline Density Map",
      description: "Where is evidence concentrated? Where is it sparse?",
      periods: [
        {
          label: "2015 · OpenAI Founding",
          density: 9,
          note: "Very dense. Founding emails (PX003), early commitment letters, mission statements, $38M donations all concentrated here.",
        },
        {
          label: "2016-2017 · Pre-Pivot Era",
          density: 6,
          note: "Moderate. Email correspondence on mission, board minutes, internal strategic docs.",
        },
        {
          label: "2018 · Microsoft First Investment",
          density: 8,
          note: "Dense. PX230 K. Scott to Nadella (Mar 2018), $1B Microsoft investment July 2018, internal pivot decisions.",
        },
        {
          label: "2019 · LP/PBC Pivot",
          density: 9,
          note: "Very dense. Brockman journal entries (PX163), corporate restructuring documents, Microsoft expanded investment.",
        },
        {
          label: "2020-2024 · Pre-Litigation Period",
          density: 3,
          note: "Sparse. Notable gap. Public statements only - limited internal evidence development. Sept 2020 Musk tweet creates SOL anchor.",
        },
        {
          label: "2025-2026 · Trial Phase",
          density: 9,
          note: "Very dense. Discovery depositions (Altman, Brockman, K. Scott), expert reports (Schizer, Wazzan, Hemel, Dudney), pre-trial motions.",
        },
      ],
    },
    gaps: {
      closable: [
        {
          gap: "Microsoft board-level knowledge of trust conditions",
          why: "PX230 establishes CTO knowledge; Count XIX (A&A) requires Microsoft knowledge at time of $1B investment. Need board-level corroboration.",
          required:
            "Expand subpoena to Microsoft board minutes 2018-2019; depose Microsoft GC on diligence file review process. Build chain from PX230 to Nadella to board.",
          leadtime:
            "30-60 days to complete depositions; Nadella testimony scheduled Day 5",
          deadline:
            "Day 5 Nadella testimony - closing argument tomorrow if testimony underwhelms",
          severity: "high",
        },
        {
          gap: "§17510.8 strict-liability framework Daubert posture",
          why: "Theory dies if Daubert excludes Schizer supplemental opinion. Reply briefing window expected within 14 days.",
          required:
            "Strengthen Schizer reply opinion with additional CA case law on §17510.8 application to large-scale donor solicitations.",
          leadtime: "14 days to reply briefing close",
          deadline: "Approx. May 14, 2026",
          severity: "critical",
        },
      ],
      unclosable: [
        {
          gap: "Direct Musk-Altman 2018-2019 conversations on for-profit conversion",
          why: "Few contemporaneous records of in-person/phone conversations during pivot decisions. Memory-based testimony only.",
          strategicResponse:
            "Rely on documentary record (Brockman journal PX163, founding emails PX003, K. Scott PX230) which provides circumstantial corroboration of representations and acknowledgments.",
          authorities:
            "CA evidence law allows informal trust formation from totality-of-circumstances; courts routinely accept documentary corroboration over witness recollection. CACI 4900 (Trust Creation Elements).",
          severity: "medium",
        },
        {
          gap: "Pre-2015 Altman thought process on charitable purpose",
          why: "Personal/early thought process predates email and documentation; no record exists.",
          strategicResponse:
            "Frame the case from 2015 founding forward - the founding email PX003 itself establishes the representational baseline.",
          authorities:
            "Trust formation analysis is anchored to representations made, not to private donor's pre-formation intent.",
          severity: "low",
        },
      ],
      contested: [
        {
          gap: "Whether OpenAI made explicit perpetual-nonprofit representations",
          why: "Plaintiff: founding emails + Brockman journal + Yup email establish this. Defense: no formal representation was ever made - donations were unrestricted gifts.",
          partyPositions:
            "Plaintiff cites PX003 ('tech belongs to the world'), PX163 ('it would be wrong to steal the non-profit from Elon'), PX200 ('spirit of a donation'). Defense cites absence of formal trust documentation, board-controlled corporate structure (Cal. Corp. Code §5210).",
          juryQuestion:
            "Did Altman's representations + Musk's donations + the founding course of conduct create an informal charitable trust under California law without a formal deed?",
          juryInstruction:
            "Plaintiff seeks instruction modeled on CACI 4900 (Trust Creation Elements) emphasizing subjective intent. Defense seeks instruction modeled on Cal. Corp. Code §5210 emphasizing board control. Compromise instruction with totality-of-circumstances framing likely.",
          severity: "critical",
        },
        {
          gap: "Microsoft BFP status at July 2019 investment",
          why: "Plaintiff: PX230 establishes CTO awareness of donor expectations 16 months before investment - Microsoft cannot be BFP. Defense: PX230 is one CTO email; institutional knowledge requires more; Microsoft conducted standard diligence.",
          partyPositions:
            "Plaintiff: PX230 alone is sufficient to defeat BFP under CA equity. Defense: Microsoft due diligence files (sealed) reflect routine investment review, not selective focus on charitable trust conditions.",
          juryQuestion:
            "Did Microsoft have actual knowledge of donor charitable expectations when it made the $1B July 2019 investment?",
          severity: "high",
        },
      ],
      strategic: [
        {
          gap: "Constructive trust on Microsoft equity remedy framing",
          why: "Wazzan tracing supports constructive trust on ~27% Microsoft equity (~$135B). Closing argument should lead with equitable narrative (broken promise) and use Wazzan only for quantification - Day 3 jury data shows technical methodology landing weakly.",
          action:
            "Restructure closing argument: Brockman journal first, founding emails second, Wazzan FIFO as quantification overlay. Press equitable remedy framing.",
          impact:
            "Closing argument positioning is the highest-leverage strategic decision before Day 9 closing.",
          severity: "high",
        },
        {
          gap: "§17510.8 jury instruction request preservation",
          why: "If court doesn't rule on Daubert before verdict, need to preserve theory via FRCP 49(a) special interrogatory or jury instruction request.",
          action:
            "File written instruction request at close of evidence per FRCP 51(c). Specifically request strict-liability framing on §17510.8 elements.",
          impact:
            "Without preservation, theory may be forfeited on appeal under FRCP 51(d).",
          severity: "high",
        },
      ],
    },
  },
  neuro: {
    caseTitle: "Neurocentria v. Mando - Patent Infringement (Markman Pending)",
    layer1: {
      title: "Evidence Intensity by Claim",
      description: "How much each evidence category supports each claim.",
      rows: [
        { id: "infringe", label: "Infringement ('061 Patent)" },
        { id: "valid", label: "Validity (§102/§103)" },
        { id: "willful", label: "Willful Infringement" },
        { id: "counter-invalid", label: "Mando Invalidity Counterclaim" },
      ],
      cols: [
        { id: "depo", label: "Deposition" },
        { id: "docs", label: "Documentary" },
        { id: "expert", label: "Expert" },
        { id: "ja", label: "Judicial Admissions" },
        { id: "public", label: "Public Record" },
      ],
      grid: [
        [
          {
            s: 5,
            l: "Mixed",
            d: "Liu inventor depo (Mar 2026) provides foundation; Mao depo (Apr 2026) introduces counterpoint.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "'061 patent specification + Mando product CoAs + supplier records establish accused-product chemistry.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Baker (plaintiff claim construction) supports broad construction; rebuttal expert pending.",
          },
          {
            s: 3,
            l: "Weak",
            d: "Mando answer admits product manufacture; denies infringement on construction grounds.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Markman ruling pending; outcome materially affects infringement strength.",
          },
        ],
        [
          {
            s: 4,
            l: "Low-Mod",
            d: "Limited deposition development on validity - focus has been infringement.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Patent specification supports validity; defense relies on prior art (CN1200366A, MIT).",
          },
          {
            s: 2,
            l: "Critical Gap",
            d: "NO plaintiff technical expert retained for §102/§103 - Ziari (defense) unrebutted.",
          },
          {
            s: 4,
            l: "Low-Mod",
            d: "PTAB-style admissions limited; patent presumption supports validity but unrebutted prior art is dangerous.",
          },
          {
            s: 5,
            l: "Mixed",
            d: "Patent issuance presumption; Mayo/Alice §101 framework concerns remain.",
          },
        ],
        [
          {
            s: 5,
            l: "Mixed",
            d: "Mando 30(b)(6) on patent awareness pending; current record limited.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Pre-suit notice letter (Sept 2025) establishes Mando awareness; product launch post-notice supports willfulness.",
          },
          {
            s: 0,
            l: "Not Yet",
            d: "Willfulness expert pending - typically tied to damages expert.",
          },
          { s: 0, l: "None", d: "No defendant willfulness admissions." },
          {
            s: 5,
            l: "Mixed",
            d: "Public patent issuance + Mando trade press coverage establish constructive notice.",
          },
        ],
        [
          {
            s: 3,
            l: "Weak",
            d: "Limited deposition development on invalidity - defense expert (Ziari) report-based.",
          },
          {
            s: 8,
            l: "Very Strong",
            d: "CN1200366A + MIT Provisional 60/510,945 + L-threonate chelation chemistry literature.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Ziari (defense) anticipation + KSR obviousness analysis; plaintiff has no rebuttal expert.",
          },
          {
            s: 0,
            l: "None",
            d: "Plaintiff has not admitted invalidity grounds.",
          },
          {
            s: 6,
            l: "Mixed",
            d: "Prior art publicly available; PTO-grant presumption favors plaintiff but doesn't preclude invalidity.",
          },
        ],
      ],
    },
    layer2: {
      title: "Vulnerability Heat Map",
      description:
        "Markman, §101, expert, standing, willfulness vulnerabilities.",
      rows: [
        { id: "infringe", label: "Infringement ('061 Patent)" },
        { id: "valid", label: "Validity (§102/§103)" },
        { id: "willful", label: "Willful Infringement" },
        { id: "counter-invalid", label: "Mando Invalidity Counterclaim" },
      ],
      cols: [
        { id: "markman", label: "Markman Risk" },
        { id: "sj101", label: "§101 Risk" },
        { id: "daubert", label: "Daubert Risk" },
        { id: "standing", label: "Standing" },
        { id: "willful", label: "Willfulness Findings" },
      ],
      grid: [
        [
          {
            s: 8,
            l: "Critical",
            d: "Aenlle-Rocha 5/5 historical pattern favors narrow construction. Adams Clarifying Declaration (Dkt.151) strengthens defense narrowing.",
          },
          {
            s: 7,
            l: "High",
            d: "§101 motion fully briefed; Mayo/Alice ineligibility risk if 'L-threonate chelation' construed broadly.",
          },
          {
            s: 5,
            l: "Moderate",
            d: "Baker supportive opinions filed; defense Daubert challenge possible.",
          },
          { s: 3, l: "Low", d: "Patent-owner standing well-established." },
          { s: 0, l: "N/A", d: "Willfulness is separate finding." },
        ],
        [
          {
            s: 5,
            l: "Moderate",
            d: "Narrow construction may eliminate certain prior-art readings; broad construction may pull in more art.",
          },
          {
            s: 7,
            l: "High",
            d: "§101 ineligibility = total invalidity; Mayo/Alice risk on natural-phenomenon framing.",
          },
          {
            s: 9,
            l: "Critical",
            d: "NO plaintiff technical expert = Ziari unrebutted at trial. Single highest-priority gap.",
          },
          {
            s: 3,
            l: "Low",
            d: "Patent-owner standing N/A for validity; this is defense to infringement.",
          },
          { s: 0, l: "N/A", d: "Willfulness is separate." },
        ],
        [
          {
            s: 5,
            l: "Moderate",
            d: "Markman outcome affects infringement scope, which affects willfulness scope.",
          },
          { s: 5, l: "Moderate", d: "§101 invalidity moots willfulness." },
          {
            s: 6,
            l: "Moderate",
            d: "No willfulness expert yet retained; deadline approaching.",
          },
          { s: 3, l: "Low", d: "Standing for willfulness not contested." },
          {
            s: 6,
            l: "Mixed",
            d: "Pre-suit notice strong; Mando objective recklessness contestable.",
          },
        ],
        [
          {
            s: 5,
            l: "Moderate",
            d: "Markman affects validity analysis indirectly through claim scope.",
          },
          {
            s: 6,
            l: "High",
            d: "§101 motion success would moot validity counterclaim by invalidating patent first.",
          },
          {
            s: 3,
            l: "Low",
            d: "Ziari expert admissible; Daubert challenge unlikely on counterclaim side.",
          },
          {
            s: 3,
            l: "Low",
            d: "Defendant standing for invalidity is automatic in patent action.",
          },
          {
            s: 0,
            l: "N/A",
            d: "Willfulness not relevant to invalidity counterclaim.",
          },
        ],
      ],
    },
    layer3: {
      title: "Witness Coverage Map",
      description:
        "Each row is a fact plaintiff must prove. Each cell shows witness reliability.",
      rows: [
        { id: "chem", label: "Mando product contains L-threonate chelation" },
        { id: "posita", label: "POSITA construes claim terms broadly" },
        { id: "prior", label: "CN1200366A doesn't anticipate '061 claims" },
        { id: "cn-trans", label: "CN1200366A translation accuracy" },
        { id: "notice", label: "Mando had pre-suit knowledge of '061 patent" },
      ],
      cols: [
        { id: "liu", label: "Dr. Liu" },
        { id: "baker", label: "Dr. Baker" },
        { id: "adams", label: "Dr. Adams" },
        { id: "ziari", label: "Dr. Ziari" },
        { id: "slutsky", label: "Dr. Slutsky" },
      ],
      grid: [
        [
          {
            s: 6,
            l: "Mixed",
            d: "Liu testified to '061 chemistry - supportive but limited Mando-product comparison.",
          },
          {
            s: 7,
            l: "Strong",
            d: "Baker plaintiff expert - declarations supporting Mando product chemistry analysis.",
          },
          {
            s: 0,
            l: "Adverse",
            d: "Adams defense expert - adverse on infringement.",
          },
          {
            s: 0,
            l: "Adverse",
            d: "Ziari defense expert - focuses on validity.",
          },
          { s: 0, l: "None", d: "Slutsky not deposed; potential rogatory." },
        ],
        [
          { s: 0, l: "None", d: "Liu inventor - not POSITA expert." },
          {
            s: 8,
            l: "Very Strong",
            d: "Baker - Stanford Chemistry; opening Markman declaration Dkt.142.",
          },
          {
            s: 0,
            l: "Adverse",
            d: "Adams - narrow construction defense expert.",
          },
          { s: 0, l: "Adverse", d: "Ziari - primarily validity focus." },
          { s: 0, l: "None", d: "Slutsky no relevance to claim construction." },
        ],
        [
          { s: 0, l: "None", d: "Liu cannot opine on prior art." },
          {
            s: 0,
            l: "None",
            d: "Baker is claim construction expert, not prior art.",
          },
          { s: 0, l: "None", d: "Adams claim construction only." },
          {
            s: 0,
            l: "Adverse",
            d: "Ziari is the prior-art expert - adverse to plaintiff.",
          },
          { s: 0, l: "None", d: "Slutsky on inventorship, not prior art." },
        ],
        [
          { s: 0, l: "None", d: "Liu cannot translate Chinese." },
          { s: 0, l: "None", d: "Baker not translation expert." },
          { s: 0, l: "None", d: "Adams not translation expert." },
          {
            s: 5,
            l: "Mixed",
            d: "Ziari relies on translation; cross examines on accuracy.",
          },
          { s: 0, l: "None", d: "Slutsky no relevance." },
        ],
        [
          { s: 0, l: "None", d: "Liu inventor - no Mando knowledge." },
          { s: 0, l: "None", d: "Baker no notice testimony." },
          { s: 0, l: "None", d: "Adams no notice testimony." },
          { s: 0, l: "None", d: "Ziari no notice testimony." },
          {
            s: 0,
            l: "None",
            d: "Slutsky no notice testimony - fact witness needed.",
          },
        ],
      ],
    },
    layer4: {
      title: "Timeline Density Map",
      description:
        "Where is evidence concentrated? Where is it sparse? The 2004-2024 gap is structural.",
      periods: [
        {
          label: "1997-2003 · Prior Art Era",
          density: 8,
          note: "Dense. CN1200366A (2003), MIT Provisional 60/510,945 (2002), L-threonate chelation literature - Mando's strongest invalidity evidence.",
        },
        {
          label: "2004-2024 · Twenty-Year Gap",
          density: 1,
          note: "VERY SPARSE. Structural gap between prior art and current litigation. Defendant should look here for additional prior art; plaintiff needs continuous commercial success evidence.",
        },
        {
          label: "2024 · '061 Issuance",
          density: 5,
          note: "Moderate. Patent issuance, prosecution history, file wrapper.",
        },
        {
          label: "2025 · Notice + Pre-Suit",
          density: 6,
          note: "Moderate. Pre-suit notice letter to Mando, initial product analysis, complaint preparation.",
        },
        {
          label: "Q3-Q4 2025 · Pleading Stage",
          density: 7,
          note: "Dense. Complaint, answer, counterclaims, initial discovery.",
        },
        {
          label: "Q1-Q2 2026 · Markman Phase",
          density: 9,
          note: "Very dense. Markman briefing complete, Liu depo (Mar 2026), Mao depo (Apr 2026), Adams clarifying declaration (Dkt.151).",
        },
      ],
    },
    gaps: {
      closable: [
        {
          gap: "Plaintiff technical expert - §102/§103 prior art rebuttal",
          why: "Without expert, Ziari's anticipation and obviousness analyses go uncontested at trial. Single highest-priority strategic action item.",
          required:
            "Identify and retain qualifying technical expert (pharmaceutical chemistry / L-threonate chelation). Coordinate scope with Markman ruling timing.",
          leadtime:
            "21-day retention; 60-90 days to expert report; 30 days to depo readiness",
          deadline: "August 15, 2026 expert disclosure deadline",
          severity: "critical",
        },
        {
          gap: "Slutsky deposition (international rogatory)",
          why: "§102(f) inventorship-defense rebuttal requires Slutsky testimony on conception. Hague Convention process required.",
          required:
            "File letter rogatory via Hague Convention this week. Identify Israeli counsel and translate request.",
          leadtime: "90-120 days from letter rogatory filing",
          deadline:
            "This week - September 11 cut-off requires immediate filing",
          severity: "critical",
        },
      ],
      unclosable: [
        {
          gap: "Pre-2024 evidence of continuous commercial success",
          why: "Twenty-year gap (2004-2024) between prior art and patent issuance creates a presumption of obviousness if no commercial-success evidence exists.",
          strategicResponse:
            "Reframe non-obviousness around long-felt-need + failure of others. Cite expert testimony on technical difficulty. The 20-year gap itself supports patentability - POSITA had decades to develop and didn't.",
          authorities:
            "Graham v. John Deere obviousness factors; Stratoflex secondary considerations.",
          severity: "high",
        },
        {
          gap: "Slutsky testimony on conception (if rogatory denied)",
          why: "Israeli court may decline rogatory; Slutsky may be uncooperative or unavailable.",
          strategicResponse:
            "Build inventorship defense from Mao deposition + documentary record (lab notebooks, MIT TLO files). Argue Mao's contributions are background research, not conception.",
          authorities:
            "35 U.S.C. §256 inventorship correction framework; documentary corroboration sufficient absent fact testimony.",
          severity: "medium",
        },
      ],
      contested: [
        {
          gap: "L-threonate chelation construction (broad vs. narrow)",
          why: "Plaintiff: plain meaning to POSITA encompasses Mando product. Defense: specification examples define narrower scope, exclude in vivo formation.",
          partyPositions:
            "Plaintiff cites Phillips v. AWH hierarchy of construction tools; Baker testimony on POSITA understanding. Defense cites specification examples + §101 ineligibility risk if construed broadly to capture in vivo formation (natural phenomenon).",
          juryQuestion:
            "What is the scope of 'L-threonate chelation' as understood by one of skill in the art? (Markman ruling controls; jury instruction follows.)",
          severity: "critical",
        },
        {
          gap: "CN1200366A translation accuracy",
          why: "Plaintiff: official translation may not capture technical scope; cross-examine Ziari on translation reliability. Defense: translation is verifiable and corroborated by other prior art.",
          partyPositions:
            "Plaintiff: technical translation requires specialized linguist; cross examines accuracy. Defense: standard professional translation + corroborating MIT provisional + L-threonate literature establishes prior art scope.",
          juryQuestion:
            "Does CN1200366A disclose the specific L-threonate chelation chemistry of '061 claims?",
          severity: "high",
        },
      ],
      strategic: [
        {
          gap: "§101 motion sequencing relative to Markman",
          why: "§101 ruling could moot Markman by invalidating patent. Order of rulings affects strategic posture.",
          action:
            "Monitor docket for §101 ruling. If §101 denied, focus shifts to Markman. If §101 granted, case effectively over - appeal posture only.",
          impact:
            "§101 ruling is potentially case-dispositive; Markman is highly material but not dispositive.",
          severity: "high",
        },
        {
          gap: "§285 fee documentation accumulation",
          why: "Mando counterclaim for §285 exceptional-case fees requires documentation from inception. Plaintiff strategic gaps (no technical expert, broad construction in face of narrow precedent, deferred Slutsky discovery) may support exceptional-case finding.",
          action:
            "Document plaintiff's strategic missteps and discovery deferrals contemporaneously. Maintain detailed time records for fee preservation.",
          impact:
            "§285 fee award post-judgment depends on contemporaneous documentation.",
          severity: "medium",
        },
      ],
    },
  },
};

const APPELLATE_RISK_DATA = {
  ...ILE_K_AR,
  juul: {
    caseTitle: "Seliavski v. JUUL Labs MDL - Appellate Risk Register",
    rulings: [
      {
        id: "juul-msj",
        date: "Pending - argued Oct 31, 2025",
        title: "MSJ Hearing - Sonner-Pak / FCLAA Preemption",
        court: "Judge Orrick · N.D. Cal.",
        dockets: [
          "Dkt.4341 motion",
          "Dkt.3447 opposition",
          "Dkt.4453 hearing transcript",
        ],
        type: "MSJ",
        adverseTo: "pending",
        favoredParty: "open",
        summary:
          "JLI MSJ hearing held October 31, 2025 on three threshold issues: (1) Sonner v. Premier Nutrition equitable-relief bar applied to SFUSD claims; (2) FCLAA preemption of state-law warning claims as applied to e-cigarettes; (3) Pak admission destroying SFUSD past-cost damages. Six-month deliberation pending.",
        standardOfReview:
          "De novo for legal questions (preemption, Sonner doctrine application, statutory interpretation) · Abuse of discretion for evidentiary rulings",
        preservation: "automatic-on-final-judgment",
        preservationDetail:
          "MSJ rulings reviewable only on final judgment per 28 U.S.C. §1291. No interlocutory appeal available unless certified under §1292(b). Track as post-trial appellate issue.",
        reversalLikelihood: 55,
        reversalAssessment:
          "FCLAA preemption is the most significant unresolved appellate issue. Whether FCLAA preempts state-law warning claims as applied to e-cigarettes is a question the circuits haven't uniformly answered. Ninth Circuit hasn't ruled definitively. If Orrick rules on preemption at SJ - granting or denying - the issue is potentially case-dispositive on the failure-to-warn claims and ripe for §1292(b) interlocutory appeal certification.",
        appealability:
          "§1292(b) certification possible if Orrick grants preemption · otherwise post-trial",
        circuitSplit: "moderate",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Cipollone v. Liggett Group (1992) [cigarette FCLAA preemption framework]; Bates v. Dow AgroSciences (2005) [labeling preemption requires textual nexus]; Wyeth v. Levine (2009) [presumption against preemption in failure-to-warn]; FDA Family Smoking Prevention Act §916 [FCLAA savings clause in tobacco context]",
        urgency: "high",
      },
      {
        id: "juul-classcert",
        date: "February 26, 2026",
        title: "Antitrust Class Certification Grant",
        court: "Judge Orrick · N.D. Cal. (related case 3:20-cv-02345)",
        dockets: ["Dkt.635 grant order"],
        type: "Class Cert",
        adverseTo: "Defendant",
        favoredParty: "Plaintiff",
        summary:
          "Direct Purchaser Class certified February 26, 2026. Court found common impact methodology survives Daubert challenge at class certification stage. Indirect purchaser class also proceeding.",
        standardOfReview:
          "Abuse of discretion (Comcast Corp. v. Behrend, 569 U.S. 27)",
        preservation: "preserved",
        preservationDetail:
          "JLI/Altria opposed certification; record preserved on damages methodology, predominance, and adequacy. Rule 23(f) interlocutory appeal window expired (14 days from grant). Defense waited for trial.",
        reversalLikelihood: 25,
        reversalAssessment:
          "Once certified, reversal probability drops materially. Court conducted rigorous analysis (Wal-Mart v. Dukes standard met). Defense's strongest issue - class-wide impact methodology - was specifically addressed and rejected. JLI/Altria will revisit on post-trial appeal but Comcast/Tyson Foods scrutiny will be deferential to district court findings.",
        appealability: "Rule 23(f) window expired · post-trial only",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Comcast Corp. v. Behrend (2013) [class-wide damages methodology]; Wal-Mart v. Dukes (2011) [commonality]; Tyson Foods v. Bouaphakeo (2016) [statistical evidence in class actions]",
        urgency: "low",
      },
      {
        id: "juul-pak",
        date: "December 21, 2021 (deposition); admissibility ruling pending",
        title: "Pak 30(b)(6) Admission - Evidentiary Posture",
        court: "Judge Orrick · N.D. Cal.",
        dockets: ["Dkt.3447 SOF citing Pak depo"],
        type: "Evidentiary",
        adverseTo: "Plaintiff",
        favoredParty: "Defendant",
        summary:
          "Quarry Pak's 30(b)(6) testimony 'we don't have that information' regarding SFUSD past program costs is the most significant evidentiary admission in the case. JLI cites at MSJ as binding admission destroying past-damages claim. SFUSD argues Pak's testimony reflects only documentation gaps, not absence of damages.",
        standardOfReview:
          "Abuse of discretion (FRE 801(d)(2)(D) admission scope; Sonner equitable-relief framework)",
        preservation: "preserved",
        preservationDetail:
          "Plaintiffs preserved the issue at MSJ briefing (Dkt.3447). If Orrick rules Pak's admission is dispositive of past damages, plaintiffs need to argue the testimony reflects documentation gap, not damages absence - preserved by full briefing.",
        reversalLikelihood: 40,
        reversalAssessment:
          "FRE 801(d)(2)(D) makes the testimony binding on SFUSD as an admission. The legal question - whether that admission is dispositive of damages - is a Sonner v. Premier Nutrition analysis. If Orrick rules narrowly that Pak admission destroys past-cost damages but preserves equitable abatement (Dorn forward-looking model), affirmance likely. If Orrick rules expansively that Pak admission destroys all damages including equitable, reversal probability rises because plaintiffs can argue the rule reads Sonner too broadly.",
        appealability: "final judgment only",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Sonner v. Premier Nutrition Corp. (9th Cir. 2020) [equitable bar requires adequate legal remedy]; FRE 801(d)(2)(D) [vicarious admissions]; Beachy line of cases on damages estimation",
        urgency: "medium",
      },
      {
        id: "juul-meta",
        date: "April 14, 2026 (production order); response due May 11, 2026",
        title: "Meta Production Order - Decedent Account Data",
        court: "Judge Orrick · N.D. Cal.",
        dockets: ["Dkt.4488 production order", "Dkt.4497 extension stip"],
        type: "Discovery",
        adverseTo: "third-party",
        favoredParty: "Plaintiff",
        summary:
          "Court ordered Meta to produce decedent account data including JLI Instagram targeting parameters and engagement metrics by user age. Production deadline May 11, 2026 (extended from May 8).",
        standardOfReview:
          "Abuse of discretion · Magistrate Judge review under FRCP 72",
        preservation: "third-party · plaintiff-protected",
        preservationDetail:
          "Order is third-party discovery; standard appellate framework applies. Plaintiff has no preservation issue. Meta will likely produce.",
        reversalLikelihood: 15,
        reversalAssessment:
          "Third-party discovery orders are rarely reversed. Production scope is narrowly tailored to JLI marketing relevant to MDL. Meta's production constitutes evidence; reversibility is downstream of any motion in limine ruling.",
        appealability:
          "interlocutory available under §1292(b) if certified · unlikely",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "FRCP 26(b) [proportionality]; Mohawk Industries v. Carpenter (2009) [collateral order doctrine for discovery]",
        urgency: "low",
      },
    ],
    pendingAlerts: [
      {
        id: "juul-alert-1",
        time: "Pre-MSJ ruling - anticipated next 30-60 days",
        type: "preservation-required",
        ruling: "MSJ ruling on Sonner-Pak / FCLAA preemption",
        urgency: "high",
        suggestedLanguage:
          "If MSJ granted on FCLAA preemption: Move within 14 days for §1292(b) certification - argue (1) the preemption question is a pure legal issue, (2) Ninth Circuit has not ruled, (3) determination materially advances termination of the litigation. The preemption ruling is the highest-value interlocutory appeal target in the case.",
        actionRequired:
          "Calendar §1292(b) deadline for 14 days post-ruling. Pre-draft certification motion now.",
      },
      {
        id: "juul-alert-2",
        time: "At antitrust trial - Sept 28, 2026",
        type: "contemporaneous-objection",
        ruling:
          "Antitrust trial-stage Daubert challenges to plaintiff economic methodology",
        urgency: "medium",
        suggestedLanguage:
          "Preserve through specific objection: 'Your Honor, defense objects to this expert's methodology under Daubert. We hereby move to exclude on grounds that (a) the methodology lacks reliability under Comcast Corp. v. Behrend, (b) the trial-specific application departs from the class-cert methodology in [specific way], and (c) the testimony fails Rule 702.'",
        actionRequired:
          "Prepare specific Daubert objection language for each expert. Pre-draft mid-trial Daubert motion if any unforeseen methodology emerges.",
      },
    ],
  },
  musk: {
    caseTitle: "Musk v. Altman et al. - Appellate Risk Register",
    rulings: [
      {
        id: "musk-pi",
        date: "March 4, 2025",
        title: "Preliminary Injunction Denial",
        court: "Judge YGR · N.D. Cal.",
        dockets: ["Dkt.121 PI denial order"],
        type: "PI",
        adverseTo: "Plaintiff",
        favoredParty: "Defendant",
        summary:
          "Musk's PI motion seeking to halt OpenAI's for-profit conversion was denied. YGR found low likelihood of success on the merits and absence of irreparable harm.",
        standardOfReview:
          "Abuse of discretion for PI denial overall · de novo for underlying legal questions including whether a charitable trust was created (Winter v. Natural Resources Defense Council factors)",
        preservation: "WAIVED",
        preservationDetail:
          "⚠️ INTERLOCUTORY APPEAL DEADLINE EXPIRED. PI denial was immediately appealable under 28 U.S.C. §1292(a)(1). 30-day notice period from March 4, 2025 expired April 3, 2025. Litigation team did not file. Issue is now reviewable only on final judgment - and only as it bears on the trust-formation issue.",
        reversalLikelihood: 30,
        reversalAssessment:
          "On post-trial appeal, the PI denial itself is largely moot - the trial outcome will speak to whether a charitable trust was created. The standard-of-review analysis for the legal issue (de novo on charitable trust formation under California law) applies fresh to the trial record. The interlocutory window's loss means plaintiff can't argue the PI standard at the Ninth Circuit; they must argue the trial verdict's underlying legal predicate. That's a net loss but recoverable.",
        appealability:
          "§1292(a)(1) interlocutory window expired April 3, 2025 (waived) · post-trial only on charitable-trust formation legal question",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Winter v. Natural Resources Defense Council (2008) [PI factors]; Estate of Heggstad (Cal. 1993) [informal trust formation]; CACI 4900 [trust elements jury instruction]",
        urgency: "high - preservation lesson",
      },
      {
        id: "musk-msj",
        date: "April 4, 2026 (partial denial)",
        title: "MSJ Partial Denial",
        court: "Judge YGR · N.D. Cal.",
        dockets: ["Dkt.390 partial denial"],
        type: "MSJ",
        adverseTo: "Defendant",
        favoredParty: "Plaintiff",
        summary:
          "YGR denied OpenAI/Altman/Microsoft MSJ on Counts XVIII (Charitable Trust Breach), IV (Unjust Enrichment), and XIX (A&A). SOL defense rejected at SJ stage. Phase 2 antitrust counts (VIII-XV) stayed pending Phase 1 verdict.",
        standardOfReview:
          "De novo (Anderson v. Liberty Lobby framework on summary judgment standard)",
        preservation: "automatic-on-final-judgment",
        preservationDetail:
          "MSJ denials are not immediately appealable. Defense preserved SOL, charitable-trust formation, and BFP defense for trial and post-trial review.",
        reversalLikelihood: 35,
        reversalAssessment:
          "On post-trial appeal, defense can argue MSJ should have been granted on SOL grounds (Sept 2020 'captured by Microsoft' tweet as constructive knowledge trigger under Pretrial Order No. 3). De novo review favors appellant. However, if jury makes credibility findings on Musk's reliance and OpenAI's representations, those findings receive deference under Anderson. The strongest appellate defense is the legal question: is informal trust formation cognizable under California law in the corporate-donation context? Schizer has the precedent, but it's contestable.",
        appealability: "final judgment only",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Anderson v. Liberty Lobby (1986); Estate of Heggstad (Cal. 1993); CA Bus. & Prof. Code §17510.8; Cal. Corp. Code §5210",
        urgency: "medium",
      },
      {
        id: "musk-wazzan",
        date: "April 4, 2026",
        title: "Wazzan Partial Daubert Exclusion",
        court: "Judge YGR · N.D. Cal.",
        dockets: ["PTO No. 2, Dkt.452"],
        type: "Daubert",
        adverseTo: "Plaintiff",
        favoredParty: "Defendant",
        summary:
          "Court excluded Wazzan Opinions 1 (reasonable royalty) and 2 (lost-investor-opportunity damages) as based on speculative counterfactual assumptions. Opinions 3-6 (FIFO tracing, Microsoft equity attribution, valuation) admitted in full. Defense will emphasize at trial that the exclusion signals methodological unreliability.",
        standardOfReview:
          "Abuse of discretion (Kumho Tire Co. v. Carmichael; FRE 702)",
        preservation: "preserved",
        preservationDetail:
          "Plaintiff preserved through Wazzan reply opinion + PTO No. 2 briefing. Cross-examination redirect prep emphasizes Opinions 3-6 stand on their own methodology.",
        reversalLikelihood: 25,
        reversalAssessment:
          "Daubert rulings receive substantial deference. Court's specific findings - Opinions 1-2 'based on speculative assumptions about counterfactual investor behavior' - are well-supported by the record. Reversal would require showing the court applied an incorrect legal standard, not just disagreement with the application. Plaintiff's stronger appeal target is the trust-formation legal question, not the Daubert exclusion.",
        appealability: "final judgment only",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Daubert v. Merrell Dow (1993); Kumho Tire (1999); FRE 702",
        urgency: "low",
      },
      {
        id: "musk-17510",
        date: "Briefed April 30, 2026 - ruling pending",
        title: "§17510.8 Strict-Liability Theory · Daubert Pending",
        court: "Judge YGR · N.D. Cal.",
        dockets: ["Dkt.467 defense Daubert", "Dkt.471 plaintiff opposition"],
        type: "Daubert",
        adverseTo: "pending",
        favoredParty: "open",
        summary:
          "Defense filed Daubert motion to exclude Schizer supplemental opinion on §17510.8 charitable solicitations strict-liability application. Plaintiff opposed. Reply expected within 14 days. Hearing not yet scheduled.",
        standardOfReview:
          "Abuse of discretion on Daubert · de novo on §17510.8 statutory interpretation",
        preservation: "pending",
        preservationDetail:
          "⚠️ HIGH-LEVERAGE PRESERVATION ISSUE. If Daubert excludes Schizer supplemental, the §17510.8 theory effectively dies. Plaintiff must (1) preserve via FRCP 51(c) jury instruction request at close of evidence and (2) request FRCP 49(a) special interrogatory on §17510.8 elements. Without preservation, theory may be forfeited on appeal under FRCP 51(d).",
        reversalLikelihood: 50,
        reversalAssessment:
          "§17510.8 has limited case law applying to large-scale donor solicitations. Whether 2015-2017 emails are 'charitable solicitations' under the statute is a novel application. If Daubert excludes, plaintiff has strong de novo legal-question appeal: did the trial court apply the correct legal standard for solicitation under §17510.8? If admitted, defense has strong appeal on whether the statute reaches private corporate-donor communications.",
        appealability: "final judgment only · FRCP 51(c) preservation REQUIRED",
        circuitSplit: "uncertain",
        circuit: "9th Cir.",
        relevantPrecedent:
          "Cal. Bus. & Prof. Code §17510.8; California charitable solicitation case law (limited); Daubert/Kumho Tire framework",
        urgency: "critical",
      },
      {
        id: "musk-pto4",
        date: "PTO No. 4, Dkt.398",
        title: "Schizer/Hemel Daubert - Both Admitted",
        court: "Judge YGR · N.D. Cal.",
        dockets: ["Dkt.398"],
        type: "Daubert",
        adverseTo: "both-affirmed",
        favoredParty: "neutral",
        summary:
          "Court admitted both Schizer (plaintiff) and Hemel (defense) charitable trust experts in full. 'Genuine doctrinal dispute' - court declined to adjudicate on Daubert.",
        standardOfReview: "Abuse of discretion",
        preservation: "automatic",
        preservationDetail: "Both sides preserved. No appeal target.",
        reversalLikelihood: 5,
        reversalAssessment:
          "Both experts admitted = no Daubert grounds for either side to appeal.",
        appealability: "none",
        circuitSplit: "low",
        circuit: "9th Cir.",
        relevantPrecedent: "FRE 702",
        urgency: "low",
      },
    ],
    pendingAlerts: [
      {
        id: "musk-alert-1",
        time: "⚡ Day 3 trial · Brockman cross-examination",
        type: "offer-of-proof",
        ruling:
          "Live evidentiary ruling - anticipated objection on Brockman cross",
        urgency: "critical",
        suggestedLanguage:
          "⚡ IF YGR SUSTAINS OBJECTION: Make offer of proof IMMEDIATELY on the record before the next question. 'Your Honor, for the record, defense would offer [exhibit/line of questioning] to show [purpose - bias, motive, prior inconsistent statement, course of conduct]. We believe this is admissible under FRE [801(d)(1)(A) prior inconsistent statement / 608 character / 613 specific instances]. The probative value outweighs any prejudice under FRE 403 because [specific reason]. Without this offer, we waive the issue for appeal under FRCP 103(a)(2).'",
        actionRequired:
          "Pre-script offers of proof for Brockman, Altman, Sutskever, K. Scott crosses. Save in War Room as Quick Templates.",
      },
      {
        id: "musk-alert-2",
        time: "Pre-Phase 1 verdict · jury instruction conference",
        type: "jury-instruction-preservation",
        ruling: "§17510.8 jury instruction request",
        urgency: "critical",
        suggestedLanguage:
          "At close of evidence, file written request per FRCP 51(c): 'Plaintiff requests an instruction on Cal. Bus. & Prof. Code §17510.8 strict liability for false or misleading charitable solicitations, modeled on the statutory plain text. Plaintiff also requests a FRCP 49(a) special interrogatory: \"Did Defendants make false or misleading representations in the 2015-2017 communications constituting charitable solicitations under §17510.8?\"'",
        actionRequired:
          "Draft the proposed instruction now. File before close of evidence. Without this, FRCP 51(d) forfeits the appellate issue.",
      },
      {
        id: "musk-alert-3",
        time: "Day 5 · Nadella testimony",
        type: "contemporaneous-objection",
        ruling: "BFP defense testimony scope",
        urgency: "high",
        suggestedLanguage:
          "If Nadella testifies beyond the scope of disclosed witness statement, object: 'Your Honor, this testimony exceeds the disclosure under FRCP 26(a)(1). Objection sustained.' If overruled, request continuance for expert response: 'Plaintiff requests a continuance to address the new substance under FRCP 26(e)(2). Failing that, plaintiff preserves objection on undisclosed-testimony grounds for appellate review.'",
        actionRequired:
          "Review Nadella's disclosed witness statement carefully. Pre-script scope objections.",
      },
    ],
  },
  neuro: {
    caseTitle: "Neurocentria v. Mando - Appellate Risk Register",
    rulings: [
      {
        id: "neuro-markman",
        date: "Briefed Q1 2026 - ruling pending",
        title: "Markman Claim Construction Ruling - Pending",
        court: "Judge Aenlle-Rocha · C.D. Cal.",
        dockets: [
          "Dkt.142 plaintiff opening",
          "Dkt.144 defense response",
          "Dkt.151 Adams Clarifying Decl. (Mar 31, 2026)",
          "Dkt.158 plaintiff reply",
        ],
        type: "Markman",
        adverseTo: "pending",
        favoredParty: "open",
        summary:
          "Markman briefing complete. Adams Clarifying Declaration (March 31, 2026) addresses §101 ambiguity raised in plaintiff's reply. Construction of 'L-threonate chelation' and 'in vivo formation' is potentially case-dispositive on infringement.",
        standardOfReview:
          "DE NOVO at Federal Circuit (Teva Pharmaceuticals USA v. Sandoz, 135 S.Ct. 831 (2015)) - the most appellant-favorable standard in patent cases",
        preservation: "automatic",
        preservationDetail:
          "Automatic preservation upon filing of Markman briefs and declarations. No additional preservation steps required for the construction itself; specific objections needed for evidentiary rulings during Markman hearing.",
        reversalLikelihood: 45,
        reversalAssessment:
          "De novo Federal Circuit review for claim construction is the most favorable standard for an appellant in patent cases. Aenlle-Rocha's 5/5 historical pattern favors narrow construction. If plaintiff loses on a key term (likely outcome), the broad-construction reversal probability is materially elevated. Federal Circuit precedent on specification-vs-plain-meaning hierarchy (Phillips v. AWH framework) and the §101 separation from claim construction are plaintiff's strongest appellate points.",
        appealability:
          "final judgment only · §1292(b) certification possible if Markman is dispositive",
        circuitSplit: "low",
        circuit: "Federal Circuit",
        relevantPrecedent:
          "Teva Pharmaceuticals USA v. Sandoz, 135 S.Ct. 831 (2015) [de novo standard]; Phillips v. AWH, 415 F.3d 1303 (Fed. Cir. 2005) [hierarchy of construction tools]; Markman v. Westview Instruments (1996); Mayo/Alice §101 framework",
        urgency: "high",
      },
      {
        id: "neuro-101",
        date: "Briefed - ruling pending",
        title: "§101 Patent Eligibility Motion - Pending",
        court: "Judge Aenlle-Rocha · C.D. Cal.",
        dockets: ["§101 motion + opposition"],
        type: "§101",
        adverseTo: "pending",
        favoredParty: "open",
        summary:
          "Mando moved for early dismissal under §101 arguing 'L-threonate chelation' as construed broadly is a natural phenomenon (in vivo formation) ineligible under Mayo/Alice framework. Specification examples create ambiguity that Adams Clarifying Declaration addresses.",
        standardOfReview: "De novo at Federal Circuit",
        preservation: "automatic",
        preservationDetail:
          "Filed motion creates automatic preservation. No additional steps needed unless Markman ruling moots the §101 issue first.",
        reversalLikelihood: 40,
        reversalAssessment:
          "§101 ineligibility ruling is highly appealable under de novo Federal Circuit review. Mayo/Alice two-step framework requires careful application - district court rulings are often modified at the Federal Circuit. If Aenlle-Rocha grants §101 dismissal, plaintiff has a strong appellate case that the specification establishes a non-natural manufactured form (the chelation chemistry) distinct from in vivo formation. If §101 denied, defense has a meaningful appeal but Federal Circuit affirmance probability rises.",
        appealability: "final judgment only · §1292(b) certification possible",
        circuitSplit: "moderate",
        circuit: "Federal Circuit",
        relevantPrecedent:
          "Mayo Collaborative Services v. Prometheus Labs (2012); Alice Corp. v. CLS Bank (2014); Diamond v. Diehr (1981); Bilski v. Kappos (2010)",
        urgency: "high",
      },
      {
        id: "neuro-sanctions",
        date: "Withdrawn (Dkt.104)",
        title: "Sanctions Motion - Withdrawn",
        court: "Judge Aenlle-Rocha · C.D. Cal.",
        dockets: ["Dkt.104 withdrawal"],
        type: "Sanctions",
        adverseTo: "none",
        favoredParty: "neutral",
        summary:
          "Defendant Rule 11 sanctions motion withdrawn. Signals willingness to de-escalate. No ruling, no appeal exposure.",
        standardOfReview: "N/A",
        preservation: "N/A",
        preservationDetail: "No motion = no preservation issue.",
        reversalLikelihood: 0,
        reversalAssessment:
          "Withdrawn motion creates no appellate risk for either side.",
        appealability: "N/A",
        circuitSplit: "N/A",
        circuit: "Federal Circuit",
        relevantPrecedent: "FRCP 11",
        urgency: "low",
      },
      {
        id: "neuro-stricken",
        date: "Stricken filings",
        title: "Stricken Stipulations (Local Rules)",
        court: "Judge Aenlle-Rocha · C.D. Cal.",
        dockets: ["Dkt.80-81 stricken"],
        type: "Procedural",
        adverseTo: "Plaintiff",
        favoredParty: "Defendant",
        summary:
          "Two stipulations stricken for non-compliance with Local Rules. Procedural slip with no merit-based appellate exposure.",
        standardOfReview: "Abuse of discretion",
        preservation: "N/A",
        preservationDetail:
          "Stricken filings have no appellate consequence on the merits.",
        reversalLikelihood: 5,
        reversalAssessment:
          "Procedural compliance rulings receive maximum deference. Not appealable in any meaningful sense.",
        appealability: "none",
        circuitSplit: "low",
        circuit: "Federal Circuit",
        relevantPrecedent: "C.D. Cal. Local Rules",
        urgency: "low",
      },
    ],
    pendingAlerts: [
      {
        id: "neuro-alert-1",
        time: "Pre-Markman ruling - pending",
        type: "preservation-required",
        ruling: "Markman ruling preservation",
        urgency: "high",
        suggestedLanguage:
          "Markman is automatic preservation. However, prepare for §1292(b) certification motion within 14 days of ruling if construction is dispositive. Argue: (1) construction is a pure legal question; (2) immediate appeal will materially advance termination; (3) substantial difference of opinion on construction methodology under Phillips. The Markman ruling is potentially case-dispositive on infringement.",
        actionRequired:
          "Pre-draft §1292(b) motion. Calendar 14-day deadline post-Markman ruling.",
      },
      {
        id: "neuro-alert-2",
        time: "Pre-fact-discovery cutoff Sept 11, 2026",
        type: "preservation-required",
        ruling: "Slutsky letter rogatory preservation",
        urgency: "critical",
        suggestedLanguage:
          "⚡ Slutsky international rogatory must be filed THIS WEEK. Without Slutsky deposition (or competent substitute evidence), §102(f) inventorship-defense rebuttal fails for evidentiary insufficiency, which is reviewable on de novo Federal Circuit appeal but cannot be created on appeal - must be developed below.",
        actionRequired:
          "File Hague Convention letter rogatory THIS WEEK. 90-120 day lead time means the September 11 cutoff is the hard ceiling.",
      },
      {
        id: "neuro-alert-3",
        time: "At expert disclosure - Aug 15, 2026",
        type: "preservation-required",
        ruling: "Plaintiff technical expert retention preservation",
        urgency: "critical",
        suggestedLanguage:
          "⚡ Without plaintiff technical expert disclosed by August 15, 2026, Ziari (defense) prior-art testimony goes uncontested. The Federal Circuit cannot create an expert record on appeal. Plaintiff would lose §102 anticipation and §103 obviousness analyses on summary judgment without ability to recover post-judgment.",
        actionRequired:
          "Authorize expert retention IMMEDIATELY. 60-90 day expert work window means August 15 is the hard ceiling. Single highest-priority strategic action item.",
      },
    ],
  },
};

const PROC_TRAPS_DATA = {
  ...ILE_K_PT,
  juul: {
    plaintiff: {
      activeMonitoring: [
        {
          id: "juul-act-1",
          time: "Active",
          severity: "medium",
          observation:
            "JLI MSJ briefing slow-walked with three stipulated extensions",
          analysis:
            "Pattern of extension stipulations on substantive briefing while pressing tight deadlines on plaintiff supplemental discovery. Asymmetric scheduling tactic: defense buys time on the issues where time helps them, demands speed where time helps plaintiff.",
          warning:
            "Resist further extensions on antitrust track. Press for trial-expert disclosure deadline (July 15, 2026) enforcement.",
          ref: "Antitrust track scheduling order",
        },
        {
          id: "juul-act-2",
          time: "Active",
          severity: "high",
          observation:
            "Aggressive sealing motions on Altria-produced documents",
          analysis:
            "~80% of Altria-produced documents flagged AEO/Outside Counsel Only at first production. JLI/Altria are protecting the institutional record from public unsealing.",
          warning:
            "Press for unsealing of Dkt.3393-equivalent materials. Public-interest framing.",
          ref: "Altria production sealing pattern",
        },
      ],
      waiverRisks: [
        {
          id: "juul-wav-1",
          time: "Critical · MSJ ruling pending",
          severity: "critical",
          ruling:
            "§1292(b) interlocutory appeal preservation if FCLAA preemption granted",
          situation:
            "If Orrick rules on FCLAA preemption (granting), plaintiff has 14 days to file §1292(b) certification opposition. Without specific objection language preserving the preemption opposition, the issue may be deemed waived for opposition purposes.",
          requiredLanguage:
            "Within 14 days of ruling, file: 'Plaintiffs respectfully oppose Defendants' anticipated §1292(b) certification motion. The FCLAA preemption ruling presents a pure legal question, but immediate appeal will not materially advance termination of the litigation because [specific reasoning preserved here]. Plaintiffs preserve all objections to certification.'",
          consequence:
            "Without preservation language, opposition may be deemed waived. Issue forfeited for §1292(b) opposition.",
          authority: "28 U.S.C. §1292(b); Coopers & Lybrand v. Livesay (1978)",
        },
        {
          id: "juul-wav-2",
          time: "At antitrust trial Sept 28, 2026",
          severity: "high",
          ruling:
            "Daubert challenges to plaintiff's economic methodology preserved or waived",
          situation:
            "Trial expert disclosure July 15 sets methodology floor. Mid-trial Daubert challenges by JLI must be specifically opposed on the record or waived for appeal.",
          requiredLanguage:
            "Specific objection: 'Your Honor, plaintiff opposes defendant's trial-stage Daubert motion on grounds that (a) the methodology is materially identical to the class-cert methodology this Court already approved (Dkt.635), (b) defendant had opportunity to challenge at class certification and did so, and (c) re-litigating Daubert at trial absent new methodology shifts is procedural sandbagging.'",
          consequence:
            "Without specific opposition, mid-trial Daubert exclusion may be deemed unobjected and unappealable.",
          authority: "FRCP 51(c); Comcast Corp. v. Behrend (2013)",
        },
      ],
      deadlineTraps: [
        {
          id: "juul-dl-1",
          trigger: "If MSJ order signals remand of opt-out cases",
          time: "14 days post-trigger",
          severity: "high",
          deadline:
            "Joint letter on supplemental discovery scope due 14 days after remand signal",
          source:
            "Buried in Dkt.4476 December 16 CMC minute entry - does NOT appear in any scheduling order",
          aiAlert:
            "This deadline is a CMC-buried trigger. When the MSJ order drops, the 14-day clock starts automatically without further notice. If you miss this, supplemental discovery on opt-out PI cases is foreclosed.",
          actionItem:
            "Pre-draft joint letter framework now. Calendar 14-day clock from MSJ ruling date.",
        },
        {
          id: "juul-dl-2",
          trigger: "Antitrust class trial Sept 28, 2026",
          time: "July 15 expert disclosure · Aug 15 Daubert · Sept 8 PTO",
          severity: "medium",
          deadline: "Cascading pre-trial deadlines",
          source: "Antitrust track scheduling order",
          aiAlert:
            "Each pre-trial deadline triggers the next. Missing any one cascades. Particularly: trial expert disclosure July 15 triggers 30-day Daubert window.",
          actionItem: "Master calendar all four deadlines now. Review weekly.",
        },
      ],
      counselPatterns: [
        {
          id: "juul-cp-1",
          counselName: "JLI Counsel - Kirkland & Ellis",
          pattern:
            "Pattern of late-night Friday filings before Monday deadlines",
          interpretation:
            "Strategic filing timing imposes weekend response cost on plaintiff team. Limits plaintiff prep time and creates pressure-driven response posture.",
          ourMove:
            "Anticipate Friday filings. Pre-positioned response teams for major deadlines. Don't react over weekends - file structured response Monday morning.",
          judgeResponse:
            "Orrick has not commented on this pattern, but FRCP-compliant filings carry no formal sanction risk.",
        },
        {
          id: "juul-cp-2",
          counselName: "Altria Counsel - Orrick (no relation)",
          pattern: "Selective 30(b)(6) topic carve-outs",
          interpretation:
            "Repeatedly objected to 30(b)(6) topics on Altria-knowledge questions. Protecting the Altria due diligence record.",
          ourMove:
            "Renew 30(b)(6) on Altria knowledge topics with refined topic list. Subpoena Altria officers individually if corporate depo blocked.",
          judgeResponse:
            "Orrick has not addressed; opposing parties bear the cost of objection persistence.",
        },
      ],
      standingOrders: [
        {
          id: "juul-so-1",
          order: "Judge Orrick MDL Standing Order - Motion Page Limits",
          requirement:
            "MSJ briefs limited to 35 pages. Reply briefs limited to 20 pages. Supplemental briefs require leave.",
          recentFiling:
            "Plaintiffs' MSJ opposition (Dkt.3447) filed at 35 pages. No supplemental briefing without leave.",
          conflict:
            "None currently. Watch for supplemental brief temptation post-MSJ ruling.",
          recommendation:
            "If supplemental issues arise post-MSJ ruling (FCLAA preemption analysis, Sonner update), file motion for leave first.",
        },
        {
          id: "juul-so-2",
          order: "Judge Orrick MDL Standing Order - Discovery Disputes",
          requirement:
            "Joint letter brief required for all discovery disputes. 5-page limit per side. Meet-and-confer before letter brief.",
          recentFiling:
            "Meta production order (Dkt.4488) preceded by joint letter compliance.",
          conflict: "None. Pattern is being followed.",
          recommendation:
            "Continue joint letter format for any remaining discovery disputes.",
        },
      ],
    },
    defendant: {
      activeMonitoring: [
        {
          id: "juul-d-act-1",
          time: "Active",
          severity: "high",
          observation:
            "Plaintiff coordinated press strategy with each major filing",
          analysis:
            "Major filings (MSJ opposition Dkt.3447, class cert briefing) accompanied by AP/Reuters press releases within 24 hours. Running parallel public-pressure campaign.",
          warning:
            "Counter with measured corporate communications. Avoid public statements that reinforce plaintiffs' narrative.",
          ref: "AP/Reuters coverage timeline 2024-2026",
        },
        {
          id: "juul-d-act-2",
          time: "Active",
          severity: "medium",
          observation: "Massive third-party subpoena program",
          analysis:
            "Subpoenas to Meta, FDA, FTC, state AGs, school districts. Building 'industry-wide coordination' narrative beyond direct case parties.",
          warning:
            "Resist over-broad third-party discovery. Object on relevance/proportionality. Move for protective orders on Meta production scope.",
          ref: "Third-party subpoena docket 2025-2026",
        },
      ],
      waiverRisks: [
        {
          id: "juul-d-wav-1",
          time: "Active · ongoing",
          severity: "high",
          ruling: "FCLAA preemption preservation across multiple counts",
          situation:
            "FCLAA preemption argument must be raised on every count where it applies, not just failure-to-warn. RICO predicate, public nuisance, antitrust state-law-mirror arguments all need preemption preservation.",
          requiredLanguage:
            "In all responsive briefing: 'Defendants reserve and preserve federal preemption defenses under FCLAA as to [specific count]. Federal preemption is a question of law reviewable de novo on appeal.'",
          consequence:
            "Failure to preserve count-by-count may forfeit preemption argument as to specific counts on appeal.",
          authority: "FRCP 51(c); Wyeth v. Levine (2009)",
        },
      ],
      deadlineTraps: [
        {
          id: "juul-d-dl-1",
          trigger: "Antitrust class trial Sept 28, 2026",
          time: "Cascading pre-trial deadlines",
          severity: "high",
          deadline:
            "Trial expert disclosure July 15, 2026 - defense rebuttal expert 30 days later",
          source: "Antitrust track scheduling order",
          aiAlert:
            "Defense rebuttal expert deadline triggers from plaintiff's disclosure. Calendar accordingly.",
          actionItem:
            "Identify rebuttal expert candidates by July 1. Retain immediately upon plaintiff disclosure.",
        },
      ],
      counselPatterns: [
        {
          id: "juul-d-cp-1",
          counselName:
            "Plaintiff MDL Co-Lead - Lieff Cabraser · Robbins Geller",
          pattern:
            "Aggressive amendment timing - frequent supplemental filings post-deadline",
          interpretation:
            "Theory diversification through late-stage amendments. Risk: amendment fatigue may erode credibility with the court.",
          ourMove:
            "Object to late amendments on FRCP 15(a)(2) good-cause grounds. Track amendment pattern for cumulative bad-faith argument.",
          judgeResponse:
            "Orrick has granted most amendments but warned in CMC that pattern may not continue.",
        },
      ],
      standingOrders: [
        {
          id: "juul-d-so-1",
          order: "Judge Orrick MDL Standing Order - Page Limits",
          requirement: "MSJ briefs 35 pages. Replies 20 pages.",
          recentFiling:
            "JLI MSJ (Dkt.4341) at 34 pages. Reply at 19 pages. Compliant.",
          conflict: "None.",
          recommendation: "Continue current briefing discipline.",
        },
      ],
    },
  },
  musk: {
    plaintiff: {
      activeMonitoring: [
        {
          id: "musk-act-1",
          time: "⚡ TRIAL DAY 3",
          severity: "critical",
          observation:
            "Defense Dkt.522 FRE 408(b) motion - pre-trial threat citation",
          analysis:
            "Defense filed Apr 25 motion citing Pampena v. Musk pattern, framing this lawsuit as competitive warfare by xAI. Setting up FRE 408(b) admissibility ruling that could reframe entire case intent.",
          warning:
            "Pre-prepare jury instruction request neutralizing competitive-motive framing. Lead with mission-and-promise narrative; relegate xAI to background.",
          ref: "Dkt.522 (Apr 25, 2026)",
        },
        {
          id: "musk-act-2",
          time: "Active",
          severity: "high",
          observation: "Microsoft GC subpoena resistance",
          analysis:
            "Aggressive opposition to Microsoft document subpoenas; selective production. Microsoft is the BFP defense - defense protects institutional knowledge record.",
          warning:
            "Press for unsealing of Microsoft due diligence files. PX230 is one CTO email; institutional record likely contains more.",
          ref: "Microsoft third-party discovery dispute",
        },
      ],
      waiverRisks: [
        {
          id: "musk-wav-1",
          time: "⚡ Day 3-9 trial · ongoing",
          severity: "critical",
          ruling: "§17510.8 strict-liability theory preservation",
          situation:
            "§17510.8 briefing complete as of Dkt.515/516 (Apr 30, 2026). If court rules adversely on this issue, an offer of proof preserving the excluded evidence and a specific objection to the ruling must be made on the record before the affected witness leaves the stand. Failure to do so forfeits the issue on appeal.",
          requiredLanguage:
            "At close of evidence per FRCP 51(c): 'Plaintiff requests an instruction on Cal. Bus. & Prof. Code §17510.8 strict liability for false or misleading charitable solicitations, modeled on the statutory plain text. Plaintiff also requests a FRCP 49(a) special interrogatory: \"Did Defendants make false or misleading representations in the 2015-2017 communications constituting charitable solicitations under §17510.8?\"' Without this, FRCP 51(d) forfeits the appellate issue.",
          consequence:
            "Theory dies on appeal if not preserved. The §17510.8 strict-liability path is unrecoverable post-trial without contemporaneous preservation.",
          authority: "FRCP 51(c); Cal. Bus. & Prof. Code §17510.8",
        },
        {
          id: "musk-wav-2",
          time: "⚡ During cross-examination · ongoing",
          severity: "critical",
          ruling: "Offer-of-proof preservation when objections sustained",
          situation:
            "When YGR sustains an objection to a line of questioning (Brockman, Altman, Sutskever, K. Scott crosses), an offer of proof must be made on the record before the next question. Without it, the excluded evidence is waived for appellate review.",
          requiredLanguage:
            "'Your Honor, for the record, defense would offer [exhibit/line of questioning] to show [purpose - bias, motive, prior inconsistent statement, course of conduct]. We believe this is admissible under FRE [801(d)(1)(A) prior inconsistent statement / 608 character / 613 specific instances]. The probative value outweighs any prejudice under FRE 403 because [specific reason]. Without this offer, we waive the issue for appeal under FRCP 103(a)(2).'",
          consequence:
            "Excluded evidence is unrecoverable on appeal without contemporaneous offer of proof.",
          authority: "FRCP 103(a)(2)",
        },
      ],
      deadlineTraps: [
        {
          id: "musk-dl-1",
          trigger: "Day 5 Nadella testimony",
          time: "Pre-Day 5",
          severity: "high",
          deadline: "Disclosure-scope verification before Nadella testifies",
          source: "FRCP 26(a)(1) disclosure requirement",
          aiAlert:
            "Microsoft witness statement disclosed on Apr 18, 2026. Any testimony beyond the scope of disclosed witness statement is objectionable. Pre-script scope objections.",
          actionItem:
            "Review Nadella's disclosed witness statement carefully. Pre-script scope objection language.",
        },
        {
          id: "musk-dl-2",
          trigger: "Close of evidence (Day 9 anticipated)",
          time: "Pre-close",
          severity: "critical",
          deadline: "Jury instruction request filing under FRCP 51(c)",
          source: "FRCP 51(c) timing requirement",
          aiAlert:
            "Written instruction requests must be filed at close of evidence per FRCP 51(c). Specifically request strict-liability framing on §17510.8 elements.",
          actionItem:
            "Draft proposed instruction now. File before close of evidence.",
        },
      ],
      counselPatterns: [
        {
          id: "musk-cp-1",
          counselName: "WilmerHale (Defense Lead)",
          pattern: "Massive document production strategy - overwhelm-by-volume",
          interpretation:
            "4M+ documents produced; only ~3% on privilege log. Few privilege claims signal confidence in documentary record.",
          ourMove:
            "Don't mistake volume for transparency. Targeted exhibit selection (PX003, PX163, PX230) is more powerful than narrative volume.",
          judgeResponse:
            "YGR has not commented on volume; managing trial efficiency under standing order.",
        },
        {
          id: "musk-cp-2",
          counselName: "Cleary Gottlieb (Microsoft)",
          pattern: "Subpoena resistance + selective production",
          interpretation:
            "Microsoft witness statement is BFP defense centerpiece. Counsel protects institutional knowledge record selectively.",
          ourMove: "Press for further unsealing. Renew with narrower scope.",
          judgeResponse:
            "YGR has been protective of third-party Microsoft document discovery; tightened production orders.",
        },
      ],
      standingOrders: [
        {
          id: "musk-so-1",
          order: "Judge YGR Pretrial Order No. 3 - SOL Framework",
          requirement:
            "SOL framework set; Sept 2020 'captured by Microsoft' tweet as constructive knowledge anchor; fraudulent concealment tolling burden on plaintiff.",
          recentFiling: "All MSJ briefing tracked PTO No. 3 framework.",
          conflict: "None. Pattern is being followed.",
          recommendation:
            "At trial, build SOL closing around fraudulent concealment with PTO No. 3 framework.",
        },
        {
          id: "musk-so-2",
          order: "Judge YGR Pretrial Order No. 4 - Daubert Framework",
          requirement:
            "Daubert hearings consolidated; Schizer/Hemel both admitted; Wazzan partial exclusion (Opinions 1-2). Wazzan Opinions 3-6 admitted.",
          recentFiling:
            "§17510.8 Daubert briefed Apr 30 (Dkts.467/471). Reply expected within 14 days.",
          conflict:
            "§17510.8 Daubert ruling pending. PTO No. 4 framework controls.",
          recommendation:
            "Strengthen Schizer reply opinion with additional CA case law on §17510.8 application.",
        },
      ],
    },
    defendant: {
      activeMonitoring: [
        {
          id: "musk-d-act-1",
          time: "⚡ TRIAL DAY 3",
          severity: "critical",
          observation:
            "Plaintiff PX230 + Brockman PX163 + Altman PX003 anchor strategy",
          analysis:
            "Plaintiff leading with documentary record. PX163 Brockman journal is pre-litigation handwritten admission landing strongly with jury per Day 3 observations.",
          warning:
            "Day 4 closing prep: anticipate plaintiff opening of closing with PX163. Pre-prepare narrative reframe.",
          ref: "Day 3 trial observations",
        },
        {
          id: "musk-d-act-2",
          time: "Active",
          severity: "high",
          observation:
            "§17510.8 strict-liability theory - late-stage amendment April 2026",
          analysis:
            "Plaintiff's late-stage theory supplementation = admission that original theories were insufficient. They needed strict liability backstop.",
          warning:
            "Frame at trial: §17510.8 is admission of common-law trust theory weakness.",
          ref: "§17510.8 supplemental theory filing April 2026",
        },
      ],
      waiverRisks: [
        {
          id: "musk-d-wav-1",
          time: "⚡ Trial · ongoing",
          severity: "critical",
          ruling: "BFP defense preservation across all counts",
          situation:
            "Microsoft BFP defense must be specifically pled and preserved as to Count XIX (A&A) and Count IV (UE). Failure to preserve count-by-count creates appellate vulnerability.",
          requiredLanguage:
            "In trial submissions: 'Microsoft preserves the bona-fide-purchaser defense as to all counts asserted against it. Microsoft acquired equity for value, in good faith, and without notice of donor charitable trust conditions.'",
          consequence:
            "Without count-specific preservation, BFP defense may be deemed waived for specific counts on appeal.",
          authority: "FRCP 51(c)",
        },
      ],
      deadlineTraps: [
        {
          id: "musk-d-dl-1",
          trigger: "Close of evidence",
          time: "Pre-close",
          severity: "high",
          deadline:
            "Defense jury instruction request - informal trust formation framework",
          source: "FRCP 51(c)",
          aiAlert:
            "Defense should request instruction modeled on Cal. Corp. Code §5210 emphasizing board control. Counter to plaintiff's CACI 4900 request.",
          actionItem:
            "Draft proposed instruction. File before close of evidence.",
        },
      ],
      counselPatterns: [
        {
          id: "musk-d-cp-1",
          counselName: "Plaintiff Counsel - Toberoff & Associates",
          pattern:
            "Selective document publication via media - Brockman journal leaked to NYT before trial",
          interpretation:
            "Running parallel public narrative. Building public sentiment as supplementary leverage.",
          ourMove:
            "Counter via measured corporate response. Avoid amplification through engagement.",
          judgeResponse:
            "YGR has not specifically addressed media coordination.",
        },
      ],
      standingOrders: [
        {
          id: "musk-d-so-1",
          order: "Judge YGR Pretrial Order No. 4",
          requirement:
            "Daubert framework consolidated. Schizer/Hemel both admitted.",
          recentFiling: "Wazzan Opinions 1-2 excluded (PTO No. 2 Dkt.452).",
          conflict: "None.",
          recommendation:
            "Pursue §17510.8 Daubert exclusion under PTO No. 4 framework.",
        },
      ],
    },
  },
  neuro: {
    plaintiff: {
      activeMonitoring: [
        {
          id: "neuro-act-1",
          time: "Recent · Mar 12, 2026",
          severity: "high",
          observation:
            "Mando filed separate MTDs for each plaintiff entity (Dkt.118 Neurocentria, Dkt.119 ThreoTech)",
          analysis:
            "Two separate MTDs filed where Aenlle-Rocha's standing order requires consolidated briefing. Procedurally defective filings - created confusion and imposed response costs even if motions were stricken.",
          warning:
            "⚡ Aenlle-Rocha struck both Dkts.118 and 119 (Dkt.163) for violating standing order. Underlying tactic remains a tell about Mando's litigation style: filing procedurally defective motions to create leverage.",
          ref: "Dkt.118, Dkt.119 (filed Mar 12, 2026); Dkt.163 (stricken)",
        },
        {
          id: "neuro-act-2",
          time: "Active",
          severity: "high",
          observation:
            "Repeated motion withdrawal pattern - Aenlle-Rocha OSC Dkt.107",
          analysis:
            "Multiple motions filed and withdrawn after oppositions filed. MTD/Venue Transfer withdrawn (Dkt.54). Sanctions Motion withdrawn (Dkt.104). Stricken stipulations (Dkts.80-81).",
          warning:
            "Pattern reveals reactive litigation style. Don't react to defense motions; they may withdraw under opposition pressure.",
          ref: "Dkt.107 OSC; Dkts.54, 104, 80-81 withdrawal/stricken record",
        },
        {
          id: "neuro-act-3",
          time: "Active · Mar 31, 2026",
          severity: "medium",
          observation:
            "Adams Clarifying Declaration filed Mar 31, 2026 (Dkt.151)",
          analysis:
            "Defense filed clarifying declaration AFTER plaintiff's reply Markman brief. Addresses §101 ambiguity raised in plaintiff's reply.",
          warning:
            "Move to strike as untimely supplemental declaration filed without leave. Or move for leave to file responsive brief.",
          ref: "Dkt.151 (Mar 31, 2026)",
        },
      ],
      waiverRisks: [
        {
          id: "neuro-wav-1",
          time: "Pre-Markman ruling · imminent",
          severity: "high",
          ruling: "§1292(b) interlocutory appeal preservation",
          situation:
            "Markman ruling pending. If construction is dispositive on infringement, plaintiff has 14 days to file §1292(b) certification motion. The motion must address (1) pure legal question, (2) substantial difference of opinion on construction methodology, (3) immediate appeal will materially advance termination.",
          requiredLanguage:
            "Within 14 days of Markman ruling: 'Plaintiffs respectfully move for §1292(b) certification of the Markman claim construction ruling. The construction is a pure legal question. Substantial difference of opinion exists between plaintiffs' and defendants' construction methodologies under Phillips v. AWH. Immediate appeal will materially advance termination of this litigation because [specific reasoning].'",
          consequence:
            "Without §1292(b) certification motion within 14 days, interlocutory appeal of dispositive Markman ruling is foreclosed.",
          authority: "28 U.S.C. §1292(b); Phillips v. AWH (Fed. Cir. 2005)",
        },
        {
          id: "neuro-wav-2",
          time: "Pre-fact-discovery cutoff Sept 11, 2026",
          severity: "critical",
          ruling: "Slutsky letter rogatory preservation",
          situation:
            "§102(f) inventorship-defect rebuttal (Slutsky as omitted inventor) requires Slutsky deposition or competent substitute evidence. Letter rogatory must be filed via Hague Convention. Without timely filing, the §102(f) record is uncreatable on appeal.",
          requiredLanguage:
            "⚡ File Hague Convention letter rogatory request to Israel THIS WEEK. Prepare with full Hague-compliant request and Israeli counsel coordination. Without this, §102(f) inventorship-defect rebuttal fails for evidentiary insufficiency.",
          consequence:
            "§102(f) record cannot be created on appeal. Defense's inventorship-omission argument re Slutsky stands uncontested.",
          authority:
            "Hague Convention on the Taking of Evidence Abroad in Civil or Commercial Matters (1970); §102(f); 35 U.S.C. §256",
        },
        {
          id: "neuro-wav-3",
          time: "Aug 15, 2026",
          severity: "critical",
          ruling: "Plaintiff technical expert disclosure preservation",
          situation:
            "Without disclosed technical expert by Aug 15, 2026, Ziari's anticipation and obviousness analyses go uncontested. Single highest-priority strategic action item.",
          requiredLanguage:
            "Retain expert IMMEDIATELY. 60-90 day expert work + 30-day deposition window = August 15 is hard ceiling. No appellate recovery available.",
          consequence:
            "§102/§103 prior-art analyses lose by default. Federal Circuit cannot create the expert record on appeal.",
          authority: "FRCP 26(a)(2); 35 U.S.C. §§ 102, 103",
        },
      ],
      deadlineTraps: [
        {
          id: "neuro-dl-1",
          trigger: "Markman ruling drops",
          time: "14 days post-trigger",
          severity: "critical",
          deadline: "§1292(b) certification motion deadline",
          source: "28 U.S.C. §1292(b)",
          aiAlert:
            "⚡ Markman ruling is potentially case-dispositive. 14-day clock starts immediately when ruling drops.",
          actionItem:
            "Pre-draft §1292(b) motion now. Calendar 14-day deadline to ruling date.",
        },
        {
          id: "neuro-dl-2",
          trigger: "Aenlle-Rocha §101 motion ruling",
          time: "Various downstream",
          severity: "high",
          deadline: "Cascade of post-§101 deadlines",
          source: "§101 motion record",
          aiAlert:
            "§101 ruling could moot Markman by invalidating patent. Order of rulings affects strategic posture.",
          actionItem:
            "Monitor docket. Pre-prepare federal-circuit-appeal posture if §101 granted.",
        },
        {
          id: "neuro-dl-3",
          trigger: "This week",
          time: "Imminent",
          severity: "critical",
          deadline: "⚡ Slutsky Hague Convention letter rogatory filing",
          source:
            "Sept 11, 2026 fact discovery cutoff + 90-120 day Hague lead time",
          aiAlert:
            "Without filing this week, Slutsky deposition is unobtainable. Sept 11 cutoff is hard ceiling.",
          actionItem:
            "File letter rogatory THIS WEEK. Engage Israeli counsel for translation and service.",
        },
      ],
      counselPatterns: [
        {
          id: "neuro-cp-1",
          counselName: "Raj V. Abhyanker - LegalForce RAPC Worldwide",
          pattern:
            "Repeated motion-filing-then-withdrawal pattern flagged in Aenlle-Rocha OSC (Dkt.107)",
          interpretation:
            "Filing procedurally defective motions to impose cost rather than because of belief in success. Reactive style, not strategically sequenced. Single-counsel structure (sole signatory) limits bandwidth.",
          ourMove:
            "⚡ Knowing Aenlle-Rocha already issued OSC for this pattern, the appropriate response to next procedural maneuver may be a SHORT brief citing the OSC and requesting sanctions rather than detailed substantive opposition. Don't reward attention-seeking motions with detailed opposition.",
          judgeResponse:
            "⚡ Aenlle-Rocha issued OSC at Dkt.107 specifically addressing this pattern. Judge has signaled intolerance for further pattern continuation. Sanctions risk for Mando counsel.",
        },
        {
          id: "neuro-cp-2",
          counselName: "Mando Procedural Posture",
          pattern: "Sole-counsel structure (Abhyanker as sole signatory)",
          interpretation:
            "Resource constraint signal. Limited bandwidth = predictable behavior. Strategic filings are individual decisions, not committee outputs.",
          ourMove:
            "File on plaintiff's preferred timeline. Defense response time will be slower. Shorter response windows favor plaintiff.",
          judgeResponse:
            "Sole-counsel arrangement is permitted; no judicial intervention available unless prejudicial.",
        },
      ],
      standingOrders: [
        {
          id: "neuro-so-1",
          order: "Judge Aenlle-Rocha Patent Standing Order (Dkt.33)",
          requirement:
            "Specific requirements for claim construction briefing, evidence disclosure, motion sequencing. Reply briefs in claim construction limited to 15 pages and must not raise new claim constructions not presented in the opening brief. Consolidated briefing required (no separate MTDs per entity).",
          recentFiling:
            "Markman briefing complete (Dkts.142, 144, 150, 158). Adams Clarifying Declaration (Dkt.151) filed post-reply.",
          conflict:
            "⚡ Mando Dkts.118 and 119 (separate MTDs per entity) violated standing order - stricken at Dkt.163. Pattern confirmed.",
          recommendation:
            "Future filings: cross-reference standing order requirements before filing. Calendar 15-page reply brief limit and standing order before any reply briefing.",
        },
        {
          id: "neuro-so-2",
          order: "Judge Aenlle-Rocha Initial Standing Order (Dkt.16)",
          requirement:
            "Required compliance with general civil procedural standing order. Consent/decline magistrate judge selection.",
          recentFiling:
            "Plaintiffs declined consent to Magistrate Judge (Dkt.19).",
          conflict: "None.",
          recommendation: "Continue compliance with general standing order.",
        },
      ],
    },
    defendant: {
      activeMonitoring: [
        {
          id: "neuro-d-act-1",
          time: "Active",
          severity: "high",
          observation:
            "Plaintiff narrowed from 5 patents to 1 ('061 only) under pressure",
          analysis:
            "Original 5-patent assertion narrowed after Markman briefing pressure. Late-stage reflects realistic assessment but timing weakens position.",
          warning:
            "Anticipate defense framing as 'patent overreach' in trial. Pre-prepare narrative reframe focused on '061 strength.",
          ref: "Dkts.34, 64, 94, 110 (amendment record)",
        },
        {
          id: "neuro-d-act-2",
          time: "Active",
          severity: "critical",
          observation:
            "Plaintiff has not retained technical or claim construction expert",
          analysis:
            "Two critical gaps: claim construction expert and §102/§103 technical expert both unfilled. Aug 15, 2026 disclosure deadline approaching.",
          warning:
            "Single highest-priority strategic action item. Without experts, Adams + Ziari testimony goes uncontested.",
          ref: "Expert disclosure record (none)",
        },
      ],
      waiverRisks: [
        {
          id: "neuro-d-wav-1",
          time: "Pre-disclosure deadline Aug 15, 2026",
          severity: "critical",
          ruling:
            "§102/§103 anticipation and obviousness rebuttal preservation",
          situation:
            "Without retained experts by Aug 15, 2026 disclosure deadline, plaintiff cannot rebut Ziari prior-art testimony at trial. Federal Circuit cannot create the expert record on appeal.",
          requiredLanguage:
            "Retain experts IMMEDIATELY. Coordinate with Markman ruling timing. Without experts, plaintiff loses on §102/§103 by default at trial.",
          consequence:
            "Anticipation and obviousness analyses go uncontested. Trial verdict turns on judge ruling alone for invalidity.",
          authority: "FRCP 26(a)(2); 35 U.S.C. §§ 102, 103",
        },
      ],
      deadlineTraps: [
        {
          id: "neuro-d-dl-1",
          trigger: "Plaintiff expert disclosure",
          time: "Aug 15, 2026",
          severity: "critical",
          deadline: "Expert retention + report preparation",
          source: "Scheduling order Dkt.32",
          aiAlert: "Hard deadline. No appellate recovery available.",
          actionItem: "Authorize expert retention IMMEDIATELY.",
        },
      ],
      counselPatterns: [
        {
          id: "neuro-d-cp-1",
          counselName: "Mando - Defense Counsel Pattern Self-Audit",
          pattern:
            "Repeated motion withdrawal pattern flagged in Aenlle-Rocha OSC (Dkt.107)",
          interpretation:
            "Pattern has accumulated risk. Future filings under heightened judicial scrutiny.",
          ourMove:
            "Avoid further withdrawal patterns. File only motions with high success probability. Focus resources on Markman, §101, and exceptional-case fee preparation.",
          judgeResponse:
            "OSC at Dkt.107. Future pattern continuation risks sanctions.",
        },
      ],
      standingOrders: [
        {
          id: "neuro-d-so-1",
          order: "Judge Aenlle-Rocha Patent Standing Order (Dkt.33)",
          requirement:
            "Specific requirements for claim construction briefing, motion sequencing.",
          recentFiling:
            "Markman briefing complete. Adams Clarifying Declaration (Dkt.151).",
          conflict:
            "⚠️ Dkts.118 and 119 stricken at Dkt.163. Future motions must be consolidated.",
          recommendation:
            "Cross-reference standing order before all future filings. No more separate-entity motions.",
        },
      ],
    },
  },
};

const THEORIES_DATA = {
  ...ILE_K_TDD,
  juul: {
    plaintiff: [
      {
        id: "pn",
        name: "Public Nuisance (SFUSD)",
        short: "Pub. Nuis.",
        score: 54,
        dim: { evid: 72, caselaw: 65, experts: 78, mvuln: 62, damages: 32 },
        elements: [
          {
            name: "Defendant created or maintained the condition",
            status: "green",
            explainer:
              "All evidence supports - internal docs, epidemiology, site reports.",
            ev: [
              {
                id: "JLI-MKT-001",
                label:
                  "JLI internal marketing & product-design docs (PX-44 to PX-67)",
                supportive: true,
              },
              {
                id: "HF-2021",
                label: "Halpern-Felsher 9/2021 epidemiological report",
                supportive: true,
              },
              {
                id: "DORN-INSP",
                label: "Six site inspection reports (Dorn)",
                supportive: true,
              },
              {
                id: "DKT-1",
                label: "Master Complaint allegations (Dkt.1 PI)",
                supportive: true,
              },
              {
                id: "ALTRIA-12B",
                label: "Altria $12.8B equity investment (PX-89)",
                supportive: true,
              },
            ],
          },
          {
            name: "Condition was a public nuisance",
            status: "green",
            explainer: "California precedent + prior MDL rulings + YRBS data.",
            ev: [
              {
                id: "CONAGRA",
                label: "People v. ConAgra (Cal. Ct. App. 2017)",
                supportive: true,
              },
              {
                id: "MDL-1132",
                label: "Prior MDL rulings (Dkts. 1132, 1845)",
                supportive: true,
              },
              {
                id: "YRBS",
                label: "YRBS 1997-2019 youth use data (Ex. 299)",
                supportive: true,
              },
            ],
          },
          {
            name: "Plaintiff suffered special injury",
            status: "amber",
            explainer:
              "Government-entity standard unsettled; Dorn time docs help but don't resolve.",
            ev: [
              {
                id: "GOVT-STD",
                label: "Govt-entity special-injury doctrine - unsettled",
                supportive: false,
              },
              {
                id: "DORN-DAM",
                label: "Dorn 800-hour staff-time documentation",
                supportive: true,
              },
              {
                id: "COATES",
                label: "Coates 9/28/21 Dep. - operational disruption",
                supportive: true,
              },
            ],
          },
          {
            name: "Damages",
            status: "red",
            explainer:
              "$34,875 documented vs $78.8M estimate; Sonner bar; Pak admission.",
            ev: [
              {
                id: "PAK-ADMIT",
                label:
                  "Pak 12/21/21 admission: 'we don't have that information'",
                supportive: false,
              },
              {
                id: "DOC-FLOOR",
                label: "$34,875 documented expenses (Pak Dep.)",
                supportive: false,
              },
              {
                id: "DORN-EST",
                label: "Dorn $78.8M abatement estimate (Sonner-vulnerable)",
                supportive: true,
              },
              {
                id: "SONNER",
                label:
                  "Sonner v. Premier (9th Cir.) - adequate-legal-remedy bar",
                supportive: false,
              },
            ],
          },
        ],
        deps: {
          role: "durable",
          text: "Most durable theory - independent of enterprise. Survives if RICO falls (62% probability). Damages exposure compresses without RICO trebling.",
        },
        narrative: {
          legal:
            "Defendant created or maintained a public nuisance - youth nicotine addiction crisis - that imposed special injury on SFUSD as a government entity tasked with educating affected students.",
          jury: "JUUL knew its product would end up in schools. Their own internal documents - the ones they didn't want you to see - show they designed JUUL to appeal to young people: the form factor that looked like a USB drive, the mango and crème brûlée flavors, the social media campaigns. They knew what they were doing. And they kept doing it after the FDA sent warning letters, after Congress called their CEO to testify, after school after school started confiscating JUULs from bathrooms. That's not an accident. That's a choice. SFUSD is suing because we had to spend years and millions of dollars cleaning up the mess JUUL knew it was making.",
        },
        vuln: {
          sol: 25,
          preempt: 55,
          causation: 50,
          expertDep: 82,
          damages: 88,
        },
        timeline: [
          { date: "Jun 2020", score: 70, event: "Complaint filed" },
          { date: "Mar 2021", score: 73, event: "Doc production complete" },
          {
            date: "Dec 2021",
            score: 45,
            event: "Pak admission - 'we don't have that information'",
          },
          {
            date: "Jun 2022",
            score: 58,
            event: "Class cert granted (PI track)",
          },
          {
            date: "Sep 2022",
            score: 62,
            event: "Dorn report survives Daubert",
          },
          {
            date: "Oct 2025",
            score: 54,
            event: "MSJ argued; under submission",
          },
        ],
        jinst: {
          source: "CACI 2020 (Public Nuisance) - modified for govt entity",
          model:
            "To establish public nuisance, plaintiff must prove: (1) defendant by acting or failing to act created a condition or permitted a condition to exist that was harmful to health or obstructed the free use of property so as to interfere with the comfortable enjoyment of life or property by an entire community or neighborhood; and (2) plaintiff suffered harm that was different in kind from the harm suffered by the general public.",
          links: [
            {
              phrase: "created a condition",
              ev: "JLI marketing strategy + product design (PX-44 to PX-67)",
            },
            {
              phrase: "harmful to health",
              ev: "Halpern-Felsher 2021 epidemiology + Dr. Anthony 2018 testimony",
            },
            {
              phrase: "comfortable enjoyment",
              ev: "Six site inspection reports - bathrooms, hallways, classrooms",
            },
            {
              phrase: "different in kind from the general public",
              ev: "Govt-entity special-injury doctrine - Pomona Police, Riverside cases - unsettled",
            },
          ],
        },
      },
      {
        id: "rico",
        name: "RICO Enterprise",
        short: "RICO",
        score: 41,
        dim: { evid: 55, caselaw: 38, experts: 62, mvuln: 78, damages: 72 },
        elements: [
          {
            name: "Existence of an enterprise",
            status: "red",
            explainer:
              "Hardest element across the MDL. Class settlements resolved RICO for the class.",
            ev: [
              {
                id: "JLI-ALTRIA",
                label: "JLI/Altria coordination (PX-89, PX-92)",
                supportive: true,
              },
              {
                id: "NO-BOWEN",
                label:
                  "Bowen MSJ ruling rejected officer-as-enterprise theory (Dkt.3527)",
                supportive: false,
              },
            ],
          },
          {
            name: "Pattern of racketeering activity",
            status: "amber",
            explainer: "Wire/mail fraud predicates colorable but contested.",
            ev: [
              {
                id: "WIRE",
                label: "FDA submission emails - wire fraud predicates",
                supportive: true,
              },
              {
                id: "MAIL",
                label: "Distributor invoicing - mail fraud predicates",
                supportive: true,
              },
              {
                id: "REL",
                label: "H-J relatedness/continuity inquiry",
                supportive: true,
                caveat: "close call",
              },
            ],
          },
          {
            name: "Defendant conducted/participated in enterprise affairs",
            status: "amber",
            explainer:
              "Reves operation/management test contested for management defendants.",
            ev: [
              {
                id: "REVES",
                label: "Reves v. Ernst & Young - operation/management test",
                supportive: false,
              },
            ],
          },
          {
            name: "Causation of injury to property",
            status: "green",
            explainer:
              "Damages-causation chain reasonably documented for opt-out PIs.",
            ev: [
              {
                id: "PI-INJ",
                label: "Opt-out PI plaintiffs - direct injury",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "primary-fragile",
          text: "Highest damages multiplier (treble) but most vulnerable element (enterprise). 35% probability survives MSJ. If RICO falls, consumer-protection claims survive (78%) and public nuisance survives (62%) - case doesn't collapse but loses RICO trebling.",
        },
        narrative: {
          legal:
            "JLI, Altria, and management defendants conducted an enterprise through a pattern of racketeering activity (wire fraud, mail fraud) directed at deceiving the FDA and the public, causing injury to plaintiff's property.",
          jury: "This wasn't one company making a mistake. It was a coordinated operation. JLI built the product. Altria put $12.8 billion behind it. The management - Bowen, Monsees, Pritzker - directed it. They lied to the FDA. They lied to retailers. They lied to the public. And every time they put another lie in the mail, every time they sent another false email, that's a federal crime. RICO exists for exactly this - when a group of people get together and run a long-term scheme that hurts a lot of people.",
        },
        vuln: {
          sol: 35,
          preempt: 25,
          causation: 48,
          expertDep: 55,
          damages: 30,
        },
        timeline: [
          {
            date: "Mar 2020",
            score: 68,
            event: "Master Complaint with RICO claims",
          },
          {
            date: "Sep 2022",
            score: 42,
            event: "Bowen MSJ ruling - rejected officer-as-enterprise",
          },
          {
            date: "May 2023",
            score: 38,
            event: "JLI class settlement (resolves RICO for class)",
          },
          { date: "Oct 2025", score: 41, event: "Opt-out RICO under MSJ" },
        ],
        jinst: {
          source: "9th Cir. Model 8.1 + 8.124",
          model:
            "To prove a RICO violation under 18 U.S.C. §1962(c), plaintiff must show: (1) the existence of an enterprise affecting interstate commerce; (2) defendant was associated with the enterprise; (3) defendant conducted or participated in the conduct of the enterprise's affairs; (4) through a pattern of racketeering activity; and (5) defendant's conduct caused injury to plaintiff's business or property.",
          links: [
            {
              phrase: "existence of an enterprise",
              ev: "JLI/Altria/distributor coordination evidence",
            },
            {
              phrase: "pattern of racketeering activity",
              ev: "Predicate acts - wire fraud (PX-101), mail fraud (PX-117)",
            },
            {
              phrase: "conducted or participated",
              ev: "Org chart + management deposition testimony",
            },
            {
              phrase: "caused injury",
              ev: "Direct-injury evidence per opt-out plaintiff",
            },
          ],
        },
      },
      {
        id: "nm",
        name: "Negligent Marketing",
        short: "Negligent Mktg.",
        score: 68,
        dim: { evid: 80, caselaw: 72, experts: 75, mvuln: 42, damages: 65 },
        elements: [
          {
            name: "Duty of care",
            status: "green",
            explainer: "Manufacturer-to-consumer duty well-established.",
            ev: [
              {
                id: "DUTY",
                label: "California products-liability duty of care",
                supportive: true,
              },
            ],
          },
          {
            name: "Breach (negligent design + marketing)",
            status: "green",
            explainer:
              "Internal docs are powerful: youth-targeting + addictive design.",
            ev: [
              {
                id: "INT-DOCS",
                label: "JLI internal mktg docs - youth focus (PX-44 to PX-67)",
                supportive: true,
              },
              {
                id: "NIC-SALT",
                label: "5% nicotine-salt formulation (PX-71)",
                supportive: true,
              },
              {
                id: "FLAVORS",
                label: "Mango, crème brûlée flavor strategy (PX-83)",
                supportive: true,
              },
              {
                id: "FDA-WARN",
                label: "FDA warning letters - defendant continued conduct",
                supportive: true,
              },
            ],
          },
          {
            name: "Causation",
            status: "amber",
            explainer:
              "General causation supported but plaintiff-specific causation requires expert.",
            ev: [
              {
                id: "GEN-CAUS",
                label: "General causation - Halpern-Felsher",
                supportive: true,
              },
              {
                id: "SPEC-CAUS",
                label:
                  "Plaintiff-specific causation expert - not retained for Seliavski",
                supportive: false,
              },
            ],
          },
          {
            name: "Damages",
            status: "green",
            explainer: "Documented harm; PI track damages model.",
            ev: [
              {
                id: "PI-DAM",
                label: "Documented PI damages - addiction, behavioral injuries",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "survivor",
          text: "Most resilient PI theory. FCLAA preemption doesn't reach negligent design cleanly. Survives independently of RICO and public nuisance. Strongest theory for opt-out PIs.",
        },
        narrative: {
          legal:
            "Defendant breached its duty of care by designing and marketing a product in a manner that foreseeably caused harm to minors, and that breach proximately caused plaintiff's damages.",
          jury: "JUUL knew its product would end up in schools. Their own internal documents - the ones they didn't want you to see - show they designed JUUL to appeal to young people: the form factor that looked like a USB drive, the mango and crème brûlée flavors, the social media campaigns. They knew what they were doing. And they kept doing it after the FDA sent warning letters, after Congress called their CEO to testify, after school after school started confiscating JUULs from bathrooms. That's not an accident. That's a choice.",
        },
        vuln: {
          sol: 32,
          preempt: 38,
          causation: 60,
          expertDep: 55,
          damages: 30,
        },
        timeline: [
          { date: "Mar 2020", score: 62, event: "Master Complaint filed" },
          {
            date: "Jun 2021",
            score: 75,
            event: "PX-44 to PX-67 produced (internal mktg)",
          },
          {
            date: "Sep 2021",
            score: 80,
            event: "Halpern-Felsher report served",
          },
          {
            date: "Oct 2025",
            score: 68,
            event: "Causation expert gap on Seliavski",
          },
        ],
        jinst: {
          source: "CACI 401 (Negligence) + 425 (Mfgr Liability)",
          model:
            "Defendant was negligent if (1) defendant owed plaintiff a duty of care; (2) defendant breached that duty; (3) the breach was a substantial factor in causing plaintiff's harm; and (4) plaintiff was harmed.",
          links: [
            {
              phrase: "duty of care",
              ev: "Manufacturer-consumer duty under California law",
            },
            {
              phrase: "breached that duty",
              ev: "Internal mktg docs (PX-44 to PX-67) + design choices (PX-71)",
            },
            {
              phrase: "substantial factor",
              ev: "General causation (Halpern-Felsher) + plaintiff-specific causation expert",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "nocaus",
        name: "No General Causation (PI)",
        short: "No Causation",
        score: 71,
        dim: { evid: 62, caselaw: 75, experts: 55, mvuln: 30, damages: 80 },
        elements: [
          {
            name: "PI plaintiffs lack medical-causation expert",
            status: "green",
            explainer:
              "Seliavski cohort opt-outs have no retained causation expert.",
            ev: [
              {
                id: "NO-EXP",
                label: "No medical causation expert retained for Seliavski",
                supportive: true,
              },
              {
                id: "DAUBERT",
                label: "Daubert standard for general causation",
                supportive: true,
              },
            ],
          },
          {
            name: "Alternative nicotine sources",
            status: "amber",
            explainer:
              "Concurrent use of cigarettes/other ENDS undermines specific causation.",
            ev: [
              {
                id: "ALT-SRC",
                label: "Plaintiffs reported alternative nicotine use",
                supportive: true,
              },
            ],
          },
          {
            name: "Pre-existing conditions",
            status: "amber",
            explainer:
              "Some plaintiffs had pre-existing behavioral health conditions.",
            ev: [],
          },
          {
            name: "Lack of dose-response data",
            status: "green",
            explainer:
              "No reliable dose-response evidence linking JUUL to specific injuries.",
            ev: [
              {
                id: "NO-DR",
                label: "No JUUL-specific dose-response epidemiology",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "primary",
          text: "Strongest defense for opt-out PI track. Knocks out compensatory damages directly. Still leaves SFUSD govt-entity track exposed.",
        },
        narrative: {
          legal:
            "PI plaintiffs cannot establish general or specific causation linking JUUL use to claimed injuries without admissible expert testimony.",
          jury: "Nicotine is addictive. Everyone knows this. Plaintiff was using cigarettes before JUUL. Plaintiff was using other nicotine products at the same time as JUUL. Plaintiff had behavioral health concerns before JUUL. The simple question is: what specifically did JUUL cause that something else didn't? Plaintiff has not retained a medical doctor to answer that question. Without that answer, this case is asking you to guess. The law doesn't allow guessing.",
        },
        vuln: {
          sol: 40,
          preempt: 25,
          causation: 30,
          expertDep: 50,
          damages: 35,
        },
        timeline: [
          { date: "Jun 2020", score: 55, event: "Complaint filed" },
          {
            date: "Mar 2024",
            score: 68,
            event: "Causation-expert deadline missed for Seliavski cohort",
          },
          {
            date: "Oct 2025",
            score: 71,
            event: "MSJ on Sonner-bar + causation",
          },
        ],
        jinst: {
          source: "CACI 430 (Causation - Substantial Factor)",
          model:
            "A substantial factor in causing harm is a factor that a reasonable person would consider to have contributed to the harm. It must be more than a remote or trivial factor. It does not have to be the only cause of the harm.",
          links: [
            {
              phrase: "substantial factor",
              ev: "Required general causation expert testimony - not present",
            },
          ],
        },
      },
      {
        id: "sol",
        name: "Statute of Limitations",
        short: "SOL",
        score: 64,
        dim: { evid: 55, caselaw: 78, experts: 48, mvuln: 45, damages: 62 },
        elements: [
          {
            name: "Discovery rule trigger date",
            status: "amber",
            explainer:
              "2018-2019 media coverage triggered awareness for many plaintiffs.",
            ev: [
              {
                id: "MEDIA-2018",
                label: "FDA warning letter coverage 2018-2019",
                supportive: true,
              },
              {
                id: "CONG-2019",
                label: "Congressional testimony March 2019",
                supportive: true,
              },
            ],
          },
          {
            name: "Tolling - minor plaintiffs",
            status: "red",
            explainer:
              "Most PI plaintiffs were minors at injury - tolling applies.",
            ev: [
              {
                id: "MINOR-TOLL",
                label: "California minor tolling (CCP §352)",
                supportive: false,
              },
            ],
          },
          {
            name: "Continuing-tort theory inapplicable",
            status: "green",
            explainer:
              "Discrete-injury cases not amenable to continuing-tort tolling.",
            ev: [],
          },
        ],
        deps: {
          role: "narrow",
          text: "Effective only against late-filed adult plaintiffs. Most Seliavski cohort were minors - tolling defeats SOL for them.",
        },
        narrative: {
          legal:
            "Plaintiffs' claims accrued when they discovered or reasonably should have discovered the injury. Late-filed claims without applicable tolling are time-barred.",
          jury: "This case isn't being filed because something happened. It's being filed because lawyers found these plaintiffs years after the fact. Plaintiff knew JUUL might be addictive in 2018. Plaintiff knew JUUL was facing FDA action in 2019. Plaintiff filed in 2023. The law has deadlines for a reason - memories fade, evidence disappears, cases get harder to defend.",
        },
        vuln: {
          sol: 65,
          preempt: 20,
          causation: 35,
          expertDep: 30,
          damages: 55,
        },
        timeline: [
          { date: "Jun 2020", score: 50, event: "Master Complaint filed" },
          {
            date: "Jan 2024",
            score: 66,
            event: "Late-filed opt-outs identified",
          },
          { date: "Oct 2025", score: 64, event: "SOL motion under MSJ" },
        ],
        jinst: {
          source: "CACI 455 (Statute of Limitations - Delayed Discovery)",
          model:
            "A claim accrues when the plaintiff discovers, or in the exercise of reasonable diligence should have discovered, the facts giving rise to the claim. If plaintiff was a minor when the injury occurred, the limitation period is tolled until plaintiff reaches the age of majority.",
          links: [
            {
              phrase: "reasonable diligence",
              ev: "FDA warning letters 2018; Congressional testimony 2019; widespread press coverage",
            },
            {
              phrase: "age of majority",
              ev: "Plaintiffs' birthdates - most Seliavski cohort minors at injury",
            },
          ],
        },
      },
      {
        id: "intervening",
        name: "Intervening Superseding Cause",
        short: "Intervening",
        score: 58,
        dim: { evid: 60, caselaw: 62, experts: 58, mvuln: 50, damages: 55 },
        elements: [
          {
            name: "Pre-existing nicotine use",
            status: "amber",
            explainer: "Some plaintiffs had cigarette use prior to JUUL.",
            ev: [],
          },
          {
            name: "Concurrent use of other ENDS",
            status: "amber",
            explainer: "Use of other vape products concurrent with JUUL.",
            ev: [],
          },
          {
            name: "Retailer ID-check failures",
            status: "green",
            explainer:
              "Retailer co-defendants ID-check failures break causation chain.",
            ev: [
              {
                id: "7-11",
                label: "Co-defendant retailer ID-check failures",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "backup",
          text: "Backup theory if causation cannot be defeated outright. Reduces apportionment rather than eliminating liability.",
        },
        narrative: {
          legal:
            "Plaintiffs' injuries were caused by intervening conduct of third parties (retailers selling to minors) and pre-existing/concurrent use, breaking the proximate-cause chain.",
          jury: "If a 7-Eleven sells JUUL to a 15-year-old, who is at fault - JUUL for making it, or the cashier who sold it? The retailer broke the law. Plaintiff was using cigarettes before JUUL. Plaintiff was using other vape products at the same time as JUUL. The law doesn't require JUUL to insure plaintiff against everyone else's choices.",
        },
        vuln: {
          sol: 35,
          preempt: 22,
          causation: 60,
          expertDep: 50,
          damages: 45,
        },
        timeline: [
          { date: "Jun 2020", score: 50, event: "Master Complaint filed" },
          {
            date: "Sep 2022",
            score: 60,
            event: "Retailer co-defendant settlements (apportionment evidence)",
          },
          { date: "Oct 2025", score: 58, event: "Intervening-cause briefing" },
        ],
        jinst: {
          source: "CACI 432 (Superseding Cause)",
          model:
            "A defendant is not liable for harm if a superseding cause produced the harm. A superseding cause is a new cause that was not a foreseeable consequence of defendant's conduct.",
          links: [
            {
              phrase: "new cause",
              ev: "Retailer ID-check failures + plaintiff's other nicotine use",
            },
            {
              phrase: "foreseeable consequence",
              ev: "Disputed - JUUL contends retailer ID-check failures were not foreseeable",
            },
          ],
        },
      },
    ],
  },
  musk: {
    plaintiff: [
      {
        id: "trust",
        name: "Charitable Trust Creation",
        short: "Charitable Trust",
        score: 72,
        dim: { evid: 82, caselaw: 60, experts: 75, mvuln: 55, damages: 88 },
        elements: [
          {
            name: "Donor intent to create trust",
            status: "green",
            explainer:
              "Founding emails + LP Agreement language + Brockman journal - strong record.",
            ev: [
              {
                id: "PX163",
                label:
                  "Brockman journal (PX163) - 'wrong to steal the non-profit'",
                supportive: true,
              },
              {
                id: "PX-FOUND",
                label: "Founding emails 2015-2017 (PX-001 to PX-021)",
                supportive: true,
              },
              {
                id: "PX200",
                label: "LP Agreement 'spirit of a donation' (PX200)",
                supportive: true,
              },
              {
                id: "LIU",
                label: "Liu trial testimony Day 2 - donor expectations",
                supportive: true,
              },
            ],
          },
          {
            name: "Charitable purpose",
            status: "green",
            explainer:
              "OpenAI's nonprofit mission was widely promoted as charitable AI for humanity.",
            ev: [
              {
                id: "NONPROF",
                label: "OpenAI Inc. 501(c)(3) status + public statements",
                supportive: true,
              },
            ],
          },
          {
            name: "Identifiable trust res",
            status: "amber",
            explainer:
              "FIFO/LIFO dispute on tracing. Wazzan exclusion of Opinions 1-2 weakened res identification.",
            ev: [
              {
                id: "DUDNEY",
                label: "Dudney FIFO testimony (defense expert)",
                supportive: false,
              },
              {
                id: "WAZZAN",
                label: "Wazzan tracing - Opinions 1-2 excluded, 4-7 admitted",
                supportive: true,
                caveat: "weakened",
              },
            ],
          },
          {
            name: "Breach by for-profit conversion",
            status: "green",
            explainer: "October 2025 conversion + Microsoft equity stake.",
            ev: [
              {
                id: "CONVERT-2025",
                label: "Oct 2025 PBC conversion",
                supportive: true,
              },
              {
                id: "MS-EQUITY",
                label: "Microsoft 27% equity ($135B)",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "primary",
          text: "Lead theory. If trust formation fails, unjust enrichment becomes primary (65% recovery probability under that). §17510.8 is narrowest fallback.",
        },
        narrative: {
          legal:
            "Defendants accepted Musk's $38M donation on representations of charitable purpose, creating a charitable trust. The October 2025 for-profit conversion breached the trust by diverting trust assets to private benefit.",
          jury: "Elon Musk gave $38 million to build something for the world. Not for profit. Not for shareholders. For everyone. The people he gave it to made him a promise - explicit, repeated, documented. Then they broke that promise. They took what was donated to benefit humanity and turned it into a for-profit company worth $157 billion. When you donate to a hospital and the hospital sells itself to a hedge fund, the law says you get that money back. This is the same thing, at a scale that matters.",
        },
        vuln: {
          sol: 62,
          preempt: 15,
          causation: 35,
          expertDep: 70,
          damages: 48,
        },
        timeline: [
          {
            date: "Aug 2024",
            score: 38,
            event: "Initial complaint (dismissed N.D. Tex.)",
          },
          {
            date: "Jan 2025",
            score: 65,
            event: "PI briefing - founding emails entered",
          },
          {
            date: "Mar 2025",
            score: 55,
            event: "PI denied - 'low likelihood of success'",
          },
          { date: "Feb 2026", score: 78, event: "MSJ denied; trial proceeds" },
          { date: "Apr 2026", score: 72, event: "Trial Day 3 - Liu testimony" },
        ],
        jinst: {
          source: "Special instruction - no model available",
          model:
            "A charitable trust is created when (1) a settlor transfers property; (2) intending to create a fiduciary relationship; (3) for a charitable purpose; (4) which is to be carried out by the trustee for the benefit of an indefinite class. The court [or jury] determines whether a charitable trust was created based on the totality of the circumstances, including the parties' communications, the nature of the transfer, and the recipient's representations.",
          links: [
            {
              phrase: "settlor transfers property",
              ev: "Musk $38M donations 2015-2017",
            },
            {
              phrase: "intending to create a fiduciary relationship",
              ev: "Founding emails (PX-001 to PX-021) + Brockman journal (PX163)",
            },
            {
              phrase: "charitable purpose",
              ev: "OpenAI nonprofit mission statements",
            },
            {
              phrase: "benefit of an indefinite class",
              ev: "'Humanity' framing in mission docs",
            },
          ],
        },
      },
      {
        id: "unjust",
        name: "Unjust Enrichment",
        short: "Unjust Enrich.",
        score: 65,
        dim: { evid: 72, caselaw: 75, experts: 55, mvuln: 62, damages: 70 },
        elements: [
          {
            name: "Defendant received benefit at plaintiff's expense",
            status: "green",
            explainer:
              "$38M flowed from Musk to OpenAI; OpenAI now valued at $157B.",
            ev: [
              {
                id: "38M",
                label: "Musk's $38M donations 2015-2017",
                supportive: true,
              },
              {
                id: "157B",
                label: "OpenAI $157B valuation (Oct 2025)",
                supportive: true,
              },
            ],
          },
          {
            name: "Retention is inequitable",
            status: "amber",
            explainer: "Depends on FIFO tracing - Wazzan vs. Dudney battle.",
            ev: [
              {
                id: "WAZZAN-RET",
                label: "Wazzan retention analysis",
                supportive: true,
              },
              {
                id: "DUDNEY-RET",
                label: "Dudney FIFO - donations spent on early R&D",
                supportive: false,
              },
            ],
          },
        ],
        deps: {
          role: "backup",
          text: "Backup if trust formation fails. Dudney-dependent - if FIFO is credited, theory collapses. Two red axes on radar (SOL + expert dependency).",
        },
        narrative: {
          legal:
            "Defendants received $38M from plaintiff under representations of charitable purpose, and now retain proceeds traceable to that contribution under a for-profit structure that benefits defendants personally.",
          jury: "Whatever you call it - trust, gift, donation - Musk gave them money based on what they said they would do. They didn't do it. They kept the money and used it to make something worth $157 billion. The rest is lawyer talk. The fair answer is: give the money back.",
        },
        vuln: {
          sol: 78,
          preempt: 18,
          causation: 45,
          expertDep: 88,
          damages: 55,
        },
        timeline: [
          { date: "Aug 2024", score: 42, event: "Complaint filed" },
          { date: "Jan 2025", score: 68, event: "PI briefing" },
          {
            date: "Sep 2020",
            score: 50,
            event: "Twitter 'captured by Microsoft' - SOL trigger",
          },
          {
            date: "Apr 2026",
            score: 65,
            event: "Wazzan testimony Day 3 (Opinions 1-2 excluded)",
          },
        ],
        jinst: {
          source: "CACI 4700 (Unjust Enrichment / Quasi-Contract)",
          model:
            "To recover under unjust enrichment, plaintiff must prove: (1) defendant received a benefit; (2) at plaintiff's expense; (3) under circumstances that make it unjust for defendant to retain the benefit without payment.",
          links: [
            { phrase: "received a benefit", ev: "$38M donations" },
            {
              phrase: "at plaintiff's expense",
              ev: "Donations from Musk's personal funds",
            },
            {
              phrase: "unjust to retain",
              ev: "Charitable representations + for-profit conversion",
            },
          ],
        },
      },
      {
        id: "175108",
        name: "§17510.8 Charitable Solicitations",
        short: "§17510.8",
        score: 48,
        dim: { evid: 60, caselaw: 35, experts: 40, mvuln: 72, damages: 52 },
        elements: [
          {
            name: "Solicitation occurred",
            status: "green",
            explainer: "Founding emails were solicitations.",
            ev: [
              {
                id: "SOLICIT",
                label: "2015-2017 emails to Musk requesting donations",
                supportive: true,
              },
            ],
          },
          {
            name: "Charitable purpose representation",
            status: "green",
            explainer: "Multiple emails reference nonprofit mission.",
            ev: [
              {
                id: "PX-FOUND-2",
                label: "PX-001 to PX-021 - nonprofit representations",
                supportive: true,
              },
            ],
          },
          {
            name: "Statute applies to oral/electronic representations",
            status: "red",
            explainer:
              "Daubert briefed but no ruling. Statute application to this fact pattern is novel.",
            ev: [
              {
                id: "NOVEL",
                label:
                  "§17510.8 application to AI nonprofit context - unsettled",
                supportive: false,
              },
              {
                id: "DAUBERT-PEND",
                label: "Daubert on supplemental opinion - pending",
                supportive: false,
              },
            ],
          },
          {
            name: "Material misrepresentation",
            status: "amber",
            explainer:
              "Disputed whether nonprofit pivot constitutes misrepresentation at solicitation time.",
            ev: [],
          },
        ],
        deps: {
          role: "narrowest",
          text: "Narrowest fallback. If admitted via Daubert, streamlined strict-liability path. If Daubert excludes the supplemental opinion, theory effectively dies.",
        },
        narrative: {
          legal:
            "Defendants made false or misleading representations in charitable solicitations subject to strict liability under California Business & Professions Code §17510.8.",
          jury: "California has a special law for one reason: people get conned out of charity money. The law says you can't make false statements to get charitable donations. Period. It doesn't matter if you meant to lie. It doesn't matter if you came to believe your lies later. If the statement was false when you made it, you owe the money back. That's what §17510.8 says, and it's what defendants did.",
        },
        vuln: {
          sol: 45,
          preempt: 20,
          causation: 50,
          expertDep: 65,
          damages: 55,
        },
        timeline: [
          {
            date: "Aug 2024",
            score: 30,
            event: "Initial complaint - §17510.8 added",
          },
          {
            date: "Apr 30 2026",
            score: 48,
            event: "Daubert briefing complete; no ruling",
          },
        ],
        jinst: {
          source: "Special - no model. Cal. Bus. & Prof. Code §17510.8",
          model:
            "Any person who, in connection with a charitable solicitation, knowingly makes a false or misleading statement of material fact, or knowingly conceals a material fact, is liable for damages including the amount donated and reasonable attorneys' fees.",
          links: [
            {
              phrase: "in connection with a charitable solicitation",
              ev: "Founding-era emails + LP Agreement",
            },
            {
              phrase: "false or misleading statement of material fact",
              ev: "Nonprofit-mission representations",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "laches",
        name: "Laches (5+ Years Delay)",
        short: "Laches",
        score: 76,
        dim: { evid: 78, caselaw: 82, experts: 55, mvuln: 35, damages: 62 },
        elements: [
          {
            name: "Knowledge of breach",
            status: "green",
            explainer:
              "Sept 2020 'captured by Microsoft' tweet establishes knowledge.",
            ev: [
              {
                id: "TWEET-2020",
                label: "Sep 2020 'captured by Microsoft' tweet (DX-401)",
                supportive: true,
              },
              {
                id: "PRESS-2019",
                label: "March 2019 press coverage of for-profit pivot",
                supportive: true,
              },
            ],
          },
          {
            name: "Unreasonable delay",
            status: "green",
            explainer: "5+ years between discovery and suit.",
            ev: [
              {
                id: "5YR",
                label: "4+ year gap between Sep 2020 tweet and Aug 2024 filing",
                supportive: true,
              },
            ],
          },
          {
            name: "Prejudice from delay",
            status: "green",
            explainer: "Massive investment + reliance during delay.",
            ev: [
              {
                id: "INVEST",
                label: "$13B+ Microsoft investment during delay",
                supportive: true,
              },
              {
                id: "WORKFORCE",
                label: "OpenAI grew from <100 to >1500 employees during delay",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "primary",
          text: "Strongest defense. Equity will not rescue plaintiff who slept on rights. Wins on prejudice + unreasonable delay alone.",
        },
        narrative: {
          legal:
            "Plaintiff knew of the for-profit structure no later than September 2020 and waited 4+ years to sue while defendants invested billions in reliance. Equity will not rescue a plaintiff who slept on his rights.",
          jury: "Mr. Musk tweeted in 2020 that OpenAI was 'captured by Microsoft.' He knew. He waited four years. Why? Because he started his own AI company - xAI - to compete with OpenAI. He didn't sue when he learned. He sued when his competitor needed an advantage. The law doesn't reward that kind of timing.",
        },
        vuln: {
          sol: 30,
          preempt: 20,
          causation: 40,
          expertDep: 25,
          damages: 35,
        },
        timeline: [
          {
            date: "Mar 2019",
            score: 50,
            event: "For-profit pivot announced publicly",
          },
          {
            date: "Sep 2020",
            score: 65,
            event: "Musk tweets 'captured by Microsoft'",
          },
          { date: "Mar 2023", score: 72, event: "xAI founded by Musk" },
          {
            date: "Aug 2024",
            score: 75,
            event: "Suit filed - 4+ years after tweet",
          },
          { date: "Apr 2026", score: 76, event: "Trial - laches centerpiece" },
        ],
        jinst: {
          source: "CACI 456 (Laches)",
          model:
            "A claim is barred by laches if (1) plaintiff unreasonably delayed bringing the claim; (2) defendant was prejudiced by the delay; and (3) it would be unjust to grant equitable relief.",
          links: [
            {
              phrase: "unreasonably delayed",
              ev: "Sep 2020 tweet - knowledge; Aug 2024 filing - 4 years later",
            },
            {
              phrase: "prejudiced by the delay",
              ev: "$13B+ investment + workforce growth in reliance",
            },
          ],
        },
      },
      {
        id: "standing",
        name: "No Donor Standing (AG-only)",
        short: "AG Standing",
        score: 68,
        dim: { evid: 55, caselaw: 88, experts: 30, mvuln: 42, damages: 52 },
        elements: [
          {
            name: "AG holds enforcement standing",
            status: "green",
            explainer:
              "California charitable trust law gives AG enforcement standing.",
            ev: [
              {
                id: "AG-STD",
                label: "Cal. Corp. Code §5142 - AG enforcement",
                supportive: true,
              },
            ],
          },
          {
            name: "Donor lacks individual standing",
            status: "green",
            explainer:
              "No private right of action under charitable trust statutes.",
            ev: [],
          },
          {
            name: "AG declined to join",
            status: "green",
            explainer: "AG's non-intervention is itself instructive.",
            ev: [
              {
                id: "AG-DECLINE",
                label: "California AG declined to join Musk's suit",
                supportive: true,
              },
            ],
          },
        ],
        deps: {
          role: "primary",
          text: "Strong threshold defense. Wins on standing alone. Independent of laches - even without delay, donor lacks standing.",
        },
        narrative: {
          legal:
            "California charitable trust law vests enforcement authority in the Attorney General. Individual donors lack standing. The AG declined to intervene; that judgment by the state's charitable enforcement authority is itself instructive.",
          jury: "In California, when there's a problem with a nonprofit, the Attorney General handles it - not individual donors. That's the law because we want a single, accountable enforcer rather than a free-for-all of donor lawsuits. Mr. Musk asked the Attorney General to bring this case. The AG looked at the same evidence we're looking at today and said no. That's not Mr. Altman's choice. That's California's choice.",
        },
        vuln: {
          sol: 25,
          preempt: 15,
          causation: 30,
          expertDep: 18,
          damages: 30,
        },
        timeline: [
          { date: "Jan 2025", score: 62, event: "AG declined to intervene" },
          {
            date: "Feb 2026",
            score: 68,
            event: "MSJ on standing - denied (preserved for trial)",
          },
        ],
        jinst: {
          source: "Special - Cal. Corp. Code §5142 framework",
          model:
            "Under California law, only the Attorney General has standing to enforce charitable trust obligations. A private donor may bring a derivative claim only with the Attorney General's consent.",
          links: [
            {
              phrase: "only the Attorney General",
              ev: "Cal. Corp. Code §5142(a)",
            },
            {
              phrase: "private donor derivative",
              ev: "AG declined consent - Jan 2025",
            },
          ],
        },
      },
      {
        id: "acquiesce",
        name: "Released by Acquiescence",
        short: "Acquiescence",
        score: 54,
        dim: { evid: 58, caselaw: 60, experts: 35, mvuln: 50, damages: 45 },
        elements: [
          {
            name: "Knowledge of for-profit structure",
            status: "green",
            explainer: "Musk informed of LP structure August 2018.",
            ev: [
              {
                id: "AUG-2018",
                label: "Aug 2018 LP-structure briefing (DX-512)",
                supportive: true,
              },
            ],
          },
          {
            name: "Express or implied consent",
            status: "amber",
            explainer: "Musk told Altman to say he was 'supportive.'",
            ev: [
              {
                id: "SUPPORTIVE",
                label: "Musk's 'supportive' email (DX-518)",
                supportive: true,
              },
              {
                id: "YUP-LESS",
                label: "'Yup' reply to 'less open' (DX-516)",
                supportive: true,
                caveat: "narrow context",
              },
            ],
          },
          {
            name: "Reliance by defendants",
            status: "amber",
            explainer:
              "OpenAI proceeded with LP structure based on Musk's non-objection.",
            ev: [],
          },
        ],
        deps: {
          role: "backup",
          text: "Weaker than laches but cleaner factual story. Musk's contemporaneous 'supportive' communications undercut his later breach-of-trust framing.",
        },
        narrative: {
          legal:
            "Plaintiff was informed of the for-profit LP structure in 2018, expressed support, and continued without objection. His acquiescence bars equitable relief.",
          jury: "Mr. Musk says he was deceived. But in 2018, when defendants told him about the for-profit structure, he wrote back to say he was 'supportive.' He told Mr. Altman to publicly say Musk was 'supportive.' That's not what someone says when they've been deceived. That's what someone says when they've agreed.",
        },
        vuln: {
          sol: 48,
          preempt: 20,
          causation: 35,
          expertDep: 30,
          damages: 40,
        },
        timeline: [
          {
            date: "Aug 2018",
            score: 50,
            event: "LP structure briefing to Musk",
          },
          { date: "Aug 2018", score: 60, event: "Musk 'supportive' email" },
          {
            date: "Apr 2026",
            score: 54,
            event: "Trial - acquiescence theory introduced",
          },
        ],
        jinst: {
          source: "Special - equitable acquiescence doctrine",
          model:
            "A party who, with knowledge of a fact pattern, expressly or by silence assents to the conduct, and thereby leads the other party to rely on that assent, is barred from later challenging the conduct in equity.",
          links: [
            { phrase: "with knowledge", ev: "Aug 2018 LP-structure briefing" },
            {
              phrase: "expressly or by silence assents",
              ev: "'Supportive' email + 'Yup' reply",
            },
          ],
        },
      },
    ],
  },
  neuro: {
    plaintiff: [
      {
        id: "direct",
        name: "Direct Infringement (§271(a))",
        short: "Direct Infr.",
        score: 72,
        dim: { evid: 78, caselaw: 75, experts: 65, mvuln: 55, damages: 62 },
        elements: [
          {
            name: "Patent validity",
            status: "green",
            explainer: "'061 patent issued; presumption of validity.",
            ev: [
              {
                id: "061-ISSUE",
                label: "'061 patent issuance + reexam survival",
                supportive: true,
              },
            ],
          },
          {
            name: "Defendant practices each claim element",
            status: "amber",
            explainer: "Markman pending; doctrine of equivalents key.",
            ev: [
              {
                id: "MARKMAN",
                label: "Markman hearing - pending",
                supportive: false,
              },
              {
                id: "DOE",
                label: "Doctrine of equivalents - alternative theory",
                supportive: true,
              },
            ],
          },
          {
            name: "Within US jurisdiction",
            status: "green",
            explainer: "US sales established.",
            ev: [],
          },
        ],
        deps: {
          role: "primary",
          text: "Lead patent theory. Markman ruling will determine claim scope.",
        },
        narrative: {
          legal:
            "Defendant's product practices each element of '061 claim 1, literally or under doctrine of equivalents.",
          jury: "Neurocentria invented this. They got a patent. The defendant copied it. That's the case. The technical details matter, but the principle is simple: you don't get to take someone else's invention without paying for it.",
        },
        vuln: {
          sol: 30,
          preempt: 20,
          causation: 35,
          expertDep: 62,
          damages: 55,
        },
        timeline: [
          { date: "Sep 2025", score: 65, event: "Complaint filed" },
          {
            date: "Mar 2026",
            score: 72,
            event: "Markman briefing complete; ruling pending",
          },
        ],
        jinst: {
          source: "AIPLA Model Patent Jury Instructions",
          model:
            "To prove infringement, plaintiff must show by a preponderance of the evidence that defendant's product practices each element of the asserted claim, either literally or under the doctrine of equivalents.",
          links: [
            {
              phrase: "each element",
              ev: "Markman claim construction (pending)",
            },
            {
              phrase: "doctrine of equivalents",
              ev: "Substantial-equivalence expert testimony",
            },
          ],
        },
      },
      {
        id: "willful",
        name: "Willful Infringement (§284)",
        short: "Willful",
        score: 58,
        dim: { evid: 62, caselaw: 55, experts: 48, mvuln: 55, damages: 78 },
        elements: [
          {
            name: "Pre-suit knowledge",
            status: "green",
            explainer: "TMLA gave defendant actual notice.",
            ev: [
              {
                id: "TMLA",
                label:
                  "Technology & Manufacturing License Agreement - actual notice",
                supportive: true,
              },
            ],
          },
          {
            name: "Egregious misconduct (Halo)",
            status: "amber",
            explainer: "Conduct not yet developed at trial.",
            ev: [],
          },
        ],
        deps: {
          role: "enhancement",
          text: "Damages multiplier (up to 3x). Doesn't affect liability - only damages amount.",
        },
        narrative: {
          legal:
            "Defendant had actual notice of '061 via TMLA and continued infringing conduct, supporting enhanced damages under §284.",
          jury: "Defendant signed an agreement that mentioned this exact patent. They knew about it. They infringed anyway. That's not a mistake. The law allows extra damages for that - up to three times what would otherwise be owed.",
        },
        vuln: {
          sol: 30,
          preempt: 18,
          causation: 40,
          expertDep: 55,
          damages: 30,
        },
        timeline: [
          {
            date: "Sep 2025",
            score: 50,
            event: "Complaint with willfulness allegations",
          },
          { date: "Mar 2026", score: 58, event: "TMLA produced in discovery" },
        ],
        jinst: {
          source: "AIPLA Model - Willful Infringement",
          model:
            "To find willful infringement, plaintiff must prove that defendant's conduct was egregious - beyond mere infringement - including subjective knowledge of the patent and disregard of objectively high risk of infringement.",
          links: [
            {
              phrase: "subjective knowledge",
              ev: "TMLA gave actual notice of '061 patent",
            },
            { phrase: "egregious", ev: "Continued sales after TMLA notice" },
          ],
        },
      },
      {
        id: "falsedesig",
        name: "False Designation of Origin",
        short: "False Desig.",
        score: 62,
        dim: { evid: 68, caselaw: 65, experts: 42, mvuln: 50, damages: 55 },
        elements: [
          {
            name: "False statement of origin",
            status: "green",
            explainer: "US-manufactured claims while sourcing from China.",
            ev: [
              {
                id: "US-MFG",
                label: "Defendant marketing - 'made in USA' claims",
                supportive: true,
              },
              {
                id: "CN1200366A",
                label: "Chinese sourcing evidence (CN1200366A linkage)",
                supportive: true,
              },
            ],
          },
          {
            name: "Materiality",
            status: "amber",
            explainer:
              "Materiality to consumer purchasing decisions to be developed.",
            ev: [],
          },
        ],
        deps: {
          role: "alternative",
          text: "Independent of patent infringement. Lanham Act §43(a) - does not require likelihood of confusion. Provides additional path to liability.",
        },
        narrative: {
          legal:
            "Defendant's 'US-manufactured' representations are false in light of China-sourced components, violating Lanham Act §43(a).",
          jury: "Defendant says 'made in USA.' That's not true. They source from China. Customers paid more for what they thought was an American-made product. That's a deception with a price tag - it's actionable on its own, separate from the patent.",
        },
        vuln: {
          sol: 25,
          preempt: 20,
          causation: 35,
          expertDep: 48,
          damages: 50,
        },
        timeline: [
          {
            date: "Sep 2025",
            score: 55,
            event: "Complaint with Lanham Act claims",
          },
          { date: "Apr 2026", score: 62, event: "Sourcing discovery produced" },
        ],
        jinst: {
          source: "9th Cir. Model - Lanham Act §43(a)",
          model:
            "To prove false designation of origin, plaintiff must show: (1) a false statement of fact about defendant's product; (2) the statement was material; (3) the statement was made in interstate commerce; (4) it caused or is likely to cause damage.",
          links: [
            {
              phrase: "false statement of fact",
              ev: "'Made in USA' claims while China-sourced",
            },
            {
              phrase: "material",
              ev: "Consumer-survey evidence (to be developed)",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "obvious",
        name: "§103 Obviousness",
        short: "§103 Obvious.",
        score: 65,
        dim: { evid: 62, caselaw: 72, experts: 78, mvuln: 48, damages: 60 },
        elements: [
          {
            name: "Prior art discloses compound",
            status: "green",
            explainer: "CN1200366A (1997) discloses compound.",
            ev: [
              {
                id: "CN-1997",
                label: "CN1200366A (1997) - compound disclosure",
                supportive: true,
              },
            ],
          },
          {
            name: "Prior art discloses use",
            status: "green",
            explainer: "MIT provisional 2003 discloses use.",
            ev: [
              {
                id: "MIT-2003",
                label: "MIT provisional (2003) - use disclosure",
                supportive: true,
              },
            ],
          },
          {
            name: "Motivation to combine",
            status: "amber",
            explainer: "Nexus between references requires expert testimony.",
            ev: [
              {
                id: "EXPERT-COMB",
                label: "Expert testimony on motivation to combine",
                supportive: false,
              },
            ],
          },
          {
            name: "Reasonable expectation of success",
            status: "amber",
            explainer:
              "Liu's 'we did not invent the molecule' admission helps; dosage optimization disputed.",
            ev: [
              {
                id: "LIU-NOT",
                label: "Liu admission - 'we did not invent the molecule'",
                supportive: true,
              },
              {
                id: "DOSAGE",
                label: "Dosage optimization - disputed obviousness",
                supportive: false,
              },
            ],
          },
        ],
        deps: {
          role: "primary",
          text: "Strongest invalidity defense. If successful, eliminates patent - kills all infringement theories simultaneously.",
        },
        narrative: {
          legal:
            "Claim 1 of '061 was obvious in light of CN1200366A (1997) + MIT provisional (2003) at the time of invention, rendering the patent invalid under §103.",
          jury: "This patent was given for combining two things that were already known. The Chinese paper from 1997 showed the molecule. The MIT paper from 2003 showed the use. Anyone in this field could have put them together. The Patent Office made a mistake. The law lets juries correct those mistakes.",
        },
        vuln: {
          sol: 18,
          preempt: 15,
          causation: 35,
          expertDep: 78,
          damages: 30,
        },
        timeline: [
          {
            date: "Sep 2025",
            score: 55,
            event: "Answer + invalidity counterclaims",
          },
          {
            date: "Mar 2026",
            score: 65,
            event: "Expert reports on §103 - Liu admission key",
          },
        ],
        jinst: {
          source: "AIPLA Model - Obviousness",
          model:
            "A claim is obvious under §103 if the differences between the claimed invention and the prior art are such that the claim, as a whole, would have been obvious at the time of the invention to a person having ordinary skill in the art.",
          links: [
            {
              phrase:
                "differences between the claimed invention and the prior art",
              ev: "CN1200366A + MIT provisional",
            },
            {
              phrase: "obvious at the time of the invention",
              ev: "Liu admission + expert testimony on motivation",
            },
          ],
        },
      },
      {
        id: "licensee",
        name: "License/Estoppel under TMLA",
        short: "License Estoppel",
        score: 71,
        dim: { evid: 82, caselaw: 75, experts: 50, mvuln: 35, damages: 62 },
        elements: [
          {
            name: "Valid TMLA covers accused acts",
            status: "green",
            explainer: "TMLA scope likely covers accused conduct.",
            ev: [
              {
                id: "TMLA-SCOPE",
                label: "TMLA Section 3 - license scope",
                supportive: true,
              },
            ],
          },
          {
            name: "Licensee estoppel",
            status: "green",
            explainer:
              "Plaintiff cannot challenge own licensee on licensed conduct.",
            ev: [],
          },
          {
            name: "No material breach by defendant",
            status: "amber",
            explainer:
              "Royalty payments current; no termination basis claimed.",
            ev: [],
          },
        ],
        deps: {
          role: "primary",
          text: "Threshold dispositive issue. Wins on scope alone. Independent of obviousness - even valid patent cannot be asserted against licensee on licensed conduct.",
        },
        narrative: {
          legal:
            "Defendant operates under a valid TMLA covering the accused acts. Plaintiff is estopped from asserting '061 against licensed activities.",
          jury: "Defendant has a license from plaintiff. They paid for it. They've been paying royalties. You can't sell someone a license and then sue them for using it. That's not how contracts work. That's not how patents work. That's not how the law works.",
        },
        vuln: {
          sol: 18,
          preempt: 15,
          causation: 25,
          expertDep: 30,
          damages: 35,
        },
        timeline: [
          {
            date: "Sep 2025",
            score: 62,
            event: "TMLA defense raised in answer",
          },
          { date: "Mar 2026", score: 71, event: "TMLA scope briefing" },
        ],
        jinst: {
          source: "Special - TMLA license + estoppel",
          model:
            "A patent owner cannot assert the patent against licensed activities of its own licensee, where the licensee operates within the scope of a valid license. Licensee estoppel further bars the patent owner from challenging activities the licensee was authorized to perform.",
          links: [
            {
              phrase: "licensed activities",
              ev: "TMLA Section 3 - defendant's product within license scope",
            },
            {
              phrase: "valid license",
              ev: "TMLA Section 8 - current royalty payments",
            },
          ],
        },
      },
      {
        id: "hist",
        name: "Prosecution History Estoppel",
        short: "PHE",
        score: 58,
        dim: { evid: 68, caselaw: 78, experts: 55, mvuln: 42, damages: 55 },
        elements: [
          {
            name: "Claim amendment during prosecution",
            status: "green",
            explainer: "Inventors amended claims after §102 rejection.",
            ev: [
              {
                id: "AMEND-2018",
                label: "Aug 2018 amendment - narrowed claim 1",
                supportive: true,
              },
            ],
          },
          {
            name: "Amendment to overcome prior art",
            status: "green",
            explainer:
              "Office Action cited prior art; amendment narrowed scope.",
            ev: [],
          },
          {
            name: "Surrendered subject matter covers accused product",
            status: "amber",
            explainer: "Festo presumption analysis required.",
            ev: [
              {
                id: "FESTO",
                label: "Festo presumption analysis",
                supportive: false,
              },
            ],
          },
        ],
        deps: {
          role: "narrowing",
          text: "Narrows infringement scope rather than eliminating. Combined with claim construction, can knock out doctrine-of-equivalents theory.",
        },
        narrative: {
          legal:
            "During prosecution, plaintiff narrowed claim 1 to overcome prior art rejection. The surrendered subject matter cannot be recaptured under doctrine of equivalents.",
          jury: "When plaintiff applied for this patent, the Patent Office said it was too broad. They had to narrow it to get the patent at all. Now, in court, they're trying to argue it covers things they gave up to get the patent in the first place. The law calls that prosecution history estoppel - you can't have it both ways.",
        },
        vuln: {
          sol: 18,
          preempt: 15,
          causation: 35,
          expertDep: 55,
          damages: 50,
        },
        timeline: [
          { date: "Sep 2025", score: 50, event: "PHE defense raised" },
          {
            date: "Mar 2026",
            score: 58,
            event: "File-history analysis complete",
          },
        ],
        jinst: {
          source: "AIPLA Model - Prosecution History Estoppel",
          model:
            "The doctrine of prosecution history estoppel limits a patent holder's ability to claim infringement under the doctrine of equivalents. When a patent claim is narrowed during prosecution to overcome prior art, the patent holder is presumed to have surrendered the territory between the original claim and the narrowed claim.",
          links: [
            {
              phrase: "narrowed during prosecution",
              ev: "Aug 2018 amendment overcoming Office Action rejection",
            },
            {
              phrase: "presumed to have surrendered",
              ev: "Festo presumption applies; rebuttal burden on patent holder",
            },
          ],
        },
      },
    ],
  },
};

const BATTLE_ISSUES = {
  ...ILE_K_BI,
  juul: [
    {
      id: "sonner",
      issue: "Sonner Equitable Relief Bar (SFUSD)",
      plaintiff: {
        position:
          "Equitable abatement is not duplicative of compensatory damages because it covers prospective remediation that legal damages cannot address. Six site inspection reports establish concrete future costs. California public nuisance doctrine has historically supported abatement as an equitable remedy.",
        cites: [
          "Dorn site reports (PX-201 to PX-206)",
          "People v. ConAgra (Cal. Ct. App. 2017)",
          "Pomona Police",
        ],
      },
      defendant: {
        position:
          "Sonner v. Premier (9th Cir. 2020) bars equitable relief in federal court when an adequate legal remedy exists. SFUSD's compensatory damages theory - however weakly supported by Pak - constitutes an adequate legal remedy that forecloses equitable abatement. The $78.8M estimate is speculative prospective harm, not the kind of irreparable harm that equity has historically remedied.",
        cites: [
          "Sonner v. Premier Nutrition Corp. (9th Cir. 2020)",
          "Pak 12/21/21 Dep. - adequate legal remedy admission",
        ],
      },
      ai: "Defense currently has the stronger position. The Ninth Circuit's Sonner holding is squarely on point and SFUSD has not identified a compelling basis for distinguishing it. Plaintiff's best argument - that prospective abatement is categorically different from compensatory damages - has mixed support in district-court cases within the circuit. The Pak admission strengthens the defense by undermining the claim that compensatory damages are inadequate.",
      winner: "defendant",
    },
    {
      id: "fclaa",
      issue: "FCLAA Preemption Scope",
      plaintiff: {
        position:
          "FCLAA preempts only express health-warning claims. Claims based on negligent design, fraudulent concealment, and product defect survive preemption because FCLAA does not occupy the entire field of tobacco regulation.",
        cites: [
          "Cipollone v. Liggett Group (1992)",
          "Altria Group, Inc. v. Good (2008)",
        ],
      },
      defendant: {
        position:
          "FCLAA preemption reaches any claim that imposes a labeling or advertising standard different from federal requirements. Plaintiff's negligent-marketing theory effectively imposes such a standard and is preempted.",
        cites: ["Cipollone (preemption framework)", "FDCA §387p(a)(2)"],
      },
      ai: "Plaintiff has the stronger position on this issue. The Cipollone-Good framework supports preserving design-defect and fraudulent-concealment claims. Negligent-marketing claims are closer to the line - depends on framing. Defense has not identified clear-error in plaintiff's framing of the design vs. labeling distinction.",
      winner: "plaintiff",
    },
    {
      id: "enterprise",
      issue: "RICO Enterprise Element",
      plaintiff: {
        position:
          "JLI, Altria, distributors, and management defendants formed an association-in-fact enterprise distinct from the JLI corporate structure, with the common purpose of marketing addictive nicotine products to youth.",
        cites: [
          "Boyle v. United States (2009)",
          "H.J. Inc. v. Northwestern Bell (1989)",
        ],
      },
      defendant: {
        position:
          "Plaintiff's enterprise theory collapses the corporate distinct-entity requirement. The 'enterprise' described is identical to JLI's normal corporate operations. Bowen MSJ ruling rejected officer-as-enterprise theory.",
        cites: [
          "Reves v. Ernst & Young (1993)",
          "Bowen MSJ Order (Dkt.3527)",
          "King v. Hilton-Davis (1991)",
        ],
      },
      ai: "Defense currently has the stronger position. The Bowen MSJ ruling is in this MDL and rejected a similar officer-as-enterprise theory. Plaintiff would need to distinguish that ruling and articulate enterprise structure beyond JLI's normal operations. Boyle is plaintiff's best supreme court hook but Bowen interpretation is closer to the operative facts.",
      winner: "defendant",
    },
  ],
  musk: [
    {
      id: "trustform",
      issue: "Charitable Trust Formation",
      plaintiff: {
        position:
          "The 2015-2017 founding emails, LP Agreement 'spirit of a donation' language, and Brockman journal establish donor intent to create a charitable trust. OpenAI's nonprofit status and public mission statements create the necessary charitable purpose.",
        cites: [
          "PX-001 to PX-021 (founding emails)",
          "PX163 (Brockman journal)",
          "PX200 (LP Agreement)",
          "Restatement (Third) of Trusts §28",
        ],
      },
      defendant: {
        position:
          "Donations were unrestricted gifts. No formal trust instrument exists. Charitable trust requires specific donor intent that exceeds general charitable purpose statements. Hemel academic position: board approval required for charitable trust obligations.",
        cites: ["Hemel academic position (DX-602)", "Cal. Corp. Code §5142"],
      },
      ai: "Plaintiff has the slightly stronger position post-Liu testimony. The combination of contemporaneous communications, the 'spirit of a donation' language in the LP Agreement, and the Brockman journal create a credible totality-of-circumstances case for trust formation. Defense's argument that no formal trust instrument exists is weakened by Cal. trust law's recognition of informal trust creation. The decisive factor will be jury inferences from the founding-era communications.",
      winner: "plaintiff",
    },
    {
      id: "fifo",
      issue: "Wazzan v. Dudney - Tracing Methodology",
      plaintiff: {
        position:
          "Wazzan's methodology - Opinions 4-7 admitted - establishes that donations remain traceable to current OpenAI assets and the Microsoft equity. FIFO is not the universal accounting standard for charitable trust tracing.",
        cites: ["Wazzan Opinions 4-7 (admitted)", "Cal. Probate Code §16003"],
      },
      defendant: {
        position:
          "Dudney's FIFO analysis is the orthodox tracing approach. Donations were spent on early R&D long before any current asset accumulation. Wazzan's exclusion of Opinions 1-2 (FIFO repudiation) leaves the defense unrebutted on the central tracing question.",
        cites: [
          "Dudney Expert Report",
          "Wazzan Daubert Order - Opinions 1-2 excluded",
        ],
      },
      ai: "Defense has slight edge after Wazzan exclusion. The exclusion of Opinions 1-2 (which directly attacked FIFO) makes Dudney's FIFO analysis effectively unrebutted on that specific point. Plaintiff's strategy to lead the jury through Wazzan's surviving opinions before Dudney can reframe is sound but high-risk. Day 3 jury observations show Seat 4 (swing) yawning during Wazzan re-exam - methodology arguments are not landing.",
      winner: "defendant",
    },
    {
      id: "sol-musk",
      issue: "SOL Trigger - Sept 2020 Tweet",
      plaintiff: {
        position:
          "The September 2020 tweet expressed frustration but did not constitute legal discovery of breach. The actual breach - for-profit conversion - occurred October 2025. Statute runs from breach, not from suspicion.",
        cites: ["Cal. Code Civ. Proc. §338", "Discovery Rule cases"],
      },
      defendant: {
        position:
          "The September 2020 tweet 'captured by Microsoft' establishes plaintiff's awareness of the alleged breach. Subsequent 4-year delay before suit triggers SOL bar. Discovery rule does not preserve claims where plaintiff publicly accused defendants of the conduct now sued upon.",
        cites: ["DX-401 (Sep 2020 tweet)", "Discovery Rule cases - Cal."],
      },
      ai: "Defense has stronger position by a clear margin. Plaintiff's own contemporaneous public statements establish discovery for SOL purposes. The 'breach didn't occur until 2025' argument fails because the suit pleads breach throughout the prior period. Plaintiff's best counter is that the conduct constituted a continuing breach rather than discrete tort - colorable but not strong on these facts.",
      winner: "defendant",
    },
  ],
  neuro: [
    {
      id: "markman",
      issue: "Claim Construction - '061 Claim 1",
      plaintiff: {
        position:
          "Claim 1 should be construed broadly to encompass defendant's product. Specification supports broad construction; prosecution history does not require narrow reading on this term.",
        cites: [
          "'061 patent specification",
          "Phillips v. AWH Corp. (Fed. Cir. 2005)",
        ],
      },
      defendant: {
        position:
          "Claim 1 must be construed narrowly to exclude defendant's product. Prosecution-history disclaimer requires narrow reading. Specification embodiments support narrow construction.",
        cites: [
          "File history Aug 2018 amendment",
          "Festo Corp. v. SMC (Fed. Cir. 2002)",
        ],
      },
      ai: "Genuinely close. Markman ruling will determine outcome of the case. Plaintiff's specification arguments are strong but the August 2018 amendment is a significant prosecution-history hook for defendant. Federal Circuit law is split on similar fact patterns. 50-50 going into the Markman hearing.",
      winner: "contested",
    },
    {
      id: "lostprofits",
      issue: "Lost Profits Availability",
      plaintiff: {
        position:
          "Lost profits available - Panduit factors satisfied: demand for patented product (yes), absence of acceptable non-infringing alternatives (CN1200366A is not commercially viable), capacity to meet demand (Neurocentria has FDA-cleared capacity), and quantifiable profit margin (financials produced).",
        cites: [
          "Panduit Corp. v. Stahlin Bros. (6th Cir. 1978)",
          "Rite-Hite v. Kelley (Fed. Cir. 1995)",
        ],
      },
      defendant: {
        position:
          "Lost profits unavailable - CN1200366A is a viable non-infringing alternative. Plaintiff lacks evidence of demand, capacity, and margin in any rigorous form. Reasonable royalty is the appropriate remedy.",
        cites: ["Panduit", "CN1200366A as non-infringing alternative"],
      },
      ai: "Defense has stronger position. CN1200366A as non-infringing alternative is the most likely fatal element to lost profits. Plaintiff's strategy to elect reasonable royalty reflects this - the upside of lost profits ($15-40M) does not justify the risk of losing the theory entirely. The Theory Election Tool flags this.",
      winner: "defendant",
    },
    {
      id: "willfulness",
      issue: "Willfulness - TMLA Notice",
      plaintiff: {
        position:
          "TMLA gave defendant actual notice of the '061 patent. Defendant's continued sales after notice supports willfulness under Halo. Enhanced damages up to 3x are warranted.",
        cites: ["TMLA", "Halo Electronics v. Pulse Electronics (2016)"],
      },
      defendant: {
        position:
          "TMLA mention of '061 was incidental and did not constitute notice of infringement claims. Defendant relied on TMLA license scope opinion. No subjective knowledge of infringement; no egregious conduct.",
        cites: [
          "TMLA Section 3 (license scope)",
          "Halo (egregious conduct standard)",
        ],
      },
      ai: "Plaintiff has slight edge. TMLA reference to '061 is plausibly notice; Halo's 'egregious' standard is fact-intensive but the defendant's reliance on license-scope is itself contested. Likely jury question that turns on credibility of the license-scope opinion process.",
      winner: "plaintiff",
    },
  ],
};

const NEURO_ELECTION = {
  electing: "plaintiff",
  decision: "Reasonable Royalty (recommended)",
  alternatives: [
    {
      name: "Reasonable Royalty",
      expectedRecovery: "$2M – $8M",
      basis: "AIDP license comparator at industry standard rate",
      established: "12 of 15 Georgia-Pacific factors with supporting evidence",
      missing:
        "Factor 12 (portion of profit customarily allowed) - no evidence in record",
      risk: "Low - strong comparator + framework gives 12 ways to argue rate upward",
      verdict: "recommended",
    },
    {
      name: "Lost Profits",
      expectedRecovery: "$15M – $40M (if fully established)",
      basis:
        "Panduit factors - demand, capacity, absence of non-infringing alternatives",
      established: "Demand for patented product (partial); capacity (partial)",
      missing:
        "Absence of non-infringing alternatives (CRITICAL - CN1200366A may constitute viable alternative); manufacturing capacity (no evidence)",
      risk: "High - defense will defeat lost profits at SJ by pointing to CN1200366A. Losing the theory entirely is realistic outcome.",
      verdict: "declined",
    },
  ],
  recommendation:
    "Elect reasonable royalty. Lost profits requires establishing absence of non-infringing alternatives - an element defendant will defeat by pointing to CN1200366A. The upside of lost profits ($15-40M) does not justify the risk of losing the theory entirely at summary judgment. Reasonable royalty has a clear comparable in the AIDP license and the Georgia-Pacific framework gives twelve ways to argue the rate upward.",
};

const WITNESSES_INTEL = {
  ...ILE_K_WI,
  juul: {
    plaintiff: [
      {
        id: "pak",
        name: "Quarry Pak",
        role: "SFUSD Senior Director · 30(b)(6)",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 55,
          consistency: 30,
          exposure: 92,
          authority: 88,
          advocacy: 25,
        },
        story:
          "Honest bureaucrat who said the most damaging thing possible for his own side. Credible-but-harmful - harder to rehabilitate than dishonest witness.",
        prediction: {
          direct:
            "SFUSD counsel will lead with educational mission, observable presence of JUUL in district schools (confiscated devices, disciplinary records), and the six schools subject to Dorn site inspections. Strategy: establish observable harm before JLI reaches damages cross. Risk of deviation on direct: low - testimony on observable harm is consistent with the record.",
          cross:
            "JLI leads with p.47:12-18 within first three questions after 30(b)(6) foundation. No experienced defense counsel delays this. After admission, JLI asks about Matthews admission on staff time, absence of cost documentation in discovery, and the gap between $34,875 documented floor and $78.8M Dorn estimate. Risk of deviation: moderate - Pak may try to contextualize prior admission. JLI contingency (production date of any later-compiled docs) already in Impeachment Bank.",
          redirect:
            "SFUSD will rehabilitate with Dorn site inspection reports - argument that observable physical damage was documented even if administrative costs weren't tracked. Whether rehabilitation works depends on how specifically JLI's cross established the scope of the admission.",
        },
        elements: [
          {
            theoryId: "pn",
            elemIdx: 3,
            status: "red",
            note: "Pak admission is the central damages problem",
          },
          {
            theoryId: "pn",
            elemIdx: 0,
            status: "amber",
            note: "Helps establish JUUL presence",
          },
          {
            theoryId: "pn",
            elemIdx: 2,
            status: "amber",
            note: "30(b)(6) designation supports special-injury claim",
          },
        ],
        depo: {
          status: "complete",
          date: "Dec 21, 2021",
          topics: [
            "SFUSD financial damages methodology",
            "30(b)(6) designation scope",
            "Site inspection process",
            "JUUL device presence in schools",
            "Confiscation procedures",
          ],
          notTopics: [
            "Cost documentation efforts post-2022",
            "Dorn report scope/methodology",
            "Sonner case awareness",
            "Specific federal-funding source allocations",
          ],
          admissions: [
            {
              cite: "p.47:12-18",
              text: '"We don\'t have that information" - re costs attributable to JUUL products',
              impact: "red",
              linkedToImpBank: true,
            },
            {
              cite: "p.23:4-9",
              text: "30(b)(6) designation scope: financial damages topic - broad",
              impact: "neutral",
            },
            {
              cite: "p.81:15-22",
              text: "Site inspection process forms predicate for Dorn report",
              impact: "amber",
            },
          ],
          gaps: [
            "Was Pak the appropriate 30(b)(6) designee or was a more knowledgeable employee available?",
            "Was 30(b)(6) notice scope broad enough to cover the $78.8M Dorn estimate or does it predate Dorn?",
          ],
          actions: [
            "Issue supplemental 30(b)(6) notice on post-2022 cost documentation before MSJ ruling",
          ],
        },
        relations: [
          {
            with: "matthews",
            type: "corroborates",
            note: "Both admit inability to quantify past costs - JLI uses for damages attack",
          },
          {
            with: "dorn",
            type: "exposes",
            note: "Dorn's $78.8M estimate undermined by Pak's admission",
          },
          {
            with: "coates",
            type: "corroborates",
            note: "Both establish operational disruption",
          },
        ],
        logistics: {
          availability: "Confirmed for trial",
          location: "San Francisco, CA",
          flags: [],
        },
        prep: {
          sessions: 2,
          sessionDates: ["Apr 12, 2026", "Apr 26, 2026"],
          covered: [
            "Cross-exam on p.47:12-18",
            "Site inspection process testimony",
            "Rehabilitation framing",
          ],
          uncovered: [
            "Sonner-bar context",
            "Rehabilitation arc on prospective costs",
            "Anticipated JLI contingency questions",
          ],
        },
      },
      {
        id: "matthews",
        name: "Vincent Matthews",
        role: "Former SFUSD Superintendent",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 72,
          consistency: 60,
          exposure: 65,
          authority: 75,
          advocacy: 35,
        },
        story:
          "Senior administrator with policy authority. Strong on systemic harm narrative; weaker on financial specifics - corroborates Pak admission.",
        prediction: {
          direct:
            "Lead on systemic impact - confiscation rates, disciplinary referrals, classroom-time loss. Strong narrative on student-welfare burden. Avoid financial specifics where possible.",
          cross:
            'JLI confronts with absence of cost-quantification documents. Asks about staff-time tracking, federal funding sources, the gap between aspirational damages and documented harm. May go to Sonner framing - "if these are real costs why aren\'t they in the budget?"',
          redirect:
            "Re-establish that systemic policy harm is qualitatively different from line-item budget impact. Public nuisance framework reaches harm that exceeds standard cost-accounting.",
        },
        elements: [
          {
            theoryId: "pn",
            elemIdx: 1,
            status: "green",
            note: "Strong on systemic public-nuisance qualification",
          },
          {
            theoryId: "pn",
            elemIdx: 2,
            status: "amber",
            note: "Helps special injury but vague on quantum",
          },
          {
            theoryId: "pn",
            elemIdx: 3,
            status: "red",
            note: "Hurts damages - admits no documentation",
          },
        ],
        depo: {
          status: "complete",
          date: "Oct 7, 2021",
          topics: [
            "SFUSD policy authority",
            "Systemic impact narrative",
            "Disciplinary statistics",
            "Confiscation tracking",
          ],
          notTopics: [
            "Specific budget allocations",
            "Cost-tracking systems pre-2018",
            "Federal funding source attribution",
          ],
          admissions: [
            {
              cite: "p.112:8-15",
              text: '"We didn\'t have a system to track JUUL-specific costs"',
              impact: "red",
            },
            {
              cite: "p.89:3-12",
              text: "Confiscation rate data - qualitative",
              impact: "green",
            },
          ],
          gaps: ["Federal funding implications for damages calc"],
          actions: ["Confirm trial availability - Matthews is now retired"],
        },
        relations: [
          {
            with: "pak",
            type: "corroborates",
            note: "Both admit no cost documentation",
          },
          {
            with: "dorn",
            type: "foundational",
            note: "Provides policy context Dorn relies on",
          },
        ],
        logistics: {
          availability: "Confirmed; retired but available",
          location: "San Francisco, CA",
          flags: [],
        },
        prep: {
          sessions: 1,
          sessionDates: ["Apr 18, 2026"],
          covered: ["Systemic harm narrative"],
          uncovered: [
            "Sonner framing",
            "Rehabilitation on damages",
            "Cross prep for cost-tracking gap",
          ],
        },
      },
      {
        id: "halpern",
        name: "Bonnie Halpern-Felsher, PhD",
        role: "Stanford Adolescent Health · Epidemiology Expert",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 78,
          consistency: 88,
          exposure: 25,
          authority: 95,
          advocacy: 40,
        },
        story:
          "Highly credentialed academic with strong subject-matter authority. Sept 2021 report establishes 75% youth ignorance of JUUL nicotine content - central plaintiff exhibit.",
        prediction: {
          direct:
            "Walk through Sept 2021 report methodology, sampling, and central finding (75% of SFUSD students 15-24 didn't know JUUL contained nicotine). Establish the deceptive-marketing chain. Q&A on broader literature placing JUUL within e-cigarette epidemiology.",
          cross:
            "JLI attacks methodology - survey instruments, sample selection bias, recall bias. May challenge causal chain between marketing and student awareness. Likely raises peer-review status of supporting publications.",
          redirect:
            "Re-establish methodology fits standard public-health survey design. Cite peer-reviewed publications. Underscore that federal regulatory bodies have relied on similar methodology.",
        },
        elements: [
          {
            theoryId: "pn",
            elemIdx: 0,
            status: "green",
            note: "Establishes JUUL created harm condition",
          },
          {
            theoryId: "pn",
            elemIdx: 1,
            status: "green",
            note: "Expert opinion on public-nuisance qualification",
          },
          {
            theoryId: "nm",
            elemIdx: 1,
            status: "green",
            note: "Establishes breach of duty - youth-targeting",
          },
          {
            theoryId: "nm",
            elemIdx: 2,
            status: "green",
            note: "General causation for negligent marketing",
          },
        ],
        depo: {
          status: "complete",
          date: "Mar 14, 2022",
          topics: [
            "Survey methodology",
            "Sample selection",
            "Awareness findings",
            "Peer-reviewed publication record",
            "Prior expert testimony",
          ],
          notTopics: [
            "Specific Seliavski causation",
            "Individual plaintiff causation per opt-out",
          ],
          admissions: [
            {
              cite: "Dep. p.156:14-20",
              text: "75% awareness gap - central finding stands per methodology",
              impact: "green",
            },
            {
              cite: "Dep. p.203:8-15",
              text: "Did not analyze individual plaintiff causation - only general",
              impact: "amber",
            },
          ],
          gaps: ["Plaintiff-specific causation for Seliavski cohort"],
          actions: [
            "Coordinate Halpern-Felsher trial calendar - academic commitments",
          ],
        },
        relations: [
          {
            with: "dorn",
            type: "foundational",
            note: "Halpern-Felsher establishes the harm; Dorn quantifies remediation",
          },
        ],
        logistics: {
          availability:
            "⚠️ Sep 30 conflict - academic commitment. Antitrust trial Day 2 if jury selection runs full day Sep 28.",
          location: "Stanford, CA",
          flags: ["calendar-conflict-sep30"],
        },
        prep: {
          sessions: 3,
          sessionDates: ["Mar 20, 2026", "Apr 5, 2026", "Apr 22, 2026"],
          covered: [
            "Direct outline",
            "Cross-prep for methodology attacks",
            "Daubert anticipation",
          ],
          uncovered: [
            "Trial-day logistics",
            "Demonstrative slides",
            "Coordinating with Dorn's testimony",
          ],
        },
        expert: {
          daubert: {
            status: "all opinions admitted",
            rulings: [
              {
                date: "Sep 2022",
                ruling:
                  "All Halpern-Felsher opinions admitted (Dkt.3892). Methodology survives Daubert challenge.",
              },
            ],
          },
          attackSurface: [
            "Survey instrument validity",
            "Sample selection bias",
            "Recall bias in self-report data",
            "Generalizability from SFUSD subset to district-wide harm",
          ],
          opposing:
            "No designated rebuttal epidemiology expert by JLI. Plaintiff has unchallenged expert on awareness gap.",
          redirect: [
            "Methodology fits standard public-health survey design",
            "Peer-reviewed publication record (NEJM, JAMA Pediatrics)",
            "Federal regulatory bodies rely on similar methodology",
          ],
          juryInstr:
            "9th Cir. Model 2.13 (Expert Opinion) - jury may accept or reject; consider qualifications, bases, and reliability of methodology",
          priorTestimony: [
            {
              case: "FDA v. Manufacturer (D.D.C. 2019)",
              topic: "Youth e-cigarette awareness",
              outcome: "Testimony admitted",
            },
            {
              case: "State AG enforcement (Mass. 2020)",
              topic: "Adolescent nicotine epidemiology",
              outcome: "Testimony admitted",
            },
          ],
        },
      },
      {
        id: "dorn",
        name: "Daniel Dorn, MS, PE",
        role: "Environmental Remediation Expert · Abatement",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 65,
          consistency: 72,
          exposure: 48,
          authority: 82,
          advocacy: 55,
        },
        story:
          "Methodology survived Daubert. Single-point-of-failure for damages theory - if attacked successfully on methodology, damages case collapses. $78.8M estimate vs $34,875 documented floor creates credibility gap that opens cross-exam path.",
        prediction: {
          direct:
            "Walk through six-school site inspection methodology. Establish damage quantification per school. Build to district-wide extrapolation with explanation of why six schools provide representative sample. Address Sonner risk preemptively - frame prospective costs as compensable abatement, not speculative damages.",
          cross:
            "JLI attacks: extrapolation from six to district-wide; gap between $34,875 documented and $78.8M estimate; foundation question - extrapolating from Pak/Matthews admissions about cost-tracking failures; whether prospective costs are speculative under Sonner.",
          redirect:
            "Re-establish prospective abatement is independently compensable under California public nuisance doctrine. Distinguish from ordinary money damages - abatement is forward-looking remediation.",
        },
        elements: [
          {
            theoryId: "pn",
            elemIdx: 3,
            status: "amber",
            note: "Best damages support but methodology under attack",
          },
          {
            theoryId: "pn",
            elemIdx: 0,
            status: "green",
            note: "Site inspections document the condition",
          },
        ],
        depo: {
          status: "complete",
          date: "Aug 18, 2022",
          topics: [
            "Site inspection methodology",
            "Six-school sample selection",
            "Abatement cost calculation",
            "Extrapolation framework",
            "Sonner-bar awareness",
          ],
          notTopics: [
            "Comparable cases with smaller extrapolation gaps",
            "Prior testimony in abatement cases generally",
          ],
          admissions: [
            {
              cite: "Dep. p.234:6-15",
              text: "Extrapolation depends on assumption that six schools represent district-wide",
              impact: "amber",
            },
            {
              cite: "Dep. p.78:4-12",
              text: "Did not independently verify Pak/Matthews cost-documentation testimony",
              impact: "red",
            },
          ],
          gaps: ["Comparable-case analysis for extrapolation defensibility"],
          actions: [
            "Prepare Dorn for Sonner-bar cross. Develop comparable-case defense for extrapolation.",
          ],
        },
        relations: [
          {
            with: "halpern",
            type: "foundational",
            note: "Relies on Halpern-Felsher's harm establishment",
          },
          {
            with: "pak",
            type: "exposed-by",
            note: "Pak admission undermines Dorn's foundation",
          },
          {
            with: "matthews",
            type: "exposed-by",
            note: "Matthews corroborates Pak admission",
          },
        ],
        logistics: {
          availability:
            "Confirmed for trial. Other expert commitments in unrelated matter Q3 2026 - coordinate scheduling.",
          location: "Sacramento, CA",
          flags: ["other-expert-commitments-q3"],
        },
        prep: {
          sessions: 3,
          sessionDates: ["Mar 28, 2026", "Apr 11, 2026", "Apr 25, 2026"],
          covered: [
            "Site inspection methodology",
            "Sonner-bar cross prep",
            "Pak admission redirect script",
          ],
          uncovered: [
            "Specific jury instruction for expert testimony",
            "Anticipated rebuttal expert (if JLI designates)",
            "Comparable-case defense for extrapolation",
          ],
        },
        expert: {
          daubert: {
            status: "all opinions admitted",
            rulings: [
              {
                date: "Sep 2022",
                ruling:
                  "Dorn methodology survives Daubert (Dkt.3920). Methodology adequately reliable; weight is for jury.",
              },
            ],
          },
          attackSurface: [
            "Extrapolation from six schools to district-wide",
            "Prospective vs documented costs (Sonner risk)",
            "Connection between JLI conduct and specific remediation items",
            "Foundation depends on Pak/Matthews - both have damaging admissions",
          ],
          opposing:
            "No designated rebuttal abatement expert by JLI. Plaintiff has unchallenged expert on remediation cost - important for trial framing.",
          redirect: [
            "Prospective abatement is compensable under California public nuisance doctrine independent of past documentation",
            "Methodology independence from Pak admission",
            "Federal regulatory bodies use similar extrapolation in cost-recovery contexts",
          ],
          juryInstr:
            "9th Cir. Model 2.13 - supplement with California public-nuisance abatement instruction",
          priorTestimony: [
            {
              case: "State v. Polluter (Cal. Sup. Ct. 2018)",
              topic: "Environmental abatement cost",
              outcome: "Testimony admitted; $42M abatement awarded",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "jli30b6",
        name: "JLI 30(b)(6) Corporate Rep",
        role: "JUUL Labs Inc. · Corporate Designee",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 42,
          consistency: 58,
          exposure: 78,
          authority: 85,
          advocacy: 70,
        },
        story:
          "Hostile witness for plaintiff. Foundation witness for JLI internal documents. High advocacy risk - jury will read corporate-designee posture as defensive.",
        prediction: {
          direct:
            "For JLI: limit testimony to factual foundation for product, regulatory compliance, FDA submission process. Avoid youth-marketing framing. Position company as good-faith regulatory actor.",
          cross:
            "Plaintiff confronts with JLI internal marketing documents (PX-44 to PX-67), Halpern-Felsher findings, FDA warning letters timeline, Altria $12.8B investment context. Likely best path: lead with the documents - make designee authenticate, then ask whether the document means what it says.",
          redirect:
            "JLI re-frames marketing strategy as adult-cessation focused, internal docs as ordinary business, FDA compliance as good faith.",
        },
        elements: [
          {
            theoryId: "pn",
            elemIdx: 0,
            status: "amber",
            note: "Reluctantly authenticates JLI marketing docs",
          },
          {
            theoryId: "nm",
            elemIdx: 1,
            status: "amber",
            note: "Foundation witness for breach evidence",
          },
        ],
        depo: {
          status: "complete",
          date: "Multiple sessions 2021-2022",
          topics: [
            "Product design",
            "FDA regulatory submissions",
            "Marketing strategy framing",
            "Internal document foundation",
          ],
          notTopics: [
            "Specific board-level decisions",
            "Altria coordination details",
            "Bowen role specifics",
          ],
          admissions: [
            {
              cite: "Dep. p.342:8-15",
              text: "Authenticated PX-44 internal marketing strategy doc",
              impact: "amber",
            },
            {
              cite: "Dep. p.401:12-22",
              text: "Authenticated FDA submission timeline",
              impact: "neutral",
            },
          ],
          gaps: [
            "Specific personal knowledge limitations on certain corporate-level decisions",
          ],
          actions: [
            "Identify successor 30(b)(6) if original designee unavailable for trial",
          ],
        },
        relations: [
          {
            with: "halpern",
            type: "contradicted-by",
            note: "Halpern-Felsher findings contradict JLI's adult-cessation framing",
          },
          {
            with: "bowen",
            type: "corroborates",
            note: "Both establish JLI corporate operations",
          },
        ],
        logistics: {
          availability: "Subject to subpoena. JLI may substitute designee.",
          location: "San Francisco, CA",
          flags: ["designee-substitution-risk"],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: ["JLI prep - outside our control"],
        },
      },
    ],
  },
  musk: {
    plaintiff: [
      {
        id: "musk",
        name: "Elon Musk",
        role: "Plaintiff · Founder & Donor",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 38,
          consistency: 48,
          exposure: 88,
          authority: 60,
          advocacy: 85,
        },
        story:
          "Polarizing public figure. Extreme prior-statement exposure (Twitter history) + extreme advocacy risk (xAI competitor). Jury will see partisan witness with credibility liability.",
        prediction: {
          direct:
            "Establish 2015-2017 charitable intent and donations. Walk through founding emails (PX-001 to PX-021). Establish reliance on nonprofit representations. Avoid 2020-2024 timeframe to minimize SOL ammunition.",
          cross:
            "Defense leads with Sept 2020 'captured by Microsoft' tweet (DX-401) - establishes knowledge for SOL. Then xAI founding (Mar 2023) - competitor motive. Then 'Yup' reply to 'less open' (DX-516) - consent to closed-source pivot. Aug 2018 'supportive' email (DX-518) - acquiescence.",
          redirect:
            "Re-establish charitable intent at donation time was sincere; subsequent for-profit conversion was the breach not the original frustration; xAI competitive activity is irrelevant to whether trust was created in 2015-2017.",
        },
        elements: [
          {
            theoryId: "trust",
            elemIdx: 0,
            status: "green",
            note: "Donor intent - primary evidence",
          },
          {
            theoryId: "laches",
            elemIdx: 0,
            status: "red",
            note: "Hurts laches defense - establishes 2020 knowledge",
          },
          {
            theoryId: "acquiesce",
            elemIdx: 1,
            status: "red",
            note: "Twitter + supportive email - defense uses heavily",
          },
        ],
        depo: {
          status: "complete",
          date: "Multiple 2025 sessions",
          topics: [
            "2015-2017 founding context",
            "Donation amounts and timing",
            "Subsequent communications with Altman",
            "Reaction to 2018 LP structure briefing",
            "2020 Twitter posts",
            "xAI founding",
          ],
          notTopics: [
            "Specific 2018 conversations contemporaneous with LP briefing",
            "Detailed financial reliance theory",
          ],
          admissions: [
            {
              cite: "Dep. Vol. III p.234",
              text: "Acknowledges Sept 2020 tweet referred to OpenAI's commercial direction",
              impact: "red",
            },
            {
              cite: "Dep. Vol. II p.118",
              text: "'Spirit of donation' language reflected mutual understanding",
              impact: "green",
            },
          ],
          gaps: ["Detailed reliance theory on each donation"],
          actions: [
            "Prepare Musk for 2020 tweet cross - pre-test rehabilitation arc",
          ],
        },
        relations: [
          {
            with: "brockman",
            type: "corroborates",
            note: "PX163 corroborates Musk's account of original promise",
          },
          {
            with: "altman",
            type: "contradicts",
            note: "Altman testimony will dispute trust creation",
          },
        ],
        logistics: {
          availability:
            "Confirmed. Multiple security/scheduling logistics required.",
          location: "Austin, TX",
          flags: ["security-coordination", "press-management"],
        },
        prep: {
          sessions: 5,
          sessionDates: ["Feb 2026 - Apr 2026"],
          covered: [
            "Direct outline",
            "Cross prep for 2020 tweet",
            "xAI relevance objections",
            "'Yup' email rehabilitation",
          ],
          uncovered: ["Real-time deviation handling for hostile cross"],
        },
      },
      {
        id: "wazzan",
        name: "C. Paul Wazzan, PhD",
        role: "Damages Expert · Tracing Methodology",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 48,
          consistency: 62,
          exposure: 65,
          authority: 88,
          advocacy: 42,
        },
        story:
          "Sophisticated damages expert. Opinions 1-2 excluded post-Daubert - creates impeachment material that Dudney will use on cross of admitted Opinions 3-6. Methodology is technical and dry - moderate jury likability.",
        prediction: {
          direct:
            "Walk through Opinions 3-6 in admitted form. Establish that donations remain traceable to current OpenAI assets and Microsoft equity. Pre-empt FIFO question by acknowledging methodological choice and defending it.",
          cross:
            "Dudney attacks: Opinions 1-2 exclusion creates record of unreliable opinions; FIFO is orthodox accounting standard; Wazzan's tracing departs from norm; donations were spent on early R&D long before current assets accumulated. Dudney frames admitted opinions as 'methodologically suspect' even though admitted.",
          redirect:
            "Re-establish that Opinions 3-6 stand independently. FIFO not the only acceptable methodology. California probate code provides alternative tracing frameworks for charitable trust contexts.",
        },
        elements: [
          {
            theoryId: "trust",
            elemIdx: 2,
            status: "amber",
            note: "Trust res identification - weakened by Opinions 1-2 exclusion",
          },
          {
            theoryId: "unjust",
            elemIdx: 1,
            status: "amber",
            note: "Retention analysis admitted but contested",
          },
        ],
        depo: {
          status: "complete",
          date: "Jan 18, 2026",
          topics: [
            "Tracing methodology",
            "Charitable trust accounting",
            "Microsoft equity attribution",
            "FIFO critique",
          ],
          notTopics: [
            "Specific transaction-level reconstruction beyond aggregate tracing",
          ],
          admissions: [
            {
              cite: "Dep. p.187:4-12",
              text: "FIFO is one orthodox approach but not the only acceptable methodology",
              impact: "amber",
            },
          ],
          gaps: [],
          actions: [
            "Coordinate with Schizer on overlapping testimony framework",
          ],
        },
        relations: [
          {
            with: "dudney",
            type: "opposing-expert",
            note: "Wazzan's Opinions 1-2 exclusion is Dudney's primary attack vector",
          },
        ],
        logistics: {
          availability: "Confirmed. Will testify Day 4-5 trial schedule.",
          location: "Los Angeles, CA",
          flags: [],
        },
        prep: {
          sessions: 4,
          sessionDates: ["Feb 2026 - Apr 2026"],
          covered: [
            "Admitted opinions framing",
            "FIFO alternative defense",
            "Cross prep for excluded-opinions attack",
          ],
          uncovered: [
            "Real-time response to Dudney's framing",
            "Demonstrative slides for jury",
          ],
        },
        expert: {
          daubert: {
            status: "partial - Opinions 1-2 excluded, Opinions 3-6 admitted",
            rulings: [
              {
                date: "Dec 8, 2025",
                ruling:
                  "PTO No. 2 (Dkt.452): Opinions 1-2 excluded as unreliable methodology departing from accepted accounting practice. Opinions 3-6 admitted as sufficiently reliable for jury weighing.",
              },
            ],
          },
          attackSurface: [
            "Excluded Opinions 1-2 used as 'methodologically suspect' impeachment",
            "FIFO as orthodox standard",
            "Wazzan's tracing departs from accepted accounting",
            "Donations spent on early R&D long before current assets accumulated",
            "Opinions 3-6 build on Opinions 1-2's analytic framework - implicit reliance on excluded material",
          ],
          opposing:
            "Dudney (defendant) - FIFO advocate. See Expert Battlefield tab for matchup detail.",
          redirect: [
            "Opinions 3-6 stand independently of excluded opinions",
            "Cal. Probate Code §16003 provides alternative tracing framework",
            "Charitable trust accounting differs from commercial accounting",
            "FIFO is one approach not the universal standard",
          ],
          juryInstr:
            "9th Cir. Model 2.13 - modified for accounting expert weighing",
          priorTestimony: [
            {
              case: "Trust dispute (Cal. Sup. Ct. 2022)",
              topic: "Charitable trust tracing",
              outcome: "Testimony admitted; $14M recovery",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "brockman",
        name: "Greg Brockman",
        role: "OpenAI President · Co-Founder",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 55,
          consistency: 52,
          exposure: 78,
          authority: 80,
          advocacy: 60,
        },
        story:
          "Author of PX163 ('wrong to steal the non-profit from Elon'). Highest-stakes cross of trial. Will frame journal as personal reflection, not institutional policy.",
        prediction: {
          direct:
            "Establish role as President, board governance understanding, current view on fiduciary obligations. Address PX163 first - characterize as personal reflection in moment of uncertainty, not institutional policy. Pre-empt 'wrong to steal' phrase.",
          cross:
            "Plaintiff should NOT lead with PX163 - Brockman has prepared rehearsed answer. Lead with foundation: role, authority, knowledge at LP formation. Establish through contemporaneous documents what board understood obligations to be. PX163 LAST - after foundation makes 'personal reflection' rehabilitation implausible.",
          redirect:
            "OpenAI re-establishes governance framework, distinguishes personal reflection from institutional commitments, addresses Microsoft investment as consistent with original mission interpretation.",
        },
        elements: [
          {
            theoryId: "trust",
            elemIdx: 0,
            status: "red",
            note: "PX163 corroborates Musk's trust-formation account",
          },
          {
            theoryId: "laches",
            elemIdx: 1,
            status: "amber",
            note: "Will testify timeline of for-profit communications",
          },
        ],
        depo: {
          status: "complete",
          date: "Multiple 2025 sessions",
          topics: [
            "President role",
            "Board governance",
            "LP formation",
            "PX163 context",
            "Microsoft relationship",
          ],
          notTopics: [
            "Specific 2015-2017 communications with Musk beyond what's in record",
          ],
          admissions: [
            {
              cite: "Dep. Vol. II p.143",
              text: "Confirmed PX163 handwriting and contemporaneous date",
              impact: "red",
            },
            {
              cite: "Dep. Vol. II p.158",
              text: "Characterizes journal entry as 'expressing frustration' - not the predicted 'personal reflection' framing",
              impact: "red",
              update: "Day 3 trial deviation noted",
            },
          ],
          gaps: [],
          actions: [
            "Build cross sequence around 'frustration' admission - different from prepared 'personal reflection'",
          ],
        },
        relations: [
          {
            with: "musk",
            type: "corroborates",
            note: "PX163 corroborates Musk's account",
          },
          {
            with: "altman",
            type: "corroborates",
            note: "Co-defendant - board governance defense",
          },
        ],
        logistics: {
          availability: "On deck - trial Day 3-4",
          location: "San Francisco, CA",
          flags: [],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: ["Defense prep - outside our control"],
        },
      },
      {
        id: "dudney",
        name: "Robert Dudney",
        role: "Defendant Damages Expert · FIFO Tracing",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 62,
          consistency: 78,
          exposure: 32,
          authority: 82,
          advocacy: 55,
        },
        story:
          "FIFO advocate. Will frame admitted methodology as 'one of several legitimate approaches.' Goal: introduce reasonable doubt about Wazzan's tracing.",
        prediction: {
          direct:
            "Walk through FIFO as orthodox tracing methodology. Apply to OpenAI's transaction history. Conclude that Musk donations were spent on early R&D activities long before current assets accumulated. Reference Wazzan's excluded Opinions 1-2 as evidence of methodological unreliability.",
          cross:
            "Plaintiff attacks: FIFO is not universally accepted in charitable trust contexts; Wazzan's Opinions 3-6 admitted means methodology survives Daubert; Dudney's analysis assumes the conclusion (no traceable assets); LIFO and pro-rata are legitimate alternatives.",
          redirect:
            "Defense re-establishes FIFO as default accounting principle, characterizes admitted Wazzan opinions as 'one methodology of several' to introduce alternatives.",
        },
        elements: [
          {
            theoryId: "trust",
            elemIdx: 2,
            status: "amber",
            note: "Defendant strongest expert on tracing element",
          },
          {
            theoryId: "unjust",
            elemIdx: 1,
            status: "red",
            note: "FIFO undermines retention analysis",
          },
        ],
        depo: {
          status: "complete",
          date: "Feb 4, 2026",
          topics: [
            "FIFO methodology",
            "Charitable trust accounting principles",
            "OpenAI financial history",
            "Wazzan critique",
          ],
          notTopics: [
            "Specific alternative methodologies (LIFO, pro-rata) Wazzan might invoke",
          ],
          admissions: [
            {
              cite: "Dep. p.156:14-22",
              text: "FIFO is 'one of several' methods - opens door to plaintiff's framing",
              impact: "green",
              note: "Plaintiff captures: FIFO is NOT universal",
            },
          ],
          gaps: [],
          actions: [
            "Develop LIFO/pro-rata cross that uses Dudney's 'one of several' admission",
          ],
        },
        relations: [
          {
            with: "wazzan",
            type: "opposing-expert",
            note: "Direct methodology contest - see Expert Battlefield",
          },
        ],
        logistics: {
          availability: "Confirmed. Trial Day 4-5.",
          location: "New York, NY",
          flags: [],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: ["Defense prep - outside our control"],
        },
        expert: {
          daubert: {
            status: "all opinions admitted",
            rulings: [
              {
                date: "Dec 8, 2025",
                ruling:
                  "PTO No. 2 (Dkt.452): Dudney FIFO methodology admitted as orthodox accounting practice.",
              },
            ],
          },
          attackSurface: [
            "FIFO is not universal - Dudney admitted 'one of several methods'",
            "Charitable trust accounting differs from commercial accounting",
            "Conclusion-driven analysis - assumes the result",
            "LIFO and pro-rata are legitimate alternatives plaintiff can advance",
          ],
          opposing:
            "Wazzan (plaintiff) - Opinions 3-6 admitted. Wazzan's tracing methodology survives Daubert.",
          redirect: [
            "FIFO is the default accounting principle worldwide",
            "Wazzan's exclusion of Opinions 1-2 demonstrates methodological problems",
            "Charitable trust accounting in this context is unsettled",
          ],
          juryInstr:
            "9th Cir. Model 2.13 - modified for accounting expert weighing",
          priorTestimony: [
            {
              case: "Bankruptcy adversary proceeding (S.D.N.Y. 2023)",
              topic: "FIFO tracing in fraudulent conveyance",
              outcome: "Testimony admitted",
            },
          ],
        },
      },
    ],
  },
  neuro: {
    plaintiff: [
      {
        id: "liu",
        name: "Dr. Guosong Liu",
        role: "Neurocentria Founder · Named Inventor",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 60,
          consistency: 42,
          exposure: 88,
          authority: 92,
          advocacy: 88,
        },
        story:
          "Named plaintiff and inventor. EXTREME advocacy risk + EXTREME prior-statement exposure (p.318:25 'we did not invent the molecule'). Most dangerous combination - visibly partisan witness who has contradicted his own case.",
        prediction: {
          direct:
            "Establish inventorship contribution, '061 patent prosecution context, MIT provisional development. Avoid 'we did not invent' phrasing. Frame inventive contribution as method/dosage optimization rather than novel composition.",
          cross:
            "Defendant leads with p.318:25 within first five questions. Then p.317:2-3 (no new synthesis method). Then p.203:14 (general awareness of magnesium research). Builds inventorship-defect theory through admissions. Likely raises GRAS Notice 499 vs ImportGenius China-origin contradiction.",
          redirect:
            "Re-frame 'did not invent the molecule' as 'did not invent in the abstract - but contributed inventive method/dosage.' Distinguish composition-of-matter vs method claims.",
        },
        elements: [
          {
            theoryId: "direct",
            elemIdx: 0,
            status: "amber",
            note: "Inventorship support but admission creates risk",
          },
          {
            theoryId: "obvious",
            elemIdx: 3,
            status: "red",
            note: "His admission helps defendant's obviousness defense",
          },
        ],
        depo: {
          status: "complete",
          date: "Feb 12, 2026",
          topics: [
            "Inventorship timeline",
            "MIT provisional development",
            "Magceutics/ThreoTech formation",
            "AIDP license terms",
            "Product formulation",
          ],
          notTopics: [
            "Specific awareness of CN1200366A before filing",
            "FAC ¶8 vs ¶13 contradiction on inventive contribution",
            "GRAS Notice 499 representations vs actual China sourcing",
          ],
          admissions: [
            {
              cite: "p.318:25",
              text: '"We did not invent the molecule" - central admission for obviousness defense',
              impact: "red",
              linkedToImpBank: true,
            },
            {
              cite: "p.317:2-3",
              text: "Confirmed no new synthesis method patented",
              impact: "red",
            },
            {
              cite: "p.203:14",
              text: "General awareness of magnesium compound prior research",
              impact: "amber",
            },
          ],
          gaps: [
            "CN1200366A specific awareness - critical",
            "FAC ¶8 vs ¶13 contradiction unexplored",
            "GRAS Notice 499 manufacturing-origin representations",
            "Detailed AIDP license scope discussion",
          ],
          actions: [
            "Issue supplemental deposition notice on CN1200366A awareness AND GRAS filing representations before September 11 fact discovery cutoff. Two items added to Countdown Checklist.",
          ],
        },
        relations: [
          {
            with: "mao",
            type: "contradicts",
            note: "Liu says 'we did not invent the molecule' but FAC attributes inventive contribution to both - internal complaint contradiction",
          },
          {
            with: "slutsky",
            type: "exposes",
            note: "Inventorship defect theory ties to Slutsky's pending deposition",
          },
        ],
        logistics: {
          availability: "Confirmed for trial",
          location: "San Francisco, CA",
          flags: [],
        },
        prep: {
          sessions: 2,
          sessionDates: ["Mar 14, 2026", "Apr 8, 2026"],
          covered: ["Direct outline", "Cross prep for p.318:25"],
          uncovered: [
            "FAC ¶8 vs ¶13 contradiction handling",
            "CN1200366A awareness questions",
            "GRAS filing rehabilitation",
          ],
        },
      },
      {
        id: "slutsky",
        name: "Dr. Inna Slutsky",
        role: "Tel Aviv University · Disputed Inventor",
        type: "fact",
        photo: "⚠️",
        cred: {
          likability: 0,
          consistency: 0,
          exposure: 0,
          authority: 0,
          advocacy: 0,
        },
        story:
          "NOT YET DEPOSED. International witness. Critical to inventorship defect theory. Hague Convention required. Initiation deadline: TODAY (May 5, 2026). 134 days to discovery cutoff.",
        prediction: {
          direct: "N/A - not yet deposed",
          cross: "N/A - not yet deposed",
          redirect: "N/A - not yet deposed",
        },
        elements: [
          {
            theoryId: "obvious",
            elemIdx: 0,
            status: "gray",
            note: "Pending - needed for inventorship defect",
          },
          { theoryId: "direct", elemIdx: 0, status: "gray", note: "Pending" },
        ],
        depo: {
          status: "NOT YET TAKEN - CRITICAL",
          date: "-",
          topics: [],
          notTopics: [
            "INVENTORSHIP - never deposed",
            "Fluorescent probe methodology - never deposed",
            "2003 MIT provisional contribution - never deposed",
          ],
          admissions: [],
          gaps: [
            "EVERYTHING. Slutsky has never been deposed. Inventorship defect theory is asserted but not yet established in record.",
          ],
          actions: [
            "🔴 INITIATE HAGUE CONVENTION PROCESS TODAY. 90-120 day lead time. Discovery cutoff: Sep 11, 2026. 134 days available - sufficient ONLY if process initiated immediately.",
          ],
        },
        relations: [
          {
            with: "liu",
            type: "exposed-by",
            note: "Liu admission ties to Slutsky inventorship contribution",
          },
        ],
        logistics: {
          availability:
            "⚠️ Tel Aviv, Israel. Hague Convention required. NOT YET CONTACTED.",
          location: "Tel Aviv, Israel",
          flags: [
            "hague-required",
            "initiation-deadline-today",
            "134-days-to-cutoff",
          ],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: [
            "No contact established. Identify Israeli counsel. Draft deposition topics. Initiate Hague process.",
          ],
        },
      },
      {
        id: "baker",
        name: "Prof. Donald Baker, PhD",
        role: "Plaintiff Claim Construction Expert",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 68,
          consistency: 78,
          exposure: 35,
          authority: 92,
          advocacy: 48,
        },
        story:
          "Plaintiff's claim construction expert. Will defend broad construction of '061 claim 1.",
        prediction: {
          direct:
            "Establish '061 claim 1 construction. Walk through specification support for broad construction. Address prosecution history without surrendering scope.",
          cross:
            "Defendant attacks: prosecution history disclaimer; specification embodiments support narrow construction; Festo presumption.",
          redirect:
            "Specification supports broad construction. August 2018 amendment narrowed only specific terms not at issue here.",
        },
        elements: [
          {
            theoryId: "direct",
            elemIdx: 1,
            status: "green",
            note: "Markman support for broad construction",
          },
        ],
        depo: {
          status: "complete",
          date: "Feb 28, 2026",
          topics: [
            "Claim construction methodology",
            "Specification analysis",
            "Prosecution history review",
          ],
          notTopics: ["Detailed Festo presumption analysis"],
          admissions: [],
          gaps: [],
          actions: [],
        },
        relations: [
          {
            with: "adams",
            type: "opposing-expert",
            note: "Direct Markman matchup",
          },
        ],
        logistics: {
          availability: "Confirmed. Markman hearing pending.",
          location: "Boston, MA",
          flags: [],
        },
        prep: {
          sessions: 2,
          sessionDates: ["Mar 5, 2026", "Apr 2, 2026"],
          covered: ["Markman direct", "Cross prep on prosecution history"],
          uncovered: ["Real-time response to Adams' framing"],
        },
        expert: {
          daubert: { status: "not yet challenged", rulings: [] },
          attackSurface: [
            "Prosecution-history disclaimer interpretation",
            "Festo presumption analysis",
            "Specification embodiment scope",
          ],
          opposing:
            "Adams (defendant) - narrow-construction advocate. See Expert Battlefield tab.",
          redirect: [
            "Specification supports broad construction",
            "Aug 2018 amendment narrowed only terms not at issue",
            "Phillips v. AWH framework supports broad construction",
          ],
          juryInstr:
            "AIPLA Model - Claim Construction (court instruction, not jury)",
          priorTestimony: [
            {
              case: "Pharma patent case (D. Del. 2023)",
              topic: "Claim construction",
              outcome: "Testimony admitted; broad construction adopted",
            },
          ],
        },
      },
    ],
    defendant: [
      {
        id: "adams",
        name: "Prof. Anne Adams, PhD",
        role: "Defendant Claim Construction Expert",
        type: "expert",
        photo: "🎓",
        cred: {
          likability: 65,
          consistency: 75,
          exposure: 30,
          authority: 88,
          advocacy: 50,
        },
        story:
          "Defendant's claim construction expert. Narrow construction advocate.",
        prediction: {
          direct:
            "Establish narrow construction of '061 claim 1 based on prosecution history. Walk through August 2018 amendment as critical surrendered scope.",
          cross:
            "Plaintiff attacks: specification supports broader construction; August 2018 amendment did not surrender claimed scope; Festo rebuttable presumption.",
          redirect:
            "Re-establish narrow construction; Festo presumption stands.",
        },
        elements: [
          {
            theoryId: "obvious",
            elemIdx: 0,
            status: "green",
            note: "Supports invalidity defense",
          },
          {
            theoryId: "hist",
            elemIdx: 0,
            status: "green",
            note: "Prosecution history estoppel expert",
          },
        ],
        depo: {
          status: "complete",
          date: "Feb 21, 2026",
          topics: [
            "Claim construction methodology",
            "Prosecution history analysis",
            "Festo presumption",
          ],
          notTopics: [],
          admissions: [],
          gaps: [],
          actions: [],
        },
        relations: [
          {
            with: "baker",
            type: "opposing-expert",
            note: "Direct Markman matchup",
          },
        ],
        logistics: {
          availability: "Confirmed",
          location: "Berkeley, CA",
          flags: [],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: ["Defense prep - outside our control"],
        },
        expert: {
          daubert: { status: "not yet challenged", rulings: [] },
          attackSurface: [
            "Specification supports broader construction",
            "August 2018 amendment scope interpretation",
            "Festo rebuttable presumption",
          ],
          opposing: "Baker (plaintiff) - broad-construction advocate.",
          redirect: [],
          juryInstr: "AIPLA Model - Claim Construction (court instruction)",
          priorTestimony: [],
        },
      },
      {
        id: "alfaro",
        name: "Mando 30(b)(6) - Alfaro",
        role: "Mando Pharm. Inc. · Corporate Designee",
        type: "fact",
        photo: "👤",
        cred: {
          likability: 50,
          consistency: 55,
          exposure: 62,
          authority: 72,
          advocacy: 75,
        },
        story:
          "Mando corporate representative. Will testify on company operations, denying California operations. CRITICAL contradiction with ImportGenius trade data + Certificates of Analysis showing Shenzhen China sourcing.",
        prediction: {
          direct:
            "Establish Mando operations. Frame as US-based company with limited California presence. Avoid manufacturing-origin specifics.",
          cross:
            "Plaintiff confronts with: Certificates of Analysis showing Shenzhen China sourcing, ImportGenius trade data showing Jordan-routed shipments (potential triangulation), GRAS Notice 499 'Manufactured in USA' representations. Builds country-of-origin fraud counterclaim.",
          redirect:
            "Mando re-frames country-of-origin as separate issue from patent claims; explains Jordan shipments as legitimate routing; addresses GRAS filing as regulatory compliance.",
        },
        elements: [
          {
            theoryId: "licensee",
            elemIdx: 0,
            status: "green",
            note: "TMLA scope foundation",
          },
          {
            theoryId: "obvious",
            elemIdx: 0,
            status: "amber",
            note: "Limited inventorship knowledge",
          },
        ],
        depo: {
          status: "complete",
          date: "Feb 26, 2026",
          topics: [
            "Mando corporate operations",
            "TMLA scope",
            "Royalty payment history",
          ],
          notTopics: [
            "Specific manufacturing origin details",
            "ImportGenius data context",
            "GRAS Notice 499 representations specifics",
            "Jordan shipping rationale",
          ],
          admissions: [
            {
              cite: "Dep. p.143:8-15",
              text: "Limited personal knowledge of manufacturing-origin documentation",
              impact: "amber",
            },
          ],
          gaps: [
            "Manufacturing origin contradiction with COA + ImportGenius",
            "GRAS filing manufacturing representations",
          ],
          actions: [
            "Issue ImportGenius custodian-of-records subpoena before September 11 cutoff. Set deposition date for ImportGenius custodian.",
          ],
        },
        relations: [
          {
            with: "liu",
            type: "contradicts",
            note: "Mando's framing contradicts ImportGenius/COA documents",
          },
        ],
        logistics: {
          availability: "Confirmed",
          location: "Los Angeles, CA",
          flags: [],
        },
        prep: {
          sessions: 0,
          sessionDates: [],
          covered: [],
          uncovered: ["Defense prep - outside our control"],
        },
      },
    ],
  },
};

const ASSETS_INTEL = {
  ...ILE_K_AI,
  juul: {
    hot: [
      "pak-depo",
      "halpern-felsher",
      "dorn-report",
      "jli-mkt-docs",
      "fda-warnings",
      "altria-investment",
    ],
    docs: [
      {
        id: "pak-depo",
        type: "depo",
        title: "Pak Deposition Transcript (30(b)(6))",
        classification: "pin",
        producer: "SFUSD",
        bates: "SFUSD-DEP-001 to 287",
        date: "Dec 21, 2021",
        confidentiality: "Public",
        pages: 287,
        exhibit: "DX-047",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Self-authenticating (deposition transcript)",
          witness: "N/A - already authenticated",
          objections: ["None - both sides want this in evidence"],
          status: "green",
          foundation: "FRE 901(b)(8)",
        },
        theoryMaps: [
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Damages",
            elemIdx: 3,
            impact: "red",
            explainer: "Central damages problem",
          },
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Created condition",
            elemIdx: 0,
            impact: "amber",
          },
        ],
        ai: {
          summary:
            "30(b)(6) deposition of SFUSD's corporate representative on financial damages. The critical passage is at p.47:12-18 where Pak testifies SFUSD 'doesn't have that information' regarding costs attributable to JUUL products. This is the most damaging document in the Assets tab for plaintiff's damages case.",
          keyPassages: [
            {
              cite: "p.47:12-18",
              text: "Damages admission - 'we don't have that information'",
              impact: "red",
            },
            {
              cite: "p.23:4-9",
              text: "30(b)(6) designation scope on financial damages topic",
              impact: "neutral",
            },
            {
              cite: "p.81:15-22",
              text: "Site inspection process - foundation for Dorn report",
              impact: "amber",
            },
          ],
          contradictions: [
            "Dorn expert report asserts $78.8M prospective abatement costs. Pak's admission that SFUSD cannot quantify past costs creates tension - Dorn's prospective estimate is only as credible as the proposition that real harm occurred, and Pak's inability to document past harm undermines the foundation for prospective estimation.",
          ],
          questions: [
            "Was Pak the appropriate 30(b)(6) designee for this topic, or was there a more knowledgeable SFUSD employee who should have been designated?",
            "Was the scope of the 30(b)(6) notice broad enough to cover the $78.8M Dorn estimate, or does the deposition predate Dorn's report?",
          ],
          witness: "Quarry Pak",
        },
      },
      {
        id: "halpern-felsher",
        type: "exhibit",
        title: "Halpern-Felsher Sept 2021 Report",
        classification: "pin",
        producer: "Plaintiffs",
        bates: "PLT-EXP-022 to 089",
        date: "Sep 14, 2021",
        confidentiality: "Public",
        pages: 67,
        exhibit: "PX-022",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Expert authentication via testimony",
          witness: "Halpern-Felsher",
          objections: ["JLI may attack methodology on cross"],
          status: "green",
          foundation: "FRE 702/703",
        },
        theoryMaps: [
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Created condition",
            elemIdx: 0,
            impact: "green",
          },
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Public nuisance",
            elemIdx: 1,
            impact: "green",
          },
          {
            theoryId: "nm",
            theory: "Negligent Marketing",
            element: "Breach",
            elemIdx: 1,
            impact: "green",
          },
          {
            theoryId: "nm",
            theory: "Negligent Marketing",
            element: "Causation",
            elemIdx: 2,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "Stanford epidemiology study establishing 75% of SFUSD students aged 15-24 who recognized a JUUL did not know it always contained nicotine. E-cigarette use by SFUSD high school students more than doubled from 7.1% to 16% (2017-2019). Central exhibit on awareness gap and youth-targeting causation chain.",
          keyPassages: [
            {
              cite: "p.64-65",
              text: "75% awareness gap finding",
              impact: "green",
            },
            { cite: "p.42-48", text: "Survey methodology", impact: "green" },
            {
              cite: "p.18-22",
              text: "Sample selection - SFUSD-specific",
              impact: "green",
            },
          ],
          contradictions: [
            "Contradicts JLI 30(b)(6) framing of marketing as adult-cessation focused.",
          ],
          questions: [
            "Is methodology generalizable from SFUSD subset to district-wide harm?",
            "Plaintiff-specific causation for Seliavski cohort - addressed in supplemental opinion?",
          ],
          witness: "Bonnie Halpern-Felsher, PhD",
        },
      },
      {
        id: "dorn-report",
        type: "exhibit",
        title: "Dorn Abatement Expert Report",
        classification: "pin",
        producer: "Plaintiffs",
        bates: "PLT-EXP-201 to 414",
        date: "Aug 2022",
        confidentiality: "Public",
        pages: 213,
        exhibit: "PX-201",
        exhibitStatus: "stipulated",
        cluster: "site-inspections",
        auth: {
          method: "Expert authentication via testimony",
          witness: "Dorn",
          objections: [
            "Sonner-bar challenge on prospective costs",
            "Methodology challenge on 6→district extrapolation",
          ],
          status: "amber",
          foundation: "FRE 702/703",
        },
        theoryMaps: [
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Damages",
            elemIdx: 3,
            impact: "amber",
            explainer: "Best damages support but methodology under attack",
          },
        ],
        ai: {
          summary:
            "Six-school site inspection abatement methodology with $78.8M district-wide estimate. Survives Daubert (Dkt.3920). Methodology depends on extrapolation defensibility and Pak admission rehabilitation.",
          keyPassages: [
            {
              cite: "p.34-67",
              text: "Six-school site inspection findings",
              impact: "green",
            },
            {
              cite: "p.142-178",
              text: "Extrapolation framework",
              impact: "amber",
            },
            {
              cite: "p.198-213",
              text: "$78.8M district-wide estimate",
              impact: "amber",
            },
          ],
          contradictions: [
            "Pak deposition admission contradicts the proposition that real harm occurred - undermines Dorn's foundation.",
          ],
          questions: [
            "Comparable-case analysis for extrapolation defensibility?",
            "Sonner-bar rehabilitation arc?",
          ],
          witness: "Daniel Dorn, MS, PE",
        },
      },
      {
        id: "jli-mkt-docs",
        type: "exhibit",
        title: "JLI Internal Marketing Documents (cluster)",
        classification: "pin",
        producer: "JLI",
        bates: "JLI-MKT-001 to 047 (47 items)",
        date: "2014-2018",
        confidentiality: "Confidential→Public (Dkt.4385)",
        pages: 892,
        exhibit: "PX-044 to PX-090",
        exhibitStatus: "contested",
        cluster: "jli-marketing",
        clusterCount: 47,
        auth: {
          method: "Authenticated via JLI 30(b)(6)",
          witness: "JLI 30(b)(6)",
          objections: [
            "Hearsay (resolved via opposing-party admission)",
            "Trade secret (resolved via sealing order)",
          ],
          status: "green",
          foundation: "FRE 801(d)(2)",
        },
        theoryMaps: [
          {
            theoryId: "nm",
            theory: "Negligent Marketing",
            element: "Breach",
            elemIdx: 1,
            impact: "green",
          },
          {
            theoryId: "rico",
            theory: "RICO Enterprise",
            element: "Pattern of racketeering",
            elemIdx: 1,
            impact: "amber",
          },
        ],
        ai: {
          summary:
            "47 internal JLI marketing documents (PX-44 to PX-90 group) showing awareness of youth demographics, deliberate flavor design (mango, crème brûlée), USB-drive form factor strategy, and social-media campaigns. Originally produced under Confidential designation; unsealed February 14, 2025 (Dkt.4385). Linchpin documents for negligent marketing theory.",
          keyPassages: [
            {
              cite: "PX-44 (Aug 2015 strategy memo)",
              text: "Form factor designed for 'discretion'",
              impact: "green",
            },
            {
              cite: "PX-67 (Mar 2016 board deck)",
              text: "Youth demographics analysis",
              impact: "green",
            },
            {
              cite: "PX-89 (Sep 2018 Altria-context)",
              text: "Altria $12.8B investment context",
              impact: "green",
            },
          ],
          contradictions: [
            "Contradicts JLI 30(b)(6) framing of marketing as adult-cessation focused.",
          ],
          questions: [
            "Are there additional unsealed documents beyond Dkt.4385 still in pipeline?",
          ],
          witness: "JLI 30(b)(6)",
        },
      },
      {
        id: "fda-warnings",
        type: "filing",
        title: "FDA Warning Letters (cluster)",
        classification: "foundation",
        producer: "FDA · public records",
        bates: "PUB-FDA-001 to 014",
        date: "Sep 2018 - Apr 2019",
        confidentiality: "Public",
        pages: 142,
        exhibit: "PX-114 to PX-127",
        exhibitStatus: "stipulated",
        cluster: "fda-warnings",
        clusterCount: 14,
        auth: {
          method: "Self-authenticating (FRE 902(5) public records)",
          witness: "N/A",
          objections: ["None"],
          status: "green",
          foundation: "FRE 902(5)",
        },
        theoryMaps: [
          {
            theoryId: "sol",
            theory: "Statute of Limitations (defense)",
            element: "Discovery rule trigger",
            elemIdx: 0,
            impact: "green",
            explainer: "Defense uses for SOL trigger date",
          },
          {
            theoryId: "nm",
            theory: "Negligent Marketing",
            element: "Breach",
            elemIdx: 1,
            impact: "green",
            explainer: "Plaintiff uses for continued conduct after notice",
          },
        ],
        ai: {
          summary:
            "14 FDA warning letters issued September 2018 - April 2019 to JLI and related entities. Foundation documents for both plaintiff (continued misconduct after notice) and defendant (constructive notice for SOL purposes). Dual-role classification.",
          keyPassages: [
            {
              cite: "PX-114 (Sep 2018)",
              text: "Initial FDA warning",
              impact: "green",
            },
          ],
          contradictions: [],
          questions: [],
          witness: "N/A - public records",
        },
      },
      {
        id: "altria-investment",
        type: "filing",
        title: "Altria $12.8B Equity Investment",
        classification: "foundation",
        producer: "Altria · SEC filings",
        bates: "PUB-ALT-001 to 022",
        date: "Dec 2018",
        confidentiality: "Public",
        pages: 122,
        exhibit: "PX-089",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Self-authenticating (FRE 902(5) public records)",
          witness: "N/A",
          objections: ["None"],
          status: "green",
          foundation: "FRE 902(5)",
        },
        theoryMaps: [
          {
            theoryId: "rico",
            theory: "RICO Enterprise",
            element: "Existence of enterprise",
            elemIdx: 0,
            impact: "amber",
          },
        ],
        ai: {
          summary:
            "Altria $12.8B equity investment establishes RICO enterprise context.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "N/A",
        },
      },
      {
        id: "site-inspections",
        type: "exhibit",
        title: "Six-School Site Inspection Reports",
        classification: "expert-support",
        producer: "Plaintiffs · Dorn",
        bates: "PLT-INSP-001 to 098",
        date: "2022",
        confidentiality: "Public",
        pages: 98,
        exhibit: "PX-202 to PX-207",
        exhibitStatus: "stipulated",
        cluster: "site-inspections",
        clusterCount: 6,
        auth: {
          method: "Expert support - not independently admissible",
          witness: "Dorn (foundational)",
          objections: [
            "May be subject to motion to strike if Dorn's expert opinion is excluded",
          ],
          status: "amber",
          foundation: "FRE 703",
        },
        theoryMaps: [
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Created condition",
            elemIdx: 0,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "Six site inspection reports - Balboa, McAteer, Aptos, Denman, Presidio, Downtown - documenting physical damage. Foundational predicate for Dorn $78.8M estimate.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "Dorn",
        },
      },
      {
        id: "pak-imp-bank",
        type: "depo",
        title: "Pak Deposition Impeachment Entry",
        classification: "impeachment",
        producer: "SFUSD",
        bates: "SFUSD-DEP-047",
        date: "Dec 21, 2021",
        confidentiality: "Public",
        pages: 1,
        exhibit: "DX-047 (dual role)",
        exhibitStatus: "stipulated",
        cluster: "pak-depo",
        auth: {
          method: "Already authenticated as part of full transcript",
          witness: "N/A",
          objections: [],
          status: "green",
          foundation: "FRE 613",
        },
        theoryMaps: [
          {
            theoryId: "pn",
            theory: "Public Nuisance (SFUSD)",
            element: "Damages",
            elemIdx: 3,
            impact: "red",
          },
        ],
        ai: {
          summary:
            "Single page p.47:12-18 - the key admission. Indexed separately as impeachment material since cross-exam will use it standalone.",
          keyPassages: [
            { cite: "p.47:12-18", text: "Damages admission", impact: "red" },
          ],
          contradictions: [],
          questions: [],
          witness: "Pak",
        },
      },
    ],
  },
  musk: {
    hot: [
      "px163",
      "px230",
      "dx516",
      "wazzan-report",
      "schizer-report",
      "founding-emails",
    ],
    docs: [
      {
        id: "px163",
        type: "exhibit",
        title: "PX163 - Brockman Journal Entry",
        classification: "pin",
        producer: "Defendants · doc production",
        bates: "OAI-BROCK-2017-3",
        date: "2017",
        confidentiality: "Confidential",
        pages: 1,
        exhibit: "PX-163",
        exhibitStatus: "contested",
        cluster: null,
        auth: {
          method: "Brockman trial testimony",
          witness: "Greg Brockman",
          objections: [
            "Hearsay objection (defense)",
            "Plaintiff's response: not for truth - contemporaneous belief; alternatively FRE 801(d)(2) opposing-party admission",
          ],
          status: "amber",
          foundation: "FRE 801(d)(2)(A) or 803 contemporaneous-belief",
        },
        theoryMaps: [
          {
            theoryId: "trust",
            theory: "Charitable Trust Creation",
            element: "Donor intent",
            elemIdx: 0,
            impact: "green",
            explainer:
              "Strongest contemporaneous corroboration of trust formation",
          },
        ],
        ai: {
          summary:
            "Single-page journal entry by Greg Brockman. Contains the phrase 'wrong to steal the non-profit from Elon.' Single most important contemporaneous document in the case for plaintiff. Hearsay objection pending - pocket brief in Hearing Prep Pack addresses.",
          keyPassages: [
            {
              cite: "PX163 line 4",
              text: "'wrong to steal the non-profit from Elon'",
              impact: "green",
            },
          ],
          contradictions: [
            "Contradicts defense framing of Brockman's later 'personal reflection' rehabilitation.",
          ],
          questions: [
            "What was the precipitating event for the journal entry?",
            "Who else at OpenAI knew about the entry contemporaneously?",
          ],
          witness: "Greg Brockman",
        },
      },
      {
        id: "px230",
        type: "exhibit",
        title: "PX230 - Kevin Scott to Nadella Email",
        classification: "pin",
        producer: "Microsoft · subpoena",
        bates: "MSFT-INT-2018-09-12",
        date: "Sep 12, 2018",
        confidentiality: "Confidential",
        pages: 1,
        exhibit: "PX-230",
        exhibitStatus: "contested",
        cluster: null,
        auth: {
          method: "Authenticated via Microsoft 30(b)(6)",
          witness: "Microsoft 30(b)(6) or Scott direct",
          objections: [
            "Hearsay (defense)",
            "Plaintiff's response: opposing-party admission via Microsoft as aiding-and-abetting defendant",
          ],
          status: "amber",
          foundation: "FRE 801(d)(2)",
        },
        theoryMaps: [
          {
            theoryId: "trust",
            theory: "Aiding & Abetting (Count XIX)",
            element: "Microsoft knowledge",
            elemIdx: 0,
            impact: "green",
            explainer:
              "Establishes Microsoft's actual knowledge of donor expectations independent of trust formation",
          },
        ],
        ai: {
          summary:
            "Email from Kevin Scott (Microsoft CTO) to Satya Nadella reading 'wonder if donors are aware.' Establishes Microsoft's actual knowledge of donor expectations independent of trust-formation question. Critical for aiding-and-abetting count.",
          keyPassages: [
            {
              cite: "PX230",
              text: "'wonder if donors are aware'",
              impact: "green",
            },
          ],
          contradictions: [
            "Contradicts defense argument that Microsoft was unaware of donor expectations.",
          ],
          questions: [
            "Who else at Microsoft saw this email contemporaneously?",
            "Was there a follow-up communication?",
          ],
          witness: "Kevin Scott or Microsoft 30(b)(6)",
        },
      },
      {
        id: "dx516",
        type: "exhibit",
        title: "DX-516 - Musk 'Yup' Reply Email",
        classification: "pin",
        producer: "Plaintiff · doc production",
        bates: "MUSK-EM-2018-06",
        date: "Jun 2018",
        confidentiality: "Public",
        pages: 1,
        exhibit: "DX-516",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Self-authenticating + Musk authenticated at PI",
          witness: "Musk (during cross)",
          objections: ["None - already authenticated"],
          status: "green",
          foundation: "FRE 902",
        },
        theoryMaps: [
          {
            theoryId: "acquiesce",
            theory: "Released by Acquiescence (defense)",
            element: "Express/implied consent",
            elemIdx: 1,
            impact: "red",
            explainer: "Strongest defense exhibit for acquiescence",
          },
          {
            theoryId: "trust",
            theory: "Charitable Trust Creation",
            element: "Donor intent",
            elemIdx: 0,
            impact: "red",
            explainer: "Plaintiff dual-role: undermines trust formation",
          },
        ],
        ai: {
          summary:
            "One-word email reply from Musk: 'Yup' - in response to Altman's email about OpenAI being 'less open' than originally framed. Defense uses as acquiescence evidence. Plaintiff must address as part of trust-formation theory.",
          keyPassages: [
            {
              cite: "DX-516",
              text: "'Yup' (in reply to Altman's 'less open' framing)",
              impact: "red",
            },
          ],
          contradictions: ["Tension with Musk's later trust-creation theory."],
          questions: [
            "What was the precise context - was Musk responding to a specific operational decision or to general framing?",
          ],
          witness: "Musk",
        },
      },
      {
        id: "wazzan-report",
        type: "exhibit",
        title: "Wazzan Damages Expert Report",
        classification: "pin",
        producer: "Plaintiff · expert disclosure",
        bates: "PLT-EXP-WAZZ-001 to 184",
        date: "Oct 2025",
        confidentiality: "Confidential",
        pages: 184,
        exhibit: "PX-WAZZ",
        exhibitStatus: "contested",
        cluster: null,
        auth: {
          method: "Expert testimony",
          witness: "Wazzan",
          objections: [
            "Daubert: Opinions 1-2 EXCLUDED; Opinions 3-6 ADMITTED (PTO No. 2, Dkt.452)",
          ],
          status: "amber",
          foundation: "FRE 702/703 - partial",
        },
        theoryMaps: [
          {
            theoryId: "trust",
            theory: "Charitable Trust Creation",
            element: "Trust res identification",
            elemIdx: 2,
            impact: "amber",
          },
          {
            theoryId: "unjust",
            theory: "Unjust Enrichment",
            element: "Inequitable retention",
            elemIdx: 1,
            impact: "amber",
          },
        ],
        ai: {
          summary:
            "Wazzan damages expert report. Opinions 1-2 (FIFO repudiation) excluded; Opinions 3-6 (tracing methodology) admitted. Excluded opinions are impeachment material on cross of admitted opinions.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "Wazzan",
        },
      },
      {
        id: "schizer-report",
        type: "exhibit",
        title: "Schizer Tax Expert Report",
        classification: "foundation",
        producer: "Plaintiff · expert disclosure",
        bates: "PLT-EXP-SCHIZ-001 to 89",
        date: "Oct 2025",
        confidentiality: "Confidential",
        pages: 89,
        exhibit: "PX-SCHIZ",
        exhibitStatus: "contested",
        cluster: null,
        auth: {
          method: "Expert testimony",
          witness: "Schizer",
          objections: [],
          status: "green",
          foundation: "FRE 702",
        },
        theoryMaps: [
          {
            theoryId: "trust",
            theory: "Charitable Trust Creation",
            element: "Charitable purpose",
            elemIdx: 1,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "Schizer tax expert report establishes charitable-purpose foundation through 501(c)(3) and donor-deductibility analysis.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "Schizer",
        },
      },
      {
        id: "founding-emails",
        type: "exhibit",
        title: "Founding Emails 2015-2017 (cluster)",
        classification: "pin",
        producer: "Both · production",
        bates: "OAI-FOUND-001 to 021",
        date: "2015-2017",
        confidentiality: "Public (post-PI)",
        pages: 127,
        exhibit: "PX-001 to PX-021",
        exhibitStatus: "stipulated",
        cluster: "founding-emails",
        clusterCount: 21,
        auth: {
          method: "Trial testimony of Brockman, Altman, Musk",
          witness: "Multiple",
          objections: [],
          status: "green",
          foundation: "FRE 901",
        },
        theoryMaps: [
          {
            theoryId: "trust",
            theory: "Charitable Trust Creation",
            element: "Donor intent",
            elemIdx: 0,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "21 founding emails 2015-2017 establishing charitable representations to Musk. Cluster includes 'spirit of donation' framing, nonprofit mission statements, and contemporaneous reliance evidence.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "Multiple",
        },
      },
    ],
  },
  neuro: {
    hot: [
      "cn1200366a",
      "mit-provisional",
      "liu-depo",
      "gras-499",
      "coa-cluster",
      "importgenius",
    ],
    docs: [
      {
        id: "cn1200366a",
        type: "exhibit",
        title: "CN1200366A - Chinese Patent Application",
        classification: "prior-art",
        producer: "Defendant · prior art disclosure",
        bates: "DEF-PA-CN1997-001 to 042",
        date: "Aug 29, 1997",
        confidentiality: "Public",
        pages: 42,
        exhibit: "DX-PA-CN1997",
        exhibitStatus: "contested",
        cluster: "prior-art",
        auth: {
          method: "Certified translation by court-certified translator",
          witness: "Translator OR technical expert",
          objections: [
            "Translation accuracy - technical terms re dosage range",
          ],
          status: "amber",
          foundation: "FRE 901 (translation certification)",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "§103 Obviousness",
            element: "Prior art compound disclosure",
            elemIdx: 0,
            impact: "green",
            explainer: "Predates '061 by ~6 years",
          },
          {
            theoryId: "obvious",
            theory: "§103 Obviousness",
            element: "Prior art use disclosure",
            elemIdx: 1,
            impact: "green",
          },
          {
            theoryId: "direct",
            theory: "Lost Profits Election",
            element: "Non-infringing alternative",
            elemIdx: 1,
            impact: "red",
            explainer:
              "Element fatal to lost profits - drives reasonable royalty election",
          },
        ],
        ai: {
          summary:
            "Chinese patent application published August 29, 1997, disclosing synthesis and oral administration forms of magnesium L-threonate (MgLT). Predates the '061 patent application by approximately six years. Discloses dosage ranges of 1mg-100g overlapping with the '061 claims.",
          keyPassages: [
            {
              cite: "CN1200366A p.4",
              text: "MgLT synthesis disclosure",
              impact: "green",
            },
            {
              cite: "CN1200366A p.7-8",
              text: "Oral administration forms",
              impact: "green",
            },
            {
              cite: "CN1200366A p.12",
              text: "Dosage range 1mg-100g",
              impact: "green",
            },
          ],
          contradictions: [
            "The '061 patent's specification asserts a novel oral dosage form - CN1200366A discloses the same oral forms, which directly contradicts the novelty assertion.",
          ],
          questions: [
            "Was CN1200366A disclosed to the USPTO during prosecution? Liu's deposition (p.203:14) acknowledged prior awareness of magnesium compound research - was he specifically asked about CN1200366A? If not, a follow-up deposition notice should be issued.",
          ],
          witness: "Translator + technical expert",
        },
      },
      {
        id: "mit-provisional",
        type: "exhibit",
        title: "MIT Provisional 60/510,945",
        classification: "prior-art",
        producer: "Defendant · prior art disclosure",
        bates: "DEF-PA-MIT2003-001 to 028",
        date: "Oct 2003",
        confidentiality: "Public",
        pages: 28,
        exhibit: "DX-PA-MIT2003",
        exhibitStatus: "stipulated",
        cluster: "prior-art",
        auth: {
          method: "Self-authenticating (FRE 902(5) public USPTO record)",
          witness: "N/A",
          objections: [],
          status: "green",
          foundation: "FRE 902(5)",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "§103 Obviousness",
            element: "Prior art use disclosure",
            elemIdx: 1,
            impact: "green",
          },
          {
            theoryId: "obvious",
            theory: "Inventorship",
            element: "Slutsky contribution",
            elemIdx: 0,
            impact: "amber",
          },
        ],
        ai: {
          summary:
            "MIT provisional patent application 60/510,945 (Oct 2003). Discloses MgLT use for cognitive enhancement. Predates '061 by ~3 years. Inventorship documents identify Slutsky as contributor - supports inventorship defect theory.",
          keyPassages: [],
          contradictions: [],
          questions: [],
          witness: "N/A",
        },
      },
      {
        id: "liu-depo",
        type: "depo",
        title: "Liu Deposition Transcript",
        classification: "pin",
        producer: "Plaintiff",
        bates: "PLT-DEP-LIU-001 to 412",
        date: "Feb 12, 2026",
        confidentiality: "Public",
        pages: 412,
        exhibit: "DX-LIU-DEP",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Self-authenticating (deposition)",
          witness: "N/A",
          objections: [],
          status: "green",
          foundation: "FRE 901",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "§103 Obviousness",
            element: "Reasonable expectation of success",
            elemIdx: 3,
            impact: "red",
          },
        ],
        ai: {
          summary:
            "Inventor deposition. Critical admission at p.318:25: 'we did not invent the molecule.' Supports defendant's obviousness defense.",
          keyPassages: [
            {
              cite: "p.318:25",
              text: "'we did not invent the molecule'",
              impact: "red",
            },
            {
              cite: "p.317:2-3",
              text: "No new synthesis method patented",
              impact: "red",
            },
            {
              cite: "p.203:14",
              text: "Prior awareness of magnesium research",
              impact: "amber",
            },
          ],
          contradictions: [
            "FAC ¶8 vs ¶13 - complaint makes inconsistent representations about inventive contribution. Not explored in deposition.",
          ],
          questions: [
            "Was Liu specifically asked about CN1200366A awareness?",
            "FAC ¶8/¶13 contradiction - supplemental depo needed?",
            "GRAS Notice 499 manufacturing-origin representations - supplemental depo needed?",
          ],
          witness: "Liu",
        },
      },
      {
        id: "gras-499",
        type: "filing",
        title: "GRAS Notice 499",
        classification: "foundation",
        producer: "Plaintiff · regulatory filing",
        bates: "PLT-REG-GRAS499-001 to 87",
        date: "2014",
        confidentiality: "Public",
        pages: 87,
        exhibit: "DX-GRAS-499",
        exhibitStatus: "stipulated",
        cluster: null,
        auth: {
          method: "Self-authenticating (FRE 902(5))",
          witness: "N/A",
          objections: [],
          status: "green",
          foundation: "FRE 902(5)",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "Country-of-Origin Fraud (counterclaim)",
            element: "False representation",
            elemIdx: 0,
            impact: "red",
            explainer:
              "States 'Manufactured in USA' - contradicts COA + ImportGenius",
          },
        ],
        ai: {
          summary:
            "Plaintiff's GRAS Notice 499 represents 'Manufactured in USA' for MgLT formulation. Direct contradiction with Certificates of Analysis showing Shenzhen, China sourcing. Foundation for country-of-origin fraud counterclaim.",
          keyPassages: [
            {
              cite: "GRAS 499 p.12",
              text: "'Manufactured in USA'",
              impact: "red",
            },
          ],
          contradictions: [
            "Three Certificates of Analysis show manufacturing origin as Shenzhen, China.",
            "ImportGenius trade data shows Jordan-routed shipments - potential triangulation scheme.",
          ],
          questions: [
            "Who at plaintiff signed the GRAS filing?",
            "What was the basis for the 'Manufactured in USA' representation?",
          ],
          witness: "Plaintiff regulatory affairs",
        },
      },
      {
        id: "coa-cluster",
        type: "exhibit",
        title: "Certificates of Analysis (cluster)",
        classification: "foundation",
        producer: "Defendant · production",
        bates: "DEF-COA-001 to 003",
        date: "2024-2025",
        confidentiality: "Confidential",
        pages: 3,
        exhibit: "DX-COA-001 to 003",
        exhibitStatus: "stipulated",
        cluster: "china-origin",
        clusterCount: 3,
        auth: {
          method: "Certified business records via Mando 30(b)(6)",
          witness: "Alfaro",
          objections: [],
          status: "green",
          foundation: "FRE 803(6)",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "Country-of-Origin Fraud",
            element: "Manufacturing origin",
            elemIdx: 0,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "Three Certificates of Analysis showing Shenzhen, China as MgLT manufacturing origin. Authenticated via Mando 30(b)(6) deposition.",
          keyPassages: [
            {
              cite: "COA-001",
              text: "'Origin: Shenzhen, China'",
              impact: "green",
            },
          ],
          contradictions: [],
          questions: [],
          witness: "Alfaro",
        },
      },
      {
        id: "importgenius",
        type: "filing",
        title: "ImportGenius Trade Data",
        classification: "foundation",
        producer: "⚠️ NOT YET PRODUCED",
        bates: "PENDING",
        date: "Jan-Mar 2025",
        confidentiality: "Pending",
        pages: 0,
        exhibit: "PENDING",
        exhibitStatus: "unauthenticated",
        cluster: "china-origin",
        auth: {
          method: "Custodian of records subpoena to ImportGenius",
          witness: "⚠️ NOT YET IDENTIFIED",
          objections: ["Authentication contested without custodian"],
          status: "red",
          foundation: "FRE 803(6) requires custodian",
        },
        theoryMaps: [
          {
            theoryId: "obvious",
            theory: "Country-of-Origin Fraud",
            element: "Triangulation scheme",
            elemIdx: 1,
            impact: "green",
          },
        ],
        ai: {
          summary:
            "ImportGenius trade data showing Jordan-routed shipments Jan-Mar 2025. Suggests triangulation scheme to obscure Chinese origin. NOT YET AUTHENTICATED - no foundation witness identified. Custodian subpoena required before September 11 fact discovery cutoff.",
          keyPassages: [],
          contradictions: [],
          questions: [
            "Issue ImportGenius custodian-of-records subpoena before September 11 fact discovery cutoff.",
          ],
          witness: "⚠️ Custodian-of-records - subpoena not yet issued",
        },
      },
    ],
  },
};

const LOCATIONS_INTEL = {
  ...ILE_K_LI,
  juul: {
    urgent: [],
    sections: [
      {
        id: "sfusd-schools",
        title: "Six SFUSD Schools - Dorn's Geographic Foundation",
        icon: "🏫",
        intro:
          "The $78.8M abatement estimate rests on site inspections of six specific SFUSD schools. Whether these schools are geographically representative of the full district is JLI's primary methodological challenge to Dorn's extrapolation.",
        pins: [
          {
            name: "Balboa High School",
            coords: "37.7218°N, 122.4435°W",
            dkts: ["PX-201", "PX-202", "Dorn Site Report 1"],
            neighborhood: "Excelsior",
            remediation: "$13.4M",
            findings:
              "68 confiscated devices 2017-2019; 4 bathroom retrofits required; HVAC remediation",
            retail:
              "5 retailers within 0.4mi sold JUUL during use period (per MDL retailer disclosures)",
          },
          {
            name: "Galileo Academy",
            coords: "37.8035°N, 122.4254°W",
            dkts: ["PX-202", "PX-203", "Dorn Site Report 2"],
            neighborhood: "Russian Hill / Marina",
            remediation: "$11.8M",
            findings: "54 devices; 3 bathroom retrofits; ventilation upgrades",
            retail: "7 retailers within 0.4mi",
          },
          {
            name: "Lincoln High School",
            coords: "37.7411°N, 122.5082°W",
            dkts: ["PX-203", "PX-204", "Dorn Site Report 3"],
            neighborhood: "Sunset",
            remediation: "$15.2M",
            findings:
              "82 devices; 5 bathroom retrofits; classroom HVAC overhaul",
            retail: "4 retailers within 0.4mi",
          },
          {
            name: "Mission High School",
            coords: "37.7635°N, 122.4244°W",
            dkts: ["PX-204", "PX-205", "Dorn Site Report 4"],
            neighborhood: "Mission",
            remediation: "$14.7M",
            findings:
              "76 devices; 4 bathroom retrofits; staff training programs",
            retail: "9 retailers within 0.4mi - highest density",
          },
          {
            name: "Washington High School",
            coords: "37.7800°N, 122.4818°W",
            dkts: ["PX-205", "PX-206", "Dorn Site Report 5"],
            neighborhood: "Richmond",
            remediation: "$12.1M",
            findings: "58 devices; 3 bathroom retrofits; assembly programs",
            retail: "5 retailers within 0.4mi",
          },
          {
            name: "Wallenberg High School",
            coords: "37.7822°N, 122.4514°W",
            dkts: ["PX-206", "PX-207", "Dorn Site Report 6"],
            neighborhood: "Western Addition",
            remediation: "$11.6M",
            findings:
              "49 devices; 3 bathroom retrofits; counselor staffing increase",
            retail: "6 retailers within 0.4mi",
          },
        ],
        analysis:
          "The six schools cluster across diverse SF neighborhoods - Excelsior, Russian Hill, Sunset, Mission, Richmond, Western Addition. Demographic and socioeconomic spread supports Dorn's representativeness argument. JLI's 'cherry-picked' framing weakens against the actual geography. Mission High has the highest retail density (9 retailers) and the highest ratio of confiscated devices to enrollment - the 'hottest' geography in the inspection set.",
      },
      {
        id: "virginia-seliavski",
        title: "Virginia - Seliavski SOL Jurisdiction",
        icon: "🏛️",
        intro:
          "Seliavski is in Virginia. Virginia's two-year personal injury SOL applies. Use period: July 2017 - Spring 2018. SOL ran Spring 2020 absent tolling. Complaint filed 2019 - within window.",
        pins: [
          {
            name: "Plaintiff residence - Virginia",
            coords: "Virginia",
            dkts: ["Dkt.1 (Master Compl.)", "CMO 1", "Bellwether order"],
            solTimeline: [
              {
                date: "July 2017",
                event: "Use period begins",
                impact: "neutral",
              },
              {
                date: "Spring 2018",
                event: "Use period ends · SOL clock starts",
                impact: "neutral",
              },
              {
                date: "Sep 2018",
                event: "FDA warning letters - JLI argues constructive notice",
                impact: "red",
              },
              {
                date: "Nov 2018",
                event:
                  "Congressional hearings - JLI argues constructive notice",
                impact: "red",
              },
              {
                date: "2019",
                event: "Complaint filed - within window",
                impact: "green",
              },
              {
                date: "Spring 2020",
                event: "SOL would have run absent tolling/tolling agreements",
                impact: "neutral",
              },
            ],
            remandVenue:
              "E.D. Va. (Eastern District of Virginia) - 'Rocket Docket'",
            remandImplication:
              "Trial within 12 months of remand. If Seliavski's case remands to E.D. Va. without medical causation expert retained, trial could arrive before plaintiff is ready.",
          },
        ],
      },
      {
        id: "altria-richmond",
        title: "Altria HQ - Richmond, Virginia",
        icon: "🏢",
        intro:
          "Altria settled the class separately from JLI - different timeline, different board authority, different decision-making calculus. Two defense teams have different authorities; map makes the organizational separation visible.",
        pins: [
          {
            name: "Altria Group HQ",
            coords: "Richmond, VA",
            dkts: [
              "Dkt.4385 (sealing res.)",
              "Class settlement Dkt.3892",
              "Dkt.4341 MSJ",
            ],
            details:
              "Settled class July 2023 (preliminary approval); finalized 2024.",
            antitrust:
              "Antitrust exposure separate from JLI - different theories, different damages allocation.",
            settlement:
              "Illovsky overture May 1, 2026 (Comm Log) was independent of JLI - FRE 408 settlement overtures pre-antitrust trial.",
            counsel:
              "Altria negotiating from Richmond - not from Gibson Dunn SF.",
          },
        ],
      },
      {
        id: "orrick-court",
        title: "Judge Orrick - N.D. Cal. JUUL MDL",
        icon: "⚖️",
        intro: "Active procedural constraints driving the litigation calendar.",
        pins: [
          {
            name: "N.D. Cal. Courtroom - Judge Orrick",
            coords: "450 Golden Gate Ave, San Francisco",
            dkts: ["Dkt.4476 (CMC)", "Standing Order", "Dkt.4341"],
            constraints: [
              "Standing order: discovery disputes by letter brief (not full motions)",
              "Dec 16, 2025 CMC minute entry (Dkt.4476) - SJ ruling response procedure",
              "Antitrust trial date: Sep 28, 2026 (scheduling order)",
              "PHV admissions: noted on docket; no special standing order requirement",
            ],
          },
        ],
      },
    ],
  },
  musk: {
    urgent: [],
    sections: [
      {
        id: "nd-texas-transfer",
        title: "N.D. Texas - The Transfer That Shaped This Case",
        icon: "📍",
        intro:
          "Musk's complaint was originally filed in N.D. Texas. OpenAI moved to transfer to N.D. Cal. Transfer granted, case assigned to Judge YGR (Oakland). The forum question was won by defendant before any substantive issue was reached - Texas charitable trust law is now California charitable trust law (Cal. Corp. Code §§5250 et seq.).",
        pins: [
          {
            name: "N.D. Texas - Original Filing",
            coords: "Northern District of Texas",
            dkts: [
              "TX 4:24-cv-00831 (orig.)",
              "Transfer order",
              "Dkt.1 (transferred)",
            ],
            timeline: [
              {
                date: "Aug 2024",
                event: "Original complaint filed in N.D. Texas",
                impact: "neutral",
              },
              {
                date: "Sep 2024",
                event: "OpenAI moves to transfer (28 USC §1404(a))",
                impact: "red",
              },
              { date: "Dec 2024", event: "Transfer granted", impact: "red" },
              {
                date: "Jan 2025",
                event: "Case assigned to Judge YGR, N.D. Cal. Oakland",
                impact: "red",
              },
            ],
            consequence:
              "California charitable trust law (Cal. Corp. Code §§5250 et seq.) governs trust creation question - not Texas law. Texas would have allowed jury determination of trust formation; California requires court determination of statutory predicate questions. Forum loss was substantive.",
          },
        ],
      },
      {
        id: "ygr-court",
        title: "Judge YGR - N.D. Cal. Oakland · TRIAL IN PROGRESS",
        icon: "🚨",
        intro: "Trial is in progress as of Day 3. Live operational view.",
        pins: [
          {
            name: "N.D. Cal. Oakland Courthouse - Judge YGR",
            coords: "1301 Clay St, Oakland",
            dkts: [
              "Dkt.515 (§17510.8)",
              "Dkt.516",
              "Dkt.452 (Daubert PTO No. 2)",
            ],
            live: true,
            schedule: {
              today: "Morning session 9:15 AM · Afternoon 1:30 PM",
              pending:
                "§17510.8 ruling pending from Apr 30 trial briefs (Dkt.515/516)",
              nextWitness: "Brockman (Day 3-4) - PX163 confrontation pending",
            },
            flags: [
              "Trial Day 3 of 5-7 days estimated",
              "§17510.8 ruling could come during Brockman cross",
              "Wazzan FIFO testimony scheduled Day 4-5",
            ],
          },
        ],
      },
    ],
  },
  neuro: {
    urgent: [
      {
        id: "slutsky-tel-aviv",
        priority: "critical",
        title: "🔴 SLUTSKY - TEL AVIV · INITIATION DEADLINE TODAY",
        countdown: "134 days to discovery cutoff",
        leadTime: "90-120 day Hague Convention lead time",
        deadline: "Initiate today - May 5, 2026",
        summary:
          "Dr. Slutsky is at Tel Aviv University. Centerpiece of inventorship defect counterclaim. Mao deposition (Feb 18, 2026) credited her for the discovery. She has not been deposed. Fact discovery closes Sep 11, 2026. 90 days from today is Aug 3 - fact-discovery cutoff is 39 days later. Barely enough time if initiated immediately. No time at all if delayed.",
        steps: [
          "File letter rogatory application in C.D. Cal.",
          "Serve through Central Authority - Israeli Ministry of Justice",
          "Coordinate with local Israeli counsel for deposition scheduling",
          "Backup: depose by remote means under FRCP 30 if rogatory delays exceed window",
        ],
        coords: "Tel Aviv, Israel · Ramat Aviv (Tel Aviv University)",
      },
    ],
    sections: [
      {
        id: "supply-chain",
        title: "China → Jordan → California - Fraud Geography",
        icon: "🌏",
        intro:
          "The Second Amended Counterclaims allege Kappa products manufactured in China but labeled 'Manufactured in USA.' ImportGenius shows shipments routed through Jordan before reaching California ports. Three pins, three labels - gap between first and third is the fraud counterclaim.",
        supplyChain: [
          {
            step: 1,
            name: "Shenzhen, China",
            coords: "22.5429°N, 114.0596°E",
            label: "Manufactured (per CoAs)",
            authStatus: "amber",
            authNote:
              "CoAs require business records certification or custodian of records testimony. Authentication method not yet confirmed. No deposition of Chinese manufacturer taken or scheduled.",
            task: "Identify Chinese manufacturer custodian - issue subpoena via Hague Convention OR establish authentication via Mando 30(b)(6) to whom CoAs were transmitted",
          },
          {
            step: 2,
            name: "Amman, Jordan",
            coords: "31.9454°N, 35.9284°E",
            label: "Routed through (per ImportGenius)",
            authStatus: "red",
            authNote:
              "ImportGenius is a commercial trade aggregator - not self-authenticating. Subpoena to ImportGenius for source data needed. No subpoena issued. No authentication witness identified.",
            task: "⚠️ URGENT: Issue ImportGenius custodian-of-records subpoena before Sep 11 discovery cutoff",
          },
          {
            step: 3,
            name: "California Port of Entry",
            coords:
              "33.7406°N, 118.2768°W (Long Beach) / 37.8044°N, 122.2712°W (Oakland)",
            label: "Labeled at destination: 'Made in USA'",
            authStatus: "red",
            authNote:
              "US Customs entry documents are public records - self-authenticating under FRE 902(5). No one has pulled these records yet.",
            task: "⚠️ Pull US Customs entry documents (CBP Form 7501) - self-authenticating under FRE 902(5). Should be done immediately, low effort.",
          },
        ],
        fraudGap:
          "Manufactured: Shenzhen, China · Routed: Jordan · Labeled: 'Made in USA'. The gap between origin and label is the §43(a) Lanham Act false-designation counterclaim. Three pins, three authentication gaps, three tasks.",
      },
      {
        id: "mando-no-cal",
        title: "Mando's Declared Non-Presence in California",
        icon: "📋",
        intro:
          "Armando Alfaro's declaration states Mando International has no California offices, employees, property, or bank accounts - foundation of personal jurisdiction challenge.",
        pins: [
          {
            name: "Mando International - Declared Location",
            coords: "Outside California (per Alfaro declaration)",
            dkts: [
              "Alfaro decl. (Dkt.62)",
              "RFA responses",
              "2nd Am. Counterclaims",
            ],
            contradictions: [
              "ImportGenius shipments arriving California ports",
              "Sales to California customers (per discovery)",
              "California business activity documented in record",
              "Website and marketing materials targeting California consumers",
            ],
            jurisdictionStatus:
              "Personal jurisdiction challenge weakens with each documented California contact. Alfaro declaration may be impeached on cross.",
          },
        ],
      },
      {
        id: "mit-cambridge",
        title: "MIT Cambridge - Prior Art Origin",
        icon: "🎓",
        intro:
          "MIT provisional 60/510,945 (Oct 10, 2003) is second of two prior-art references forming §103 obviousness defense. Filed by Liu's group at MIT Brain & Cognitive Sciences.",
        pins: [
          {
            name: "MIT Brain & Cognitive Sciences",
            coords: "77 Massachusetts Ave, Cambridge, MA",
            dkts: ["MIT Provisional 60/510,945", "Dkt.151 (Adams decl.)"],
            significance: [
              "Liu's awareness - he was filing author of MIT provisional, then named inventor on '061. Argument that he was unaware of prior magnesium research undercut by his own prior filing.",
              "Authentication: USPTO public records straightforward - confirmed. No subpoena to MIT TLO required for public record portion.",
              "Backup: MIT TLO records of provisional filing may have additional prosecution history if needed beyond USPTO public file.",
            ],
            authStatus: "green",
            authNote:
              "USPTO public records authentication confirmed. FRE 902(5) self-authenticating.",
          },
        ],
      },
      {
        id: "aenlle-court",
        title: "Judge Aenlle-Rocha - C.D. Cal. Patent Court",
        icon: "⚖️",
        intro: "Active procedural constraints from patent standing order.",
        pins: [
          {
            name: "C.D. Cal. - Judge Aenlle-Rocha",
            coords: "350 W 1st St, Los Angeles",
            dkts: ["Dkt.33 (standing order)", "Dkt.107 (OSC)", "Dkt.151"],
            constraints: [
              "Patent standing order (Dkt.33) - claim construction briefing format/timing",
              "OSC (Dkt.107): Abhyanker's serial MTD filings violated standing order requiring consolidated briefing",
              "Claim construction briefing closed Mar 30 with Adams clarifying declaration (Dkt.151)",
              "Markman hearing not yet set",
              "Fact discovery cutoff: Sep 11, 2026",
            ],
          },
        ],
      },
    ],
  },
};

const ATTORNEY_COMMS = {
  ...ILE_K_ACOMMS,
  juul: [
    {
      attyEmail: "tboutrous@gibsondunn.com",
      threads: [
        {
          id: "jt1",
          subject: "Re: Meet & Confer - Document Production Schedule",
          type: "meet-confer",
          date: "Apr 28, 2026",
          unread: false,
          messages: [
            {
              from: "Sharp Strategy Plus",
              to: "Theodore J. Boutrous Jr.",
              date: "Apr 28, 2026 14:32",
              body: "Theodore - Following up on Friday's M&C. Per Judge Orrick's standing order on letter briefs, please confirm by EOD Wed (Apr 30) whether JLI will agree to the rolling production schedule we proposed for the Altria coordination documents (PX-89 to PX-127). Otherwise we will need to bring this to the court via letter brief.",
            },
            {
              from: "Theodore J. Boutrous Jr.",
              to: "Sharp Strategy Plus",
              date: "Apr 30, 2026 11:08",
              body: "Sharp - JLI agrees to rolling production on PX-89 to PX-104 by May 8, with PX-105 to PX-127 by May 22. We will not produce PX-128 onwards without further M&C - those documents remain subject to our Apr 14 privilege objections. Please confirm receipt.",
            },
          ],
          aiObligations: [
            {
              who: "JLI / Boutrous",
              obligation: "Produce PX-89 to PX-104",
              deadline: "May 8, 2026",
              linkedDkt: "Dkt.4476",
            },
            {
              who: "JLI / Boutrous",
              obligation: "Produce PX-105 to PX-127",
              deadline: "May 22, 2026",
              linkedDkt: "Dkt.4476",
            },
            {
              who: "Both / next M&C",
              obligation: "M&C on PX-128+ privilege objections",
              deadline: "TBD pre-May 22",
              linkedDkt: "Dkt.4476",
            },
          ],
          aiFlags: [
            "Letter-brief threshold engaged - Orrick standing order",
            "Privilege objections preserved on PX-128+ - track for future motion practice",
          ],
        },
      ],
    },
    {
      attyEmail: "silloyd@silloyd.com",
      threads: [
        {
          id: "jt2",
          subject:
            "Plaintiff Steering Committee - Trial Witness Order Sequencing",
          type: "scheduling",
          date: "Apr 22, 2026",
          unread: false,
          messages: [
            {
              from: "Sarah Sharp",
              to: "Sin-Ting Mary Liu",
              date: "Apr 22, 2026 09:14",
              body: "Mary - PSC call Friday. We're recommending Halpern-Felsher first (75% awareness finding sets the emotional foundation), then Dorn (abatement quantification) before Pak. Pak's p.47:12-18 admission lands harder if jury already accepts the harm baseline. Counter-proposal welcome.",
            },
          ],
          aiObligations: [
            {
              who: "PSC",
              obligation:
                "Friday witness order call - confirm Halpern-Felsher → Dorn → Pak sequence",
              deadline: "Apr 26, 2026",
              linkedDkt: "Dkt.4476",
            },
          ],
          aiFlags: [
            "Sequencing decision ties to Witnesses tab Trial Sequencing Tool - Halpern-Felsher Sep 30 academic conflict still flagged",
          ],
        },
      ],
    },
    {
      attyEmail: "willovsky@cooley.com",
      threads: [
        {
          id: "jt3",
          subject: "Re: Settlement Discussion - FRE 408",
          type: "settlement",
          date: "May 1, 2026",
          unread: true,
          messages: [
            {
              from: "Bill Illovsky (Altria Counsel)",
              to: "Sharp Strategy Plus",
              date: "May 1, 2026 16:42",
              body: "Sarah - Per FRE 408, this email is for settlement discussion only. Altria is open to discussing pre-trial resolution of the antitrust track exposure separate from JLI. We can discuss range and structure if you are interested. Please respond by May 14 if PSC wishes to engage.",
            },
          ],
          aiObligations: [
            {
              who: "PSC",
              obligation:
                "Decide whether to engage Altria settlement track separately from JLI",
              deadline: "May 14, 2026",
              linkedDkt: "FRE 408 - confidential",
            },
          ],
          aiFlags: [
            "FRE 408 protected - confidential settlement",
            "Altria negotiating from Richmond independently of JLI/Gibson Dunn",
            "Two-track defense: Altria settlement vs JLI antitrust trial Sep 28",
          ],
        },
      ],
    },
  ],
  musk: [
    {
      attyEmail: "toberlin@morganlewis.com",
      threads: [
        {
          id: "mt1",
          subject: "Re: Day 3 Trial Logistics - Brockman Examination Schedule",
          type: "scheduling",
          date: "May 4, 2026",
          unread: false,
          messages: [
            {
              from: "Marc Toberlin",
              to: "Trial team",
              date: "May 4, 2026 19:32",
              body: "All - Brockman direct begins 9:15 AM tomorrow. Defense will lead with PX163 framing - characterizing as 'personal reflection' (not 'frustration' as he said in dep). Plaintiff cross outline updated: foundation first, PX163 LAST after admission of LP-formation timeline establishes board-knowledge predicate. If §17510.8 ruling drops mid-examination, request brief recess.",
            },
          ],
          aiObligations: [
            {
              who: "Plaintiff cross team",
              obligation: "Brockman cross - foundation first, PX163 LAST",
              deadline: "May 5, 2026 AM",
              linkedDkt: "",
            },
            {
              who: "Trial team",
              obligation:
                "Monitor §17510.8 ruling - request recess if drops mid-Brockman",
              deadline: "May 5, 2026",
              linkedDkt: "Dkt.515/516",
            },
          ],
          aiFlags: [
            "Live trial intelligence",
            "Day 3 - most important witness of trial",
            "§17510.8 ruling timing variable",
          ],
        },
      ],
    },
  ],
  neuro: [
    {
      attyEmail: "raj@legalforcelaw.com",
      threads: [
        {
          id: "nt1",
          subject: "URGENT - Slutsky Hague Convention Initiation",
          type: "discovery",
          date: "May 5, 2026",
          unread: true,
          messages: [
            {
              from: "Raj Abhyanker",
              to: "Litigation team",
              date: "May 5, 2026 06:12",
              body: "Team - TODAY is the initiation deadline for the Slutsky Hague Convention process. 134 days to discovery cutoff. 90-120 day Hague lead time. NO TIME LEFT. Please confirm by 12:00 PM PT today: (1) Letter rogatory application drafted (filed C.D. Cal. by COB); (2) Israeli counsel identified for local coordination; (3) Backup plan for FRCP 30 remote deposition if rogatory delayed.",
            },
          ],
          aiObligations: [
            {
              who: "Litigation team",
              obligation: "File letter rogatory application - C.D. Cal.",
              deadline: "COB May 5, 2026",
              linkedDkt: "",
            },
            {
              who: "Litigation team",
              obligation: "Identify Israeli local counsel",
              deadline: "May 5, 2026",
              linkedDkt: "",
            },
            {
              who: "Litigation team",
              obligation: "Develop FRCP 30 remote-deposition backup plan",
              deadline: "May 7, 2026",
              linkedDkt: "",
            },
          ],
          aiFlags: [
            "⚠️ MOST URGENT TASK - entire inventorship counterclaim depends on Slutsky",
            "If process not initiated TODAY, math fails - 90 days from today is Aug 3 vs Sep 11 cutoff",
          ],
        },
      ],
    },
  ],
};

const PLAYBOOK_DATA = {
  ...ILE_K_PB,
  juul: {
    plaintiff: {
      opponentName: "JLI / Altria Defense Counsel - Kirkland & Ellis · Orrick",
      strongestCase: {
        title: "The Defense Brief, Written in Their Voice",
        opening:
          "This case is at heart a regulatory-failure case dressed up as a tort. Plaintiffs ask this Court to fault JUUL for selling a legal product that the FDA permitted on the market while it conducted MDO review. The FCLAA preempts state-law warning claims as applied to nicotine-delivery devices. The MDL framework consolidated cases for efficiency, but Sonner v. Premier Nutrition forecloses equitable relief where, as here, plaintiffs have an adequate legal remedy via the $255M class settlement they themselves negotiated. SFUSD's representative testified under oath that the district 'does not have' documentation of the past program costs they now seek to recover - a binding 30(b)(6) admission that destroys the past-damages prong of the public nuisance claim. As to Seliavski's PI claim, no medical causation expert has been retained or disclosed. The remaining antitrust claims survived class certification, but at trial plaintiffs cannot prove horizontal coordination on the per se framework - Altria's investment was a vertical participation in a consumer market, not a horizontal carve-up.",
        evidenceSelection: [
          "Pak 30(b)(6) admission ('we don't have that information') - anchor exhibit, played in opening",
          "FDA MDO order (currently stayed by 5th Cir.) - narrative of regulatory tolerance",
          "$255M class settlement agreement - adequate legal remedy under Sonner",
          "Altria FTC due diligence files (Dkt.3393) reframed as 'comprehensive review of any acquisition target,' not selective focus on youth users",
          "Burns/Monsees deposition denials of youth targeting (despite JUUL School docs) - credibility contest, not record proof",
        ],
        framing:
          "Lead with the Pak admission. Then federal preemption framework. Then class settlement adequacy. Save antitrust for the back half - it's their strongest claim but the weakest claim for FCLAA preemption argument. Don't lead with credibility attacks on the founders; the documents speak for themselves only on the youth-marketing question, which is a regulatory problem, not a tort. Frame this as compensable injury question, not a moral judgment question.",
      },
      decisionTree: [
        {
          decision:
            "Are they trying to win outright on SJ or just narrow the claims?",
          leaning: "narrow",
          probability: 78,
          rationale:
            "The evidence record is too well-developed for a full SJ win. JLI's smartest play is to peel off the public nuisance claim (via Pak admission + Sonner) and leave the antitrust track to settle from a weaker position. A full-win attempt risks Orrick treating their motion as overreach.",
          shifters: [
            "Adverse Sonner-Pak ruling → may attempt §1292(b) certification",
            "FCLAA preemption ruling → reshapes everything",
          ],
        },
        {
          decision:
            "If SJ ruling goes against them, do they appeal interlocutory or wait for final judgment?",
          leaning: "wait",
          probability: 70,
          rationale:
            "Interlocutory appeal would signal weakness and create more settlement pressure. JLI prefers a unified post-trial appellate posture where they can attack class certification, FCLAA preemption denial, and the verdict in one filing. EXCEPTION: FCLAA preemption is the one issue where §1292(b) certification is plausible because it's a pure legal question.",
          shifters: [
            "FCLAA preemption denial → §1292(b) becomes likely",
            "Adverse class cert decertification motion → interlocutory appeal under Rule 23(f) (already expired)",
          ],
        },
        {
          decision:
            "At what settlement number does antitrust trial risk exceed settlement cost?",
          leaning: "$1.8B-$2.4B aggregate",
          probability: 60,
          rationale:
            "Per the Damages Model, defense BATNA includes (a) ~$1.5B antitrust class compensatory + (b) potential $1B-$2B SFUSD entity damages if they lose Sonner-Pak. Add reputational tax (Altria public-company exposure) and the trial-risk-adjusted defense BATNA is $1.8B-$2.4B. Below that, JLI/Altria pay; above that, they trial.",
          shifters: [
            "Bellwether opt-out PI verdict in plaintiff-favorable venue → BATNA shifts +$500M",
            "Adverse FCLAA preemption ruling → settlement window opens",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Aggressive sealing motions on every Altria document",
          pattern:
            "~80% of Altria-produced documents flagged AEO/Outside Counsel Only at first production",
          interpretation:
            "They believe internal Altria documents are devastating. Their preferred posture is restricted access and post-trial sealing, not litigation through them.",
          implications:
            "Press for unsealing. The Dkt.3393 unsealed FTC due diligence files were the breakthrough; expand the unsealing pattern via specific motion practice with public-interest framing.",
        },
        {
          observation: "Slow-walking MSJ briefing with extension stipulations",
          pattern:
            "3 stipulated extensions on MSJ; they argued October 2025 hearing rather than push for earlier",
          interpretation:
            "They prefer to delay the SJ ruling because (a) discovery continues to favor plaintiffs as Altria docs unseal and (b) Pak admission leverage decays as plaintiff's expert work matures.",
          implications:
            "Resist further extensions on antitrust track. Press for trial-expert disclosure deadline enforcement. Time is plaintiff's friend.",
        },
        {
          observation: "Specific 30(b)(6) topic carve-outs",
          pattern:
            "JLI repeatedly objected to 30(b)(6) topics on Altria-knowledge questions",
          interpretation:
            "They are protecting the Altria due diligence record. They believe a fully-developed Altria-knowledge story leads to coordination liability.",
          implications:
            "Renew 30(b)(6) on Altria knowledge topics. Subpoena Altria officers individually if corporate depo blocked.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "FDA Regulatory Expert",
            rationale:
              "Plant the regulatory-tolerance narrative early. FDA permitted JUUL on the market while reviewing the MDO. This becomes the lens through which all subsequent testimony is heard.",
          },
          {
            rank: 2,
            witness: "Pak (re-call from 30(b)(6))",
            rationale:
              "Lock in the 'we don't have that information' admission in front of the jury. This is their lead weapon on damages.",
          },
          {
            rank: 3,
            witness: "Burns",
            rationale:
              "Sworn denial of youth targeting. They expect plaintiff to impeach with JUUL School documents - but Burns's calm denial in the face of impeachment is their best chance to recover credibility.",
          },
          {
            rank: 4,
            witness: "Altria 30(b)(6) on routine due diligence",
            rationale:
              "Reframe Dkt.3393 as professional standard practice, not selective youth-user focus. Late in case-in-chief because it depends on the jury already accepting the regulatory-tolerance framing.",
          },
        ],
        crossExamStrategy:
          "Sequence cross to maximize Burns-Monsees contradictions. If both testify within a 24-hour window, the jury can hold both denials side-by-side against JUUL School documents. Press for the calendar pairing.",
      },
      pressurePoints: [
        {
          event: "Adverse Sonner-Pak ruling",
          impact:
            "Eliminates JLI's strongest SJ argument. Settlement window opens immediately.",
          likelihood: 50,
          ourMove:
            "Pre-draft a $1.5B+ demand letter to be issued within 5 business days of ruling. Coordinate with antitrust track plaintiffs for joint demand.",
        },
        {
          event: "Adverse FCLAA preemption ruling",
          impact:
            "Removes the federal-preemption escape hatch. Forces JLI/Altria to defend on substantive merits.",
          likelihood: 60,
          ourMove:
            "Calendar §1292(b) opposition window. Pre-draft opposition to certification motion.",
        },
        {
          event: "Strong antitrust trial expert opinion (Comcast methodology)",
          impact:
            "Eliminates JLI's strongest trial-stage attack on damages methodology.",
          likelihood: 55,
          ourMove:
            "Coordinate trial expert work with class-cert expert. Aim for July 15 disclosure with Daubert-survivable methodology.",
        },
        {
          event: "Bellwether opt-out PI verdict in plaintiff-favorable venue",
          impact:
            "Establishes pricing for opt-out PI cohort. Forces JLI to settle remaining opt-outs to avoid serial trials.",
          likelihood: 35,
          ourMove:
            "Identify strongest opt-out case in most plaintiff-favorable venue (E.D. Pa? D. Mass?) and prioritize for first trial post-MDL remand.",
        },
      ],
    },
    defendant: {
      opponentName:
        "Plaintiffs' Counsel - Lieff Cabraser · Robbins Geller · MDL Co-Lead",
      strongestCase: {
        title: "The Plaintiffs' Brief, Written in Their Voice",
        opening:
          "This is a public-health crisis caused by a deliberate corporate campaign to addict a generation of children. JUUL Labs designed its product to deliver high-dose nicotine through a sleek, USB-shaped device specifically engineered to evade adult and authority detection. JLI marketed this product through 'JUUL School' programs in middle and high schools - internal documents (Dkt.3447 SOF ¶47) show youth-engagement KPIs tracked at the board level. Halpern-Felsher's 75% ignorance survey establishes that adolescents using JUUL did not know it contained nicotine. When JUUL faced market saturation, Altria invested $12.8B with full due diligence visibility into the youth-user base - Dkt.3393 unsealed materials confirm. The federal FDA MDO is currently stayed by the 5th Circuit but the underlying record speaks: this is a nicotine-delivery device targeted to minors. SFUSD bears continuing abatement costs (six schools physically modified per Dorn). Seliavski represents the addiction injury class. Antitrust class certification (Dkt.635) confirmed coordination on a Sherman Act §1 framework.",
        evidenceSelection: [
          "JUUL School marketing materials (Dkt.3447 SOF ¶47) - anchor exhibit, played in opening",
          "Halpern-Felsher 75% ignorance survey - establishes failure-to-warn",
          "Altria due diligence files (Dkt.3393) - coordination evidence",
          "Dorn six-school physical inspection reports - quantifies SFUSD past + forward-looking damages",
          "Burns/Monsees sworn denials contradicted by board-distributed JUUL School materials",
        ],
        framing:
          "Lead with children harmed. Then JUUL School. Then board-level distribution. Then Altria coordination. Save the regulatory framework for rebuttal - they'll bring it. Frame this as moral judgment not regulatory oversight. Drive emotional valence in opening; let documents do the rest.",
      },
      decisionTree: [
        {
          decision: "Push for SJ on antitrust + nuisance, or trial?",
          leaning: "trial",
          probability: 65,
          rationale:
            "Class certification was their breakthrough. They want a verdict, not just SJ posture. The damages model is more impressive at trial than in MSJ briefing.",
          shifters: [
            "Adverse FCLAA preemption ruling → may push trial earlier",
            "Adverse Sonner-Pak ruling → forces aggressive trial schedule",
          ],
        },
        {
          decision:
            "Settle antitrust track separately or wait for SFUSD verdict to set ceiling?",
          leaning: "wait",
          probability: 60,
          rationale:
            "Strategic logic: SFUSD verdict establishes the entity-damages anchor. With SFUSD anchor, antitrust settlement leverage is materially higher.",
          shifters: [
            "Strong opt-out PI verdict → may settle antitrust early to capture momentum",
            "Indirect purchaser class trial scheduling → forces parallel resolution",
          ],
        },
        {
          decision: "Common Benefit Fund fee motion timing",
          leaning: "file late 2026",
          probability: 75,
          rationale:
            "They want to file after trial verdicts establish the value created. Fee request based on $X total recovery is anchored to a verdict number, not a settlement number.",
          shifters: [
            "Major settlement before trial → may file earlier with settlement value as anchor",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Coordinated press strategy with each major filing",
          pattern:
            "Major filings (MSJ opposition, class cert briefing) accompanied by AP/Reuters press releases within 24 hours",
          interpretation:
            "They are running a parallel public-pressure campaign. Verdict narrative is being shaped publicly to influence settlement leverage and bench-trial sentiment.",
          implications:
            "Counter with measured corporate communications. Avoid public statements that reinforce their narrative. Refer reporters to filings, not personal statements.",
        },
        {
          observation: "Massive third-party subpoena program",
          pattern:
            "Subpoenas to Meta, FDA, FTC, state AGs, school districts - far beyond direct case parties",
          interpretation:
            "Building a broader narrative arc beyond JLI. They want a 'industry-wide coordination' story for jury and for regulatory leverage.",
          implications:
            "Resist over-broad third-party discovery. Object on relevance/proportionality. Move for protective orders on Meta production scope.",
        },
        {
          observation: "Trial expert disclosure timing",
          pattern:
            "Class-cert experts preserved methodology; trial expert disclosure deferred to deadline",
          interpretation:
            "They are letting Daubert posture marinate while building the strongest possible methodology. Class-cert ruling validates the core approach.",
          implications:
            "Pre-prepare Daubert challenges keyed to specific methodology shifts. File mid-trial Daubert if methodology changes.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "Halpern-Felsher",
            rationale:
              "Establishes ignorance. 75% of youth users did not know JUUL contained nicotine. Anchors entire failure-to-warn theory.",
          },
          {
            rank: 2,
            witness: "Pak (SFUSD CFO)",
            rationale:
              "Despite the 'we don't have that information' admission, plaintiffs use Pak to humanize SFUSD. Frame the documentation gap as resource constraint, not evidence absence.",
          },
          {
            rank: 3,
            witness: "Antitrust class economic expert",
            rationale:
              "Comcast-survivable methodology demonstration. Damages quantification.",
          },
          {
            rank: 4,
            witness: "Burns",
            rationale:
              "Hostile witness called by plaintiffs. JUUL School documents impeachment. Late in case-in-chief because plaintiffs want maximum jury readiness for impeachment.",
          },
        ],
        crossExamStrategy:
          "Sequence cross to break the emotional throughline. If Halpern-Felsher and Pak testify back-to-back, jury anger compounds. Press for staggered scheduling with neutral witnesses interleaved.",
      },
      pressurePoints: [
        {
          event: "FCLAA preemption granted",
          impact:
            "Removes their failure-to-warn theory. Reshapes case fundamentals.",
          likelihood: 30,
          ourMove:
            "Re-anchor offers immediately. Pre-draft global resolution frame with FCLAA-grant as fulcrum.",
        },
        {
          event: "Sonner-Pak SJ granted on entity damages",
          impact:
            "Eliminates SFUSD past-damages claim. Cuts deepest into their public-impact narrative.",
          likelihood: 40,
          ourMove:
            "Public-relations pivot - frame as procedural ruling, not liability finding. Re-anchor settlement floor.",
        },
        {
          event: "Adverse class decertification ruling",
          impact:
            "Class action collapses to individual cases. Massive settlement leverage swing.",
          likelihood: 15,
          ourMove:
            "Comcast-style methodology challenge brought late in pre-trial. Daubert-style attack on damages-aggregation expert.",
        },
        {
          event: "Common Benefit Fund fee motion ceiling cap",
          impact: "Limits plaintiffs' attorneys' fees recovery.",
          likelihood: 50,
          ourMove:
            "Negotiate cap as part of any global settlement. Reduces plaintiff-side advocacy intensity in remaining cases.",
        },
      ],
    },
  },
  musk: {
    plaintiff: {
      opponentName:
        "OpenAI / Altman / Microsoft Defense - WilmerHale · Cleary Gottlieb",
      strongestCase: {
        title: "The Defense Brief, Written in Their Voice",
        opening:
          "On September 10, 2020, Elon Musk publicly tweeted that 'OpenAI is essentially captured by Microsoft.' That tweet starts a constructive-knowledge SOL clock under California law. By his own statement, Musk knew of the alleged 'capture' more than three years before this lawsuit. The complaint is time-barred. Even if the SOL is tolled - and it is not - Dudney's FIFO analysis demonstrates that the $38M in donations had been fully spent within months of receipt; there is no traceable trust res to which a constructive trust can attach. The OpenAI nonprofit operated, made grants, paid researchers - funds were used as the donor knew they would be. As to charitable trust formation, no such trust was ever created. There is no formal deed, no specific donor restriction, no enforceable expectation under California law. Estate of Heggstad requires unequivocal manifestation of trust intent - informal email correspondence about 'spirit' and 'tech belongs to the world' is insufficient. Microsoft's $1B investment in 2019 was a routine technology partnership, conducted with full diligence. Plaintiff's late-amendment §17510.8 supplemental theory is an admission that the original common-law theory lacks merit. Most fundamentally: Musk himself, in 2017, attempted to merge OpenAI into Tesla - an unclean-hands attempt to convert OpenAI to a for-profit Tesla subsidiary - which is the exact conduct he now claims to oppose.",
        evidenceSelection: [
          "Musk Sept 10, 2020 'captured by Microsoft' tweet - anchor SOL exhibit",
          "DX-516 Yup email (Musk one-word reply agreeing with 'less open') - direct impeachment",
          "DX-827 LP term sheet to Musk Aug 2018 (silence = acquiescence)",
          "DX-862 Musk 'tell them I'm supportive' Mar 2019 - ratification of for-profit arm",
          "Musk Tesla merger bid 2017 - unclean hands",
          "Dudney FIFO analysis - no trust res survives",
          "Microsoft due diligence files (sealed) - routine investment review",
        ],
        framing:
          "Lead with the SOL tweet. Visual - the tweet itself displayed as Exhibit DX-1. Then Musk's own conduct (Yup email, term sheet silence, Tesla bid) before any analysis of trust formation. Frame the case as a sour-grapes lawsuit by a competitor (xAI) whose pre-trial threats (Dkt.522 Apr 25, 2026 Brockman threat, Pampena pattern) confirm intent to attack OpenAI competitively. Save the legal arguments about charitable trust formation for the second half - those are weakest ground.",
      },
      decisionTree: [
        {
          decision: "Push for verdict or pursue mid-trial settlement?",
          leaning: "verdict",
          probability: 72,
          rationale:
            "Defense believes the SOL framework + Dudney FIFO + Musk's own conduct exhibits create a defensible verdict path. Settlement at trial-stage signals weakness on Microsoft BFP and risks setting precedent for future donor-litigation.",
          shifters: [
            "Brockman journal landing strongly with jury → settlement window opens",
            "Adverse §17510.8 Daubert ruling → defense leverage rises",
          ],
        },
        {
          decision: "Witness strategy: call Microsoft Nadella early or late?",
          leaning: "late (Day 5)",
          probability: 80,
          rationale:
            "Late call lets defense observe plaintiff's case-in-chief on PX230 K. Scott email. Nadella testimony as BFP rebuttal carries more weight after jury has seen plaintiff's strongest evidence.",
          shifters: [
            "Plaintiff calls K. Scott in case-in-chief → may move Nadella earlier",
            "Strong PX230 impact → pre-Nadella corrective testimony from Microsoft GC",
          ],
        },
        {
          decision:
            "§17510.8 Daubert posture: aggressive challenge or measured?",
          leaning: "aggressive",
          probability: 70,
          rationale:
            "Schizer supplemental opinion is the entire §17510.8 theory. If excluded, theory dies. Aggressive Daubert is high-leverage with low downside - even if denied, it preserves the appellate record.",
          shifters: [
            "Court signals receptivity → may negotiate scope rather than full exclusion",
            "Adverse rulings on Schizer original opinion → may cool aggressive approach",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Massive document production from OpenAI side",
          pattern: "4M+ documents produced; only ~3% on privilege log",
          interpretation:
            "They are confident in the documentary record. Few privilege claims signal they want jury to see the institutional decision-making. Strategy is overwhelm-with-volume.",
          implications:
            "Don't mistake volume for transparency. Targeted exhibit selection (PX003, PX163, PX230) is more powerful than narrative volume. Build closing around 5-7 anchor exhibits.",
        },
        {
          observation:
            "Pre-trial threat (Dkt.522) and competitive-attack framing",
          pattern:
            "Defense escalated to FRE 408(b) admissibility motion citing Pampena v. Musk pattern",
          interpretation:
            "They are setting up a competitor-attack narrative. Frame: this lawsuit is xAI/Musk competitive warfare, not legitimate fiduciary enforcement.",
          implications:
            "Anticipate FRE 408(b) ruling. Pre-prepare jury instruction request neutralizing competitive-motive framing. Lead with mission-and-promise narrative; relegate xAI to background.",
        },
        {
          observation: "Microsoft GC subpoena resistance",
          pattern:
            "Aggressive opposition to Microsoft document subpoenas; selective production",
          interpretation:
            "They are protecting Microsoft's institutional knowledge record. Microsoft is the BFP defense - they cannot allow internal records to undermine it.",
          implications:
            "Press for unsealing of Microsoft due diligence files. PX230 is one CTO email; institutional record likely contains more. Renew subpoenas with narrower scope.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "Dudney (Damages Expert)",
            rationale:
              "Plant the FIFO seed before damages evidence lands. Dudney's analysis becomes the lens through which Wazzan's tracing is heard.",
          },
          {
            rank: 2,
            witness: "Hemel (Charitable Trust Defense Expert)",
            rationale:
              "Establish academic case against informal trust formation. Hemel before Altman ensures defense legal framework anchored before any factual testimony.",
          },
          {
            rank: 3,
            witness: "Altman",
            rationale:
              "Fact testimony on what the founding emails 'really meant' and the 2019 LP/PBC pivot. Comes after defense legal framework is established.",
          },
          {
            rank: 4,
            witness: "Nadella (Microsoft BFP)",
            rationale:
              "BFP defense. Day 5. Established institutional posture by this point.",
          },
          {
            rank: 5,
            witness: "Sutskever",
            rationale:
              "Last if called. His testimony about the Nov 2023 board crisis is damaging to OpenAI's image even if helpful on legal issues. Defense will minimize his testimony or possibly not call at all.",
          },
        ],
        crossExamStrategy:
          "If Dudney and Hemel testify consecutively, attack the inconsistency between Hemel's academic charitable-trust analysis and Dudney's tracing-only framework. Frame: Hemel says no trust ever formed, but Dudney spends all his energy on tracing - if no trust, why trace? Press the contradiction.",
      },
      pressurePoints: [
        {
          event: "Brockman journal admitted with strong jury attention",
          impact:
            "Pre-litigation handwritten admission lands hardest with jury. Settlement leverage rises.",
          likelihood: 75,
          ourMove:
            "Day 4 closing argument restructure: lead with PX163. Anchor Wazzan tracing as quantification overlay, not primary evidence.",
        },
        {
          event: "§17510.8 Daubert excluded",
          impact:
            "Strict-liability theory dies. Plaintiff falls back to common-law charitable trust.",
          likelihood: 50,
          ourMove:
            "FRCP 51(c) jury instruction request preserved at close of evidence. FRCP 49(a) special interrogatory on §17510.8 elements. Preserve appellate posture.",
        },
        {
          event: "Microsoft GC documents unsealed",
          impact: "Institutional-knowledge record may undermine BFP defense.",
          likelihood: 25,
          ourMove:
            "Renew subpoena with public-interest framing. File motion to unseal selected categories of Microsoft due diligence.",
        },
        {
          event: "Adverse SOL jury instruction",
          impact:
            "Pretrial Order No. 3 framework controls. SOL toll/equity defenses go to jury.",
          likelihood: 60,
          ourMove:
            "FRCP 51(c) preserve with strong tolling language. Build SOL closing around fraudulent concealment.",
        },
      ],
    },
    defendant: {
      opponentName:
        "Plaintiff's Counsel - Toberoff & Associates · Akhilesh Krishnan",
      strongestCase: {
        title: "The Plaintiffs' Brief, Written in Their Voice",
        opening:
          "This is a story about a $38 million bait-and-switch. Elon Musk, Sam Altman, and Greg Brockman founded OpenAI in 2015 with explicit promises that 'the technology would belong to the world' (PX003 founding email) and that the organization would forever be a 501(c)(3) charity. On those promises, Musk donated $38 million from 2015 to 2020. The defendants then converted that nonprofit, with its donated assets, into a for-profit company now valued at over $300 billion - and gave 27% equity to Microsoft for a $1 billion investment that Microsoft made with full knowledge that those donor expectations existed (PX230, March 2018, Kevin Scott to Satya Nadella: 'I wonder if the big OpenAI donors are aware of this'). The Brockman journal (PX163), in handwriting and pre-litigation, admits 'it would be wrong to steal the non-profit from Elon.' Under California charitable-trust law, this constitutes the fiduciary breach. Schizer, a Daubert-survived charitable-trust expert, supports the framework. Wazzan FIFO traces the $38M through to Microsoft equity. The remedy is constructive trust on the equity benefit. Beyond common-law trust theory, Cal. Bus. & Prof. Code §17510.8 imposes strict liability for false or misleading charitable solicitations - the founding-era emails are themselves the solicitations.",
        evidenceSelection: [
          "PX003 Altman founding email ('tech belongs to the world')",
          "PX163 Brockman journal ('it would be wrong to steal the non-profit')",
          "PX230 K. Scott to Nadella March 2018 (Microsoft pre-investment knowledge)",
          "PX200 ratifying corp documents 'spirit of a donation'",
          "$38M donation records 2015-2020",
          "OpenAI 2019 LP/PBC pivot corporate documents",
          "Microsoft $1B (2019) + $10B (2023) investment timeline",
          "Schizer Daubert-survived expert opinion (PTO No. 4 admitted)",
          "Wazzan FIFO Opinions 3-6 (admitted)",
        ],
        framing:
          "Lead with the founding promise (PX003). Then the breach (LP/PBC pivot 2019). Then the journal (PX163) - pre-litigation handwritten admission. Microsoft BFP collapse via PX230. Damages quantified by Wazzan. Constructive trust on Microsoft equity is the remedy. Schizer supports legal framework. Save §17510.8 strict liability for closing if statute admitted.",
      },
      decisionTree: [
        {
          decision: "Lead trial with PX163 or PX003?",
          leaning: "PX003",
          probability: 65,
          rationale:
            "PX003 is the founding promise. PX163 is the fiduciary admission. Chronologically, PX003 establishes the trust formation; PX163 establishes the breach. Lead with creation, end with breach.",
          shifters: [
            "If Day 1 jury observation shows weak engagement → may pivot to lead with PX163 to drive emotional throughline",
          ],
        },
        {
          decision:
            "Push §17510.8 strict-liability theory or rely on common-law trust?",
          leaning: "both",
          probability: 70,
          rationale:
            "Theory diversification = appellate insurance. If common-law trust loses, §17510.8 supplements. Daubert ruling on Schizer supplemental will determine §17510.8 viability.",
          shifters: [
            "§17510.8 Daubert excluded → drop, rely on common-law charitable trust",
            "Common-law evidence stronger than expected → may de-emphasize §17510.8",
          ],
        },
        {
          decision:
            "Damages: constructive trust on Microsoft equity or restitution?",
          leaning: "constructive trust",
          probability: 78,
          rationale:
            "Constructive trust on ~27% Microsoft OpenAI equity is the most powerful remedy. Restitution alone caps at $38M nominal donations. Constructive trust captures appreciation = $135B+ at current valuation.",
          shifters: [
            "Wazzan tracing methodology challenged at trial → may need restitution fallback",
            "Adverse Microsoft BFP ruling → constructive trust limited to OpenAI equity, not Microsoft",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Selective document publication via media",
          pattern:
            "Brockman journal PX163 leaked to NYT before trial; founding emails released to press",
          interpretation:
            "Plaintiff is running parallel public narrative. Building public sentiment as supplementary leverage to legal proceedings.",
          implications:
            "Counter via measured corporate response. Avoid amplification through engagement. Refer media to court filings.",
        },
        {
          observation: "Aggressive amendment timing",
          pattern: "§17510.8 supplemental theory added April 2026, post-MSJ",
          interpretation:
            "Late-stage theory supplementation = admission that original theories were insufficient. They needed strict liability backstop.",
          implications:
            "Frame at trial: §17510.8 is admission of common-law trust theory weakness. Use against credibility of overall theory.",
        },
        {
          observation: "Dudney depositions emphasis",
          pattern:
            "Plaintiffs invested significant cross-examination resources on Dudney; questioned every page",
          interpretation:
            "They view Dudney as the most dangerous defense expert. FIFO theory is what they fear most.",
          implications:
            "Day 3 trial Dudney testimony preparation: anticipate aggressive cross. Pre-prepare redirect outline.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "Musk",
            rationale:
              "Day 1 anchor. His testimony on founding promises and the breach is the emotional through-line.",
          },
          {
            rank: 2,
            witness: "Brockman (re-call)",
            rationale:
              "PX163 journal authentication. Brockman's hostile-witness moment is a centerpiece.",
          },
          {
            rank: 3,
            witness: "Schizer (charitable trust expert)",
            rationale:
              "Establishes legal framework after fact testimony lands.",
          },
          {
            rank: 4,
            witness: "K. Scott (PX230 author)",
            rationale:
              "BFP-defense collapse. Late in case-in-chief because impact requires prior facts established.",
          },
          {
            rank: 5,
            witness: "Wazzan (damages)",
            rationale:
              "FIFO tracing as quantification. Last because methodology requires prior facts.",
          },
        ],
        crossExamStrategy:
          "Press contradictions between Schizer's broad charitable-trust theory and Wazzan's narrow tracing methodology. Frame: if everything is in trust, why need to trace? Either trust theory works (Schizer), or it doesn't (need Wazzan). Both can't be right.",
      },
      pressurePoints: [
        {
          event: "Sonner-style Heggstad denial",
          impact:
            "Charitable trust theory collapses if California courts require formal trust deed. Falls back to UE only.",
          likelihood: 25,
          ourMove:
            "Pre-prepare UE-only closing arguments. Wazzan tracing remains viable for restitution.",
        },
        {
          event:
            "PX230 institutional-knowledge interpretation contested at trial",
          impact:
            "BFP defense for Microsoft strengthened. Limits damages reach.",
          likelihood: 55,
          ourMove:
            "Strong Nadella cross. Pre-prepare BFP-defeat questioning sequence.",
        },
        {
          event: "Brockman journal authenticity attacked at trial",
          impact: "PX163 reliability undermined.",
          likelihood: 15,
          ourMove:
            "Authentication overkill. Multiple witnesses, forensic handwriting, contemporaneous notes.",
        },
        {
          event: "§17510.8 Daubert excluded",
          impact:
            "Strict-liability theory dies; common-law charitable trust theory only.",
          likelihood: 50,
          ourMove:
            "FRCP 51(c) preservation. Build common-law closing without reliance on §17510.8.",
        },
      ],
    },
  },
  neuro: {
    plaintiff: {
      opponentName: "Mando Defense - LegalForce RAPC Worldwide (Raj Abhyanker)",
      strongestCase: {
        title: "The Defense Brief, Written in Their Voice",
        opening:
          "This case fails at three independent points. First, on §101: 'L-threonate chelation' as plaintiff seeks to construe it would encompass natural in-vivo formation - a natural phenomenon ineligible for patenting under Mayo/Alice. The specification examples narrow the claim scope to manufactured chemical complexes; the broad construction plaintiff seeks renders the claims invalid as a matter of law. Second, on Markman: Federal Circuit precedent (Phillips v. AWH) requires construing claim terms by hierarchy of construction tools, with specification embodiments as the most informative. The specification examples disclose only the manufactured form, not the in-vivo form; the term must be construed narrowly. Aenlle-Rocha's historical 5/5 pattern of narrow construction supports this approach. Third, on prior art: CN1200366A (2003) and the MIT Provisional 60/510,945 (2002) anticipate or render obvious the disclosed L-threonate chelation chemistry. Plaintiff has not retained a technical expert to rebut this analysis - the §102/§103 record is uncontested. Even if construction is broadly favorable to plaintiff, infringement on the accused Mando product is not established because the chemistry analyses do not show identical claim scope. Each independent ground requires dismissal; together they confirm summary judgment.",
        evidenceSelection: [
          "CN1200366A (2003) - anchor anticipation reference",
          "MIT Provisional 60/510,945 (2002) - corroborating prior art",
          "Adams Clarifying Declaration Dkt.151 - narrowing construction support",
          "Adams Declaration Dkt.144 - opening claim construction response",
          "Ziari Initial Report - anticipation + obviousness analysis",
          "Plaintiff's failure to retain technical expert (gap)",
          "Specification examples 4-7 - embodiment disclosure",
          "§101 motion record - Mayo/Alice framework",
        ],
        framing:
          "Lead with §101 ineligibility because it is most appellant-favorable on de novo Federal Circuit review. Then Markman (also de novo). Save infringement for closing - even if Markman ruling unfavorable, infringement is contestable on chemistry analysis. Frame plaintiff's litigation pattern as overreach: 5 patents originally asserted, narrowed to 1 under pressure; failure to retain technical expert; absent inventorship rebuttal. Frame the case as plaintiff over-claiming prior art with no expert support.",
      },
      decisionTree: [
        {
          decision: "Push §101 motion or Markman first?",
          leaning: "§101 first if possible",
          probability: 70,
          rationale:
            "§101 ineligibility = total invalidity. Markman alone affects scope but not ultimate validity. §101 ruling could moot Markman. Federal Circuit de novo standard favors appellant on §101.",
          shifters: [
            "Court signals Markman ruling first → defense pivots to Markman strategy",
            "§101 motion denied → defense Markman becomes critical fallback",
          ],
        },
        {
          decision: "Pursue §285 exceptional-case fees post-judgment?",
          leaning: "yes",
          probability: 80,
          rationale:
            "Plaintiff's strategic missteps (no technical expert, broad construction in face of narrow precedent, deferred Slutsky discovery) support exceptional-case finding. §285 award could exceed $1M.",
          shifters: [
            "Plaintiff cures expert gap before disclosure deadline → fees probability decreases",
          ],
        },
        {
          decision: "How aggressive on discovery? Trade-off: cost vs leverage",
          leaning: "moderate",
          probability: 60,
          rationale:
            "Defense has the better case on the merits; aggressive discovery raises cost without strengthening position materially. Selective discovery on plaintiff's expert gap and inventorship issues maximizes ROI.",
          shifters: [
            "§101 ruling adverse → may shift to aggressive discovery to broaden Markman record",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Repeated motion withdrawal pattern",
          pattern:
            "Multiple motions filed and withdrawn after oppositions filed (flagged in OSC at Dkt.107). MTD/Venue Transfer withdrawn (Dkt.54). Sanctions Motion withdrawn (Dkt.104). Stricken stipulations (Dkts.80-81).",
          interpretation:
            "Defense counsel is filing motions to create leverage or impose cost rather than because of belief in success. Litigation style is reactive and pressure-responsive, not strategically sequenced.",
          implications:
            "Maintain consistent offensive pressure - defense will retreat when challenged. Don't react to defense motions; they may withdraw under opposition pressure. Allocate resources accordingly.",
        },
        {
          observation: "Sole-counsel structure",
          pattern:
            "Abhyanker as sole signatory on all major filings; no co-counsel arrangement",
          interpretation:
            "Resource constraint signal. Limited bandwidth = predictable behavior. Strategic filings are individual decisions, not committee outputs. Reactive style is structural.",
          implications:
            "File on plaintiff's preferred timeline. Defense response time will be slower. Shorter response windows favor plaintiff.",
        },
        {
          observation: "§101 motion timing post-narrowing",
          pattern:
            "§101 motion filed AFTER plaintiff narrowed to single '061 patent (4/5 patents dismissed). §101 timing suggests defense saw opening only after narrowing.",
          interpretation:
            "Defense did not believe original 5-patent assertion was §101-vulnerable but does believe single '061 patent is. Selective vulnerability assessment.",
          implications:
            "Markman ruling will determine §101 risk. Press for narrow construction on §101-irrelevant terms while avoiding §101-relevant terminology.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "Adams (Claim Construction)",
            rationale:
              "Lead with claim construction for jury context. Adams's narrow construction frame is most favorable defense ground.",
          },
          {
            rank: 2,
            witness: "Ziari (Prior Art)",
            rationale:
              "Prior art testimony lands harder after construction is established. CN1200366A + MIT is impactful only after claim scope is anchored.",
          },
          {
            rank: 3,
            witness: "Liu (cross-examination)",
            rationale:
              "Liu's prior testimony 'we did not invent the molecule' is impeachment-grade for invalidity. Late in case-in-chief because impact depends on prior testimony.",
          },
          {
            rank: 4,
            witness: "Slutsky (if available via rogatory)",
            rationale:
              "§102(f) inventorship-defect rebuttal. Last because availability uncertain.",
          },
        ],
        crossExamStrategy:
          "Press Adams on narrow-construction reading limitations into claims. Frame: Phillips v. AWH says specification illustrates, not limits. Press contradiction between Adams's narrow construction and Ziari's broad anticipation analysis - they cannot both be right on claim scope.",
      },
      pressurePoints: [
        {
          event: "Markman ruling broad-construction grant",
          impact:
            "Defense's strongest argument collapses. Forces settlement consideration.",
          likelihood: 30,
          ourMove:
            "Pre-prepare immediate damages demand letter. §1292(b) certification motion pre-drafted for §101 issue separately.",
        },
        {
          event: "§101 motion denied",
          impact:
            "Validity cannot be challenged at SJ stage. Trial inevitable.",
          likelihood: 55,
          ourMove:
            "Press §1292(b) opposition. Aggressive discovery on Slutsky inventorship to lock in §102(f) defense.",
        },
        {
          event: "Plaintiff retains qualified technical expert",
          impact:
            "§102/§103 prior-art analyses become contested. Defense vulnerability rises.",
          likelihood: 60,
          ourMove:
            "Daubert-target the new expert immediately upon disclosure. Challenge methodology on Markman-narrowing.",
        },
        {
          event: "Slutsky letter rogatory granted",
          impact:
            "§102(f) inventorship-defect rebuttal evidentiary record develops. Defense vulnerable.",
          likelihood: 50,
          ourMove:
            "File letter rogatory THIS WEEK. Begin Slutsky deposition outline preparation.",
        },
      ],
    },
    defendant: {
      opponentName:
        "Plaintiff's Counsel - Fortis LLP (Sang N. Dang) · ThreoTech",
      strongestCase: {
        title: "The Plaintiffs' Brief, Written in Their Voice",
        opening:
          "The '061 patent issued from a 2024 prosecution that addressed and overcame all prior art that the examiner considered. The patent's claim term 'L-threonate chelation' encompasses, by its plain meaning to one of skill in the art, the manufactured chemical complex that constitutes the inventive form of magnesium L-threonate. Mando's accused products contain that chemical complex per documented chemistry analysis. Mando admits manufacture of the accused products in its answer. Defendant's narrow-construction theory reads limitations into the claims that the specification does not disclaim. CN1200366A discloses related but distinct chemistry; the MIT Provisional discloses synthesis approaches but not the specific claimed chelation. Both fail anticipation under §102 because they do not disclose every limitation of the claim. KSR motivation-to-combine fails because the references teach away from the claimed chelation. Mando's §101 motion is meritless: the claimed chelation is a manufactured chemical complex, not a natural phenomenon. The Adams Clarifying Declaration overstates the scope of the specification examples and conflates §101 with claim construction (separate inquiries under Federal Circuit precedent). The pattern of motion withdrawal by defense counsel - multiple motions filed and withdrawn after oppositions filed - confirms that the substantive defenses lack merit.",
        evidenceSelection: [
          "'061 patent specification + prosecution history",
          "Mando product Certificates of Analysis (CoAs)",
          "Mando answer admitting manufacture",
          "Phillips v. AWH plain meaning analysis framework",
          "POSITA testimony (Baker, when retained)",
          "Mayo/Alice Step 2 'inventive concept' framework",
          "Defense motion withdrawal pattern (Dkt.107 OSC)",
        ],
        framing:
          "Lead with the patent - issued 2024, claims plain meaning, accused products contain the chemistry. Walk through Phillips hierarchy: claim language first, specification context, prosecution history. Reach §101 only as rebuttal - Adams's analysis conflates eligibility with construction. Save defense motion-withdrawal pattern for rebuttal - it undermines defense credibility. Don't lead with attorney conduct; lead with patent and chemistry.",
      },
      decisionTree: [
        {
          decision: "Retain technical expert by August 15, 2026?",
          leaning: "yes (must)",
          probability: 90,
          rationale:
            "Without expert, Ziari prior-art testimony goes uncontested. §102/§103 analyses lose by default. Single highest-priority strategic action.",
          shifters: [
            "Markman ruling broad → simplifies expert work, may delay",
            "Aenlle-Rocha narrow Markman → urgent retention before Markman appeal",
          ],
        },
        {
          decision:
            "File §1292(b) interlocutory appeal motion if §101 granted?",
          leaning: "yes",
          probability: 75,
          rationale:
            "§101 dismissal is potentially case-dispositive. De novo Federal Circuit review favors appellant. §1292(b) certification preserves appellate posture.",
          shifters: [
            "§101 denied → no §1292(b) needed",
            "Adverse Markman simultaneous → may consolidate appeals",
          ],
        },
        {
          decision:
            "Slutsky international deposition: pursue or pivot to documentary record?",
          leaning: "pursue",
          probability: 70,
          rationale:
            "§102(f) inventorship-defect rebuttal requires Slutsky testimony. Letter rogatory must be filed THIS WEEK. Without Slutsky, defense's inventorship attack stands.",
          shifters: [
            "Hague Convention rejection likely → pivot to documentary defense (lab notebooks, MIT TLO)",
            "Slutsky uncooperative → fall back on Mao deposition + documentary",
          ],
        },
      ],
      resourceTells: [
        {
          observation: "Patent narrowing (5 → 1)",
          pattern:
            "Originally asserted 5 patents; narrowed to '061 only after Markman briefing pressure",
          interpretation:
            "Plaintiff initially over-claimed. Narrowing reflects realistic assessment but late-stage timing weakens position. Defense uses pattern to argue overreach.",
          implications:
            "Anticipate defense framing as 'patent overreach' in trial. Pre-prepare narrative reframe: focus on '061 strength, deflect from withdrawn patents.",
        },
        {
          observation: "Plaintiff's failure to retain technical expert",
          pattern:
            "No technical expert retained 102 days before disclosure deadline",
          interpretation:
            "Resource constraint or strategic choice? If choice, dangerous - Ziari uncontested. If constraint, signals plaintiff under-resourced. Defense exploits either way.",
          implications:
            "Retain expert immediately. Treat as critical-priority. Monitor disclosure deadline.",
        },
        {
          observation: "Aggressive amendment pursuit",
          pattern:
            "Multiple amendment motions filed (Dkts.34, 64, 94) - repeated attempts to amend",
          interpretation:
            "Plaintiff persistence suggests they need narrative latitude. May indicate complaint defects they're trying to remedy mid-case.",
          implications:
            "Ziari report should anticipate any newly-pled theory. Track amendment rulings carefully - granted amendments expand defense scope.",
        },
      ],
      witnessSequencing: {
        predictedOrder: [
          {
            rank: 1,
            witness: "Liu (Inventor/Plaintiff Principal)",
            rationale:
              "Establishes patent inventiveness through inventor testimony. Anchors plaintiff's narrative.",
          },
          {
            rank: 2,
            witness: "Mao (Co-Inventor)",
            rationale:
              "Corroborates Liu testimony. Defense will impeach with Mao's prior YouTube video about 'accidental discovery.'",
          },
          {
            rank: 3,
            witness: "POSITA Expert (when retained)",
            rationale:
              "Claim construction support. Late because technical foundation requires inventor testimony first.",
          },
          {
            rank: 4,
            witness: "Damages Expert (TBD)",
            rationale: "Last. Depends on infringement found first.",
          },
        ],
        crossExamStrategy:
          "Press Liu on his prior testimony 'we did not invent the molecule' - invalidity-grade impeachment. Press Mao on 'accidental discovery' video - undermines deliberate-invention narrative. If both testify back-to-back, the impeachment compounds.",
      },
      pressurePoints: [
        {
          event: "§101 motion granted",
          impact:
            "Patent invalid. Case effectively ends. Federal Circuit appeal only.",
          likelihood: 30,
          ourMove:
            "Pre-draft Federal Circuit notice of appeal. §1292(b) certification motion. Preserve appellate record on Markman issues.",
        },
        {
          event: "Markman ruling narrow-construction grant",
          impact:
            "Infringement scope dramatically reduced. Settlement leverage shift to defense.",
          likelihood: 55,
          ourMove:
            "Re-anchor settlement demands. §1292(b) appeal preparation. Secondary patent assertion preparation if available.",
        },
        {
          event: "Slutsky letter rogatory denied",
          impact:
            "§102(f) inventorship-defect rebuttal compromised. Defense leverage rises.",
          likelihood: 35,
          ourMove:
            "Pivot to documentary inventorship record. Develop Mao deposition testimony for inventorship analysis.",
        },
        {
          event: "Defense files §285 exceptional-case fees motion",
          impact: "Plaintiff exposure to $1M+ fee award post-judgment.",
          likelihood: 45,
          ourMove:
            "Document litigation conduct contemporaneously. Avoid strategic missteps that support exceptional-case finding (e.g., late expert retention).",
        },
      ],
    },
  },
};

const DEPOSITION_DATA = {
  ...ILE_K_DEPS,
  juul: [
    {
      id: "pak",
      witness: "Quarry Pak",
      role: "SFUSD 30(b)(6) - Program Costs",
      party: "Plaintiff",
      takenBy: "Defendant (JLI)",
      date: "Dec 21, 2021",
      duration: "7 hrs",
      pages: 287,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.47:12-18",
          quote: "We don't have that information.",
          why: "Undermines SFUSD compensatory damages - JLI's centerpiece SJ admission",
          supports: "Defendant",
          linkTheory: "Sonner-Pak two-step",
        },
        {
          cite: "p.183:4-9",
          quote: "I am not aware of any document that quantifies staff time.",
          why: "Compounds the past-cost gap with separate documentation gap",
          supports: "Defendant",
          linkTheory: "Sonner bar",
        },
        {
          cite: "p.219:16-23",
          quote: "We track outcomes but not specific JUUL-attributable costs.",
          why: "Confirms category-wide measurement gap",
          supports: "Defendant",
          linkTheory: "Sonner bar",
        },
      ],
      designation: {
        complete: true,
        plaintiffRanges: [
          [14, 28],
          [156, 162],
        ],
        defendantRanges: [
          [47, 52],
          [183, 187],
          [219, 224],
        ],
        rule32Status:
          "Counter-designations filed · Plaintiff designated 22 pages, Defendant 14 pages",
        useCase: "MSJ centerpiece · Hearing Oct 31, 2025",
      },
      bankLinks: ["juul-pak-costs"],
      warLinks: [],
    },
    {
      id: "monsees",
      witness: "James Monsees",
      role: "Co-Founder, JUUL Labs",
      party: "Defendant",
      takenBy: "Plaintiff",
      date: "Mar 14, 2022",
      duration: "6.5 hrs",
      pages: 312,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.94:11-18",
          quote: "I reviewed all major marketing initiatives through 2018.",
          why: "Locks in marketing-level oversight - contradicts later 'wasn't involved' framing",
          supports: "Plaintiff",
          linkTheory: "Direct involvement / scienter",
        },
        {
          cite: "p.218:7-14",
          quote: "I do not recall specific approval of youth-targeted content.",
          why: "Contradicted by Q3 2018 youth-cohort metrics on his email distribution list (Dkt.3447 ¶47)",
          supports: "Plaintiff",
          linkTheory: "Direct involvement / RICO scienter",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [
          [94, 98],
          [218, 224],
        ],
        defendantRanges: [],
        rule32Status:
          "Plaintiff designations filed · Defendant counter-designations pending",
        useCase: "Trial PI cross + RICO punitive",
      },
      bankLinks: ["juul-monsees-marketing"],
      warLinks: [],
    },
    {
      id: "bowen",
      witness: "Adam Bowen",
      role: "Co-Founder, JUUL Labs",
      party: "Defendant",
      takenBy: "Plaintiff",
      date: "Apr 8, 2022",
      duration: "5 hrs",
      pages: 241,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.142:3-12",
          quote:
            "The nicotine salt formulation was an intentional engineering choice.",
          why: "Establishes deliberate engineering for high-peak delivery - undercuts harm-reduction framing",
          supports: "Plaintiff",
          linkTheory: "Design defect / failure to warn",
        },
        {
          cite: "p.197:9-15",
          quote: "Higher peak delivery makes the product more satisfying.",
          why: "Pharmacological standard treats this as more addictive - useful for cross translation",
          supports: "Plaintiff",
          linkTheory: "Design defect",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [],
        rule32Status:
          "Designations not yet filed · pending trial date confirmation",
        useCase: "Trial direct examination support",
      },
      bankLinks: ["juul-bowen-nicotine"],
      warLinks: [],
    },
    {
      id: "burns",
      witness: "Kevin Burns",
      role: "Former CEO, JUUL Labs",
      party: "Defendant",
      takenBy: "Plaintiff",
      date: "Sep 30, 2021",
      duration: "6 hrs",
      pages: 298,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.156:14-22",
          quote: "I attended all board meetings during my tenure.",
          why: "Locks in attendance at Sept 2018 board meeting where JUUL School deck was presented",
          supports: "Plaintiff",
          linkTheory: "RICO scienter / punitive",
        },
        {
          cite: "p.234:8-15",
          quote:
            "JUUL was designed for adult smokers - that was the entire mission.",
          why: "Public denial contradicted by Sept 2018 board deck (Dkt.3393)",
          supports: "Plaintiff",
          linkTheory: "Punitive / RICO scienter",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [
          [156, 160],
          [234, 239],
        ],
        defendantRanges: [],
        rule32Status:
          "Plaintiff designations filed · Defendant counter pending",
        useCase: "Trial PI cross",
      },
      bankLinks: ["juul-burns-juulschool"],
      warLinks: [],
    },
    {
      id: "matthews",
      witness: "Vincent Matthews",
      role: "SFUSD Superintendent",
      party: "Plaintiff",
      takenBy: "Defendant (JLI)",
      date: "Oct 12, 2021",
      duration: "4 hrs",
      pages: 178,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.83:4-11",
          quote:
            "I am not aware of any document that quantifies staff time spent on JUUL-related issues.",
          why: "Pairs with Pak admission - two SFUSD officials independently confirm documentation gap",
          supports: "Defendant",
          linkTheory: "Sonner-Pak two-step",
        },
      ],
      designation: {
        complete: true,
        plaintiffRanges: [[12, 18]],
        defendantRanges: [[83, 87]],
        rule32Status: "Both sides designated · 22 pages total",
        useCase: "MSJ supplemental",
      },
      bankLinks: ["juul-matthews-staff"],
      warLinks: [],
    },
    {
      id: "altria-30b6",
      witness: "Altria Group 30(b)(6)",
      role: "Altria Corporate Designee",
      party: "Defendant",
      takenBy: "Plaintiff",
      date: "Jul 22, 2025",
      duration: "7 hrs",
      pages: 325,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.198:5-14",
          quote:
            "Altria reviewed JLI's customer demographic data as part of standard pre-investment due diligence.",
          why: "Establishes Altria pre-investment knowledge for RICO enterprise theory · pairs with Dkt.3393",
          supports: "Plaintiff",
          linkTheory: "RICO enterprise / scienter",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [],
        rule32Status: "Designations pending · antitrust trial Sept 28 driver",
        useCase: "Antitrust trial · also RICO PI",
      },
      bankLinks: ["juul-altria-dd"],
      warLinks: [],
    },
    {
      id: "seliavski",
      witness: "Julia Seliavski",
      role: "Named Plaintiff",
      party: "Plaintiff",
      takenBy: "Defendant (JLI)",
      date: "May 17, 2024",
      duration: "5 hrs",
      pages: 213,
      status: "raw",
      keyAdmissions: [
        {
          cite: "p.42:8-17",
          quote: "A friend told me JUUL was just flavored vapor.",
          why: "Foundational testimony on initial use circumstances · age 16 first use",
          supports: "Both",
          linkTheory: "Causation / damages",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [],
        rule32Status: "Not yet designated · awaits remand to E.D. Va.",
        useCase: "Trial direct + cross",
      },
      bankLinks: [],
      warLinks: [],
    },
  ],
  musk: [
    {
      id: "altman",
      witness: "Sam Altman",
      role: "CEO, OpenAI",
      party: "Defendant",
      takenBy: "Plaintiff (Musk)",
      date: "Sep 12, 2025",
      duration: "8 hrs",
      pages: 402,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.78:14-22",
          quote: "I drafted the May 2015 founding email.",
          why: "Authenticates PX-003 - 'tech belongs to the world' founding charitable representation",
          supports: "Plaintiff",
          linkTheory: "Charitable trust / Count XVIII",
        },
        {
          cite: "p.187:8-17",
          quote: "OpenAI Inc. obtained 501(c)(3) status from the IRS.",
          why: "Locks in legal charitable status - undercuts later 'never was a charity' framing",
          supports: "Plaintiff",
          linkTheory: "Charitable trust",
        },
      ],
      designation: {
        complete: true,
        plaintiffRanges: [
          [78, 84],
          [187, 194],
        ],
        defendantRanges: [[245, 252]],
        rule32Status: "Both sides designated · used Day 2 trial",
        useCase: "Trial cross - IN USE",
      },
      bankLinks: ["musk-altman-charity", "musk-altman-msft"],
      warLinks: ["musk-warroom-current"],
    },
    {
      id: "brockman",
      witness: "Greg Brockman",
      role: "Co-Founder & President, OpenAI",
      party: "Defendant",
      takenBy: "Plaintiff (Musk)",
      date: "Oct 4, 2025",
      duration: "6 hrs",
      pages: 287,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.156:11-19",
          quote: "That was my journal - those are my words.",
          why: "Authenticates PX-163 - 'wrong to steal non-profit from Elon'",
          supports: "Plaintiff",
          linkTheory: "Charitable trust / Count XVIII",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [[156, 162]],
        defendantRanges: [],
        rule32Status: "Designations being finalized for Day 4 trial use",
        useCase: "Trial cross Day 4",
      },
      bankLinks: ["musk-brockman-journal"],
      warLinks: ["musk-warroom-next"],
    },
    {
      id: "sutskever",
      witness: "Ilya Sutskever",
      role: "Former Chief Scientist, OpenAI",
      party: "Defendant",
      takenBy: "Plaintiff (Musk)",
      date: "Aug 2025 (Vol. 1) · Reopened pursuant to Dkt.324",
      duration: "5 hrs (Vol. 1)",
      pages: 218,
      status: "raw",
      keyAdmissions: [
        {
          cite: "p.94:3-11 (Vol. 1)",
          quote: "I was aware of the LP conversion discussions in 2018.",
          why: "Knowledge of conversion timing · supports plaintiff Phase 1 case",
          supports: "Plaintiff",
          linkTheory: "Charitable trust",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [],
        rule32Status:
          "⚡ SECOND DEPOSITION ORDERED (Dkt.324) · Brockman memo production · designations cannot be finalized until Vol. 2 complete",
        useCase: "Trial designation pending Vol. 2",
      },
      bankLinks: [],
      warLinks: ["musk-warroom-upcoming"],
    },
    {
      id: "kscott",
      witness: "Kevin Scott",
      role: "CTO, Microsoft",
      party: "Defendant (Microsoft)",
      takenBy: "Plaintiff (Musk)",
      date: "Nov 18, 2025",
      duration: "4 hrs",
      pages: 165,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.87:14-21",
          quote: "PX-230 is an email I sent in March 2018.",
          why: "Authenticates Microsoft CTO email questioning donor terms 16 months pre-investment · Count XIX aiding & abetting",
          supports: "Plaintiff",
          linkTheory: "Aiding & abetting / Count XIX",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [[87, 94]],
        defendantRanges: [],
        rule32Status:
          "Plaintiff designations filed · Microsoft counter pending",
        useCase: "Trial cross Day 5",
      },
      bankLinks: [],
      warLinks: ["musk-warroom-day5"],
    },
    {
      id: "musk-self",
      witness: "Elon Musk",
      role: "Plaintiff",
      party: "Plaintiff",
      takenBy: "Defendant",
      date: "Dec 8, 2025",
      duration: "7 hrs",
      pages: 340,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.142:8-15",
          quote: "My reply was 'Yup' - I don't remember the specific context.",
          why: "Authenticates DX-516 · defense impeachment foundation · January 2016 less-open agreement",
          supports: "Defendant",
          linkTheory: "SOL / Acquiescence",
        },
        {
          cite: "p.211:4-10",
          quote: "I told Sam to characterize me as supportive in March 2019.",
          why: "Authenticates DX-862 · acquiescence to LP conversion · SOL trigger evidence",
          supports: "Defendant",
          linkTheory: "SOL / Laches",
        },
      ],
      designation: {
        complete: true,
        plaintiffRanges: [[58, 62]],
        defendantRanges: [
          [142, 148],
          [211, 217],
        ],
        rule32Status: "Used Day 2 trial · all designations exhausted",
        useCase: "Trial cross - USED Day 2",
      },
      bankLinks: ["musk-musk-supportive", "musk-musk-yup"],
      warLinks: ["musk-warroom-current"],
    },
  ],
  neuro: [
    {
      id: "liu",
      witness: "Dr. Guosong Liu",
      role: "Named Inventor, '061 Patent",
      party: "Plaintiff",
      takenBy: "Defendant (Mando)",
      date: "Mar 18, 2026",
      duration: "6 hrs",
      pages: 298,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.318:25-p.319:8",
          quote: "We did not invent the molecule.",
          why: "§103 obviousness · §102(f) inventorship · undermines patent validity",
          supports: "Defendant",
          linkTheory: "Invalidity",
        },
        {
          cite: "p.142:14-21",
          quote: "Mao performed the actual synthesis under my direction.",
          why: "Pairs with Mao YouTube Ex.6 + Mao depo crediting Slutsky · §102(f) Slutsky omission",
          supports: "Defendant",
          linkTheory: "§102(f) Invalidity",
        },
        {
          cite: "p.247:11-19",
          quote: "The specification describes in vivo formation accurately.",
          why: "Locks in §101 ineligibility self-admission · col.4 lines 12-18",
          supports: "Defendant",
          linkTheory: "§101 Ineligibility",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [
          [142, 148],
          [247, 253],
          [318, 322],
        ],
        rule32Status:
          "Defendant designations filed for §101 motion · plaintiff counter pending",
        useCase: "§101 motion · trial cross",
      },
      bankLinks: [
        "neuro-liu-inventorship",
        "neuro-liu-prior-art",
        "neuro-liu-spec",
      ],
      warLinks: ["neuro-witness-liu"],
    },
    {
      id: "mao",
      witness: "Dr. Fei Mao",
      role: "Lab Collaborator (not named on '061)",
      party: "Defendant",
      takenBy: "Defendant (Mando)",
      date: "Apr 22, 2026",
      duration: "4 hrs",
      pages: 184,
      status: "admissions",
      keyAdmissions: [
        {
          cite: "p.78:9-17",
          quote: "I identified the optimal synthesis pathway.",
          why: "Inventive contribution · pairs with YouTube Ex.6 · supports §102(f)",
          supports: "Defendant",
          linkTheory: "§102(f) Invalidity",
        },
      ],
      designation: {
        complete: false,
        plaintiffRanges: [],
        defendantRanges: [[78, 84]],
        rule32Status:
          "Defendant designations filed · plaintiff counter pending",
        useCase: "§102(f) trial cross",
      },
      bankLinks: ["neuro-mao-credit"],
      warLinks: ["neuro-witness-mao"],
    },
  ],
};

const OUTSTANDING_DEPOSITIONS = {
  ...ILE_K_OUT,
  juul: [
    {
      witness: "Nicholas Pritzker",
      role: "JUUL Director (Antitrust Defendant)",
      party: "Defendant",
      why: "Director-level antitrust defendant - separate from PI track. Antitrust class certified Feb 26, 2026 (Dkt.635). Trial Sept 28, 2026.",
      deadlineEvent: "Antitrust trial",
      deadlineDate: "Sep 28, 2026",
      daysToDeadline: 146,
      complications:
        "Schedule subpoena coordination · separate from MDL PI 30(b)(6) work · estimate 4 hr depo + prep",
      urgency: "high",
      action: "Notice deposition within 30 days · prep package needed",
    },
    {
      witness: "Riaz Valani",
      role: "JUUL Director (Antitrust Defendant)",
      party: "Defendant",
      why: "Director-level antitrust defendant - Sherman §1 horizontal allocation testimony · class trial Sept 28",
      deadlineEvent: "Antitrust trial",
      deadlineDate: "Sep 28, 2026",
      daysToDeadline: 146,
      complications: "Coordinate with Pritzker depo · same defense team",
      urgency: "high",
      action: "Notice deposition within 30 days · joint scheduling possible",
    },
    {
      witness: "Dr. Bonnie Halpern-Felsher",
      role: "UCSF Professor - Plaintiff expert (defense will take)",
      party: "Plaintiff",
      why: "Defense-noticed depo of plaintiff expert · 9/2021 youth nicotine awareness study (75% gap data) is plaintiff's centerpiece exhibit · defense will attack methodology",
      deadlineEvent: "Antitrust trial",
      deadlineDate: "Sep 28, 2026",
      daysToDeadline: 146,
      complications:
        "Plaintiff prep required · Daubert motion expected pre-trial · estimate 6 hr defense exam · Coordinate with PI track exposure",
      urgency: "high",
      action:
        "Plaintiff prep session within 21 days · anticipate Daubert challenge",
    },
    {
      witness: "SFUSD Program Director (additional 30(b)(6) topic)",
      role: "SFUSD second 30(b)(6) - Program Implementation",
      party: "Plaintiff",
      why: "Beyond Pak's cost testimony · defense seeking program-implementation 30(b)(6) on prevention efforts · supports Sonner-bar argument by showing remedies in place",
      deadlineEvent: "MSJ ruling / Antitrust trial",
      deadlineDate: "Sep 28, 2026",
      daysToDeadline: 146,
      complications:
        "Identify designee · prep to NOT compound Pak admissions · risk of further Sonner-bar testimony",
      urgency: "medium",
      action:
        "Identify and prep designee within 45 days · rehearse problem topics",
    },
  ],
  musk: [],
  neuro: [
    {
      witness: "Dr. Inna Slutsky",
      role: "Tel Aviv University Researcher",
      party: "Defendant",
      why: "Foundational evidence for inventorship-defect counterclaim · centerpiece of §102(f) defense",
      deadlineEvent: "Fact Discovery Cut-Off",
      deadlineDate: "Sep 11, 2026",
      daysToDeadline: 129,
      complications:
        "⚡ SUBPOENA TO TEL AVIV UNIVERSITY OR INTERNATIONAL DEPOSITION COMMISSION LETTER REQUIRED. Lead time for Hague Convention letter rogatory: 90-120 days minimum. This deposition must be initiated within the next two weeks or it will not be complete before fact discovery closes.",
      urgency: "critical",
      action:
        "FILE LETTER ROGATORY THIS WEEK · alternative: voluntary appearance under § 1782 · escalate to partner",
    },
    {
      witness: "MIT TLO Custodian of Records",
      role: "Records custodian for MIT Provisional 60/510,945",
      party: "Defendant",
      why: "Authenticate MIT Provisional · inventorship date · prior art analysis",
      deadlineEvent: "Fact Discovery Cut-Off",
      deadlineDate: "Sep 11, 2026",
      daysToDeadline: 129,
      complications:
        "Domestic Rule 30(b)(6) subpoena · MIT TLO process · estimate 30+ days lead time including authentication response",
      urgency: "high",
      action: "Issue subpoena within 30 days",
    },
    {
      witness: "Wei Zhang",
      role: "Mando Director of Licensing",
      party: "Defendant",
      why: "TMLA scope contradiction · Impeachment Bank seq. ready · 30(b)(6) topic",
      deadlineEvent: "Fact Discovery Cut-Off",
      deadlineDate: "Sep 11, 2026",
      daysToDeadline: 129,
      complications:
        "30(b)(6) notice required · standard scheduling · estimate 6 hr depo",
      urgency: "medium",
      action: "Notice 30(b)(6) within 60 days",
    },
    {
      witness: "Kappa Engineer Representative",
      role: "Mando Manufacturing 30(b)(6)",
      party: "Defendant",
      why: "Country-of-origin testimony · Lanham Act §43(a) foundation",
      deadlineEvent: "Fact Discovery Cut-Off",
      deadlineDate: "Sep 11, 2026",
      daysToDeadline: 129,
      complications: "30(b)(6) topic notice · CoA + customs records foundation",
      urgency: "medium",
      action: "Notice 30(b)(6) within 60 days",
    },
    {
      witness: "Dr. Robert Liu",
      role: "Neurocentria's Named Inventor - Plaintiff witness (defense will take)",
      party: "Plaintiff",
      why: "Defense-noticed depo of named inventor · §102(f) inventorship questions · MIT collaboration history · centerpiece of defense prior-art and inventorship attack",
      deadlineEvent: "Fact Discovery Cut-Off",
      deadlineDate: "Sep 11, 2026",
      daysToDeadline: 129,
      complications:
        "Plaintiff prep required · multiple sensitive topics: MIT, CN1200366A, lab-notebook chronology · estimate 8+ hr defense exam · Liu's testimony anchors the validity defense",
      urgency: "critical",
      action:
        "Plaintiff prep package within 14 days · rehearse inventorship narrative · prepare for hostile MIT-collaboration questioning",
    },
    {
      witness: "Plaintiff's Technical Expert (TBD)",
      role: "Plaintiff infringement / claim-construction expert",
      party: "Plaintiff",
      why: "Not yet named · expected Markman testimony + infringement opinion · defense will move to exclude under Daubert",
      deadlineEvent: "Expert disclosure deadline",
      deadlineDate: "Aug 15, 2026",
      daysToDeadline: 102,
      complications:
        "Expert selection critical · post-Markman scope of work depends on claim construction · coordinate with §101 ruling timing",
      urgency: "high",
      action:
        "Identify and retain technical expert within 21 days · pre-Markman engagement preferred",
    },
  ],
};

const SETTLEMENT_DATA = {
  ...ILE_K_SET,
  juul: {
    context:
      "JUUL MDL is the most settled-against matter in this binder. Class actions resolved 2022-2024. Opt-out PI cases (Seliavski cohort) now under SJ submission. Antitrust class trial Sept 28, 2026 creates two-front pressure on JLI's ceiling.",
    ledger: [
      {
        date: "Dec 2022",
        ts: "2022-12-15",
        type: "settlement",
        side: "JLI Class",
        amount: 1700000000,
        label: "JLI personal injury class settlement - preliminary approval",
        linkedTo: "Dkt.4023",
        note: "Master settlement covering ~10,000 PI claims · sets per-plaintiff baseline",
        isAnchor: true,
      },
      {
        date: "Jul 2023",
        ts: "2023-07-08",
        type: "settlement",
        side: "Altria Class",
        amount: 235000000,
        label: "Altria class settlement - preliminary approval",
        linkedTo: "Dkt.4112",
        note: "Altria-only fund · separate from JLI claims",
        isAnchor: true,
      },
      {
        date: "Sep 2023",
        ts: "2023-09-22",
        type: "approval",
        side: "Court",
        amount: 1700000000,
        label: "JLI class settlement - final approval",
        linkedTo: "Dkt.4156",
        note: "Judge Orrick approves over limited objections",
        isAnchor: true,
      },
      {
        date: "Mar 2024",
        ts: "2024-03-14",
        type: "approval",
        side: "Court",
        amount: 235000000,
        label: "Altria class settlement - final approval",
        linkedTo: "Dkt.4287",
        note: "Bridge for opt-outs · not a release for opt-out PIs",
        isAnchor: true,
      },
      {
        date: "Aug 2024",
        ts: "2024-08-20",
        type: "demand",
        side: "Plaintiff",
        amount: 250000,
        label: "Seliavski initial demand (pre-litigation)",
        linkedTo: "Pre-suit correspondence",
        note: "Aspirational anchor · 5x B.B. minor's compromise · before SJ posture clarified",
      },
      {
        date: "Feb 2025",
        ts: "2025-02-11",
        type: "offer",
        side: "JLI",
        amount: 35000,
        label: "JLI opening opt-out offer",
        linkedTo: "Dkt.4302 (mediation statement)",
        note: "Aligned to documented past costs · Sonner anchor visible",
      },
      {
        date: "Oct 2025",
        ts: "2025-10-31",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "MSJ Hearing complete · under submission",
        linkedTo: "Dkt.4341",
        note: "Both sides paused active negotiations pending ruling",
        isMilestone: true,
      },
      {
        date: "Feb 2026",
        ts: "2026-02-26",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Antitrust class certified (Dkt.635)",
        linkedTo: "3:20-cv-02345-WHO",
        note: "Cross-pressure: defense ceiling for opt-out PIs compresses · two-front war begins",
        isMilestone: true,
      },
      {
        date: "Mar 2026",
        ts: "2026-03-15",
        type: "movement",
        side: "Plaintiff",
        amount: 175000,
        label: "Reduced Seliavski demand · post-MSJ posture",
        linkedTo: "Mediation request",
        note: "Acknowledges Sonner risk on SFUSD · still 5x JLI offer",
      },
    ],
    comparables: [
      {
        id: "opioid-mdl",
        name: "In re National Prescription Opiate Litigation MDL 2804",
        court: "N.D. Ohio (Polster)",
        year: 2022,
        type: "settlement",
        amount: 26000000000,
        theory: "Public nuisance",
        outcome: "Distributors + J&J global settlement framework",
        analogy: 9,
        side: "Plaintiff",
        track: "sfusd",
        ai: "Strong template: same MDL coordinated structure, same public nuisance theory at municipal scale. Sets ceiling for SFUSD-style claims at the largest scale ever achieved.",
      },
      {
        id: "sf-purdue",
        name: "City and County of San Francisco v. Purdue Pharma",
        court: "N.D. Cal. (Breyer)",
        year: 2022,
        type: "verdict",
        amount: 87500000,
        theory: "Public nuisance bench trial",
        outcome: "Plaintiff verdict against Walgreens",
        analogy: 10,
        side: "Plaintiff",
        track: "sfusd",
        ai: "Identical court (N.D. Cal.) and identical municipal-plaintiff posture (San Francisco entity). Public nuisance framework directly applicable. Highest comparable for SFUSD.",
      },
      {
        id: "jj-cal-opioid",
        name: "Johnson & Johnson California Opioid Trial",
        court: "Cal. Sup. Ct. (Wilson)",
        year: 2021,
        type: "verdict",
        amount: 0,
        theory: "Public nuisance",
        outcome: "Defense verdict - public nuisance theory rejected",
        analogy: 7,
        side: "Defendant",
        track: "sfusd",
        ai: "Cautionary comparable. CA state court rejected public nuisance theory citing Sonner-like adequate-remedy reasoning. JLI will cite this heavily on SJ.",
      },
      {
        id: "bb-minor",
        name: "B.B. minor's compromise (within JUUL MDL)",
        court: "N.D. Cal. (Orrick) Dkt.3716",
        year: 2024,
        type: "settlement",
        amount: 175000,
        theory: "Individual nicotine PI",
        outcome: "Confidential minor's compromise approved",
        analogy: 9,
        side: "Plaintiff",
        track: "seliavski",
        ai: "Direct internal benchmark - same MDL, similar age at first use, similar injury profile. Strongest single Seliavski comparable. Plaintiff floor anchor.",
      },
      {
        id: "pesce-msj",
        name: "Pesce v. JLI MSJ ruling",
        court: "N.D. Cal. (Orrick) (within MDL)",
        year: 2024,
        type: "ruling",
        amount: 0,
        theory: "Individual PI causation",
        outcome: "Partial SJ - case narrowed on causation expert gap",
        analogy: 8,
        side: "Defendant",
        track: "seliavski",
        ai: "Procedural benchmark. Demonstrates Orrick's willingness to grant SJ where causation expert is missing. Direct read on Seliavski risk if expert not retained pre-remand.",
      },
      {
        id: "sfusd-this",
        name: "SFUSD-JLI prior settlement (this MDL track)",
        court: "N.D. Cal. (Orrick)",
        year: 2022,
        type: "settlement",
        amount: 12450000,
        theory: "Public nuisance",
        outcome: "Pre-verdict settlement · undisclosed",
        analogy: 10,
        side: "Plaintiff",
        track: "sfusd",
        ai: "This case's own predecessor track. Material amount sets ceiling for additional municipal claims and informs comparable district plaintiffs.",
      },
      {
        id: "propane-antitrust",
        name: "In re Pre-filled Propane Tank Antitrust Litigation",
        court: "W.D. Mo.",
        year: 2023,
        type: "settlement",
        amount: 21000000,
        theory: "Sherman §1 horizontal allocation",
        outcome: "Direct purchaser class settlement",
        analogy: 6,
        side: "Plaintiff",
        track: "antitrust",
        ai: "Same legal theory (Sherman §1 horizontal). Different industry, different scale. Useful for normalization analysis.",
      },
      {
        id: "visa-mc",
        name: "Visa/Mastercard interchange MDL 1720",
        court: "E.D.N.Y.",
        year: 2024,
        type: "settlement",
        amount: 5540000000,
        theory: "Sherman §1",
        outcome: "Direct purchaser class settlement",
        analogy: 5,
        side: "Plaintiff",
        track: "antitrust",
        ai: "Largest direct-purchaser antitrust class settlement template. Different facts but valuable benchmark for class-scale recovery.",
      },
      {
        id: "swisher",
        name: "Swisher International Tobacco Settlement",
        court: "Multi-jurisdictional",
        year: 2022,
        type: "settlement",
        amount: 115000000,
        theory: "Tobacco youth marketing",
        outcome: "AG settlement framework",
        analogy: 6,
        side: "Plaintiff",
        track: "sfusd",
        ai: "Tobacco-specific youth marketing framework. Lower analogy because AG-driven not municipal, but theory overlap supports SFUSD anchor.",
      },
    ],
    batna: {
      plaintiff: {
        trialCost: 8000000,
        probWeighted: 12000000,
        floorDefault: 4000000,
        note: "Two-track defense (PI + antitrust) means plaintiff trial cost burden is sustainable through consolidation. Floor reflects rejection of below-class-baseline offers.",
      },
      defendant: {
        trialCost: 25000000,
        riskAssessment: 50000000,
        ceilingDefault: 35000000,
        note: "Two-front war premium: simultaneous remand trials (CA/FL/TX) + antitrust class trial Sept 28, 2026 doubles cost burn rate. JLI ceiling compressed materially by parallel exposure.",
      },
      twoFront: {
        active: true,
        description:
          "Two-front war: MDL PI remand trials (CA, FL, TX) simultaneously with antitrust class trial Sept 28, 2026 in N.D. Cal.",
        premium: 12000000,
        note: "Defense cost asymmetry compresses JLI's ceiling by ~$12M relative to single-front benchmarks. This is plaintiff's structural leverage.",
      },
      analysis:
        "Settlement zone EXISTS for Seliavski-cohort opt-out PIs at consolidated treatment. Plaintiff floor ~$4M (above class baseline + opt-out premium). Defendant ceiling ~$35M (compressed by two-front war). Midpoint ~$19.5M for full Seliavski cohort. Individual settlement scales from $35K-$300K per plaintiff depending on injury severity. SJ ruling will move both anchors materially within days of issuance.",
      ai: "⚡ Window of opportunity: SJ ruling imminent. Pre-ruling settlements protect both sides from ruling tail risk. Post-ruling, the losing side faces accelerated leverage compression. Recommend mediation initiation in next 14 days.",
    },
  },
  musk: {
    context:
      "Phase 1 trial in progress. No active settlement discussions during trial. Phase 2 antitrust counterclaims trigger if plaintiff prevails - that creates the leverage cliff for post-Phase 1 negotiations.",
    ledger: [
      {
        date: "Feb 2024",
        ts: "2024-02-29",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Original suit filed (San Francisco state court)",
        linkedTo: "Original complaint",
        note: "Voluntarily dismissed",
        isMilestone: true,
      },
      {
        date: "Apr 2024",
        ts: "2024-04-30",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Refiled in N.D. Cal. as Musk v. Altman et al.",
        linkedTo: "Dkt.1",
        note: "Reassigned to Judge YGR",
        isMilestone: true,
      },
      {
        date: "Aug 2024",
        ts: "2024-08-15",
        type: "demand",
        side: "Plaintiff",
        amount: 0,
        label:
          "Initial framework demand: structural restructuring + return to non-profit",
        linkedTo: "Dkt.45 (Musk's letter brief)",
        note: "Equitable demand · no monetary anchor",
      },
      {
        date: "Mar 2025",
        ts: "2025-03-04",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Preliminary injunction DENIED (YGR)",
        linkedTo: "Dkt.213",
        note: "Defense-favorable equity signal · settlement leverage shifted to OpenAI",
        isMilestone: true,
      },
      {
        date: "Oct 2025",
        ts: "2025-10-08",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Phase 1 trial set · two-phase order entered",
        linkedTo: "Pretrial Order No.3",
        note: "Phase 2 antitrust stayed pending Phase 1",
      },
      {
        date: "Apr 2026",
        ts: "2026-04-22",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Trial begins · Phase 1 in progress",
        linkedTo: "Day 1 of trial",
        note: "Settlement discussions paused during trial",
        isMilestone: true,
      },
    ],
    comparables: [
      {
        id: "ncaa-house",
        name: "NCAA v. House (NIL settlement)",
        court: "N.D. Cal. (Wilken)",
        year: 2024,
        type: "settlement",
        amount: 2780000000,
        theory: "Multi-class structural reform + monetary",
        outcome: "Antitrust class settlement with structural injunctive relief",
        analogy: 6,
        side: "Plaintiff",
        track: "trust",
        ai: "Structural-reform-plus-money framework parallel. Different theory but useful template for hybrid charitable-structural remedy if Phase 1 favors plaintiff.",
      },
      {
        id: "theranos-civil",
        name: "SEC v. Theranos / Holmes",
        court: "N.D. Cal.",
        year: 2018,
        type: "civil enforcement",
        amount: 1500000,
        theory: "Charitable misrepresentation analog",
        outcome: "Civil penalty + officer bar",
        analogy: 4,
        side: "Plaintiff",
        track: "trust",
        ai: "Misrepresentation analogy only. Different remedy structure. Useful for narrative framing on representations to donors.",
      },
      {
        id: "wikimedia-charity",
        name: "Charitable Trust Enforcement Pattern (CA AG actions)",
        court: "Various Cal. Sup. Ct.",
        year: 2020,
        type: "AG enforcement",
        amount: 0,
        theory: "Cy pres + restoration",
        outcome: "Restoration to charitable purpose with continued operations",
        analogy: 8,
        side: "Plaintiff",
        track: "trust",
        ai: "Cy pres restoration framework - most directly applicable remedy structure. Suggests Phase 1 plaintiff verdict supports settlement preserving research mission with structural reforms.",
      },
      {
        id: "hewlett-foundation",
        name: "Donor Restitution Cases under Cal. §17510.8",
        court: "CA state actions",
        year: 2019,
        type: "restitution",
        amount: 25000000,
        theory: "Charitable solicitations strict-liability",
        outcome: "Donor restitution awards",
        analogy: 7,
        side: "Plaintiff",
        track: "trust",
        ai: "§17510.8 directly relevant to pending YGR ruling on statute applicability. Strong template for monetary remedy if statute applied.",
      },
      {
        id: "ag-decline",
        name: "CA Attorney General declined to intervene (this case)",
        court: "CA AG",
        year: 2025,
        type: "decline",
        amount: 0,
        theory: "Charitable trust enforcement",
        outcome: "AG investigated and declined to join Musk's suit",
        analogy: 9,
        side: "Defendant",
        track: "trust",
        ai: "Strongest defense leverage point - the state authority charged with charitable trust enforcement reviewed and declined. Defense will weigh this in any settlement framework.",
      },
      {
        id: "ms-aml",
        name: "Microsoft past A&A settlements",
        court: "Various federal/state",
        year: 2023,
        type: "settlement",
        amount: 50000000,
        theory: "Knowing-assistance benchmarks",
        outcome: "Average MS A&A settlement range",
        analogy: 5,
        side: "Plaintiff",
        track: "msft",
        ai: "Microsoft baseline for aiding-and-abetting settlements. Not directly analogous (different theories) but useful for MS internal benchmark.",
      },
    ],
    batna: {
      plaintiff: {
        trialCost: 15000000,
        probWeighted: 8000000000,
        floorDefault: 500000000,
        note: "Trial cost reflects Phase 1 + appeals. Probability-weighted outcome reflects equitable remedies (constructive trust on for-profit equity). Floor reflects minimum acceptable structural remedy.",
      },
      defendant: {
        trialCost: 25000000,
        riskAssessment: 15000000000,
        ceilingDefault: 2000000000,
        note: "OpenAI defense cost reflects Phase 1 + Phase 2 antitrust counterclaims. Risk assessment reflects equity disgorgement worst case. Ceiling reflects acceptable monetary substitution.",
      },
      twoFront: {
        active: true,
        description:
          "Two-phase structure: Phase 1 verdict triggers Phase 2 antitrust counterclaims if plaintiff prevails. Cascading liability creates non-linear exposure for defendants.",
        premium: 5000000000,
        note: "If Phase 1 plaintiff verdict, Phase 2 immediate · if Phase 1 defense verdict, both sides recalibrate. Settlement window before verdict avoids tail risk for both.",
      },
      analysis:
        "Settlement zone is theoretical pre-verdict. Phase 1 verdict will create immediate, sharp settlement leverage. Pre-verdict structural-reform settlement (e.g., charitable foundation governance + research-mission preservation) is the only path to deal that doesn't require monetary disgorgement at OpenAI-fatal scale.",
      ai: "⚡ Phase 1 verdict is the leverage cliff. Pre-verdict settlement requires both sides to accept verdict-uncertainty. Post-verdict, the losing side faces accelerated pressure. Microsoft's separate aiding-and-abetting exposure (Count XIX) is the partition where MS may settle independently regardless of OpenAI position.",
    },
  },
  neuro: {
    context:
      "Pre-trial · trial March 9, 2027 · mediation ordered for December 31, 2026. §101 motion ruling pending - that ruling will create the largest single leverage swing in the case.",
    ledger: [
      {
        date: "Late 2024",
        ts: "2024-11-15",
        type: "demand",
        side: "Plaintiff",
        amount: 18000000,
        label: "Pre-litigation demand · pre-suit licensing offer rejected",
        linkedTo: "Pre-suit correspondence",
        note: "5% royalty on accused sales · Mando declined",
      },
      {
        date: "Mar 2025",
        ts: "2025-03-12",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Suit filed in C.D. Cal.",
        linkedTo: "Dkt.1",
        note: "Patent + Lanham + DTSA claims",
        isMilestone: true,
      },
      {
        date: "Jul 2025",
        ts: "2025-07-28",
        type: "offer",
        side: "Mando",
        amount: 500000,
        label: "Mando opening offer · nuisance value",
        linkedTo: "Settlement letter",
        note: "Aligned to defense theory: §101 + §102 invalidity",
      },
      {
        date: "Q4 2025",
        ts: "2025-11-30",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Mando files §101 + §102 invalidity motion",
        linkedTo: "Dkt.85",
        note: "Motion pending · binary leverage event",
        isMilestone: true,
      },
      {
        date: "Q1 2026",
        ts: "2026-02-15",
        type: "docket",
        side: "Court",
        amount: 0,
        label: "Court orders mediation by Dec 31, 2026",
        linkedTo: "Dkt.124",
        note: "Compulsory ADR · standing order from Aenlle-Rocha",
        isMilestone: true,
      },
      {
        date: "Mar 2026",
        ts: "2026-03-31",
        type: "docket",
        side: "Plaintiff",
        amount: 0,
        label: "Adams declaration filed (Dkt.151)",
        linkedTo: "Dkt.151",
        note: "Clarifying declaration on chain of title",
      },
    ],
    comparables: [
      {
        id: "mg-lthreonate",
        name: "Magnesium L-Threonate licensing benchmark",
        court: "Industry data",
        year: 2022,
        type: "licensing",
        amount: 6000000,
        theory: "Patent licensing reasonable royalty",
        outcome: "Industry reference license rate",
        analogy: 9,
        side: "Plaintiff",
        track: "patent",
        ai: "Direct industry analogue. Reasonable-royalty rate supports plaintiff anchor in 4-7% range.",
      },
      {
        id: "cd-cal-pharma",
        name: "In re Pharma Patent Settlements (C.D. Cal. 2022-2024)",
        court: "C.D. Cal. average",
        year: 2023,
        type: "settlement",
        amount: 12000000,
        theory: "Patent infringement settlement",
        outcome: "Average single-patent C.D. Cal. settlement",
        analogy: 8,
        side: "Plaintiff",
        track: "patent",
        ai: "Same court average. Directly applicable to negotiation midpoint anchor.",
      },
      {
        id: "halo-willful",
        name: "Halo Electronics v. Pulse",
        court: "S. Ct.",
        year: 2016,
        type: "ruling",
        amount: 0,
        theory: "Willfulness standard",
        outcome: "Lowered willfulness bar for §284 enhancement",
        analogy: 7,
        side: "Plaintiff",
        track: "patent",
        ai: "Standard-of-proof anchor. Plaintiff cites for willfulness premium · defense for limit on enhancement.",
      },
      {
        id: "alice-corp",
        name: "Alice Corp. v. CLS Bank",
        court: "S. Ct.",
        year: 2014,
        type: "ruling",
        amount: 0,
        theory: "§101 ineligibility framework",
        outcome: "Two-step framework applied broadly",
        analogy: 8,
        side: "Defendant",
        track: "patent",
        ai: "Defense leverage. If §101 motion granted under Alice/Mayo framework, plaintiff's leverage collapses materially.",
      },
      {
        id: "lanham-disgorgement",
        name: "Lanham Act §1117 disgorgement settlements",
        court: "Various federal",
        year: 2023,
        type: "settlement",
        amount: 8000000,
        theory: "False designation of origin",
        outcome: "Disgorgement-based settlement framework",
        analogy: 7,
        side: "Plaintiff",
        track: "lanham",
        ai: "Country-of-origin claim benchmark. Supports plaintiff disgorgement anchor at $8-12M range.",
      },
      {
        id: "dtsa-trade",
        name: "DTSA willful misappropriation settlement range",
        court: "C.D. Cal. average",
        year: 2023,
        type: "settlement",
        amount: 5000000,
        theory: "DTSA actual + exemplary",
        outcome: "Average willful DTSA settlement",
        analogy: 6,
        side: "Plaintiff",
        track: "dtsa",
        ai: "DTSA exemplary-damages framework. Useful for trade-secret claim anchor though discovery still needed.",
      },
    ],
    batna: {
      plaintiff: {
        trialCost: 4000000,
        probWeighted: 15000000,
        floorDefault: 6000000,
        note: "Single-patent case · trial cost manageable. Probability-weighted outcome reflects §101 risk + willfulness upside. Floor reflects minimum acceptable royalty + Lanham damages.",
      },
      defendant: {
        trialCost: 4000000,
        riskAssessment: 35000000,
        ceilingDefault: 15000000,
        note: "Defense cost similar to plaintiff. Risk assessment reflects willful patent (3x) + Lanham + DTSA cumulative exposure. Ceiling reflects acceptable disposition without admission.",
      },
      twoFront: {
        active: false,
        description:
          "Single-front patent litigation. No parallel proceedings creating cost asymmetry.",
        premium: 0,
        note: "§101 ruling is the single biggest leverage event. Pre-ruling vs post-ruling negotiations are categorically different.",
      },
      analysis:
        "Pre-§101-ruling settlement zone: $6M-$15M with midpoint ~$10M. Post-ruling-grant: defense ceiling collapses to nuisance value. Post-ruling-deny: plaintiff floor rises to $10M+ range. Mediation ordered for Dec 31, 2026 should be timed strategically relative to §101 ruling - both sides may benefit from pre-ruling resolution to avoid leverage cliff.",
      ai: "⚡ §101 ruling is the leverage event. Court typically rules within 90 days of full briefing. Pre-ruling settlement protects both sides from binary outcome. Post-ruling, the side that lost the motion faces accelerated pressure. Recommend mediation initiation 30+ days before ordered date to allow strategic timing.",
    },
  },
};

const WAR_ROOM_DATA = {
  ...ILE_K_WR,
  musk: {
    trialDay: 3,
    trialDate: "April 30, 2026",
    courtroom: "Courtroom 1, Oakland Division",
    judge: "Judge Yvonne Gonzalez Rogers",
    currentWitness: {
      name: "Elon Musk",
      role: "Plaintiff",
      status: "Cross continuing from Day 2",
      direct:
        "Established Musk donated ~$44M based on explicit representation that 'tech belongs to the world' (PX-003 founding email).",
      crossHammered: [
        {
          topic: "Tesla merger bid (2017)",
          effect:
            "defense argued Musk wanted to absorb OpenAI into Tesla - competitive, not charitable, motive",
        },
        {
          topic: "'Yup' reply to less-open email (DX-516, Jan 2016)",
          effect:
            "defense established Musk affirmatively agreed OpenAI should become 'less open' three years before LP conversion",
        },
        {
          topic: "xAI competitive motive",
          effect:
            "defense suggested current litigation is competitive harassment - xAI funding round filing timeline",
        },
      ],
      crossRemaining: [
        {
          topic: "DX-862 'Tell them I'm supportive' (Mar 2019)",
          exhibit: "DX-862",
          exDesc: "Musk's email to Altman re for-profit transition messaging",
          objection:
            "FRE 408 if framed as settlement/positioning · be ready to call out non-settlement context",
          priority: "HIGH",
        },
        {
          topic: "September 2020 'captured by Microsoft' tweet",
          exhibit: "DX-204",
          exDesc: "Public tweet establishing constructive knowledge for SOL",
          objection:
            "Limit relevance to SOL only · oppose framing as merits admission",
          priority: "CRITICAL",
        },
        {
          topic: "Pre-suit demand correspondence",
          exhibit: "DX-733",
          exDesc: "2023 demand letters showing pre-litigation positioning",
          objection:
            "FRE 408 settlement communications · move to exclude or limit",
          priority: "HIGH",
        },
      ],
    },
    nextWitness: {
      name: "Greg Brockman",
      role: "Defendant",
      who: "Co-Founder & President, OpenAI",
      keyDocs: [
        {
          ex: "PX-163",
          desc: "Journal entry - 'wrong to steal non-profit from Elon' (2017)",
          strength: "Plaintiff's most powerful contemporaneous document",
        },
      ],
      directOutline: [
        "Establish founding relationship with Musk and Altman",
        "Authenticate journal as Brockman's contemporaneous personal record",
        "Walk through 2016-2018 strategic discussions about non-profit form",
        "Have Brockman read PX-163 entry aloud",
        "Establish what 'steal non-profit' meant in his own usage",
        "Connect to LP conversion he later approved",
      ],
      defenseStrategy:
        "Will minimize PX-163 as personal musing, not institutional commitment. Expect 'I was venting,' 'I was wrong then,' or 'I changed my mind' framings.",
    },
    upcomingQueue: [
      {
        name: "Reid Hoffman",
        when: "Day 4 PM",
        note: "Founding board member · fiduciary duty cross",
      },
      {
        name: "Kevin Scott",
        when: "Day 5",
        note: "Microsoft CTO · PX-230 internal email",
      },
      {
        name: "Dr. Brad Smith",
        when: "Day 5 PM",
        note: "Microsoft General Counsel · DD process",
      },
      {
        name: "Defense rebuttal",
        when: "Day 6+",
        note: "defense case-in-chief estimated 4-5 days",
      },
    ],
    liveIssues: [
      {
        flag: "§17510.8 TRIAL BRIEFS",
        ref: "Dkt.515/516 filed this morning",
        desc: "Cal. Bus. & Prof. Code §17510.8 charitable-solicitations applicability briefs filed by both sides. YGR may rule before or during Brockman exam.",
        action: "Pocket brief ready · one tap",
        priority: "CRITICAL",
      },
    ],
    exhibits: {
      admitted: 23,
      pivotal: [
        {
          id: "PX-003",
          desc: "May 2015 founding email - 'tech belongs to the world'",
          when: "Day 1",
          status: "in",
        },
        {
          id: "PX-163",
          desc: "Brockman journal - 'wrong to steal non-profit from Elon'",
          when: "Day 2",
          status: "in",
        },
        {
          id: "PX-230",
          desc: "Kevin Scott MS-internal email re donor terms",
          when: "Day 2",
          status: "in",
        },
        {
          id: "DX-516",
          desc: "Musk 'Yup' reply to less-open proposal",
          when: "Day 2",
          status: "in",
        },
        {
          id: "PX-200",
          desc: "LP Agreement - 'spirit of a donation' clause",
          when: "Day 3 PM",
          status: "queued",
          objection:
            "defense may challenge foundation/relevance given LP timing relative to donations",
        },
      ],
    },
    liveNotes: [
      {
        time: "10:14 AM",
        author: "S. Pratap (co-counsel)",
        witness: "Musk",
        text: "Jury note-taking increased noticeably when 'Yup' email (DX-516) was shown - two jurors leaned forward. Juror 7 made a sustained note.",
      },
      {
        time: "10:42 AM",
        author: "S. Pratap (co-counsel)",
        witness: "Musk",
        text: "Juror 4 looked skeptical during xAI competitive-motive line. Cross-arms posture sustained ~3 minutes. Reverse signal.",
      },
      {
        time: "11:08 AM",
        author: "R. Abhyanker",
        witness: "Musk",
        text: "Musk's pace dropped when defense pressed on Tesla merger 2017 framing. Could be tactical pause but jurors read it as discomfort.",
      },
    ],
  },
  neuro: {
    trialDate: "March 9, 2027",
    courtroom: "Courtroom 540",
    judge: "Judge Fernando L. Aenlle-Rocha · C.D. Cal.",
    witnessReadiness: [
      {
        name: "Dr. Guosong Liu",
        role: "Named Inventor (Plaintiff)",
        status: "green",
        depo: "Complete · Mar 2026",
        cross: "Built · 3 Impeachment Bank sequences",
        notes: "Strongest defense witness on inventorship/§101",
      },
      {
        name: "Dr. Fei Mao",
        role: "Lab Collaborator",
        status: "green",
        depo: "Complete · Apr 2026",
        cross: "Built · 1 Impeachment Bank sequence",
        notes:
          "YouTube Ex.6 + Slutsky deposition (rogatory pending) anchor §102(f)",
      },
      {
        name: "Dr. Inna Slutsky",
        role: "Tel Aviv U. Researcher",
        status: "amber",
        depo: "Not scheduled",
        cross: "None",
        notes:
          "Subpoena status: unknown · centerpiece of inventorship defect counterclaim · CRITICAL TO SCHEDULE",
      },
      {
        name: "Dr. Sara Adams",
        role: "Declarant (Dkt.151)",
        status: "yellow",
        depo: "Declaration filed Mar 31",
        cross: "Partial outline",
        notes: "3 exhibits assigned · clarifying declaration on chain of title",
      },
      {
        name: "Armando Alfaro",
        role: "CEO, Mando",
        status: "red",
        depo: "Not deposed",
        cross: "None",
        notes:
          "⚡ NOTHING PREPARED · critical fact witness on operations/sourcing/labeling",
      },
      {
        name: "Wei Zhang",
        role: "Mando Director of Licensing",
        status: "yellow",
        depo: "30(b)(6) scheduled Jul 2026",
        cross: "Outline drafted",
        notes: "TMLA scope contradiction (Impeachment Bank seq. 5)",
      },
      {
        name: "Kappa Engineer Rep.",
        role: "Mando 30(b)(6) Manufacturing",
        status: "yellow",
        depo: "Scheduled Aug 15, 2026",
        cross: "Outline drafted",
        notes: "Country-of-origin sequence built",
      },
      {
        name: "MIT TLO Custodian",
        role: "Records Custodian",
        status: "red",
        depo: "Not subpoenaed",
        cross: "N/A",
        notes: "⚡ Need subpoena for MIT Provisional 60/510,945 records",
      },
      {
        name: "Dr. Halpern-Felsher (rebuttal)",
        role: "Plaintiff Expert",
        status: "yellow",
        depo: "Expert report Aug 1, 2026",
        cross: "Daubert outline",
        notes: "Methodology challenge in development",
      },
    ],
    exhibitPipeline: [
      {
        id: "Ex.4",
        name: "CN1200366A - 1997 Chinese prior art patent",
        status: "green",
        auth: "Certified translation authenticated · ready",
        use: "§102 anticipation",
      },
      {
        id: "Ex.6",
        name: "Mao YouTube Interview (2014)",
        status: "green",
        auth: "Authenticated · transcript prepared",
        use: "§102(f) inventorship",
      },
      {
        id: "Ex.8",
        name: "'061 Patent specification col.4",
        status: "green",
        auth: "Public record",
        use: "§101 in-vivo formation self-admission",
      },
      {
        id: "Ex.23",
        name: "Certificates of Analysis (China origin)",
        status: "green",
        auth: "Authenticated via Mando production",
        use: "Lanham Act false designation",
      },
      {
        id: "Ex.24",
        name: "U.S. Customs import records",
        status: "green",
        auth: "Records-keeper foundation set",
        use: "Pairs with Ex.23",
      },
      {
        id: "Ex.31",
        name: "ImportGenius trade data (Jordan routing)",
        status: "amber",
        auth: "Foundation witness not yet identified",
        use: "Country-of-origin · admissibility contested",
      },
      {
        id: "Ex.42",
        name: "MIT Provisional 60/510,945",
        status: "red",
        auth: "⚡ Custodian of records subpoena needed",
        use: "Inventorship date · prior art analysis",
      },
      {
        id: "Ex.50",
        name: "TMLA + side amendments",
        status: "yellow",
        auth: "Originals located · authentication pending",
        use: "Licensee estoppel · scope analysis",
      },
    ],
    milTracker: {
      filed: 0,
      deadline: "Jan 8, 2027",
      deadlineDays: 248,
      note: "Derived from Aenlle-Rocha standing order - typically 28 days before final pretrial conference. Final PTC Feb 5, 2027.",
      likelySubjects: [
        {
          subj: "CN1200366A admissibility as prior art",
          by: "Plaintiff",
          strength: "low - translation authenticated, defense will prevail",
        },
        {
          subj: "Mao YouTube video authentication",
          by: "Plaintiff",
          strength: "low - defense ready with foundation witness",
        },
        {
          subj: "ImportGenius trade data foundation",
          by: "Defendant",
          strength: "medium - open question, foundation gap real",
        },
        {
          subj: "Expert opinion on Chinese patent law",
          by: "Plaintiff",
          strength: "medium - qualifications challenge possible",
        },
      ],
    },
    countdown: [
      {
        date: "Jul 11, 2026",
        label: "Slutsky deposition target",
        days: 67,
        priority: "high",
        status: "unscheduled",
      },
      {
        date: "Aug 1, 2026",
        label: "Plaintiff expert reports due",
        days: 88,
        priority: "medium",
        status: "on-track",
      },
      {
        date: "Sep 11, 2026",
        label: "Fact Discovery Cut-Off",
        days: 129,
        priority: "critical",
        status: "on-track",
      },
      {
        date: "Nov 13, 2026",
        label: "Last motions hearing (incl. SJ)",
        days: 192,
        priority: "high",
        status: "pending",
      },
      {
        date: "Jan 8, 2027",
        label: "MIL filing deadline (derived)",
        days: 248,
        priority: "medium",
        status: "derived",
      },
      {
        date: "Feb 5, 2027",
        label: "Final Pretrial Conference",
        days: 276,
        priority: "high",
        status: "pending",
      },
      {
        date: "Mar 9, 2027",
        label: "TRIAL",
        days: 308,
        priority: "critical",
        status: "pending",
      },
    ],
  },
  juul: {
    sj: {
      title: "SFUSD/Seliavski - SJ Track",
      hearing: "October 31, 2025 - under submission",
      daysSinceHearing: 187,
      status: {
        label: "WAITING ROOM",
        desc: "MSJ hearing complete · ruling under submission",
      },
      aiNote:
        "Judge Orrick's median time to ruling on complex SJ motions in this MDL has been 47 days. The ruling is now 187 days post-hearing - substantially longer than median. This may indicate a complex or split decision on individual claims, or that Orrick is coordinating with the parallel antitrust track.",
      derivedDeadlines: [
        {
          trigger: "If ruling signals remand",
          deadline: "Joint letter on supplemental discovery scope",
          window: "14 days post-ruling",
          ref: "Per Orrick Dec 16, 2025 CMC instruction (Dkt.4341)",
        },
        {
          trigger: "If SJ for plaintiff",
          deadline: "Damages discovery reopens",
          window: "30 days",
          ref: "Standard remand protocol",
        },
        {
          trigger: "If SJ for defense",
          deadline: "Notice of appeal",
          window: "30 days",
          ref: "FRAP 4(a)(1)(A)",
        },
      ],
      keyDocs: [
        { id: "Dkt.3527", desc: "Bowen Reply ISO MSJ - RICO conduct element" },
        {
          id: "Dkt.3447",
          desc: "SOF Statement of Facts - youth metrics SOF ¶47",
        },
        { id: "Dkt.4094", desc: "Seliavski opt-out master complaint" },
      ],
    },
    antitrust: {
      title: "Antitrust Class - Trial Track",
      trial: "September 28, 2026",
      daysToTrial: 146,
      status: {
        label: "146 DAYS · ACTIVE PREP",
        desc: "Trial preparation across DPP, IPP, IRP classes",
      },
      optOutDeadline: { date: "June 13, 2026", days: 39, critical: true },
      witnessReadiness: [
        {
          name: "Altria Group 30(b)(6)",
          role: "Defendant",
          status: "amber",
          depo: "Subpoena issued",
          cross: "Outline draft only - antitrust-specific",
          notes: "Pre-investment knowledge focus · DD files Dkt.3393",
        },
        {
          name: "JLI 30(b)(6) - Antitrust",
          role: "Defendant",
          status: "amber",
          depo: "Scheduled Jun 2026",
          cross: "Outline draft",
          notes: "Distinct from MDL PI 30(b)(6) work - fresh prep needed",
        },
        {
          name: "Nicholas Pritzker",
          role: "Defendant Director",
          status: "red",
          depo: "Not yet noticed",
          cross: "None",
          notes: "⚡ Director-level defendant · separate from PI track",
        },
        {
          name: "Riaz Valani",
          role: "Defendant Director",
          status: "red",
          depo: "Not yet noticed",
          cross: "None",
          notes: "⚡ Director-level defendant · separate from PI track",
        },
        {
          name: "Expert Leger (DPP overcharge)",
          role: "Plaintiff Expert",
          status: "green",
          depo: "Daubert survived (Dkt.635)",
          cross: "N/A - plaintiff's witness",
          notes: "Methodology qualified · ready for direct",
        },
        {
          name: "Expert Singh (market analysis)",
          role: "Plaintiff Expert",
          status: "green",
          depo: "Daubert survived",
          cross: "N/A - plaintiff's witness",
          notes: "Transaction analysis qualified",
        },
      ],
      exhibitPipeline: [
        {
          id: "Dkt.635",
          desc: "100+ page class certification opinion",
          status: "yellow",
          note: "Need to translate findings to exhibit list",
        },
        {
          id: "Dkt.663",
          desc: "Class notice plan order",
          status: "yellow",
          note: "Procedural exhibit set TBD",
        },
        {
          id: "Dec 2018 SPA",
          desc: "Altria-JLI Stock Purchase Agreement",
          status: "green",
          note: "Authenticated",
        },
        {
          id: "Dkt.3393",
          desc: "Altria DD files (unsealed Mar 2025)",
          status: "green",
          note: "Authenticated · pre-investment knowledge",
        },
        {
          id: "Leger Report",
          desc: "Direct Purchaser overcharge expert report",
          status: "yellow",
          note: "Trial exhibit conversion needed",
        },
        {
          id: "Singh Report",
          desc: "Market transaction analysis",
          status: "yellow",
          note: "Trial exhibit conversion needed",
        },
        {
          id: "Trade data",
          desc: "Wholesale + DTC + retail volumes 2018-present",
          status: "red",
          note: "⚡ Class-period transaction data not yet exhibit-ready",
        },
      ],
      flaggedQuestions: [
        {
          q: "IPP class trial date relationship",
          desc: "Indirect Purchaser Class is also proceeding in parallel. No trial date set yet for IPP class. The relationship between the two trials needs to be clarified before pretrial conference. Joint trial? Sequential? Coordinated discovery?",
          ref: "Dkt.635 (IPP class certified) · Dkt.663 (notice plan)",
          ai: "Suggest: file letter brief to Orrick before June pretrial conference proposing trial structure. Alternative: separate trials risk inconsistent verdicts on same facts.",
        },
        {
          q: "Excluded states (AR/SC/TN/VA)",
          desc: "4 states excluded from indirect classes - what's the path forward for indirect purchasers in those states?",
          ref: "Dkt.635 - exclusion order",
          ai: "State-court parallel actions likely · monitor state court filings",
        },
      ],
    },
  },
};

const IMPEACHMENT_SEQUENCES = {
  ...ILE_K_IMP,
  juul: [
    {
      id: "juul-pak-costs",
      witness: "Quarry Pak",
      witnessRole: "SFUSD 30(b)(6)",
      party: "Plaintiff",
      priority: "Critical",
      type: "Admission",
      topic: "SFUSD past costs - no documentation",
      priorText: "We don't have that information.",
      priorCite: "Pak Depo p.47:12–18 (Dec 21, 2021)",
      inconsistency:
        "At trial, SFUSD will claim quantifiable past program costs as damages. Pak's deposition admission as 30(b)(6) representative is JLI's strongest single piece of evidence - anchors both the Sonner bar and the SJ damages argument.",
      script: [
        {
          t: "foundation",
          q: "Mr. Pak, you were designated by SFUSD as its 30(b)(6) representative on the topic of program costs?",
        },
        {
          t: "foundation",
          q: "And you understood that meant you were testifying on behalf of SFUSD, not just yourself?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Exhibit 47, your deposition transcript dated December 21, 2021. Do you recognize this document?",
        },
        {
          t: "confrontation",
          q: "At page 47, line 12, you were asked: 'What past program costs has SFUSD incurred specifically attributable to JUUL products?' Do you see that question?",
        },
        {
          t: "confrontation",
          q: "And your sworn answer, on behalf of SFUSD, was: 'We don't have that information.' Is that correct?",
        },
        {
          t: "followup",
          q: "Has SFUSD compiled any document since this deposition that quantifies past program costs attributable to JUUL?",
        },
      ],
      contingency:
        "If Pak claims documents were compiled later, ask for the production date and compare to the close of fact discovery. If he says 'we tried but couldn't,' that's the testimony you want - preserve it for closing argument.",
      useFor: ["MSJ", "Trial Cross", "Sonner Argument", "Closing"],
    },
    {
      id: "juul-monsees-marketing",
      witness: "James Monsees",
      witnessRole: "Co-Founder, JUUL Labs",
      party: "Defendant",
      priority: "Critical",
      type: "Contradiction",
      topic: "Field marketing oversight - Q3 2018 youth metrics list",
      priorText: "I was not directly involved in field marketing operations.",
      priorCite: "Monsees Depo (deposition prep)",
      inconsistency:
        "Monsees will deny marketing-level oversight at trial. Dkt.3447 SOF ¶47 places him on the Q3 2018 youth-cohort engagement metrics distribution list - directly contradicting any 'I wasn't involved' framing.",
      script: [
        {
          t: "foundation",
          q: "Mr. Monsees, as co-founder, you were involved in major strategic marketing decisions through 2018, correct?",
        },
        {
          t: "foundation",
          q: "You earlier testified you 'reviewed all major marketing initiatives' through 2018 - that's accurate?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Exhibit 3447, the Statement of Facts in this litigation. Paragraph 47 is highlighted.",
        },
        {
          t: "confrontation",
          q: "Paragraph 47 states that you were on the distribution list for Q3 2018 youth-cohort engagement metrics. Do you see that?",
        },
        {
          t: "confrontation",
          q: "You received those quarterly metrics, did you not?",
        },
        {
          t: "followup",
          q: "Then your testimony that you were 'not directly involved in field marketing' was incorrect, was it not?",
        },
      ],
      contingency:
        "If he claims he forwarded without reading, pull the next email in the chain - Monsees responded on the metrics distribution list. The reply email establishes engagement.",
      useFor: ["Trial Cross", "Closing", "Punitive Argument"],
    },
    {
      id: "juul-burns-juulschool",
      witness: "Kevin Burns",
      witnessRole: "Former CEO, JUUL Labs",
      party: "Defendant",
      priority: "Critical",
      type: "Document",
      topic: "JUUL School outreach denial - Sept 2018 board deck",
      priorText:
        "We never targeted youth. JUUL was designed for adult smokers.",
      priorCite: "Burns 2018 Congressional testimony",
      inconsistency:
        "Burns publicly denied youth targeting. The Sept 2018 board deck (unsealed Dkt.3393) lists the 'JUUL School' outreach program by school district - Burns attended that board meeting.",
      script: [
        {
          t: "foundation",
          q: "Mr. Burns, you served as CEO of JUUL Labs from December 2017 through September 2019, correct?",
        },
        {
          t: "foundation",
          q: "As CEO, you attended JUUL Labs board of directors meetings during that period?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Exhibit 2891, the September 2018 JUUL Labs board deck, unsealed pursuant to Dkt.3393.",
        },
        {
          t: "confrontation",
          q: "On page 14 of this deck, an in-person outreach program is described - labeled 'JUUL School' - listing California high schools by district. Do you see that?",
        },
        {
          t: "confrontation",
          q: "You attended the board meeting at which this deck was presented?",
        },
        {
          t: "confrontation",
          q: "And your earlier testimony that JUUL 'never targeted youth' is inconsistent with this program, isn't it?",
        },
      ],
      contingency:
        "If Burns claims he didn't see the deck, the meeting attendance record establishes his presence. If he claims 'JUUL School' was about education not outreach, the program description language defeats that - the deck describes 'in-person engagement' at named high schools.",
      useFor: ["Trial Cross", "Closing", "Punitive Argument"],
    },
    {
      id: "juul-matthews-staff",
      witness: "Vincent Matthews",
      witnessRole: "SFUSD Superintendent",
      party: "Plaintiff",
      priority: "High",
      type: "Admission",
      topic: "Staff time documentation gap",
      priorText:
        "I am not aware of any document that quantifies staff time spent on JUUL-related issues.",
      priorCite: "Matthews Depo p.83:4–11 (Oct 2021)",
      inconsistency:
        "Matthews's separate admission that no staff-time quantification exists pairs with Pak's 30(b)(6) admission. Use them in tandem to show the entire SFUSD damages theory lacks documentary support.",
      script: [
        {
          t: "foundation",
          q: "Superintendent Matthews, you served as Superintendent of SFUSD throughout the relevant period, correct?",
        },
        {
          t: "foundation",
          q: "In that role, you would be the senior official responsible for tracking material expenditures of SFUSD staff time on policy matters, correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you your deposition transcript at page 83. The relevant passage is highlighted.",
        },
        {
          t: "confrontation",
          q: "You were asked whether SFUSD had any document that quantifies staff time spent on JUUL-related issues. Your answer was that you were 'not aware of any such document.' Correct?",
        },
        {
          t: "followup",
          q: "Mr. Pak, your 30(b)(6) representative on this very topic, gave the same answer - 'we don't have that information.' Are you aware that two SFUSD officials independently testified the documentation does not exist?",
        },
      ],
      contingency:
        "If Matthews tries to walk back, ask whether he has searched for such a document since the deposition. The 'no such document' admission compounds.",
      useFor: ["MSJ", "Trial Cross", "Sonner Argument"],
    },
    {
      id: "juul-bowen-nicotine",
      witness: "Adam Bowen",
      witnessRole: "Co-Founder, JUUL Labs",
      party: "Defendant",
      priority: "High",
      type: "Document",
      topic: "59mg/mL nicotine engineering",
      priorText:
        "We designed JUUL to help adult smokers - the nicotine level was calibrated to provide effective cessation support.",
      priorCite: "Anticipated trial testimony",
      inconsistency:
        "Bowen will frame 59mg/mL as cessation-calibrated. The 2014 patent application he co-authored describes engineering for nicotine salt formulations specifically to deliver higher peak levels than free-base nicotine - directly impeaching any 'just for cessation' framing.",
      script: [
        {
          t: "foundation",
          q: "Dr. Bowen, you co-invented the JUUL pod technology with Mr. Monsees, correct?",
        },
        {
          t: "foundation",
          q: "You're a named inventor on the JUUL nicotine-salt patent application?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Exhibit 312, your 2014 patent application for nicotine-salt formulations.",
        },
        {
          t: "confrontation",
          q: "In the specification, you explain that nicotine-salt formulations deliver higher peak nicotine levels than free-base nicotine. Do you see that passage?",
        },
        {
          t: "confrontation",
          q: "That higher peak delivery was an intentional engineering choice, was it not?",
        },
        {
          t: "followup",
          q: "Higher peak nicotine delivery makes a product more addictive, particularly for novice users - that's pharmacologically standard, correct?",
        },
      ],
      contingency:
        "If Bowen tries to draw a distinction between 'addictive' and 'satisfying,' read back his own specification language describing 'rapid satisfaction' - and ask the jury to weigh that against the 75% youth ignorance figure.",
      useFor: ["Trial Cross", "Failure to Warn", "Punitive"],
    },
    {
      id: "juul-altria-dd",
      witness: "Altria Group Representative",
      witnessRole: "Altria 30(b)(6)",
      party: "Defendant",
      priority: "High",
      type: "Document",
      topic: "Pre-investment knowledge of youth penetration",
      priorText:
        "Altria conducted standard due diligence and had no specific awareness of youth use levels at investment.",
      priorCite: "Anticipated trial framing",
      inconsistency:
        "Altria will frame the December 2018 investment as routine. The unsealed due-diligence files (Dkt.3393, Mar 2025) show Altria reviewed JLI's quarterly youth-cohort metrics 60 days before closing - directly proving pre-investment knowledge for the RICO enterprise theory.",
      script: [
        {
          t: "foundation",
          q: "You're testifying as Altria's 30(b)(6) representative on pre-investment due diligence, correct?",
        },
        {
          t: "foundation",
          q: "As part of standard due diligence on a $12.8 billion equity investment, Altria reviewed JLI's customer demographic data, correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Exhibit 3393, the unsealed Altria due-diligence file produced pursuant to court order March 2025.",
        },
        {
          t: "confrontation",
          q: "This file contains JLI's Q3 2018 user demographic analysis, dated 60 days before Altria's December 2018 investment. Correct?",
        },
        {
          t: "confrontation",
          q: "That analysis breaks out users by age band - including a 16–24 cohort. Correct?",
        },
        {
          t: "followup",
          q: "Altria reviewed this document before signing the equity purchase agreement?",
        },
      ],
      contingency:
        "If the witness claims Altria reviewed but did not act on the data, that itself supports the RICO knowledge element - the question is awareness, not response.",
      useFor: ["RICO Enterprise", "Punitive", "Closing"],
    },
  ],
  musk: [
    {
      id: "musk-altman-charity",
      witness: "Sam Altman",
      witnessRole: "CEO, OpenAI",
      party: "Defendant",
      priority: "Critical",
      type: "Prior Statement",
      topic: "Charitable mission denial",
      priorText:
        "I always characterized OpenAI as a research organization, not a charity.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Altman will deny charitable framing at trial. PX-003 - his own May 2015 founding email - uses the phrase 'tech belongs to the world.' OpenAI's IRS Form 1023 sought 501(c)(3) status. The charitable representation is in his own words and on the public record.",
      script: [
        {
          t: "foundation",
          q: "Mr. Altman, you were a co-founder of OpenAI in 2015?",
        },
        {
          t: "foundation",
          q: "You drafted communications to potential donors and supporters during the founding period?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Plaintiff's Exhibit 003, an email you sent on May 25, 2015 to Mr. Musk and others.",
        },
        {
          t: "confrontation",
          q: "In this email, you wrote - these are your exact words - 'tech belongs to the world.' Correct?",
        },
        {
          t: "confrontation",
          q: "OpenAI Inc. subsequently filed IRS Form 1023 seeking 501(c)(3) public charity status - and obtained it. Correct?",
        },
        {
          t: "followup",
          q: "So your earlier testimony that OpenAI was 'never a charity' is inconsistent with your own founding communications and the IRS designation, is it not?",
        },
      ],
      contingency:
        "If Altman tries to characterize 'belongs to the world' as aspirational rather than charitable, the IRS Form 1023 closes the gap - that filing is a legal representation, not aspiration. Read the form's mission statement back to him verbatim.",
      useFor: ["Phase 1 Cross", "Count XVIII", "Closing"],
    },
    {
      id: "musk-altman-msft",
      witness: "Sam Altman",
      witnessRole: "CEO, OpenAI",
      party: "Defendant",
      priority: "High",
      type: "Contradiction",
      topic: "Microsoft donor-term transparency",
      priorText:
        "OpenAI provided full transparency to Microsoft regarding donor representations.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Altman will claim transparency to Microsoft. PX-230 (Mar 2018) - Kevin Scott's MS-internal email - shows the MS CTO questioning whether donors were aware of the LP structure 16 months before the $1B investment. If MS was fully informed, Scott wouldn't have raised the question internally.",
      script: [
        {
          t: "foundation",
          q: "Mr. Altman, OpenAI received a $1 billion investment from Microsoft in July 2019, correct?",
        },
        {
          t: "foundation",
          q: "You testified earlier that OpenAI was fully transparent with Microsoft about donor representations during the diligence process. Correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Plaintiff's Exhibit 230, an internal Microsoft email dated March 2018 from Kevin Scott, then-Chief Technology Officer of Microsoft.",
        },
        {
          t: "confrontation",
          q: "In this email, sixteen months before the investment closed, Mr. Scott asks his Microsoft team - not OpenAI - whether 'donors are aware' of the LP conversion plans. Do you see that?",
        },
        {
          t: "confrontation",
          q: "If OpenAI had been fully transparent with Microsoft about donor representations, Mr. Scott would not have needed to ask that question internally, would he?",
        },
        {
          t: "followup",
          q: "Did anyone at OpenAI tell Mr. Scott specifically how donor representations had been characterized in the founding period?",
        },
      ],
      contingency:
        "If Altman says 'I don't know what Scott knew,' that's the answer - preserve it. The whole point is that MS was guessing because OpenAI did not characterize. Press for one specific instance of OpenAI explaining donor terms to MS, and let his silence speak.",
      useFor: ["Count XIX (Aiding & Abetting)", "Phase 1 Cross", "Closing"],
    },
    {
      id: "musk-brockman-journal",
      witness: "Greg Brockman",
      witnessRole: "Co-Founder & President, OpenAI",
      party: "Defendant",
      priority: "Critical",
      type: "Document",
      topic: "Journal entry - 'wrong to steal non-profit from Elon'",
      priorText:
        "That journal entry was personal speculation, not an admission of any breach.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Brockman will minimize PX-163 as personal musing. The journal is contemporaneous, written by the co-founder, before any litigation was contemplated. Under FRE 801(d)(2)(D), it is admissible as a statement against the entity's interest by an authorized agent.",
      script: [
        {
          t: "foundation",
          q: "Mr. Brockman, you co-founded OpenAI in 2015 with Mr. Altman?",
        },
        {
          t: "foundation",
          q: "You served as President of OpenAI through the LP conversion period?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Plaintiff's Exhibit 163, an entry from your personal journal dated 2017.",
        },
        {
          t: "confrontation",
          q: "You wrote, in your own hand: 'wrong to steal non-profit from Elon.' Correct?",
        },
        {
          t: "confrontation",
          q: "That was 2017 - long before any litigation was filed against OpenAI?",
        },
        {
          t: "confrontation",
          q: "You weren't writing for an audience. You were writing for yourself. Correct?",
        },
        {
          t: "followup",
          q: "You used the word 'steal,' not 'transfer' or 'restructure.' Why that word?",
        },
      ],
      contingency:
        "If Brockman says 'I was venting,' embrace it - venting is unfiltered. Venting reveals what you actually think. If he says 'I was wrong then,' ask what changed his mind and when, and watch the answer struggle.",
      useFor: ["Count XVIII", "Phase 1 Cross", "Closing"],
    },
    {
      id: "musk-musk-supportive",
      witness: "Elon Musk",
      witnessRole: "Plaintiff",
      party: "Plaintiff",
      priority: "High",
      type: "Document",
      topic: "'Tell them I'm supportive' - Mar 2019",
      priorText: "I never approved or ratified the for-profit conversion.",
      priorCite: "Anticipated plaintiff testimony",
      inconsistency:
        "Musk will testify he never approved the LP. DX-862 (Mar 2019) - 'Tell them I'm supportive' - instructs Altman to characterize Musk as supportive of the for-profit transition. Defense impeachment exhibit; preserves SOL/laches arguments.",
      script: [
        {
          t: "foundation",
          q: "Mr. Musk, you've testified you never approved the for-profit conversion of OpenAI. Correct?",
        },
        {
          t: "foundation",
          q: "You communicated with Mr. Altman about the conversion in early 2019?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defendant's Exhibit 862, a March 2019 email from you to Mr. Altman.",
        },
        {
          t: "confrontation",
          q: "In this email, you wrote: 'Tell them I'm supportive.' Correct?",
        },
        {
          t: "confrontation",
          q: "You instructed Mr. Altman to characterize you to others - donors, employees, the public - as supportive of the conversion plans?",
        },
        {
          t: "followup",
          q: "Now in this courtroom, four years later, you're asking the Court to find you opposed the conversion. Help me understand the difference.",
        },
      ],
      contingency:
        "If Musk says 'I was strategic,' that's the SOL trigger - he knew enough by March 2019 to position himself. If he says 'I changed my mind,' ask when and let the timeline drive him into the September 2020 'captured by Microsoft' tweet.",
      useFor: ["SOL Defense", "Laches", "Phase 1 Closing"],
    },
    {
      id: "musk-musk-yup",
      witness: "Elon Musk",
      witnessRole: "Plaintiff",
      party: "Plaintiff",
      priority: "High",
      type: "Document",
      topic: "'Yup' email - Jan 2016",
      priorText:
        "I always insisted that OpenAI's research be open and that the technology be widely available.",
      priorCite: "Anticipated plaintiff testimony",
      inconsistency:
        "Musk will frame himself as the consistent advocate for openness. DX-516 (Jan 2016) - Musk's one-word reply 'Yup' - agrees with a proposal that OpenAI become 'less open' as a strategic matter. Direct impeachment of openness narrative.",
      script: [
        {
          t: "foundation",
          q: "Mr. Musk, you've testified that you consistently insisted on OpenAI's openness from 2015 forward. Correct?",
        },
        {
          t: "foundation",
          q: "You corresponded by email with the OpenAI co-founders about strategic direction in early 2016?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defendant's Exhibit 516, an email chain from January 2016.",
        },
        {
          t: "confrontation",
          q: "In this chain, a co-founder writes that OpenAI may need to become 'less open' for strategic reasons. Your reply, in full, is one word: 'Yup.' Correct?",
        },
        {
          t: "confrontation",
          q: "'Yup' is an affirmative response, isn't it?",
        },
        {
          t: "followup",
          q: "That January 2016 email shows you agreed with becoming 'less open' three years before the for-profit conversion you now claim to oppose, doesn't it?",
        },
      ],
      contingency:
        "If Musk says 'Yup' was sarcastic or non-committal, ask him to identify any other email in the record where he objected to becoming less open. The silence in the record speaks.",
      useFor: ["Trust Defense", "SOL", "Phase 1 Closing"],
    },
  ],
  neuro: [
    {
      id: "neuro-liu-inventorship",
      witness: "Dr. Guosong Liu",
      witnessRole: "Named Co-Inventor (with Mao), '061 Patent",
      party: "Plaintiff",
      priority: "Critical",
      type: "Prior Statement",
      topic: "Inventorship - Slutsky omitted",
      priorText:
        "I personally conceived and synthesized the magnesium L-threonate composition described in the '061 patent.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Liu and Mao are BOTH named co-inventors on the '061 patent. The actual inventorship defect is the OMISSION of Dr. Inna Slutsky. Mao's 2014 YouTube interview (Ex.6) describes the discovery as having been made through 'an accidental discovery' using a fluorescent dye tool he invented 15 years earlier - and Mao's deposition (Feb 18, 2026) credits Slutsky for the actual discovery in Liu's MIT lab. Liu's own deposition acknowledges the molecule and synthesis preexisted (\"We did not invent the molecule\"). Under § 102(f) and § 256, a patent must name all inventors and only inventors. Slutsky's omission is the dispositive § 102(f) impeachment.",
      script: [
        {
          t: "foundation",
          q: "Dr. Liu, you are the sole named inventor on U.S. Patent No. '061?",
        },
        {
          t: "foundation",
          q: "You signed the inventor's oath, attesting that you and Dr. Mao were the inventors of the subject matter claimed. Correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defense Exhibit 6, a 2014 YouTube interview by Dr. Mao describing the origin of the discovery underlying the '061 patent.",
        },
        {
          t: "confrontation",
          q: "In this interview, Dr. Mao describes 'an accidental discovery' made years before Magceutics was formed in 2005. He states the discovery was made using a fluorescent dye tool he invented 15 years earlier. Correct?",
        },
        {
          t: "confrontation",
          q: "And in his February 18, 2026 deposition, Dr. Mao testified that Dr. Inna Slutsky - your Ph.D. researcher at MIT - was the person who actually made that discovery. Correct?",
        },
        {
          t: "confrontation",
          q: "Dr. Slutsky is not named as a co-inventor on the '061 patent. Correct?",
        },
        {
          t: "confrontation",
          q: "'Did the chemistry' means he performed the actual synthesis of the compound, not merely assisted with administrative tasks, correct?",
        },
        {
          t: "confrontation",
          q: "And yet Dr. Mao is not named as an inventor on the '061 patent. Correct?",
        },
        {
          t: "followup",
          q: "Under U.S. patent law, the failure to name a co-inventor invalidates the patent - you understand that?",
        },
      ],
      contingency:
        "If Liu disputes Slutsky's contribution, walk through Dr. Mao's YouTube quote and Mao's sworn deposition crediting Slutsky. The test under §102(f) is contribution to conception - Slutsky's observation of magnesium-induced synaptic enhancement IS the conception underlying the patent. If Liu claims Slutsky's contribution was just 'lab work,' the response is that the lab observation IS the inventive contribution claimed by the patent.",
      useFor: ["§102(f) Invalidity", "Trial Cross", "Closing"],
    },
    {
      id: "neuro-liu-prior-art",
      witness: "Dr. Guosong Liu",
      witnessRole: "Named Inventor, '061 Patent",
      party: "Plaintiff",
      priority: "High",
      type: "Document",
      topic: "CN1200366A - 1997 Chinese prior art",
      priorText:
        "I was not aware of any prior art disclosing magnesium L-threonate at the time I filed the '061 application.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Liu will deny prior art awareness. He earlier testified his MIT lab 'reviewed all relevant Chinese patents during background research.' CN1200366A (1997) is exactly that. The MIT TLO prosecution file should record the search.",
      script: [
        {
          t: "foundation",
          q: "Dr. Liu, you conducted your patent-relevant research at MIT?",
        },
        {
          t: "foundation",
          q: "You earlier testified that as part of your work at MIT, your lab 'reviewed all relevant Chinese patents during background research.' Correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defense Exhibit 4, Chinese Patent Application CN1200366A, published in 1997 - two years before your priority date.",
        },
        {
          t: "confrontation",
          q: "This 1997 Chinese patent discloses magnesium L-threonate compositions. Correct?",
        },
        {
          t: "confrontation",
          q: "It was published before you began the work that led to the '061 application?",
        },
        {
          t: "confrontation",
          q: "Either your MIT lab's background research found this patent - and you knew of it - or your earlier testimony about reviewing 'all relevant Chinese patents' was inaccurate. Which is it?",
        },
      ],
      contingency:
        "If Liu pivots to 'we reviewed but didn't catch this one,' that's still inequitable conduct - at minimum, it raises duty-of-disclosure questions. The MIT TLO prosecution file (Ex.7) is the next exhibit; pull it on redirect to lock in.",
      useFor: ["§102 Anticipation", "Inequitable Conduct", "Trial Cross"],
    },
    {
      id: "neuro-liu-spec",
      witness: "Dr. Guosong Liu",
      witnessRole: "Named Inventor, '061 Patent",
      party: "Plaintiff",
      priority: "Critical",
      type: "Document",
      topic: "In vivo formation - § 101 self-admission",
      priorText:
        "The '061 patent claims a manufactured compound, not a naturally occurring substance.",
      priorCite: "Anticipated direct testimony",
      inconsistency:
        "Liu will deny that the patent admits natural formation. The '061 patent specification, column 4, lines 12–18, describes 'in vivo formation' of the compound. Under § 101, products of nature are ineligible for patent protection. The patentee's own specification admits the issue.",
      script: [
        {
          t: "foundation",
          q: "Dr. Liu, you were involved in the drafting and review of the '061 patent specification before filing?",
        },
        {
          t: "foundation",
          q: "You reviewed the specification language before the inventor's oath was signed?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defense Exhibit 8, the '061 patent. Please turn to column 4.",
        },
        {
          t: "confrontation",
          q: "At column 4, lines 12 through 18, the specification describes 'in vivo formation' of the magnesium L-threonate compound. Could you read those lines aloud, please?",
        },
        {
          t: "confrontation",
          q: "'In vivo formation' means the compound forms naturally inside a living organism, correct?",
        },
        {
          t: "followup",
          q: "You're aware that under U.S. patent law, naturally occurring products are not patent-eligible?",
        },
      ],
      contingency:
        "If Liu says 'in vivo formation' refers to a manufacturing step inside lab tissue cultures, ask for any place in the specification that describes the lab tissue culture. The specification doesn't say that - the words 'in vivo' speak for themselves.",
      useFor: ["§101 Ineligibility", "Trial Cross", "MTD Argument"],
    },
    {
      id: "neuro-mao-credit",
      witness: "Dr. Fei Mao",
      witnessRole: "Lab collaborator (not named on '061)",
      party: "Defendant",
      priority: "High",
      type: "Admission",
      topic: "Independent contribution to synthesis",
      priorText:
        "I prepared materials and assisted Dr. Liu but did not contribute to the inventive concept.",
      priorCite: "Anticipated witness response",
      inconsistency:
        "Mao's 2014 YouTube interview (Ex.6) describes 'an accidental discovery' made years before Magceutics's formation, using a fluorescent dye tool he invented 15 years earlier. His Feb 18, 2026 deposition credits Dr. Inna Slutsky - Liu's MIT Ph.D. researcher - for the actual discovery. Yet Slutsky is not named on the Magtein patents. Pair with Mao's separate undisclosed prior fluorescent probe patents (135+ patents) - the foundational detection technology that enabled the discovery was never disclosed to the USPTO during prosecution.",
      script: [
        {
          t: "foundation",
          q: "Dr. Mao, you worked in Dr. Liu's MIT laboratory during the period the '061 invention was being developed?",
        },
        {
          t: "foundation",
          q: "As part of that work, you performed laboratory procedures involving magnesium compounds?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defense Exhibit 6, your 2014 YouTube interview describing the origin of the Magtein discovery.",
        },
        {
          t: "confrontation",
          q: "In this interview, you describe the Magtein discovery as 'an accidental discovery' made using a fluorescent dye tool you invented 15 years earlier. Correct?",
        },
        {
          t: "confrontation",
          q: "And in your February 18, 2026 deposition, you testified that the discovery was made by Dr. Inna Slutsky in Dr. Liu's MIT lab. Correct?",
        },
        {
          t: "confrontation",
          q: "Dr. Slutsky is not listed as a co-inventor on any of the Magtein patents. Correct?",
        },
        {
          t: "confrontation",
          q: "You are named as a co-inventor on more than 135 prior patents, including patents on fluorescent molecular probes. None of those probe patents were disclosed to the USPTO during prosecution of the Magtein patents. Correct?",
        },
        {
          t: "confrontation",
          q: "In your own remarks during this interview, you describe identifying the optimal synthesis pathway. Do you recall those words?",
        },
        {
          t: "followup",
          q: "Identifying the optimal synthesis pathway sounds like inventive contribution to me. Help me understand why you're not named on the patent.",
        },
      ],
      contingency:
        "If Mao defers to Liu on inventorship, the defense pivots to Slutsky omission - the actual person who made the discovery. If he disputes Slutsky's contribution, walk through his own deposition and YouTube interview crediting Slutsky. If he disputes the materiality of his undisclosed probe patents, walk through their detection chemistry as the foundational technology for the magnesium-synaptic observation.",
      useFor: ["§102(f) Invalidity", "Trial Cross", "Closing"],
    },
    {
      id: "neuro-magtein-origin",
      witness: "Dr. Guosong Liu / Edward Lee (AIDP)",
      witnessRole: "Plaintiff/Counter-Defendant - Magtein® labeling",
      party: "Plaintiff",
      priority: "High",
      type: "Contradiction",
      topic: "Magtein® country-of-origin counterclaim",
      priorText:
        "Magtein® products are 'Manufactured in the USA' and compliant with FDA GRAS standards based on GRAS Notice No. 499.",
      priorCite:
        "Magtein® product packaging, magceutics.com, Amazon listings, GRAS Notice No. 499 signed by Dr. Liu",
      inconsistency:
        "This is Mando's COUNTERCLAIM (Counts VII, IIX, X) against Plaintiffs. Magtein® labels say 'Manufactured in the USA.' Certificates of Analysis identify CHINA as the country of origin for magnesium L-threonate hydrate (the primary active ingredient). Import records (ImportGenius) show shipments from Chinese exporters, in some cases routed through Jordan (under JOFTA) before US entry. Under FTC's Made in USA Rule, 16 C.F.R. Part 323, the unqualified claim is permissible only if the product is 'all or virtually all' US-origin - which is not the case when the primary active ingredient comes from China. This impeachment supports Mando's Lanham Act §43(a)(1)(B), §17200, and fraud counterclaims against Liu, ThreoTech, and AIDP.",
      script: [
        {
          t: "foundation",
          q: "Dr. Liu, you signed GRAS Notice No. 499 in your capacity as President of Magceutics, attesting to specific formulation, manufacturing practices, and product specifications. Correct?",
        },
        {
          t: "foundation",
          q: "Magtein® product labels and the magceutics.com website state 'Manufactured in the USA.' Correct?",
        },
        {
          t: "exhibit",
          q: "I'm showing you Defense Exhibit 23, Certificates of Analysis for Magtein® product batches.",
        },
        {
          t: "confrontation",
          q: "Each Certificate of Analysis identifies the country of origin for magnesium L-threonate hydrate - the primary active ingredient - as China. Correct?",
        },
        {
          t: "exhibit",
          q: "And here are ImportGenius trade records showing shipments of magnesium L-threonate from Chinese exporters, with some shipments routed through Jordan before entering the United States.",
        },
        {
          t: "confrontation",
          q: "The active ingredient was sourced from China and, in some shipments, routed through Jordan to claim JOFTA preferential treatment. Correct?",
        },
        {
          t: "confrontation",
          q: "And under the FTC's Made in USA Rule, 16 C.F.R. Part 323, an unqualified 'Manufactured in the USA' claim is only permitted where the product is 'all or virtually all' made in the United States. Correct?",
        },
        {
          t: "followup",
          q: "So Magtein® products are marketed as 'Manufactured in the USA' while the primary active ingredient comes from China. That is the basis of Mando's counterclaim under the Lanham Act, §17200, and fraud. Correct?",
        },
      ],
      contingency:
        "If the witness claims domestic encapsulation satisfies the standard, the response is FTC 16 C.F.R. Part 323 - domestic encapsulation does not meet 'all or virtually all' when the primary active ingredient is foreign-sourced. If the witness claims unawareness of CoAs, walk through Edward Lee's role at AIDP authorizing labeling and sourcing practices.",
      useFor: [
        "Lanham Act",
        "Trial Cross",
        "Closing",
        "Mando Counterclaim Damages",
      ],
    },
  ],
};

const DAMAGES_TRACKS = {
  ...ILE_K_DAM,
  juul: [
    {
      id: "juul-sfusd",
      title: "SFUSD - Public Nuisance",
      subtitle: "Largest exposure track",
      mode: "exposure",
      anchor: "Cal. Civ. Code §§3479-3480 · equitable abatement",
      sliders: [
        {
          id: "dorn",
          label: "Jury acceptance of Dorn abatement estimate",
          min: 0,
          max: 100,
          def: 50,
          suffix: "%",
          anchor:
            "$78.8M Dorn estimate (full) · $34,875 documented past costs (floor)",
          ai: "Pak's 30(b)(6) admission ('we don't have that information') is JLI's strongest evidence for heavy discounting. Plaintiff rehabilitates with Dorn site inspection reports at 6 schools (Balboa, McAteer, Aptos, Denman, Presidio, Downtown).",
        },
        {
          id: "liability",
          label: "Liability share (joint and several %)",
          min: 0,
          max: 100,
          def: 60,
          suffix: "%",
          anchor: "JLI alone (~50%) vs JLI + Altria + NMDs (~80%)",
          ai: "Joint and several liability under California public nuisance allows recovery against any defendant for the full amount; allocation is a retrial issue.",
        },
        {
          id: "punitive",
          label: "Punitive multiplier (× compensatory)",
          min: 0,
          max: 300,
          def: 0,
          suffix: "x",
          div: 100,
          anchor: "Cooper Industries 9:1 cap · Philip Morris v. Williams",
          ai: "Punitive on public nuisance is unusual but available where intentional misconduct toward third parties is proven. Internal documents (Dkt.3447) support intent finding.",
        },
      ],
      toggles: [
        {
          id: "sonner",
          label: "Sonner v. Premier Nutrition (9th Cir. 2020)",
          def: "available",
          options: [
            {
              val: "available",
              label: "✓ Abatement available",
              desc: "Equitable relief permitted",
            },
            {
              val: "barred",
              label: "✗ Barred by Sonner",
              desc: "Adequate legal remedy → no equity",
            },
          ],
        },
      ],
    },
    {
      id: "juul-seliavski",
      title: "Seliavski - Individual PI",
      subtitle: "Risk-adjusted expected value",
      mode: "ev",
      anchor: "Opt-out PI · remand to E.D. Va. anticipated",
      sliders: [
        {
          id: "tolling",
          label: "SOL tolling probability",
          min: 0,
          max: 100,
          def: 40,
          suffix: "%",
          anchor:
            "Spring 2020 SOL accrual · American Pipe / fraudulent concealment",
          ai: "Without tolling, claim is barred. Discovery rule and fraudulent concealment are the only paths. Strongest argument: JLI's internal suppression of addiction data.",
        },
        {
          id: "expert",
          label: "Causation expert retained & survives Daubert",
          min: 0,
          max: 100,
          def: 0,
          suffix: "%",
          anchor: "Currently no expert · case-ending if remand without",
          ai: "Highest-priority gap. Without medical causation linking JUUL specifically to Seliavski's injuries, SJ is virtually certain post-remand. Move expert disclosure pre-remand.",
        },
        {
          id: "damages",
          label: "Compensatory damages (if liable)",
          min: 5000,
          max: 500000,
          def: 50000,
          suffix: "$",
          anchor:
            "Per-plaintiff range · VA tort precedent for nicotine dependence",
          ai: "VA tort range. Higher end requires documented serious injury - addiction, behavioral injuries, medical expenses, lost income.",
        },
        {
          id: "punitive",
          label: "Punitive multiplier",
          min: 0,
          max: 300,
          def: 150,
          suffix: "x",
          div: 100,
          anchor: "VA Code §8.01-38.1 caps punitive at $350K",
          ai: "Virginia caps punitive at $350K. Multiplier above 1× requires fraudulent concealment finding.",
        },
      ],
      toggles: [],
    },
    {
      id: "juul-antitrust",
      title: "Antitrust - Direct Purchaser Class",
      subtitle: "Trial September 28, 2026",
      urgent: true,
      mode: "exposure",
      anchor: "Sherman §1 · class certified Feb 26, 2026 (Dkt.635)",
      sliders: [
        {
          id: "classSize",
          label: "Direct purchaser class size",
          min: 1000,
          max: 50000,
          def: 8000,
          suffix: "members",
          anchor: "Wholesale + direct retail + DTC purchasers",
          ai: "DPP class certified by Judge Orrick (Dkt.635) - definite. Indirect purchaser classes excluded for AR/SC/TN/VA.",
        },
        {
          id: "overcharge",
          label: "Per-unit overcharge",
          min: 0,
          max: 300,
          def: 120,
          suffix: "$",
          div: 100,
          anchor: "Leger expert opinion · Perry rebuttal range",
          ai: "Expert Leger qualified on overcharge methodology. Defendant Perry rebuttal goes to weight not admissibility.",
        },
        {
          id: "units",
          label: "Average units purchased per class member",
          min: 1,
          max: 1000,
          def: 240,
          suffix: "units",
          anchor: "Wholesale unit volumes Oct 2018 – present",
          ai: "Class period from Oct 5, 2018. Larger purchasers (chain retailers) drive average up; small buyers cap downside.",
        },
      ],
      toggles: [
        {
          id: "treble",
          label: "Clayton Act §4 trebling",
          def: "treble",
          options: [
            {
              val: "treble",
              label: "✓ Treble damages (mandatory)",
              desc: "15 U.S.C. §15 - automatic 3×",
            },
            {
              val: "single",
              label: "✗ Single damages only",
              desc: "If trebling denied (rare)",
            },
          ],
        },
      ],
    },
  ],
  musk: [
    {
      id: "musk-trust",
      title: "Phase 1 - Charitable Trust Breach",
      subtitle: "Constructive trust on for-profit equity",
      mode: "ev",
      anchor: "Count XVIII · trial in progress · OpenAI ~$300B valuation",
      sliders: [
        {
          id: "liability",
          label: "Phase 1 liability finding probability",
          min: 0,
          max: 100,
          def: 35,
          suffix: "%",
          anchor: "YGR denied PI March 2025 · trial outcome uncertain",
          ai: "PI denial signaled defense-favorable equity view. PX-003, PX-163 strong but DX-516, DX-862 are devastating impeachment.",
        },
        {
          id: "equity",
          label: "Constructive trust equity %",
          min: 0,
          max: 50,
          def: 15,
          suffix: "%",
          anchor: "OpenAI ~$300B post-conversion valuation",
          ai: "FIFO tracing from $38M donations to current equity. Plaintiff seeks all equity attributable to trust assets.",
        },
        {
          id: "valuation",
          label: "OpenAI valuation at trial",
          min: 100,
          max: 500,
          def: 300,
          suffix: "B",
          anchor: "$300B Oct 2025 PBC · subject to private market shifts",
          ai: "Valuation drives the dollar number. PBC conversion crystallized one valuation; ongoing rounds may move it materially.",
        },
      ],
      toggles: [
        {
          id: "sol",
          label: "SOL - Sept 2020 tweet trigger",
          def: "tolled",
          options: [
            {
              val: "tolled",
              label: "✓ Tolling succeeds",
              desc: "Fraudulent concealment found",
            },
            {
              val: "barred",
              label: "✗ SOL bars claim",
              desc: "Sept 2020 tweet = constructive knowledge",
            },
          ],
        },
      ],
    },
    {
      id: "musk-ue",
      title: "Count IV - Unjust Enrichment",
      subtitle: "Donor restitution",
      mode: "ev",
      anchor: "Count IV · disgorgement of donor-funded value",
      sliders: [
        {
          id: "liability",
          label: "UE liability probability",
          min: 0,
          max: 100,
          def: 30,
          suffix: "%",
          anchor: "Linked to Phase 1 · independent counts unlikely",
          ai: "UE rises and falls with charitable trust theory.",
        },
        {
          id: "donations",
          label: "Tracked donations (Dudney FIFO)",
          min: 30,
          max: 80,
          def: 38,
          suffix: "M",
          anchor: "$38M baseline · expert tracing",
          ai: "Plaintiff expert Dudney's tracing methodology: $38M traceable from donor accounts to OpenAI Inc.",
        },
        {
          id: "multiplier",
          label: "Equity attribution multiplier",
          min: 1,
          max: 5000,
          def: 1500,
          suffix: "×",
          div: 1,
          anchor: "Donations × growth ratio = equity value",
          ai: "$38M seed → $300B+ enterprise value. Tracing methodology determines accepted multiplier at trial.",
        },
      ],
      toggles: [],
    },
    {
      id: "musk-msft",
      title: "Count XIX - Aiding & Abetting (MS)",
      subtitle: "Microsoft knowing assistance",
      mode: "ev",
      anchor: "Count XIX · joint and several",
      sliders: [
        {
          id: "liability",
          label: "MS knowing-assistance liability probability",
          min: 0,
          max: 100,
          def: 25,
          suffix: "%",
          anchor: "PX-230 (Kevin Scott) shows CTO-level knowledge",
          ai: "Aiding & abetting requires knowing assistance, not negligence. PX-230 is plaintiff's best document; defense will argue it shows MS doing DD, not aiding.",
        },
        {
          id: "equity",
          label: "MS equity at risk (% of $135B+)",
          min: 0,
          max: 100,
          def: 20,
          suffix: "%",
          anchor: "~$135B+ MS equity in OpenAI",
          ai: "Constructive trust theory traces MS equity to alleged breach. Plaintiff seeks divestiture or disgorgement.",
        },
      ],
      toggles: [],
    },
  ],
  neuro: [
    {
      id: "neuro-patent",
      title: "Patent - '061 Reasonable Royalty",
      subtitle: "Largest exposure track",
      mode: "exposure",
      anchor: "35 U.S.C. §284 · reasonable royalty floor",
      sliders: [
        {
          id: "royalty",
          label: "Reasonable royalty rate",
          min: 0,
          max: 1500,
          def: 500,
          suffix: "%",
          div: 100,
          anchor: "Industry 1-15% · Georgia-Pacific factors",
          ai: "Georgia-Pacific 15-factor analysis. Compound supplements typically 3-7% royalty range.",
        },
        {
          id: "sales",
          label: "Annual accused sales",
          min: 1,
          max: 100,
          def: 25,
          suffix: "M",
          anchor: "Mando product line · '061-reading SKUs",
          ai: "Discovery from Mando's accounting will set this. Lab testing has confirmed one product to date.",
        },
        {
          id: "years",
          label: "Damages period",
          min: 1,
          max: 6,
          def: 3,
          suffix: "yrs",
          anchor: "35 USC §286 6-year limit · §287 marking",
          ai: "Marking statute 35 USC §287 may further limit recovery if patent not properly marked during the period.",
        },
      ],
      toggles: [
        {
          id: "willful",
          label: "Willful infringement (35 USC §284)",
          def: "none",
          options: [
            {
              val: "none",
              label: "✗ No willfulness (1×)",
              desc: "Standard reasonable royalty",
            },
            {
              val: "some",
              label: "⚠ Some enhancement (2×)",
              desc: "Halo Electronics partial",
            },
            {
              val: "max",
              label: "✓ Treble damages (3×)",
              desc: "Egregious willfulness",
            },
          ],
        },
      ],
    },
    {
      id: "neuro-lanham",
      title: "Lanham Act - False Designation",
      subtitle: "Country-of-origin claim",
      mode: "exposure",
      anchor: "15 USC §1117 · disgorgement available",
      sliders: [
        {
          id: "profits",
          label: "Defendant's profits subject to disgorgement",
          min: 0,
          max: 50,
          def: 8,
          suffix: "M",
          anchor: "Mando product profits · CoA + import documents",
          ai: "Section 43(a) does not require likelihood of confusion - only false statement of origin and materiality.",
        },
        {
          id: "enhancement",
          label: "Enhancement multiplier",
          min: 100,
          max: 300,
          def: 100,
          suffix: "×",
          div: 100,
          anchor: "15 USC §1117(a) discretionary up to 3×",
          ai: "Court discretion under §1117(a). Willful false designation supports enhancement.",
        },
      ],
      toggles: [],
    },
    {
      id: "neuro-dtsa",
      title: "Trade Secret - DTSA",
      subtitle: "Defend Trade Secrets Act",
      mode: "ev",
      anchor: "18 USC §1836 · actual + exemplary",
      sliders: [
        {
          id: "liability",
          label: "DTSA liability probability",
          min: 0,
          max: 100,
          def: 25,
          suffix: "%",
          anchor: "Misappropriation through TMLA access required",
          ai: "Discovery focus: did Mando access proprietary formulation data via TMLA and use it outside scope?",
        },
        {
          id: "actual",
          label: "Actual damages",
          min: 0,
          max: 30,
          def: 5,
          suffix: "M",
          anchor: "Lost profits + R&D acceleration value",
          ai: "DTSA permits actual damages OR royalty in lieu. Choose higher under §1836.",
        },
      ],
      toggles: [
        {
          id: "exemplary",
          label: "Exemplary damages (willful misappropriation)",
          def: "none",
          options: [
            {
              val: "none",
              label: "✗ No exemplary (1×)",
              desc: "Standard actual damages",
            },
            {
              val: "max",
              label: "✓ Exemplary 2× (willful)",
              desc: "DTSA §1836(b)(3)(C)",
            },
          ],
        },
      ],
    },
  ],
};

function computeDamages(track, sliders, toggles) {
  const get = (sid) => {
    const s = track.sliders.find((x) => x.id === sid);
    if (!s) return 0;
    const raw = sliders[sid] !== undefined ? sliders[sid] : s.def;
    return s.div ? raw / s.div : raw;
  };
  const tg = (tid) => {
    const t = track.toggles.find((x) => x.id === tid);
    if (!t) return null;
    return toggles[tid] || t.def;
  };
  switch (track.id) {
    case "juul-sfusd": {
      const dorn = get("dorn") / 100,
        liab = get("liability") / 100,
        pun = get("punitive");
      const documented = 34875,
        dornFull = 78800000;
      const sonnerBarred = tg("sonner") === "barred";
      const abate = sonnerBarred ? 0 : dornFull * dorn;
      const comp = (documented + abate) * liab;
      const punAmt = comp * pun;
      return {
        mode: "exposure",
        lines: [
          {
            label: "Documented past costs",
            value: documented * liab,
            key: "doc",
          },
          {
            label: `Equitable abatement (${(dorn * 100).toFixed(0)}% × Dorn)`,
            value: abate * liab,
            key: "abate",
            note: sonnerBarred ? "Barred by Sonner" : null,
            isZero: sonnerBarred,
          },
          {
            label: `Punitive (${pun.toFixed(2)}× compensatory)`,
            value: punAmt,
            key: "pun",
            isZero: pun === 0,
          },
        ],
        total: comp + punAmt,
      };
    }
    case "juul-seliavski": {
      const tolling = get("tolling") / 100,
        expert = get("expert") / 100,
        dam = get("damages"),
        pun = get("punitive");
      const prob = tolling * expert;
      const punAmt = dam * pun;
      const ifLiable = dam + punAmt;
      const ev = ifLiable * prob;
      return {
        mode: "ev",
        lines: [
          { label: "Compensatory if liable", value: dam, key: "comp" },
          { label: `Punitive (${pun.toFixed(2)}×)`, value: punAmt, key: "pun" },
          {
            label: "Subtotal if liable",
            value: ifLiable,
            key: "sub",
            isHeavy: true,
          },
          {
            label: `× Liability probability (${(tolling * 100).toFixed(0)}% × ${(expert * 100).toFixed(0)}% = ${(prob * 100).toFixed(1)}%)`,
            value: -ifLiable * (1 - prob),
            key: "prob",
            isReduction: true,
          },
        ],
        total: ev,
        probability: prob,
      };
    }
    case "juul-antitrust": {
      const cs = get("classSize"),
        oc = get("overcharge"),
        u = get("units");
      const tv = tg("treble") === "treble" ? 3 : 1;
      const single = cs * oc * u;
      const tboost = single * (tv - 1);
      return {
        mode: "exposure",
        lines: [
          {
            label: `Single damages (${cs.toLocaleString()} × $${oc.toFixed(2)} × ${u} units)`,
            value: single,
            key: "base",
          },
          {
            label: `Clayton Act §4 ${tv}× trebling`,
            value: tboost,
            key: "treble",
            note: tv === 1 ? "Single damages only" : null,
            isZero: tv === 1,
          },
        ],
        total: single * tv,
      };
    }
    case "musk-trust": {
      const liab = get("liability") / 100,
        eq = get("equity") / 100,
        val = get("valuation") * 1e9;
      const sol = tg("sol") === "barred";
      const trustValue = val * eq;
      const ifLiable = trustValue;
      const adjLiab = sol ? 0 : liab;
      const ev = ifLiable * adjLiab;
      return {
        mode: "ev",
        lines: [
          {
            label: `OpenAI valuation × constructive trust % (${(eq * 100).toFixed(0)}%)`,
            value: trustValue,
            key: "trust",
          },
          {
            label: "If liability finding (subtotal)",
            value: ifLiable,
            key: "sub",
            isHeavy: true,
          },
          {
            label: sol
              ? "× SOL bars claim (0%)"
              : `× Phase 1 liability probability (${(liab * 100).toFixed(0)}%)`,
            value: -ifLiable * (1 - adjLiab),
            key: "prob",
            isReduction: true,
            note: sol ? "SOL bars equitable claim" : null,
          },
        ],
        total: ev,
        probability: adjLiab,
      };
    }
    case "musk-ue": {
      const liab = get("liability") / 100,
        don = get("donations") * 1e6,
        mult = get("multiplier");
      const restitution = don * mult;
      const ev = restitution * liab;
      return {
        mode: "ev",
        lines: [
          {
            label: `Donations × attribution multiplier ($${(don / 1e6).toFixed(0)}M × ${mult.toFixed(0)}×)`,
            value: restitution,
            key: "rest",
          },
          {
            label: `× UE liability probability (${(liab * 100).toFixed(0)}%)`,
            value: -restitution * (1 - liab),
            key: "prob",
            isReduction: true,
          },
        ],
        total: ev,
        probability: liab,
      };
    }
    case "musk-msft": {
      const liab = get("liability") / 100,
        eq = get("equity") / 100;
      const msEquity = 135e9 * eq;
      const ev = msEquity * liab;
      return {
        mode: "ev",
        lines: [
          {
            label: `MS equity at risk (${(eq * 100).toFixed(0)}% of $135B)`,
            value: msEquity,
            key: "eq",
          },
          {
            label: `× Knowing-assistance probability (${(liab * 100).toFixed(0)}%)`,
            value: -msEquity * (1 - liab),
            key: "prob",
            isReduction: true,
          },
        ],
        total: ev,
        probability: liab,
      };
    }
    case "neuro-patent": {
      const r = get("royalty") / 100,
        sales = get("sales") * 1e6,
        years = get("years");
      const wt = tg("willful");
      const wm = wt === "max" ? 3 : wt === "some" ? 2 : 1;
      const royalty = sales * r * years;
      const enhanced = royalty * (wm - 1);
      return {
        mode: "exposure",
        lines: [
          {
            label: `Reasonable royalty ($${(sales / 1e6).toFixed(0)}M × ${(r * 100).toFixed(2)}% × ${years} yrs)`,
            value: royalty,
            key: "roy",
          },
          {
            label: `Willfulness ${wm}× enhancement (35 USC §284)`,
            value: enhanced,
            key: "will",
            note: wm === 1 ? "No willfulness finding" : null,
            isZero: wm === 1,
          },
        ],
        total: royalty * wm,
      };
    }
    case "neuro-lanham": {
      const profits = get("profits") * 1e6,
        enh = get("enhancement");
      const dis = profits;
      const enhAmt = dis * (enh - 1);
      return {
        mode: "exposure",
        lines: [
          {
            label: "Defendant's profits (disgorgement)",
            value: dis,
            key: "dis",
          },
          {
            label: `Enhancement ${enh.toFixed(2)}× (15 USC §1117(a))`,
            value: enhAmt,
            key: "enh",
            isZero: enh === 1,
          },
        ],
        total: dis * enh,
      };
    }
    case "neuro-dtsa": {
      const liab = get("liability") / 100,
        act = get("actual") * 1e6;
      const exVal = tg("exemplary") === "max" ? 2 : 1;
      const subtotal = act * exVal;
      const ev = subtotal * liab;
      return {
        mode: "ev",
        lines: [
          { label: "Actual damages (if liable)", value: act, key: "act" },
          {
            label: `Exemplary ${exVal}×`,
            value: act * (exVal - 1),
            key: "ex",
            isZero: exVal === 1,
          },
          {
            label: "Subtotal if liable",
            value: subtotal,
            key: "sub",
            isHeavy: true,
          },
          {
            label: `× DTSA liability probability (${(liab * 100).toFixed(0)}%)`,
            value: -subtotal * (1 - liab),
            key: "prob",
            isReduction: true,
          },
        ],
        total: ev,
        probability: liab,
      };
    }
    case "ile-naqvi":
    case "ile-chid": {
      const proceeds = get("proceeds") * 1e6;
      const pool = get("lenderloss") * 1e6;
      const share = get("share") / 100;
      const capped = tg("honey") === "applied";
      const forf = capped ? proceeds : Math.max(proceeds, pool * share);
      const restit = pool * share;
      return {
        mode: "exposure",
        lines: [
          {
            label: capped
              ? "Forfeiture - Honeycutt-capped personal proceeds"
              : "Forfeiture - government gross-proceeds theory",
            value: forf,
            isHeavy: true,
          },
          {
            label: "Restitution exposure - MVRA share of lender pool",
            value: restit,
          },
          {
            label: "Statutory floor if §225 stands: 120 months (see anchors)",
            value: 0,
            isZero: true,
            note: "Months, not dollars - modeled in the anchor line",
          },
        ],
        total: forf + restit,
      };
    }
    default:
      return { mode: "exposure", lines: [], total: 0 };
  }
}
function fmtUSD(n) {
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n < 0 ? "−$" : "$") + (abs / 1e9).toFixed(2) + "B";
  if (abs >= 1e6) return (n < 0 ? "−$" : "$") + (abs / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return (n < 0 ? "−$" : "$") + (abs / 1e3).toFixed(0) + "K";
  return (n < 0 ? "−$" : "$") + Math.round(abs).toLocaleString();
}
function damagesDemoScenarios(track) {
  const s = {
    "juul-sfusd": [
      {
        name: "Sonner bars equity (defense best)",
        desc: "If Judge Orrick finds an adequate legal remedy exists (per Sonner v. Premier Nutrition), equitable abatement is unavailable. Exposure collapses to documented past costs only.",
        apply: {
          sliders: { dorn: 0, liability: 30, punitive: 0 },
          toggles: { sonner: "barred" },
        },
      },
      {
        name: "Plaintiff prevails on abatement (plaintiff best)",
        desc: "Jury fully accepts the Dorn $78.8M abatement estimate; joint and several liability across all defendants; modest punitive on intentional youth-targeting.",
        apply: {
          sliders: { dorn: 90, liability: 80, punitive: 50 },
          toggles: { sonner: "available" },
        },
      },
      {
        name: "Mediation realistic midpoint",
        desc: "Sonner argument has traction but does not fully prevail. Jury accepts ~50% of Dorn estimate. Punitive declined. This is the negotiation zone.",
        apply: {
          sliders: { dorn: 50, liability: 60, punitive: 0 },
          toggles: { sonner: "available" },
        },
      },
    ],
    "juul-seliavski": [
      {
        name: "Tolling fails (case-ending)",
        desc: "Court finds Spring 2020 SOL ran without tolling. Claim is barred at the threshold. Expected value collapses to zero regardless of merits.",
        apply: {
          sliders: { tolling: 0, expert: 0, damages: 50000, punitive: 150 },
        },
      },
      {
        name: "Tolling succeeds + expert retained",
        desc: "Fraudulent concealment finding tolls SOL to filing date. Causation expert retained pre-remand and survives Daubert. Probability multiplies.",
        apply: {
          sliders: { tolling: 75, expert: 80, damages: 80000, punitive: 200 },
        },
      },
      {
        name: "Conservative settlement target",
        desc: "Both arguments succeed at moderate rates. Damages set at midrange. This is the realistic range for individual settlement discussions.",
        apply: {
          sliders: { tolling: 50, expert: 40, damages: 60000, punitive: 100 },
        },
      },
    ],
    "juul-antitrust": [
      {
        name: "Single damages only (defense unicorn)",
        desc: "If Clayton Act §4 trebling is somehow denied (rare; nearly automatic). Single damages reflect actual overcharge proof.",
        apply: {
          sliders: { classSize: 5000, overcharge: 80, units: 200 },
          toggles: { treble: "single" },
        },
      },
      {
        name: "Full treble + Leger overcharge accepted",
        desc: "Class size at upper end, Leger expert overcharge methodology fully accepted by jury, treble mandatory under Clayton Act §4.",
        apply: {
          sliders: { classSize: 15000, overcharge: 200, units: 300 },
          toggles: { treble: "treble" },
        },
      },
      {
        name: "Trial midpoint estimate",
        desc: "Mid-range class size and overcharge with mandatory trebling. Realistic working number for September 28 trial preparation.",
        apply: {
          sliders: { classSize: 8000, overcharge: 120, units: 240 },
          toggles: { treble: "treble" },
        },
      },
    ],
    "musk-trust": [
      {
        name: "Defense prevails (PI denial signal)",
        desc: "YGR's PI denial in March 2025 reflected her view of equities. Phase 1 verdict for defense - equity claims dismissed.",
        apply: {
          sliders: { liability: 10, equity: 15, valuation: 300 },
          toggles: { sol: "tolled" },
        },
      },
      {
        name: "SOL bars (game over)",
        desc: "Court finds Sept 2020 'captured by Microsoft' tweet was constructive knowledge. Tolling fails. SOL bars all equitable claims.",
        apply: {
          sliders: { liability: 50, equity: 15, valuation: 300 },
          toggles: { sol: "barred" },
        },
      },
      {
        name: "Plaintiff prevails (high-end)",
        desc: "Brockman PX-163 + Altman PX-003 carry the day. Jury finds breach. Constructive trust on 20% of equity at $400B valuation.",
        apply: {
          sliders: { liability: 65, equity: 20, valuation: 400 },
          toggles: { sol: "tolled" },
        },
      },
    ],
    "musk-ue": [
      {
        name: "UE rises with trust theory",
        desc: "Phase 1 trust verdict for plaintiff drags UE finding along. Donor restitution measured by FIFO tracing methodology accepted.",
        apply: { sliders: { liability: 50, donations: 38, multiplier: 1500 } },
      },
      {
        name: "Defense prevails on UE",
        desc: "Without trust breach, UE has no foundation. Donor recovery limited or denied entirely.",
        apply: { sliders: { liability: 10, donations: 38, multiplier: 500 } },
      },
      {
        name: "Negotiation midpoint",
        desc: "Realistic settlement range assuming uncertainty on Phase 1 outcome.",
        apply: { sliders: { liability: 30, donations: 38, multiplier: 1000 } },
      },
    ],
    "musk-msft": [
      {
        name: "MS DD defense prevails",
        desc: "PX-230 shows MS doing due diligence, not aiding. Knowing-assistance not proven. Microsoft escapes liability.",
        apply: { sliders: { liability: 10, equity: 15 } },
      },
      {
        name: "Plaintiff carries the day on aiding",
        desc: "Jury finds PX-230 (Kevin Scott questioning donor terms) is knowing assistance. MS equity exposure significant.",
        apply: { sliders: { liability: 55, equity: 30 } },
      },
      {
        name: "Realistic settlement target",
        desc: "Mid-range probability. MS equity stake structures the negotiation.",
        apply: { sliders: { liability: 25, equity: 20 } },
      },
    ],
    "neuro-patent": [
      {
        name: "Defense - patent invalid",
        desc: "§101 ineligibility ruling or §102 anticipation finding eliminates damages entirely. Royalty calculation moot.",
        apply: {
          sliders: { royalty: 0, sales: 25, years: 3 },
          toggles: { willful: "none" },
        },
      },
      {
        name: "Plaintiff - willful + treble",
        desc: "Patent survives validity challenges, court finds willfulness post-Halo. Treble damages under §284.",
        apply: {
          sliders: { royalty: 700, sales: 40, years: 4 },
          toggles: { willful: "max" },
        },
      },
      {
        name: "Reasonable settlement range",
        desc: "Mid-range royalty rate, single damages, full damages period. The negotiation zone for licensing settlement.",
        apply: {
          sliders: { royalty: 500, sales: 25, years: 3 },
          toggles: { willful: "none" },
        },
      },
    ],
    "neuro-lanham": [
      {
        name: "Disgorgement only (no enhancement)",
        desc: "Court orders profit disgorgement on country-of-origin claim. No enhancement. Standard remedy.",
        apply: { sliders: { profits: 8, enhancement: 100 } },
      },
      {
        name: "Willful enhancement (3×)",
        desc: "Court finds willful false designation. Profits trebled under §1117(a) discretion.",
        apply: { sliders: { profits: 12, enhancement: 300 } },
      },
      {
        name: "Conservative recovery",
        desc: "Lower-end profits, single disgorgement. Realistic for negotiation purposes.",
        apply: { sliders: { profits: 5, enhancement: 100 } },
      },
    ],
    "neuro-dtsa": [
      {
        name: "DTSA fails (no misappropriation)",
        desc: "Discovery shows Mando did not access proprietary formulation data through TMLA. DTSA claim collapses.",
        apply: {
          sliders: { liability: 5, actual: 5 },
          toggles: { exemplary: "none" },
        },
      },
      {
        name: "Willful misappropriation + 2×",
        desc: "Plaintiff proves willful misappropriation through TMLA access. DTSA exemplary damages multiply by 2.",
        apply: {
          sliders: { liability: 60, actual: 10 },
          toggles: { exemplary: "max" },
        },
      },
      {
        name: "Realistic risk-adjusted",
        desc: "Moderate liability probability, mid-range actual damages, no exemplary. Conservative settlement reference.",
        apply: {
          sliders: { liability: 25, actual: 5 },
          toggles: { exemplary: "none" },
        },
      },
    ],
  };
  return s[track.id] || [];
}

const DEMONSTRATIVES_DATA = {
  ...ILE_K_DEMO,
  juul: [
    {
      id: "juul-altria-flow",
      title: "The Altria–JUUL Deal: $12.8B for 35% + Market Exit",
      description:
        "In December 2018, Altria paid $12.8 billion for 35% of JUUL Labs. Simultaneously, Altria withdrew its competing e-cigarette products (MarkTen, Green Smoke) from the U.S. market. Plaintiffs allege this was a horizontal market allocation under the Sherman Act - the foundation of the parallel antitrust class action (3:20-cv-02345-WHO, Trial Sept 28, 2026). The deal also gave JUUL access to Altria's 400,000+ retail distribution locations and FDA lobbying infrastructure.",
      useFor:
        "Antitrust theory · RICO enterprise element · Class certification context",
      variants: 2,
    },
    {
      id: "juul-nicotine",
      title: "Nicotine Concentration: JUUL vs The Market",
      description:
        "JUUL pods deliver nicotine at 59mg/mL - roughly 2x the concentration of any closed-system competitor at launch. A single pod contains the nicotine equivalent of approximately one full pack of cigarettes (20 cigarettes). The high-concentration nicotine salt formulation was a key engineering choice that made the product more addictive, particularly for novice users. The FDA Marketing Denial Order (June 2022, since stayed) cited 59mg/mL as a basis for its denial of marketing authorization.",
      useFor:
        "Failure-to-warn · Design defect (CA risk-utility) · Halpern-Felsher 75% awareness gap",
      variants: 2,
    },
    {
      id: "juul-sonner",
      title: "The Sonner-Pak Two-Step (SJ Logic)",
      description:
        "JLI's summary judgment centerpiece is a symmetrical legal argument. STEP 1 - The Pak admission: SFUSD's 30(b)(6) representative testified the district 'does not have' quantifiable past program costs. Without quantifiable damages, plaintiffs lack legal damages. STEP 2 - The Sonner bar: Sonner v. Premier Nutrition (9th Cir. 2020) holds that equitable abatement requires no adequate legal remedy at law. If plaintiff has any legal damages theory, Sonner bars equity. The argument is symmetrical - it cannot lose both ways.",
      useFor: "MSJ Hearing (Oct 31 2025) · Motion in limine · Hearing prep",
      variants: 2,
    },
  ],
  musk: [
    {
      id: "musk-money-flow",
      title: "The Donation Trail: Charity → For-Profit → Microsoft",
      description:
        "From 2015 to 2018, donors contributed approximately $38 million to OpenAI, Inc., a 501(c)(3) public charity. In March 2019, OpenAI announced a for-profit subsidiary structure (OpenAI LP). Microsoft invested $1B in 2019 and ultimately $13B+. By 2024, Microsoft held an equity position valued at over $135 billion. In October 2025, OpenAI converted to a public benefit corporation, with the original 501(c)(3) becoming a minority stakeholder. Plaintiff seeks constructive trust on the for-profit equity as a charitable trust remedy.",
      useFor: "Constructive trust theory · Damages · Closing argument",
      variants: 2,
    },
    {
      id: "musk-knowledge",
      title: "Who Knew What When: The Knowledge Timeline",
      description:
        "Plaintiff's case rests on five contemporaneous communications spanning 2015 to 2019, each authored by a different OpenAI insider or business partner. PX-003 (May 2015): Altman's founding email - 'tech belongs to the world.' DX-516 (Jan 2016): Musk's one-word 'Yup' email. PX-163 (2017): Brockman's journal - 'wrong to steal non-profit from Elon.' PX-230 (Mar 2018): Microsoft CTO Kevin Scott's internal email. DX-862 (Mar 2019): 'Tell them I'm supportive.' Together they form the knowledge map at the heart of Counts XVIII, IV, and XIX.",
      useFor: "Trial summary · Witness order · Phase 1 closing",
      variants: 2,
    },
    {
      id: "musk-phases",
      title: "The Two Phases of Trial",
      description:
        "Judge Yvonne Gonzalez Rogers structured this trial in two phases by Pretrial Order. Phase 1 (now in progress): Count XVIII - breach of charitable trust, Count IV - unjust enrichment, Count XIX - aiding and abetting against Microsoft. Phase 2 (stayed): Counts VIII–XV - antitrust counterclaims (alleging Musk used litigation to harm OpenAI competitor xAI). The structure forces a clean equity-first verdict before market claims activate. If Phase 1 favors Musk, Phase 2 antitrust counterclaims trigger immediately.",
      useFor: "Trial preparation · Client briefing · Strategic planning",
      variants: 2,
    },
  ],
  neuro: [
    {
      id: "neuro-prior-art",
      title: "Prior Art Side-by-Side: '061 vs CN1200366A",
      description:
        "The '061 patent (Claim 1) covers a magnesium L-threonate composition. The Chinese patent CN1200366A, published in 1997 (two years before the '061 priority date), discloses the same compound, the same chemistry, and the same claimed properties. Under 35 U.S.C. § 102, prior art that anticipates Claim 1 invalidates the patent. The Chinese patent is exhibit Ex.4. The defendant's invalidity case rests on this comparison. If the §101 motion does not dispose of the patent, §102 anticipation will.",
      useFor: "§102 anticipation · Claim construction · Markman hearing",
      variants: 2,
    },
    {
      id: "neuro-inventorship",
      title: "Inventorship: Liu + Mao Named, Slutsky Omitted",
      description:
        "The '061 patent names BOTH Dr. Guosong Liu AND Dr. Fei Mao as co-inventors. The actual inventorship defect is the OMISSION of Dr. Inna Slutsky. In Mao's 2014 YouTube interview (Ex.6), he describes the discovery as 'an accidental discovery' made using a fluorescent dye tool he invented 15 years earlier. In his Feb 18, 2026 deposition, Mao credits Slutsky - Liu's Ph.D. researcher at MIT - for the actual discovery in Liu's lab. Under 35 U.S.C. § 102(f) and § 256, a patent must name all inventors and only inventors. Slutsky's omission as inventor invalidates or requires correction of the patent.",
      useFor:
        "§102(f) invalidity · §256 inventorship correction · Slutsky letter rogatory",
      variants: 2,
    },
    {
      id: "neuro-license",
      title: "TMLA License Scope: What Was Covered, What Was Not",
      description:
        "The Technology and Materials License Agreement defined the scope of authorized activity between the parties. Inside the scope: specific named territories, specific named product formulations, royalty obligations on covered sales. Outside the scope: manufacture in unlicensed territories, reformulation, sublicensing without consent. The plaintiff alleges defendant's activities exceeded scope. The defendant counters that licensee estoppel bars the plaintiff from asserting against any licensed activity. The scope question is dispositive on multiple counts.",
      useFor: "Licensee estoppel · Contract counterclaim · Scope discovery",
      variants: 2,
    },
  ],
};

function renderDemoSVG(id, variant, th, dark) {
  const W = 720,
    H = 420;
  const txt = th.text,
    muted = th.muted,
    faint = th.faint,
    bg = th.bg,
    card = th.card,
    border = th.border;
  const RED = "#ef4444",
    BLU = "#3b82f6",
    PUR = "#a78bfa",
    ORN = "#f59e0b",
    GRN = "#10b981";
  if(id==="ile-roundtrip"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><defs><marker id="ilA" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0 0L10 5L0 10z" fill={RED}/></marker></defs><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE $145M CIRCLE · 2021-2024 · S-1 ¶27</text><rect x="60" y="160" width="180" height="70" rx="10" fill={card} stroke={BLU} strokeWidth="1.5"/><text x="150" y="190" fill={txt} fontSize="13" fontWeight="700" textAnchor="middle">iLearningEngines</text><text x="150" y="208" fill={muted} fontSize="9" textAnchor="middle">Maryland · NASDAQ: AILE</text><rect x="480" y="160" width="180" height="70" rx="10" fill={card} stroke={RED} strokeWidth="1.5"/><text x="570" y="186" fill={txt} fontSize="13" fontWeight="700" textAnchor="middle">Entity 1 Accounts</text><text x="570" y="204" fill={RED} fontSize="9" textAnchor="middle">controlled by CC-2</text><rect x="280" y="310" width="160" height="58" rx="10" fill={card} stroke={ORN} strokeWidth="1.5"/><text x="360" y="334" fill={txt} fontSize="12" fontWeight="700" textAnchor="middle">Entities 2 · 4</text><text x="360" y="352" fill={muted} fontSize="9" textAnchor="middle">no online trace · backdated</text><path d="M242 178 C330 120 410 120 478 178" fill="none" stroke={RED} strokeWidth="2.5" markerEnd="url(#ilA)"/><path d="M560 232 C520 290 470 315 442 330" fill="none" stroke={RED} strokeWidth="2.5" markerEnd="url(#ilA)"/><path d="M278 330 C230 310 180 275 152 234" fill="none" stroke={RED} strokeWidth="2.5" markerEnd="url(#ilA)"/><text x="360" y="118" fill={RED} fontSize="11" fontWeight="700" textAnchor="middle">$145M out - dressed as spend</text><text x="360" y="300" fill={RED} fontSize="10" textAnchor="middle">returned as “customer payments”</text><g fontFamily="monospace" fontSize="8">{["5s","6s","7s","8s","9s","10s"].map((w,i)=><g key={w}><rect x={250+i*38} y="132" width="30" height="16" rx="8" fill={RED} opacity="0.16"/><text x={265+i*38} y="143" fill={RED} textAnchor="middle" fontWeight="700">{w}</text></g>)}</g><text x="360" y="402" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">wire counts 4s-10s ride these transfers · ≥90% of reported revenue fabricated</text></svg>;}if(id==="ile-roundtrip"&&variant===1){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE SAME CIRCLE - DEFENSE OVERLAY</text><rect x="60" y="150" width="170" height="64" rx="10" fill={card} stroke={BLU} strokeWidth="1.5"/><text x="145" y="178" fill={txt} fontSize="12" fontWeight="700" textAnchor="middle">iLearningEngines</text><text x="145" y="196" fill={muted} fontSize="9" textAnchor="middle">books both legs</text><rect x="490" y="150" width="170" height="64" rx="10" fill={card} stroke={RED} strokeWidth="1.5"/><text x="575" y="178" fill={txt} fontSize="12" fontWeight="700" textAnchor="middle">Entity 1 (CC-2)</text><text x="575" y="196" fill={muted} fontSize="9" textAnchor="middle">same dollars return</text><path d="M232 168 L488 168" stroke={RED} strokeWidth="2.5"/><path d="M488 200 L232 200" stroke={GRN} strokeWidth="2.5"/><text x="360" y="158" fill={RED} fontSize="9" textAnchor="middle" fontFamily="monospace">OUT</text><text x="360" y="222" fill={GRN} fontSize="9" textAnchor="middle" fontFamily="monospace">BACK</text><rect x="150" y="258" width="420" height="52" rx="9" fill={GRN} opacity="0.10"/><rect x="150" y="258" width="420" height="52" rx="9" fill="none" stroke={GRN}/><text x="360" y="280" fill={GRN} fontSize="11" fontWeight="700" textAnchor="middle">OCT 11 2024 - LENDER B APPROVED THE ~$2.5M TRANSFER IN WRITING</text><text x="360" y="298" fill={muted} fontSize="9" textAnchor="middle">the transfer charged as concealment in Count TEN · texts produced at Dkt.17</text><rect x="150" y="326" width="420" height="46" rx="9" fill={ORN} opacity="0.10"/><rect x="150" y="326" width="420" height="46" rx="9" fill="none" stroke={ORN}/><text x="360" y="346" fill={ORN} fontSize="10" fontWeight="700" textAnchor="middle">NETTING: circular flow means the same dollars counted twice</text><text x="360" y="362" fill={muted} fontSize="9" textAnchor="middle">the §225 gross-receipts fight - receipts, or a loop?</text><text x="360" y="404" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">defense build: approval timestamps on every October leg</text></svg>;}if(id==="ile-firewall"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">COUNT 11s FIREWALL - EVERY DELETION DIRECTIVE IN THE S-1</text><text x="190" y="70" fill={PUR} fontSize="13" fontWeight="800" textAnchor="middle">CHIDAMBARAN</text><text x="530" y="70" fill={GRN} fontSize="13" fontWeight="800" textAnchor="middle">NAQVI</text><line x1="360" y1="52" x2="360" y2="380" stroke={border} strokeWidth="2" strokeDasharray="6 5"/>{[["Sep 2024 - directs CC-2: delete WhatsApp threads","¶45"],["Sep 2024 - encrypted-app migration order","¶45"],["May 2025 - post-subpoena: delete again","¶56"],["2025 - repeated directives to CC-2","¶¶55-56"]].map((r,i)=><g key={i}><rect x="56" y={92+i*62} width="268" height="48" rx="9" fill={RED} opacity="0.10"/><rect x="56" y={92+i*62} width="268" height="48" rx="9" fill="none" stroke={RED} opacity="0.6"/><text x="70" y={112+i*62} fill={txt} fontSize="10">{r[0]}</text><text x="70" y={128+i*62} fill={RED} fontSize="9" fontFamily="monospace">{r[1]}</text></g>)}<rect x="400" y="140" width="264" height="72" rx="9" fill={GRN} opacity="0.12"/><rect x="400" y="140" width="264" height="72" rx="9" fill="none" stroke={GRN}/><text x="532" y="166" fill={GRN} fontSize="10" fontWeight="700" textAnchor="middle">Committee forensic expert:</text><text x="532" y="184" fill={txt} fontSize="10" textAnchor="middle">deleted items “appear to be spam”</text><text x="532" y="200" fill={muted} fontSize="9" fontFamily="monospace" textAnchor="middle">Dkt.17 at 8-9</text><text x="532" y="262" fill={faint} fontSize="11" textAnchor="middle" fontStyle="italic">Count 11s: not charged</text><text x="360" y="404" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">the grand jury drew this line - Rule 14 asks the Court to honor it</text></svg>;}if(id==="ile-firewall"&&variant===1){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">OBSTRUCTION TIMELINE - WHO, WHEN, CHARGED AGAINST WHOM</text><line x1="70" y1="230" x2="660" y2="230" stroke={border} strokeWidth="2"/>{[["Sep 2024",140,"Chid → CC-2: delete threads",RED,-1],["May 2025",340,"grand jury subpoenas land",ORN,1],["May 2025",420,"Chid → CC-2: delete again (¶56)",RED,-1],["Jul 2026",560,"S-1 filed - 11s vs Chidambaran ONLY",PUR,1]].map((e,i)=><g key={i}><circle cx={e[1]} cy="230" r="6" fill={e[3]}/><line x1={e[1]} y1="230" x2={e[1]} y2={e[4]<0?166:294} stroke={e[3]} strokeDasharray="3 3"/><text x={e[1]} y={e[4]<0?150:314} fill={e[3]} fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="monospace">{e[0]}</text><text x={e[1]} y={e[4]<0?134:330} fill={txt} fontSize="9" textAnchor="middle">{e[2]}</text></g>)}<rect x="120" y="356" width="480" height="40" rx="8" fill={GRN} opacity="0.10"/><text x="360" y="381" fill={GRN} fontSize="10" fontWeight="700" textAnchor="middle">Naqvi’s deleted items: “appear to be spam” - the company’s own expert</text></svg>;}if(id==="ile-fakestack"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">SHOWN TO INVESTOR A - DEC 17, 2023 DILIGENCE EMAIL (¶34)</text>{[["Entity 1","$143M",143,"#7f1d1d"],["Entity 2","$50M",50,"#991b1b"],["Entity 3","$29M",29,"#b91c1c"],["Entity 4","$5M",5,"#dc2626"]].map((e,i,arr)=>{const total=227;const H=280;let y=350;for(let k=arr.length-1;k>i;k--)y-=arr[k][2]/total*H;const h=e[2]/total*H;return <g key={i}><rect x="180" y={y-h} width="150" height={h} fill={e[3]} stroke={bg} strokeWidth="1.5"/><text x="345" y={y-h/2+4} fill={txt} fontSize="11" fontWeight="700">{e[0]} · {e[1]}</text></g>;})}<text x="255" y="392" fill={RED} fontSize="13" fontWeight="800" textAnchor="middle">$227M FABRICATED</text><rect x="490" y="330" width="150" height="20" fill={GRN} opacity="0.8"/><text x="565" y="322" fill={GRN} fontSize="10" fontWeight="700" textAnchor="middle">what was real</text><text x="565" y="392" fill={muted} fontSize="9" textAnchor="middle">≥90% fabricated (S-1 ¶2)</text><text x="360" y="410" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">itemized by customer, in the company’s own diligence channel</text></svg>;}if(id==="ile-chron"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">EXPOSURE CHRONOLOGY - REPORT TO APPROVAL (COUNTS NINE-TEN)</text><line x1="70" y1="210" x2="660" y2="210" stroke={border} strokeWidth="2"/>{[["AUG 29",120,"Short report publishes",RED,"stock −50% in a day"],["SEP 5",280,"Lender meeting · NYC",ORN,"in-person assurances"],["SEP 10",420,"CC-3 false confirmation",ORN,"“no liabilities” retold"],["OCT 11",580,"Lender B approves ~$2.5M",GRN,"Count TEN transfer - in writing"]].map((e,i)=><g key={i}><circle cx={e[1]} cy="210" r="7" fill={e[3]}/><text x={e[1]} y="180" fill={e[3]} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="monospace">{e[0]}</text><text x={e[1]} y="244" fill={txt} fontSize="10" fontWeight="600" textAnchor="middle">{e[2]}</text><text x={e[1]} y="260" fill={muted} fontSize="9" textAnchor="middle">{e[4]}</text></g>)}<rect x="470" y="290" width="220" height="54" rx="9" fill={GRN} opacity="0.12"/><rect x="470" y="290" width="220" height="54" rx="9" fill="none" stroke={GRN}/><text x="580" y="312" fill={GRN} fontSize="11" fontWeight="800" textAnchor="middle">APPROVED</text><text x="580" y="330" fill={muted} fontSize="9" textAnchor="middle">the lender saw it and said yes</text><text x="360" y="400" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">concealment from a counterparty that approved the transfer - the chronology is the defense</text></svg>;}if(id==="ile-mostculpable"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><rect x="80" y="70" width="560" height="240" rx="14" fill={card} stroke={border}/><text x="120" y="150" fill={GRN} fontSize="72" fontFamily="Georgia, serif">“</text><text x="360" y="160" fill={txt} fontSize="16" fontWeight="600" textAnchor="middle">There is no more culpable defendant in this case</text><text x="360" y="188" fill={txt} fontSize="16" fontWeight="600" textAnchor="middle">than this particular defendant.</text><text x="360" y="246" fill={muted} fontSize="10" fontFamily="monospace" textAnchor="middle">THE GOVERNMENT · APR 22, 2026 · DETENTION HEARING · TR. 8:16-22</text><rect x="200" y="264" width="320" height="26" rx="13" fill={GRN} opacity="0.14"/><text x="360" y="281" fill={GRN} fontSize="10" fontWeight="800" textAnchor="middle">SAID OF THE CO-DEFENDANT - NOT OF MR. NAQVI</text><text x="360" y="356" fill={faint} fontSize="10" textAnchor="middle">the record the government cannot take back</text><text x="360" y="376" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">use: role differentiation · severance · apportionment · sentencing</text></svg>;}if(id==="ile-comparators"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">EXECUTIVE FRAUD SENTENCES · MONTHS · POST-TRIAL UNLESS NOTED</text>{[["Milton (Nikola)",48,GRN,""],["Watson (Ozy)",116,ORN,"E.D.N.Y."],["Ng (Goldman/1MDB)",120,ORN,""],["Holmes (Theranos)",135,RED,""],["Balwani (Theranos)",155,RED,"the number two"],["Hwang (Archegos)",216,RED,""]].map((e,i)=>{const w=e[1]*2.1;return <g key={i}><text x="150" y={84+i*48} fill={txt} fontSize="10" textAnchor="end">{e[0]}</text><rect x="160" y={70+i*48} width={w} height="22" rx="5" fill={e[2]} opacity="0.75"/><text x={166+w} y={85+i*48} fill={txt} fontSize="10" fontWeight="700">{e[1]}</text><text x={166+w+30} y={85+i*48} fill={muted} fontSize="8" fontFamily="monospace">{e[3]}</text></g>;})}<line x1={160+120*2.1} y1="58" x2={160+120*2.1} y2="368" stroke={PUR} strokeWidth="2" strokeDasharray="7 5"/><text x={160+120*2.1} y="390" fill={PUR} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="monospace">§225 MANDATORY FLOOR - 120 MO</text><text x="360" y="412" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">a non-1s plea moves the whole board below the line</text></svg>;}if(id==="chid-gatekeepers"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE GATEKEEPER RING</text><circle cx="360" cy="215" r="66" fill={card} stroke={PUR} strokeWidth="2"/><text x="360" y="210" fill={txt} fontSize="12" fontWeight="800" textAnchor="middle">THE</text><text x="360" y="228" fill={txt} fontSize="12" fontWeight="800" textAnchor="middle">NUMBERS</text>{[["OUTSIDE AUDITOR","revenue confirmations · annual audits",360,78],["DEAL COUNSEL","S-4 · 8-K · every public filing",610,215],["SPAC DILIGENCE","18 months · data room · Olivier",360,362],["LENDER DILIGENCE","draw certificates · covenants",110,215]].map((n,i)=><g key={i}><line x1="360" y1="215" x2={n[2]} y2={n[3]} stroke={border} strokeDasharray="4 4"/><rect x={n[2]-105} y={n[3]-26} width="210" height="52" rx="10" fill={card} stroke={GRN}/><text x={n[2]} y={n[3]-6} fill={GRN} fontSize="10" fontWeight="800" textAnchor="middle">✓ {n[0]}</text><text x={n[2]} y={n[3]+12} fill={muted} fontSize="8.5" textAnchor="middle">{n[1]}</text></g>)}<text x="360" y="404" fill={faint} fontSize="10" textAnchor="middle">the government must prove he intended to deceive the professionals he hired to check the numbers</text></svg>;}if(id==="chid-gatekeepers"&&variant===1){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">FIVE YEARS OF SIGN-OFFS</text><line x1="70" y1="220" x2="660" y2="220" stroke={border} strokeWidth="2"/>{[["2021",130,"audit ✓"],["2022",240,"audit ✓"],["2023",350,"audit ✓ · S-4 counsel ✓"],["APR 2024",480,"SPAC close · 8-K counsel ✓"],["2024",610,"lender draws certified ✓"]].map((e,i)=><g key={i}><circle cx={e[1]} cy="220" r="6" fill={GRN}/><text x={e[1]} y="196" fill={GRN} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="monospace">{e[0]}</text><text x={e[1]} y="250" fill={txt} fontSize="9" textAnchor="middle">{e[2]}</text></g>)}<rect x="150" y="300" width="420" height="56" rx="10" fill={PUR} opacity="0.10"/><rect x="150" y="300" width="420" height="56" rx="10" fill="none" stroke={PUR}/><text x="360" y="324" fill={PUR} fontSize="11" fontWeight="700" textAnchor="middle">Every number the indictment calls a lie passed a professional review</text><text x="360" y="342" fill={muted} fontSize="9" textAnchor="middle">reliance on professionals - the shape of reasonable doubt in a public-company case</text></svg>;}if(id==="chid-equity"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">RODE IT TO ZERO - FOUNDER STAKE VALUE</text><line x1="80" y1="340" x2="660" y2="340" stroke={border}/><path d="M110 110 L300 130 L430 240 L520 336 L620 338" fill="none" stroke={PUR} strokeWidth="3"/><circle cx="110" cy="110" r="7" fill={PUR}/><text x="118" y="98" fill={txt} fontSize="11" fontWeight="800">APR 2024 · the bell</text><text x="118" y="114" fill={muted} fontSize="9">~50.37M shares · greater than $500M on paper</text><circle cx="520" cy="336" r="6" fill={ORN}/><text x="510" y="316" fill={ORN} fontSize="9" fontWeight="700" textAnchor="end">DEC 20 2024 · Ch.11</text><circle cx="620" cy="338" r="6" fill={RED}/><text x="620" y="362" fill={RED} fontSize="10" fontWeight="800" textAnchor="middle">MAR 6 2025 · Ch.7 · $0</text><rect x="330" y="70" width="300" height="52" rx="10" fill={GRN} opacity="0.10"/><rect x="330" y="70" width="300" height="52" rx="10" fill="none" stroke={GRN}/><text x="480" y="92" fill={GRN} fontSize="11" fontWeight="800" textAnchor="middle">NO STOCK SALES ALLEGED</text><text x="480" y="110" fill={muted} fontSize="9" textAnchor="middle">looters take the money out - he went down with it</text><text x="360" y="404" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">motive rebuttal · forfeiture: paper value at listing vs realized value of zero</text></svg>;}if(id==="chid-225"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">18 U.S.C. §225 - THE DUSTED-OFF STATUTE</text><line x1="70" y1="180" x2="660" y2="180" stroke={border} strokeWidth="2"/><circle cx="100" cy="180" r="8" fill={ORN}/><text x="100" y="152" fill={ORN} fontSize="11" fontWeight="800" textAnchor="middle" fontFamily="monospace">1990</text><text x="100" y="212" fill={muted} fontSize="9" textAnchor="middle">enacted - S&amp;L crisis</text>{[210,285,395,470].map((x,i)=><circle key={i} cx={x} cy="180" r="3" fill={faint}/>)}<text x="345" y="152" fill={faint} fontSize="9" textAnchor="middle" fontStyle="italic">three decades · a handful of reported charges</text><circle cx="620" cy="180" r="9" fill={RED}/><text x="620" y="150" fill={RED} fontSize="11" fontWeight="800" textAnchor="middle" fontFamily="monospace">2026</text><text x="620" y="212" fill={txt} fontSize="9" textAnchor="middle" fontWeight="700">this case</text><rect x="110" y="252" width="500" height="46" rx="9" fill={PUR} opacity="0.10"/><rect x="110" y="252" width="500" height="46" rx="9" fill="none" stroke={PUR}/><text x="360" y="272" fill={PUR} fontSize="10" fontWeight="700" textAnchor="middle">No Second Circuit gloss: “organize” · “manage” · “supervise” · “gross receipts”</text><text x="360" y="288" fill={muted} fontSize="9" textAnchor="middle">Rule 12 construction motion · rule of lenity · charge conference leverage</text><rect x="180" y="326" width="360" height="30" rx="15" fill={RED} opacity="0.14"/><text x="360" y="346" fill={RED} fontSize="11" fontWeight="800" textAnchor="middle" fontFamily="monospace">MANDATORY FLOOR: 120 MONTHS</text><text x="360" y="398" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">the count that prices every plea - and the first count to attack</text></svg>;}if(id==="chid-ciminelli"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE CIMINELLI FILTER - 598 U.S. 306 (2023)</text>{["4s","5s","6s","7s","8s","9s","10s"].map((w,i)=><g key={w}><rect x={130+i*70} y="60" width="52" height="26" rx="13" fill={BLU} opacity="0.16"/><text x={156+i*70} y="77" fill={BLU} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="monospace">{w}</text></g>)}<path d="M150 100 L570 100 L440 190 L280 190 Z" fill={card} stroke={border}/><text x="360" y="140" fill={txt} fontSize="11" fontWeight="700" textAnchor="middle">does the scheme target a traditional</text><text x="360" y="158" fill={txt} fontSize="11" fontWeight="700" textAnchor="middle">PROPERTY object?</text><rect x="96" y="240" width="250" height="88" rx="10" fill={GRN} opacity="0.10"/><rect x="96" y="240" width="250" height="88" rx="10" fill="none" stroke={GRN}/><text x="221" y="266" fill={GRN} fontSize="10" fontWeight="800" textAnchor="middle">PASSES</text><text x="221" y="286" fill={txt} fontSize="9.5" textAnchor="middle">wires moving lender money</text><text x="221" y="302" fill={muted} fontSize="9" textAnchor="middle">$100M in draws - property, plainly</text><rect x="374" y="240" width="250" height="88" rx="10" fill={ORN} opacity="0.10"/><rect x="374" y="240" width="250" height="88" rx="10" fill="none" stroke={ORN}/><text x="499" y="266" fill={ORN} fontSize="10" fontWeight="800" textAnchor="middle">RUNS AMBER</text><text x="499" y="286" fill={txt} fontSize="9.5" textAnchor="middle">diligence emails · auditor confirmations</text><text x="499" y="302" fill={muted} fontSize="9" textAnchor="middle">attenuated from any property deprivation</text><text x="360" y="380" fill={faint} fontSize="9" textAnchor="middle" fontFamily="monospace">test every charged transmission · Rule 29 at close · preserve for appeal</text></svg>;}if(id==="chid-cooperator"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE COOPERATOR’S LEDGER - CC-2</text><circle cx="360" cy="200" r="52" fill={card} stroke={RED} strokeWidth="2"/><text x="360" y="196" fill={txt} fontSize="14" fontWeight="800" textAnchor="middle">CC-2</text><text x="360" y="214" fill={RED} fontSize="8.5" textAnchor="middle" fontFamily="monospace">COOPERATING</text>{[["SOLE SIGNATORY","every Entity 1 account · 2021-2024",360,72],["HAND ON EACH WIRE","executed the $145M the chart shows (¶27)",620,200],["THE DELETIONS WERE HIS","his account · Sep 2024 · his device",360,342],["5K1.1 MOTIVE","a sentence only the government can move to cut",100,200]].map((n,i)=><g key={i}><line x1="360" y1="200" x2={n[2]} y2={n[3]} stroke={border} strokeDasharray="4 4"/><rect x={n[2]-100} y={n[3]-26} width="200" height="52" rx="10" fill={card} stroke={RED} opacity="0.9"/><text x={n[2]} y={n[3]-6} fill={RED} fontSize="9.5" fontWeight="800" textAnchor="middle">{n[0]}</text><text x={n[2]} y={n[3]+12} fill={muted} fontSize="8.5" textAnchor="middle">{n[1]}</text></g>)}<text x="360" y="398" fill={faint} fontSize="10" textAnchor="middle">Count 11s rests on his memory of unwritten instructions</text></svg>;}if(id==="chid-bail"&&variant===0){return <svg viewBox="0 0 720 420" style={{width:"100%",height:"auto",display:"block"}} xmlns="http://www.w3.org/2000/svg"><text x="24" y="30" fill={muted} fontSize="10" fontFamily="monospace" letterSpacing="2">THE COMPLIANCE RECORD</text><line x1="70" y1="200" x2="660" y2="200" stroke={border} strokeWidth="2"/>{[["APR 2026",120,"detained on de novo review",RED],["MAY 2026",260,"released - consented $15M package",GRN],["JUN-AUG",430,"home confinement · Potomac · zero violations",GRN],["SEP 14",600,"status conference",PUR]].map((e,i)=><g key={i}><circle cx={e[1]} cy="200" r="7" fill={e[3]}/><text x={e[1]} y="172" fill={e[3]} fontSize="10" fontWeight="800" textAnchor="middle" fontFamily="monospace">{e[0]}</text><text x={e[1]} y="232" fill={txt} fontSize="9" textAnchor="middle">{e[2]}</text></g>)}{[300,340,380,420,460,500].map((x,i)=><text key={i} x={x+10} y="264" fill={GRN} fontSize="11" textAnchor="middle">✓</text>)}<rect x="160" y="300" width="400" height="52" rx="10" fill={GRN} opacity="0.10"/><rect x="160" y="300" width="400" height="52" rx="10" fill="none" stroke={GRN}/><text x="360" y="322" fill={GRN} fontSize="11" fontWeight="700" textAnchor="middle">Every week of clean compliance is currency</text><text x="360" y="340" fill={muted} fontSize="9" textAnchor="middle">conditions modifications · travel · sentencing mitigation</text></svg>;}
  if (id === "juul-altria-flow" && variant === 0) {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="altG" x1="0" x2="1">
            <stop offset="0" stopColor="#0c4a6e" />
            <stop offset="1" stopColor="#075985" />
          </linearGradient>
          <linearGradient id="juulG" x1="0" x2="1">
            <stop offset="0" stopColor="#dc2626" />
            <stop offset="1" stopColor="#991b1b" />
          </linearGradient>
          <marker
            id="ar1"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="#64748b" />
          </marker>
        </defs>
        <text
          x={W / 2}
          y="32"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          December 2018: The Altria–JUUL Deal
        </text>
        <text
          x={W / 2}
          y="52"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          A horizontal market allocation alleged under the Sherman Act
        </text>
        <rect
          x="40"
          y="100"
          width="180"
          height="120"
          rx="14"
          fill="url(#altG)"
          stroke="#0369a1"
          strokeWidth="2"
        />
        <text
          x="130"
          y="138"
          textAnchor="middle"
          fill="#fff"
          fontSize="22"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Altria
        </text>
        <text
          x="130"
          y="160"
          textAnchor="middle"
          fill="#bae6fd"
          fontSize="10"
          fontFamily="system-ui"
        >
          (Marlboro / Philip Morris USA)
        </text>
        <text
          x="130"
          y="195"
          textAnchor="middle"
          fill="#fff"
          fontSize="20"
          fontWeight="700"
          fontFamily="monospace"
        >
          $12.8B
        </text>
        <line
          x1="220"
          y1="160"
          x2="335"
          y2="160"
          stroke="#64748b"
          strokeWidth="2.5"
          markerEnd="url(#ar1)"
        />
        <text
          x="277"
          y="148"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="monospace"
        >
          CASH
        </text>
        <rect
          x="335"
          y="100"
          width="200"
          height="120"
          rx="14"
          fill="url(#juulG)"
          stroke="#7f1d1d"
          strokeWidth="2"
        />
        <text
          x="435"
          y="138"
          textAnchor="middle"
          fill="#fff"
          fontSize="22"
          fontWeight="700"
          fontFamily="system-ui"
        >
          JUUL Labs
        </text>
        <text
          x="435"
          y="160"
          textAnchor="middle"
          fill="#fecaca"
          fontSize="10"
          fontFamily="system-ui"
        >
          (JLI)
        </text>
        <text
          x="435"
          y="195"
          textAnchor="middle"
          fill="#fff"
          fontSize="18"
          fontWeight="700"
          fontFamily="monospace"
        >
          35% equity
        </text>
        <line
          x1="535"
          y1="160"
          x2="650"
          y2="160"
          stroke={RED}
          strokeWidth="2.5"
          markerEnd="url(#ar1)"
          strokeDasharray="6 3"
        />
        <rect
          x="650"
          y="135"
          width="65"
          height="50"
          rx="6"
          fill={RED + "22"}
          stroke={RED}
          strokeWidth="1.5"
        />
        <text
          x="682"
          y="155"
          textAnchor="middle"
          fill={RED}
          fontSize="9"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Market
        </text>
        <text
          x="682"
          y="170"
          textAnchor="middle"
          fill={RED}
          fontSize="9"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Exit
        </text>
        <line
          x1="130"
          y1="220"
          x2="130"
          y2="285"
          stroke={ORN}
          strokeWidth="2"
          markerEnd="url(#ar1)"
        />
        <line
          x1="130"
          y1="285"
          x2="280"
          y2="285"
          stroke={ORN}
          strokeWidth="2"
        />
        <line
          x1="280"
          y1="285"
          x2="280"
          y2="305"
          stroke={ORN}
          strokeWidth="2"
          markerEnd="url(#ar1)"
        />
        <line
          x1="130"
          y1="285"
          x2="280"
          y2="285"
          stroke={ORN}
          strokeWidth="2"
          strokeDasharray="0"
        />
        <rect
          x="60"
          y="305"
          width="180"
          height="60"
          rx="6"
          fill={ORN + "15"}
          stroke={ORN}
          strokeWidth="1"
        />
        <text
          x="150"
          y="328"
          textAnchor="middle"
          fill={ORN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          MarkTen / Green Smoke
        </text>
        <text
          x="150"
          y="346"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          withdrawn from US market
        </text>
        <text
          x="150"
          y="358"
          textAnchor="middle"
          fill={muted}
          fontSize="8"
          fontFamily="system-ui"
        >
          (per se §1 violation alleged)
        </text>
        <rect
          x="260"
          y="305"
          width="180"
          height="60"
          rx="6"
          fill={BLU + "15"}
          stroke={BLU}
          strokeWidth="1"
        />
        <text
          x="350"
          y="328"
          textAnchor="middle"
          fill={BLU}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          400,000+ retail locations
        </text>
        <text
          x="350"
          y="346"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          distribution access
        </text>
        <text
          x="350"
          y="358"
          textAnchor="middle"
          fill={muted}
          fontSize="8"
          fontFamily="system-ui"
        >
          (Marlboro infrastructure)
        </text>
        <rect
          x="460"
          y="305"
          width="180"
          height="60"
          rx="6"
          fill={PUR + "15"}
          stroke={PUR}
          strokeWidth="1"
        />
        <text
          x="550"
          y="328"
          textAnchor="middle"
          fill={PUR}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          FDA / lobbying
        </text>
        <text
          x="550"
          y="346"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          regulatory expertise
        </text>
        <text
          x="550"
          y="358"
          textAnchor="middle"
          fill={muted}
          fontSize="8"
          fontFamily="system-ui"
        >
          (40-yr Big Tobacco playbook)
        </text>
        <line
          x1="435"
          y1="220"
          x2="435"
          y2="285"
          stroke={BLU}
          strokeWidth="2"
        />
        <line
          x1="435"
          y1="285"
          x2="350"
          y2="285"
          stroke={BLU}
          strokeWidth="2"
          markerEnd="url(#ar1)"
        />
        <line
          x1="435"
          y1="285"
          x2="550"
          y2="285"
          stroke={PUR}
          strokeWidth="2"
          markerEnd="url(#ar1)"
        />
        <text
          x={W / 2}
          y={H - 10}
          textAnchor="middle"
          fill={faint}
          fontSize="9"
          fontFamily="monospace"
        >
          Antitrust Class: 3:20-cv-02345-WHO · Trial Sept 28, 2026
        </text>
      </svg>
    );
  }
  if (id === "juul-altria-flow" && variant === 1) {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Sherman Act §1: Per Se Horizontal Market Allocation
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          Two competitors. One agreement. One exits.
        </text>
        <rect
          x="80"
          y="90"
          width="240"
          height="240"
          rx="12"
          fill={dark ? "#1e293b" : "#f1f5f9"}
          stroke={BLU}
          strokeWidth="2"
        />
        <text
          x="200"
          y="118"
          textAnchor="middle"
          fill={BLU}
          fontSize="14"
          fontWeight="700"
          fontFamily="system-ui"
        >
          BEFORE: Two Competitors
        </text>
        <circle cx="140" cy="180" r="40" fill={BLU} opacity="0.85" />
        <text
          x="140"
          y="186"
          textAnchor="middle"
          fill="#fff"
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Altria
        </text>
        <text
          x="140"
          y="240"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          MarkTen, Green Smoke
        </text>
        <text
          x="140"
          y="254"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          e-cig competitor
        </text>
        <circle cx="260" cy="180" r="40" fill={RED} opacity="0.85" />
        <text
          x="260"
          y="186"
          textAnchor="middle"
          fill="#fff"
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui"
        >
          JUUL
        </text>
        <text
          x="260"
          y="240"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          closed-system pod
        </text>
        <text
          x="260"
          y="254"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          market leader
        </text>
        <line
          x1="180"
          y1="180"
          x2="220"
          y2="180"
          stroke={ORN}
          strokeWidth="2"
          strokeDasharray="3 3"
        />
        <text
          x="200"
          y="170"
          textAnchor="middle"
          fill={ORN}
          fontSize="10"
          fontFamily="monospace"
          fontWeight="700"
        >
          vs.
        </text>
        <text
          x="200"
          y="290"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
          fontStyle="italic"
        >
          Active competition for closed-system market
        </text>
        <line
          x1="335"
          y1="200"
          x2="395"
          y2="200"
          stroke={ORN}
          strokeWidth="3"
          markerEnd="url(#ar2)"
        />
        <defs>
          <marker
            id="ar2"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill={ORN} />
          </marker>
        </defs>
        <text
          x="365"
          y="190"
          textAnchor="middle"
          fill={ORN}
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          DEC 2018
        </text>
        <text
          x="365"
          y="220"
          textAnchor="middle"
          fill={ORN}
          fontSize="9"
          fontFamily="monospace"
        >
          $12.8B
        </text>
        <rect
          x="400"
          y="90"
          width="240"
          height="240"
          rx="12"
          fill={dark ? "#1e293b" : "#f1f5f9"}
          stroke={RED}
          strokeWidth="2"
        />
        <text
          x="520"
          y="118"
          textAnchor="middle"
          fill={RED}
          fontSize="14"
          fontWeight="700"
          fontFamily="system-ui"
        >
          AFTER: One Player
        </text>
        <circle cx="520" cy="180" r="55" fill={RED} opacity="0.95" />
        <text
          x="520"
          y="178"
          textAnchor="middle"
          fill="#fff"
          fontSize="14"
          fontWeight="700"
          fontFamily="system-ui"
        >
          JUUL Labs
        </text>
        <text
          x="520"
          y="195"
          textAnchor="middle"
          fill="#fff"
          fontSize="9"
          fontFamily="system-ui"
        >
          + 35% Altria stake
        </text>
        <text
          x="520"
          y="262"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
          fontStyle="italic"
        >
          Altria withdraws - competitor eliminated
        </text>
        <text
          x="520"
          y="280"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          Altria gets 35% of the surviving
        </text>
        <text
          x="520"
          y="293"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          company. Customer choice eliminated.
        </text>
        <rect
          x="40"
          y="350"
          width={W - 80}
          height="55"
          rx="8"
          fill={ORN + "15"}
          stroke={ORN + "55"}
        />
        <text
          x={W / 2}
          y="370"
          textAnchor="middle"
          fill={ORN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Plaintiffs' theory: per se §1 horizontal market allocation
        </text>
        <text
          x={W / 2}
          y="388"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          Class certified Feb 26, 2026 · Trial September 28, 2026 · 31 IPP
          states + DPP class
        </text>
      </svg>
    );
  }
  if (id === "juul-nicotine" && variant === 0) {
    const data = [
      { n: "JUUL pod", v: 59, c: RED },
      { n: "Marlboro (per cigarette)", v: 12, c: ORN },
      { n: "NJOY pod", v: 25, c: BLU },
      { n: "blu PLUS+", v: 24, c: BLU },
      { n: "FDA-approved nicotine gum", v: 4, c: GRN },
    ];
    const max = 60;
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Nicotine Concentration: JUUL vs The Market
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          milligrams per milliliter (mg/mL)
        </text>
        {data.map((d, i) => {
          const y = 90 + i * 52;
          const w = (d.v / max) * 450;
          return (
            <g key={i}>
              <text
                x="20"
                y={y + 22}
                fill={txt}
                fontSize="11"
                fontWeight={i === 0 ? "700" : "500"}
                fontFamily="system-ui"
              >
                {d.n}
              </text>
              <rect
                x="220"
                y={y + 5}
                width={w}
                height="32"
                rx="3"
                fill={d.c}
                opacity={i === 0 ? "1" : "0.7"}
              />
              <text
                x={220 + w + 8}
                y={y + 27}
                fill={d.c}
                fontSize="13"
                fontWeight="700"
                fontFamily="monospace"
              >
                {d.v} mg/mL
              </text>
            </g>
          );
        })}
        <line
          x1="220"
          y1="80"
          x2="220"
          y2="350"
          stroke={border}
          strokeWidth="1"
        />
        <line
          x1="220"
          y1="350"
          x2="700"
          y2="350"
          stroke={border}
          strokeWidth="1"
        />
        <rect
          x="40"
          y="370"
          width={W - 80}
          height="40"
          rx="6"
          fill={RED + "12"}
          stroke={RED + "44"}
        />
        <text
          x={W / 2}
          y="390"
          textAnchor="middle"
          fill={RED}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          ≈ 5x the FDA-approved cessation gum · ≈ 2.5x closed-system competitors
        </text>
        <text
          x={W / 2}
          y="404"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          Source: FDA MDO June 2022 (since stayed by 5th Cir.); product labels
        </text>
      </svg>
    );
  }
  if (id === "juul-nicotine" && variant === 1) {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="32"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          One JUUL Pod ≈ One Pack of Cigarettes
        </text>
        <text
          x={W / 2}
          y="52"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          visualizing the dose
        </text>
        <rect
          x="60"
          y="100"
          width="280"
          height="280"
          rx="12"
          fill={dark ? "#1e293b" : "#fef2f2"}
          stroke={RED}
          strokeWidth="2"
        />
        <text
          x="200"
          y="125"
          textAnchor="middle"
          fill={RED}
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui"
        >
          1 JUUL POD
        </text>
        <rect
          x="180"
          y="145"
          width="40"
          height="100"
          rx="6"
          fill={RED}
          opacity="0.9"
        />
        <rect x="190" y="155" width="20" height="12" rx="2" fill="#fef2f2" />
        <text
          x="200"
          y="265"
          textAnchor="middle"
          fill={txt}
          fontSize="22"
          fontWeight="700"
          fontFamily="monospace"
        >
          59 mg/mL
        </text>
        <text
          x="200"
          y="285"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
        >
          ~0.7 mL liquid
        </text>
        <text
          x="200"
          y="305"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
        >
          ~41 mg total nicotine
        </text>
        <text
          x="200"
          y="345"
          textAnchor="middle"
          fill={RED}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          ≈ ENTIRE pack of cigarettes
        </text>
        <text
          x="380"
          y="240"
          textAnchor="middle"
          fill={txt}
          fontSize="20"
          fontFamily="monospace"
        >
          ≈
        </text>
        <rect
          x="420"
          y="100"
          width="280"
          height="280"
          rx="12"
          fill={dark ? "#1e293b" : "#fef2f2"}
          stroke={ORN}
          strokeWidth="2"
        />
        <text
          x="560"
          y="125"
          textAnchor="middle"
          fill={ORN}
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui"
        >
          20 CIGARETTES (1 pack)
        </text>
        {Array.from({ length: 20 }).map((_, i) => {
          const col = i % 5,
            row = Math.floor(i / 5);
          return (
            <g key={i}>
              <rect
                x={460 + col * 30}
                y={150 + row * 38}
                width="22"
                height="32"
                rx="2"
                fill="#fff"
                stroke={ORN}
                strokeWidth="0.8"
              />
              <rect
                x={460 + col * 30}
                y={150 + row * 38}
                width="22"
                height="6"
                rx="2"
                fill={ORN}
              />
            </g>
          );
        })}
        <text
          x="560"
          y="345"
          textAnchor="middle"
          fill={ORN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          ~0.6 mg nicotine each → ~12 mg/mL avg
        </text>
        <text
          x={W / 2}
          y="395"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
          fontStyle="italic"
        >
          75% of SFUSD students who recognized JUUL did not know it contained
          nicotine (Halpern-Felsher 2021)
        </text>
      </svg>
    );
  }
  if (id === "juul-sonner") {
    const v = variant;
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <marker
            id="ar3"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill={muted} />
          </marker>
        </defs>
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          The Sonner-Pak Two-Step
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          JLI's symmetrical SJ logic - it cannot lose both ways
        </text>
        {v === 0 ? (
          <g>
            <rect
              x={W / 2 - 150}
              y="80"
              width="300"
              height="60"
              rx="8"
              fill={BLU + "15"}
              stroke={BLU}
              strokeWidth="2"
            />
            <text
              x={W / 2}
              y="103"
              textAnchor="middle"
              fill={BLU}
              fontSize="11"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Question 1
            </text>
            <text
              x={W / 2}
              y="123"
              textAnchor="middle"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Does plaintiff have legal damages?
            </text>
            <line
              x1={W / 2 - 100}
              y1="140"
              x2="200"
              y2="180"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar3)"
            />
            <line
              x1={W / 2 + 100}
              y1="140"
              x2="540"
              y2="180"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar3)"
            />
            <text
              x={W / 2 - 115}
              y="165"
              textAnchor="middle"
              fill={GRN}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              YES
            </text>
            <text
              x={W / 2 + 115}
              y="165"
              textAnchor="middle"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              NO (Pak admission)
            </text>
            <rect
              x="60"
              y="190"
              width="280"
              height="80"
              rx="8"
              fill={ORN + "15"}
              stroke={ORN}
              strokeWidth="2"
            />
            <text
              x="200"
              y="213"
              textAnchor="middle"
              fill={ORN}
              fontSize="11"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Sonner v. Premier Nutrition (9th Cir. 2020)
            </text>
            <text
              x="200"
              y="233"
              textAnchor="middle"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              Adequate legal remedy exists →
            </text>
            <text
              x="200"
              y="248"
              textAnchor="middle"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              no equitable abatement available
            </text>
            <text
              x="200"
              y="263"
              textAnchor="middle"
              fill={ORN}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              EQUITY BARRED
            </text>
            <rect
              x="400"
              y="190"
              width="280"
              height="80"
              rx="8"
              fill={RED + "15"}
              stroke={RED}
              strokeWidth="2"
            />
            <text
              x="540"
              y="213"
              textAnchor="middle"
              fill={RED}
              fontSize="11"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Pak Deposition (Dec 21, 2021)
            </text>
            <text
              x="540"
              y="233"
              textAnchor="middle"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              "We don't have that information"
            </text>
            <text
              x="540"
              y="248"
              textAnchor="middle"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              on past program costs
            </text>
            <text
              x="540"
              y="263"
              textAnchor="middle"
              fill={RED}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              NO LEGAL DAMAGES
            </text>
            <line
              x1="200"
              y1="270"
              x2="200"
              y2="305"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar3)"
            />
            <line
              x1="540"
              y1="270"
              x2="540"
              y2="305"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar3)"
            />
            <rect
              x={W / 2 - 180}
              y="315"
              width="360"
              height="70"
              rx="10"
              fill={dark ? "#1e293b" : "#0f172a"}
              stroke={GRN}
              strokeWidth="2.5"
            />
            <text
              x={W / 2}
              y="340"
              textAnchor="middle"
              fill={GRN}
              fontSize="14"
              fontWeight="700"
              fontFamily="system-ui"
            >
              SUMMARY JUDGMENT FOR DEFENSE
            </text>
            <text
              x={W / 2}
              y="358"
              textAnchor="middle"
              fill="#94a3b8"
              fontSize="10"
              fontFamily="system-ui"
            >
              Either path leads to the same destination - 
            </text>
            <text
              x={W / 2}
              y="374"
              textAnchor="middle"
              fill="#94a3b8"
              fontSize="10"
              fontFamily="system-ui"
            >
              the argument is symmetrical and dispositive
            </text>
          </g>
        ) : (
          <g>
            <rect
              x="50"
              y="80"
              width={W - 100}
              height="55"
              rx="8"
              fill={BLU + "12"}
              stroke={BLU}
              strokeWidth="1.5"
            />
            <text
              x="80"
              y="105"
              fill={BLU}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              STEP 1
            </text>
            <text
              x="80"
              y="124"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Pak 30(b)(6): SFUSD has no quantifiable past program costs
            </text>
            <text
              x={W - 90}
              y="115"
              textAnchor="end"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              Dec 21, 2021
            </text>
            <line
              x1={W / 2}
              y1="135"
              x2={W / 2}
              y2="165"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar3)"
            />
            <rect
              x="50"
              y="170"
              width={W - 100}
              height="55"
              rx="8"
              fill={ORN + "12"}
              stroke={ORN}
              strokeWidth="1.5"
            />
            <text
              x="80"
              y="195"
              fill={ORN}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              STEP 2
            </text>
            <text
              x="80"
              y="214"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Sonner: equitable abatement unavailable absent inadequate legal
              remedy
            </text>
            <text
              x={W - 90}
              y="205"
              textAnchor="end"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              9th Cir. 2020
            </text>
            <line
              x1={W / 2}
              y1="225"
              x2={W / 2}
              y2="255"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar3)"
            />
            <rect
              x="50"
              y="260"
              width={W - 100}
              height="55"
              rx="8"
              fill={PUR + "12"}
              stroke={PUR}
              strokeWidth="1.5"
            />
            <text
              x="80"
              y="285"
              fill={PUR}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              RESULT
            </text>
            <text
              x="80"
              y="304"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Either plaintiff has legal damages (Sonner bars equity) OR
              plaintiff has none (no harm)
            </text>
            <line
              x1={W / 2}
              y1="315"
              x2={W / 2}
              y2="345"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar3)"
            />
            <rect
              x="180"
              y="350"
              width={W - 360}
              height="50"
              rx="10"
              fill={GRN + "22"}
              stroke={GRN}
              strokeWidth="2"
            />
            <text
              x={W / 2}
              y="375"
              textAnchor="middle"
              fill={GRN}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              SJ FOR DEFENSE
            </text>
            <text
              x={W / 2}
              y="391"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              symmetrical · cannot lose both ways
            </text>
          </g>
        )}
      </svg>
    );
  }
  if (id === "musk-money-flow" && variant === 0) {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <marker
            id="ar4"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill={ORN} />
          </marker>
        </defs>
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          The Donation Trail: Charity → For-Profit → Microsoft
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          $38M in charitable donations seeded a $135B+ for-profit equity stake
        </text>
        <rect
          x="40"
          y="90"
          width="160"
          height="100"
          rx="10"
          fill={GRN + "22"}
          stroke={GRN}
          strokeWidth="2"
        />
        <text
          x="120"
          y="118"
          textAnchor="middle"
          fill={GRN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          2015–2018
        </text>
        <text
          x="120"
          y="138"
          textAnchor="middle"
          fill={txt}
          fontSize="13"
          fontWeight="700"
          fontFamily="system-ui"
        >
          DONORS
        </text>
        <text
          x="120"
          y="158"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontFamily="system-ui"
        >
          Musk + others
        </text>
        <text
          x="120"
          y="178"
          textAnchor="middle"
          fill={GRN}
          fontSize="14"
          fontWeight="700"
          fontFamily="monospace"
        >
          ~$38M
        </text>
        <line
          x1="200"
          y1="140"
          x2="245"
          y2="140"
          stroke={ORN}
          strokeWidth="2.5"
          markerEnd="url(#ar4)"
        />
        <rect
          x="245"
          y="90"
          width="160"
          height="100"
          rx="10"
          fill={BLU + "22"}
          stroke={BLU}
          strokeWidth="2"
        />
        <text
          x="325"
          y="115"
          textAnchor="middle"
          fill={BLU}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          501(c)(3)
        </text>
        <text
          x="325"
          y="133"
          textAnchor="middle"
          fill={txt}
          fontSize="12"
          fontWeight="700"
          fontFamily="system-ui"
        >
          OpenAI Inc.
        </text>
        <text
          x="325"
          y="151"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          "tech belongs to
        </text>
        <text
          x="325"
          y="164"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          the world" - PX-003
        </text>
        <text
          x="325"
          y="182"
          textAnchor="middle"
          fill={BLU}
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          PUBLIC CHARITY
        </text>
        <line
          x1="405"
          y1="140"
          x2="450"
          y2="140"
          stroke={ORN}
          strokeWidth="2.5"
          markerEnd="url(#ar4)"
        />
        <text
          x="427"
          y="130"
          textAnchor="middle"
          fill={ORN}
          fontSize="8"
          fontWeight="700"
          fontFamily="monospace"
        >
          2019
        </text>
        <rect
          x="450"
          y="90"
          width="170"
          height="100"
          rx="10"
          fill={PUR + "22"}
          stroke={PUR}
          strokeWidth="2"
        />
        <text
          x="535"
          y="115"
          textAnchor="middle"
          fill={PUR}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          2019 LP Conversion
        </text>
        <text
          x="535"
          y="133"
          textAnchor="middle"
          fill={txt}
          fontSize="12"
          fontWeight="700"
          fontFamily="system-ui"
        >
          OpenAI LP
        </text>
        <text
          x="535"
          y="151"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          "capped-profit"
        </text>
        <text
          x="535"
          y="164"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          subsidiary structure
        </text>
        <text
          x="535"
          y="182"
          textAnchor="middle"
          fill={PUR}
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          FOR-PROFIT
        </text>
        <line
          x1="620"
          y1="140"
          x2="665"
          y2="140"
          stroke={ORN}
          strokeWidth="2.5"
          markerEnd="url(#ar4)"
        />
        <rect
          x="245"
          y="225"
          width="160"
          height="80"
          rx="10"
          fill={dark ? "#1e293b" : "#f8fafc"}
          stroke={muted}
          strokeWidth="1"
        />
        <text
          x="325"
          y="248"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Brockman journal
        </text>
        <text
          x="325"
          y="266"
          textAnchor="middle"
          fill={txt}
          fontSize="10"
          fontStyle="italic"
          fontFamily="system-ui"
        >
          "wrong to steal
        </text>
        <text
          x="325"
          y="280"
          textAnchor="middle"
          fill={txt}
          fontSize="10"
          fontStyle="italic"
          fontFamily="system-ui"
        >
          non-profit from Elon"
        </text>
        <text
          x="325"
          y="298"
          textAnchor="middle"
          fill={RED}
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          PX-163
        </text>
        <line
          x1="325"
          y1="190"
          x2="325"
          y2="225"
          stroke={RED}
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
        <rect
          x="450"
          y="225"
          width="170"
          height="80"
          rx="10"
          fill={dark ? "#1e293b" : "#f8fafc"}
          stroke={muted}
          strokeWidth="1"
        />
        <text
          x="535"
          y="248"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Kevin Scott / MS CTO
        </text>
        <text
          x="535"
          y="266"
          textAnchor="middle"
          fill={txt}
          fontSize="10"
          fontStyle="italic"
          fontFamily="system-ui"
        >
          internal email
        </text>
        <text
          x="535"
          y="280"
          textAnchor="middle"
          fill={txt}
          fontSize="10"
          fontFamily="system-ui"
        >
          questioning donor terms
        </text>
        <text
          x="535"
          y="298"
          textAnchor="middle"
          fill={RED}
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          PX-230 · 16 mo. pre-MS investment
        </text>
        <line
          x1="535"
          y1="190"
          x2="535"
          y2="225"
          stroke={RED}
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
        <rect
          x="35"
          y="320"
          width={W - 70}
          height="80"
          rx="10"
          fill={dark ? "#0f172a" : "#fef2f2"}
          stroke={ORN}
          strokeWidth="2.5"
        />
        <text
          x="60"
          y="348"
          fill={ORN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          RESULT (Oct 2025): Public Benefit Corporation conversion
        </text>
        <text x="60" y="370" fill={txt} fontSize="11" fontFamily="system-ui">
          Microsoft equity position: $135B+ · OpenAI valuation: $300B+
        </text>
        <text x="60" y="388" fill={muted} fontSize="10" fontFamily="system-ui">
          501(c)(3) original entity becomes minority stakeholder. Plaintiff
          seeks constructive trust on equity.
        </text>
      </svg>
    );
  }
  if (id === "musk-money-flow" && variant === 1) {
    const events = [
      {
        y: 0,
        x: 80,
        c: GRN,
        t: "2015",
        l: "Founding · 501(c)(3)",
        sub: "PX-003: 'tech belongs to the world'",
        ev: "$38M raised",
      },
      {
        y: 0,
        x: 230,
        c: ORN,
        t: "Jan 2016",
        l: "Yup email",
        sub: "DX-516 - Musk acknowledges 'less open' direction",
        ev: "",
      },
      {
        y: 0,
        x: 380,
        c: RED,
        t: "2017",
        l: "Brockman journal",
        sub: "PX-163: 'wrong to steal non-profit from Elon'",
        ev: "",
      },
      {
        y: 0,
        x: 530,
        c: PUR,
        t: "Mar 2018",
        l: "MS internal questions",
        sub: "PX-230: Kevin Scott email re donor terms",
        ev: "",
      },
      {
        y: 0,
        x: 680,
        c: RED,
        t: "Mar 2019",
        l: "LP Conversion",
        sub: "DX-862: 'Tell them I'm supportive'",
        ev: "$1B from MS",
      },
    ];
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Knowledge Timeline 2015–2019: The Five Exhibits
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          contemporaneous communications · plaintiff's case in five documents
        </text>
        <line
          x1="50"
          y1="200"
          x2={W - 30}
          y2="200"
          stroke={muted}
          strokeWidth="2"
        />
        {events.map((e, i) => (
          <g key={i}>
            <circle
              cx={e.x}
              cy="200"
              r="9"
              fill={e.c}
              stroke={dark ? "#0f172a" : "#fff"}
              strokeWidth="3"
            />
            <text
              x={e.x}
              y={i % 2 === 0 ? "178" : "230"}
              textAnchor="middle"
              fill={e.c}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              {e.t}
            </text>
            <line
              x1={e.x}
              y1={i % 2 === 0 ? "195" : "205"}
              x2={e.x}
              y2={i % 2 === 0 ? "265" : "135"}
              stroke={e.c}
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
            <rect
              x={e.x - 72}
              y={i % 2 === 0 ? "265" : "95"}
              width="144"
              height={e.ev ? "75" : "60"}
              rx="6"
              fill={dark ? "#1e293b" : "#f8fafc"}
              stroke={e.c + "66"}
              strokeWidth="1.5"
            />
            <text
              x={e.x}
              y={i % 2 === 0 ? "285" : "115"}
              textAnchor="middle"
              fill={e.c}
              fontSize="11"
              fontWeight="700"
              fontFamily="system-ui"
            >
              {e.l}
            </text>
            <foreignObject
              x={e.x - 66}
              y={i % 2 === 0 ? "291" : "121"}
              width="132"
              height="60"
            >
              <div
                style={{
                  fontSize: "8.5px",
                  color: muted,
                  fontFamily: "system-ui",
                  lineHeight: "1.4",
                  textAlign: "center",
                  fontStyle: "italic",
                }}
                xmlns="http://www.w3.org/1999/xhtml"
              >
                {e.sub}
              </div>
            </foreignObject>
            {e.ev && (
              <text
                x={e.x}
                y={i % 2 === 0 ? "330" : "160"}
                textAnchor="middle"
                fill={GRN}
                fontSize="9"
                fontWeight="700"
                fontFamily="monospace"
              >
                {e.ev}
              </text>
            )}
          </g>
        ))}
        <rect
          x="40"
          y="370"
          width={W - 80}
          height="38"
          rx="6"
          fill={ORN + "15"}
          stroke={ORN + "55"}
        />
        <text
          x={W / 2}
          y="392"
          textAnchor="middle"
          fill={ORN}
          fontSize="10"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Five exhibits, four years, three counts: Trust (XVIII) · Unjust
          Enrichment (IV) · Aiding & Abetting MS (XIX)
        </text>
      </svg>
    );
  }
  if (id === "musk-knowledge") {
    return renderDemoSVG("musk-money-flow", 1, th, dark);
  }
  if (id === "musk-phases") {
    const v = variant;
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          The Two Phases of Trial
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          Pretrial Order - equity claims first, market claims second
        </text>
        {v === 0 ? (
          <g>
            <rect
              x="40"
              y="80"
              width="320"
              height="280"
              rx="14"
              fill={dark ? "#0f172a" : "#eff6ff"}
              stroke={BLU}
              strokeWidth="2.5"
            />
            <text
              x="200"
              y="110"
              textAnchor="middle"
              fill={BLU}
              fontSize="14"
              fontWeight="700"
              fontFamily="system-ui"
            >
              PHASE 1 - IN PROGRESS
            </text>
            <text
              x="200"
              y="130"
              textAnchor="middle"
              fill={muted}
              fontSize="10"
              fontFamily="system-ui"
            >
              Equity claims · charitable trust framework
            </text>
            <line
              x1="60"
              y1="148"
              x2="340"
              y2="148"
              stroke={BLU + "44"}
              strokeWidth="1"
            />
            <rect
              x="60"
              y="160"
              width="280"
              height="55"
              rx="6"
              fill={BLU + "15"}
              stroke={BLU + "55"}
            />
            <text
              x="75"
              y="180"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              COUNT XVIII
            </text>
            <text
              x="75"
              y="197"
              fill={txt}
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Breach of Charitable Trust
            </text>
            <text
              x="75"
              y="210"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              vs Altman, Brockman, OpenAI Inc.
            </text>
            <rect
              x="60"
              y="225"
              width="280"
              height="55"
              rx="6"
              fill={BLU + "15"}
              stroke={BLU + "55"}
            />
            <text
              x="75"
              y="245"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              COUNT IV
            </text>
            <text
              x="75"
              y="262"
              fill={txt}
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Unjust Enrichment
            </text>
            <text
              x="75"
              y="275"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              disgorgement of for-profit equity
            </text>
            <rect
              x="60"
              y="290"
              width="280"
              height="55"
              rx="6"
              fill={BLU + "15"}
              stroke={BLU + "55"}
            />
            <text
              x="75"
              y="310"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              COUNT XIX
            </text>
            <text
              x="75"
              y="327"
              fill={txt}
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Aiding & Abetting
            </text>
            <text
              x="75"
              y="340"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              vs Microsoft (Kevin Scott PX-230)
            </text>
            <rect
              x="380"
              y="80"
              width="320"
              height="280"
              rx="14"
              fill={dark ? "#0f172a" : "#faf5ff"}
              stroke={PUR}
              strokeWidth="2.5"
              strokeDasharray="6 4"
            />
            <text
              x="540"
              y="110"
              textAnchor="middle"
              fill={PUR}
              fontSize="14"
              fontWeight="700"
              fontFamily="system-ui"
            >
              PHASE 2 - STAYED
            </text>
            <text
              x="540"
              y="130"
              textAnchor="middle"
              fill={muted}
              fontSize="10"
              fontFamily="system-ui"
            >
              Activates only if Phase 1 favors plaintiff
            </text>
            <line
              x1="400"
              y1="148"
              x2="680"
              y2="148"
              stroke={PUR + "44"}
              strokeWidth="1"
            />
            <rect
              x="400"
              y="160"
              width="280"
              height="78"
              rx="6"
              fill={PUR + "15"}
              stroke={PUR + "55"}
            />
            <text
              x="415"
              y="180"
              fill={PUR}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              COUNTS VIII–XV
            </text>
            <text
              x="415"
              y="198"
              fill={txt}
              fontSize="11"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Antitrust Counterclaims
            </text>
            <text
              x="415"
              y="213"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              OpenAI alleges Musk used litigation
            </text>
            <text
              x="415"
              y="225"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              to harm xAI competitor
            </text>
            <rect
              x="400"
              y="248"
              width="280"
              height="97"
              rx="6"
              fill={dark ? "#1a1a1a" : "#fff"}
              stroke={PUR + "33"}
              strokeWidth="1"
            />
            <text
              x="415"
              y="268"
              fill={PUR}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              EVIDENTIARY
            </text>
            <text
              x="415"
              y="285"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              • Pampena v. Musk pattern
            </text>
            <text
              x="415"
              y="300"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              • Apr 25 2026 Brockman threat (Dkt.522)
            </text>
            <text
              x="415"
              y="315"
              fill={txt}
              fontSize="10"
              fontFamily="system-ui"
            >
              • xAI funding round timing
            </text>
            <text
              x="415"
              y="335"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              FRE 408(b) admissible for intent
            </text>
            <rect
              x="40"
              y="375"
              width={W - 80}
              height="35"
              rx="6"
              fill={ORN + "15"}
              stroke={ORN + "55"}
            />
            <text
              x={W / 2}
              y="397"
              textAnchor="middle"
              fill={ORN}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Phase 1 verdict triggers Phase 2 - outcome cascades
            </text>
          </g>
        ) : (
          <g>
            <rect
              x="60"
              y="100"
              width={W - 120}
              height="50"
              rx="8"
              fill={GRN + "22"}
              stroke={GRN}
            />
            <text
              x="80"
              y="125"
              fill={GRN}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              NOW
            </text>
            <text
              x="80"
              y="142"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Phase 1 trial in progress (Counts XVIII, IV, XIX)
            </text>
            <line
              x1={W / 2}
              y1="155"
              x2={W / 2}
              y2="180"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar5)"
            />
            <defs>
              <marker
                id="ar5"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={muted} />
              </marker>
            </defs>
            <rect
              x="60"
              y="185"
              width={W - 120}
              height="50"
              rx="8"
              fill={BLU + "22"}
              stroke={BLU}
            />
            <text
              x="80"
              y="210"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              VERDICT
            </text>
            <text
              x="80"
              y="227"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Phase 1 verdict - equity claims decided
            </text>
            <line
              x1={W / 2 - 130}
              y1="240"
              x2="220"
              y2="280"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar5)"
            />
            <line
              x1={W / 2 + 130}
              y1="240"
              x2={W - 220}
              y2="280"
              stroke={muted}
              strokeWidth="2"
              markerEnd="url(#ar5)"
            />
            <text
              x={W / 2 - 150}
              y="265"
              fill={GRN}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              PLTF WINS
            </text>
            <text
              x={W / 2 + 150}
              y="265"
              fill={RED}
              fontSize="9"
              fontWeight="700"
              fontFamily="monospace"
            >
              DEF WINS
            </text>
            <rect
              x="80"
              y="290"
              width="280"
              height="80"
              rx="8"
              fill={PUR + "22"}
              stroke={PUR}
            />
            <text
              x="100"
              y="312"
              fill={PUR}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              PHASE 2 ACTIVATES
            </text>
            <text
              x="100"
              y="330"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Antitrust counterclaims (Counts VIII–XV)
            </text>
            <text
              x="100"
              y="347"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              OpenAI alleges Musk used litigation as anticompetitive weapon
            </text>
            <text
              x="100"
              y="362"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              vs xAI · proceeds to trial
            </text>
            <rect
              x={W - 360}
              y="290"
              width="280"
              height="80"
              rx="8"
              fill={muted + "22"}
              stroke={muted}
            />
            <text
              x={W - 340}
              y="312"
              fill={muted}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              CASE CLOSES
            </text>
            <text
              x={W - 340}
              y="330"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Equity claims dismissed
            </text>
            <text
              x={W - 340}
              y="347"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              OpenAI may pursue Phase 2 separately
            </text>
            <text
              x={W - 340}
              y="362"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              to recover litigation costs
            </text>
          </g>
        )}
      </svg>
    );
  }
  if (id === "neuro-prior-art") {
    const v = variant;
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Prior Art Side-by-Side: '061 vs CN1200366A
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          35 U.S.C. § 102 anticipation analysis
        </text>
        {v === 0 ? (
          <g>
            <rect
              x="40"
              y="80"
              width="320"
              height="290"
              rx="10"
              fill={dark ? "#0f172a" : "#fef2f2"}
              stroke={RED}
              strokeWidth="2"
            />
            <text
              x="200"
              y="110"
              textAnchor="middle"
              fill={RED}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              U.S. Patent No. '061
            </text>
            <text
              x="200"
              y="128"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              Priority date: 1999 · Filed: 2002
            </text>
            <line x1="60" y1="145" x2="340" y2="145" stroke={RED + "44"} />
            <text
              x="60"
              y="170"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              CLAIM 1
            </text>
            <text
              x="60"
              y="190"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              A composition comprising magnesium
            </text>
            <text
              x="60"
              y="205"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              L-threonate having [stated properties]
            </text>
            <text
              x="60"
              y="225"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              Inventor: Dr. Guosong Liu (sole)
            </text>
            <text
              x="60"
              y="255"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              SPECIFICATION
            </text>
            <text
              x="60"
              y="275"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Col. 4, lines 12–18:
            </text>
            <text
              x="60"
              y="290"
              fill={txt}
              fontSize="11"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              "...in vivo formation of the compound..."
            </text>
            <rect
              x="60"
              y="305"
              width="280"
              height="50"
              rx="5"
              fill={RED + "22"}
              stroke={RED + "55"}
            />
            <text
              x="200"
              y="325"
              textAnchor="middle"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ⚠ Self-admission: § 101 ineligibility
            </text>
            <text
              x="200"
              y="343"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              "In vivo formation" = product of nature
            </text>
            <rect
              x="380"
              y="80"
              width="320"
              height="290"
              rx="10"
              fill={dark ? "#0f172a" : "#eff6ff"}
              stroke={BLU}
              strokeWidth="2"
            />
            <text
              x="540"
              y="110"
              textAnchor="middle"
              fill={BLU}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              CN1200366A
            </text>
            <text
              x="540"
              y="128"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              Published: 1997 · Chinese Patent Office
            </text>
            <line x1="400" y1="145" x2="680" y2="145" stroke={BLU + "44"} />
            <text
              x="400"
              y="170"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              DISCLOSED
            </text>
            <text
              x="400"
              y="190"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Magnesium L-threonate composition
            </text>
            <text
              x="400"
              y="205"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              with [substantially same properties]
            </text>
            <text
              x="400"
              y="225"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              Pre-dates '061 priority by 2 years
            </text>
            <text
              x="400"
              y="255"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="monospace"
            >
              EFFECT
            </text>
            <text
              x="400"
              y="275"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Anticipates Claim 1 element-by-element
            </text>
            <text
              x="400"
              y="290"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              under 35 U.S.C. § 102
            </text>
            <rect
              x="400"
              y="305"
              width="280"
              height="50"
              rx="5"
              fill={BLU + "22"}
              stroke={BLU + "55"}
            />
            <text
              x="540"
              y="325"
              textAnchor="middle"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ⚡ Anticipation: claim invalid
            </text>
            <text
              x="540"
              y="343"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              Defense Ex.4 · authenticated translation in record
            </text>
          </g>
        ) : (
          <g>
            <line
              x1={W / 2}
              y1="80"
              x2={W / 2}
              y2="380"
              stroke={muted}
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <text
              x="120"
              y="110"
              fill={BLU}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              CN1200366A (1997)
            </text>
            <text
              x={W - 120}
              y="110"
              fill={RED}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              '061 patent (1999)
            </text>
            {[
              [
                "Compound",
                "magnesium L-threonate",
                "magnesium L-threonate",
                "✓ same",
              ],
              [
                "Properties",
                "crosses BBB; cognitive support",
                "crosses BBB; cognitive support",
                "✓ same",
              ],
              [
                "Method of synthesis",
                "reaction of Mg with L-threonate",
                "reaction of Mg with L-threonate",
                "✓ same",
              ],
              [
                "In vivo formation",
                "claimed property",
                "admitted in spec col.4",
                "✓ same",
              ],
              [
                "Inventor",
                "[multiple Chinese researchers]",
                "Liu (alone) - Mao excluded",
                "✗ Mao should have been named",
              ],
            ].map((row, i) => {
              const y = 145 + i * 45;
              return (
                <g key={i}>
                  <rect
                    x="30"
                    y={y - 15}
                    width={W - 60}
                    height="38"
                    rx="4"
                    fill={
                      i % 2 ? (dark ? "#0f172a" : "#f8fafc") : "transparent"
                    }
                  />
                  <text
                    x="50"
                    y={y + 8}
                    fill={muted}
                    fontSize="9"
                    fontWeight="600"
                    fontFamily="monospace"
                  >
                    {row[0]}
                  </text>
                  <text
                    x={W / 2 - 15}
                    y={y + 8}
                    textAnchor="end"
                    fill={txt}
                    fontSize="10"
                    fontFamily="system-ui"
                  >
                    {row[1]}
                  </text>
                  <text
                    x={W / 2 + 15}
                    y={y + 8}
                    fill={txt}
                    fontSize="10"
                    fontFamily="system-ui"
                  >
                    {row[2]}
                  </text>
                  <text
                    x={W - 50}
                    y={y + 8}
                    textAnchor="end"
                    fill={row[3].startsWith("✓") ? GRN : RED}
                    fontSize="9"
                    fontWeight="700"
                    fontFamily="monospace"
                  >
                    {row[3]}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    );
  }
  if (id === "neuro-inventorship") {
    const v = variant;
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Inventorship: Liu + Mao Named, Slutsky Omitted
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          35 U.S.C. § 102(f) + § 256 - omitted inventor invalidates the patent
        </text>
        {v === 0 ? (
          <g>
            <rect
              x="40"
              y="90"
              width="200"
              height="280"
              rx="10"
              fill={dark ? "#0f172a" : "#eff6ff"}
              stroke={BLU}
              strokeWidth="2"
            />
            <text
              x="140"
              y="115"
              textAnchor="middle"
              fill={BLU}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Dr. Liu
            </text>
            <text
              x="140"
              y="132"
              textAnchor="middle"
              fill={muted}
              fontSize="10"
              fontFamily="system-ui"
            >
              Named on '061 ✓
            </text>
            <text
              x="60"
              y="160"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Role:
            </text>
            <text
              x="60"
              y="176"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              MIT prof; supervised lab
            </text>
            <text
              x="60"
              y="206"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Depo admission:
            </text>
            <text
              x="60"
              y="222"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              "We did not invent
            </text>
            <text
              x="60"
              y="234"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              the molecule"
            </text>
            <rect
              x="60"
              y="320"
              width="160"
              height="40"
              rx="5"
              fill={BLU + "22"}
              stroke={BLU + "55"}
            />
            <text
              x="140"
              y="343"
              textAnchor="middle"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ✓ Properly named
            </text>
            <rect
              x="260"
              y="90"
              width="200"
              height="280"
              rx="10"
              fill={dark ? "#0f172a" : "#eff6ff"}
              stroke={BLU}
              strokeWidth="2"
            />
            <text
              x="360"
              y="115"
              textAnchor="middle"
              fill={BLU}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Dr. Mao
            </text>
            <text
              x="360"
              y="132"
              textAnchor="middle"
              fill={muted}
              fontSize="10"
              fontFamily="system-ui"
            >
              Named on '061 ✓
            </text>
            <text
              x="280"
              y="160"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Role:
            </text>
            <text
              x="280"
              y="176"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              co-inventor; ran probe tech
            </text>
            <text
              x="280"
              y="206"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              YouTube Ex.6 (2014):
            </text>
            <text
              x="280"
              y="222"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              "founded on accidental
            </text>
            <text
              x="280"
              y="234"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              discovery using my tool"
            </text>
            <text
              x="280"
              y="264"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Depo (Feb 18, 2026):
            </text>
            <text
              x="280"
              y="280"
              fill={ORN}
              fontSize="9"
              fontWeight="700"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              "Slutsky made it"
            </text>
            <rect
              x="280"
              y="320"
              width="160"
              height="40"
              rx="5"
              fill={BLU + "22"}
              stroke={BLU + "55"}
            />
            <text
              x="360"
              y="343"
              textAnchor="middle"
              fill={BLU}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ✓ Properly named
            </text>
            <rect
              x="480"
              y="90"
              width="200"
              height="280"
              rx="10"
              fill={dark ? "#0f172a" : "#fef2f2"}
              stroke={RED}
              strokeWidth="2"
            />
            <text
              x="580"
              y="115"
              textAnchor="middle"
              fill={RED}
              fontSize="13"
              fontWeight="700"
              fontFamily="system-ui"
            >
              Dr. Slutsky
            </text>
            <text
              x="580"
              y="132"
              textAnchor="middle"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ⚡ NOT named
            </text>
            <text
              x="500"
              y="160"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Role:
            </text>
            <text
              x="500"
              y="176"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              Liu's Ph.D. researcher MIT
            </text>
            <text
              x="500"
              y="206"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Mao testimony:
            </text>
            <text
              x="500"
              y="222"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              made the "accidental
            </text>
            <text
              x="500"
              y="234"
              fill={muted}
              fontSize="9"
              fontStyle="italic"
              fontFamily="system-ui"
            >
              discovery"
            </text>
            <text
              x="500"
              y="264"
              fill={txt}
              fontSize="10"
              fontWeight="600"
              fontFamily="system-ui"
            >
              Current location:
            </text>
            <text
              x="500"
              y="280"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              Tel Aviv University
            </text>
            <rect
              x="500"
              y="320"
              width="160"
              height="40"
              rx="5"
              fill={RED + "22"}
              stroke={RED + "55"}
            />
            <text
              x="580"
              y="343"
              textAnchor="middle"
              fill={RED}
              fontSize="10"
              fontWeight="700"
              fontFamily="system-ui"
            >
              ⚡ Should be named
            </text>
          </g>
        ) : (
          <g>
            <rect
              x="40"
              y="90"
              width={W - 80}
              height="50"
              rx="6"
              fill={BLU + "15"}
              stroke={BLU + "55"}
            />
            <text
              x="60"
              y="113"
              fill={BLU}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              '061 PATENT
            </text>
            <text
              x="60"
              y="131"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Names Dr. Liu and Dr. Mao as co-inventors
            </text>
            <text
              x={W - 60}
              y="120"
              textAnchor="end"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              official record
            </text>
            <line
              x1={W / 2}
              y1="145"
              x2={W / 2}
              y2="170"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar6)"
            />
            <defs>
              <marker
                id="ar6"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={muted} />
              </marker>
            </defs>
            <rect
              x="40"
              y="175"
              width={W - 80}
              height="50"
              rx="6"
              fill={ORN + "15"}
              stroke={ORN + "55"}
            />
            <text
              x="60"
              y="198"
              fill={ORN}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              EX. 6 - MAO YOUTUBE INTERVIEW (2014)
            </text>
            <text
              x="60"
              y="216"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Mao: "founded on accidental discovery using my tool"
            </text>
            <text
              x={W - 60}
              y="205"
              textAnchor="end"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              contradicts inventorship
            </text>
            <line
              x1={W / 2}
              y1="230"
              x2={W / 2}
              y2="255"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar6)"
            />
            <rect
              x="40"
              y="260"
              width={W - 80}
              height="50"
              rx="6"
              fill={RED + "15"}
              stroke={RED + "55"}
            />
            <text
              x="60"
              y="283"
              fill={RED}
              fontSize="11"
              fontWeight="700"
              fontFamily="monospace"
            >
              MAO DEPO (Feb 18, 2026)
            </text>
            <text
              x="60"
              y="301"
              fill={txt}
              fontSize="11"
              fontFamily="system-ui"
            >
              Credits Dr. Slutsky for the discovery - but she is NOT named
            </text>
            <text
              x={W - 60}
              y="290"
              textAnchor="end"
              fill={muted}
              fontSize="9"
              fontFamily="monospace"
            >
              if violated → invalid
            </text>
            <line
              x1={W / 2}
              y1="315"
              x2={W / 2}
              y2="340"
              stroke={muted}
              strokeWidth="1.5"
              markerEnd="url(#ar6)"
            />
            <rect
              x="120"
              y="345"
              width={W - 240}
              height="50"
              rx="8"
              fill={GRN + "22"}
              stroke={GRN}
            />
            <text
              x={W / 2}
              y="368"
              textAnchor="middle"
              fill={GRN}
              fontSize="12"
              fontWeight="700"
              fontFamily="system-ui"
            >
              DEFENSE INVALIDITY POSITION
            </text>
            <text
              x={W / 2}
              y="385"
              textAnchor="middle"
              fill={muted}
              fontSize="9"
              fontFamily="system-ui"
            >
              Slutsky omission → § 102(f) / § 256 invalidity or correction
            </text>
          </g>
        )}
      </svg>
    );
  }
  if (id === "neuro-license") {
    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <text
          x={W / 2}
          y="30"
          textAnchor="middle"
          fill={txt}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui"
        >
          TMLA License Scope
        </text>
        <text
          x={W / 2}
          y="50"
          textAnchor="middle"
          fill={muted}
          fontSize="11"
          fontFamily="system-ui"
        >
          What the Technology and Materials License Agreement covered - and what
          it did not
        </text>
        <circle
          cx={W / 2}
          cy="225"
          r="125"
          fill={GRN + "15"}
          stroke={GRN}
          strokeWidth="2"
        />
        <circle
          cx={W / 2}
          cy="225"
          r="170"
          fill="none"
          stroke={RED}
          strokeWidth="2"
          strokeDasharray="6 3"
        />
        <text
          x={W / 2}
          y="115"
          textAnchor="middle"
          fill={RED}
          fontSize="11"
          fontWeight="700"
          fontFamily="monospace"
        >
          OUTSIDE SCOPE → INFRINGEMENT
        </text>
        <text
          x={W / 2}
          y="145"
          textAnchor="middle"
          fill={GRN}
          fontSize="11"
          fontWeight="700"
          fontFamily="monospace"
        >
          INSIDE SCOPE → LICENSED
        </text>
        <text
          x={W / 2 - 90}
          y="195"
          fill={txt}
          fontSize="10"
          fontFamily="system-ui"
        >
          • Named territories
        </text>
        <text
          x={W / 2 - 90}
          y="215"
          fill={txt}
          fontSize="10"
          fontFamily="system-ui"
        >
          • Specified formulations
        </text>
        <text
          x={W / 2 - 90}
          y="235"
          fill={txt}
          fontSize="10"
          fontFamily="system-ui"
        >
          • Royalty obligations
        </text>
        <text
          x={W / 2 - 90}
          y="255"
          fill={txt}
          fontSize="10"
          fontFamily="system-ui"
        >
          • Defined products
        </text>
        <text
          x={W / 2 + 10}
          y="320"
          textAnchor="end"
          fill={RED}
          fontSize="9"
          fontFamily="system-ui"
        >
          ⚠ Unlicensed territories
        </text>
        <text
          x={W / 2 + 10}
          y="335"
          textAnchor="end"
          fill={RED}
          fontSize="9"
          fontFamily="system-ui"
        >
          ⚠ Reformulation
        </text>
        <text x="55" y="320" fill={RED} fontSize="9" fontFamily="system-ui">
          ⚠ Sublicensing without consent
        </text>
        <text x="55" y="335" fill={RED} fontSize="9" fontFamily="system-ui">
          ⚠ Country-of-origin misrepresentation
        </text>
        <rect
          x="40"
          y="365"
          width={W - 80}
          height="40"
          rx="6"
          fill={ORN + "15"}
          stroke={ORN + "55"}
        />
        <text
          x={W / 2}
          y="385"
          textAnchor="middle"
          fill={ORN}
          fontSize="11"
          fontWeight="700"
          fontFamily="system-ui"
        >
          Scope question is dispositive on multiple counts
        </text>
        <text
          x={W / 2}
          y="399"
          textAnchor="middle"
          fill={muted}
          fontSize="9"
          fontFamily="system-ui"
        >
          Licensee estoppel bars assertion against licensed activity · Lanham
          Act applies to off-scope conduct
        </text>
      </svg>
    );
  }
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: "auto", display: "block" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <text
        x={W / 2}
        y={H / 2}
        textAnchor="middle"
        fill={muted}
        fontSize="14"
        fontFamily="system-ui"
      >
        Demonstrative not yet generated
      </text>
    </svg>
  );
}

const LEARN_STORIES = {
  ...ILE_K_LEARN,
  juul: {
    plaintiff: [
      {
        title: "Chapter 1 - The Promise",
        text: "In 2015, two Stanford design students stood before the world with a sleek silver device and a story: cigarettes had killed millions, and they had built something better. JUUL, they said, was a harm-reduction tool for the adult smoker who wanted out. A product designed to save lives - not start new addictions. Investors wrote checks. Press releases praised the engineering. The story sounded clean.",
      },
      {
        title: "Chapter 2 - The Reality",
        text: "Within three years, JUUL was the most popular brand among American teenagers. Internal documents would later show the company tracked youth engagement metrics by quarter. A campaign called 'Buzz' ran on Instagram in April 2018, targeting what marketers privately described as 'youth-adjacent novice users.' Meanwhile a program called 'JUUL School' sent representatives into California high schools - its existence later confirmed by the Sept 2018 board deck unsealed in Dkt.3393.",
      },
      {
        title: "Chapter 3 - Julia's Story",
        text: "Julia Seliavski was sixteen when she picked up her first JUUL pod at a 7-Eleven in Richmond, Virginia. By the spring of 2018, she could not stop. Her injuries followed the same arc as a generation of American teenagers - addiction, mood swings, anger, irritability. She did not know what nicotine salt was. She did not know each pod delivered roughly the nicotine of a full pack of cigarettes. Her name now appears on the master complaint.",
      },
      {
        title: "Chapter 4 - The Numbers",
        text: "Each JUUL pod delivered nicotine at 59 milligrams per milliliter - roughly twice the concentration of any closed-system competitor on the market. A 2021 study at SFUSD schools found that 75% of students who recognized a JUUL did not know it contained nicotine. They called the bathroom aerosol 'fruity fog.' E-cigarette use among SFUSD high schoolers more than doubled between 2017 and 2019. Bathroom doors were broken. Fences were cut to access vaping hotspots.",
      },
      {
        title: "Chapter 5 - Altria Joins",
        text: "In December 2018, Altria Group - the maker of Marlboro - paid $12.8 billion for a 35% stake in JUUL Labs. Simultaneously, Altria withdrew its own e-cigarette products from the U.S. market. The plaintiffs allege this was no coincidence. It was a horizontal market allocation, a Sherman Act violation, and the foundation of a RICO enterprise theory targeting both companies. The antitrust class was certified in February 2026. Trial is set for September 28, 2026.",
      },
      {
        title: "Chapter 6 - The Reckoning",
        text: "In June 2022 the FDA issued a marketing denial order. JUUL settled a class case for $255 million. But Julia opted out - choosing to pursue her own personal injury claim. Her case sits today in the Northern District of California, on the brink of summary judgment, with Judge Orrick poised to remand the surviving claims to the Eastern District of Virginia. Plaintiff's strongest move now is the Monsees deposition, where the founder's own contradictions can be read back into the record.",
      },
    ],
    defendant: [
      {
        title: "Chapter 1 - An Innovation in Harm Reduction",
        text: "JUUL was conceived as an answer to America's deadliest preventable health problem. Roughly 480,000 Americans die each year from cigarette smoking. JUUL's founders engineered a device that delivered nicotine without combustion - without the tar, the carbon monoxide, the carcinogens that kill smokers. Like nicotine gum, like the patch, like every prior cessation tool, the product was designed for adult smokers who wanted to quit. That design intent is documented in a decade of pre-launch internal materials.",
      },
      {
        title: "Chapter 2 - The Regulatory Frame",
        text: "From 2015 forward, JUUL operated inside the FDA's Pre-Market Tobacco Application framework. The company restricted its sales channels, instituted age-verification at retail and online, and removed flavored pods at FDA's request. The June 2022 marketing denial order - the centerpiece of the plaintiffs' regulatory narrative - was stayed by the Fifth Circuit weeks later. It is non-final. It is not a finding of liability.",
      },
      {
        title: "Chapter 3 - The Pak Admission",
        text: "At the heart of the SFUSD case sits one deposition. On December 21, 2021, SFUSD's 30(b)(6) representative Quarry Pak was asked what JUUL-specific costs the district had incurred. Her answer was: 'We don't have that information.' Superintendent Matthews said the same. Two witnesses, on the record, conceding that the legal damages plaintiffs seek cannot be quantified. That is JLI's summary judgment centerpiece.",
      },
      {
        title: "Chapter 4 - Sonner Bars Equitable Relief",
        text: "Without quantifiable legal damages, plaintiffs pivoted to public-nuisance abatement - equitable relief. But Ninth Circuit precedent in Sonner v. Premier Nutrition holds that equitable abatement is unavailable when an adequate legal remedy exists. JLI's brief frames the question with care: either plaintiffs have a legal damages theory (in which case Sonner bars equity) or they do not (in which case there is no harm). The argument is symmetrical. It cannot lose both ways.",
      },
      {
        title: "Chapter 5 - Causation and the Opt-Outs",
        text: "Julia Seliavski has not retained a medical causation expert. Without an expert linking JUUL specifically to her claimed nicotine dependence - as opposed to the dozens of other nicotine products she could have used - there is no causation chain. The Sonner-Pak two-step disposes of the SFUSD damages question. The expert gap disposes of Seliavski's. JLI's path to summary judgment runs straight through both.",
      },
      {
        title: "Chapter 6 - The Path Forward",
        text: "After SJ, the case will likely be remanded to the Eastern District of Virginia. There, JLI will press FCLAA federal preemption - a powerful argument that Judge Orrick declined to reach in 2020 but that the Ninth Circuit may apply broadly. Statute of limitations defenses await on the opt-out PIs whose claims accrued in spring 2020. Class settlements have already disposed of the broadest tort exposure. The defense playbook is not finished. It is winning.",
      },
    ],
    judge: [
      {
        title: "Chapter 1 - MDL Formation",
        text: "In October 2019 the Judicial Panel on Multidistrict Litigation transferred all federal JUUL cases to the Northern District of California. The matter became MDL No. 2913 and was assigned to Judge William H. Orrick. Eight years later, the docket exceeds 4,500 entries. The case has produced PTOs, CMOs, sealing orders, MSJ rulings, class settlements, and a parallel antitrust action - all from one courtroom.",
      },
      {
        title: "Chapter 2 - The October 31, 2025 SJ Hearing",
        text: "For 43 minutes on October 31, 2025, defense counsel walked Judge Orrick through the Sonner-Pak two-step. The Judge asked sharp questions about whether Sonner actually applies when the Pak admission eliminates legal damages. The logic he probed was circular: if there are no legal damages, then by definition there is no adequate legal remedy - so Sonner should not bar equity. Six months later, the Court remained on submission.",
      },
      {
        title: "Chapter 3 - Sealing Standards",
        text: "Judge Orrick applies the Kamakana standard rigorously. He has issued show-cause orders for overbroad sealing requests - including on the fee committee materials in Dkt.4167. Counsel learn quickly: every sealing request must be narrowly tailored, supported by compelling reasons for dispositive or trial-related exhibits, and explained on the record. He monitors these personally.",
      },
      {
        title: "Chapter 4 - Class Settlements",
        text: "Judge Orrick approved the $255 million JLI class settlement after rigorous fairness review. He approved separate Altria settlements. Each came with detailed orders, OSCs where appropriate, and careful attention to opt-out procedure. The settlements removed the broadest tort exposure from the MDL but left the SFUSD case, the opt-out PIs, and the antitrust action to be resolved on their own tracks.",
      },
      {
        title: "Chapter 5 - Remand Calculus",
        text: "At the December 16, 2025 case management conference, Orrick confirmed his intent to remand opt-out PIs after SJ. He asked plaintiff counsel a hard question: what supplemental discovery, after eight years of MDL, is genuinely non-duplicative? The January 6, 2026 joint letter narrowed it to deponent emails and social media records. He also flagged personal jurisdiction concerns over the Non-Management Director defendants in transferee state courts - federal RICO provides nationwide service of process, but state-court analogs do not.",
      },
      {
        title: "Chapter 6 - What Comes Next",
        text: "The summary judgment ruling will be the next major event. Orrick takes time because his rulings are written to survive Ninth Circuit review on multiple grounds simultaneously. After SJ, expect a remand order, NMD personal jurisdiction analysis, and continued attention to the parallel antitrust trial set for September 28, 2026. The MDL is not closing - it is concentrating.",
      },
    ],
  },
  musk: {
    plaintiff: [
      {
        title: "Chapter 1 - The Founding Vision",
        text: "In May 2015, Sam Altman and Greg Brockman wrote to Elon Musk and a small circle of co-founders. They were starting a non-profit AI lab. The mission, in Altman's own words: 'Tech belongs to the world.' Musk pledged tens of millions of dollars. So did others. The IRS would ultimately grant 501(c)(3) status. OpenAI, Inc. was a charitable trust - funded by donors who believed the technology would never be enclosed.",
      },
      {
        title: "Chapter 2 - Brockman's Doubt",
        text: "By 2017 the founders were already arguing about whether the non-profit could ever raise enough capital to compete with Google. In a contemporaneous journal entry - pre-litigation, written by a co-founder - Greg Brockman acknowledged that the LP structure under consideration was 'wrong to steal non-profit from Elon.' The journal is plaintiff's exhibit PX-163. It is not litigation posturing. It is a confession.",
      },
      {
        title: "Chapter 3 - The Pivot",
        text: "In March 2019 OpenAI announced its for-profit subsidiary structure. Months later, Microsoft invested $1 billion. By 2024 it had invested more than $13 billion. The non-profit's most valuable asset - the technology paid for by charitable donations - was now controlled by a for-profit entity. Microsoft eventually held an equity position worth more than $135 billion. Charity money had funded the most valuable AI company in history.",
      },
      {
        title: "Chapter 4 - The Conversion",
        text: "In October 2025 OpenAI converted to a public benefit corporation. The non-profit became a minority stakeholder of the entity it had created. The board that approved the conversion was the same board that had taken the donations. The plaintiff's theory: that conversion is a per-se breach of the charitable trust, and the constructive remedy is disgorgement of the for-profit equity.",
      },
      {
        title: "Chapter 5 - The Theory",
        text: "Plaintiff's case rests on three counts. Count XVIII - breach of charitable trust. Count IV - unjust enrichment. Count XIX - aiding and abetting against Microsoft. The factual proofs are concrete: Altman's PX-003 founding email, Brockman's PX-163 journal, and Kevin Scott's PX-230 internal email at Microsoft sixteen months before the $1 billion investment. The remedy sought is constructive trust on the for-profit equity.",
      },
      {
        title: "Chapter 6 - What's at Stake",
        text: "This is not a typical contract case. It is a charitable-trust enforcement action with constructive-trust remedies - a body of law where equity protects the donor class against fiduciary self-dealing. Judge Yvonne Gonzalez Rogers is hearing Phase 1 now. The verdict will turn on whether Altman, Brockman, and Microsoft executives told the truth about what OpenAI was, or what they wanted it to be.",
      },
    ],
    defendant: [
      {
        title: "Chapter 1 - The Mission Was Always Practical",
        text: "OpenAI was founded to build safe artificial general intelligence. The non-profit form was a vehicle, not a vow. The May 2015 founding email used aspirational language because all founding documents do. What followed was a decade of disciplined research, peer-reviewed publication, and - when capital requirements made the non-profit form impossible to maintain - a careful restructuring that preserved the mission inside a capped-profit entity.",
      },
      {
        title: "Chapter 2 - Musk Was Always Informed",
        text: "In August 2018 Musk received the term sheet for the LP structure. He said nothing for months. In January 2016 he had already replied to a proposal that OpenAI become 'less open' with a single word: 'Yup.' That email is defense exhibit DX-516. In March 2019, Altman asked him to acknowledge the for-profit structure publicly. His instruction came back: 'Tell them I'm supportive.' That is DX-862. The record is consistent. Musk knew, and Musk acquiesced.",
      },
      {
        title: "Chapter 3 - No Donor Standing",
        text: "California charitable-trust enforcement is statutorily vested in the Attorney General - not in individual donors. Musk is not a charitable actor. He is a competitor. He runs xAI. The Attorney General reviewed Musk's allegations and declined to join his suit. That declination is itself a judgment by the state authority charged with protecting California's charitable assets.",
      },
      {
        title: "Chapter 4 - Laches Bars Equity",
        text: "Musk knew of OpenAI's for-profit structure by March 2019. In September 2020 he tweeted that OpenAI had been 'captured by Microsoft.' He filed suit five years after that. During those five years, OpenAI invested billions of dollars in reliance on the structure Musk now seeks to unwind. Equity does not rescue a plaintiff who slept on his rights while watching the value he claims accrue to others.",
      },
      {
        title: "Chapter 5 - Microsoft Did Its Own Diligence",
        text: "On Count XIX, plaintiff alleges Microsoft aided and abetted a charitable-trust breach. But Microsoft conducted its own due diligence on the LP structure for fifteen months before the $1 billion investment. PX-230 - Kevin Scott's internal email - shows MS asking the right questions, not sleepwalking through them. To find aiding and abetting, the jury must find that Microsoft knowingly assisted a breach. The record shows Microsoft acted in good faith.",
      },
      {
        title: "Chapter 6 - The Phases Ahead",
        text: "After the Phase 1 verdict, Phase 2 antitrust counterclaims will activate. OpenAI alleges that Musk used this litigation as a competitive weapon - to slow OpenAI down while xAI catches up. Pretrial Order No. 3 set the SOL framework. Defense exhibits DX-516, DX-827, and DX-862 will be introduced systematically. The defense theory is not just that Musk is wrong on the law. It is that he is in court to win a market, not a charity.",
      },
    ],
    judge: [
      {
        title: "Chapter 1 - The Phases",
        text: "Judge YGR structured this trial in two phases. Phase 1 covers Count XVIII - charitable trust breach - Count IV - unjust enrichment - and Count XIX - aiding and abetting Microsoft. Phase 2, the antitrust counterclaims, was stayed pending Phase 1 verdict. The structure forces clarity: equity claims first, market claims second. Each phase will be decided on its own factual record.",
      },
      {
        title: "Chapter 2 - The Preliminary Injunction Denial",
        text: "In March 2025, Judge YGR denied Musk's preliminary injunction. Her order found no irreparable harm from the for-profit conversion - a ruling that telegraphed her view of the equities. The PI denial did not resolve the merits, but it set the tone. Plaintiff would need to prove charitable-trust breach at trial. Status quo would not be enjoined while that happened.",
      },
      {
        title: "Chapter 3 - Pretrial Order No. 3",
        text: "Pretrial Order No. 3 set the statute-of-limitations framework. The order references Musk's September 2020 'captured by Microsoft' tweet as a potential trigger date. That single tweet now drives the SOL question. Plaintiff must prove fraudulent concealment to toll. The order is consequential - a structural framing that organizes how every fact is heard.",
      },
      {
        title: "Chapter 4 - The §17510.8 Question",
        text: "On April 30, 2026, both sides filed competing briefs on whether California Business and Professions Code §17510.8 - the charitable-solicitations strict-liability statute - applies. If it applies, the standard for liability shifts. If it does not, the case is decided on common-law charitable-trust principles. The Court has not yet ruled. The ruling, when it issues, will be the most consequential evidentiary decision of the trial.",
      },
      {
        title: "Chapter 5 - YGR's Style",
        text: "Judge YGR runs a tight courtroom. She has read every filing. She identifies inconsistencies in real time. She presses witnesses personally. Counsel must know the record cold - every exhibit, every transcript line, every cross-reference. Overreach is punished. Vague statements draw immediate questions. The trial moves fast because the Judge moves fast.",
      },
      {
        title: "Chapter 6 - What Comes Next",
        text: "Phase 1 verdict will be the next major event. Whatever the result, post-trial motions will follow - Rule 59 motions, fee motions, possible appeals. If plaintiff prevails, Phase 2 antitrust counterclaims activate immediately. If defense prevails, OpenAI may still pursue counterclaims to recover litigation costs. Either way, the matter is far from finished.",
      },
    ],
  },
  neuro: {
    plaintiff: [
      {
        title: "Chapter 1 - The Discovery",
        text: "In 1999, in a laboratory at the Massachusetts Institute of Technology, Dr. Guosong Liu and his collaborators investigated a question with practical implications for cognitive health: could a magnesium compound cross the blood-brain barrier and improve neural function? The compound they identified - magnesium L-threonate - became the foundation of U.S. Patent No. '061. Neurocentria built a company around it. The patent was the company.",
      },
      {
        title: "Chapter 2 - The License",
        text: "The Technology and Materials License Agreement gave defendant Mando access to the formulation under specific conditions. The TMLA defined the licensed scope. It defined the unlicensed activities. It defined termination. When the relationship soured, Neurocentria documented what it believed to be Mando's continued use of the formulation outside the license - including the manufacture of products in jurisdictions that had no formal licensing relationship at all.",
      },
      {
        title: "Chapter 3 - The Infringement",
        text: "Plaintiff alleges Mando manufactured and distributed magnesium L-threonate products that read on Claim 1 of the '061 patent. The lab testing of one product is in evidence. Plaintiff alleges this is willful - Mando had actual notice of the patent through the TMLA, and continued the activity anyway. Under 35 U.S.C. § 284, willful infringement supports up to three times enhanced damages.",
      },
      {
        title: "Chapter 4 - The Country-of-Origin Claim",
        text: "Plaintiff also alleges false designation of origin under section 43(a) of the Lanham Act. The defense markets product as U.S.-manufactured, but Certificates of Analysis and import records suggest the actual source is Chinese. Section 43(a) does not require likelihood of confusion - only that the origin statement was false and material. Customs documents and CoAs are the evidentiary foundation.",
      },
      {
        title: "Chapter 5 - Trade Secret Misappropriation",
        text: "If discovery confirms that Mando accessed proprietary formulation data through the TMLA and used that data to develop competing products outside the licensed scope, a claim lies under the Defend Trade Secrets Act. The DTSA permits exemplary damages up to twice the actual damages for willful misappropriation. The TMLA relationship is the access vector. The competing products are the use.",
      },
      {
        title: "Chapter 6 - What's at Stake",
        text: "Plaintiff seeks an injunction, damages, willfulness premium, and Lanham Act remedies. The case sits in the Central District of California before Judge Aenlle-Rocha. Plaintiff has narrowed from five patents to one. The defense has filed two motions - including a §101 patent-eligibility challenge. Trial is set for March 2027. Mediation is ordered by December 31, 2026.",
      },
    ],
    defendant: [
      {
        title: "Chapter 1 - The Prior Art",
        text: "In 1997, two years before Dr. Liu's MIT work, the Chinese patent CN1200366A disclosed magnesium L-threonate. The Chinese patent describes the same compound, the same chemistry, the same claimed properties. Under 35 U.S.C. § 102, prior art that anticipates Claim 1 invalidates the patent. The Chinese patent has been authenticated. It is exhibit Ex.4. It anticipates.",
      },
      {
        title: "Chapter 2 - In Vivo Formation",
        text: "Column 4, lines 12-18 of the '061 patent specification describes 'in vivo formation' of the compound. That language is not ambiguous. It is the patentee's own admission that the molecule forms naturally in the body. Under § 101 ineligibility doctrine, products of nature cannot be patented. The specification - the patentee's own document - admits the patent's eligibility problem on its face.",
      },
      {
        title: "Chapter 3 - The Inventorship Issue",
        text: "Liu and Mao are BOTH named co-inventors on the '061 patent. The actual inventorship defect alleged in Mando's counterclaim is the OMISSION of Dr. Inna Slutsky - Liu's MIT Ph.D. researcher. In Mao's 2014 YouTube interview (Ex.6), Mao describes the discovery as 'an accidental discovery' made using a fluorescent dye tool he invented 15 years earlier. In his Feb 18, 2026 deposition, Mao credits Slutsky for the actual discovery in Liu's MIT lab. Under § 102(f) and § 256, a patent must name all inventors and only inventors. The Slutsky letter rogatory pending under the Hague Convention will determine whether she should have been named.",
      },
      {
        title: "Chapter 4 - Standing Problems",
        text: "Plaintiff's First Amended Complaint contains internally contradictory allegations regarding patent ownership. FAC ¶8 and FAC ¶13 cannot both be true. Without a clean chain of title, plaintiff lacks standing under Federal Circuit precedent. The TMLA assignment chain raises additional questions about whether the asserted patent rights transferred to plaintiff or remain elsewhere.",
      },
      {
        title: "Chapter 5 - Defenses In Depth",
        text: "Beyond invalidity and standing, defense has licensee estoppel - to the extent the TMLA covered the activity, plaintiff cannot now assert. Prosecution history estoppel narrows the doctrine of equivalents. Laches may bar pre-filing damages because plaintiff waited years before suing. Each defense disposes of part of the case. Together they dispose of all of it.",
      },
      {
        title: "Chapter 6 - What Comes Next",
        text: "The §101 motion to dismiss is fully briefed and pending. If the Court grants it, the case ends. If the Court denies it, claim construction follows under the Court's Patent Standing Order. Mediation is ordered by December 31, 2026. The defense theory is to let the patent destroy itself with its own specification. The §101 ruling is the centerpiece of that theory.",
      },
    ],
    judge: [
      {
        title: "Chapter 1 - Procedural Compliance",
        text: "Judge Aenlle-Rocha runs a courtroom on the principle that local rules are not suggestions. Two stipulations have been stricken in this case for non-compliance with Civil Local Rules. The Court does not hold hearings on minor procedural defects. It simply strikes the offending document. Counsel adapt quickly or lose filings.",
      },
      {
        title: "Chapter 2 - Patent Narrowing",
        text: "Plaintiff originally asserted five patents. The Court approved narrowing to one. The narrowing was efficient - a signal that the Court will not tolerate kitchen-sink patent litigation. The asserted patent is the '061. The dispositive issue is its validity. The Court has organized the case around that question.",
      },
      {
        title: "Chapter 3 - Standing Order",
        text: "The Court's Patent Standing Order imposes detailed claim construction procedures. It requires early disclosure of asserted claims and accused products. It defines the briefing schedule. It governs expert reports. The Standing Order is the operating system of the case. Counsel who do not study it will lose deadlines they did not know existed.",
      },
      {
        title: "Chapter 4 - On Submission",
        text: "The Court vacated the initial scheduling conference, finding in-person appearance unnecessary. The Court rules on paper. That preference is consequential - defense's §101 motion is paper-friendly because the patent specification is its own best evidence. Plaintiff cannot rely on argument, charisma, or live demonstration. The record must speak for itself.",
      },
      {
        title: "Chapter 5 - The Mediation Order",
        text: "The Court has ordered private mediation by December 31, 2026. The order signals that the Court expects the parties to attempt resolution before trial. Mediation will not be perfunctory. The Court will scrutinize the good-faith participation of each side. A party that walks through the process without a real settlement posture risks adverse procedural rulings later.",
      },
      {
        title: "Chapter 6 - What Comes Next",
        text: "The §101 motion ruling is the next major event. After §101 rules, claim construction proceeds. Expert disclosure deadlines follow. The trial date - March 2027 - is the firm anchor. Mediation will happen in 2026. Each phase compresses the case toward a binary outcome: either the patent survives §101 and reaches the merits, or it does not.",
      },
    ],
  },
};

const SIDEBAR_GROUPS = [
  {
    id: "basics",
    label: "Case Basics",
    hint: "Orient & ground",
    accent: "#5b8def",
    items: [
      ["learn", "📖", "Learn the Case", "tab"],
      ["timelines", "📅", "Timeline", "tab"],
      ["assets", "📁", "Assets", "tab"],
      ["chat", "💬", "AI Chat", "tab"],
    ],
  },
  {
    id: "discovery",
    label: "Discovery",
    hint: "What we know",
    accent: "#10b981",
    items: [
      ["deps", "📋", "Depositions", "tab"],
      ["experts", "🎓", "Experts", "tab"],
      ["witnesses", "👁️", "Witnesses", "tab"],
      ["contraengine", "🔍", "Contradictions", "tab"],
    ],
  },
  {
    id: "strategy",
    label: "Strategy",
    hint: "How we win",
    accent: "#a78bfa",
    items: [
      ["theories", "⚖️", "Theories", "tab"],
      ["stratmap", "🌡️", "Strategic Map", "tab"],
      ["playbook", "🎯", "Opponent Playbook", "tab"],
      ["damages", "💰", "Damages Model", "tab"],
    ],
  },
  {
    id: "risk",
    label: "Risk & Exposure",
    hint: "Defensive prep",
    accent: "#f59e0b",
    items: [
      ["traps", "⚠️", "Procedural Traps", "tab"],
      ["impeach", "🎯", "Impeachment Bank", "tab"],
      ["settleintel", "🤝", "Settlement Intel", "tab"],
      ["appellate", "⚖️", "Appellate Risk", "tab"],
    ],
  },
  {
    id: "trial",
    label: "Trial",
    hint: "Courtroom",
    accent: "#dc2626",
    items: [
      ["hearing", "🎤", "Hearing Prep", "rb"],
      ["warroom", "⚔️", "War Room", "tab"],
      ["jury", "⚖️", "Jury Intel", "tab"],
      ["demos", "📊", "Demonstratives", "tab"],
    ],
  },
  {
    id: "drafting",
    label: "Drafting",
    hint: "Filings & research",
    accent: "#3b82f6",
    items: [
      ["motions", "📋", "Motions", "rb"],
      ["caselaw", "🔬", "Caselaw", "rb"],
      ["drafts", "⚡", "Drafts", "rb"],
    ],
  },
  {
    id: "people",
    label: "People & Court",
    hint: "Humans in the loop",
    accent: "#ec4899",
    items: [
      ["client", "💼", "Client Hub", "tab"],
      ["judgeintel", "👨‍⚖️", "Judge Intel", "tab"],
      ["attorneys", "👥", "Attorneys", "tab"],
      ["locations", "📍", "Locations", "tab"],
    ],
  },
];

/* ═══════════════════════════════════════════════
   JUUL MDL CASE DATA  –  3:19-md-02913-WHO
   Seliavski v. Juul Labs, Inc. et al.
═══════════════════════════════════════════════ */
const JUUL_F_TL = [
  {
    plaintiff: "All",
    date: "Mar 17, 2026",
    label: "Class notice plan approved (Dkt.663) - antitrust case",
    detail:
      "Judge Orrick approves the direct and indirect purchaser class notice plan in the antitrust case. Notice is directed to all class members, including direct purchasers who bought directly from JLI and indirect purchasers who bought from brick-and-mortar retailers. The notice identifies the opt-out deadline and the September 28 2026 trial date. Defendants Altria Group, JLI, Nicholas Pritzker, and Riaz Valani remain active in the antitrust case.",
    ref: "Dkt.663 (3:20-cv-02345-WHO, Mar 17, 2026)",
    type: "factual",
    ecf: "Note: Defendants filed petition for Rule 23(f) interlocutory review of class cert at Ninth Circuit - pending. If Ninth Circuit grants review, trial schedule may be affected. Indirect purchaser class: www.JuulAntitrustConsumer.com.",
  },
  {
    plaintiff: "All",
    date: "Feb 26, 2026",
    label:
      "Full class certification order - DPP, IPP, IRP classes certified (Dkt.635)",
    detail:
      "Judge Orrick issues 100+ page class certification opinion in the antitrust case. DPP class certified: all U.S. persons/entities that purchased JUUL products directly from JLI, Oct 5 2018 to present. IPP class certified: individuals who bought JUUL pods from brick-and-mortar retailers for personal use from Oct 5 2018 to present, for states allowing indirect purchaser class actions. IRP class certified: all businesses that bought JUUL pods, Dec 1 2018 through Mar 31 2025. EXCLUDED from indirect classes: Arkansas, South Carolina, Tennessee, Virginia - those states do not allow class actions by indirect purchasers for antitrust claims.",
    ref: "Dkt.635 (3:20-cv-02345-WHO, Feb 26, 2026)",
    type: "factual",
    ecf: "Cartwright Act applied extraterritorially to 31 Illinois Brick repealer states. Court rejects defendants' extraterritoriality objection - anticompetitive agreement meetings occurred in California; effectuation from California. Defendants' challenge to indirect experts goes to weight not admissibility. Expert Leger (DPP overcharge) - allowed. Expert Perry (DPP overcharge rebuttal) - rebuttal only. Expert Singh (market transaction analysis) - allowed. Defense expert Dobbins excluded on pharmacology scope - limited to nicotine addiction, not reasonableness of Altria's market exit.",
  },
  {
    plaintiff: "All",
    date: "Feb 5, 2026",
    label: "Antitrust court issues short order - DPP class certified (Dkt.627)",
    detail:
      "Judge Orrick issues a short order granting the Direct Purchaser Plaintiff (DPP) motion for class certification, granting in part the IPP and IRP motions for class certification, and resolving parties' motions to exclude experts. The short order precedes the full written opinion. DPP class: all persons and entities in the U.S. that purchased JUUL e-cigarette products directly from JLI from October 5, 2018 to the present.",
    ref: "Dkt.627 (3:20-cv-02345-WHO, Feb 5, 2026)",
    type: "factual",
    ecf: "Three distribution channels at issue: wholesale, direct retail, direct-to-consumer. Court finds prices 'generally uniform' across channels. Defendants' typicality and adequacy objections rejected - only two defendants, three channels, one product set (JUUL pods).",
  },
  {
    plaintiff: "All",
    date: "Feb 2023",
    label: "FTC dismisses administrative complaint - vacates ALJ decision",
    detail:
      "The FTC dismisses its own administrative complaint against Altria and JLI, vacating the ALJ's initial decision that had found the defendants liable. The FTC cites procedural reasons - dismissal would have deprived staff attorneys of appellate review of the ALJ decision. The dismissal moots the government enforcement case but does not affect the private antitrust class action in N.D. Cal., which continues on its own track.",
    ref: "FTC Docket 9393 (Feb 2023); 3:20-cv-02345-WHO",
    type: "factual",
    ecf: "FTC dismissal is neutral on the merits - the agency did not conclude the conduct was lawful. Private plaintiff class action in N.D. Cal. is unaffected. Plaintiffs argue FTC's withdrawal signals institutional resource constraints, not a concession on the antitrust theory.",
  },
  {
    plaintiff: "SFUSD",
    date: "Oct 7, 2022",
    label: "MSJ Hearing before Judge Orrick (1:30 PM)",
    detail:
      "Summary judgment hearing held in Courtroom 2, 17th Floor, 450 Golden Gate Avenue, San Francisco. Multiple defendants' MSJs heard simultaneously. Court takes matters under submission.",
    ref: "Dkt. scheduling order",
    type: "factual",
    ecf: "Hearing encompasses JLI MSJ (Dkt.3396), Bowen MSJ, Altria MSJ, and Management Defendant MSJs.",
  },
  {
    plaintiff: "SFUSD",
    date: "Sep 27, 2022",
    label: "Bowen files Reply ISO MSJ (Dkt.3527)",
    detail:
      "Boersch & Illovsky (for Bowen) files reply on RICO conduct element, racketeering activity pattern, RICO causation, negligence statute of limitations, and duty. Key argument: product design alone insufficient for RICO conduct without directing affairs of enterprise.",
    ref: "Dkt.3527",
    type: "factual",
    ecf: "Bowen: 'Plaintiff\'s outrageous position, that a corporate officer or director is liable under RICO as long as he manages some aspect of a corporation accused of fraud, would push civil RICO past the breaking point.'",
  },
  {
    plaintiff: "SFUSD",
    date: "Sep 12, 2022",
    label: "SFUSD files Consolidated Opposition to MSJ (Dkt.3447)",
    detail:
      "Co-Lead Counsel (London/LCHB, Sharp/Girard Sharp, Kawamoto/Keller Rohrback, Relkin/Weitz & Luxenberg) file 149-page opposition addressing RICO, negligence, and nuisance claims against all defendants. Includes detailed SOF from depositions, expert reports, and documentary evidence.",
    ref: "Dkt.3447",
    type: "factual",
    ecf: "SFUSD presents evidence: cut fences, boarded bathroom door, 'fruity fog,' student declarations, Dorn site inspection reports, $34,875+ in documented direct expenditures. Identifies 4 categories of damages.",
  },
  {
    plaintiff: "All",
    date: "Sep 2022",
    label: "JUUL settles with 34 State AGs for $438.5M",
    detail:
      "JUUL Labs pays $438.5 million to 34 state AGs in September 2022 to settle youth marketing claims. Includes marketing restrictions, data deletion, independent compliance officer. Does NOT cover Seliavski PI, SFUSD government entity claim, or the antitrust class action (3:20-cv-02345). JLI separately settles consumer class for $255M (Dkt.4138, Sept 2023); Altria settles consumer class separately. Opt-out PI plaintiffs including Seliavski remain active.",
    ref: "Public record / Dkt.3600+",
    type: "factual",
    ecf: "Settlement includes marketing restrictions, data deletion, independent compliance officer. Does not resolve individual PI claims.",
  },
  {
    plaintiff: "SFUSD",
    date: "Aug 15, 2022",
    label: "JLI files Motion for Summary Judgment re SFUSD (Dkt.3396)",
    detail:
      "Kirkland & Ellis (Bernick, Smith, Farrell, Wilcox) files JLI's MSJ on SFUSD's public nuisance, negligence, and gross negligence claims. Three grounds: (1) SFUSD lacks statutory standing under Rincon Band v. Flynt; (2) equitable abatement unavailable under Sonner; (3) zero evidence of past damages.",
    ref: "Dkt.3396 (Filed Under Seal)",
    type: "factual",
    ecf: "JLI seeks judgment that SFUSD has 'no fact witnesses, no documents, and no experts that can quantify any harm.' Abatement demand: hundreds of millions of dollars for 15-year program.",
  },
  {
    plaintiff: "All",
    date: "Jun 2022",
    label: "FDA issues Marketing Denial Order for JUUL products",
    detail:
      "FDA issues Marketing Denial Order in June 2022 based on insufficient toxicological data and no net public health benefit finding. JLI obtains emergency 5th Circuit stay the same day. FDA later withdraws MDO for further review - it never became final. Plaintiffs use the non-final MDO to rebut JLI harm-reduction narrative; JLI argues non-final status means it cannot establish inadequacy as a legal matter.",
    ref: "Public record",
    type: "factual",
    ecf: "MDO based on toxicological and genotoxicity concerns. 5th Circuit stays MDO pending JUUL appeal. FDA withdraws MDO for further review.",
  },
  {
    plaintiff: "All",
    date: "Aug 20, 2021",
    label:
      "Antitrust court grants MTD - compels DPP arbitration; Sherman Act survives",
    detail:
      "Judge Orrick grants JLI's motion to compel Direct Purchaser Plaintiffs (DPPs) to arbitrate their claims based on JLI website clickwrap arbitration policy. However, Sherman Act and Clayton Act claims against JLI and Altria go forward. Individual director defendants (Pritzker, Valani) dismissed from antitrust case on standing grounds. This ruling creates a two-track structure: arbitration for some DPPs, litigation for others and all indirect purchasers.",
    ref: "3:20-cv-02345-WHO (Aug 20, 2021)",
    type: "factual",
    ecf: "Court enforces JLI website arbitration clause - users who registered after Aug 2018 modifications constructively assented. Sherman Act claims survive because JUUL-as-enterprise claims are distinct from individual purchaser contract claims. IPPs and IRPs not subject to arbitration; their claims proceed.",
  },
  {
    plaintiff: "All",
    date: "Apr 1, 2020",
    label: "FTC files administrative complaint - Altria/JLI non-compete",
    detail:
      "The Federal Trade Commission files an administrative complaint alleging Altria and JLI unlawfully agreed that Altria would cease competing in e-cigarettes. The FTC characterized the Altria investment as a horizontal market allocation conspiracy. The administrative proceeding proceeds before an ALJ while the private class action is simultaneously pending in N.D. Cal.",
    ref: "FTC Dkt. 9393; 3:20-cv-02345-WHO",
    type: "factual",
    ecf: "FTC complaint parallels the private antitrust class action. ALJ issues initial decision against defendants. FTC later dismisses complaint Feb 2023 - vacating ALJ decision - to allow further review, mooting the government case without prejudice to private plaintiffs.",
  },
  {
    plaintiff: "All",
    date: "Sep 2019",
    label: "JUUL suspends US sales of non-tobacco flavored pods",
    detail:
      "After FDA and congressional pressure, JUUL suspends mint and mango pod sales in the United States.",
    ref: "Master Compl. ¶¶148-152",
    type: "factual",
    ecf: "JUUL retains tobacco and menthol flavors. Mint accounted for 70%+ of US pod sales at time of suspension.",
  },
  {
    plaintiff: "All",
    date: "Dec 2018",
    label: "Altria acquires 35% stake in JUUL for $12.8B - non-compete alleged",
    detail:
      "Altria pays $12.8 billion all-cash for 35% of JLI in December 2018. Simultaneously, Altria withdraws its competing e-cigarette products (MarkTen, Green Smoke) from the U.S. market. Altria brings 400,000+ retail distribution locations, FDA regulatory expertise, and lobbying infrastructure. The non-compete structure is the foundation of the parallel antitrust class action (3:20-cv-02345-WHO). Antitrust class certified Feb 26 2026. Trial September 28 2026.",
    ref: "Master Compl. ¶¶98-112",
    type: "factual",
    ecf: "Sherman Act theory: Altria paid $12.8B for equity AND agreed to exit the e-cigarette market - a per se horizontal market allocation eliminating Altria as the most likely competitive threat to JUUL. Three distribution channels: wholesale, direct retail, direct-to-consumer. Prices across channels found generally uniform by class cert court. Expert Leger qualified on overcharge methodology.",
  },
  {
    plaintiff: "All",
    date: "Dec 2018",
    label: "Altria acquires 35% stake - non-compete agreement alleged",
    detail:
      "Altria pays $12.8 billion all-cash for 35% of JLI and simultaneously withdraws its competing closed-system e-cigarette products (MarkTen, Green Smoke) from the U.S. market. Plaintiffs allege this withdrawal was not a legitimate business decision but a horizontal non-compete agreement in violation of the Sherman Act - Altria exiting the market in exchange for equity and access to JLI's distribution infrastructure.",
    ref: "3:20-cv-02345-WHO; TAC ¶¶98-112",
    type: "factual",
    ecf: "Altria brings cigarette distribution network (400,000+ retail locations), regulatory expertise, and lobbying infrastructure to JLI. Plaintiffs allege the non-compete artificially eliminated the most likely competitive threat to JUUL's closed-system market dominance.",
  },
  {
    plaintiff: "All",
    date: "Apr 2018",
    label: "FDA sends JUUL warning letter re: youth marketing",
    detail:
      "FDA warns JUUL about marketing practices targeting minors. Congressional hearings follow.",
    ref: "Master Compl. ¶¶135-140",
    type: "factual",
    ecf: "FDA letter cites 'combustible cigarette' marketing to youth, fruit-flavored pods, social media campaigns.",
  },
  {
    plaintiff: "Seliavski",
    date: "Jul 2017",
    label: "Seliavski begins using JUUL",
    detail:
      "Julia Seliavski (Richmond, VA) begins using JUUL products. Use continues through Spring 2018.",
    ref: "Dkt.4094 ¶7",
    type: "factual",
    ecf: "Product use period: July 2017 – Spring 2018. JUUL pods purchased at retailers including 7-Eleven, Circle K, and Walgreens.",
  },
  {
    plaintiff: "All",
    date: "Jun 2015",
    label: "JUUL launched commercially",
    detail:
      "JUUL Labs (then PAX Labs) releases JUUL e-cigarette. Pod system delivers nicotine salt at ~5% concentration - far higher than prior devices.",
    ref: "Master Compl. ¶42",
    type: "factual",
    ecf: "JUUL designed to deliver nicotine more efficiently than cigarettes. Pods contain ~0.7 mL e-liquid, ~59 mg/mL nicotine.",
  },
  {
    plaintiff: "Seliavski",
    date: "Spring 2018",
    label: "Seliavski develops addiction and behavioral injuries",
    detail:
      "Julia Seliavski (Richmond, VA), age 16 at first use, develops nicotine addiction and behavioral injuries from JUUL use July 2017 through Spring 2018. Injuries claimed: nicotine addiction, nicotine poisoning, anger/outbursts, mood swings, irritability. JUUL pods purchased at 7-Eleven, Circle K, Walgreens. No medical causation expert retained - highest-priority gap before remand to E.D. Va.",
    ref: "Dkt.4094 ¶8",
    type: "factual",
    ecf: "Injuries checked: Addiction, Nicotine Poisoning, Anger/Outbursts, Mood Swings, Irritability.",
  },
  {
    plaintiff: "SFUSD",
    date: "2014-2015",
    label: "SFUSD adds e-cigarette prohibition to tobacco-free policy",
    detail:
      "San Francisco Unified School District amends its tobacco-free policy to specifically prohibit e-cigarettes, including JUUL. Student vaping still relatively low at this time.",
    ref: "Pak 4/8/21 Dep. 99:5-7",
    type: "factual",
    ecf: "Policy prohibits ENDS 'any time, in buildings, on school property, in district vehicles.' Restorative practices used over suspension.",
  },
  {
    plaintiff: "SFUSD",
    date: "2015-2017",
    label: "SFUSD achieves historic low combustible cigarette rates",
    detail:
      "SFUSD reduces high school students who ever tried a cigarette from 60% (1997) to 18.6% (2019). 30-day smoking rate falls from 19.1% to 6.5%. E-cigarette use still low before JUUL's rise.",
    ref: "Ex. 299 (HS YRBS Results, 1997-2019)",
    type: "factual",
    ecf: "SFUSD had succeeded in reducing youth combustible use through sustained education, community involvement, media literacy.",
  },
  {
    plaintiff: "SFUSD",
    date: "2017-2018 school year",
    label: "JUUL-led youth vaping crisis explodes at SFUSD",
    detail:
      "Beginning in approximately 2017-18, e-cigarette use - led by JUUL - grows at alarming rates. Reports pour in from principals, nurses, and social workers. Students using JUUL in classrooms, hallways, bathrooms. One youth outreach worker (class of 2018) saw daily JUUL use including students selling JUULpods during class.",
    ref: "Pak 10/7/21 Dep. 34-35; Coates Dep. 36-38",
    type: "factual",
    ecf: "JUUL was 'the most popular product' and 'most familiar product to our students and staff.' Kim Coates: e-cigarettes are 'right at the top' among student health concerns.",
  },
  {
    plaintiff: "SFUSD",
    date: "Fall 2018",
    label: "SFUSD creates 'JUUL School' professional development program",
    detail:
      "Fall 2018: professional development training presented to SFUSD health educators and PE teachers titled 'JUUL School.' SFUSD developed and distributed vaping prevention resources for all grade levels - training staff to recognize JUUL, implement health education, and connect students to intervention services.",
    ref: "Ex. 210 (SFUSD_001936); Coates Dep. 33:6-15",
    type: "factual",
    ecf: "Quarry Pak: 'Years. And we\'re still working on it.' Resources developed from scratch because educational materials for e-cigarettes did not previously exist.",
  },
  {
    plaintiff: "SFUSD",
    date: "2018-2019",
    label: "Physical property damage documented at SFUSD schools",
    detail:
      "Bathroom door at Balboa High School broken and boarded up by students rushing to vape. Fence cut at McAteer/Ruth Asawa SF School of the Arts to access 'the canyon' vaping hotspot. Master key stolen at Aptos MS to unlock gym. Garden area shut down at Denman MS. Balcony stairwell chained at Presidio MS. JUUL devices hidden in seat covers and water valves at Downtown HS.",
    ref: "Dorn site notes – Balboa HS, McAteer, Aptos MS, Denman MS, Presidio MS",
    type: "factual",
    ecf: "Ex. 215-221. Dorn: student vaping is 'having a catastrophic effect' on SFUSD\'s ability to educate students.",
  },
  {
    plaintiff: "SFUSD",
    date: "2018-2019",
    label: "SFUSD students unaware JUUL contains nicotine",
    detail:
      "75% of SFUSD students aged 15-24 who could recognize a JUUL did not know it always contained nicotine. E-cigarette use by SFUSD high school students who tried in past 30 days more than doubled from 7.1% to 16% from 2017-2019.",
    ref: "Halpern-Felsher Sept. 2021 Rpt. at 64-65",
    type: "factual",
    ecf: "Students described bathroom aerosol as 'fruity fog.' Pediatrician treating SFUSD-area patients: '90% of kids in high school reported constant use in bathrooms.'",
  },
  {
    plaintiff: "SFUSD",
    date: "Oct 2021 – May 2022",
    label: "SFUSD depositions - Pak, Coates, Matthews, Lingrell",
    detail:
      "SFUSD corporate representatives (30(b)(6)) and executives deposed by JLI over multiple sessions. Key witnesses: Quarry Pak (program administrator), Kimberly Coates (Executive Director, Student & Family Services), Supt. Vincent Matthews, Erica Lingrell (program administrator). JLI uses testimony to argue no quantifiable past damages.",
    ref: "Pak Deps. 4/8/21, 5/13/21, 5/27/21, 10/7/21, 10/15/21, 12/21/21; Coates Dep. 9/28/21",
    type: "factual",
    ecf: "Pak 12/21/21: 'We don\'t have that information' re costs incurred. Matthews: unaware of document quantifying staff time. JLI later uses these admissions in MSJ (Dkt.3396).",
  },
];

const JUUL_P_TL = [
  {
    plaintiff: "All",
    date: "10/02/2019",
    label: "MDL Transfer Order - MDL No. 2913 created",
    detail:
      "MDL Panel transfers all federal JUUL cases to N.D. Cal., San Francisco. Assigned to Judge William H. Orrick.",
    ref: "Dkt.1",
    type: "procedural",
    ecf: "TRANSFER ORDER by MDL Panel. 3:19-md-02913-WHO created. Judge Orrick appointed. First status conference set.",
  },
  {
    plaintiff: "All",
    date: "10/02/2019",
    label: "Pretrial Order #1 - Initial Case Management",
    detail:
      "Judge Orrick sets leadership application deadline, defines PTO procedures, establishes direct-filing protocol.",
    ref: "Dkt.2",
    type: "procedural",
    ecf: "PRETRIAL ORDER #1. Leadership apps due 10/16/2019. All new JUUL cases to be direct-filed in N.D. Cal.",
  },
  {
    plaintiff: "All",
    date: "12/13/2019",
    label: "CMO No. 3 - Direct Filing Order",
    detail:
      "Case Management Order No. 3 formalizes direct-filing procedure. All PI plaintiffs may file directly in the MDL.",
    ref: "Dkt.309",
    type: "procedural",
    ecf: "CASE MANAGEMENT ORDER NO. 3. Direct filing in MDL without need to first file in home district.",
  },
  {
    plaintiff: "All",
    date: "03/11/2020",
    label: "Consolidated Master Complaint (PI) filed",
    detail:
      "Plaintiffs file the Consolidated Master Complaint (Personal Injury). Named defendants include JUUL Labs, Altria entities, management defendants, e-liquid manufacturers, distributors, and retailers.",
    ref: "Dkt.388",
    type: "procedural",
    ecf: "PLAINTIFFS' CONSOLIDATED MASTER COMPLAINT (PERSONAL INJURY). All PI plaintiffs adopt by filing Short-Form Complaint per CMO No. 7.",
  },
  {
    plaintiff: "All",
    date: "03/27/2020",
    label: "CMO No. 7 - Short-Form Complaint system adopted",
    detail:
      "Court establishes Short-Form Complaint procedure. Individual plaintiffs file abbreviated complaints adopting Master Complaint allegations.",
    ref: "Dkt.405",
    type: "procedural",
    ecf: "CASE MANAGEMENT ORDER NO. 7. Governs adoption of Master Complaint (PI) and Short-Form Complaint procedure.",
  },
  {
    plaintiff: "All",
    date: "04/06/2020",
    label: "Amended Master Complaint (PI) filed",
    detail:
      "Plaintiffs file Amended Consolidated Master Complaint (PI) adding retailer defendants including Walmart, 7-Eleven, Speedway, Circle K, Chevron, Walgreens.",
    ref: "Dkt.420",
    type: "procedural",
    ecf: "AMENDED MASTER COMPLAINT (PI). Adds retailer defendants.",
  },
  {
    plaintiff: "All",
    date: "05/23/2023",
    label: "JLI Class Action Settlement - Final Approval",
    detail:
      "Judge Orrick grants final approval of JLI class action settlement. Settlement resolves class consumer claims.",
    ref: "Dkt.3779",
    type: "procedural",
    ecf: "ORDER GRANTING FINAL APPROVAL OF JLI CLASS ACTION SETTLEMENT. Individual PI cases proceed separately under short-form complaint system.",
  },
  {
    plaintiff: "Seliavski",
    date: "07/31/2023",
    label: "Seliavski Amended Short-Form Complaint filed",
    detail:
      "William Levin (Levin Simes LLP) files Amended Short-Form Complaint (PI) for Julia Seliavski. Dkt.4094. E.D. Va. as transferee district.",
    ref: "Dkt.4094",
    type: "procedural",
    ecf: "AMENDED SHORT-FORM COMPLAINT AND JURY DEMAND (PERSONAL INJURY). Julia Seliavski. Filed 7/31/2023. Incorporates all PI Master Complaint allegations. Jury trial demanded.",
  },
  {
    plaintiff: "All",
    date: "08/09/2023",
    label: "JLI Settlement Fairness Hearing held",
    detail:
      "Judge Orrick holds Fairness Hearing re: JLI class settlement and Altria preliminary approval. Zoom hearing.",
    ref: "Dkt.4105",
    type: "procedural",
    ecf: "Minute Entry: Motion Hearing held 8/9/2023 re 4054 Motion for Final Approval JLI Class Action Settlement.",
  },
  {
    plaintiff: "SFUSD",
    date: "12/2019",
    label: "SFUSD files original complaint (Case No. 3:19-cv-8177)",
    detail:
      "San Francisco Unified School District files original complaint against JUUL Labs and related defendants in N.D. Cal., consolidated into MDL 2913.",
    ref: "Case No. 3:19-cv-08177-WHO",
    type: "procedural",
    ecf: "Original complaint. Three claims against JLI: public nuisance (Count I), negligence (Count III), gross negligence (Count IV). RICO claim (Count II) against other defendants only.",
  },
  {
    plaintiff: "SFUSD",
    date: "03/11/2021",
    label: "SFUSD files Amended Complaint (ECF No. 9)",
    detail:
      "SFUSD files Amended Complaint (163 paragraphs) asserting public nuisance, RICO, negligence, and gross negligence. Seeks abatement fund and damages.",
    ref: "ECF 9, Case No. 3:19-cv-08177",
    type: "procedural",
    ecf: "Am. Compl. alleges widespread classroom disruptions, disciplinary issues, staff diversions, and hazardous waste from JUUL products. Abatement demand: $163M+ (Kelder) + $109-145M (Winickoff).",
  },
  {
    plaintiff: "SFUSD",
    date: "08/15/2022",
    label: "JLI files MSJ on SFUSD claims (Dkt.3396)",
    detail:
      "Kirkland & Ellis files JLI's Motion for Summary Judgment on SFUSD's public nuisance, negligence, and gross negligence claims. Filed under seal. Argues: (1) Rincon bars SFUSD's nuisance claim; (2) Sonner bars abatement; (3) zero past damages.",
    ref: "Dkt.3396",
    type: "procedural",
    ecf: "Motion filed under seal. Companion motions filed by Bowen, Monsees, Altria, ODDs. All heard together Oct 7, 2022.",
  },
  {
    plaintiff: "SFUSD",
    date: "09/12/2022",
    label: "SFUSD files Consolidated Opposition to MSJ (Dkt.3447)",
    detail:
      "149-page opposition on behalf of SFUSD. Addresses all defendants' MSJs on RICO, negligence, and nuisance. Includes detailed statement of facts, Table of Authorities with 100+ cases.",
    ref: "Dkt.3447",
    type: "procedural",
    ecf: "Filed by Sarah London (LCHB), Dena Sharp (Girard Sharp), Dean Kawamoto (Keller Rohrback), Ellen Relkin (Weitz & Luxenberg) as Co-Lead Counsel.",
  },
  {
    plaintiff: "SFUSD",
    date: "09/27/2022",
    label: "Bowen files Reply ISO MSJ (Dkt.3527)",
    detail:
      "Boersch & Illovsky files Bowen's 20-page reply brief. Addresses RICO conduct element, pattern of racketeering, RICO causation, statute of limitations, and duty arguments.",
    ref: "Dkt.3527",
    type: "procedural",
    ecf: "Bowen joins and incorporates replies filed concurrently by JLI, Altria, NMDs, and Monsees.",
  },
  {
    plaintiff: "All",
    date: "04/23/2020",
    label: "Antitrust complaint filed - Saveri (Admin Motion to Relate)",
    ref: "Dkt.493 (3:19-md-02913); Dkt.1 (3:20-cv-02345)",
    type: "procedural",
    ecf: "Saveri Law Firm files admin motion to relate the antitrust class action to the MDL. Flannery Associates files separate motion (Dkt.515). Court relates cases May 26 2020 - antitrust case becomes companion to MDL under same judge.",
  },
  {
    plaintiff: "All",
    date: "05/26/2020",
    label: "Antitrust case related to MDL - Order (Dkt.583)",
    ref: "Dkt.583 (3:19-md-02913)",
    type: "procedural",
    ecf: "Judge Orrick relates antitrust actions (3:20-cv-02345) to MDL 2913. Both cases proceed on coordinated track. Antitrust case assigned Case No. 3:20-cv-02345-WHO.",
  },
  {
    plaintiff: "All",
    date: "04/01/2020",
    label: "FTC files administrative complaint - Altria/JLI non-compete",
    ref: "FTC Dkt. 9393",
    type: "procedural",
    ecf: "Federal Trade Commission files administrative complaint before ALJ alleging Altria and JLI agreed that Altria would cease competing in e-cigarettes in violation of Section 5 of the FTC Act. ALJ proceeds to initial decision finding liability against defendants. Separate from N.D. Cal. private antitrust class action.",
  },
  {
    plaintiff: "All",
    date: "08/20/2021",
    label:
      "Antitrust court grants MTD - compels DPP arbitration; Sherman Act survives",
    ref: "3:20-cv-02345-WHO (Aug 20, 2021)",
    type: "procedural",
    ecf: "Court grants JLI motion to compel Direct Purchaser Plaintiffs to arbitrate - JLI website user agreement clickwrap policy enforced. Sherman Act and Clayton Act claims against both JLI and Altria survive and proceed. Individual director defendants (Pritzker, Valani) dismissed from antitrust case on standing grounds. IPPs and IRPs not subject to arbitration - their claims continue on litigation track.",
  },
  {
    plaintiff: "All",
    date: "01/20/2022",
    label:
      "Court compels arbitration for new DPPs - stays order pending amendment",
    ref: "3:20-cv-02345-WHO (Jan 20, 2022)",
    type: "procedural",
    ecf: "Court finds addition of two new DPPs whose registrations preceded Aug 2018 website changes does not upend earlier arbitration ruling - those plaintiffs also constructively assented. Court compels arbitration but stays effect of order. Gives DPPs leave to amend with new named plaintiffs who purchased prior to the Aug 2018 website modifications that established objective constructive assent.",
  },
  {
    plaintiff: "All",
    date: "02/2023",
    label: "FTC dismisses administrative complaint - vacates ALJ decision",
    ref: "FTC Dkt. 9393 (Feb 2023)",
    type: "procedural",
    ecf: "FTC dismisses its own administrative complaint, vacating the ALJ initial decision finding liability. FTC cites procedural reason: dismissal would have deprived staff attorneys of opportunity to have ALJ decision reviewed on appeal. Dismissal is NOT a merits determination - FTC does not conclude the conduct was lawful. Private antitrust class action (3:20-cv-02345) is entirely unaffected.",
  },
  {
    plaintiff: "All",
    date: "02/05/2026",
    label: "Antitrust short order - DPP class certified (Dkt.627)",
    ref: "Dkt.627 (3:20-cv-02345-WHO)",
    type: "procedural",
    ecf: "Judge Orrick issues short order granting DPP motion for class certification, granting in part IPP and IRP motions, and resolving motions to exclude experts. Full written opinion to follow. DPP class: all persons and entities in U.S. that purchased JUUL e-cigarette products directly from JLI from October 5 2018 to present.",
  },
  {
    plaintiff: "All",
    date: "02/26/2026",
    label: "Full class certification order - DPP, IPP, IRP certified (Dkt.635)",
    ref: "Dkt.635 (3:20-cv-02345-WHO)",
    type: "procedural",
    ecf: "100+ page certification opinion. DPP class certified. IPP class certified for CA FL HI MA NY RI (individuals buying from brick-and-mortar retailers Oct 5 2018 to present). IRP class certified for CA FL MI (businesses buying JUUL pods Dec 1 2018 through Mar 31 2025). EXCLUDED: AR SC TN VA - states that do not allow indirect purchaser antitrust class actions. Cartwright Act applied extraterritorially to 31 Illinois Brick repealer states. Expert rulings: Leger (DPP overcharge) allowed; Perry (DPP rebuttal) allowed in rebuttal only; Singh (market transaction analysis) allowed; Dobbins limited to pharmacology and nicotine addiction only; HSR expert admissibility reserved.",
  },
  {
    plaintiff: "All",
    date: "03/17/2026",
    label: "Antitrust class notice plan approved (Dkt.663)",
    ref: "Dkt.663 (3:20-cv-02345-WHO)",
    type: "procedural",
    ecf: "Court approves direct and indirect purchaser class notice plan. Notice to all class members of opt-out deadline and trial date. Active defendants: Altria Group, JLI, Nicholas Pritzker, Riaz Valani. Indirect purchaser class website: JuulAntitrustConsumer.com.",
  },
  {
    plaintiff: "All",
    date: "03/30/2026",
    label: "Antitrust class notice commenced",
    ref: "PRNewswire; Dkt.663",
    type: "procedural",
    ecf: "Class notice published via PRNewswire and direct mail to known class members. Notice identifies opt-out deadline of June 13 2026 and trial date of September 28 2026. Defendants filed Rule 23(f) petition for interlocutory review of class certification at Ninth Circuit - petition pending; could affect trial schedule if granted.",
  },
  {
    plaintiff: "All",
    date: "07/2023",
    label: "Altria class settlement - preliminary approval motion (Dkt.4082)",
    ref: "Dkt.4082",
    type: "procedural",
    ecf: "Plaintiffs move for preliminary approval of Altria class settlement. Separate from the $255M JLI class settlement. Hearing held Sept 6 2023 (Dkt.4126). Preliminary approval granted Sept 7 2023 (Dkt.4130). Final approval hearing follows.",
  },
  {
    plaintiff: "All",
    date: "05/23/2023",
    label: "Altria class settlement - final approval (Dkt.4238)",
    ref: "Dkt.4238",
    type: "procedural",
    ecf: "Judge Orrick grants final approval of Altria consumer class settlement. Attorney fees motion (Dkt.4192) and tribal attorney fees (Dkt.4214) separately resolved. Common benefit fund assessments apply to all participating plaintiffs. Settlement distribution ongoing through 2026.",
  },
  {
    plaintiff: "All",
    date: "10/31/2025",
    label: "Dispositive motion hearing - MDL opt-out cases (Dkt.4453)",
    ref: "Dkt.4453 (Transcript)",
    type: "procedural",
    ecf: "Judge Orrick holds 43-minute SJ hearing on CMO 17/19 opt-out cases via Zoom. Tentative issued. Defense requests fraud claims narrowed. Personal jurisdiction on NMDs deferred to transferee courts. Court signals intent to remand after SJ ruling. Cases covered: Aragona, Lane, Legacki, Nessmith, Sedgwick. Tribal cases in mediation.",
  },
  {
    plaintiff: "All",
    date: "12/16/2025",
    label: "CMC - remand and supplemental discovery scope (Dkt.4476)",
    ref: "Dkt.4476 (Transcript)",
    type: "procedural",
    ecf: "Case management conference on opt-out PI cases. Orrick confirms remand intent post-SJ. Parties dispute scope of supplemental discovery - depositions of founders re FDA approval, text messages, expert overlap. Court orders joint or separate letter brief by Jan 6 2026 identifying what additional discovery is truly non-duplicative.",
  },
  {
    plaintiff: "All",
    date: "04/14/2026",
    label: "Court orders Meta account data production",
    ref: "Dkt.4497; Order Apr 14, 2026",
    type: "procedural",
    ecf: "Judge Orrick orders Meta Platforms to produce decedent account data. Production deadline extended to May 8 2026 by stipulation (Dkt.4497, filed Apr 24 2026). Plaintiff must file non-compliance notice within 3 business days of any failure. Evidence may show JLI youth-targeted advertising on Instagram and TikTok.",
  },
];

const JUUL_S_TL = [
  {
    plaintiff: "All",
    date: "Jun 13, 2026",
    label: "🔴 JUUL Antitrust - Opt-Out Deadline (CRITICAL)",
    detail:
      "Hard deadline for direct purchaser class members to opt out of 3:20-cv-02345-WHO. Any class member who purchased JUUL products directly from JLI between Oct 5 2018 and Feb 26 2026 who does not timely opt out will be bound by any judgment or settlement.",
    ref: "Dkt.663; Class Notice",
    type: "scheduling",
    ecf: "CRITICAL: After this date no opt-outs permitted. Defendants' Rule 23(f) petition pending at Ninth Circuit.",
  },
  {
    plaintiff: "SFUSD",
    date: "May 11, 2026",
    label: "Supplemental Discovery Deadline - Social Media / Meta",
    detail:
      "Deadline for supplemental non-duplicative discovery including Meta account data production (per Dkt.4488 / Dkt.4497). Judge Orrick ordered Meta to produce decedent account data Apr 14, 2026.",
    ref: "Dkt.4488; Dkt.4497",
    type: "scheduling",
    ecf: "Meta production deadline. Plaintiff must file non-compliance notice within 3 business days of any failure.",
  },
  {
    plaintiff: "All",
    date: "Sep 28, 2026",
    label: "🔴 JUUL Antitrust - Trial Date (3:20-cv-02345-WHO)",
    detail:
      "Antitrust trial scheduled in In re JUUL Labs, Inc. Antitrust Litigation. Theory: Altria and JLI conspired under Sherman Act to restrain competition in closed system e-vapor products market. Altria paid $12.8B for 35% stake and exited, allegedly fixing prices at supracompetitive levels.",
    ref: "3:20-cv-02345-WHO; Dkt.663",
    type: "scheduling",
    ecf: "NOTE: Defendants' Rule 23(f) petition for Ninth Circuit review of class certification is pending and could affect trial schedule.",
  },
  {
    plaintiff: "All",
    date: "Late 2026",
    label: "Common Benefit Fee Motion - Co-Lead Counsel",
    detail:
      "Co-Lead Counsel (London, Sharp, Haberman et al.) anticipate filing additional common benefit fee and cost motions following supplemental discovery and remand. Third Supplemental Expense Recommendations filed Oct 2025 (Dkt.4443).",
    ref: "Dkt.4443; MDL Admin Order",
    type: "scheduling",
    ecf: "Common benefit fund draws from participating plaintiff settlements. All opt-out plaintiffs subject to common benefit assessment per MDL orders.",
  },
  {
    plaintiff: "Seliavski",
    date: "TBD",
    label: "Plaintiff Fact Sheet due (Seliavski)",
    detail:
      "Individual plaintiff must submit completed Plaintiff Fact Sheet per CMO No. 8.",
    ref: "CMO No. 8 (Dkt.406)",
    type: "scheduling",
    ecf: "Fact sheet includes medical records, product purchase details, healthcare providers.",
  },
  {
    plaintiff: "All",
    date: "TBD",
    label: "Expert Designation Deadline",
    detail:
      "Expert disclosures due under applicable scheduling order for applicable PI cases.",
    ref: "Scheduling Order TBD",
    type: "scheduling",
    ecf: "Includes damages expert, toxicology expert, addiction medicine expert.",
  },
  {
    plaintiff: "All",
    date: "TBD",
    label: "Bellwether Pool Selection",
    detail:
      "Court may select individual PI cases as bellwether trials for the remaining MDL personal injury docket.",
    ref: "Dkt.938",
    type: "scheduling",
    ecf: "ORDER REGARDING BELLWETHER SELECTION 9/9/2020. Individual PI bellwethers to be selected for trial.",
  },
  {
    plaintiff: "All",
    date: "TBD post-SJ",
    label: "Remand Order - Opt-Out PI Cases to Transferee Courts",
    detail:
      "Following summary judgment ruling, Judge Orrick expected to issue remand orders transferring opt-out PI cases (including Seliavski) to transferee courts in CA, FL, and TX. Plaintiff counsel have signaled intent to seek immediate remand.",
    ref: "Oct 31, 2025 Hearing; Dkt.4476",
    type: "scheduling",
    ecf: "Remand targets: N.D. Cal. (SFUSD), M.D. Fla., S.D. Fla. (Schlesinger), D.D.C. Pre-remand: causation expert must be retained and disclosed before transfer order.",
  },
  {
    plaintiff: "All",
    date: "Dec 16, 2025",
    label: "CMC - Opt-Out Cases: Remand & Discovery Scope",
    detail:
      "Case Management Conference on opt-out PI cases. Judge Orrick confirms intent to remand after SJ. Both sides dispute scope of supplemental discovery (depositions of founders re FDA approval, text messages, expert overlap). Court requests joint/separate letter brief by Jan 6, 2026 on what additional discovery is truly non-duplicative.",
    ref: "Dkt.4476 (Transcript)",
    type: "scheduling",
    ecf: "Schlesinger Law for PI plaintiffs. Gunster (JLI), Kellogg Hansen (NMDs), Arnold & Porter (Altria). Court: 'I want to avoid duplication of discovery and burden' and 'let the transferor courts address state law issues.'",
  },
  {
    plaintiff: "All",
    date: "Jan 28, 2025",
    label: "Scheduling Order - CMO 17/19 Trial Schedule",
    detail:
      "Judge Orrick enters scheduling order per joint stipulation. Dispositive motions deadline: May 16, 2025. PI opposition deadline: June 13, 2025. Reply deadline: July 11, 2025. Per Dkt.4375.",
    ref: "Dkt.4375",
    type: "scheduling",
    ecf: "Per CMO 17 and CMO 19. Schedule covers Aragona, Lane, Legacki, Nessmith, Sedgwick and Dupree/Tortorici. Hearing on dispositive motions to be set following completion of briefing.",
  },
  {
    plaintiff: "All",
    date: "Jul 25, 2025",
    label: "Plaintiffs File Oppositions to Dispositive Motions",
    detail:
      "Plaintiffs file oppositions to all defendants dispositive motions per CMO 17/19. Dkt.4427. Briefing extended per joint stipulation. Non-management defendants (Pritzker, Valani, Huh) on extended schedule through Aug 27.",
    ref: "Dkt.4427-4429",
    type: "procedural",
    ecf: "Schlesinger Law Offices for PI Plaintiffs. Gunster (JLI), Arnold & Porter (Altria), Kellogg Hansen (NMDs), Orrick H&S (Monsees), Illovsky Gates (Bowen).",
  },
  {
    plaintiff: "All",
    date: "Mar 17, 2026",
    label: "JUUL Antitrust - Class Notice Plan Approved (3:20-cv-02345)",
    detail:
      "Judge Orrick grants order approving Direct Purchaser Class notice plan in the parallel antitrust case. Class: all US persons who purchased JUUL products directly from JLI between Oct 5 2018 and Feb 26 2026.",
    ref: "Dkt.663 (3:20-cv-02345-WHO)",
    type: "scheduling",
    ecf: "Antitrust case. Defendants: Altria Group, JLI, Nicholas Pritzker, Riaz Valani. Direct purchaser class certified.",
  },
  {
    plaintiff: "All",
    date: "Mar 30, 2026",
    label: "JUUL Antitrust - Class Notice Commenced",
    detail:
      "Direct purchaser class notice begins per PRNewswire. Class members have until June 13 2026 to opt out. Opt-out form at JuulAntitrustConsumer.com.",
    ref: "PRNewswire; Dkt.663",
    type: "scheduling",
    ecf: "CRITICAL DEADLINE: Opt-out deadline June 13 2026. Any class member who does not opt out will be bound by the judgment and release.",
  },
  {
    plaintiff: "All",
    date: "Oct 31, 2025",
    label: "Dispositive Motion Hearing - MDL Opt-Out Cases",
    detail:
      "Judge Orrick holds dispositive motion hearing on CMO 17/19 opt-out cases via Zoom. Tentative issued. Defense requests fraud claims narrowed. Personal jurisdiction on NMD defendants deferred to transferee courts. Court indicates intent to remand after ruling.",
    ref: "Dkt.4453 (Transcript)",
    type: "scheduling",
    ecf: "Hearing covers Aragona, Lane, Legacki, Nessmith, Sedgwick. Tribal cases (3) in mediation. Court signals plan to remand once SJ ruled, with guidance to transferee courts on discovery scope.",
  },
  {
    plaintiff: "All",
    date: "Sep 26, 2025",
    label: "Defendants File Replies ISO Dispositive Motions",
    detail:
      "Extended reply deadline per Dkt.4441. JLI, Monsees, Bowen, Pritzker, Valani, Huh file replies in support of dispositive motions. Hearing set Oct 31, 2025 at 9:30 AM by videoconference.",
    ref: "Dkt.4441",
    type: "scheduling",
    ecf: "Hearing reset from Sep 24 (Rosh Hashanah conflict) to Oct 31, 2025 at 9:30 AM by Zoom per Dkt.4435.",
  },
];

const JUUL_DOCKET = [
  {
    date: "04/24/2026",
    num: 4497,
    text: "STIPULATION AND ORDER extending Meta Platforms account data production deadline to May 8, 2026. Third-party social media production for supplemental opt-out discovery.",
    tags: ["procedural", "discovery"],
  },
  {
    date: "04/14/2026",
    num: 4495,
    text: "ORDER: Court orders Meta Platforms to produce account data related to JUUL advertising targeting. Deadline set (later extended to May 8, 2026 per Dkt.4497).",
    tags: ["procedural", "discovery"],
  },
  {
    date: "03/19/2026",
    num: 4490,
    text: "NOTICE OF COMPLIANCE with supplemental discovery order. Opt-out plaintiffs certify compliance with Jan 6, 2026 scoping letter.",
    tags: ["procedural", "discovery"],
  },
  {
    date: "03/06/2026",
    num: 4489,
    text: "ORDER granting motion for further settlement distribution. JLI class settlement ($255M) additional distribution authorized.",
    tags: ["procedural", "settlement"],
  },
  {
    date: "02/26/2026",
    num: 635,
    text: "ORDER GRANTING CLASS CERTIFICATION in antitrust parallel case 3:20-cv-02345-WHO. Direct Purchaser, Indirect Purchaser, and Indirect Reseller classes certified. Altria HSR expert admissibility reserved. Expert Slutsky designated as class expert. California Cartwright Act applied extraterritorially to 31 states. Trial set September 28, 2026.",
    tags: ["factual", "procedural", "antitrust"],
  },
  {
    date: "02/05/2026",
    num: 627,
    text: "SHORT ORDER GRANTING IN PART class certification in antitrust parallel case 3:20-cv-02345-WHO. DPP class certified. Full opinion to follow.",
    tags: ["procedural", "antitrust"],
  },
  {
    date: "01/06/2026",
    num: 4480,
    text: "JOINT LETTER from parties per Dec 16, 2025 CMC order: scope of non-duplicative supplemental discovery limited to deponent emails and social media records not yet produced in MDL.",
    tags: ["procedural", "discovery"],
  },
  {
    date: "12/16/2025",
    num: 4476,
    text: "CASE MANAGEMENT CONFERENCE. Judge Orrick confirms intent to remand opt-out PI cases post-SJ ruling. Orders joint letter by Jan 6, 2026 on supplemental discovery scope.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "11/19/2025",
    num: 4456,
    text: "ORDER GRANTING attorney fee motion for co-lead counsel common benefit fees.",
    tags: ["procedural"],
  },
  {
    date: "10/31/2025",
    num: 4453,
    text: "MSJ HEARING via Zoom. Judge Orrick hears argument on SJ motions for Aragona, Lane, Legacki, Nessmith, Sedgwick, Seliavski, SFUSD. 43 minutes. Orrick raises Sonner-Pak circular logic issue; telegraphs remand intent post-ruling. Under submission.",
    tags: ["procedural", "factual"],
  },
  {
    date: "05/03/2025",
    num: 4167,
    text: "ORDER TO SHOW CAUSE RE OVERBROAD SEALING of attorney fee committee materials. Orrick requires narrowly tailored justification for all sealed exhibits.",
    tags: ["procedural"],
  },
  {
    date: "03/01/2025",
    num: 3393,
    text: "ORDER granting in part motion to unseal Altria due diligence documents. Key pre-investment materials partially unsealed revealing Altria knowledge of youth demographics prior to $12.8B investment.",
    tags: ["factual", "evidence"],
  },
  {
    date: "02/26/2025",
    num: 4488,
    text: "ORDER: Supplemental discovery for opt-out PI plaintiffs. Parties to complete email and social media production disputes by May 11, 2026 (45 days from Mar 27, 2026).",
    tags: ["procedural", "discovery"],
  },
  {
    date: "12/07/2023",
    num: 4167,
    text: "ORDER TO SHOW CAUSE RE SEALING OF SETTLEMENT INFORMATION. JLI and Fee Committee ordered to show cause within 5 days why fee committee recommendations should remain sealed. Information does not appear to meet good cause standard given stage of JLI settlement and need for transparency for class members evaluating pending fee motion.",
    tags: ["procedural", "factual"],
  },
  {
    date: "08/09/2023",
    num: 4105,
    text: "MINUTE ENTRY: Fairness Hearing re JLI Class Action Settlement Final Approval. Before Judge Orrick via Zoom.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "08/09/2023",
    num: 4109,
    text: "CLERK'S NOTICE: Hearing re Altria Preliminary Approval set for 9/6/2023 02:00 PM.",
    tags: ["scheduling"],
  },
  {
    date: "07/31/2023",
    num: 4094,
    text: "AMENDED SHORT-FORM COMPLAINT (PI) - Julia Seliavski. Levin, William (Levin Simes LLP). Filed 7/31/2023. Transferee: E.D. Virginia.",
    tags: ["factual", "procedural"],
  },
  {
    date: "07/27/2023",
    num: 4082,
    text: "MOTION FOR PRELIMINARY APPROVAL OF ALTRIA CLASS ACTION SETTLEMENT. Filed by All Plaintiffs. Hearing set 9/6/2023.",
    tags: ["procedural", "factual"],
  },
  {
    date: "05/23/2023",
    num: 3779,
    text: "ORDER GRANTING FINAL APPROVAL OF JLI CLASS ACTION SETTLEMENT. Class consumer claims resolved. Individual PI cases proceed separately.",
    tags: ["procedural", "factual"],
  },
  {
    date: "02/01/2023",
    num: 3400,
    text: "ORDER RE JLI CLASS SETTLEMENT PRELIMINARY APPROVAL. Judge Orrick grants preliminary approval. Notice to class authorized.",
    tags: ["procedural"],
  },
  {
    date: "11/10/2022",
    num: 3200,
    text: "PRETRIAL ORDER RE JLI CLASS ACTION SETTLEMENT. Class notice, opt-out procedures, fairness hearing schedule established.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "09/27/2022",
    num: 3527,
    text: "BOWEN REPLY IN SUPPORT OF MOTION FOR SUMMARY JUDGMENT re SFUSD. RICO conduct, racketeering activity, causation, statute of limitations, duty.",
    tags: ["procedural", "factual"],
  },
  {
    date: "09/12/2022",
    num: 3447,
    text: "SFUSD CONSOLIDATED OPPOSITION TO DEFENDANTS' MOTIONS FOR SUMMARY JUDGMENT. 149 pages. RICO, negligence, nuisance. Co-Lead Counsel.",
    tags: ["procedural", "factual"],
  },
  {
    date: "09/06/2022",
    num: 3000,
    text: "NOTICE OF JUUL $438.5 MILLION MULTI-STATE AG SETTLEMENT with 34 State Attorneys General. Individual PI claims not released.",
    tags: ["factual"],
  },
  {
    date: "08/15/2022",
    num: 3396,
    text: "JLI MOTION FOR SUMMARY JUDGMENT re SFUSD (FILED UNDER SEAL). Kirkland & Ellis. Challenges SFUSD's standing, abatement remedy, and absence of past damages evidence.",
    tags: ["procedural", "factual"],
  },
  {
    date: "06/02/2022",
    num: 2800,
    text: "NOTICE OF FDA MARKETING DENIAL ORDER for JUUL products. FDA MDO based on toxicological data. JLI seeks emergency stay in 5th Circuit.",
    tags: ["factual"],
  },
  {
    date: "09/09/2021",
    num: 2200,
    text: "CASE MANAGEMENT ORDER RE TRIAL SCHEDULING AND PI BELLWETHER PROCEDURES. Individual PI bellwether candidates identified.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "05/03/2021",
    num: 1786,
    text: "ORDER APPOINTING SPECIAL MASTER HON. (RET.) STEPHEN G. LARSON to oversee depositions. Larson LLP, Los Angeles.",
    tags: ["procedural"],
  },
  {
    date: "04/28/2021",
    num: 1772,
    text: "Administrative Motion to Seal Plaintiffs' Motion for Class Certification. Initiates class cert briefing.",
    tags: ["procedural", "factual"],
  },
  {
    date: "03/10/2021",
    num: 1547,
    text: "CASE MANAGEMENT ORDER NO. 14 - NON-BELLWETHER CLASS REPRESENTATIVES FACT SHEET. Additional PFS requirements for non-bellwether class reps.",
    tags: ["procedural"],
  },
  {
    date: "02/08/2021",
    num: 1379,
    text: "CASE MANAGEMENT ORDER NO. 10-A - DEPOSITION SCHEDULING, CUSTODIAL FILE PRODUCTION, AND TRANSCRIPTS FROM RELATED ACTIONS.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "11/09/2020",
    num: 1125,
    text: "ORDER RE LEXECON AND BELLWETHER SELECTION FOR PI CASES. Individual PI bellwether candidates identified for trial pool.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "09/21/2020",
    num: 995,
    text: "CASE MANAGEMENT ORDER NO. 12 - SUPPLEMENTAL PLAINTIFF FACT SHEET AND RETAILER FACT SHEET. Additional discovery requirements for retailer defendants.",
    tags: ["procedural"],
  },
  {
    date: "09/09/2020",
    num: 938,
    text: "ORDER REGARDING BELLWETHER SELECTION AND CASE/TRIAL SCHEDULE. PI bellwether selection procedures and trial calendar established.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "08/21/2020",
    num: 914,
    text: "CASE MANAGEMENT ORDER NO. 11 - REMOTE DEPOSITIONS. COVID-era procedures for remote deposition protocol.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "06/19/2020",
    num: 680,
    text: "ORDER APPOINTING HON. (RET.) GAIL A. ANDLER as Common Benefit Special Master. Oversees common benefit timekeeping.",
    tags: ["procedural"],
  },
  {
    date: "05/29/2020",
    num: 626,
    text: "MOTION TO STAY filed by JLI based on FDA Primary Jurisdiction. JLI argues PMTA review should precede MDL adjudication. Denied.",
    tags: ["procedural", "factual"],
  },
  {
    date: "05/27/2020",
    num: 586,
    text: "CASE MANAGEMENT ORDER 5(A) - COMMON BENEFIT FEE AND EXPENSE FUND. Establishes 8% assessment. Governs billing and reimbursement.",
    tags: ["procedural"],
  },
  {
    date: "05/20/2020",
    num: 572,
    text: "CASE MANAGEMENT ORDER NO. 9 - JOINT COORDINATION ORDER. MDL and state court JUUL cases coordinate discovery and scheduling.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "05/20/2020",
    num: 573,
    text: "CASE MANAGEMENT ORDER NO. 10 - DEPOSITION PROTOCOL ORDER. Procedures for depositions across all MDL cases. Limits, cross-noticing, scheduling.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "04/06/2020",
    num: 420,
    text: "AMENDED MASTER COMPLAINT (PI). Adds retailer defendants: 7-Eleven, Walmart, Circle K, Speedway, Chevron, Walgreens. Refines factual allegations.",
    tags: ["factual", "procedural"],
  },
  {
    date: "03/27/2020",
    num: 405,
    text: "CASE MANAGEMENT ORDER NO. 7 - SHORT-FORM COMPLAINT PROCEDURE. Governs adoption of Master Complaint (PI) by individual plaintiffs.",
    tags: ["procedural"],
  },
  {
    date: "03/27/2020",
    num: 406,
    text: "CASE MANAGEMENT ORDER NO. 8 - FACT SHEET IMPLEMENTATION. Plaintiff Fact Sheet (PFS) requirements. Defendant to receive PFS before discovery.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "03/11/2020",
    num: 388,
    text: "CONSOLIDATED MASTER COMPLAINT (PI) filed. All named defendants. Jury trial demanded. Basis for all short-form complaints.",
    tags: ["factual", "procedural"],
  },
  {
    date: "01/14/2020",
    num: 357,
    text: "CASE MANAGEMENT ORDER NO. 6 - DISCOVERY DISPUTE RESOLUTION PROCEDURES. Signed by MJ Corley. Joint statement process for discovery disputes.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "01/13/2020",
    num: 352,
    text: "CASE MANAGEMENT ORDER NO. 5 - COMMON BENEFIT TIMEKEEPING AND EXPENSES PROTOCOL. 8% common benefit assessment established.",
    tags: ["procedural"],
  },
  {
    date: "12/17/2019",
    num: 322,
    text: "CASE MANAGEMENT ORDER NO. 4 - RULE 502(D) AND PRIVILEGED MATERIALS ORDER. Governs clawback of inadvertently produced privileged docs.",
    tags: ["procedural"],
  },
  {
    date: "12/13/2019",
    num: 309,
    text: "CASE MANAGEMENT ORDER NO. 3 - DIRECT FILING ORDER. Plaintiffs may file directly in MDL. Establishes direct-filing structure for all PI plaintiffs.",
    tags: ["procedural"],
  },
  {
    date: "11/19/2019",
    num: 262,
    text: "CASE MANAGEMENT ORDER NO. 2 - INITIAL CASE CENSUS. Leadership firms submit census to Ankura. Plaintiff leadership selected.",
    tags: ["procedural"],
  },
  {
    date: "10/25/2019",
    num: 193,
    text: "CASE MANAGEMENT ORDER NO. 1 - INITIAL CENSUS. Status conference Nov 8, 2019. Defense to identify all related cases.",
    tags: ["procedural", "scheduling"],
  },
  {
    date: "10/02/2019",
    num: 1,
    text: "TRANSFER ORDER by MDL Panel. All federal JUUL cases transferred to N.D. Cal. Judge Orrick assigned. MDL No. 2913 created.",
    tags: ["procedural"],
  },
  {
    date: "10/02/2019",
    num: 2,
    text: "PRETRIAL ORDER #1 by Judge Orrick. Leadership apps due 10/16/2019. Direct filing protocol. Briefing schedule.",
    tags: ["procedural", "scheduling"],
  },
  {
    dt: "Mar 17, 2026",
    dkt: "Dkt.663",
    type: "Order",
    title:
      "Order Granting Class Notice Plan - Direct Purchaser Antitrust Class (3:20-cv-02345-WHO)",
    sum: "Judge Orrick approved Direct Purchaser Class Notice Plan for In re JUUL Labs Antitrust Litigation (20-cv-02345). Notice commenced March 30 2026. Class defined as all US persons/entities that purchased JUUL products directly from JLI from Oct 5 2018 to Feb 26 2026.",
  },
  {
    dt: "Mar 30, 2026",
    dkt: "Notice",
    type: "Class Notice",
    title: "Antitrust Direct Purchaser Class Notice Commenced",
    sum: "PRNewswire class notice published March 30 2026. Opt-out deadline: June 13 2026. Trial date: September 28 2026. Defendants: Altria Group, JLI, Nicholas Pritzker, Riaz Valani. Indirect purchaser class proceeding separately at www.JuulAntitrustConsumer.com.",
  },
  {
    dt: "Jun 13, 2026",
    dkt: "Deadline",
    type: "Deadline",
    title: "Antitrust Class Opt-Out Deadline",
    sum: "Deadline for Direct Purchaser Class members to exclude themselves from 3:20-cv-02345-WHO. Members who do nothing will be bound by all orders and judgments and lose right to sue individually.",
  },
  {
    dt: "Sep 28, 2026",
    dkt: "Trial",
    type: "Trial",
    title:
      "Antitrust Trial - In re JUUL Labs Antitrust Litigation (3:20-cv-02345-WHO)",
    sum: "Trial date set for Direct Purchaser antitrust class action. Plaintiffs must prove defendants violated antitrust laws by conspiring to restrain competition in closed system e-vapor products market.",
  },
];

const JUUL_THEORIES = {
  plaintiff: [
    {
      title: "Failure to Warn - Nicotine Addiction",
      levels: [
        "JLI's failure to warn is the strongest claim in this MDL. Internal documents confirm JLI knew by 2018 that JUUL pods delivered 59mg/mL nicotine salt - uniquely addictive and far stronger than prior devices - yet issued no adequate warning. The FDA's June 2022 Marketing Denial Order found insufficient evidence of public health benefit. Internal board decks tracked youth uptake metrics. This theory supports compensatory and punitive damages for conscious disregard of known risk.",
        "JLI failed to adequately warn consumers that JUUL's nicotine-salt formulation was uniquely addictive. Internal awareness of youth use is documented in Dkt.3447 SOF. The FDA MDO, though non-final, supports the inadequacy of JLI's warnings. Direct evidence of JLI's knowledge of addiction risks is strong from internal communications produced in discovery.",
        "Plaintiffs allege JLI failed to warn of JUUL's addictive properties. JLI counters that its packaging included tobacco warnings consistent with FDA requirements and that FCLAA preemption bars state-law warning claims. The strength of the claim depends on whether the court finds FCLAA preemption applies to e-cigarettes, which is an open question in the Ninth Circuit.",
        "JLI argues its warnings met applicable FDA standards and that general public awareness of nicotine addiction negates any duty to provide more specific warnings. The FCLAA preemption argument has traction in some circuits. The stayed MDO does not establish inadequacy as a legal matter. Youth product-use claims face voluntary assumption of risk arguments.",
        "The failure-to-warn claim faces significant preemption headwinds under FCLAA and FDA's comprehensive tobacco regulatory scheme. JLI's good-faith regulatory compliance and voluntary assumption of risk by adult smokers limits recovery. Absent proof plaintiff was unaware of nicotine addiction generally, causation is difficult to establish.",
      ],
    },
    {
      title: "Antitrust - Altria Non-Compete & Market Allocation",
      levels: [
        "The antitrust theory (3:20-cv-02345-WHO) is new and powerful: Altria paid $12.8B for 35% of JLI and simultaneously agreed to exit the e-cigarette market - ceasing sales of its MarkTen and other competing products. This is a per se horizontal agreement to restrain competition. Class period: Oct 5 2018 – Feb 26 2026. Direct purchaser class certified. Trial September 28 2026. Experts Leger, Perry, Singh qualified. This is the strongest unfiled-damages theory in the JUUL portfolio.",
        "The antitrust case alleges Altria and JLI violated Sherman Act §1 through a horizontal market allocation: Altria exited the e-vapor market in exchange for a 35% equity stake, eliminating the most likely competitive threat to JUUL's market dominance. Expert Leger quantifies supracompetitive pricing damages to direct purchasers. The class cert order survived Altria's 23(f) challenge and the opt-out deadline is June 13 2026.",
        "The antitrust case faces the rule of reason vs. per se question: Altria argues its market exit was a legitimate business decision incidental to a lawful investment, not a horizontal agreement to allocate markets. The market definition (closed system e-vapor products) is contested. Defendants argue open-system e-cigarettes and combustible cigarettes are in the same relevant market, which dilutes market power analysis.",
        "Altria's defense is that a vertical investment - one company buying equity in another - is not per se illegal market allocation. Altria exited competing products as part of its capital reallocation strategy, not a conspiracy. The rule of reason analysis requires plaintiffs to show actual anticompetitive effects in a properly defined market, and defendants have procompetitive justifications (investment to fund JUUL's harm-reduction mission).",
        "The antitrust case is the most speculative. Antitrust class actions against investors in the same product category face serious legal uncertainty. The Ninth Circuit may grant 23(f) review and decertify the class. Even if liability is established, the but-for world (what would prices have been without the Altria investment?) requires highly speculative expert modeling.",
      ],
    },
    {
      title: "RICO Enterprise - JLI + Altria Conspiracy",
      levels: [
        "The RICO enterprise theory is powerful: Altria's $12.8 billion investment in December 2018 came after Altria's own due diligence confirmed youth-skewed user demographics. Internal Altria documents show Altria knew JUUL was marketed to youth. The coordinated market-exit agreement - Altria agreed to cease selling its own e-cigarette products - is classic RICO predicate: wire fraud + market allocation. The enterprise operated continuously from 2018 through the MDL filing date.",
        "JUUL and Altria formed an enterprise under 18 U.S.C. §1962(c) through fraudulent marketing to minors and coordinated suppression of competitive e-cigarette alternatives. The Altria investment gave JLI access to Altria's retail distribution network while Altria withdrew its competing products. Predicate acts: repeated mail and wire fraud through misleading marketing claims.",
        "RICO requires a 'pattern of racketeering activity' - at least two predicate acts within 10 years. The predicate acts theory here (wire fraud via misleading marketing) has survived motions to dismiss but faces the 'pattern' requirement. Courts distinguish legitimate business activities from racketeering. The enterprise element depends on proving a common fraudulent purpose beyond ordinary competition.",
        "JLI argues RICO cannot apply to ordinary product marketing decisions. The enterprise theory requires proof of a structured criminal enterprise, not merely business cooperation. Courts have been skeptical of MDL RICO claims based solely on product marketing. The distinction between conscious corporate decisions and RICO racketeering is a high bar.",
        "The RICO claim is the most legally tenuous. RICO was designed for organized crime, not consumer product manufacturers. The predicate act theory requires establishing wire fraud as a pattern rather than business strategy. SFUSD's RICO claims were significantly narrowed at summary judgment. Individual PI plaintiffs face causation challenges connecting JLI's alleged enterprise acts to personal injuries.",
      ],
    },
    {
      title: "Public Nuisance - SFUSD School District",
      levels: [
        "SFUSD's public nuisance claim is the bellwether government entity theory. JLI's marketing created a youth vaping epidemic in SFUSD schools: bathroom doors broken, fences cut, master keys stolen, gardens shut down - all documented by expert Dorn in site inspections. SFUSD created the JUUL School program from scratch. Physical property damage + ongoing abatement costs = textbook public nuisance. The California Supreme Court's People v. ConAgra supports broad public nuisance in consumer product cases.",
        "SFUSD suffered concrete, documentable harm from JLI's youth-targeted marketing: documented property damage at 6 schools, $34,875+ in direct expenditures, $78.8M in abatement costs (Dorn Report), and tens of millions in staff diversion costs. The nuisance theory is strongest because SFUSD can point to specific physical harm at specific schools with documentary support from the Dorn site inspections and SFUSD's own records.",
        "JLI's MSJ (Dkt.3396) raises three grounds against SFUSD: (1) Rincon Band v. Flynt bars SFUSD standing as a school district; (2) Sonner v. Premier Nutrition bars equitable abatement; (3) SFUSD cannot quantify past damages - Pak depo '12/21/21: we don't have that information.' Court took MSJ under submission after Oct 31 2025 hearing. Ruling pending.",
        "JLI's public nuisance defense has real traction: SFUSD failed to quantify past damages in discovery. Pak deposition admissions ('we don't have that information') undercut the damages case. The Rincon Band standing argument may bar government entity nuisance claims entirely. Without quantifiable past damages, the equitable abatement remedy under Sonner is also barred.",
        "The public nuisance claim is legally uncertain. The California Supreme Court's ConAgra decision reversed the public nuisance verdict against paint manufacturers. SFUSD's failure to document past damages is fatal if the court applies Sonner strictly. The $78M Dorn abatement estimate is prospective - courts have rejected forward-looking abatement as equitable overreach.",
      ],
    },
    {
      title: "Negligent Marketing - Youth Targeting",
      levels: [
        "JLI negligently marketed JUUL to minors through social media campaigns, school-proximity retail placement, fruit-flavored pod marketing, and the 'JUUL School' representative program that sent reps into high schools. Internal documents show JLI tracked youth uptake metrics as a KPI. The negligence standard - duty, breach, causation, damages - is met: JLI had a duty not to market addictive products to minors, breached it intentionally, and caused the youth vaping epidemic documented in SFUSD and Seliavski's injuries.",
        "JLI's marketing of JUUL negligently targeted minors through channels known to reach young people. The JUUL School program documented at Dkt.3447 SOF shows JLI trained reps specifically to visit high schools. FDA warning letters and internal documents confirm JLI's knowledge that youth were a primary user base. Causation is established by expert testimony on youth uptake rates and Seliavski's specific use history.",
        "The negligent marketing claim requires proof that JLI knew its marketing would reach minors and failed to take reasonable steps to prevent it. JLI argues it targeted adult smokers seeking harm reduction and cannot be liable for third-party retailers selling to minors. Proving that JLI itself (as opposed to retailers) was the proximate cause of Seliavski's purchase is the key causation challenge.",
        "JLI argues it marketed JUUL as an adult smoking cessation device. Retailers, not JLI, sold products to minors. JLI implemented age-verification systems and restricted access. The chain of causation from JLI's marketing decisions to Seliavski's purchase at a third-party retailer is attenuated. Comparative fault of retailers and parents may reduce JLI's share significantly.",
        "Negligent marketing is the weakest theory for individual PI plaintiffs. The comparative fault framework allows JLI to apportion liability to retailers, parents, and Seliavski herself (minor plaintiff voluntarily used a product with visible nicotine warnings). Product sellers generally cannot be liable for the negligent acts of independent distributors under Restatement (Third) of Torts.",
      ],
    },
  ],
  defendant: [
    {
      title: "FCLAA Federal Preemption",
      levels: [
        "The Federal Cigarette Labeling and Advertising Act preempts state warning claims as to cigarette advertising. JLI's strongest defense is that JUUL is a tobacco product subject to FDA jurisdiction and that state warning claims are preempted. The Surgeon General's warning requirements occupy the field. This is a threshold defense that, if successful, would eliminate the failure-to-warn claims entirely.",
        "FCLAA preemption is JLI's most reliable defense. Congress intended uniform federal regulation of tobacco product labeling. State law claims that impose additional or different requirements than the FCLAA warning scheme are preempted. Several circuits have applied FCLAA preemption to cigarette product liability claims and JLI argues the same framework applies to e-cigarettes regulated as tobacco products.",
        "FCLAA preemption applies to cigarettes specifically. Whether JUUL, as an e-cigarette, falls within FCLAA's scope is contested. The FDA regulates e-cigarettes as tobacco products under the Deeming Rule, but this doesn't resolve whether FCLAA's preemption language was intended to cover products not contemplated in 1969. Courts are divided on this question.",
        "The FCLAA preemption argument faces the fact that JUUL's advertising and marketing claims went beyond mere labeling - social media campaigns, influencer marketing, and school-proximity placement are not traditional 'labeling' that FCLAA preempts. Plaintiffs argue that FCLAA preempts only advertising claims, not product design defect and negligent marketing theories.",
        "FCLAA preemption is unlikely to succeed as a complete defense. Modern courts have read FCLAA preemption narrowly to avoid immunizing all tobacco product advertising. The FDA's Deeming Rule classifies e-cigarettes as tobacco products for regulatory purposes but does not expand FCLAA's preemption clause. JLI would still face design defect and misrepresentation claims not covered by FCLAA.",
      ],
    },
    {
      title: "Pak Deposition - No Quantifiable SFUSD Damages",
      levels: [
        "Quarry Pak's December 21 2021 deposition is JLI's single most valuable piece of evidence: SFUSD's own 30(b)(6) witness testified 'we don't have that information' when asked about past costs incurred. Superintendent Matthews was 'unaware of any document that quantifies staff time.' This admission from SFUSD's own representative is direct evidence of the fatal weakness in SFUSD's damages case. Without quantifiable past damages, both compensatory damages and equitable abatement are unavailable.",
        "The Pak deposition testimony directly contradicts SFUSD's damages claims. SFUSD's $34,875 in documented direct expenditures is de minimis. The $78.8M Dorn prospective abatement estimate is forward-looking and relies on assumptions the court should reject under Sonner v. Premier Nutrition, which bars equitable relief when adequate legal remedies exist. SFUSD cannot establish past harm with legal certainty required for damages.",
        "SFUSD counters that Pak's testimony was about specific line-item cost tracking, not total harm. Dkt.3447 presents four categories of damages beyond the direct expenditure figure. The Dorn site inspection reports and expert testimony provide the jury with concrete evidence of physical harm at each school. Pak's admission of imperfect record-keeping does not eliminate SFUSD's damages case entirely.",
        "SFUSD has extensive evidence of harm beyond what Pak's deposition captured: Dorn site inspection reports documenting physical damage at 6 schools, student declarations, staff testimonials, and the Halpern-Felsher study showing 75% of SFUSD students did not know JUUL contained nicotine. The Pak deposition is a credibility vulnerability, not a case-ending admission.",
        "The Pak deposition is a minor vulnerability that SFUSD has addressed through supplemental expert testimony and discovery. The $34,875 figure understates harm because SFUSD did not systematically track all JUUL-related costs. Courts routinely allow juries to estimate harm from fragmentary records. JLI's reliance on Pak overreads the testimony as a legal admission rather than an evidentiary gap.",
      ],
    },
    {
      title: "Statute of Limitations - MDL Opt-Out Cases",
      levels: [
        "Multiple MDL opt-out plaintiffs face statute of limitations defenses. For Seliavski, product use ended in Spring 2018. Virginia's two-year personal injury SOL ran by Spring 2020 absent tolling. JLI argues discovery rule tolling requires actual knowledge, not constructive knowledge - and FDA warning letters and congressional hearings put plaintiffs on constructive notice by 2018. The CMO 17/19 briefing schedule addressed this for the opt-out group with mixed results.",
        "The SOL defense is strongest for Seliavski (product use: July 2017 – Spring 2018). Virginia applies a two-year SOL for personal injury. JLI argues the discovery rule does not apply because Seliavski should have known of her injury when she became addicted. Public awareness of JUUL's nicotine content through national media coverage starting in 2018 defeats any late-discovery argument.",
        "SOL is contested in this MDL. The discovery rule tolls limitations until a plaintiff knew or should have known of the injury and its cause. The youth of plaintiffs, the concealment of JUUL's nicotine content (75% of SFUSD students didn't know), and JLI's active suppression of addiction data support equitable tolling. The court has not ruled definitively on SOL for Seliavski's Virginia claims.",
        "The discovery rule generally applies to latent disease and concealed defect claims, giving plaintiffs more time. Seliavski's addiction injuries were apparent contemporaneously with use. JLI did not actively conceal that JUUL contained nicotine. The public availability of information about JUUL's nicotine content through FDA proceedings, congressional hearings, and news coverage means the discovery rule provides limited tolling benefit.",
        "SOL is a minor defense in this MDL given the scope of JLI's concealment and the youth of plaintiffs. Courts have applied fraudulent concealment tolling broadly where the defendant controlled knowledge of the defect. The class action filing in 2019 tolled limitations under American Pipe for class members. For PI plaintiffs like Seliavski who filed short-form complaints, the MDL direct-filing orders preserved their claims.",
      ],
    },
    {
      title: "FDA Regulatory Compliance / Primary Jurisdiction",
      levels: [
        "JLI had FDA authorization to sell JUUL products during the relevant period. FDA reviewed and continues to review JLI's PMTA. The FDA's MDO was immediately stayed - it has no legal effect as a final agency determination. Congress designated FDA as the expert agency for tobacco product regulation. Courts should defer to FDA's ongoing review rather than second-guess the agency's regulatory judgments through tort law.",
        "JLI's good-faith engagement with FDA's regulatory process (filing a comprehensive PMTA, cooperating with FDA review) demonstrates compliance with the applicable regulatory scheme. Courts applying the primary jurisdiction doctrine should stay state tort claims until FDA completes its premarket review. JLI's voluntary youth prevention programs further demonstrate reasonable care.",
        "The FDA primary jurisdiction argument succeeded in delaying the MDL but was ultimately rejected. Judge Orrick found that FDA's ongoing review does not displace state tort law claims. The MDO was stayed pending further FDA review but the court found the MDO supported plaintiffs' inadequate-warning theory. FDA compliance is a mitigating factor, not a complete defense.",
        "Primary jurisdiction is a procedural doctrine that courts apply at their discretion. Courts in this MDL have declined to stay proceedings pending FDA review. FDA compliance is relevant to the reasonableness of JLI's conduct but does not establish a complete defense to negligence or failure-to-warn claims. JLI's own internal documents undercut any claim of full regulatory compliance.",
        "FDA regulatory compliance provides minimal protection in this MDL. Compliance with federal minimum standards does not immunize manufacturers from state tort liability when they had independent knowledge of risks beyond what their regulatory submissions disclosed. JLI's internal documents showing knowledge of youth appeal defeat the compliance defense.",
      ],
    },
    {
      title: "Comparative Fault - Voluntary Use / Retailer Chain",
      levels: [
        "JLI's strongest affirmative defense for individual PI plaintiffs: Seliavski voluntarily purchased and used a product labeled as containing nicotine. Virginia's contributory negligence doctrine is complete bar - any fault by plaintiff eliminates recovery entirely. Retailers, not JLI, sold JUUL to a minor; JLI cannot be liable for the independent negligence of distributors. Parents share responsibility for failing to monitor minor's behavior.",
        "Comparative fault allocates liability among JLI, retailers (7-Eleven, Circle K, Walgreens), parents, and the plaintiff. JLI argues that third-party retailer sales to minors are superseding causes that break the causal chain between JLI's marketing and Seliavski's injury. Expert testimony on retail-level negligence and parental supervision significantly reduces JLI's share of liability.",
        "Comparative fault is a standard defense that reduces but does not eliminate JLI's liability. Courts distinguish between JLI's upstream negligence (creating the addictive product and marketing it) and downstream voluntary use. Product manufacturers cannot escape liability for defective products simply by pointing to consumers who used them. JLI's responsibility for creating an unreasonably dangerous product persists regardless of comparative fault.",
        "Comparative fault analysis weighs JLI's conduct against Seliavski's voluntary use and the retailer's negligent sale to a minor. Most courts apportion significant fault to the manufacturer even in cases of voluntary use of addictive products because the manufacturer is the most efficient actor to prevent harm. JLI's internal documents show it was aware youth would use the product.",
        "Comparative fault is a minor mitigation at best. Courts have consistently refused to allow tobacco and nicotine manufacturers to escape liability by pointing to consumer choice for products designed to create addiction. The very theory of addiction liability defeats voluntary-use defenses: if the product creates compulsion, the initial voluntary use cannot be a meaningful intervening cause.",
      ],
    },
  ],
};

const JUUL_ATTORNEYS = [
  {
    id: "a1",
    name: "Sarah R. London",
    party: "Plaintiff",
    firm: "Lieff Cabraser Heimann & Bernstein LLP",
    role: "Co-Lead Counsel / MDL Plaintiffs",
    email: "slondon@lchb.com",
    phone: "415-956-1000",
    bar: "CA",
    involvement:
      "Co-Lead Counsel for MDL plaintiffs. Filed Third Supplemental Expense Recommendations for attorney fee motion (Dkt.4443, Oct 2025). Primary contact for class action settlement administration and fee distribution. Lead on SFUSD consolidated briefing strategy.",
    dkts: [
      "Dkt.4443 (Fee Motion)",
      "Dkt.3447 (SFUSD Opp. MSJ)",
      "Dkt.3449 (SFUSD Con. Opp.)",
    ],
  },
  {
    id: "a2",
    name: "Dena Sharp",
    party: "Plaintiff",
    firm: "Girard Sharp LLP",
    role: "Co-Lead Counsel / Settlement",
    email: "dsharp@girardsharp.com",
    phone: "415-981-4800",
    bar: "CA",
    involvement:
      "Co-Lead Counsel managing JLI and Altria class action settlement disbursement. Filed multiple motions for disbursement of settlement funds (Dkt.4390, Dkt.4444, Dkt.4487). Key liaison between settlement administrator and Court.",
    dkts: [
      "Dkt.4487 (Mar 2026 Disbursement)",
      "Dkt.4444 (Oct 2025 Disbursement)",
      "Dkt.4390 (Feb 2025 Disbursement)",
    ],
  },
  {
    id: "a3",
    name: "Jeffrey L. Haberman",
    party: "Plaintiff",
    firm: "Schlesinger Law Offices, P.A.",
    role: "Opt-Out Plaintiff Counsel",
    email: "jhaberman@schlesingerlaw.com",
    phone: "954-467-8800",
    bar: "FL",
    involvement:
      "Lead counsel for Schlesinger opt-out plaintiffs (Dupree, Tortorici, Sedgwick, Nessmith et al.). Filed motion for extension of supplemental discovery time (Dkt.4484, Feb 2026). Filed notice of compliance (Dkt.4490, Mar 2026). Appeared at Oct 31, 2025 and Apr 30, 2025 hearings.",
    dkts: [
      "Dkt.4490 (Compliance Notice)",
      "Dkt.4484 (Discovery Extension)",
      "Dkt.4426 (Stipulation)",
    ],
  },
  {
    id: "a4",
    name: "Scott P. Schlesinger",
    party: "Plaintiff",
    firm: "Schlesinger Law Offices, P.A.",
    role: "Opt-Out Plaintiff Counsel",
    email: "sschlesinger@schlesingerlaw.com",
    phone: "954-467-8800",
    bar: "FL",
    involvement:
      "Principal of Schlesinger Law Offices representing opt-out PI plaintiffs. Appeared at Oct 31, 2025 motion hearing on SJ motions (43 min hearing, Dkt.4450). Co-signed July 2025 stipulation regarding extension of time for dispositive motions (Dkt.4426).",
    dkts: ["Dkt.4450 (Oct 31 Hearing)", "Dkt.4426 (Stipulation)"],
  },
  {
    id: "a5",
    name: "Matthew W.H. Wessler",
    party: "Plaintiff",
    firm: "Gupta Wessler LLP",
    role: "Opt-Out Plaintiff / Appellate Counsel",
    email: "matt@guptawessler.com",
    phone: "202-888-1741",
    bar: "DC / MA",
    involvement:
      "Pro hac vice admitted Oct 24, 2025 (Dkt.4446) for opt-out plaintiff representation. Appeared at Oct 31, 2025 motion hearing on SJ motions. Appellate practice focus - likely handling any interlocutory appeal or post-SJ remand appellate issues.",
    dkts: ["Dkt.4446 (PHV Admission)", "Dkt.4450 (Oct 31 Hearing)"],
  },
  {
    id: "a6",
    name: "Kenneth J. Brennan",
    party: "Plaintiff",
    firm: "Schlesinger Law Offices, P.A.",
    role: "Opt-Out Plaintiff Counsel",
    email: "kbrennan@schlesingerlaw.com",
    phone: "954-467-8800",
    bar: "FL",
    involvement:
      "Appeared at Apr 30, 2025 motions to remand hearing (36 min, Dkt.4406). Argued remand of opt-out PI cases to transferee courts. Partner at Schlesinger Law working alongside Haberman and Schlesinger on opt-out plaintiff strategy.",
    dkts: ["Dkt.4406 (Apr 30 Hearing)"],
  },
  {
    id: "a7",
    name: "Timothy S. Danninger",
    party: "Defendant",
    firm: "Kirkland & Ellis LLP",
    role: "Lead JLI Defense Counsel",
    email: "timothy.danninger@kirkland.com",
    phone: "312-862-2000",
    bar: "IL",
    involvement:
      "Lead Kirkland & Ellis attorney for Juul Labs, Inc. throughout remand-phase MDL. Filed JLI opposition to discovery extension (Dkt.4486, Feb 2026), motion to dismiss non-opt-out plaintiffs (Dkt.4401, Apr 2025), and proposed order on dismissal (Dkt.4411). Primary signatory on JLI stipulations and notices. Appeared at Oct 31, 2025 and Apr 30, 2025 hearings.",
    dkts: [
      "Dkt.4486 (Opp. Discovery Ext.)",
      "Dkt.4401 (MTD Non-Opt-Out)",
      "Dkt.4369 (Joint Chart Sealing)",
    ],
  },
  {
    id: "a8",
    name: "David Kouba",
    party: "Defendant",
    firm: "Kirkland & Ellis LLP",
    role: "JLI Defense Counsel",
    email: "david.kouba@kirkland.com",
    phone: "202-879-5000",
    bar: "DC",
    involvement:
      "Kirkland & Ellis partner on JLI MDL defense team. Filed Altria unsealing notice (Dkt.3393, Mar 2025) - notice of filing of documents to be unsealed. Appeared at Oct 31, 2025 and Apr 30, 2025 hearings. Manages sealing/unsealing motions practice for JLI.",
    dkts: [
      "Dkt.3393 (Unsealing Notice)",
      "Dkt.4450 (Oct 31 Hearing)",
      "Dkt.4406 (Apr 30 Hearing)",
    ],
  },
  {
    id: "a9",
    name: "Derek Reinbold",
    party: "Defendant",
    firm: "Kirkland & Ellis LLP",
    role: "JLI Defense Counsel",
    email: "derek.reinbold@kirkland.com",
    phone: "312-862-2000",
    bar: "IL",
    involvement:
      "Appeared at Oct 31, 2025 motion hearing on SJ motions on behalf of JLI. Part of the Kirkland & Ellis core MDL defense team alongside Danninger and Kouba. Handles SJ oral argument and briefing.",
    dkts: ["Dkt.4450 (Oct 31 Hearing)", "Dkt.4406 (Apr 30 Hearing)"],
  },
  {
    id: "a10",
    name: "Timothy J. McGinn",
    party: "Defendant",
    firm: "Kirkland & Ellis LLP",
    role: "JLI Defense Counsel",
    email: "timothy.mcginn@kirkland.com",
    phone: "312-862-2000",
    bar: "IL",
    involvement:
      "Appeared at Oct 31, 2025 hearing on SJ motions. Part of Kirkland & Ellis MDL team for JLI. Works on dispositive motions and remand opposition strategy alongside Danninger.",
    dkts: ["Dkt.4450 (Oct 31 Hearing)"],
  },
  {
    id: "a11",
    name: "Michael Guzman",
    party: "Defendant",
    firm: "Kobre & Kim LLP",
    role: "Non-Management Director Defendants",
    email: "michael.guzman@kobrekim.com",
    phone: "212-488-1200",
    bar: "NY",
    involvement:
      "Counsel for Non-Management Director Defendants Nicholas Pritzker, Hoyoung Huh, and Riaz Valani. Filed administrative motions to seal and responses to Court sealing orders (Dkt.4381, Dkt.4383, Dkt.4386, Dkt.4392). Manages the director defendants' separate litigation track on RICO personal liability.",
    dkts: [
      "Dkt.4392 (Director Response)",
      "Dkt.4383 (Motion to Seal)",
      "Dkt.4381 (Motion to Seal)",
    ],
  },
  {
    id: "a12",
    name: "Neville S. Hedley",
    party: "Defendant",
    firm: "Withdrawn",
    role: "Former Counsel for Reilly Stephens",
    email: "",
    phone: "",
    bar: "",
    involvement:
      "Former counsel for defendant Reilly Stephens. Granted leave to withdraw Dec 9, 2025 (Dkt.4464). Reilly Stephens is now either pro se or has obtained new counsel.",
    dkts: ["Dkt.4464 (Withdrawal Order)"],
  },
  {
    id: "a13",
    name: "James Neil Kramer",
    party: "Defendant",
    firm: "Orrick, Herrington & Sutcliffe LLP",
    role: "Lead Counsel - James Monsees",
    email: "jkramer@orrick.com",
    phone: "415-773-5700",
    bar: "CA",
    involvement:
      "Lead attorney representing defendant James Monsees (JUUL Labs co-founder). Orrick's The Orrick Building, 405 Howard Street, San Francisco. Lead Attorney / Attorney to Be Noticed. Handles Monsees' individual defense on RICO personal liability and failure to warn claims in which Monsees is named personally as a defendant.",
    dkts: ["Monsees Individual Defense"],
  },
  {
    id: "a14",
    name: "Kevin Michael Askew",
    party: "Defendant",
    firm: "Orrick, Herrington & Sutcliffe LLP",
    role: "Counsel - James Monsees",
    email: "kaskew@orrick.com",
    phone: "213-629-2020",
    bar: "CA",
    involvement:
      "Attorney to Be Noticed for defendant James Monsees. Orrick Los Angeles office, 355 S. Grand Ave., 27th Floor. Works alongside Kramer on Monsees' individual defense strategy for personal liability claims arising from his role as JUUL co-founder and former CEO.",
    dkts: ["Monsees Individual Defense"],
  },
  {
    id: "a15",
    name: "Lauren B. Seaton",
    party: "Defendant",
    firm: "Orrick, Herrington & Sutcliffe LLP",
    role: "Counsel - James Monsees",
    email: "lseaton@orrick.com",
    phone: "949-567-6700",
    bar: "CA",
    involvement:
      "Attorney to Be Noticed for defendant James Monsees. Orrick Irvine office, 2050 Main Street, Suite 1100. Part of the Orrick team defending Monsees on personal liability allegations related to his leadership of JUUL Labs during the period of alleged youth marketing.",
    dkts: ["Monsees Individual Defense"],
  },
  {
    id: "a16",
    name: "Sunny Hwang",
    party: "Defendant",
    firm: "Orrick, Herrington & Sutcliffe LLP",
    role: "Counsel - James Monsees",
    email: "shwang@orrick.com",
    phone: "213-629-2020",
    bar: "CA",
    involvement:
      "Attorney to Be Noticed for defendant James Monsees. Orrick Los Angeles office, 355 S. Grand Avenue, Suite 2700. Part of the Orrick defense team for Monsees on individual RICO and tort liability claims arising from his co-founder and executive roles at JUUL Labs.",
    dkts: ["Monsees Individual Defense"],
  },
];
const MANDO_ATTORNEYS = [
  {
    id: "m1",
    name: "Raj Abhyanker",
    party: "Defendant",
    firm: "Raj Abhyanker P.C.",
    role: "Lead Defense Counsel",
    email: "raj@legalforcelaw.com",
    phone: "650-701-7517",
    bar: "CA",
    ph: "⚖️",
    involvement:
      "Lead defense counsel for Mando Inc. and affiliated defendants. Managing invalidity and non-infringement strategy, claim construction, and counterclaims. LegalForce RAPC, San Jose CA.",
    dkts: [
      "MTD (Dkt.118)",
      "Answer + Counterclaims (Dkt.119)",
      "Claim Construction (Dkt.140)",
    ],
  },
  {
    id: "m2",
    name: "Barnes & Thornburg LLP",
    party: "Plaintiff",
    firm: "Barnes & Thornburg LLP",
    role: "Plaintiff Co-Counsel",
    email: "info@btlaw.com",
    phone: "317-236-1313",
    bar: "IN / CA",
    ph: "🏛️",
    involvement:
      "Co-counsel for Neurocentria on patent infringement and Lanham Act claims. Supporting on claim construction briefing, expert witness coordination, and damages strategy for the 061 patent family.",
    dkts: [
      "FAC (Dkt.60)",
      "Opp. to MTDs (Dkts.146-147)",
      "Claim Construction Brief (Dkt.142)",
    ],
  },
  {
    id: "m3",
    name: "Schulman Bhattacharya LLC",
    party: "Plaintiff",
    firm: "Schulman Bhattacharya LLC",
    role: "Plaintiff Lead Counsel",
    email: "info@schulmanbhattacharya.com",
    phone: "240-356-8550",
    bar: "MD / DC",
    ph: "⚖️",
    involvement:
      "Lead plaintiff patent counsel. Filed and briefed the operative First Amended Complaint. Managing infringement contentions and expert witness strategy for the 061 patent.",
    dkts: ["FAC (Dkt.60)", "Opp. to MTDs (Dkts.146-147)"],
  },
];
const JUUL_THREADS = [
  {
    id: "secA",
    l: "A",
    n: "Early Procedural Motions (2019)",
    threads: [
      {
        id: "A1",
        t: "JLI Motion to Enforce Pretrial Order #1",
        docs: [
          {
            d: "25",
            y: "Motion",
            p: 1,
            f: "10/11/2019",
            s: "JLI Motion to Enforce Pretrial Order #1",
          },
          {
            d: "26",
            y: "Related Motion",
            p: 0,
            f: "10/11/2019",
            s: "JLI Motion to Shorten Time on 25",
          },
          {
            d: "27",
            y: "Opposition",
            p: 0,
            f: "10/14/2019",
            s: "NesSmith Plaintiffs' Opposition to 26",
          },
          {
            d: "51",
            y: "Order",
            p: 1,
            f: "10/16/2019",
            s: "Order on 26 & 25 (briefing schedule)",
          },
          {
            d: "131",
            y: "Statement",
            p: 0,
            f: "10/18/2019",
            s: "NesSmith Statement re Order 51",
          },
          {
            d: "142",
            y: "Reply",
            p: 0,
            f: "10/18/2019",
            s: "JLI Reply re 25 + Status of Feldman Protective Order",
          },
          {
            d: "233",
            y: "Notice",
            p: 0,
            f: "11/07/2019",
            s: "JLI Notice re Order 51",
          },
          {
            d: "236",
            y: "Notice",
            p: 0,
            f: "11/08/2019",
            s: "NesSmith Notice in Response to JLI Notice 233",
          },
        ],
      },
      {
        id: "A2",
        t: "Cora Johnson Motion to Appoint Counsel",
        docs: [
          {
            d: "105",
            y: "Motion",
            p: 1,
            f: "10/16/2019",
            s: "Motion to Appoint Counsel (Dickinson)",
          },
        ],
      },
    ],
  },
  {
    id: "secB",
    l: "B",
    n: "Common Benefit Fund / CMO 5 (2020)",
    threads: [
      {
        id: "B1",
        t: "Common Benefit Order Briefing Extensions",
        docs: [
          {
            d: "494",
            y: "Stipulation",
            p: 1,
            f: "04/23/2020",
            s: "Stip to Extend Common Benefit Order Deadline",
          },
          {
            d: "495",
            y: "Order",
            p: 0,
            f: "04/24/2020",
            s: "Order Granting 494",
          },
          {
            d: "507",
            y: "Stipulation",
            p: 1,
            f: "04/27/2020",
            s: "Further Stip to Extend Deadline",
          },
          {
            d: "508",
            y: "Order",
            p: 0,
            f: "04/27/2020",
            s: "Order Granting 507",
          },
        ],
      },
      {
        id: "B2",
        t: "Plaintiffs' Motion for CMO 5(A) Common Benefit Fee",
        docs: [
          {
            d: "522",
            y: "Motion",
            p: 1,
            f: "05/02/2020",
            s: "Plaintiffs' Motion for Entry of CMO 5(A)",
          },
          {
            d: "575",
            y: "Clerk's Notice",
            p: 0,
            f: "05/22/2020",
            s: "Hearing on 522 vacated; written order to issue",
          },
        ],
      },
    ],
  },
  {
    id: "secC",
    l: "C",
    n: "Liaison Counsel Appointments (2020)",
    threads: [
      {
        id: "C1",
        t: "Defense Liaison Counsel",
        docs: [
          {
            d: "533",
            y: "Motion",
            p: 1,
            f: "05/08/2020",
            s: "Proposal to Appoint Guzman as Liaison for Founder/Director Defendants",
          },
          {
            d: "534",
            y: "Application",
            p: 1,
            f: "05/08/2020",
            s: "Application of O'Donnell to Serve as Liaison for Distributor/Retailer Defendants",
          },
          {
            d: "550",
            y: "Order",
            p: 0,
            f: "05/13/2020",
            s: "Order Granting 533; allowing O'Donnell & Esbrook to address court at 5/15 CMC",
          },
        ],
      },
      {
        id: "C2",
        t: "Tribal Leadership Sub-Committee",
        docs: [
          {
            d: "782",
            y: "Motion",
            p: 1,
            f: "07/07/2020",
            s: "Tribal Plaintiffs' Motion to Appoint Counsel & Establish Tribal Sub-Committee",
          },
        ],
      },
    ],
  },
  {
    id: "secD",
    l: "D",
    n: "Sealing of Master Complaints (2020)",
    threads: [
      {
        id: "D1",
        t: "Master Complaints Sealing",
        docs: [
          {
            d: "386",
            y: "Admin Motion to Seal",
            p: 1,
            f: "03/10/2020",
            s: "Plaintiffs' Consolidated Master Complaints (PI & Class)",
          },
          {
            d: "418",
            y: "Admin Motion to Seal",
            p: 1,
            f: "04/06/2020",
            s: "Amended Consolidated Master Complaints",
          },
          {
            d: "537",
            y: "Admin Motion to Seal",
            p: 1,
            f: "05/08/2020",
            s: "Amended Government Entity Complaints",
          },
          {
            d: "555",
            y: "Exhibit Correction",
            p: 0,
            f: "05/15/2020",
            s: "Correction to 537",
          },
          {
            d: "662",
            y: "Statement",
            p: 0,
            f: "06/11/2020",
            s: "JLI Statement re 386, 418, 537",
          },
          {
            d: "663",
            y: "Order",
            p: 0,
            f: "06/12/2020",
            s: "Order on Motions to Seal Complaints (386, 418, 537)",
          },
        ],
      },
    ],
  },
  {
    id: "secE",
    l: "E",
    n: "Case Relation Motions (2020)",
    threads: [
      {
        id: "E1",
        t: "Antitrust / Related Cases",
        docs: [
          {
            d: "493",
            y: "Motion",
            p: 1,
            f: "04/23/2020",
            s: "Admin Motion to Relate Cases (Saveri)",
          },
          {
            d: "515",
            y: "Motion",
            p: 1,
            f: "04/29/2020",
            s: "Admin Motion to Relate Cases (Flannery)",
          },
          {
            d: "509",
            y: "Opposition",
            p: 0,
            f: "04/27/2020",
            s: "JLI's Response to 493",
          },
          {
            d: "583",
            y: "Order",
            p: 0,
            f: "05/26/2020",
            s: "Order Denying 421 In Part; Relating Antitrust Actions (487, 493, 515)",
          },
        ],
      },
      {
        id: "E2",
        t: "Redfield/Riccelli Related Cases",
        docs: [
          {
            d: "585",
            y: "Motion",
            p: 1,
            f: "05/27/2020",
            s: "Admin Motion to Relate Cases (Saveri)",
          },
        ],
      },
    ],
  },
  {
    id: "secF",
    l: "F",
    n: "Motion for Leave to Amend Complaints (2020)",
    threads: [
      {
        id: "F1",
        t: "Motion for Leave to File Amended Complaints",
        docs: [
          {
            d: "421",
            y: "Motion",
            p: 1,
            f: "04/06/2020",
            s: "Plaintiffs' Motion for Leave to File Amended Complaints",
          },
          {
            d: "498",
            y: "Opposition",
            p: 0,
            f: "04/24/2020",
            s: "Altria/JLI Opposition",
          },
          {
            d: "499",
            y: "Letter Brief",
            p: 0,
            f: "04/24/2020",
            s: "Martinez Letter Brief re 421",
          },
          {
            d: "500",
            y: "Opposition",
            p: 0,
            f: "04/24/2020",
            s: "Altria/JLI Statement of Partial Non-Opposition",
          },
          {
            d: "527",
            y: "Reply",
            p: 0,
            f: "05/05/2020",
            s: "Plaintiffs' Reply re Class Action Complaint",
          },
          {
            d: "583",
            y: "Order",
            p: 0,
            f: "05/26/2020",
            s: "Order Denying 421 In Part",
          },
        ],
      },
    ],
  },
  {
    id: "secG",
    l: "G",
    n: "Alternative Service (2020)",
    threads: [
      {
        id: "G1",
        t: "Alternative Service Motion",
        docs: [
          {
            d: "486",
            y: "Motion",
            p: 1,
            f: "04/22/2020",
            s: "Plaintiffs' Admin Motion for Alternative Service",
          },
          {
            d: "518",
            y: "Order",
            p: 0,
            f: "05/01/2020",
            s: "Order Granting 486",
          },
        ],
      },
    ],
  },
  {
    id: "secH",
    l: "H",
    n: "First-Round Substantive MTDs (Class Action) - All Resolved by Order 1084",
    threads: [
      {
        id: "H1",
        t: "JLI Motion #1 (Stay Based on FDA Primary Jurisdiction)",
        docs: [
          {
            d: "625",
            y: "Statement",
            p: 0,
            f: "05/29/2020",
            s: "JLI Statement re MTDs",
          },
          {
            d: "626",
            y: "Motion",
            p: 1,
            f: "05/29/2020",
            s: "JLI Motion to Stay",
          },
          {
            d: "630",
            y: "Request for Judicial Notice",
            p: 0,
            f: "05/29/2020",
            s: "JLI RJN ISO 626, 627, 628, 629",
          },
          {
            d: "631",
            y: "Declaration",
            p: 0,
            f: "05/29/2020",
            s: "Gildersleeve Decl. ISO 626-630",
          },
          {
            d: "650",
            y: "Joinder",
            p: 0,
            f: "06/05/2020",
            s: "Monsees Joinder",
          },
          {
            d: "744",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "McLane Joinder",
          },
          {
            d: "749",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "Tobacco Tech / eLiquitech Joinder",
          },
          {
            d: "754",
            y: "Admin Motion to Seal",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Motion to Seal Opposition",
          },
          {
            d: "755",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "760",
            y: "RJN re Opp.",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' RJN ISO Opp",
          },
          {
            d: "761",
            y: "Response to RJN",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Response to JLI RJN",
          },
          {
            d: "781",
            y: "Order Extending",
            p: 0,
            f: "07/07/2020",
            s: "Order Extending Deadline (granting 780 stip)",
          },
          { d: "811", y: "Reply", p: 0, f: "07/20/2020", s: "JLI Reply" },
          {
            d: "853",
            y: "Decl. ISO Sealing",
            p: 0,
            f: "08/03/2020",
            s: "Smith Decl. ISO 754",
          },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H2",
        t: "JLI Motion #2 (Federal Preemption)",
        docs: [
          {
            d: "627",
            y: "Motion",
            p: 1,
            f: "05/29/2020",
            s: "JLI Motion to Dismiss Claims Preempted",
          },
          { d: "630", y: "RJN", p: 0, f: "05/29/2020", s: "(shared w/ H1)" },
          { d: "631", y: "Decl.", p: 0, f: "05/29/2020", s: "(shared)" },
          { d: "650", y: "Joinder", p: 0, f: "06/05/2020", s: "Monsees" },
          { d: "744", y: "Joinder", p: 0, f: "06/29/2020", s: "McLane" },
          {
            d: "749",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "Tobacco Tech / eLiquitech",
          },
          {
            d: "756",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opposition",
          },
          { d: "812", y: "Reply", p: 0, f: "07/20/2020", s: "JLI Reply" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Substantive MTD Order",
          },
        ],
      },
      {
        id: "H3",
        t: "JLI Motion #3 (RICO Failure to State Claim)",
        docs: [
          {
            d: "628",
            y: "Motion",
            p: 1,
            f: "05/29/2020",
            s: "JLI Motion to Dismiss RICO",
          },
          { d: "630", y: "RJN", p: 0, f: "", s: "(shared)" },
          { d: "631", y: "Decl.", p: 0, f: "", s: "(shared)" },
          { d: "650", y: "Joinder", p: 0, f: "06/05/2020", s: "Monsees" },
          {
            d: "757",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opp to RICO MTDs (consolidated)",
          },
          {
            d: "759",
            y: "RJN re Opp.",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' RJN ISO 757",
          },
          { d: "813", y: "Reply", p: 0, f: "07/20/2020", s: "JLI Reply" },
          {
            d: "832",
            y: "Joinder",
            p: 0,
            f: "07/29/2020",
            s: "NMD Joinder in Replies",
          },
          {
            d: "833",
            y: "Joinder",
            p: 0,
            f: "07/29/2020",
            s: "Bowen Joinder in Replies",
          },
          {
            d: "1044",
            y: "Notice of Supp. Auth.",
            p: 0,
            f: "10/12/2020",
            s: "JLI Notice re Purdue Pharma Order",
          },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H4",
        t: "JLI Motion #4 (CA Subclass / Subject-Matter Jurisdiction)",
        docs: [
          {
            d: "629",
            y: "Motion",
            p: 1,
            f: "05/29/2020",
            s: "JLI Motion to Dismiss CA Subclass",
          },
          { d: "630", y: "RJN", p: 0, f: "", s: "(shared)" },
          { d: "631", y: "Decl.", p: 0, f: "", s: "(shared)" },
          {
            d: "753",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "Monsees Joinder",
          },
          {
            d: "758",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opposition (consolidated w/ Altria opp.)",
          },
          { d: "814", y: "Reply", p: 0, f: "07/20/2020", s: "JLI Reply" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H5",
        t: "Altria MTD UCL/RICO from Class Complaint",
        docs: [
          {
            d: "632",
            y: "Motion",
            p: 1,
            f: "05/29/2020",
            s: "Altria Motion to Dismiss UCL/RICO",
          },
          {
            d: "633",
            y: "RJN",
            p: 0,
            f: "05/29/2020",
            s: "Altria RJN ISO 632",
          },
          {
            d: "650",
            y: "Joinder",
            p: 0,
            f: "06/05/2020",
            s: "Monsees Joinder",
          },
          {
            d: "753",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "Monsees Joinder",
          },
          {
            d: "757",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opp (RICO portion)",
          },
          {
            d: "758",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Opp (CA Subclass portion)",
          },
          { d: "810", y: "Reply", p: 0, f: "07/20/2020", s: "Altria Reply" },
          { d: "832", y: "Joinder", p: 0, f: "07/29/2020", s: "NMD Joinder" },
          { d: "833", y: "Joinder", p: 0, f: "07/29/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H6",
        t: "Bowen MTD RICO",
        docs: [
          {
            d: "645",
            y: "Motion",
            p: 1,
            f: "06/05/2020",
            s: "Bowen Motion to Dismiss RICO",
          },
          {
            d: "650",
            y: "Joinder",
            p: 0,
            f: "06/05/2020",
            s: "Monsees Joinder",
          },
          {
            d: "757",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "827", y: "Reply", p: 0, f: "07/27/2020", s: "Bowen Reply" },
          {
            d: "829",
            y: "Joinder",
            p: 0,
            f: "07/27/2020",
            s: "Monsees Joinder in Replies",
          },
          { d: "832", y: "Joinder", p: 0, f: "07/29/2020", s: "NMD Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H7",
        t: "Monsees MTD RICO Class",
        docs: [
          {
            d: "647",
            y: "Motion",
            p: 1,
            f: "06/05/2020",
            s: "Monsees Motion to Dismiss RICO Class",
          },
          { d: "648", y: "RJN", p: 0, f: "06/05/2020", s: "Monsees RJN" },
          {
            d: "649",
            y: "Admin Motion to Seal",
            p: 0,
            f: "06/05/2020",
            s: "Monsees Admin Mot to Seal",
          },
          {
            d: "757",
            y: "Opposition",
            p: 0,
            f: "06/29/2020",
            s: "Plaintiffs' Consolidated RICO Opp",
          },
          {
            d: "824",
            y: "Reply",
            p: 0,
            f: "07/27/2020",
            s: "NMD Reply re 649 (re Class RICO)",
          },
          {
            d: "828",
            y: "Reply",
            p: 0,
            f: "07/27/2020",
            s: "Monsees Reply re 647",
          },
          { d: "832", y: "Joinder", p: 0, f: "07/29/2020", s: "NMD Joinder" },
          { d: "833", y: "Joinder", p: 0, f: "07/29/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H8",
        t: "Altria MTD from 7 Government Entity Complaints",
        docs: [
          {
            d: "738",
            y: "Motion",
            p: 1,
            f: "06/22/2020",
            s: "Altria MTD Government Entity Complaints",
          },
          {
            d: "739",
            y: "RJN",
            p: 0,
            f: "06/22/2020",
            s: "Altria RJN ISO 738",
          },
          {
            d: "817",
            y: "Opposition",
            p: 0,
            f: "07/20/2020",
            s: "Plaintiffs' Opp to 738 & 740",
          },
          {
            d: "818",
            y: "Response to RJN",
            p: 0,
            f: "07/20/2020",
            s: "Plaintiffs' Opp to Altria RJN",
          },
          { d: "879", y: "Reply", p: 0, f: "08/17/2020", s: "Altria Reply" },
          {
            d: "880",
            y: "RJN ISO Reply",
            p: 0,
            f: "08/17/2020",
            s: "Altria RJN ISO Reply",
          },
          {
            d: "885",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "NMD Joinder re 879, 880, 881",
          },
          {
            d: "887",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "903", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H9",
        t: "JLI MTD Bellwether Government Entity",
        docs: [
          {
            d: "740",
            y: "Motion",
            p: 1,
            f: "06/22/2020",
            s: "JLI MTD Bellwether Government Entity",
          },
          {
            d: "817",
            y: "Opposition",
            p: 0,
            f: "07/20/2020",
            s: "Plaintiffs' Opp",
          },
          { d: "881", y: "Reply", p: 0, f: "08/17/2020", s: "JLI Reply" },
          { d: "885", y: "Joinder", p: 0, f: "08/18/2020", s: "NMD Joinder" },
          {
            d: "887",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "903", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H10",
        t: "NMD MTD Class Action Complaint",
        docs: [
          {
            d: "748",
            y: "Motion",
            p: 1,
            f: "06/29/2020",
            s: "NMD MTD Class Action",
          },
          {
            d: "830",
            y: "Opposition",
            p: 0,
            f: "07/27/2020",
            s: "Plaintiffs' Consolidated Opp (Personal Jurisdiction)",
          },
          { d: "872", y: "Reply", p: 0, f: "08/17/2020", s: "NMD Reply" },
          {
            d: "886",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "902", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H11",
        t: "Distributor/Retailer MTD (7-Eleven et al.)",
        docs: [
          {
            d: "750",
            y: "Motion",
            p: 1,
            f: "06/29/2020",
            s: "Distributor/Retailer MTD Preempted Claims",
          },
          {
            d: "847",
            y: "Opposition",
            p: 0,
            f: "07/31/2020",
            s: "Plaintiffs' Opp",
          },
          {
            d: "878",
            y: "Reply",
            p: 0,
            f: "08/17/2020",
            s: "Distributor/Retailer Reply",
          },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H12",
        t: "Bowen MTD CA Subclass",
        docs: [
          {
            d: "751",
            y: "Motion",
            p: 1,
            f: "06/29/2020",
            s: "Bowen MTD CA Subclass",
          },
          {
            d: "753",
            y: "Joinder",
            p: 0,
            f: "06/29/2020",
            s: "Monsees Joinder",
          },
          {
            d: "830",
            y: "Opposition",
            p: 0,
            f: "07/27/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "876", y: "Reply", p: 0, f: "08/17/2020", s: "Bowen Reply" },
          {
            d: "886",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          {
            d: "902",
            y: "Joinder",
            p: 0,
            f: "08/19/2020",
            s: "Bowen Joinder in CA Replies",
          },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H13",
        t: "Monsees MTD CA Subclass (corrected)",
        docs: [
          {
            d: "752",
            y: "Motion (orig.)",
            p: 0,
            f: "06/29/2020",
            s: "Monsees MTD CA Subclass (deficient)",
          },
          {
            d: "763",
            y: "Correction Notice",
            p: 0,
            f: "06/30/2020",
            s: "Correction to 752",
          },
          {
            d: "778",
            y: "Motion (corr.)",
            p: 1,
            f: "07/06/2020",
            s: "Corrected MTD (CORRECTION OF 752)",
          },
          {
            d: "830",
            y: "Opposition",
            p: 0,
            f: "07/27/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "883", y: "Reply", p: 0, f: "08/17/2020", s: "Monsees Reply" },
          {
            d: "886",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "902", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H14",
        t: "NMD MTD Government Entity Complaints",
        docs: [
          {
            d: "771",
            y: "Motion",
            p: 1,
            f: "07/01/2020",
            s: "NMD MTD Government Entity Complaints",
          },
          {
            d: "849",
            y: "Opposition",
            p: 0,
            f: "07/31/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "882", y: "Reply", p: 0, f: "08/17/2020", s: "NMD Reply" },
          {
            d: "887",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "903", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H15",
        t: "Bowen MTD Bellwether Public Entity",
        docs: [
          {
            d: "772",
            y: "Motion",
            p: 1,
            f: "07/01/2020",
            s: "Bowen MTD Bellwether Public Entity",
          },
          {
            d: "849",
            y: "Opposition",
            p: 0,
            f: "07/31/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "877", y: "Reply", p: 0, f: "08/17/2020", s: "Bowen Reply" },
          {
            d: "887",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
      {
        id: "H16",
        t: "Monsees MTD Government Entity (corrected)",
        docs: [
          {
            d: "773",
            y: "Motion (orig.)",
            p: 0,
            f: "07/01/2020",
            s: "Monsees MTD Government Entity (deficient)",
          },
          {
            d: "779",
            y: "Motion (corr.)",
            p: 1,
            f: "07/06/2020",
            s: "Corrected (CORRECTION OF 773)",
          },
          {
            d: "849",
            y: "Opposition",
            p: 0,
            f: "07/31/2020",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "884", y: "Reply", p: 0, f: "08/17/2020", s: "Monsees Reply" },
          {
            d: "887",
            y: "Joinder",
            p: 0,
            f: "08/18/2020",
            s: "Monsees Joinder",
          },
          { d: "903", y: "Joinder", p: 0, f: "08/19/2020", s: "Bowen Joinder" },
          {
            d: "1084",
            y: "Order",
            p: 0,
            f: "10/23/2020",
            s: "Order on Substantive MTDs",
          },
        ],
      },
    ],
  },
  {
    id: "secI",
    l: "I",
    n: "CMO 8 - Routine MTDs Without Prejudice for Failure to Submit Discovery",
    threads: [
      {
        id: "I-1",
        t: "First Three MTDs (resolved by Order 1073)",
        docs: [
          {
            d: "852",
            y: "Motion",
            p: 1,
            f: "08/03/2020",
            s: "First MTD W/O Prejudice",
          },
          {
            d: "862",
            y: "Motion",
            p: 1,
            f: "08/13/2020",
            s: "Second MTD W/O Prejudice",
          },
          {
            d: "905",
            y: "Motion",
            p: 1,
            f: "08/20/2020",
            s: "Third MTD W/O Prejudice",
          },
          {
            d: "864",
            y: "Opposition to 852",
            p: 0,
            f: "08/14",
            s: "Rodriguez, Rivas",
          },
          {
            d: "871",
            y: "Opposition to 852",
            p: 0,
            f: "08/17",
            s: "Rodriguez, Rivas",
          },
          {
            d: "918",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "919",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "920",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "921",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "923",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "1043",
            y: "Opposition to 862",
            p: 0,
            f: "08/27, 10/12",
            s: "Various",
          },
          {
            d: "937",
            y: "Opposition to 905",
            p: 0,
            f: "09/09",
            s: "Matarazzo, Lawless",
          },
          {
            d: "1007",
            y: "Opposition to 905",
            p: 0,
            f: "09/30",
            s: "Matarazzo, Lawless",
          },
          {
            d: "1023",
            y: "Proposed Order",
            p: 0,
            f: "10/07/2020",
            s: "JLI Proposed Order",
          },
          {
            d: "1073",
            y: "Order",
            p: 1,
            f: "10/21/2020",
            s: "Order Granting 852, 862, 905",
          },
          {
            d: "1167",
            y: "Convert MTD w/Prej",
            p: 1,
            f: "11/25/2020",
            s: "Motion to Convert 1073 to with-prejudice",
          },
          {
            d: "1176",
            y: "Opposition",
            p: 0,
            f: "12/09/2020",
            s: "Plaintiffs' Opp",
          },
          { d: "1189", y: "Reply", p: 0, f: "12/16/2020", s: "JLI Reply" },
          {
            d: "1474",
            y: "Order",
            p: 1,
            f: "02/25/2021",
            s: "Order Granting 1167 (re Dkt 1073)",
          },
        ],
      },
      {
        id: "I-2",
        t: "MTDs 924, 935, 956 (resolved by Order 1119)",
        docs: [
          {
            d: "924",
            y: "Motion",
            p: 1,
            f: "08/28/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "935",
            y: "Motion",
            p: 1,
            f: "09/04/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "956",
            y: "Motion",
            p: 1,
            f: "09/10/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "959",
            y: "Opposition to 924",
            p: 0,
            f: "09/11/2020",
            s: "Various",
          },
          {
            d: "960",
            y: "Opposition to 924",
            p: 0,
            f: "09/11/2020",
            s: "Various",
          },
          {
            d: "961",
            y: "Opposition to 924",
            p: 0,
            f: "09/11/2020",
            s: "Various",
          },
          {
            d: "962",
            y: "Opposition to 924",
            p: 0,
            f: "09/11/2020",
            s: "Various",
          },
          {
            d: "963",
            y: "Opposition to 924",
            p: 0,
            f: "09/11/2020",
            s: "Various",
          },
          {
            d: "964",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "978",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "979",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "980",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "981",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "982",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "984",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "988",
            y: "Opposition to 935",
            p: 0,
            f: "09/14-21",
            s: "Various",
          },
          {
            d: "999",
            y: "Opposition to 956",
            p: 0,
            f: "09/24/2020",
            s: "Cooper, Brown",
          },
          {
            d: "1000",
            y: "Opposition to 956",
            p: 0,
            f: "09/24/2020",
            s: "Cooper, Brown",
          },
          { d: "1110", y: "Proposed Order", p: 0, f: "10/30/2020", s: "" },
          {
            d: "1119",
            y: "Order",
            p: 1,
            f: "11/02/2020",
            s: "Order Granting 924, 935, 956",
          },
          {
            d: "1169",
            y: "Convert MTD w/Prej",
            p: 1,
            f: "12/03/2020",
            s: "Motion to Convert 1119",
          },
          {
            d: "1193",
            y: "Opposition",
            p: 0,
            f: "12/17/2020",
            s: "Plaintiffs' Opp",
          },
          { d: "1205", y: "Reply", p: 0, f: "12/23/2020", s: "JLI Reply" },
          {
            d: "1474",
            y: "Order",
            p: 1,
            f: "02/25/2021",
            s: "Order (also covers 1169)",
          },
        ],
      },
      {
        id: "I-3",
        t: "MTDs 976, 1001, 1012, 1040, 1058 (resolved by Order 1173)",
        docs: [
          {
            d: "976",
            y: "Motion",
            p: 1,
            f: "09/17/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1001",
            y: "Motion",
            p: 1,
            f: "09/24/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1012",
            y: "Motion",
            p: 1,
            f: "10/01/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1040",
            y: "Motion",
            p: 1,
            f: "10/09/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1058",
            y: "Motion",
            p: 1,
            f: "10/15/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1008",
            y: "Opposition to 976",
            p: 0,
            f: "09/30/2020",
            s: "Singer, L D., Harris",
          },
          {
            d: "1010",
            y: "Opposition to 976",
            p: 0,
            f: "09/30/2020",
            s: "Singer, L D., Harris",
          },
          {
            d: "1011",
            y: "Opposition to 976",
            p: 0,
            f: "09/30/2020",
            s: "Singer, L D., Harris",
          },
          {
            d: "1024",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1025",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1031",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1032",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1033",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1034",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1035",
            y: "Opposition to 1001",
            p: 0,
            f: "10/07-08",
            s: "Various",
          },
          {
            d: "1057",
            y: "Opposition to 1012",
            p: 0,
            f: "10/15/2020",
            s: "Maan, Baker",
          },
          {
            d: "1059",
            y: "Opposition to 1012",
            p: 0,
            f: "10/15/2020",
            s: "Maan, Baker",
          },
          {
            d: "1080",
            y: "Opposition to 1040",
            p: 0,
            f: "10/23/2020",
            s: "Various",
          },
          {
            d: "1081",
            y: "Opposition to 1040",
            p: 0,
            f: "10/23/2020",
            s: "Various",
          },
          {
            d: "1082",
            y: "Opposition to 1040",
            p: 0,
            f: "10/23/2020",
            s: "Various",
          },
          {
            d: "1083",
            y: "Opposition to 1040",
            p: 0,
            f: "10/23/2020",
            s: "Various",
          },
          {
            d: "1085",
            y: "Opposition to 1040",
            p: 0,
            f: "10/23/2020",
            s: "Various",
          },
          {
            d: "1170",
            y: "Proposed Order",
            p: 0,
            f: "12/04/2020",
            s: "Consolidated Proposed Order",
          },
          {
            d: "1173",
            y: "Order",
            p: 1,
            f: "12/08/2020",
            s: "Order Granting (entry modified to remove 1171)",
          },
        ],
      },
      {
        id: "I-4",
        t: "MTDs 1086, 1102, 1121, 1136, 1161, 1171, 1178 (resolved by Orders 1373 / 1447 / 1596)",
        docs: [
          {
            d: "1086",
            y: "Motion",
            p: 1,
            f: "10/23/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1102",
            y: "Motion",
            p: 1,
            f: "10/29/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1121",
            y: "Motion",
            p: 1,
            f: "11/05/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1136",
            y: "Motion",
            p: 1,
            f: "11/13/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1161",
            y: "Motion",
            p: 1,
            f: "11/20/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1171",
            y: "Motion",
            p: 1,
            f: "12/04/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1178",
            y: "Motion",
            p: 1,
            f: "12/10/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1128",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1129",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1130",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1131",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1132",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1133",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1141",
            y: "Opposition to 1102",
            p: 0,
            f: "11/12-13",
            s: "Various",
          },
          {
            d: "1362",
            y: "Erroneous Orders",
            p: 0,
            f: "02/03/2021",
            s: "Orders entered in error (disregard)",
          },
          {
            d: "1363",
            y: "Erroneous Orders",
            p: 0,
            f: "02/03/2021",
            s: "Orders entered in error (disregard)",
          },
          {
            d: "1373",
            y: "Order",
            p: 1,
            f: "02/04/2021",
            s: "Order Re MTDs 1086, 1102, 1121, 1136, 1161, 1171, 1178",
          },
          {
            d: "1416",
            y: "Proposed Order",
            p: 0,
            f: "02/16/2021",
            s: "Consolidated Proposed Order",
          },
          {
            d: "1447",
            y: "Amended Order",
            p: 1,
            f: "02/22/2021",
            s: "Order Granting 1171",
          },
          {
            d: "1596",
            y: "Consolidated Order",
            p: 1,
            f: "03/23/2021",
            s: "Order Granting 1192, 1210, 1237",
          },
        ],
      },
      {
        id: "I-5",
        t: "MTDs 1192, 1210, 1237 (resolved by Order 1596)",
        docs: [
          {
            d: "1192",
            y: "Motion",
            p: 1,
            f: "12/17/2020",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1210",
            y: "Motion",
            p: 1,
            f: "12/29/2020",
            s: "MTD W/O Prejudice (CMO 8 & 12)",
          },
          {
            d: "1237",
            y: "Motion",
            p: 1,
            f: "01/07/2021",
            s: "MTD W/O Prejudice (CMO 8 & 12)",
          },
          {
            d: "1422",
            y: "Opposition to 1210",
            p: 0,
            f: "02/17/2021",
            s: "Helton",
          },
          {
            d: "1209",
            y: "Opposition to 1192",
            p: 0,
            f: "12/29-30/2020",
            s: "Hanover, Ahmed",
          },
          {
            d: "1213",
            y: "Opposition to 1192",
            p: 0,
            f: "12/29-30/2020",
            s: "Hanover, Ahmed",
          },
          {
            d: "1568",
            y: "Proposed Order",
            p: 0,
            f: "03/16/2021",
            s: "Consolidated Proposed Order",
          },
          {
            d: "1596",
            y: "Order",
            p: 1,
            f: "03/23/2021",
            s: "Consolidated Order",
          },
        ],
      },
      {
        id: "I-6",
        t: "MTDs 1274, 1308, 1344, 1374, 1413 (resolved by Order 1697)",
        docs: [
          {
            d: "1274",
            y: "Motion",
            p: 1,
            f: "01/15/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1308",
            y: "Motion",
            p: 1,
            f: "01/22/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1344",
            y: "Motion",
            p: 1,
            f: "01/29/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1374",
            y: "Motion",
            p: 1,
            f: "02/05/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1413",
            y: "Motion",
            p: 1,
            f: "02/16/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1346",
            y: "Proposed Order",
            p: 0,
            f: "01/29/2021",
            s: "Re 1171, 1178",
          },
          {
            d: "1697",
            y: "Order",
            p: 1,
            f: "04/13/2021",
            s: "Order Granting 1308, 1344, 1374, 1413",
          },
        ],
      },
      {
        id: "I-7",
        t: "Convert MTD 1167 / 1169 / 1243 (with prejudice)",
        docs: [
          {
            d: "1243",
            y: "Motion",
            p: 1,
            f: "01/08/2021",
            s: "Motion to Convert 1173 to w/Prej",
          },
          {
            d: "1474",
            y: "Order",
            p: 0,
            f: "02/25/2021",
            s: "Order Granting 1167, 1169, 1243",
          },
          {
            d: "1569/1594",
            y: "Order",
            p: 0,
            f: "03/16-23/2021",
            s: "Order Granting 1243",
          },
        ],
      },
      {
        id: "I-8",
        t: "MTDs 1448, 1487, 1533, 1558 (resolved by Order 1798)",
        docs: [
          {
            d: "1448",
            y: "Motion",
            p: 1,
            f: "02/22/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1487",
            y: "Motion",
            p: 1,
            f: "03/01/2021",
            s: "MTD W/O Prejudice (CMO 8 & 12)",
          },
          {
            d: "1533",
            y: "Motion",
            p: 1,
            f: "03/08/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1558",
            y: "Motion",
            p: 1,
            f: "03/15/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1788",
            y: "Proposed Order",
            p: 0,
            f: "05/03/2021",
            s: "Consolidated",
          },
          {
            d: "1798",
            y: "Order",
            p: 1,
            f: "05/04/2021",
            s: "Order Granting 1448, 1558",
          },
        ],
      },
      {
        id: "I-9",
        t: "MTDs 1586, 1587, 1614, 1634, 1660, 1687, 1718 (resolved by Order 1960)",
        docs: [
          {
            d: "1586",
            y: "Motion",
            p: 1,
            f: "03/22/2021",
            s: "MTD W/O Prejudice (CMO 8)",
          },
          {
            d: "1587",
            y: "Motion",
            p: 1,
            f: "03/22/2021",
            s: "MTD W/O Prejudice (CMO 13)",
          },
          {
            d: "1614",
            y: "Motion",
            p: 1,
            f: "03/26/2021",
            s: "Motion to Convert (Dismissal w/Prej)",
          },
          {
            d: "1634",
            y: "Motion",
            p: 1,
            f: "03/29/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1660",
            y: "Motion",
            p: 1,
            f: "04/05/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1687",
            y: "Motion",
            p: 1,
            f: "04/12/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1718",
            y: "Motion",
            p: 1,
            f: "04/19/2021",
            s: "MTD W/O Prejudice",
          },
          { d: "1711", y: "Opposition to 1586", p: 0, f: "04/15", s: "Woods" },
          { d: "1857", y: "Opposition to 1586", p: 0, f: "05/13", s: "Woods" },
          {
            d: "1688",
            y: "Opposition to 1634",
            p: 0,
            f: "04/12/2021",
            s: "Pendleton",
          },
          {
            d: "1749",
            y: "Opposition to 1687",
            p: 0,
            f: "04/23",
            s: "Sharp, T. Williams",
          },
          {
            d: "1760",
            y: "Opposition to 1687",
            p: 0,
            f: "04/26",
            s: "Sharp, T. Williams",
          },
          {
            d: "1852",
            y: "Opposition to 1718",
            p: 0,
            f: "05/12/2021",
            s: "Kim, Hinson",
          },
          {
            d: "1853",
            y: "Opposition to 1718",
            p: 0,
            f: "05/12/2021",
            s: "Kim, Hinson",
          },
          { d: "1945", y: "Proposed Order", p: 0, f: "06/08/2021", s: "" },
          { d: "1946", y: "Proposed Order", p: 0, f: "06/08/2021", s: "" },
          {
            d: "1960",
            y: "Order",
            p: 1,
            f: "06/10/2021",
            s: "Order Granting 1586, 1634, 1660, 1687, 1718",
          },
          {
            d: "1961",
            y: "Order",
            p: 1,
            f: "06/10/2021",
            s: "Order Granting 1614 (Convert)",
          },
        ],
      },
      {
        id: "I-10",
        t: "MTDs 1752, 1761, 1789, 1835, 1870, 1874, 1900 (resolved by Orders 2097 etc.)",
        docs: [
          {
            d: "1752",
            y: "Motion",
            p: 1,
            f: "04/23/2021",
            s: "Motion to Convert (w/Prej)",
          },
          {
            d: "1761",
            y: "Motion",
            p: 1,
            f: "04/26/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1789",
            y: "Motion",
            p: 1,
            f: "05/03/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1835",
            y: "Motion",
            p: 1,
            f: "05/11/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1870",
            y: "Motion",
            p: 1,
            f: "05/17/2021",
            s: "Motion to Convert",
          },
          {
            d: "1874",
            y: "Motion",
            p: 1,
            f: "05/17/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1900",
            y: "Motion",
            p: 1,
            f: "05/24/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1793",
            y: "Opposition to 1761",
            p: 0,
            f: "05/04-06/08",
            s: "Banner, McCarthy, A.H., Hickins",
          },
          {
            d: "1794",
            y: "Opposition to 1761",
            p: 0,
            f: "05/04-06/08",
            s: "Banner, McCarthy, A.H., Hickins",
          },
          {
            d: "1854",
            y: "Opposition to 1761",
            p: 0,
            f: "05/04-06/08",
            s: "Banner, McCarthy, A.H., Hickins",
          },
          {
            d: "1944",
            y: "Opposition to 1761",
            p: 0,
            f: "05/04-06/08",
            s: "Banner, McCarthy, A.H., Hickins",
          },
          {
            d: "1889",
            y: "Opposition to 1835",
            p: 0,
            f: "05/21",
            s: "Hicks, Marin",
          },
          {
            d: "1941",
            y: "Opposition to 1835",
            p: 0,
            f: "06/07",
            s: "Hicks, Marin",
          },
          {
            d: "1886",
            y: "Opposition to 1870",
            p: 0,
            f: "05/21/2021",
            s: "Valentine",
          },
          {
            d: "1971",
            y: "Opposition to 1789",
            p: 0,
            f: "06/14/2021",
            s: "J.M.D.",
          },
          {
            d: "2097",
            y: "Order",
            p: 1,
            f: "07/14/2021",
            s: "Order Granting 1870 (Convert)",
          },
          {
            d: "2260",
            y: "Order",
            p: 1,
            f: "08/17/2021",
            s: "Order Granting 1988 (Convert)",
          },
        ],
      },
      {
        id: "I-11",
        t: "MTDs 1907, 1929, 1943, 1973, 1974 (resolved by Order 2265)",
        docs: [
          {
            d: "1907",
            y: "Motion",
            p: 1,
            f: "05/25/2021",
            s: "MTD W/O Prejudice (CMO 14)",
          },
          {
            d: "1929",
            y: "Motion",
            p: 1,
            f: "06/01/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1943",
            y: "Motion",
            p: 1,
            f: "06/07/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1973",
            y: "Motion",
            p: 1,
            f: "06/14/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "1974",
            y: "Motion",
            p: 1,
            f: "06/14/2021",
            s: "MTD W/O Prejudice (CMO 13)",
          },
          {
            d: "2049",
            y: "Opposition to 1943",
            p: 0,
            f: "07/06/2021",
            s: "Hiser",
          },
          {
            d: "2142",
            y: "Notice of Withdrawal",
            p: 0,
            f: "07/22/2021",
            s: "JLI Withdrawal of 1973, 1943 (selected pls)",
          },
          {
            d: "2094",
            y: "Order",
            p: 1,
            f: "07/13/2021",
            s: "Order Granting (some)",
          },
          {
            d: "2265",
            y: "Order",
            p: 1,
            f: "08/17/2021",
            s: "Order Granting 1907, 1943, 1973",
          },
        ],
      },
      {
        id: "I-12",
        t: "Convert Motions 1988, 2095",
        docs: [
          {
            d: "1988",
            y: "Motion",
            p: 1,
            f: "06/16/2021",
            s: "Motion to Convert",
          },
          {
            d: "2143",
            y: "Notice of Withdrawal",
            p: 0,
            f: "07/22/2021",
            s: "JLI Withdrawal",
          },
          {
            d: "2260",
            y: "Order",
            p: 1,
            f: "08/17/2021",
            s: "Order Granting 1988",
          },
          {
            d: "2095",
            y: "Motion",
            p: 1,
            f: "07/13/2021",
            s: "Motion to Convert",
          },
          {
            d: "2347",
            y: "Order",
            p: 1,
            f: "09/09/2021",
            s: "Order Granting 2095",
          },
        ],
      },
      {
        id: "I-13",
        t: "MTDs 2009, 2032, 2051, 2071, 2091 (resolved by Order 2346 / Amended 2378)",
        docs: [
          {
            d: "2009",
            y: "Motion",
            p: 1,
            f: "06/21/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2032",
            y: "Motion",
            p: 1,
            f: "06/28/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2051",
            y: "Motion",
            p: 1,
            f: "07/06/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2071",
            y: "Motion",
            p: 1,
            f: "07/09/2021",
            s: "MTD W/O Prejudice (CMO 14)",
          },
          {
            d: "2091",
            y: "Motion",
            p: 1,
            f: "07/12/2021",
            s: "MTD W/O Prejudice",
          },
          { d: "2341", y: "Proposed Order", p: 0, f: "09/07/2021", s: "" },
          {
            d: "2346",
            y: "Order",
            p: 1,
            f: "09/09/2021",
            s: "Order Granting 2009, 2032, 2051, 2071, 2091",
          },
          {
            d: "2378",
            y: "Amended Order",
            p: 1,
            f: "09/20/2021",
            s: "Amends 2346",
          },
        ],
      },
      {
        id: "I-14",
        t: "MTDs 2179, 2212, 2223, 2254, 2258 (resolved by Order 2367)",
        docs: [
          {
            d: "2179",
            y: "Motion",
            p: 1,
            f: "07/26/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2212",
            y: "Motion",
            p: 1,
            f: "08/03/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2223",
            y: "Motion",
            p: 1,
            f: "08/09/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2254",
            y: "Motion",
            p: 1,
            f: "08/16/2021",
            s: "Motion to Convert",
          },
          {
            d: "2258",
            y: "Motion",
            p: 1,
            f: "08/17/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2259",
            y: "Withdrawal",
            p: 0,
            f: "08/17",
            s: "JLI Notice of Withdrawal",
          },
          {
            d: "2276",
            y: "Withdrawal",
            p: 0,
            f: "08/19/2021",
            s: "JLI Notice of Withdrawal",
          },
          {
            d: "2367",
            y: "Order",
            p: 1,
            f: "09/16/2021",
            s: "Order Granting 2179, 2212, 2223, 2254, 2258",
          },
          {
            d: "2451",
            y: "Order",
            p: 1,
            f: "10/14/2021",
            s: "Order Granting 2254 (Convert)",
          },
        ],
      },
      {
        id: "I-15",
        t: "MTDs 2317, 2342, 2351 (resolved by Order 2452)",
        docs: [
          {
            d: "2317",
            y: "Motion",
            p: 1,
            f: "08/30/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2342",
            y: "Motion",
            p: 1,
            f: "09/07/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2351",
            y: "Motion",
            p: 1,
            f: "09/13/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2401",
            y: "Opposition to 2351",
            p: 0,
            f: "09/27/2021",
            s: "Suleman",
          },
          {
            d: "2506",
            y: "Proposed Order",
            p: 0,
            f: "11/01/2021",
            s: "Consolidated",
          },
          {
            d: "2452",
            y: "Order",
            p: 1,
            f: "10/14/2021",
            s: "Consolidated Order Granting",
          },
        ],
      },
      {
        id: "I-16",
        t: "MTDs 2372, 2377, 2400, 2418, 2441, 2472 (resolved by Order 2583)",
        docs: [
          {
            d: "2372",
            y: "Motion",
            p: 1,
            f: "09/17/2021",
            s: "Motion to Convert",
          },
          {
            d: "2377",
            y: "Motion",
            p: 1,
            f: "09/20/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2400",
            y: "Motion",
            p: 1,
            f: "09/27/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2418",
            y: "Motion",
            p: 1,
            f: "10/04/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2441",
            y: "Motion",
            p: 1,
            f: "10/11/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2450",
            y: "Motion",
            p: 1,
            f: "10/14/2021",
            s: "Motion to Convert",
          },
          {
            d: "2472",
            y: "Motion",
            p: 1,
            f: "10/18/2021",
            s: "MTD W/O Prejudice (CMO 8 & 13)",
          },
          {
            d: "2414",
            y: "Opposition to 2400",
            p: 0,
            f: "09/30/2021",
            s: "Ghannam",
          },
          {
            d: "2555",
            y: "Proposed Order",
            p: 0,
            f: "11/22/2021",
            s: "Consolidated",
          },
          {
            d: "2583",
            y: "Order",
            p: 1,
            f: "11/29/2021",
            s: "Order Granting 2377, 2400, 2418, 2441, 2472",
          },
          {
            d: "2584",
            y: "Order",
            p: 1,
            f: "11/29/2021",
            s: "Order Granting 2372, 2450 (Convert)",
          },
        ],
      },
      {
        id: "I-17",
        t: "MTDs 2507, 2522, 2523, 2543, 2558, 2574 (resolved by Order 2508 / 2678)",
        docs: [
          {
            d: "2507",
            y: "Motion",
            p: 1,
            f: "11/01/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2522",
            y: "Motion",
            p: 1,
            f: "11/08/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2523",
            y: "Motion",
            p: 1,
            f: "11/08/2021",
            s: "MTD W/O Prejudice (CMO 13)",
          },
          {
            d: "2543",
            y: "Motion",
            p: 1,
            f: "11/15/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2558",
            y: "Motion",
            p: 1,
            f: "11/22/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2574",
            y: "Motion",
            p: 1,
            f: "11/29/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2508",
            y: "Order",
            p: 1,
            f: "11/02/2021",
            s: "Order Granting 2507",
          },
          {
            d: "2667",
            y: "Proposed Order",
            p: 0,
            f: "12/22/2021",
            s: "Terminate 2507",
          },
          {
            d: "2678",
            y: "Order",
            p: 1,
            f: "12/22/2021",
            s: "Order Terminating 2507",
          },
        ],
      },
      {
        id: "I-18",
        t: "MTDs 2605, 2643, 2662, 2729, 2730, 2754 (resolved by Orders 2855 / 2938 / 2939)",
        docs: [
          {
            d: "2605",
            y: "Motion",
            p: 1,
            f: "12/06/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2643",
            y: "Motion",
            p: 1,
            f: "12/14/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2662",
            y: "Motion",
            p: 1,
            f: "12/20/2021",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2729",
            y: "Motion",
            p: 1,
            f: "01/03/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2730",
            y: "Motion",
            p: 1,
            f: "01/03/2022",
            s: "Motion to Convert (re 2508, 2583)",
          },
          {
            d: "2754",
            y: "Motion",
            p: 1,
            f: "01/10/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2855",
            y: "Order",
            p: 1,
            f: "02/09/2022",
            s: "Order Granting (some)",
          },
          {
            d: "2938",
            y: "Order",
            p: 1,
            f: "03/15/2022",
            s: "Order Granting 2754",
          },
          {
            d: "2939",
            y: "Order",
            p: 1,
            f: "03/15/2022",
            s: "Order Granting 2730 (Convert)",
          },
        ],
      },
      {
        id: "I-19",
        t: "MTDs 2778, 2798, 2811, 2874, 2902, 2915, 2921 (resolved by Orders 3042 / 3053 / 3249)",
        docs: [
          {
            d: "2778",
            y: "Motion",
            p: 1,
            f: "01/18/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2798",
            y: "Motion",
            p: 1,
            f: "01/24/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2811",
            y: "Motion",
            p: 1,
            f: "01/31/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2874",
            y: "Motion",
            p: 1,
            f: "02/14/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2902",
            y: "Motion",
            p: 1,
            f: "02/21/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2915",
            y: "Motion",
            p: 1,
            f: "02/28/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2921",
            y: "Motion",
            p: 1,
            f: "03/07/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3032",
            y: "Proposed Order",
            p: 0,
            f: "04/13/2022",
            s: "Re 2778",
          },
          {
            d: "3042",
            y: "Order",
            p: 1,
            f: "04/14/2022",
            s: "Order Granting 2874, 2902, 2915, 2921 (Minute Entry)",
          },
          {
            d: "3053",
            y: "Order",
            p: 1,
            f: "04/22/2022",
            s: "Order Granting 2778",
          },
          {
            d: "3249",
            y: "Order",
            p: 1,
            f: "05/25/2022",
            s: "Order Granting 2934, 2944, 3010, 3020, 3028",
          },
        ],
      },
      {
        id: "I-20",
        t: "MTDs 2934, 2944, 3010, 3020, 3028 (resolved by Order 3249)",
        docs: [
          {
            d: "2934",
            y: "Motion",
            p: 1,
            f: "03/14/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "2944",
            y: "Motion",
            p: 1,
            f: "03/21/2022",
            s: "Motion to Convert (re 2855)",
          },
          {
            d: "3010",
            y: "Motion",
            p: 1,
            f: "04/04/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3020",
            y: "Motion",
            p: 1,
            f: "04/06/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3028",
            y: "Motion",
            p: 1,
            f: "04/11/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3249",
            y: "Order",
            p: 1,
            f: "05/25/2022",
            s: "Order Granting 2934, 2944, 3010, 3020, 3028",
          },
          {
            d: "3293",
            y: "Order",
            p: 1,
            f: "06/15/2022",
            s: "Order Granting 2944",
          },
        ],
      },
      {
        id: "I-21",
        t: "MTDs 3043, 3054, 3169 (resolved by Order 3342)",
        docs: [
          {
            d: "3043",
            y: "Motion",
            p: 1,
            f: "04/18/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3054",
            y: "Motion",
            p: 1,
            f: "04/25/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3055",
            y: "Motion",
            p: 1,
            f: "04/25/2022",
            s: "Motion to Convert (w/ prej)",
          },
          {
            d: "3169",
            y: "Motion",
            p: 1,
            f: "05/09/2022",
            s: "MTD W/O Prejudice",
          },
          { d: "3279", y: "Proposed Order", p: 0, f: "06/10/2022", s: "" },
          { d: "3280", y: "Proposed Order", p: 0, f: "06/10/2022", s: "" },
          {
            d: "3306",
            y: "Order",
            p: 1,
            f: "06/21/2022",
            s: "Consolidated Order",
          },
          {
            d: "3341",
            y: "Order",
            p: 1,
            f: "07/14/2022",
            s: "Order Granting 3055 (Convert)",
          },
          {
            d: "3342",
            y: "Order",
            p: 1,
            f: "07/15/2022",
            s: "Order Granting 3043, 3054, 3169",
          },
        ],
      },
      {
        id: "I-22",
        t: "MTDs 3201, 3242, 3264, 3272, 3285, 3302, 3321, 3334, 3355, 3376 (resolved by Orders 3377 / 3471 / 3685)",
        docs: [
          {
            d: "3201",
            y: "Motion",
            p: 1,
            f: "05/16/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3242",
            y: "Motion",
            p: 1,
            f: "05/23/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3264",
            y: "Motion",
            p: 1,
            f: "05/31/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3272",
            y: "Motion",
            p: 1,
            f: "06/06/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3285",
            y: "Motion",
            p: 1,
            f: "06/13/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3302",
            y: "Motion",
            p: 1,
            f: "06/20/2022",
            s: "MTD W/O Prejudice (CMO 8 & 13)",
          },
          {
            d: "3321",
            y: "Motion",
            p: 1,
            f: "06/27/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3334",
            y: "Motion",
            p: 1,
            f: "07/11/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3355",
            y: "Motion",
            p: 1,
            f: "07/25/2022",
            s: "Motion to Convert",
          },
          {
            d: "3376",
            y: "Motion",
            p: 1,
            f: "08/08/2022",
            s: "MTD W/O Prejudice",
          },
          { d: "3369", y: "Proposed Order", p: 0, f: "08/02/2022", s: "" },
          { d: "3370", y: "Proposed Order", p: 0, f: "08/02/2022", s: "" },
          {
            d: "3377",
            y: "Order",
            p: 1,
            f: "08/08/2022",
            s: "Consolidated Order Granting 3264, 3272",
          },
          {
            d: "3471",
            y: "Order",
            p: 1,
            f: "09/16/2022",
            s: "Order Granting 3285, 3334",
          },
          {
            d: "3685",
            y: "Order",
            p: 1,
            f: "12/01/2022",
            s: "Order Granting 3348, 3376",
          },
          {
            d: "3713 (Min.)",
            y: "Order",
            p: 1,
            f: "12/16/2022",
            s: "Granting 3355, 3386, 3417, 3428, 3434, 3435, 3464, 3486, 3510, 3604, 3632",
          },
          {
            d: "3738",
            y: "Order",
            p: 1,
            f: "01/06/2023",
            s: "Consolidated Order",
          },
        ],
      },
      {
        id: "I-23",
        t: "MTDs 3348, 3386, 3417, 3428, 3434, 3464, 3486, 3510 (resolved by Orders 3685 / 3713)",
        docs: [
          {
            d: "3348",
            y: "Motion",
            p: 1,
            f: "07/18/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3386",
            y: "Motion",
            p: 1,
            f: "08/15/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3417",
            y: "Motion",
            p: 1,
            f: "08/22/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3428",
            y: "Motion",
            p: 1,
            f: "08/29/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3434",
            y: "Motion",
            p: 1,
            f: "09/06/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3435",
            y: "Motion",
            p: 1,
            f: "09/06/2022",
            s: "Motion to Convert",
          },
          {
            d: "3464",
            y: "Motion",
            p: 1,
            f: "09/13/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3486",
            y: "Motion",
            p: 1,
            f: "09/19/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3510",
            y: "Motion",
            p: 1,
            f: "09/26/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3685",
            y: "Order",
            p: 1,
            f: "12/01/2022",
            s: "Granting 3348, 3376",
          },
          {
            d: "3713 (Min.)",
            y: "Order",
            p: 1,
            f: "12/16/2022",
            s: "Granting 3355, 3386, 3417, 3428, 3434, 3435, 3464, 3486, 3510",
          },
        ],
      },
      {
        id: "I-24",
        t: "MTDs 3604, 3632, 3666, 3672, 3679 (resolved by Orders 3713 / 3738 / 3859)",
        docs: [
          {
            d: "3604",
            y: "Motion",
            p: 1,
            f: "10/17/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3632",
            y: "Motion",
            p: 1,
            f: "10/24/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3666",
            y: "Motion",
            p: 1,
            f: "11/14/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3672",
            y: "Motion",
            p: 1,
            f: "11/21/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3679",
            y: "Motion",
            p: 1,
            f: "11/28/2022",
            s: "MTD W/O Prejudice",
          },
          {
            d: "3694",
            y: "Opposition to 3679",
            p: 0,
            f: "12/12/2022",
            s: "Timpani",
          },
          {
            d: "3713 (Min.)",
            y: "Order",
            p: 1,
            f: "12/16/2022",
            s: "Granting 3604, 3632",
          },
          {
            d: "3738",
            y: "Order",
            p: 1,
            f: "01/06/2023",
            s: "Consolidated Order",
          },
          {
            d: "3872",
            y: "Order",
            p: 1,
            f: "03/23/2023",
            s: "Mooting 3679, 3672",
          },
          {
            d: "3873",
            y: "Order",
            p: 0,
            f: "03/23/2023",
            s: "Mooting 3679, 3672",
          },
        ],
      },
      {
        id: "I-25",
        t: "MTD with Prejudice 3797, 3918 (CMO 16)",
        docs: [
          {
            d: "3797",
            y: "Motion",
            p: 1,
            f: "02/15/2023",
            s: "MTD with Prejudice",
          },
          {
            d: "3808",
            y: "Opposition to 3797",
            p: 0,
            f: "02/20-23/2023",
            s: "Kherkher, Pry, Alfano, Sedgwick",
          },
          {
            d: "3809",
            y: "Opposition to 3797",
            p: 0,
            f: "02/20-23/2023",
            s: "Kherkher, Pry, Alfano, Sedgwick",
          },
          {
            d: "3810",
            y: "Opposition to 3797",
            p: 0,
            f: "02/20-23/2023",
            s: "Kherkher, Pry, Alfano, Sedgwick",
          },
          {
            d: "3815",
            y: "Opposition to 3797",
            p: 0,
            f: "02/20-23/2023",
            s: "Kherkher, Pry, Alfano, Sedgwick",
          },
          { d: "3811", y: "Reply", p: 0, f: "02/21/2023", s: "JLI" },
          { d: "3812", y: "Reply", p: 0, f: "02/21/2023", s: "JLI" },
          { d: "3839", y: "Status Update", p: 0, f: "03/09/2023", s: "JLI" },
          {
            d: "3859",
            y: "Order",
            p: 1,
            f: "03/21/2023",
            s: "Order Granting 3797",
          },
          {
            d: "3862",
            y: "Orders (member cases)",
            p: 1,
            f: "03/21/2023",
            s: "Spread to member cases",
          },
          {
            d: "3863",
            y: "Orders (member cases)",
            p: 0,
            f: "03/21/2023",
            s: "Spread to member cases",
          },
          {
            d: "3864",
            y: "Orders (member cases)",
            p: 0,
            f: "03/21/2023",
            s: "Spread to member cases",
          },
          {
            d: "3918",
            y: "Motion",
            p: 1,
            f: "04/13/2023",
            s: "MTD with Prejudice (CMO 16)",
          },
          {
            d: "3937",
            y: "Opposition to 3918",
            p: 0,
            f: "04/21/2023",
            s: "Puerta",
          },
          { d: "3957", y: "Status Update", p: 0, f: "04/25/2023", s: "JLI" },
          {
            d: "3962",
            y: "Order",
            p: 1,
            f: "04/26/2023",
            s: "Order Granting 3918",
          },
        ],
      },
      {
        id: "I-26",
        t: "Non-Communicating Plaintiffs MTDs",
        docs: [
          {
            d: "4061",
            y: "Motion",
            p: 1,
            f: "07/11/2023",
            s: "MTD Non-Communicating Pls (JLI)",
          },
          {
            d: "4074",
            y: "Order",
            p: 1,
            f: "07/21/2023",
            s: "Order Granting 4061",
          },
          {
            d: "4140",
            y: "Motion",
            p: 1,
            f: "09/20/2023",
            s: "2nd MTD Non-Communicating Pls (JLI)",
          },
          {
            d: "4147",
            y: "Order",
            p: 1,
            f: "10/27/2023",
            s: "Order Granting 4140",
          },
          {
            d: "4216",
            y: "Motion",
            p: 1,
            f: "04/23/2024",
            s: "Altria MTD Non-Communicating Pls",
          },
          {
            d: "4247",
            y: "Notice",
            p: 0,
            f: "05/22/2024",
            s: "Supplemental Decl.",
          },
          {
            d: "4274",
            y: "Order",
            p: 1,
            f: "07/09/2024",
            s: "Order Granting 4216",
          },
          {
            d: "4401",
            y: "Motion",
            p: 1,
            f: "04/14/2025",
            s: "JLI 1st MTD Non-Opt Out Pls",
          },
          { d: "4411", y: "Proposed Order", p: 0, f: "05/09/2025", s: "" },
          {
            d: "4412",
            y: "Order",
            p: 1,
            f: "05/13/2025",
            s: "Order Granting 4401",
          },
        ],
      },
    ],
  },
  {
    id: "secJ",
    l: "J",
    n: "Substantive Second-Round MTDs (Class Action / Government Entity) - Resolved by Order 1694",
    threads: [
      {
        id: "J1",
        t: "NMD 2nd MTD Class & Government Entity",
        docs: [
          {
            d: "1221",
            y: "Admin Motion to Seal",
            p: 0,
            f: "01/04/2021",
            s: "NMD Mot to Seal",
          },
          {
            d: "1222",
            y: "Motion",
            p: 1,
            f: "01/04/2021",
            s: "NMD MTD Second Amended Class & Government Entity (Redacted)",
          },
          {
            d: "1364",
            y: "Opposition",
            p: 0,
            f: "02/03/2021",
            s: "Plaintiffs' Consolidated Opp",
          },
          {
            d: "1457",
            y: "Stipulation",
            p: 0,
            f: "02/23/2021",
            s: "Stip re Reply Page Limit",
          },
          { d: "1463", y: "Reply", p: 0, f: "02/24/2021", s: "NMD Reply" },
          {
            d: "1470",
            y: "Joinder",
            p: 0,
            f: "02/24/2021",
            s: "Monsees Joinder",
          },
          {
            d: "1495",
            y: "Joinder",
            p: 0,
            f: "03/02/2021",
            s: "NMD Joinder in Replies",
          },
          {
            d: "1694",
            y: "Order",
            p: 1,
            f: "04/13/2021",
            s: "Order on 1222, 1223, 1225, 1229",
          },
        ],
      },
      {
        id: "J2",
        t: "Altria 2nd MTD UCL/RICO",
        docs: [
          {
            d: "1223",
            y: "Motion",
            p: 1,
            f: "01/04/2021",
            s: "Altria MTD UCL/RICO 2nd Amended",
          },
          { d: "1224", y: "RJN", p: 0, f: "01/04/2021", s: "Altria RJN" },
          {
            d: "1226",
            y: "Admin Motion to Seal",
            p: 0,
            f: "01/04/2021",
            s: "Altria Mot to Seal",
          },
          {
            d: "1338",
            y: "Amended Mot to Seal",
            p: 0,
            f: "01/28/2021",
            s: "Altria Amended Mot to Seal",
          },
          {
            d: "1364",
            y: "Opposition",
            p: 0,
            f: "02/03/2021",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "1465", y: "Reply", p: 0, f: "02/24/2021", s: "Altria Reply" },
          {
            d: "1466",
            y: "RJN re Reply",
            p: 0,
            f: "02/24/2021",
            s: "Altria RJN ISO Reply",
          },
          {
            d: "1468",
            y: "Mot to Seal Reply",
            p: 0,
            f: "02/24/2021",
            s: "Altria Mot to Seal",
          },
          {
            d: "1470",
            y: "Joinder",
            p: 0,
            f: "02/24/2021",
            s: "Monsees Joinder",
          },
          { d: "1495", y: "Joinder", p: 0, f: "03/02/2021", s: "NMD Joinder" },
          {
            d: "1694",
            y: "Order",
            p: 1,
            f: "04/13/2021",
            s: "Order on Substantive 2nd Round MTDs",
          },
        ],
      },
      {
        id: "J3",
        t: "JLI MTD CA Implied Warranty",
        docs: [
          {
            d: "1225",
            y: "Motion",
            p: 1,
            f: "01/04/2021",
            s: "JLI MTD CA Breach of Implied Warranty",
          },
          { d: "1694", y: "Order", p: 1, f: "04/13/2021", s: "Order" },
        ],
      },
      {
        id: "J4",
        t: "Bowen MTD RICO (2nd Round)",
        docs: [
          {
            d: "1229",
            y: "Motion",
            p: 1,
            f: "01/04/2021",
            s: "Bowen MTD RICO Class & Government",
          },
          {
            d: "1364",
            y: "Opposition",
            p: 0,
            f: "02/03/2021",
            s: "Plaintiffs' Consolidated Opp",
          },
          { d: "1467", y: "Reply", p: 0, f: "02/24/2021", s: "Bowen Reply" },
          {
            d: "1470",
            y: "Joinder",
            p: 0,
            f: "02/24/2021",
            s: "Monsees Joinder",
          },
          { d: "1495", y: "Joinder", p: 0, f: "03/02/2021", s: "NMD Joinder" },
          { d: "1694", y: "Order", p: 1, f: "04/13/2021", s: "Order" },
        ],
      },
      {
        id: "J5",
        t: "JLI MTD/Strike Subclass for Lack of Jurisdiction",
        docs: [
          {
            d: "1397",
            y: "Motion",
            p: 1,
            f: "02/08/2021",
            s: "JLI MTD/Strike Subclass Claims",
          },
          {
            d: "1481",
            y: "Opposition",
            p: 0,
            f: "02/26/2021",
            s: "Plaintiffs' Opp",
          },
          { d: "1557", y: "Reply", p: 0, f: "03/12/2021", s: "JLI Reply" },
          {
            d: "(class cert)",
            y: "Order",
            p: 0,
            f: "",
            s: "Resolved with class cert",
          },
        ],
      },
    ],
  },
  {
    id: "secK",
    l: "K",
    n: "Bellwether Personal Injury MTDs (2021)",
    threads: [
      {
        id: "K1",
        t: "Bowen, Altria, NMD, Monsees Bellwether MTDs (Round 1)",
        docs: [
          {
            d: "1499",
            y: "Stipulation",
            p: 0,
            f: "03/02/2021",
            s: "Page Limit Stip",
          },
          {
            d: "1500",
            y: "Admin Mot to Seal",
            p: 0,
            f: "03/02/2021",
            s: "NMD Mot to Seal",
          },
          {
            d: "1501",
            y: "Motion",
            p: 1,
            f: "03/02/2021",
            s: "Bowen MTD Bellwether PI",
          },
          {
            d: "1502",
            y: "Motion",
            p: 1,
            f: "03/02/2021",
            s: "Altria MTD Bellwether PI",
          },
          {
            d: "1503",
            y: "Motion",
            p: 1,
            f: "03/02/2021",
            s: "Monsees MTD Bellwether PI",
          },
          {
            d: "1509",
            y: "Order",
            p: 0,
            f: "03/03/2021",
            s: "Granting 1499 (page limit)",
          },
          {
            d: "1800",
            y: "Opposition",
            p: 0,
            f: "05/04/2021",
            s: "Plaintiffs' Opp to 1502 (Altria)",
          },
          {
            d: "1801",
            y: "Opposition",
            p: 0,
            f: "05/04/2021",
            s: "Plaintiffs' Consolidated Opp to 1501, 1503",
          },
          {
            d: "1802",
            y: "Opposition",
            p: 0,
            f: "05/04/2021",
            s: "Plaintiffs' Opp to NMDs Bellwether",
          },
          {
            d: "1923",
            y: "Stipulation",
            p: 0,
            f: "05/28/2021",
            s: "Page Limit Stip Reply",
          },
          {
            d: "1928",
            y: "Order",
            p: 0,
            f: "06/01/2021",
            s: "Order Granting 1923",
          },
          { d: "1930", y: "Reply", p: 0, f: "06/01/2021", s: "Altria Reply" },
          {
            d: "1931",
            y: "Stipulation",
            p: 0,
            f: "06/01/2021",
            s: "NMD Reply Page Limit Stip",
          },
          { d: "1932", y: "Reply", p: 0, f: "06/01/2021", s: "Bowen Reply" },
          {
            d: "1933",
            y: "Mot to Seal Reply",
            p: 0,
            f: "06/01/2021",
            s: "NMD",
          },
          { d: "1934", y: "Reply", p: 0, f: "06/01/2021", s: "Monsees Reply" },
          {
            d: "1938",
            y: "Mot to Seal",
            p: 0,
            f: "06/03/2021",
            s: "NMD Amended Reply",
          },
          {
            d: "2144",
            y: "Order",
            p: 1,
            f: "07/22/2021",
            s: "Order on Bellwether MTDs",
          },
        ],
      },
      {
        id: "K2",
        t: "NMD 2nd MTD Bellwether PI Cases",
        docs: [
          {
            d: "2413",
            y: "Motion",
            p: 1,
            f: "09/29/2021",
            s: "NMD MTD Amended PI Bellwether Complaints",
          },
          {
            d: "2427",
            y: "Stipulation",
            p: 0,
            f: "10/06/2021",
            s: "Stip to Extend Briefing",
          },
          {
            d: "2428",
            y: "Order",
            p: 0,
            f: "10/07/2021",
            s: "Order Granting 2427",
          },
          {
            d: "2495",
            y: "Opposition",
            p: 0,
            f: "10/27/2021",
            s: "Plaintiffs' Opp",
          },
          {
            d: "2549",
            y: "Reply",
            p: 0,
            f: "11/17/2021",
            s: "NMD (Valani) Reply",
          },
          {
            d: "2751",
            y: "Order",
            p: 1,
            f: "01/06/2022",
            s: "Order on NMD 2nd MTD Bellwether",
          },
        ],
      },
    ],
  },
  {
    id: "secL",
    l: "L",
    n: "Tribal Bellwether MTDs (2021-2022)",
    threads: [
      {
        id: "L1",
        t: "Tribal Bellwether MTDs",
        docs: [
          {
            d: "2431",
            y: "Motion",
            p: 1,
            f: "10/08/2021",
            s: "JLI Partial MTD Tribal Bellwether",
          },
          {
            d: "2432",
            y: "Motion",
            p: 1,
            f: "10/08/2021",
            s: "NMD MTD Tribal (St. Regis & Grand Traverse)",
          },
          {
            d: "2434",
            y: "Motion",
            p: 1,
            f: "10/08/2021",
            s: "Altria MTD (St. Regis & Grand Traverse)",
          },
          {
            d: "2435",
            y: "Motion",
            p: 1,
            f: "10/08/2021",
            s: "Bowen MTD Tribal Bellwether",
          },
          {
            d: "2437",
            y: "Motion",
            p: 1,
            f: "10/08/2021",
            s: "Monsees MTD Tribal Bellwether",
          },
          {
            d: "2559",
            y: "Opposition",
            p: 0,
            f: "11/22/2021",
            s: "St. Regis Mohawk Opp",
          },
          {
            d: "2560",
            y: "Opposition",
            p: 0,
            f: "11/22/2021",
            s: "Grand Traverse Opp",
          },
          { d: "2674", y: "Reply", p: 0, f: "12/22/2021", s: "NMD Reply" },
          { d: "2676", y: "Reply", p: 0, f: "12/22/2021", s: "Altria Reply" },
          { d: "2677", y: "Reply", p: 0, f: "12/22/2021", s: "Bowen Reply" },
          { d: "2679", y: "Reply", p: 0, f: "12/22/2021", s: "Monsees Reply" },
          { d: "2680", y: "Reply", p: 0, f: "12/22/2021", s: "JLI Reply" },
          {
            d: "2802",
            y: "Order",
            p: 1,
            f: "01/28/2022",
            s: "Order on Tribal Bellwether MTDs",
          },
        ],
      },
      {
        id: "L2",
        t: "Tribal Plaintiff's Claims Partial Dismissal (Cheyenne & Arapaho)",
        docs: [
          {
            d: "3359",
            y: "Motion",
            p: 1,
            f: "07/28/2022",
            s: "JLI MTD Plaintiff's Claims (Tribal)",
          },
          {
            d: "3427",
            y: "Opposition",
            p: 0,
            f: "08/29/2022",
            s: "Cheyenne & Arapaho Opp",
          },
          { d: "3483", y: "Reply", p: 0, f: "09/19/2022", s: "JLI Reply" },
        ],
      },
      {
        id: "L3",
        t: "JLI MTD Tribal Bellwether Claims",
        docs: [
          {
            d: "3312",
            y: "Motion",
            p: 1,
            f: "06/23/2022",
            s: "JLI MTD Tribal Bellwether Claims",
          },
        ],
      },
    ],
  },
  {
    id: "secM",
    l: "M",
    n: "Class Certification & Daubert (2021-2022)",
    threads: [
      {
        id: "M1",
        t: "Plaintiffs' Class Certification Motion",
        docs: [
          {
            d: "1750",
            y: "Mot to Shorten Time",
            p: 0,
            f: "04/23/2021",
            s: "Plaintiffs' Mot for Expedited Consideration",
          },
          {
            d: "1751",
            y: "Mot to Modify Page",
            p: 0,
            f: "04/23/2021",
            s: "Plaintiffs' Mot to Modify Page Limits",
          },
          { d: "1753", y: "Order", p: 0, f: "04/23/2021", s: "Granting 1750" },
          { d: "1754", y: "Order", p: 0, f: "04/23/2021", s: "Granting 1751" },
          {
            d: "1772",
            y: "Mot to Seal",
            p: 1,
            f: "04/28/2021",
            s: "Plaintiffs' Class Cert (sealed)",
          },
          {
            d: "1970",
            y: "Notice",
            p: 0,
            f: "06/11/2021",
            s: "JLI/Altria Notice re 1772",
          },
          {
            d: "2050",
            y: "Order on Sealing",
            p: 0,
            f: "07/06/2021",
            s: "Re 1772, 1981",
          },
          {
            d: "2303",
            y: "Mot to Seal Opp",
            p: 0,
            f: "08/27/2021",
            s: "NMD Opp to Class Cert",
          },
          {
            d: "2305",
            y: "Notice (Redacted)",
            p: 0,
            f: "08/27/2021",
            s: "NMD Redacted Opp",
          },
          {
            d: "2308",
            y: "Mot to Seal",
            p: 0,
            f: "08/27/2021",
            s: "Altria Opp",
          },
          {
            d: "2309-2313",
            y: "Mot to Seal",
            p: 0,
            f: "08/28/2021",
            s: "JLI Class Opp / Daubert",
          },
          {
            d: "2381-2384",
            y: "Joint Notice/Sealing",
            p: 0,
            f: "09/20/2021",
            s: "Sealing chart",
          },
          {
            d: "2518",
            y: "Stmt of Recent Decision",
            p: 0,
            f: "11/05/2021",
            s: "Plaintiffs",
          },
          {
            d: "2438",
            y: "Mot to Seal Reply",
            p: 0,
            f: "10/08/2021",
            s: "Plaintiffs' Reply",
          },
          {
            d: "2439",
            y: "Mot to Seal Daubert Opp",
            p: 0,
            f: "10/08/2021",
            s: "Plaintiffs",
          },
          { d: "2497", y: "Joint Mot to Seal", p: 0, f: "10/28/2021", s: "" },
          {
            d: "2503",
            y: "Mot to Seal",
            p: 0,
            f: "10/29/2021",
            s: "Plaintiffs' Reply re Hanssens Daubert",
          },
          {
            d: "2479",
            y: "Mot to Strike",
            p: 0,
            f: "10/19/2021",
            s: "Defs MTS Improper Class Expert Rebuttal",
          },
          { d: "2496", y: "Order on MTS", p: 0, f: "10/27/2021", s: "" },
          {
            d: "2493",
            y: "Opposition",
            p: 0,
            f: "10/26/2021",
            s: "Plaintiffs' Opp to MTS",
          },
          {
            d: "2534",
            y: "Suppl. Murphy Report",
            p: 0,
            f: "11/10/2021",
            s: "Mot to Seal",
          },
          {
            d: "2535/2536",
            y: "Mot to Seal Reply Daubert",
            p: 0,
            f: "11/10-11/2021",
            s: "JLI Reply",
          },
          {
            d: "2550",
            y: "Suppl. Singer Report",
            p: 0,
            f: "11/17/2021",
            s: "Mot to Seal",
          },
          {
            d: "2551",
            y: "Joint Notice (Daubert)",
            p: 0,
            f: "11/18/2021",
            s: "",
          },
          {
            d: "2552",
            y: "Reply (Daubert)",
            p: 0,
            f: "11/18/2021",
            s: "Plaintiffs",
          },
          {
            d: "2779",
            y: "Statement of Recent Decision",
            p: 0,
            f: "01/18/2022",
            s: "JLI",
          },
          {
            d: "3327",
            y: "Order on Class Cert",
            p: 1,
            f: "06/28/2022",
            s: "Order on Class Cert",
          },
        ],
      },
      {
        id: "M2",
        t: "Daubert Motions (Class Cert Stage)",
        docs: [
          {
            d: "2675",
            y: "Stipulation",
            p: 0,
            f: "12/22/2021",
            s: "Stip Daubert Briefing Time",
          },
          { d: "2681", y: "Order", p: 0, f: "12/23/2021", s: "Granting 2675" },
          {
            d: "2682",
            y: "Stipulation (NMD page)",
            p: 0,
            f: "12/23/2021",
            s: "",
          },
          { d: "2716", y: "Order", p: 0, f: "12/28/2021", s: "Granting 2682" },
          {
            d: "2683",
            y: "Mot to Seal",
            p: 0,
            f: "12/23/2021",
            s: "Altria Daubert",
          },
          { d: "2685", y: "Mot to Seal", p: 0, f: "12/23/2021", s: "Altria" },
          {
            d: "2686",
            y: "Motion",
            p: 1,
            f: "12/23/2021",
            s: "NMD Omnibus Daubert (MIL)",
          },
          { d: "2701", y: "Motion", p: 1, f: "12/24/2021", s: "JLI Roadmap" },
          {
            d: "2702",
            y: "Motion",
            p: 1,
            f: "12/24/2021",
            s: "JLI #3 Toxicity/Health",
          },
          {
            d: "2705",
            y: "Motion",
            p: 1,
            f: "12/24/2021",
            s: "JLI #6 Abatement",
          },
          {
            d: "2706",
            y: "Motion",
            p: 1,
            f: "12/24/2021",
            s: "JLI #2 Addiction",
          },
          {
            d: "2707",
            y: "Motion",
            p: 1,
            f: "12/24/2021",
            s: "JLI #5 Failures to Act",
          },
          {
            d: "2708",
            y: "Motion",
            p: 1,
            f: "12/24/2021",
            s: "JLI #4 Fact Narrations/State of Mind",
          },
          {
            d: "2709",
            y: "Motion",
            p: 1,
            f: "12/27/2021",
            s: "JLI #1 Marketing",
          },
          {
            d: "2727",
            y: "Motion",
            p: 1,
            f: "12/31/2021",
            s: "Plaintiffs' Mot to Preclude Arnett",
          },
          {
            d: "2758",
            y: "Joinder",
            p: 0,
            f: "01/11/2022",
            s: "Altria Joinder in JLI Daubert",
          },
          {
            d: "2823",
            y: "Opposition",
            p: 0,
            f: "02/03/2022",
            s: "Plaintiffs' Omnibus Opp to 2701-2709, 2685, 2686",
          },
          {
            d: "2840",
            y: "Order",
            p: 0,
            f: "02/07/2022",
            s: "Order Re Defendants' Daubert Charts",
          },
          {
            d: "3270",
            y: "Order",
            p: 1,
            f: "06/02/2022",
            s: "Order on Mots to Exclude (2683, 2686, 2701, 2709)",
          },
        ],
      },
    ],
  },
  {
    id: "secN",
    l: "N",
    n: "B.B. Bellwether Trial (Bain Case)",
    threads: [
      {
        id: "N1",
        t: "B.B. Bellwether MSJ & Daubert",
        docs: [
          {
            d: "2647",
            y: "Plaintiff Mot Partial SJ",
            p: 1,
            f: "12/15/2021",
            s: "Plaintiffs' MSJ on Choice-of-Law",
          },
          { d: "2648", y: "NMD MSJ", p: 1, f: "12/15/2021", s: "NMD MSJ" },
          { d: "2650", y: "Bowen MSJ", p: 1, f: "12/15/2021", s: "Bowen MSJ" },
          {
            d: "2653",
            y: "Monsees MSJ",
            p: 1,
            f: "12/15/2021",
            s: "Monsees MSJ re Bain SAC",
          },
          {
            d: "2654",
            y: "Altria MSJ",
            p: 1,
            f: "12/15/2021",
            s: "Altria MSJ",
          },
          {
            d: "2784",
            y: "Opp to Altria MSJ",
            p: 0,
            f: "01/21/2022",
            s: "Plaintiffs",
          },
          { d: "2787", y: "Opp to Pl MSJ", p: 0, f: "01/21/2022", s: "Altria" },
          { d: "2788", y: "Opp to Pl MSJ", p: 0, f: "01/21/2022", s: "JLI" },
          {
            d: "2791",
            y: "Opp to JLI MSJ",
            p: 0,
            f: "01/21/2022",
            s: "Plaintiffs",
          },
          { d: "2825", y: "Reply", p: 0, f: "02/04/2022", s: "Plaintiffs" },
          { d: "2826", y: "Reply", p: 0, f: "02/04/2022", s: "Plaintiffs" },
          {
            d: "2831/2832",
            y: "Mot to Strike Decls",
            p: 0,
            f: "02/04-05/2022",
            s: "JLI MTS B.B. Declarations",
          },
          { d: "2838", y: "Order", p: 0, f: "02/07/2022", s: "Granting 2832" },
          { d: "2839", y: "Order", p: 0, f: "02/07/2022", s: "Granting 2835" },
          {
            d: "2842",
            y: "Reply (Altria MSJ)",
            p: 0,
            f: "02/07/2022",
            s: "Altria",
          },
          {
            d: "3083",
            y: "Order",
            p: 1,
            f: "04/29/2022",
            s: "Order on MSJs re B.B.",
          },
        ],
      },
      {
        id: "N2",
        t: "B.B. Bellwether Trial Motions in Limine",
        docs: [
          {
            d: "2945",
            y: "Motion in Limine",
            p: 1,
            f: "03/21/2022",
            s: "Plaintiff B.B. MIL (Redacted)",
          },
          {
            d: "2950",
            y: "Defendants' MIL",
            p: 1,
            f: "03/21/2022",
            s: "JLI MIL",
          },
          {
            d: "3021",
            y: "Opp to Defs MIL",
            p: 0,
            f: "04/08/2022",
            s: "Plaintiffs (Redacted)",
          },
          { d: "3025", y: "Opp to Pl MIL", p: 0, f: "04/08/2022", s: "JLI" },
        ],
      },
      {
        id: "N3",
        t: "B.B. Bellwether Trial - Other Pretrial Motions",
        docs: [
          {
            d: "3093",
            y: "Mot to Play Depo Designations",
            p: 1,
            f: "04/29/2022",
            s: "Plaintiffs",
          },
          { d: "3147", y: "Opposition", p: 0, f: "05/05/2022", s: "JLI" },
          { d: "3159", y: "Reply", p: 0, f: "05/06/2022", s: "Plaintiffs" },
          {
            d: "3106",
            y: "Mot to Compel",
            p: 1,
            f: "04/29/2022",
            s: "Plaintiffs Mot to Compel Augustson File",
          },
          {
            d: "3109",
            y: "Mot to Shorten",
            p: 0,
            f: "04/29/2022",
            s: "Plaintiffs",
          },
          {
            d: "3129",
            y: "Stipulation",
            p: 0,
            f: "05/03/2022",
            s: "Stip resolving 3061, 3106",
          },
          { d: "3130", y: "Order", p: 0, f: "05/03/2022", s: "Granting 3129" },
          { d: "3131", y: "Order", p: 0, f: "05/03/2022", s: "Mooting 3106" },
          { d: "3132", y: "Order", p: 0, f: "05/03/2022", s: "Mooting 3109" },
          {
            d: "3125",
            y: "Mot to Compel Compliance",
            p: 1,
            f: "05/03/2022",
            s: "JLI MTC Compliance with Pretrial Order",
          },
          { d: "3126", y: "Mot to Expedite", p: 0, f: "05/03/2022", s: "JLI" },
          { d: "3139", y: "Order", p: 0, f: "05/04/2022", s: "Granting 3126" },
          {
            d: "3163",
            y: "Opposition",
            p: 0,
            f: "05/06/2022",
            s: "Plaintiffs",
          },
          {
            d: "3203",
            y: "Suppl. Brief",
            p: 0,
            f: "05/16/2022",
            s: "Plaintiffs Re MIL #17 (PMI)",
          },
          {
            d: "3236",
            y: "Stipulation",
            p: 0,
            f: "05/21/2022",
            s: "Briefing on MIL re PMI",
          },
          { d: "3238", y: "Order", p: 0, f: "05/23/2022", s: "Re MIL Rulings" },
        ],
      },
      {
        id: "N4",
        t: "B.B. Settlement / Minor's Compromise",
        docs: [
          {
            d: "3701",
            y: "Mot to Seal",
            p: 1,
            f: "12/12/2022",
            s: "Petition for Court Approval of Minor's Compromise",
          },
          { d: "3716", y: "Order", p: 1, f: "12/19/2022", s: "Granting 3701" },
        ],
      },
    ],
  },
  {
    id: "secO",
    l: "O",
    n: "Pesce Bellwether Case (2022)",
    threads: [
      {
        id: "O1",
        t: "Pesce MSJ & Daubert",
        docs: [
          {
            d: "2953",
            y: "Pl Mot to Exclude Steinberg",
            p: 1,
            f: "03/22/2022",
            s: "Pesce",
          },
          {
            d: "2956",
            y: "Pl Partial MSJ",
            p: 1,
            f: "03/22/2022",
            s: "Pesce - Affirmative Defenses",
          },
          { d: "2959", y: "NMD MSJ", p: 1, f: "03/22/2022", s: "NMD" },
          {
            d: "2960",
            y: "JLI Daubert",
            p: 1,
            f: "03/22/2022",
            s: "JLI Mot to Exclude Pesce Case-Specific Opinions",
          },
          { d: "2963", y: "Bowen MSJ", p: 1, f: "03/22/2022", s: "Bowen" },
          { d: "2966", y: "Monsees MSJ", p: 1, f: "03/22/2022", s: "Monsees" },
          { d: "2967", y: "JLI MSJ", p: 1, f: "03/22/2022", s: "JLI" },
          {
            d: "2968",
            y: "Altria Daubert",
            p: 1,
            f: "03/22/2022",
            s: "Altria Mot Re Pl Experts",
          },
          {
            d: "2971",
            y: "Altria MSJ",
            p: 1,
            f: "03/22/2022",
            s: "Altria MSJ",
          },
          {
            d: "3072",
            y: "Opp to JLI Daubert",
            p: 1,
            f: "04/28/2022",
            s: "Pesce",
          },
          {
            d: "3075",
            y: "Opp to Altria Daubert",
            p: 1,
            f: "04/28/2022",
            s: "Pesce",
          },
          {
            d: "3077",
            y: "Opp to Pl MSJ (NMD)",
            p: 1,
            f: "04/29/2022",
            s: "NMD",
          },
          {
            d: "3079",
            y: "Opp to Pl MSJ (Bowen)",
            p: 1,
            f: "04/29/2022",
            s: "Bowen",
          },
          {
            d: "3080",
            y: "Opp to Bowen MSJ",
            p: 1,
            f: "04/29/2022",
            s: "Pesce",
          },
          {
            d: "3084",
            y: "Opp to Monsees MSJ",
            p: 1,
            f: "04/29/2022",
            s: "Pesce",
          },
          {
            d: "3086",
            y: "Opp to Pl MSJ (Altria)",
            p: 1,
            f: "04/29/2022",
            s: "Altria",
          },
          { d: "3088", y: "Opp to NMD MSJ", p: 1, f: "04/29/2022", s: "Pesce" },
          {
            d: "3090",
            y: "Opp to Pl MSJ (Monsees)",
            p: 1,
            f: "04/29/2022",
            s: "Monsees",
          },
          { d: "3091", y: "Opp to JLI MSJ", p: 1, f: "04/29/2022", s: "Pesce" },
          {
            d: "3177",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "Pesce ISO Mot Exclude Steinberg",
          },
          {
            d: "3181",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "Pesce ISO Partial MSJ",
          },
          { d: "3184", y: "Reply", p: 0, f: "05/13/2022", s: "NMD ISO 2959" },
          { d: "3188", y: "Reply", p: 0, f: "05/13/2022", s: "Bowen ISO 2963" },
          {
            d: "3190",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "Monsees ISO 2966",
          },
          {
            d: "3191",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "JLI ISO 2960 (Daubert)",
          },
          {
            d: "3193",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "JLI ISO 2967 (MSJ)",
          },
          {
            d: "3194",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "Altria ISO 2968 (Daubert)",
          },
          {
            d: "3195",
            y: "Reply",
            p: 0,
            f: "05/13/2022",
            s: "Altria ISO 2971 (MSJ)",
          },
        ],
      },
    ],
  },
  {
    id: "secP",
    l: "P",
    n: "SFUSD Bellwether Trial (2022-2023)",
    threads: [
      {
        id: "P1",
        t: "SFUSD MSJ",
        docs: [
          {
            d: "3384",
            y: "NMD MSJ",
            p: 1,
            f: "08/15/2022",
            s: "NMD MSJ vs SFUSD",
          },
          {
            d: "3388",
            y: "NMD Daubert",
            p: 1,
            f: "08/15/2022",
            s: "NMD Daubert",
          },
          {
            d: "3394",
            y: "Bowen MSJ",
            p: 1,
            f: "08/15/2022",
            s: "Bowen MSJ vs SFUSD",
          },
          { d: "3396", y: "JLI MSJ", p: 1, f: "08/15/2022", s: "JLI MSJ" },
          {
            d: "3400",
            y: "Altria Daubert",
            p: 1,
            f: "08/15/2022",
            s: "Altria Daubert",
          },
          {
            d: "3401",
            y: "Monsees MSJ",
            p: 1,
            f: "08/15/2022",
            s: "Monsees MSJ",
          },
          {
            d: "3405",
            y: "Altria MSJ",
            p: 1,
            f: "08/15/2022",
            s: "Altria MSJ",
          },
          {
            d: "3408",
            y: "JLI Daubert",
            p: 1,
            f: "08/15/2022",
            s: "JLI Daubert (Abatement Experts)",
          },
          {
            d: "3409",
            y: "Joinder",
            p: 0,
            f: "08/16/2022",
            s: "Monsees Joinder in JLI Daubert + NMD Daubert",
          },
          {
            d: "3447",
            y: "Opp to MSJ",
            p: 1,
            f: "09/12/2022",
            s: "SFUSD Consolidated Opp (Redacted)",
          },
          {
            d: "3458",
            y: "Opp to Daubert (Omnibus)",
            p: 1,
            f: "09/12/2022",
            s: "SFUSD",
          },
          {
            d: "3460",
            y: "Pl Daubert (Mefford)",
            p: 1,
            f: "09/12/2022",
            s: "SFUSD Mot to Exclude Mefford",
          },
          {
            d: "3462",
            y: "JLI Daubert",
            p: 1,
            f: "09/12/2022",
            s: "JLI Mot Re Surveillance Experts",
          },
          { d: "3552", y: "Pl MIL", p: 1, f: "10/03/2022", s: "SFUSD MIL" },
          { d: "3556", y: "Defs MIL", p: 1, f: "10/03/2022", s: "JLI MIL" },
          {
            d: "3911",
            y: "Order",
            p: 1,
            f: "04/10/2023",
            s: "Order on MSJs (3384, 3394, 3396, 3401, 3405, 3807)",
          },
        ],
      },
      {
        id: "P2",
        t: "SFUSD MIL Plaintiff (Flora)",
        docs: [
          {
            d: "3927",
            y: "Motion",
            p: 1,
            f: "04/19/2023",
            s: "Plaintiffs Mot to Exclude Flora",
          },
          { d: "3982", y: "Opposition", p: 0, f: "04/28/2023", s: "Altria" },
        ],
      },
      {
        id: "P3",
        t: "SFUSD Trial-Time Motions",
        docs: [
          { d: "3788", y: "MIL", p: 1, f: "02/10/2023", s: "SFUSD" },
          { d: "3790", y: "MIL", p: 1, f: "02/10/2023", s: "Altria" },
          { d: "3807", y: "Suppl. MSJ", p: 1, f: "02/17/2023", s: "Altria" },
          {
            d: "3821",
            y: "Opp to Defs MIL",
            p: 0,
            f: "02/28/2023",
            s: "SFUSD",
          },
          { d: "3824", y: "Opp to Pl MIL", p: 0, f: "02/28/2023", s: "Altria" },
          {
            d: "3830",
            y: "Opp to Suppl MSJ",
            p: 0,
            f: "03/03/2023",
            s: "SFUSD",
          },
          {
            d: "3831",
            y: "Pl MIL",
            p: 1,
            f: "03/03/2023",
            s: "Plaintiffs Mot to Exclude Post-Discovery Acts",
          },
          {
            d: "3835",
            y: "Mot to Quash Subpoena",
            p: 1,
            f: "03/08/2023",
            s: "Pritzker",
          },
          {
            d: "3854",
            y: "Mot to Strike Grunberg",
            p: 1,
            f: "03/16/2023",
            s: "Altria MTS Suppl Grunberg Report",
          },
          {
            d: "3866",
            y: "Mot to Strike Prochaska",
            p: 1,
            f: "03/22/2023",
            s: "Altria",
          },
          {
            d: "3875",
            y: "Suppl Brief",
            p: 0,
            f: "03/24/2023",
            s: "SFUSD Re Nicotine Disclosures",
          },
          { d: "3884", y: "Reply MIL", p: 0, f: "03/28/2023", s: "Plaintiffs" },
          {
            d: "3886",
            y: "Opposition",
            p: 0,
            f: "03/28/2023",
            s: "Plaintiffs",
          },
          { d: "3893", y: "Reply", p: 0, f: "03/31/2023", s: "Altria" },
          {
            d: "3890",
            y: "Opp Prochaska MTS",
            p: 0,
            f: "03/31/2023",
            s: "SFUSD",
          },
          {
            d: "3896",
            y: "Reply Prochaska MTS",
            p: 0,
            f: "04/03/2023",
            s: "Altria",
          },
          {
            d: "3911",
            y: "Order Re MIL & SJ",
            p: 1,
            f: "04/10/2023",
            s: "Order on MSJs and Remaining MILs",
          },
          {
            d: "3914",
            y: "Mot to Strike Interrog",
            p: 1,
            f: "04/11/2023",
            s: "Altria MTS Pl Suppl Resp Interrog 17",
          },
          { d: "3921", y: "Opposition", p: 0, f: "04/17/2023", s: "SFUSD" },
          {
            d: "3972",
            y: "Mot for Mistrial",
            p: 1,
            f: "04/27/2023",
            s: "Altria",
          },
          {
            d: "4024",
            y: "MJOL",
            p: 1,
            f: "05/09/2023",
            s: "Altria Mot for JMOL",
          },
        ],
      },
    ],
  },
  {
    id: "secQ",
    l: "Q",
    n: "Class Action Settlements",
    threads: [
      {
        id: "Q1",
        t: "JLI Class Settlement",
        docs: [
          {
            d: "3722",
            y: "Mot to Seal",
            p: 1,
            f: "12/19/2022",
            s: "Appendix C to Class Settlement",
          },
          {
            d: "3724",
            y: "Motion for Prelim. Approval",
            p: 1,
            f: "12/19/2022",
            s: "Plaintiffs",
          },
          {
            d: "3726",
            y: "Clerk's Notice",
            p: 0,
            f: "12/21/2022",
            s: "Hearing scheduled",
          },
          {
            d: "3733",
            y: "Mot to Compel Settlement",
            p: 1,
            f: "01/04/2023",
            s: "Altria Mot to Compel Settlement Documents",
          },
          {
            d: "3734",
            y: "Mot to Stay",
            p: 1,
            f: "01/04/2023",
            s: "Altria Mot to Stay Pending Cert Appeal",
          },
          {
            d: "3741",
            y: "Opp to Prelim. Approval",
            p: 0,
            f: "01/06/2023",
            s: "Altria",
          },
          { d: "3745", y: "Reply", p: 0, f: "01/10/2023", s: "Plaintiffs" },
          {
            d: "3779",
            y: "Order Granting Prelim. Approval",
            p: 1,
            f: "01/30/2023",
            s: "",
          },
          {
            d: "3781",
            y: "Notice",
            p: 0,
            f: "02/01/2023",
            s: "Plaintiffs Setting Deadlines",
          },
          {
            d: "4054",
            y: "Mot for Final Approval",
            p: 1,
            f: "06/23/2023",
            s: "Plaintiffs",
          },
          {
            d: "4055",
            y: "Mot for Atty Fees",
            p: 1,
            f: "06/23/2023",
            s: "Plaintiffs",
          },
          { d: "4062", y: "Objection", p: 0, f: "07/14/2023", s: "Murphy" },
          {
            d: "4075",
            y: "Notice of Objections",
            p: 0,
            f: "07/21/2023",
            s: "Plaintiffs",
          },
          { d: "4091", y: "Reply", p: 0, f: "07/29/2023", s: "Plaintiffs" },
          {
            d: "4138",
            y: "Order Final Approval",
            p: 1,
            f: "09/19/2023",
            s: "",
          },
          { d: "4179", y: "Order on Atty Fees", p: 1, f: "12/18/2023", s: "" },
        ],
      },
      {
        id: "Q2",
        t: "Altria Class Settlement",
        docs: [
          {
            d: "4080",
            y: "Mot to Seal",
            p: 1,
            f: "07/27/2023",
            s: "Appendix A to Altria Settlement",
          },
          {
            d: "4082",
            y: "Motion for Prelim. Approval",
            p: 1,
            f: "07/27/2023",
            s: "Plaintiffs",
          },
          {
            d: "4109",
            y: "Clerk's Notice",
            p: 0,
            f: "08/10/2023",
            s: "Hearing",
          },
          { d: "4126", y: "Minute Entry", p: 0, f: "09/06/2023", s: "Hearing" },
          {
            d: "4130",
            y: "Order Granting Prelim. Approval",
            p: 1,
            f: "09/07/2023",
            s: "",
          },
          {
            d: "4131",
            y: "Notice",
            p: 0,
            f: "09/08/2023",
            s: "Plaintiffs Setting Deadlines",
          },
          {
            d: "4191",
            y: "Mot for Final Approval",
            p: 1,
            f: "01/16/2024",
            s: "Plaintiffs",
          },
          {
            d: "4192",
            y: "Mot for Atty Fees",
            p: 1,
            f: "01/16/2024",
            s: "Plaintiffs",
          },
          { d: "4198", y: "Reply", p: 0, f: "02/20/2024", s: "Plaintiffs" },
          { d: "4200", y: "Objection", p: 0, f: "03/05/2024", s: "Madrigal" },
          {
            d: "4214",
            y: "Mot for Atty Fees (Tribal)",
            p: 1,
            f: "04/09/2024",
            s: "Fee Committee",
          },
          { d: "4238", y: "Order on Atty Fees", p: 1, f: "05/15/2024", s: "" },
        ],
      },
      {
        id: "Q3",
        t: "Settlement Disbursements",
        docs: [
          {
            d: "4325",
            y: "Mot for Disbursement",
            p: 1,
            f: "10/04/2024",
            s: "Plaintiffs",
          },
          { d: "4326", y: "Order", p: 0, f: "10/08/2024", s: "Granting 4325" },
          {
            d: "4344",
            y: "Mot for Disbursement",
            p: 1,
            f: "12/02/2024",
            s: "Plaintiffs (Status Update)",
          },
          { d: "4350", y: "Order", p: 0, f: "12/06/2024", s: "Granting 4344" },
          {
            d: "4390",
            y: "Mot for Disbursement",
            p: 1,
            f: "02/28/2025",
            s: "Plaintiffs",
          },
          { d: "4394", y: "Order", p: 0, f: "03/21/2025", s: "Granting 4390" },
          {
            d: "4444",
            y: "Mot for Disbursement",
            p: 1,
            f: "10/20/2025",
            s: "Plaintiffs",
          },
          {
            d: "4487",
            y: "Mot for Disbursement",
            p: 1,
            f: "02/26/2026",
            s: "Plaintiffs",
          },
          { d: "4489", y: "Order", p: 0, f: "03/06/2026", s: "Granting 4487" },
        ],
      },
      {
        id: "Q4",
        t: "Fee Committee Motions",
        docs: [
          {
            d: "4048",
            y: "Motion to Appoint Fee Committee",
            p: 1,
            f: "06/09/2023",
            s: "Plaintiffs",
          },
          {
            d: "4119",
            y: "Admin Mot to Extend",
            p: 0,
            f: "08/29/2023",
            s: "Plaintiffs",
          },
          { d: "4122", y: "Order", p: 0, f: "09/05/2023", s: "Granting 4119" },
          {
            d: "4152",
            y: "Mot for Atty Fees (JLI)",
            p: 1,
            f: "11/12/2023",
            s: "Plaintiffs Fee Committee Recs",
          },
          { d: "4156", y: "Opposition", p: 0, f: "11/17/2023", s: "Stephens" },
          {
            d: "4159",
            y: "Opposition",
            p: 0,
            f: "11/27/2023",
            s: "Plaintiffs",
          },
          { d: "4163", y: "Reply", p: 0, f: "12/04/2023", s: "Plaintiffs" },
          {
            d: "4167",
            y: "Order to Show Cause",
            p: 0,
            f: "12/07/2023",
            s: "Re Sealing",
          },
          { d: "4179", y: "Order on Atty Fees", p: 1, f: "12/18/2023", s: "" },
          {
            d: "4320",
            y: "Mot for Atty Fees (Tribal)",
            p: 1,
            f: "09/27/2024",
            s: "Fee Committee",
          },
          { d: "4337", y: "Order", p: 0, f: "11/05/2024", s: "Granting 4320" },
          {
            d: "4443",
            y: "Mot for Atty Fees (3rd Suppl)",
            p: 1,
            f: "10/06/2025",
            s: "Fee Committee",
          },
        ],
      },
    ],
  },
  {
    id: "secR",
    l: "R",
    n: "Discovery & Other Motions (Various)",
    threads: [
      {
        id: "R1",
        t: "Mot for Issuance of Subpoena (Dunlap)",
        docs: [
          {
            d: "1791",
            y: "Mot to Seal",
            p: 0,
            f: "05/03/2021",
            s: "Plaintiffs",
          },
          {
            d: "1792",
            y: "Motion",
            p: 1,
            f: "05/03/2021",
            s: "Plaintiffs Mot for Subpoena Under 28 USC 1783",
          },
          { d: "1875", y: "Opposition", p: 0, f: "05/17/2021", s: "Dunlap" },
          { d: "1899", y: "Reply", p: 0, f: "05/24/2021", s: "Plaintiffs" },
          { d: "1908", y: "Order", p: 0, f: "05/25/2021", s: "Granting 1792" },
          { d: "1914", y: "Notice", p: 0, f: "05/26/2021", s: "Plaintiffs" },
        ],
      },
      {
        id: "R2",
        t: "Mot for Protective Order (Colgate Subpoenas)",
        docs: [
          {
            d: "1968",
            y: "Motion",
            p: 1,
            f: "06/11/2021",
            s: "Colgate Mot for Protective Order",
          },
          { d: "1969", y: "Mot to Seal", p: 0, f: "06/11/2021", s: "" },
        ],
      },
      {
        id: "R3",
        t: "JLI Mot to Stay FDA Communications Order",
        docs: [
          {
            d: "2017",
            y: "Notice (Non-Compliance)",
            p: 0,
            f: "06/22/2021",
            s: "Plaintiffs",
          },
          {
            d: "2020",
            y: "Motion",
            p: 1,
            f: "06/23/2021",
            s: "JLI Ex Parte Mot to Stay",
          },
          {
            d: "2022",
            y: "Order",
            p: 0,
            f: "06/24/2021",
            s: "Granting Limited Stay",
          },
          {
            d: "2035",
            y: "Mot for Reconsideration",
            p: 1,
            f: "06/28/2021",
            s: "JLI",
          },
          {
            d: "2037",
            y: "Opposition",
            p: 0,
            f: "06/29/2021",
            s: "Plaintiffs",
          },
        ],
      },
      {
        id: "R4",
        t: "Joint Letter Briefs / Discovery Disputes",
        docs: [
          {
            d: "1438",
            y: "Mot to Seal",
            p: 0,
            f: "02/19/2021",
            s: "Joint Letter Brief Re Rule 35 Exams",
          },
          {
            d: "1691",
            y: "Mot to Seal",
            p: 0,
            f: "04/12/2021",
            s: "Letter Brief re Gifford Deposition",
          },
          {
            d: "1759",
            y: "Mot to Seal",
            p: 0,
            f: "04/26/2021",
            s: "Altria re Letter Brief",
          },
          { d: "1773", y: "Order", p: 0, f: "04/29/2021", s: "On 1691, 1759" },
          {
            d: "1979",
            y: "Mot to Seal",
            p: 0,
            f: "06/14/2021",
            s: "Plaintiffs Letter Brief Re Privilege Logs",
          },
          {
            d: "1980",
            y: "Mot to Seal",
            p: 0,
            f: "06/14/2021",
            s: "Plaintiffs Re PMTA Discovery",
          },
          {
            d: "1981",
            y: "Mot to Seal",
            p: 0,
            f: "06/14/2021",
            s: "Joint Re PI Bellwether Trial Candidates",
          },
          {
            d: "2050",
            y: "Order on Sealing",
            p: 0,
            f: "07/06/2021",
            s: "Re 1772, 1981",
          },
          {
            d: "2096",
            y: "Mot to Seal",
            p: 0,
            f: "07/13/2021",
            s: "JLI Memo ISO 1979",
          },
          { d: "2129", y: "Order", p: 0, f: "07/19/2021", s: "Re 1979" },
        ],
      },
      {
        id: "R5",
        t: "Discovery Mot for Supplemental Fact Discovery",
        docs: [
          { d: "3011", y: "Motion", p: 1, f: "04/04/2022", s: "JLI" },
          { d: "3013", y: "Mot to Seal", p: 0, f: "04/04/2022", s: "JLI" },
        ],
      },
      {
        id: "R6",
        t: "JLI Mot to Compel Augustson File / Mot to Shorten",
        docs: [
          {
            d: "3106",
            y: "Mot to Compel",
            p: 1,
            f: "04/29/2022",
            s: "Plaintiffs",
          },
          {
            d: "3109",
            y: "Mot to Shorten",
            p: 0,
            f: "04/29/2022",
            s: "Plaintiffs",
          },
          {
            d: "3129",
            y: "Stipulation",
            p: 0,
            f: "05/03/2022",
            s: "Resolving",
          },
          { d: "3131", y: "Order", p: 0, f: "05/03/2022", s: "Mooting" },
          { d: "3132", y: "Order", p: 0, f: "05/03/2022", s: "Mooting" },
        ],
      },
      {
        id: "R7",
        t: "Privacy Objection Discovery Dispute",
        docs: [
          { d: "4372", y: "Mot to Seal", p: 1, f: "01/22/2025", s: "NMD" },
          { d: "4373", y: "Order", p: 0, f: "01/23/2025", s: "Granting 4372" },
          { d: "4381", y: "Mot to Seal", p: 0, f: "02/04/2025", s: "NMD Stmt" },
          { d: "4382", y: "Order", p: 0, f: "02/05/2025", s: "Granting 4381" },
          { d: "4383", y: "Mot to Seal", p: 0, f: "02/11/2025", s: "NMD" },
          { d: "4384", y: "Order", p: 0, f: "02/12/2025", s: "Granting 4383" },
        ],
      },
      {
        id: "R8",
        t: "NMD Response to 1/28/25 Order",
        docs: [
          {
            d: "4392",
            y: "Response",
            p: 1,
            f: "03/19/2025",
            s: "NMD Re Sealing",
          },
        ],
      },
    ],
  },
  {
    id: "secS",
    l: "S",
    n: "Sealing Resolution Orders (2024-2025)",
    threads: [
      {
        id: "S1",
        t: "Final Sealing Orders",
        docs: [
          {
            d: "4293",
            y: "Order Re Sealing",
            p: 1,
            f: "09/06/2024",
            s: "Re B.B. Bellwether Sealing Mots",
          },
          { d: "4294", y: "Order", p: 0, f: "09/06/2024", s: "Granting 3246" },
          {
            d: "4295",
            y: "Order",
            p: 0,
            f: "09/06/2024",
            s: "Re Pesce/SFUSD Mots to Seal",
          },
          { d: "4296", y: "Order", p: 0, f: "09/12/2024", s: "Granting 3889" },
          { d: "4297", y: "Order", p: 0, f: "09/12/2024", s: "Granting 3888" },
          { d: "4298", y: "Order", p: 0, f: "09/12/2024", s: "Granting 4133" },
          {
            d: "4299",
            y: "Order",
            p: 1,
            f: "09/13/2024",
            s: "Re MSJ/MIL/SFUSD Sealing",
          },
          {
            d: "4322",
            y: "Stipulation",
            p: 0,
            f: "09/30/2024",
            s: "Extension of Time",
          },
          {
            d: "4329",
            y: "Stipulation",
            p: 0,
            f: "10/28/2024",
            s: "Further Extension",
          },
          { d: "4330", y: "Order", p: 0, f: "10/29/2024", s: "Granting 4329" },
          {
            d: "4343",
            y: "Stipulation",
            p: 0,
            f: "11/30/2024",
            s: "Further Extension",
          },
          {
            d: "4374",
            y: "Final Order Re Sealing",
            p: 1,
            f: "01/28/2025",
            s: "",
          },
          {
            d: "4376",
            y: "Order",
            p: 0,
            f: "01/28/2025",
            s: "Granting 3868 in part",
          },
          { d: "4377", y: "Order", p: 0, f: "01/28/2025", s: "Granting 3867" },
          { d: "4378", y: "Order", p: 0, f: "01/28/2025", s: "Granting 3837" },
          { d: "4379", y: "Order", p: 0, f: "01/28/2025", s: "Granting 3836" },
          { d: "4380", y: "Order", p: 0, f: "01/28/2025", s: "Granting 3972" },
          {
            d: "4385",
            y: "Notice of Unsealing",
            p: 0,
            f: "02/12/2025",
            s: "Plaintiffs",
          },
          {
            d: "4386",
            y: "Admin Mot for Extension",
            p: 0,
            f: "02/13/2025",
            s: "NMD",
          },
          { d: "4387", y: "Order", p: 0, f: "02/14/2025", s: "Granting 4386" },
          {
            d: "4391",
            y: "Notice of Unsealing",
            p: 0,
            f: "03/17/2025",
            s: "JLI",
          },
          {
            d: "4393",
            y: "Notice of Unsealing",
            p: 0,
            f: "03/19/2025",
            s: "Altria",
          },
        ],
      },
    ],
  },
  {
    id: "secT",
    l: "T",
    n: "Modifications & Withdrawal Motions (Various)",
    threads: [
      {
        id: "T1",
        t: "Mot to Modify CMOs",
        docs: [
          {
            d: "4316",
            y: "Motion",
            p: 1,
            f: "09/25/2024",
            s: "Plaintiffs Mot to Modify CMO 5, 5(A), Special Master Order",
          },
          { d: "4332", y: "Minute Entry", p: 0, f: "10/30/2024", s: "Hearing" },
        ],
      },
      {
        id: "T2",
        t: "Various Withdrawal Motions",
        docs: [
          {
            d: "3366",
            y: "Motion",
            p: 1,
            f: "08/01/2022",
            s: "JLI 2nd Mot to Withdraw",
          },
          {
            d: "3382",
            y: "Motion",
            p: 1,
            f: "08/12/2022",
            s: "Altria Mot to Withdraw (Delligatti)",
          },
          {
            d: "4124",
            y: "Motion",
            p: 1,
            f: "09/06/2023",
            s: "McLane Mot to Withdraw",
          },
          { d: "4142", y: "Order", p: 0, f: "10/16/2023", s: "Granting 4124" },
          {
            d: "4442",
            y: "Motion",
            p: 1,
            f: "09/23/2025",
            s: "Reilly Stephens Mot to Withdraw (Hedley)",
          },
          { d: "4464", y: "Order", p: 0, f: "12/09/2025", s: "Granting 4442" },
        ],
      },
      {
        id: "T3",
        t: "Mot to Substitute (Russell-Smith / NesSmith)",
        docs: [
          {
            d: "965",
            y: "Motion",
            p: 1,
            f: "09/14/2020",
            s: "Russell-Smith Mot to Substitute",
          },
          { d: "966", y: "Proposed Order", p: 0, f: "09/14/2020", s: "" },
          {
            d: "4328",
            y: "Motion",
            p: 1,
            f: "10/25/2024",
            s: "NesSmith Mot to Substitute & Amend",
          },
          { d: "4340", y: "Reply", p: 0, f: "11/15/2024", s: "NesSmith" },
        ],
      },
      {
        id: "T4",
        t: "CMO 20 (Minor's Compromise)",
        docs: [
          {
            d: "4221",
            y: "Admin Motion",
            p: 1,
            f: "05/02/2024",
            s: "Plaintiffs",
          },
          {
            d: "4222",
            y: "Order",
            p: 0,
            f: "05/03/2024",
            s: "Granting 4221 (CMO 20)",
          },
        ],
      },
      {
        id: "T5",
        t: "CMO 21 (Settlement Administration)",
        docs: [
          {
            d: "4223",
            y: "Admin Motion",
            p: 1,
            f: "05/05/2024",
            s: "Plaintiffs",
          },
        ],
      },
      {
        id: "T6",
        t: "Schedule Modifications (Opt-Outs)",
        docs: [
          {
            d: "4348",
            y: "Stipulation",
            p: 1,
            f: "(referenced)",
            s: "Schedule",
          },
          { d: "4354", y: "Order", p: 0, f: "12/09/2024", s: "Granting 4348" },
          {
            d: "4370",
            y: "Stipulation",
            p: 1,
            f: "(referenced)",
            s: "Schedule",
          },
          { d: "4375", y: "Order", p: 0, f: "01/28/2025", s: "Granting 4370" },
          {
            d: "4417",
            y: "Stipulation",
            p: 1,
            f: "05/16/2025",
            s: "Extension of Time for Dispositive Motions",
          },
          { d: "4420", y: "Order", p: 0, f: "05/21/2025", s: "Granting 4417" },
          {
            d: "4426",
            y: "Stipulation",
            p: 1,
            f: "07/10/2025",
            s: "Re Schedule (Dupree, Tortorici, Shapiro)",
          },
          {
            d: "4434",
            y: "Stipulation",
            p: 1,
            f: "(referenced)",
            s: "Briefing Schedule",
          },
          { d: "4435", y: "Order", p: 0, f: "08/25/2025", s: "Granting 4434" },
          {
            d: "4484",
            y: "Mot to Extend Discovery",
            p: 1,
            f: "02/17/2026",
            s: "Plaintiffs",
          },
          { d: "4486", y: "Opposition", p: 0, f: "02/24/2026", s: "JLI" },
          {
            d: "4488",
            y: "Order",
            p: 0,
            f: "02/26/2026",
            s: "Re Mot to Extend",
          },
          {
            d: "4490",
            y: "Notice of Compliance",
            p: 0,
            f: "03/19/2026",
            s: "Plaintiffs",
          },
        ],
      },
    ],
  },
];
const MUSK_THREADS = [
  {
    id: "secA",
    l: "A",
    n: "Case Initiation & Early Filings",
    threads: [
      {
        id: "A1",
        t: "Complaint & Service",
        docs: [
          {
            d: "1",
            y: "Complaint",
            p: 1,
            f: "08/05/2024",
            s: "RICO Complaint filed by Elon Musk against Samuel Altman, Gregory Brockman, OpenAI entities",
          },
          {
            d: "2",
            y: "Certificate of Interested Entities",
            p: 0,
            f: "08/05/2024",
            s: "Filed by Elon Musk",
          },
          {
            d: "3",
            y: "Proposed Summons",
            p: 0,
            f: "08/05/2024",
            s: "Filed by Elon Musk",
          },
          {
            d: "5",
            y: "Summons Issued",
            p: 0,
            f: "08/06/2024",
            s: "Summons issued re all defendants",
          },
          {
            d: "7",
            y: "Consent/Declination",
            p: 0,
            f: "08/06/2024",
            s: "Plaintiff declines Magistrate Judge jurisdiction",
          },
          {
            d: "9",
            y: "Order Reassigning Case",
            p: 0,
            f: "08/07/2024",
            s: "Case reassigned to Judge Yvonne Gonzalez Rogers",
          },
          {
            d: "12",
            y: "Waiver of Service",
            p: 0,
            f: "08/22/2024",
            s: "Service waived by all OpenAI defendants; answer due 10/04/2024",
          },
          {
            d: "13",
            y: "Notice of Appearance",
            p: 0,
            f: "09/06/2024",
            s: "Jordan Eth appears for all OpenAI defendants",
          },
          {
            d: "17",
            y: "Corporate Disclosure Statement",
            p: 0,
            f: "09/06/2024",
            s: "OpenAI defendants' corporate disclosures",
          },
        ],
      },
    ],
  },
  {
    id: "secB",
    l: "B",
    n: "First Motion to Dismiss (Original Complaint)",
    threads: [
      {
        id: "B1",
        t: "OpenAI MTD Original Complaint",
        docs: [
          {
            d: "25",
            y: "Motion to Dismiss",
            p: 1,
            f: "10/08/2024",
            s: "OpenAI Defendants' Motion to Dismiss Complaint of Plaintiff Elon Musk",
          },
          {
            d: "26",
            y: "Request for Judicial Notice",
            p: 0,
            f: "10/08/2024",
            s: "ISO Motion to Dismiss",
          },
          {
            d: "27",
            y: "Order Directing Joint Statement",
            p: 0,
            f: "10/11/2024",
            s: "Court vacates 11/12 hearing; schedules CMC for 12/16/2024",
          },
          {
            d: "28",
            y: "Stipulation re Briefing",
            p: 0,
            f: "10/14/2024",
            s: "Stip regarding briefing schedule",
          },
          {
            d: "29",
            y: "Order Granting Stip",
            p: 0,
            f: "10/15/2024",
            s: "Granting 28 stipulation",
          },
          {
            d: "30",
            y: "Joint Statement",
            p: 0,
            f: "10/16/2024",
            s: "Joint Statement Outlining Elements of Claims",
          },
          {
            d: "38",
            y: "Order Denying MTD as Moot",
            p: 0,
            f: "11/19/2024",
            s: "Denying 24 Motion to Intervene; Denying as Moot 25 MTD",
          },
        ],
      },
      {
        id: "B2",
        t: "Motion to Intervene (Horowitz)",
        docs: [
          {
            d: "24",
            y: "Motion to Intervene",
            p: 1,
            f: "10/08/2024",
            s: "Leonard G. Horowitz Motion to Intervene",
          },
          {
            d: "31",
            y: "Opposition",
            p: 0,
            f: "10/22/2024",
            s: "Joint Opposition filed by Elon Musk",
          },
          {
            d: "38",
            y: "Order Denying",
            p: 0,
            f: "11/19/2024",
            s: "Motion to Intervene DENIED",
          },
        ],
      },
    ],
  },
  {
    id: "secC",
    l: "C",
    n: "First Amended Complaint & New Defendants",
    threads: [
      {
        id: "C1",
        t: "FAC Filing",
        docs: [
          {
            d: "32",
            y: "First Amended Complaint",
            p: 1,
            f: "11/14/2024",
            s: "Verified FAC adding Zilis, xAI, Templeton, Hoffman, Microsoft, Bonta",
          },
          {
            d: "34",
            y: "Certificate of Interested Entities",
            p: 0,
            f: "11/14/2024",
            s: "Updated CIE filed",
          },
          {
            d: "35",
            y: "Corporate Disclosure (xAI)",
            p: 0,
            f: "11/14/2024",
            s: "xAI Corp. corporate disclosure",
          },
          {
            d: "36",
            y: "Summons Issued",
            p: 0,
            f: "11/15/2024",
            s: "Summons re all defendants including new parties",
          },
          {
            d: "52",
            y: "Joint Stipulation re Response to FAC",
            p: 0,
            f: "12/03/2024",
            s: "Extending response deadline",
          },
          {
            d: "61",
            y: "Order Granting Stip",
            p: 0,
            f: "12/05/2024",
            s: "Response to FAC due 1/31/2025; CMC set for 5/19/2025",
          },
        ],
      },
      {
        id: "C2",
        t: "New Defendant Appearances & Disclosures",
        docs: [
          {
            d: "43",
            y: "Notice of Appearance (Dechert/Templeton/Microsoft)",
            p: 0,
            f: "11/26/2024",
            s: "Russell Cohen appears for Templeton, Microsoft",
          },
          {
            d: "56",
            y: "Notice of Appearance (Hoffman)",
            p: 0,
            f: "12/04/2024",
            s: "Cohen appears for Reid Hoffman",
          },
          {
            d: "53",
            y: "Waiver of Service (Microsoft/Templeton)",
            p: 0,
            f: "12/04/2024",
            s: "Answer due 1/14/2025",
          },
          {
            d: "54",
            y: "Waiver of Service (Hoffman)",
            p: 0,
            f: "12/04/2024",
            s: "Answer due 1/30/2025",
          },
          {
            d: "70",
            y: "Certificate of Interested Entities",
            p: 0,
            f: "12/20/2024",
            s: "Templeton, Hoffman, Microsoft CIE",
          },
          {
            d: "71",
            y: "Rule 7.1 Disclosures",
            p: 0,
            f: "12/20/2024",
            s: "Microsoft Corp. Rule 7.1",
          },
        ],
      },
    ],
  },
  {
    id: "secD",
    l: "D",
    n: "Preliminary Injunction",
    threads: [
      {
        id: "D1",
        t: "PI Motion \u2014 Briefing",
        docs: [
          {
            d: "46",
            y: "Motion for Preliminary Injunction",
            p: 1,
            f: "11/29/2024",
            s: "Plaintiffs' Motion for PI (re OpenAI conversion to for-profit)",
          },
          {
            d: "62",
            y: "Stipulation re Hearing Date",
            p: 0,
            f: "12/06/2024",
            s: "Parties stip to change hearing date",
          },
          {
            d: "63",
            y: "Order Granting Stip",
            p: 0,
            f: "12/09/2024",
            s: "Hearing reset for 1/14/2025",
          },
          {
            d: "64",
            y: "Opposition (OpenAI)",
            p: 0,
            f: "12/13/2024",
            s: "OpenAI Defendants' Opposition",
          },
          {
            d: "65",
            y: "Opposition (Microsoft/Hoffman/Templeton)",
            p: 0,
            f: "12/13/2024",
            s: "Dechert Defendants' Opposition",
          },
          {
            d: "66",
            y: "Stip re Reply Schedule",
            p: 0,
            f: "12/17/2024",
            s: "Consolidating replies",
          },
          {
            d: "68",
            y: "Order Granting Stip",
            p: 0,
            f: "12/20/2024",
            s: "Plaintiffs' Reply due 12/27/2024",
          },
          {
            d: "73",
            y: "Reply",
            p: 0,
            f: "12/27/2024",
            s: "Plaintiffs' Reply with declarations from Birchall, Gracias, Zilis",
          },
          {
            d: "74",
            y: "Elon Musk Declaration",
            p: 0,
            f: "12/28/2024",
            s: "In support of Reply",
          },
          {
            d: "83",
            y: "OpenAI Objections to New Evidence",
            p: 0,
            f: "01/03/2025",
            s: "Objections to Reply evidence",
          },
          {
            d: "84",
            y: "Joinder in OpenAI Objections",
            p: 0,
            f: "01/03/2025",
            s: "Microsoft/Hoffman/Templeton joinder",
          },
          {
            d: "90",
            y: "Admin Motion re Defendants' Objections",
            p: 0,
            f: "01/10/2025",
            s: "Plaintiffs' admin motion",
          },
          {
            d: "92",
            y: "Stip re Hearing Date",
            p: 0,
            f: "01/12/2025",
            s: "Reset PI hearing to 2/4/2025",
          },
          {
            d: "93",
            y: "Order Granting Various Motions",
            p: 0,
            f: "01/13/2025",
            s: "Hearing reset; Parties respond to USA Statement of Interest by 1/20/2025",
          },
          {
            d: "96",
            y: "Response to USA SOI (OpenAI)",
            p: 0,
            f: "01/20/2025",
            s: "OpenAI response to USA Statement of Interest",
          },
          {
            d: "98",
            y: "Response to USA SOI (Plaintiffs)",
            p: 0,
            f: "01/20/2025",
            s: "Plaintiffs' response",
          },
          {
            d: "107",
            y: "Samuel Altman Declaration",
            p: 0,
            f: "02/05/2025",
            s: "ISO OpenAI's Opposition",
          },
          {
            d: "116",
            y: "OpenAI Request to File Supplemental Material",
            p: 0,
            f: "02/12/2025",
            s: "Re conversion approval",
          },
          {
            d: "119",
            y: "Plaintiffs' Opposition to Supp Material",
            p: 0,
            f: "02/13/2025",
            s: "Opposing supplemental filing",
          },
          {
            d: "121",
            y: "Order Denying PI",
            p: 1,
            f: "03/04/2025",
            s: "PI DENIED; moot 118 motion",
          },
        ],
      },
      {
        id: "D2",
        t: "USA & Amicus Filings re PI",
        docs: [
          {
            d: "72",
            y: "Motion for Leave to File Amicus (Encode Justice)",
            p: 1,
            f: "12/27/2024",
            s: "ISO PI \u2014 re for-profit conversion",
          },
          {
            d: "77",
            y: "Motion for Leave to File Amicus (Jennings/Delaware AG)",
            p: 1,
            f: "12/30/2024",
            s: "Delaware AG amicus motion",
          },
          {
            d: "78",
            y: "Order Granting Jennings Amicus",
            p: 0,
            f: "01/02/2025",
            s: "Granted",
          },
          {
            d: "81",
            y: "Admin Motion \u2014 Response to Delaware AG Brief",
            p: 0,
            f: "01/03/2025",
            s: "Plaintiffs request leave",
          },
          {
            d: "87",
            y: "Statement of Interest",
            p: 0,
            f: "01/10/2025",
            s: "USA & FTC Statement of Interest",
          },
          {
            d: "89",
            y: "Opposition to Encode Justice Amicus",
            p: 0,
            f: "01/10/2025",
            s: "Dechert Defendants' opposition",
          },
          {
            d: "91",
            y: "OpenAI Joinder in 89",
            p: 0,
            f: "01/10/2025",
            s: "OpenAI joinder",
          },
          {
            d: "93",
            y: "Order \u2014 Granting Amicus Motions",
            p: 0,
            f: "01/13/2025",
            s: "72, 81, 90 granted; parties respond to USA SOI by 1/20",
          },
          {
            d: "94",
            y: "Encode Justice Appearance",
            p: 0,
            f: "01/14/2025",
            s: "Amicus curiae appearance",
          },
        ],
      },
    ],
  },
  {
    id: "secE",
    l: "E",
    n: "MTDs of First Amended Complaint",
    threads: [
      {
        id: "E1",
        t: "Attorney General of California MTD",
        docs: [
          {
            d: "101",
            y: "Motion to Dismiss (AG Bonta)",
            p: 1,
            f: "01/31/2025",
            s: "AG's Motion to Dismiss FAC",
          },
          {
            d: "129",
            y: "Opposition",
            p: 0,
            f: "03/24/2025",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "163",
            y: "Order \u2014 Denying as Moot",
            p: 0,
            f: "05/01/2025",
            s: "Denied as moot (163)",
          },
        ],
      },
      {
        id: "E2",
        t: "Microsoft / Templeton / Hoffman MTD FAC",
        docs: [
          {
            d: "99",
            y: "Stip re Page Limits",
            p: 0,
            f: "01/24/2025",
            s: "Page limits for MTD briefing",
          },
          {
            d: "100",
            y: "Order Granting Stip",
            p: 0,
            f: "01/27/2025",
            s: "Briefing schedule set",
          },
          {
            d: "102",
            y: "Motion to Dismiss FAC (Dechert Defendants)",
            p: 1,
            f: "01/31/2025",
            s: "Microsoft/Templeton/Hoffman MTD",
          },
          {
            d: "127",
            y: "Opposition",
            p: 0,
            f: "03/24/2025",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "148",
            y: "Reply (Microsoft)",
            p: 0,
            f: "04/10/2025",
            s: "Microsoft Reply",
          },
          {
            d: "155",
            y: "Supplemental Brief (Plaintiffs)",
            p: 0,
            f: "04/11/2025",
            s: "Re Microsoft MTD",
          },
          {
            d: "159",
            y: "Supplemental Brief (Microsoft)",
            p: 0,
            f: "04/16/2025",
            s: "ISO Motion to Dismiss",
          },
          {
            d: "163",
            y: "Order \u2014 Granting in Part / Denying in Part",
            p: 1,
            f: "05/01/2025",
            s: "Microsoft MTD partially granted",
          },
        ],
      },
      {
        id: "E3",
        t: "OpenAI Defendants MTD FAC",
        docs: [
          {
            d: "103",
            y: "Motion to Dismiss FAC (OpenAI)",
            p: 1,
            f: "01/31/2025",
            s: "OpenAI Defendants' MTD",
          },
          {
            d: "104",
            y: "Request for Judicial Notice",
            p: 0,
            f: "01/31/2025",
            s: "ISO MTD",
          },
          {
            d: "128",
            y: "Opposition",
            p: 0,
            f: "03/24/2025",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "150",
            y: "Reply (OpenAI)",
            p: 0,
            f: "04/10/2025",
            s: "OpenAI Reply",
          },
          {
            d: "157",
            y: "Statement of Recent Decision (OpenAI)",
            p: 0,
            f: "04/15/2025",
            s: "Re 103 MTD",
          },
          {
            d: "158",
            y: "Response re Recent Decision (Plaintiffs)",
            p: 0,
            f: "04/15/2025",
            s: "",
          },
          {
            d: "161",
            y: "Response re Recent Decision (Plaintiffs)",
            p: 0,
            f: "04/23/2025",
            s: "",
          },
          {
            d: "163",
            y: "Order \u2014 Granting in Part / Denying in Part",
            p: 1,
            f: "05/01/2025",
            s: "OpenAI MTD partially granted; SAC due 5/22/2025",
          },
        ],
      },
      {
        id: "E4",
        t: "Adler et al Amicus Motion (Ex-OpenAI Employees)",
        docs: [
          {
            d: "151",
            y: "Notice of Appearance (Volokh)",
            p: 0,
            f: "04/11/2025",
            s: "Eugene Volokh for ex-employees",
          },
          {
            d: "152",
            y: "Motion for Leave to File Amicus Brief",
            p: 1,
            f: "04/11/2025",
            s: "Ex-OpenAI employees ISO Plaintiffs' Opposition to MTD",
          },
          {
            d: "153",
            y: "Pro Hac Vice \u2014 Lawrence Lessig",
            p: 0,
            f: "04/11/2025",
            s: "Lessig appears for amici",
          },
          {
            d: "154",
            y: "Corrected Motion for Leave to File Amicus Brief",
            p: 1,
            f: "04/11/2025",
            s: "Corrected version",
          },
          {
            d: "160",
            y: "Opposition to 154",
            p: 0,
            f: "04/22/2025",
            s: "OpenAI opposition",
          },
          {
            d: "163",
            y: "Order \u2014 Denying 152 and 154",
            p: 0,
            f: "05/01/2025",
            s: "Both amicus motions DENIED",
          },
        ],
      },
    ],
  },
  {
    id: "secF",
    l: "F",
    n: "Case Management & Pretrial Order",
    threads: [
      {
        id: "F1",
        t: "Scheduling & Case Management",
        docs: [
          {
            d: "123",
            y: "Stip re CMC and Briefing",
            p: 0,
            f: "03/12/2025",
            s: "Continuing CMC to 4/4/2025",
          },
          {
            d: "124",
            y: "Order Granting Stip",
            p: 0,
            f: "03/14/2025",
            s: "CMC reset for 4/4/2025",
          },
          {
            d: "125",
            y: "Joint Case Management Statement",
            p: 0,
            f: "03/14/2025",
            s: "Filed by OpenAI defendants",
          },
          {
            d: "146",
            y: "Case Management & Pretrial Order",
            p: 1,
            f: "04/09/2025",
            s: "Trial set for 3/30/2026; discovery cutoffs, expert schedule",
          },
          {
            d: "149",
            y: "CMC Minute Entry",
            p: 0,
            f: "04/04/2025",
            s: "Initial CMC held on 4/4/2025",
          },
        ],
      },
    ],
  },
  {
    id: "secG",
    l: "G",
    n: "Answer & Counterclaims",
    threads: [
      {
        id: "G1",
        t: "OpenAI Answer & Counterclaims",
        docs: [
          {
            d: "147",
            y: "Answer & Counterclaims",
            p: 1,
            f: "04/09/2025",
            s: "OpenAI Defendants' Answer and Counterclaims against Musk and xAI",
          },
          {
            d: "162",
            y: "Stip re Answer Amendment Deadline",
            p: 0,
            f: "04/29/2025",
            s: "Extension to amend answer",
          },
          {
            d: "168",
            y: "Order Granting Stip",
            p: 0,
            f: "05/14/2025",
            s: "Amendment due 5/28/2025",
          },
          {
            d: "176",
            y: "Amended Answer & Counterclaims",
            p: 1,
            f: "06/05/2025",
            s: "OpenAI Amended Answer and Counterclaims against Musk/xAI",
          },
          {
            d: "229",
            y: "Second Amended Counterclaims",
            p: 1,
            f: "08/15/2025",
            s: "OpenAI Second Amended Counterclaims",
          },
        ],
      },
      {
        id: "G2",
        t: "MTD OpenAI Counterclaims",
        docs: [
          {
            d: "166",
            y: "Motion to Dismiss Counterclaims",
            p: 1,
            f: "05/07/2025",
            s: "Musk/xAI Motion to Dismiss OpenAI's Counterclaims; hearing 6/17/2025",
          },
          {
            d: "172",
            y: "Opposition",
            p: 0,
            f: "05/28/2025",
            s: "OpenAI Opposition",
          },
          {
            d: "179",
            y: "Reply",
            p: 0,
            f: "06/11/2025",
            s: "Plaintiffs' Reply",
          },
          {
            d: "228",
            y: "Order \u2014 Denying MTD Counterclaims",
            p: 1,
            f: "08/12/2025",
            s: "MTD Counterclaims DENIED; SAC MTDs granted in part",
          },
        ],
      },
    ],
  },
  {
    id: "secH",
    l: "H",
    n: "Second Amended Complaint",
    threads: [
      {
        id: "H1",
        t: "SAC Filing",
        docs: [
          {
            d: "170",
            y: "Second Amended Complaint",
            p: 1,
            f: "05/22/2025",
            s: "Musk/xAI Second Amended Complaint (dropping Zilis, Bonta)",
          },
          {
            d: "169",
            y: "Stipulated Protective Order",
            p: 0,
            f: "05/22/2025",
            s: "Protective order filed",
          },
          {
            d: "171",
            y: "Order Granting Protective Order",
            p: 0,
            f: "05/28/2025",
            s: "Granted",
          },
        ],
      },
      {
        id: "H2",
        t: "MTDs of Second Amended Complaint",
        docs: [
          {
            d: "173",
            y: "Motion to Dismiss SAC (Microsoft)",
            p: 1,
            f: "06/05/2025",
            s: "Microsoft MTD SAC; hearing 7/15/2025",
          },
          {
            d: "174",
            y: "Microsoft Answer (partial)",
            p: 0,
            f: "06/05/2025",
            s: "Microsoft answers Counts IV, V, XIX, XX, XXI",
          },
          {
            d: "175",
            y: "Motion to Dismiss SAC (OpenAI)",
            p: 1,
            f: "06/05/2025",
            s: "OpenAI MTD SAC; hearing 7/15/2025",
          },
          {
            d: "181",
            y: "Opposition (Plaintiffs to OpenAI)",
            p: 0,
            f: "06/20/2025",
            s: "",
          },
          {
            d: "182",
            y: "Opposition (Plaintiffs to Microsoft)",
            p: 0,
            f: "06/20/2025",
            s: "",
          },
          { d: "189", y: "Reply (Microsoft)", p: 0, f: "06/27/2025", s: "" },
          { d: "190", y: "Reply (OpenAI)", p: 0, f: "06/27/2025", s: "" },
          {
            d: "202",
            y: "Hearing Vacated",
            p: 0,
            f: "07/08/2025",
            s: "MTD hearings on 7/15 vacated",
          },
          {
            d: "228",
            y: "Order \u2014 Granting MTDs in Part",
            p: 1,
            f: "08/12/2025",
            s: "Counts III, XX, XXI dismissed; MTD counterclaims denied; Defenses amendment granted",
          },
        ],
      },
    ],
  },
  {
    id: "secI",
    l: "I",
    n: "Discovery",
    threads: [
      {
        id: "I1",
        t: "Discovery Order Referral",
        docs: [
          {
            d: "184",
            y: "Order of Reference to Magistrate",
            p: 0,
            f: "06/23/2025",
            s: "Referred to MJ Kandis Westmore for discovery",
          },
          {
            d: "185",
            y: "Order of Recusal (MJ Westmore)",
            p: 0,
            f: "06/24/2025",
            s: "MJ Westmore recuses",
          },
          {
            d: "187",
            y: "Notice of Referral to MJ Hixson",
            p: 0,
            f: "06/27/2025",
            s: "Referred to MJ Thomas Hixson",
          },
        ],
      },
      {
        id: "I2",
        t: "Discovery Letter Briefs & Orders",
        docs: [
          {
            d: "180",
            y: "Discovery Letter Brief (OpenAI)",
            p: 1,
            f: "06/20/2025",
            s: "OpenAI re Musk ESI/custodians",
          },
          {
            d: "183",
            y: "Opposition (Plaintiffs)",
            p: 0,
            f: "06/22/2025",
            s: "",
          },
          {
            d: "188",
            y: "Order re 180",
            p: 0,
            f: "06/27/2025",
            s: "MJ Hixson order \u2014 Plaintiffs respond by 6/30",
          },
          {
            d: "191",
            y: "Response (Plaintiffs)",
            p: 0,
            f: "06/29/2025",
            s: "",
          },
          {
            d: "192",
            y: "Discovery Letter Brief (OpenAI) #2",
            p: 1,
            f: "06/30/2025",
            s: "Re additional custodians",
          },
          {
            d: "194",
            y: "Discovery Order (MJ Hixson)",
            p: 1,
            f: "07/01/2025",
            s: "Order re 180, 191, 192",
          },
          {
            d: "199",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "07/07/2025",
            s: "Re xAI documents",
          },
          {
            d: "200",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "07/07/2025",
            s: "Re Musk phone/iCloud",
          },
          {
            d: "201",
            y: "Joint Discovery Letter Brief (Plaintiffs)",
            p: 1,
            f: "07/07/2025",
            s: "Re OpenAI documents",
          },
          {
            d: "203",
            y: "Discovery Order (MJ Hixson)",
            p: 1,
            f: "07/09/2025",
            s: "Order re 199, 200, 201",
          },
          {
            d: "210",
            y: "Joint Letter Brief re Eight Partners (8VC) Privilege",
            p: 1,
            f: "07/18/2025",
            s: "Non-party 8VC privilege assertions",
          },
          {
            d: "213",
            y: "Discovery Order re 8VC",
            p: 0,
            f: "07/21/2025",
            s: "Order \u2014 further briefs due 7/28",
          },
          {
            d: "221",
            y: "Further Joint Letter Brief (8VC)",
            p: 0,
            f: "07/28/2025",
            s: "",
          },
          {
            d: "224",
            y: "Discovery Order re 221",
            p: 0,
            f: "07/29/2025",
            s: "",
          },
          {
            d: "226",
            y: "Discovery Order re 210, 221",
            p: 0,
            f: "07/30/2025",
            s: "",
          },
          {
            d: "230",
            y: "Joint Letter Brief",
            p: 1,
            f: "08/21/2025",
            s: "Re Musk personal phones/texts",
          },
          {
            d: "234",
            y: "Discovery Letter Brief (Plaintiffs)",
            p: 1,
            f: "08/22/2025",
            s: "",
          },
          {
            d: "236",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "08/26/2025",
            s: "",
          },
          {
            d: "237",
            y: "Discovery Order (MJ Hixson)",
            p: 1,
            f: "08/28/2025",
            s: "Order re 230, 236",
          },
          {
            d: "239",
            y: "Discovery Letter Brief (Plaintiffs)",
            p: 1,
            f: "09/02/2025",
            s: "Re Altria [sic] deposition",
          },
          {
            d: "240",
            y: "Joint Discovery Letter Brief (OpenAI)",
            p: 1,
            f: "09/02/2025",
            s: "Re Jared Birchall/Shivon Zilis",
          },
          {
            d: "241",
            y: "Joint Discovery Letter Brief (Microsoft)",
            p: 1,
            f: "09/03/2025",
            s: "",
          },
          {
            d: "242",
            y: "Joint Discovery Letter Brief (OpenAI)",
            p: 1,
            f: "09/03/2025",
            s: "",
          },
          {
            d: "251",
            y: "Discovery Order (MJ Hixson)",
            p: 1,
            f: "09/09/2025",
            s: "Order re 239, 241",
          },
          {
            d: "252",
            y: "Order \u2014 Status Update re 240",
            p: 0,
            f: "09/09/2025",
            s: "",
          },
          {
            d: "263",
            y: "Discovery Order re 242",
            p: 1,
            f: "09/12/2025",
            s: "",
          },
          {
            d: "264",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "09/12/2025",
            s: "Re Zilis/Birchall deposition",
          },
          {
            d: "273",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "09/19/2025",
            s: "Re Zilis confidentiality",
          },
          {
            d: "276",
            y: "Discovery Order (MJ Hixson)",
            p: 1,
            f: "09/22/2025",
            s: "Order re 240, 264, 273",
          },
          {
            d: "285",
            y: "Discovery Letter Brief (Plaintiffs)",
            p: 1,
            f: "09/26/2025",
            s: "Re Ilya Sutskever deposition",
          },
          {
            d: "288",
            y: "Order re Sealing Motion",
            p: 0,
            f: "09/29/2025",
            s: "",
          },
          {
            d: "293",
            y: "Admin Motion to Consider Sealing",
            p: 0,
            f: "10/06/2025",
            s: "OpenAI motion to seal joint letter brief",
          },
          {
            d: "294",
            y: "Joint Discovery Letter Brief",
            p: 1,
            f: "10/06/2025",
            s: "Re Sutskever deposition",
          },
          {
            d: "295",
            y: "Admin Motion to Consider Sealing (Plaintiffs)",
            p: 0,
            f: "10/07/2025",
            s: "Re alternative service motion",
          },
          {
            d: "296",
            y: "Admin Motion \u2014 Alternative Service re Sutskever",
            p: 1,
            f: "10/07/2025",
            s: "Motion to compel Sutskever deposition",
          },
          {
            d: "305",
            y: "Discovery Order re 296",
            p: 0,
            f: "10/09/2025",
            s: "",
          },
          {
            d: "309",
            y: "Order Granting 296",
            p: 0,
            f: "10/09/2025",
            s: "Alternative service/compliance granted",
          },
          {
            d: "312",
            y: "Discovery Order re 301",
            p: 0,
            f: "10/09/2025",
            s: "",
          },
          {
            d: "314",
            y: "Discovery Order re 311",
            p: 0,
            f: "10/09/2025",
            s: "",
          },
          {
            d: "315",
            y: "Discovery Order & Recommendation re 294",
            p: 0,
            f: "10/10/2025",
            s: "",
          },
          {
            d: "324",
            y: "Discovery Order re Sutskever",
            p: 1,
            f: "10/17/2025",
            s: "Sutskever to produce Brockman memo; second deposition ordered",
          },
          {
            d: "325",
            y: "Order re Sealing (293)",
            p: 0,
            f: "10/17/2025",
            s: "Denied",
          },
          {
            d: "326",
            y: "Order re Sealing (295)",
            p: 0,
            f: "10/17/2025",
            s: "Granted in part / denied in part",
          },
          {
            d: "335",
            y: "Motion for OSC \u2014 Contempt (Sutskever)",
            p: 1,
            f: "10/28/2025",
            s: "Plaintiffs seek contempt sanction re Sutskever compliance",
          },
          {
            d: "337",
            y: "Sutskever Motion to Stay Discovery Order",
            p: 1,
            f: "10/28/2025",
            s: "Sutskever motion to stay 324",
          },
          {
            d: "339",
            y: "Order \u2014 Denying OSC; Sutskever Compliance by 11/5",
            p: 0,
            f: "10/29/2025",
            s: "",
          },
          {
            d: "342",
            y: "Sutskever Motion for Relief from Magistrate Order",
            p: 1,
            f: "10/29/2025",
            s: "Appeal of 324 to District Judge",
          },
          {
            d: "348",
            y: "Order \u2014 Denying 342 and 337",
            p: 1,
            f: "10/31/2025",
            s: "Sutskever relief DENIED; stay DENIED",
          },
          {
            d: "354",
            y: "Order Adopting Magistrate Recommendation",
            p: 0,
            f: "11/10/2025",
            s: "Denial of special master appointment",
          },
        ],
      },
      {
        id: "I3",
        t: "Schedule Modification",
        docs: [
          {
            d: "214",
            y: "Stip to Modify Case Schedule",
            p: 0,
            f: "07/22/2025",
            s: "Parties stip to extend discovery deadlines",
          },
          {
            d: "215",
            y: "Order Granting in Part",
            p: 1,
            f: "07/25/2025",
            s: "Modified schedule \u2014 depo deadline 10/1; expert reports 10/6; MSJ 10/10",
          },
          {
            d: "267",
            y: "Admin Motion to Modify Schedule (Plaintiffs)",
            p: 0,
            f: "09/17/2025",
            s: "Further modification requested",
          },
          { d: "271", y: "Opposition (OpenAI)", p: 0, f: "09/19/2025", s: "" },
          { d: "274", y: "Joinder (Microsoft)", p: 0, f: "09/20/2025", s: "" },
          {
            d: "278",
            y: "Order \u2014 Granting Modified Schedule",
            p: 1,
            f: "09/22/2025",
            s: "MSJ 10/17; opp 11/7; reply 11/21; hearing 1/7/2026; trial reset",
          },
        ],
      },
    ],
  },
  {
    id: "secJ",
    l: "J",
    n: "Motion to Strike Defenses",
    threads: [
      {
        id: "J1",
        t: "MTS OpenAI's 55 Defenses",
        docs: [
          {
            d: "186",
            y: "Motion to Strike 55 Defenses",
            p: 1,
            f: "06/26/2025",
            s: "Musk/xAI MTS; hearing 8/5/2025",
          },
          { d: "205", y: "Opposition (OpenAI)", p: 0, f: "07/10/2025", s: "" },
          { d: "209", y: "Reply", p: 0, f: "07/17/2025", s: "" },
          {
            d: "222",
            y: "Order \u2014 Granting in Part",
            p: 1,
            f: "07/29/2025",
            s: "MTS granted in part",
          },
          {
            d: "227",
            y: "Motion to Amend Defenses (OpenAI)",
            p: 1,
            f: "08/06/2025",
            s: "OpenAI motion to amend its defenses",
          },
          {
            d: "228",
            y: "Order \u2014 Granting 227",
            p: 0,
            f: "08/12/2025",
            s: "Amendment granted (part of omnibus order)",
          },
        ],
      },
    ],
  },
  {
    id: "secK",
    l: "K",
    n: "Summary Judgment",
    threads: [
      {
        id: "K1",
        t: "MSJ Briefing",
        docs: [
          {
            d: "327",
            y: "Motion for Summary Judgment (OpenAI) [REDACTED]",
            p: 1,
            f: "10/17/2025",
            s: "OpenAI MSJ; hearing 1/7/2026",
          },
          {
            d: "328",
            y: "Admin Motion to Seal re OpenAI MSJ",
            p: 0,
            f: "10/17/2025",
            s: "Sealing motion with exhibits",
          },
          {
            d: "329",
            y: "Motion for Summary Judgment (Microsoft)",
            p: 1,
            f: "10/17/2025",
            s: "Microsoft MSJ; hearing 1/7/2026",
          },
          {
            d: "330",
            y: "Admin Motion to Seal re Microsoft MSJ",
            p: 0,
            f: "10/17/2025",
            s: "",
          },
          {
            d: "350",
            y: "Opposition to OpenAI MSJ [REDACTED]",
            p: 0,
            f: "11/07/2025",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "351",
            y: "Admin Motion to Seal re PI Opp (OpenAI)",
            p: 0,
            f: "11/07/2025",
            s: "",
          },
          {
            d: "352",
            y: "Opposition to Microsoft MSJ [REDACTED]",
            p: 0,
            f: "11/07/2025",
            s: "Plaintiffs' Opposition",
          },
          {
            d: "353",
            y: "Admin Motion to Seal re PI Opp (Microsoft)",
            p: 0,
            f: "11/07/2025",
            s: "",
          },
          {
            d: "355",
            y: "Admin Motion re Corrected Exhibit",
            p: 0,
            f: "11/19/2025",
            s: "",
          },
          {
            d: "357",
            y: "Reply ISO Microsoft MSJ [REDACTED]",
            p: 0,
            f: "11/21/2025",
            s: "",
          },
          {
            d: "358",
            y: "Temporary Sealing Motion (Microsoft Reply)",
            p: 0,
            f: "11/21/2025",
            s: "",
          },
          {
            d: "359",
            y: "Reply ISO OpenAI MSJ [REDACTED]",
            p: 0,
            f: "11/21/2025",
            s: "",
          },
          {
            d: "360",
            y: "Temporary Sealing Motion (OpenAI Reply)",
            p: 0,
            f: "11/21/2025",
            s: "",
          },
          {
            d: "364",
            y: "Stip re Sealing Stipulation Deadline",
            p: 0,
            f: "12/03/2025",
            s: "",
          },
          {
            d: "368",
            y: "Admin Motion \u2014 Omnibus Sealing Stipulation",
            p: 0,
            f: "12/12/2025",
            s: "",
          },
          { d: "375", y: "Order re Sealing", p: 0, f: "12/30/2025", s: "" },
          {
            d: "383",
            y: "MSJ Hearing Minute Entry",
            p: 0,
            f: "01/07/2026",
            s: "Hearing held 1/7/2026",
          },
          {
            d: "385",
            y: "Transcript of 1/7/2026 Hearing",
            p: 0,
            f: "01/10/2026",
            s: "",
          },
          {
            d: "386",
            y: "Order re Trial Date",
            p: 0,
            f: "01/13/2026",
            s: "Trial reset: jury selection 4/27/2026; trial 4/28\u20135/22/2026",
          },
          {
            d: "390",
            y: "Order \u2014 Denying OpenAI MSJ; Granting in Part Microsoft MSJ",
            p: 1,
            f: "01/15/2026",
            s: "OpenAI MSJ DENIED; Microsoft MSJ granted in part",
          },
        ],
      },
    ],
  },
  {
    id: "secL",
    l: "L",
    n: "Trial Preparation",
    threads: [
      {
        id: "L1",
        t: "Motions in Limine & Daubert",
        docs: [
          {
            d: "393",
            y: "Motion to Exclude Wazzan Opinions 3-6",
            p: 1,
            f: "01/16/2026",
            s: "OpenAI Daubert re damages expert",
          },
          {
            d: "394",
            y: "Admin Motion to File Corrected 393",
            p: 0,
            f: "01/18/2026",
            s: "",
          },
          {
            d: "396",
            y: "Opposition (Plaintiffs)",
            p: 0,
            f: "01/30/2026",
            s: "",
          },
          { d: "397", y: "Reply (OpenAI)", p: 0, f: "02/06/2026", s: "" },
          {
            d: "402",
            y: "MIL No. 1 (Plaintiffs)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "403",
            y: "MIL No. 2 (Plaintiffs)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "404",
            y: "MIL No. 3 (Plaintiffs)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "405",
            y: "Admin Motion to Seal MIL No. 4",
            p: 0,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "406",
            y: "MIL No. 4 (Plaintiffs)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "407",
            y: "MIL No. 5 (Plaintiffs)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "409",
            y: "MIL No. 1 \u2014 Exclude Stuart Russell (OpenAI)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "410",
            y: "MIL No. 2 \u2014 Exclude News Articles (OpenAI)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "412",
            y: "MIL No. 1 (Microsoft)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "413",
            y: "Admin Motion to Seal re Microsoft MIL",
            p: 0,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "414",
            y: "MIL No. 2 (Microsoft)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "415",
            y: "MIL No. 3 (Microsoft)",
            p: 1,
            f: "02/24/2026",
            s: "",
          },
          {
            d: "416",
            y: "Microsoft Opposition to Plaintiffs MIL No. 3",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "417",
            y: "Plaintiffs Opposition to OpenAI MIL No. 1",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "418",
            y: "Plaintiffs Opposition to OpenAI MIL No. 2",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "419",
            y: "Plaintiffs Opposition to Microsoft MIL No. 1",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "420",
            y: "Plaintiffs Opposition to Microsoft MIL No. 2",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "421",
            y: "Plaintiffs Opposition to Microsoft MIL No. 3",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "422",
            y: "OpenAI Opposition to Plaintiffs MIL No. 1",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "423",
            y: "OpenAI Opposition to Plaintiffs MIL No. 3",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "424",
            y: "OpenAI Opposition to Plaintiffs MIL No. 2",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "425",
            y: "OpenAI Opposition to Plaintiffs MIL No. 5",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "426",
            y: "Admin Motion to Seal (OpenAI re MIL No. 4 Opp)",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "452",
            y: "Pretrial Order No. 2 \u2014 MILs & Daubert Rulings",
            p: 1,
            f: "03/25/2026",
            s: "Rulings on all MILs",
          },
          {
            d: "401",
            y: "Order \u2014 Granting Corrected MTD Motion; Continuing Hearing on Wazzan",
            p: 1,
            f: "02/20/2026",
            s: "",
          },
        ],
      },
      {
        id: "L2",
        t: "Pretrial Orders & Filings",
        docs: [
          {
            d: "427",
            y: "Pretrial Conference Statement (Plaintiffs)",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          {
            d: "428",
            y: "Exhibit List (Plaintiffs)",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          {
            d: "429",
            y: "Joint Discovery Designations",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          {
            d: "430",
            y: "Proposed Order re Trial Stipulations (OpenAI)",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          { d: "431", y: "Joint Witness List", p: 0, f: "02/26/2026", s: "" },
          {
            d: "432",
            y: "Joint Expert Witness List",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          {
            d: "433",
            y: "Joint Proposed Jury Instructions",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          {
            d: "434",
            y: "Competing Verdict Forms",
            p: 0,
            f: "02/26/2026",
            s: "",
          },
          { d: "446", y: "Pretrial Order No. 1", p: 1, f: "03/16/2026", s: "" },
          {
            d: "447",
            y: "Joint Letter re Pretrial Order No. 1",
            p: 0,
            f: "03/20/2026",
            s: "",
          },
          { d: "448", y: "Stip re Parties", p: 0, f: "03/23/2026", s: "" },
          {
            d: "456",
            y: "Pretrial Order No. 3 \u2014 Remedies / Statute of Limitations",
            p: 1,
            f: "03/31/2026",
            s: "",
          },
          {
            d: "457",
            y: "Emergency Motion to Intervene (Veleber)",
            p: 0,
            f: "03/31/2026",
            s: "",
          },
          {
            d: "458",
            y: "CMC set for 4/13/2026",
            p: 0,
            f: "04/01/2026",
            s: "",
          },
          {
            d: "459",
            y: "Plaintiff Amended Notice of Remedies",
            p: 0,
            f: "04/07/2026",
            s: "",
          },
          { d: "477", y: "Pretrial Order No. 4", p: 1, f: "04/17/2026", s: "" },
          {
            d: "484",
            y: "Notice re Waiver of Remedies",
            p: 0,
            f: "04/20/2026",
            s: "",
          },
          {
            d: "485",
            y: "Notice re Fraud Claims",
            p: 0,
            f: "04/20/2026",
            s: "",
          },
          {
            d: "487",
            y: "Order re 484, 485",
            p: 0,
            f: "04/21/2026",
            s: "Court orders response by 4/22/2026",
          },
          {
            d: "496",
            y: "Order Denying Intervention (Veleber)",
            p: 0,
            f: "04/24/2026",
            s: "",
          },
          {
            d: "497",
            y: "Order \u2014 Fraud Claims Dismissed With Prejudice",
            p: 1,
            f: "04/24/2026",
            s: "Re Dkt 485",
          },
          {
            d: "452",
            y: "Pretrial Order No. 2",
            p: 1,
            f: "03/25/2026",
            s: "MIL and Daubert rulings",
          },
        ],
      },
      {
        id: "L3",
        t: "Trial",
        docs: [
          {
            d: "506",
            y: "Minute Entry \u2014 Jury Selection",
            p: 0,
            f: "04/27/2026",
            s: "Jury selection 4/27/2026 (5:01 hours)",
          },
          {
            d: "505",
            y: "Notice \u2014 Admitted Exhibits Link",
            p: 0,
            f: "04/28/2026",
            s: "Public link to admitted trial exhibits",
          },
          {
            d: "510",
            y: "Minute Entry \u2014 Trial Day 1",
            p: 0,
            f: "04/28/2026",
            s: "Jury trial begins 4/28/2026 (4:59 hours); granting Stip re Parties (448)",
          },
          {
            d: "513",
            y: "Minute Entry \u2014 Trial Day 2",
            p: 0,
            f: "04/29/2026",
            s: "Jury trial 4/29/2026 (5:54 hours); charging conference 5/1/2026 at 10AM",
          },
          {
            d: "515",
            y: "Trial Brief \u2014 Cal. Bus. & Prof. Code \u00a7 17510.8 (Plaintiffs)",
            p: 0,
            f: "04/30/2026",
            s: "",
          },
          {
            d: "516",
            y: "Trial Brief \u2014 Charitable Solicitations (OpenAI)",
            p: 0,
            f: "04/30/2026",
            s: "",
          },
          {
            d: "521",
            y: "Minute Entry \u2014 Trial Day 3",
            p: 0,
            f: "04/30/2026",
            s: "Jury trial 4/30/2026 (5:32 hours)",
          },
        ],
      },
    ],
  },
];
const NEURO_THREADS = [
  {
    id: "secA",
    l: "A",
    n: "Case Initiation & Service",
    threads: [
      {
        id: "A1",
        t: "Complaint & Initial Filings",
        docs: [
          {
            d: "1",
            y: "Complaint",
            p: 1,
            f: "06/27/2025",
            s: "Patent infringement complaint by Neurocentria / ThreoTech vs. Mando Int'l (Kappy Nutrition)",
          },
          {
            d: "4",
            y: "Notice of Interested Parties",
            p: 0,
            f: "06/27/2025",
            s: "Filed by Plaintiffs",
          },
          {
            d: "14",
            y: "Summons Issued",
            p: 0,
            f: "07/14/2025",
            s: "21-day summons re Mando International, LLC",
          },
          {
            d: "15",
            y: "Order Reassigning Case",
            p: 0,
            f: "07/15/2025",
            s: "Reassigned to Judge Fernando L. Aenlle-Rocha",
          },
          {
            d: "16",
            y: "Initial Standing Order",
            p: 0,
            f: "07/16/2025",
            s: "",
          },
          {
            d: "17",
            y: "Proof of Service",
            p: 0,
            f: "07/22/2025",
            s: "Service on Mando via Clerk's Office; answer due 8/6/2025",
          },
          {
            d: "18",
            y: "Notice of Appearance (Abhyanker)",
            p: 0,
            f: "08/06/2025",
            s: "Raj Abhyanker appears for Mando",
          },
          {
            d: "19",
            y: "Declined Consent to Magistrate",
            p: 0,
            f: "08/06/2025",
            s: "Mando declines magistrate jurisdiction",
          },
        ],
      },
    ],
  },
  {
    id: "secB",
    l: "B",
    n: "Defendant's First Motion to Dismiss / Transfer",
    threads: [
      {
        id: "B1",
        t: "MTD / Transfer Briefing",
        docs: [
          {
            d: "20",
            y: "Motion to Dismiss / Transfer",
            p: 1,
            f: "08/06/2025",
            s: "Mando moves to dismiss (12(b)(2), 12(b)(3), 12(b)(6)) or transfer to N.D. Tex.; hearing 9/5/2025",
          },
          {
            d: "24",
            y: "Opposition",
            p: 0,
            f: "08/15/2025",
            s: "Plaintiffs' Opposition",
          },
          { d: "25", y: "Reply", p: 0, f: "08/19/2025", s: "Mando Reply" },
          {
            d: "26",
            y: "Order Taking Under Submission",
            p: 0,
            f: "08/31/2025",
            s: "Hearing vacated; decided without oral argument",
          },
          {
            d: "69",
            y: "Order Denying as Moot",
            p: 1,
            f: "12/12/2025",
            s: "Denied as moot following parties' withdrawals",
          },
        ],
      },
    ],
  },
  {
    id: "secC",
    l: "C",
    n: "Scheduling & Initial Case Management",
    threads: [
      {
        id: "C1",
        t: "Scheduling Conference & Orders",
        docs: [
          {
            d: "21",
            y: "Order Setting Scheduling Conference",
            p: 0,
            f: "08/08/2025",
            s: "Scheduling Conference set 9/26/2025",
          },
          {
            d: "27",
            y: "Joint Rule 26(f) Report",
            p: 0,
            f: "09/11/2025",
            s: "Filed by Neurocentria",
          },
          {
            d: "30",
            y: "Order Vacating Scheduling Conference",
            p: 0,
            f: "09/24/2025",
            s: "Court vacates CMC; scheduling order to issue",
          },
          {
            d: "31",
            y: "Order re ADR",
            p: 0,
            f: "09/24/2025",
            s: "Case ordered to private mediator; ADR deadline 12/31/2026",
          },
          {
            d: "32",
            y: "Scheduling & Trial Order",
            p: 1,
            f: "09/24/2025",
            s: "Trial set 3/9/2027; Final PTC 2/5/2027",
          },
          {
            d: "33",
            y: "Patent Standing Order",
            p: 0,
            f: "09/29/2025",
            s: "Court's patent-specific rules governing this action",
          },
        ],
      },
    ],
  },
  {
    id: "secD",
    l: "D",
    n: "Answer & Counterclaims",
    threads: [
      {
        id: "D1",
        t: "Mando's Answer & First Counterclaims",
        docs: [
          {
            d: "54",
            y: "Withdrawal of MTD (Mando)",
            p: 0,
            f: "11/11/2025",
            s: "Mando withdraws its Motion to Dismiss",
          },
          {
            d: "55",
            y: "Answer & First Counterclaims",
            p: 1,
            f: "11/12/2025",
            s: "Mando Answer + Counterclaims: declaratory relief, patent invalidity, fraud on USPTO, unfair competition, false advertising (Lanham Act)",
          },
          {
            d: "59",
            y: "Summons \u2014 Guosong Liu",
            p: 0,
            f: "11/13/2025",
            s: "Re Counterclaim defendants",
          },
          {
            d: "60",
            y: "Summons \u2014 Fei Mao",
            p: 0,
            f: "11/13/2025",
            s: "",
          },
          {
            d: "63",
            y: "Summons \u2014 AIDP, Inc.",
            p: 0,
            f: "11/13/2025",
            s: "",
          },
        ],
      },
      {
        id: "D2",
        t: "Mando's First Amended Counterclaims",
        docs: [
          {
            d: "70",
            y: "First Amended Counterclaims",
            p: 1,
            f: "12/15/2025",
            s: "Mando's FAC: declaratory relief, patent non-infringement and invalidity",
          },
          {
            d: "90",
            y: "Order \u2014 MTD Moot (re FAC)",
            p: 0,
            f: "01/09/2026",
            s: "Court denies as moot original counterclaim MTDs following FAC",
          },
        ],
      },
      {
        id: "D3",
        t: "Mando's Second Amended Counterclaims & Crossclaims",
        docs: [
          {
            d: "107",
            y: "Order \u2014 Granting Leave; Directing SAC",
            p: 1,
            f: "02/24/2026",
            s: "Court grants FAC motion; orders Mando to file Second Amended Counterclaims/Crossclaims",
          },
          {
            d: "112",
            y: "Second Amended Counterclaims",
            p: 1,
            f: "03/03/2026",
            s: "Mando SACC against Neurocentria, ThreoTech, AIDP, Inc.; includes AIDP as crossclaim defendant",
          },
          {
            d: "117",
            y: "Proof of Service \u2014 AIDP (Initial)",
            p: 0,
            f: "03/12/2026",
            s: "AIDP served via CEO Jennifer Gu",
          },
          {
            d: "134",
            y: "Summons Request re SACC",
            p: 0,
            f: "03/17/2026",
            s: "Mando requests summons for SACC",
          },
          {
            d: "137",
            y: "Summons \u2014 AIDP re SACC",
            p: 0,
            f: "03/19/2026",
            s: "",
          },
          {
            d: "140",
            y: "Proof of Service \u2014 AIDP (Substituted)",
            p: 0,
            f: "03/21/2026",
            s: "Substituted service on AIDP at corporate address",
          },
          {
            d: "167",
            y: "Proof of Service \u2014 AIDP (Personal Service on President)",
            p: 0,
            f: "04/17/2026",
            s: "Personal service on Mark Thurston, AIDP President",
          },
        ],
      },
    ],
  },
  {
    id: "secE",
    l: "E",
    n: "Motion to Amend Plaintiffs' Complaint",
    threads: [
      {
        id: "E1",
        t: "First Motion for Leave to File FAC (withdrawn)",
        docs: [
          {
            d: "34",
            y: "First Motion for Leave to File FAC",
            p: 1,
            f: "10/03/2025",
            s: "Plaintiffs seek leave to amend complaint; hearing 10/31/2025",
          },
          { d: "35", y: "Opposition (Mando)", p: 0, f: "10/08/2025", s: "" },
          { d: "36", y: "Reply (Plaintiffs)", p: 0, f: "10/17/2025", s: "" },
          {
            d: "37",
            y: "Motion to File Corrected Opposition (Mando)",
            p: 0,
            f: "10/17/2025",
            s: "",
          },
          {
            d: "38",
            y: "Opposition to Mando's Correction Motion",
            p: 0,
            f: "10/24/2025",
            s: "",
          },
          {
            d: "42",
            y: "Order Taking Under Submission",
            p: 0,
            f: "10/30/2025",
            s: "Hearing vacated",
          },
          {
            d: "53",
            y: "Withdrawal \u2014 Motion for Leave to Amend",
            p: 0,
            f: "11/11/2025",
            s: "Plaintiffs withdraw motion to amend",
          },
        ],
      },
      {
        id: "E2",
        t: "Second Motion for Leave to File FAC",
        docs: [
          {
            d: "64",
            y: "Second Motion for Leave to File FAC",
            p: 1,
            f: "11/21/2025",
            s: "Renewed motion; hearing 12/19/2025",
          },
          { d: "65", y: "Opposition (Mando)", p: 0, f: "11/25/2025", s: "" },
          { d: "67", y: "Reply (Plaintiffs)", p: 0, f: "12/05/2025", s: "" },
          {
            d: "78",
            y: "Order Taking Under Submission",
            p: 0,
            f: "12/18/2025",
            s: "Hearing vacated",
          },
          {
            d: "89",
            y: "Order \u2014 Granting Stip to Dismiss Claims",
            p: 0,
            f: "01/08/2026",
            s: "Stipulation to dismiss certain causes of action approved",
          },
          {
            d: "91",
            y: "Order \u2014 Denying FAC Motion Without Prejudice",
            p: 0,
            f: "01/09/2026",
            s: "Denied; parties may renew by 1/23/2026",
          },
        ],
      },
      {
        id: "E3",
        t: "Third (Renewed) Motion for Leave to File FAC",
        docs: [
          {
            d: "94",
            y: "Renewed Motion for Leave to File FAC",
            p: 1,
            f: "01/23/2026",
            s: "Plaintiffs' renewed motion; hearing 2/20/2026",
          },
          {
            d: "99",
            y: "Opposition (Mando) with RJN",
            p: 0,
            f: "01/28/2026",
            s: "Mando opposition + request for judicial notice",
          },
          { d: "102", y: "Reply (Plaintiffs)", p: 0, f: "02/06/2026", s: "" },
          {
            d: "106",
            y: "Order Taking Under Submission",
            p: 0,
            f: "02/18/2026",
            s: "Hearing vacated",
          },
          {
            d: "107",
            y: "Order \u2014 Granting Leave to File FAC; OSC re Sanctions",
            p: 1,
            f: "02/24/2026",
            s: "FAC motion GRANTED; Mando ordered to show cause re bad-faith conduct by 3/6/2026",
          },
          {
            d: "110",
            y: "First Amended Complaint",
            p: 1,
            f: "02/26/2026",
            s: "Plaintiffs file FAC",
          },
        ],
      },
    ],
  },
  {
    id: "secF",
    l: "F",
    n: "MTD Counterclaims",
    threads: [
      {
        id: "F1",
        t: "MTD Original Counterclaims (Counts VIII-XIII)",
        docs: [
          {
            d: "66",
            y: "Motion to Dismiss Counts VIII-XIII",
            p: 1,
            f: "12/03/2025",
            s: "Plaintiffs MTD original counterclaims; hearing 1/16/2026",
          },
          {
            d: "68",
            y: "Motion to Strike Gavenman Declaration (Mando)",
            p: 0,
            f: "12/12/2025",
            s: "Mando MTS + evidentiary objections + seal request; hearing 1/16/2026",
          },
          {
            d: "82",
            y: "Opposition to MTS (Plaintiffs)",
            p: 0,
            f: "12/24/2025",
            s: "",
          },
          { d: "83", y: "Reply ISO MTS (Mando)", p: 0, f: "12/24/2025", s: "" },
          {
            d: "90",
            y: "Order \u2014 MTDs Moot (re FAC)",
            p: 0,
            f: "01/09/2026",
            s: "Moot following Mando's First Amended Counterclaims",
          },
        ],
      },
      {
        id: "F2",
        t: "MTD First Amended Counterclaims (Counts VI-VII)",
        docs: [
          {
            d: "84",
            y: "Motion to Dismiss Counts VI-VII of FAC",
            p: 1,
            f: "12/29/2025",
            s: "Plaintiffs MTD; hearing 2/20/2026",
          },
          { d: "92", y: "Opposition (Mando)", p: 0, f: "01/11/2026", s: "" },
          { d: "101", y: "Reply (Plaintiffs)", p: 0, f: "02/06/2026", s: "" },
          {
            d: "106",
            y: "Order Taking Under Submission",
            p: 0,
            f: "02/18/2026",
            s: "Hearing vacated",
          },
          {
            d: "107",
            y: "Order \u2014 FAC granted; MTD Moot",
            p: 0,
            f: "02/24/2026",
            s: "Moot following order granting leave for SACC",
          },
        ],
      },
      {
        id: "F3",
        t: "MTD Second Amended Counterclaims",
        docs: [
          {
            d: "133",
            y: "Motion to Dismiss SACC",
            p: 1,
            f: "03/17/2026",
            s: "Plaintiffs/AIDP MTD of SACC; hearing 4/17/2026",
          },
          { d: "155", y: "Opposition (Mando)", p: 0, f: "03/31/2026", s: "" },
          {
            d: "157",
            y: "Reply (Plaintiffs/AIDP)",
            p: 0,
            f: "04/03/2026",
            s: "",
          },
          {
            d: "165",
            y: "Order Taking Under Submission",
            p: 0,
            f: "04/15/2026",
            s: "Hearing vacated",
          },
        ],
      },
      {
        id: "F4",
        t: "MTD First Amended Complaint (Mando Consolidated MTD)",
        docs: [
          {
            d: "118",
            y: "Motion to Dismiss FAC (Neurocentria) [STRICKEN]",
            p: 0,
            f: "03/12/2026",
            s: "Stricken \u2014 filed as separate motion violating Standing Order",
          },
          {
            d: "119",
            y: "Motion to Dismiss FAC (ThreoTech) [STRICKEN]",
            p: 0,
            f: "03/12/2026",
            s: "Stricken \u2014 must consolidate",
          },
          {
            d: "163",
            y: "Order Striking 118 and 119",
            p: 0,
            f: "04/14/2026",
            s: "Mando may file single consolidated MTD by 4/24/2026; hearing 5/22/2026",
          },
          {
            d: "168",
            y: "Motion for Leave to Exceed Page Limit (Mando)",
            p: 1,
            f: "04/21/2026",
            s: "",
          },
          {
            d: "169",
            y: "Consolidated Motion to Dismiss FAC",
            p: 1,
            f: "04/24/2026",
            s: "Mando's consolidated MTD; hearing 5/22/2026",
          },
          {
            d: "171",
            y: "Opposition to Page Limit Motion",
            p: 0,
            f: "05/01/2026",
            s: "",
          },
          {
            d: "173",
            y: "Opposition to Consolidated MTD",
            p: 0,
            f: "05/01/2026",
            s: "",
          },
        ],
      },
    ],
  },
  {
    id: "secG",
    l: "G",
    n: "Claim Construction",
    threads: [
      {
        id: "G1",
        t: "Preliminary Claim Construction Disclosures",
        docs: [
          {
            d: "86",
            y: "Stip re CC Deadline",
            p: 0,
            f: "01/08/2026",
            s: "Extension to 1/19/2026 for '061 Patent",
          },
          { d: "88", y: "Order Approving Stip", p: 0, f: "01/08/2026", s: "" },
          {
            d: "95",
            y: "Joint Claim Construction Statement",
            p: 1,
            f: "01/23/2026",
            s: "Filed by all parties",
          },
          {
            d: "96",
            y: "Notice of Errata to JCCS",
            p: 0,
            f: "01/24/2026",
            s: "",
          },
          {
            d: "111",
            y: "Joint Notice of Narrowed Issues for CC",
            p: 0,
            f: "02/27/2026",
            s: "",
          },
          {
            d: "113",
            y: "Notice Requesting Clarification of CC Briefing Schedule",
            p: 0,
            f: "03/08/2026",
            s: "Mando re re-added patents",
          },
          {
            d: "114",
            y: "Reply re CC Briefing Clarification",
            p: 0,
            f: "03/09/2026",
            s: "Plaintiffs' response",
          },
          {
            d: "115",
            y: "Mando Notice re Patent Standing Order",
            p: 0,
            f: "03/09/2026",
            s: "",
          },
        ],
      },
      {
        id: "G2",
        t: "Claim Construction Briefing",
        docs: [
          {
            d: "116",
            y: "Plaintiffs' Claim Construction Brief",
            p: 1,
            f: "03/09/2026",
            s: "With exhibits A-F and Baker declaration",
          },
          {
            d: "143",
            y: "Mando's Responsive CC Brief",
            p: 1,
            f: "03/23/2026",
            s: "With exhibits 1-3",
          },
          {
            d: "144",
            y: "Abhyanker Declaration ISO CC Brief",
            p: 0,
            f: "03/24/2026",
            s: "",
          },
          {
            d: "150",
            y: "Plaintiffs' Reply CC Brief",
            p: 0,
            f: "03/30/2026",
            s: "",
          },
          {
            d: "151",
            y: "Clarifying Declaration of Dr. Sara Adams",
            p: 0,
            f: "03/31/2026",
            s: "Mando expert re claim construction",
          },
          {
            d: "152",
            y: "Abhyanker Declaration ISO Adams",
            p: 0,
            f: "03/31/2026",
            s: "",
          },
        ],
      },
    ],
  },
  {
    id: "secH",
    l: "H",
    n: "Sanctions Proceedings",
    threads: [
      {
        id: "H1",
        t: "Mando's Rule 11 Motion (withdrawn)",
        docs: [
          {
            d: "93",
            y: "Mando's Rule 11 Motion for Sanctions",
            p: 1,
            f: "01/19/2026",
            s: "Re magnesium L-threonate natural occurrence argument; hearing 3/6/2026",
          },
          {
            d: "103",
            y: "Opposition (Plaintiffs)",
            p: 0,
            f: "02/13/2026",
            s: "",
          },
          {
            d: "104",
            y: "Withdrawal of Rule 11 Motion (Mando)",
            p: 0,
            f: "02/13/2026",
            s: "Mando withdraws motion",
          },
        ],
      },
      {
        id: "H2",
        t: "Court's OSC re Mando / Abhyanker Conduct",
        docs: [
          {
            d: "107",
            y: "OSC Issued Against Mando/Abhyanker",
            p: 1,
            f: "02/24/2026",
            s: "Court OSC re: bad faith filing, repeated motion withdrawal after oppositions, false representations; response due 3/6/2026",
          },
          {
            d: "108",
            y: "Mando's Response to OSC",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "109",
            y: "Sealed Exhibit B to Abhyanker Declaration",
            p: 0,
            f: "02/25/2026",
            s: "",
          },
          {
            d: "139",
            y: "Plaintiffs' Reply to OSC Response",
            p: 0,
            f: "03/20/2026",
            s: "With detailed fee tabulation",
          },
          {
            d: "149",
            y: "Mando's Reply ISO Response to OSC",
            p: 0,
            f: "03/27/2026",
            s: "",
          },
        ],
      },
      {
        id: "H3",
        t: "Plaintiffs' Motion for Sanctions (First)",
        docs: [
          {
            d: "161",
            y: "First Motion for Sanctions (Plaintiffs)",
            p: 1,
            f: "04/13/2026",
            s: "Under 28 U.S.C. \u00a7 1927, L.R. 83-7, inherent power; hearing 5/15/2026",
          },
          { d: "166", y: "Opposition (Mando)", p: 0, f: "04/16/2026", s: "" },
          { d: "172", y: "Reply (Plaintiffs)", p: 0, f: "05/01/2026", s: "" },
        ],
      },
      {
        id: "H4",
        t: "Plaintiffs' Second Motion for Sanctions",
        docs: [
          {
            d: "170",
            y: "Second Motion for Sanctions (Plaintiffs)",
            p: 1,
            f: "04/28/2026",
            s: "Against Mando/Abhyanker under L.R. 83-7, 83-8, \u00a7 1927, inherent power; hearing 6/5/2026",
          },
        ],
      },
    ],
  },
  {
    id: "secI",
    l: "I",
    n: "Sealing Motions",
    threads: [
      {
        id: "I1",
        t: "Gavenman Declaration Sealing (re MTD Counterclaims)",
        docs: [
          {
            d: "97",
            y: "Admin Motion to Seal (Mando)",
            p: 1,
            f: "01/27/2026",
            s: "Unopposed motion to seal portions of Gavenman declaration (Dkt 66-1)",
          },
          {
            d: "125",
            y: "Motion to File Exhibit 5 Under Seal [STRICKEN]",
            p: 0,
            f: "03/13/2026",
            s: "Stricken \u2014 public version filed without redactions; motion misfiled",
          },
          { d: "164", y: "Order Striking 125", p: 0, f: "04/14/2026", s: "" },
        ],
      },
    ],
  },
];

const JUUL_WITNESSES = [
  {
    id: "qp",
    name: "Quarry Pak",
    role: "SFUSD 30(b)(6) - Program Costs",
    ph: "👩‍💼",
    party: "Plaintiff",
    notes: [
      "SFUSD Finance 30(b)(6) witness. Deposed Dec 21, 2021. JLI's most important deposition exhibit.",
      "THE admission: when asked what costs SFUSD incurred addressing JUUL crisis - 'We don't have that information.'",
      "JLI uses this as SJ centerpiece (Dkt.3396): SFUSD cannot quantify past program costs = no damages = equitable relief barred under Sonner.",
      "SFUSD rebuttal: Pak's testimony about line-item tracking, not total harm. Dorn Report ($78.8M forward-looking) and $34,875 documented direct costs supplement record.",
      "Superintendent Matthews also deposed: 'unaware of any document that quantifies staff time.' Both admissions used together in JLI SJ brief.",
    ],
  },
  {
    id: "jm",
    name: "James Monsees",
    role: "JUUL Co-Founder / CEO (Orrick client)",
    ph: "👔",
    party: "Defendant",
    notes: [
      "Co-founded JUUL 2015 with Bowen. Orrick represents Monsees personally (Kramer, Askew, Seaton, Hwang on docket).",
      "Testified JUUL never designed for youth - contradicted by Dkt.3447 SOF ¶47: on distribution list for youth uptake board decks. Best impeachment exhibit.",
      "JUUL School program approved under Monsees leadership: reps trained to visit high schools and track youth metrics as KPI.",
      "Depo topics: youth metrics knowledge, FDA PMTA strategy, mint/mango flavor marketing, Altria negotiation.",
    ],
  },
  {
    id: "js",
    name: "Julia Seliavski",
    role: "Named PI Plaintiff - VA Opt-Out",
    ph: "👩",
    party: "Plaintiff",
    notes: [
      "Richmond, VA. First use: July 2017 at age 16. Last use: Spring 2018. Purchased at 7-Eleven, Circle K, Walgreens.",
      "Injuries: nicotine addiction, poisoning, anger/outbursts, mood swings. Case remands to E.D. Va. post-SJ.",
      "CRITICAL GAP: No medical causation expert retained. Without expert, JLI moves SJ on PI damages in remand court.",
      "SOL: VA 2-year personal injury SOL ran Spring 2020 absent tolling. Discovery rule and fraudulent concealment arguments needed.",
      "VA contributory negligence = complete bar to recovery. JLI will push comparative fault via retailer chain (7-Eleven, etc.).",
    ],
  },
  {
    id: "ar",
    name: "Altria Group (Corp. Rep.)",
    role: "30(b)(6) - Antitrust + RICO Defendant",
    ph: "🏢",
    party: "Defendant",
    notes: [
      "Dec 2018: paid $12.8B for 35% AND simultaneously withdrew MarkTen and Green Smoke from market. These two facts together are the Sherman Act §1 horizontal market allocation theory.",
      "30(b)(6) topics: due diligence files showing youth penetration knowledge pre-investment; market exit decision-making; regulatory expertise provided to JLI.",
      "Altria consumer class settled separately from JLI $255M class. Antitrust case (3:20-cv-02345) opt-out deadline June 13 2026. Trial Sept 28 2026.",
      "Rule 23(f) petition by Altria at Ninth Circuit pending - could affect antitrust trial schedule.",
    ],
  },
  {
    id: "dorn",
    name: "Expert: Dorn (SFUSD Abatement)",
    role: "SFUSD Expert - Site Inspection / Abatement",
    ph: "🔬",
    party: "Plaintiff",
    notes: [
      "SFUSD's damages expert. Conducted site inspections at SFUSD schools documenting physical damage: bathroom locks broken, fences cut, master keys stolen, gardens shut down.",
      "$78.8M forward-looking abatement estimate covering infrastructure, curriculum, staff training, and intervention programs.",
      "JLI attacks Dorn under Sonner v. Premier Nutrition: equitable abatement unavailable when adequate legal remedy exists. If Pak admission defeats legal damages, Dorn's equitable estimate also fails.",
      "Site inspection reports are key supporting documents for SFUSD's public nuisance theory - physical property damage documented at 6 specific schools.",
    ],
  },
  {
    id: "ab",
    name: "Adam Bowen",
    role: "JUUL Co-Founder / Chief Product Officer",
    ph: "👔",
    party: "Defendant",
    notes: [
      "Co-inventor of JUUL nicotine salt delivery mechanism. The 59mg/mL formulation (vs 16mg in combustible cigs) is central to design defect and failure-to-warn claims.",
      "USB charger concealability was a deliberate product design choice - cited in youth usage discovery.",
      "Filed own MSJ (Reply: Dkt.3527). Internal emails show Bowen tracked early user addiction signals.",
      "Depo topics: nicotine salt bioavailability design rationale, addiction risk awareness, formulation alternatives rejected.",
    ],
  },
  {
    id: "np",
    name: "Nicholas Pritzker",
    role: "JUUL Board Member / Tao Capital",
    ph: "💼",
    party: "Defendant",
    notes: [
      "JUUL investor via Tao Capital Management. Board member during 2017-2019 peak youth-marketing controversy.",
      "Individual RICO defendant - requires showing Pritzker conducted or participated in directing the enterprise. Board meeting minutes are key.",
      "Dismissed from antitrust case (3:20-cv-02345) on standing grounds Aug 2021, but remains in MDL PI and SFUSD cases.",
      "Co-defendant in antitrust class cert Feb 26 2026 (Dkt.635) as individual investor with Valani.",
    ],
  },
  {
    id: "rv",
    name: "Riaz Valani",
    role: "JUUL Board Member / D.E. Shaw Investor",
    ph: "💼",
    party: "Defendant",
    notes: [
      "JUUL investor via D.E. Shaw. Board member during youth-marketing controversy.",
      "Co-individual defendant with Pritzker in antitrust class action (3:20-cv-02345). Class certified Feb 26 2026.",
      "Dismissed from antitrust case on standing grounds Aug 2021 alongside Pritzker; remains in MDL.",
      "Board-level awareness of youth marketing same issue as Pritzker - key to individual RICO liability.",
    ],
  },
  {
    id: "fda",
    name: "FDA Expert / Corporate Rep.",
    role: "Regulatory Expert (Both Sides)",
    ph: "🏛️",
    party: "Both",
    notes: [
      "FDA issued MDO June 2022 based on insufficient toxicological data and no net public health benefit. 5th Circuit emergency stay issued same day.",
      "MDO withdrawn for further review - legally non-final. JLI argues this means no regulatory finding of inadequacy. Plaintiffs argue it shows inadequacy independent of final status.",
      "PMTA review ongoing. JLI's good-faith regulatory engagement (comprehensive PMTA submission) is its regulatory compliance defense - Orrick will argue courts should defer to FDA.",
      "Altria HSR (Hart Scott Rodino) expert admissibility reserved at class cert (Dkt.635) - admitting this expert risks jury confusion on investment antitrust theory.",
    ],
  },
];

const JUUL_MOTIONS = [
  {
    type: "past",
    date: "May 29, 2020",
    title: "Motion to Stay (FDA Primary Jurisdiction)",
    dkt: 626,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Denied",
    detail:
      "JLI argued FDA PMTA review should precede MDL adjudication. Judge Orrick denied stay.",
  },
  {
    type: "past",
    date: "Jun 22, 2020",
    title: "Motion to Dismiss Bellwether GE Complaints",
    dkt: 740,
    movant: "Defendant",
    opponent: "Plaintiff",
    status: "Partial",
    detail:
      "Motion to dismiss government entity bellwether complaints. Partially granted.",
  },
  {
    type: "past",
    date: "Feb 01, 2023",
    title: "JLI Class Settlement Preliminary Approval",
    dkt: 3400,
    movant: "Plaintiff",
    opponent: "-",
    status: "Granted",
    detail:
      "Judge Orrick grants preliminary approval of JLI class action settlement. Notice to class authorized.",
  },
  {
    type: "past",
    date: "May 23, 2023",
    title: "JLI Class Settlement Final Approval",
    dkt: 3779,
    movant: "Plaintiff",
    opponent: "-",
    status: "Granted",
    detail:
      "Final approval granted. Class consumer claims resolved. Individual PI cases proceed.",
  },
  {
    type: "past",
    date: "Jul 27, 2023",
    title: "Altria Class Settlement Preliminary Approval",
    dkt: 4082,
    movant: "Plaintiff",
    opponent: "-",
    status: "Pending",
    detail:
      "Motion for preliminary approval of Altria class action settlement. Hearing set 9/6/2023.",
  },
  {
    type: "past",
    date: "Jul 31, 2023",
    title: "Seliavski Amended Short-Form Complaint",
    dkt: 4094,
    movant: "Plaintiff",
    opponent: "-",
    status: "Filed",
    detail:
      "Amended PI complaint for Julia Seliavski. Levin Simes LLP. 19 causes of action. Jury demand.",
  },
  {
    type: "upcoming",
    date: "TBD",
    title: "Plaintiff Fact Sheet Submission",
    dkt: null,
    movant: "Plaintiff",
    opponent: "-",
    status: "Due",
    detail:
      "PFS per CMO No. 8. Must include medical records, product history, healthcare providers.",
  },
  {
    type: "upcoming",
    date: "TBD",
    title: "Expert Designation Deadline",
    dkt: null,
    movant: "Both",
    opponent: "-",
    status: "Due",
    detail: "Damages, toxicology, addiction medicine expert disclosures.",
  },
  {
    type: "upcoming",
    date: "TBD",
    title: "Bellwether Selection (Individual PI)",
    dkt: null,
    movant: "Court",
    opponent: "-",
    status: "Due",
    detail:
      "Individual PI bellwether pool selection from remaining short-form complaints.",
  },
];

const JUUL_CASELAW = [
  {
    id: "jcl28",
    cs: "Sonner v. Premier Nutrition Corp.",
    ci: "971 F.3d 834 (9th Cir. 2020)",
    h: "Equitable relief (restitution, abatement) is unavailable when an adequate remedy at law exists. Applied by JLI to argue SFUSD equitable abatement claim is barred because SFUSD has an adequate remedy in legal damages.",
    by: "Defendant",
    color: "#3b82f6",
    ctx: "SFUSD Damages / Equitable Relief",
    doc: "Dkt.3396",
    v: true,
    quote: null,
  },
  {
    id: "jcl32",
    cs: "In re JUUL Antitrust Litigation",
    ci: "No. 3:20-cv-02345-WHO (N.D. Cal. Feb. 26, 2026)",
    h: "Judge Orrick certified Direct Purchaser, Indirect Purchaser, and Indirect Reseller classes. Applied California Cartwright Act extraterritorially to 31 Illinois Brick repealer states. Excluded AR SC TN VA as indirect purchaser class action bars.",
    by: "Plaintiff",
    color: "#ef4444",
    ctx: "Antitrust Class Certification / Parallel Case",
    doc: "Dkt.635 (3:20-cv-02345)",
    v: true,
    quote:
      "The court finds common questions of law and fact are plentiful among the certified classes.",
  },
  {
    id: "jcl31",
    cs: "American Pipe & Construction Co. v. Utah",
    ci: "414 U.S. 538 (1974)",
    h: "Filing a class action tolls the statute of limitations for all putative class members. JUUL opt-out plaintiffs rely on American Pipe tolling to establish their claims were timely when they opted out and filed individual complaints.",
    by: "Plaintiff",
    color: "#ef4444",
    ctx: "Statute of Limitations / Class Action Tolling",
    doc: "CMO 17/19 SOL briefing",
    v: true,
    quote: null,
  },
  {
    id: "jcl27",
    cs: "Halpern-Felsher Nicotine Ignorance Study",
    ci: "SFUSD Expert Report (Dkt.3447)",
    h: "Expert survey showing 75% of SFUSD students who used JUUL did not know the product contained nicotine -- key support for failure-to-warn and public nuisance damages.",
    by: "Plaintiff",
    color: "#ef4444",
    ctx: "Failure to Warn / SFUSD Damages",
    doc: "Dkt.3447",
    v: true,
    quote:
      "75% of student users surveyed did not know JUUL contained nicotine.",
  },
  {
    cs: "Anderson v. Liberty Lobby, Inc.",
    ci: "477 U.S. 242 (1986)",
    ctx: "SJ Standard - Nonmovant Burden",
    h: "Party opposing summary judgment must present 'significant probative evidence' supporting each element of its claims; speculative evidence is insufficient.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.3396 (JLI MSJ)",
  },
  {
    cs: "Celotex Corp. v. Catrett",
    ci: "477 U.S. 317 (1986)",
    ctx: "SJ Standard - Moving Party",
    h: "Once movant identifies absence of evidence to support plaintiff's case, nonmovant must show specific facts demonstrating genuine issue for trial.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.3396 (JLI MSJ)",
  },
  {
    id: "jcl29",
    cs: "Fox v. Ethicon Endo-Surgery, Inc.",
    ci: "35 Cal.4th 797 (2005)",
    h: "Discovery rule for California personal injury: limitations period does not begin until plaintiff discovers or should have discovered the injury and its cause. Key for Seliavski VA SOL tolling analysis.",
    by: "Plaintiff",
    color: "#ef4444",
    ctx: "Statute of Limitations / Discovery Rule",
    doc: "CMO 17/19 SOL briefs",
    v: true,
    quote: null,
  },
  {
    cs: "Rincon Band of Luiseño Mission Indians v. Flynt",
    ci: "70 Cal. App. 5th 1059 (2021)",
    ctx: "Public Nuisance - Governmental Standing",
    h: "Under Cal. Civ. Code §§ 3493-3494 and CCP § 731, 'private person' does not include governmental entities not specifically authorized by statute. But government entities retain standing to sue for nuisances within their own jurisdictions on property-damage theory.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.3396 (JLI MSJ); Dkt.3447 (SFUSD Opp.)",
  },
  {
    id: "jcl33",
    cs: "Altria Group, Inc. v. Good",
    ci: "555 U.S. 70 (2008)",
    h: "FCLAA does not preempt state tort claims based on fraud -- only claims based on smoking and health such that preemption depends on whether the claim is effectively a failure-to-warn claim disguised as fraud. Plaintiffs use this to preserve misrepresentation claims from FCLAA preemption.",
    by: "Plaintiff",
    color: "#ef4444",
    ctx: "FCLAA Preemption Limit",
    doc: "Plaintiffs MSJ opposition",
    v: true,
    quote: null,
  },
  {
    cs: "Altria Group v. Good",
    ci: "555 U.S. 70 (2008)",
    ctx: "Federal Preemption Defense",
    h: "Federal Cigarette Labeling Act does not preempt state fraud claims based on deceptive advertising of cigarette health risks.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Master Complaint",
  },
  {
    cs: "Cipollone v. Liggett Group",
    ci: "505 U.S. 504 (1992)",
    ctx: "Preemption Scope",
    h: "FCLA preempts 'smoking and health' state claims based on advertising or promotion, but not design defect or intentional tort claims.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Master Complaint",
  },
  {
    cs: "In re JUUL Labs (JUUL IV)",
    ci: "2022 WL 1814440 (N.D. Cal. June 2, 2022)",
    ctx: "Daubert - Causation Experts",
    h: "Causation disputes present 'battle of the experts to be resolved by jury.' Dr. Cutler's methodology goes to weight not admissibility.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "In re JUUL Labs (JUUL III)",
    ci: "2022 WL 1601418 (N.D. Cal. Apr. 29, 2022)",
    ctx: "SJ - Altria Duty / Causation",
    h: "Denying Altria SJ on duty and causation; jury may reasonably infer Altria's knowledge of youth uptake motivated its investment and actions to boost JUUL sales.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "In re JUUL Labs (JUUL II)",
    ci: "533 F. Supp. 3d 858 (N.D. Cal. 2021)",
    ctx: "Second Round MTD - RICO Enterprise",
    h: "Court reaffirms RICO enterprise theory; JLI as corporate enterprise used by individual defendants as vehicle for fraud scheme.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447; Dkt.3527",
  },
  {
    cs: "In re JUUL Labs (JUUL I)",
    ci: "497 F. Supp. 3d 552 (N.D. Cal. 2020)",
    ctx: "MTD Rulings - RICO/Nuisance/Negligence",
    h: "Judge Orrick denies most motions to dismiss; finds government entity school district plaintiffs stated viable claims for RICO, nuisance, and negligence against JLI, Altria, and management defendants.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "In re Volkswagen 'Clean Diesel' Mktg.",
    ci: "MDL 2672, 2017 WL 4890594 (N.D. Cal.)",
    ctx: "RICO Property Injury - Overpayment",
    h: "Out-of-pocket loss not required for financial loss to be tangible or concrete for RICO property injury.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Cedric Kushner Promotions v. King",
    ci: "533 U.S. 158 (2001)",
    ctx: "RICO Enterprise - Corporate Defendant",
    h: "RICO protects public from those who would use a corporation as a vehicle for unlawful activity; corporation itself can be the RICO enterprise.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Reves v. Ernst & Young",
    ci: "507 U.S. 170 (1993)",
    ctx: "RICO Conduct Element",
    h: "To 'conduct' enterprise affairs one must have some part in directing those affairs; the standard does not require primary responsibility.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Dkt.3447; Dkt.3527",
  },
  {
    cs: "H.J. Inc. v. Northwestern Bell",
    ci: "492 U.S. 229 (1989)",
    ctx: "RICO Pattern - Continuity",
    h: "Pattern of racketeering activity requires continuity - either a closed period of repeated conduct or an open-ended scheme with threat of repetition.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Bridge v. Phoenix Bond & Indem. Co.",
    ci: "553 U.S. 639 (2008)",
    ctx: "RICO Causation / Mail Fraud",
    h: "RICO plaintiff asserting mail fraud need not show reliance; 'by reason of' requires direct relation - foreseeable and natural consequence of scheme.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Salinas v. United States",
    ci: "522 U.S. 52 (1997)",
    ctx: "RICO Conspiracy - § 1962(d)",
    h: "§ 1962(d) does not require each conspirator personally commit predicate acts; agreeing to facilitate the scheme suffices.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Diaz v. Gates",
    ci: "420 F.3d 897 (9th Cir. 2005)",
    ctx: "RICO - Injury to Business or Property",
    h: "RICO 'injury to business or property' analyzed under state law; concrete financial loss need not be out-of-pocket.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "People v. ConAgra Grocery Products Co.",
    ci: "17 Cal. App. 5th 51 (2017)",
    ctx: "Abatement Remedy - Equitable Fund",
    h: "Abatement order is equitable remedy; equitable abatement fund to remediate hazard is not a disguised damages award and may be ordered against manufacturer.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3396; Dkt.3447",
  },
  {
    cs: "Selma Pressure Treating Co. v. Osmose Wood Preserving",
    ci: "221 Cal. App. 3d 1601 (1990)",
    ctx: "Public Nuisance - Government Entity as 'Person'",
    h: "Where a public entity can show a property interest injuriously affected by a nuisance, it may pursue the full panoply of tort remedies available to private persons under CCP § 731.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3396; Dkt.3447",
  },
  {
    cs: "Orange Cnty. Water Dist. v. Sabic Innovative Plastics",
    ci: "14 Cal. App. 5th 343 (2017)",
    ctx: "Public Nuisance - Property Interest",
    h: "Property interest sufficient to support private nuisance is generally sufficient for public nuisance; parties agreed public agency may bring nuisance claim on its own behalf where own property rights are affected.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Lamont Storm Water Dist. v. Pavich",
    ci: "78 Cal. App. 4th 1081 (2000)",
    ctx: "Public Nuisance - Special District Standing",
    h: "When legislature grants public entities power to abate nuisances, it does so specifically and in clear terms; storm water district lacked such authority.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.3396; Dkt.3447",
  },
  {
    cs: "J'Aire Corp. v. Gregory",
    ci: "24 Cal. 3d 799 (1979)",
    ctx: "Negligence - Economic Loss / Duty",
    h: "Recovery for negligently caused purely economic harm may be permitted where the transaction was intended to affect plaintiff and harm was foreseeable (Biakanja factors).",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Dkt.3447; Dkt.3527",
  },
  {
    cs: "Southern California Gas Leak Cases",
    ci: "7 Cal. 5th 391 (2019)",
    ctx: "Negligence - Economic Loss Rule",
    h: "Duty to prevent economic harm to third parties limited to cases where conduct was intended to affect plaintiff and harm was sufficiently foreseeable.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Dkt.3447 (SFUSD Opp.)",
  },
  {
    cs: "Greenman v. Yuba Power Prods.",
    ci: "59 Cal.2d 57 (1963)",
    ctx: "Strict Products Liability",
    h: "Strict liability applies when a manufacturer places a defective product on the market and the product causes injury.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Master Complaint",
  },
  {
    cs: "Barker v. Lull Eng'g Co.",
    ci: "20 Cal.3d 413 (1978)",
    ctx: "Design Defect Standard",
    h: "Product is defective in design if it fails the consumer expectation test or risk-utility analysis.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Master Complaint",
  },
  {
    id: "jcl30",
    cs: "Rincon Band of Luiseno Mission Indians v. Flynt",
    ci: "No. 19-cv-1145 (S.D. Cal.)",
    h: "Government entities (including school districts) lack standing to assert public nuisance claims based on products available to the public generally. JLI cites this to challenge SFUSD standing in JUUL nuisance claim.",
    by: "Defendant",
    color: "#3b82f6",
    ctx: "SFUSD Standing / Public Nuisance",
    doc: "Dkt.3396",
    v: true,
    quote: null,
  },
];

const JUUL_ASSETS = [
  /* ── Key Pleadings ── */
  {
    id: 1,
    cat: "Filing",
    title: "Seliavski Amended Short-Form Complaint (Dkt.4094)",
    desc: "PI SFC. 19 causes of action. Addiction, nicotine poisoning, behavioral injuries. Jury demand. Levin Simes LLP.",
    date: "Jul 31, 2023",
    pages: 11,
    color: "#ef4444",
  },
  {
    id: 2,
    cat: "Filing",
    title: "Master Complaint PI (Dkt.388/420)",
    desc: "Consolidated Master Complaint (PI) - adopted by all PI plaintiffs via Short-Form Complaint per CMO No. 7.",
    date: "Apr 6, 2020",
    pages: 280,
    color: "#ef4444",
  },
  {
    id: 3,
    cat: "Filing",
    title: "SFUSD Amended Complaint (ECF 9, Case 19-cv-8177)",
    desc: "SFUSD's 163-paragraph amended complaint. Public nuisance (Count I), RICO (Count II), negligence (Count III), gross negligence (Count IV). Abatement + damages.",
    date: "Mar 11, 2021",
    pages: 163,
    color: "#ef4444",
  },
  /* ── Summary Judgment Briefing (SFUSD) ── */
  {
    id: 4,
    cat: "Filing",
    title: "JLI Motion for Summary Judgment re SFUSD (Dkt.3396)",
    desc: "Kirkland & Ellis (Bernick, Smith, Farrell, Wilcox). 34 pp. (Filed Under Seal). Three grounds: Rincon standing bar, Sonner abatement bar, zero past damages evidence.",
    date: "Aug 15, 2022",
    pages: 34,
    color: "#3b82f6",
  },
  {
    id: 5,
    cat: "Filing",
    title: "SFUSD Consolidated Opposition to MSJ (Dkt.3447)",
    desc: "149-page consolidated opposition. RICO, negligence, public nuisance. Extensive SOF from depositions, expert reports, documentary evidence. Co-Lead Counsel.",
    date: "Sep 12, 2022",
    pages: 149,
    color: "#ef4444",
  },
  {
    id: 6,
    cat: "Filing",
    title: "Bowen Reply ISO MSJ - SFUSD (Dkt.3527)",
    desc: "Boersch & Illovsky (Illovsky). 20 pp. RICO conduct element; pattern of racketeering; RICO causation; statute of limitations; duty arguments.",
    date: "Sep 27, 2022",
    pages: 20,
    color: "#3b82f6",
  },
  /* ── CMOs & Court Orders ── */
  {
    id: 7,
    cat: "Court Order",
    title: "CMO No. 7 - Short-Form Complaint System (Dkt.405)",
    desc: "Governs adoption of Master Complaint (PI) and Short-Form Complaint procedure. Basis for all PI individual filings.",
    date: "Mar 27, 2020",
    pages: 8,
    color: "#10b981",
  },
  {
    id: 8,
    cat: "Court Order",
    title: "CMO No. 8 - Plaintiff Fact Sheet Implementation (Dkt.406)",
    desc: "Plaintiff Fact Sheet requirements. Medical records, product purchase history, healthcare providers. Mandatory per CMO 8.",
    date: "Mar 27, 2020",
    pages: 12,
    color: "#10b981",
  },
  {
    id: 9,
    cat: "Court Order",
    title: "JLI Class Settlement Final Approval (Dkt.3779)",
    desc: "Final approval of JLI class action settlement. Class consumer claims resolved. Individual PI short-form complaint cases proceed.",
    date: "May 23, 2023",
    pages: 45,
    color: "#10b981",
  },
  /* ── Judge Orrick Standing Orders ── */
  {
    id: 10,
    cat: "Local Rules",
    title: "Judge Orrick - Standing Order for Civil Cases (May 2023)",
    desc: "All civil practice requirements: discovery disputes (joint 5-page letter brief), protective orders, courtesy copies, summary judgment limits (1 motion), class action settlement guidance. Zoom for law & motion.",
    date: "May 2023",
    pages: 4,
    color: "#a78bfa",
  },
  {
    id: 11,
    cat: "Local Rules",
    title: "Judge Orrick - Civil Pretrial Order (Aug 23, 2023)",
    desc: "Pretrial conference requirements. Joint statement 14 days prior. Witness lists, exhibit exchange, motions in limine (25-page limit), voir dire, verdict form. Trial 8:30 AM–1:30 PM.",
    date: "Aug 23, 2023",
    pages: 9,
    color: "#a78bfa",
  },
  {
    id: 12,
    cat: "Local Rules",
    title: "Judge Orrick - CMC Order Template (May 25, 2023)",
    desc: "Case Management Conference requirements. Joint CMS 7 days prior. All parties via Zoom. Pro se Legal Help Center at 450 Golden Gate Ave, 15th Fl.",
    date: "May 25, 2023",
    pages: 3,
    color: "#a78bfa",
  },
  {
    id: 13,
    cat: "Local Rules",
    title: "Judge Orrick - Standing Order on Admin Motions to Seal (Sep 2022)",
    desc: "Sealing requirements: certify compliance, identify each doc, identify designating party, state basis, rebut public access presumption (good cause or compelling reasons per Kamakana). Narrowly tailored only.",
    date: "Sep 2022",
    pages: 2,
    color: "#a78bfa",
  },
  {
    id: 14,
    cat: "Local Rules",
    title:
      "Judge Orrick - Juror Questionnaires & Social Media Research (Aug 23, 2023)",
    desc: "No LinkedIn research (notification risk). No follow/friend requests. No in-person surveillance. Only publicly available sources. No contact with prospective jurors in any form.",
    date: "Aug 23, 2023",
    pages: 2,
    color: "#a78bfa",
  },
  {
    id: 15,
    cat: "Local Rules",
    title:
      "N.D. Cal. - Standing Order: Joint Case Management Statement (Nov 2023)",
    desc: "All N.D. Cal. judges require identical information in Joint CMS: 19-point checklist including jurisdiction, facts, legal issues, motions, amendment, evidence preservation, discovery, ADR, trial estimate.",
    date: "Nov 2023",
    pages: 2,
    color: "#a78bfa",
  },
  /* ── Evidence ── */
  {
    id: 16,
    cat: "Evidence",
    title: "FDA Marketing Denial Order (Jun 2022)",
    desc: "FDA MDO finding JUUL's toxicological data insufficient to support premarket authorization. Later stayed by 5th Circuit; FDA withdrew for further review.",
    date: "Jun 2022",
    pages: 28,
    color: "#f59e0b",
  },
  {
    id: 17,
    cat: "Evidence",
    title: "JUUL Internal Product & Marketing Documents",
    desc: "Internal docs: 5% nicotine salt formulation, 'JUUL School' response materials, board decks on youth data, mint-flavour scheme communications. Subject to protective order.",
    date: "Discovery",
    pages: 500,
    color: "#f59e0b",
  },
  {
    id: 18,
    cat: "Evidence",
    title: "SFUSD Site Inspection Reports (Dorn)",
    desc: "Michael Dorn expert reports from site visits to all SFUSD secondary schools. Balboa HS boarded bathroom, McAteer cut fence, Aptos stolen key, Denman closed garden. Infrastructure cost estimate: $78.8M.",
    date: "2022",
    pages: 120,
    color: "#f59e0b",
  },
  {
    id: 19,
    cat: "Filing",
    title: "MDL Transfer Order (Dkt.1) + Pretrial Order #1 (Dkt.2)",
    desc: "MDL Panel transfer order creating MDL 2913. Judge Orrick assigned. Pretrial Order #1 establishes leadership, direct filing, and initial conference procedures.",
    date: "Oct 2, 2019",
    pages: 13,
    color: "#a78bfa",
  },
];

const JUUL_LOCS = [
  {
    id: "nc",
    name: "N.D. Cal. - Judge Orrick, Courtroom 2",
    area: "450 Golden Gate Ave, SF, CA 94102",
    notes:
      "MDL forum. Courtroom 2, 17th Floor. Zoom for CMC and law & motion (Wednesdays 2 PM). In-person for Pretrial Conference (Mondays 2 PM). Courtroom Deputy: Jean Davis - whocrd@cand.uscourts.gov / 415-522-2077.",
  },
  {
    id: "lr",
    name: "Local Rules & Standing Orders (Judge Orrick)",
    area: "N.D. Cal.",
    notes:
      "Key standing orders: Civil Cases (May 2023) · Pretrial Order (Aug 2023) · CMC Order · Sealing Order (Sep 2022) · Juror Questionnaires (Aug 2023). Discovery disputes: joint 5-page letter brief required. Summary judgment: 1 motion per party. Courtesy copies only for exhibits >100 pp in SJ/TRO/PI motions.",
  },
  {
    id: "sfusd",
    name: "San Francisco Unified School District HQ",
    area: "555 Franklin St, San Francisco, CA",
    notes:
      "Plaintiff SFUSD. 107 schools, ~52,000 students, 9,200 employees. $1.1B annual budget with $125M deficit. Student & Family Services Division: lead division on e-cigarette response. Quarry Pak (30b6 rep), Kim Coates (Exec. Director), Erica Lingrell (program admin).",
  },
  {
    id: "jl",
    name: "JUUL Labs, Inc. HQ",
    area: "San Francisco, CA",
    notes:
      "Principal place of business (original). Former PAX Labs. All product design decisions, nicotine salt formulation, marketing campaign decisions made here. Founders Monsees and Bowen both CA residents.",
  },
  {
    id: "ag",
    name: "Altria Group / Philip Morris USA HQ",
    area: "Richmond, VA",
    notes:
      "$12.8B JUUL stake executed Dec 2018. Altria Client Services and AGDC also based in Richmond. After investment, installed K.C. Crosthwaite (career Altria exec) as JLI CEO. All Virginia corporations.",
  },
  {
    id: "pl",
    name: "Plaintiff - Julia Seliavski",
    area: "Richmond, VA",
    notes:
      "Plaintiff resides in Richmond, VA. Used JUUL July 2017–Spring 2018. Would have filed in USDC E.D. Virginia absent direct filing order (CMO No. 3).",
  },
  {
    id: "fda",
    name: "FDA / Center for Tobacco Products",
    area: "Silver Spring, MD",
    notes:
      "Issued Marketing Denial Order June 2022 (toxicological concerns). MDO stayed by 5th Circuit; FDA withdrew for further review. Issued warning letter to JUUL Apr 2018. PMTA review ongoing. Relevant to preemption and FDA primary jurisdiction arguments.",
  },
];
/* END JUUL DATA */

const MUSK_F_TL = [
  {
    date: "2015-10-01",
    label: "OpenAI Founding - 'For the benefit of humanity'",
    detail:
      "Musk, Altman, Brockman and others found OpenAI as a nonprofit. Altman email proposes 'some sort of nonprofit' where 'tech belongs to the world.'",
    ref: "PX003",
    type: "factual",
    ecf: "May 2015 Altman email (PX003/DX-501): 'view investment in spirit of a donation.'",
  },
  {
    date: "2017-09-20",
    label: "Musk issues ultimatum to take over OpenAI",
    detail:
      "Musk sends ultimatum demanding CEO role or control. Altman and Brockman reassure Musk of nonprofit commitment. Musk steps down from board Feb 2018.",
    ref: "PX157/158/159",
    type: "factual",
    ecf: "PX157 (DX-704): Sept 20, 2017 Musk ultimatum email. PX158/159: Altman/Brockman reassurances.",
  },
  {
    date: "2019-03-01",
    label: "OpenAI LP (for-profit subsidiary) formed",
    detail:
      "OpenAI forms capped-profit LP subsidiary. LP Agreement front page: 'view investment in spirit of a donation.' Defendants argue nonprofit board approved.",
    ref: "PX200/DX-839",
    type: "factual",
    ecf: "Oct 2018 OpenAI LP Agreement.",
  },
  {
    date: "2019-07-22",
    label: "Microsoft invests $1B in OpenAI",
    detail:
      "Microsoft makes initial $1B investment in OpenAI LP. Kevin Scott (Microsoft CTO) email Mar 2018: 'I wonder if the big OpenAI donors are aware of these plans?'",
    ref: "PX230/MDX-1599",
    type: "factual",
    ecf: "Mar 7, 2018 Scott-to-Nadella email (PX230/MDX-1599).",
  },
  {
    date: "2020-09-24",
    label: "Musk tweets OpenAI 'captured by Microsoft'",
    detail:
      "Musk publicly posts that OpenAI is 'essentially captured by Microsoft' on Twitter/X.",
    ref: "MDX-1537",
    type: "factual",
    ecf: "Sept 24, 2020 Musk tweet (MDX-1537). Key defense SOL evidence.",
  },
  {
    date: "2023-11-01",
    label: "OpenAI board fires Altman; Microsoft reinstates him",
    detail:
      "OpenAI board removes Altman. Microsoft orchestrates his reinstatement within days. Musk argues this is key evidence of Microsoft control and breach.",
    ref: "SAC ¶¶ 150-180",
    type: "factual",
    ecf: "Nov 2023 OpenAI board coup and Altman reinstatement.",
  },
  {
    date: "2024-02-01",
    label: "OpenAI announces for-profit conversion plan",
    detail:
      "OpenAI announces plan to convert from nonprofit to public benefit corporation. Triggers preliminary injunction motion by Musk.",
    ref: "Dkt.46",
    type: "factual",
    ecf: "Plaintiffs allege this is culmination of charitable trust breach.",
  },
];
const MUSK_P_TL = [
  {
    date: "2024-08-05",
    label: "Complaint filed - RICO against OpenAI",
    ref: "Dkt.1",
    type: "procedural",
    ecf: "Musk v. Altman et al., 4:24-cv-04722-YGR. Original 21-count complaint.",
  },
  {
    date: "2024-11-14",
    label: "First Amended Complaint - adds Microsoft, Hoffman",
    ref: "Dkt.32",
    type: "procedural",
    ecf: "FAC adds Microsoft, Reid Hoffman, Shivon Zilis, Deannah Templeton, CA AG Bonta.",
  },
  {
    date: "2024-11-29",
    label: "Motion for Preliminary Injunction filed",
    ref: "Dkt.46",
    type: "procedural",
    ecf: "Re OpenAI conversion to for-profit. Denied 3/4/2025 (Dkt.121).",
  },
  {
    date: "2025-03-04",
    label: "Preliminary Injunction DENIED",
    ref: "Dkt.121",
    type: "procedural",
    ecf: "Judge YGR denies PI. Express contract claim dismissed w/o leave.",
  },
  {
    date: "2025-05-01",
    label: "MTD Order - charitable trust claim survives",
    ref: "Dkt.163",
    type: "procedural",
    ecf: "Express contract dismissed w/o leave; charitable trust, unjust enrichment survive. Microsoft MTD granted in part.",
  },
  {
    date: "2025-08-12",
    label: "MTD SAC Order; Counterclaims DENIED",
    ref: "Dkt.228",
    type: "procedural",
    ecf: "RICO counts dismissed. OpenAI counterclaim MTD denied. Antitrust (VIII-XV) stayed.",
  },
  {
    date: "2026-01-15",
    label: "MSJ: OpenAI DENIED; Microsoft GRANTED in part",
    ref: "Dkt.390",
    type: "procedural",
    ecf: "Trial reset to 4/27/2026 jury selection, 4/28–5/22/2026 trial.",
  },
  {
    date: "2026-04-24",
    label: "Fraud claims dismissed with prejudice",
    ref: "Dkt.497",
    type: "procedural",
    ecf: "Plaintiffs voluntarily dismiss fraud/constructive fraud claims per Pretrial Order No. 4.",
  },
  {
    date: "2026-04-27",
    label: "Jury selection - Trial begins 4/28",
    ref: "Dkt.506",
    type: "procedural",
    ecf: "Jury selection 5:01 hours. Trial begins 4/28/2026.",
  },
  {
    date: "2026-04-30",
    label: "Trial Day 3 - §17510.8 briefs filed",
    ref: "Dkt.515/516",
    type: "procedural",
    ecf: "Competing briefs on Cal. Bus. & Prof. Code §17510.8 applicability.",
  },
];
const MUSK_S_TL = [
  {
    date: "2026-05-22",
    label: "Estimated end of trial (plaintiff case-in-chief)",
    ref: "Dkt.386",
    type: "scheduling",
    ecf: "Trial estimated through ~5/22/2026. Judge YGR.",
  },
  {
    date: "2026-05-22",
    label: "OpenAI counterclaims - Phase 2 TBD",
    ref: "Dkt.229",
    type: "scheduling",
    ecf: "OpenAI Second Amended Counterclaims proceeding in parallel. Schedule TBD.",
  },
  {
    date: "2027-01-01",
    label: "Antitrust Phase 2 Trial (est.)",
    ref: "Dkt.228",
    type: "scheduling",
    ecf: "Counts VIII-XV (antitrust) stayed pending Phase 1 outcome.",
  },
];

const MUSK_THEORIES = {
  plaintiff: [
    {
      title: "Charitable Trust Created by Representations + Donations",
      text: [
        "Musk's ~$38M in donations, combined with explicit written and oral representations that OpenAI would remain a nonprofit open-source entity, created an enforceable charitable trust under California law. The trust res is the donated funds and IP built with them; the trustee is OpenAI, Inc.; the beneficiary is the public.",
        "Strongest: Altman's own emails ('tech belongs to the world'), the Certificate of Incorporation ('not organized for private gain'), and Brockman's private journal ('wrong to steal the non-profit from Elon') establish both the representations and the intent. Schizer's expert report confirms a charitable trust was created under California Corp. Code §18506.",
        "Defendants will argue Musk's gifts were 'unrestricted' because no formal deed of trust was signed. But California law requires no writing for charitable trust creation - consistent representations plus accepted donations suffice. The LP Agreement's 'spirit of a donation' language actually supports the trust.",
        "Weakest point: Musk himself suggested for-profit structures in 2017 and raised no objection when the LP was formed in 2018. Defense will argue his conduct proves he understood the for-profit pivot.",
        "Musk's failure to object to the LP in 2018 is the biggest obstacle. The counterargument is that he was still being reassured by Altman and Brockman that the nonprofit mission would be preserved.",
      ],
      ground:
        "Count XVIII; Cal. Corp. Code §5142(a); PX003/DX-501; PX157/PX158; PX161/PX163",
    },
    {
      title: "Microsoft's Aiding & Abetting - Knowledge via Kevin Scott Email",
      text: [
        "The March 7, 2018 Kevin Scott email to Satya Nadella - 'I wonder if the big OpenAI donors are aware of these plans? Ideologically, I can't imagine that they funded an open effort…to then go build a closed, for profit thing on its back' - proves Microsoft had actual knowledge of OpenAI's charitable obligations before investing a single dollar.",
        "Strongest: The Kevin Scott email (PX230/MDX-1599) is devastating for Microsoft because it shows Microsoft's own CTO understood donors expected OpenAI to remain open and nonprofit - yet Microsoft invested $1B anyway in 2019. This is exactly the 'knowledge' element for aiding and abetting.",
        "Defense will argue the email shows Scott's personal speculation, not institutional knowledge, and that the OpenAI nonprofit board (including Musk's own associate Shivon Zilis) gave written warranties that no third-party rights existed.",
        "The board approval defense is strong - Zilis was on the OpenAI board and approved Microsoft deals. That's tough for Musk to overcome.",
        "Focus closing argument on whether Microsoft's deployment of $13.75B to extract exclusive IP licenses can be 'innocent' conduct. The scale of investment and the board coup assistance make pure innocence implausible.",
      ],
      ground:
        "Count XIX; PX230/MDX-1599; Altman-Nadella text thread (Nov 2023)",
    },
    {
      title: "Unjust Enrichment - $38M Subsidized $1 Trillion Enterprise",
      text: [
        "Even absent an enforceable charitable trust, Musk's $38 million in donations, personal reputation, and recruiting connections were used to build a company now valued at over $1 trillion. Defendants cannot retain that benefit when it was obtained on the false premise that they were building a nonprofit for humanity.",
        "Strongest: Unjust enrichment does not require a contract or a trust - only that plaintiff conferred a benefit, defendant retained it, and retention would be unjust. Musk's donations clearly conferred the early-stage funding that made OpenAI possible. The scale of unjust enrichment is extraordinary.",
        "Defense: Musk's donations were unrestricted gifts, freely given; no strings attached; spent for their intended purposes years ago. Dudney's FIFO analysis shows Musk's money was used up before any alleged breach.",
        "Statute of limitations is a real problem - if the claim accrued in 2019 when Microsoft invested, the 2-year statute may have run before Musk filed in 2024. Tolling arguments based on fraudulent concealment are available but uncertain.",
        "Frame the closing argument around fundamental fairness: Altman and Brockman used Musk's name, money, and connections as 'free startup capital' and now claim no obligation. The jury should find that unconscionable.",
      ],
      ground: "Count IV; Cal. Common Law; PX112/DX-1304 (contribution summary)",
    },
  ],
  defendant: [
    {
      title: "No Charitable Trust - Unrestricted Gifts, Board Approval",
      text: [
        "Musk made voluntary, unrestricted donations to a duly constituted nonprofit corporation whose board - not its donors - controls its activities. No deed of trust was ever signed. The nonprofit board, exercising its fiduciary duties, approved all for-profit activities after careful deliberation.",
        "Strongest: California courts require more than a donor's subjective intent to create a charitable trust. The formal corporate structure of OpenAI Inc. with independent board governance defeats any claim that Musk 'imposed' conditions on his donations.",
        "Plaintiff will point to Altman's emails and Brockman's journal as establishing conditions. But those documents show internal concerns about optics, not legally binding promises to Musk.",
        "The January 2016 'less open' email - where Musk himself replied 'Yup' - shows Musk understood from day one that full openness was not guaranteed.",
        "Musk's own 'Yup' reply to 'it will make sense to start being less open' is dispositive. A donor who agreed to becoming less open at the outset cannot later claim a trust requiring perpetual openness.",
      ],
      ground:
        "Statute of limitations; Dudney FIFO; DX-516 ('Yup' email); DX-532 (Brockman/Google)",
    },
    {
      title: "Statute of Limitations - Musk Knew by 2020 at the Latest",
      text: [
        "Musk's claims are time-barred. He publicly tweeted in September 2020 that 'OpenAI is essentially captured by Microsoft.' The 3-year limitations period for charitable trust breach began no later than that date - Musk filed in 2024, four years later.",
        "Strongest: Musk's own tweet - 'OpenAI is essentially captured by Microsoft' - is an admission of actual knowledge of the alleged breach in September 2020. It is exceedingly difficult for a plaintiff to claim delayed discovery when he posted his 'discovery' to 100 million Twitter followers.",
        "Plaintiff will argue the limitations period is tolled because defendants actively concealed the extent of the for-profit pivot and the specific Microsoft deals. But concealment tolling requires efforts to conceal - not mere public silence.",
        "Microsoft's $1B investment was publicly announced in July 2019. The general outlines of OpenAI's commercialization were publicly known by 2020.",
        "The 'equitable tolling' and 'fraudulent concealment' arguments Musk must rely on are fact-intensive and weak. Press release-level public disclosure generally defeats concealment arguments under California law.",
      ],
      ground:
        "Dkt. 456 (Pretrial Order No. 3); MDX-1537 (Musk tweet); California Code of Civil Procedure §338",
    },
    {
      title: "Unclean Hands - Musk Tried to Take Over OpenAI for Tesla/Himself",
      text: [
        "Musk's own conduct bars equitable relief. In 2017, he demanded that OpenAI be merged into Tesla and that he receive control over 60%+ of equity. His donations were motivated by competitive interest, not pure philanthropy. He launched xAI in 2023 - a competing for-profit AI company - and this suit is weaponized competition, not charitable enforcement.",
        "Strongest: Musk's own emails from 2017 demanding Tesla merger and personal control undercut his claim to be a disinterested charitable enforcer. The Attorney General - not a competitor - is the proper party to enforce charitable trusts. Musk's xAI is OpenAI's direct competitor.",
        "Plaintiff will argue unclean hands must be related to the specific conduct at issue and that Musk's failed Tesla bid is irrelevant to whether Altman and Brockman made enforceable promises.",
        "The xAI competitive angle is strongest: Musk has a $500B+ financial stake in destroying OpenAI as a competitor. Courts do not let competitors weaponize charitable trust law for market share.",
        "Musk's standing and motivation are the most powerful defense themes. The jury will be skeptical of a billionaire suing a former partner - whose company he tried to take over - under the guise of protecting the public charitable interest.",
      ],
      ground:
        "DX-827/PX236 (Tesla merger proposal); Musk xAI formation 2023; Rob Bonta Amicus",
    },
  ],
};
const MUSK_WITNESSES = [
  {
    id: "em",
    name: "Elon Musk",
    role: "Plaintiff; Founder",
    ph: "💼",
    party: "Plaintiff",
    notes: [
      "Testified Trial Day 1 (4/28/2026). Direct testimony re his intent when donating, conversations with Altman/Brockman, understanding of nonprofit mission.",
      "Key: Established that he donated on explicit promise that 'tech belongs to the world' and OpenAI would remain nonprofit and open-source.",
      "Cross: Defense hammered on Tesla merger bid (2017), his 'Yup' reply to 'less open' email, and xAI competitive launch in 2023.",
      "Watch: Whether his credibility holds after cross on competitive motive. Jury sympathy uncertain - Musk is polarizing figure.",
    ],
  },
  {
    id: "sa",
    name: "Samuel Altman",
    role: "CEO, OpenAI (Defendant)",
    ph: "🤖",
    party: "Defendant",
    notes: [
      "Co-founder and CEO. Testified re founding decisions, charitable purpose, for-profit pivot rationale.",
      "Key emails: May 2015 'tech belongs to the world' (PX003); Sept 2017 reassurances to Musk (PX158); Nov 2023 board crisis Nadella text thread (PX306).",
      "Filed declaration ISO PI opposition (Dkt. 107): claimed he never promised Musk specific nonprofit permanence.",
      "Cross opportunities: Nov 2023 board firing ('lack of candor'); recapitalization decision; Microsoft relationship management.",
    ],
  },
  {
    id: "gb",
    name: "Gregory Brockman",
    role: "Co-founder, President (Defendant)",
    ph: "👤",
    party: "Defendant",
    notes: [
      "TRIAL DEVELOPMENT (Dkt.522): OpenAI filed May 3 2026 to elicit through Brockman that Musk sent him a message Apr 25 2026 (2 days before trial): By the end of this week, you and Sam will be the most hated men in America. If you insist, so it will be. YGR must rule on admissibility before or during Brockman examination.",
      "Co-founder, President. Private journal entries admitted at trial (PX161, PX163): 'wrong to steal the non-profit from Elon.'",
      "Journal establishes his own subjective belief that a commitment was made to Musk - devastating admission for defense.",
      "Sept 2017 ultimatum response: relayed Altman's commitment to nonprofit; Zilis testified he told her he was 'great with it.'",
      "Currently on leave from OpenAI. Signed all corporate documents including LP Agreement.",
    ],
  },
  {
    id: "is",
    name: "Ilya Sutskever",
    role: "Former Chief Scientist, OpenAI",
    ph: "🔬",
    party: "Defendant",
    notes: [
      "Co-founder. Wrote Jan 2016 'less open' email (DX-516) - Musk's 'Yup' reply is defense's best evidence.",
      "Discovery disputes: Court ordered Sutskever to produce 'Brockman memo' re board crisis. Contempt motion filed (Dkt. 335); denied (Dkt. 339). Second deposition ordered.",
      "Now runs SSI (Safe Superintelligence Inc.) after leaving OpenAI following the Nov 2023 board crisis.",
      "Watch: Testimony re open-source intent and what was understood at founding; board crisis role.",
    ],
  },
  {
    id: "sn",
    name: "Satya Nadella",
    role: "CEO, Microsoft (Defendant)",
    ph: "💻",
    party: "Defendant",
    notes: [
      "Testified re Microsoft's knowledge of OpenAI obligations, basis for $1B-$13.75B investment decisions.",
      "Nov 2023 board crisis: Text thread with Altman (PX306); Nadella hired Altman to Microsoft within hours of firing.",
      "Key: Whether he knew Kevin Scott's March 2018 email concerns about donor expectations before investing.",
      "Critical for Count XIX (aiding and abetting). Defense: Nadella relied on OpenAI board warranties; no independent knowledge of Musk's alleged trust conditions.",
    ],
  },
  {
    id: "ks",
    name: "Kevin Scott",
    role: "CTO, Microsoft",
    ph: "📧",
    party: "Defendant",
    notes: [
      "March 7, 2018 email to Nadella (PX230/MDX-1599): 'I wonder if the big OpenAI donors are aware of these plans? Ideologically, I can't imagine that they funded an open effort… to then go build a closed, for profit thing on its back.'",
      "This is plaintiff's single best document on Microsoft's knowledge element for Count XIX.",
      "Defense will argue: speculative personal musing; not institutional knowledge; Scott did not work on the OpenAI deal.",
      "Watch on cross: Whether Scott had any further communications re donor concerns before Microsoft invested in July 2019.",
    ],
  },
  {
    id: "jb",
    name: "Jared Birchall",
    role: "Musk's Financial Advisor",
    ph: "💰",
    party: "Plaintiff",
    notes: [
      "Managed all of Musk's donations to OpenAI - wire transfers, rent payments, Tesla donations.",
      "Testimony: Established the ~$38.2M total donation amount; confirmed Musk's instructions re charitable purpose.",
      "Key exhibit: PX112/DX-1304 (contribution summary). Also has communications with Altman/OpenAI re donation logistics.",
      "Defense cross: Whether Birchall was aware of any restrictions or conditions on the donations (there were no written conditions).",
    ],
  },
  {
    id: "sz",
    name: "Shivon Zilis",
    role: "Former OpenAI Board; Musk Associate",
    ph: "⚡",
    party: "Plaintiff",
    notes: [
      "Was simultaneously an OpenAI board member and close associate of Musk. Mother of Musk's children.",
      "Key role: Relayed Sept 2017 reassurances between Altman and Musk. Approved Microsoft's investment transactions on the OpenAI board.",
      "Defense: Zilis as OpenAI board member provided explicit written warranties that no third-party conditions on donations existed - this defeats Microsoft's knowledge.",
      "Plaintiff: Zilis's involvement shows Microsoft had an 'insider' aware of the Musk-OpenAI relationship.",
      "Discovery dispute: Deposition of Zilis (Dkt. 264, 273, 276) - confidentiality issues.",
    ],
  },
  {
    id: "wz",
    name: "Dr. C. Paul Wazzan",
    role: "Damages Expert (Plaintiff)",
    ph: "📊",
    party: "Plaintiff",
    notes: [
      "Plaintiff's damages expert. Opinions 3-6 survived Daubert motion (PTO No. 2, 3/25/2026).",
      "Valuations include: (1) constructive trust on value of charitable interest lost; (2) disgorgement of OpenAI/Microsoft profits attributable to charitable trust breach.",
      "Key methodology: Wazzan analyzed OpenAI's $157B valuation (as of recapitalization) and Microsoft's ~27% stake ($135B value).",
      "Defense challenged Opinions 1-2 (causation methodology); those were excluded. Opinions 3-6 (damages quantification conditional on liability finding) survived.",
    ],
  },
  {
    id: "ds",
    name: "Prof. David Schizer",
    role: "Nonprofit Law Expert (Plaintiff)",
    ph: "⚖️",
    party: "Plaintiff",
    notes: [
      "Dean Emeritus, Columbia Law School. Expert on nonprofit law, charitable trusts, and fiduciary duties.",
      "Opinion: Musk's donations created an enforceable charitable trust under California law. OpenAI board's approval of for-profit conversion did not extinguish trustee duties.",
      "Counterpart: Prof. Daniel Hemel (Defense) disputes trust creation elements and argues board approval was valid exercise of fiduciary discretion.",
      "§17510.8 issue: Schizer supports applicability to OpenAI's solicitations from major donors including Musk.",
    ],
  },
  {
    id: "ld",
    name: "Louis Dudney",
    role: "Financial Expert (Defense)",
    ph: "📉",
    party: "Defendant",
    notes: [
      "Defense's tracing expert. FIFO (first-in, first-out) analysis of OpenAI's use of Musk's donations.",
      "Opinion: Musk's donations were fully spent by OpenAI on legitimate nonprofit activities before the alleged breach dates. Therefore no 'res' of the trust remains to be traced into for-profit subsidiaries.",
      "Plaintiff's counter: FIFO tracing is an arbitrary methodology; the IP and human capital built with Musk's money is the res.",
      "Critical: If Dudney's tracing analysis is credited, plaintiffs' constructive trust remedy disappears.",
    ],
  },
  {
    id: "mm",
    name: "Mira Murati",
    role: "Former CTO / Interim CEO",
    ph: "🎯",
    party: "Defendant",
    notes: [
      "Led OpenAI as interim CEO during the Nov 2023 board crisis. Former CTO.",
      "Key evidence: Murati-Templeton text thread (PX307) during board crisis shows Microsoft involvement.",
      "Left OpenAI in September 2024 to start own company.",
      "Watch: Whether her testimony supports the narrative that Microsoft orchestrated the board crisis or that it was internal governance.",
    ],
  },
];
const MUSK_MOTIONS = [
  {
    type: "past",
    date: "Nov 29, 2024",
    title: "Motion for Preliminary Injunction",
    dkt: 46,
    movant: "Plaintiff",
    opponent: "OpenAI/Microsoft",
    status: "DENIED",
    detail:
      "To block OpenAI for-profit conversion. Denied 3/4/2025 (Dkt.121). Court found low likelihood of success on merits.",
  },
  {
    type: "past",
    date: "Jan 31, 2025",
    title: "OpenAI MTD FAC",
    dkt: 103,
    movant: "OpenAI",
    opponent: "Plaintiff",
    status: "Granted in Part",
    detail:
      "Express contract dismissed w/o leave (5/1/2025). Charitable trust (Count XVIII) survives. RICO partially survived.",
  },
  {
    type: "past",
    date: "Jan 31, 2025",
    title: "Microsoft MTD FAC",
    dkt: 102,
    movant: "Microsoft",
    opponent: "Plaintiff",
    status: "Granted in Part",
    detail:
      "Some claims dismissed; aiding and abetting (Count XIX) survived to trial. Dkt.163.",
  },
  {
    type: "past",
    date: "May 7, 2025",
    title: "MTD OpenAI Counterclaims",
    dkt: 166,
    movant: "Plaintiff",
    opponent: "OpenAI",
    status: "DENIED",
    detail:
      "Musk's motion to dismiss OpenAI counterclaims denied 8/12/2025 (Dkt.228). Counterclaims proceed.",
  },
  {
    type: "past",
    date: "Jun 26, 2025",
    title: "Motion to Strike 55 OpenAI Defenses",
    dkt: 186,
    movant: "Plaintiff",
    opponent: "OpenAI",
    status: "Granted in Part",
    detail:
      "MTS granted in part (Dkt.222, 7/29/2025). OpenAI moved to amend defenses (Dkt.227); granted.",
  },
  {
    type: "past",
    date: "Oct 17, 2025",
    title: "OpenAI Motion for Summary Judgment",
    dkt: 327,
    movant: "OpenAI",
    opponent: "Plaintiff",
    status: "DENIED",
    detail: "MSJ denied 1/15/2026 (Dkt.390). Case proceeds to trial.",
  },
  {
    type: "past",
    date: "Oct 17, 2025",
    title: "Microsoft Motion for Summary Judgment",
    dkt: 329,
    movant: "Microsoft",
    opponent: "Plaintiff",
    status: "Granted in Part",
    detail:
      "MSJ granted in part; some claims dismissed. Count XIX (aiding & abetting) survives to trial. Dkt.390.",
  },
  {
    type: "past",
    date: "Jan 16, 2026",
    title: "OpenAI Daubert - Wazzan Opinions 3-6",
    dkt: 393,
    movant: "OpenAI",
    opponent: "Plaintiff",
    status: "DENIED",
    detail:
      "Wazzan Opinions 3-6 NOT excluded (PTO No. 2, 3/25/2026, Dkt.452). Opinions 1-2 excluded.",
  },
  {
    type: "past",
    date: "Feb 24, 2026",
    title: "Microsoft MIL Nos. 1 & 2",
    dkt: 412,
    movant: "Microsoft",
    opponent: "Plaintiff",
    status: "GRANTED",
    detail:
      "MIL No. 1 (Arnold depo excerpts) GRANTED. MIL No. 2 (news articles) GRANTED (PTO No. 2, Dkt.452).",
  },
  {
    type: "past",
    date: "Apr 17, 2026",
    title: "Pretrial Order No. 4",
    dkt: 477,
    movant: "Court",
    opponent: "-",
    status: "Entered",
    detail:
      "Plaintiff waived certain remedies (Dkt.484). Fraud claims (VI, VII) dismissed with prejudice 4/24/2026 (Dkt.497).",
  },
  {
    type: "upcoming",
    date: "May 22, 2026 (est.)",
    title: "End of Plaintiff's Case-in-Chief",
    dkt: null,
    movant: "Plaintiff",
    opponent: "-",
    status: "Trial",
    detail: "Plaintiff expects to rest ~5/22. Defense case-in-chief to follow.",
  },
  {
    type: "upcoming",
    date: "TBD",
    title: "Motions for JMOL (Rule 50(a))",
    dkt: null,
    movant: "Defense",
    opponent: "Plaintiff",
    status: "Expected",
    detail:
      "OpenAI and Microsoft expected to move for judgment as matter of law at close of plaintiff's case.",
  },
  {
    type: "upcoming",
    date: "Post-trial",
    title: "OpenAI Counterclaims - Phase 2",
    dkt: 229,
    movant: "OpenAI",
    opponent: "Plaintiff",
    status: "Pending",
    detail:
      "Second Amended Counterclaims (misappropriation of trade secrets, tortious interference, breach of fiduciary duty) proceed after Phase 1.",
  },
  {
    type: "upcoming",
    date: "Post-trial",
    title: "Antitrust Claims Phase 2 Trial",
    dkt: null,
    movant: "Plaintiff (xAI)",
    opponent: "OpenAI/Microsoft",
    status: "Stayed",
    detail:
      "Counts VIII-XV antitrust claims stayed pending Phase 1 outcome. Phase 2 trial date TBD.",
  },
];
const MUSK_CASELAW = [
  {
    cs: "Holt v. College of Osteopathic Physicians",
    ci: "61 Cal.2d 750 (1964)",
    ctx: "Charitable Trust Creation",
    h: "A charitable trust can be created without a formal writing if there are consistent representations of charitable purpose and the trustee accepts the trust res.",
    v: true,
    by: "Plaintiff",
    quote:
      "'A charitable trust may be created by any manifestation of intent to devote property to charitable purposes'",
    qv: true,
    doc: "Schizer expert report",
  },
  {
    cs: "Lynch v. Spilman",
    ci: "67 Cal.2d 251 (1967)",
    ctx: "Charitable Trust Enforcement",
    h: "A donor who makes contributions based on representations of charitable purpose has standing to seek enforcement of the charitable trust.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "SAC ¶¶ 200-210",
  },
  {
    cs: "Cal. Corp. Code §5142(a)",
    ci: "California Corporations Code",
    ctx: "Charitable Trust; Attorney General",
    h: "The Attorney General, or 'any person granted relator status,' may bring action to enforce charitable trusts. Musk claims donor relator status.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Count XVIII; Pretrial Order No. 3",
  },
  {
    cs: "In re Pacific Enterprises Securities Litigation",
    ci: "47 F.3d 373 (9th Cir. 1995)",
    ctx: "Aiding & Abetting Elements",
    h: "Aiding and abetting liability requires: (1) knowledge of the primary wrong; (2) substantial assistance in the wrong; (3) causal connection.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Count XIX; MSJ Order (Dkt.390)",
  },
  {
    cs: "Casey v. U.S. Bank Nat'l Assoc.",
    ci: "127 Cal.App.4th 1138 (2005)",
    ctx: "Aiding & Abetting - Knowledge",
    h: "Bank held liable for aiding and abetting breach of fiduciary duty where it had actual or constructive knowledge that a wrongful transaction was being facilitated.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Count XIX; PX230 (Kevin Scott email)",
  },
  {
    cs: "Wolf v. CIR",
    ci: "4 T.C. 752 (1945)",
    ctx: "Charitable Trust / Restricted Gifts",
    h: "Gifts with conditions attached are enforceable as charitable trusts regardless of whether formal trust documents were executed.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Schizer report",
  },
  {
    cs: "California Code of Civil Procedure §338",
    ci: "Cal. C.C.P. §338",
    ctx: "Statute of Limitations",
    h: "3-year limitations period for fraud and breach of fiduciary duty claims. Accrual: when plaintiff discovers or should have discovered the wrong.",
    v: true,
    by: "Defendant",
    quote: null,
    qv: null,
    doc: "Pretrial Order No. 3 (Dkt.456)",
  },
  {
    cs: "California Code of Civil Procedure §343",
    ci: "Cal. C.C.P. §343",
    ctx: "Statute of Limitations - Unjust Enrichment",
    h: "Residual 4-year statute of limitations applies to unjust enrichment claims not covered by specific provisions.",
    v: true,
    by: "Both",
    quote: null,
    qv: null,
    doc: "Pretrial Order No. 3",
  },
  {
    cs: "Donovan v. Bierwirth",
    ci: "680 F.2d 263 (2d Cir. 1982)",
    ctx: "Fiduciary Duty of Trustees",
    h: "Trustees of charitable entities must act exclusively in the interest of the beneficiaries; self-dealing violates fundamental fiduciary obligations.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Count XVIII theories",
  },
  {
    cs: "Cleveland v. Policy Management Systems",
    ci: "526 U.S. 795 (1999)",
    ctx: "Unclean Hands Defense",
    h: "The equitable doctrine of unclean hands requires that defendant's alleged wrongdoing be sufficiently related to plaintiff's misconduct.",
    v: true,
    by: "Plaintiff",
    quote:
      "'The doctrine applies only where the party's misconduct is directly related to the claim being asserted'",
    qv: true,
    doc: "Defense - unclean hands challenge",
  },
  {
    cs: "People ex rel. Becerra v. Johnson & Johnson",
    ci: "77 Cal.App.5th 295 (2022)",
    ctx: "Charitable Trust - Cal. Corp. Code §17510.8",
    h: "Cal. Bus. & Prof. Code §17510.8 applies to solicitations of charitable contributions regardless of whether entity is a formal charitable trust.",
    v: true,
    by: "Plaintiff",
    quote: null,
    qv: null,
    doc: "Trial Brief Dkt.515; §17510.8 dispute",
  },
  {
    cs: "OpenAI LP Agreement (Oct 2018)",
    ci: "PX200 / DX-839",
    ctx: "For-Profit Structure / Trust Res",
    h: "The LP Agreement itself states that investors should 'view investment…in the spirit of a donation.' Plaintiff argues this language confirms charitable trust obligations continued.",
    v: true,
    by: "Plaintiff",
    quote: "'view any investment in OpenAI in the spirit of a donation'",
    qv: true,
    doc: "PX200/DX-839; Count XVIII",
  },
];
const MUSK_ASSETS = [
  {
    id: 1,
    cat: "Evidence",
    title: "PX003/DX-501 - Altman 'Tech Belongs to World' Email",
    desc: "May 2015: Altman proposes 'some sort of nonprofit' where 'tech belongs to the world.' Founding purpose email. ADMITTED.",
    date: "May 2015",
    pages: 2,
    color: "#ef4444",
  },
  {
    id: 2,
    cat: "Evidence",
    title: "PX161/163 - Brockman Private Journals",
    desc: "Brockman: 'it'd be wrong to steal the non-profit from Elon' and 'cannot say we are committed to the non-profit…story will be we weren't honest with him.' ADMITTED.",
    date: "2017",
    pages: 8,
    color: "#ef4444",
  },
  {
    id: 3,
    cat: "Evidence",
    title: "PX230/MDX-1599 - Kevin Scott to Nadella",
    desc: "Mar 7, 2018: 'I wonder if the big OpenAI donors are aware of these plans?' Plaintiff's best evidence of Microsoft knowledge. ADMITTED.",
    date: "Mar 7, 2018",
    pages: 2,
    color: "#ef4444",
  },
  {
    id: 4,
    cat: "Evidence",
    title: "DX-516 - Sutskever 'Less Open' Email + Musk 'Yup'",
    desc: "Jan 4, 2016: Sutskever proposes 'less open'; Musk replies 'Yup.' Defense's best evidence that Musk agreed to non-openness from the start. ADMITTED.",
    date: "Jan 4, 2016",
    pages: 2,
    color: "#3b82f6",
  },
  {
    id: 5,
    cat: "Evidence",
    title: "PX200/DX-839 - OpenAI LP Agreement",
    desc: "Oct 2018 LP Agreement. Front page: 'view investment in spirit of a donation.' Capped-profit structure. ADMITTED.",
    date: "Oct 2018",
    pages: 45,
    color: "#f59e0b",
  },
  {
    id: 6,
    cat: "Evidence",
    title: "MDX-1537 - Musk 'Captured by Microsoft' Tweet",
    desc: "Sep 24, 2020: Musk tweets 'OpenAI is essentially captured by Microsoft.' Defense's key SOL evidence. ADMITTED.",
    date: "Sep 24, 2020",
    pages: 1,
    color: "#3b82f6",
  },
  {
    id: 7,
    cat: "Evidence",
    title: "PX286/287 - OpenAI PBC Cap Table",
    desc: "Post-recapitalization cap table showing Microsoft ~27% ($135B value). Evidence of scale of wealth transfer from nonprofit.",
    date: "Oct 2025",
    pages: 3,
    color: "#ef4444",
  },
  {
    id: 8,
    cat: "Evidence",
    title: "PX304/DX-1040 - Nov 2023 Board Firing Consent",
    desc: "Unanimous written consent firing Altman. Board cited 'lack of candor' as grounds. Key to the board coup narrative.",
    date: "Nov 16, 2023",
    pages: 4,
    color: "#ef4444",
  },
  {
    id: 9,
    cat: "Evidence",
    title: "PX306/DX-311 - Altman-Nadella Text Thread",
    desc: "Nov 18, 2023: Text thread during board crisis showing Microsoft coordination with Altman during firing. ADMITTED.",
    date: "Nov 18, 2023",
    pages: 6,
    color: "#ef4444",
  },
  {
    id: 10,
    cat: "Filing",
    title: "Second Amended Complaint (Dkt.170)",
    desc: "93 pages. 21 counts. Core allegations re charitable trust, unjust enrichment, and Microsoft aiding and abetting.",
    date: "May 22, 2025",
    pages: 93,
    color: "#a78bfa",
  },
  {
    id: 11,
    cat: "Expert",
    title: "Wazzan Expert Report (Opinions 3-6)",
    desc: "Damages quantification. Opinions 3-6 survived Daubert (PTO No. 2). Includes constructive trust and disgorgement calculations based on OpenAI/Microsoft valuations.",
    date: "2025",
    pages: 120,
    color: "#10b981",
  },
  {
    id: 12,
    cat: "Expert",
    title: "Schizer Nonprofit Law Expert Report",
    desc: "Charitable trust creation and breach analysis. Supports applicability of Cal. Bus. & Prof. Code §17510.8 to OpenAI's donor solicitations.",
    date: "2025",
    pages: 85,
    color: "#10b981",
  },
  {
    id: 13,
    cat: "Evidence",
    title: "Amended Joint Exhibit List (Dkt.428)",
    desc: "1,601 exhibits. Full exhibit list admitted/offered at trial. Public link to daily admitted exhibits: box.com link in trial status.",
    date: "Feb 2026",
    pages: 104,
    color: "#f59e0b",
  },
  {
    id: 14,
    cat: "Filing",
    title: "Pretrial Order No. 2 (Dkt.452) - MILs/Daubert Rulings",
    desc: "Wazzan Opinions 3-6 NOT excluded. Russell partially excluded. Microsoft MIL Nos. 1 & 2 GRANTED.",
    date: "Mar 25, 2026",
    pages: 18,
    color: "#6b7280",
  },
  {
    id: 15,
    cat: "Filing",
    title: "Competing §17510.8 Trial Briefs (Dkt.515/516)",
    desc: "4/30/2026 briefs on Cal. Bus. & Prof. Code §17510.8 (charitable solicitations statute). Pending ruling by Judge YGR.",
    date: "Apr 30, 2026",
    pages: 30,
    color: "#6b7280",
  },
  {
    id: 16,
    cat: "Local Rules",
    title: "N.D. Cal. Civil Local Rules (L.R. 1-1 through 80-5)",
    desc: "N.D. Cal. Civil Local Rules effective Dec 1 2023. Key rules for Musk v. Altman: L.R. 7-1 through 7-12 (motions -- opposition due 14 days, reply 7 days after opposition; no oral argument unless ordered); L.R. 7-3 (meet-and-confer required before filing any motion except TRO); L.R. 11-3 (signature block with bar number and firm); L.R. 16-9 (pretrial conference statement 21 days before trial); L.R. 37-1 (discovery disputes -- must confer within 7 days of discovery of dispute; then 5-page joint letter to Magistrate); L.R. 54-1 (bill of costs within 14 days of judgment); L.R. 65-1 (TRO and PI -- must file proposed order). Filing: ECF mandatory; chambers copy required for filings over 25 pages.",
    date: "Dec 1, 2023",
    pages: 120,
    color: "#3b82f6",
  },
  {
    id: 17,
    cat: "Local Rules",
    title: "YGR Individual Standing Order (Courtroom 1, Oakland)",
    desc: "Judge YGR Standing Order as of 2025. Pre-hearing requirements: submit 3-page letter brief at least 7 days before any noticed hearing (both parties; no separate reply unless ordered). Page limits strictly enforced -- motions 25 pages, oppositions 25 pages, replies 15 pages; sur-replies not permitted without leave. Trial: counsel must pre-mark all exhibits before trial; PX (plaintiff), DX (defense), JX (joint); list filed 7 days before trial. Witnesses: direct examination by declaration for bench trials unless YGR orders otherwise. Jurors: no social media research on jurors during trial; questionnaires approved in advance. Discovery: all disputes to Magistrate Judge via 5-page joint letter -- do NOT file discovery motions with YGR. Chambers copies: required for any document over 10 pages filed within 24 hours of ECF filing; to Clerk, Rm 400, 1301 Clay St Oakland.",
    date: "Current",
    pages: 8,
    color: "#3b82f6",
  },
  {
    id: 18,
    cat: "Local Rules",
    title: "N.D. Cal. Standing Order for All Judges (Civil Cases)",
    desc: "Uniform Standing Order adopted by all N.D. Cal. judges. Case management: initial CMC within 60 days of service; joint CMC statement due 7 days before CMC per L.R. 16-9; ADR certification required. Discovery: all disputes via 5-page joint letter to assigned Magistrate Judge -- YGR does not hear discovery disputes directly. Magistrate for Musk v. Altman: check docket. E-filing: all documents via ECF; no fax filings; large documents split into segments under 50MB. Proposed orders: must be submitted in Word format to YGR_Orders@cand.uscourts.gov within 24 hours of any order that requires a proposed form.",
    date: "Current",
    pages: 12,
    color: "#3b82f6",
  },
  {
    id: 19,
    cat: "Local Rules",
    title: "N.D. Cal. ESI Guidelines and Model Protective Order",
    desc: "N.D. Cal. Guidelines for the Discovery of Electronically Stored Information (ESI). Production format: native with extracted text and metadata unless otherwise agreed; TIFF with OCR for documents with redactions; load file in Concordance DAT or Relativity format. Metadata fields: custodian, date created, date modified, file type, MD5 hash. Privilege logs: must be produced within 30 days of assertion; Sedona Conference format preferred. FRCP 502(d) clawback: parties must submit proposed 502(d) order in any case involving large ESI productions -- protects against inadvertent waiver. Deposition protocol: 30(b)(6) notice must be served at least 21 days before deposition; exhibits must be pre-marked and served 48 hours in advance. Model Protective Order: two-tier (Confidential / Attorneys Eyes Only); AEO designation for trade secrets and financial data. Applicable to all remaining Musk v. Altman discovery and trial exhibit exchange.",
    date: "Current",
    pages: 15,
    color: "#6b7280",
  },
  {
    id: 20,
    cat: "Local Rules",
    title: "YGR Courtroom 1 Oakland Trial Procedures",
    desc: "Courtroom 1 trial-specific procedures, Ronald V. Dellums Federal Building, 1301 Clay St, Oakland CA 94612. Daily schedule: 8:30 AM counsel setup; 9:00 AM jury/bench session; 1-hour lunch recess typically 12:00-1:00 PM; 4:30 PM adjourn unless otherwise ordered. Exhibits: PX (plaintiff), DX (defense), JX (joint exhibits), MDX (demonstratives). All exhibits must be pre-marked and listed in joint exhibit list filed 7 days before trial (Dkt.428 in this case is the Amended Joint Exhibit List). Objections: standing objections not permitted -- must object each time. Bench conferences: counsel must approach only when invited. Technology: IT coordinator must be approved 72 hours in advance; laptop/HDMI connection available; Elmo document camera in courtroom. Witnesses: sequestration order standard; witnesses not to read trial transcripts during trial. Jury: YGR does not allow counsel to contact jurors after discharge without court order. Clerk: Frances Stone, ygr_crd@cand.uscourts.gov.",
    date: "Current",
    pages: 6,
    color: "#10b981",
  },
  {
    id: "ma21",
    cat: "Filing",
    title: "Dkt.522 - OpenAI App. to Admit Musk Threat to Brockman",
    desc: "Filed May 3 2026 by MoFo/Wachtell. Musk sent Brockman a message on Apr 25 2026 (2 days before trial): when Brockman proposed mutual drop of claims, Musk replied: By the end of this week, you and Sam will be the most hated men in America. If you insist, so it will be. OpenAI argues admissible under FRE 408(b) as evidence of motive and bias (punitive litigation intent vs competitor), not to prove liability. Exhibit A: Pampena v. Musk Vol.7 transcript (Judge Breyer, Mar 9 2026) establishing pattern -- Musk threat through Ringler: World War III until the end of time for real.",
    date: "May 3, 2026",
    pages: 4,
    color: "#ef4444",
  },
];
const MUSK_LOCS = [
  {
    id: "nd",
    name: "U.S. District Court - N.D. Cal. Oakland",
    area: "1301 Clay Street, Oakland CA 94612",
    notes:
      "Courtroom 1, 4th Floor. Judge YGR. Trial in progress. Magistrate Judge Hixson handles discovery.",
  },
  {
    id: "openai",
    name: "OpenAI Headquarters",
    area: "3180 18th Street, San Francisco, CA 94110",
    notes:
      "Primary operational location. Site of board meetings, including Nov 2023 firing and reinstatement of Altman.",
  },
  {
    id: "msft",
    name: "Microsoft Corporation - Headquarters",
    area: "One Microsoft Way, Redmond, WA 98052",
    notes:
      "Washington corporation. Kevin Scott and Satya Nadella principal witnesses. All Microsoft investments in OpenAI approved here.",
  },
  {
    id: "xai",
    name: "xAI Corp.",
    area: "Palo Alto, CA",
    notes:
      "Musk's competing AI company, formed March 2023. Defense argues xAI motivation undermines Musk's charitable standing.",
  },
  {
    id: "tesla",
    name: "Tesla, Inc.",
    area: "Austin, TX (HQ); formerly Palo Alto, CA",
    notes:
      "In 2017 Musk proposed OpenAI merge into Tesla. Relevant to unclean hands defense and competitive motive.",
  },
  {
    id: "ssi",
    name: "Safe Superintelligence Inc. (Sutskever)",
    area: "Palo Alto, CA",
    notes:
      "Ilya Sutskever's company after leaving OpenAI. Sutskever is a key witness re founding intent and board crisis.",
  },
];
const MUSK_ATTORNEYS = [
  {
    name: "Marc Toberoff",
    firm: "Toberoff & Associates, P.C.",
    role: "Lead Plaintiff Counsel (PHV)",
    party: "Plaintiff",
    bar: "CA Bar #188547",
    email: "mtoberoff@toberoffandassociates.com",
    phone: "(310) 246-3333",
    involvement:
      "Lead attorney for Musk and xAI. Former entertainment law specialist turned litigation partner. Argued PI motion, led briefing strategy.",
    dkts: ["1", "46", "127", "350"],
  },
  {
    name: "Steven F. Molo",
    firm: "MoloLamken LLP",
    role: "Lead Trial Counsel (PHV)",
    party: "Plaintiff",
    bar: "NY",
    email: "smolo@mololamken.com",
    phone: "(212) 607-8160",
    involvement:
      "Lead trial attorney at MoloLamken. Former AUSA SDNY. Handling direct examination of key witnesses including Musk.",
    dkts: ["350", "427"],
  },
  {
    name: "Robert K. Kry",
    firm: "MoloLamken LLP",
    role: "Trial Counsel (PHV)",
    party: "Plaintiff",
    bar: "NY",
    email: "rkry@mololamken.com",
    phone: "",
    involvement:
      "Briefing lead on motions to dismiss, MSJ opposition. Trial preparation and jury instructions.",
    dkts: ["128", "350"],
  },
  {
    name: "Jennifer M. Schubert",
    firm: "MoloLamken LLP",
    role: "Trial Counsel (PHV)",
    party: "Plaintiff",
    bar: "NY",
    email: "jschubert@mololamken.com",
    phone: "",
    involvement:
      "Expert witness coordination. Wazzan and Schizer reports. MIL briefing.",
    dkts: ["396"],
  },
  {
    name: "Walter H. Hawes IV",
    firm: "MoloLamken LLP",
    role: "Trial Counsel (PHV)",
    party: "Plaintiff",
    bar: "DC",
    email: "whawes@mololamken.com",
    phone: "",
    involvement:
      "Discovery coordination. Sutskever deposition disputes. ESI matters.",
    dkts: ["234", "285"],
  },
  {
    name: "Jordan Eth",
    firm: "Morrison & Foerster LLP",
    role: "Lead Attorney for OpenAI (Local)",
    party: "Defendant",
    bar: "CA Bar #121617",
    email: "jeth@mofo.com",
    phone: "(415) 268-7126",
    involvement:
      "Lead local counsel for all OpenAI defendants. Filed first appearances. Coordinating with Wachtell at trial.",
    dkts: ["13", "103", "175", "327"],
  },
  {
    name: "William Savitt",
    firm: "Wachtell, Lipton, Rosen & Katz",
    role: "Lead PHV Trial Counsel (OpenAI)",
    party: "Defendant",
    bar: "NY",
    email: "wdsavitt@wlrk.com",
    phone: "(212) 403-1000",
    involvement:
      "Lead trial counsel for OpenAI. Wachtell's top litigator. Handling cross-examination of Musk, direct of Altman.",
    dkts: ["327", "359"],
  },
  {
    name: "Bradley R. Wilson",
    firm: "Wachtell, Lipton, Rosen & Katz",
    role: "PHV Trial Counsel (OpenAI)",
    party: "Defendant",
    bar: "NY",
    email: "brwilson@wlrk.com",
    phone: "",
    involvement: "Trial briefing; pretrial orders; MSJ brief lead.",
    dkts: ["327", "357", "359"],
  },
  {
    name: "Randall W. Jackson",
    firm: "Wachtell, Lipton, Rosen & Katz",
    role: "PHV Trial Counsel (OpenAI)",
    party: "Defendant",
    bar: "NY",
    email: "rwjackson@wlrk.com",
    phone: "",
    involvement: "Trial witness coordination; deposition prep.",
    dkts: [],
  },
  {
    name: "Alison Lynn Plessman",
    firm: "Hueston Hennigan LLP",
    role: "Local Counsel (OpenAI)",
    party: "Defendant",
    bar: "CA",
    email: "aplessman@hueston.com",
    phone: "(213) 788-4340",
    involvement:
      "Local counsel for OpenAI. Court filings, discovery coordination.",
    dkts: [],
  },
  {
    name: "Russell P. Cohen",
    firm: "Dechert LLP",
    role: "Lead Attorney for Microsoft",
    party: "Defendant",
    bar: "CA Bar #213104",
    email: "russ.cohen@dechert.com",
    phone: "(415) 262-4500",
    involvement:
      "Lead local and PHV counsel for Microsoft. Filed all Microsoft motions. Handling Nadella/Scott direct examinations.",
    dkts: ["102", "173", "329", "357"],
  },
  {
    name: "Andrew J. Levander",
    firm: "Dechert LLP",
    role: "PHV Trial Counsel (Microsoft)",
    party: "Defendant",
    bar: "NY",
    email: "andrew.levander@dechert.com",
    phone: "(212) 698-3683",
    involvement:
      "Dechert's senior partner leading Microsoft defense. Strategy counsel.",
    dkts: ["102", "329"],
  },
  {
    name: "John A. Jurata Jr.",
    firm: "Dechert LLP",
    role: "PHV Trial Counsel (Microsoft)",
    party: "Defendant",
    bar: "DC",
    email: "jay.jurata@dechert.com",
    phone: "",
    involvement: "Microsoft MSJ lead; MIL briefing; damages arguments.",
    dkts: ["329", "357"],
  },
  {
    name: "Howard M. Ullman",
    firm: "Dechert LLP",
    role: "Trial Counsel (Microsoft)",
    party: "Defendant",
    bar: "CA",
    email: "howard.ullman@dechert.com",
    phone: "",
    involvement: "Local support for Microsoft discovery and trial preparation.",
    dkts: [],
  },
];

export default function App() {
  useEffect(() => {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", "CaseBinder litigation intelligence binder for USA v. Chidambaran and Naqvi (E.D.N.Y. 1:26-cr-00097): live docket timeline, theories of the case, witnesses, depositions, contradictions, jury intel and trial strategy for both defendants.");
    let l = document.querySelector('link[rel="canonical"]');
    if (!l) { l = document.createElement("link"); l.setAttribute("rel", "canonical"); document.head.appendChild(l); }
    l.setAttribute("href", "https://www.casebinder.ai/farhan");
  }, []);

  const [dark, setDark] = useState(true);
  const th = dark ? DK : LT;
  const [page, setPage] = useState("home");
  const [signInFirm, setSignInFirm] = useState("");
  const [fromOrrick, setFromOrrick] = useState(false);
  const [tab, setTab] = useState("timelines");
  const [caseId, setCaseId] = useState("neuro");
  const isJuul = caseId === "juul";
  const isIle = caseId === "naqvi" || caseId === "chid";
  const aFTL = isIle
    ? ILE_F_TL
    : caseId === "juul"
      ? JUUL_F_TL
      : caseId === "musk"
        ? MUSK_F_TL
        : F_TL;
  const aPTL = isIle
    ? ILE_P_TL
    : caseId === "juul"
      ? JUUL_P_TL
      : caseId === "musk"
        ? MUSK_P_TL
        : P_TL;
  const aSTL = isIle
    ? ILE_S_TL
    : caseId === "juul"
      ? JUUL_S_TL
      : caseId === "musk"
        ? MUSK_S_TL
        : S_TL;
  const aD = isIle
    ? ILE_DOCKET
    : caseId === "juul"
      ? JUUL_DOCKET
      : caseId === "musk"
        ? [
            {
              date: "08/05/2024",
              num: 1,
              text: "COMPLAINT - 21-count complaint vs. Altman, Brockman, OpenAI, Microsoft, Hoffman. RICO, fraud, breach of fiduciary duty, charitable trust.",
              tags: ["factual", "procedural"],
            },
            {
              date: "11/14/2024",
              num: 32,
              text: "FIRST AMENDED COMPLAINT - adds Microsoft, Reid Hoffman, Shivon Zilis, Deannah Templeton, CA AG Bonta. Reduces to 20 counts.",
              tags: ["factual", "procedural"],
            },
            {
              date: "11/29/2024",
              num: 46,
              text: "MOTION FOR PRELIMINARY INJUNCTION - re OpenAI for-profit conversion.",
              tags: ["procedural"],
            },
            {
              date: "12/09/2024",
              num: 65,
              text: "SECOND AMENDED COMPLAINT - 20 counts including Count XVIII charitable trust, Count IV unjust enrichment, Count XIX aiding & abetting (Microsoft).",
              tags: ["factual", "procedural"],
            },
            {
              date: "03/04/2025",
              num: 121,
              text: "ORDER DENYING Preliminary Injunction. Judge YGR: Musk failed to show irreparable harm.",
              tags: ["procedural"],
            },
            {
              date: "03/11/2025",
              num: 127,
              text: "PRETRIAL ORDER NO. 1 - Sets trial for May 5, 2026. Identifies Count XVIII (charitable trust), Count IV (unjust enrichment), Count XIX (aiding & abetting) for Phase 1.",
              tags: ["scheduling"],
            },
            {
              date: "04/30/2025",
              num: 180,
              text: "SCHEDULING ORDER - Expert deadline, discovery cutoff, motions in limine schedule.",
              tags: ["scheduling"],
            },
            {
              date: "10/31/2025",
              num: 290,
              text: "MOTIONS IN LIMINE hearing. Court rules on evidentiary issues for trial.",
              tags: ["procedural"],
            },
            {
              date: "01/15/2026",
              num: 340,
              text: "JOINT PRETRIAL STATEMENT filed. Exhibit lists, witness lists, stipulated facts.",
              tags: ["procedural", "scheduling"],
            },
            {
              date: "02/28/2026",
              num: 380,
              text: "PRETRIAL ORDER NO. 3 - SOL framework ruling. Court identifies Sept 2020 Musk tweet as potential SOL trigger.",
              tags: ["factual", "procedural"],
            },
            {
              date: "04/08/2026",
              num: 430,
              text: "DAUBERT RULING - Dudney FIFO tracing methodology allowed. Garza excluded. Henningfield limited.",
              tags: ["procedural"],
            },
            {
              date: "04/28/2026",
              num: 500,
              text: "🔴 TRIAL BEGINS - Day 1. Jury sworn. Opening statements.",
              tags: ["factual", "scheduling"],
            },
            {
              date: "04/29/2026",
              num: 505,
              text: "TRIAL DAY 2 - Plaintiff witnesses. Musk testifies.",
              tags: ["factual"],
            },
            {
              date: "04/30/2026",
              num: 510,
              text: "TRIAL DAY 3 - Cross-examination of Musk continues. §17510.8 competing briefs filed (Dkt.515/516).",
              tags: ["factual", "procedural"],
            },
            {
              date: "04/30/2026",
              num: 515,
              text: "PLAINTIFF BRIEF - §17510.8 Cal. Bus. & Prof. Code applicability brief filed.",
              tags: ["procedural", "factual"],
            },
            {
              date: "04/30/2026",
              num: 516,
              text: "DEFENSE BRIEF - Opposition to §17510.8 charitable solicitations theory.",
              tags: ["procedural"],
            },
          ]
        : DOCKET;
  const aT = isIle
    ? caseId === "chid"
      ? CHID_THEORIES
      : NAQVI_THEORIES
    : caseId === "juul"
      ? JUUL_THEORIES
      : caseId === "musk"
        ? MUSK_THEORIES
        : THEORIES;
  const [customWits, setCustomWits] = useState([]);
  const [customCL, setCustomCL] = useState([]);
  const [showAddWit, setShowAddWit] = useState(false);
  const [witDraft, setWitDraft] = useState({
    name: "",
    role: "",
    party: "Plaintiff",
    notes: "",
  });
  const aW = [
    ...(isIle
      ? ILE_WITNESSES
      : caseId === "juul"
        ? JUUL_WITNESSES
        : caseId === "musk"
          ? MUSK_WITNESSES
          : WITNESSES),
    ...customWits,
  ];
  const aCL = isIle
    ? ILE_CASELAW
    : caseId === "juul"
      ? JUUL_CASELAW.map((c, i) => ({
          id: c.id || "jcl" + i,
          title: c.cs || c.title || "",
          cite: c.ci || c.cite || "",
          holding: c.h || c.holding || "",
          party: c.by || c.party || "Both",
          color:
            c.by === "Plaintiff"
              ? "#ef4444"
              : c.by === "Defendant"
                ? "#3b82f6"
                : "#10b981",
          cat: c.ctx || c.cat || "",
          doc: c.doc || "",
          v: c.v,
          quote: c.quote || null,
          cs: c.cs || c.title || "",
        }))
      : caseId === "musk"
        ? MUSK_CASELAW.map((c, i) => ({
            id: c.id || "mcl" + i,
            title: c.cs || "",
            cite: c.ci || "",
            holding: c.h || "",
            party: c.by || "Both",
            color:
              c.by === "Plaintiff"
                ? "#ef4444"
                : c.by === "Defendant"
                  ? "#3b82f6"
                  : "#10b981",
            cat: c.ctx || "",
            doc: c.doc || "",
            v: c.v,
            quote: c.quote || null,
            cs: c.cs || "",
          }))
        : CASELAW;
  const aM = isIle
    ? ILE_MOTIONS
    : caseId === "juul"
      ? JUUL_MOTIONS
      : caseId === "musk"
        ? MUSK_MOTIONS
        : MOTIONS;
  const [customLocs, setCustomLocs] = useState([]);
  const [assetEditMode, setAssetEditMode] = useState(false);
  const [assetOrder, setAssetOrder] = useState(null);
  const [witEditMode, setWitEditMode] = useState(false);
  const [clEditMode, setClEditMode] = useState(false);
  const [locEditMode, setLocEditMode] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [witOrder, setWitOrder] = useState(null);
  const [locOrder, setLocOrder] = useState(null);
  const aL = [
    ...(isIle
      ? ILE_LOCS
      : caseId === "juul"
        ? JUUL_LOCS
        : caseId === "musk"
          ? MUSK_LOCS
          : LOCS),
    ...customLocs,
  ];
  const aA = [
    ...(isIle
      ? ILE_ASSETS
      : caseId === "juul"
        ? JUUL_ASSETS
        : caseId === "musk"
          ? MUSK_ASSETS
          : ASSETS),
    ...customWits.map((w, i) => ({
      id: `cw${i}`,
      cat: "Witness",
      title: w.name,
      desc: `${w.role} · ${w.party}`,
      date: "Added",
      pages: 1,
      color: "#a78bfa",
    })),
  ];
  const aCaseName = isIle
    ? caseId === "chid"
      ? "1:26-cr-00097-NGG-1"
      : "1:26-cr-00097-NGG-2"
    : caseId === "juul"
      ? "3:19-md-02913"
      : caseId === "musk"
        ? "4:24-cv-04722-YGR"
        : "2:25-cv-05871";
  const chatSys = isIle
    ? caseId === "chid"
      ? ILE_SYS_CHID
      : ILE_SYS_NAQVI
    : caseId === "juul"
      ? "You are a litigation AI for Seliavski v. JUUL MDL (3:19-md-02913), N.D. Cal., Judge Orrick. SFUSD and Seliavski plaintiffs; JLI and Altria defendants. Key issues: RICO, public nuisance, failure to warn, youth marketing. Class settlements complete. Opt-out PI and SFUSD at SJ stage. Oct 31 2025 MSJ hearing and Dec 16 2025 CMC on remand scope complete. CRITICAL NEW: Separate antitrust class action 3:20-cv-02345-WHO (In re JUUL Labs Antitrust Litigation) - Direct Purchaser Class certified; notice commenced March 30 2026; opt-out deadline June 13 2026; antitrust trial September 28 2026. Defendants in antitrust case: Altria Group, JLI, Pritzker, Valani. Indirect purchaser class also proceeding. Answer concisely in 3-4 sentences."
      : caseId === "musk"
        ? "You are a litigation AI for Musk v. Altman et al (4:24-cv-04722-YGR), N.D. Cal., Judge Gonzalez Rogers. Trial began April 28 2026; as of April 30 2026 (Day 3) trial is ongoing. Claims at trial: Count XVIII (Breach of Charitable Trust vs Altman/Brockman/OpenAI Inc.), Count IV (Unjust Enrichment), Count XIX (Aiding & Abetting vs Microsoft). Musk donated ~$38M to OpenAI Inc. on representations it would remain a nonprofit open-source organization; claims defendants pivoted to for-profit. Key evidence: Brockman journal (PX163) - it would be wrong to steal the non-profit from Elon; Kevin Scott email (PX230) - wonder if donors are aware; Musk Yup reply to less open (DX-516); LP Agreement spirit of a donation language (PX200). Key defenses: no trust created, unrestricted gifts, SOL (Musk tweeted captured by Microsoft Sept 2020), unclean hands (xAI competitor), Dudney FIFO tracing. Hot issue at trial: Cal Bus Prof Code 17510.8 applicability - briefed April 30 2026. OpenAI counterclaims pending Phase 2. Answer concisely in 3-4 sentences."
        : "You are a litigation AI for Neurocentria v. Mando (2:25-cv-05871-FLA-MAR), C.D. Cal., Judge Aenlle-Rocha. Patent infringement, 061 patent only. Claim construction pending. Trial 3/9/27. Answer concisely in 3-4 sentences.";
  const aCaseShort = isIle
    ? caseId === "chid"
      ? "USA v. Chidambaran"
      : "USA v. Naqvi"
    : caseId === "juul"
      ? "Seliavski v. Juul"
      : caseId === "musk"
        ? "Musk v. Altman (OpenAI)"
        : "Neurocentria v. Mando";
  const [customTL, setCustomTL] = useState([]);
  const [tlMode, setTlMode] = useState("all");
  const [tlIdx, setTlIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tlPf, setTlPf] = useState("all");
  const [tlSearch, setTlSearch] = useState("");
  const [tlView, setTlView] = useState("timeline");
  const [tlDocType, setTlDocType] = useState("all");
  const [panel, setPanel] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [dkF, setDkF] = useState("all");
  const [dkS, setDkS] = useState("");
  const [dkTags, setDkTags] = useState({});
  const [rbP, setRbP] = useState("theories");
  const [rbLv, setRbLv] = useState(2);
  const [theoryLvls, setTheoryLvls] = useState({});
  const [rbSide, setRbSide] = useState("defendant");
  const [theoryEdits, setTheoryEdits] = useState({});
  const [editMode, setEditMode] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [auditLog, setAuditLog] = useState([]);
  const [regenKey, setRegenKey] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [chatListening, setChatListening] = useState(false);
  const chatRecRef = useRef(null);
  const recordReturnTabRef = useRef(null);
  // every page / tab / case change opens at the top - no carried-over scroll offset
  useEffect(() => {
    const el = document.getElementById("cb-main-scroll");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [page, tab, caseId, rbP]);
  const toast = (text, kind = "info") => {
    if (kind === "ok") kind = "success";
    const id = Date.now() + Math.random();
    setToasts((p) => [...p, { id, text, kind }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  };
  function tKey(s, ti, pi) {
    return `${s}-${ti}-${pi}`;
  }
  function tText(s, ti, pi) {
    return theoryEdits[tKey(s, ti, pi)]?.text || aT[s][ti].text[pi];
  }
  function tPct(k) {
    return theoryEdits[k]?.editPct || 0;
  }
  function tIsAI(k) {
    return theoryEdits[k]?.aiGenerated;
  }
  function saveEdit() {
    if (!editMode) return;
    const { s, ti, pi } = editMode;
    const k = tKey(s, ti, pi);
    const orig = aT[s][ti].text[pi];
    const before = theoryEdits[k]?.text || orig;
    const changed = [...editDraft].filter(
      (c, i) => c !== (orig[i] || ""),
    ).length;
    const pct = Math.min(
      100,
      Math.round((changed / Math.max(orig.length, 1)) * 150),
    );
    setTheoryEdits((prev) => ({
      ...prev,
      [k]: {
        text: editDraft,
        original: orig,
        editPct: pct,
        aiGenerated: false,
      },
    }));
    setAuditLog((prev) => [
      ...prev,
      {
        ts: new Date().toLocaleString(),
        k,
        action: "Human Edit",
        before,
        after: editDraft,
        user: "Attorney",
        pct,
      },
    ]);
    setEditMode(null);
  }
  async function regenPara(s, ti, pi) {
    const k = tKey(s, ti, pi);
    const cur = tText(s, ti, pi);
    const title = aT[s][ti].title;
    setRegenKey(k);
    try {
      const r = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 400,
          messages: [
            {
              role: "user",
              content: `Litigation AI for case ${aCaseName}. Theory: "${title}". Current analysis: "${cur}". Regenerate as a precise improved litigation analysis paragraph. Output only the paragraph text.`,
            },
          ],
        }),
      });
      const d = await r.json();
      const newT = d.content
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .join("")
        .trim();
      if (newT) {
        setTheoryEdits((prev) => ({
          ...prev,
          [k]: {
            text: newT,
            original: aT[s][ti].text[pi],
            editPct: 100,
            aiGenerated: true,
          },
        }));
        setAuditLog((prev) => [
          ...prev,
          {
            ts: new Date().toLocaleString(),
            k,
            action: "AI Regenerated",
            before: cur,
            after: newT,
            user: "CaseBinder AI",
            pct: 100,
          },
        ]);
      }
    } catch (e) {
      console.error(e);
    }
    setRegenKey(null);
  }
  const [aFilter, setAFilter] = useState("All");
  const [aSel, setASel] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [enabledTabs, setEnabledTabs] = useState(null);
  const [installedApps, setInstalledApps] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showDash, setShowDash] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadAssoc, setUploadAssoc] = useState({ wit: "", loc: "" });
  const [showShare, setShowShare] = useState(false);
  const [showAddTL, setShowAddTL] = useState(false);
  const [showRec, setShowRec] = useState(false);
  const [showAddTheory, setShowAddTheory] = useState(false);
  const [newTheoryTitle, setNewTheoryTitle] = useState("");
  const [newTheoryText, setNewTheoryText] = useState("");
  const [customTheories, setCustomTheories] = useState({});
  const [theorySuggLoading, setTheorySuggLoading] = useState(false);
  const [suggestedTheories, setSuggestedTheories] = useState([]);
  const [recActive, setRecActive] = useState(false);
  const [recTranscript, setRecTranscript] = useState([]);
  const [recInterim, setRecInterim] = useState("");
  const [recInferences, setRecInferences] = useState([]);
  const [recElapsed, setRecElapsed] = useState(0);
  const [recSRRef] = useState({ sr: null, timer: null });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatFile, setChatFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showRecordSetup, setShowRecordSetup] = useState(false);
  const [recordType, setRecordType] = useState("depo");
  const [recordWitness, setRecordWitness] = useState("");
  const [recordNote, setRecordNote] = useState("");
  const [recordingLabel, setRecordingLabel] = useState("");
  const [simTick, setSimTick] = useState(0);
  const [simTimer, setSimTimer] = useState(null);
  const [selectedCounsel, setSelectedCounsel] = useState([]);
  const [expandingTheory, setExpandingTheory] = useState(null);
  const [depoWitness, setDepoWitness] = useState(null);
  const [depoTopicsWit, setDepoTopicsWit] = useState(null);
  const [depoDate, setDepoDate] = useState("");
  const [depoTime, setDepoTime] = useState("09:00");
  const [depoLoc, setDepoLoc] = useState(
    "Orrick, Herrington & Sutcliffe LLP\n2050 Main Street, Suite 1100\nIrvine, CA 92614-8255",
  );
  const [draftLoading, setDraftLoading] = useState(null);
  const [suggestedTraps, setSuggestedTraps] = useState([]);
  const [trapSuggLoading, setTrapSuggLoading] = useState(false);
  const [customTraps, setCustomTraps] = useState({});
  const [trapEditMode, setTrapEditMode] = useState(null);
  const [trapEdits, setTrapEdits] = useState({});
  const [expandingTrap, setExpandingTrap] = useState(null);
  const [learnPage, setLearnPage] = useState(0);
  const [learnPersp, setLearnPersp] = useState("plaintiff");
  const [learnPlaying, setLearnPlaying] = useState(false);
  const [learnFlipDir, setLearnFlipDir] = useState(0);
  const [demoSelected, setDemoSelected] = useState(0);
  const [demoVariants, setDemoVariants] = useState({});
  const [demoDescs, setDemoDescs] = useState({});
  const [demoEditMode, setDemoEditMode] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [damTrack, setDamTrack] = useState(0);
  const [damSliders, setDamSliders] = useState({});
  const [damToggles, setDamToggles] = useState({});
  const [damScenarios, setDamScenarios] = useState({});
  const [damSavedName, setDamSavedName] = useState("");
  const [damShowSave, setDamShowSave] = useState(false);
  const [damAILoading, setDamAILoading] = useState(false);
  const [damAIScenarios, setDamAIScenarios] = useState([]);
  const [damCompare, setDamCompare] = useState(false);
  const [settleCompTrack, setSettleCompTrack] = useState("all");
  const [settleCompSide, setSettleCompSide] = useState("all");
  const [settleCompType, setSettleCompType] = useState("all");
  const [settleNewEntries, setSettleNewEntries] = useState({});
  const [settleShowAdd, setSettleShowAdd] = useState(false);
  const [settleNewEntry, setSettleNewEntry] = useState({
    label: "",
    amount: "",
    type: "demand",
    side: "Plaintiff",
    date: "",
    linkedTo: "",
    note: "",
  });
  const [settleBatnaInputs, setSettleBatnaInputs] = useState({});
  const [depExpanded, setDepExpanded] = useState({});
  const [depSearch, setDepSearch] = useState("");
  const [depStatusFilter, setDepStatusFilter] = useState("all");
  const [depPartyFilter, setDepPartyFilter] = useState("all");
  const [judgeSection, setJudgeSection] = useState("ruling");
  const [contraSection, setContraSection] = useState("live");
  const [jurySection, setJurySection] = useState("voir");
  const [juryPersp, setJuryPersp] = useState("all");
  const [juryGridExp] = useState({});
  const [expertsPersp, setExpertsPersp] = useState("all");
  const [expertsExpanded, setExpertsExpanded] = useState({});
  const [clientPersp, setClientPersp] = useState("all");
  const [clientSection, setClientSection] = useState("status");
  const [clientFreq, setClientFreq] = useState("weekly");
  const [warPersp, setWarPersp] = useState("all");
  const [settlePersp, setSettlePersp] = useState("all");
  const [damPersp, setDamPersp] = useState("all");
  const [contraPersp, setContraPersp] = useState("all");
  const [stratmapSection, setStratmapSection] = useState("heatmap");
  const [stratmapLayer, setStratmapLayer] = useState("layer1");
  const [stratmapGapType, setStratmapGapType] = useState("closable");
  const [stratmapCellSel, setStratmapCellSel] = useState(null);
  const [appellatePersp, setAppellatePersp] = useState("all");
  const [appellateSection, setAppellateSection] = useState("rulings");
  const [playbookPersp, setPlaybookPersp] = useState("all");
  const [playbookSection, setPlaybookSection] = useState("strongest");
  const [trapsPersp, setTrapsPersp] = useState("all");
  const [theoriesPersp, setTheoriesPersp] = useState("all");
  const [theoryDetailId, setTheoryDetailId] = useState(null);
  const [theoryViewMode, setTheoryViewMode] = useState("dashboard");
  const [theorySubTab, setTheorySubTab] = useState("elements");
  const [theoryNarrMode, setTheoryNarrMode] = useState("legal");
  const [witIntelPersp, setWitIntelPersp] = useState("all");
  const [witViewMode, setWitViewMode] = useState("map");
  const [selectedWitId, setSelectedWitId] = useState(null);
  const [witSubTab, setWitSubTab] = useState("profile");
  const [seqOrder, setSeqOrder] = useState({});
  const [assetsViewMode, setAssetsViewMode] = useState("hot");
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [docSubTab, setDocSubTab] = useState("ai");
  const [assetsClassFilter, setAssetsClassFilter] = useState("all");
  const [pinnedDocs, setPinnedDocs] = useState({});
  const [customAssets, setCustomAssets] = useState({});
  const [hiddenDocs, setHiddenDocs] = useState({});
  const [assetUploadDraft, setAssetUploadDraft] = useState({
    title: "",
    desc: "",
    date: "",
    classification: "pin",
    exhibit: "",
    type: "exhibit",
  });
  const [assetsSearch, setAssetsSearch] = useState("");
  const [locExpanded, setLocExpanded] = useState({});
  const [locView, setLocView] = useState("list");
  const [locMapPinId, setLocMapPinId] = useState(null);
  const [attyView, setAttyView] = useState("counsel");
  const [attySelected, setAttySelected] = useState(null);
  const [attyThreadId, setAttyThreadId] = useState(null);
  const [emailIntegrations, setEmailIntegrations] = useState({
    gmail: null,
    outlook: null,
  });
  const [showEmailConnect, setShowEmailConnect] = useState(null);
  const [trapsSection, setTrapsSection] = useState("monitor");
  const [impSearch, setImpSearch] = useState("");
  const [impStatusFilter, setImpStatusFilter] = useState("all");
  const [impPriorityFilter, setImpPriorityFilter] = useState("all");
  const [impTypeFilter, setImpTypeFilter] = useState("all");
  const [impPartyFilter, setImpPartyFilter] = useState("all");
  const [impStatuses, setImpStatuses] = useState({});
  const [impExpanded, setImpExpanded] = useState({});
  const [impMarkOpen, setImpMarkOpen] = useState(null);
  const [impAILoading, setImpAILoading] = useState(false);
  const [impAIResult, setImpAIResult] = useState(null);
  const [impCustomSeqs, setImpCustomSeqs] = useState([]);
  const [warTrack, setWarTrack] = useState("sj");
  const [warNotes, setWarNotes] = useState({});
  const [warNoteInput, setWarNoteInput] = useState("");
  const [sq, setSq] = useState("");
  const [ct, setCt] = useState("pacer");
  const [aiModel, setAiModel] = useState("multi");
  const [mFilter, setMFilter] = useState("all");
  const [wFilter, setWFilter] = useState("all");
  const [clFilter, setClFilter] = useState("all");
  const [gapPersp, setGapPersp] = useState("Defendant");
  const [hmPersp, setHmPersp] = useState("all");
  const [hearPersp, setHearPersp] = useState("Defendant");
  const [trapPersp, setTrapPersp] = useState("Defendant");
  const [ctrPersp, setCtrPersp] = useState("Defendant");
  const [pbPersp, setPbPersp] = useState("Defendant");
  const [settPersp, setSettPersp] = useState(2);
  const [judgePersp, setJudgePersp] = useState(3);
  const pR = useRef(null);
  const bm = [
    {
      id: "naqvi",
      num: "1:26-cr-00097-NGG-2",
      name: "USA v. Naqvi (iLearning)",
      court: "E.D.N.Y.",
      status: "Released · $3M bond",
      c: "#10b981",
    },
    {
      id: "chid",
      num: "1:26-cr-00097-NGG-1",
      name: "USA v. Chidambaran",
      court: "E.D.N.Y.",
      status: "Pretrial · SC 9/14",
      c: "#a78bfa",
    },
    {
      id: "musk",
      num: "4:24-cv-04722-YGR",
      name: "Musk v. Altman et al",
      court: "N.D. Cal.",
      status: "🔴 Trial",
      c: "#ef4444",
    },
    {
      id: "neuro",
      num: "2:25-cv-05871-FLA-MAR",
      name: "Neurocentria v. Mando",
      court: "C.D. Cal",
      status: "Trial 3/9/27",
      c: "#3b82f6",
    },
    {
      id: "juul",
      num: "3:19-md-02913-WHO",
      name: "Seliavski v. Juul",
      court: "N.D. Cal. MDL",
      status: "Active",
      c: "#f59e0b",
    },
  ];
  const allTL = useMemo(
    () =>
      [...aFTL, ...aPTL, ...aSTL, ...customTL].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      ),
    [customTL, aFTL, aPTL, aSTL],
  );
  const tlD =
    tlMode === "all"
      ? allTL
      : tlMode === "factual"
        ? aFTL
        : tlMode === "procedural"
          ? aPTL
          : aSTL;
  const tlDfBase =
    isJuul && tlPf !== "all"
      ? tlD.filter(
          (t) => !t.plaintiff || t.plaintiff === "All" || t.plaintiff === tlPf,
        )
      : tlD;
  const tlDf = tlSearch
    ? tlDfBase.filter(
        (t) =>
          t.label?.toLowerCase().includes(tlSearch.toLowerCase()) ||
          t.detail?.toLowerCase().includes(tlSearch.toLowerCase()) ||
          String(t.ref || "")
            .toLowerCase()
            .includes(tlSearch.toLowerCase()),
      )
    : tlDfBase;
  const ci = tlDf[Math.min(tlIdx, tlDf.length - 1)];
  useEffect(() => {
    if (playing) {
      pR.current = setInterval(
        () =>
          setTlIdx((p) => {
            if (p >= tlDf.length - 1) {
              setPlaying(false);
              return p;
            }
            return p + 1;
          }),
        1200,
      );
    } else if (pR.current) clearInterval(pR.current);
    return () => {
      if (pR.current) clearInterval(pR.current);
    };
  }, [playing, tlD.length]);
  // AI helper with graceful demo fallback when proxy is unreachable
  async function aiCall(body, demoFallback) {
    try {
      const r = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (!d.content) throw new Error(d.error?.message || "No content");
      const text = d.content
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .join("")
        .trim();
      if (!text) throw new Error("Empty response");
      return { text, demo: false };
    } catch (err) {
      console.warn("[AI fallback]", err.message);
      const fb =
        typeof demoFallback === "function" ? demoFallback() : demoFallback;
      return {
        text:
          fb ||
          "Demo mode - proxy unreachable. Run `npm run dev` with ANTHROPIC_API_KEY set in .env to enable live AI.",
        demo: true,
      };
    }
  }
  // Contextual chat fallbacks per case
  function chatDemoResponse(q, cid) {
    const ql = q.toLowerCase();
    const banner =
      "🟡 **Demo mode** - Live AI is unavailable. Run `npm run dev` with `ANTHROPIC_API_KEY` set in `.env` to enable live AI.\n\n";
    let body;
    if (cid === "juul") {
      if (ql.match(/sj|summary judgment|orrick/))
        body =
          "JLI's summary judgment motions remain under submission since the October 31, 2025 hearing (Dkt.4453). Judge Orrick telegraphed remand intent and questioned the Sonner-Pak two-step's circular logic. If SJ is denied, JLI faces remand and jury trial; if granted on causation, opt-out PI plaintiffs lose leverage. SFUSD stays in N.D. Cal. regardless.";
      else if (ql.match(/pak|deposition/))
        body =
          "The Pak deposition is the linchpin of JLI's defense: Pak admitted SFUSD cannot quantify past JUUL-attributable program costs. JLI uses this for step 1 of the Sonner-Pak two-step (no legal damages → invoke Sonner to bar equitable abatement = $0 for SFUSD). Orrick's Oct 31 hearing identified the circularity in step 2.";
      else if (ql.match(/rico|enterprise/))
        body =
          "The RICO enterprise theory alleges JLI, Altria, and individual directors (Pritzker, Huh, Valani) operated as an associated-in-fact enterprise to market addictive nicotine products to minors. Predicate acts include wire fraud (FDA submissions) and mail fraud (marketing materials). Director liability requires showing direction of enterprise affairs.";
      else if (ql.match(/opt.?out|remand/))
        body =
          "Post-SJ, opt-out PI plaintiffs (Seliavski, Aragona, Lane, Legacki, Nessmith, Sedgwick) remand to transferee courts in CA, FL, and TX with plaintiff-favorable jury pools. SFUSD stays in N.D. Cal. The single most critical pre-remand action is retaining a Seliavski medical causation expert.";
      else if (ql.match(/seliavski|personal injury|injury/))
        body =
          "Seliavski's strongest argument is fraudulent concealment + design defect: JLI concealed JUUL's addictive properties through misleading FDA submissions while designing 59mg/mL nicotine salts in a concealable form factor specifically to maximize addiction. Virginia contributory negligence is the key vulnerability - must establish 0% plaintiff fault.";
      else
        body =
          "Without live AI, here's a synthesis from the case record: the JUUL MDL's current inflection point is the pending SJ ruling. JLI/Altria settled the antitrust class actions; opt-out PI cases and SFUSD's public nuisance theory remain. Key dates: Meta production May 8, 2026; supplemental discovery dispute deadline May 11, 2026; antitrust opt-out June 13, 2026; antitrust trial Sept 28, 2026.";
    } else if (cid === "musk") {
      if (ql.match(/scott|microsoft|knowledge/))
        body =
          "The Kevin Scott email (PX230, March 2018) is critical for Count XIX (aiding & abetting): Microsoft's CTO questioned whether OpenAI donors knew about the for-profit conversion plans. This establishes pre-investment knowledge at the CTO level - but plaintiff still needs to bridge to board-level knowledge for the July 2019 $1B investment to support the constructive trust on Microsoft's ~27% equity stake.";
      else if (ql.match(/brockman|journal/))
        body =
          "The Brockman journal entry (PX163) is foundational evidence of charitable mission representations made to Musk during 2015-2017 solicitations. Combined with Altman's founding email (PX003), it establishes the elements of Cal. Bus. & Prof. Code §17510.8 strict liability if YGR rules the statute applies (Dkt.515/516 competing briefs).";
      else if (ql.match(/fifo|tracing|dudney/))
        body =
          "Dudney's FIFO tracing methodology connects Musk's $38M in donations to OpenAI's current $157B+ enterprise value. Defense was permitted by Daubert to challenge the methodology - they'll argue Microsoft's $1B investment and GPT-4 commercial success break the causal chain. This is the central battleground for Count IV (unjust enrichment) damages.";
      else if (ql.match(/17510|charitable|solicitation/))
        body =
          "Cal. Bus. & Prof. Code §17510.8 imposes strict liability for false representations in charitable solicitations. If YGR rules the statute applies (competing briefs Dkt.515/516, filed Apr 30 2026), Altman and Brockman face strict liability for 2015-2019 emails to Musk - significantly stronger than common-law charitable trust theory.";
      else if (ql.match(/sol|statute of limitations|laches|acquiescence/))
        body =
          'The strongest defense argument is acquiescence: Musk was informed of the for-profit LP structure in August 2018 (DX-827), said "Yup" in January 2016 (DX-516), and told Altman to say he was "supportive" in March 2019 (DX-862). Five years of delay before filing creates textbook laches. Plaintiff needs evidence of continued reliance post-2018 to rebut.';
      else
        body =
          "Without live AI, here's a synthesis: Phase 1 trial is in progress with three counts - IV (unjust enrichment), XVIII (charitable trust breach), XIX (aiding & abetting). The Section 17510.8 ruling and YGR's decision on Dkt.522 (the Musk threat to Brockman) are the two most important pending issues. Trial outcome will determine whether stayed Phase 2 antitrust counterclaims activate.";
    } else {
      if (ql.match(/mtd|motion to dismiss/))
        body =
          "Two MTDs are pending: (1) §101 patent eligibility challenge to the '061 patent arguing it's directed to a natural phenomenon (magnesium L-threonate's effect on cognition); (2) standing challenge based on FAC ¶8/¶13 contradiction. Plaintiff opposed both. Key inflection point: 4/17/26 hearing.";
      else if (ql.match(/§101|section 101|patent.?eligib/))
        body =
          "The §101 challenge argues the '061 patent claims are directed to a natural phenomenon (the brain's response to magnesium) and conventional dosage forms. Specification admits in vivo formation; Liu admitted the molecule wasn't invented. Plaintiff's response: the specific dosage form is non-obvious and claim-limiting. Defendant's strongest argument heading into SJ.";
      else if (ql.match(/contradiction|fac|standing|paragraph 8|13/))
        body =
          'The FAC ¶8 vs ¶13 contradiction is fatal for standing: ¶8 alleges Neurocentria "owns" the patents, while ¶13 alleges all rights were assigned to ThreoTech. Defendant argues Neurocentria divested standing. Plaintiff must amend or lose at the threshold - this is the strongest non-merits dismissal vector.';
      else if (ql.match(/plant.?based|infring/))
        body =
          "The Plant-Based Product infringement claim relies entirely on a single Amazon marketing slogan - vulnerable to Twombly. Critical evidence gap: independent compositional testing confirming the qualifying dosage form. Without lab results, this count survives only if plaintiff can amend with specific factual allegations.";
      else if (ql.match(/country.?of.?origin|fraud|jordan|china/))
        body =
          "The Country-of-Origin counterclaim is strong if AIDP routed Chinese-sourced magnesium L-threonate through Jordan to claim US-Jordan FTA benefits without substantial transformation. Documentary backbone: AIDP's internal sourcing records and U.S. Customs CBP data. Lanham Act §43(a) supports false designation claim independent of confusion.";
      else
        body =
          "Without live AI, here's a synthesis from the case record: Neurocentria v. Mando is a §101 patent eligibility battle with two pending MTDs. Defense strategy is layered SJ on §101 and standing. Key evidence gaps: lab testing of Plant-Based Product, damages expert (Sept 18 2026 deadline), and Dr. Slutsky declaration. Trial set March 9, 2027.";
    }
    return (
      banner +
      body +
      "\n\n*With live AI, the assistant would tailor this with full case context, exact citations, and follow-up questions.*"
    );
  }
  // Demo expansion for theory expand button
  function expandDemoResponse(theoryTitle, theoryText, side) {
    return (
      `🟡 **Demo mode** - Live AI is unavailable. Run \`npm run dev\` with ANTHROPIC_API_KEY set in .env to enable live expansion.\n\n` +
      `## ${theoryTitle}\n\n` +
      `**Legal Standard.** ${theoryText}\n\n` +
      `**Factual Basis.** Without access to the live AI synthesis, a full expansion would integrate the case docket, deposition testimony, and exhibit record to develop the factual narrative. The AI would identify which specific filings and witness statements support each element of this theory and connect them in a structured argument.\n\n` +
      `**Authority.** Live AI expansion would cite the controlling cases for this jurisdiction, identify analogous decisions, and flag any contrary authority that needs to be distinguished. The expansion would map each authority to a specific argumentative move.\n\n` +
      `**Policy Argument.** Beyond doctrinal analysis, the live expansion would develop the policy considerations a court would weigh - judicial economy, deterrent effect, regulatory compatibility, and competing equities. These shape how courts apply the controlling rule when facts are close.\n\n` +
      `**Likely Counter-Arguments.** Live AI would anticipate the opposing side's strongest responses, weigh the relative strength of each, and propose anticipatory framing in the principal brief.\n\n` +
      `**Conclusion.** The court should rule for the ${side === "plaintiff" ? "plaintiff" : "defendant"} on this theory because [the live expansion would synthesize the strongest version of the argument with case-specific facts].\n\n` +
      `---\n*To enable live ~1000-word AI expansion with citations and case-specific analysis, ensure the proxy server is running and ANTHROPIC_API_KEY is configured.*`
    );
  }
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      } else if (e.key === "Escape") {
        if (showSearch) setShowSearch(false);
        else if (showDash) setShowDash(false);
        else if (showSettings) setShowSettings(false);
        else if (showShare) setShowShare(false);
        else if (showMarketplace) setShowMarketplace(false);
        else if (panel) setPanel(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showSearch, showDash, showSettings, showShare, showMarketplace, panel]);
  const fDk = useMemo(
    () =>
      aD
        .map((d, i) => ({ ...d, idx: i, at: dkTags[i] || [] }))
        .filter(
          (d) =>
            (dkF === "all" || d.at.includes(dkF)) &&
            (!dkS || (d.text || "").toLowerCase().includes(dkS.toLowerCase())),
        ),
    [dkF, dkS, dkTags, aD],
  );
  const fA = aA.filter((a) => aFilter === "All" || a.cat === aFilter);
  const openP = (t, c, r, loading = false) =>
    setPanel({ title: t, content: c, ref: r, loading });
  const cancelRecordSetup = () => {
    setShowRecordSetup(false);
    if (recordReturnTabRef.current) {
      setTab(recordReturnTabRef.current);
      recordReturnTabRef.current = null;
    }
  };
  const sp = panel || (tab === "assets" && aSel);
  const NI = (
    <div style={{ display: "flex", gap: 6 }}>
      {page !== "home" && (
        <button
          onClick={() => setShowShare(true)}
          style={{
            height: 34,
            padding: "0 12px",
            display: "flex",
            borderRadius: 7,
            background: th.surface,
            border: `1px solid ${th.border}`,
            color: th.dim,
            cursor: "pointer",
            fontSize: 12,
            alignItems: "center",
            gap: 6,
            fontWeight: 500,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = th.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = th.dim;
          }}
        >
          👥<span>Share</span>
        </button>
      )}
      <button
        onClick={() => setShowSearch(true)}
        title="Search (⌘K)"
        style={{
          height: 34,
          padding: "0 12px",
          borderRadius: 7,
          background: th.surface,
          border: `1px solid ${th.border}`,
          color: th.dim,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 500,
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = th.text;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = th.dim;
        }}
      >
        <span>🔍</span>
        <span>Search</span>
        <kbd
          style={{
            fontSize: 9,
            fontFamily: "monospace",
            color: th.faint,
            background: th.bg,
            border: `1px solid ${th.border}`,
            padding: "1px 5px",
            borderRadius: 3,
            fontWeight: 500,
            marginLeft: 2,
          }}
        >
          ⌘K
        </kbd>
      </button>
      <button
        onClick={() => setShowDash(true)}
        style={{
          height: 34,
          padding: "0 12px",
          borderRadius: 7,
          background: th.surface,
          border: `1px solid ${th.border}`,
          color: th.dim,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 500,
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = th.text;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = th.dim;
        }}
      >
        <span>📋</span>
        <span>Overview</span>
      </button>
    </div>
  );
  const Modals = (
    <>
      {showSearch && (
        <SM
          th={th}
          onClose={() => setShowSearch(false)}
          onSel={() => {
            setShowSearch(false);
            setPage("case");
          }}
        />
      )}
      {showShare && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.7)",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowShare(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: th.surface,
              border: `1px solid ${th.border}`,
              borderRadius: 12,
              padding: 24,
              width: 460,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              👥 Share Case
            </div>
            <div style={{ fontSize: 13, color: th.muted, marginBottom: 14 }}>
              Invite collaborators to view or contribute to this case binder.
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              <input
                placeholder="Email address"
                style={{
                  flex: 1,
                  padding: "9px 12px",
                  fontSize: 13,
                  background: th.bg,
                  border: `1px solid ${th.border}`,
                  borderRadius: 6,
                  color: th.text,
                  outline: "none",
                }}
              />
              <select
                style={{
                  padding: "9px 12px",
                  fontSize: 12,
                  background: th.bg,
                  border: `1px solid ${th.border}`,
                  borderRadius: 6,
                  color: th.text,
                  outline: "none",
                }}
              >
                <option>Editor</option>
                <option>Viewer</option>
              </select>
              <button
                style={{
                  padding: "9px 18px",
                  fontSize: 13,
                  background: "#1e40af",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Invite
              </button>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              People with access
            </div>
            {[
              {
                name: "You",
                email: "jsmith@smithlaw.com",
                role: "Owner",
                ph: "👤",
              },
              {
                name: "Sarah Chen",
                email: "schen@smithlaw.com",
                role: "Editor",
                ph: "👩‍💼",
              },
              {
                name: "Client: Kappa Nutrition",
                email: "info@kappanutrition.com",
                role: "Viewer",
                ph: "🏢",
              },
            ].map((p) => (
              <div
                key={p.email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: `1px solid ${th.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{p.ph}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: th.dim }}>{p.email}</div>
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    color:
                      p.role === "Owner"
                        ? "#10b981"
                        : p.role === "Editor"
                          ? "#3b82f6"
                          : "#6b7280",
                    background:
                      p.role === "Owner"
                        ? "#10b98118"
                        : p.role === "Editor"
                          ? "#3b82f618"
                          : "#6b728018",
                    padding: "3px 8px",
                    borderRadius: 4,
                  }}
                >
                  {p.role}
                </span>
              </div>
            ))}
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: th.bg,
                borderRadius: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Share link</div>
                <div
                  style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: th.dim,
                  }}
                >
                  https://casebinder.ai/s/mando-v-neuro-5871
                </div>
              </div>
              <button
                style={{
                  fontSize: 11,
                  background: th.surface,
                  color: th.dim,
                  border: `1px solid ${th.border}`,
                  borderRadius: 4,
                  padding: "5px 12px",
                  cursor: "pointer",
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
      {showDash && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.7)",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDash(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: th.surface,
              border: `1px solid ${th.border}`,
              borderRadius: 12,
              padding: 24,
              width: 500,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
              📋 Cases
            </div>
            {bm.map((b) => (
              <div
                key={b.num}
                onClick={() => {
                  setShowDash(false);
                  setCaseId(b.id || "neuro");
                  setPage("case");
                  setTab("ringbinder");
                  setPanel(null);
                }}
                style={{
                  background: th.card,
                  borderRadius: 8,
                  border: `1px solid ${th.border}`,
                  borderLeft: `4px solid ${b.c}`,
                  padding: "14px 16px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        fontFamily: "monospace",
                        color: b.c,
                      }}
                    >
                      {b.num}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {b.name}
                    </div>
                    <div style={{ fontSize: 10, color: th.muted }}>
                      {b.court}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ fontSize: 7, color: th.faint }}>
                      Your perspective:
                    </div>
                    <div style={{ display: "flex", gap: 2 }}>
                      {["Plaintiff", "Defendant"].map((p) => (
                        <button
                          key={p}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: 7,
                            padding: "2px 6px",
                            background:
                              p === "Defendant" ? "#3b82f618" : "transparent",
                            color: p === "Defendant" ? "#3b82f6" : th.faint,
                            border: `1px solid ${p === "Defendant" ? "#3b82f6" : th.border}`,
                            borderRadius: 3,
                            cursor: "pointer",
                          }}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showSettings && (
        <SetM
          th={th}
          dark={dark}
          setDark={setDark}
          aiModel={aiModel}
          setAiModel={setAiModel}
          onClose={() => setShowSettings(false)}
          onPage={(p) => setPage(p)}
          openMarketplace={() => {
            setShowMarketplace(true);
            setTimeout(() => setShowSettings(false), 50);
          }}
          emailIntegrations={emailIntegrations}
          setEmailIntegrations={setEmailIntegrations}
          toast={toast}
        />
      )}
      {showUpload && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => {
            setShowUpload(false);
            setUploadAssoc({ wit: "", loc: "" });
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: th.surface,
              border: `1px solid ${th.border}`,
              borderRadius: 10,
              padding: 20,
              width: 480,
              maxHeight: "88vh",
              overflow: "auto",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              Upload Asset
            </div>
            <div
              style={{
                border: `2px dashed ${th.border}`,
                borderRadius: 8,
                padding: 16,
                textAlign: "center",
                marginBottom: 10,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 18 }}>📂</span>
              <div style={{ fontSize: 10, color: th.muted }}>
                Drag & drop or click to browse
              </div>
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                marginBottom: 4,
                color: th.muted,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Type
            </div>
            <div
              style={{
                display: "flex",
                gap: 4,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              {[
                ["local-rule", "📏", "Local Rule"],
                ["depo", "📝", "Depo Transcript"],
                ["hearing", "🎙️", "Hearing Transcript"],
                ["interview", "🗣️", "Interview"],
                ["filing", "📄", "Filing"],
                ["exhibit", "📎", "Exhibit"],
                ["photo", "📷", "Photo"],
                ["video", "🎬", "Video"],
                ["audio", "🔊", "Audio"],
                ["other", "📋", "Other"],
              ].map(([k, e, l]) => (
                <button
                  key={k}
                  onClick={() =>
                    setAssetUploadDraft((p) => ({ ...p, type: k }))
                  }
                  style={{
                    padding: "4px 9px",
                    fontSize: 9,
                    background: assetUploadDraft.type === k ? "#7c3aed" : th.bg,
                    color: assetUploadDraft.type === k ? "#fff" : th.dim,
                    border: `1px solid ${assetUploadDraft.type === k ? "#7c3aed" : th.border}`,
                    borderRadius: 4,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    fontWeight: assetUploadDraft.type === k ? 700 : 500,
                  }}
                >
                  <span>{e}</span>
                  <span>{l}</span>
                </button>
              ))}
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                marginBottom: 4,
                color: th.muted,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Classification (legal role)
            </div>
            <div
              style={{
                display: "flex",
                gap: 4,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              {[
                ["pin", "📌", "Pin"],
                ["foundation", "🧱", "Foundation"],
                ["impeachment", "⚔️", "Impeachment"],
                ["expert-support", "🎓", "Expert Support"],
                ["prior-art", "📜", "Prior Art"],
              ].map(([k, e, l]) => (
                <button
                  key={k}
                  onClick={() =>
                    setAssetUploadDraft((p) => ({ ...p, classification: k }))
                  }
                  style={{
                    padding: "4px 9px",
                    fontSize: 9,
                    background:
                      assetUploadDraft.classification === k ? "#7c3aed" : th.bg,
                    color:
                      assetUploadDraft.classification === k ? "#fff" : th.dim,
                    border: `1px solid ${assetUploadDraft.classification === k ? "#7c3aed" : th.border}`,
                    borderRadius: 4,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    fontWeight:
                      assetUploadDraft.classification === k ? 700 : 500,
                  }}
                >
                  <span>{e}</span>
                  <span>{l}</span>
                </button>
              ))}
            </div>
            <input
              value={assetUploadDraft.title}
              onChange={(e) =>
                setAssetUploadDraft((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Title *"
              style={{
                width: "100%",
                padding: 7,
                fontSize: 11,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                boxSizing: "border-box",
                outline: "none",
                marginBottom: 6,
              }}
            />
            <textarea
              value={assetUploadDraft.desc}
              onChange={(e) =>
                setAssetUploadDraft((p) => ({ ...p, desc: e.target.value }))
              }
              placeholder="Description / summary"
              rows={2}
              style={{
                width: "100%",
                padding: 7,
                fontSize: 11,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                boxSizing: "border-box",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                marginBottom: 6,
              }}
            />
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <input
                type="date" value={assetUploadDraft.date} onClick={(e) => { try { e.currentTarget.showPicker(); } catch (_) {} }}
                onChange={(e) =>
                  setAssetUploadDraft((p) => ({ ...p, date: e.target.value }))
                }
                placeholder="Date" style={{ colorScheme: dark ? "dark" : "light",
                  flex: 1,
                  padding: 7,
                  fontSize: 11,
                  background: th.bg,
                  border: `1px solid ${th.border}`,
                  borderRadius: 4,
                  color: th.text,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
              <input
                value={assetUploadDraft.exhibit}
                onChange={(e) =>
                  setAssetUploadDraft((p) => ({
                    ...p,
                    exhibit: sanitizeExhibitNo(e.target.value)
                  }))
                }
                placeholder="Exhibit # (e.g. #1A, #2D.1)"
                style={{
                  flex: 1,
                  padding: 7,
                  fontSize: 11,
                  background: th.bg,
                  border: `1px solid ${th.border}`,
                  borderRadius: 4,
                  color: th.text,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, color: th.dim, marginBottom: 3 }}>
                  Associated Witness
                </div>
                <input
                  value={uploadAssoc.wit}
                  onChange={(e) =>
                    setUploadAssoc((p) => ({ ...p, wit: e.target.value }))
                  }
                  placeholder="Witness name"
                  style={{
                    width: "100%",
                    padding: 6,
                    fontSize: 10,
                    background: th.bg,
                    border: `1px solid ${th.border}`,
                    borderRadius: 4,
                    color: th.text,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, color: th.dim, marginBottom: 3 }}>
                  Associated Location
                </div>
                <input
                  value={uploadAssoc.loc}
                  onChange={(e) =>
                    setUploadAssoc((p) => ({ ...p, loc: e.target.value }))
                  }
                  placeholder="Location name"
                  style={{
                    width: "100%",
                    padding: 6,
                    fontSize: 10,
                    background: th.bg,
                    border: `1px solid ${th.border}`,
                    borderRadius: 4,
                    color: th.text,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
            <div
              style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => {
                  setShowUpload(false);
                  setAssetUploadDraft({
                    title: "",
                    desc: "",
                    date: "",
                    classification: "pin",
                    exhibit: "",
                    type: "exhibit",
                  });
                  setUploadAssoc({ wit: "", loc: "" });
                }}
                style={{
                  padding: "5px 14px",
                  fontSize: 10,
                  background: th.border,
                  color: th.muted,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const t = (assetUploadDraft.title || "").trim();
                  if (!t) {
                    toast("Title is required", "warn");
                    return;
                  }
                  const newId = "ca-" + Date.now();
                  const newDoc = {
                    id: newId,
                    type: assetUploadDraft.type || "exhibit",
                    title: t,
                    classification: assetUploadDraft.classification || "pin",
                    producer: "User upload",
                    bates: "USER-" + Date.now().toString().slice(-6),
                    date: fmtUploadDate(assetUploadDraft.date),
                    confidentiality: "Public",
                    pages: 0,
                    exhibit: assetUploadDraft.exhibit || "",
                    exhibitStatus: "pending",
                    cluster: null,
                    assocWitness: uploadAssoc.wit || "",
                    assocLocation: uploadAssoc.loc || "",
                    auth: {
                      method: "User-uploaded - authentication pending",
                      witness: uploadAssoc.wit || "⚠️ Not yet identified",
                      objections: ["Authentication pending - uploaded by user"],
                      status: "pending", foundation: "FRE 901 (authentication pending)",
                    },
                    theoryMaps: [],
                    ai: {
                      summary:
                        assetUploadDraft.desc ||
                        "User-uploaded document. AI intelligence not yet generated.",
                      keyPassages: [],
                      contradictions: [],
                      questions: [
                        "Establish authentication chain",
                        "Identify foundation witness",
                        "Assess theory mapping",
                      ],
                      witness: uploadAssoc.wit || "",
                    },
                  };
                  setCustomAssets((p) => ({
                    ...p,
                    [caseId]: [...(p[caseId] || []), newDoc],
                  }));
                  setShowUpload(false);
                  setAssetUploadDraft({
                    title: "",
                    desc: "",
                    date: "",
                    classification: "pin",
                    exhibit: "",
                    type: "exhibit",
                  });
                  setUploadAssoc({ wit: "", loc: "" });
                  toast(`Uploaded: ${t.slice(0, 40)}`, "success");
                }}
                style={{
                  padding: "5px 14px",
                  fontSize: 10,
                  background: "#1e40af",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  /* HOME */
  if (page === "devdocs")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
            position: "sticky",
            top: 0,
            zIndex: 100,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>CaseBinder</span>
            <span
              style={{
                fontSize: 9,
                color: "#a78bfa",
                fontFamily: "monospace",
                marginLeft: 4,
              }}
            >
              / Developer Docs
            </span>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {[
              "Overview",
              "Quick Start",
              "API Reference",
              "Extensions",
              "Publishing",
            ].map((s) => (
              <button
                key={s}
                onClick={() =>
                  document
                    .getElementById("doc-" + s.toLowerCase().replace(" ", "-"))
                    .scrollIntoView({ behavior: "smooth" })
                }
                style={{
                  fontSize: 10,
                  color: "#94a3b8",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {s}
              </button>
            ))}
            <button
              onClick={() => setPage("home")}
              style={{
                padding: "6px 14px",
                fontSize: 10,
                fontWeight: 600,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Get API Key
            </button>
          </div>
        </nav>
        <div
          style={{ maxWidth: 900, margin: "0 auto", padding: "48px 24px 80px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div
              style={{
                fontSize: 10,
                fontFamily: "monospace",
                color: "#a78bfa",
                fontWeight: 600,
                letterSpacing: 2,
                marginBottom: 10,
              }}
            >
              DEVELOPER PLATFORM
            </div>
            <h1
              style={{
                fontSize: 32,
                fontWeight: 700,
                margin: "0 0 14px",
                lineHeight: 1.2,
              }}
            >
              Build on CaseBinder
            </h1>
            <p
              style={{
                fontSize: 12,
                color: "#94a3b8",
                maxWidth: 560,
                margin: "0 auto 24px",
                lineHeight: 1.7,
              }}
            >
              Add custom views to any case binder, integrate your own data
              sources, and publish extensions to the marketplace - all with a
              single REST API.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <a
                href="mailto:api@casebinder.ai?subject=API Key Request"
                style={{
                  padding: "10px 24px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#1e40af,#a78bfa)",
                  color: "#fff",
                  borderRadius: 8,
                  textDecoration: "none",
                }}
              >
                Request API Key
              </a>
              <a
                href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
                target="_blank"
                style={{
                  padding: "10px 24px",
                  fontSize: 11,
                  color: "#a78bfa",
                  background: "transparent",
                  border: "1px solid #a78bfa44",
                  borderRadius: 8,
                  textDecoration: "none",
                }}
              >
                U.S. Patent Pending ↗
              </a>
            </div>
          </div>
          <div id="doc-overview" style={{ marginBottom: 48 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 6,
                borderBottom: "1px solid #1e293b",
                paddingBottom: 10,
              }}
            >
              Overview
            </h2>
            <p
              style={{
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              CaseBinder extensions are React components that snap into the left
              navigation of any case. They receive the full case context - 
              docket entries, witnesses, evidence, theories, attorneys - and can
              read and write via the REST API. Extensions can be kept private or
              published to the marketplace for any firm to install.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 12,
              }}
            >
              {[
                [
                  "🔌",
                  "Left-Nav Views",
                  "Render any React component as a tab in any case. Receives case context as props.",
                ],
                [
                  "🔗",
                  "REST + GraphQL API",
                  "Full read/write access to case data, docket entries, and AI inference endpoints.",
                ],
                [
                  "📡",
                  "Webhooks",
                  "Subscribe to case events - new filings, theory updates, witness additions - in real time.",
                ],
                [
                  "🔐",
                  "Private or Public",
                  "Keep extensions internal to your firm, or publish to the marketplace for others to install.",
                ],
                [
                  "🤖",
                  "AI Endpoints",
                  "Call CaseBinder's case-aware AI from your extension with full docket context included.",
                ],
                [
                  "🏛️",
                  "PACER Data",
                  "Access live docket data from PACER and all 50 state court systems via the API.",
                ],
              ].map(([icon, title, desc], i) => (
                <div
                  key={i}
                  style={{
                    background: "#0f172a",
                    borderRadius: 8,
                    border: "1px solid #1e293b",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#e2e8f0",
                      marginBottom: 4,
                    }}
                  >
                    {title}
                  </div>
                  <p
                    style={{
                      fontSize: 8,
                      color: "#475569",
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div id="doc-quick-start" style={{ marginBottom: 48 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 6,
                borderBottom: "1px solid #1e293b",
                paddingBottom: 10,
              }}
            >
              Quick Start
            </h2>
            <p
              style={{
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              Build and test your first extension in under 10 minutes. You need
              a CaseBinder API key and Node.js 18+.
            </p>
            {[
              {
                step: "1",
                title: "Connect to the API",
                code: "curl -H 'Authorization: Bearer cb_live_xxx' https://api.casebinder.ai/v1/cases",
              },
              {
                step: "2",
                title: "Authenticate",
                code: `// REST API - no SDK needed
const API = "https://api.casebinder.ai/v1";
const KEY = "cb_live_xxxxxxxxxxxxxxxx";

const cb = new CaseBinder({
  apiKey: "cb_live_xxxxxxxxxxxxxxxx"
});`,
              },
              {
                step: "3",
                title: "Read case data",
                code: `// Fetch a case by docket number
const res = await fetch(API + "/cases/3:19-md-02913-WHO",
  { headers: { Authorization: "Bearer " + KEY } }
);
const caseData = await res.json();

console.log(caseData.theories);   // All theories
console.log(caseData.witnesses);  // All witnesses  
console.log(caseData.docket);     // Live ECF entries`,
              },
              {
                step: "4",
                title: "Create a custom view",
                code: `// my-extension/index.jsx
export default function MyView({ caseId, theme }) {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    cb.cases.get(caseId).then(setData);
  }, [caseId]);

  return (
    <div style={{ padding: 16, color: theme.text }}>
      <h3>My Custom Analysis</h3>
      {data && <p>{data.theories.length} theories loaded</p>}
    </div>
  );
}`,
              },
              {
                step: "5",
                title: "Register your extension",
                code: `// casebinder.config.json
{
  "name": "my-extension",
  "version": "1.0.0",
  "icon": "📊",
  "label": "My Analysis",
  "visibility": "private",  // or "public"
  "entry": "./index.jsx"
}`,
              },
            ].map(({ step, title, code }, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg,#1e40af,#a78bfa)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {step}
                  </div>
                  <div
                    style={{ fontSize: 11, fontWeight: 600, color: "#e2e8f0" }}
                  >
                    {title}
                  </div>
                </div>
                <div
                  style={{
                    background: "#020817",
                    border: "1px solid #1e293b",
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontFamily: "monospace",
                    fontSize: 9,
                    color: "#7dd3fc",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    overflowX: "auto",
                  }}
                >
                  {code}
                </div>
              </div>
            ))}
            <div
              style={{
                background: "#0f172a",
                border: "1px solid #10b98133",
                borderRadius: 8,
                padding: "12px 16px",
                marginTop: 8,
              }}
            >
              <span style={{ color: "#10b981", fontWeight: 600, fontSize: 10 }}>
                ✓ Done.
              </span>{" "}
              <span style={{ fontSize: 9, color: "#94a3b8" }}>
                Deploy with{" "}
              </span>
              <code
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  color: "#7dd3fc",
                }}
              >
                cb publish
              </code>
              <span style={{ fontSize: 9, color: "#94a3b8" }}>
                {" "}
                and your extension appears in the left nav of every case for
                your account.
              </span>
            </div>
          </div>
          <div id="doc-api-reference" style={{ marginBottom: 48 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 6,
                borderBottom: "1px solid #1e293b",
                paddingBottom: 10,
              }}
            >
              API Reference
            </h2>
            <div
              style={{
                background: "#020817",
                border: "1px solid #1e293b",
                borderRadius: 8,
                padding: "12px 16px",
                fontFamily: "monospace",
                fontSize: 9,
                color: "#7dd3fc",
                marginBottom: 16,
              }}
            >
              Base URL: https://api.casebinder.ai/v1 Authentication: Bearer
              cb_live_xxxxxxxxxxxxxxxx
            </div>
            {[
              {
                method: "GET",
                path: "/cases/{docket}",
                desc: "Fetch full case data including theories, witnesses, docket entries, evidence gaps, and attorneys.",
                resp: "{id, docket, case_name, court, theories[], witnesses[], docket_entries[], attorneys[], evidence_gaps[], settlement_signals[]}",
              },
              {
                method: "GET",
                path: "/cases/{docket}/docket",
                desc: "Stream live PACER docket entries. Use ?since=timestamp to get only new entries.",
                resp: "{entries:[{ecf_number, date, type, title, url, parties[]}]}",
              },
              {
                method: "POST",
                path: "/cases/{docket}/theories",
                desc: "Add a new theory to a case. Appears immediately in the Theories tab.",
                resp: "{id, title, text, side, created_at}",
              },
              {
                method: "GET",
                path: "/cases/{docket}/ai",
                desc: "Run a case-aware AI inference with full docket context pre-loaded.",
                resp: "{response, tokens_used, model}",
              },
              {
                method: "POST",
                path: "/cases/{docket}/ai",
                desc: "POST with {prompt, system} to run a custom AI query with full case context. Used by extensions to add AI features.",
                resp: "{response, tokens_used}",
              },
              {
                method: "GET",
                path: "/marketplace/extensions",
                desc: "List all public marketplace extensions available to install.",
                resp: "{extensions:[{id, name, author, icon, description, install_count, rating}]}",
              },
              {
                method: "POST",
                path: "/extensions",
                desc: "Register a new extension. Body: {name, label, icon, visibility, entry_url}.",
                resp: "{id, api_key, install_url}",
              },
            ].map(({ method, path, desc, resp }, i) => (
              <div
                key={i}
                style={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: method === "GET" ? "#10b981" : "#f59e0b",
                      background: method === "GET" ? "#10b98118" : "#f59e0b18",
                      padding: "2px 8px",
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    {method}
                  </span>
                  <code
                    style={{
                      fontSize: 9,
                      fontFamily: "monospace",
                      color: "#7dd3fc",
                    }}
                  >
                    {path}
                  </code>
                </div>
                <div style={{ fontSize: 9, color: "#64748b", marginBottom: 6 }}>
                  {desc}
                </div>
                <div
                  style={{
                    fontSize: 8,
                    fontFamily: "monospace",
                    color: "#475569",
                    background: "#020817",
                    padding: "6px 10px",
                    borderRadius: 4,
                  }}
                >
                  → {resp}
                </div>
              </div>
            ))}
          </div>
          <div id="doc-extensions" style={{ marginBottom: 48 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 6,
                borderBottom: "1px solid #1e293b",
                paddingBottom: 10,
              }}
            >
              Example Extensions
            </h2>
            <p
              style={{
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.7,
                marginBottom: 16,
              }}
            >
              Copy any of these starter templates to build your own extension.
            </p>
            {[
              {
                icon: "📊",
                name: "Damages Calculator",
                desc: "Reads case evidence and theories, prompts user for assumptions, and outputs a structured damages model with NPV calculations.",
                code: `export default function DamagesCalc({ caseId, theme }) {
  const [result, setResult] = React.useState(null);
  
  const calculate = async () => {
    const res = await fetch(\`/api/v1/cases/\${caseId}/ai\`, {
      method: "POST",
      headers: { Authorization: \`Bearer \${CB_API_KEY}\` },
      body: JSON.stringify({
        prompt: "Based on the case evidence and theories, \
                 estimate economic damages with a range."
      })
    });
    const data = await res.json();
    setResult(data.response);
  };

  return (
    <div style={{ padding: 16 }}>
      <button onClick={calculate}>Calculate Damages</button>
      {result && <pre>{result}</pre>}
    </div>
  );
}`,
              },
              {
                icon: "🔔",
                name: "Filing Alert",
                desc: "Polls PACER every 15 minutes and sends a Slack or email notification when a new filing matches a keyword filter.",
                code: `// casebinder.webhook.js
export async function onDocketEntry(entry, caseData) {
  const keywords = ["summary judgment", "motion to remand",
                    "expert disclosure"];
  
  const matched = keywords.filter(k =>
    entry.title.toLowerCase().includes(k)
  );
  
  if (matched.length > 0) {
    await fetch(process.env.SLACK_WEBHOOK, {
      method: "POST",
      body: JSON.stringify({
        text: \`🔔 New filing in \${caseData.case_name}:
               \${entry.title} (Dkt.\${entry.ecf_number})\`
      })
    });
  }
}`,
              },
            ].map(({ icon, name, desc, code }, i) => (
              <div
                key={i}
                style={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 10,
                  padding: "16px 18px",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontSize: 18 }}>{icon}</span>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#e2e8f0",
                      }}
                    >
                      {name}
                    </div>
                    <div style={{ fontSize: 8, color: "#475569" }}>{desc}</div>
                  </div>
                </div>
                <div
                  style={{
                    background: "#020817",
                    border: "1px solid #1e293b",
                    borderRadius: 6,
                    padding: "10px 14px",
                    fontFamily: "monospace",
                    fontSize: 8,
                    color: "#7dd3fc",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {code}
                </div>
              </div>
            ))}
          </div>
          <div id="doc-publishing" style={{ marginBottom: 48 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 6,
                borderBottom: "1px solid #1e293b",
                paddingBottom: 10,
              }}
            >
              Publishing to the Marketplace
            </h2>
            <p
              style={{
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              Once your extension is working privately, publish it so any
              CaseBinder user can install it in one click.
            </p>
            {[
              {
                n: "1",
                title: "Set visibility to public",
                desc: "In casebinder.config.json, set visibility to 'public'. Private extensions are only visible to your firm account.",
              },
              {
                n: "2",
                title: "Add a manifest",
                desc: "Include a clear name, description (max 160 chars), icon emoji, category tag, and screenshots. Good manifests get 3x more installs.",
              },
              {
                n: "3",
                title: "Deploy and submit",
                desc: "Run cb publish --api-key cb_live_xxx. Your extension enters a 24-hour review. CaseBinder reviews for security, performance, and legal accuracy.",
              },
              {
                n: "4",
                title: "Monetize (optional)",
                desc: "Set a monthly price per seat. CaseBinder takes 20% and handles billing. Most paid extensions charge $25-$150/attorney/month.",
              },
              {
                n: "5",
                title: "Analytics",
                desc: "The developer dashboard shows installs, active users, error rates, and revenue. Access at developer.casebinder.ai.",
              },
            ].map(({ n, title, desc }, i) => (
              <div
                key={i}
                style={{ display: "flex", gap: 14, marginBottom: 14 }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#a78bfa18",
                    border: "1px solid #a78bfa33",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#a78bfa",
                    flexShrink: 0,
                  }}
                >
                  {n}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#e2e8f0",
                      marginBottom: 2,
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{ fontSize: 9, color: "#64748b", lineHeight: 1.5 }}
                  >
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              background: "linear-gradient(135deg,#0f172a,#1e1035)",
              borderRadius: 12,
              border: "1px solid #a78bfa33",
              padding: "24px 28px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 14, marginBottom: 6 }}>🛠️</div>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Need help?
            </h3>
            <p style={{ fontSize: 10, color: "#64748b", marginBottom: 16 }}>
              Join our developer Slack, browse the GitHub examples repo, or
              email the API team directly.
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <a
                href="mailto:api@casebinder.ai"
                style={{
                  padding: "8px 18px",
                  fontSize: 10,
                  fontWeight: 600,
                  background: "#a78bfa",
                  color: "#fff",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                ✉️ api@casebinder.ai
              </a>
              <a
                href="mailto:api@casebinder.ai?subject=Developer Slack Invite"
                style={{
                  padding: "8px 18px",
                  fontSize: 10,
                  color: "#a78bfa",
                  background: "transparent",
                  border: "1px solid #a78bfa44",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                💬 Join Developer Slack
              </a>
              <a
                href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
                target="_blank"
                style={{
                  padding: "8px 18px",
                  fontSize: 10,
                  color: "#94a3b8",
                  background: "transparent",
                  border: "1px solid #1e293b",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                📄 Patent Filing
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  if (page === "signin")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>CaseBinder</span>
          </div>
          <button
            onClick={() => setPage("home")}
            style={{
              fontSize: 10,
              color: "#94a3b8",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        </nav>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 440 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#fff",
                  margin: "0 auto 12px",
                }}
              >
                CB
              </div>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>
                Sign in to CaseBinder
              </h1>
              <p style={{ fontSize: 10, color: "#64748b", margin: 0 }}>
                Or access your firm's private portal below
              </p>
            </div>
            <div
              style={{
                background: "#0f172a",
                border: "1px solid #1e293b",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: "#64748b",
                  marginBottom: 10,
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ACCESS YOUR FIRM'S PORTAL
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input
                  value={signInFirm}
                  onChange={(e) => setSignInFirm(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      signInFirm.toLowerCase().includes("orrick")
                    )
                      setPage("orrick");
                  }}
                  placeholder="e.g. orrick"
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    fontSize: 10,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                  }}
                />
                <span
                  style={{
                    fontSize: 10,
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  casebinder.ai
                </span>
                <button
                  onClick={() => {
                    if (signInFirm.toLowerCase().includes("orrick"))
                      setPage("orrick");
                  }}
                  style={{
                    padding: "8px 14px",
                    fontSize: 10,
                    fontWeight: 600,
                    background: "#1e40af",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Go
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  ["orrick", "Orrick"],
                  ["kirkland", "Kirkland & Ellis"],
                  ["lieff", "Lieff Cabraser"],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      setSignInFirm(k);
                      if (k === "orrick") setPage("orrick");
                    }}
                    style={{
                      fontSize: 8,
                      padding: "3px 10px",
                      background: "#1e293b",
                      color: "#94a3b8",
                      border: "1px solid #334155",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                background: "#0f172a",
                border: "1px solid #1e293b",
                borderRadius: 10,
                padding: "20px",
              }}
            >
              <input
                placeholder="Work email"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 10,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 6,
                  color: "#f8fafc",
                  outline: "none",
                  marginBottom: 10,
                  boxSizing: "border-box",
                }}
              />
              <input
                type="password"
                placeholder="Password"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 10,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 6,
                  color: "#f8fafc",
                  outline: "none",
                  marginBottom: 14,
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={() => setPage("home")}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  marginBottom: 10,
                }}
              >
                Sign In
              </button>
              <div
                style={{ textAlign: "center", fontSize: 9, color: "#475569" }}
              >
                No account?{" "}
                <button
                  onClick={() => setPage("signup")}
                  style={{
                    color: "#3b82f6",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 9,
                  }}
                >
                  Sign up free
                </button>
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 8,
                  color: "#475569",
                  marginTop: 14,
                }}
              >
                {[
                  ["Contact", "contact"],
                  ["Privacy", "privacy"],
                  ["Terms", "terms"],
                  ["Support", "support"],
                ].flatMap(([l, p], i) => [
                  i > 0 && <span key={"d" + i}>·</span>,
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      color: "#64748b",
                      textDecoration: "none",
                      margin: "0 6px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 8,
                    }}
                  >
                    {l}
                  </button>,
                ])}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  if (page === "orrick")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#f8f9fa",
          color: "#1a2332",
          minHeight: "100vh",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 32px",
            height: 60,
            boxShadow: "0 1px 4px rgba(0,0,0,.05)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img
              src="data:image/png;base64,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"
              alt="Orrick"
              style={{ height: 34, objectFit: "contain" }}
            />
            <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />
            <span
              style={{
                fontSize: 9,
                color: "#94a3b8",
                fontFamily: "monospace",
                letterSpacing: 0.5,
              }}
            >
              CaseBinder Portal
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: "#64748b" }}>
              orrick.casebinder.ai
            </span>
            <button
              onClick={() => setPage("home")}
              style={{
                padding: "6px 14px",
                fontSize: 10,
                background: "#1a2332",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              ← Home
            </button>
          </div>
        </nav>
        <div
          style={{
            background: "linear-gradient(135deg,#1a2332,#2d4a6e)",
            padding: "28px 32px",
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "#94a3b8",
              marginBottom: 4,
              fontFamily: "monospace",
              letterSpacing: 1,
            }}
          >
            ORRICK · CASEBINDER PORTAL · orrick.casebinder.ai
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#fff",
              margin: "0 0 4px",
            }}
          >
            Active Litigation
          </h1>
          <p style={{ fontSize: 10, color: "#94a3b8", margin: 0 }}>
            Litigation intelligence for Orrick matters - powered by CaseBinder
            AI
          </p>
        </div>
        <div
          style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 60px" }}
        >
          <div
            onClick={() => {
              setCaseId("juul");
              setPage("case");
              setFromOrrick(true);
              setDark(false);
            }}
            style={{
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              padding: "20px 24px",
              marginBottom: 12,
              cursor: "pointer",
              boxShadow: "0 1px 4px rgba(0,0,0,.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontFamily: "monospace",
                      color: "#f59e0b",
                      background: "#f59e0b18",
                      border: "1px solid #f59e0b33",
                      padding: "2px 8px",
                      borderRadius: 10,
                      fontWeight: 600,
                    }}
                  >
                    MDL Active
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      fontFamily: "monospace",
                      color: "#64748b",
                    }}
                  >
                    3:19-md-02913-WHO · N.D. Cal. · Judge Orrick
                  </span>
                </div>
                <h2
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#1a2332",
                    margin: "0 0 4px",
                  }}
                >
                  Seliavski v. JUUL Labs, Inc. et al.
                </h2>
                <p
                  style={{
                    fontSize: 10,
                    color: "#64748b",
                    margin: "0 0 12px",
                    lineHeight: 1.5,
                  }}
                >
                  Orrick counsel represents defendant James Monsees (JUUL
                  Co-Founder). Four Orrick attorneys on the docket. SJ motions
                  pending.
                </p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    "SJ Ruling Pending",
                    "Remand Anticipated Post-SJ",
                    "Meta Production Due May 11, 2026",
                    "Common Benefit Fee Motion Late 2026",
                  ].map((t, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 7,
                        color: "#3b82f6",
                        background: "#3b82f618",
                        padding: "2px 8px",
                        borderRadius: 6,
                        fontFamily: "monospace",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div
                style={{ textAlign: "right", flexShrink: 0, marginLeft: 20 }}
              >
                <div
                  style={{ fontSize: 26, fontWeight: 700, color: "#1e40af" }}
                >
                  4
                </div>
                <div style={{ fontSize: 8, color: "#64748b" }}>
                  Orrick attorneys
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: "#10b981",
                    marginTop: 8,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    justifyContent: "flex-end",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#10b981",
                      display: "inline-block",
                    }}
                  />
                  Active
                </div>
              </div>
            </div>
            <div
              style={{
                borderTop: "1px solid #f1f5f9",
                marginTop: 14,
                paddingTop: 12,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              {[
                [
                  "James Neil Kramer",
                  "Lead Counsel",
                  "jkramer@orrick.com",
                  "415-773-5700",
                ],
                ["Kevin Askew", "Counsel", "kaskew@orrick.com", "213-629-2020"],
                [
                  "Lauren Seaton",
                  "Counsel",
                  "lseaton@orrick.com",
                  "949-567-6700",
                ],
                ["Sunny Hwang", "Counsel", "shwang@orrick.com", "213-629-2020"],
              ].map(([name, role, email, phone], i) => (
                <div key={i} style={{ minWidth: 160 }}>
                  <div
                    style={{ fontSize: 9, fontWeight: 600, color: "#1a2332" }}
                  >
                    {name}
                  </div>
                  <div style={{ fontSize: 8, color: "#94a3b8" }}>{role}</div>
                  <a
                    href={"mailto:" + email}
                    style={{
                      fontSize: 8,
                      color: "#3b82f6",
                      textDecoration: "none",
                      display: "block",
                    }}
                  >
                    {email}
                  </a>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <span style={{ fontSize: 9, color: "#3b82f6", fontWeight: 600 }}>
                Open Case Binder →
              </span>
            </div>
          </div>
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 10,
              border: "1px dashed #e2e8f0",
              padding: "16px 24px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>
              Additional Orrick matters will appear here as they are onboarded
              to CaseBinder
            </div>
            <button
              style={{
                fontSize: 9,
                color: "#1e40af",
                background: "transparent",
                border: "1px solid #1e40af44",
                borderRadius: 4,
                padding: "5px 14px",
                cursor: "pointer",
              }}
            >
              Request Matter Onboarding
            </button>
          </div>
          <div
            style={{
              textAlign: "center",
              marginTop: 24,
              fontSize: 8,
              color: "#cbd5e1",
            }}
          >
            Powered by CaseBinder AI ·{" "}
            <a
              href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
              target="_blank"
              style={{ color: "#94a3b8" }}
            >
              U.S. Patent Pending
            </a>{" "}
            · White-labeled for Orrick, Herrington & Sutcliffe LLP
          </div>
        </div>
      </div>
    );
  if (page === "signup")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>CaseBinder</span>
          </div>
          <button
            onClick={() => setPage("signin")}
            style={{
              fontSize: 10,
              color: "#94a3b8",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            Have an account? Sign in
          </button>
        </nav>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>
                Start your free trial
              </h1>
              <p style={{ fontSize: 10, color: "#64748b" }}>
                30-day full access · No credit card required
              </p>
            </div>
            <div
              style={{
                background: "#0f172a",
                border: "1px solid #1e293b",
                borderRadius: 10,
                padding: "22px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <input
                  placeholder="First name"
                  style={{
                    padding: "10px 12px",
                    fontSize: 10,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <input
                  placeholder="Last name"
                  style={{
                    padding: "10px 12px",
                    fontSize: 10,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <input
                placeholder="Work email"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 10,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 6,
                  color: "#f8fafc",
                  outline: "none",
                  marginBottom: 10,
                  boxSizing: "border-box",
                }}
              />
              <input
                placeholder="Law firm"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 10,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 6,
                  color: "#f8fafc",
                  outline: "none",
                  marginBottom: 10,
                  boxSizing: "border-box",
                }}
              />
              <input
                type="password"
                placeholder="Create password"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 10,
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 6,
                  color: "#f8fafc",
                  outline: "none",
                  marginBottom: 14,
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={() => setPage("home")}
                style={{
                  width: "100%",
                  padding: "11px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  marginBottom: 10,
                }}
              >
                Create Account
              </button>
              <div
                style={{ fontSize: 8, color: "#475569", textAlign: "center" }}
              >
                Enterprise? Contact{" "}
                <span style={{ color: "#3b82f6" }}>sales@casebinder.ai</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  if (page === "benefits")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#f8fafc" }}>
              CaseBinder
            </span>
            <span
              style={{
                fontSize: 7,
                fontFamily: "monospace",
                color: "#475569",
                background: "#1e293b",
                padding: "1px 6px",
                borderRadius: 3,
              }}
            >
              BETA
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={() => setPage("home")}
              style={{
                fontSize: 10,
                color: "#94a3b8",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
            <button
              onClick={() => setPage("devdocs")}
              style={{
                fontSize: 10,
                color: "#a78bfa",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              Developer Docs
            </button>
            <button
              onClick={() => setPage("home")}
              style={{
                padding: "6px 16px",
                fontSize: 10,
                fontWeight: 600,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Get Access
            </button>
          </div>
        </nav>
        <div
          style={{
            maxWidth: 1000,
            margin: "0 auto",
            padding: "60px 24px 80px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div
              style={{
                fontSize: 10,
                fontFamily: "monospace",
                color: "#10b981",
                fontWeight: 600,
                marginBottom: 12,
                letterSpacing: 2,
              }}
            >
              ENTERPRISE LITIGATION INTELLIGENCE
            </div>
            <h1
              style={{
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1.2,
                margin: "0 0 16px",
                backgroundImage: "linear-gradient(135deg,#3b82f6,#10b981)", backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent",
              }}
            >
              The Operating System
              <br />
              for Complex Litigation
            </h1>
            <p
              style={{
                fontSize: 13,
                color: "#94a3b8",
                maxWidth: 600,
                margin: "0 auto 24px",
                lineHeight: 1.7,
              }}
            >
              CaseBinder automatically pulls your live docket, builds the case
              strategy, and keeps every attorney on the same page - from first
              filing to verdict.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setPage("home")}
                style={{
                  padding: "10px 24px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Request Demo
              </button>
              <a
                href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
                target="_blank"
                style={{
                  padding: "10px 24px",
                  fontSize: 11,
                  color: "#3b82f6",
                  background: "transparent",
                  border: "1px solid #3b82f644",
                  borderRadius: 8,
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                📄 Patent Pending
              </a>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 16,
              marginBottom: 50,
            }}
          >
            {[
              [
                "🔄",
                "Live Docket Sync",
                "Automatically syncs with PACER and all 50 state dockets in real time. The moment a filing lands, it appears in your case binder - indexed, categorized, and linked to the relevant case strategy.",
              ],
              [
                "🧠",
                "Case Intelligence Engine",
                "AI reads every filing, deposition, exhibit, and order and builds a living analysis of your case. Theories, contradictions, evidence gaps, and settlement signals update automatically as the case evolves.",
              ],
              [
                "🛒",
                "Developer Marketplace",
                "Attorneys and developers can build custom extensions on top of CaseBinder via our open API, publish them to the marketplace, and install them in one click - turning CaseBinder into a platform, not just a product.",
              ],
            ].map(([icon, title, desc], i) => (
              <div
                key={i}
                style={{
                  background: "#0f172a",
                  borderRadius: 10,
                  border: "1px solid #1e293b",
                  padding: "20px 18px",
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 10 }}>{icon}</div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#f8fafc",
                    marginBottom: 8,
                  }}
                >
                  {title}
                </div>
                <p
                  style={{
                    fontSize: 9,
                    color: "#64748b",
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {desc}
                </p>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 50 }}>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                textAlign: "center",
                marginBottom: 6,
              }}
            >
              Everything in one binder
            </h2>
            <p
              style={{
                fontSize: 10,
                color: "#64748b",
                textAlign: "center",
                marginBottom: 24,
              }}
            >
              20+ specialized views, each powered by case-specific AI
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 10,
              }}
            >
              {[
                [
                  "⚔️",
                  "War Room",
                  "Live trial command - witness queue, exhibit pipeline, jury observations, real-time strategic moves.",
                ],
                [
                  "⚖️",
                  "Jury Intelligence",
                  "Voir dire bank, archetype mapping, juror grid, theme calibration, closing argument testing.",
                ],
                [
                  "🎓",
                  "Expert Battlefield",
                  "Plaintiff vs defendant expert matrix with Daubert status, vulnerability scores, head-to-head AI.",
                ],
                [
                  "🌡️",
                  "Strategic Map",
                  "Heat map (4 layers - evidence, vulnerability, witness coverage, timeline) + gap analysis (4 types).",
                ],
                [
                  "💰",
                  "Damages Model",
                  "Track-by-track damages with sliders, scenarios, AI-suggested inflection points.",
                ],
                [
                  "🤝",
                  "Settlement Intel",
                  "BATNA calculator, comparable verdicts, demand/offer ledger, two-front war analysis.",
                ],
                [
                  "🎯",
                  "Impeachment Bank",
                  "Witness-specific impeachment sequences from depositions, filings, and prior testimony.",
                ],
                [
                  "🔍",
                  "Contradiction Engine",
                  "Real-time inconsistency flagging across depositions, briefs, errata, and cross-case filings.",
                ],
                [
                  "⚖️",
                  "Appellate Risk Register",
                  "Every adverse ruling tracked with standard of review, preservation status, and reversal likelihood. Live preservation alerts during trial.",
                ],
                [
                  "💼",
                  "Client Communication Hub",
                  "AI-generated status reports, budget vs reality, decision log - risk management + institutional memory.",
                ],
                [
                  "📋",
                  "Depositions",
                  "All depositions with party tags, status, key admissions, impeachment-grade flags.",
                ],
                [
                  "📰",
                  "Live Docket Sync",
                  "PACER + 50 state courts in real time. Every filing indexed, AI-analyzed, linked to strategy.",
                ],
              ].map(([icon, title, desc], i) => (
                <div
                  key={i}
                  style={{
                    background: "#0f172a",
                    borderRadius: 8,
                    border: "1px solid #1e293b",
                    padding: "14px 12px",
                  }}
                >
                  <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: "#e2e8f0",
                      marginBottom: 4,
                    }}
                  >
                    {title}
                  </div>
                  <p
                    style={{
                      fontSize: 8,
                      color: "#475569",
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              background: "linear-gradient(135deg,#0f172a,#1e1035)",
              borderRadius: 14,
              border: "1px solid #a78bfa33",
              padding: "32px 32px",
              marginBottom: 50,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 32,
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 9,
                    fontFamily: "monospace",
                    color: "#a78bfa",
                    fontWeight: 600,
                    marginBottom: 8,
                    letterSpacing: 1,
                  }}
                >
                  DEVELOPER MARKETPLACE
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#f8fafc",
                    margin: "0 0 12px",
                    lineHeight: 1.3,
                  }}
                >
                  Build extensions.
                  <br />
                  Publish to the marketplace.
                  <br />
                  Or keep them private.
                </h2>
                <p
                  style={{
                    fontSize: 10,
                    color: "#64748b",
                    lineHeight: 1.7,
                    marginBottom: 16,
                  }}
                >
                  CaseBinder exposes a full API so developers - inside or
                  outside your firm - can create custom views, integrations, and
                  AI-powered features that snap into the left nav of any case.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    "REST + GraphQL API",
                    "Webhooks",
                    "Custom left-nav views",
                    "PACER data access",
                    "Event streaming",
                    "Private or public publish",
                  ].map((f, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 8,
                        color: "#a78bfa",
                        background: "#a78bfa18",
                        border: "1px solid #a78bfa33",
                        padding: "3px 8px",
                        borderRadius: 10,
                        fontFamily: "monospace",
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  [
                    "🤝",
                    "Settlement Calculator",
                    "Predicts settlement ranges based on similar MDL outcomes. By Lex Machina.",
                    "Installed",
                  ],
                  [
                    "📊",
                    "Damages Modeler",
                    "Builds economic damages models from case facts. By Cornerstone Research.",
                    "4.9 ⭐",
                  ],
                  [
                    "📡",
                    "Expert Witness Finder",
                    "Searches expert databases and flags conflicts. By Bloomberg Law.",
                    "4.7 ⭐",
                  ],
                  [
                    "🌐",
                    "State Court Sync",
                    "Extends PACER sync to all 50 state dockets. By CourtLink.",
                    "4.8 ⭐",
                  ],
                  [
                    "🔐",
                    "Conflict Checker",
                    "Checks new matters against firm client database. By Thomson Reuters.",
                    "4.6 ⭐",
                  ],
                ].map(([icon, name, desc, badge], i) => (
                  <div
                    key={i}
                    style={{
                      background: "#1e293b",
                      borderRadius: 8,
                      border: "1px solid #334155",
                      padding: "10px 12px",
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{icon}</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: "#e2e8f0",
                        }}
                      >
                        {name}
                      </div>
                      <div style={{ fontSize: 7, color: "#475569" }}>
                        {desc}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 7,
                        color: badge === "Installed" ? "#10b981" : "#94a3b8",
                        background:
                          badge === "Installed" ? "#10b98118" : "transparent",
                        padding: "2px 6px",
                        borderRadius: 10,
                        fontFamily: "monospace",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {badge}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              background: "#0f172a",
              borderRadius: 14,
              border: "1px solid #1e293b",
              padding: "28px 32px",
              marginBottom: 50,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 32,
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 9,
                    fontFamily: "monospace",
                    color: "#10b981",
                    fontWeight: 600,
                    marginBottom: 8,
                    letterSpacing: 1,
                  }}
                >
                  LIVE DOCKET SYNC
                </div>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#f8fafc",
                    margin: "0 0 12px",
                  }}
                >
                  PACER + all 50 state courts.
                  <br />
                  One binder. Real time.
                </h2>
                <p style={{ fontSize: 10, color: "#64748b", lineHeight: 1.7 }}>
                  CaseBinder connects directly to PACER and state court filing
                  systems. Every new order, motion, and stipulation is
                  automatically pulled, indexed, and surfaced in the relevant
                  tab - with AI-generated analysis attached. Your case binder is
                  always current, even when you are not.
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {[
                  [
                    "⚡",
                    "Instant alerts",
                    "New filings appear within minutes of docketing",
                  ],
                  [
                    "🔎",
                    "Auto-indexed",
                    "Filings tagged by type, party, and case relevance",
                  ],
                  [
                    "🗂️",
                    "ECF linked",
                    "Every exhibit and docket entry one click from the binder",
                  ],
                  [
                    "🏛️",
                    "All courts",
                    "Federal + state, trial and appellate, MDL and standard",
                  ],
                ].map(([icon, title, desc], i) => (
                  <div
                    key={i}
                    style={{
                      background: "#1e293b",
                      borderRadius: 8,
                      padding: "12px 10px",
                      border: "1px solid #334155",
                    }}
                  >
                    <div style={{ fontSize: 14, marginBottom: 4 }}>{icon}</div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        color: "#e2e8f0",
                        marginBottom: 2,
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{ fontSize: 8, color: "#475569", lineHeight: 1.4 }}
                    >
                      {desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              background: "#0f172a",
              borderRadius: 14,
              border: "1px solid #1e293b",
              padding: "28px 32px",
              marginBottom: 40,
            }}
          >
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#f8fafc",
                marginBottom: 6,
                textAlign: "center",
              }}
            >
              CaseBinder vs. Harvey AI vs. Legora
            </h2>
            <p
              style={{
                fontSize: 9,
                color: "#64748b",
                textAlign: "center",
                marginBottom: 20,
              }}
            >
              Why litigation partners choose CaseBinder
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
                gap: 0,
              }}
            >
              <div
                style={{
                  fontSize: 8,
                  fontWeight: 600,
                  color: "#475569",
                  padding: "6px 0",
                  borderBottom: "1px solid #1e293b",
                }}
              ></div>
              <div
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  color: "#3b82f6",
                  padding: "6px 8px",
                  borderBottom: "1px solid #1e293b",
                  textAlign: "center",
                  background: "#3b82f60a",
                  borderRadius: "4px 4px 0 0",
                }}
              >
                CaseBinder
              </div>
              <div
                style={{
                  fontSize: 8,
                  color: "#475569",
                  padding: "6px 8px",
                  borderBottom: "1px solid #1e293b",
                  textAlign: "center",
                }}
              >
                Harvey AI
              </div>
              <div
                style={{
                  fontSize: 8,
                  color: "#475569",
                  padding: "6px 8px",
                  borderBottom: "1px solid #1e293b",
                  textAlign: "center",
                }}
              >
                Legora
              </div>
              {[
                [
                  "Use case",
                  "Matter command center",
                  "General legal drafting",
                  "Legal research",
                ],
                [
                  "Pricing model",
                  "Per matter or seat",
                  "Per seat only",
                  "Per seat only",
                ],
                [
                  "Case-specific AI context",
                  "Deep - docket, evidence, witnesses",
                  "None",
                  "Limited",
                ],
                [
                  "Full PACER + state docket sync",
                  "Yes - real-time, all 50 states",
                  "No",
                  "No",
                ],
                [
                  "Litigation strategy AI",
                  "Playbooks + procedural traps",
                  "Generic",
                  "Generic",
                ],
                [
                  "Hearing prep pack",
                  "Docket bundle + argument outline + rebuttals",
                  "No",
                  "No",
                ],
                [
                  "Live AI deposition intel",
                  "Flags witness inconsistencies vs. case record",
                  "No",
                  "No",
                ],
                ["Developer marketplace", "Open API + extensions", "No", "No"],
                [
                  "Value prop",
                  "Case command center per matter",
                  "Faster drafting",
                  "Research speed",
                ],
                [
                  "Target buyer",
                  "Litigation partner",
                  "Firm IT / COO",
                  "Associates",
                ],
              ].map(([l, cb, h, lg], i) => [
                <div
                  key={l + "l"}
                  style={{
                    fontSize: 8,
                    color: "#94a3b8",
                    padding: "7px 0",
                    borderBottom: "1px solid #1e293b0a",
                  }}
                >
                  {l}
                </div>,
                <div
                  key={l + "cb"}
                  style={{
                    fontSize: 8,
                    color: "#10b981",
                    padding: "7px 8px",
                    borderBottom: "1px solid #1e293b0a",
                    textAlign: "center",
                    background: "#10b98106",
                  }}
                >
                  {cb}
                </div>,
                <div
                  key={l + "h"}
                  style={{
                    fontSize: 8,
                    color: "#475569",
                    padding: "7px 8px",
                    borderBottom: "1px solid #1e293b0a",
                    textAlign: "center",
                  }}
                >
                  {h}
                </div>,
                <div
                  key={l + "lg"}
                  style={{
                    fontSize: 8,
                    color: "#475569",
                    padding: "7px 8px",
                    borderBottom: "1px solid #1e293b0a",
                    textAlign: "center",
                  }}
                >
                  {lg}
                </div>,
              ])}
            </div>
            <div
              style={{
                marginTop: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 12,
                borderTop: "1px solid #1e293b",
              }}
            >
              <div
                style={{
                  fontSize: 7,
                  color: "#475569",
                  fontFamily: "monospace",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ color: "#f59e0b" }}>⚠</span>U.S. Patent Pending
              </div>
              <a
                href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 7,
                  color: "#3b82f6",
                  textDecoration: "none",
                  fontFamily: "monospace",
                  padding: "3px 8px",
                  background: "#3b82f618",
                  border: "1px solid #3b82f633",
                  borderRadius: 4,
                }}
              >
                📄 View Patent Application
              </a>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
              Ready to open the binder?
            </h2>
            <p style={{ fontSize: 10, color: "#64748b", marginBottom: 20 }}>
              Join Am Law 100 firms using CaseBinder on their most complex
              matters.
            </p>
            <button
              onClick={() => setPage("home")}
              style={{
                padding: "12px 32px",
                fontSize: 12,
                fontWeight: 700,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Request Demo
            </button>
          </div>
        </div>
      </div>
    );
  if (
    ["pilot", "demo", "enterprise", "contact", "sales", "support"].includes(
      page,
    )
  )
    return (() => {
      const FORM_CONFIG = {
        pilot: {
          eyebrow: "PILOT REQUEST",
          headline: "Start a CaseBinder pilot",
          sub: "Run CaseBinder on 1–5 active matters for 60 days. We'll set you up with a dedicated success manager and live PACER + state docket sync from day one.",
          cta: "Request Pilot Setup",
          badge: "$10K per matter / month · Up to 5 matters",
          accent: "#10b981",
        },
        demo: {
          eyebrow: "SCHEDULE DEMO",
          headline: "See CaseBinder on a live matter",
          sub: "30-minute walkthrough using one of your active cases. We'll pull your docket from PACER, build the case binder live, and show you the AI deposition intel and hearing prep pack in action.",
          cta: "Schedule My Demo",
          badge: "Practice Group · $3,500 per attorney / mo",
          accent: "#3b82f6",
        },
        enterprise: {
          eyebrow: "FIRM-WIDE DEPLOYMENT",
          headline: "Deploy CaseBinder firm-wide",
          sub: "Custom deployment for Am Law 100 and Vault 100 firms. SOC 2, dedicated infrastructure, white-label branding, custom AI fine-tuning on your firm's matter data, and executive-level analytics.",
          cta: "Contact Enterprise Team",
          badge: "Firm-wide · Custom · multi-year deal",
          accent: "#a78bfa",
        },
        contact: {
          eyebrow: "GENERAL INQUIRY",
          headline: "Get in touch with CaseBinder",
          sub: "Press, partnerships, integrations, or anything else that doesn't fit a specific category. We respond within one business day.",
          cta: "Send Message",
          badge: null,
          accent: "#10b981",
        },
        sales: {
          eyebrow: "SALES",
          headline: "Talk to our sales team",
          sub: "Pricing questions, custom deployments, procurement, RFP responses - talk to someone who understands litigation practice.",
          cta: "Contact Sales",
          badge: null,
          accent: "#3b82f6",
        },
        support: {
          eyebrow: "TECH SUPPORT",
          headline: "CaseBinder support",
          sub: "Issues with PACER sync, case binders, integrations, billing, or anything not working as expected. Production support 24/7 for Practice Group and Firm-Wide tiers.",
          cta: "Open Support Ticket",
          badge: null,
          accent: "#f59e0b",
        },
      };
      const cfg = FORM_CONFIG[page];
      const submit = () => {
        toast(
          cfg.cta + " submitted - we'll respond within one business day",
          "success",
        );
        setTimeout(() => setPage("home"), 1500);
      };
      return (
        <div
          style={{
            fontFamily: "system-ui,sans-serif",
            background: "#0a0f1e",
            color: "#f8fafc",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <link
            href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
            rel="stylesheet"
          />
          <nav
            style={{
              background: "#0f172a",
              borderBottom: "1px solid #1e293b",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 24px",
              height: 52,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
              onClick={() => setPage("home")}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                CB
              </div>
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                CaseBinder<span style={{ color: "#10b981" }}>.ai</span>
              </span>
            </div>
            <button
              onClick={() => setPage("home")}
              style={{
                fontSize: 10,
                color: "#94a3b8",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              ← Home
            </button>
          </nav>
          <div style={{ flex: 1, padding: "40px 24px" }}>
            <div style={{ maxWidth: 560, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontFamily: "monospace",
                    color: cfg.accent,
                    fontWeight: 700,
                    letterSpacing: 2,
                    marginBottom: 10,
                  }}
                >
                  {cfg.eyebrow}
                </div>
                <h1
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    margin: "0 0 8px",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {cfg.headline}
                </h1>
                <p
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    lineHeight: 1.6,
                    maxWidth: 480,
                    margin: "0 auto",
                  }}
                >
                  {cfg.sub}
                </p>
                {cfg.badge && (
                  <div
                    style={{
                      display: "inline-block",
                      marginTop: 14,
                      padding: "4px 12px",
                      fontSize: 9,
                      background: cfg.accent + "18",
                      color: cfg.accent,
                      border: `1px solid ${cfg.accent}55`,
                      borderRadius: 14,
                      fontFamily: "monospace",
                      fontWeight: 600,
                    }}
                  >
                    {cfg.badge}
                  </div>
                )}
              </div>
              <div
                style={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 12,
                  padding: "24px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <input
                    placeholder="First name *"
                    style={{
                      padding: "10px 12px",
                      fontSize: 11,
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 6,
                      color: "#f8fafc",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <input
                    placeholder="Last name *"
                    style={{
                      padding: "10px 12px",
                      fontSize: 11,
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 6,
                      color: "#f8fafc",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <input
                  placeholder="Work email *"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 11,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                    marginBottom: 10,
                    boxSizing: "border-box",
                  }}
                />
                <input
                  placeholder="Law firm *"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 11,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                    marginBottom: 10,
                    boxSizing: "border-box",
                  }}
                />
                {(page === "pilot" ||
                  page === "demo" ||
                  page === "enterprise") && (
                  <input
                    placeholder="# of attorneys / matters"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: 11,
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 6,
                      color: "#f8fafc",
                      outline: "none",
                      marginBottom: 10,
                      boxSizing: "border-box",
                    }}
                  />
                )}
                {page === "support" && (
                  <select
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: 11,
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 6,
                      color: "#f8fafc",
                      outline: "none",
                      marginBottom: 10,
                      boxSizing: "border-box",
                    }}
                  >
                    <option>Issue category</option>
                    <option>PACER sync not pulling new filings</option>
                    <option>AI deposition intel error</option>
                    <option>Hearing prep pack export</option>
                    <option>SSO / DMS integration</option>
                    <option>Billing or account question</option>
                    <option>Other</option>
                  </select>
                )}
                <textarea
                  rows={5}
                  placeholder={
                    page === "support"
                      ? "Describe the issue (include matter number if applicable)"
                      : page === "contact"
                        ? "Tell us what you're working on"
                        : "Anything else we should know?"
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 11,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    color: "#f8fafc",
                    outline: "none",
                    marginBottom: 14,
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
                <button
                  onClick={submit}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: 12,
                    fontWeight: 700,
                    background: `linear-gradient(135deg,${cfg.accent},#10b981)`,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {cfg.cta}
                </button>
                <div
                  style={{
                    fontSize: 8,
                    color: "#475569",
                    textAlign: "center",
                    marginTop: 10,
                  }}
                >
                  By submitting you agree to our{" "}
                  <button
                    onClick={() => setPage("terms")}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#3b82f6",
                      cursor: "pointer",
                      fontSize: 8,
                      padding: 0,
                    }}
                  >
                    Terms
                  </button>{" "}
                  and{" "}
                  <button
                    onClick={() => setPage("privacy")}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#3b82f6",
                      cursor: "pointer",
                      fontSize: 8,
                      padding: 0,
                    }}
                  >
                    Privacy Policy
                  </button>
                  .
                </div>
              </div>
              <div
                style={{
                  background: "#0f172a",
                  borderRadius: 8,
                  border: "1px dashed #1e293b",
                  padding: "16px 18px",
                  marginTop: 14,
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 18 }}>📞</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#e2e8f0",
                      marginBottom: 2,
                    }}
                  >
                    Need to talk now?
                  </div>
                  <div style={{ fontSize: 9, color: "#64748b" }}>
                    Sand Hill Road, Menlo Park · Pacific Time business hours
                  </div>
                </div>
                <a
                  href="mailto:hello@casebinder.ai"
                  style={{
                    fontSize: 10,
                    color: "#3b82f6",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  hello@casebinder.ai →
                </a>
              </div>
            </div>
          </div>
          <div
            style={{
              borderTop: "1px solid #1e293b",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              fontSize: 9,
              color: "#475569",
            }}
          >
            <div>
              <strong style={{ color: "#94a3b8" }}>CaseBinder.ai</strong> - A
              product of{" "}
              <strong style={{ color: "#94a3b8" }}>
                PatentVC | Trademarkia
              </strong>{" "}
              · 3000 Sand Hill Blvd 3-245, Menlo Park, CA 94025
            </div>
            <div style={{ display: "flex", gap: 14 }}>
              {[
                ["Contact", "contact"],
                ["Sales", "sales"],
                ["Support", "support"],
                ["Privacy", "privacy"],
                ["Terms", "terms"],
              ].map(([l, p]) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#64748b",
                    cursor: "pointer",
                    fontSize: 9,
                    padding: 0,
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    })();
  if (page === "privacy")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              CaseBinder<span style={{ color: "#10b981" }}>.ai</span>
            </span>
          </div>
          <button
            onClick={() => setPage("home")}
            style={{
              fontSize: 10,
              color: "#94a3b8",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            ← Home
          </button>
        </nav>
        <div
          style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px 60px" }}
        >
          <div
            style={{
              fontSize: 9,
              fontFamily: "monospace",
              color: "#10b981",
              fontWeight: 700,
              letterSpacing: 2,
              marginBottom: 10,
            }}
          >
            LEGAL · PRIVACY POLICY
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 6px" }}>
            Privacy Policy
          </h1>
          <p
            style={{
              fontSize: 10,
              color: "#64748b",
              marginBottom: 24,
              fontFamily: "monospace",
            }}
          >
            Last updated: May 5, 2026 · Effective: May 5, 2026
          </p>
          {[
            [
              "1. Who we are",
              'CaseBinder.ai is a product of PatentVC | Trademarkia ("PatentVC"), a Delaware corporation with offices at 3000 Sand Hill Blvd 3-245, Menlo Park, CA 94025. This Privacy Policy describes how we collect, use, and protect information when you use CaseBinder.',
            ],
            [
              "2. Information we collect",
              "We collect: (a) account information (name, email, firm) when you sign up; (b) case data (filings, dockets, depositions, exhibits, theories, witness lists, client notes) that you upload or that we pull from PACER and state court systems on your behalf; (c) usage data (logins, feature use, AI prompts, error logs) collected automatically; (d) billing information processed by our payment provider.",
            ],
            [
              "3. How we use information",
              "We use case data exclusively to provide the CaseBinder service to you and your firm - building case binders, generating AI analysis, surfacing contradictions, drafting documents. We do not use your case data to train shared AI models. We do not sell case data. We do not share case data with other firms.",
            ],
            [
              "4. AI processing and confidentiality",
              "CaseBinder processes case content through large language models to generate the case-specific intelligence in each tab. AI inference is performed in our SOC 2 Type II compliant infrastructure. AI prompts and outputs are logged for the requesting firm only and are isolated from other tenants. Firm-specific AI fine-tuning (Firm-Wide tier) is performed in dedicated tenant environments.",
            ],
            [
              "5. PACER and state docket data",
              "CaseBinder pulls public docket data from PACER and state court electronic filing systems on your behalf using your firm's PACER credentials or our master account where permitted. PACER usage charges are passed through at cost. State docket data is sourced from court systems and authorized aggregators.",
            ],
            [
              "6. Data retention",
              "Active matter data is retained for the duration of your subscription plus 90 days. Closed matters can be archived or exported in standard formats (JSON, ZIP, PDF). Backups are retained for 30 days. You can request immediate deletion of any matter at any time.",
            ],
            [
              "7. Your rights",
              "You can access, export, correct, or delete your data at any time through Settings or by contacting us. California residents have additional rights under CCPA; EU residents under GDPR.",
            ],
            [
              "8. Security",
              "We use industry-standard encryption in transit and at rest. Access to production systems is limited to authorized engineers with audit logging. We carry cyber liability insurance and conduct annual penetration testing.",
            ],
            [
              "9. Contact",
              "Privacy questions: privacy@casebinder.ai · DPO: dpo@casebinder.ai · Security disclosures: security@casebinder.ai",
            ],
          ].map(([t, b], i) => (
            <div key={i} style={{ marginBottom: 22 }}>
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#10b981",
                }}
              >
                {t}
              </h2>
              <p style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.7 }}>
                {b}
              </p>
            </div>
          ))}
        </div>
        <div
          style={{
            borderTop: "1px solid #1e293b",
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            fontSize: 9,
            color: "#475569",
          }}
        >
          <div>
            <strong style={{ color: "#94a3b8" }}>CaseBinder.ai</strong> - A
            product of{" "}
            <strong style={{ color: "#94a3b8" }}>PatentVC | Trademarkia</strong>{" "}
            · 3000 Sand Hill Blvd 3-245, Menlo Park, CA 94025
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {[
              ["Contact", "contact"],
              ["Sales", "sales"],
              ["Support", "support"],
              ["Privacy", "privacy"],
              ["Terms", "terms"],
            ].map(([l, p]) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 9,
                  padding: 0,
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    );

  if (page === "terms")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: "#0a0f1e",
          color: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <nav
          style={{
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => setPage("home")}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              CB
            </div>
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              CaseBinder<span style={{ color: "#10b981" }}>.ai</span>
            </span>
          </div>
          <button
            onClick={() => setPage("home")}
            style={{
              fontSize: 10,
              color: "#94a3b8",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            ← Home
          </button>
        </nav>
        <div
          style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px 60px" }}
        >
          <div
            style={{
              fontSize: 9,
              fontFamily: "monospace",
              color: "#3b82f6",
              fontWeight: 700,
              letterSpacing: 2,
              marginBottom: 10,
            }}
          >
            LEGAL · TERMS OF SERVICE
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 6px" }}>
            Terms of Service
          </h1>
          <p
            style={{
              fontSize: 10,
              color: "#64748b",
              marginBottom: 24,
              fontFamily: "monospace",
            }}
          >
            Last updated: May 5, 2026 · Effective: May 5, 2026
          </p>
          {[
            [
              "1. Agreement",
              'By using CaseBinder.ai ("Service"), you agree to these Terms. CaseBinder is a product of PatentVC | Trademarkia ("Provider"). If you accept on behalf of a firm, you represent that you have authority to bind that firm.',
            ],
            [
              "2. Subscription and pricing",
              "CaseBinder is offered in three tiers: Pilot ($10,000 per active matter per month, up to 5 matters), Practice Group ($3,500 per attorney per month, billed annually), and Firm-Wide (custom volume pricing under master service agreement). Pilot is offered as a 60-day evaluation. Practice Group renews annually. Firm-Wide terms are governed by the negotiated MSA.",
            ],
            [
              "3. Acceptable use",
              "You will use CaseBinder only for legitimate litigation practice. You will not use the Service to generate misleading filings, harass opposing parties, or violate court rules. You will not reverse engineer the Service or attempt to extract our model weights, training data, or proprietary prompts.",
            ],
            [
              "4. Your data ownership",
              "You retain all rights to your case data. You grant Provider a limited license to process your case data solely to provide the Service to you. Upon termination, you may export your data in standard formats. We will delete your data 90 days after termination unless you request earlier deletion.",
            ],
            [
              "5. AI-generated output",
              "CaseBinder generates AI analysis, drafts, and summaries based on your case data. You are responsible for attorney review of any AI output before filing, sending to clients, or relying on for legal advice. AI output is not a substitute for attorney judgment. Provider makes no warranty regarding the accuracy of AI output.",
            ],
            [
              "6. Confidentiality",
              "Provider treats all case data as confidential and subject to attorney-client privilege protections. Provider does not access case data except as necessary to provide the Service or as required by court order with prior notice to you. Provider personnel sign confidentiality agreements and complete annual training.",
            ],
            [
              "7. Service availability",
              "Provider targets 99.9% uptime for Practice Group and Firm-Wide tiers, with credits for unplanned outages exceeding the SLA. Pilot tier is provided on a best-effort basis. PACER and state court source systems may be unavailable independently of CaseBinder.",
            ],
            [
              "8. Termination",
              "You may terminate at any time. Annual subscriptions are non-refundable but you may continue using the Service through the end of the billing period. Provider may terminate for material breach or non-payment with 30 days' notice.",
            ],
            [
              "9. Limitation of liability",
              "Provider's aggregate liability is limited to fees paid in the prior 12 months. Provider is not liable for indirect, consequential, or punitive damages.",
            ],
            [
              "10. Governing law",
              "These Terms are governed by California law. Disputes are resolved by binding arbitration in San Mateo County, California, except either party may seek injunctive relief in court for IP misuse.",
            ],
            [
              "11. Contact",
              "Legal questions: legal@casebinder.ai · Master Service Agreements (Firm-Wide): enterprise@casebinder.ai",
            ],
          ].map(([t, b], i) => (
            <div key={i} style={{ marginBottom: 22 }}>
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#3b82f6",
                }}
              >
                {t}
              </h2>
              <p style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.7 }}>
                {b}
              </p>
            </div>
          ))}
        </div>
        <div
          style={{
            borderTop: "1px solid #1e293b",
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            fontSize: 9,
            color: "#475569",
          }}
        >
          <div>
            <strong style={{ color: "#94a3b8" }}>CaseBinder.ai</strong> - A
            product of{" "}
            <strong style={{ color: "#94a3b8" }}>PatentVC | Trademarkia</strong>{" "}
            · 3000 Sand Hill Blvd 3-245, Menlo Park, CA 94025
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {[
              ["Contact", "contact"],
              ["Sales", "sales"],
              ["Support", "support"],
              ["Privacy", "privacy"],
              ["Terms", "terms"],
            ].map(([l, p]) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 9,
                  padding: 0,
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    );

  if (page === "home")
    return (
      <div
        style={{
          fontFamily: "system-ui,sans-serif",
          background: th.bg,
          color: th.text,
          minHeight: "100vh",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <nav
          style={{
            background: th.nav,
            borderBottom: `1px solid ${th.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 56,
            position: "sticky",
            top: 0,
            zIndex: 50,
            backdropFilter: "blur(8px)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                boxShadow: "0 1px 3px rgba(30,64,175,0.25)",
              }}
            >
              CB
            </div>
            <span
              style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px" }}
            >
              CaseBinder<span style={{ color: "#10b981" }}>.ai</span>
            </span>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              onClick={() => setPage("benefits")}
              style={{
                fontSize: 12,
                color: th.dim,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "7px 12px",
                fontWeight: 500,
              }}
            >
              Product
            </button>
            <button
              onClick={() => setPage("devdocs")}
              style={{
                fontSize: 12,
                color: th.dim,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "7px 12px",
                fontWeight: 500,
              }}
            >
              Developers
            </button>
            <button
              onClick={() => setPage("signin")}
              style={{
                fontSize: 12,
                color: th.dim,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "7px 14px",
                fontWeight: 500,
              }}
            >
              Sign In
            </button>
            <button
              onClick={() => setPage("signup")}
              style={{
                fontSize: 12,
                fontWeight: 600,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "8px 18px",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(30,64,175,0.25)",
              }}
            >
              Sign Up Free
            </button>
          </div>
        </nav>
        <div
          style={{
            maxWidth: 1080,
            margin: "0 auto",
            padding: "64px 32px 96px",
          }}
        >
          {/* HERO */}
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontFamily: "monospace",
                color: "#10b981",
                fontWeight: 600,
                letterSpacing: 1.5,
                marginBottom: 20,
                padding: "5px 14px",
                background: "#10b98112",
                border: "1px solid #10b98133",
                borderRadius: 20,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#10b981",
                  animation: "pulse 2s ease infinite",
                  display: "inline-block",
                }}
              />
              LITIGATION INTELLIGENCE PLATFORM
            </div>
            <h1
              style={{
                fontSize: 46,
                fontWeight: 700,
                lineHeight: 1.05,
                margin: "0 0 20px",
                letterSpacing: "-0.6px",
                backgroundImage: "linear-gradient(135deg,#1e40af,#10b981)", backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent",
              }}
            >
              Litigation Intelligence.
              <br />
              Case Strategy. Automated.
            </h1>
            <p
              style={{
                fontSize: 15,
                color: th.muted,
                maxWidth: 600,
                margin: "0 auto 28px",
                lineHeight: 1.6,
              }}
            >
              Pull any federal or state case. Auto-generate timelines, theories,
              witness analysis, caselaw verification, and AI-drafted filings.
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => {
                  const el = document.getElementById("cb-cases");
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{
                  padding: "12px 22px",
                  fontSize: 13,
                  fontWeight: 600,
                  background: "linear-gradient(135deg,#1e40af,#10b981)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 9,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(30,64,175,0.3)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Open a Live Case <span style={{ fontSize: 14 }}>↓</span>
              </button>
              <button
                onClick={() => setPage("benefits")}
                style={{
                  padding: "12px 22px",
                  fontSize: 13,
                  fontWeight: 500,
                  background: "transparent",
                  color: th.text,
                  border: `1px solid ${th.border}`,
                  borderRadius: 9,
                  cursor: "pointer",
                }}
              >
                See Features →
              </button>
              <button
                onClick={() => setPage("devdocs")}
                style={{
                  padding: "12px 22px",
                  fontSize: 13,
                  fontWeight: 500,
                  background: "transparent",
                  color: "#a78bfa",
                  border: "1px solid transparent",
                  borderRadius: 9,
                  cursor: "pointer",
                }}
              >
                For Developers
              </button>
            </div>
          </div>

          {/* TRUST STRIP */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 28,
              flexWrap: "wrap",
              marginBottom: 64,
              paddingBottom: 32,
              borderBottom: `1px solid ${th.border}`,
            }}
          >
            {[
              ["🏛️", "PACER + 50 state courts"],
              ["⚡", "Real-time docket sync"],
              ["🤖", "AI-powered analysis"],
              ["🔒", "SOC 2 · zero retention"],
              ["📄", "U.S. Patent pending"],
            ].map(([icon, text], i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 11,
                  color: th.muted,
                }}
              >
                <span style={{ fontSize: 14 }}>{icon}</span>
                {text}
              </div>
            ))}
          </div>

          {/* LIVE CASES - PRIMARY ACTION */}
          <div id="cb-cases" style={{ marginBottom: 56, scrollMarginTop: 80 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                marginBottom: 18,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontFamily: "monospace",
                      color: "#10b981",
                      fontWeight: 600,
                      letterSpacing: 1,
                      padding: "2px 8px",
                      background: "#10b98112",
                      border: "1px solid #10b98133",
                      borderRadius: 10,
                    }}
                  >
                    STEP 1
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: th.faint,
                      fontFamily: "monospace",
                    }}
                  >
                    3 active matters · click to open
                  </span>
                </div>
                <h2
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    margin: 0,
                    letterSpacing: "-0.3px",
                  }}
                >
                  Open a Live Case
                </h2>
                <p
                  style={{
                    fontSize: 12,
                    color: th.muted,
                    margin: "4px 0 0",
                    lineHeight: 1.5,
                  }}
                >
                  Each binder is fully populated with real PACER data, AI
                  analysis, and witness intel.
                </p>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))",
                gap: 14,
              }}
            >
              {bm.map((b, i) => {
                const desc =
                  b.id === "naqvi"
                    ? "Criminal · iLearningEngines securities and wire fraud, E.D.N.Y. Second-named defendant; role defense. Dkt.60 travel ruling pending."
                    : b.id === "chid"
                      ? "Criminal · CFCE §225, securities and wire fraud, obstruction. First-named defendant. Status conference 9/14."
                      : b.id === "musk"
                        ? "Charitable trust · Musk donations vs. OpenAI for-profit pivot. Trial in progress."
                        : b.id === "juul"
                          ? "MDL · 4,500+ docket entries. SJ pending. Antitrust subclass certified."
                          : "Patent infringement · §101 challenge to '061. Trial March 2027.";
                const stat =
                  b.id === "naqvi"
                    ? "Dkt.60 pending"
                    : b.id === "chid"
                      ? "SC 9/14"
                      : b.id === "musk"
                        ? "Trial Day 3"
                        : b.id === "juul"
                          ? "SJ pending"
                          : "Claim construction";
                return (
                  <div
                    key={b.num}
                    onClick={() => {
                      setCaseId(b.id || "neuro");
                      setPage("case");
                      setTab("ringbinder");
                      setPanel(null);
                    }}
                    style={{
                      background: th.card,
                      borderRadius: 12,
                      border: `1px solid ${th.border}`,
                      padding: "18px 20px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.borderColor = b.c + "66";
                      e.currentTarget.style.boxShadow = dark
                        ? `0 8px 24px rgba(0,0,0,0.4),0 0 0 1px ${b.c}33`
                        : `0 8px 24px rgba(0,0,0,0.08),0 0 0 1px ${b.c}33`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = th.border;
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: b.c,
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 10,
                        marginTop: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          fontFamily: "monospace",
                          color: b.c,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                        }}
                      >
                        {b.num}
                      </span>
                      <span
                        style={{
                          fontSize: 8,
                          fontFamily: "monospace",
                          color: b.c,
                          background: b.c + "15",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontWeight: 600,
                          letterSpacing: 0.5,
                        }}
                      >
                        {stat.toUpperCase()}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        marginBottom: 5,
                        letterSpacing: "-0.2px",
                      }}
                    >
                      {b.name}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: th.muted,
                        marginBottom: 10,
                        fontFamily: "monospace",
                      }}
                    >
                      {b.court}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: th.muted,
                        lineHeight: 1.55,
                        marginBottom: 14,
                        minHeight: 34,
                      }}
                    >
                      {desc}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 12,
                        borderTop: `1px solid ${th.border}`,
                      }}
                    >
                      <span style={{ fontSize: 10, color: th.faint }}>
                        {b.status}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: b.c,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        Open Binder →
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* COMPACT SEARCH - secondary action */}
          <div
            style={{
              background: th.card,
              borderRadius: 12,
              border: `1px solid ${th.border}`,
              padding: "18px 22px",
              marginBottom: 64,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  background: th.surface,
                  border: `1px solid ${th.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                🔍
              </div>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "-0.1px",
                  }}
                >
                  Pull a different case
                </div>
                <div style={{ fontSize: 10, color: th.muted }}>
                  From PACER or any state court
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                ["pacer", "🏛️ Federal"],
                ["ca", "🐻 CA"],
                ["tx", "⭐ TX"],
                ["ny", "🗽 NY"],
                ["fl", "🌴 FL"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setCt(k)}
                  style={{
                    padding: "6px 11px",
                    fontSize: 11,
                    fontWeight: 500,
                    background: ct === k ? "#1e40af" : th.surface,
                    color: ct === k ? "#fff" : th.dim,
                    border: `1px solid ${ct === k ? "#1e40af" : th.border}`,
                    borderRadius: 7,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flex: 1, minWidth: 240 }}>
              <input
                value={sq}
                onChange={(e) => setSq(e.target.value)}
                placeholder="Case number or party name…"
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  fontSize: 12,
                  background: th.surface,
                  border: `1px solid ${th.border}`,
                  borderRadius: 8,
                  color: th.text,
                  outline: "none",
                }}
              />
              <button
                onClick={() => setPage("case")}
                style={{
                  padding: "10px 18px",
                  fontSize: 12,
                  background: "#1e40af",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Pull Case
              </button>
            </div>
          </div>

          {/* PLATFORM FEATURES */}
          <div style={{ marginBottom: 56 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontFamily: "monospace",
                  color: "#3b82f6",
                  fontWeight: 600,
                  letterSpacing: 1,
                  padding: "2px 8px",
                  background: "#3b82f612",
                  border: "1px solid #3b82f633",
                  borderRadius: 10,
                }}
              >
                STEP 2
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: th.faint,
                  fontFamily: "monospace",
                }}
              >
                16 specialized views per binder
              </span>
            </div>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 700,
                margin: "0 0 4px",
                letterSpacing: "-0.3px",
              }}
            >
              Everything in One Binder
            </h2>
            <p
              style={{
                fontSize: 12,
                color: th.muted,
                margin: "0 0 24px",
                lineHeight: 1.5,
              }}
            >
              Each tab is case-specific AI: docket, witnesses, theories, and
              evidence - always in sync.
            </p>

            {/* CORE PLATFORM */}
            <div style={{ marginBottom: 18 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: th.dim,
                  marginBottom: 10,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  fontFamily: "monospace",
                }}
              >
                Core
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))",
                  gap: 10,
                }}
              >
                {[
                  [
                    "⚔️",
                    "War Room",
                    "Live trial command center with witness queue, exhibit pipeline, real-time jury observations, and strategic moves panel.",
                  ],
                  [
                    "⚖️",
                    "Jury Intelligence",
                    "Voir dire bank, archetype mapping, juror-by-juror reaction grid, theme calibration, closing argument testing.",
                  ],
                  [
                    "🎓",
                    "Expert Battlefield",
                    "Plaintiff vs defendant expert matrix with Daubert status, vulnerability scores, and head-to-head AI assessment.",
                  ],
                  [
                    "🌡️",
                    "Strategic Map",
                    "Heat map across 4 layers - evidence intensity, vulnerability, witness coverage, timeline density - plus 4-type gap analysis.",
                  ],
                  [
                    "⚖️",
                    "Appellate Risk Register",
                    "Every adverse ruling tracked with standard of review, preservation status, and AI reversal likelihood. Live record-preservation alerts at trial.",
                  ],
                  [
                    "💼",
                    "Client Communication Hub",
                    "AI-generated client status reports, budget vs. reality, common benefit fund tracking, decision log - risk management plus institutional memory.",
                  ],
                ].map(([i, t, d]) => (
                  <div
                    key={t}
                    style={{
                      background: th.card,
                      borderRadius: 10,
                      border: `1px solid ${th.border}`,
                      padding: "14px 16px",
                      transition: "all 0.15s",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#3b82f655";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = th.border;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{i}</span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: "-0.1px",
                        }}
                      >
                        {t}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: th.muted,
                        lineHeight: 1.55,
                      }}
                    >
                      {d}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ADVANCED INTELLIGENCE */}
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: th.dim,
                  marginBottom: 10,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  fontFamily: "monospace",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                Advanced Intelligence{" "}
                <span
                  style={{
                    fontSize: 8,
                    color: "#a78bfa",
                    background: "#a78bfa15",
                    padding: "1px 7px",
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  AI
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
                  gap: 10,
                }}
              >
                {[
                  [
                    "🔍",
                    "Contradiction Engine",
                    "Real-time alerts when new filings, errata, or supplemental briefs contradict the prior record. Cross-case contradictions surfaced automatically.",
                  ],
                  [
                    "🎯",
                    "Impeachment Bank",
                    "Witness-specific impeachment sequences pre-built from depositions, prior filings, and contradictory exhibits.",
                  ],
                  [
                    "💰",
                    "Damages Model",
                    "Track-by-track interactive sliders (SFUSD, antitrust, charitable trust, patent royalty) with AI-suggested inflection scenarios.",
                  ],
                  [
                    "🤝",
                    "Settlement Intel + BATNA",
                    "BATNA calculator, comparable verdicts library, demand/offer ledger, two-front war premium analysis.",
                  ],
                  [
                    "📋",
                    "Depositions",
                    "All depositions with party tags, status (designated/impeachment/admissions), key admissions, and contradiction flags.",
                  ],
                  [
                    "📰",
                    "Live Docket Sync",
                    "PACER + all 50 state courts in real time. Every filing auto-indexed and AI-analyzed.",
                  ],
                  [
                    "👨‍⚖️",
                    "Judge Intelligence",
                    "Tone, preferences, ruling patterns from this judge's orders. Historical win/loss analysis.",
                  ],
                  [
                    "⚠️",
                    "Procedural Traps",
                    "Warns before filing if certifications, page limits, or deadlines are at risk.",
                  ],
                  [
                    "⚡",
                    "Auto-Drafts",
                    "One-click AI drafts for motions, letters, briefs - 80% done before you open them. Plaintiff or defense framing.",
                  ],
                  [
                    "🎤",
                    "Hearing Prep Pack",
                    "Full hearing bundle: case summary, argument outline, judge questions, exhibit quick-reference, ECF document index.",
                  ],
                ].map(([i, t, d]) => (
                  <div
                    key={t}
                    style={{
                      background: th.card,
                      borderRadius: 10,
                      border: `1px solid ${th.border}`,
                      padding: "12px 14px",
                      transition: "all 0.15s",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#a78bfa55";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = th.border;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{i}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "-0.1px",
                        }}
                      >
                        {t}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: th.muted,
                        lineHeight: 1.55,
                      }}
                    >
                      {d}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* INTEGRATIONS */}
          <div style={{ marginBottom: 56 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: "-0.1px",
                  margin: 0,
                }}
              >
                Integrations
              </h3>
              <span style={{ fontSize: 11, color: th.faint }}>
                Works with your stack
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                "🏛️ PACER",
                "🤖 Harvey.ai",
                "📂 Clio",
                "📅 Calendar",
                "🎥 Zoom",
                "📚 Westlaw",
                "📖 Lexis+",
              ].map((n) => (
                <div
                  key={n}
                  style={{
                    background: th.card,
                    borderRadius: 8,
                    border: `1px solid ${th.border}`,
                    padding: "10px 16px",
                    fontSize: 11,
                    fontWeight: 600,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#3b82f644";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = th.border;
                  }}
                >
                  {n}
                </div>
              ))}
            </div>
          </div>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div
                style={{
                  background: th.card,
                  borderRadius: 10,
                  border: `1px solid ${th.border}`,
                  padding: "16px 20px",
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 12, fontWeight: 700, marginBottom: 3 }}
                    >
                      🛒 Developer Marketplace & API
                    </div>
                    <div
                      style={{
                        fontSize: 8,
                        color: th.muted,
                        lineHeight: 1.5,
                        maxWidth: 380,
                      }}
                    >
                      Build custom extensions on top of CaseBinder, add them to
                      your left nav, and publish to the marketplace. Open API
                      with webhooks, PACER data access, and full case context.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        flexWrap: "wrap",
                        marginTop: 8,
                      }}
                    >
                      {[
                        "REST + GraphQL",
                        "Webhooks",
                        "Custom views",
                        "PACER data",
                        "Private or public",
                      ].map((f, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 7,
                            color: "#a78bfa",
                            background: "#a78bfa18",
                            border: "1px solid #a78bfa33",
                            padding: "2px 7px",
                            borderRadius: 8,
                            fontFamily: "monospace",
                          }}
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  <a
                    href="mailto:api@casebinder.ai?subject=API Key Request"
                    style={{
                      padding: "8px 18px",
                      fontSize: 9,
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#1e40af,#a78bfa)",
                      color: "#fff",
                      borderRadius: 6,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Request API Key →
                  </a>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontFamily: "monospace",
                  color: "#10b981",
                  fontWeight: 600,
                  marginBottom: 6,
                  letterSpacing: 1,
                }}
              >
                ENTERPRISE PRICING
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>
                Built for Am Law 100. Priced for ROI.
              </h2>
              <p
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  maxWidth: 520,
                  margin: "0 auto",
                  lineHeight: 1.6,
                }}
              >
                One senior partner hour pays for a full day of CaseBinder. At
                $1,000/hr billing rates, the math is simple.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 14,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  background: "#0f172a",
                  borderRadius: 10,
                  border: "1px solid #1e293b",
                  padding: "20px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 8,
                    fontFamily: "monospace",
                    color: "#10b981",
                    marginBottom: 8,
                    letterSpacing: 1,
                  }}
                >
                  PILOT
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#f8fafc",
                    marginBottom: 4,
                  }}
                >
                  $10K
                </div>
                <div
                  style={{ fontSize: 8, color: "#64748b", marginBottom: 14 }}
                >
                  per active matter / month
                </div>
                <div
                  style={{
                    borderTop: "1px solid #1e293b",
                    paddingTop: 12,
                    marginBottom: 12,
                  }}
                >
                  {[
                    "Up to 5 active matters",
                    "Full ring binder AI suite",
                    "Live PACER + state docket sync",
                    "Hearing prep pack + argument outline",
                    "Evidence gap analysis",
                    "Firm branding",
                  ].map((f, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 8,
                        color: "#94a3b8",
                        display: "flex",
                        gap: 6,
                        alignItems: "flex-start",
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ color: "#10b981", flexShrink: 0 }}>✓</span>
                      {f}
                    </div>
                  ))}
                </div>
                <div
                  style={{ fontSize: 7, color: "#475569", marginBottom: 10 }}
                >
                  Ideal for: First 1–5 matters. 60-day pilot deal.
                </div>
                <button
                  onClick={() => setPage("pilot")}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: 9,
                    background: "#1e293b",
                    color: "#94a3b8",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Request Pilot
                </button>
              </div>
              <div
                style={{
                  background: "linear-gradient(135deg,#1e40af,#0d9488)",
                  borderRadius: 10,
                  border: "2px solid #3b82f6",
                  padding: "20px 18px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#f59e0b",
                    color: "#1a1a1a",
                    fontSize: 7,
                    fontWeight: 700,
                    padding: "2px 10px",
                    borderRadius: 10,
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                  }}
                >
                  MOST POPULAR
                </div>
                <div
                  style={{
                    fontSize: 8,
                    fontFamily: "monospace",
                    color: "#bfdbfe",
                    marginBottom: 8,
                    letterSpacing: 1,
                  }}
                >
                  PRACTICE GROUP
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: 4,
                  }}
                >
                  $3,500
                </div>
                <div
                  style={{ fontSize: 8, color: "#bfdbfe", marginBottom: 14 }}
                >
                  per attorney / month · billed annually
                </div>
                <div
                  style={{
                    borderTop: "1px solid #3b82f666",
                    paddingTop: 12,
                    marginBottom: 12,
                  }}
                >
                  {[
                    "Unlimited active matters",
                    "Live PACER + state docket sync",
                    "AI deposition intel - flags record inconsistencies",
                    "Hearing prep pack + argument outline",
                    "Opponent playbook + auto-drafts",
                    "Opposing counsel tools",
                    "SSO + DMS integration",
                    "Dedicated onboarding",
                  ].map((f, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 8,
                        color: "#e0f2fe",
                        display: "flex",
                        gap: 6,
                        alignItems: "flex-start",
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ color: "#fbbf24", flexShrink: 0 }}>✓</span>
                      {f}
                    </div>
                  ))}
                </div>
                <div
                  style={{ fontSize: 7, color: "#bfdbfe", marginBottom: 10 }}
                >
                  20 attorneys = $840K ARR · 50 attorneys = $2.1M ARR
                </div>
                <button
                  onClick={() => setPage("demo")}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: 9,
                    fontWeight: 700,
                    background: "#fff",
                    color: "#1e40af",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Schedule Demo
                </button>
              </div>
              <div
                style={{
                  background: "#0f172a",
                  borderRadius: 10,
                  border: "1px solid #1e293b",
                  padding: "20px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 8,
                    fontFamily: "monospace",
                    color: "#a78bfa",
                    marginBottom: 8,
                    letterSpacing: 1,
                  }}
                >
                  FIRM-WIDE
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#f8fafc",
                    marginBottom: 4,
                  }}
                >
                  Custom
                </div>
                <div
                  style={{ fontSize: 8, color: "#64748b", marginBottom: 14 }}
                >
                  volume pricing · multi-year deal
                </div>
                <div
                  style={{
                    borderTop: "1px solid #1e293b",
                    paddingTop: 12,
                    marginBottom: 12,
                  }}
                >
                  {[
                    "Firm-wide seat licensing",
                    "Live PACER + all 50 state courts",
                    "AI deposition intel across all matters",
                    "Custom AI fine-tuning on firm data",
                    "Developer marketplace + private extensions",
                    "Executive litigation analytics",
                    "White-label branding",
                    "SOC 2 + SLA",
                  ].map((f, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 8,
                        color: "#94a3b8",
                        display: "flex",
                        gap: 6,
                        alignItems: "flex-start",
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ color: "#a78bfa", flexShrink: 0 }}>✓</span>
                      {f}
                    </div>
                  ))}
                </div>
                <div
                  style={{ fontSize: 7, color: "#475569", marginBottom: 10 }}
                >
                  Orrick (1,100 atty) at $2,500/seat = $33M ARR ceiling
                </div>
                <button
                  onClick={() => setPage("enterprise")}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: 9,
                    background: "#1e293b",
                    color: "#a78bfa",
                    border: "1px solid #a78bfa44",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Contact Enterprise
                </button>
              </div>
            </div>
            <div
              style={{
                background: "#0f172a",
                borderRadius: 10,
                border: "1px solid #1e293b",
                padding: "16px 20px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#f8fafc",
                  marginBottom: 10,
                }}
              >
                ROI at a Glance - Am Law 100 Litigation Team (20 Attorneys)
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 12,
                }}
              >
                {[
                  ["$1,000/hr", "Avg. partner billing rate"],
                  ["2 hrs/wk", "Time saved per attorney"],
                  ["$2M+", "Annual billing recovered"],
                  ["3 weeks", "Payback period"],
                ].map(([v, l], i) => (
                  <div key={i} style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: i === 2 ? "#10b981" : "#3b82f6",
                      }}
                    >
                      {v}
                    </div>
                    <div
                      style={{ fontSize: 7, color: "#64748b", marginTop: 2 }}
                    >
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                background: "#0f172a",
                borderRadius: 10,
                border: "1px solid #1e293b",
                padding: "16px 20px",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#f8fafc",
                  marginBottom: 10,
                }}
              >
                CaseBinder vs. Harvey AI vs. Legora
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
                  gap: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 8,
                    fontWeight: 600,
                    color: "#64748b",
                    padding: "5px 0",
                    borderBottom: "1px solid #1e293b",
                  }}
                ></div>
                <div
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: "#3b82f6",
                    padding: "5px 8px",
                    borderBottom: "1px solid #1e293b",
                    textAlign: "center",
                    background: "#3b82f608",
                    borderRadius: "4px 4px 0 0",
                  }}
                >
                  CaseBinder
                </div>
                <div
                  style={{
                    fontSize: 8,
                    fontWeight: 600,
                    color: "#64748b",
                    padding: "5px 8px",
                    borderBottom: "1px solid #1e293b",
                    textAlign: "center",
                  }}
                >
                  Harvey AI
                </div>
                <div
                  style={{
                    fontSize: 8,
                    fontWeight: 600,
                    color: "#64748b",
                    padding: "5px 8px",
                    borderBottom: "1px solid #1e293b",
                    textAlign: "center",
                  }}
                >
                  Legora
                </div>
                {[
                  [
                    "Use case",
                    "Matter command center",
                    "General legal drafting",
                    "Legal research & drafting",
                  ],
                  [
                    "Pricing model",
                    "Per matter or per seat",
                    "Per seat",
                    "Per seat",
                  ],
                  [
                    "Case-specific AI context",
                    "Deep - docket, witnesses, evidence",
                    "None",
                    "Limited",
                  ],
                  [
                    "Full PACER + state docket sync",
                    "Yes - real-time, all 50 states",
                    "No",
                    "No",
                  ],
                  [
                    "Litigation strategy AI",
                    "Playbooks + procedural traps",
                    "Generic",
                    "Generic",
                  ],
                  [
                    "Hearing prep pack",
                    "Docket bundle + argument outline + rebuttals",
                    "No",
                    "No",
                  ],
                  [
                    "Live AI deposition intel",
                    "Flags witness inconsistencies vs. case record",
                    "No",
                    "No",
                  ],
                  [
                    "Developer marketplace",
                    "Open API + extensions",
                    "No",
                    "No",
                  ],
                  [
                    "Value prop",
                    "Case command center per matter",
                    "Faster drafting",
                    "Research acceleration",
                  ],
                  [
                    "Target buyer",
                    "Litigation partner",
                    "Firm IT / COO",
                    "Associates / research",
                  ],
                ].map(([l, cb, h, lg], i) => [
                  <div
                    key={l + "l"}
                    style={{
                      fontSize: 8,
                      color: "#94a3b8",
                      padding: "6px 0",
                      borderBottom: "1px solid #1e293b10",
                    }}
                  >
                    {l}
                  </div>,
                  <div
                    key={l + "cb"}
                    style={{
                      fontSize: 8,
                      color: "#10b981",
                      padding: "6px 8px",
                      borderBottom: "1px solid #1e293b10",
                      textAlign: "center",
                      background: "#10b98106",
                    }}
                  >
                    {cb}
                  </div>,
                  <div
                    key={l + "h"}
                    style={{
                      fontSize: 8,
                      color: "#64748b",
                      padding: "6px 8px",
                      borderBottom: "1px solid #1e293b10",
                      textAlign: "center",
                    }}
                  >
                    {h}
                  </div>,
                  <div
                    key={l + "lg"}
                    style={{
                      fontSize: 8,
                      color: "#64748b",
                      padding: "6px 8px",
                      borderBottom: "1px solid #1e293b10",
                      textAlign: "center",
                    }}
                  >
                    {lg}
                  </div>,
                ])}
              </div>
              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 12,
                  borderTop: "1px solid #1e293b",
                }}
              >
                <div
                  style={{
                    fontSize: 7,
                    color: "#475569",
                    fontFamily: "monospace",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span style={{ color: "#f59e0b" }}>⚠</span>U.S. Patent Pending
                </div>
                <a
                  href="https://drive.google.com/file/d/11qKv0H9gq9xXf6SptI7ulQIw1Q2ui8lb/view"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 7,
                    color: "#3b82f6",
                    textDecoration: "none",
                    fontFamily: "monospace",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 8px",
                    background: "#3b82f618",
                    border: "1px solid #3b82f633",
                    borderRadius: 4,
                  }}
                >
                  📄 View Patent Application
                </a>
              </div>
            </div>
          </div>
          <div
            style={{
              maxWidth: 900,
              margin: "24px auto 0",
              padding: "20px 16px",
              borderTop: `1px solid ${th.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ flex: 1, minWidth: 240 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: th.text,
                  marginBottom: 4,
                }}
              >
                CaseBinder.ai
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: th.muted,
                  lineHeight: 1.6,
                  maxWidth: 340,
                }}
              >
                The operating system for complex litigation. A product of{" "}
                <strong>PatentVC | Trademarkia</strong>.
              </div>
              <div
                style={{
                  fontSize: 8,
                  color: th.faint,
                  marginTop: 6,
                  fontFamily: "monospace",
                }}
              >
                3000 Sand Hill Blvd 3-245 · Menlo Park, CA 94025
              </div>
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div
                  style={{
                    fontSize: 8,
                    color: th.faint,
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontFamily: "monospace",
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Company
                </div>
                {[
                  ["Contact", "contact"],
                  ["Sales", "sales"],
                  ["Support", "support"],
                ].map(([l, p]) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      display: "block",
                      fontSize: 9,
                      color: th.muted,
                      textDecoration: "none",
                      marginBottom: 3,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textAlign: "left",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 8,
                    color: th.faint,
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontFamily: "monospace",
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Legal
                </div>
                {[
                  ["Privacy", "privacy"],
                  ["Terms", "terms"],
                  ["Sign Up", "signup"],
                ].map(([l, p]) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      display: "block",
                      fontSize: 9,
                      color: th.muted,
                      textDecoration: "none",
                      marginBottom: 3,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textAlign: "left",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 8,
                    color: th.faint,
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontFamily: "monospace",
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Built by
                </div>
                <a
                  href="https://patentvc.com"
                  target="_blank"
                  rel="noopener"
                  style={{
                    display: "block",
                    fontSize: 9,
                    color: th.muted,
                    textDecoration: "none",
                    marginBottom: 3,
                  }}
                >
                  PatentVC
                </a>
                <a
                  href="https://trademarkia.com"
                  target="_blank"
                  rel="noopener"
                  style={{
                    display: "block",
                    fontSize: 9,
                    color: th.muted,
                    textDecoration: "none",
                    marginBottom: 3,
                  }}
                >
                  Trademarkia
                </a>
              </div>
            </div>
          </div>
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              padding: "12px 16px",
              fontSize: 8,
              color: th.faint,
              textAlign: "center",
              fontFamily: "monospace",
            }}
          >
            © 2026 PatentVC, Inc. · All rights reserved · CaseBinder™ is a
            trademark of PatentVC
          </div>
        </div>
        {Modals}
        <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}@keyframes slideIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes spin{to{transform:rotate(360deg)}}button:hover{opacity:.85}kbd{font-family:monospace}::selection{background:rgba(59,130,246,0.25)}`}</style>
      </div>
    );

  const DEPO_TOPICS = {
    js: [
      "Dates, frequency and duration of JUUL product use",
      "How plaintiff first learned of JUUL (advertising, social media, peer)",
      "Physical and behavioral symptoms plaintiff attributes to JUUL use",
      "Medical treatment sought for nicotine addiction or related injuries",
      "Prior tobacco or nicotine product use history",
      "Plaintiff's awareness (or lack thereof) of nicotine content in JUUL pods",
      "Social media exposure to JUUL marketing during use period",
      "Purchases: retailer, frequency, pod flavor, quantity",
      "Any warnings plaintiff observed or received regarding JUUL products",
      "Damages: lost wages, medical expenses, out-of-pocket costs",
    ],
    jm: [
      "Role in designing the JUUL nicotine salt formulation (5% concentration)",
      "Knowledge of youth appeal of JUUL product design and marketing",
      "Internal communications regarding addiction risk and youth usage",
      "Knowledge of JUUL's marketing practices on social media platforms",
      "Decision-making on flavored pod offerings (mango, mint, etc.)",
      "Meetings with Altria regarding the 35% stake transaction",
      "Knowledge of FDA regulatory strategy and warning letters",
      "Any internal studies or reports regarding JUUL addiction potential",
      "Role in JUUL's PMTA submissions to FDA",
      "Relationship with advertising agencies used for JUUL campaigns",
    ],
    ab: [
      "Co-inventor role in nicotine salt delivery mechanism",
      "Knowledge of bioavailability and addiction potential of nicotine salts",
      "Internal emails regarding youth usage data tracking",
      "Role in device hardware design and USB charger form factor",
      "Knowledge of beta testing and early user feedback on addictiveness",
      "Any engineering decisions made to maximize nicotine delivery efficiency",
      "Relationship with James Monsees on formulation decisions",
      "Knowledge of JUUL's marketing review processes",
      "Post-departure communications with JUUL or Altria",
      "Personal use of JUUL products and any noted physiological effects",
    ],
    np: [
      "Role as JUUL investor and board member via Tao Capital",
      "Knowledge of governance decisions during the Altria negotiation",
      "Board-level awareness of youth marketing controversy",
      "Communications with other board members regarding FDA scrutiny",
      "Due diligence conducted before Altria's $12.8B investment",
      "Knowledge of any internal studies on addiction or youth usage",
      "Board approval process for JUUL's marketing strategy",
    ],
    gS: [
      "Role in JUUL's marketing and brand strategy",
      "Approval of social media campaigns targeting youth demographics",
      "Communications with advertising agencies and influencers",
      "Knowledge of JUUL's 'Make the Switch' campaign targeting smokers vs. youth",
      "Awareness of FDA warnings and regulatory correspondence",
      "Any internal discussions regarding age verification on JUUL.com",
    ],
    default: [
      "Personal and professional background",
      "Knowledge of JUUL products and marketing practices",
      "Communications with JUUL executives regarding nicotine content",
      "Any documents reviewed in preparation for deposition",
      "Knowledge of JUUL's regulatory submissions to FDA",
      "Prior deposition or trial testimony in related JUUL proceedings",
    ],
    rv: [
      "Role as JUUL board member and D.E. Shaw representative during 2017-2019",
      "Board meeting attendance and materials received regarding youth marketing controversy",
      "Knowledge of JUUL School program and any board-level discussion of regulatory risks",
      "Communications with Pritzker, Monsees, and Bowen regarding FDA scrutiny",
      "Due diligence conducted before and after Altria $12.8B investment",
      "Any board votes or resolutions regarding marketing strategy 2017-2019",
      "Knowledge of any JUUL internal studies on addiction or youth demographics",
    ],
    qp: [
      "Role as SFUSD Finance officer and 30(b)(6) designee on program costs",
      "Specific clarification: what categories of costs Pak was asked about vs total harm",
      "Documents reviewed before deposition and what records SFUSD maintained",
      "How SFUSD tracked costs attributable specifically to JUUL vs general vaping",
      "Quarry Pak statement We do not have that information -- what information specifically",
      "What data was available to SFUSD at time of deposition and since",
      "SFUSD budget line items created for vaping intervention 2017-2023",
      "Communications with SFUSD counsel regarding damages quantification strategy",
    ],
    dorn: [
      "Methodology for site inspections at each of the 6 SFUSD schools",
      "Basis for $78.8M forward-looking abatement estimate: assumptions, discount rates, useful life",
      "Whether site damage was caused by JUUL specifically vs general vaping",
      "Qualifications as an expert on school physical plant abatement",
      "Whether $78.8M figure accounts for any amounts already spent by SFUSD",
      "How Dorn distinguished JUUL-specific harm from background youth substance use",
      "Whether Dorn was present at or reviewed the Pak deposition",
    ],
    ar: [
      "Altria internal due diligence process for the JLI investment (Dkt.3393 unsealed materials)",
      "What Altria knew about JUUL user demographics -- specifically age distribution -- before Dec 2018 investment",
      "Decision-making process for withdrawing MarkTen and Green Smoke simultaneously with investment",
      "Communications between Altria and JLI regarding youth marketing practices 2017-2019",
      "Altria retail network agreements with JLI post-investment: shelf space, display placement",
      "Altria regulatory expertise provided to JLI and FDA interaction strategy",
      "Altria shareholder communications re harm-reduction narrative vs internal due diligence findings",
      "Knowledge of FTC complaint filed Apr 1 2020 and any pre-complaint discussions with FTC staff",
    ],
  };
  /* CASE VIEW */

  return (
    <div
      style={{
        fontFamily: "system-ui,sans-serif",
        background: th.bg,
        color: th.text,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <nav
        style={{
          background: th.nav,
          borderBottom: `1px solid ${th.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          height: 52,
          position: "relative",
          flexShrink: 0,
          boxShadow: dark
            ? "0 1px 0 rgba(255,255,255,0.02)"
            : "0 1px 2px rgba(0,0,0,0.03)",
        }}
      >
        <div
          onClick={() => setShowDash(true)}
          title="Switch case"
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            zIndex: 0,
            minWidth: 0,
            cursor: "pointer",
            padding: "6px 14px",
            borderRadius: 8,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = th.surface;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: th.text,
              whiteSpace: "nowrap",
              letterSpacing: "-0.2px",
              display: "flex",
              alignItems: "center",
              gap: 6,
              justifyContent: "center",
            }}
          >
            {isIle
              ? caseId === "chid"
                ? "USA v. Chidambaran"
                : "USA v. Naqvi"
              : isJuul
                ? "Seliavski v. Juul Labs"
                : caseId === "musk"
                  ? "Musk v. Altman et al."
                  : "Neurocentria v. Mando"}
            <span style={{ fontSize: 9, color: th.faint, fontWeight: 500 }}>
              ▾
            </span>
          </div>
          <div
            style={{
              fontSize: 9,
              color: th.faint,
              fontFamily: "monospace",
              marginTop: 2,
              letterSpacing: 0.3,
            }}
          >
            {isJuul
              ? "3:19-md-02913-WHO · N.D. Cal."
              : "2:25-cv-05871-FLA-MAR · C.D. Cal."}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            zIndex: 1,
          }}
          onClick={() => setPage("home")}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: "linear-gradient(135deg,#1e40af,#10b981)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              fontWeight: 700,
              color: "#fff",
              boxShadow: "0 1px 3px rgba(30,64,175,0.25)",
            }}
          >
            {fromOrrick ? (
              <img
                src="data:image/png;base64,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"
                alt="Orrick"
                style={{ height: 22, objectFit: "contain", display: "block" }}
              />
            ) : (
              <span style={{ fontSize: 8, fontWeight: 700, color: "#fff" }}>
                CB
              </span>
            )}
          </div>
          {!fromOrrick && (
            <span
              style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px" }}
            >
              CaseBinder
            </span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <button
            onClick={() => {
              if (tab !== "depo") recordReturnTabRef.current = tab;
              setTab("depo");
              if (!isRecording) setShowRecordSetup(true);
            }}
            style={{
              height: 34,
              padding: "0 14px",
              fontSize: 12,
              fontWeight: 600,
              background:
                tab === "depo"
                  ? isRecording
                    ? "#ef4444"
                    : "#1e40af"
                  : th.surface,
              color:
                tab === "depo" ? "#fff" : isRecording ? "#ef4444" : "#dc2626",
              border: `1px solid ${tab === "depo" ? (isRecording ? "#ef4444" : "#1e40af") : isRecording ? "#ef4444" : "#dc262644"}`,
              borderRadius: 7,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "all 0.15s",
            }}
          >
            {isRecording ? (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: tab === "depo" ? "#fff" : "#ef4444",
                  display: "inline-block",
                  animation: "pulse 1s infinite",
                }}
              />
            ) : (
              <span style={{ fontSize: 11 }}>●</span>
            )}
            <span>{isRecording ? "Recording" : "Record"}</span>
          </button>
          {NI}
          <button
            onClick={() => setShowSettings(true)}
            title="Settings"
            style={{
              width: 34,
              height: 34,
              borderRadius: 7,
              background: th.surface,
              border: `1px solid ${th.border}`,
              color: th.dim,
              cursor: "pointer",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = th.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = th.dim;
            }}
          >
            ⚙
          </button>
        </div>
      </nav>
      {showRec &&
        (() => {
          /* ── per-case witness roster ─────────────────────────────────────── */
          const REC_WITNESSES = isIle
            ? [
                {
                  id: "to",
                  name: "Tom Olivier",
                  role: "SPAC President / AAI Solutions",
                  party: "Defendant",
                  qs: [
                    "Walk through the 18-month diligence - what did the data room show on Entity 1?",
                    "Why did you keep working with Mr. Naqvi after the resignations?",
                    "What did you rely on when you bought the assets out of Chapter 7?",
                  ],
                },
                {
                  id: "cc2",
                  name: "Co-Conspirator 2",
                  role: "iLearning VP - account controller",
                  party: "Plaintiff",
                  qs: [
                    "Who directed each deletion - name, date, channel?",
                    "Did Mr. Naqvi ever instruct you to move money or delete records?",
                    "What approvals accompanied the October 2024 transfers?",
                  ],
                },
                {
                  id: "e1",
                  name: "Employee 1",
                  role: "Subpoena recipient (May 2025)",
                  party: "Plaintiff",
                  qs: [
                    "Who did you tell about the grand jury subpoena, and when?",
                    "Did you ever discuss it with Mr. Naqvi?",
                  ],
                },
                {
                  id: "bg",
                  name: "Bonnie Gerety",
                  role: "Executive hired post-report",
                  party: "Defendant",
                  qs: [
                    "Describe the WhatsApp threads Mr. Naqvi produced - routine business or concealment?",
                    "What was the operations posture after the August 2024 report?",
                  ],
                },
              ]
            : isJuul
              ? [
                  {
                    id: "qp",
                    name: "Quarry Pak",
                    role: "SFUSD 30(b)(6)",
                    party: "Plaintiff",
                    qs: [
                      "Clarify what specific cost categories you were asked about in the December 2021 deposition.",
                      "What records did SFUSD actually maintain for vaping-related expenditures?",
                      "Did SFUSD create any new budget line items specifically for JUUL response after 2017?",
                      "Were you present for or have you reviewed the Superintendent Matthews deposition?",
                      "What SFUSD documentation exists for the $34,875 in direct expenditures cited in Dkt.3447?",
                      "Has SFUSD retained a cost-accounting expert to quantify program costs since your deposition?",
                    ],
                  },
                  {
                    id: "jm",
                    name: "James Monsees",
                    role: "JLI Co-Founder / CEO",
                    party: "Defendant",
                    qs: [
                      "When did you first see internal data showing JUUL's user base skewed toward minors?",
                      "Were you on the distribution list for the board decks tracking youth uptake as a KPI - Dkt.3447 SOF para 47?",
                      "Who approved the JUUL School field representative program that visited high schools?",
                      "What steps, if any, did you take after learning that underage users were purchasing at convenience stores?",
                      "Did you personally review JUUL's age-verification policy on JUUL.com before the 2018 Congressional hearings?",
                      "Describe your role in negotiating the Altria $12.8B investment and any conditions placed on it.",
                    ],
                  },
                  {
                    id: "js",
                    name: "Julia Seliavski",
                    role: "Named PI Plaintiff",
                    party: "Plaintiff",
                    qs: [
                      "Walk me through your first time using a JUUL pod - where were you and who introduced it to you?",
                      "Before using JUUL, did you know the product contained nicotine? What did you think it was?",
                      "Did you see any warning label on the JUUL pod or device when you first purchased it?",
                      "How did you obtain JUUL pods? Were you ever asked for identification at 7-Eleven or Circle K?",
                      "When did you first understand your symptoms - anger, mood swings, irritability - were related to nicotine?",
                      "Have you seen any doctor or mental-health professional specifically for nicotine addiction or JUUL-related injuries?",
                    ],
                  },
                  {
                    id: "ar",
                    name: "Altria Group",
                    role: "30(b)(6) - RICO / Antitrust",
                    party: "Defendant",
                    qs: [
                      "What did Altria's pre-investment due diligence reveal about the age distribution of JUUL's existing user base?",
                      "Who at Altria made the decision to simultaneously withdraw MarkTen and Green Smoke alongside the JLI investment?",
                      "Were MarkTen and Green Smoke commercially viable at the time of withdrawal, or were they struggling?",
                      "What regulatory expertise did Altria specifically provide to JLI after the December 2018 investment?",
                      "Was Altria aware of the FTC's investigation of the JLI investment prior to its April 2020 complaint?",
                      "What internal communications exist between Altria and JLI executives regarding youth marketing between 2018 and 2020?",
                    ],
                  },
                  {
                    id: "ab",
                    name: "Adam Bowen",
                    role: "JLI Co-Founder / CPO",
                    party: "Defendant",
                    qs: [
                      "What was the design rationale for 59mg/mL nicotine concentration versus lower concentrations tested?",
                      "Were alternative formulations considered that would have delivered a lower nicotine hit per puff?",
                      "Was the USB charger form factor - which makes the device highly concealable - a deliberate design choice?",
                      "What internal addiction-risk studies, if any, did JLI commission before launch?",
                      "Did you or your team track early user feedback specifically referencing addiction or difficulty stopping?",
                      "What is your current understanding of why the 5% pod became the dominant product despite higher nicotine delivery?",
                    ],
                  },
                  {
                    id: "dorn",
                    name: "Expert: Dorn",
                    role: "SFUSD Abatement Expert",
                    party: "Plaintiff",
                    qs: [
                      "Walk me through your site inspection methodology at each of the six SFUSD schools.",
                      "How did you attribute physical damage - broken bathroom locks, cut fences - specifically to JUUL versus general vaping?",
                      "What assumptions underlie your $78.8M forward-looking estimate? Discount rate, useful life, inflation?",
                      "Does the $78.8M figure account for any amounts SFUSD has already spent on remediation?",
                      "How does your estimate respond to Sonner v. Premier Nutrition - if SFUSD has no legal damages, does your equitable figure survive?",
                      "Were you present at or did you review Quarry Pak's deposition before finalizing your report?",
                    ],
                  },
                ]
              : caseId === "musk"
                ? [
                    {
                      id: "em",
                      name: "Elon Musk",
                      role: "Plaintiff / Founder",
                      party: "Plaintiff",
                      qs: [
                        "When you replied 'Yup' to that email - DX-516 - what specifically were you agreeing with?",
                        "Did you review the August 2018 LP term sheet - DX-827 - before it was circulated? Why did you not object to the for-profit structure?",
                        "In March 2019 you said 'tell them I'm supportive' - DX-862. Who were you instructing to tell whom, and supportive of what specifically?",
                        "When did you first believe OpenAI had materially deviated from its founding charitable mission?",
                        "Your September 2020 tweet said OpenAI is not open source - was that your first public statement of concern, or had you raised it privately earlier?",
                        "Can you identify a single document from your tenure on the OpenAI board that committed OpenAI to remaining open-source forever?",
                      ],
                    },
                    {
                      id: "sa",
                      name: "Samuel Altman",
                      role: "CEO, OpenAI (Defendant)",
                      party: "Defendant",
                      qs: [
                        "When was the decision made internally to convert OpenAI to a capped-profit structure?",
                        "Was Elon Musk consulted before the conversion, and if so what was his response at that time?",
                        "What did you tell prospective donors about OpenAI's structure after the 2019 conversion?",
                        "Has OpenAI ever represented itself as a nonprofit in any fundraising materials post-2019?",
                        "Describe the board's deliberation process before approving the Microsoft $1B investment in July 2019.",
                        "How does OpenAI's current valuation of $157B square with the original charitable mission of benefiting all of humanity?",
                      ],
                    },
                    {
                      id: "sn",
                      name: "Satya Nadella",
                      role: "CEO, Microsoft (Defendant)",
                      party: "Defendant",
                      qs: [
                        "What did Microsoft know about OpenAI's for-profit conversion before the July 2019 $1B investment?",
                        "Referring to PX-230 - the Kevin Scott email - what was the nature of Microsoft's engagement with OpenAI 16 months before investment?",
                        "Did Microsoft conduct due diligence on OpenAI's nonprofit status and governance structure before investing?",
                        "Were any Microsoft board members or counsel aware of the Musk-OpenAI dispute prior to the investment?",
                        "Has Microsoft ever received or reviewed OpenAI's original Certificate of Incorporation as a nonprofit?",
                        "What representations did OpenAI make to Microsoft about its charitable mission during investment negotiations?",
                      ],
                    },
                    {
                      id: "ld",
                      name: "Louis Dudney",
                      role: "Financial Expert (Defense)",
                      party: "Defendant",
                      qs: [
                        "Walk me through your FIFO accounting methodology for tracing Musk's $44.4M donations.",
                        "Why does $38M rather than the full $44.4M trace to the current $157B enterprise value?",
                        "What valuation date and methodology did you use for the $157B enterprise value figure?",
                        "Have you accounted for the dilution of Musk's notional 'interest' through subsequent funding rounds?",
                        "Does your analysis depend on the proposition that Musk's donations created an enforceable interest?",
                        "How does your model change if the 2019 capped-profit conversion is found to have been unauthorized?",
                      ],
                    },
                  ]
                : [
                    {
                      id: "liu",
                      name: "Dr. Liu",
                      role: "Defendant Inventor",
                      party: "Defendant",
                      qs: [
                        "In your deposition you testified about awareness of CN1200366A - when exactly did you first encounter that document?",
                        "Did you disclose CN1200366A to patent counsel before filing the application? If not, why not?",
                        "How does the in vivo formation step in Claim 1 differ from the in vitro process in CN1200366A?",
                        "Were you aware of the MIT lab publications that predated your filing at the time of application?",
                        "Can you identify any element of your claimed invention that is not present in CN1200366A combined with the MIT publications?",
                        "Were you the sole inventor, or did other researchers at Mando contribute to the claimed invention?",
                      ],
                    },
                    {
                      id: "mao",
                      name: "Dr. Mao",
                      role: "Defendant Inventor",
                      party: "Defendant",
                      qs: [
                        "The YouTube video posted under your name demonstrates the accused product functionality - did you authorize that video?",
                        "Does the accused product perform the in vivo formation step as described in the asserted claims?",
                        "What is the actual country of origin for the components shown in the Certificates of Analysis?",
                        "Are you aware of any internal Mando documentation showing awareness of Neurocentria's patents before this litigation?",
                        "What role did you play in commercializing the accused product versus the underlying research?",
                        "Have you reviewed the ImportGenius trade data showing component origins? Is it accurate?",
                      ],
                    },
                    {
                      id: "sl",
                      name: "Slutsky",
                      role: "Disputed Inventor",
                      party: "Plaintiff",
                      qs: [
                        "What was your specific inventive contribution to the claims asserted in this case?",
                        "When and how did you assign - or not assign - your rights to Neurocentria?",
                        "Are you aware of any gap in the TMLA chain of title that might affect Neurocentria's standing?",
                        "Did you work with Dr. Liu or Dr. Mao on any aspect of the claimed invention?",
                        "Have you signed any document that could be interpreted as a release or waiver of inventorship rights?",
                        "What documentation exists of your inventive contribution contemporaneous with the alleged invention date?",
                      ],
                    },
                  ];

          /* ── UI state ──────────────────────────────────────────────────────── */
          const [recSelWit, setRecSelWit] = React.useState(null);
          const fmtTime = (s) => {
            const h = Math.floor(s / 3600),
              m = Math.floor((s % 3600) / 60),
              sec = s % 60;
            return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
          };

          /* ── start / stop ──────────────────────────────────────────────────── */
          const startRec = () => {
            const SR =
              window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SR) {
              alert(
                "Speech recognition not supported. Use Chrome or Safari on macOS.",
              );
              return;
            }
            const sr = new SR();
            sr.lang = "en-US";
            sr.continuous = true;
            sr.interimResults = true;
            sr.maxAlternatives = 1;
            sr.onresult = async (e) => {
              let interim = "";
              for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) {
                  const txt = e.results[i][0].transcript.trim();
                  if (!txt) return;
                  setRecTranscript((p) => [
                    ...p,
                    { t: new Date().toLocaleTimeString(), txt },
                  ]);
                  setRecInterim("");
                  const selWitName = recSelWit
                    ? (REC_WITNESSES.find((w) => w.id === recSelWit) || {}).name
                    : null;
                  const docketCtx = aD
                    .slice(0, 20)
                    .map(
                      (d) =>
                        `Dkt.${d.num} (${d.date}): ${(d.text || "").slice(0, 120)}`,
                    )
                    .join("\n");
                  const witCtx = REC_WITNESSES.map(
                    (w) =>
                      `${w.name} (${w.role}): ${(w.qs || []).slice(0, 3).join(" | ")}`,
                  ).join("\n");
                  const caseName = isIle
                    ? caseId === "chid"
                      ? "USA v. Chidambaran (1:26-cr-00097-NGG-1)"
                      : "USA v. Naqvi (1:26-cr-00097-NGG-2)"
                    : isJuul
                      ? "Seliavski v. JUUL Labs MDL (3:19-md-02913-WHO)"
                      : caseId === "musk"
                        ? "Musk v. Altman et al. (4:24-cv-04722-YGR)"
                        : "Neurocentria v. Mando (2:25-cv-05871-FLA-MAR)";
                  try {
                    const r = await fetch("/api/messages", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "anthropic-version": "2023-06-01",
                      },
                      body: JSON.stringify({
                        model: "claude-sonnet-4-20250514",
                        max_tokens: 320,
                        system: `You are a live litigation AI for ${caseName}.${selWitName ? ` Current witness: ${selWitName}.` : ""}\n\nRecent docket:\n${docketCtx}\n\nWitness roster:\n${witCtx}\n\nAnalyze what was just said and respond with JSON only:\n{"flag":"CONTRADICTION"|"KEY_FACT"|"IMPEACH"|"PROCEDURAL"|"NEUTRAL","short":"one sentence ≤12 words","detail":"2-3 sentences citing docket entries or case facts","followUp":"one sharp follow-up question if applicable, else empty string"}`,
                        messages: [
                          {
                            role: "user",
                            content: `Statement: "${txt}"\n\nJSON only:`,
                          },
                        ],
                      }),
                    });
                    const d = await r.json();
                    const raw = d.content?.[0]?.text || "{}";
                    let inf;
                    try {
                      inf = JSON.parse(raw.replace(/```json|```/g, "").trim());
                    } catch {
                      inf = {
                        flag: "NEUTRAL",
                        short: "Logged",
                        detail: raw.slice(0, 120),
                        followUp: "",
                      };
                    }
                    if (inf.flag !== "NEUTRAL") {
                      setRecInferences((p) =>
                        [
                          {
                            t: new Date().toLocaleTimeString(),
                            quote: txt.slice(0, 60),
                            flag: inf.flag,
                            short: inf.short,
                            detail: inf.detail,
                            followUp: inf.followUp || "",
                          },
                          ...p,
                        ].slice(0, 20),
                      );
                    }
                  } catch (err) {
                    console.error("Rec inference error:", err);
                  }
                } else {
                  interim += e.results[i][0].transcript;
                }
              }
              setRecInterim(interim);
            };
            sr.onerror = (e) => {
              if (e.error !== "no-speech" && e.error !== "aborted")
                setRecInferences((p) =>
                  [
                    {
                      t: new Date().toLocaleTimeString(),
                      quote: "",
                      flag: "PROCEDURAL",
                      short: "Mic error: " + e.error,
                      detail:
                        "Check microphone permissions in browser settings.",
                      followUp: "",
                    },
                    ...p,
                  ].slice(0, 20),
                );
            };
            sr.onend = () => {
              if (recSRRef.sr === sr) sr.start();
            };
            sr.start();
            recSRRef.sr = sr;
            recSRRef.timer = setInterval(
              () => setRecElapsed((p) => p + 1),
              1000,
            );
            setRecActive(true);
            setRecElapsed(0);
          };
          const stopRec = () => {
            if (recSRRef.sr) {
              recSRRef.sr.onend = null;
              recSRRef.sr.stop();
              recSRRef.sr = null;
            }
            if (recSRRef.timer) {
              clearInterval(recSRRef.timer);
              recSRRef.timer = null;
            }
            setRecActive(false);
            setRecInterim("");
          };

          const flagColor = {
            CONTRADICTION: "#ef4444",
            KEY_FACT: "#3b82f6",
            IMPEACH: "#f59e0b",
            PROCEDURAL: "#8b5cf6",
            NEUTRAL: "#6b7280",
          };
          const selWit = REC_WITNESSES.find((w) => w.id === recSelWit) || null;

          return (
            <div
              style={{
                background: "#07090f",
                borderBottom: "1px solid #1f2937",
                flexShrink: 0,
                maxHeight: recActive ? "380px" : "48px",
                transition: "max-height 0.3s ease",
                overflow: "hidden",
              }}
            >
              {/* ── top control bar ── */}
              <div
                style={{
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderBottom: recActive ? "1px solid #1f2937" : "none",
                }}
              >
                <button
                  onClick={recActive ? stopRec : startRec}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: recActive ? "#ef4444" : "rgba(239,68,68,0.15)",
                    border:
                      "1px solid " +
                      (recActive ? "#ef4444" : "rgba(239,68,68,0.3)"),
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                  }}
                >
                  {recActive ? "⏹" : "🎙"}
                </button>
                <span
                  style={{
                    fontSize: 11,
                    color: "#ef4444",
                    fontWeight: 700,
                    letterSpacing: 0.5,
                  }}
                >
                  {recActive ? "🔴 LIVE - Recording" : "● READY"}
                </span>
                {recActive && (
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      color: "#ef4444",
                    }}
                  >
                    {fmtTime(recElapsed)}
                  </span>
                )}
                {recInterim && (
                  <span
                    style={{
                      fontSize: 10,
                      color: "rgba(239,68,68,0.4)",
                      fontStyle: "italic",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    "{recInterim}"
                  </span>
                )}
                {selWit && (
                  <span
                    style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}
                  >
                    Witness:{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 600 }}>
                      {selWit.name}
                    </span>
                  </span>
                )}
                <span
                  style={{
                    fontSize: 10,
                    color: "rgba(239,68,68,0.3)",
                    marginLeft: "auto",
                  }}
                >
                  {recTranscript.length} utterances · {recInferences.length}{" "}
                  flags
                </span>
                <button
                  onClick={() => {
                    stopRec();
                    setShowRec(false);
                    setRecTranscript([]);
                    setRecInferences([]);
                    setRecElapsed(0);
                    setRecSelWit(null);
                  }}
                  style={{
                    fontSize: 13,
                    background: "transparent",
                    color: "rgba(239,68,68,0.4)",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>

              {/* ── three-column live panel ── */}
              {recActive && (
                <div
                  style={{ display: "flex", height: 332, overflow: "hidden" }}
                >
                  {/* COL 1 - Witness roster */}
                  <div
                    style={{
                      width: 160,
                      flexShrink: 0,
                      overflowY: "auto",
                      borderRight: "1px solid #1f2937",
                      background: "#07090f",
                      padding: "8px 6px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 7,
                        fontWeight: 700,
                        color: "#374151",
                        letterSpacing: 1,
                        marginBottom: 6,
                        padding: "0 4px",
                      }}
                    >
                      WITNESSES
                    </div>
                    {REC_WITNESSES.map((w) => (
                      <button
                        key={w.id}
                        onClick={() =>
                          setRecSelWit(recSelWit === w.id ? null : w.id)
                        }
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "6px 8px",
                          marginBottom: 3,
                          borderRadius: 4,
                          border:
                            "1px solid " +
                            (recSelWit === w.id ? "#374151" : "transparent"),
                          background:
                            recSelWit === w.id ? "#111827" : "transparent",
                          cursor: "pointer",
                          display: "block",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: recSelWit === w.id ? 700 : 400,
                            color: recSelWit === w.id ? "#e5e7eb" : "#9ca3af",
                            lineHeight: 1.3,
                          }}
                        >
                          {w.name}
                        </div>
                        <div
                          style={{
                            fontSize: 7,
                            color:
                              w.party === "Plaintiff"
                                ? "#3b82f688"
                                : "#ef444488",
                            marginTop: 1,
                          }}
                        >
                          {w.role}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* COL 2 - Follow-up questions for selected witness */}
                  <div
                    style={{
                      width: 220,
                      flexShrink: 0,
                      overflowY: "auto",
                      borderRight: "1px solid #1f2937",
                      padding: "8px 10px",
                      background: "#06080d",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 7,
                        fontWeight: 700,
                        color: "#374151",
                        letterSpacing: 1,
                        marginBottom: 6,
                      }}
                    >
                      {selWit
                        ? `FOLLOW-UP Qs - ${selWit.name.split(" ")[0].toUpperCase()}`
                        : "FOLLOW-UP QUESTIONS"}
                    </div>
                    {!selWit && (
                      <div
                        style={{
                          fontSize: 8,
                          color: "#374151",
                          fontStyle: "italic",
                          lineHeight: 1.7,
                        }}
                      >
                        Select a witness to see recommended follow-up questions.
                      </div>
                    )}
                    {selWit &&
                      selWit.qs.map((q, i) => (
                        <div
                          key={i}
                          style={{
                            marginBottom: 7,
                            padding: "5px 7px",
                            background: "rgba(55,65,81,0.15)",
                            borderRadius: 3,
                            borderLeft: "2px solid #1f2937",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: "#4b5563",
                              marginBottom: 1,
                            }}
                          >
                            Q{i + 1}
                          </div>
                          <div
                            style={{
                              fontSize: 8.5,
                              color: "#d1d5db",
                              lineHeight: 1.5,
                            }}
                          >
                            {q}
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* COL 3 - Transcript + inferences */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Transcript (top half) */}
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "8px 10px",
                        borderBottom: "1px solid #1f2937",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          fontWeight: 700,
                          color: "#374151",
                          letterSpacing: 1,
                          marginBottom: 6,
                        }}
                      >
                        TRANSCRIPT
                      </div>
                      {recTranscript.length === 0 && (
                        <div
                          style={{
                            fontSize: 8,
                            color: "#374151",
                            fontStyle: "italic",
                          }}
                        >
                          Speak clearly - live transcription active…
                        </div>
                      )}
                      {[...recTranscript].reverse().map((u, i) => (
                        <div
                          key={i}
                          style={{
                            marginBottom: 5,
                            borderLeft: "2px solid #1f2937",
                            paddingLeft: 6,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: "#374151",
                              marginBottom: 1,
                            }}
                          >
                            {u.t}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#d1d5db",
                              lineHeight: 1.4,
                            }}
                          >
                            {u.txt}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Inferences (bottom half) */}
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "8px 10px",
                        background: "#06090f",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          fontWeight: 700,
                          color: "#374151",
                          letterSpacing: 1,
                          marginBottom: 6,
                        }}
                      >
                        AI FLAGS
                      </div>
                      {recInferences.length === 0 && (
                        <div
                          style={{
                            fontSize: 8,
                            color: "#374151",
                            fontStyle: "italic",
                          }}
                        >
                          Contradictions and key facts appear here…
                        </div>
                      )}
                      {recInferences.map((inf, i) => {
                        const fc = flagColor[inf.flag] || "#6b7280";
                        return (
                          <div
                            key={i}
                            style={{
                              marginBottom: 7,
                              background: fc + "10",
                              border: "1px solid " + fc + "28",
                              borderRadius: 4,
                              padding: "5px 8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                marginBottom: 3,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 7,
                                  fontWeight: 700,
                                  color: fc,
                                  background: fc + "20",
                                  padding: "1px 5px",
                                  borderRadius: 3,
                                }}
                              >
                                {inf.flag}
                              </span>
                              <span style={{ fontSize: 7, color: "#374151" }}>
                                {inf.t}
                              </span>
                            </div>
                            {inf.quote && (
                              <div
                                style={{
                                  fontSize: 7.5,
                                  color: "#6b7280",
                                  fontStyle: "italic",
                                  marginBottom: 2,
                                }}
                              >
                                "{inf.quote}…"
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 8.5,
                                fontWeight: 600,
                                color: "#e5e7eb",
                                marginBottom: 2,
                              }}
                            >
                              {inf.short}
                            </div>
                            <div
                              style={{
                                fontSize: 7.5,
                                color: "#9ca3af",
                                lineHeight: 1.4,
                              }}
                            >
                              {inf.detail}
                            </div>
                            {inf.followUp && (
                              <div
                                style={{
                                  marginTop: 4,
                                  padding: "3px 6px",
                                  background: "rgba(59,130,246,0.08)",
                                  border: "1px solid rgba(59,130,246,0.2)",
                                  borderRadius: 3,
                                  fontSize: 7.5,
                                  color: "#60a5fa",
                                }}
                              >
                                → {inf.followUp}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      <div
        style={{
          flex: 1, overflow: "hidden", display: "flex", height: "calc(100vh - 52px)", minHeight: 0, maxHeight: "calc(100vh - 52px)",
        }}
      >
        {(() => {
          const sbW = sidebarCollapsed ? 56 : 216;
          const isActive = (id, kind) =>
            kind === "tab" ? tab === id : tab === "ringbinder" && rbP === id;
          const handleClick = (id, kind) =>
            kind === "tab"
              ? (setTab(id), setPanel(null))
              : (setTab("ringbinder"), setRbP(id));
          return (
            <nav
              style={{
                width: sbW,
                minWidth: sbW,
                background: th.nav,
                borderRight: `1px solid ${th.border}`,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden",
                flexShrink: 0,
                transition: "width 0.18s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: sidebarCollapsed ? "center" : "space-between",
                  padding: sidebarCollapsed ? "10px 0" : "10px 14px 8px",
                  borderBottom: `1px solid ${th.border}`,
                  minHeight: 38,
                }}
              >
                {!sidebarCollapsed && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: th.text,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Case Binder
                  </span>
                )}
                <button
                  onClick={() => setSidebarCollapsed((v) => !v)}
                  title={
                    sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                  }
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 5,
                    background: "transparent",
                    border: "none",
                    color: th.dim,
                    cursor: "pointer",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {sidebarCollapsed ? "›" : "‹"}
                </button>
              </div>
              <div
                style={{
                  flex: 1,
                  padding: sidebarCollapsed ? "6px 4px" : "6px 6px",
                  display: "flex",
                  flexDirection: "column",
                  gap: sidebarCollapsed ? 2 : 2,
                }}
              >
                {SIDEBAR_GROUPS.map((grp) => {
                  const items = grp.items.filter(
                    ([id]) => !enabledTabs || enabledTabs.includes(id),
                  );
                  if (!items.length) return null;
                  const grpCollapsed = collapsedGroups[grp.id];
                  return (
                    <div
                      key={grp.id}
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      {!sidebarCollapsed && (
                        <button
                          onClick={() =>
                            setCollapsedGroups((p) => ({
                              ...p,
                              [grp.id]: !p[grp.id],
                            }))
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 10px 5px",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: th.muted,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            textTransform: "uppercase",
                            fontFamily: "system-ui,-apple-system,sans-serif",
                          }}
                        >
                          <span>{grp.label}</span>
                          <span
                            style={{
                              fontSize: 8,
                              opacity: 0.5,
                              transition: "transform 0.18s ease",
                              transform: grpCollapsed
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                              display: "inline-block",
                            }}
                          >
                            ▾
                          </span>
                        </button>
                      )}
                      {sidebarCollapsed && (
                        <div
                          style={{
                            height: 1,
                            background: th.border,
                            margin: "4px 8px",
                            opacity: 0.5,
                          }}
                        />
                      )}
                      {(!grpCollapsed || sidebarCollapsed) && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {items.map(([id, icon, lbl, kind]) => {
                            const active = isActive(id, kind);
                            return (
                              <button
                                key={id}
                                onClick={() => handleClick(id, kind)}
                                title={lbl}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: sidebarCollapsed ? 0 : 11,
                                  padding: sidebarCollapsed
                                    ? "10px 0"
                                    : "8px 11px",
                                  justifyContent: sidebarCollapsed
                                    ? "center"
                                    : "flex-start",
                                  background: active
                                    ? dark
                                      ? "#1a2233"
                                      : "#eef2ff"
                                    : "transparent",
                                  color: active
                                    ? dark
                                      ? "#fff"
                                      : "#1e40af"
                                    : th.text,
                                  border: "none",
                                  borderLeft:
                                    active && !sidebarCollapsed
                                      ? `3px solid ${grp.accent}`
                                      : "3px solid transparent",
                                  borderRadius: sidebarCollapsed ? 7 : 6,
                                  cursor: "pointer",
                                  fontSize: 12,
                                  fontWeight: active ? 600 : 500,
                                  letterSpacing: active ? -0.05 : -0.05,
                                  textAlign: "left",
                                  width: "100%",
                                  fontFamily:
                                    "system-ui,-apple-system,sans-serif",
                                  transition:
                                    "background 0.12s ease, color 0.12s ease, border-color 0.12s ease",
                                }}
                                onMouseEnter={(e) => {
                                  if (!active)
                                    e.currentTarget.style.background =
                                      th.surface;
                                }}
                                onMouseLeave={(e) => {
                                  if (!active)
                                    e.currentTarget.style.background =
                                      "transparent";
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: sidebarCollapsed ? 16 : 14,
                                    lineHeight: 1,
                                    flexShrink: 0,
                                    width: sidebarCollapsed ? "auto" : 18,
                                    textAlign: "center",
                                    opacity: active ? 1 : 0.85,
                                  }}
                                >
                                  {icon}
                                </span>
                                {!sidebarCollapsed && (
                                  <span
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {lbl}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>
          );
        })()}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
          <div id="cb-main-scroll" style={{ flex: 1, overflow: "auto" }}>
            {/* TIMELINES */}
            {tab === "timelines" && (
              <div
                style={{
                  maxWidth: sp ? 640 : 900,
                  margin: "0 auto",
                  padding: "16px 16px 60px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                    {tlView === "docket"
                      ? "📋 ECF Docket"
                      : tlView === "threads"
                        ? "🧵 Motion Threads"
                        : "📅 Timeline"}
                  </h1>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[
                      ["timeline", "Timeline"],
                      ["docket", "Docket"],
                      ["threads", "Threads"],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        onClick={() => setTlView(v)}
                        style={{
                          padding: "5px 12px",
                          fontSize: 12,
                          fontWeight: tlView === v ? 600 : 400,
                          background: tlView === v ? th.surface : "transparent",
                          color: tlView === v ? th.text : th.dim,
                          border: `1px solid ${tlView === v ? th.border : "transparent"}`,
                          borderRadius: 5,
                          cursor: "pointer",
                        }}
                      >
                        {l}
                      </button>
                    ))}
                    <button
                      onClick={() => setShowAddTL(true)}
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        background: "#1e40af",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        cursor: "pointer",
                        marginLeft: 4,
                        fontWeight: 600,
                      }}
                    >
                      + Add
                    </button>
                  </div>
                </div>
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <input
                    value={tlSearch}
                    onChange={(e) => setTlSearch(e.target.value)}
                    placeholder={
                      tlDocType === "#"
                        ? "Enter docket number (e.g. 3447)..."
                        : "Search events, ECF text, keywords..."
                    }
                    style={{
                      width: "100%",
                      padding: "9px 34px 9px 12px",
                      fontSize: 13,
                      background: th.surface,
                      border: `1px solid ${th.border}`,
                      borderRadius: 6,
                      color: th.text,
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: tlDocType === "#" ? "monospace" : "inherit",
                    }}
                  />
                  {tlSearch && (
                    <button
                      onClick={() => setTlSearch("")}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        color: th.dim,
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 3,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  {(tlView === "timeline"
                    ? [
                        ["all", "#a78bfa", "All"],
                        ["factual", "#ef4444", "Key Facts"],
                        ["procedural", "#3b82f6", "Procedural"],
                        ["scheduling", "#10b981", "Scheduling"],
                      ]
                    : []
                  ).map(([k, c, l]) => (
                    <button
                      key={k}
                      onClick={() => {
                        setTlMode(k);
                        setTlIdx(0);
                      }}
                      style={{
                        padding: "4px 12px",
                        fontSize: 11,
                        fontWeight: tlMode === k ? 600 : 400,
                        color: tlMode === k ? c : th.dim,
                        background: tlMode === k ? `${c}15` : "transparent",
                        border: `1px solid ${tlMode === k ? c : th.border}`,
                        borderRadius: 10,
                        cursor: "pointer",
                      }}
                    >
                      {l}
                    </button>
                  ))}
                  {tlView === "timeline" && (
                    <span style={{ margin: "0 6px", color: th.border }}>|</span>
                  )}
                  {(tlView === "docket"
                    ? [
                        ["all", "All"],
                        ["motion", "Motions"],
                        ["order", "Orders"],
                        ["response", "Responses"],
                        ["reply", "Replies"],
                        ["hearing", "Hearings"],
                        ["stipulation", "Stips"],
                        ["complaint", "Pleadings"],
                        ["settlement", "Settlement"],
                        ["#", "By Dkt #"],
                      ]
                    : []
                  ).map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => setTlDocType(k)}
                      style={{
                        padding: "4px 12px",
                        fontSize: 11,
                        fontWeight: tlDocType === k ? 600 : 400,
                        color: tlDocType === k ? "#f59e0b" : th.faint,
                        background:
                          tlDocType === k ? "#f59e0b15" : "transparent",
                        border: `1px solid ${tlDocType === k ? "#f59e0b33" : "transparent"}`,
                        borderRadius: 10,
                        cursor: "pointer",
                      }}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                {tlView === "docket" && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: th.dim,
                        marginBottom: 8,
                        fontFamily: "monospace",
                      }}
                    >
                      ECF DOCKET - {" "}
                      {isIle
                        ? "1:26-cr-00097-NGG"
                        : caseId === "juul"
                          ? "3:19-md-02913-WHO"
                          : caseId === "musk"
                            ? "4:24-cv-04722-YGR"
                            : "2:25-cv-05871-FLA-MAR"}
                    </div>
                    {aD
                      .filter(
                        (d) =>
                          (!tlSearch ||
                            (d.text + String(d.num))
                              .toLowerCase()
                              .includes(tlSearch.toLowerCase())) &&
                          (tlDocType === "all" ||
                            tlDocType === "#" ||
                            (tlDocType === "motion" &&
                              (d.text || "")
                                .toLowerCase()
                                .includes("motion")) ||
                            (tlDocType === "order" &&
                              (d.text || "").toLowerCase().includes("order")) ||
                            (tlDocType === "response" &&
                              ((d.text || "")
                                .toLowerCase()
                                .includes("response") ||
                                (d.text || "")
                                  .toLowerCase()
                                  .includes("opposition") ||
                                (d.text || "")
                                  .toLowerCase()
                                  .includes("opp."))) ||
                            (tlDocType === "reply" &&
                              (d.text || "").toLowerCase().includes("reply")) ||
                            (tlDocType === "hearing" &&
                              (d.text || "")
                                .toLowerCase()
                                .includes("hearing")) ||
                            (tlDocType === "stipulation" &&
                              ((d.text || "")
                                .toLowerCase()
                                .includes("stipul") ||
                                (d.text || "")
                                  .toLowerCase()
                                  .includes("stip"))) ||
                            (tlDocType === "complaint" &&
                              ((d.text || "")
                                .toLowerCase()
                                .includes("complaint") ||
                                (d.text || "")
                                  .toLowerCase()
                                  .includes("pleading") ||
                                (d.text || "")
                                  .toLowerCase()
                                  .includes("amended"))) ||
                            (tlDocType === "settlement" &&
                              (d.text || "")
                                .toLowerCase()
                                .includes("settl"))) &&
                          (tlDocType !== "#" ||
                            String(d.num).includes(tlSearch)),
                      )
                      .map((d, i) => (
                        <div
                          key={i}
                          onClick={() =>
                            openP(
                              `ECF #${d.num} - ${d.text.slice(0, 40)}`,
                              `**ECF #${d.num}**\n${d.date} · ${d.type || "filing"}\n\n${d.text}\n\nTags: ${(d.tags || []).join(", ") || "-"}\n\n---\n**Actions available:**\n• Prepare a response to this filing\n• Search cases cited in this document\n• Download from PACER (${isIle ? "1:26-cr-00097-NGG" : isJuul ? "3:19-md-02913-WHO" : caseId === "musk" ? "4:24-cv-04722-YGR" : "2:25-cv-05871-FLA-MAR"} Dkt.${d.num})`,
                              d.ref,
                            )
                          }
                          style={{
                            background: th.card,
                            borderRadius: 6,
                            border: `1px solid ${th.border}`,
                            padding: "10px 14px",
                            marginBottom: 6,
                            display: "flex",
                            gap: 10,
                            alignItems: "flex-start",
                            cursor: "pointer",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontFamily: "monospace",
                              color: "#f59e0b",
                              fontWeight: 700,
                              flexShrink: 0,
                              minWidth: 42,
                            }}
                          >
                            #{d.num}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 13,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              {d.text}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: th.dim,
                                marginTop: 3,
                              }}
                            >
                              {d.date}
                              {d.tags &&
                                d.tags.map((t) => (
                                  <span
                                    key={t}
                                    style={{
                                      fontSize: 10,
                                      color: "#3b82f6",
                                      background: "#3b82f618",
                                      padding: "2px 6px",
                                      borderRadius: 6,
                                      marginLeft: 4,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {t}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                {tlView === "threads" && (
                  <div>
                    {/* Thread state: expandedSec set, expandedThread set */}
                    {(() => {
                      const threadData = isIle
                        ? ILE_THREADS
                        : caseId === "juul"
                          ? JUUL_THREADS
                          : caseId === "musk"
                            ? MUSK_THREADS
                            : NEURO_THREADS;
                      const filtered = tlSearch
                        ? threadData
                            .map((sec) => ({
                              ...sec,
                              threads: sec.threads.filter(
                                (th) =>
                                  th.id
                                    .toLowerCase()
                                    .includes(tlSearch.toLowerCase()) ||
                                  th.t
                                    .toLowerCase()
                                    .includes(tlSearch.toLowerCase()) ||
                                  th.docs.some(
                                    (d) =>
                                      String(d.d).includes(tlSearch) ||
                                      d.s
                                        .toLowerCase()
                                        .includes(tlSearch.toLowerCase()) ||
                                      d.y
                                        .toLowerCase()
                                        .includes(tlSearch.toLowerCase()),
                                  ),
                              ),
                            }))
                            .filter((sec) => sec.threads.length > 0)
                        : threadData;
                      if (!filtered.length)
                        return (
                          <div
                            style={{
                              padding: "40px",
                              textAlign: "center",
                              color: th.muted,
                              fontSize: 11,
                            }}
                          >
                            No threads match your search.
                          </div>
                        );
                      const typeColor = (y) => {
                        const s = y.toLowerCase();
                        if (s.includes("order") || s.includes("minute"))
                          return { bg: "#D1FAE5", c: "#065F46" };
                        if (
                          s.includes("oppos") ||
                          s === "opp" ||
                          s.includes("opp to")
                        )
                          return { bg: "#FEE2E2", c: "#991B1B" };
                        if (s.includes("reply") || s.includes("suppl"))
                          return { bg: "#FEF3C7", c: "#92400E" };
                        if (
                          s.includes("motion") ||
                          s.includes("msj") ||
                          s.includes("mil") ||
                          s.includes("daubert") ||
                          s.includes("strik") ||
                          s.includes("seal") ||
                          s.includes("compel") ||
                          s.includes("dismiss")
                        )
                          return { bg: "#DBEAFE", c: "#1E40AF" };
                        if (s.includes("stip"))
                          return { bg: "#D1FAE5", c: "#065F46" };
                        if (s.includes("joinder"))
                          return { bg: "#EDE9FE", c: "#5B21B6" };
                        return { bg: "#F1F5F9", c: "#475569" };
                      };
                      return filtered.map((sec, si) => (
                        <div
                          key={sec.id}
                          style={{
                            marginBottom: 6,
                            border: `1px solid ${th.border}`,
                            borderRadius: 8,
                            overflow: "hidden",
                            boxShadow: "0 1px 4px rgba(0,0,0,.06)",
                          }}
                        >
                          <div
                            onClick={() => {
                              const el = document.getElementById(
                                "secbody-" + sec.id,
                              );
                              const ch = document.getElementById(
                                "secchev-" + sec.id,
                              );
                              el.style.display =
                                el.style.display === "none" ? "block" : "none";
                              ch.style.transform =
                                el.style.display === "none"
                                  ? ""
                                  : "rotate(90deg)";
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "9px 14px",
                              cursor: "pointer",
                              background: dark ? "#0F2040" : "#0D1B2E",
                              userSelect: "none",
                            }}
                          >
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 5,
                                background: "#C9A84C",
                                color: "#0D1B2E",
                                fontSize: 10.5,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {sec.l}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#fff",
                                flex: 1,
                              }}
                            >
                              {sec.n}
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                color: "rgba(255,255,255,.5)",
                                background: "rgba(255,255,255,.08)",
                                padding: "2px 10px",
                                borderRadius: 999,
                              }}
                            >
                              {sec.threads.length} thread
                              {sec.threads.length !== 1 ? "s" : ""} ·{" "}
                              {sec.threads.reduce(
                                (a, t) => a + t.docs.length,
                                0,
                              )}{" "}
                              filings
                            </span>
                            <svg
                              id={"secchev-" + sec.id}
                              style={{
                                width: 13,
                                height: 13,
                                color: "rgba(255,255,255,.4)",
                                flexShrink: 0,
                                transition: "transform .2s",
                                transform: "rotate(90deg)",
                              }}
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M6 4l4 4-4 4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                          <div
                            id={"secbody-" + sec.id}
                            style={{ display: "block" }}
                          >
                            {sec.threads.map((th2, ti) => (
                              <div
                                key={th2.id}
                                style={{
                                  borderBottom: `1px solid ${th.border}`,
                                }}
                              >
                                <div
                                  onClick={() => {
                                    const el = document.getElementById(
                                      "thdocs-" + sec.id + "-" + ti,
                                    );
                                    const ch = document.getElementById(
                                      "thchev-" + sec.id + "-" + ti,
                                    );
                                    el.style.display =
                                      el.style.display === "none"
                                        ? "block"
                                        : "none";
                                    ch.style.transform =
                                      el.style.display === "none"
                                        ? ""
                                        : "rotate(90deg)";
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 14px",
                                    cursor: "pointer",
                                    background: th.card,
                                    transition: "background .1s",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#C9A84C",
                                      minWidth: 42,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {th2.id}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      color: th.text,
                                      flex: 1,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {th2.t}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: th.muted,
                                      background: th.surface,
                                      padding: "3px 9px",
                                      borderRadius: 999,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {th2.docs.length} filing
                                    {th2.docs.length !== 1 ? "s" : ""}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openP(
                                        "Thread: " + th2.id + " - " + th2.t,
                                        "**" +
                                          th2.t +
                                          "**\n\nFilings in this thread:\n" +
                                          th2.docs
                                            .map(
                                              (d) =>
                                                "• Dkt." +
                                                d.d +
                                                " (" +
                                                d.y +
                                                ") - " +
                                                d.s +
                                                " [" +
                                                d.f +
                                                "]",
                                            )
                                            .join("\n") +
                                          "\n\nTo download: go to PACER, search case 3:19-md-02913-WHO, click each docket number above.",
                                      );
                                    }}
                                    style={{
                                      fontSize: 12,
                                      background: "#1e40af22",
                                      color: "#3b82f6",
                                      border: "1px solid #3b82f644",
                                      borderRadius: 4,
                                      padding: "4px 10px",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      marginRight: 4,
                                    }}
                                  >
                                    ⬇ All
                                  </button>
                                  <svg
                                    id={"thchev-" + sec.id + "-" + ti}
                                    style={{
                                      width: 12,
                                      height: 12,
                                      color: th.muted,
                                      flexShrink: 0,
                                      transition: "transform .2s",
                                    }}
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <path
                                      d="M6 4l4 4-4 4"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </div>
                                <div
                                  id={"thdocs-" + sec.id + "-" + ti}
                                  style={{ display: "none", background: th.bg }}
                                >
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        {[
                                          ["Dkt #", "60px"],
                                          ["Type", "130px"],
                                          ["Filed", "100px"],
                                          ["Description", ""],
                                        ].map(([h, w]) => (
                                          <th
                                            key={h}
                                            style={{
                                              padding: "7px 14px",
                                              textAlign: "left",
                                              fontSize: 11,
                                              fontWeight: 600,
                                              textTransform: "uppercase",
                                              letterSpacing: ".07em",
                                              color: th.muted,
                                              background: th.surface,
                                              borderBottom: `1px solid ${th.border}`,
                                              width: w || "auto",
                                            }}
                                          >
                                            {h}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {th2.docs.map((doc, di) => {
                                        const tc = typeColor(doc.y);
                                        return (
                                          <tr
                                            key={di}
                                            style={{
                                              background: doc.p
                                                ? "rgba(201,168,76,.05)"
                                                : "transparent",
                                              cursor: "pointer",
                                            }}
                                            onClick={() =>
                                              openP(
                                                "Dkt." + doc.d + " - " + doc.s,
                                                "**Dkt. " +
                                                  doc.d +
                                                  "**\n" +
                                                  doc.f +
                                                  " · " +
                                                  doc.y +
                                                  "\n\n" +
                                                  doc.s +
                                                  "\n\n---\nThread: " +
                                                  th2.id +
                                                  " - " +
                                                  th2.t +
                                                  "\n\n**Actions:**\n• Prepare a response to this filing\n• Search cases cited in this document\n• Download from PACER (3:19-md-02913-WHO Dkt." +
                                                  doc.d +
                                                  ")",
                                                doc.d,
                                              )
                                            }
                                          >
                                            <td
                                              style={{
                                                padding: "8px 14px",
                                                borderBottom: `1px solid ${th.border}40`,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: 13,
                                                  fontWeight: 700,
                                                  color: "#0E4B8A",
                                                }}
                                              >
                                                {doc.d}
                                              </span>
                                              {doc.p && (
                                                <span
                                                  style={{
                                                    color: "#C9A84C",
                                                    fontSize: 12,
                                                    marginLeft: 4,
                                                  }}
                                                >
                                                  ★
                                                </span>
                                              )}
                                            </td>
                                            <td
                                              style={{
                                                padding: "8px 14px",
                                                borderBottom: `1px solid ${th.border}40`,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  display: "inline-block",
                                                  padding: "3px 9px",
                                                  borderRadius: 4,
                                                  fontSize: 12,
                                                  fontWeight: 500,
                                                  background: tc.bg,
                                                  color: tc.c,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {doc.y}
                                              </span>
                                            </td>
                                            <td
                                              style={{
                                                padding: "8px 14px",
                                                borderBottom: `1px solid ${th.border}40`,
                                                fontSize: 12,
                                                color: th.muted,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {doc.f}
                                            </td>
                                            <td
                                              style={{
                                                padding: "8px 14px",
                                                borderBottom: `1px solid ${th.border}40`,
                                                fontSize: 12,
                                                color: th.muted,
                                              }}
                                            >
                                              {doc.s || "-"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
                {tlView === "timeline" && (
                  <div>
                    {" "}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 8,
                        border: `1px solid ${th.border}`,
                        padding: "10px 14px",
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          marginBottom: 8,
                        }}
                      >
                        <button
                          onClick={() => {
                            if (playing) setPlaying(false);
                            else {
                              if (tlIdx >= tlD.length - 1) setTlIdx(0);
                              setPlaying(true);
                            }
                          }}
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: playing ? "#ef4444" : "#1e40af",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {playing ? "⏸" : "▶"}
                        </button>
                        <span
                          style={{
                            fontSize: 8,
                            fontFamily: "monospace",
                            color: th.dim,
                          }}
                        >
                          {Math.min(tlIdx + 1, tlDf.length)}/{tlDf.length}
                        </span>
                      </div>
                      <div style={{ position: "relative", height: 36 }}>
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            left: 0,
                            right: 0,
                            height: 2,
                            background: th.border,
                          }}
                        />
                        {tlDf.map((it, i) => {
                          const p = (i / Math.max(tlDf.length - 1, 1)) * 100,
                            ac = i === Math.min(tlIdx, tlD.length - 1),
                            c = TC[it.type];
                          return (
                            <button
                              key={i}
                              onClick={() => {
                                setTlIdx(i);
                                setPlaying(false);
                              }}
                              style={{
                                position: "absolute",
                                left: `${p}%`,
                                top: ac ? 4 : 8,
                                transform: "translateX(-50%)",
                                width: ac ? 16 : 8,
                                height: ac ? 16 : 8,
                                borderRadius: "50%",
                                background: i <= tlIdx ? c : th.border,
                                border: ac
                                  ? `3px solid ${c}`
                                  : `2px solid ${i <= tlIdx ? c : th.faint}`,
                                cursor: "pointer",
                                transition: "all .3s",
                                zIndex: ac ? 3 : 1,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                    {ci && (
                      <div
                        onClick={() => openP(ci.label, ci.ecf, ci.ref)}
                        style={{
                          background: th.card,
                          borderRadius: 6,
                          borderLeft: `4px solid ${TC[ci.type]}`,
                          padding: "12px 16px",
                          marginBottom: 10,
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontFamily: "monospace",
                            color: TC[ci.type],
                            fontWeight: 600,
                          }}
                        >
                          {ci.date}
                        </span>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            marginTop: 3,
                          }}
                        >
                          {ci.label}
                        </div>
                        {ci.detail && (
                          <p
                            style={{
                              fontSize: 13,
                              lineHeight: 1.6,
                              color: th.muted,
                              margin: "6px 0 0",
                            }}
                          >
                            {ci.detail}
                          </p>
                        )}
                      </div>
                    )}
                    {tlDf.map((it, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setTlIdx(i);
                          openP(it.label, it.ecf, it.ref);
                        }}
                        style={{
                          padding: "7px 12px",
                          background: tlIdx === i ? th.surface : th.card,
                          borderRadius: 5,
                          borderLeft: `3px solid ${tlIdx === i ? TC[it.type] : "transparent"}`,
                          cursor: "pointer",
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: TC[it.type],
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: "monospace",
                            color: th.dim,
                            width: 90,
                            flexShrink: 0,
                          }}
                        >
                          {it.date}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            color: tlIdx === i ? th.text : th.muted,
                            fontWeight: tlIdx === i ? 600 : 400,
                            flex: 1,
                          }}
                        >
                          {it.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* DOCKET tab removed */}
            {/* CHAT */}{" "}
            {tab === "chat" && (
              <div
                style={{
                  height: "calc(100vh - 52px)",
                  display: "flex",
                  flexDirection: "column",
                  background: th.bg,
                }}
              >
                <div
                  style={{
                    padding: "14px 18px 10px",
                    borderBottom: `1px solid ${th.border}`,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>💬</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {isIle
                          ? caseId === "chid"
                            ? "USA v. Chidambaran"
                            : "USA v. Naqvi"
                          : caseId === "juul"
                            ? "Seliavski v. JUUL MDL"
                            : caseId === "musk"
                              ? "Musk v. Altman (OpenAI)"
                              : "Neurocentria v. Mando"}{" "}
 - Case AI
                      </div>
                      <div style={{ fontSize: 8, color: th.dim }}>
                        Ask anything about the case · voice dictate · upload
                        documents
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "14px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {chatMsgs.length === 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginTop: 20,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: th.dim,
                          marginBottom: 4,
                        }}
                      >
                        Suggested questions
                      </div>
                      {(isIle
                        ? [
                            "What is the fastest path to a differentiated resolution for Naqvi before the 9/14 conference?",
                            "How does the most-culpable transcript play against ¶40 at trial?",
                            "What does a Dkt.60 grant actually authorize, and what must happen before travel?",
                            "Which cooperator poses the biggest Bruton problem at a joint trial?",
                          ]
                        : caseId === "juul"
                          ? [
                              "What is the current status of JLI's summary judgment motions?",
                              "What is the Pak deposition and why does it matter for SFUSD damages?",
                              "Explain the RICO enterprise theory against JLI and Altria.",
                              "What happens to opt-out plaintiffs after the SJ ruling?",
                              "What is the strongest argument for Seliavski's personal injury claim?",
                            ]
                          : caseId === "musk"
                            ? [
                                "What does the Kevin Scott email prove about Microsoft's knowledge?",
                                "Explain the Brockman journal entry and why it matters at trial.",
                                "What is the FIFO tracing defense and how does Musk defeat it?",
                                "How does the §17510.8 charitable solicitations issue affect the case?",
                                "What is the strongest argument for statute of limitations dismissal?",
                              ]
                            : [
                                "What is the status of the two pending motions to dismiss?",
                                "Explain the §101 patent eligibility challenge to the '061 patent.",
                                "What is the FAC ¶8 vs ¶13 contradiction and why does it matter?",
                                "What evidence is needed for the Plant-Based Product infringement claim?",
                                "How strong is the Country-of-Origin fraud counterclaim?",
                              ]
                      ).map((q, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setChatInput(q);
                          }}
                          style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            fontSize: 9,
                            background: th.surface,
                            color: th.muted,
                            border: `1px solid ${th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            lineHeight: 1.4,
                          }}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                  {chatMsgs.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        flexDirection: m.r === "user" ? "row-reverse" : "row",
                        gap: 8,
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: m.r === "user" ? "#1e40af" : "#10b98118",
                          border: `1px solid ${m.r === "user" ? "#3b82f6" : "#10b98133"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {m.r === "user" ? "👤" : "⚖️"}
                      </div>
                      <div
                        style={{
                          maxWidth: "78%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        {m.demo && (
                          <span
                            style={{
                              fontSize: 7,
                              color: "#f59e0b",
                              background: "#f59e0b18",
                              border: "1px solid #f59e0b33",
                              padding: "2px 7px",
                              borderRadius: 8,
                              alignSelf:
                                m.r === "user" ? "flex-end" : "flex-start",
                              fontFamily: "monospace",
                              fontWeight: 600,
                              letterSpacing: 0.3,
                            }}
                          >
                            DEMO RESPONSE
                          </span>
                        )}
                        <div
                          style={{
                            padding: "10px 14px",
                            borderRadius: 10,
                            fontSize: 10,
                            lineHeight: 1.65,
                            background: m.r === "user" ? "#1e40af" : th.card,
                            color: m.r === "user" ? "#fff" : th.text,
                            border: `1px solid ${m.r === "user" ? "#3b82f644" : m.demo ? "#f59e0b33" : th.border}`,
                            whiteSpace: "pre-wrap",
                          }}
                          className="cb-md" dangerouslySetInnerHTML={{
                            __html: mdToHtml(m.t) }}
                        />
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "#10b98118",
                          border: "1px solid #10b98133",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                        }}
                      >
                        ⚖️
                      </div>
                      <div
                        style={{
                          padding: "10px 14px",
                          borderRadius: 10,
                          background: th.card,
                          border: `1px solid ${th.border}`,
                          display: "flex",
                          gap: 4,
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: th.dim,
                            animation: "pulse 1s ease infinite",
                          }}
                        />
                        <div
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: th.dim,
                            animation: "pulse 1s ease 0.2s infinite",
                          }}
                        />
                        <div
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: th.dim,
                            animation: "pulse 1s ease 0.4s infinite",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {chatFile && (
                  <div
                    style={{
                      margin: "0 18px 8px",
                      padding: "8px 12px",
                      background: th.surface,
                      border: `1px solid ${th.border}`,
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ fontSize: 8, color: th.muted }}>
                      📎 {chatFile.name}
                    </div>
                    <button
                      onClick={() => setChatFile(null)}
                      style={{
                        fontSize: 9,
                        background: "transparent",
                        border: "none",
                        color: "#ef4444",
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div
                  style={{
                    padding: "10px 18px 16px",
                    borderTop: `1px solid ${th.border}`,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "flex-end" }}
                  >
                    <label
                      title="Upload document"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: th.surface,
                        border: `1px solid ${th.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        fontSize: 14,
                      }}
                    >
                      📎
                      <input
                        type="file"
                        accept=".pdf,.txt,.docx"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (f) setChatFile(f);
                        }}
                      />
                    </label>
                    <button
                      onClick={() =>
                        window.open(
                          "https://www.google.com/search?q=" +
                            encodeURIComponent(
                              (chatInput || "Seliavski v JUUL MDL") +
                                " site:law360.com OR site:courtlistener.com OR site:reuters.com",
                            ),
                          "_blank",
                        )
                      }
                      title="Search Google for more info"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: th.surface,
                        border: `1px solid ${th.border}`,
                        color: "#3b82f6",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                      }}
                    >
                      🔍
                    </button>
                    <button
                      onClick={() => {
                        if (
                          !(
                            "webkitSpeechRecognition" in window ||
                            "SpeechRecognition" in window
                          )
                        ) {
                          alert("Voice not supported in this browser");
                          return;
                        }
                        if (chatListening) {
                          const r = chatRecRef.current;
                          chatRecRef.current = null;
                          if (r) { r.__manualStop = true; try { r.stop(); } catch (_) {} }
                          setChatListening(false);
                          return;
                        }
                        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
                        const rec = new SR();
                        rec.lang = "en-US";
                        rec.continuous = true;
                        rec.interimResults = true;
                        rec.maxAlternatives = 1;
                        rec.onresult = (e) => {
                          let finalTxt = "";
                          for (let i = e.resultIndex; i < e.results.length; i++) {
                            if (e.results[i].isFinal) finalTxt += e.results[i][0].transcript + " ";
                          }
                          if (finalTxt.trim())
                            setChatInput((p) => (p ? p.replace(/\s+$/, "") + " " : "") + finalTxt.trim());
                        };
                        rec.onerror = (e) => {
                          // permission problems end the session; transient errors (no-speech, network) are retried by onend
                          if (e && (e.error === "not-allowed" || e.error === "service-not-allowed" || e.error === "audio-capture")) {
                            rec.__manualStop = true;
                            if (chatRecRef.current === rec) chatRecRef.current = null;
                            setChatListening(false);
                            if (e.error !== "audio-capture") alert("Microphone access is blocked for this site.");
                          }
                        };
                        rec.onend = () => {
                          // Chrome ends a session after silence or ~60s; keep listening until the user stops it
                          if (chatRecRef.current === rec && !rec.__manualStop) {
                            try { rec.start(); return; } catch (_) {}
                          }
                          if (chatRecRef.current === rec) chatRecRef.current = null;
                          setChatListening(false);
                        };
                        chatRecRef.current = rec;
                        rec.start();
                        setChatListening(true);
                      }}
                      title={chatListening ? "Stop recording" : "Voice input"}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: chatListening ? "#ef4444" : th.surface,
                        border: `1px solid ${chatListening ? "#ef4444" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        fontSize: 14,
                        transition: "all 0.2s",
                      }}
                    >
                      {chatListening ? "⏹️" : "🎙️"}
                    </button>
                    <textarea
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          document.getElementById("chat-send-btn").click();
                        }
                      }}
                      placeholder={
                        "Ask anything about " +
                        (isIle
                          ? "the iLearning prosecution - counts, bail posture, cooperators, 9/14 strategy..."
                          : caseId === "juul"
                            ? "the JUUL MDL - theories, evidence, strategy, next steps..."
                            : caseId === "musk"
                              ? "Musk v. Altman - charitable trust, witnesses, trial strategy..."
                              : "the Neurocentria v. Mando case...")
                      }
                      rows={2}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        fontSize: 10,
                        background: th.surface,
                        border: `1px solid ${th.border}`,
                        borderRadius: 8,
                        color: th.text,
                        resize: "none",
                        outline: "none",
                        lineHeight: 1.5,
                        fontFamily: "inherit",
                      }}
                    />
                    <button
                      id="chat-send-btn"
                      disabled={chatLoading || !chatInput.trim()}
                      onClick={async () => {
                        const q = chatInput.trim();
                        if (!q) return;
                        const userMsg = {
                          r: "user",
                          t:
                            q +
                            (chatFile
                              ? " [Attached: " + chatFile.name + "]"
                              : ""),
                        };
                        const newMsgs = [...chatMsgs, userMsg];
                        setChatMsgs(newMsgs);
                        setChatInput("");
                        setChatFile(null);
                        setChatLoading(true);
                        const msgs = newMsgs.map((m) => ({
                          role: m.r === "user" ? "user" : "assistant",
                          content: m.t,
                        }));
                        const result = await aiCall(
                          {
                            model: "claude-sonnet-4-20250514",
                            max_tokens: 1200,
                            system: chatSys,
                            messages: msgs,
                          },
                          () => chatDemoResponse(q, caseId),
                        );
                        setChatMsgs((p) => [
                          ...p,
                          { r: "ai", t: result.text, demo: result.demo },
                        ]);
                        if (result.demo)
                          toast(
                            "Demo mode - start the proxy server for live AI",
                            "warn",
                          );
                        setChatLoading(false);
                      }}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background:
                          chatLoading || !chatInput.trim()
                            ? "#1e40af66"
                            : "#1e40af",
                        color: "#fff",
                        border: "none",
                        cursor:
                          chatLoading || !chatInput.trim()
                            ? "not-allowed"
                            : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      ➤
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: 7,
                      color: th.faint,
                      marginTop: 5,
                      textAlign: "center",
                    }}
                  >
                    Shift+Enter for new line · Powered by CaseBinder AI · Case context
                    included automatically
                  </div>
                </div>
              </div>
            )}{" "}
            {/* ATTORNEYS */}{" "}
            {tab === "attorneys" &&
              (() => {
                const attorneys = isIle
                  ? ILE_ATTORNEYS
                  : caseId === "juul"
                    ? JUUL_ATTORNEYS
                    : caseId === "musk"
                      ? MUSK_ATTORNEYS
                      : MANDO_ATTORNEYS;
                const filtered = attorneys.filter(
                  (a) => clFilter === "all" || a.party === clFilter,
                );
                const totalCount = attorneys.length;
                const caseTitle = isIle
                  ? caseId === "chid"
                    ? "USA v. Chidambaran 1:26-cr-00097-NGG-1 · All appearances"
                    : "USA v. Naqvi 1:26-cr-00097-NGG-2 · All appearances"
                  : caseId === "juul"
                    ? "Seliavski v. JUUL MDL 3:19-md-02913 · All appearances"
                    : caseId === "musk"
                      ? "Musk v. Altman et al 4:24-cv-04722-YGR · All appearances"
                      : "Neurocentria v. Mando 2:25-cv-05871 · All appearances";
                const caseSubject = isIle
                  ? "Re: United States v. Chidambaran et al (1:26-cr-00097-NGG)"
                  : caseId === "juul"
                    ? "Re: Seliavski v. JUUL MDL (3:19-md-02913)"
                    : caseId === "musk"
                      ? "Re: Musk v. Altman et al (4:24-cv-04722-YGR)"
                      : "Re: Neurocentria v. Mando (2:25-cv-05871)";
                const allDef = attorneys
                  .filter((a) => a.party === "Defendant" && a.email)
                  .map((a) => a.email)
                  .join(",");
                const allPlt = attorneys
                  .filter((a) => a.party === "Plaintiff" && a.email)
                  .map((a) => a.email)
                  .join(",");
                const comms = ATTORNEY_COMMS[caseId] || [];
                const gmailConn = emailIntegrations.gmail;
                const outlookConn = emailIntegrations.outlook;
                const isConnected = gmailConn || outlookConn;
                const selectedAttyComms = attySelected
                  ? comms.find((c) => c.attyEmail === attySelected)
                  : null;
                const selectedThread =
                  selectedAttyComms && attyThreadId
                    ? selectedAttyComms.threads.find(
                        (t) => t.id === attyThreadId,
                      )
                    : null;

                return (
                  <div
                    style={{
                      maxWidth: attyView === "comms" ? 1400 : 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg,#0e7490,#155e75)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 7,
                              color: "#cffafe",
                              fontFamily: "monospace",
                              letterSpacing: 1.5,
                              marginBottom: 6,
                              fontWeight: 700,
                            }}
                          >
                            ATTORNEYS · LIVE COMMUNICATION HUB
                          </div>
                          <h1
                            style={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: "#fff",
                              margin: "0 0 6px",
                              letterSpacing: "-0.5px",
                            }}
                          >
                            👥 Attorneys & Counsel
                          </h1>
                          <div style={{ fontSize: 10, color: "#cffafe" }}>
                            {caseTitle} · {totalCount} attorneys on record
                          </div>
                        </div>
                        {isConnected && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "7px 12px",
                              background: "#10b98122",
                              border: "1px solid #10b98155",
                              borderRadius: 7,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 8,
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#10b981",
                                animation: "pulse 1.5s infinite",
                                display: "inline-block",
                              }}
                            />
                            <span
                              style={{
                                fontSize: 9,
                                color: "#fff",
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            >
                              {gmailConn ? "Gmail" : "Outlook"} connected ·
                              syncing every 2 min
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        marginBottom: 14,
                        padding: 5,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        width: "fit-content",
                      }}
                    >
                      {[
                        ["counsel", "👥", "Counsel Roster"],
                        ["comms", "✉️", "Communication Log"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => {
                            setAttyView(k);
                            setAttySelected(null);
                            setAttyThreadId(null);
                          }}
                          style={{
                            padding: "7px 14px",
                            fontSize: 10,
                            fontWeight: attyView === k ? 700 : 500,
                            background:
                              attyView === k ? "#0e7490" : "transparent",
                            color: attyView === k ? "#fff" : th.muted,
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                          {k === "comms" && !isConnected && (
                            <span
                              style={{
                                fontSize: 7,
                                fontFamily: "monospace",
                                color: "#f59e0b",
                                background: "#f59e0b22",
                                padding: "1px 5px",
                                borderRadius: 8,
                                marginLeft: 3,
                              }}
                            >
                              connect email
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {attyView === "counsel" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            marginBottom: 14,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              fontWeight: 600,
                              letterSpacing: 1,
                              fontFamily: "monospace",
                              marginRight: 4,
                            }}
                          >
                            FILTER
                          </span>
                          {[
                            ["all", "⚖️", "All Counsel", ""],
                            ["Plaintiff", "🔴", "Plaintiff Counsel", "#ef4444"],
                            ["Defendant", "🔵", "Defense Counsel", "#3b82f6"],
                          ].map(([k, e, l, c]) => (
                            <button
                              key={k}
                              onClick={() => setClFilter(k)}
                              style={{
                                padding: "7px 13px",
                                fontSize: 10,
                                fontWeight: clFilter === k ? 700 : 500,
                                background:
                                  clFilter === k ? c || "#0e7490" : th.surface,
                                color: clFilter === k ? "#fff" : th.muted,
                                border: `1px solid ${clFilter === k ? c || "#0e7490" : th.border}`,
                                borderRadius: 6,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <span>{e}</span>
                              <span>{l}</span>
                            </button>
                          ))}
                          <div style={{ flex: 1 }} />
                          <span
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              fontWeight: 600,
                              letterSpacing: 1,
                              fontFamily: "monospace",
                              marginRight: 4,
                            }}
                          >
                            BULK EMAIL
                          </span>
                          <a
                            href={
                              "mailto:" +
                              allPlt +
                              "?subject=" +
                              encodeURIComponent(caseSubject)
                            }
                            style={{
                              padding: "7px 12px",
                              fontSize: 9,
                              fontWeight: 700,
                              background: "#ef4444",
                              color: "#fff",
                              borderRadius: 6,
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            ✉️ All Plaintiff Counsel
                          </a>
                          <a
                            href={
                              "mailto:" +
                              allDef +
                              "?subject=" +
                              encodeURIComponent(caseSubject)
                            }
                            style={{
                              padding: "7px 12px",
                              fontSize: 9,
                              fontWeight: 700,
                              background: "#3b82f6",
                              color: "#fff",
                              borderRadius: 6,
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            ✉️ All Defense Counsel
                          </a>
                        </div>

                        {selectedCounsel.length > 0 && (
                          <div
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                              padding: "10px 14px",
                              background: dark ? "#1e1b4b" : "#dbeafe",
                              border: "1px solid #3b82f655",
                              borderRadius: 8,
                              marginBottom: 12,
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span style={{ fontSize: 14 }}>✉️</span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: th.text,
                              }}
                            >
                              {selectedCounsel.length} attorney
                              {selectedCounsel.length === 1 ? "" : "s"} selected
                            </span>
                            <div style={{ flex: 1 }} />
                            <a
                              href={
                                "mailto:" +
                                selectedCounsel.join(",") +
                                "?subject=" +
                                encodeURIComponent(caseSubject)
                              }
                              style={{
                                padding: "6px 14px",
                                fontSize: 9,
                                fontWeight: 700,
                                background: "#3b82f6",
                                color: "#fff",
                                borderRadius: 5,
                                textDecoration: "none",
                              }}
                            >
                              Email Selected
                            </a>
                            <button
                              onClick={() => setSelectedCounsel([])}
                              style={{
                                padding: "6px 10px",
                                fontSize: 9,
                                background: "transparent",
                                color: th.muted,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                cursor: "pointer",
                              }}
                            >
                              Clear
                            </button>
                          </div>
                        )}

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill,minmax(290px,1fr))",
                            gap: 11,
                          }}
                        >
                          {filtered.map((a, i) => {
                            const checked =
                              a.email && selectedCounsel.includes(a.email);
                            const sideC =
                              a.party === "Plaintiff" ? "#ef4444" : "#3b82f6";
                            return (
                              <div
                                key={i}
                                style={{
                                  background: th.card,
                                  borderRadius: 10,
                                  border: `2px solid ${checked ? "#0e7490" : sideC + "33"}`,
                                  padding: "13px 15px",
                                  position: "relative",
                                }}
                              >
                                {a.email && (
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) =>
                                      setSelectedCounsel((p) =>
                                        e.target.checked
                                          ? [...p, a.email]
                                          : p.filter((x) => x !== a.email),
                                      )
                                    }
                                    style={{
                                      position: "absolute",
                                      top: 11,
                                      right: 11,
                                      width: 15,
                                      height: 15,
                                      cursor: "pointer",
                                      accentColor: "#0e7490",
                                    }}
                                  />
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 7,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      fontWeight: 700,
                                      color: sideC,
                                      background: sideC + "18",
                                      padding: "3px 7px",
                                      borderRadius: 8,
                                      letterSpacing: 0.8,
                                    }}
                                  >
                                    {a.party.toUpperCase()}
                                  </span>
                                  {a.bar && (
                                    <span
                                      style={{
                                        fontSize: 7,
                                        color: th.faint,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {a.bar}
                                    </span>
                                  )}
                                  {a.phv && (
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#a78bfa",
                                        background: "#a78bfa18",
                                        padding: "2px 6px",
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      PHV
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    marginBottom: 2,
                                    paddingRight: 24,
                                    letterSpacing: "-0.2px",
                                  }}
                                >
                                  {a.name}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: "#3b82f6",
                                    fontWeight: 600,
                                    marginBottom: 2,
                                  }}
                                >
                                  {a.firm}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.dim,
                                    fontStyle: "italic",
                                    marginBottom: 9,
                                  }}
                                >
                                  {a.role}
                                </div>
                                {(a.email || a.phone) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      marginBottom: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {a.email && (
                                      <a
                                        href={"mailto:" + a.email}
                                        style={{
                                          fontSize: 9,
                                          color: "#0e7490",
                                          textDecoration: "none",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        ✉️ {a.email}
                                      </a>
                                    )}
                                    {a.phone && (
                                      <span
                                        style={{
                                          fontSize: 9,
                                          color: th.dim,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 3,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        📞 {a.phone}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    lineHeight: 1.6,
                                    marginBottom: 9,
                                  }}
                                >
                                  {a.involvement}
                                </div>
                                {a.dkts && a.dkts.length > 0 && (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 4,
                                      marginBottom: 8,
                                    }}
                                  >
                                    {a.dkts.map((d, j) => (
                                      <span
                                        key={j}
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color: "#f59e0b",
                                          background: "#f59e0b15",
                                          border: "1px solid #f59e0b33",
                                          padding: "2px 6px",
                                          borderRadius: 4,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {d}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {a.email && (
                                  <a
                                    href={
                                      "mailto:" +
                                      a.email +
                                      "?subject=" +
                                      encodeURIComponent(caseSubject)
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      padding: "6px 11px",
                                      fontSize: 9,
                                      background: "#0e7490",
                                      color: "#fff",
                                      borderRadius: 5,
                                      textDecoration: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ✉️ Email {a.name.split(" ")[0]}
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {attyView === "comms" && (
                      <>
                        {!isConnected && (
                          <div
                            style={{
                              padding: "32px 24px",
                              textAlign: "center",
                              background: th.card,
                              borderRadius: 11,
                              border: `1px dashed ${th.border}`,
                              marginBottom: 14,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 36,
                                opacity: 0.4,
                                marginBottom: 10,
                              }}
                            >
                              ✉️
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: th.text,
                                marginBottom: 6,
                              }}
                            >
                              Connect your email to use the Communication Log
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.muted,
                                maxWidth: 480,
                                margin: "0 auto 16px",
                                lineHeight: 1.6,
                              }}
                            >
                              The Communication Log syncs your email threads
                              with each attorney on this case, extracts
                              deadlines and obligations from message text, and
                              surfaces commitments. Connect Gmail or Outlook in
                              Settings.
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                justifyContent: "center",
                              }}
                            >
                              <button
                                onClick={() => {
                                  setEmailIntegrations((p) => ({
                                    ...p,
                                    gmail: {
                                      email: "raj@patentvc.com",
                                      lastSync: "just now",
                                    },
                                  }));
                                  toast("✓ Gmail connected (demo)", "success");
                                }}
                                style={{
                                  padding: "9px 18px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: "#ea4335",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                📧 Connect Gmail
                              </button>
                              <button
                                onClick={() => {
                                  setEmailIntegrations((p) => ({
                                    ...p,
                                    outlook: {
                                      email: "raj@patentvc.com",
                                      lastSync: "just now",
                                    },
                                  }));
                                  toast(
                                    "✓ Outlook connected (demo)",
                                    "success",
                                  );
                                }}
                                style={{
                                  padding: "9px 18px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: "#0078d4",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                📧 Connect Outlook
                              </button>
                            </div>
                          </div>
                        )}
                        {isConnected && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "260px 320px 1fr",
                              gap: 10,
                              height: "calc(100vh - 240px)",
                              minHeight: 500,
                            }}
                          >
                            <div
                              style={{
                                background: th.card,
                                borderRadius: 9,
                                border: `1px solid ${th.border}`,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <div
                                style={{
                                  padding: "10px 13px",
                                  borderBottom: `1px solid ${th.border}`,
                                  background: th.surface,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: th.text,
                                    marginBottom: 2,
                                  }}
                                >
                                  Attorneys
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {comms.length} with threads ·{" "}
                                  {comms.reduce(
                                    (a, c) =>
                                      a +
                                      c.threads.filter((t) => t.unread).length,
                                    0,
                                  )}{" "}
                                  unread
                                </div>
                              </div>
                              <div style={{ flex: 1, overflow: "auto" }}>
                                {comms.map((c) => {
                                  const atty = attorneys.find(
                                    (a) => a.email === c.attyEmail,
                                  );
                                  if (!atty) return null;
                                  const isOpen = attySelected === c.attyEmail;
                                  const unread = c.threads.filter(
                                    (t) => t.unread,
                                  ).length;
                                  return (
                                    <div
                                      key={c.attyEmail}
                                      onClick={() => {
                                        setAttySelected(c.attyEmail);
                                        setAttyThreadId(null);
                                      }}
                                      style={{
                                        padding: "10px 13px",
                                        cursor: "pointer",
                                        background: isOpen
                                          ? dark
                                            ? "#1e3a5f"
                                            : "#dbeafe"
                                          : "transparent",
                                        borderBottom: `1px solid ${th.border}`,
                                        borderLeft: `3px solid ${atty.party === "Plaintiff" ? "#ef4444" : "#3b82f6"}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          marginBottom: 2,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: th.text,
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {atty.name}
                                        </span>
                                        {unread > 0 && (
                                          <span
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#fff",
                                              background: "#dc2626",
                                              padding: "1px 6px",
                                              borderRadius: 8,
                                              fontWeight: 700,
                                            }}
                                          >
                                            {unread}
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          color: th.muted,
                                          fontFamily: "monospace",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {atty.firm}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          color: th.faint,
                                          marginTop: 2,
                                        }}
                                      >
                                        {c.threads.length} thread
                                        {c.threads.length === 1 ? "" : "s"}
                                      </div>
                                    </div>
                                  );
                                })}
                                {comms.length === 0 && (
                                  <div
                                    style={{
                                      padding: "24px 16px",
                                      textAlign: "center",
                                      fontSize: 9,
                                      color: th.muted,
                                    }}
                                  >
                                    No synced threads yet for this case.
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  padding: "8px 13px",
                                  borderTop: `1px solid ${th.border}`,
                                  background: th.surface,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 8,
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background: "#10b981",
                                    animation: "pulse 1.5s infinite",
                                    display: "inline-block",
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  Sync · 2min interval
                                </span>
                              </div>
                            </div>

                            <div
                              style={{
                                background: th.card,
                                borderRadius: 9,
                                border: `1px solid ${th.border}`,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <div
                                style={{
                                  padding: "10px 13px",
                                  borderBottom: `1px solid ${th.border}`,
                                  background: th.surface,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: th.text,
                                  }}
                                >
                                  {selectedAttyComms
                                    ? attorneys.find(
                                        (a) =>
                                          a.email ===
                                          selectedAttyComms.attyEmail,
                                      )?.name || "Threads"
                                    : "Threads"}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {selectedAttyComms
                                    ? selectedAttyComms.threads.length +
                                      " thread" +
                                      (selectedAttyComms.threads.length === 1
                                        ? ""
                                        : "s")
                                    : "Select an attorney"}
                                </div>
                              </div>
                              <div style={{ flex: 1, overflow: "auto" }}>
                                {selectedAttyComms ? (
                                  selectedAttyComms.threads.map((t) => {
                                    const isOpen = attyThreadId === t.id;
                                    const typeC =
                                      {
                                        "meet-confer": "#f59e0b",
                                        settlement: "#10b981",
                                        discovery: "#3b82f6",
                                        scheduling: "#a78bfa",
                                      }[t.type] || "#6b7280";
                                    return (
                                      <div
                                        key={t.id}
                                        onClick={() => setAttyThreadId(t.id)}
                                        style={{
                                          padding: "11px 13px",
                                          cursor: "pointer",
                                          background: isOpen
                                            ? dark
                                              ? "#1e3a5f"
                                              : "#dbeafe"
                                            : "transparent",
                                          borderBottom: `1px solid ${th.border}`,
                                          borderLeft: `3px solid ${typeC}`,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: 4,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: typeC,
                                              background: typeC + "18",
                                              padding: "2px 6px",
                                              borderRadius: 8,
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              textTransform: "uppercase",
                                            }}
                                          >
                                            {t.type}
                                          </span>
                                          {t.unread && (
                                            <span
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#fff",
                                                background: "#dc2626",
                                                padding: "1px 5px",
                                                borderRadius: 8,
                                                fontWeight: 700,
                                              }}
                                            >
                                              NEW
                                            </span>
                                          )}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: th.text,
                                            marginBottom: 2,
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {t.subject}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.muted,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          {t.date} · {t.messages.length} message
                                          {t.messages.length === 1 ? "" : "s"}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.muted,
                                            lineHeight: 1.5,
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                          }}
                                        >
                                          {t.messages[
                                            t.messages.length - 1
                                          ].body.slice(0, 140)}
                                          …
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div
                                    style={{
                                      padding: "24px 16px",
                                      textAlign: "center",
                                      fontSize: 9,
                                      color: th.muted,
                                    }}
                                  >
                                    {comms.length > 0
                                      ? "Select an attorney to view threads."
                                      : "No threads available."}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              style={{
                                background: th.card,
                                borderRadius: 9,
                                border: `1px solid ${th.border}`,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {selectedThread ? (
                                <>
                                  <div
                                    style={{
                                      padding: "12px 16px",
                                      borderBottom: `1px solid ${th.border}`,
                                      background: th.surface,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {selectedThread.subject}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {selectedThread.date} ·{" "}
                                      {selectedThread.messages.length} message
                                      {selectedThread.messages.length === 1
                                        ? ""
                                        : "s"}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      overflow: "auto",
                                      padding: "14px 18px",
                                    }}
                                  >
                                    {selectedThread.messages.map((m, mi) => (
                                      <div
                                        key={mi}
                                        style={{
                                          marginBottom: 14,
                                          padding: "11px 14px",
                                          background: th.surface,
                                          borderRadius: 7,
                                          borderLeft: `3px solid ${mi === selectedThread.messages.length - 1 ? "#10b981" : "#6b7280"}`,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 6,
                                            fontSize: 9,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: th.text,
                                              fontWeight: 700,
                                            }}
                                          >
                                            {m.from}
                                          </span>
                                          <span style={{ color: th.muted }}>
                                            {m.date}
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.muted,
                                            fontFamily: "monospace",
                                            marginBottom: 7,
                                          }}
                                        >
                                          To: {m.to}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.7,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {m.body}
                                        </div>
                                      </div>
                                    ))}
                                    {selectedThread.aiObligations &&
                                      selectedThread.aiObligations.length >
                                        0 && (
                                        <div
                                          style={{
                                            marginTop: 14,
                                            padding: "12px 16px",
                                            background: dark
                                              ? "#1e1b4b"
                                              : "#f5f3ff",
                                            borderRadius: 8,
                                            border: "1px solid #a78bfa55",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#a78bfa",
                                              fontWeight: 700,
                                              letterSpacing: 1.2,
                                              marginBottom: 8,
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 6,
                                            }}
                                          >
                                            <span style={{ fontSize: 11 }}>
                                              🤖
                                            </span>
                                            <span>
                                              AI ANALYSIS · EXTRACTED
                                              OBLIGATIONS
                                            </span>
                                          </div>
                                          {selectedThread.aiObligations.map(
                                            (o, oi) => (
                                              <div
                                                key={oi}
                                                style={{
                                                  padding: "9px 12px",
                                                  background: th.card,
                                                  borderRadius: 6,
                                                  marginBottom: 6,
                                                  borderLeft:
                                                    "2px solid #a78bfa",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    marginBottom: 4,
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontSize: 7,
                                                      fontFamily: "monospace",
                                                      color: "#a78bfa",
                                                      background: "#a78bfa15",
                                                      padding: "2px 7px",
                                                      borderRadius: 8,
                                                      fontWeight: 700,
                                                      textTransform:
                                                        "uppercase",
                                                    }}
                                                  >
                                                    {o.who}
                                                  </span>
                                                  <span
                                                    style={{
                                                      fontSize: 7,
                                                      fontFamily: "monospace",
                                                      color: "#dc2626",
                                                      background: "#dc262615",
                                                      padding: "2px 7px",
                                                      borderRadius: 8,
                                                      fontWeight: 700,
                                                    }}
                                                  >
                                                    ⏰ {o.deadline}
                                                  </span>
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 10,
                                                    color: th.text,
                                                    lineHeight: 1.55,
                                                    marginBottom: 6,
                                                  }}
                                                >
                                                  {o.obligation}
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    toast(
                                                      "✓ Added to War Room countdown checklist",
                                                      "success",
                                                    );
                                                  }}
                                                  style={{
                                                    padding: "4px 10px",
                                                    fontSize: 8,
                                                    fontWeight: 700,
                                                    background: "#a78bfa",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: 5,
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  ＋ Add to War Room
                                                </button>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    {selectedThread.aiFlags &&
                                      selectedThread.aiFlags.length > 0 && (
                                        <div
                                          style={{
                                            marginTop: 11,
                                            padding: "11px 14px",
                                            background: "#f59e0b15",
                                            borderRadius: 7,
                                            borderLeft: "3px solid #f59e0b",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#f59e0b",
                                              fontWeight: 700,
                                              letterSpacing: 1.2,
                                              marginBottom: 6,
                                            }}
                                          >
                                            ⚠ AI FLAGS
                                          </div>
                                          {selectedThread.aiFlags.map(
                                            (f, fi) => (
                                              <div
                                                key={fi}
                                                style={{
                                                  fontSize: 9,
                                                  color: th.text,
                                                  padding: "3px 0",
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                · {f}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                  </div>
                                </>
                              ) : (
                                <div
                                  style={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: th.muted,
                                    fontSize: 10,
                                    padding: 24,
                                    textAlign: "center",
                                  }}
                                >
                                  Select a thread to view full conversation and
                                  AI analysis.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}{" "}
            {/* ASSETS */}
            {tab === "assets" &&
              (() => {
                const data = ASSETS_INTEL[caseId] || ASSETS_INTEL.juul;
                const allDocsRaw = [
                  ...data.docs,
                  ...(customAssets[caseId] || []),
                ];
                const docs = allDocsRaw.filter((d) => !hiddenDocs[d.id]);
                const sel = selectedDocId
                  ? docs.find((d) => d.id === selectedDocId)
                  : null;
                const pinOverride = pinnedDocs[caseId] || {};
                const defaultPinnedDoc = (d) => d.classification === "pin" || (data.hot || []).includes(d.id);
                const isPinned = (d) => (pinOverride[d.id] !== undefined ? pinOverride[d.id] : defaultPinnedDoc(d));
                const togglePin = (d) => setPinnedDocs((p) => ({ ...p, [caseId]: { ...(p[caseId] || {}), [d.id]: !isPinned(d) } }));
                const TYPES = [
                  { k: "pin", icon: "📌", label: "Pinned" },
                  { k: "local-rule", icon: "📏", label: "Local Rule" },
                  { k: "depo", icon: "📝", label: "Depo Transcript" },
                  { k: "hearing", icon: "🎙️", label: "Hearing Transcript" },
                  { k: "interview", icon: "🗣️", label: "Interview" },
                  { k: "filing", icon: "📄", label: "Filing" },
                  { k: "exhibit", icon: "📎", label: "Exhibit" },
                  { k: "photo", icon: "📷", label: "Photo" },
                  { k: "video", icon: "🎬", label: "Video" },
                  { k: "audio", icon: "🔊", label: "Audio" },
                  { k: "other", icon: "📋", label: "Other" },
                ];
                const typeMeta = Object.fromEntries(TYPES.map((t) => [t.k, t])); const docType = (d) => (d.type && typeMeta[d.type] && d.type !== "pin" ? d.type : "other");
                const cFilter =
                  assetsClassFilter === "all" ? "pin" : assetsClassFilter;
                const matchSearch = (d) => {
                  if (!assetsSearch) return true;
                  const q = assetsSearch.toLowerCase();
                  return [
                    d.title,
                    d.bates || "",
                    d.producer || "",
                    d.exhibit || "",
                    (d.ai && d.ai.summary) || "",
                    (d.theoryMaps || []).map((t) => t.theory).join(" "),
                  ]
                    .join(" ")
                    .toLowerCase()
                    .includes(q);
                };
                const filteredDocs =
                  cFilter === "pin"
                    ? docs.filter((d) => isPinned(d) && matchSearch(d))
                    : docs.filter(
                        (d) => docType(d) === cFilter && matchSearch(d),
                      );
                const counts = Object.fromEntries(
                  TYPES.map((t) => [
                    t.k,
                    t.k === "pin"
                      ? docs.filter((d) => isPinned(d)).length
                      : docs.filter((d) => docType(d) === t.k).length,
                  ]),
                );
                const cMeta = {
                  pin: {
                    label: "PINNED",
                    icon: "📌",
                    color: "#eab308",
                    desc: "Most important - case-dispositive evidence.",
                  },
                  foundation: {
                    label: "FOUNDATION",
                    icon: "🧱",
                    color: "#3b82f6",
                    desc: "Background facts not contested but must be in evidence",
                  },
                  impeachment: {
                    label: "IMPEACHMENT",
                    icon: "⚔️",
                    color: "#dc2626",
                    desc: "Material for cross-examination - distinct from substantive evidence",
                  },
                  "expert-support": {
                    label: "EXPERT SUPPORT",
                    icon: "🎓",
                    color: "#a78bfa",
                    desc: "Materials experts rely on - admissibility separate from underlying",
                  },
                  "prior-art": {
                    label: "PRIOR ART",
                    icon: "📜",
                    color: "#0891b2",
                    desc: "Patent prior art (patent cases only)",
                  },
                };
                const authStatusC = {
                  green: "#10b981",
                  amber: "#f59e0b",
                  red: "#dc2626", pending: "#a78bfa",
                };
                const impactC = {
                  red: "#dc2626",
                  amber: "#f59e0b",
                  green: "#10b981",
                  neutral: "#6b7280",
                };
                const handleDelete = (d) => {
                  setConfirmRemove({
                    name: d.title,
                    action: () => {
                      if (d.id && String(d.id).startsWith("ca-")) {
                        setCustomAssets((p) => {
                          const arr = (p[caseId] || []).filter(
                            (x) => x.id !== d.id,
                          );
                          return { ...p, [caseId]: arr };
                        });
                      } else {
                        setHiddenDocs((p) => ({ ...p, [d.id]: true }));
                      }
                      if (selectedDocId === d.id) setSelectedDocId(null);
                    },
                  });
                };

                return (
                  <div
                    style={{
                      maxWidth: 1400,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg,#7c3aed,#5b21b6)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#e9d5ff",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        ASSETS · EVIDENTIARY COMMAND CENTER
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Assets
                      </h1>
                      <div style={{ fontSize: 10, color: "#e9d5ff" }}>
                        {docs.length} documents · search + filter by type · pin
                        · upload · delete
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 14,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          flex: 1,
                          minWidth: 240,
                          maxWidth: 420,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 11,
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: 13,
                            color: th.muted,
                            pointerEvents: "none",
                          }}
                        >
                          🔍
                        </span>
                        <input
                          value={assetsSearch}
                          onChange={(e) => setAssetsSearch(e.target.value)}
                          placeholder="Search title, bates, producer, exhibit #, summary..."
                          style={{
                            width: "100%",
                            padding: "8px 32px 8px 32px",
                            fontSize: 11,
                            background: th.surface,
                            border: `1px solid ${th.border}`,
                            borderRadius: 7,
                            color: th.text,
                            boxSizing: "border-box",
                            outline: "none",
                          }}
                        />
                        {assetsSearch && (
                          <button
                            onClick={() => setAssetsSearch("")}
                            style={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "transparent",
                              border: "none",
                              color: th.muted,
                              cursor: "pointer",
                              fontSize: 13,
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setAssetEditMode((m) => !m)}
                        style={{
                          padding: "8px 13px",
                          fontSize: 10,
                          fontWeight: 600,
                          background: assetEditMode ? "#f59e0b" : th.surface,
                          color: assetEditMode ? "#fff" : th.muted,
                          border: `1px solid ${assetEditMode ? "#f59e0b" : th.border}`,
                          borderRadius: 7,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        {assetEditMode ? "✓ Done" : "✏️ Edit / Delete"}
                      </button>
                      <button
                        onClick={() => {
                          setAssetUploadDraft({
                            title: "",
                            desc: "",
                            date: "",
                            classification: "pin",
                            exhibit: "",
                            type: "exhibit",
                          });
                          setUploadAssoc({ wit: "", loc: "" });
                          setShowUpload(true);
                        }}
                        style={{
                          padding: "8px 13px",
                          fontSize: 10,
                          fontWeight: 700,
                          background: "linear-gradient(135deg,#1e40af,#7c3aed)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 7,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          boxShadow: "0 1px 3px rgba(124,58,237,0.3)",
                        }}
                      >
                        <span style={{ fontSize: 13 }}>＋</span>
                        <span>Upload</span>
                      </button>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        marginBottom: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      {TYPES.map((t) => {
                        const active = cFilter === t.k;
                        const cnt = counts[t.k];
                        const c = t.k === "pin" ? "#eab308" : "#7c3aed";
                        return (
                          <button
                            key={t.k}
                            onClick={() => {
                              setAssetsClassFilter(t.k);
                              setSelectedDocId(null);
                            }}
                            style={{
                              padding: "6px 11px",
                              fontSize: 9,
                              fontWeight: active ? 700 : 500,
                              background: active ? c : th.surface,
                              color: active ? "#fff" : th.muted,
                              border: `1px solid ${active ? c : th.border}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              opacity: cnt === 0 && !active ? 0.5 : 1,
                            }}
                          >
                            <span>{t.icon}</span>
                            <span>{t.label}</span>
                            <span
                              style={{
                                fontSize: 8,
                                opacity: 0.75,
                                fontFamily: "monospace",
                                padding: "1px 5px",
                                background: active
                                  ? "rgba(255,255,255,0.22)"
                                  : th.bg,
                                borderRadius: 8,
                                fontWeight: 700,
                              }}
                            >
                              {cnt}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {filteredDocs.length === 0 ? (
                      <div
                        style={{
                          padding: "36px 24px",
                          textAlign: "center",
                          background: th.card,
                          borderRadius: 11,
                          border: `1px dashed ${th.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 32,
                            opacity: 0.4,
                            marginBottom: 8,
                          }}
                        >
                          {typeMeta[cFilter] ? typeMeta[cFilter].icon : "📋"}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: th.text,
                            marginBottom: 6,
                          }}
                        >
                          {assetsSearch
                            ? "No matches"
                            : `No ${typeMeta[cFilter] ? typeMeta[cFilter].label.toLowerCase() : "items"} yet`}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.muted,
                            maxWidth: 340,
                            margin: "0 auto",
                            lineHeight: 1.55,
                          }}
                        >
                          {assetsSearch
                            ? "Try clearing the search, or pick a different type."
                            : cFilter === "pin"
                              ? "Open a category and tap ☆ on any document to pin it here. Or click Upload to add a new document."
                              : "No items in this category yet. Click Upload to add one."}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill,minmax(320px,1fr))",
                          gap: 10,
                        }}
                      >
                        {filteredDocs.map((d) => {
                          const cls =
                            cMeta[d.classification] || cMeta.foundation;
                          const aC = authStatusC[d.auth.status];
                          
                          const tm = typeMeta[docType(d)] || typeMeta.other;
                          return (
                            <div
                              key={d.id}
                              onClick={(e) => {
                                if (
                                  e.target.closest(".star-btn") ||
                                  e.target.closest(".del-btn")
                                )
                                  return;
                                setSelectedDocId(d.id);
                                setDocSubTab("ai");
                              }}
                              style={{
                                background: th.card,
                                borderRadius: 10,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${cls.color}`,
                                padding: "12px 14px",
                                cursor: "pointer",
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 9,
                                  right: 9,
                                  display: "flex",
                                  gap: 4,
                                  zIndex: 2,
                                }}
                              >
                                {assetEditMode && (
                                  <button
                                    className="del-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(d);
                                    }}
                                    title="Delete"
                                    style={{
                                      width: 25,
                                      height: 25,
                                      fontSize: 12,
                                      lineHeight: 1,
                                      background: "#dc262615",
                                      border: "1px solid #dc262655",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      color: "#dc2626",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: 700,
                                    }}
                                  >
                                    ✕
                                  </button>
                                )}
                                <button
                                  className="star-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePin(d);
                                    toast(
                                      isPinned(d)
                                        ? `Unpinned`
                                        : `Pinned ⭐`,
                                      "success",
                                    );
                                  }}
                                  title={
                                    isPinned(d)
                                      ? "Unpin"
                                      : "Pin"
                                  }
                                  style={{
                                    width: 25,
                                    height: 25,
                                    fontSize: 14,
                                    lineHeight: 1,
                                    background:
                                      isPinned(d)
                                        ? "#eab30822"
                                        : "transparent",
                                    border: `1px solid ${isPinned(d) ? "#eab30855" : th.border}`,
                                    borderRadius: 5,
                                    cursor: "pointer",
                                    color:
                                      isPinned(d)
                                        ? "#eab308"
                                        : th.muted,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isPinned(d) ? "⭐" : "☆"}
                                </button>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  marginBottom: 6,
                                  flexWrap: "wrap",
                                  paddingRight: assetEditMode ? 60 : 32,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: cls.color,
                                    background: cls.color + "18",
                                    padding: "2px 7px",
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    letterSpacing: 0.6,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 3,
                                  }}
                                >
                                  <span>{cls.icon}</span>
                                  <span>{cls.label}</span>
                                </span>
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: "#7c3aed",
                                    background: "#7c3aed18",
                                    padding: "2px 7px",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    letterSpacing: 0.5,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 3,
                                  }}
                                >
                                  <span>{tm.icon}</span>
                                  <span>{tm.label}</span>
                                </span>
                                <span
                                  title={
                                    d.auth.status === "green"
                                      ? "Authenticated"
                                      : d.auth.status === "amber"
                                        ? "Authentication contested"
                                        : "No foundation witness"
                                  }
                                  style={{
                                    display: "inline-block",
                                    width: 9,
                                    height: 9,
                                    borderRadius: "50%",
                                    background: aC,
                                    boxShadow: `0 0 0 2px ${aC}33`,
                                  }}
                                />
                                {d.exhibit && d.exhibit !== "PENDING" && (
                                  <span
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#3b82f6",
                                      background: "#3b82f618",
                                      padding: "2px 6px",
                                      borderRadius: 5,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {d.exhibit}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: th.text,
                                  letterSpacing: "-0.1px",
                                  lineHeight: 1.4,
                                  marginBottom: 4,
                                }}
                              >
                                {d.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  fontFamily: "monospace",
                                  marginBottom: 6,
                                }}
                              >
                                {d.bates} · {d.date}
                                {d.pages > 0 ? " · " + d.pages + "pp" : ""}
                                {d.cluster
                                  ? " · cluster of " + (d.clusterCount || 0)
                                  : ""}
                              </div>
                              {d.ai && d.ai.summary && (
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.text,
                                    lineHeight: 1.55,
                                    paddingTop: 6,
                                    borderTop: `1px solid ${th.border}`,
                                  }}
                                >
                                  {d.ai.summary.slice(0, 150)}
                                  {d.ai.summary.length > 150 ? "…" : ""}
                                </div>
                              )}
                              {d.theoryMaps && d.theoryMaps.length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 3,
                                    marginTop: 7,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {d.theoryMaps.slice(0, 2).map((tmm, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: impactC[tmm.impact] || "#999",
                                        background:
                                          (impactC[tmm.impact] || "#999") +
                                          "18",
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        fontWeight: 600,
                                      }}
                                    >
                                      · {tmm.element || tmm.theory.slice(0, 18)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {sel && (
                      <div
                        style={{
                          position: "fixed",
                          top: 0,
                          right: 0,
                          width: 580,
                          height: "100vh",
                          background: th.card,
                          borderLeft: `1px solid ${th.border}`,
                          boxShadow: "-4px 0 24px rgba(0,0,0,0.25)",
                          zIndex: 100,
                          overflow: "auto",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {(() => {
                          const cls =
                            cMeta[sel.classification] || cMeta.foundation;
                          const aC = authStatusC[sel.auth.status];
                          const tm = typeMeta[docType(sel)] || typeMeta.other;
                          return (
                            <>
                              <div
                                style={{
                                  padding: "14px 18px",
                                  borderBottom: `1px solid ${th.border}`,
                                  flexShrink: 0,
                                  background: cls.color + "08",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 7,
                                    marginBottom: 7,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: cls.color,
                                      background: cls.color + "18",
                                      padding: "3px 9px",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      letterSpacing: 0.8,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <span>{cls.icon}</span>
                                    <span>{cls.label}</span>
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#7c3aed",
                                      background: "#7c3aed18",
                                      padding: "3px 9px",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 3,
                                    }}
                                  >
                                    <span>{tm.icon}</span>
                                    <span>{tm.label}</span>
                                  </span>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      background: aC,
                                      boxShadow: `0 0 0 2px ${aC}33`,
                                    }}
                                  />
                                  {sel.exhibit && sel.exhibit !== "PENDING" && (
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: sel.exhibit.startsWith("PX")
                                          ? "#ef4444"
                                          : "#3b82f6",
                                        background:
                                          (sel.exhibit.startsWith("PX")
                                            ? "#ef4444"
                                            : "#3b82f6") + "18",
                                        padding: "3px 9px",
                                        borderRadius: 8,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {sel.exhibit}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => setSelectedDocId(null)}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: th.muted,
                                      cursor: "pointer",
                                      fontSize: 18,
                                      marginLeft: "auto",
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                                <h2
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: th.text,
                                    margin: "0 0 4px",
                                    letterSpacing: "-0.3px",
                                  }}
                                >
                                  {sel.title}
                                </h2>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {sel.bates} · {sel.date} ·{" "}
                                  {sel.pages > 0 ? sel.pages + "pp" : "pending"}{" "}
                                  · {sel.confidentiality}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 1,
                                  padding: 7,
                                  background: th.surface,
                                  borderBottom: `1px solid ${th.border}`,
                                  overflowX: "auto",
                                  flexShrink: 0,
                                }}
                              >
                                {[
                                  ["ai", "🤖", "AI Intelligence"],
                                  ["auth", "🔐", "Authentication"],
                                  ["theory", "🎯", "Theory Mapping"],
                                  ["production", "📦", "Production"],
                                  ["exhibit", "📋", "Exhibit Info"],
                                ].map(([k, e, l]) => (
                                  <button
                                    key={k}
                                    onClick={() => setDocSubTab(k)}
                                    style={{
                                      padding: "6px 11px",
                                      fontSize: 9,
                                      fontWeight: docSubTab === k ? 700 : 500,
                                      background:
                                        docSubTab === k
                                          ? "#7c3aed"
                                          : "transparent",
                                      color:
                                        docSubTab === k ? "#fff" : th.muted,
                                      border: "none",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <span>{e}</span>
                                    <span>{l}</span>
                                  </button>
                                ))}
                              </div>
                              <div
                                style={{
                                  flex: 1,
                                  overflow: "auto",
                                  padding: "14px 18px",
                                }}
                              >
                                {docSubTab === "ai" && sel.ai && (
                                  <div>
                                    <div
                                      style={{
                                        padding: "12px 14px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fefce8",
                                        borderLeft: "3px solid #eab308",
                                        borderRadius: "0 6px 6px 0",
                                        marginBottom: 11,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color: "#a16207",
                                          fontWeight: 700,
                                          letterSpacing: 1.2,
                                          marginBottom: 5,
                                        }}
                                      >
                                        📝 SUMMARY
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: th.text,
                                          lineHeight: 1.7,
                                        }}
                                      >
                                        {sel.ai.summary}
                                      </div>
                                    </div>
                                    {sel.ai.keyPassages &&
                                      sel.ai.keyPassages.length > 0 && (
                                        <div style={{ marginBottom: 11 }}>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: th.muted,
                                              fontWeight: 700,
                                              letterSpacing: 1,
                                              marginBottom: 6,
                                            }}
                                          >
                                            📌 KEY PASSAGES
                                          </div>
                                          {sel.ai.keyPassages.map((p, i) => {
                                            const c =
                                              impactC[p.impact] || "#6b7280";
                                            return (
                                              <div
                                                key={i}
                                                style={{
                                                  padding: "7px 11px",
                                                  background: c + "08",
                                                  borderLeft: `3px solid ${c}`,
                                                  borderRadius: "0 5px 5px 0",
                                                  marginBottom: 4,
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    color: c,
                                                    fontWeight: 700,
                                                    marginBottom: 3,
                                                  }}
                                                >
                                                  {p.cite}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 10,
                                                    color: th.text,
                                                    lineHeight: 1.55,
                                                  }}
                                                >
                                                  {p.text}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    {sel.ai.contradictions &&
                                      sel.ai.contradictions.length > 0 && (
                                        <div style={{ marginBottom: 11 }}>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#dc2626",
                                              fontWeight: 700,
                                              letterSpacing: 1,
                                              marginBottom: 6,
                                            }}
                                          >
                                            ⚠ CONTRADICTIONS DETECTED
                                          </div>
                                          {sel.ai.contradictions.map((c, i) => (
                                            <div
                                              key={i}
                                              style={{
                                                fontSize: 9,
                                                padding: "7px 11px",
                                                background: "#dc262608",
                                                borderLeft:
                                                  "2px solid #dc262655",
                                                borderRadius: "0 4px 4px 0",
                                                marginBottom: 4,
                                                color: th.text,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {c}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    {sel.ai.questions &&
                                      sel.ai.questions.length > 0 && (
                                        <div>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#a78bfa",
                                              fontWeight: 700,
                                              letterSpacing: 1,
                                              marginBottom: 6,
                                            }}
                                          >
                                            ❓ QUESTIONS RAISED
                                          </div>
                                          {sel.ai.questions.map((q, i) => (
                                            <div
                                              key={i}
                                              style={{
                                                fontSize: 9,
                                                padding: "6px 11px",
                                                background: dark
                                                  ? "#1e1b4b"
                                                  : "#f5f3ff",
                                                borderLeft:
                                                  "2px solid #a78bfa55",
                                                borderRadius: "0 4px 4px 0",
                                                marginBottom: 4,
                                                color: th.text,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {q}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                )}
                                {docSubTab === "auth" && (
                                  <div>
                                    <div
                                      style={{
                                        padding: "12px 14px",
                                        background: aC + "15",
                                        border: `1px solid ${aC}55`,
                                        borderLeft: `4px solid ${aC}`,
                                        borderRadius: "0 6px 6px 0",
                                        marginBottom: 10,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color: aC,
                                          fontWeight: 700,
                                          letterSpacing: 1.2,
                                          marginBottom: 5,
                                        }}
                                      >
                                        {sel.auth.status === "green"
                                          ? "✓ AUTHENTICATED"
                                          : sel.auth.status === "amber"
                                            ? "⚠ CONTESTED"
                                            : sel.auth.status === "pending" ? "⏳ AUTHENTICATION PENDING" : "🔴 NO FOUNDATION WITNESS"}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 700,
                                          color: th.text,
                                        }}
                                      >
                                        {sel.auth.foundation}
                                      </div>
                                    </div>
                                    {[
                                      ["FOUNDATION METHOD", sel.auth.method],
                                      ["FOUNDATION WITNESS", sel.auth.witness],
                                    ].map(([l, v]) => (
                                      <div
                                        key={l}
                                        style={{
                                          padding: "10px 13px",
                                          background: th.surface,
                                          borderRadius: 6,
                                          marginBottom: 7,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: th.muted,
                                            fontWeight: 700,
                                            letterSpacing: 1,
                                            marginBottom: 4,
                                          }}
                                        >
                                          {l}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color:
                                              l === "FOUNDATION WITNESS" &&
                                              (v.includes("NOT YET") ||
                                                v.includes("⚠"))
                                                ? "#dc2626"
                                                : th.text,
                                            lineHeight: 1.6,
                                            fontWeight:
                                              l === "FOUNDATION WITNESS" &&
                                              (v.includes("NOT YET") ||
                                                v.includes("⚠"))
                                                ? 700
                                                : 400,
                                          }}
                                        >
                                          {v}
                                        </div>
                                      </div>
                                    ))}
                                    {sel.auth.objections &&
                                      sel.auth.objections.length > 0 && (
                                        <div
                                          style={{
                                            padding: "10px 13px",
                                            background: sel.auth.objections[0]
                                              .toLowerCase()
                                              .includes("none")
                                              ? "#10b98108"
                                              : "#dc262608",
                                            borderLeft: `3px solid ${sel.auth.objections[0].toLowerCase().includes("none") ? "#10b98155" : "#dc262655"}`,
                                            borderRadius: "0 6px 6px 0",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: sel.auth.objections[0]
                                                .toLowerCase()
                                                .includes("none")
                                                ? "#10b981"
                                                : "#dc2626",
                                              fontWeight: 700,
                                              letterSpacing: 1,
                                              marginBottom: 5,
                                            }}
                                          >
                                            ANTICIPATED OBJECTIONS
                                          </div>
                                          {sel.auth.objections.map((o, i) => (
                                            <div
                                              key={i}
                                              style={{
                                                fontSize: 9,
                                                padding: "3px 0",
                                                color: th.text,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              · {o}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                )}
                                {docSubTab === "theory" && (
                                  <div>
                                    {(sel.theoryMaps || []).map((tmm, i) => {
                                      const c =
                                        impactC[tmm.impact] || "#6b7280";
                                      return (
                                        <div
                                          key={i}
                                          style={{
                                            padding: "11px 13px",
                                            background: th.surface,
                                            borderLeft: `3px solid ${c}`,
                                            borderRadius: "0 6px 6px 0",
                                            marginBottom: 7,
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 7,
                                              marginBottom: 5,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: c,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              {tmm.impact}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                color: th.text,
                                              }}
                                            >
                                              {tmm.theory}
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.muted,
                                              marginBottom: 4,
                                            }}
                                          >
                                            Element:{" "}
                                            <span
                                              style={{
                                                color: th.text,
                                                fontWeight: 600,
                                              }}
                                            >
                                              {tmm.element}
                                            </span>
                                          </div>
                                          {tmm.explainer && (
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                                marginTop: 5,
                                              }}
                                            >
                                              {tmm.explainer}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {(!sel.theoryMaps ||
                                      sel.theoryMaps.length === 0) && (
                                      <div
                                        style={{
                                          padding: "20px",
                                          textAlign: "center",
                                          color: th.muted,
                                          fontSize: 10,
                                        }}
                                      >
                                        No theory mappings yet for this
                                        document.
                                      </div>
                                    )}
                                  </div>
                                )}
                                {docSubTab === "production" && (
                                  <div>
                                    {[
                                      ["Producing party", sel.producer],
                                      ["Bates / range", sel.bates],
                                      ["Production date", sel.date],
                                      [
                                        "Pages",
                                        sel.pages > 0
                                          ? sel.pages + " pages"
                                          : "pending",
                                      ],
                                      ["Confidentiality", sel.confidentiality],
                                      ["Type", tm.icon + " " + tm.label],
                                    ].map(([l, v]) => (
                                      <div
                                        key={l}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          padding: "8px 13px",
                                          background: th.surface,
                                          borderRadius: 5,
                                          marginBottom: 5,
                                          gap: 10,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 8,
                                            fontFamily: "monospace",
                                            color: th.muted,
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {l}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            textAlign: "right",
                                            fontWeight: 600,
                                            fontFamily:
                                              l === "Bates / range"
                                                ? "monospace"
                                                : "inherit",
                                          }}
                                        >
                                          {v}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {docSubTab === "exhibit" && (
                                  <div>
                                    {sel.exhibit &&
                                    sel.exhibit !== "PENDING" ? (
                                      <>
                                        <div
                                          style={{
                                            padding: "14px 18px",
                                            background: dark
                                              ? "#0f172a"
                                              : "#dbeafe",
                                            border: "1px solid #3b82f655",
                                            borderRadius: 8,
                                            textAlign: "center",
                                            marginBottom: 11,
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#3b82f6",
                                              fontWeight: 700,
                                              letterSpacing: 1.5,
                                              marginBottom: 5,
                                            }}
                                          >
                                            EXHIBIT NUMBER
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 24,
                                              fontWeight: 800,
                                              color: sel.exhibit.startsWith(
                                                "PX",
                                              )
                                                ? "#ef4444"
                                                : "#3b82f6",
                                              letterSpacing: "-0.5px",
                                            }}
                                          >
                                            {sel.exhibit}
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            padding: "10px 13px",
                                            background: th.surface,
                                            borderRadius: 5,
                                            marginBottom: 6,
                                            display: "flex",
                                            justifyContent: "space-between",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 8,
                                              fontFamily: "monospace",
                                              color: th.muted,
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              textTransform: "uppercase",
                                            }}
                                          >
                                            Status
                                          </span>
                                          <span
                                            style={{
                                              fontSize: 9,
                                              fontFamily: "monospace",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              color:
                                                sel.exhibitStatus ===
                                                "stipulated"
                                                  ? "#10b981"
                                                  : (sel.exhibitStatus === "contested" || sel.exhibitStatus === "pending")
                                                    ? "#f59e0b"
                                                    : "#dc2626",
                                              textTransform: "uppercase",
                                            }}
                                          >
                                            {sel.exhibitStatus}
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <div
                                        style={{
                                          padding: "20px",
                                          textAlign: "center",
                                          color: "#dc2626",
                                          fontSize: 11,
                                          background: "#dc262615",
                                          border: "1px dashed #dc262655",
                                          borderRadius: 8,
                                          fontWeight: 600,
                                        }}
                                      >
                                        ⚠ Exhibit number not yet assigned.
                                        Authentication required before exhibit
                                        list inclusion.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "ringbinder" && (
              <div style={{ padding: "14px 16px 60px" }}>
                <div style={{ maxWidth: sp ? 520 : 800, margin: "0 auto" }}>
                  {/* THEORIES */}
                  {rbP === "theories" && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <h2
                          style={{ fontSize: 15, fontWeight: 700, margin: 0 }}
                        >
                          Theories of the Case
                        </h2>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            onClick={() => setShowAddTheory((t) => !t)}
                            style={{
                              fontSize: 8,
                              padding: "3px 9px",
                              background: showAddTheory
                                ? "#1e40af"
                                : th.surface,
                              color: showAddTheory ? "#fff" : th.dim,
                              border: `1px solid ${showAddTheory ? "#1e40af" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            ＋ Add Theory
                          </button>
                          <button
                            onClick={async () => {
                              const fallbackSuggs = isIle
                                ? rbSide === "plaintiff"
                                  ? [
                                      {
                                        title:
                                          "Post-Exposure Lulling as a Freestanding Wire Theory",
                                        text: "S-1 ¶¶46-48 and Counts NINE-TEN: after the Aug 29 report, transactions were staged as customer payments and false confirmations flowed to Lender B. Concealment after exposure is the cleanest intent evidence and survives even if early-period scienter frays.",
                                      },
                                      {
                                        title:
                                          "§225 Receipts Through Lender Draws",
                                        text: "Gross receipts under 18 U.S.C. §225 can be built from the $100M in lender draws and SPAC proceeds rather than round-tripped principal, sidestepping the netting defense to ¶27.",
                                      },
                                    ]
                                  : [
                                      {
                                        title:
                                          "Ciminelli Property-Object Challenge",
                                        text: "Under Ciminelli v. United States, 598 U.S. 306 (2023), wire fraud requires a traditional property object. Test each charged transmission - diligence emails to auditors are attenuated from any property deprivation.",
                                      },
                                      {
                                        title:
                                          "Rule 14 Severance with Bruton Overlay",
                                        text: "Count 11s obstruction proof and the no-liabilities statement (S-1 ¶30) are Chidambaran-only evidence. Severance, or at minimum redaction plus limiting instructions, protects the role defense.",
                                      },
                                    ]
                                : caseId === "musk"
                                  ? rbSide === "plaintiff"
                                    ? [
                                        {
                                          title:
                                            "§17510.8 Charitable Solicitations Liability",
                                          text: "Cal. Bus. & Prof. Code §17510.8 imposes strict liability for false or misleading representations in charitable solicitations. Altman and Brockman's 2015-2017 emails to Musk were solicitations; their representations about nonprofit mission were false or misleading by 2019 at the latest.",
                                        },
                                        {
                                          title:
                                            "Constructive Trust on Microsoft Equity",
                                          text: "A constructive trust should be imposed on Microsoft's ~27% equity stake in OpenAI ($135B value) as proceeds of the charitable trust breach. Microsoft knowingly benefited from Altman's breach; equity requires disgorging the ill-gotten equity interest rather than mere damages.",
                                        },
                                        {
                                          title:
                                            "Per Se Breach via For-Profit Conversion",
                                          text: "OpenAI's October 2025 conversion from nonprofit to public benefit corporation is itself a per se breach of the charitable trust. The conversion cannot be ratified by the very board that breached the trust.",
                                        },
                                      ]
                                    : [
                                        {
                                          title: "Laches - 5+ Years Delay",
                                          text: "Musk knew of OpenAI's for-profit structure by March 2019 (public announcement) and tweeted 'captured by Microsoft' in September 2020. Five years of delay while OpenAI invested billions in reliance is textbook laches. Equity will not rescue a plaintiff who slept on his rights.",
                                        },
                                        {
                                          title: "Musk Had No Donor Standing",
                                          text: "California charitable trust law gives enforcement standing to the Attorney General, not individual donors. Musk is a competitor, not a charitable actor. The AG declined to join Musk's suit - that judgment by the state's charitable enforcement authority is itself instructive.",
                                        },
                                        {
                                          title: "Released by Acquiescence",
                                          text: "Musk was informed of the for-profit LP structure in August 2018, expressed no objection, and even told Altman to say he was 'supportive.' His subsequent silence and then Twitter complaint establish acquiescence that bars equitable relief.",
                                        },
                                      ]
                                  : isJuul
                                    ? rbSide === "plaintiff"
                                      ? [
                                          {
                                            title: "Fraudulent Concealment",
                                            text: "JLI actively concealed JUUL's addictive properties through misleading FDA submissions and suppression of internal research. Fraudulent concealment tolls statutes of limitation and supports punitive damages - stronger than failure to warn.",
                                          },
                                          {
                                            title: "Design Defect",
                                            text: "JUUL was defectively designed: 59mg/mL nicotine salts with concealable form factor made it unreasonably dangerous. Under California risk-utility, the youth addiction epidemic vastly outweighs any demonstrated cessation benefit.",
                                          },
                                          {
                                            title: "Unjust Enrichment",
                                            text: "JLI was unjustly enriched by billions in revenue from underage users and deceived adults. Disgorgement is available as equitable remedy without precise damages proof - powerful for SFUSD.",
                                          },
                                        ]
                                      : [
                                          {
                                            title: "Lack of General Causation",
                                            text: "Without medical causation experts, PI plaintiffs cannot establish JUUL specifically caused their injuries versus other nicotine products. General causation requires epidemiological evidence linking JUUL to specific health outcomes.",
                                          },
                                          {
                                            title: "Statute of Limitations",
                                            text: "Many opt-out PI claims may be time-barred. JUUL health risks received extensive media coverage 2018-2019 triggering the discovery rule. Plaintiffs filing late without tolling face dismissal.",
                                          },
                                          {
                                            title:
                                              "Intervening Superseding Cause",
                                            text: "Pre-existing nicotine use or concurrent use of other addictive substances may constitute intervening causes breaking the causation chain between JUUL use and claimed injuries.",
                                          },
                                        ]
                                    : rbSide === "plaintiff"
                                      ? [
                                          {
                                            title: "Willful Infringement",
                                            text: "Defendant had actual notice of the 061 patent through the TMLA yet continued to sell infringing products. Willful infringement supports enhanced damages up to three times under 35 U.S.C. section 284.",
                                          },
                                          {
                                            title:
                                              "False Designation of Origin",
                                            text: "Defendant markets as US-manufactured while sourcing from China - false designation under Lanham Act section 43(a). Does not require likelihood of confusion, only that origin statement was false and material.",
                                          },
                                          {
                                            title:
                                              "Trade Secret Misappropriation",
                                            text: "If defendant accessed proprietary formulation data through the TMLA and used it to develop competing products, a claim lies under the Defend Trade Secrets Act with exemplary damages.",
                                          },
                                        ]
                                      : [
                                          {
                                            title: "Licensee Estoppel",
                                            text: "To the extent defendant operated under a valid TMLA license, plaintiff may be estopped from asserting patents against licensed activities. Scope of the TMLA license is a threshold dispositive issue.",
                                          },
                                          {
                                            title:
                                              "Prosecution History Estoppel",
                                            text: "Claim amendments during prosecution to overcome prior art may have surrendered claim scope, limiting equivalents available to plaintiff and potentially excluding defendant products.",
                                          },
                                          {
                                            title: "Laches",
                                            text: "If plaintiff was aware of defendant infringing activities before filing suit, laches may bar pre-filing damages. The TMLA relationship gave plaintiff early notice of defendant products.",
                                          },
                                        ];
                              setSuggestedTheories(fallbackSuggs);
                            }}
                            disabled={theorySuggLoading}
                            style={{
                              fontSize: 8,
                              padding: "3px 9px",
                              background:
                                "linear-gradient(135deg,#1e40af,#10b981)",
                              color: "#fff",
                              border: "none",
                              borderRadius: 3,
                              cursor: theorySuggLoading
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            {theorySuggLoading
                              ? "Thinking..."
                              : "✨ Suggest More"}
                          </button>
                        </div>
                      </div>
                      {[
                        ["plaintiff", "Plaintiff"],
                        ["defendant", "Defendant"],
                      ].map(([k, lbl]) => (
                        <button
                          key={k}
                          onClick={() => setRbSide(k)}
                          style={{
                            padding: "4px 12px",
                            fontSize: 9,
                            background:
                              rbSide === k
                                ? k === "plaintiff"
                                  ? "#1e40af"
                                  : "#10b981"
                                : th.surface,
                            color: rbSide === k ? "#fff" : th.dim,
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontWeight: rbSide === k ? 600 : 400,
                          }}
                        >
                          {lbl}
                        </button>
                      ))}
                      {showAddTheory && (
                        <div
                          style={{
                            background: th.surface,
                            border: `1px solid ${th.border}`,
                            borderRadius: 8,
                            padding: "12px 16px",
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              marginBottom: 8,
                            }}
                          >
                            New Theory - {" "}
                            {rbSide === "plaintiff" ? "Plaintiff" : "Defendant"}{" "}
                            Side
                          </div>
                          <input
                            value={newTheoryTitle}
                            onChange={(e) => setNewTheoryTitle(e.target.value)}
                            placeholder="Theory title *"
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              fontSize: 10,
                              background: th.bg,
                              border: `1px solid ${th.border}`,
                              borderRadius: 4,
                              color: th.text,
                              outline: "none",
                              marginBottom: 8,
                              boxSizing: "border-box",
                            }}
                          />
                          <textarea
                            value={newTheoryText}
                            onChange={(e) => setNewTheoryText(e.target.value)}
                            placeholder="Describe the theory - what is the legal basis, key facts, and why it is strong or weak?"
                            rows={4}
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              fontSize: 9,
                              background: th.bg,
                              border: `1px solid ${th.border}`,
                              borderRadius: 4,
                              color: th.text,
                              outline: "none",
                              resize: "vertical",
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                              marginBottom: 8,
                              lineHeight: 1.5,
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: 5,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                setShowAddTheory(false);
                                setNewTheoryTitle("");
                                setNewTheoryText("");
                              }}
                              style={{
                                padding: "4px 12px",
                                fontSize: 9,
                                background: th.border,
                                color: th.muted,
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                if (!newTheoryTitle.trim()) return;
                                const nt = {
                                  title: newTheoryTitle.trim(),
                                  levels: [
                                    newTheoryText.trim() ||
                                      "Custom theory - add description.",
                                    newTheoryText.trim() || "Custom theory.",
                                    newTheoryText.trim() || "Custom theory.",
                                    newTheoryText.trim() || "Custom theory.",
                                    newTheoryText.trim() || "Custom theory.",
                                  ],
                                };
                                setCustomTheories((p) => ({
                                  ...p,
                                  [rbSide]: [...(p[rbSide] || []), nt],
                                }));
                                setShowAddTheory(false);
                                setNewTheoryTitle("");
                                setNewTheoryText("");
                                toast(
                                  `"${nt.title}" added to ${rbSide === "plaintiff" ? "Plaintiff" : "Defendant"} theories`,
                                  "success",
                                );
                              }}
                              style={{
                                padding: "4px 12px",
                                fontSize: 9,
                                background: "#1e40af",
                                color: "#fff",
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              Save Theory
                            </button>
                          </div>
                        </div>
                      )}
                      {theorySuggLoading && (
                        <div style={{ marginBottom: 12 }}>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: "#a78bfa",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                width: 12,
                                height: 12,
                                border: "2px solid #a78bfa",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            />
                            Generating theories...
                          </div>
                          {[0, 1, 2].map((i) => (
                            <div
                              key={i}
                              style={{
                                background: "#a78bfa08",
                                border: "1px solid #a78bfa22",
                                borderRadius: 8,
                                padding: "12px 14px",
                                marginBottom: 6,
                                opacity: 1 - i * 0.25,
                              }}
                            >
                              <div
                                style={{
                                  height: 10,
                                  background: "#a78bfa18",
                                  borderRadius: 3,
                                  width: "60%",
                                  marginBottom: 8,
                                }}
                              />
                              <div
                                style={{
                                  height: 7,
                                  background: "#a78bfa10",
                                  borderRadius: 3,
                                  width: "100%",
                                  marginBottom: 4,
                                }}
                              />
                              <div
                                style={{
                                  height: 7,
                                  background: "#a78bfa10",
                                  borderRadius: 3,
                                  width: "85%",
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {suggestedTheories.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#a78bfa",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <span style={{ fontSize: 12 }}>✨</span>{" "}
                              {suggestedTheories.length} New Theories Suggested
                            </div>
                            <button
                              onClick={() => setSuggestedTheories([])}
                              style={{
                                fontSize: 8,
                                background: "transparent",
                                color: th.faint,
                                border: `1px solid ${th.border}`,
                                borderRadius: 3,
                                padding: "2px 6px",
                                cursor: "pointer",
                              }}
                            >
                              ✕ Clear
                            </button>
                          </div>
                          {suggestedTheories.map((s, i) => (
                            <div
                              key={i}
                              style={{
                                background: `linear-gradient(135deg,${"#a78bfa"}08,${"#3b82f6"}06)`,
                                border: "1px solid #a78bfa44",
                                borderRadius: 10,
                                padding: "14px 16px",
                                marginBottom: 8,
                                animation: `slideIn 0.4s ease ${i * 0.15}s both`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: 6,
                                }}
                              >
                                <div>
                                  <span
                                    style={{
                                      fontSize: 6,
                                      fontFamily: "monospace",
                                      color: "#a78bfa",
                                      background: "#a78bfa18",
                                      padding: "1px 5px",
                                      borderRadius: 10,
                                      marginBottom: 4,
                                      display: "inline-block",
                                    }}
                                  >
                                    New Theory {i + 1}
                                  </span>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: th.text,
                                      marginTop: 2,
                                    }}
                                  >
                                    {s.title}
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setCustomTheories((p) => ({
                                      ...p,
                                      [rbSide]: [
                                        ...(p[rbSide] || []),
                                        {
                                          title: s.title,
                                          levels: [
                                            s.text,
                                            s.text,
                                            s.text,
                                            s.text,
                                            s.text,
                                          ],
                                        },
                                      ],
                                    }));
                                    setSuggestedTheories((p) =>
                                      p.filter((_, j) => j !== i),
                                    );
                                  }}
                                  style={{
                                    fontSize: 9,
                                    background:
                                      "linear-gradient(135deg,#1e40af,#a78bfa)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    padding: "4px 10px",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    marginLeft: 10,
                                    flexShrink: 0,
                                  }}
                                >
                                  ＋ Add
                                </button>
                              </div>
                              <p
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  lineHeight: 1.6,
                                  margin: 0,
                                }}
                              >
                                {s.text}
                              </p>
                            </div>
                          ))}
                          {suggestedTheories.length > 0 && (
                            <button
                              onClick={() => {
                                setCustomTheories((p) => ({
                                  ...p,
                                  [rbSide]: [
                                    ...(p[rbSide] || []),
                                    ...suggestedTheories.map((s) => ({
                                      title: s.title,
                                      levels: [
                                        s.text,
                                        s.text,
                                        s.text,
                                        s.text,
                                        s.text,
                                      ],
                                    })),
                                  ],
                                }));
                                setSuggestedTheories([]);
                              }}
                              style={{
                                width: "100%",
                                padding: "7px",
                                fontSize: 9,
                                background: "#a78bfa18",
                                color: "#a78bfa",
                                border: "1px solid #a78bfa44",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              ＋ Add All {suggestedTheories.length} Theories
                            </button>
                          )}
                        </div>
                      )}
                      {[...(isJuul ? JUUL_THEORIES[rbSide] : aT[rbSide] || []), ...(customTheories[rbSide] || [])].map(
                        (t, i) => {
                          const lv = theoryLvls[rbSide + String(i)] ?? 2;
                          const setLv = (v) =>
                            setTheoryLvls((p) => ({
                              ...p,
                              [rbSide + String(i)]: v,
                            })); const ek = rbSide + String(i) + String(lv); const tTxt = theoryLevelText(t, lv); const hasEdit = theoryEdits[ek] !== undefined && theoryEdits[ek] !== null; const shown = hasEdit ? theoryEdits[ek] : tTxt;
                          return (
                            <div
                              key={i}
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                borderLeft: `3px solid ${rbSide === "plaintiff" ? "#1e40af" : "#10b981"}`,
                                padding: "14px 18px",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 6,
                                }}
                              >
                                <span style={{ fontSize: 12, fontWeight: 700 }}>
                                  {t.title}
                                </span>
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: FC[lv],
                                    background: `${FC[lv]}18`,
                                    padding: "2px 7px",
                                    borderRadius: 10,
                                  }}
                                >
                                  {FV[lv]}
                                </span>
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <input
                                  type="range"
                                  min={0}
                                  max={4}
                                  value={lv}
                                  onChange={(e) => setLv(+e.target.value)}
                                  style={{
                                    width: "100%",
                                    height: 4,
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    background: `linear-gradient(to right,${FC[lv]} ${lv * 25}%,#4b556333 ${lv * 25}%)`,
                                    borderRadius: 3,
                                    outline: "none",
                                    cursor: "pointer",
                                    accentColor: FC[lv],
                                  }}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginTop: 2,
                                  }}
                                >
                                  {FV.map((lbl, fi) => (
                                    <span
                                      key={fi}
                                      onClick={() => setLv(fi)}
                                      style={{
                                        fontSize: 6,
                                        color: lv === fi ? FC[fi] : th.faint,
                                        cursor: "pointer",
                                        fontWeight: lv === fi ? 700 : 400,
                                      }}
                                    >
                                      {lbl}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <p
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  lineHeight: 1.65,
                                  margin: "0 0 6px",
                                }}
                              >
                                {shown}
                              </p>
                              {hasEdit && (() => {
                                  const orig = tTxt;
                                  const edited = shown;
                                  const pct = theoryEditPct(orig, edited);
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 8,
                                      }}
                                    >
                                      <div
                                        style={{
                                          flex: 1,
                                          height: 3,
                                          background: th.surface,
                                          borderRadius: 2,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          style={{
                                            height: "100%",
                                            width: pct + "%",
                                            background:
                                              pct > 60
                                                ? "#10b981"
                                                : pct > 30
                                                  ? "#f59e0b"
                                                  : "#3b82f6",
                                            borderRadius: 2,
                                            transition: "width 0.3s",
                                          }}
                                        />
                                      </div>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color:
                                            pct > 60
                                              ? "#10b981"
                                              : pct > 30
                                                ? "#f59e0b"
                                                : "#3b82f6",
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {pct}% edited by you
                                      </span>
                                    </div>
                                  );
                                })()}
                              {editMode === rbSide + String(i) + String(lv) && (
                                <textarea
                                  value={
                                    shown
                                  }
                                  onChange={(e) =>
                                    setTheoryEdits((p) =>
                                      Object.assign({}, p, {
                                        [rbSide + String(i) + String(lv)]:
                                          e.target.value,
                                      }),
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    fontSize: 9,
                                    color: th.text,
                                    background: th.surface,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 4,
                                    padding: "8px",
                                    lineHeight: 1.65,
                                    resize: "vertical",
                                    minHeight: 80,
                                    fontFamily: "inherit",
                                    boxSizing: "border-box",
                                    marginTop: 4,
                                    outline: "none",
                                  }}
                                  rows={5}
                                />
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 4,
                                  flexWrap: "wrap",
                                  marginTop: 8,
                                }}
                              >
                                <button
                                  onClick={async () => {
                                    const cur = tTxt; const txt = shown; const isEdited = hasEdit; const editedPct = isEdited ? theoryEditPct(cur, txt) : 0;
                                    const theoryKey =
                                      rbSide + String(i) + String(lv);
                                    setExpandingTheory(theoryKey);
                                    openP(
                                      t.title + " - Expanding",
                                      "",
                                      t.title,
                                      true,
                                    );
                                    const result = await aiCall(
                                      {
                                        model: "claude-sonnet-4-20250514",
                                        max_tokens: 1800,
                                        system:
                                        "You are a senior litigation attorney writing an expanded legal brief section. Write approximately 800-1000 words. Use Markdown: ## and ### headings for sections, **bold** for key terms, and clear paragraphs. Include specific legal standards, relevant case citations, factual basis, policy arguments, and a strong conclusion. Be specific to the case facts provided. " +
                                        theoryStanceInstruction(lv),
                                        messages: [
                                          {
                                            role: "user",
                                            content:
                                              "Case: " +
                                              (isJuul
                                                ? "Seliavski v. JUUL MDL (3:19-md-02913-WHO), N.D. Cal., Judge Orrick"
                                                : isIle
                                                  ? "USA v. Chidambaran & Naqvi (1:26-cr-00097-NGG), E.D.N.Y., Judge Garaufis, STATUS CONF 9/14/26"
                                                  : caseId === "musk"
                                                    ? "Musk v. Altman et al. (4:24-cv-04722-YGR), N.D. Cal., Judge YGR, TRIAL IN PROGRESS"
                                                    : "Neurocentria v. Mando (2:25-cv-05871-FLA-MAR), C.D. Cal.") +
                                              ". Strength level: " + FV[lv] + " (" + theoryStanceInstruction(lv) + "). Theory: " +
                                              t.title +
                                              (isEdited
                                                ? " [" +
                                                  editedPct +
                                                  "% edited by attorney]"
                                                : "") +
                                              ". Expand the following into a detailed 800-1000 word brief section:\n\n" +
                                              txt,
                                          },
                                        ],
                                      },
                                      () =>
                                        expandDemoResponse(
                                          t.title,
                                          txt,
                                          rbSide,
                                        ),
                                    );
                                    openP(
                                      t.title +
                                        (isEdited
                                          ? " (" + editedPct + "% edited)"
                                          : "") +
                                        (result.demo
                                          ? " - Demo"
                                          : " - Expanded"),
                                      result.text,
                                      t.title +
                                        (isEdited
                                          ? " · " +
                                            editedPct +
                                            "% of this theory was written by you"
                                          : "") +
                                        (result.demo ? " · DEMO MODE" : ""),
                                      false,
                                    );
                                    if (result.demo)
                                      toast(
                                        "Expand running in demo mode - start proxy for live AI",
                                        "warn",
                                      );
                                    setExpandingTheory(null);
                                  }}
                                  disabled={
                                    expandingTheory ===
                                    rbSide + String(i) + String(lv)
                                  }
                                  style={{
                                    fontSize: 8,
                                    background:
                                      expandingTheory ===
                                      rbSide + String(i) + String(lv)
                                        ? "#1e40af99"
                                        : "linear-gradient(135deg,#1e40af,#10b981)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    padding: "4px 10px",
                                    cursor:
                                      expandingTheory ===
                                      rbSide + String(i) + String(lv)
                                        ? "not-allowed"
                                        : "pointer",
                                    fontWeight: 600,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    transition: "all 0.2s",
                                  }}
                                >
                                  {expandingTheory ===
                                  rbSide + String(i) + String(lv) ? (
                                    <>
                                      <span
                                        style={{
                                          display: "inline-block",
                                          width: 8,
                                          height: 8,
                                          border: "1.5px solid #ffffff66",
                                          borderTopColor: "#fff",
                                          borderRadius: "50%",
                                          animation:
                                            "spin 0.8s linear infinite",
                                        }}
                                      />{" "}
                                      Generating...
                                    </>
                                  ) : (
                                    <>⬆ Expand</>
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    setEditMode((m) =>
                                      m === rbSide + String(i) + String(lv)
                                        ? null
                                        : rbSide + String(i) + String(lv),
                                    )
                                  }
                                  style={{
                                    fontSize: 8,
                                    background:
                                      editMode ===
                                      rbSide + String(i) + String(lv)
                                        ? "#f59e0b"
                                        : th.surface,
                                    color:
                                      editMode ===
                                      rbSide + String(i) + String(lv)
                                        ? "#fff"
                                        : th.dim,
                                    border: `1px solid ${editMode === rbSide + String(i) + String(lv) ? "#f59e0b" : th.border}`,
                                    borderRadius: 4,
                                    padding: "4px 10px",
                                    cursor: "pointer",
                                  }}
                                >
                                  {editMode === rbSide + String(i) + String(lv)
                                    ? "Done"
                                    : "Edit"}
                                </button>
                                {hasEdit && (
                                  <button
                                    onClick={() =>
                                      setTheoryEdits((p) =>
                                        Object.assign({}, p, {
                                          [rbSide + String(i) + String(lv)]:
                                            undefined,
                                        }),
                                      )
                                    }
                                    style={{
                                      fontSize: 8,
                                      background: "transparent",
                                      color: "#ef4444",
                                      border: "1px solid #ef444433",
                                      borderRadius: 4,
                                      padding: "4px 8px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                  {/* WITNESSES */}
                  {rbP === "witnesses" && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                        }}
                      >
                        <h2
                          style={{ fontSize: 15, fontWeight: 700, margin: 0 }}
                        >
                          Witnesses
                        </h2>
                        <div style={{ display: "flex", gap: 5 }}>
                          {witEditMode && (
                            <button
                              onClick={() => {
                                const sorted = [...aW].sort((a, b) =>
                                  a.name.localeCompare(b.name),
                                );
                                setWitOrder(sorted.map((w) => w.id || w.name));
                              }}
                              style={{
                                padding: "3px 9px",
                                fontSize: 8,
                                background: th.surface,
                                color: th.dim,
                                border: `1px solid ${th.border}`,
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              A→Z
                            </button>
                          )}
                          {witEditMode && (
                            <button
                              onClick={() => setWitOrder(null)}
                              style={{
                                padding: "3px 9px",
                                fontSize: 8,
                                background: th.surface,
                                color: th.dim,
                                border: `1px solid ${th.border}`,
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={() => setWitEditMode((m) => !m)}
                            style={{
                              padding: "3px 9px",
                              fontSize: 8,
                              background: witEditMode ? "#f59e0b" : "#1e40af",
                              color: "#fff",
                              border: "none",
                              borderRadius: 3,
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            {witEditMode ? "✓ Done" : "✏️ Edit"}
                          </button>
                          {!witEditMode && (
                            <button
                              onClick={() => setShowAddWit((w) => !w)}
                              style={{
                                padding: "3px 9px",
                                fontSize: 8,
                                background: "#1e40af",
                                color: "#fff",
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              ＋ Add Witness
                            </button>
                          )}
                        </div>
                      </div>
                      {showAddWit && (
                        <div
                          style={{
                            background: th.surface,
                            border: `1px solid ${th.border}`,
                            borderRadius: 8,
                            padding: "12px 16px",
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              marginBottom: 8,
                            }}
                          >
                            New Witness → also saved as Asset
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <input
                              value={witDraft.name}
                              onChange={(e) =>
                                setWitDraft((p) => ({
                                  ...p,
                                  name: e.target.value,
                                }))
                              }
                              placeholder="Full name *"
                              style={{
                                padding: "6px 8px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 4,
                                color: th.text,
                                outline: "none",
                              }}
                            />
                            <input
                              value={witDraft.role}
                              onChange={(e) =>
                                setWitDraft((p) => ({
                                  ...p,
                                  role: e.target.value,
                                }))
                              }
                              placeholder="Role (CEO, Expert...)"
                              style={{
                                padding: "6px 8px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 4,
                                color: th.text,
                                outline: "none",
                              }}
                            />
                          </div>
                          <div
                            style={{ display: "flex", gap: 4, marginBottom: 8 }}
                          >
                            {["Plaintiff", "Defendant", "Both"].map((p) => (
                              <button
                                key={p}
                                onClick={() =>
                                  setWitDraft((d) => ({ ...d, party: p }))
                                }
                                style={{
                                  padding: "3px 9px",
                                  fontSize: 8,
                                  background:
                                    witDraft.party === p
                                      ? p === "Plaintiff"
                                        ? "#ef444418"
                                        : p === "Defendant"
                                          ? "#3b82f618"
                                          : "#10b98118"
                                      : "transparent",
                                  color:
                                    witDraft.party === p
                                      ? p === "Plaintiff"
                                        ? "#ef4444"
                                        : p === "Defendant"
                                          ? "#3b82f6"
                                          : "#10b981"
                                      : th.dim,
                                  border: `1px solid ${witDraft.party === p ? (p === "Plaintiff" ? "#ef4444" : p === "Defendant" ? "#3b82f6" : "#10b981") : th.border}`,
                                  borderRadius: 3,
                                  cursor: "pointer",
                                }}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={witDraft.notes}
                            onChange={(e) =>
                              setWitDraft((p) => ({
                                ...p,
                                notes: e.target.value,
                              }))
                            }
                            placeholder="Key notes (one per line)"
                            rows={2}
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              fontSize: 10,
                              background: th.bg,
                              border: `1px solid ${th.border}`,
                              borderRadius: 4,
                              color: th.text,
                              outline: "none",
                              resize: "vertical",
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                              marginBottom: 8,
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: 5,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                setShowAddWit(false);
                                setWitDraft({
                                  name: "",
                                  role: "",
                                  party: "Plaintiff",
                                  notes: "",
                                });
                              }}
                              style={{
                                padding: "4px 12px",
                                fontSize: 9,
                                background: th.border,
                                color: th.muted,
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                if (!witDraft.name) return;
                                const nw = {
                                  id: `cw${Date.now()}`,
                                  ph: "👤",
                                  name: witDraft.name,
                                  role: witDraft.role || "Witness",
                                  party: witDraft.party,
                                  notes: witDraft.notes
                                    ? witDraft.notes.split("\n").filter(Boolean)
                                    : ["Added by attorney"],
                                };
                                setCustomWits((p) => [...p, nw]);
                                setShowAddWit(false);
                                setWitDraft({
                                  name: "",
                                  role: "",
                                  party: "Plaintiff",
                                  notes: "",
                                });
                                toast(
                                  `${nw.name} saved + linked to Assets`,
                                  "success",
                                );
                              }}
                              style={{
                                padding: "4px 12px",
                                fontSize: 9,
                                background: "#1e40af",
                                color: "#fff",
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              💾 Save + Add to Assets
                            </button>
                          </div>
                        </div>
                      )}
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {[
                          ["all", "All"],
                          ["Plaintiff", "Plaintiff"],
                          ["Defendant", "Defendant"],
                          ["Both", "Both"],
                        ].map(([k, l]) => (
                          <button
                            key={k}
                            onClick={() => setWFilter(k)}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              fontWeight: wFilter === k ? 600 : 400,
                              color: wFilter === k ? "#3b82f6" : th.dim,
                              background:
                                wFilter === k ? "#3b82f615" : "transparent",
                              border: `1px solid ${wFilter === k ? "#3b82f6" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                              fontFamily: "monospace",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill,minmax(220px,1fr))",
                          gap: 8,
                        }}
                      >
                        {(witOrder
                          ? [...aW].sort(
                              (a, b) =>
                                witOrder.indexOf(a.id || a.name) -
                                witOrder.indexOf(b.id || b.name),
                            )
                          : aW
                        )
                          .filter(
                            (w) => wFilter === "all" || w.party === wFilter,
                          )
                          .map((w, wi) => (
                            <div
                              key={w.id}
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${witEditMode ? "#f59e0b44" : th.border}`,
                                borderLeft: `3px solid ${w.id && String(w.id).startsWith("cw") ? "#a78bfa" : th.border}`,
                                padding: "12px 14px",
                              }}
                            >
                              {witEditMode && (
                                <div
                                  style={{
                                    background: "#f59e0b0a",
                                    borderBottom: "1px solid #f59e0b22",
                                    padding: "5px 0 8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ display: "flex", gap: 4 }}>
                                    <button
                                      onClick={() =>
                                        setWitOrder((p) => {
                                          const base = p
                                            ? [...p]
                                            : aW.map((x) => x.id || x.name);
                                          const i = base.indexOf(
                                            w.id || w.name,
                                          );
                                          if (i <= 0) return base;
                                          [base[i - 1], base[i]] = [
                                            base[i],
                                            base[i - 1],
                                          ];
                                          return base;
                                        })
                                      }
                                      disabled={wi === 0}
                                      style={{
                                        width: 22,
                                        height: 22,
                                        fontSize: 10,
                                        background: th.surface,
                                        border: `1px solid ${th.border}`,
                                        borderRadius: 3,
                                        cursor: "pointer",
                                        color: th.dim,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      onClick={() =>
                                        setWitOrder((p) => {
                                          const base = p
                                            ? [...p]
                                            : aW.map((x) => x.id || x.name);
                                          const i = base.indexOf(
                                            w.id || w.name,
                                          );
                                          if (i < 0) return base;
                                          [base[i], base[i + 1]] = [
                                            base[i + 1],
                                            base[i],
                                          ];
                                          return base;
                                        })
                                      }
                                      disabled={
                                        wi ===
                                        aW.filter(
                                          (x) =>
                                            wFilter === "all" ||
                                            x.party === wFilter,
                                        ).length -
                                          1
                                      }
                                      style={{
                                        width: 22,
                                        height: 22,
                                        fontSize: 10,
                                        background: th.surface,
                                        border: `1px solid ${th.border}`,
                                        borderRadius: 3,
                                        cursor: "pointer",
                                        color: th.dim,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                  <button
                                    onClick={() =>
                                      setConfirmRemove({
                                        name: w.name,
                                        action: () => {
                                          if (
                                            w.id &&
                                            String(w.id).startsWith("cw")
                                          )
                                            setCustomWits((p) =>
                                              p.filter((x) => x.id !== w.id),
                                            );
                                          else
                                            setWitOrder((p) => {
                                              const base = p
                                                ? [...p]
                                                : aW.map((x) => x.id || x.name);
                                              return base.filter(
                                                (x) => x !== (w.id || w.name),
                                              );
                                            });
                                        },
                                      })
                                    }
                                    style={{
                                      fontSize: 7,
                                      background: "#ef444415",
                                      color: "#ef4444",
                                      border: "1px solid #ef444433",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ✕ Remove
                                  </button>
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  marginBottom: 6,
                                }}
                              >
                                <span style={{ fontSize: 18 }}>
                                  {w.ph || "👤"}
                                </span>
                                <div>
                                  <div
                                    style={{ fontSize: 11, fontWeight: 600 }}
                                  >
                                    {w.name}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 7,
                                        color: "#3b82f6",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {w.role}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 6,
                                        fontFamily: "monospace",
                                        color:
                                          w.party === "Plaintiff"
                                            ? "#ef4444"
                                            : w.party === "Defendant"
                                              ? "#3b82f6"
                                              : "#6b7280",
                                        background:
                                          w.party === "Plaintiff"
                                            ? "#ef444418"
                                            : w.party === "Defendant"
                                              ? "#3b82f618"
                                              : "#6b728018",
                                        padding: "1px 4px",
                                        borderRadius: 2,
                                      }}
                                    >
                                      {w.party}
                                    </span>
                                    {w.id && String(w.id).startsWith("cw") && (
                                      <span
                                        style={{
                                          fontSize: 5,
                                          color: "#a78bfa",
                                          background: "#a78bfa18",
                                          padding: "1px 4px",
                                          borderRadius: 2,
                                        }}
                                      >
                                        Custom ✓ Asset
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {(w.notes || []).map((n, i) => (
                                <div
                                  key={i}
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    padding: "2px 0 2px 8px",
                                    borderLeft: `2px solid ${th.border}`,
                                    marginBottom: 2,
                                  }}
                                >
                                  {n}
                                </div>
                              ))}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 4,
                                  marginTop: 8,
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setUploadAssoc({ wit: w.name, loc: "" });
                                    setShowUpload(true);
                                  }}
                                  title={"Add asset for " + w.name}
                                  style={{
                                    fontSize: 7,
                                    background: th.surface,
                                    color: th.dim,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 3,
                                    padding: "2px 7px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Add Asset
                                </button>
                                <button
                                  onClick={() => setDepoWitness(w)}
                                  style={{
                                    fontSize: 7,
                                    background: "#1e40af",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 3,
                                    padding: "2px 7px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Notice of Deposition
                                </button>
                                <button
                                  onClick={() =>
                                    setDepoTopicsWit(
                                      depoTopicsWit?.id === w.id ? null : w,
                                    )
                                  }
                                  style={{
                                    fontSize: 7,
                                    background:
                                      depoTopicsWit?.id === w.id
                                        ? "#a78bfa"
                                        : "#a78bfa22",
                                    color:
                                      depoTopicsWit?.id === w.id
                                        ? "#fff"
                                        : "#a78bfa",
                                    border: "1px solid #a78bfa44",
                                    borderRadius: 3,
                                    padding: "2px 7px",
                                    cursor: "pointer",
                                  }}
                                >
                                  💡 Depo Topics
                                </button>
                              </div>
                              {depoTopicsWit && depoTopicsWit.id === w.id && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    background: "#a78bfa0a",
                                    border: "1px solid #a78bfa22",
                                    borderRadius: 6,
                                    padding: "10px 12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 600,
                                      color: "#a78bfa",
                                      marginBottom: 6,
                                    }}
                                  >
                                    💡 Suggested Deposition Topics
                                  </div>
                                  {(
                                    DEPO_TOPICS[w.id] || DEPO_TOPICS.default
                                  ).map((t, ti) => (
                                    <div
                                      key={ti}
                                      style={{
                                        display: "flex",
                                        gap: 6,
                                        alignItems: "flex-start",
                                        marginBottom: 4,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 8,
                                          color: "#a78bfa",
                                          flexShrink: 0,
                                          marginTop: 1,
                                        }}
                                      >
                                        {ti + 1}.
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 8,
                                          color: th.muted,
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {t}
                                      </span>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => {
                                      const topics = (
                                        DEPO_TOPICS[w.id] || DEPO_TOPICS.default
                                      )
                                        .map((t, i) => `${i + 1}. ${t}`)
                                        .join("\n");
                                      navigator.clipboard &&
                                        navigator.clipboard.writeText(
                                          `Suggested Deposition Topics - ${w.name}\n\n` +
                                            topics,
                                        );
                                    }}
                                    style={{
                                      marginTop: 8,
                                      fontSize: 7,
                                      background: "#a78bfa22",
                                      color: "#a78bfa",
                                      border: "1px solid #a78bfa44",
                                      borderRadius: 3,
                                      padding: "2px 8px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    📋 Copy Topics
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                      {depoWitness && (
                        <div
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,.65)",
                            zIndex: 400,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => setDepoWitness(null)}
                        >
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              background: th.surface,
                              border: `1px solid ${th.border}`,
                              borderRadius: 12,
                              padding: 24,
                              width: 440,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                marginBottom: 2,
                              }}
                            >
                              Notice of Deposition
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.dim,
                                marginBottom: 14,
                              }}
                            >
                              Witness: {depoWitness.name}
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 8,
                                marginBottom: 8,
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.dim,
                                    marginBottom: 3,
                                  }}
                                >
                                  Date
                                </div>
                                <input
                                  type="date"
                                  value={depoDate}
                                  onChange={(e) => setDepoDate(e.target.value)}
                                  style={{
                                    width: "100%",
                                    padding: 6,
                                    fontSize: 10,
                                    background: th.bg,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 4,
                                    color: th.text,
                                    outline: "none",
                                    boxSizing: "border-box",
                                  }}
                                />
                              </div>
                              <div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.dim,
                                    marginBottom: 3,
                                  }}
                                >
                                  Time
                                </div>
                                <input
                                  type="time"
                                  value={depoTime}
                                  onChange={(e) => setDepoTime(e.target.value)}
                                  style={{
                                    width: "100%",
                                    padding: 6,
                                    fontSize: 10,
                                    background: th.bg,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 4,
                                    color: th.text,
                                    outline: "none",
                                    boxSizing: "border-box",
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.dim,
                                  marginBottom: 3,
                                }}
                              >
                                Location
                              </div>
                              <textarea
                                value={depoLoc}
                                onChange={(e) => setDepoLoc(e.target.value)}
                                rows={3}
                                style={{
                                  width: "100%",
                                  padding: 6,
                                  fontSize: 9,
                                  background: th.bg,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 4,
                                  color: th.text,
                                  outline: "none",
                                  boxSizing: "border-box",
                                  resize: "vertical",
                                  fontFamily: "inherit",
                                  lineHeight: 1.5,
                                }}
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 6,
                                justifyContent: "flex-end",
                              }}
                            >
                              <button
                                onClick={() => setDepoWitness(null)}
                                style={{
                                  padding: "5px 12px",
                                  fontSize: 9,
                                  background: th.border,
                                  color: th.muted,
                                  border: "none",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={async () => {
                                  const dn = depoWitness.name;
                                  openP(
                                    "Notice of Deposition - " + dn,
                                    "",
                                    dn,
                                    true,
                                  );
                                  setDepoWitness(null);
                                  try {
                                    const rsp = await fetch("/api/messages", {
                                      method: "POST",
                                      headers: {
                                        "Content-Type": "application/json",
                                      },
                                      body: JSON.stringify({
                                        model: "claude-sonnet-4-20250514",
                                        max_tokens: 1800,
                                        system:
                                          "Draft a complete formal Notice of Deposition per FRCP 30. Include: full case caption, notice to all parties, deponent name, date/time/location, specific topics for examination based on the witness role, document requests, and certificate of service.",
                                        messages: [
                                          {
                                            role: "user",
                                            content:
                                              "Case: " +
                                              (isJuul
                                                ? "Seliavski v. JUUL Labs No. 3:19-md-02913-WHO (N.D. Cal.)"
                                                : isIle
                                                  ? "USA v. Chidambaran & Naqvi No. 1:26-cr-00097-NGG (E.D.N.Y.)"
                                                  : caseId === "musk"
                                                    ? "Musk v. Altman No. 4:24-cv-04722-YGR (N.D. Cal.)"
                                                    : "Neurocentria v. Mando No. 2:25-cv-05871-FLA-MAR (C.D. Cal.)") +
                                              ". Witness: " +
                                              dn +
                                              ". Date: " +
                                              depoDate +
                                              ". Time: " +
                                              depoTime +
                                              ". Location: " +
                                              depoLoc +
                                              ".",
                                          },
                                        ],
                                      }),
                                    });
                                    const d = await rsp.json();
                                    openP(
                                      "Notice of Deposition - " + dn,
                                      d.content
                                        .filter((b) => b.type === "text")
                                        .map((b) => b.text)
                                        .join("")
                                        .trim(),
                                      dn,
                                      false,
                                    );
                                  } catch (e) {
                                    openP(
                                      "Error",
                                      "Could not generate. Try again.",
                                      dn,
                                      false,
                                    );
                                  }
                                }}
                                style={{
                                  padding: "5px 14px",
                                  fontSize: 9,
                                  background: "#1e40af",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                Generate Notice
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* LOCATIONS */}
                  {rbP === "locations" && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <h2
                          style={{ fontSize: 15, fontWeight: 700, margin: 0 }}
                        >
                          Locations
                        </h2>
                        <div style={{ display: "flex", gap: 5 }}>
                          {locEditMode && (
                            <button
                              onClick={() => {
                                const sorted = [...aL]
                                  .filter(
                                    (l) =>
                                      l.name && !l.name.includes("Local Rules"),
                                  )
                                  .sort((a, b) => a.name.localeCompare(b.name));
                                setLocOrder(sorted.map((l) => l.id));
                              }}
                              style={{
                                padding: "3px 9px",
                                fontSize: 8,
                                background: th.surface,
                                color: th.dim,
                                border: `1px solid ${th.border}`,
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              A→Z
                            </button>
                          )}
                          {locEditMode && (
                            <button
                              onClick={() => setLocOrder(null)}
                              style={{
                                padding: "3px 9px",
                                fontSize: 8,
                                background: th.surface,
                                color: th.dim,
                                border: `1px solid ${th.border}`,
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={() => setLocEditMode((m) => !m)}
                            style={{
                              fontSize: 8,
                              padding: "3px 9px",
                              background: locEditMode ? "#f59e0b" : "#1e40af",
                              color: "#fff",
                              border: "none",
                              borderRadius: 3,
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            {locEditMode ? "✓ Done" : "✏️ Edit"}
                          </button>
                          {!locEditMode && (
                            <button
                              onClick={() => {
                                const nm = prompt("Location name:");
                                if (nm)
                                  setCustomLocs((p) => [
                                    ...p,
                                    {
                                      id: "loc" + Date.now(),
                                      name: nm,
                                      area: "User Added",
                                      notes: "Custom location.",
                                      _custom: true,
                                    },
                                  ]);
                              }}
                              style={{
                                fontSize: 8,
                                padding: "3px 9px",
                                background: "#1e40af",
                                color: "#fff",
                                border: "none",
                                borderRadius: 3,
                                cursor: "pointer",
                              }}
                            >
                              ＋ Add Location
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill,minmax(220px,1fr))",
                          gap: 8,
                        }}
                      >
                        {(locOrder
                          ? [...aL].sort(
                              (a, b) =>
                                locOrder.indexOf(a.id) - locOrder.indexOf(b.id),
                            )
                          : aL
                        )
                          .filter(
                            (l) => l.name && !l.name.includes("Local Rules"),
                          )
                          .map((l, li) => (
                            <div
                              key={l.id}
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${locEditMode ? "#f59e0b44" : l._custom ? "#a78bfa44" : th.border}`,
                                padding: "12px 14px",
                              }}
                            >
                              {locEditMode && (
                                <div
                                  style={{
                                    background: "#f59e0b0a",
                                    borderBottom: "1px solid #f59e0b22",
                                    padding: "5px 0 8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ display: "flex", gap: 4 }}>
                                    <button
                                      onClick={() =>
                                        setLocOrder((p) => {
                                          const base = p
                                            ? [...p]
                                            : aL
                                                .filter(
                                                  (x) =>
                                                    x.name &&
                                                    !x.name.includes(
                                                      "Local Rules",
                                                    ),
                                                )
                                                .map((x) => x.id);
                                          const i = base.indexOf(l.id);
                                          if (i <= 0) return base;
                                          const n = [...base];
                                          [n[i - 1], n[i]] = [n[i], n[i - 1]];
                                          return n;
                                        })
                                      }
                                      disabled={li === 0}
                                      style={{
                                        width: 22,
                                        height: 22,
                                        fontSize: 10,
                                        background: th.surface,
                                        border: `1px solid ${th.border}`,
                                        borderRadius: 3,
                                        cursor: "pointer",
                                        color: th.dim,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      onClick={() =>
                                        setLocOrder((p) => {
                                          const base = p
                                            ? [...p]
                                            : aL
                                                .filter(
                                                  (x) =>
                                                    x.name &&
                                                    !x.name.includes(
                                                      "Local Rules",
                                                    ),
                                                )
                                                .map((x) => x.id);
                                          const i = base.indexOf(l.id);
                                          if (i < 0) return base;
                                          const n = [...base];
                                          [n[i], n[i + 1]] = [n[i + 1], n[i]];
                                          return n;
                                        })
                                      }
                                      disabled={
                                        li ===
                                        aL.filter(
                                          (x) =>
                                            x.name &&
                                            !x.name.includes("Local Rules"),
                                        ).length -
                                          1
                                      }
                                      style={{
                                        width: 22,
                                        height: 22,
                                        fontSize: 10,
                                        background: th.surface,
                                        border: `1px solid ${th.border}`,
                                        borderRadius: 3,
                                        cursor: "pointer",
                                        color: th.dim,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                  <button
                                    onClick={() =>
                                      setConfirmRemove({
                                        name: l.name,
                                        action: () => {
                                          if (l._custom)
                                            setCustomLocs((p) =>
                                              p.filter((x) => x.id !== l.id),
                                            );
                                          else
                                            setLocOrder((p) => {
                                              const base = p
                                                ? [...p]
                                                : aL
                                                    .filter(
                                                      (x) =>
                                                        x.name &&
                                                        !x.name.includes(
                                                          "Local Rules",
                                                        ),
                                                    )
                                                    .map((x) => x.id);
                                              return base.filter(
                                                (x) => x !== l.id,
                                              );
                                            });
                                        },
                                      })
                                    }
                                    style={{
                                      fontSize: 7,
                                      background: "#ef444415",
                                      color: "#ef4444",
                                      border: "1px solid #ef444433",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ✕ Remove
                                  </button>
                                </div>
                              )}
                              <div style={{ fontSize: 11, fontWeight: 600 }}>
                                📍 {l.name}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.dim,
                                  fontFamily: "monospace",
                                  marginTop: 2,
                                }}
                              >
                                {l.area}
                              </div>
                              <p
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  margin: "4px 0 8px",
                                  lineHeight: 1.4,
                                }}
                              >
                                {l.notes}
                              </p>
                              {!locEditMode && (
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button
                                    onClick={() => {
                                      setUploadAssoc({ wit: "", loc: l.name });
                                      setShowUpload(true);
                                    }}
                                    style={{
                                      fontSize: 7,
                                      background: th.surface,
                                      color: th.dim,
                                      border: `1px solid ${th.border}`,
                                      borderRadius: 3,
                                      padding: "2px 7px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Add Asset
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {/* MOTIONS */}
                  {rbP === "motions" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 8px",
                        }}
                      >
                        Motions
                      </h2>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {[
                          ["all", "All"],
                          ["Plaintiff", "Plaintiff"],
                          ["Defendant", "Defendant"],
                          ["Both", "Both"],
                        ].map(([k, l]) => (
                          <button
                            key={k}
                            onClick={() => setMFilter(k)}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              fontWeight: mFilter === k ? 600 : 400,
                              color: mFilter === k ? "#3b82f6" : th.dim,
                              background:
                                mFilter === k ? "#3b82f615" : "transparent",
                              border: `1px solid ${mFilter === k ? "#3b82f6" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                              fontFamily: "monospace",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                      {(isJuul
                        ? [
                            {
                              id: "m3",
                              party: "Defendant",
                              title: "Motions for Summary Judgment",
                              date: "May-Aug 2025",
                              status: "Under Submission",
                              cat: "Dispositive",
                              desc: "JLI moved for SJ on SFUSD causation/damages (Pak depo admissions) and individual PI causation. Heard Oct 31, 2025 (43 min, Dkt.4450). Written order to follow.",
                            },
                            {
                              id: "m7",
                              party: "Plaintiff",
                              title: "Meta Account Data Production",
                              date: "Apr 2026",
                              status: "Pending",
                              cat: "Discovery",
                              desc: "Court ordered production of decedent Meta account data Apr 14, 2026. Meta deadline extended to May 8, 2026 by stipulation Dkt.4497 (filed Apr 24, 2026).",
                            },
                            {
                              id: "m4",
                              party: "Plaintiff",
                              title: "Motions to Remand Opt-Out Cases",
                              date: "Apr 2025",
                              status: "Under Submission",
                              cat: "Remand",
                              desc: "Opt-out plaintiffs seek remand to transferee courts for state law claims. Heard Apr 30, 2025 (36 min, Dkt.4406). Motions taken under submission.",
                            },
                            {
                              id: "m6",
                              party: "Plaintiff",
                              title:
                                "Motion for Extension - Supplemental Discovery",
                              date: "Feb 2026",
                              status: "Granted",
                              cat: "Discovery",
                              desc: "Opt-out plaintiffs (Dupree, Tortorici, Sedgwick et al.) sought extended time for supplemental discovery. Granted Feb 26, 2026 (Dkt.4488). Compliance filed Mar 19, 2026 (Dkt.4490).",
                            },
                            {
                              id: "m5",
                              party: "Defendant",
                              title: "Motion to Dismiss Non-Opt-Out Plaintiffs",
                              date: "Apr 2025",
                              status: "Granted",
                              cat: "Dismissal",
                              desc: "JLI moved to dismiss plaintiffs who failed to opt out. Granted May 13, 2025 (Dkt.4412) - cleared non-participating cases from MDL docket.",
                            },
                            {
                              id: "m2",
                              party: "Both",
                              title:
                                "Class Certification + JLI/Altria Settlements",
                              date: "2021-2023",
                              status: "Granted",
                              cat: "Class",
                              desc: "SFUSD and consumer class certified. $255M JLI class settlement and separate Altria class settlement approved. Further distribution authorized Mar 6, 2026 (Dkt.4489).",
                            },
                            {
                              id: "m1",
                              party: "Defendant",
                              title: "Motion to Stay MDL Pending FDA Action",
                              date: "May 2020",
                              status: "Denied",
                              cat: "Jurisdictional",
                              desc: "JLI sought to stay the MDL pending FDA PMTA review. Orrick denied - courts not FDA adjudicate youth marketing tort claims (Dkt.626, May 2020).",
                            },
                          ]
                        : isIle
                          ? [
                              {
                                id: "il1",
                                party: "Both",
                                title:
                                  "Superseding Indictment S-1 (adds Count 11s vs Chidambaran only)",
                                dkt: 57,
                                movant: "Government",
                                opponent: "Both",
                                status: "Filed",
                                detail:
                                  "Thirty pages; forfeiture allegations; arraigned 7/16 - not guilty. Frames Naqvi severance and the CEO plea-pressure architecture.",
                              },
                              {
                                id: "il2",
                                party: "Defendant",
                                title: "Naqvi Bail Package - $3,000,022",
                                dkt: 33,
                                movant: "Naqvi",
                                opponent: "Government",
                                status: "Granted",
                                detail:
                                  "Three homes with confessions of judgment + $1,035,000 unsecured; released 5/14 - home detention, GPS, passport surrendered.",
                              },
                              {
                                id: "il3",
                                party: "Defendant",
                                title: "Chidambaran Consented $15M Package",
                                dkt: 35,
                                movant: "Chidambaran",
                                opponent: "Government (consent)",
                                status: "Granted",
                                detail:
                                  "Partially secured appearance bond; home confinement in Potomac MD.",
                              },
                              {
                                id: "il4",
                                party: "Defendant",
                                title: "Travel Application #1 - Bay Area",
                                dkt: 59,
                                movant: "Naqvi",
                                opponent: "Government",
                                status: "Denied",
                                detail:
                                  "Short notice; denied 7/22. The lesson is encoded in the renewed leave-first application.",
                              },
                              {
                                id: "il5",
                                party: "Defendant",
                                title: "Renewed Travel Application (PENDING)",
                                dkt: 60,
                                movant: "Naqvi",
                                opponent: "Government (no position)",
                                status: "Pending",
                                detail:
                                  "Weeks of Aug 17/24; five companies incl. Patent VC; PTS defers. Grant requires an itinerary letter and PTS approval before travel.",
                              },
                              {
                                id: "il6",
                                party: "Defendant",
                                title:
                                  "Anticipated: Rule 14 Severance (Count 11s)",
                                dkt: 0,
                                movant: "Naqvi",
                                opponent: "Government",
                                status: "Anticipated",
                                detail:
                                  "Bruton overlay on the ¶30 statement; file per the 9/14 schedule.",
                              },
                            ]
                          : caseId === "musk"
                            ? [
                                {
                                  id: "mk1",
                                  party: "Plaintiff",
                                  title:
                                    "Motion for Preliminary Injunction re For-Profit Conversion",
                                  dkt: 46,
                                  movant: "Plaintiff",
                                  opponent: "Defendants",
                                  status: "Denied",
                                  detail:
                                    "Musk sought to enjoin OpenAI for-profit conversion. Denied March 4 2025 (Dkt.121) -- YGR found no irreparable harm shown.",
                                },
                                {
                                  id: "mk2",
                                  party: "Both",
                                  title: "Defendants Motions to Dismiss SAC",
                                  dkt: 140,
                                  movant: "Defendant",
                                  opponent: "Plaintiff",
                                  status: "Partial",
                                  detail:
                                    "OpenAI and Microsoft MTDs. Antitrust counts (VIII-XV) stayed for Phase 2. Remaining counts (XVIII charitable trust, IV unjust enrichment, XIX aiding & abetting) proceed to trial.",
                                },
                                {
                                  id: "mk3",
                                  party: "Both",
                                  title: "Daubert Motions -- Expert Testimony",
                                  dkt: 420,
                                  movant: "Both",
                                  opponent: "Both",
                                  status: "Ruled",
                                  detail:
                                    "Daubert ruling Apr 8 2026: Dudney (FIFO tracing) allowed; Garza excluded; Henningfield limited to clinical pharmacology only. Defense experts allowed on SOL and valuation.",
                                },
                                {
                                  id: "mk4",
                                  party: "Plaintiff",
                                  title:
                                    "Section 17510.8 Applicability Brief (Dkt.515)",
                                  dkt: 515,
                                  movant: "Plaintiff",
                                  opponent: "Defendants",
                                  status: "Pending",
                                  detail:
                                    "Competing briefs filed Apr 30 2026 (Dkt.515/516). Plaintiff argues Cal. Bus. & Prof. Code section 17510.8 imposes strict liability for false representations in charitable solicitations -- would significantly strengthen Count XVIII.",
                                },
                                {
                                  id: "mk5",
                                  party: "Both",
                                  title: "Phase 1 Trial -- Counts XVIII IV XIX",
                                  dkt: null,
                                  movant: "Both",
                                  opponent: "Both",
                                  status: "Trial",
                                  detail:
                                    "Trial in progress. Phase 1: Count XVIII (breach of charitable trust), Count IV (unjust enrichment), Count XIX (aiding & abetting vs Microsoft). Phase 2 antitrust counts stayed. Trial commenced April 28 2026.",
                                },
                              ]
                            : aM
                      )
                        .filter(
                          (m) =>
                            mFilter === "all" ||
                            m.party === mFilter ||
                            m.party === "Both",
                        )
                        .map((m, i) => (
                          <div
                            key={i}
                            onClick={() =>
                              openP(
                                m.title,
                                m.desc + " Status: " + m.status,
                                m.title,
                              )
                            }
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              borderLeft: `3px solid ${m.party === "Plaintiff" ? "#ef4444" : m.party === "Defendant" ? "#3b82f6" : "#f59e0b"}`,
                              padding: "10px 14px",
                              marginBottom: 6,
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 3,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  flex: 1,
                                  lineHeight: 1.4,
                                }}
                              >
                                {m.title}
                              </div>
                              <span
                                style={{
                                  fontSize: 6,
                                  fontFamily: "monospace",
                                  fontWeight: 600,
                                  color:
                                    m.status === "Granted"
                                      ? "#10b981"
                                      : m.status === "Denied"
                                        ? "#ef4444"
                                        : "#f59e0b",
                                  background:
                                    m.status === "Granted"
                                      ? "#10b98118"
                                      : m.status === "Denied"
                                        ? "#ef444418"
                                        : "#f59e0b18",
                                  padding: "2px 5px",
                                  borderRadius: 3,
                                  marginLeft: 6,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {m.status}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 7,
                                color: th.dim,
                                marginBottom: 4,
                              }}
                            >
                              {m.date} · {m.cat}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: th.muted,
                                lineHeight: 1.4,
                              }}
                            >
                              {m.desc}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  {/* CASELAW */}
                  {rbP === "caselaw" && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                        }}
                      >
                        <h2
                          style={{ fontSize: 15, fontWeight: 700, margin: 0 }}
                        >
                          Caselaw
                        </h2>
                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            alignItems: "center",
                          }}
                        >
                          <button
                            onClick={() => setClEditMode((m) => !m)}
                            style={{
                              fontSize: 8,
                              padding: "3px 9px",
                              background: clEditMode ? "#f59e0b" : th.surface,
                              color: clEditMode ? "#fff" : th.dim,
                              border: `1px solid ${clEditMode ? "#f59e0b" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                              fontWeight: clEditMode ? 600 : 400,
                            }}
                          >
                            {clEditMode ? "✓ Done" : "✏️ Edit"}
                          </button>
                          {!clEditMode && (
                            <label
                              style={{
                                fontSize: 8,
                                padding: "3px 9px",
                                background: "#1e40af",
                                color: "#fff",
                                borderRadius: 3,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              📤 Upload Case
                              <input
                                type="file"
                                accept=".pdf,.txt"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const f = e.target.files[0];
                                  if (!f) return;
                                  const reader = new FileReader();
                                  reader.onload = (ev) => {
                                    const preview = String(
                                      ev.target.result,
                                    ).slice(0, 400);
                                    setCustomCL((p) => [
                                      ...p,
                                      {
                                        id: `cl${Date.now()}`,
                                        cat: "Uploaded",
                                        title: f.name.replace(/\.[^.]+$/, ""),
                                        cite: "Uploaded: " + f.name,
                                        holding: preview,
                                        party: "Both",
                                        color: "#a78bfa",
                                      },
                                    ]);
                                  };
                                  reader.readAsText(f);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {[
                          ["all", "All"],
                          ["Plaintiff", "Plaintiff"],
                          ["Defendant", "Defendant"],
                          ["Both", "Both"],
                        ].map(([k, l]) => (
                          <button
                            key={k}
                            onClick={() => setClFilter(k)}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              fontWeight: clFilter === k ? 600 : 400,
                              color: clFilter === k ? "#3b82f6" : th.dim,
                              background:
                                clFilter === k ? "#3b82f615" : "transparent",
                              border: `1px solid ${clFilter === k ? "#3b82f6" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                              fontFamily: "monospace",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                        }}
                      >
                        {[...aCL, ...(customCL || [])]
                          .filter(
                            (c) => clFilter === "all" || c.party === clFilter,
                          )
                          .map((c, i) => (
                            <div
                              key={i}
                              onClick={() =>
                                openP(
                                  c.title || c.cs || "Case",
                                  `**${c.title || c.cs}**\n${c.cite || ""}\n\n**Holding:** ${c.holding || "See full opinion."}\n\n**Party citing:** ${c.party || "Both"}\n\n${c.quote ? `**Key Quote:**\n"${c.quote}"\n\n` : ""}${c.doc ? "**Document:** " + c.doc + "\n\n" : ""}---\n**To view full opinion:** Search ${c.cite || c.title} on Westlaw, Lexis, or CourtListener.\nIf you have a PDF, upload it via 📤 Upload Case above.`,
                                  c.cite,
                                )
                              }
                              style={{
                                cursor: "pointer",
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                borderLeft: `3px solid ${c.color || "#3b82f6"}`,
                                padding: "10px 12px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: 2,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 600,
                                    flex: 1,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {c.title}
                                </div>
                                {c.party && c.party !== "Both" && (
                                  <span
                                    style={{
                                      fontSize: 6,
                                      fontFamily: "monospace",
                                      color:
                                        c.party === "Plaintiff"
                                          ? "#ef4444"
                                          : "#3b82f6",
                                      background:
                                        c.party === "Plaintiff"
                                          ? "#ef444418"
                                          : "#3b82f618",
                                      padding: "1px 4px",
                                      borderRadius: 2,
                                      marginLeft: 4,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {c.party}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.dim,
                                  marginBottom: 4,
                                }}
                              >
                                {c.cite}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  lineHeight: 1.4,
                                  marginBottom: 6,
                                }}
                              >
                                {(c.holding || "").slice(0, 110)}
                                {(c.holding || "").length > 110 ? "..." : ""}
                              </div>
                              {c.doc && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: 2,
                                    paddingTop: 5,
                                    borderTop: `1px solid ${th.border}`,
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      const asset = aA.find(
                                        (a) =>
                                          a.title &&
                                          (c.doc
                                            ? c.doc
                                                .split(";")
                                                .some((d) =>
                                                  a.title.includes(
                                                    d
                                                      .trim()
                                                      .replace(/[()]/g, "")
                                                      .split(" ")[0],
                                                  ),
                                                )
                                            : false),
                                      );
                                      if (asset) {
                                        openP(
                                          asset.title,
                                          asset.desc,
                                          asset.title,
                                        );
                                      } else {
                                        openP(
                                          "Source: " + c.doc,
                                          c.holding || "",
                                          "",
                                        );
                                      }
                                    }}
                                    style={{
                                      fontSize: 7,
                                      color: "#3b82f6",
                                      background: "#3b82f615",
                                      border: "1px solid #3b82f633",
                                      borderRadius: 3,
                                      padding: "2px 6px",
                                      cursor: "pointer",
                                      fontFamily: "monospace",
                                      textAlign: "left",
                                    }}
                                  >
                                    📄 {(c.doc || "").split(";")[0].trim()}
                                  </button>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 3,
                                      alignItems: "center",
                                    }}
                                  >
                                    {c.v === true && (
                                      <span
                                        title="Verified citation - holding quoted in source"
                                        style={{
                                          fontSize: 6,
                                          color: "#10b981",
                                          background: "#10b98118",
                                          border: "1px solid #10b98133",
                                          borderRadius: 10,
                                          padding: "1px 5px",
                                        }}
                                      >
                                        ✓ Verified
                                      </span>
                                    )}
                                    {c.v === false && (
                                      <span
                                        title="Citation present but holding not directly quoted"
                                        style={{
                                          fontSize: 6,
                                          color: "#f59e0b",
                                          background: "#f59e0b18",
                                          border: "1px solid #f59e0b33",
                                          borderRadius: 10,
                                          padding: "1px 5px",
                                        }}
                                      >
                                        ~ Cited
                                      </span>
                                    )}
                                    {c.quote && (
                                      <span
                                        title={"Quoted in source: " + c.quote}
                                        style={{
                                          fontSize: 6,
                                          color: "#a78bfa",
                                          background: "#a78bfa18",
                                          border: "1px solid #a78bfa33",
                                          borderRadius: 10,
                                          padding: "1px 5px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          openP(
                                            "Quote from " + c.cs,
                                            c.quote,
                                            c.doc || "",
                                          )
                                        }
                                      >
                                        💬 Quote
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {c.id && String(c.id).startsWith("cl") && (
                                <div
                                  style={{
                                    fontSize: 6,
                                    color: "#a78bfa",
                                    marginTop: 3,
                                  }}
                                >
                                  📎 Uploaded
                                </div>
                              )}
                              {clEditMode && (
                                <button
                                  onClick={() =>
                                    setConfirmRemove({
                                      name: c.title || c.cs || "this case",
                                      action: () => {
                                        if (
                                          c.id &&
                                          String(c.id).startsWith("cl")
                                        )
                                          setCustomCL((p) =>
                                            p.filter((x) => x.id !== c.id),
                                          );
                                      },
                                    })
                                  }
                                  style={{
                                    width: "100%",
                                    marginTop: 6,
                                    fontSize: 8,
                                    background: "#ef444415",
                                    color: "#ef4444",
                                    border: "1px solid #ef444433",
                                    borderRadius: 4,
                                    padding: "4px 0",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  ✕ Remove
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {/* CONTRADICTIONS */}
                  {rbP === "contradictions" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 6px",
                        }}
                      >
                        🔍 Contradiction Detection
                      </h2>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {["Defendant", "Plaintiff"].map((p) => (
                          <button
                            key={p}
                            onClick={() => setCtrPersp(p)}
                            style={{
                              padding: "3px 10px",
                              fontSize: 8,
                              fontWeight: ctrPersp === p ? 600 : 400,
                              background:
                                ctrPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef444418"
                                    : "#3b82f618"
                                  : "transparent",
                              color:
                                ctrPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6"
                                  : th.dim,
                              border: `1px solid ${ctrPersp === p ? (p === "Plaintiff" ? "#ef4444" : "#3b82f6") : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            Favors {p}
                          </button>
                        ))}
                      </div>
                      {(ctrPersp === "Defendant"
                        ? isIle
                          ? [
                              {
                                src1: "Gov Detention Memo (Dkt.16)",
                                q1: "Naqvi presented as an equivalent flight and economic-danger risk to the CEO.",
                                src2: "Apr 22 Hr'g Tr. 8:16-22",
                                q2: "There is no more culpable defendant in this case than this particular defendant.",
                                sev: "Critical",
                                why: "The government differentiated the defendants on the record at bail; parity theming at trial collides with its own transcript.",
                              },
                              {
                                src1: "Detention obstruction theming (Dkt.16)",
                                q1: "Deletion and encrypted-app conduct attributed to both defendants.",
                                src2: "S-1 Count 11s + committee forensic finding",
                                q2: "Count 11s charges Chidambaran alone; items deleted from Naqvi's account appear to be spam.",
                                sev: "High",
                                why: "The charging decision and the company's own expert resolved the obstruction question in Naqvi's favor.",
                              },
                            ]
                          : caseId === "musk"
                            ? [
                                {
                                  src1: "Musk Litigation-as-Competitor-Attack Statement (Apr 25, 2026)",
                                  q1: "Musk initiated settlement contact with Brockman two days before trial, suggesting a mutual drop of claims.",
                                  src2: "Musk Response to Brockman Settlement Proposal (Dkt.522)",
                                  q2: "When Brockman proposed both sides drop claims, Musk replied: By the end of this week, you and Sam will be the most hated men in America. If you insist, so it will be.",
                                  sev: "Critical",
                                  why: "OpenAI filed Dkt.522 to admit this as motive evidence under FRE 408(b). Musk claims this lawsuit is about charitable trust enforcement and AI safety. The message reveals an intent to destroy competitors personally, not vindicate legal rights. Parallels the World War III threat in Pampena v. Musk (Judge Breyer trial, Mar 9 2026) establishing a pattern of litigation-as-intimidation. YGR must now rule on admissibility -- if admitted, it severely undermines Musk credibility with the jury on intent.",
                                },
                                {
                                  src1: "Musk Jan 2016 Email - Yup",
                                  q1: "Sutskever: it will make sense to start being less open. Musk: Yup.",
                                  src2: "Musk Trial Testimony Apr 28 2026",
                                  q2: "Musk testified he donated on the belief OpenAI would remain open-source and nonprofit in perpetuity.",
                                  sev: "Critical",
                                  why: "Musk own one-word reply agreeing with less open in 2016 is the defense single best exhibit. Used to impeach Musk claim he was promised perpetual openness.",
                                },
                                {
                                  src1: "Altman Aug 2018 LP Term Sheet DX-827",
                                  q1: "Altman sent Musk the full for-profit LP term sheet Aug 31 2018. Musk declined equity but raised no objection.",
                                  src2: "Musk Claim: Never Agreed to For-Profit Conversion",
                                  q2: "Musk contends defendants concealed and implemented for-profit structure without his knowledge or consent.",
                                  sev: "Critical",
                                  why: "Musk received the LP term sheet and said nothing. Defense argues his silence is acquiescence. Plaintiff must show charitable mission representations continued after August 2018.",
                                },
                                {
                                  src1: "Altman Mar 2019 Tell Them Im Supportive DX-862",
                                  q1: "Altman sent Musk draft announcement of for-profit arm March 6 2019. Musk replied tell them Im supportive.",
                                  src2: "Musk Claim: Misled About For-Profit Nature",
                                  q2: "Musk maintains he was not informed the for-profit pivot would commercialize OpenAI IP and benefit Microsoft at expense of charitable mission.",
                                  sev: "High",
                                  why: "Tell them Im supportive is a devastating admission. Defense uses it to argue Musk ratified the for-profit structure in March 2019 - triggering limitations period and defeating the trust claim.",
                                },
                              ]
                            : isJuul
                              ? [
                                  {
                                    src1: "JLI CEO Kevin Burns Public Testimony",
                                    q1: "JUUL was never designed for youth. We only want adult smokers to switch.",
                                    src2: "Internal JUUL School Program (Dkt.3447 SOF para 47)",
                                    q2: "JLI trained reps to visit high schools. Monsees on youth uptake metrics board deck distribution list. Buzz campaign targeted social media channels reaching minors.",
                                    sev: "Critical",
                                    why: "Burns sworn denial contradicted by JUUL School documentation and board-level youth KPI tracking. This is the SFUSD case core -- JLI cultivated youth users while denying it publicly. Centerpiece of Dkt.3447 opposition to JLI MSJ.",
                                  },
                                  {
                                    src1: "JLI PMTA Submission to FDA",
                                    q1: "JUUL is a harm-reduction product for adult smokers. Our formulation provides the cigarette experience without the harm.",
                                    src2: "FDA Marketing Denial Order -- Jun 23 2022",
                                    q2: "FDA found JLI provided insufficient toxicological data, could not determine net public health benefit, and failed to demonstrate reduced harm vs combustible cigarettes.",
                                    sev: "High",
                                    why: "JLI built its regulatory compliance defense on FDA deference. MDO (stayed but non-final) shows FDA itself rejected JLI harm-reduction claims. Plaintiffs use this to rebut FCLAA preemption and the primary jurisdiction defense Orrick denied in 2020.",
                                  },
                                  {
                                    src1: "Altria Dec 2018 Shareholder Press Release",
                                    q1: "Altria investing in JUUL to support a harm-reduction product for adult smokers transitioning from combustible cigarettes.",
                                    src2: "Altria Internal Pre-Investment Due Diligence (Dkt.3393 partially unsealed)",
                                    q2: "Altria due diligence files show it knew JUUL user demographics skewed young prior to the $12.8B investment and simultaneous withdrawal of MarkTen and Green Smoke.",
                                    sev: "High",
                                    why: "Altria public harm-reduction narrative contradicts what its due diligence found. This supports RICO enterprise: Altria knew of JLI youth marketing problem and invested anyway, facilitating the enterprise. Market exit was not a legitimate business decision -- it was horizontal market allocation.",
                                  },
                                  {
                                    src1: "JLI Age Verification Program",
                                    q1: "JLI maintained robust age verification on JUUL.com and worked proactively with retailers to prevent underage sales.",
                                    src2: "Congressional Testimony + Seliavski Purchase Record + Halpern-Felsher Study",
                                    q2: "Seliavski bought at 7-Eleven and Circle K at age 16. Congressional hearings established teens bought pods at gas stations and pharmacies. 75% of SFUSD students did not know JUUL contained nicotine.",
                                    sev: "High",
                                    why: "JLI claims age verification precluded youth access. Actual purchase records and the 75% ignorance rate demolish this. If 75% of student users did not know the product contained nicotine, how was any warning adequate?",
                                  },
                                  {
                                    src1: "JUUL Nicotine Content Packaging",
                                    q1: "JUUL pods disclosed nicotine content on packaging consistent with FDA requirements for tobacco products.",
                                    src2: "Halpern-Felsher Expert Survey (SFUSD MSJ Opposition)",
                                    q2: "75% of SFUSD students who used JUUL did not know the product contained nicotine. Students described JUUL pods as just flavored vapor.",
                                    sev: "High",
                                    why: "The failure-to-warn theory in one number: 75% ignorance. JLI argues general public awareness of nicotine in tobacco products. Plaintiffs counter: teens did not perceive JUUL as a tobacco product -- the USB charger form factor and fruit flavors reinforced that misperception. Halpern-Felsher admitted at Dkt.3447; key impeachment of JLI compliance defense.",
                                  },
                                ]
                              : [
                                  {
                                    src1: "Liu Depo p.318",
                                    q1: "We did not invent the molecule.",
                                    src2: "061 Patent Claim 1",
                                    q2: "Claims a dosage form comprising magnesium threonate - implying novel composition.",
                                    sev: "Critical",
                                    why: "Named inventor testimony undermines the foundational premise. Defense uses this at §101 and §103.",
                                  },
                                  {
                                    src1: "Mao YouTube Video",
                                    q1: "Credits Inna Slutsky for accidental discovery.",
                                    src2: "Patent Inventorship",
                                    q2: "Slutsky not named as co-inventor on any Magtein Patent.",
                                    sev: "High",
                                    why: "Inventorship omission renders patents unenforceable under §256.",
                                  },
                                  {
                                    src1: "Product Labeling",
                                    q1: "Manufactured in the USA",
                                    src2: "Certificates of Analysis",
                                    q2: "China identified as country of origin for MgLT hydrate.",
                                    sev: "Critical",
                                    why: "Documentary proof of false origin claims. Combined with ImportGenius routing data, this supports fraud and Lanham Act counterclaims.",
                                  },
                                ]
                        : isIle
                          ? [
                              {
                                src1: "S-1 ¶¶46-48 concealment theory",
                                q1: "Transactions staged to mislead Lender B into believing customer payments were real.",
                                src2: "Count TEN (Oct 11, 2024 texts)",
                                q2: "Lender B representatives approve release of approximately $2.5M to Entity 1.",
                                sev: "High",
                                why: "A transfer the lender approved in writing is hard to cast as concealment from that lender; the chronology favors the defense.",
                              },
                              {
                                src1: "Play-ball fragment (gov presentation)",
                                q1: "Fragment framed as an instruction to restart the scheme.",
                                src2: "Dkt.17 Ex.1 full chain (produced by Naqvi)",
                                q2: "Read in full, the chain relays a prospective investor's preconditions.",
                                sev: "High",
                                why: "FRE 106 completeness defeats the fragment; the voluntary production is itself the credibility argument.",
                              },
                            ]
                          : caseId === "musk"
                            ? [
                                {
                                  src1: "Brockman Private Journal PX163",
                                  q1: "Brockman private journal: it would be wrong to steal the non-profit from Elon",
                                  src2: "OpenAI Litigation Position",
                                  q2: "OpenAI contends no promises were made about perpetual nonprofit status. Donations were unrestricted.",
                                  sev: "Critical",
                                  why: "Brockman own handwritten admission directly contradicts OpenAI litigation position. Admitted at trial as PX163. Written before litigation with no motive to deceive.",
                                },
                                {
                                  src1: "Kevin Scott Email to Nadella PX230",
                                  q1: "Kevin Scott to Nadella Mar 7 2018: I wonder if the big OpenAI donors are aware of these plans? I can not imagine they funded an open effort to then go build a closed for profit thing.",
                                  src2: "Microsoft Position: No Knowledge of Trust Conditions",
                                  q2: "Microsoft contends it had no knowledge of any charitable trust conditions or special promises made to Musk, and conducted extensive due diligence.",
                                  sev: "Critical",
                                  why: "Microsoft CTO put in writing exactly the concern that donors like Musk were unaware their donations would fund a closed for-profit enterprise - 16 months before the July 2019 first billion investment.",
                                },
                                {
                                  src1: "Altman Tech Belongs to World Email PX003",
                                  q1: "Altman to Musk May 2015: Some sort of nonprofit, where tech belongs to the world and technology owned by the foundation.",
                                  src2: "OpenAI Defense: Unrestricted Gifts",
                                  q2: "OpenAI contends Musk donations were unrestricted gifts with no conditions attached. Musk had no legal right to dictate organizational structure.",
                                  sev: "High",
                                  why: "OpenAI claim that donations were unrestricted directly conflicts with Altman founding pitch to Musk. Plaintiff argues these representations are the consideration for the charitable trust.",
                                },
                              ]
                            : isJuul
                              ? [
                                  {
                                    src1: "SFUSD 30(b)(6) -- Quarry Pak Deposition Dec 21 2021",
                                    q1: "When asked what costs SFUSD incurred addressing the JUUL crisis, Pak testified: We do not have that information. Superintendent Matthews: unaware of any document that quantifies staff time.",
                                    src2: "SFUSD MSJ Opposition (Dkt.3447) + Dorn Expert Report",
                                    q2: "SFUSD claims $34,875 documented direct expenditures + $78.8M forward-looking abatement (Dorn site inspection of 6 schools) + tens of millions in staff diversion and curriculum development.",
                                    sev: "Critical",
                                    why: "JLI SJ centerpiece on SFUSD damages (Dkt.3396). Pak admission from SFUSD own witness that it cannot quantify past costs directly contradicts the damages model. JLI also argues Sonner v. Premier Nutrition bars equitable abatement when no adequate legal remedy exists. SFUSD must show Pak spoke to line-item tracking only, not total harm, and that Dorn provides foundation for trial.",
                                  },
                                  {
                                    src1: "Seliavski Personal Injury Complaint",
                                    q1: "Plaintiff alleges nicotine addiction, poisoning, and behavioral injuries directly caused by JUUL products used at age 16 from July 2017 through Spring 2018.",
                                    src2: "Causation Expert Record",
                                    q2: "No medical causation expert has been identified, retained, or disclosed for Seliavski PI claims. No expert report on specific causation linking JUUL specifically to her injuries.",
                                    sev: "Critical",
                                    why: "JLI moved for SJ on PI causation (Oct 31 2025 hearing). Without a qualifying medical causation expert, Seliavski cannot establish that JUUL specifically caused her injuries. Must retain and disclose before remand to E.D. Va. or face near-certain SJ on personal injury damages. Single most urgent action item in the MDL.",
                                  },
                                  {
                                    src1: "JLI FCLAA Preemption Defense",
                                    q1: "The Federal Cigarette Labeling and Advertising Act preempts all state law warning claims against tobacco product advertisers. Congress occupied the field with uniform federal warnings.",
                                    src2: "Orrick 2020 Primary Jurisdiction Denial + Plaintiff FCLAA Rebuttal",
                                    q2: "Judge Orrick denied JLI stay on FDA primary jurisdiction grounds in 2020. However, Ninth Circuit has not ruled definitively on FCLAA preemption scope for e-cigarettes, and the statute was written for cigarettes, not e-vapor products.",
                                    sev: "High",
                                    why: "FCLAA preemption, if successful, eliminates all state-law failure-to-warn claims. This is a threshold dispositive issue that would reshape the entire case. Orrick denied the 2020 stay but did not rule on FCLAA merits. Plaintiffs must argue JUUL advertising does not qualify as cigarette advertising under FCLAA.",
                                  },
                                  {
                                    src1: "Seliavski Virginia Statute of Limitations",
                                    q1: "Virginia applies a 2-year personal injury SOL. Seliavski stopped using JUUL in Spring 2018, meaning the SOL would have run by Spring 2020 absent tolling.",
                                    src2: "Seliavski Complaint Filed -- MDL",
                                    q2: "Seliavski filed a short-form complaint in the MDL in 2021. MDL direct filing and American Pipe tolling arguments were invoked to preserve her claims.",
                                    sev: "High",
                                    why: "JLI will raise SOL as threshold bar in remand E.D. Va. proceeding. American Pipe tolling from the class action filing tolls limitations for class members -- but Seliavski is an opt-out plaintiff. Need to establish whether she filed before or after the SOL ran, and whether discovery rule or fraudulent concealment tolled the period.",
                                  },
                                ]
                              : [
                                  {
                                    src1: "FAC par 8",
                                    q1: "ThreoTech holds all rights by assignment.",
                                    src2: "FAC par 13",
                                    q2: "Neurocentria owns the same patents.",
                                    sev: "High",
                                    why: "Defendant exploits this to challenge standing.",
                                  },
                                  {
                                    src1: "TMLA Section 5(a)",
                                    q1: "AIDP owns or has license to the marks.",
                                    src2: "Prior Rule 11 Representations",
                                    q2: "AIDP represented all rights in 3 federal courts.",
                                    sev: "High",
                                    why: "Defendant argues hedge language undermines chain of title.",
                                  },
                                ]
                      ).map((c, i) => (
                        <div
                          key={i}
                          style={{
                            background: th.card,
                            borderRadius: 8,
                            border: `1px solid ${th.border}`,
                            borderLeft: `3px solid ${c.sev === "Critical" ? "#ef4444" : "#f59e0b"}`,
                            padding: "12px 16px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 6,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 7,
                                fontFamily: "monospace",
                                fontWeight: 600,
                                color:
                                  c.sev === "Critical" ? "#ef4444" : "#f59e0b",
                                background:
                                  c.sev === "Critical"
                                    ? "#ef444418"
                                    : "#f59e0b18",
                                padding: "2px 6px",
                                borderRadius: 3,
                              }}
                            >
                              ⚠️ {c.sev}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                background: th.surface,
                                borderRadius: 4,
                                padding: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.dim,
                                  marginBottom: 3,
                                }}
                              >
                                {c.src1}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: th.text,
                                  fontStyle: "italic",
                                }}
                              >
                                {c.q1}
                              </div>
                            </div>
                            <div
                              style={{
                                background: th.surface,
                                borderRadius: 4,
                                padding: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.dim,
                                  marginBottom: 3,
                                }}
                              >
                                {c.src2}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: th.text,
                                  fontStyle: "italic",
                                }}
                              >
                                {c.q2}
                              </div>
                            </div>
                          </div>
                          <p
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.5,
                              margin: "8px 0 6px",
                            }}
                          >
                            {c.why}
                          </p>
                          <div style={{ display: "flex", gap: 3 }}>
                            <button
                              onClick={() =>
                                openP(
                                  "Use in Motion: " + c.src1 + " vs " + c.src2,
                                  `**CONTRADICTION EVIDENCE**

**Source A:** ${c.src1}
> "${c.q1}"

**Source B:** ${c.src2}
> "${c.q2}"

**Why It Matters:** ${c.why}

---
**Suggested Motion Use:**
This contradiction is suitable for:
• Summary Judgment opposition - demonstrates genuine dispute of material fact
• Motion in Limine - to preclude inconsistent testimony at trial
• Cross-examination impeachment outline

**Docket entries to attach:**
${c.src2.includes("Dkt") ? c.src2 : "See case record - search docket for: " + c.src2}

**Action buttons:**
→ Prepare a response brief citing this contradiction
→ Search related docket entries
→ Flag for deposition cross-examination`,
                                  c.src1,
                                )
                              }
                              style={{
                                fontSize: 7,
                                color: "#3b82f6",
                                background: "#3b82f615",
                                border: "1px solid #3b82f633",
                                borderRadius: 3,
                                cursor: "pointer",
                                padding: "2px 6px",
                              }}
                            >
                              Use in Motion
                            </button>
                            <button
                              onClick={() =>
                                openP(
                                  "Depo Outline: " + c.src1 + " vs " + c.src2,
                                  `**DEPOSITION OUTLINE ENTRY**

**Contradiction to Exploit:**
${c.src1} vs. ${c.src2}

**Source A Quote:**
"${c.q1}"

**Source B Quote (Impeachment):**
"${c.q2}"

**Why This Matters for Depo:** ${c.why}

---
**Suggested Deposition Questions:**
1. "You testified [Source A quote] - is that accurate?"
2. "Are you familiar with [Source B document]?"
3. "Does [Source B quote] refresh your recollection?"
4. "How do you reconcile these two statements?"
5. "Did you ever communicate with [witness] about this inconsistency?"

**Documents to mark as exhibits:**
• ${c.src1}
• ${c.src2}
${c.src2.includes("Dkt") ? "\n**Pull from PACER:** " + c.src2 : ""}`,
                                  c.src2,
                                )
                              }
                              style={{
                                fontSize: 7,
                                color: "#f59e0b",
                                background: "#f59e0b15",
                                border: "1px solid #f59e0b33",
                                borderRadius: 3,
                                cursor: "pointer",
                                padding: "2px 6px",
                              }}
                            >
                              Add to Depo Outline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* OPPONENT PLAYBOOK */}
                  {rbP === "playbook" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 6px",
                        }}
                      >
                        🎯 Opponent Playbook
                      </h2>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {["Defendant", "Plaintiff"].map((p) => (
                          <button
                            key={p}
                            onClick={() => setPbPersp(p)}
                            style={{
                              padding: "3px 10px",
                              fontSize: 8,
                              fontWeight: pbPersp === p ? 600 : 400,
                              background:
                                pbPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef444418"
                                    : "#3b82f618"
                                  : "transparent",
                              color:
                                pbPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6"
                                  : th.dim,
                              border: `1px solid ${pbPersp === p ? (p === "Plaintiff" ? "#ef4444" : "#3b82f6") : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            {p} View
                          </button>
                        ))}
                      </div>
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 8,
                          border: `1px solid ${th.border}`,
                          borderLeft: "3px solid #f59e0b",
                          padding: "14px 18px",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 4,
                          }}
                        >
                          {isJuul
                            ? pbPersp === "Defendant"
                              ? "Predicted: JLI Sonner-Pak Two-Step + Suppress Remand Record"
                              : "Predicted: SFUSD Geographic Causation + PI Expert Retained = Remand Leverage"
                            : isIle
                              ? pbPersp === "Defendant"
                                ? "Predicted: Defense files the severance/Bruton package plus a §225 construction motion at the 9/14 schedule, trading STA consent for firm Feb 2027 dates - 75% probability"
                                : "Predicted: Government locks CC-2/CC-3 cooperation and floats a differentiated non-1s track for the CFO before the schedule hardens - 65% probability"
                              : caseId === "musk"
                                ? pbPersp === "Defendant"
                                  ? "Predicted: OpenAI/Microsoft Contest SOL, Acquiescence, and Musk Punitive Intent (Dkt.522)"
                                  : "Predicted: Musk Presses Charitable Trust + §17510.8, Opposes Dkt.522 Threat Admission"
                                : pbPersp === "Defendant"
                                  ? "Predicted: Plaintiff Opposes MTDs"
                                  : "Predicted: Defendant Moves for SJ on Invalidity"}
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#f59e0b",
                            marginBottom: 4,
                          }}
                        >
                          {isJuul
                            ? pbPersp === "Defendant"
                              ? "Sonner-Pak Two-Step Win -- 68% probability if Orrick accepts full argument"
                              : "Remand to Plaintiff-Favorable Courts -- 85% probability post any SJ ruling"
                            : isIle
                              ? pbPersp === "Defendant"
                                ? "Severance + §225 Attack - 75% probability"
                                : "Cooperation Lock + Two-Track Plea - 65% probability"
                              : caseId === "musk"
                                ? pbPersp === "Defendant"
                                  ? "SOL + Acquiescence Defense - 74% probability"
                                  : "Charitable Trust Victory + Section 17510.8 - 61% probability"
                                : pbPersp === "Defendant"
                                  ? "Opposition Briefs - 85% probability"
                                  : "SJ on §101 - 72% probability"}
                        </div>
                        <p
                          style={{
                            fontSize: 9,
                            color: th.muted,
                            lineHeight: 1.6,
                            margin: 0,
                          }}
                        >
                          {isJuul
                            ? pbPersp === "Defendant"
                              ? "JLI master strategy: (1) Sonner-Pak Two-Step -- use Pak admission to kill SFUSD legal damages, then invoke Sonner to bar equitable abatement = $0 for SFUSD. (2) PI no-expert SJ -- collapse opt-out cases. (3) FTC dismissal + FDA MDO non-finality = regulatory exculpation narrative. (4) Narrow supplemental discovery = remand with thin record. Risk: Orrick identified the Sonner-Pak circular logic problem at Oct 31 hearing and may be skeptical of Step 2."
                              : "SFUSD stays in N.D. Cal. -- it does NOT remand. SFUSD must build SFUSD-area-specific geographic causation from Meta production. PI opt-out cases (Seliavski, Aragona, etc.) remand to transferee courts where plaintiff-friendly juries await. Seliavski causation expert is the single most critical pre-remand action. Antitrust trial Sept 28 2026 creates a two-front war that compounds JLI settlement pressure."
                            : isIle
                              ? pbPersp === "Defendant"
                                ? "Defense strategy into 9/14: arrive with an agreed schedule; sever the Count 11s spillover or extract redactions and limiting instructions; keep the compliance record spotless - it funds the Dkt.60 follow-through and every future conditions ask; answer ¶40 head-on through the source witness."
                                : "Government strategy into 9/14: propose January dates and broad exclusions; front-load 3500 for the cooperators; build the opening on the bell photo and the 8-K and brief the 403 fight early; keep pressure asymmetric - 11s and the most-culpable record aim every incentive at the CEO."
                              : caseId === "musk"
                                ? pbPersp === "Defendant"
                                  ? "Defense strategy in Phase 1 trial: use Musk Yup email (DX-516), LP term sheet silence (DX-827), and Tell Them Im Supportive (DX-862) to argue acquiescence and SOL. Section 17510.8 brief (Dkt.516) contests statute applicability. FIFO tracing challenge attacks damages chain."
                                  : "Musk trial strategy: rely on Brockman journal (PX163), Kevin Scott email (PX230), and Altman founding email (PX003) to establish charitable trust and Microsoft knowledge. Argue section 17510.8 creates strict liability. Use Dudney FIFO tracing to establish unjust enrichment damages."
                                : pbPersp === "Defendant"
                                  ? "Plaintiff has filed oppositions to both MTDs. If denied, expect aggressive discovery targeting Kappa internal formulation records and prior art knowledge."
                                  : "After fact discovery closes Sept 2026, defendant moves for SJ on section 101 patent eligibility and challenges Neurocentria standing."}
                        </p>
                        <div style={{ marginTop: 8 }}>
                          <button
                            onClick={async () => {
                              openP(
                                "Generating Pre-draft Response…",
                                "",
                                null,
                                true,
                              );
                              await new Promise((r) => setTimeout(r, 80));
                              const rsp = await fetch("/api/messages", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  model: "claude-sonnet-4-20250514",
                                  max_tokens: 1000,
                                  messages: [
                                    {
                                      role: "user",
                                      content:
                                        "You are a litigation AI. Draft a brief response outline for this playbook move: " +
                                        JSON.stringify(pbSide) +
                                        " side, case: " +
                                        (isJuul
                                          ? "Seliavski v JUUL MDL 3:19-md-02913-WHO, N.D. Cal., Judge Orrick"
                                          : isIle
                                            ? "USA v. Chidambaran & Naqvi 1:26-cr-00097-NGG, E.D.N.Y., Judge Garaufis - Status Conf 9/14/26"
                                            : caseId === "musk"
                                              ? "Musk v Altman 4:24-cv-04722-YGR, N.D. Cal., Judge YGR - Trial May 2026"
                                              : "Neurocentria v Mando") +
                                        ". Format as numbered outline with key arguments, supporting evidence, and strategic considerations.",
                                    },
                                  ],
                                }),
                              }).catch(() => null);
                              const txt = rsp
                                ? await rsp
                                    .json()
                                    .then(
                                      (d) =>
                                        d.content?.[0]?.text ||
                                        "Unable to generate draft.",
                                    )
                                    .catch(() => "Unable to generate draft.")
                                : "AI unavailable. Key response arguments:\n1. Challenge standing\n2. Attack causation chain\n3. Invoke procedural bars\n4. Counter with competing expert";
                              openP("Pre-draft Response", txt, null, false);
                            }}
                            style={{
                              fontSize: 8,
                              background: "#1e40af",
                              color: "#fff",
                              border: "none",
                              borderRadius: 4,
                              padding: "4px 10px",
                              cursor: "pointer",
                            }}
                          >
                            ⚡ Pre-draft Response
                          </button>
                        </div>
                      </div>
                      {(pbPersp === "Defendant"
                        ? isJuul
                          ? [
                              {
                                move: "The Sonner-Pak Two-Step -- Eliminate SFUSD Entirely",
                                prob: "78%",
                                when: "SJ ruling pending since Oct 31 2025",
                                why: "JLI best-case scenario is a two-step: (1) Pak admission destroys legal damages -- SFUSD cannot quantify past program costs. (2) Sonner v. Premier Nutrition bars equitable abatement when no adequate legal remedy exists. If both steps succeed, SFUSD has $0 in legal damages AND $0 in equitable relief. Case over for SFUSD. JLI knows this. Orrick knows this. The Oct 31 hearing telegraphed Orrick is skeptical of the Sonner application.",
                              },
                              {
                                move: "SJ on PI Causation -- Collapse Opt-Out Cases",
                                prob: "72%",
                                when: "SJ ruling pending",
                                why: "No medical causation expert retained for Seliavski or most opt-out PI plaintiffs. Without a qualifying causation expert, PI plaintiffs cannot establish that JUUL specifically (as opposed to any nicotine product) caused their injuries. If SJ granted on PI causation, the entire opt-out remand structure collapses -- JLI faces only the antitrust case and possibly SFUSD. This is the scenario where JLI needs no settlement.",
                              },
                              {
                                move: "FCLAA Federal Preemption -- Threshold Dispositive",
                                prob: "55%",
                                when: "Post-SJ remand briefing",
                                why: "FCLAA preemption applied to JUUL would eliminate all state-law failure-to-warn claims -- the primary damage theory in the opt-out PI cases. Orrick denied the 2020 stay on primary jurisdiction grounds, not FCLAA merits. The Ninth Circuit has not ruled on FCLAA scope for e-cigarettes. JLI will re-raise this in remand transferee courts. If it succeeds in any transferee court, it triggers appellate review that could reshape the entire MDL.",
                              },
                              {
                                move: "Narrow Supplemental Discovery -- Transfer with Thin Record",
                                prob: "85%",
                                when: "Per Jan 6 2026 letter brief and May 11 2026 deadline",
                                why: "JLI strategy at Dec 16 2025 CMC: keep supplemental discovery as narrow as possible. Transfer remand cases with 8+ years of MDL discovery already done, but minimal new material. Goal: remand courts face existing record -- no new experts, no new theories. This makes it harder for plaintiffs to build new evidence in transferee courts and reduces JLI per-case cost exposure.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  move: "Rule 14 Severance + Bruton Redactions",
                                  prob: "75%",
                                  when: "Q4 2026 motions per the 9/14 schedule",
                                  why: "Count 11s and the no-liabilities statement create spillover only severance or redaction can cure; even a denial extracts limiting instructions and frames the role defense.",
                                },
                                {
                                  move: "§225 Construction / Rule 29 Attack",
                                  prob: "60%",
                                  when: "Pretrial motions and close of the government case",
                                  why: "A dormant statute with no Second Circuit gloss on organize/manage/supervise or receipts; removing Count 1s removes the 120-month floor from every conversation.",
                                },
                                {
                                  move: "FRE 106 + MIL on Deletion Theming",
                                  prob: "80%",
                                  when: "Motions in limine",
                                  why: "The full play-ball chain and the spam finding neutralize the two loudest detention-stage themes before a jury hears them.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    move: "SOL Defense -- Sept 2020 Tweet as Knowledge Trigger",
                                    prob: "74%",
                                    when: "Throughout Phase 1 trial and findings of fact",
                                    why: "Per Pretrial Order No. 3: defense argues Musk Sept 2020 tweet (captured by Microsoft) shows constructive knowledge of for-profit structure, starting SOL clock. Will press in cross and proposed FOF.",
                                  },
                                  {
                                    move: "Acquiescence Defense -- DX-516/827/862",
                                    prob: "81%",
                                    when: "Trial testimony and closing",
                                    why: "Defense trifecta: Yup email (DX-516 Jan 2016), LP term sheet silence (DX-827 Aug 2018), Tell Them Im Supportive (DX-862 Mar 2019). Argues Musk was informed, failed to object, and affirmatively ratified the for-profit structure.",
                                  },
                                  {
                                    move: "Dudney FIFO Tracing Challenge",
                                    prob: "65%",
                                    when: "Cross-examination and post-trial brief",
                                    why: "Defense allowed by Daubert to attack Dudney FIFO methodology connecting $38M donations to $157B+ enterprise value. Will argue Microsoft investment and GPT-4 commercial success break the causal chain from Musk donations to current equity.",
                                  },
                                  {
                                    move: "Section 17510.8 Opposition (Dkt.516)",
                                    prob: "70%",
                                    when: "Pending YGR ruling on competing briefs",
                                    why: "Defense brief filed Apr 30 2026 contests Cal. Bus. & Prof. Code section 17510.8 strict liability applicability. If statute does not apply, plaintiff must prove harder common-law charitable trust standard.",
                                  },
                                ]
                              : [
                                  {
                                    move: "Motion for Reconsideration",
                                    prob: "60%",
                                    when: "Within 14 days of adverse ruling",
                                    why: "Plaintiff has significant resources and will not concede easily. Expect reconsideration on any section 101 ruling arguing factual disputes preclude pleading-stage resolution.",
                                  },
                                  {
                                    move: "Aggressive Fact Discovery",
                                    prob: "90%",
                                    when: "Immediately if MTDs denied",
                                    why: "Plaintiff needs to close the Plant-Based Product testing gap and build a damages model. Expect RFPs targeting Kappa internal communications, formulation records, and financial data.",
                                  },
                                  {
                                    move: "Expert Retention - Damages",
                                    prob: "75%",
                                    when: "Before 9/18/26 expert disclosure",
                                    why: "Plaintiff has no damages expert yet. This is a critical gap - must retain one to survive summary judgment on the patent infringement claims.",
                                  },
                                ]
                        : isJuul
                          ? [
                              {
                                move: "Remand Motion -- Race to Plaintiff-Favorable Juries",
                                prob: "90%",
                                when: "Within 14 days of SJ ruling",
                                why: "This is the key plaintiff strategic play. Orrick telegraphed remand at Oct 31 2025 hearing. Transferee courts in N.D. Cal. (San Francisco), S.D. Fla. (Miami), and N.D. Tex. (Dallas) have plaintiff-favorable jury pools for consumer product liability. Once remanded, JLI faces state tort claims in state-law-friendly venues with no federal preemption backstop. Settlement pressure spikes immediately on remand order. Plaintiff needs favorable SJ ruling on causation to preserve leverage.",
                              },
                              {
                                move: "Seliavski Expert -- Retain and Disclose BEFORE Remand",
                                prob: "90%",
                                when: "Urgent -- before remand transfer order",
                                why: "Single most important action item in the MDL. Without a medical causation expert linking JUUL specifically to Seliavski nicotine dependency and behavioral injuries, JLI wins SJ on PI damages in E.D. Va. Virginia contributory negligence is a complete bar -- if Seliavski is 1% at fault, she loses 100%. The expert must address both general causation (JUUL causes addiction in minors) and specific causation (JUUL caused Seliavski injuries). Must also establish that 59mg/mL nicotine salts are meaningfully different from other nicotine products plaintiff may have used.",
                              },
                              {
                                move: "Meta Production -- The Potential Smoking Gun",
                                prob: "80%",
                                when: "By May 8 2026 per Dkt.4497 extension",
                                why: "The Meta account data production (Apr 14 2026 order) is the wildcard. If Meta produces data showing JLI placed targeted advertising toward users under 18 on Instagram, it would be third-party corroboration of the youth-targeting theory that JLI cannot explain away as litigation advocacy. This could be the document that forces settlement -- third-party ad targeting data is harder to dispute than internal JLI communications. Plaintiff should push for maximum production scope: all advertising targeting parameters, age demographic breakdowns, engagement metrics by user age.",
                              },
                              {
                                move: "Antitrust Opt-Out Deadline -- June 13 2026",
                                prob: "95%",
                                when: "CRITICAL -- June 13 2026 deadline",
                                why: "Opt-out deadline for direct and indirect purchaser antitrust classes (3:20-cv-02345). Any class member who does not opt out by June 13 2026 is bound by judgment or settlement. This is a separate but parallel case creating a two-front litigation war for JLI and Altria simultaneously. Antitrust trial set Sept 28 2026. Plaintiff strategy: let antitrust case compound settlement pressure on the MDL PI cases by forcing JLI and Altria to litigate on both tracks simultaneously through fall 2026.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  move: "Lock CC-2 Cooperation",
                                  prob: "70%",
                                  when: "Before the 9/14 schedule",
                                  why: "The account controller and deletion executor converts bank records into narrated testimony; 5K1 leverage peaks before motion practice begins.",
                                },
                                {
                                  move: "Differentiated Plea Track for the CFO",
                                  prob: "60%",
                                  when: "Aug-Sep 2026",
                                  why: "The government's own most-culpable framing invites a two-track resolution that simplifies a four-week trial into a one-defendant story.",
                                },
                                {
                                  move: "Bell Photo + 8-K Opening Architecture",
                                  prob: "90%",
                                  when: "Trial",
                                  why: "S-1 p.13 and the $421M number anchor the narrative; expect a 403 fight the government believes it wins.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    move: "Section 17510.8 Strict Liability (Dkt.515)",
                                    prob: "60%",
                                    when: "Pending YGR ruling on competing briefs",
                                    why: "If court applies Cal. Bus. & Prof. Code section 17510.8, Altman and Brockman face strict liability for false representations in charitable solicitations to Musk 2015-2019 -- significantly stronger than common-law trust theory.",
                                  },
                                  {
                                    move: "Proposed Findings of Fact -- Charitable Trust",
                                    prob: "95%",
                                    when: "Post-trial",
                                    why: "YGR will require proposed FOF from both sides. Plaintiff must connect each trial exhibit to each element of Count XVIII (charitable trust breach), Count IV (unjust enrichment), and Count XIX (aiding & abetting). PX003/PX163/PX230 are the backbone.",
                                  },
                                  {
                                    move: "Constructive Trust on Microsoft Equity",
                                    prob: "55%",
                                    when: "Post-trial if liability found",
                                    why: "If Count XVIII and XIX succeed, plaintiff will seek constructive trust on portion of Microsoft 49% OpenAI equity stake ($135B+ value) as remedy for aiding and abetting the charitable trust breach -- the largest potential damages theory in the case.",
                                  },
                                  {
                                    move: "Activate Phase 2 Antitrust Claims",
                                    prob: "80%",
                                    when: "Post-Phase 1 verdict if plaintiff wins",
                                    why: "Counts VIII-XV antitrust claims are stayed pending Phase 1. A plaintiff Phase 1 verdict creates massive settlement pressure for Phase 2 and likely triggers intensive antitrust discovery into OpenAI-Microsoft commercial relationship.",
                                  },
                                ]
                              : [
                                  {
                                    move: "SJ on Section 101 Patent Ineligibility",
                                    prob: "72%",
                                    when: "After expert discovery Oct 2026",
                                    why: "Defendant strongest argument: specification admits in vivo formation, Liu admitted the molecule was not invented, and dosage form is conventional. Defendant will argue no reasonable jury could find patent eligibility.",
                                  },
                                  {
                                    move: "SJ on Neurocentria Standing",
                                    prob: "65%",
                                    when: "With or before section 101 SJ",
                                    why: "If FAC paragraph 8 and paragraph 13 contradiction persists, defendant will argue Neurocentria divested standing when it allegedly assigned all rights to ThreoTech.",
                                  },
                                  {
                                    move: "Daubert Challenge to Damages Expert",
                                    prob: "55%",
                                    when: "After expert discovery",
                                    why: "Defendant will challenge damages methodology, particularly if it relies on the TMLA 2g dosage standard as royalty benchmark or fails to account for design-arounds.",
                                  },
                                ]
                      ).map((m, i) => (
                        <div
                          key={i}
                          style={{
                            background: th.card,
                            borderRadius: 6,
                            border: `1px solid ${th.border}`,
                            padding: "10px 14px",
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                flex: 1,
                                lineHeight: 1.3,
                              }}
                            >
                              {m.move}
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color:
                                  +m.prob.replace("%", "") > 70
                                    ? "#ef4444"
                                    : +m.prob.replace("%", "") > 60
                                      ? "#f59e0b"
                                      : "#6b7280",
                                marginLeft: 8,
                                flexShrink: 0,
                              }}
                            >
                              {m.prob}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: "#10b981",
                              marginTop: 2,
                              marginBottom: 2,
                            }}
                          >
                            {m.when}
                          </div>
                          <p
                            style={{
                              fontSize: 8,
                              color: th.muted,
                              lineHeight: 1.5,
                              margin: 0,
                            }}
                          >
                            {m.why}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* JUDGE INTEL */}
                  {rbP === "judge" &&
                    (() => {
                      const judge = isJuul
                        ? {
                            name: "Judge William H. Orrick",
                            direction: "Defendant",
                            confidence: 58,
                            reasons: [
                              {
                                type: "def",
                                text: "Methodical 6+ month SJ deliberation since Oct 31 2025 hearing suggests Orrick is taking the Sonner-Pak two-step argument seriously - defense's centerpiece SJ position.",
                              },
                              {
                                type: "def",
                                text: "Pak admission (no quantifiable past program costs) was well-received at the SJ hearing as a basis to grant SJ on legal damages.",
                              },
                              {
                                type: "def",
                                text: "Telegraphed remand intent at Oct 31 hearing - opt-out PIs face SJ in transferee courts where plaintiffs lack causation experts.",
                              },
                              {
                                type: "pl",
                                text: "Counter - denied JLI's 2020 stay on FDA primary jurisdiction (plaintiff-favorable). Approved $255M class settlement after rigorous fairness review.",
                              },
                            ],
                            basis:
                              "SJ hearing transcripts (Dkt.4453), 6+ months of deliberation, prior orders, sealing reviews, and CMC orders.",
                          }
                        : isIle
                          ? {
                              name: "Judge Nicholas G. Garaufis",
                              direction: "Neutral",
                              confidence: 58,
                              reasons: [
                                {
                                  type: "def",
                                  text: "Accepted structured release packages within three weeks of detaining both defendants - collateral and consent move him, not rhetoric (Dkt.42/43, Dkt.35).",
                                },
                                {
                                  type: "pl",
                                  text: "Granted the government's bail appeals same-day and detained de novo (Dkt.12, 22, 23) - procedural readiness is rewarded, and this USAO team has it.",
                                },
                                {
                                  type: "def",
                                  text: "Solved the bond-recording problem in 11 days with a narrow limited-purpose unsealing (Dkt.56; 6/22 order) - ministerial requests framed narrowly move fast.",
                                },
                                {
                                  type: "pl",
                                  text: "Denied the short-notice travel application outright (Dkt.59) - lead time and full papering are non-negotiable regardless of merits.",
                                },
                              ],
                              basis:
                                "Bail-war rulings Apr-May 2026, bond-administration orders, the travel-application pattern (Dkt.59/60), and the 7/16 posture offering four trial weeks in Jan/Feb 2027.",
                            }
                          : caseId === "musk"
                            ? {
                                name: "Judge Yvonne Gonzalez Rogers (YGR)",
                                direction: "Defendant",
                                confidence: 54,
                                reasons: [
                                  {
                                    type: "def",
                                    text: "Denied Musk's preliminary injunction in March 2025 (Dkt.121) - found no irreparable harm from OpenAI's for-profit conversion. Defense's first major win.",
                                  },
                                  {
                                    type: "def",
                                    text: "Pretrial Order No. 3 references Sept 2020 Musk tweet ('captured by Microsoft') as a potential SOL trigger - opens the strongest defense argument.",
                                  },
                                  {
                                    type: "def",
                                    text: "YGR known for rigorous scrutiny and skepticism of overreach. Musk's litigation history (Pampena threats, exhibit attached to Dkt.522) tracks the pattern she punishes.",
                                  },
                                  {
                                    type: "pl",
                                    text: "Counter - PX163 (Brockman journal) admitted at trial. §17510.8 ruling on competing briefs (Dkt.515/516) is forthcoming and could swing the case if applied.",
                                  },
                                ],
                                basis:
                                  "PI ruling, Pretrial Order No. 3, evidentiary rulings during Phase 1 trial, and YGR's record on tech / antitrust matters.",
                              }
                            : {
                                name: "Judge Aenlle-Rocha",
                                direction: "Defendant",
                                confidence: 60,
                                reasons: [
                                  {
                                    type: "def",
                                    text: "Approved plaintiff's narrowing from 5 patents to 1 with ease - the defense's most important pre-discovery win, signaling skepticism of broad patent claims.",
                                  },
                                  {
                                    type: "def",
                                    text: "Stricken plaintiff's non-compliant stipulations (Dkt.85) - zero tolerance for procedural slips, which favors the side that briefs more precisely.",
                                  },
                                  {
                                    type: "def",
                                    text: "Vacated scheduling conference, finding in-person appearance unnecessary - efficiency-focused, willing to rule on paper. Defense's §101 motion is paper-friendly.",
                                  },
                                  {
                                    type: "pl",
                                    text: "Counter - granted leave to amend liberally; ordered private mediation by Dec 31 2026; the §101 ruling itself is pending and not yet decided.",
                                  },
                                ],
                                basis:
                                  "Patent narrowing order, stricken filings, Standing Order, scheduling rulings, and pending MTDs.",
                              };
                      const dirColor =
                        judge.direction === "Defendant"
                          ? "#3b82f6"
                          : judge.direction === "Plaintiff"
                            ? "#ef4444"
                            : "#6b7280";
                      const dirLabel =
                        judge.direction === "Defendant"
                          ? "DEFENDANT"
                          : judge.direction === "Plaintiff"
                            ? "PLAINTIFF"
                            : "BALANCED";
                      return (
                        <div>
                          <h2
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              margin: "0 0 10px",
                            }}
                          >
                            👨‍⚖️ {judge.name}
                          </h2>
                          {(isJuul || caseId === "musk") && (
                            <div
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                padding: "12px 16px",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  marginBottom: 6,
                                }}
                              >
                                Court Contact
                              </div>
                              {(isJuul
                                ? [
                                    {
                                      label: "Courtroom",
                                      v: "Courtroom 2, 17th Floor, 450 Golden Gate Ave, SF, CA 94102",
                                    },
                                    {
                                      label: "Deputy",
                                      v: "Jean Davis - whocrd@cand.uscourts.gov / 415-522-2077",
                                    },
                                    {
                                      label: "Law & Motion",
                                      v: "Wednesdays 2 PM Zoom. Pretrial: Mondays 2 PM in-person.",
                                    },
                                    {
                                      label: "Disputes",
                                      v: "Joint 5-page letter brief. No standalone discovery motions.",
                                    },
                                  ]
                                : [
                                    {
                                      label: "Courtroom",
                                      v: "Courtroom 1, Ronald V. Dellums Federal Building, 1301 Clay St, Oakland, CA 94612",
                                    },
                                    {
                                      label: "Deputy",
                                      v: "Frances Stone - ygr_crd@cand.uscourts.gov",
                                    },
                                    {
                                      label: "Law & Motion",
                                      v: "Tuesdays 2 PM. Standing order requires pre-hearing letter brief (3 pages max).",
                                    },
                                    {
                                      label: "Disputes",
                                      v: "Discovery disputes: 5-page joint letter via Magistrate Judge (N.D. Cal. Standing Order).",
                                    },
                                    {
                                      label: "N.D. Cal. Rules",
                                      v: "Civil L.R. 7, 11, 37. Standing Order for All Judges of the N.D. Cal. applies.",
                                    },
                                  ]
                              ).map((item, i) => (
                                <div
                                  key={i}
                                  style={{ fontSize: 9, marginBottom: 3 }}
                                >
                                  <span
                                    style={{ fontWeight: 600, color: th.dim }}
                                  >
                                    {item.label}:{" "}
                                  </span>
                                  <span style={{ color: th.muted }}>
                                    {item.v}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* JUDGE LIKELY FAVORS - redesigned card */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${th.border}`,
                              borderLeft: `4px solid ${dirColor}`,
                              padding: "16px 18px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 14,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  letterSpacing: "-0.1px",
                                }}
                              >
                                Who Does the Judge Likely Favor?
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: "#a78bfa",
                                  background: "#a78bfa15",
                                  border: "1px solid #a78bfa33",
                                  padding: "2px 7px",
                                  borderRadius: 10,
                                  letterSpacing: 0.5,
                                  fontWeight: 600,
                                }}
                              >
                                ⚡ AUTO
                              </span>
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 14,
                                marginBottom: 14,
                              }}
                            >
                              {/* Direction big */}
                              <div
                                style={{
                                  background: dirColor + "08",
                                  border: `1px solid ${dirColor}33`,
                                  borderRadius: 8,
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: th.muted,
                                    letterSpacing: 1,
                                    marginBottom: 6,
                                  }}
                                >
                                  LIKELY FAVORS
                                </div>
                                <div
                                  style={{
                                    fontSize: 24,
                                    fontWeight: 800,
                                    color: dirColor,
                                    letterSpacing: "-0.5px",
                                    marginBottom: 4,
                                  }}
                                >
                                  {dirLabel}
                                </div>
                                <div style={{ fontSize: 9, color: th.muted }}>
                                  {judge.direction === "Plaintiff"
                                    ? "plaintiff-favorable signals"
                                    : judge.direction === "Defendant"
                                      ? "defense-favorable signals"
                                      : "mixed signals"}
                                </div>
                              </div>
                              {/* Confidence */}
                              <div
                                style={{
                                  background: th.surface,
                                  borderRadius: 8,
                                  padding: "14px 16px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: th.muted,
                                    letterSpacing: 1,
                                    marginBottom: 6,
                                  }}
                                >
                                  CONFIDENCE
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 6,
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 30,
                                      fontWeight: 800,
                                      color: dirColor,
                                      lineHeight: 1,
                                      letterSpacing: "-1px",
                                    }}
                                  >
                                    {judge.confidence}
                                    <span style={{ fontSize: 14 }}>%</span>
                                  </span>
                                  <span
                                    style={{ fontSize: 9, color: th.muted }}
                                  >
                                    {judge.confidence < 55
                                      ? "low-mid"
                                      : judge.confidence < 70
                                        ? "moderate"
                                        : judge.confidence < 85
                                          ? "high"
                                          : "very high"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    height: 6,
                                    background: th.bg,
                                    borderRadius: 3,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${judge.confidence}%`,
                                      background: dirColor,
                                      borderRadius: 3,
                                      transition: "width 0.4s ease",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Why */}
                            <div style={{ marginBottom: 8 }}>
                              <div
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: th.dim,
                                  letterSpacing: 0.5,
                                  textTransform: "uppercase",
                                  marginBottom: 8,
                                  fontFamily: "monospace",
                                }}
                              >
                                Why
                              </div>
                              {judge.reasons.map((r, i) => {
                                const c =
                                  r.type === "def"
                                    ? "#3b82f6"
                                    : r.type === "pl"
                                      ? "#ef4444"
                                      : "#6b7280";
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      marginBottom: 6,
                                      padding: "7px 10px",
                                      background: c + "08",
                                      borderLeft: `2px solid ${c}`,
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: c,
                                        fontWeight: 700,
                                        padding: "1px 6px",
                                        background: c + "18",
                                        borderRadius: 8,
                                        height: 14,
                                        lineHeight: 1.7,
                                        flexShrink: 0,
                                        marginTop: 1,
                                      }}
                                    >
                                      {r.type === "def"
                                        ? "DEF"
                                        : r.type === "pl"
                                          ? "PL"
                                          : "NEU"}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        lineHeight: 1.55,
                                      }}
                                    >
                                      {r.text}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: th.faint,
                                fontStyle: "italic",
                                lineHeight: 1.5,
                                paddingTop: 8,
                                borderTop: `1px dashed ${th.border}`,
                              }}
                            >
                              📊 Based on: {judge.basis}
                            </div>
                          </div>
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              padding: "14px 18px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                marginBottom: 8,
                              }}
                            >
                              This Case - Tone & Disposition
                            </div>
                            {(isJuul
                              ? [
                                  "Oct 31 2025 MSJ Hearing (43 min, Dkt.4453): Orrick questioned defense counsel closely on whether Sonner v. Premier Nutrition actually applies when Pak admission eliminates legal damages. Orrick logic: if JLI wins on Pak (no legal damages), then by definition there IS no adequate legal remedy -- so Sonner should not bar equitable abatement. Defense must address this circular logic problem in the written ruling.",
                                  "Oct 31 2025 Hearing: Orrick raised personal jurisdiction over Non-Management Director defendants (Pritzker, Valani, Huh) in transferee remand courts. Federal RICO provides nationwide service of process in federal court but not in state court. NMD fragmentation risk on remand is real.",
                                  "Dec 16 2025 CMC: Orrick confirmed remand intent post-SJ. Specifically asked: what supplemental discovery is truly non-duplicative after 8+ years of MDL? Ordered joint letter Jan 6 2026. Jan 6 letter established tight discovery scope: only deponent emails and social media records not yet produced.",
                                  "Orrick 2020: Denied JLI stay on FDA primary jurisdiction grounds (Dkt.626). Holding: tort claims based on youth marketing are adjudicated by courts, not FDA. Sets precedent that FDA regulatory process does not preempt MDL tort claims.",
                                  "Orrick settlement fairness review: approved $255M JLI class settlement with rigorous review but flagged overbroad fee committee sealing (OSC Dkt.4167, Dec 7 2023). Issued OSC -- expects narrowly tailored redactions only.",
                                  "Tone: methodical, skeptical of overreach by both sides. Will not shortcut the record. His 6+ month SJ deliberation period reflects the complexity of the Sonner-Pak interaction -- he is shaping a ruling that survives Ninth Circuit review on both issues simultaneously.",
                                  "Sealing: Kamakana standard throughout. OSC on Dkt.4167 shows he monitors fee committee materials personally. Counsel should certify all sealing requests as narrowly tailored before filing.",
                                  "Zoom for all CMC and law and motion hearings. Courtroom 2, 17th Floor, 450 Golden Gate Ave SF. In-person for pretrial conferences and trial only.",
                                ]
                              : isIle
                                ? [
                                    "Detained both defendants on de novo review, then accepted negotiated packages within three weeks - structure over rhetoric (Dkt.22/23; 42/43; 35).",
                                    "Expects the parties to arrive at the 9/14 conference with an agreed motion schedule and trial dates; offered four weeks in Jan/Feb 2027 unprompted.",
                                    "Letter-motion practice with proposed orders attached; the short-notice 7/22 application was denied on timing alone (Dkt.59 vs Dkt.60).",
                                    "Sealed-bond mechanics need lead time - county clerks required raised-seal certified copies; solved by limited-purpose order in 11 days (Dkt.56; 6/22).",
                                  ]
                                : caseId === "musk"
                                  ? [
                                      "Denied Musk preliminary injunction March 4 2025 (Dkt.121) - found no irreparable harm from OpenAI for-profit conversion.",
                                      "Trial in progress: Phase 1 (Count XVIII charitable trust, Count IV unjust enrichment, Count XIX aiding & abetting vs. Microsoft). Phase 2 antitrust counts stayed.",
                                      "Pretrial Order No. 3 set statute of limitations framework - Sept 2020 Musk tweet as potential SOL trigger.",
                                      "YGR known for exhaustive preparation - counsel must know record cold. She identifies inconsistencies and presses witnesses personally.",
                                      "§17510.8 (Cal. Bus. & Prof. Code charitable solicitations) - competing briefs filed Apr 30 2026 (Dkt.515/516). Court ruling forthcoming.",
                                      "N.D. Cal. Standing Orders apply. Discovery disputes via Magistrate Judge. 3-page pre-hearing letter brief required.",
                                      "Sealing: Kamakana standard (compelling reasons for dispositive/trial exhibits). Overbroad sealing will draw OSC.",
                                    ]
                                  : [
                                      "Prefers motions under submission - vacated scheduling conference finding in-person unnecessary.",
                                      "Zero tolerance for non-compliance - STRICKEN two stipulations for Local Rules violations.",
                                      "Favors early narrowing - approved dismissal of 4 of 5 patents without hesitation.",
                                      "Grants leave to amend liberally but expects strict compliance with deadlines.",
                                      "Patent Standing Order imposes detailed claim construction procedures.",
                                      "Ordered private mediation - preference that parties attempt resolution before trial.",
                                    ]
                            ).map((p, i) => (
                              <div
                                key={i}
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  padding: "4px 0 4px 10px",
                                  borderLeft: `2px solid ${th.border}`,
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                {p}
                              </div>
                            ))}
                          </div>
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              padding: "12px 16px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                marginBottom: 6,
                              }}
                            >
                              Practical Implications
                            </div>
                            {(isJuul
                              ? [
                                  {
                                    label: "Discovery",
                                    note: "Joint 5-page letter brief only. Jan 6 letter due re supplemental discovery scope for remand.",
                                  },
                                  {
                                    label: "Sealing",
                                    note: "Kamakana standard. Narrowly tailored. Will issue OSC for overbroad requests.",
                                  },
                                  {
                                    label: "Remand",
                                    note: "Expect post-SJ remand order. Prepare for transfer to transferee court for state law claims.",
                                  },
                                  {
                                    label: "CMC",
                                    note: "Zoom Wednesdays 2 PM. Pro se Help Center at 450 Golden Gate Ave, 15th Fl.",
                                  },
                                ]
                              : isIle
                                ? [
                                    {
                                      label: "9/14 Conf",
                                      note: "Arrive with an agreed schedule and Feb 2027 dates; trade STA exclusion for firm deadlines, never give it free.",
                                    },
                                    {
                                      label: "Letters",
                                      note: "Letter + proposed order + real lead time; short notice dies here (Dkt.59).",
                                    },
                                    {
                                      label: "Travel",
                                      note: "A Dkt.60 grant is not authorization - itinerary letter plus PTS approval per meeting before any booking.",
                                    },
                                    {
                                      label: "Bonds",
                                      note: "Raised-seal certified copies for anything recorded in county land records (6/22 order).",
                                    },
                                  ]
                                : caseId === "musk"
                                  ? [
                                      {
                                        label: "Trial",
                                        note: "Phase 1 trial in progress. YGR runs tight courtroom. Be precise on exhibits and testimony references.",
                                      },
                                      {
                                        label: "§17510.8",
                                        note: "Competing briefs filed Apr 30 2026. Court ruling on Cal. Bus. & Prof. Code charitable solicitations applicability is a live hot issue.",
                                      },
                                      {
                                        label: "SOL",
                                        note: "Pretrial Order No. 3 sets SOL framework. Sept 2020 Musk tweet (captured by Microsoft) flagged as potential trigger. Prepare discovery rule tolling arguments.",
                                      },
                                      {
                                        label: "N.D. Cal. Rules",
                                        note: "Civil L.R. 7-1 through 7-12 govern motion practice. Standing Order for All N.D. Cal. Judges + YGR Individual Standing Order both apply. Oakland courthouse.",
                                      },
                                    ]
                                  : [
                                      {
                                        label: "Briefing",
                                        note: "Keep concise. Judge reads carefully but acts on paper.",
                                      },
                                      {
                                        label: "Compliance",
                                        note: "Follow local rules exactly. Non-compliance = stricken filings.",
                                      },
                                      {
                                        label: "Strategy",
                                        note: "Narrowing the case early will be rewarded.",
                                      },
                                      {
                                        label: "Settlement",
                                        note: "Mediation order signals good-faith negotiations expected before trial.",
                                      },
                                    ]
                            ).map((item, i) => (
                              <div key={i} style={{ marginBottom: 6 }}>
                                <span
                                  style={{
                                    fontSize: 8,
                                    fontWeight: 600,
                                    color: "#3b82f6",
                                  }}
                                >
                                  {item.label}:
                                </span>
                                <span
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    marginLeft: 4,
                                  }}
                                >
                                  {item.note}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  {/* AUTO DRAFTS */}
                  {rbP === "drafts" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 4px",
                        }}
                      >
                        {isJuul
                          ? "⚡ Auto-Drafts - Upcoming Actions"
                          : isIle
                            ? "⚡ Auto-Drafts - Pretrial Actions"
                            : caseId === "musk"
                              ? "⚡ Auto-Drafts - Trial Actions"
                              : "⚡ Auto-Drafts"}
                      </h2>
                      <p
                        style={{
                          fontSize: 8,
                          color: th.muted,
                          margin: "0 0 10px",
                        }}
                      >
                        Upcoming filings not yet submitted
                      </p>
                      {(isJuul
                        ? [
                            {
                              title: "Meta Account Data Response",
                              deadline: "May 8, 2026",
                              priority: "High",
                              desc: "Per ECF Stip. Dkt.4497 (filed 04/24/26): Meta deadline to respond to Court Order Regarding Production of Decedent Meta Account Data (Apr 14, 2026) extended to May 8, 2026.",
                            },
                            {
                              title: "Email + Social Media Dispute Resolution",
                              deadline: "May 11, 2026",
                              priority: "High",
                              desc: "45 days from Mar 27, 2026 per Feb 26 Order (Dkt.4488): resolve all disputes re email and social media productions - or Defendants file non-compliance notice and parties submit joint discovery letter.",
                            },
                            {
                              title: "Remand Motion - Opt-Out PI Cases",
                              deadline: "TBD post-SJ",
                              priority: "High",
                              desc: "Following SJ ruling, move for remand of opt-out PI cases to transferee courts in CA, FL, and TX. Argue all federal questions resolved by SJ ruling.",
                            },
                            {
                              title: "Common Benefit Fees + Costs Motion",
                              deadline: "Late 2026 / Early 2027",
                              priority: "Medium",
                              desc: "Co-Lead Counsel anticipate additional motions re common benefit fees and costs to be filed in late 2026 or early 2027 following supplemental discovery and remand proceedings.",
                            },
                          ]
                        : isIle
                          ? [
                              {
                                title:
                                  "Rule 14 Severance Motion (Count 11s Spillover)",
                                deadline: "Per 9/14 motion schedule",
                                priority: "High",
                                desc: "Sever, or in the alternative redact and instruct: Count 11s obstruction proof (deletion directives to CC-2, ¶¶45, 56) and the no-liabilities statement (¶30) are Chidambaran-only evidence a joint jury cannot unhear. Attach the spam finding and the 11s charging decision.",
                              },
                              {
                                title:
                                  "Motion in Limine - Deletion/WhatsApp Theming vs Naqvi",
                                deadline: "With pretrial motions",
                                priority: "High",
                                desc: "Exclude detention-stage obstruction theming against Naqvi under FRE 403/404(b): the company's own forensic expert called his deleted items spam and the grand jury charged 11s against the CEO alone.",
                              },
                              {
                                title: "Dkt.60 Itinerary Letter (if granted)",
                                deadline: "Before any travel",
                                priority: "Critical",
                                desc: "Per the application at 3: follow-up letter with the specific interview itinerary; PTS approval for each meeting including any Patent VC session; role scope mirrors the letter - remote, sales/marketing, contract, no fundraising, no investor contact.",
                              },
                              {
                                title: "9/14 Joint Schedule Proposal",
                                deadline: "Sep 14, 2026",
                                priority: "High",
                                desc: "Briefing schedule (severance, §225 Rule 12, MILs), firm Feb 2027 trial dates, and 3500/Jencks production deadlines in exchange for STA exclusion consent.",
                              },
                            ]
                          : caseId === "musk"
                            ? [
                                {
                                  title: "Section 17510.8 Reply Brief",
                                  deadline: "TBD -- per YGR order",
                                  priority: "High",
                                  desc: "Per competing briefs filed Apr 30 2026 (Dkt.515/516): if Court requests reply or holds hearing on Cal. Bus. & Prof. Code section 17510.8 applicability, prepare reply brief arguing strict liability for false charitable solicitation representations applies to Altman and Brockman 2015-2019 communications to Musk.",
                                },
                                {
                                  title:
                                    "Proposed Findings of Fact and Conclusions of Law",
                                  deadline: "Post-trial per YGR order",
                                  priority: "High",
                                  desc: "Following Phase 1 trial on Count XVIII (charitable trust), Count IV (unjust enrichment), Count XIX (aiding & abetting), prepare proposed findings of fact citing trial testimony and exhibits (PX003, PX163, PX230, PX200, DX-516, DX-827, DX-862). Address SOL framework per Pretrial Order No. 3.",
                                },
                                {
                                  title:
                                    "Opposition to OpenAI Phase 2 Antitrust Counterclaims",
                                  deadline: "Post-Phase 1 verdict",
                                  priority: "Medium",
                                  desc: "OpenAI antitrust counterclaims (Counts VIII-XV) are stayed pending Phase 1 verdict. If Phase 1 verdict favors Musk, OpenAI will activate Phase 2 antitrust claims alleging Musk used litigation to damage competitor xAI. Prepare response now.",
                                },
                              ]
                            : [
                                {
                                  title:
                                    "Opposition to MTD - Patent Eligibility",
                                  deadline: "Per schedule",
                                  priority: "High",
                                  desc: "Oppose defendant 101 motion. Lead argument: claim directed to non-obvious specific dosage form, not abstract idea.",
                                },
                                {
                                  title: "Expert Disclosure - Damages",
                                  deadline: "Sep 18, 2026",
                                  priority: "High",
                                  desc: "FRCP 26(a)(2) expert disclosure. Retain and disclose damages expert - no expert identified yet.",
                                },
                              ]
                      ).map((d, i) => (
                        <div
                          key={i}
                          style={{
                            background: th.card,
                            borderRadius: 8,
                            border: `1px solid ${th.border}`,
                            borderLeft: `3px solid ${d.priority === "High" ? "#ef4444" : "#f59e0b"}`,
                            padding: "12px 16px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: 4,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                flex: 1,
                                lineHeight: 1.4,
                              }}
                            >
                              {d.title}
                            </div>
                            <span
                              style={{
                                fontSize: 6,
                                fontFamily: "monospace",
                                color:
                                  d.priority === "High" ? "#ef4444" : "#f59e0b",
                                background:
                                  d.priority === "High"
                                    ? "#ef444418"
                                    : "#f59e0b18",
                                padding: "2px 5px",
                                borderRadius: 3,
                                marginLeft: 6,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {d.priority}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: "#10b981",
                              marginBottom: 4,
                            }}
                          >
                            📅 {d.deadline}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.5,
                              marginBottom: 6,
                            }}
                          >
                            {d.desc}
                          </div>
                          <button
                            onClick={async () => {
                              openP(
                                "Generating " + d.title + "…",
                                "Estimated time: 10–20 seconds. CaseBinder AI is drafting this document.",
                                null,
                                true,
                              );
                              const prompt = `Draft a legal filing titled "${d.title}" for ${isJuul ? "Seliavski v. JUUL MDL 3:19-md-02913" : isIle ? "USA v. Chidambaran & Naqvi 1:26-cr-00097-NGG" : caseId === "musk" ? "Musk v. Altman 4:24-cv-04722-YGR" : "Neurocentria v. Mando 2:25-cv-05871"}. Context: ${d.desc} Write 600-900 words in formal legal style.`;
                              try {
                                const r = await fetch("/api/messages", {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    model: "claude-sonnet-4-20250514",
                                    max_tokens: 1200,
                                    messages: [
                                      { role: "user", content: prompt },
                                    ],
                                  }),
                                });
                                const dd = await r.json();
                                openP(
                                  d.title,
                                  dd.content
                                    .filter((b) => b.type === "text")
                                    .map((b) => b.text)
                                    .join(""),
                                  d.title,
                                  false,
                                );
                              } catch (e) {
                                alert("drafting error");
                                setDraftLoading(null);
                              }
                            }}
                            style={{
                              fontSize: 8,
                              background:
                                draftLoading === d.title
                                  ? "#1e40af99"
                                  : "linear-gradient(135deg,#1e40af,#10b981)",
                              color: "#fff",
                              border: "none",
                              borderRadius: 4,
                              padding: "4px 10px",
                              cursor: "pointer",
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {draftLoading === d.title ? (
                              <>
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 8,
                                    height: 8,
                                    border: "1.5px solid #ffffff55",
                                    borderTopColor: "#fff",
                                    borderRadius: "50%",
                                    animation: "spin 0.8s linear infinite",
                                  }}
                                />
                                Drafting...
                              </>
                            ) : (
                              <>Auto-Draft</>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* EVIDENCE GAPS */}
                  {rbP === "gaps" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 6px",
                        }}
                      >
                        🧩 Evidence Gaps
                      </h2>
                      <p
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          lineHeight: 1.5,
                          margin: "0 0 10px",
                        }}
                      >
                        {gapPersp === "Defendant"
                          ? isJuul
                            ? "JLI/Altria evidentiary gaps - areas where plaintiff record is strong and defense must address before remand."
                            : "Critical evidentiary gaps that, if filled, would substantially strengthen the defendant counterclaims and invalidity defenses."
                          : isJuul
                            ? "Gaps in the SFUSD and Seliavski evidentiary record that must be closed before remand to transferee courts."
                            : "Gaps in plaintiff's affirmative case that must be closed before trial. Without these, key counts are vulnerable to summary judgment."}
                      </p>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {["Plaintiff", "Defendant"].map((p) => (
                          <button
                            key={p}
                            onClick={() => setGapPersp(p)}
                            style={{
                              padding: "3px 10px",
                              fontSize: 8,
                              fontWeight: gapPersp === p ? 600 : 400,
                              background:
                                gapPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef444418"
                                    : "#3b82f618"
                                  : "transparent",
                              color:
                                gapPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6"
                                  : th.dim,
                              border: `1px solid ${gapPersp === p ? (p === "Plaintiff" ? "#ef4444" : "#3b82f6") : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            {p} Gaps
                          </button>
                        ))}
                      </div>
                      {(gapPersp === "Defendant"
                        ? isJuul
                          ? [
                              {
                                gap: "Seliavski Medical Causation - Expert Required",
                                priority: "High",
                                holder: "No expert retained yet",
                                action:
                                  "Retain medical causation expert immediately; disclose before remand transfer order",
                                why: "No medical causation expert has been identified or retained for Seliavski PI case. Once remanded to transferee court, expert disclosure deadlines trigger immediately. Without a causation expert linking JUUL specifically to Seliavski's claimed nicotine dependency and health injuries, defendant will move for SJ on personal injury damages. This is the single most urgent gap in the entire MDL opt-out record.",
                              },
                              {
                                gap: "SFUSD Program Cost Quantification",
                                priority: "High",
                                holder: "SFUSD - Finance and Operations",
                                action:
                                  "Retain cost accountant; supplement Pak depo with CFO-level testimony; review Dkt.3450-3457 exhibits",
                                why: "The Pak deposition admission ('we do not have that information') on SFUSD past program costs is JLI's SJ centerpiece. Must develop concrete cost documentation - staff hours, counseling program budgets, administrative burden, intervention costs - before remand to survive damages phase. Dorn Report covers infrastructure ($78.8M) but program costs remain underserved.",
                              },
                              {
                                gap: "Altria Pre-Investment Due Diligence Files",
                                priority: "High",
                                holder: "Altria Client Services LLC",
                                action:
                                  "Subpoena Altria due diligence files from Dec 2018 investment; Dkt.3393 unsealing order",
                                why: "For the RICO enterprise theory, plaintiff needs to show Altria knew of youth penetration at the time of the $12.8B investment. Altria internal due diligence documents were partially unsealed (Dkt.3393, Mar 2025) but key exhibits remain sealed. These files are the evidentiary backbone of the enterprise knowledge element.",
                              },
                              {
                                gap: "Social Media / Meta Youth Targeting Evidence",
                                priority: "Medium",
                                holder: "Meta Platforms, Inc.",
                                action:
                                  "Monitor May 8, 2026 Meta production per Dkt.4497; review decedent Meta account data",
                                why: "Court ordered production of decedent Meta account data (Apr 14, 2026). Plaintiff should push for broader social media discovery showing JLI placed targeted ads toward minors on Instagram and TikTok. This evidence would significantly strengthen the failure to warn theory (knew minors were seeing marketing) and the RICO enterprise theory (coordinated deceptive marketing).",
                              },
                              {
                                gap: "JLI Youth Marketing Intent Evidence",
                                priority: "High",
                                holder: "JLI / Altria internal documents",
                                action:
                                  "Review Dkt.3447 SOF; depose Monsees, Bowen, Pritzker on board decks",
                                why: "Plaintiff must establish JLI's subjective awareness that JUUL was marketing to minors. The JUUL School program documents and board-level youth uptake tracking metrics are powerful, but JLI will argue these show internal monitoring, not intentional youth targeting. Need deposition testimony from executives linking the data to affirmative marketing decisions.",
                              },
                              {
                                gap: "JLI Revenue Attribution to SFUSD Market Area",
                                priority: "Medium",
                                holder: "JLI financial records",
                                action:
                                  "RFP for sales data by zip code / retailer near SFUSD schools; existing MDL financial production",
                                why: "For SFUSD public nuisance causation, plaintiff needs to show JUUL products were specifically available and sold in the SFUSD geographic area during the relevant period. General national sales data is insufficient - need retailer-level sales records tying JUUL units to SFUSD-area stores.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  gap: "Dkt.60 Ruling - URGENT (window two is now)",
                                  priority: "High",
                                  holder: "Court - Garaufis",
                                  action:
                                    "Docket watch. On grant: itinerary letter + PTS approvals before any booking. On denial: video interview or a Houston-radius meeting with PTS pre-approval.",
                                  why: "The requested weeks are Aug 17 and Aug 24; the ruling gates the Patent VC interview and the employment-stability mitigation record.",
                                },
                                {
                                  gap: "Defense Forensic Accountant Not Retained",
                                  priority: "High",
                                  holder: "Milbank",
                                  action:
                                    "Retain a restructuring/ASC 606 expert before the 9/14 schedule locks disclosure dates.",
                                  why: "The government's tracing is three years ahead via grand jury; netting plus lender-approval analysis is the only answer to ¶27.",
                                },
                                {
                                  gap: "Olivier and Gerety Not Interviewed",
                                  priority: "High",
                                  holder: "Defense team",
                                  action:
                                    "Interviews and declarations within two weeks; prepare Olivier for both defense and government postures.",
                                  why: "The most informed diligence buyer kept working with Naqvi - the strongest third-party validation available.",
                                },
                                {
                                  gap: "Full Committee Forensic Report",
                                  priority: "Medium",
                                  holder: "Company / special committee",
                                  action:
                                    "Rule 17(c) subpoena for the complete report behind the spam finding.",
                                  why: "Convert a quoted finding into a trial-ready expert exhibit before privilege fights consume the calendar.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    gap: "YGR Ruling on Dkt.522 Admissibility - URGENT",
                                    priority: "High",
                                    holder: "Court - YGR",
                                    action:
                                      "Monitor YGR ruling on OpenAI Dkt.522 application filed May 3 2026. Prepare both admission and exclusion scenarios before Brockman testimony.",
                                    why: "OpenAI intends to elicit the Apr 25 2026 Musk message through Brockman examination. If YGR admits it (FRE 408(b) motive/bias), Musk faces devastating credibility blow: he initiated settlement then threatened Brockman personally. Defense must prepare cross-examination strategy for either outcome. Pampena v. Musk World War III pattern (Exhibit A to Dkt.522) is admissible in that case -- YGR will consider this precedent.",
                                  },
                                  {
                                    gap: "Musk Post-2019 Reliance Evidence",
                                    priority: "High",
                                    holder:
                                      "Musk personal records / xAI communications",
                                    action:
                                      "Subpoena evidence of what Musk did in reliance on OpenAI charitable mission after Aug 2018 LP term sheet",
                                    why: "Defense will argue Musk was informed of for-profit structure (Aug 2018 LP term sheet, Mar 2019 supportive email) and took no action. Plaintiff needs evidence of continued reliance post-2018 to rebut acquiescence.",
                                  },
                                  {
                                    gap: "OpenAI Corporate Governance 2018-2019 Conversion",
                                    priority: "High",
                                    holder: "OpenAI / Altman personal files",
                                    action:
                                      "Demand full board minutes and communications from Nov 2018 to Jan 2020 when for-profit LP was structured",
                                    why: "Charitable trust theory depends on showing conversion was unauthorized by original documents. Defense claims board had authority. Need internal governance records showing what representations were made about charitable mission constraints during LP structure negotiation.",
                                  },
                                  {
                                    gap: "Microsoft Due Diligence Files -- July 2019 Investment",
                                    priority: "High",
                                    holder: "Microsoft Corp.",
                                    action:
                                      "Compel or subpoena Microsoft internal due diligence files from $1B July 2019 OpenAI investment",
                                    why: "Count XIX (aiding & abetting) requires showing Microsoft knew of trust conditions when it invested $1B. Kevin Scott email (PX230) shows Microsoft CTO questioning donor awareness in March 2018. Microsoft due diligence files for the July 2019 investment are the best evidence of actual knowledge.",
                                  },
                                  {
                                    gap: "Dudney FIFO Tracing -- OpenAI Asset Chain",
                                    priority: "Medium",
                                    holder: "OpenAI financial records",
                                    action:
                                      "Ensure Dudney expert has complete asset and equity transfer records from 2015 nonprofit through 2025 public benefit conversion",
                                    why: "Damages theory (Count IV unjust enrichment) requires tracing $38M in Musk donations through to the $157B+ enterprise value. Defense FIFO tracing challenge (allowed by Daubert) will contest the chain. Complete financial records from 2015-2025 are essential.",
                                  },
                                ]
                              : [
                                  {
                                    gap: "Dr. Slutsky declaration or deposition",
                                    priority: "High",
                                    holder: "Tel Aviv University",
                                    action: "Subpoena via Hague Convention",
                                    why: "Slutsky testimony is the linchpin of the inventorship counterclaim. Without her confirming she contributed to conception, the inventorship challenge rests solely on Mao statements which plaintiff can characterize as background research, not conception.",
                                  },
                                  {
                                    gap: "AIDP internal sourcing records",
                                    priority: "High",
                                    holder: "AIDP, Inc.",
                                    action:
                                      "RFP for CoAs, imports, supplier contracts",
                                    why: "The fraud and Lanham Act counterclaims depend on proving that AIDP/ThreoTech knew the active ingredient was Chinese-sourced while marketing it as USA-manufactured. Internal sourcing records are the documentary backbone.",
                                  },
                                  {
                                    gap: "Jordan routing documentation",
                                    priority: "Medium",
                                    holder: "U.S. Customs / AIDP",
                                    action: "Subpoena CBP records",
                                    why: "If magnesium L-threonate was routed through Jordan to exploit the US-Jordan FTA duty-free provisions without substantial transformation, this strengthens both the fraud counterclaim and potential customs violations.",
                                  },
                                  {
                                    gap: "MIT provisional prosecution file",
                                    priority: "Medium",
                                    holder: "MIT TLO",
                                    action: "Third-party subpoena",
                                    why: "The MIT provisional prosecution history would reveal whether MIT considered the claims broad enough to encompass magnesium L-threonate - relevant to section 103 obviousness.",
                                  },
                                ]
                        : isJuul
                          ? [
                              {
                                gap: "FDA MDO Non-Finality + FTC Dismissal Double Argument",
                                priority: "High",
                                holder: "FDA / JLI regulatory record",
                                action:
                                  "Monitor 5th Circuit stay proceedings; track FDA PMTA review status",
                                why: "The FDA June 2022 Marketing Denial Order has been stayed by the 5th Circuit and withdrawn for further review - making it non-final. JLI will argue the non-final MDO cannot establish inadequacy of JUUL's regulatory compliance. Plaintiff must develop independent evidence of harm without relying solely on the MDO as per se proof of liability.",
                              },
                              {
                                gap: "SFUSD-Specific Geographic Causation -- SFUSD Stays in N.D. Cal.",
                                priority: "High",
                                holder: "Individual PI plaintiffs",
                                action:
                                  "Build SFUSD-area-specific causation record: retailer sales data near SFUSD schools, SFUSD-area JLI Instagram ad targeting data from Meta production, school-by-school incident reports.",
                                why: "SFUSD does NOT remand to a transferee court -- it was filed in N.D. Cal. and stays with Judge Orrick. Only individual PI opt-out cases (Seliavski, Aragona, etc.) go to transferee courts. SFUSD must prove JLI specifically targeted the San Francisco geographic area -- not just national youth marketing generally. Without SFUSD-area-specific sales and ad targeting evidence, JLI argues SFUSD costs were caused by a national phenomenon and cannot be attributed to JLI marketing in SF. The Meta production (May 8 2026) is the key to closing this gap.",
                              },
                              {
                                gap: "Non-Management Director Knowledge Evidence",
                                priority: "Medium",
                                holder:
                                  "Nicholas Pritzker, Hoyoung Huh, Riaz Valani records",
                                action:
                                  "Review Dkt.4392 unsealed materials; depose Pritzker, Huh, Valani on board meeting awareness",
                                why: "RICO personal liability for Non-Management Director Defendants (Pritzker, Huh, Valani) requires showing they participated in directing the enterprise's affairs. The Jan 2025 unsealing order (Dkt.4374) released some director materials. Need deposition testimony and board meeting records showing what the directors knew and approved regarding youth marketing strategy.",
                              },
                              {
                                gap: "JLI Capsule Vendor Supply Chain",
                                priority: "Medium",
                                holder:
                                  "JLI procurement records / pod manufacturers",
                                action:
                                  "RFP for JUUL pod manufacturing contracts; depose supply chain witnesses",
                                why: "For design defect theory, plaintiff should establish that JLI had design alternatives available - lower nicotine concentrations, different aerosolization rates, alternative flavor systems - and consciously chose the most addictive design. Evidence of rejected design alternatives or internal safety debates strengthens both design defect and punitive damages theories.",
                              },
                              {
                                gap: "SFUSD Peer District Comparative Data",
                                priority: "Low",
                                holder:
                                  "Other California school districts; CDC / SAMHSA data",
                                action:
                                  "Subpoena peer district JUUL-related program costs; obtain CDC vaping prevalence data",
                                why: "JLI will argue SFUSD costs were no higher than any other California school district facing the same youth vaping epidemic - suggesting the costs were caused by a general social phenomenon, not by JUUL specifically. Comparative data from peer districts showing SFUSD costs were higher due to JUUL's concentrated SFUSD-area marketing would rebut this argument.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  gap: "CC-2 Cooperation Status Unresolved",
                                  priority: "High",
                                  holder: "USAO",
                                  action:
                                    "Resolve the 5K1 posture before the 9/14 schedule; his testimony narrates the deletions and the account plumbing.",
                                  why: "Without CC-2, Count 11s rests on artifacts and Employee 1 alone, and the ¶27 tracing loses its narrator.",
                                },
                                {
                                  gap: "¶40 Source Witness Lock-In",
                                  priority: "High",
                                  holder: "USAO",
                                  action:
                                    "Pin down who testifies to at-the-urging-of-Naqvi and corroborate before the defense finds the seam.",
                                  why: "It is the single allegation tying Naqvi to affirmative deception design - and the defense knows it.",
                                },
                                {
                                  gap: "403 Exposure on the Bell Photo",
                                  priority: "Medium",
                                  holder: "USAO",
                                  action:
                                    "Brief admissibility early; prepare a sanitized alternative demonstrative.",
                                  why: "Celebration imagery invites a prejudice objection the defense will fully litigate.",
                                },
                                {
                                  gap: "Loss Narrative vs D. Md. Dismissal",
                                  priority: "Medium",
                                  holder: "USAO",
                                  action:
                                    "Prepare the one-sentence standards answer and an in-limine motion to exclude the civil outcome.",
                                  why: "The dismissal with prejudice is the defense genesis attack; contain it pretrial.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    gap: "SOL Tolling -- Fraudulent Concealment Acts",
                                    priority: "High",
                                    holder:
                                      "OpenAI internal communications 2019-2020",
                                    action:
                                      "Establish affirmative concealment acts by OpenAI after for-profit conversion -- board communications, public statements vs internal strategy",
                                    why: "Defense SOL argument (Pretrial Order No. 3): Sept 2020 Musk tweet may be SOL trigger. Need evidence of affirmative concealment of the nature and extent of for-profit conversion to toll limitations under fraudulent concealment doctrine.",
                                  },
                                  {
                                    gap: "Section 17510.8 Solicitations Record",
                                    priority: "High",
                                    holder:
                                      "Musk / OpenAI communications 2015-2019",
                                    action:
                                      "Build complete record of all solicitations from Altman and Brockman to Musk containing charitable mission representations",
                                    why: "Cal. Bus. & Prof. Code section 17510.8 applicability brief filed Apr 30 2026 (Dkt.515/516). If statute applies, creates strict liability for false representations in charitable solicitations. Need comprehensive record of every communication from OpenAI founders to Musk characterizing the organizational mission.",
                                  },
                                  {
                                    gap: "OpenAI Counterclaims Phase 2 -- Antitrust",
                                    priority: "Medium",
                                    holder: "OpenAI litigation files",
                                    action:
                                      "Monitor OpenAI antitrust counterclaims (Counts VIII-XV) stayed pending Phase 1 verdict -- prepare rebuttal expert and defense strategy now",
                                    why: "OpenAI antitrust counterclaims alleging Musk used litigation to damage competitor xAI are stayed for Phase 2. If Phase 1 verdict favors Musk, OpenAI will activate these claims. Plaintiff must prepare counterclaim defense while pursuing main case.",
                                  },
                                  {
                                    gap: "Microsoft Board Knowledge -- July 2019 Investment",
                                    priority: "Medium",
                                    holder:
                                      "Microsoft Board records; Altman communications to Nadella",
                                    action:
                                      "Establish chain from Kevin Scott email (PX230, Mar 2018) to board-level approval of $1B July 2019 investment",
                                    why: "Count XIX requires Microsoft knowledge at time of investment. PX230 establishes CTO-level awareness in March 2018. Need evidence Nadella-level awareness reached board approval -- corporate governance records from July 2019 investment decision.",
                                  },
                                ]
                              : [
                                  {
                                    gap: "Lab testing of Plant-Based Product",
                                    priority: "High",
                                    holder: "Plaintiff counsel",
                                    action: "Commission independent analysis",
                                    why: "The FAC relies on a single Amazon marketing slogan to allege infringement. Without compositional testing confirming qualifying dosage form, this count is vulnerable to dismissal under Twombly.",
                                  },
                                  {
                                    gap: "Damages model / lost profits",
                                    priority: "High",
                                    holder: "Expert witness",
                                    action: "Retain damages expert immediately",
                                    why: "No damages expert retained. Without a reasonable royalty calculation or lost profits model, plaintiff cannot survive summary judgment on damages. Deadline 9/18/26.",
                                  },
                                  {
                                    gap: "Kappa knowledge of 061 patent",
                                    priority: "Medium",
                                    holder: "Kappa",
                                    action:
                                      "RFP for patent-related communications",
                                    why: "Induced infringement requires proof Kappa had specific knowledge its products infringed. General wellness marketing is insufficient - need internal communications showing patent awareness.",
                                  },
                                  {
                                    gap: "Consumer confusion survey",
                                    priority: "Medium",
                                    holder: "Expert witness",
                                    action: "Teflon/Eveready survey",
                                    why: "Trademark counts depend on likelihood of confusion between Kappa post-termination branding and Magtein. Without survey evidence, plaintiff relies on conclusory allegations.",
                                  },
                                ]
                      ).map((g, i) => (
                        <div
                          key={i}
                          style={{
                            background: th.card,
                            borderRadius: 8,
                            border: `1px solid ${th.border}`,
                            borderLeft: `3px solid ${g.priority === "High" ? "#ef4444" : "#f59e0b"}`,
                            padding: "12px 16px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 4,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 600 }}>
                              {g.gap}
                            </div>
                            <span
                              style={{
                                fontSize: 6,
                                fontFamily: "monospace",
                                color:
                                  g.priority === "High"
                                    ? "#ef4444"
                                    : g.priority === "Medium"
                                      ? "#f59e0b"
                                      : "#6b7280",
                                background:
                                  g.priority === "High"
                                    ? "#ef444418"
                                    : g.priority === "Medium"
                                      ? "#f59e0b18"
                                      : "#6b728018",
                                padding: "2px 5px",
                                borderRadius: 3,
                              }}
                            >
                              {g.priority}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: th.dim,
                              marginBottom: 4,
                            }}
                          >
                            Holder: {g.holder} · Action: {g.action}
                          </div>
                          <p
                            style={{
                              fontSize: 8,
                              color: th.muted,
                              lineHeight: 1.5,
                              margin: "4px 0 6px",
                            }}
                          >
                            {g.why}
                          </p>
                          <button
                            onClick={async () => {
                              openP(
                                "Generating Request for Production…",
                                "",
                                null,
                                true,
                              );
                              await new Promise((r) => setTimeout(r, 80));
                              const rsp = await fetch("/api/messages", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  model: "claude-sonnet-4-20250514",
                                  max_tokens: 1200,
                                  messages: [
                                    {
                                      role: "user",
                                      content:
                                        "Draft a Request for Production of Documents complying with N.D. Cal. Local Rules (Fed. R. Civ. P. 34, LR 37-2) for a JUUL MDL opt-out PI case. Address the evidence gap: " +
                                        g.holder +
                                        " / " +
                                        g.action +
                                        ". Include: numbered definitions, instructions, and 5-8 targeted document requests. Format formally.",
                                    },
                                  ],
                                }),
                              }).catch(() => null);
                              const txt = rsp
                                ? await rsp
                                    .json()
                                    .then(
                                      (d) =>
                                        d.content?.[0]?.text ||
                                        "Unable to generate.",
                                    )
                                    .catch(() => "Unable to generate.")
                                : "REQUEST FOR PRODUCTION\n\nTO: " +
                                  g.holder +
                                  "\n\nPLEASE TAKE NOTICE that pursuant to Fed. R. Civ. P. 34 and N.D. Cal. Local Rule 37-2, Plaintiff requests production of:\n\n1. All documents relating to " +
                                  g.action +
                                  "\n2. All communications referencing the above\n3. All reports, studies, or analyses related to the above\n\n[Formal RFP to be finalized with counsel.]";
                              openP(
                                "Request for Production - " + g.holder,
                                txt,
                                null,
                                false,
                              );
                            }}
                            style={{
                              fontSize: 7,
                              background: "#1e40af",
                              color: "#fff",
                              border: "none",
                              borderRadius: 3,
                              padding: "3px 8px",
                              cursor: "pointer",
                            }}
                          >
                            Generate Request
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* HEAT MAP */}
                  {rbP === "heatmap" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 8px",
                        }}
                      >
                        🌡️ Evidence Heat Map
                      </h2>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {[
                          ["all", "All"],
                          ["Plaintiff", "Plaintiff"],
                          ["Defendant", "Defendant"],
                        ].map(([k, l]) => (
                          <button
                            key={k}
                            onClick={() => setHmPersp(k)}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              fontWeight: hmPersp === k ? 600 : 400,
                              color: hmPersp === k ? "#3b82f6" : th.dim,
                              background:
                                hmPersp === k ? "#3b82f615" : "transparent",
                              border: `1px solid ${hmPersp === k ? "#3b82f6" : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 8,
                          border: `1px solid ${th.border}`,
                          padding: "16px 18px",
                        }}
                      >
                        {(hmPersp === "Plaintiff"
                          ? isJuul
                            ? [
                                {
                                  period: "Failure to Warn -- Youth Marketing",
                                  strength: 82,
                                  c: "#10b981",
                                  label:
                                    "Very strong -- JUUL School (Dkt.3447 SOF para 47), Burns contradiction, 75% ignorance rate (Halpern-Felsher). Best theory for remand.",
                                },
                                {
                                  period: "RICO Enterprise (JLI + Altria)",
                                  strength: 65,
                                  c: "#3b82f6",
                                  label:
                                    "Good -- Altria coordination documented, due diligence files (Dkt.3393 unsealed). Causation chain and pattern requirement are gaps.",
                                },
                                {
                                  period: "Public Nuisance -- SFUSD",
                                  strength: 52,
                                  c: "#f59e0b",
                                  label:
                                    "Contested -- Physical damage documented at 6 schools (Dorn). Pak admission and Sonner equitable relief bar create SJ risk on damages.",
                                },
                                {
                                  period: "PI Causation -- Seliavski",
                                  strength: 28,
                                  c: "#ef4444",
                                  label:
                                    "Critical gap -- no medical causation expert retained. JLI SJ motion pending. Must fix before remand to E.D. Va.",
                                },
                                {
                                  period: "SFUSD Program Cost Damages",
                                  strength: 46,
                                  c: "#f59e0b",
                                  label:
                                    "Moderate -- Dorn $78.8M forward-looking strong. Past program costs gap (Pak depo) limits legal damages. Sonner threat to equitable relief.",
                                },
                                {
                                  period: "Antitrust (3:20-cv-02345)",
                                  strength: 71,
                                  c: "#10b981",
                                  label:
                                    "Strong -- Class certified Feb 26 2026. Sherman Act horizontal market allocation theory. Opt-out deadline June 13 2026. Trial Sept 28 2026.",
                                },
                              ]
                            : isIle
                              ? [
                                  {
                                    period:
                                      "Dkt.60 Window (Aug 24-28) - Ruling Pending",
                                    strength: 75,
                                    c: "#f59e0b",
                                    label:
                                      "Live procedural event - gov takes no position, PTS defers; a grant still requires the itinerary letter and PTS approvals",
                                  },
                                  {
                                    period: "$145M Round-Trip Records (¶27)",
                                    strength: 90,
                                    c: "#ef4444",
                                    label:
                                      "The government spine - netting and lender-approval overlays are the only defense answers",
                                  },
                                  {
                                    period:
                                      "Most-Culpable Transcript (Tr. 8:16-22)",
                                    strength: 85,
                                    c: "#10b981",
                                    label:
                                      "Role-differentiation anchor for every Naqvi brief and the eventual apportionment fight",
                                  },
                                  {
                                    period: "9/14 Schedule Conference",
                                    strength: 70,
                                    c: "#f59e0b",
                                    label:
                                      "Locks motions, trial dates, and STA posture - leverage on both sides peaks now",
                                  },
                                ]
                              : caseId === "musk"
                                ? [
                                    {
                                      period:
                                        "Musk Pre-Trial Threat (Dkt.522 - FRE 408(b))",
                                      strength: 85,
                                      c: "#10b981",
                                      label:
                                        "Very strong -- Musk message to Brockman Apr 25 2026 establishes punitive competitor-attack motive. Pattern from Pampena v. Musk World War III threat. If admitted by YGR, reframes entire case intent.",
                                    },
                                    {
                                      period:
                                        "Charitable Trust Breach (Count XVIII)",
                                      strength: 72,
                                      c: "#10b981",
                                      label:
                                        "Strong -- Brockman journal PX163 + Altman founding email PX003 + LP agreement language",
                                    },
                                    {
                                      period: "Unjust Enrichment (Count IV)",
                                      strength: 65,
                                      c: "#3b82f6",
                                      label:
                                        "Good -- $38M donations traceable via Dudney FIFO to $157B+ enterprise value",
                                    },
                                    {
                                      period:
                                        "Aiding & Abetting Microsoft (Count XIX)",
                                      strength: 58,
                                      c: "#f59e0b",
                                      label:
                                        "Moderate -- Kevin Scott PX230 establishes CTO-level knowledge; board-level proof gap",
                                    },
                                    {
                                      period: "SOL Defense Risk",
                                      strength: 35,
                                      c: "#ef4444",
                                      label:
                                        "Elevated risk -- Sept 2020 tweet as trigger; fraudulent concealment tolling must be proven",
                                    },
                                    {
                                      period: "Section 17510.8 Liability",
                                      strength: 60,
                                      c: "#3b82f6",
                                      label:
                                        "Contested -- competing briefs Apr 30 2026; if statute applies, creates strict liability",
                                    },
                                  ]
                                : [
                                    {
                                      period: "Infringement 061 Claim 1",
                                      strength: 65,
                                      c: "#3b82f6",
                                      label:
                                        "Moderate - lab testing for one product only",
                                    },
                                    {
                                      period: "Contributory Infringement",
                                      strength: 30,
                                      c: "#ef4444",
                                      label:
                                        "Weak - Kappa sells finished products not components",
                                    },
                                    {
                                      period: "Trademark Confusion",
                                      strength: 45,
                                      c: "#f59e0b",
                                      label:
                                        "Moderate - post-termination branding similarities",
                                    },
                                    {
                                      period: "False Advertising",
                                      strength: 35,
                                      c: "#ef4444",
                                      label:
                                        "Weak - no objective falsity benchmark",
                                    },
                                    {
                                      period: "Damages",
                                      strength: 20,
                                      c: "#ef4444",
                                      label:
                                        "Very weak - no expert or model yet",
                                    },
                                  ]
                          : hmPersp === "Defendant"
                            ? isJuul
                              ? [
                                  {
                                    period:
                                      "SFUSD Damages -- Pak Depo (SJ Centerpiece)",
                                    strength: 76,
                                    c: "#10b981",
                                    label:
                                      "Strong -- Pak 30(b)(6) We do not have that information + Matthews. Two witnesses. Combined with Sonner equitable relief bar, this is JLI best SJ argument.",
                                  },
                                  {
                                    period:
                                      "PI Causation -- No Seliavski Expert",
                                    strength: 72,
                                    c: "#10b981",
                                    label:
                                      "Very strong -- no medical causation expert retained or disclosed. SJ on PI damages is well-supported absent expert. May be case-ending for Seliavski.",
                                  },
                                  {
                                    period: "FCLAA Federal Preemption",
                                    strength: 48,
                                    c: "#f59e0b",
                                    label:
                                      "Contested -- powerful if Ninth Circuit applies broadly. Orrick denied 2020 stay but not FCLAA merits. Circuit split gives this life.",
                                  },
                                  {
                                    period: "No SFUSD-Specific Causation",
                                    strength: 55,
                                    c: "#f59e0b",
                                    label:
                                      "Moderate-strong -- JLI argues JUUL was a national phenomenon, not SFUSD-specific. Plaintiff must show SFUSD-area marketing specifically caused SFUSD costs.",
                                  },
                                  {
                                    period:
                                      "Altria Regulatory Compliance Defense",
                                    strength: 34,
                                    c: "#ef4444",
                                    label:
                                      "Weak -- Dkt.3393 unsealed due diligence files show Altria knew about youth users. MDO non-finality helps but does not eliminate harm-reduction narrative damage.",
                                  },
                                  {
                                    period:
                                      "Statute of Limitations -- Opt-Out PIs",
                                    strength: 58,
                                    c: "#f59e0b",
                                    label:
                                      "Moderate -- SOL ran Spring 2020 for Seliavski absent tolling. American Pipe tolling depends on when she filed her SFC. Discovery rule and fraudulent concealment are plaintiff counters.",
                                  },
                                ]
                              : isIle
                                ? [
                                    {
                                      period:
                                        "Bank Records - $145M Round Trips (¶27)",
                                      strength: 90,
                                      c: "#ef4444",
                                      label:
                                        "Devastating for the defense - the circular-flow spine; only netting and approval overlays soften it",
                                    },
                                    {
                                      period:
                                        "Most-Culpable Transcript (Tr. 8:16-22)",
                                      strength: 85,
                                      c: "#10b981",
                                      label:
                                        "Very strong for Naqvi - the government's own differentiation on the record",
                                    },
                                    {
                                      period: "Spam Finding + 11s Exclusion",
                                      strength: 80,
                                      c: "#10b981",
                                      label:
                                        "Strong - adverse-party expert plus the charging decision kill obstruction theming vs Naqvi",
                                    },
                                    {
                                      period: "¶40 At the Urging of Naqvi",
                                      strength: 70,
                                      c: "#ef4444",
                                      label:
                                        "The worst sentence in the S-1 for the role defense - confront it through the source witness",
                                    },
                                    {
                                      period:
                                        "Lender B Approval Texts (Count TEN)",
                                      strength: 65,
                                      c: "#10b981",
                                      label:
                                        "Strong cross material - the lender approved the transfer charged as concealment",
                                    },
                                  ]
                                : caseId === "musk"
                                  ? [
                                      {
                                        period: "Musk Yup Email (DX-516)",
                                        strength: 82,
                                        c: "#10b981",
                                        label:
                                          "Very strong -- one-word reply agreeing with less open is the defense best exhibit; direct impeachment",
                                      },
                                      {
                                        period:
                                          "LP Term Sheet Silence (DX-827)",
                                        strength: 75,
                                        c: "#10b981",
                                        label:
                                          "Strong -- Musk received Aug 2018 for-profit term sheet and said nothing; acquiescence argument",
                                      },
                                      {
                                        period:
                                          "Tell Them Im Supportive (DX-862)",
                                        strength: 70,
                                        c: "#3b82f6",
                                        label:
                                          "Strong -- Mar 2019 ratification of for-profit arm; triggers SOL and defeats trust claim",
                                      },
                                      {
                                        period: "SOL -- Sept 2020 Tweet",
                                        strength: 65,
                                        c: "#3b82f6",
                                        label:
                                          "Good -- captured by Microsoft tweet as constructive knowledge trigger; Pretrial Order No. 3 framework",
                                      },
                                      {
                                        period: "Unrestricted Gifts Defense",
                                        strength: 42,
                                        c: "#f59e0b",
                                        label:
                                          "Moderate -- board had authority to convert; but Brockman journal PX163 and Altman email PX003 undercut",
                                      },
                                    ]
                                  : [
                                      {
                                        period: "Obviousness (103)",
                                        strength: 90,
                                        c: "#10b981",
                                        label:
                                          "Strong - CN1200366A + MIT + Liu admission",
                                      },
                                      {
                                        period: "101 Ineligibility",
                                        strength: 75,
                                        c: "#10b981",
                                        label:
                                          "Strong - specification admits in vivo formation",
                                      },
                                      {
                                        period: "Country-of-Origin Fraud",
                                        strength: 70,
                                        c: "#3b82f6",
                                        label:
                                          "Good - CoAs + imports; need routing docs",
                                      },
                                      {
                                        period: "Inventorship (Slutsky)",
                                        strength: 30,
                                        c: "#ef4444",
                                        label: "Weak - need Slutsky testimony",
                                      },
                                      {
                                        period: "TMLA Enforceability",
                                        strength: 80,
                                        c: "#10b981",
                                        label:
                                          "Strong - chain-of-title gaps documented",
                                      },
                                    ]
                            : isJuul
                              ? [
                                  {
                                    period: "Internal Marketing Documents",
                                    strength: 90,
                                    c: "#10b981",
                                    label:
                                      "Overwhelming - JUUL School, board decks, mint flavor youth targeting",
                                  },
                                  {
                                    period: "Class Settlements ($255M+Altria)",
                                    strength: 75,
                                    c: "#10b981",
                                    label:
                                      "Strong signal - class resolved; shifts focus to PI and SFUSD",
                                  },
                                  {
                                    period: "FDA MDO (Stayed by 5th Cir.)",
                                    strength: 50,
                                    c: "#f59e0b",
                                    label:
                                      "Contested - strong signal of harm; stay means non-final",
                                  },
                                  {
                                    period: "SFUSD Program Cost Damages",
                                    strength: 40,
                                    c: "#f59e0b",
                                    label:
                                      "Contested - Dorn Report strong; Pak depo creates quantification gap",
                                  },
                                  {
                                    period: "Seliavski PI Causation",
                                    strength: 30,
                                    c: "#ef4444",
                                    label:
                                      "Critical gap - no medical expert; both sides aware this is the weak link",
                                  },
                                ]
                              : isIle
                                ? [
                                    {
                                      period:
                                        "Dec 17 2023 Fake-Revenue Email (¶34)",
                                      strength: 88,
                                      c: "#10b981",
                                      label:
                                        "Quantified deception in the defendants own diligence channel - $227M itemized by sham customer",
                                    },
                                    {
                                      period:
                                        "Payments Stop at Resignation (¶49)",
                                      strength: 82,
                                      c: "#10b981",
                                      label:
                                        "The cleanest jury inference that the revenue was never real",
                                    },
                                    {
                                      period: "8-K + Bell Photo (¶¶37-38)",
                                      strength: 80,
                                      c: "#10b981",
                                      label:
                                        "Public signed filing plus the image - the opening architecture, 403 fight pending",
                                    },
                                    {
                                      period: "Post-Subpoena Deletions (¶56)",
                                      strength: 78,
                                      c: "#10b981",
                                      label:
                                        "Consciousness of guilt - Chidambaran-only and severance-sensitive",
                                    },
                                  ]
                                : caseId === "musk"
                                  ? [
                                      {
                                        period:
                                          "Plaintiff Core Exhibits (PX003/163/230)",
                                        strength: 88,
                                        c: "#10b981",
                                        label:
                                          "Very strong -- Altman founding email, Brockman journal, Kevin Scott-Nadella email all admitted at trial",
                                      },
                                      {
                                        period:
                                          "Defense Impeachment Evidence (DX-516/827/862)",
                                        strength: 80,
                                        c: "#10b981",
                                        label:
                                          "Strong both ways -- Yup email, term sheet silence, and supportive email are devastating for plaintiff but defense has them too",
                                      },
                                      {
                                        period: "SOL Framework",
                                        strength: 55,
                                        c: "#f59e0b",
                                        label:
                                          "Contested -- Pretrial Order No. 3 set framework; Sept 2020 tweet is key; fraudulent concealment tolling disputed",
                                      },
                                      {
                                        period: "Section 17510.8 Applicability",
                                        strength: 50,
                                        c: "#f59e0b",
                                        label:
                                          "Open question -- competing briefs filed Apr 30 2026; statute would create strict liability if applicable",
                                      },
                                      {
                                        period: "Microsoft Aiding & Abetting",
                                        strength: 60,
                                        c: "#3b82f6",
                                        label:
                                          "Moderate-strong -- PX230 shows CTO-level knowledge 16 months before $1B investment",
                                      },
                                    ]
                                  : [
                                      {
                                        period: "Prior Art (Pre-1997)",
                                        strength: 90,
                                        c: "#10b981",
                                        label:
                                          "Strong - CN1200366A well-documented",
                                      },
                                      {
                                        period: "Patent Ownership/Standing",
                                        strength: 50,
                                        c: "#f59e0b",
                                        label:
                                          "Contested - contradictory FAC allegations",
                                      },
                                      {
                                        period: "Country of Origin",
                                        strength: 70,
                                        c: "#3b82f6",
                                        label:
                                          "Good - CoAs + imports available",
                                      },
                                      {
                                        period: "Contract (TMLA)",
                                        strength: 55,
                                        c: "#f59e0b",
                                        label:
                                          "Contested - assignment chain unclear",
                                      },
                                      {
                                        period: "Inventorship",
                                        strength: 30,
                                        c: "#ef4444",
                                        label: "Weak - needs Slutsky",
                                      },
                                      {
                                        period: "Damages",
                                        strength: 25,
                                        c: "#ef4444",
                                        label: "Weak - neither side has expert",
                                      },
                                    ]
                        ).map((h, i) => (
                          <div key={i} style={{ marginBottom: 10 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: 3,
                              }}
                            >
                              <span style={{ fontSize: 9, fontWeight: 600 }}>
                                {h.period}
                              </span>
                              <span style={{ fontSize: 8, color: h.c }}>
                                {h.strength}%
                              </span>
                            </div>
                            <div
                              style={{
                                height: 6,
                                background: th.surface,
                                borderRadius: 3,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${h.strength}%`,
                                  background: h.c,
                                  borderRadius: 3,
                                }}
                              />
                            </div>
                            <div
                              style={{
                                fontSize: 7,
                                color: th.dim,
                                marginTop: 2,
                              }}
                            >
                              {h.label}
                            </div>
                            <button
                              onClick={() =>
                                openP(
                                  "Counter Strategy: " + h.period,
                                  "**Evidence:** " +
                                    h.period +
                                    "\n**Strength:** " +
                                    h.strength +
                                    "%\n\n**Counter Strategies:**\n" +
                                    (h.strength >= 70
                                      ? "This evidence is strong. Priority counters:\n1. Retain opposing expert to challenge methodology\n2. File motion in limine to exclude or limit scope\n3. Seek targeted discovery on foundational assumptions\n4. Identify inconsistencies in supporting documents\n5. Brief judge on legal standard limiting this evidence type"
                                      : "This evidence has weaknesses. Exploit via:\n1. File targeted discovery gaps motion\n2. Challenge authentication or foundation\n3. Retain expert to quantify uncertainty\n4. Cross-examine on methodology limitations\n5. Seek sanctions if spoliation suspected"),
                                  h.period,
                                )
                              }
                              style={{
                                marginTop: 4,
                                fontSize: 7,
                                background: h.c + "18",
                                color: h.c,
                                border: "1px solid " + h.c + "44",
                                borderRadius: 3,
                                padding: "2px 8px",
                                cursor: "pointer",
                              }}
                            >
                              🛡 Counter Strategy
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* SETTLEMENT */}
                  {rbP === "settlement" &&
                    (() => {
                      const settlement = isJuul
                        ? {
                            fav: 1.4,
                            label: "Slightly Favors Plaintiff",
                            likelihood: 62,
                            likelihoodLabel:
                              "Mixed - class settled, opt-out + SFUSD pending",
                            likelihoodColor: "#f59e0b",
                            summary:
                              "MDL: JLI Class Settled ($255M) · Altria Settled · Opt-Out + SFUSD Active",
                            explanation:
                              "JLI and Altria completed class settlements. Individual PI and SFUSD cases remain. Post-SJ: if SJ denied, JLI faces remand and jury trial; if granted, PI plaintiffs lose leverage. SFUSD case requires policy concessions beyond money.",
                          }
                        : isIle
                          ? {
                              fav: 2.5,
                              label: "Plea Architecture · Pre-Motion",
                              likelihood: 6,
                              likelihoodLabel:
                                "Possible - window open before 9/14",
                              likelihoodColor: "#f59e0b",
                              summary:
                                "Differentiated Naqvi resolution plausible before the schedule locks",
                              explanation:
                                "No civil-style settlement exists; leverage is count architecture. The §225 ten-year floor is the government anchor; the most-culpable transcript, the 11s exclusion, and the spam finding are the counter-anchors. A non-1s disposition (371 or a wire count) removes the mandatory minimum entirely. After the 9/14 schedule, government sunk costs harden - and Balwani's post-trial 155 months is the standing warning for the number two who tries it.",
                            }
                          : caseId === "musk"
                            ? {
                                fav: 2.0,
                                label: "Neutral · Trial in Progress",
                                likelihood: 4,
                                likelihoodLabel: "Highly Unlikely - mid-trial",
                                likelihoodColor: "#ef4444",
                                summary:
                                  "Phase 1 Trial in Progress · Settlement Unlikely",
                                explanation:
                                  "Trial is underway (Phase 1). Mid-trial settlement is highly unlikely - Musk is a named plaintiff with personal grievance and xAI competitive interest; OpenAI cannot settle the charitable trust without AG involvement. Post-verdict: if Musk wins Phase 1, settlement pressure on Phase 2 antitrust increases dramatically. If defendants win Phase 1, Musk faces SOL and acquiescence arguments that will make Phase 2 harder.",
                              }
                            : {
                                fav: 2.6,
                                label: "Slightly Favors Defendant",
                                likelihood: 58,
                                likelihoodLabel:
                                  "Possible - mediation ordered by Dec 31, 2026",
                                likelihoodColor: "#10b981",
                                summary:
                                  "Court-Ordered Mediation by Dec 31, 2026",
                                explanation:
                                  "Settlement posture is neutral-to-slightly-favoring-defendant. Defendant withdrew sanctions; plaintiff narrowed from 5 patents to 1. Mediation ordered by Dec 31, 2026. Key inflection: 4/17/26 hearing.",
                              };
                      const pct = (settlement.fav / 4) * 100;
                      const labelColor =
                        settlement.fav < 1.5
                          ? "#ef4444"
                          : settlement.fav < 2.5
                            ? "#f59e0b"
                            : "#3b82f6";
                      return (
                        <div>
                          <h2
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              margin: "0 0 10px",
                            }}
                          >
                            🤝 Settlement Signals
                          </h2>
                          {/* Two-up: Favorability + Likelihood */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                              marginBottom: 10,
                            }}
                          >
                            {/* FAVORABILITY - auto-computed, read-only */}
                            <div
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                padding: "14px 16px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: 10,
                                }}
                              >
                                <div style={{ fontSize: 11, fontWeight: 600 }}>
                                  Settlement Favorability
                                </div>
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: "#a78bfa",
                                    background: "#a78bfa15",
                                    border: "1px solid #a78bfa33",
                                    padding: "2px 7px",
                                    borderRadius: 10,
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                  }}
                                >
                                  ⚡ AUTO
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: 6,
                                  fontFamily: "monospace",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    color: "#ef4444",
                                    fontWeight: 600,
                                  }}
                                >
                                  ◀ PLAINTIFF
                                </span>
                                <span style={{ fontSize: 7, color: th.faint }}>
                                  NEUTRAL
                                </span>
                                <span
                                  style={{
                                    fontSize: 7,
                                    color: "#3b82f6",
                                    fontWeight: 600,
                                  }}
                                >
                                  DEFENDANT ▶
                                </span>
                              </div>
                              {/* Custom static slider with fixed marker */}
                              <div
                                style={{
                                  position: "relative",
                                  height: 24,
                                  marginBottom: 6,
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 10,
                                    left: 0,
                                    right: 0,
                                    height: 5,
                                    borderRadius: 3,
                                    background:
                                      "linear-gradient(to right,#ef4444 0%,#f59e0b 35%,#6b7280 50%,#3b82f6 70%,#10b981 100%)",
                                    opacity: 0.85,
                                  }}
                                />
                                {/* Tick marks */}
                                {[0, 25, 50, 75, 100].map((p) => (
                                  <div
                                    key={p}
                                    style={{
                                      position: "absolute",
                                      top: 7,
                                      left: `calc(${p}% - 1px)`,
                                      width: 2,
                                      height: 11,
                                      background: th.bg,
                                      borderRadius: 1,
                                      opacity: 0.5,
                                    }}
                                  />
                                ))}
                                {/* Fixed marker */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 4,
                                    left: `calc(${pct}% - 8px)`,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: "#fff",
                                    border: `3px solid ${labelColor}`,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                    zIndex: 1,
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  textAlign: "center",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: labelColor,
                                  marginTop: 4,
                                }}
                              >
                                {settlement.label}
                              </div>
                            </div>

                            {/* LIKELIHOOD - large gauge */}
                            <div
                              style={{
                                background: th.card,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                padding: "14px 16px",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: 10,
                                }}
                              >
                                <div style={{ fontSize: 11, fontWeight: 600 }}>
                                  Likelihood to Settle
                                </div>
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: "#a78bfa",
                                    background: "#a78bfa15",
                                    border: "1px solid #a78bfa33",
                                    padding: "2px 7px",
                                    borderRadius: 10,
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                  }}
                                >
                                  ⚡ AUTO
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 6,
                                  marginBottom: 8,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 36,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: settlement.likelihoodColor,
                                    letterSpacing: "-1px",
                                  }}
                                >
                                  {settlement.likelihood}
                                  <span style={{ fontSize: 18 }}>%</span>
                                </span>
                                <span style={{ fontSize: 9, color: th.muted }}>
                                  {settlement.likelihood < 10
                                    ? "unlikely"
                                    : settlement.likelihood < 40
                                      ? "low"
                                      : settlement.likelihood < 70
                                        ? "mixed"
                                        : "high"}
                                </span>
                              </div>
                              <div
                                style={{
                                  height: 6,
                                  borderRadius: 3,
                                  background: th.surface,
                                  overflow: "hidden",
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${settlement.likelihood}%`,
                                    background: settlement.likelihoodColor,
                                    borderRadius: 3,
                                    transition: "width 0.4s ease",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  lineHeight: 1.5,
                                }}
                              >
                                {settlement.likelihoodLabel}
                              </div>
                            </div>
                          </div>

                          {/* Posture summary */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              borderLeft: "3px solid #f59e0b",
                              padding: "14px 18px",
                              marginBottom: 12,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                marginBottom: 5,
                              }}
                            >
                              {settlement.summary}
                            </div>
                            <p
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.6,
                                margin: 0,
                              }}
                            >
                              {settlement.explanation}
                            </p>
                          </div>
                          {(isJuul
                            ? [
                                {
                                  signal:
                                    "SJ Under Submission -- Oct 31 2025 Hearing",
                                  dir: "Negative",
                                  detail:
                                    "Orrick held 43-minute SJ hearing via Zoom on Oct 31 2025 (Dkt.4453). Cases: Aragona, Lane, Legacki, Nessmith, Sedgwick, Seliavski, SFUSD. Judge telegraphed remand intent. Both sides are invested in SJ outcome -- delays settlement until ruling. Ruling is the next major inflection point for settlement leverage.",
                                },
                                {
                                  signal:
                                    "No Seliavski Causation Expert -- Settlement Pressure on Plaintiff",
                                  dir: "Negative for Plaintiff",
                                  detail:
                                    "Seliavski has no medical causation expert retained. If JLI wins SJ on PI causation, Seliavski loses leverage entirely and the individual PI opt-out cases collapse in remand courts. This creates reverse settlement pressure: plaintiff must settle or retain an expert before SJ ruling. JLI is likely holding out for the ruling.",
                                },
                                {
                                  signal:
                                    "Remand Signaled by Orrick at Dec 16 2025 CMC",
                                  dir: "Positive for Plaintiff",
                                  detail:
                                    "At Dec 16 2025 CMC (Dkt.4476), Orrick confirmed intent to remand opt-out PI cases post-SJ. Ordered joint letter by Jan 6 2026 on supplemental discovery scope. Remand to plaintiff-favorable juries in CA FL and TX would dramatically increase JLI settlement pressure. This is the inflection point plaintiff is positioning toward.",
                                },
                                {
                                  signal:
                                    "Antitrust Trial Sept 28 2026 -- Opt-Out Deadline June 13 2026",
                                  dir: "Positive for Plaintiff",
                                  detail:
                                    "Parallel antitrust case (3:20-cv-02345) has class certified Feb 26 2026 and trial set Sept 28 2026. Direct and indirect purchaser classes. Opt-out deadline June 13 2026. Two-front war -- JLI and Altria face both MDL PI remand trial AND antitrust class trial in 2026. Settlement pressure is compounding.",
                                },
                                {
                                  signal:
                                    "Meta Social Media Production -- May 8 2026",
                                  dir: "Positive for Plaintiff",
                                  detail:
                                    "Court ordered Meta account data production Apr 14 2026 (extended to May 8 2026 per Dkt.4497). If Meta production reveals JLI placed targeted advertising toward minors on Instagram, it will significantly strengthen failure-to-warn and RICO enterprise theories and increase JLI settlement pressure on PI opt-out cases before remand.",
                                },
                                {
                                  signal:
                                    "JLI Class Settlement Final ($255M, Sept 2023)",
                                  dir: "Positive",
                                  detail:
                                    "JLI resolved consumer class claims for $255M. Altria separately settled consumer class. Further distribution authorized Mar 6 2026 (Dkt.4489). Class pressure removed -- but opt-out PI (Seliavski and similar) and SFUSD government entity claims remain active and unsettled. These are the high-value remaining litigation targets.",
                                },
                              ]
                            : isIle
                              ? [
                                  {
                                    signal:
                                      "Gov takes NO POSITION on renewed travel (Dkt.60)",
                                    dir: "Positive for Defendant",
                                    detail:
                                      "After leading the detention fight in April, the government declined to oppose the renewed application and PTS defers. The posture repair after the 7/22 denial is complete, and the compliance record is now currency for every future request.",
                                  },
                                  {
                                    signal:
                                      "S-1 adds Count 11s vs Chidambaran ONLY",
                                    dir: "Positive for Defendant",
                                    detail:
                                      "The grand jury resolved detention-stage obstruction theming in Naqvi's favor - and simultaneously aimed maximal plea pressure at the CEO. Watch for cooperation inversion; a cooperating Chidambaran flips the table.",
                                  },
                                  {
                                    signal:
                                      "Milbank substitution + leave-first letter practice",
                                    dir: "Positive for Defendant",
                                    detail:
                                      "A former SDNY criminal division chief recalibrated to Garaufis process norms within one cycle; Dkt.60 drew no opposition.",
                                  },
                                  {
                                    signal:
                                      "Watson (Ozy Media) 116 months in this district",
                                    dir: "Negative for Defendant",
                                    detail:
                                      "Same courthouse, fabricated-metrics DNA, post-trial sentence in Dec 2024 - the reference point the government will cite for any executive who tries the case and loses.",
                                  },
                                ]
                              : caseId === "musk"
                                ? [
                                    {
                                      signal:
                                        "Musk Apr 25 2026 Pre-Trial Threat to Brockman (Dkt.522)",
                                      dir: "Negative for Plaintiff",
                                      detail:
                                        "Two days before trial, Musk messaged Brockman to gauge settlement. When Brockman suggested mutual drop of claims, Musk responded: By the end of this week, you and Sam will be the most hated men in America. OpenAI filed Dkt.522 on May 3 2026 to admit this as FRE 408(b) motive/bias evidence. If YGR admits it: devastating to Musk credibility on intent; directly contradicts his charitable trust enforcement narrative. YGR ruling on admissibility is now the most urgent pre-testimony event in the trial.",
                                    },
                                    {
                                      signal: "Trial in progress (Phase 1)",
                                      dir: "Negative",
                                      detail:
                                        "Mid-trial settlement is structurally unlikely. Musk has personal and competitive (xAI) reasons to see the case through. OpenAI cannot settle the charitable trust count without California AG involvement, which adds institutional complexity.",
                                    },
                                    {
                                      signal:
                                        "OpenAI for-profit conversion completed (2025)",
                                      dir: "Negative",
                                      detail:
                                        "OpenAI converted to a public benefit corporation in late 2025. This fait accompli reduces OpenAI incentive to settle before a final judgment -- they already have what they wanted. Musk may seek constructive trust on Microsoft equity as damages, which would require Microsoft at the table.",
                                    },
                                    {
                                      signal:
                                        "Phase 2 antitrust claims (Counts VIII-XV) stayed",
                                      dir: "Positive",
                                      detail:
                                        "The antitrust counts are stayed pending Phase 1. A Phase 1 plaintiff verdict would dramatically increase settlement pressure on Phase 2 -- OpenAI and Microsoft would face exposure on the competition law theory before a second trial.",
                                    },
                                    {
                                      signal:
                                        "California AG declined to join (but monitors)",
                                      dir: "Neutral",
                                      detail:
                                        "AG Rob Bonta is named as a defendant (dropped) and is monitoring the case. Any settlement of charitable trust claims would likely require AG notice and possibly approval. This creates institutional friction to settlement.",
                                    },
                                  ]
                                : [
                                    {
                                      signal:
                                        "Sanctions motion withdrawn (Dkt.104)",
                                      dir: "Positive",
                                      detail:
                                        "Defendant stepped back from Rule 11 attack -- signals willingness to de-escalate.",
                                    },
                                    {
                                      signal:
                                        "Plaintiff narrowed to 1 of 5 patents",
                                      dir: "Positive",
                                      detail:
                                        "Voluntary narrowing often precedes settlement discussions.",
                                    },
                                    {
                                      signal: "Three active MTDs fully briefed",
                                      dir: "Negative",
                                      detail:
                                        "Heavy dispositive motion investment delays settlement until after court rules.",
                                    },
                                  ]
                          ).map((s, i) => (
                            <div
                              key={i}
                              style={{
                                background: th.card,
                                borderRadius: 6,
                                border: `1px solid ${th.border}`,
                                padding: "10px 14px",
                                marginBottom: 6,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div style={{ fontSize: 10, fontWeight: 600 }}>
                                  {s.signal}
                                </div>
                                <span
                                  style={{
                                    fontSize: 8,
                                    color: s.dir.includes("↑")
                                      ? "#10b981"
                                      : "#ef4444",
                                  }}
                                >
                                  {s.dir}
                                </span>
                              </div>
                              <p
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  lineHeight: 1.5,
                                  margin: "4px 0 0",
                                }}
                              >
                                {s.detail}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  {/* HEARING PREP */}
                  {rbP === "hearing" && (
                    <div>
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          margin: "0 0 8px",
                        }}
                      >
                        🎤 Hearing Prep Pack
                      </h2>
                      <div
                        style={{ display: "flex", gap: 3, marginBottom: 10 }}
                      >
                        {["Plaintiff", "Defendant"].map((p) => (
                          <button
                            key={p}
                            onClick={() => setHearPersp(p)}
                            style={{
                              padding: "3px 10px",
                              fontSize: 8,
                              fontWeight: hearPersp === p ? 600 : 400,
                              background:
                                hearPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef444418"
                                    : "#3b82f618"
                                  : "transparent",
                              color:
                                hearPersp === p
                                  ? p === "Plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6"
                                  : th.dim,
                              border: `1px solid ${hearPersp === p ? (p === "Plaintiff" ? "#ef4444" : "#3b82f6") : th.border}`,
                              borderRadius: 3,
                              cursor: "pointer",
                            }}
                          >
                            {p} Perspective
                          </button>
                        ))}
                      </div>
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 8,
                          border: `1px solid ${th.border}`,
                          padding: "14px 18px",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            marginBottom: 6,
                          }}
                        >
                          {isJuul
                            ? "SJ Ruling + Remand CMC"
                            : isIle
                              ? "Status Conference - 9/14/26 11:00 AM (Ctrm 10B S)"
                              : caseId === "musk"
                                ? "Phase 1 Trial -- Counts XVIII IV XIX"
                                : "MTD Hearing - 4/17/26 1:30 PM"}{" "}
                          · {hearPersp} View
                        </div>
                        <p
                          style={{
                            fontSize: 9,
                            color: th.muted,
                            lineHeight: 1.5,
                            margin: "0 0 10px",
                          }}
                        >
                          {isJuul
                            ? hearPersp === "Plaintiff"
                              ? "SFUSD and opt-out plaintiffs: SJ ruling triggers immediate remand motion to transferee state courts in CA, FL, and TX. Orrick telegraphed remand intent at Oct 31, 2025 hearing. Priority: favorable SJ ruling preserving state law claims, then expedited remand with plaintiff-friendly juries."
                              : "JLI strategy: if SJ granted on causation/damages, argue PI plaintiffs have no viable claims for remand. If denied, oppose remand on preemption grounds. Keep supplemental discovery scope narrow per Jan 6 letter."
                            : isIle
                              ? hearPersp === "Plaintiff"
                                ? "Government posture into 9/14: propose January 2027 dates, a tight motion schedule, and broad STA exclusion; cooperation windows for CC-2 and CC-3 close as the schedule locks. Expect an opening built on the bell photo and the 8-K, with the 403 fight briefed early."
                                : "Defense posture into 9/14: arrive with an agreed schedule - severance/Bruton, §225 Rule 12, MILs - and trade STA consent for firm Feb 2027 dates plus 3500 deadlines. Keep the compliance record spotless; it funds every conditions request including the Dkt.60 follow-through."
                              : caseId === "musk"
                                ? hearPersp === "Plaintiff"
                                  ? "URGENT - YGR Ruling on Dkt.522: OpenAI filed May 3 2026 to admit Musk Apr 25 message to Brockman (By the end of this week, you and Sam will be the most hated men in America). Oppose under FRE 408(a)(2) (settlement communication) and FRE 403 (unfair prejudice). If admitted, expect OpenAI to use Exhibit A (Pampena v. Musk Vol.7, Korman testimony, World War III threat through Ringler) to establish pattern of punitive litigation threats. This single ruling could reframe the intent element for the entire trial. | Plaintiff strategy: anchor testimony to PX003 (Altman founding email), PX163 (Brockman journal), and PX230 (Kevin Scott-Nadella email). These three exhibits establish (1) charitable trust representations, (2) internal acknowledgment of wrongdoing, and (3) Microsoft knowledge. Press section 17510.8 strict liability theory per Dkt.515. Counter SOL defense with fraudulent concealment tolling."
                                  : "Defense strategy: deploy DX-516 (Yup email), DX-827 (LP term sheet silence), DX-862 (supportive email) to argue acquiescence and SOL. Attack Dudney FIFO tracing chain. Contest section 17510.8 applicability per Dkt.516. Emphasize Sept 2020 tweet as Pretrial Order No. 3 SOL trigger."
                                : hearPersp === "Plaintiff"
                                  ? "Lead with claim construction - argue plain language covers capsule form. Ready for 101 challenge."
                                  : "Press Liu deposition admissions and prior art on 103 obviousness."}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {(isJuul
                            ? hearPersp === "Plaintiff"
                              ? [
                                  "Sonner-Pak Circular Trap: if JLI wins Pak (no legal damages), there IS no adequate legal remedy -- so Sonner should NOT bar equitable abatement. Orrick signaled awareness of this at Oct 31 hearing. Press the circularity aggressively.",
                                  "SFUSD stays in N.D. Cal. -- SFUSD does NOT go to a transferee court. It was filed here. Only individual PI opt-outs (Seliavski, Aragona, etc.) remand to their original transferee courts. Orrick retains SFUSD.",
                                  "Halpern-Felsher 75%: three-quarters of SFUSD JUUL users did not know the product contained nicotine. Single most powerful number in the case. JLI argues general awareness -- counter with SFUSD-area-specific survey data.",
                                  "FTC dismissal Feb 2023: JLI will cite FTC dropping its antitrust complaint as exculpatory. Counter: FTC dismissal is a resource decision, not a merits finding. Private antitrust is independent of FTC enforcement.",
                                  "Meta production (May 8 2026): if data shows Instagram targeted under-18 users for JLI ads, it is third-party corroboration that defeats JLI age verification defense entirely.",
                                ]
                              : [
                                  "Pak-Sonner sequencing: argue (1) Pak destroys past legal damages AND (2) adequate legal remedy still exists via future abatement costs -- so Sonner bars equitable relief. Do NOT concede there is no legal remedy at all. That defeats Sonner.",
                                  "FTC dismissed antitrust complaint Feb 2023. FDA MDO stayed and withdrawn. Two agencies stepped back. Both are powerful exculpatory signals -- deploy early in any remand briefing.",
                                  "PI causation gap: no Seliavski medical expert = silent record = no specific causation. JLI should emphasize that this is plaintiff choice, not a gap JLI created.",
                                  "NMD jurisdiction: ask Orrick to address personal jurisdiction over Pritzker, Valani, Huh in each transferee state before entering remand order. Federal RICO nationwide service disappears on remand.",
                                  "Supplemental discovery Jan 6 2026 letter established scope: deponent emails and social media only. Oppose any attempt to expand scope in remand courts.",
                                ]
                            : isIle
                              ? hearPersp === "Plaintiff"
                                ? [
                                    "Lock a two-track resolution posture before filing burdens mount",
                                    "Front-load 3500 preparation for CC-2 and CC-3 - the defense will demand early production dates",
                                    "Preserve the bell photo for opening; brief the 403 fight now",
                                  ]
                                : [
                                    "File the severance/Bruton package on day one of the schedule",
                                    "Serve the Rule 17(c) subpoena for the full committee forensic report",
                                    "Submit the Dkt.60 itinerary letter the day any grant posts - PTS approval per meeting",
                                    "Interview Olivier and Gerety before the conference",
                                  ]
                              : caseId === "musk"
                                ? hearPersp === "Plaintiff"
                                  ? [
                                      "PX163 (Brockman journal) is your anchor -- handwritten before litigation, no motive to deceive: wrong to steal non-profit from Elon",
                                      "PX230 (Kevin Scott to Nadella, Mar 2018): CTO questioned whether OpenAI donors were aware 16 months before $1B Microsoft investment",
                                      "PX003 (Altman founding email): tech belongs to the world -- directly contradicts OpenAI unrestricted gifts defense",
                                      "Section 17510.8: if applicable, creates strict liability -- no need to prove common-law charitable trust elements",
                                      "Dudney FIFO: $38M Musk donations traceable through nonprofit to $157B+ enterprise value -- press the chain of title",
                                    ]
                                  : [
                                      "DX-516: Musk one-word Yup reply agreeing with less open (Jan 2016) -- best impeachment exhibit in the trial",
                                      "DX-827: Musk received Aug 2018 LP term sheet showing for-profit structure and said nothing -- acquiescence",
                                      "DX-862: Tell them Im supportive (Mar 2019) -- ratification of for-profit arm triggers SOL and defeats trust claim",
                                      "Section 17510.8 (Dkt.516): contest applicability -- donations to a nonprofit are not charitable solicitations in commercial sense",
                                      "Sept 2020 tweet (MDX-1537): Musk publicly stated captured by Microsoft -- actual knowledge, SOL clock running per Pretrial Order No. 3",
                                    ]
                                : hearPersp === "Plaintiff"
                                  ? [
                                      "Lead with plain claim language",
                                      "Distinguish CN1200366A on dosage specificity",
                                      "Emphasize commercial success and long-felt need",
                                      "Attack Liu depo context - partial quote only",
                                      "Request claim construction ruling before 101 ruling",
                                    ]
                                  : [
                                      "Liu: we did not invent the molecule - repeat throughout",
                                      "CN1200366A anticipates every limitation",
                                      "101: specification admits in vivo formation = natural phenomenon",
                                      "Press for judgment on pleadings if 101 wins",
                                      "Request costs and fees if court agrees on invalidity",
                                    ]
                          ).map((item, i) => (
                            <div
                              key={i}
                              style={{
                                background: th.surface,
                                borderRadius: 4,
                                padding: "7px 10px",
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.4,
                                borderLeft: `2px solid ${hearPersp === "Plaintiff" ? "#ef4444" : "#3b82f6"}`,
                              }}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 8,
                          border: `1px solid ${th.border}`,
                          padding: "12px 16px",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <div style={{ fontSize: 10, fontWeight: 600 }}>
                            📋 Relevant Docket Entries
                          </div>
                          <button
                            onClick={() => {
                              const entries = aD
                                .filter((d) =>
                                  (d.text || "")
                                    .toLowerCase()
                                    .match(
                                      /hearing|motion|order|ruling|judgment|pretrial|status conference/,
                                    ),
                                )
                                .slice(0, 20);
                              const txt =
                                "HEARING BINDER - " +
                                (isIle
                                  ? caseId === "chid"
                                    ? "USA v. Chidambaran (1:26-cr-00097-NGG-1)"
                                    : "USA v. Naqvi (1:26-cr-00097-NGG-2)"
                                  : isJuul
                                    ? "Seliavski v. JUUL Labs MDL (3:19-md-02913-WHO)"
                                    : caseId === "musk"
                                      ? "Musk v. Altman et al. (4:24-cv-04722-YGR)"
                                      : "Neurocentria v. Mando (2:25-cv-05871-FLA-MAR)") +
                                "\n" +
                                new Date().toLocaleDateString() +
                                "\n\nDOWNLOAD THESE ECF ENTRIES FROM PACER TO BUILD YOUR PHYSICAL BINDER:\n" +
                                entries
                                  .map(
                                    (d, i) =>
                                      `${i + 1}. Dkt.${d.num} - ${d.text.slice(0, 80)} (${d.date})`,
                                  )
                                  .join("\n") +
                                "\n\nPACER INSTRUCTIONS:\n1. Go to pacer.gov → Log in\n2. Search case: " +
                                (isJuul
                                  ? "3:19-md-02913-WHO"
                                  : "2:25-cv-05871-FLA-MAR") +
                                "\n3. Click each docket number above to download PDF\n4. Print double-sided, tab-indexed\n5. Bring to Courtroom " +
                                (isJuul
                                  ? "2, 450 Golden Gate Ave, SF, CA"
                                  : "350 W 1st St, LA, CA");
                              const blob = new Blob([txt], {
                                type: "text/plain",
                              });
                              const a = document.createElement("a");
                              a.href = URL.createObjectURL(blob);
                              a.download = "hearing-binder-checklist.txt";
                              a.click();
                            }}
                            style={{
                              fontSize: 8,
                              background: "#1e40af",
                              color: "#fff",
                              border: "none",
                              borderRadius: 4,
                              padding: "4px 10px",
                              cursor: "pointer",
                            }}
                          >
                            ⬇ Download Checklist
                          </button>
                        </div>
                        {aD
                          .filter((d) =>
                            (d.text || "")
                              .toLowerCase()
                              .match(
                                /hearing|motion|order|ruling|judgment|pretrial|status conference/,
                              ),
                          )
                          .slice(0, 12)
                          .map((d, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "flex-start",
                                padding: "4px 0",
                                borderBottom: `1px solid ${th.border}55`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: "#f59e0b",
                                  flexShrink: 0,
                                  minWidth: 36,
                                }}
                              >
                                #{d.num}
                              </span>
                              <span
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  flex: 1,
                                  lineHeight: 1.4,
                                }}
                              >
                                {d.text.slice(0, 80)}
                                {d.text.length > 80 ? "…" : ""}
                              </span>
                              <span
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  flexShrink: 0,
                                }}
                              >
                                {d.date}
                              </span>
                            </div>
                          ))}
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 7,
                            color: th.faint,
                            fontStyle: "italic",
                          }}
                        >
                          Download checklist includes PACER download
                          instructions for physical binder preparation
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          const ctx = isIle
                            ? `USA v. Chidambaran & Naqvi, ${hearPersp} perspective`
                            : isJuul
                              ? `Seliavski v. JUUL MDL, ${hearPersp} perspective`
                              : caseId === "musk"
                                ? `Musk v. Altman, ${hearPersp} perspective`
                                : `Neurocentria v. Mando, ${hearPersp} perspective`;
                          openP("Generating Hearing Pack...", "", ctx, true);
                          try {
                            const r = await fetch("/api/messages", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                model: "claude-sonnet-4-20250514",
                                max_tokens: 1000,
                                messages: [
                                  {
                                    role: "user",
                                    content: `Generate complete hearing prep pack for ${ctx}. Include: 5 key arguments, 3 anticipated judge questions with answers, top 3 exhibits, closing statement.`,
                                  },
                                ],
                              }),
                            });
                            const d = await r.json();
                            openP(
                              "Hearing Pack",
                              d.content
                                .filter((b) => b.type === "text")
                                .map((b) => b.text)
                                .join(""),
                              "Hearing Prep",
                            );
                          } catch (e) {
                            alert("Error");
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "10px",
                          fontSize: 11,
                          background: "linear-gradient(135deg,#1e40af,#10b981)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        🎤 Generate Full Hearing Pack
                      </button>
                      <button
                        onClick={async () => {
                          openP(
                            "Generating ECF Index...",
                            "",
                            isIle
                              ? "iLearning Status Conf"
                              : isJuul
                                ? "JUUL Hearing"
                                : caseId === "musk"
                                  ? "Musk Trial"
                                  : "Mando Hearing",
                            true,
                          );
                          try {
                            const rsp = await fetch("/api/messages", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                model: "claude-sonnet-4-20250514",
                                max_tokens: 1400,
                                system:
                                  "Generate a precise hearing document index with ECF docket numbers.",
                                messages: [
                                  {
                                    role: "user",
                                    content:
                                      "Case: " +
                                      (isIle
                                        ? "USA v. Chidambaran & Naqvi 1:26-cr-00097-NGG (E.D.N.Y., Judge Garaufis)"
                                        : isJuul
                                          ? "Seliavski v. JUUL MDL 3:19-md-02913-WHO (N.D. Cal., Judge Orrick)"
                                          : caseId === "musk"
                                            ? "Musk v. Altman 4:24-cv-04722-YGR (N.D. Cal., Judge YGR) - TRIAL"
                                            : "Neurocentria v. Mando 2:25-cv-05871-FLA-MAR (C.D. Cal.)") +
                                      ". " +
                                      hearPersp +
                                      ".\nGenerate a printable hearing document index:\n1. CRITICAL FILINGS with actual ECF docket numbers\n2. KEY EXHIBITS with docket references\n3. CONTROLLING AUTHORITY to cite\n4. PRE-HEARING CHECKLIST\n\nUse actual docket numbers from the case record.",
                                  },
                                ],
                              }),
                            });
                            const d = await rsp.json();
                            const txt = d.content
                              .filter((b) => b.type === "text")
                              .map((b) => b.text)
                              .join("")
                              .trim();
                            openP(
                              "Hearing Document Index - " + hearPersp,
                              txt +
                                "\n\n---\nTO ASSEMBLE BUNDLE:\nDownload each docket from PACER (case " +
                                (isJuul
                                  ? "3:19-md-02913-WHO"
                                  : "2:25-cv-05871-FLA-MAR") +
                                "). Print this index as cover page. Arrange documents in docket order.",
                              "Hearing Bundle",
                              false,
                            );
                          } catch (e) {
                            openP(
                              "Error",
                              "Could not generate index.",
                              "Hearing Bundle",
                              false,
                            );
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "8px",
                          fontSize: 9,
                          background: th.surface,
                          color: th.muted,
                          border: `1px solid ${th.border}`,
                          borderRadius: 6,
                          cursor: "pointer",
                          marginTop: 6,
                        }}
                      >
                        📋 ECF Document Index + Hearing Bundle
                      </button>
                    </div>
                  )}
                  {/* PROCEDURAL TRAPS */}
                  {rbP === "traps" && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <h2
                          style={{ fontSize: 15, fontWeight: 700, margin: 0 }}
                        >
                          ⚠️ Procedural Traps
                        </h2>
                        <button
                          onClick={async () => {
                            setTrapSuggLoading(true);
                            const fallback = isJuul
                              ? trapPersp === "Defendant"
                                ? [
                                    {
                                      trap: "Premature SJ Without 56(d) Declaration",
                                      rule: "FRCP 56(d); Judge Orrick Standing Order",
                                      risk: "High",
                                      why: "Filing SJ before discovery closes risks denial with prejudice without a 56(d) declaration showing further discovery is needed.",
                                      fix: "File 56(d) declaration if any discovery outstanding.",
                                    },
                                    {
                                      trap: "Kamakana Sealing Mislabeled",
                                      rule: "Kamakana v. Honolulu; N.D. Cal. LR 79-5",
                                      risk: "High",
                                      why: "Dispositive motion exhibits require compelling reasons - not good cause. Mislabeling risks public disclosure of confidential exhibits.",
                                      fix: "Apply compelling reasons standard to all SJ and pretrial exhibits.",
                                    },
                                    {
                                      trap: "Joint Letter Brief Requirement",
                                      rule: "Judge Orrick Standing Order §III.A",
                                      risk: "Medium",
                                      why: "Discovery disputes not raised via joint 5-page letter will be stricken. No standalone motions permitted.",
                                      fix: "File joint letter at first sign of any discovery dispute.",
                                    },
                                  ]
                                : [
                                    {
                                      trap: "Mandatory Pre-Trial Disclosure Deadline",
                                      rule: "FRCP 26(a)(3); Scheduling Order",
                                      risk: "High",
                                      why: "Exhibit and witness lists due 28 days before trial. Missing this deadline results in automatic exclusion.",
                                      fix: "Calendar 28-day pre-trial disclosure deadline now.",
                                    },
                                    {
                                      trap: "Contention Precision Under LPR 3-1",
                                      rule: "C.D. Cal. LPR 3-1",
                                      risk: "High",
                                      why: "Infringement contentions must specifically identify each accused product, claim, and limitation. Vague contentions risk preclusion.",
                                      fix: "Supplement contentions before close of fact discovery.",
                                    },
                                  ]
                              : [];
                            setSuggestedTraps(fallback);
                            try {
                              const rsp = await fetch("/api/messages", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  model: "claude-sonnet-4-20250514",
                                  max_tokens: 900,
                                  system:
                                    "Return ONLY a valid JSON array of objects with keys: trap, rule, risk (High/Medium/Low), why, fix. No markdown, no other text.",
                                  messages: [
                                    {
                                      role: "user",
                                      content:
                                        "Case: " +
                                        (isIle
                                          ? "USA v. Chidambaran & Naqvi 1:26-cr-00097-NGG (E.D.N.Y., Judge Garaufis)"
                                          : isJuul
                                            ? "Seliavski v. JUUL MDL 3:19-md-02913-WHO (N.D. Cal., Judge Orrick)"
                                            : caseId === "musk"
                                              ? "Musk v. Altman 4:24-cv-04722-YGR (N.D. Cal., Judge YGR) - TRIAL"
                                              : "Neurocentria v. Mando 2:25-cv-05871-FLA-MAR (C.D. Cal.)") +
                                        ". " +
                                        trapPersp +
                                        " perspective. Return 3 procedural traps as JSON array.",
                                    },
                                  ],
                                }),
                              });
                              const d = await rsp.json();
                              const txt =
                                d.content
                                  ?.filter((b) => b.type === "text")
                                  .map((b) => b.text)
                                  .join("")
                                  .trim() || "";
                              const parsed = JSON.parse(
                                txt.replace(/```json|```/g, "").trim(),
                              );
                              if (Array.isArray(parsed) && parsed.length > 0)
                                setSuggestedTraps(parsed);
                            } catch (e) {
                            } finally {
                              setTrapSuggLoading(false);
                            }
                          }}
                          style={{
                            fontSize: 8,
                            padding: "3px 9px",
                            background: "#a78bfa",
                            color: "#fff",
                            border: "none",
                            borderRadius: 3,
                            cursor: "pointer",
                          }}
                        >
                          {trapSuggLoading ? "⏳…" : "✨ Suggest More"}
                        </button>
                      </div>
                      <p
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          lineHeight: 1.5,
                          margin: "0 0 10px",
                        }}
                      >
                        {trapPersp === "Defendant"
                          ? isJuul
                            ? "Procedural risks under Judge Orrick standing orders. Key: joint 5-page letter for disputes, Kamakana sealing, Jan 6 supplemental discovery deadline."
                            : isIle
                              ? "Defense procedural risks: Garaufis letter-motion norms, STA exclusion language, Curcio cleanup from the early joint appearance (Dkt.13), sealed-bond mechanics."
                              : caseId === "musk"
                                ? "Defense trial procedural risks: YGR Individual Standing Order, N.D. Cal. Local Rules, Kamakana sealing for trial exhibits."
                                : "These are procedural risks that could result in stricken filings, waived defenses, or sanctions for the defense. Judge Aenlle-Rocha has demonstrated zero tolerance for Local Rules violations (Dkt.85)."
                          : isJuul
                            ? "Plaintiff procedural risks before remand: Jan 6 supplemental discovery letter, causation expert retention, sealing audit."
                            : isIle
                              ? "Government procedural risks: 3500/Jencks timing commitments, Brady obligations on the committee forensic report, and preserving the no-position posture on conditions."
                              : caseId === "musk"
                                ? "Plaintiff trial procedural risks: YGR standing order compliance, N.D. Cal. sealing standards, proposed findings of fact deadlines."
                                : "These are procedural vulnerabilities in plaintiff's current pleading and litigation posture that defendant is actively exploiting in pending motions."}
                      </p>
                      {(trapPersp === "Defendant"
                        ? isJuul
                          ? [
                              {
                                trap: "Sonner-Pak Circular Logic -- Do Not Concede Both Prongs",
                                rule: "Sonner v. Premier Nutrition Corp., 971 F.3d 834 (9th Cir. 2020); FRCP 56",
                                risk: "High",
                                fix: "JLI must carefully sequence the Pak and Sonner arguments. Do not concede Pak destroys all legal damages AND simultaneously invoke Sonner -- Orrick identified the circularity at Oct 31 hearing.",
                                why: "Orrick Oct 31 2025 hearing: if JLI wins on Pak (no past legal damages), then by definition there is no adequate legal remedy for SFUSD -- so Sonner should NOT bar equitable abatement. Defense must show adequate legal remedy EXISTS (future damages) even if past damages are unquantified. Brief must address this tension directly or lose on the more important Sonner prong.",
                              },
                              {
                                trap: "Meta Account Data -- May 8 2026 deadline",
                                rule: "Court Order Apr 14 2026; Stip. Dkt.4497",
                                risk: "High",
                                fix: "Comply by May 8 2026. Coordinate with Meta counsel on production format.",
                                why: "Per Dkt.4497 (Apr 24 2026): Meta deadline extended to May 8 2026. Missing deadline risks contempt, adverse inference at remand trial, and loss of the third-party corroboration that plaintiffs most need for youth-targeting proof.",
                              },
                              {
                                trap: "Email/Social Media disputes -- May 11 2026",
                                rule: "Feb 26 2026 Order (Dkt.4488)",
                                risk: "High",
                                fix: "Resolve or file non-compliance notice + joint letter by May 11 2026.",
                                why: "45 days from Mar 27 2026. Failure triggers non-compliance notice and joint discovery letter. JLI must resolve all disputes or risk Orrick drawing adverse inference on supplemental discovery scope.",
                              },
                              {
                                trap: "NMD Personal Jurisdiction Gap at Remand",
                                rule: "RICO 18 U.S.C. sec 1965; FRCP 4(k)(1)(C); State long-arm statutes",
                                risk: "High",
                                fix: "Confirm transferee courts have personal jurisdiction over each NMD (Pritzker, Valani, Huh) before remand briefing. RICO nationwide service of process applies in federal court -- does not apply if cases remanded to state court.",
                                why: "Orrick raised NMD personal jurisdiction at Oct 31 2025 hearing. Federal RICO provides nationwide service of process (sec 1965) in federal court -- this is why MDL can sue Pritzker (CA) in N.D. Cal. If cases are remanded to state courts, state long-arm statutes apply and NMDs may challenge jurisdiction in each transferee state. This could fragment the RICO enterprise claim.",
                              },
                              {
                                trap: "Discovery -- joint 5-page letter brief required",
                                rule: "Judge Orrick Standing Order",
                                risk: "High",
                                fix: "Joint letter brief only. No standalone discovery motions.",
                                why: "Orrick Standing Order: joint 5-page letter for all discovery disputes. Per Dec 16 2025 CMC: supplemental discovery scoped by Jan 6 2026 letter. Standalone motions are stricken.",
                              },
                              {
                                trap: "Antitrust Rule 23(f) Petition -- Could Delay Sept 28 2026 Trial",
                                rule: "FRCP 23(f); Fed. R. App. P. 5",
                                risk: "Medium",
                                fix: "Monitor Ninth Circuit Rule 23(f) petition by Altria. If granted, antitrust trial schedule is in flux.",
                                why: "Altria filed a Rule 23(f) petition at the Ninth Circuit to challenge the Feb 26 2026 class certification order (Dkt.635, case 3:20-cv-02345). Rule 23(f) petitions are rarely granted but if the Ninth Circuit grants review, antitrust trial date Sept 28 2026 could be postponed. This would relieve the two-front war pressure on JLI and Altria -- a significant strategic development to monitor.",
                              },
                              {
                                trap: "Sealing -- Kamakana standard",
                                rule: "Judge Orrick Sealing Order (Sep 2022)",
                                risk: "High",
                                fix: "Narrowly tailored. Compelling reasons for dispositive filings. Certify compliance.",
                                why: "Orrick OSC on fee committee sealing (Dkt.4167 Dec 2023) shows he monitors all sealing personally. Overbroad sealing of SJ briefs, remand papers, or antitrust coordination documents triggers OSC and public disclosure of confidential materials.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  trap: "Grant of Dkt.60 Is NOT Travel Authorization",
                                  rule: "Dkt.60 at 3 - follow-up itinerary letter required; PTS approval per meeting",
                                  risk: "High",
                                  fix: "On grant: same-day Heller letter with the specific interviews; PTS sign-off per meeting; only then book. Any Patent VC session must match the represented scope exactly - remote, sales/marketing, contract, no fundraising, no investor contact.",
                                  why: "A premature calendar invite is a conditions-violation risk during the exact window the Court is being asked to trust.",
                                },
                                {
                                  trap: "Short-Notice Letter Practice",
                                  rule: "Garaufis individual practices; Dkt.59 denial",
                                  risk: "High",
                                  fix: "Every application: letter + proposed order + real lead time. Mirror the 8/11 leave-first structure.",
                                  why: "The 7/22 application died on timing alone; posture repair took three weeks.",
                                },
                                {
                                  trap: "STA Exclusion Without Consideration",
                                  rule: "18 U.S.C. §3161(h)(7)",
                                  risk: "Medium",
                                  fix: "Consent only in exchange for firm Feb 2027 dates and locked 3500 deadlines at the 9/14 conference.",
                                  why: "Open-ended exclusions surrender the calendar leverage the court's four-week offer created.",
                                },
                                {
                                  trap: "Sealed-Bond Mechanics",
                                  rule: "Dkt.42/43; 6/22 limited-purpose order",
                                  risk: "Low",
                                  fix: "Assume raised-seal certified copies and clerk lead time for anything recorded in county land records.",
                                  why: "County clerks rejected photocopies of the confessions of judgment; eleven calendar days burned.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    trap: "YGR Standing Order -- 3-Page Pre-Hearing Letter Brief (7 Days Out)",
                                    rule: "YGR Individual Standing Order (2025); N.D. Cal. Civil L.R. 7-1",
                                    risk: "High",
                                    fix: "Submit 3-page pre-hearing letter brief at least 7 days before any scheduled hearing. Both parties must file; no separate reply unless YGR orders one. Failure = non-hearing.",
                                    why: "YGR Standing Order requires pre-hearing letter brief for all civil motion hearings. Oakland Courtroom 1. Letter brief must identify the relief sought, core argument, and key authorities. Failure to file will result in YGR taking the motion off-calendar or ruling on the papers without argument.",
                                  },
                                  {
                                    trap: "Kamakana Compelling Reasons Standard -- Trial Exhibits",
                                    rule: "Kamakana v. City of Honolulu, 447 F.3d 1172 (9th Cir. 2006); N.D. Cal. Civil L.R. 79-5(c)",
                                    risk: "High",
                                    fix: "For any trial exhibit or post-trial filing (proposed FOF, brief), apply compelling reasons standard -- not good cause. Narrowly tailored only. Submit declaration establishing specific harm from disclosure.",
                                    why: "YGR applies the higher Kamakana compelling reasons standard to all materials filed in connection with trial and dispositive motions. The good cause standard applies only to non-dispositive filings. Overbroad sealing of PX003, PX163, PX230, DX-516 etc. will draw an OSC and may result in public filing of entire submission.",
                                  },
                                  {
                                    trap: "L.R. 37-1 Meet-and-Confer Before Discovery Letter to Magistrate",
                                    rule: "N.D. Cal. Civil L.R. 37-1; N.D. Cal. Standing Order for All Judges",
                                    risk: "High",
                                    fix: "Before filing any discovery dispute letter to the Magistrate Judge, meet-and-confer in person or by telephone (not email) within 7 days of discovering the dispute. File joint letter within 7 days of failed conference.",
                                    why: "L.R. 37-1 requires good-faith in-person or telephone conference before filing discovery motions. Email exchanges do not satisfy L.R. 37-1. Discovery disputes go to the assigned Magistrate -- NOT to YGR. Filing discovery motion directly with YGR will be stricken.",
                                  },
                                  {
                                    trap: "L.R. 16-9 Pretrial Statement -- 21 Days Before Trial",
                                    rule: "N.D. Cal. Civil L.R. 16-9; YGR Standing Order",
                                    risk: "High",
                                    fix: "Joint pretrial conference statement due 21 days before trial commencement. Must include: witness list, exhibit list, deposition designations, proposed jury instructions, proposed verdict form, stipulated facts, and disputed issues of law.",
                                    why: "L.R. 16-9 requires comprehensive joint pretrial statement 21 days before trial. Missing deadlines or submitting incomplete statements can result in exclusion of witnesses, exhibits, or legal theories not disclosed in the pretrial statement. In Musk v. Altman, Phase 1 trial commenced April 28 2026 -- late additions to witness or exhibit lists will face exclusion motions.",
                                  },
                                  {
                                    trap: "ECF Chambers Copy Requirement -- Filings Over 10 Pages",
                                    rule: "N.D. Cal. Civil L.R. 5-1; YGR Individual Standing Order",
                                    risk: "Medium",
                                    fix: "Deliver paper chambers copy to Clerk, Room 400, 1301 Clay St Oakland, within 24 hours of any ECF filing over 10 pages. Label with case number, docket entry number, and date filed.",
                                    why: "YGR requires paper chambers copy for filings over 10 pages. Failure to deliver chambers copy within 24 hours of ECF filing can result in the court not having the document for a scheduled hearing. For trial briefs and proposed findings of fact this is especially critical.",
                                  },
                                  {
                                    trap: "Proposed Order Submission -- Word Format to YGR Orders Email",
                                    rule: "N.D. Cal. Standing Order for All Judges; YGR Standing Order",
                                    risk: "Medium",
                                    fix: "Submit proposed orders in Word (.docx) format to YGR_Orders@cand.uscourts.gov within 24 hours of any ruling that requires a proposed order. Do not file proposed orders via ECF unless directed.",
                                    why: "YGR requires Word format proposed orders by email, not ECF filing. Filing proposed order only via ECF and not emailing Word version to YGR_Orders inbox is a common error that delays entry of orders. Particularly relevant for any post-trial injunction, constructive trust, or findings of fact order.",
                                  },
                                ]
                              : [
                                  {
                                    trap: "Meet-and-confer certification",
                                    rule: "L.R. 7-3",
                                    risk: "High",
                                    fix: "Add certification to all motion-related filings.",
                                    why: "Judge Aenlle-Rocha STRICKEN two stipulations (Dkts.80-81) for Local Rules violations.",
                                  },
                                  {
                                    trap: "AIDP answer to 2d Amended CC",
                                    rule: "FRCP 12(a)",
                                    risk: "Medium",
                                    fix: "Track AIDP response deadline: 4/10/26.",
                                    why: "AIDP served with Second Amended Counterclaims on 3/20/26.",
                                  },
                                  {
                                    trap: "Claim construction sur-reply risk",
                                    rule: "Patent Standing Order 3(e)",
                                    risk: "Medium",
                                    fix: "Monitor for unauthorized supplemental filings.",
                                    why: "Both sides filed declarations after reply deadline - judge may strike.",
                                  },
                                ]
                        : isJuul
                          ? [
                              {
                                trap: "Email/Social Media disputes - May 11, 2026",
                                rule: "Feb 26, 2026 Order (Dkt.4488)",
                                risk: "High",
                                fix: "Resolve all disputes or file non-compliance notice + joint letter.",
                                why: "45-day deadline from Mar 27, 2026. Failure risks sanctions and adverse ruling in remand courts.",
                              },
                              {
                                trap: "Medical causation expert - Seliavski - retain NOW",
                                rule: "FRCP 26(a)(2)",
                                risk: "High",
                                fix: "Retain and disclose causation expert before remand - cannot wait until after transfer.",
                                why: "No medical causation expert for Seliavski PI. Once remanded, expert deadlines trigger immediately. Failure risks SJ on PI damages.",
                              },
                              {
                                trap: "Meta Account Data coordination - May 8, 2026",
                                rule: "Court Order Apr 14, 2026; Dkt.4497",
                                risk: "High",
                                fix: "Monitor Meta production. Review for evidence of youth-targeted advertising.",
                                why: "Court ordered production of decedent Meta data. May contain JLI social media targeting evidence - critical for failure to warn and RICO theories.",
                              },
                              {
                                trap: "Common benefit fee records - audit now",
                                rule: "MDL Administrative Order",
                                risk: "Medium",
                                fix: "Begin time record audit for late 2026 fee motion.",
                                why: "Co-Lead Counsel anticipate common benefit fee motions late 2026 or early 2027. Complete time records required.",
                              },
                            ]
                          : isIle
                            ? [
                                {
                                  trap: "Parity Theming After the Most-Culpable Record",
                                  rule: "Credibility risk; Apr 22 Hr'g Tr. 8:16-22",
                                  risk: "High",
                                  fix: "Differentiate deliberately in the opening; tie Naqvi to conduct-specific proof (¶40, ¶62(o)-(p)) rather than enterprise-wide rhetoric.",
                                  why: "The defense will read the transcript to the jury the first time the government blurs the defendants together.",
                                },
                                {
                                  trap: "Fragmenting the Play-Ball Chain",
                                  rule: "FRE 106 rule of completeness",
                                  risk: "Medium",
                                  fix: "If the fragment is used, pre-admit the full chain and argue meaning, not excerpt.",
                                  why: "The full chain is already public at Dkt.17 Ex.1 - partial use invites an instructed correction.",
                                },
                                {
                                  trap: "Bruton Exposure Through CC-3",
                                  rule: "Bruton; Richardson redactions",
                                  risk: "High",
                                  fix: "Redact the no-liabilities retelling or accept severance risk; script the witness precisely.",
                                  why: "One unredacted sentence can mistry a four-week joint trial.",
                                },
                                {
                                  trap: "STA Findings Discipline",
                                  rule: "18 U.S.C. §3161(h)(7) on-the-record findings",
                                  risk: "Low",
                                  fix: "Tie each exclusion to specific complexity findings.",
                                  why: "Sloppy exclusion findings are appellate gifts after conviction.",
                                },
                              ]
                            : caseId === "musk"
                              ? [
                                  {
                                    trap: "FRE 408(b) Admissibility - Dkt.522 Musk Threat to Brockman (URGENT)",
                                    rule: "Fed. R. Evid. 408(a)(2) and 408(b); Rhoades v. Avon Prod. 9th Cir. 2007",
                                    risk: "High",
                                    fix: "Oppose Dkt.522 immediately. Argue (1) statement arose from settlement negotiation -- 408(a)(2) bars it; (2) any motive inference is speculative and unfairly prejudicial under FRE 403.",
                                    why: "OpenAI filed Dkt.522 May 3 2026 to elicit through Brockman that Musk messaged him Apr 25 2026: By the end of this week, you and Sam will be the most hated men in America. If you insist, so it will be. OpenAI cites Rhoades (9th Cir.) -- 408(b) permits admission for bias/motive not liability. Exhibit A to Dkt.522 is the Pampena v. Musk trial transcript establishing a pattern of Musk threats in litigation (World War III until the end of time for real through Ringler). If admitted: jurors hear Musk made two nearly identical threats in two separate cases. This is potentially the most damaging evidence of the trial on the intent element.",
                                  },
                                  {
                                    trap: "L.R. 16-9 Pretrial Statement Omission -- Waiver of Witnesses and Exhibits",
                                    rule: "N.D. Cal. Civil L.R. 16-9; FRCP 26(a)(3); YGR Standing Order",
                                    risk: "High",
                                    fix: "Audit joint pretrial statement (due 21 days before trial) to ensure every intended witness and every exhibit is disclosed. Any witness or exhibit not on the pretrial statement faces exclusion motion.",
                                    why: "L.R. 16-9 pretrial statement is the controlling disclosure document. YGR treats undisclosed witnesses and exhibits as waived absent good cause. In Phase 1 trial, if plaintiff needs to add a rebuttal witness mid-trial or introduce an exhibit not on Dkt.428 amended joint exhibit list, must file motion with specific good cause showing.",
                                  },
                                  {
                                    trap: "FRCP 52(a) Proposed FOF -- No Record Citations",
                                    rule: "FRCP 52(a); YGR Individual Standing Order",
                                    risk: "High",
                                    fix: "Every proposed finding of fact must cite specific trial transcript page and line, exhibit number, or deposition designation. Uncited assertions are disregarded. Prepare real-time citation index during trial.",
                                    why: "YGR's post-trial proposed findings of fact procedure requires tight anchoring to the trial record. Generalized narrative unsupported by transcript citations will be struck. Plaintiff must maintain a running citation file during Phase 1 trial -- transcript pages for PX003 admission, Brockman examination on PX163, Kevin Scott deposition designations re PX230 -- to build complete post-trial FOF.",
                                  },
                                  {
                                    trap: "Section 17510.8 Issue Preservation -- No Jury Instruction or Special Finding Request",
                                    rule: "Cal. Bus. & Prof. Code section 17510.8; FRCP 51(c); FRCP 49(a)",
                                    risk: "High",
                                    fix: "If YGR does not rule on Dkt.515/516 before verdict, request a special interrogatory or jury instruction on section 17510.8 elements before the case is submitted. File written request per FRCP 51(c) at close of evidence.",
                                    why: "The section 17510.8 strict liability theory is pending on competing briefs (Dkt.515/516). If the court does not rule and plaintiff fails to request a specific jury instruction or FRCP 49(a) special finding on each statutory element, the theory may be forfeited on appeal under FRCP 51(d). Written instruction request must be made on the record at close of evidence.",
                                  },
                                  {
                                    trap: "Kamakana Standard -- Post-Trial Proposed FOF Contains Sealed Exhibit References",
                                    rule: "Kamakana v. City of Honolulu, 447 F.3d 1172; N.D. Cal. Civil L.R. 79-5(c)",
                                    risk: "High",
                                    fix: "File a motion to seal or redact any proposed FOF that references exhibits under seal (PX163 Brockman journal, financial records) simultaneously with the FOF. Submit sealed version and public version.",
                                    why: "Post-trial proposed findings of fact are dispositive filings subject to the Kamakana compelling reasons standard. Any FOF that quotes or relies on sealed exhibits must be filed under seal with a contemporaneous motion establishing compelling reasons. Filing unsealed FOF that references sealed exhibits will result in full public disclosure of the sealed content.",
                                  },
                                  {
                                    trap: "SOL -- Thin Fraudulent Concealment Record Insufficient for Tolling",
                                    rule: "Cal. Code Civ. Proc. section 338(d); Clemens v. DaimlerChrysler Corp.",
                                    risk: "Medium",
                                    fix: "Ensure trial record contains affirmative acts of concealment -- not just silence -- by OpenAI after the for-profit LP was formed. Need board communications directing non-disclosure to prior donors and public statements inconsistent with internal strategy.",
                                    why: "Fraudulent concealment tolling under California law requires more than non-disclosure. Plaintiff must show OpenAI affirmatively misled Musk or took active steps to prevent discovery of the for-profit conversion. Sept 2020 tweet (MDX-1537: captured by Microsoft) may establish constructive knowledge as of that date under Pretrial Order No. 3 framework, leaving only the period 2019-2020 for tolling.",
                                  },
                                  {
                                    trap: "L.R. 7-3 Meet-and-Confer Certification Missing from Post-Trial Motions",
                                    rule: "N.D. Cal. Civil L.R. 7-3(a)",
                                    risk: "Medium",
                                    fix: "Any post-trial motion (JNOV, new trial, motion for injunction or constructive trust) must include a L.R. 7-3 certification that counsel met and conferred with opposing counsel at least 7 days before filing. Email does not satisfy the requirement.",
                                    why: "L.R. 7-3(a) requires pre-filing meet-and-confer for all motions except TROs and emergency relief. Post-trial motions for constructive trust on Microsoft equity, injunctive relief regarding OpenAI conversion, or Rule 50(b) JNOV must all include L.R. 7-3 certification. Missing certification results in YGR striking the motion without prejudice to refile.",
                                  },
                                ]
                              : [
                                  {
                                    trap: "FAC para 8 vs para 13 - irreconcilable ownership",
                                    rule: "Twombly/Iqbal",
                                    risk: "High",
                                    fix: "Amend to clarify exclusive license vs assignment.",
                                    why: "Defendant MTDs both lead with this contradiction.",
                                  },
                                  {
                                    trap: "No compositional testing for Plant-Based Product",
                                    rule: "FRCP 12(b)(6)",
                                    risk: "High",
                                    fix: "Commission lab analysis immediately.",
                                    why: "Defendant argues allegation rests entirely on an Amazon slogan.",
                                  },
                                  {
                                    trap: "Liu testimony: Neurocentria not a competitor of Kappa",
                                    rule: "Lexmark, 572 U.S. 118",
                                    risk: "High",
                                    fix: "Replead commercial injury or drop Neurocentria from Lanham Act counts.",
                                    why: "Creates serious Lexmark standing problem for Counts VI-VIII.",
                                  },
                                ]
                      ).map((t, i) => (
                        <div
                          key={i}
                          style={{
                            background: th.card,
                            borderRadius: 8,
                            border: `1px solid ${th.border}`,
                            borderLeft: `3px solid ${t.risk === "High" ? "#ef4444" : "#f59e0b"}`,
                            padding: "12px 16px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 4,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 600 }}>
                              {t.trap}
                            </div>
                            <span
                              style={{
                                fontSize: 6,
                                fontFamily: "monospace",
                                color:
                                  t.risk === "High" ? "#ef4444" : "#f59e0b",
                                background:
                                  t.risk === "High" ? "#ef444418" : "#f59e0b18",
                                padding: "2px 5px",
                                borderRadius: 3,
                              }}
                            >
                              {t.risk}
                            </span>
                          </div>
                          <div style={{ fontSize: 8, color: th.dim }}>
                            Rule: {t.rule}
                          </div>
                          <p
                            style={{
                              fontSize: 8,
                              color: th.muted,
                              lineHeight: 1.5,
                              margin: "4px 0 4px",
                            }}
                          >
                            {t.why}
                          </p>
                          <div
                            style={{
                              fontSize: 8,
                              color: "#10b981",
                              marginBottom: 6,
                            }}
                          >
                            Fix: {t.fix}
                          </div>
                          <button
                            onClick={() =>
                              openP(
                                t.trap,
                                `**Rule:** ${t.rule}\n**Risk Level:** ${t.risk}\n\n**Why it's a trap:**\n${t.why}\n\n**How to avoid:**\n${t.fix}\n\n**Action items:**\n1. Review current filings for compliance\n2. Calendar all related deadlines\n3. Flag for lead counsel review`,
                                t.trap,
                              )
                            }
                            style={{
                              fontSize: 7,
                              background: "#a78bfa18",
                              color: "#a78bfa",
                              border: "1px solid #a78bfa33",
                              borderRadius: 3,
                              padding: "2px 8px",
                              cursor: "pointer",
                            }}
                          >
                            🔍 Expand
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* LIVE DEPO */}
            {tab === "warroom" &&
              (() => {
                const data = WAR_ROOM_DATA[caseId] || WAR_ROOM_DATA.musk;
                const SC = {
                  green: "#10b981",
                  amber: "#f59e0b",
                  yellow: "#eab308",
                  red: "#ef4444",
                };
                const sec = (title, emoji, children) => (
                  <div
                    style={{
                      background: th.card,
                      borderRadius: 10,
                      border: `1px solid ${th.border}`,
                      padding: "14px 16px",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: th.text,
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        letterSpacing: "-0.1px",
                      }}
                    >
                      {emoji} {title}
                    </div>
                    {children}
                  </div>
                );
                const _warBanner = (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto 0",
                      padding: "0 16px",
                    }}
                  >
                    <div
                      style={{
                        background:
                          warPersp === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : warPersp === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${warPersp === "plaintiff" ? "#dc262655" : warPersp === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: warPersp === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: warPersp === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {warPersp === "plaintiff"
                            ? "🔴 Plaintiff Counsel - your strategic dashboard"
                            : warPersp === "defendant"
                              ? "🔵 Defendant Counsel - your strategic dashboard"
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              warPersp === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {warPersp === "plaintiff"
                            ? "Trial themes · witness readiness · evidence map · daily moves from plaintiff vantage"
                            : warPersp === "defendant"
                              ? "Trial themes · witness readiness · evidence map · daily moves from defendant vantage"
                              : "Toggle to see the dashboard from one side's vantage"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setWarPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: warPersp === v ? 700 : 500,
                              background:
                                warPersp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                warPersp === "all"
                                  ? warPersp === v
                                    ? th.text
                                    : th.muted
                                  : warPersp === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                warPersp === v
                                  ? warPersp === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${warPersp === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
                if (caseId === "musk" || isIle) {
                  const d = data;
                  const addNote = (text, witness) => {
                    if (!text.trim()) return;
                    const note = {
                      time: new Date().toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      }),
                      author: "You",
                      witness: witness || d.currentWitness.name,
                      text: text.trim(),
                    };
                    setWarNotes((p) => ({
                      ...p,
                      [caseId]: [note, ...(p[caseId] || d.liveNotes)],
                    }));
                    setWarNoteInput("");
                    toast("Note logged", "success");
                  };
                  const allNotes = warNotes[caseId] || d.liveNotes;
                  return (
                    <div
                      style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        padding: "16px 16px 60px",
                      }}
                    >
                      {_warBanner}
                      {/* HEADER - TRIAL DAY LIVE */}
                      <div
                        style={{
                          background: "linear-gradient(135deg,#7f1d1d,#991b1b)",
                          borderRadius: 12,
                          padding: "16px 20px",
                          marginBottom: 14,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: "#fef2f2",
                              animation: "pulse 1.2s infinite",
                            }}
                          />
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: "#fef2f2",
                              letterSpacing: 1.5,
                              fontFamily: "monospace",
                            }}
                          >
                            LIVE · IN COURT
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 7,
                            color: "#fecaca",
                            fontFamily: "monospace",
                            letterSpacing: 1.5,
                            marginBottom: 6,
                            fontWeight: 700,
                          }}
                        >
                          WAR ROOM · TRIAL DAY VIEW
                        </div>
                        <h1
                          style={{
                            fontSize: 22,
                            fontWeight: 800,
                            color: "#fff",
                            margin: "0 0 5px",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          Day {d.trialDay} of Trial - {d.trialDate}
                        </h1>
                        <div style={{ fontSize: 10, color: "#fecaca" }}>
                          {d.courtroom} · {d.judge}
                        </div>
                      </div>

                      {/* LIVE ISSUES BANNER */}
                      {d.liveIssues.map((li, i) => (
                        <div
                          key={i}
                          style={{
                            background: "#f59e0b15",
                            border: "1px solid #f59e0b66",
                            borderLeft: "4px solid #f59e0b",
                            borderRadius: 8,
                            padding: "10px 14px",
                            marginBottom: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <span style={{ fontSize: 18 }}>⚠</span>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#f59e0b",
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                              }}
                            >
                              {li.flag} · {li.priority}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: th.text,
                                fontWeight: 600,
                                margin: "3px 0 2px",
                              }}
                            >
                              {li.ref}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.5,
                              }}
                            >
                              {li.desc}
                            </div>
                          </div>
                          <button
                            style={{
                              padding: "7px 14px",
                              fontSize: 9,
                              fontWeight: 700,
                              background: "#f59e0b",
                              color: "#1a1a1a",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            📄 Pocket Brief →
                          </button>
                        </div>
                      ))}

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 340px",
                          gap: 14,
                        }}
                      >
                        {/* LEFT COLUMN */}
                        <div>
                          {/* CURRENT WITNESS */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `2px solid #ef4444`,
                              padding: "16px 18px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 10,
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: "#ef4444",
                                    fontWeight: 700,
                                    letterSpacing: 1.5,
                                    marginBottom: 3,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  ● ON THE STAND
                                </div>
                                <h2
                                  style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    margin: "0 0 2px",
                                    letterSpacing: "-0.3px",
                                  }}
                                >
                                  {d.currentWitness.name}
                                </h2>
                                <div style={{ fontSize: 9, color: th.muted }}>
                                  {d.currentWitness.role} ·{" "}
                                  {d.currentWitness.status}
                                </div>
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  padding: "3px 8px",
                                  background: "#ef444418",
                                  color: "#ef4444",
                                  border: "1px solid #ef444444",
                                  borderRadius: 10,
                                  fontWeight: 700,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                CROSS · DAY 2 → 3
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                padding: "8px 10px",
                                background: dark ? "#0f172a" : "#f8fafc",
                                borderRadius: 6,
                                marginBottom: 10,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              <span
                                style={{
                                  color: th.text,
                                  fontWeight: 700,
                                  fontStyle: "normal",
                                }}
                              >
                                Direct established:{" "}
                              </span>
                              {d.currentWitness.direct}
                            </div>

                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: th.dim,
                                letterSpacing: 1,
                                marginBottom: 7,
                                fontFamily: "monospace",
                              }}
                            >
                              ✗ ALREADY HAMMERED (Day 2)
                            </div>
                            {d.currentWitness.crossHammered.map((c, i) => (
                              <div
                                key={i}
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  padding: "5px 10px",
                                  background: th.bg,
                                  borderLeft: "2px solid #6b7280",
                                  borderRadius: "0 4px 4px 0",
                                  marginBottom: 4,
                                }}
                              >
                                <span
                                  style={{ color: th.text, fontWeight: 600 }}
                                >
                                  {c.topic}:
                                </span>{" "}
                                {c.effect}
                              </div>
                            ))}

                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: "#ef4444",
                                letterSpacing: 1,
                                marginTop: 12,
                                marginBottom: 7,
                                fontFamily: "monospace",
                              }}
                            >
                              ► REMAINING CROSS TOPICS
                            </div>
                            {d.currentWitness.crossRemaining.map((c, i) => {
                              const pc =
                                c.priority === "CRITICAL"
                                  ? "#ef4444"
                                  : "#f59e0b";
                              return (
                                <div
                                  key={i}
                                  style={{
                                    background: dark ? "#0f172a" : "#fef2f2",
                                    border: `1px solid ${pc}55`,
                                    borderRadius: 7,
                                    padding: "10px 12px",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 8,
                                      marginBottom: 5,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: th.text,
                                        flex: 1,
                                      }}
                                    >
                                      {c.topic}
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 7,
                                        padding: "2px 7px",
                                        background: pc + "22",
                                        color: pc,
                                        border: `1px solid ${pc}55`,
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.4,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {c.priority}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                      fontSize: 9,
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          color: "#f59e0b",
                                          fontWeight: 700,
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        EXHIBIT
                                      </span>
                                      <div
                                        style={{
                                          color: th.text,
                                          fontWeight: 600,
                                          marginTop: 2,
                                        }}
                                      >
                                        {c.exhibit}
                                      </div>
                                      <div
                                        style={{
                                          color: th.muted,
                                          fontSize: 8,
                                          marginTop: 1,
                                        }}
                                      >
                                        {c.exDesc}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          color: "#a78bfa",
                                          fontWeight: 700,
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        OBJECTION READY
                                      </span>
                                      <div
                                        style={{
                                          color: th.text,
                                          marginTop: 2,
                                          fontSize: 9,
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {c.objection}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* NEXT WITNESS */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${th.border}`,
                              padding: "16px 18px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 8,
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: "#3b82f6",
                                    fontWeight: 700,
                                    letterSpacing: 1.5,
                                    marginBottom: 3,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  ► UP NEXT
                                </div>
                                <h3
                                  style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    margin: "0 0 2px",
                                  }}
                                >
                                  {d.nextWitness.name}
                                </h3>
                                <div style={{ fontSize: 9, color: th.muted }}>
                                  {d.nextWitness.who}
                                </div>
                              </div>
                              <button
                                style={{
                                  padding: "6px 12px",
                                  fontSize: 9,
                                  fontWeight: 700,
                                  background: "#3b82f6",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                📋 Direct Outline →
                              </button>
                            </div>
                            {d.nextWitness.keyDocs.map((kd, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: "7px 10px",
                                  background: "#3b82f615",
                                  borderLeft: "3px solid #3b82f6",
                                  borderRadius: "0 5px 5px 0",
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: "#3b82f6",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    marginBottom: 2,
                                  }}
                                >
                                  KEY DOCUMENT · {kd.ex}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    fontWeight: 600,
                                    marginBottom: 2,
                                  }}
                                >
                                  {kd.desc}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontStyle: "italic",
                                  }}
                                >
                                  {kd.strength}
                                </div>
                              </div>
                            ))}
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.55,
                                padding: "8px 10px",
                                background: "#a78bfa10",
                                borderLeft: "2px solid #a78bfa",
                                borderRadius: "0 4px 4px 0",
                                marginBottom: 10,
                              }}
                            >
                              <span
                                style={{ color: "#a78bfa", fontWeight: 700 }}
                              >
                                Defense strategy:{" "}
                              </span>
                              {d.nextWitness.defenseStrategy}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: th.dim,
                                letterSpacing: 0.8,
                                marginBottom: 5,
                                fontFamily: "monospace",
                              }}
                            >
                              DIRECT EXAM OUTLINE (auto-loaded)
                            </div>
                            {d.nextWitness.directOutline.map((o, i) => (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: "4px 0",
                                  fontSize: 9,
                                  color: th.muted,
                                }}
                              >
                                <span
                                  style={{
                                    color: "#3b82f6",
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    flexShrink: 0,
                                  }}
                                >
                                  {i + 1}.
                                </span>
                                {o}
                              </div>
                            ))}
                          </div>

                          {/* UPCOMING QUEUE */}
                          {sec(
                            "Upcoming Witness Queue",
                            "🗓️",
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 5,
                              }}
                            >
                              {d.upcomingQueue.map((w, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    background: th.bg,
                                    borderRadius: 6,
                                    fontSize: 10,
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: th.text,
                                      }}
                                    >
                                      {w.name}
                                    </span>
                                    <span
                                      style={{
                                        color: th.faint,
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        marginLeft: 8,
                                      }}
                                    >
                                      {w.when}
                                    </span>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        marginTop: 2,
                                      }}
                                    >
                                      {w.note}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>,
                          )}

                          {/* EXHIBITS */}
                          {sec(
                            `Exhibit Tracker - ${d.exhibits.admitted} admitted`,
                            "📎",
                            <div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 700,
                                  color: th.dim,
                                  letterSpacing: 0.8,
                                  marginBottom: 6,
                                  fontFamily: "monospace",
                                }}
                              >
                                PIVOTAL EXHIBITS
                              </div>
                              {d.exhibits.pivotal.map((ex, i) => {
                                const isQ = ex.status === "queued";
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      padding: "7px 10px",
                                      background: isQ ? "#f59e0b10" : th.bg,
                                      border: isQ
                                        ? "1px dashed #f59e0b66"
                                        : "none",
                                      borderRadius: 6,
                                      marginBottom: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 9,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        color: isQ ? "#f59e0b" : "#10b981",
                                        minWidth: 54,
                                      }}
                                    >
                                      {ex.id}
                                    </span>
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: 9,
                                        color: th.text,
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {ex.desc}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: isQ ? "#f59e0b" : "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {isQ ? "⏳ " + ex.when : "✓ " + ex.when}
                                    </span>
                                  </div>
                                );
                              })}
                              {d.exhibits.pivotal.find((e) => e.objection) && (
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: "#f59e0b",
                                    padding: "7px 10px",
                                    background: "#f59e0b10",
                                    borderLeft: "2px solid #f59e0b",
                                    borderRadius: "0 4px 4px 0",
                                    marginTop: 6,
                                    lineHeight: 1.5,
                                    fontStyle: "italic",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      fontStyle: "normal",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    ⚠ ANTICIPATED OBJECTION (PX-200):{" "}
                                  </span>
                                  {
                                    d.exhibits.pivotal.find((e) => e.objection)
                                      .objection
                                  }
                                </div>
                              )}
                            </div>,
                          )}
                        </div>

                        {/* RIGHT COLUMN - LIVE NOTES (sticky) */}
                        <div>
                          <div
                            style={{
                              position: "sticky",
                              top: "70px",
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${th.border}`,
                              padding: "14px 16px",
                              maxHeight: "calc(100vh - 90px)",
                              overflow: "auto",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: th.text,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                📝 Live Notes{" "}
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 6px",
                                    background: "#10b98118",
                                    color: "#10b981",
                                    borderRadius: 6,
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                  }}
                                >
                                  ● LIVE
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                }}
                              >
                                {allNotes.length} entries
                              </span>
                            </div>

                            {/* Note input */}
                            <div
                              style={{
                                marginBottom: 12,
                                padding: "8px 10px",
                                background: th.bg,
                                borderRadius: 6,
                                border: `1px solid ${th.border}`,
                              }}
                            >
                              <textarea
                                value={warNoteInput}
                                onChange={(e) =>
                                  setWarNoteInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" &&
                                    (e.metaKey || e.ctrlKey)
                                  )
                                    addNote(warNoteInput);
                                }}
                                placeholder="Log a courtroom observation... (⌘+↵)"
                                rows={2}
                                style={{
                                  width: "100%",
                                  padding: 6,
                                  fontSize: 9,
                                  background: "transparent",
                                  border: "none",
                                  color: th.text,
                                  outline: "none",
                                  resize: "none",
                                  fontFamily: "inherit",
                                  lineHeight: 1.4,
                                  boxSizing: "border-box",
                                }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginTop: 4,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: th.faint,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  tagged: {d.currentWitness.name}
                                </div>
                                <button
                                  onClick={() => addNote(warNoteInput)}
                                  disabled={!warNoteInput.trim()}
                                  style={{
                                    fontSize: 8,
                                    padding: "3px 9px",
                                    fontWeight: 700,
                                    background: warNoteInput.trim()
                                      ? "#10b981"
                                      : th.border,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: warNoteInput.trim()
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                >
                                  + Add
                                </button>
                              </div>
                            </div>

                            {/* Notes list */}
                            {allNotes.map((n, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: "8px 10px",
                                  marginBottom: 6,
                                  background: th.bg,
                                  borderRadius: 6,
                                  borderLeft: "2px solid #3b82f6",
                                  animation:
                                    i === 0 && n.author === "You"
                                      ? "slideIn 0.4s ease"
                                      : "none",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    marginBottom: 3,
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: th.faint,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    {n.time}
                                  </span>
                                  <span style={{ color: "#3b82f6" }}>
                                    → {n.witness}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.text,
                                    lineHeight: 1.55,
                                    marginBottom: 3,
                                  }}
                                >
                                  {n.text}
                                </div>
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: th.faint,
                                    fontStyle: "italic",
                                  }}
                                >
 - {n.author}
                                </div>
                              </div>
                            ))}

                            <div
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                padding: "8px 4px",
                                fontStyle: "italic",
                                borderTop: `1px dashed ${th.border}`,
                                marginTop: 8,
                                lineHeight: 1.5,
                              }}
                            >
                              💡 Notes are timestamped and witness-tagged. They
                              feed the jury profile when the case shifts to
                              deliberation prep.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (caseId === "neuro") {
                  const d = data;
                  return (
                    <div
                      style={{
                        maxWidth: 1100,
                        margin: "0 auto",
                        padding: "16px 16px 60px",
                      }}
                    >
                      {_warBanner}
                      {/* HEADER - PRE-TRIAL COUNTDOWN */}
                      <div
                        style={{
                          background: "linear-gradient(135deg,#1e3a8a,#1e40af)",
                          borderRadius: 12,
                          padding: "16px 20px",
                          marginBottom: 14,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            padding: "3px 8px",
                            background: "#fef3c7",
                            borderRadius: 8,
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#78350f",
                            fontFamily: "monospace",
                            letterSpacing: 0.5,
                          }}
                        >
                          PRE-TRIAL
                        </div>
                        <div
                          style={{
                            fontSize: 7,
                            color: "#bfdbfe",
                            fontFamily: "monospace",
                            letterSpacing: 1.5,
                            marginBottom: 6,
                            fontWeight: 700,
                          }}
                        >
                          WAR ROOM · PREPARATION DASHBOARD
                        </div>
                        <h1
                          style={{
                            fontSize: 22,
                            fontWeight: 800,
                            color: "#fff",
                            margin: "0 0 5px",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          Trial in {d.countdown[d.countdown.length - 1].days}{" "}
                          days - {d.trialDate}
                        </h1>
                        <div style={{ fontSize: 10, color: "#bfdbfe" }}>
                          {d.judge} · {d.courtroom}
                        </div>
                      </div>

                      {/* COUNTDOWN STRIP */}
                      {sec(
                        "Countdown Checklist",
                        "⏳",
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 5,
                          }}
                        >
                          {d.countdown.map((c, i) => {
                            const pc =
                              c.priority === "critical"
                                ? "#ef4444"
                                : c.priority === "high"
                                  ? "#f59e0b"
                                  : "#3b82f6";
                            const stColor =
                              c.status === "unscheduled"
                                ? "#ef4444"
                                : c.status === "derived"
                                  ? "#a78bfa"
                                  : "#10b981";
                            return (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "9px 12px",
                                  background:
                                    c.priority === "critical"
                                      ? "#ef444410"
                                      : th.bg,
                                  border:
                                    c.priority === "critical"
                                      ? "1px solid #ef444444"
                                      : "none",
                                  borderRadius: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    color: pc,
                                    minWidth: 58,
                                    textAlign: "right",
                                  }}
                                >
                                  {c.days}d
                                </span>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: th.text,
                                    }}
                                  >
                                    {c.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {c.date}
                                  </div>
                                </div>
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 7px",
                                    background: stColor + "18",
                                    color: stColor,
                                    border: `1px solid ${stColor}44`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {c.status === "unscheduled"
                                    ? "⚡ UNSCHEDULED"
                                    : c.status === "derived"
                                      ? "DERIVED"
                                      : c.status === "on-track"
                                        ? "ON TRACK"
                                        : "PENDING"}
                                </span>
                              </div>
                            );
                          })}
                        </div>,
                      )}

                      {/* WITNESS READINESS */}
                      {sec(
                        `Witness Readiness - ${d.witnessReadiness.length} witnesses`,
                        "👥",
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                          }}
                        >
                          {d.witnessReadiness.map((w, i) => {
                            const c = SC[w.status];
                            const isCrit = w.status === "red";
                            return (
                              <div
                                key={i}
                                style={{
                                  padding: "10px 12px",
                                  background: isCrit ? "#ef444410" : th.bg,
                                  borderLeft: `3px solid ${c}`,
                                  borderRadius: "0 6px 6px 0",
                                  border: isCrit
                                    ? "1px solid #ef444444"
                                    : "none",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 6,
                                    marginBottom: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: th.text,
                                      flex: 1,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {w.name}
                                  </div>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: c,
                                      boxShadow: `0 0 0 2px ${c}33`,
                                      marginTop: 3,
                                      flexShrink: 0,
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                    marginBottom: 5,
                                  }}
                                >
                                  {w.role}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{ color: th.dim, fontWeight: 600 }}
                                    >
                                      Depo:{" "}
                                    </span>
                                    {w.depo}
                                  </div>
                                  <div>
                                    <span
                                      style={{ color: th.dim, fontWeight: 600 }}
                                    >
                                      Cross:{" "}
                                    </span>
                                    {w.cross}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: isCrit ? "#ef4444" : th.muted,
                                    lineHeight: 1.4,
                                    marginTop: 5,
                                    fontStyle: isCrit ? "normal" : "italic",
                                    fontWeight: isCrit ? 700 : 400,
                                  }}
                                >
                                  {w.notes}
                                </div>
                              </div>
                            );
                          })}
                        </div>,
                      )}

                      {/* EXHIBIT PIPELINE */}
                      {sec(
                        `Exhibit Pipeline - ${d.exhibitPipeline.length} exhibits`,
                        "📎",
                        <div>
                          {d.exhibitPipeline.map((ex, i) => {
                            const c = SC[ex.status];
                            return (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "8px 12px",
                                  background: th.bg,
                                  borderLeft: `3px solid ${c}`,
                                  borderRadius: "0 5px 5px 0",
                                  marginBottom: 4,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    color: c,
                                    minWidth: 78,
                                  }}
                                >
                                  {ex.id}
                                </span>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: th.text,
                                    }}
                                  >
                                    {ex.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                      marginTop: 1,
                                    }}
                                  >
                                    {ex.auth} · {ex.use}
                                  </div>
                                </div>
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 9,
                                    height: 9,
                                    borderRadius: "50%",
                                    background: c,
                                    boxShadow: `0 0 0 2px ${c}33`,
                                    flexShrink: 0,
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>,
                      )}

                      {/* MIL TRACKER */}
                      {sec(
                        `Motion in Limine Tracker - ${d.milTracker.filed} filed · deadline ${d.milTracker.deadlineDays}d away`,
                        "⚔️",
                        <div>
                          <div
                            style={{
                              padding: "10px 12px",
                              background: "#a78bfa15",
                              borderLeft: "3px solid #a78bfa",
                              borderRadius: "0 6px 6px 0",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#a78bfa",
                                letterSpacing: 0.5,
                                marginBottom: 3,
                                fontFamily: "monospace",
                              }}
                            >
                              AI-DERIVED DEADLINE
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: th.text,
                                marginBottom: 3,
                              }}
                            >
                              {d.milTracker.deadline}{" "}
                              <span
                                style={{ color: th.muted, fontWeight: 400 }}
                              >
                                ({d.milTracker.deadlineDays} days away)
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.5,
                              }}
                            >
                              {d.milTracker.note}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: th.dim,
                              letterSpacing: 0.8,
                              marginBottom: 6,
                              fontFamily: "monospace",
                            }}
                          >
                            LIKELY MIL SUBJECTS
                          </div>
                          {d.milTracker.likelySubjects.map((mil, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "8px 12px",
                                background: th.bg,
                                borderRadius: 6,
                                marginBottom: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 7,
                                  padding: "2px 7px",
                                  background:
                                    mil.by === "Plaintiff"
                                      ? "#ef444418"
                                      : "#3b82f618",
                                  color:
                                    mil.by === "Plaintiff"
                                      ? "#ef4444"
                                      : "#3b82f6",
                                  borderRadius: 8,
                                  fontWeight: 700,
                                  fontFamily: "monospace",
                                }}
                              >
                                {mil.by === "Plaintiff" ? "P" : "D"}
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  fontSize: 10,
                                  color: th.text,
                                }}
                              >
                                {mil.subj}
                              </span>
                              <span
                                style={{
                                  fontSize: 8,
                                  color: mil.strength.startsWith("low")
                                    ? "#ef4444"
                                    : "#f59e0b",
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {mil.strength}
                              </span>
                            </div>
                          ))}
                        </div>,
                      )}
                    </div>
                  );
                }
                // JUUL - TWO TRACKS
                const tk = warTrack || "sj";
                const sj = data.sj,
                  at = data.antitrust;
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {_warBanner}
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#0c4a6e,#075985)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#bae6fd",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        WAR ROOM · TWO PARALLEL TRACKS
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 5px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Seliavski v. JUUL MDL
                      </h1>
                      <div style={{ fontSize: 10, color: "#bae6fd" }}>
                        3:19-md-02913-WHO · N.D. Cal. · Judge Orrick
                      </div>
                    </div>

                    {/* TRACK TOGGLE */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                      }}
                    >
                      <button
                        onClick={() => setWarTrack("sj")}
                        style={{
                          flex: 1,
                          textAlign: "left",
                          padding: "11px 16px",
                          fontSize: 11,
                          fontWeight: tk === "sj" ? 700 : 500,
                          background: tk === "sj" ? "#3b82f6" : "transparent",
                          color: tk === "sj" ? "#fff" : th.muted,
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        <div>{sj.title}</div>
                        <div
                          style={{
                            fontSize: 8,
                            fontFamily: "monospace",
                            fontWeight: 500,
                            opacity: 0.8,
                            marginTop: 2,
                          }}
                        >
                          {sj.status.label}
                        </div>
                      </button>
                      <button
                        onClick={() => setWarTrack("antitrust")}
                        style={{
                          flex: 1,
                          textAlign: "left",
                          padding: "11px 16px",
                          fontSize: 11,
                          fontWeight: tk === "antitrust" ? 700 : 500,
                          background:
                            tk === "antitrust" ? "#ef4444" : "transparent",
                          color: tk === "antitrust" ? "#fff" : th.muted,
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -4,
                            fontSize: 6,
                            padding: "2px 6px",
                            background: "#f59e0b",
                            color: "#1a1a1a",
                            borderRadius: 8,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            boxShadow: "0 2px 6px #f59e0b66",
                            fontFamily: "system-ui",
                          }}
                        >
                          {at.daysToTrial}d
                        </span>
                        <div>{at.title}</div>
                        <div
                          style={{
                            fontSize: 8,
                            fontFamily: "monospace",
                            fontWeight: 500,
                            opacity: 0.8,
                            marginTop: 2,
                          }}
                        >
                          {at.status.label}
                        </div>
                      </button>
                    </div>

                    {tk === "sj" ? (
                      <div>
                        {/* SJ - WAITING ROOM */}
                        <div
                          style={{
                            background: dark ? "#0f172a" : "#fef9f3",
                            borderRadius: 10,
                            border: `2px dashed #f59e0b66`,
                            padding: "24px",
                            marginBottom: 12,
                            textAlign: "center",
                          }}
                        >
                          <div style={{ fontSize: 36, marginBottom: 8 }}>
                            ⏳
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              fontFamily: "monospace",
                              color: "#f59e0b",
                              letterSpacing: 1.5,
                              fontWeight: 700,
                              marginBottom: 6,
                            }}
                          >
                            STATUS
                          </div>
                          <h2
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              margin: "0 0 4px",
                            }}
                          >
                            Under Submission
                          </h2>
                          <div
                            style={{
                              fontSize: 11,
                              color: th.muted,
                              marginBottom: 14,
                            }}
                          >
                            {sj.hearing} - ruling expected within 60–90 days of
                            hearing
                          </div>
                          <div
                            style={{
                              display: "inline-flex",
                              gap: 14,
                              padding: "12px 20px",
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#ef4444",
                                  fontFamily: "monospace",
                                }}
                              >
                                {sj.daysSinceHearing}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.5,
                                }}
                              >
                                DAYS POST-HEARING
                              </div>
                            </div>
                            <div style={{ width: 1, background: th.border }} />
                            <div>
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#f59e0b",
                                  fontFamily: "monospace",
                                }}
                              >
                                47
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.5,
                                }}
                              >
                                ORRICK MEDIAN
                              </div>
                            </div>
                            <div style={{ width: 1, background: th.border }} />
                            <div>
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#a78bfa",
                                  fontFamily: "monospace",
                                }}
                              >
                                +{sj.daysSinceHearing - 47}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.5,
                                }}
                              >
                                DAYS OVER
                              </div>
                            </div>
                          </div>
                        </div>

                        {sec(
                          "AI Analysis",
                          "🧠",
                          <div
                            style={{
                              padding: "10px 12px",
                              background: "#a78bfa10",
                              borderLeft: "3px solid #a78bfa",
                              borderRadius: "0 6px 6px 0",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.65,
                                fontStyle: "italic",
                              }}
                            >
                              {sj.aiNote}
                            </div>
                          </div>,
                        )}

                        {sec(
                          "Derived Deadlines (Pending Ruling)",
                          "📅",
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            {sj.derivedDeadlines.map((dd, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: "10px 12px",
                                  background: th.bg,
                                  borderLeft: "3px solid #6b7280",
                                  borderRadius: "0 6px 6px 0",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: "#a78bfa",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    marginBottom: 3,
                                  }}
                                >
                                  IF: {dd.trigger}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: th.text,
                                    marginBottom: 3,
                                  }}
                                >
                                  {dd.deadline}
                                </div>
                                <div style={{ fontSize: 9, color: th.muted }}>
                                  <span
                                    style={{
                                      color: th.dim,
                                      fontFamily: "monospace",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {dd.window}
                                  </span>{" "}
                                  · {dd.ref}
                                </div>
                              </div>
                            ))}
                          </div>,
                        )}

                        {sec(
                          "Key SJ Briefing Documents",
                          "📋",
                          <div>
                            {sj.keyDocs.map((kd, i) => (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "7px 10px",
                                  background: th.bg,
                                  borderRadius: 5,
                                  marginBottom: 4,
                                  fontSize: 9,
                                }}
                              >
                                <span
                                  style={{
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    color: "#3b82f6",
                                    minWidth: 78,
                                  }}
                                >
                                  {kd.id}
                                </span>
                                <span style={{ flex: 1, color: th.muted }}>
                                  {kd.desc}
                                </span>
                              </div>
                            ))}
                          </div>,
                        )}
                      </div>
                    ) : (
                      <div>
                        {/* ANTITRUST - ACTIVE PREP */}
                        {/* OPT-OUT URGENT BANNER */}
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg,#ef4444,#dc2626)",
                            borderRadius: 10,
                            padding: "14px 18px",
                            marginBottom: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                          }}
                        >
                          <div style={{ fontSize: 32, lineHeight: 1 }}>⚠</div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 8,
                                color: "#fef2f2",
                                fontWeight: 700,
                                letterSpacing: 1,
                                fontFamily: "monospace",
                                marginBottom: 2,
                              }}
                            >
                              URGENT · OPT-OUT DEADLINE
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#fff",
                              }}
                            >
                              Direct Purchaser Class - {at.optOutDeadline.date}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#fecaca",
                                marginTop: 1,
                              }}
                            >
                              Class members must file opt-out election by this
                              date or be bound by class judgment
                            </div>
                          </div>
                          <div
                            style={{
                              textAlign: "center",
                              padding: "8px 14px",
                              background: "rgba(0,0,0,0.25)",
                              borderRadius: 8,
                              border: "1px solid rgba(255,255,255,0.2)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 24,
                                fontWeight: 800,
                                color: "#fff",
                                fontFamily: "monospace",
                                lineHeight: 1,
                              }}
                            >
                              {at.optOutDeadline.days}
                            </div>
                            <div
                              style={{
                                fontSize: 7,
                                color: "#fecaca",
                                fontFamily: "monospace",
                                letterSpacing: 1,
                                marginTop: 2,
                              }}
                            >
                              DAYS LEFT
                            </div>
                          </div>
                        </div>

                        {sec(
                          `Witness Readiness - Antitrust Class`,
                          "👥",
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 8,
                            }}
                          >
                            {at.witnessReadiness.map((w, i) => {
                              const c = SC[w.status];
                              const isCrit = w.status === "red";
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: "10px 12px",
                                    background: isCrit ? "#ef444410" : th.bg,
                                    borderLeft: `3px solid ${c}`,
                                    borderRadius: "0 6px 6px 0",
                                    border: isCrit
                                      ? "1px solid #ef444444"
                                      : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 6,
                                      marginBottom: 4,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: th.text,
                                        flex: 1,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {w.name}
                                    </div>
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: c,
                                        boxShadow: `0 0 0 2px ${c}33`,
                                        marginTop: 3,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                      marginBottom: 5,
                                    }}
                                  >
                                    {w.role}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          color: th.dim,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Depo:{" "}
                                      </span>
                                      {w.depo}
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          color: th.dim,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Cross:{" "}
                                      </span>
                                      {w.cross}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: isCrit ? "#ef4444" : th.muted,
                                      lineHeight: 1.4,
                                      marginTop: 5,
                                      fontStyle: isCrit ? "normal" : "italic",
                                      fontWeight: isCrit ? 700 : 400,
                                    }}
                                  >
                                    {w.notes}
                                  </div>
                                </div>
                              );
                            })}
                          </div>,
                        )}

                        {sec(
                          `Exhibit Pipeline - Antitrust Class`,
                          "📎",
                          <div>
                            {at.exhibitPipeline.map((ex, i) => {
                              const c = SC[ex.status];
                              return (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "8px 12px",
                                    background: th.bg,
                                    borderLeft: `3px solid ${c}`,
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 4,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      color: c,
                                      minWidth: 90,
                                    }}
                                  >
                                    {ex.id}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: th.text,
                                      }}
                                    >
                                      {ex.desc}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontFamily: "monospace",
                                        marginTop: 1,
                                      }}
                                    >
                                      {ex.note}
                                    </div>
                                  </div>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 9,
                                      height: 9,
                                      borderRadius: "50%",
                                      background: c,
                                      boxShadow: `0 0 0 2px ${c}33`,
                                      flexShrink: 0,
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>,
                        )}

                        {sec(
                          `AI-Flagged Open Questions`,
                          "🧠",
                          <div>
                            {at.flaggedQuestions.map((fq, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: "12px 14px",
                                  background: "#a78bfa10",
                                  borderLeft: "3px solid #a78bfa",
                                  borderRadius: "0 6px 6px 0",
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#a78bfa",
                                    marginBottom: 4,
                                  }}
                                >
                                  ❓ {fq.q}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                    marginBottom: 5,
                                  }}
                                >
                                  {fq.desc}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.faint,
                                    fontFamily: "monospace",
                                    marginBottom: 6,
                                  }}
                                >
                                  {fq.ref}
                                </div>
                                <div
                                  style={{
                                    padding: "7px 10px",
                                    background: th.card,
                                    borderRadius: 5,
                                    fontSize: 9,
                                    color: th.text,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      fontSize: 7,
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    AI ANALYSIS:{" "}
                                  </span>
                                  {fq.ai}
                                </div>
                              </div>
                            ))}
                          </div>,
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "impeach" &&
              (() => {
                const baseSeqs =
                  IMPEACHMENT_SEQUENCES[caseId] || IMPEACHMENT_SEQUENCES.juul;
                const allSeqs = [
                  ...baseSeqs,
                  ...impCustomSeqs.filter((s) => s.caseId === caseId),
                ];
                const PRI = {
                  Critical: "#ef4444",
                  High: "#f59e0b",
                  Medium: "#3b82f6",
                };
                const TYP = {
                  Admission: "#3b82f6",
                  Contradiction: "#ef4444",
                  Document: "#f59e0b",
                  "Prior Statement": "#a78bfa",
                };
                const STEP = {
                  foundation: ["#6b7280", "FOUNDATION"],
                  exhibit: ["#f59e0b", "EXHIBIT"],
                  confrontation: ["#ef4444", "CONFRONTATION"],
                  followup: ["#3b82f6", "FOLLOW-UP"],
                };
                const STATUS_LABELS = {
                  unused: ["Unused", th.muted],
                  deployed: ["✓ Deployed", "#10b981"],
                  saved: ["🔖 Saved for Closing", "#a78bfa"],
                };
                const getStatus = (id) => impStatuses[id] || "unused";
                const visible = allSeqs.filter((s) => {
                  if (impSearch) {
                    const q = impSearch.toLowerCase();
                    const hay = (
                      s.witness +
                      " " +
                      s.witnessRole +
                      " " +
                      s.topic +
                      " " +
                      s.priorText +
                      " " +
                      s.inconsistency +
                      " " +
                      (s.useFor || []).join(" ")
                    ).toLowerCase();
                    if (!hay.includes(q)) return false;
                  }
                  if (
                    impPriorityFilter !== "all" &&
                    s.priority.toLowerCase() !== impPriorityFilter
                  )
                    return false;
                  if (
                    impTypeFilter !== "all" &&
                    s.type.toLowerCase().replace(" ", "") !== impTypeFilter
                  )
                    return false;
                  if (
                    impStatusFilter !== "all" &&
                    getStatus(s.id) !== impStatusFilter
                  )
                    return false;
                  if (
                    impPartyFilter !== "all" &&
                    s.party.toLowerCase() !== impPartyFilter
                  )
                    return false;
                  return true;
                });
                const sortPri = { Critical: 0, High: 1, Medium: 2 };
                visible.sort(
                  (a, b) =>
                    sortPri[a.priority] - sortPri[b.priority] ||
                    (getStatus(a.id) === "unused" ? -1 : 1),
                );
                const counts = {
                  total: allSeqs.length,
                  unused: allSeqs.filter((s) => getStatus(s.id) === "unused")
                    .length,
                  critical: allSeqs.filter(
                    (s) =>
                      s.priority === "Critical" && getStatus(s.id) === "unused",
                  ).length,
                  deployed: allSeqs.filter(
                    (s) => getStatus(s.id) === "deployed",
                  ).length,
                  saved: allSeqs.filter((s) => getStatus(s.id) === "saved")
                    .length,
                };
                const setStatus = (id, st) => {
                  setImpStatuses((p) => ({ ...p, [id]: st }));
                  setImpMarkOpen(null);
                  toast(
                    `Marked ${STATUS_LABELS[st][0].replace("✓ ", "").replace("🔖 ", "")}`,
                    "success",
                  );
                };
                const toggleExp = (id) =>
                  setImpExpanded((p) => ({ ...p, [id]: !p[id] }));
                const aiGen = async () => {
                  setImpAILoading(true);
                  setImpAIResult(null);
                  try {
                    const demoFallback = () => {
                      const demos = {
                        juul: {
                          id: "juul-ai-halpern",
                          witness: "Dr. Bonnie Halpern-Felsher",
                          witnessRole: "UCSF Professor (Defense Witness)",
                          party: "Defendant",
                          priority: "High",
                          type: "Document",
                          topic:
                            "75% youth nicotine awareness gap - defense expert cross",
                          priorText:
                            "JUUL conducted comprehensive prevention efforts to inform youth about nicotine content.",
                          priorCite: "Defense expert disclosure",
                          inconsistency:
                            "If a JLI executive testifies about prevention efforts, Halpern-Felsher's own September 2021 study (Ex. 41) shows 75% of SFUSD students who recognized JUUL did not know it contained nicotine. Use her own data against the prevention narrative.",
                          script: [
                            {
                              t: "foundation",
                              q: "Dr. Halpern-Felsher, you authored the September 2021 study on SFUSD student nicotine awareness?",
                            },
                            {
                              t: "foundation",
                              q: "Your study sampled SFUSD students aged 15 to 24, correct?",
                            },
                            {
                              t: "exhibit",
                              q: "I'm showing you Plaintiff's Exhibit 41, your September 2021 report at pages 64-65.",
                            },
                            {
                              t: "confrontation",
                              q: "Your data shows that 75% of students who recognized a JUUL device did not know it always contained nicotine. Correct?",
                            },
                            {
                              t: "confrontation",
                              q: "That data was collected after JUUL claims its prevention efforts had been deployed. Correct?",
                            },
                            {
                              t: "followup",
                              q: "If 75% of recognizing students did not know nicotine was present, the prevention efforts you described did not reach those students, did they?",
                            },
                          ],
                          contingency:
                            "If she defends the prevention efforts as 'ongoing,' the time-stamp on her study (post-deployment) is the answer. The data is what it is.",
                          useFor: ["Trial Cross", "Closing", "Punitive"],
                        },
                        musk: {
                          id: "musk-ai-hoffman",
                          witness: "Reid Hoffman",
                          witnessRole: "Founding Board Member, OpenAI",
                          party: "Defendant",
                          priority: "Medium",
                          type: "Admission",
                          topic:
                            "Founding board awareness of donor representations",
                          priorText:
                            "As a founding board member, I was not involved in donor communications.",
                          priorCite: "Anticipated direct testimony",
                          inconsistency:
                            "Hoffman will distance himself from donor representations. As founding board member, he was on the distribution list for the May 2015 Altman email (PX-003). Board members have non-delegable fiduciary duties - distance is not a defense.",
                          script: [
                            {
                              t: "foundation",
                              q: "Mr. Hoffman, you served as a founding board member of OpenAI Inc. starting in 2015?",
                            },
                            {
                              t: "foundation",
                              q: "In that role, you owed fiduciary duties to OpenAI Inc. and its charitable beneficiaries?",
                            },
                            {
                              t: "exhibit",
                              q: "I'm showing you Plaintiff's Exhibit 003, the May 2015 founding email.",
                            },
                            {
                              t: "confrontation",
                              q: "You were on the distribution list for this email. Correct?",
                            },
                            {
                              t: "confrontation",
                              q: "This email represented OpenAI as 'belonging to the world' - language consistent with charitable purpose. Correct?",
                            },
                            {
                              t: "followup",
                              q: "As a fiduciary, you had a duty to ensure those representations were not breached, did you not?",
                            },
                          ],
                          contingency:
                            "If Hoffman claims passive board role, fiduciary duty is non-delegable - pull the relevant Delaware case law on closing. The Caremark standard applies even to startup boards.",
                          useFor: ["Count XVIII", "Phase 1 Cross", "Closing"],
                        },
                        neuro: {
                          id: "neuro-ai-zhang",
                          witness: "Wei Zhang",
                          witnessRole: "Mando Director of Licensing",
                          party: "Defendant",
                          priority: "Medium",
                          type: "Contradiction",
                          topic:
                            "TMLA scope - manufacturing rights interpretation",
                          priorText:
                            "The TMLA permitted Mando to manufacture in any territory where the licensed compound was sold.",
                          priorCite: "Anticipated direct testimony",
                          inconsistency:
                            "Zhang will broadly interpret TMLA scope. The actual TMLA § 4.2 limits manufacturing to 'Listed Territories,' which do not include China. Pair this with the country-of-origin evidence - both impeachments compound.",
                          script: [
                            {
                              t: "foundation",
                              q: "Mr. Zhang, you served as Mando's Director of Licensing during the TMLA negotiation?",
                            },
                            {
                              t: "foundation",
                              q: "In that role, you negotiated the territorial provisions of the TMLA?",
                            },
                            {
                              t: "exhibit",
                              q: "I'm showing you Plaintiff's Exhibit 12, the executed TMLA. Please turn to Section 4.2.",
                            },
                            {
                              t: "confrontation",
                              q: "Section 4.2 limits manufacturing rights to 'Listed Territories,' which are defined in Schedule A. Correct?",
                            },
                            {
                              t: "confrontation",
                              q: "Schedule A does not list the People's Republic of China among the Listed Territories. Correct?",
                            },
                            {
                              t: "followup",
                              q: "Your earlier testimony that the TMLA permitted manufacturing 'in any territory' is therefore inconsistent with the actual contract language. Correct?",
                            },
                          ],
                          contingency:
                            "If Zhang claims a side-letter or amendment expanded the territories, demand its production. Without a written amendment, parol-evidence rule keeps the impeachment clean.",
                          useFor: [
                            "Licensee Estoppel Defense",
                            "Trial Cross",
                            "Country of Origin",
                          ],
                        },
                      };
                      return demos[caseId] || demos.juul;
                    };
                    const result = await aiCall(
                      {
                        model: "claude-sonnet-4-20250514",
                        max_tokens: 2000,
                        system:
                          "You are a senior trial lawyer building cross-examination sequences. Return only a JSON object matching the exact schema.",
                        messages: [
                          {
                            role: "user",
                            content: `Generate one new impeachment sequence for case ${caseId}. Format: {id, witness, witnessRole, party, priority, type, topic, priorText, priorCite, inconsistency, script:[{t,q}], contingency, useFor:[]}. Be specific to actual case witnesses and exhibits.`,
                          },
                        ],
                      },
                      () => JSON.stringify(demoFallback()),
                    );
                    try {
                      const parsed =
                        typeof result.text === "string"
                          ? JSON.parse(result.text)
                          : result.text;
                      setImpAIResult(parsed);
                    } catch {
                      setImpAIResult(demoFallback());
                    }
                  } catch {
                    const demos = {
                      juul: { id: "juul-ai-halpern" },
                      musk: { id: "musk-ai-hoffman" },
                      neuro: { id: "neuro-ai-zhang" },
                    };
                    setImpAIResult(demos[caseId]);
                  }
                  setImpAILoading(false);
                };
                const addToBank = () => {
                  if (!impAIResult) return;
                  setImpCustomSeqs((p) => [
                    ...p,
                    {
                      ...impAIResult,
                      caseId,
                      id: impAIResult.id + "-" + Date.now(),
                      addedAt: new Date().toISOString(),
                    },
                  ]);
                  setImpAIResult(null);
                  toast("Added to Impeachment Bank", "success");
                };
                const filterPills = [
                  {
                    label: "Status",
                    value: impStatusFilter,
                    setter: setImpStatusFilter,
                    options: [
                      ["all", "All"],
                      ["unused", "Unused"],
                      ["deployed", "Deployed"],
                      ["saved", "Saved for Closing"],
                    ],
                  },
                  {
                    label: "Priority",
                    value: impPriorityFilter,
                    setter: setImpPriorityFilter,
                    options: [
                      ["all", "All"],
                      ["critical", "🔴 Critical"],
                      ["high", "🟡 High"],
                      ["medium", "🔵 Medium"],
                    ],
                  },
                  {
                    label: "Type",
                    value: impTypeFilter,
                    setter: setImpTypeFilter,
                    options: [
                      ["all", "All"],
                      ["admission", "Admission"],
                      ["contradiction", "Contradiction"],
                      ["document", "Document"],
                      ["priorstatement", "Prior Statement"],
                    ],
                  },
                ];
                return (
                  <div
                    style={{
                      maxWidth: 980,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#ef4444",
                              background: "#ef444418",
                              border: "1px solid #ef444433",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            🎯 TRIAL-READY
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            Cross-examination procedures · live status
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Impeachment Bank
                        </h1>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            fontFamily: "monospace",
                          }}
                        >
                          <span style={{ color: th.text, fontWeight: 700 }}>
                            {counts.total} sequences
                          </span>{" "}
                          ·{" "}
                          <span
                            style={{
                              color: counts.unused > 0 ? "#ef4444" : th.muted,
                              fontWeight: 700,
                            }}
                          >
                            {counts.unused} unused
                          </span>{" "}
                          ·{" "}
                          <span
                            style={{
                              color: counts.critical > 0 ? "#ef4444" : th.muted,
                              fontWeight: 700,
                            }}
                          >
                            {counts.critical} critical
                          </span>
                          {counts.deployed > 0 && (
                            <>
                              {" "}
                              ·{" "}
                              <span style={{ color: "#10b981" }}>
                                {counts.deployed} deployed
                              </span>
                            </>
                          )}
                          {counts.saved > 0 && (
                            <>
                              {" "}
                              ·{" "}
                              <span style={{ color: "#a78bfa" }}>
                                {counts.saved} saved
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={aiGen}
                        disabled={impAILoading}
                        style={{
                          padding: "9px 16px",
                          fontSize: 10,
                          fontWeight: 700,
                          background: impAILoading
                            ? "#a78bfa66"
                            : "linear-gradient(135deg,#1e40af,#a78bfa)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 7,
                          cursor: impAILoading ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {impAILoading ? (
                          <>
                            <span
                              style={{
                                display: "inline-block",
                                width: 11,
                                height: 11,
                                border: "2px solid #fff",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "spin 0.8s linear infinite",
                              }}
                            />
                            Generating…
                          </>
                        ) : (
                          <>✨ AI: Generate Sequence</>
                        )}
                      </button>
                    </div>

                    {/* AI Result Banner */}
                    {impAIResult && (
                      <div
                        style={{
                          background: "#a78bfa10",
                          border: "1px solid #a78bfa55",
                          borderLeft: "3px solid #a78bfa",
                          borderRadius: 8,
                          padding: "12px 14px",
                          marginBottom: 14,
                          animation: "slideIn 0.4s ease",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#a78bfa",
                              }}
                            >
                              ✨ AI-Generated Sequence
                            </span>
                            <span
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                fontFamily: "monospace",
                              }}
                            >
                              review and add to bank
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 5 }}>
                            <button
                              onClick={addToBank}
                              style={{
                                padding: "5px 12px",
                                fontSize: 9,
                                fontWeight: 700,
                                background: "#10b981",
                                color: "#fff",
                                border: "none",
                                borderRadius: 5,
                                cursor: "pointer",
                              }}
                            >
                              + Add to Bank
                            </button>
                            <button
                              onClick={() => setImpAIResult(null)}
                              style={{
                                padding: "5px 9px",
                                fontSize: 9,
                                background: "transparent",
                                color: th.muted,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                cursor: "pointer",
                              }}
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: th.text,
                            }}
                          >
                            {impAIResult.witness}
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              padding: "2px 7px",
                              background:
                                impAIResult.party === "Plaintiff"
                                  ? "#ef444418"
                                  : "#3b82f618",
                              color:
                                impAIResult.party === "Plaintiff"
                                  ? "#ef4444"
                                  : "#3b82f6",
                              border: `1px solid ${impAIResult.party === "Plaintiff" ? "#ef444444" : "#3b82f644"}`,
                              borderRadius: 8,
                              fontWeight: 700,
                              fontFamily: "monospace",
                              letterSpacing: 0.4,
                            }}
                          >
                            {impAIResult.party}
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              padding: "2px 7px",
                              background: PRI[impAIResult.priority] + "22",
                              color: PRI[impAIResult.priority],
                              border: `1px solid ${PRI[impAIResult.priority]}55`,
                              borderRadius: 8,
                              fontWeight: 700,
                              fontFamily: "monospace",
                              letterSpacing: 0.4,
                            }}
                          >
                            {impAIResult.priority}
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              padding: "2px 7px",
                              background:
                                (TYP[impAIResult.type] || th.faint) + "18",
                              color: TYP[impAIResult.type] || th.muted,
                              border: `1px solid ${(TYP[impAIResult.type] || th.faint) + "33"}`,
                              borderRadius: 8,
                              fontWeight: 600,
                              fontFamily: "monospace",
                              letterSpacing: 0.4,
                            }}
                          >
                            {impAIResult.type}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: th.text,
                            marginBottom: 6,
                          }}
                        >
                          {impAIResult.topic}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          {impAIResult.inconsistency}
                        </div>
                        <div
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            marginTop: 6,
                            fontStyle: "italic",
                          }}
                        >
                          Script: {(impAIResult.script || []).length} steps ·
                          Click "Add to Bank" to make it permanent
                        </div>
                      </div>
                    )}

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background:
                          impPartyFilter === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : impPartyFilter === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${impPartyFilter === "plaintiff" ? "#dc262655" : impPartyFilter === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: impPartyFilter === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: impPartyFilter === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {impPartyFilter === "plaintiff"
                            ? "🔴 Plaintiff View - sequences targeting plaintiff witnesses"
                            : impPartyFilter === "defendant"
                              ? "🔵 Defendant View - sequences targeting defendant witnesses"
                              : "All Impeachment Sequences"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              impPartyFilter === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {impPartyFilter === "plaintiff"
                            ? "Defense offense playbook · attacks on plaintiff's case-in-chief"
                            : impPartyFilter === "defendant"
                              ? "Plaintiff offense playbook · attacks on defendant's affirmative case"
                              : "Toggle to see how each side's counsel sees the bank"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All", th.muted],
                          [
                            "plaintiff",
                            "🔴 Plaintiff",
                            impPartyFilter === "plaintiff" ? "#fff" : "#ef4444",
                          ],
                          [
                            "defendant",
                            "🔵 Defendant",
                            impPartyFilter === "defendant" ? "#fff" : "#3b82f6",
                          ],
                        ].map(([v, l, activeC]) => (
                          <button
                            key={v}
                            onClick={() => setImpPartyFilter(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: impPartyFilter === v ? 700 : 500,
                              background:
                                impPartyFilter === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                impPartyFilter === "all"
                                  ? impPartyFilter === v
                                    ? th.text
                                    : th.muted
                                  : impPartyFilter === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                impPartyFilter === v
                                  ? impPartyFilter === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${impPartyFilter === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Search + Filters */}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        padding: "10px 12px",
                        marginBottom: 12,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 14, color: th.faint }}>
                          🔍
                        </span>
                        <input
                          value={impSearch}
                          onChange={(e) => setImpSearch(e.target.value)}
                          placeholder="Search witness, topic, prior statement…"
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 11,
                            background: th.bg,
                            border: `1px solid ${th.border}`,
                            borderRadius: 5,
                            color: th.text,
                            outline: "none",
                          }}
                        />
                        {impSearch && (
                          <button
                            onClick={() => setImpSearch("")}
                            style={{
                              fontSize: 9,
                              padding: "4px 9px",
                              background: "transparent",
                              color: th.muted,
                              border: `1px solid ${th.border}`,
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        {filterPills.map((f) => (
                          <div
                            key={f.label}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 8,
                                color: th.faint,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                              }}
                            >
                              {f.label.toUpperCase()}:
                            </span>
                            <div style={{ display: "flex", gap: 3 }}>
                              {f.options.map(([v, l]) => (
                                <button
                                  key={v}
                                  onClick={() => f.setter(v)}
                                  style={{
                                    padding: "3px 8px",
                                    fontSize: 8,
                                    background:
                                      f.value === v ? "#ef4444" : "transparent",
                                    color: f.value === v ? "#fff" : th.muted,
                                    border: `1px solid ${f.value === v ? "#ef4444" : th.border}`,
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontWeight: f.value === v ? 700 : 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        {(impStatusFilter !== "all" ||
                          impPriorityFilter !== "all" ||
                          impTypeFilter !== "all" ||
                          impPartyFilter !== "all" ||
                          impSearch) && (
                          <button
                            onClick={() => {
                              setImpSearch("");
                              setImpStatusFilter("all");
                              setImpPriorityFilter("all");
                              setImpTypeFilter("all");
                              setImpPartyFilter("all");
                            }}
                            style={{
                              fontSize: 8,
                              padding: "3px 8px",
                              background: "transparent",
                              color: "#ef4444",
                              border: "1px solid #ef444433",
                              borderRadius: 4,
                              cursor: "pointer",
                              marginLeft: "auto",
                            }}
                          >
                            Reset all
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Card list */}
                    {visible.length === 0 ? (
                      <div
                        style={{
                          padding: "40px 20px",
                          textAlign: "center",
                          color: th.faint,
                          background: th.card,
                          border: `1px solid ${th.border}`,
                          borderRadius: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 30,
                            opacity: 0.4,
                            marginBottom: 8,
                          }}
                        >
                          🎯
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: th.muted,
                          }}
                        >
                          No sequences match your filters
                        </div>
                        <div style={{ fontSize: 9, marginTop: 3 }}>
                          Adjust filters or click ✨ AI Generate to create new
                          sequences.
                        </div>
                      </div>
                    ) : (
                      visible.map((s) => {
                        const st = getStatus(s.id);
                        const isExp = impExpanded[s.id];
                        const isUsed = st !== "unused";
                        const priColor = PRI[s.priority];
                        const typColor = TYP[s.type] || th.faint;
                        const partyColor =
                          s.party === "Plaintiff"
                            ? "#ef4444"
                            : s.party === "Defendant"
                              ? "#3b82f6"
                              : "#a78bfa";
                        return (
                          <div
                            key={s.id}
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${st === "deployed" ? "#10b98144" : st === "saved" ? "#a78bfa44" : priColor + "55"}`,
                              borderLeft: `4px solid ${st === "deployed" ? "#10b981" : st === "saved" ? "#a78bfa" : priColor}`,
                              padding: "14px 16px",
                              marginBottom: 10,
                              opacity: st === "deployed" ? 0.65 : 1,
                              transition: "all 0.2s",
                              position: "relative",
                            }}
                          >
                            {/* Header row */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 10,
                                marginBottom: 8,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    flexWrap: "wrap",
                                    marginBottom: 4,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: th.text,
                                    }}
                                  >
                                    {s.witness}
                                  </span>
                                  <span
                                    style={{ fontSize: 9, color: th.muted }}
                                  >
                                    · {s.witnessRole}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: partyColor + "18",
                                      color: partyColor,
                                      border: `1px solid ${partyColor}44`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    {s.party.toUpperCase()}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: priColor + "22",
                                      color: priColor,
                                      border: `1px solid ${priColor}55`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    {s.priority.toUpperCase()}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: typColor + "18",
                                      color: typColor,
                                      border: `1px solid ${typColor}33`,
                                      borderRadius: 8,
                                      fontWeight: 600,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    {s.type}
                                  </span>
                                  {st !== "unused" && (
                                    <span
                                      style={{
                                        fontSize: 7,
                                        padding: "2px 7px",
                                        background: STATUS_LABELS[st][1] + "18",
                                        color: STATUS_LABELS[st][1],
                                        border: `1px solid ${STATUS_LABELS[st][1]}44`,
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.4,
                                      }}
                                    >
                                      {STATUS_LABELS[st][0]}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div
                                style={{ position: "relative", flexShrink: 0 }}
                              >
                                <button
                                  onClick={() =>
                                    setImpMarkOpen(
                                      impMarkOpen === s.id ? null : s.id,
                                    )
                                  }
                                  style={{
                                    padding: "5px 10px",
                                    fontSize: 9,
                                    background: STATUS_LABELS[st][1] + "15",
                                    color: STATUS_LABELS[st][1],
                                    border: `1px solid ${STATUS_LABELS[st][1]}44`,
                                    borderRadius: 5,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Mark ▾
                                </button>
                                {impMarkOpen === s.id && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      right: 0,
                                      top: "calc(100% + 4px)",
                                      background: th.surface,
                                      border: `1px solid ${th.border}`,
                                      borderRadius: 6,
                                      padding: 4,
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                                      zIndex: 10,
                                      minWidth: 170,
                                    }}
                                  >
                                    {Object.entries(STATUS_LABELS).map(
                                      ([k, [lbl, c]]) => (
                                        <button
                                          key={k}
                                          onClick={() => setStatus(s.id, k)}
                                          style={{
                                            display: "block",
                                            width: "100%",
                                            textAlign: "left",
                                            padding: "7px 10px",
                                            fontSize: 9,
                                            background:
                                              st === k
                                                ? c + "18"
                                                : "transparent",
                                            color: st === k ? c : th.text,
                                            border: "none",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            fontWeight: st === k ? 700 : 500,
                                          }}
                                        >
                                          {lbl}
                                          {st === k && (
                                            <span
                                              style={{
                                                float: "right",
                                                fontSize: 8,
                                                color: c,
                                              }}
                                            >
                                              •
                                            </span>
                                          )}
                                        </button>
                                      ),
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Topic */}
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: th.text,
                                marginBottom: 8,
                                lineHeight: 1.4,
                              }}
                            >
                              {s.topic}
                            </div>

                            {/* Prior Statement Box */}
                            <div
                              style={{
                                background: dark ? "#0f172a" : "#fef9f3",
                                border: `1px dashed ${typColor}66`,
                                borderRadius: 6,
                                padding: "9px 12px",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  color: typColor,
                                  fontWeight: 700,
                                  letterSpacing: 1,
                                  marginBottom: 4,
                                  fontFamily: "monospace",
                                }}
                              >
                                PRIOR STATEMENT
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: th.text,
                                  fontStyle: "italic",
                                  lineHeight: 1.5,
                                  marginBottom: 4,
                                }}
                              >
                                "{s.priorText}"
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  fontFamily: "monospace",
                                }}
                              >
 - {s.priorCite}
                              </div>
                            </div>

                            {/* Inconsistency */}
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                lineHeight: 1.55,
                                marginBottom: 10,
                                padding: "7px 10px",
                                background: priColor + "08",
                                borderLeft: `2px solid ${priColor}66`,
                                borderRadius: "0 4px 4px 0",
                              }}
                            >
                              <span
                                style={{
                                  color: priColor,
                                  fontWeight: 700,
                                  fontSize: 8,
                                  letterSpacing: 0.5,
                                  fontFamily: "monospace",
                                  display: "block",
                                  marginBottom: 3,
                                }}
                              >
                                WHAT IT UNDERCUTS
                              </span>
                              {s.inconsistency}
                            </div>

                            {/* Expand toggle */}
                            <button
                              onClick={() => toggleExp(s.id)}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                fontSize: 10,
                                fontWeight: 600,
                                background: isExp ? priColor + "15" : th.bg,
                                color: isExp ? priColor : th.text,
                                border: `1px solid ${isExp ? priColor + "55" : th.border}`,
                                borderRadius: 6,
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                {isExp ? "▾" : "▸"} Cross-Examination Script (
                                {s.script.length} steps)
                              </span>
                              <span
                                style={{
                                  fontSize: 8,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                }}
                              >
                                {isExp ? "hide" : "expand"}
                              </span>
                            </button>

                            {/* Expanded script */}
                            {isExp && (
                              <div
                                style={{
                                  marginTop: 10,
                                  animation: "fadeIn 0.3s ease",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                  }}
                                >
                                  {s.script.map((step, i) => {
                                    const [c, lbl] = STEP[step.t] || [
                                      "#6b7280",
                                      "STEP",
                                    ];
                                    return (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          gap: 10,
                                          padding: "10px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderRadius: 6,
                                          borderLeft: `3px solid ${c}`,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: c,
                                            fontFamily: "monospace",
                                            flexShrink: 0,
                                            minWidth: 24,
                                            paddingTop: 1,
                                          }}
                                        >
                                          {i + 1}.
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontWeight: 700,
                                              color: c,
                                              letterSpacing: 1,
                                              fontFamily: "monospace",
                                              marginBottom: 3,
                                            }}
                                          >
                                            {lbl}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 10,
                                              color: th.text,
                                              lineHeight: 1.55,
                                            }}
                                          >
                                            {step.q}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Contingency block */}
                                <div
                                  style={{
                                    marginTop: 10,
                                    padding: "10px 12px",
                                    background: "#a78bfa10",
                                    border: `1px dashed #a78bfa66`,
                                    borderRadius: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 1,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    ⚠ IF WITNESS REHABILITATES
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.6,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {s.contingency}
                                  </div>
                                </div>

                                {/* Use For tags */}
                                {s.useFor && s.useFor.length > 0 && (
                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 8,
                                        color: th.faint,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      USE FOR:
                                    </span>
                                    {s.useFor.map((u, i) => (
                                      <span
                                        key={i}
                                        style={{
                                          fontSize: 8,
                                          padding: "2px 7px",
                                          background: th.bg,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {u}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}

                    {visible.length > 0 && (
                      <div
                        style={{
                          marginTop: 14,
                          padding: "0 4px",
                          fontSize: 8,
                          color: th.faint,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          💡 Showing {visible.length} of {counts.total}{" "}
                          sequences · Mark deployed during trial to track what's
                          been used
                        </span>
                        <span style={{ fontFamily: "monospace" }}>
                          Hand-print ready · co-counsel friendly
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "judgeintel" &&
              (() => {
                const data = JUDGE_INTEL_DATA[caseId] || JUDGE_INTEL_DATA.juul;
                const sec = judgeSection;
                const sectionBtn = (k, emoji, label, count) => (
                  <button
                    key={k}
                    onClick={() => setJudgeSection(k)}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#a78bfa" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                    {count !== undefined && (
                      <span
                        style={{
                          fontSize: 8,
                          padding: "1px 6px",
                          background:
                            sec === k ? "rgba(255,255,255,0.25)" : th.surface,
                          color: sec === k ? "#fff" : th.faint,
                          borderRadius: 8,
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* Judge Profile Header */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#0f172a,#1e293b)",
                        borderRadius: 12,
                        padding: "18px 22px",
                        marginBottom: 14,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 14,
                          right: 14,
                          padding: "3px 8px",
                          background: "#a78bfa",
                          color: "#1a1a1a",
                          borderRadius: 8,
                          fontSize: 9,
                          fontWeight: 700,
                          fontFamily: "monospace",
                          letterSpacing: 0.5,
                        }}
                      >
                        PREDICTIVE INTEL
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          alignItems: "flex-start",
                        }}
                      >
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg,#a78bfa,#7c3aed)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 24,
                            flexShrink: 0,
                            boxShadow: "0 4px 12px rgba(167,139,250,0.4)",
                          }}
                        >
                          👨‍⚖️
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 7,
                              color: "#a78bfa",
                              fontFamily: "monospace",
                              letterSpacing: 1.5,
                              marginBottom: 4,
                              fontWeight: 700,
                            }}
                          >
                            JUDGE INTELLIGENCE
                          </div>
                          <h1
                            style={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: "#fff",
                              margin: "0 0 5px",
                              letterSpacing: "-0.5px",
                            }}
                          >
                            {data.judge.name}
                          </h1>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#cbd5e1",
                              marginBottom: 6,
                            }}
                          >
                            {data.judge.court} · Appointed{" "}
                            {data.judge.appointed}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "#94a3b8",
                              fontFamily: "monospace",
                              marginBottom: 8,
                            }}
                          >
                            {data.judge.role}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#cbd5e1",
                              lineHeight: 1.6,
                            }}
                          >
                            {data.judge.bio}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section selector */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn(
                        "ruling",
                        "📊",
                        "Ruling Patterns",
                        data.rulingPatterns.length,
                      )}
                      {sectionBtn(
                        "oral",
                        "🎙️",
                        "Oral Argument",
                        data.oralArgument.length,
                      )}
                      {sectionBtn("jury", "⚖️", "Jury Charge")}
                      {sectionBtn(
                        "rules",
                        "📋",
                        "Local Rules",
                        data.localRules.length,
                      )}
                    </div>

                    {/* RULING PATTERNS */}
                    {sec === "ruling" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#a78bfa10",
                            borderLeft: "2px solid #a78bfa",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                            How does this judge rule on the issues that matter
                            to this case?
                          </span>{" "}
                          Pattern analysis sourced from public court records and
                          Westlaw integration. Each issue includes record, AI
                          analysis, citations, and a directional trend
                          indicator.
                        </div>
                        {data.rulingPatterns.map((p, i) => {
                          const trendC = p.trend.includes("defense")
                            ? "#3b82f6"
                            : p.trend.includes("plaintiff")
                              ? "#ef4444"
                              : p.trend.includes("transparency")
                                ? "#10b981"
                                : p.trend.includes("strict") ||
                                    p.trend.includes("form-strict")
                                  ? "#f59e0b"
                                  : "#a78bfa";
                          return (
                            <div
                              key={i}
                              style={{
                                background: th.card,
                                borderRadius: 9,
                                border: `1px solid ${th.border}`,
                                borderLeft: `3px solid ${trendC}`,
                                padding: "14px 16px",
                                marginBottom: 9,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 10,
                                  marginBottom: 8,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: th.text,
                                      marginBottom: 4,
                                      letterSpacing: "-0.1px",
                                    }}
                                  >
                                    {p.issue}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: trendC,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      marginBottom: 6,
                                    }}
                                  >
                                    📈 {p.record}
                                  </div>
                                </div>
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "3px 9px",
                                    background: trendC + "22",
                                    color: trendC,
                                    border: `1px solid ${trendC}55`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {p.trend}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: th.muted,
                                  lineHeight: 1.6,
                                  marginBottom: 8,
                                }}
                              >
                                {p.analysis}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 5,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontWeight: 700,
                                    color: th.faint,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    alignSelf: "center",
                                  }}
                                >
                                  CITES:
                                </span>
                                {p.cites.map((c, j) => (
                                  <span
                                    key={j}
                                    style={{
                                      fontSize: 8,
                                      padding: "2px 7px",
                                      background: th.bg,
                                      color: th.muted,
                                      border: `1px solid ${th.border}`,
                                      borderRadius: 8,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {c}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ORAL ARGUMENT */}
                    {sec === "oral" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#a78bfa10",
                            borderLeft: "2px solid #a78bfa",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                            What questions does this judge ask?
                          </span>{" "}
                          Hearing transcripts analyzed by AI to extract
                          questions asked, arguments pushed back on, signals of
                          skepticism or persuasion. Use this for upcoming
                          hearings.
                        </div>
                        {data.oralArgument.map((h, i) => (
                          <div
                            key={i}
                            style={{
                              background: th.card,
                              borderRadius: 9,
                              border: `1px solid ${th.border}`,
                              padding: "14px 16px",
                              marginBottom: 9,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 10,
                                marginBottom: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: "#a78bfa",
                                    fontFamily: "monospace",
                                    letterSpacing: 0.5,
                                    marginBottom: 3,
                                    fontWeight: 700,
                                  }}
                                >
                                  {h.date}
                                </div>
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: th.text,
                                  }}
                                >
                                  {h.hearing}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                    marginTop: 2,
                                  }}
                                >
                                  {h.duration > 0
                                    ? `Duration: ${h.duration} min · `
                                    : ""}
                                  {h.ref}
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.text,
                                lineHeight: 1.55,
                                padding: "8px 10px",
                                background: dark ? "#0f172a" : "#f8fafc",
                                borderRadius: 5,
                                marginBottom: 11,
                                fontStyle: "italic",
                              }}
                            >
                              {h.summary}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: th.dim,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 7,
                              }}
                            >
                              KEY MOMENTS
                            </div>
                            {h.keyMoments.map((m, j) => {
                              const isCrit = m.importance === "critical";
                              const sigC =
                                m.signal.includes("plaintiff-favorable") ||
                                m.signal.includes("plaintiff-friendly")
                                  ? "#ef4444"
                                  : m.signal.includes("defense")
                                    ? "#3b82f6"
                                    : "#a78bfa";
                              return (
                                <div
                                  key={j}
                                  style={{
                                    padding: "9px 11px",
                                    background: isCrit
                                      ? dark
                                        ? "#1f0a0a"
                                        : "#fef2f2"
                                      : th.bg,
                                    border: isCrit
                                      ? "2px solid #ef444466"
                                      : `1px solid ${th.border}`,
                                    borderRadius: 6,
                                    marginBottom: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "baseline",
                                      gap: 8,
                                      marginBottom: 4,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: th.dim,
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                      }}
                                    >
                                      {m.minute}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: th.text,
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {m.topic}
                                    </span>
                                    {isCrit && (
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "1px 6px",
                                          background: "#ef4444",
                                          color: "#fff",
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        ⚠ CRITICAL
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      fontStyle: "italic",
                                      lineHeight: 1.5,
                                      marginBottom: 5,
                                      padding: "6px 9px",
                                      background: dark ? "#0f172a" : "#fff",
                                      borderLeft: `2px solid ${sigC}`,
                                      borderRadius: "0 4px 4px 0",
                                    }}
                                  >
                                    "{m.quote}"
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: sigC,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.3,
                                      fontWeight: 600,
                                    }}
                                  >
                                    ↳ Signal: {m.signal}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* JURY CHARGE */}
                    {sec === "jury" && (
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 9,
                          border: `1px solid ${th.border}`,
                          padding: "16px 18px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 14,
                            padding: "8px 12px",
                            background: "#a78bfa10",
                            borderLeft: "2px solid #a78bfa",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                            How does this judge handle jury instruction
                            disputes?
                          </span>{" "}
                          Pattern from prior trials informs how to prepare
                          proposed instructions and special interrogatories.
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              padding: "11px 13px",
                              background: th.bg,
                              borderRadius: 7,
                              borderLeft: "3px solid #3b82f6",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: "#3b82f6",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 5,
                              }}
                            >
                              📚 MODEL APPROACH
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              {data.juryCharge.modelApproach}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "11px 13px",
                              background: th.bg,
                              borderRadius: 7,
                              borderLeft: "3px solid #f59e0b",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: "#f59e0b",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 5,
                              }}
                            >
                              ⏱ TIMELINE
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              {data.juryCharge.timeline}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "11px 13px",
                              background: th.bg,
                              borderRadius: 7,
                              borderLeft: "3px solid #10b981",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: "#10b981",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 5,
                              }}
                            >
                              🎯 SPECIAL INTERROGATORIES
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              {data.juryCharge.specialInterrogatories}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "11px 13px",
                              background: th.bg,
                              borderRadius: 7,
                              borderLeft: "3px solid #ef4444",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: "#ef4444",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 5,
                              }}
                            >
                              📜 HISTORICAL DISPUTES
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              {data.juryCharge.historicalDisputes}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "11px 13px",
                              background: "#a78bfa10",
                              borderRadius: 7,
                              border: "1px solid #a78bfa44",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: "#a78bfa",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                                marginBottom: 8,
                              }}
                            >
                              🔮 LIKELY CHARGE ISSUES IN THIS CASE
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                              }}
                            >
                              {data.juryCharge.likelyChargeIssues.map(
                                (iss, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      padding: "4px 0",
                                      lineHeight: 1.5,
                                      display: "flex",
                                      gap: 7,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#a78bfa",
                                        fontFamily: "monospace",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {i + 1}.
                                    </span>
                                    {iss}
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* LOCAL RULES */}
                    {sec === "rules" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#a78bfa10",
                            borderLeft: "2px solid #a78bfa",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                            What local rules and standing orders apply?
                          </span>{" "}
                          These exist in the docket but are buried. Surfaced
                          here as actionable requirements with their source
                          citations.
                        </div>
                        {data.localRules.map((r, i) => (
                          <div
                            key={i}
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              borderLeft: r.isCritical
                                ? `4px solid #ef4444`
                                : `2px solid ${th.border}`,
                              padding: "12px 14px",
                              marginBottom: 7,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 10,
                                marginBottom: 5,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 3,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: th.text,
                                    }}
                                  >
                                    {r.rule}
                                  </span>
                                  {r.isCritical && (
                                    <span
                                      style={{
                                        fontSize: 7,
                                        padding: "2px 7px",
                                        background: "#ef444418",
                                        color: "#ef4444",
                                        border: "1px solid #ef444444",
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.4,
                                      }}
                                    >
                                      ⚡ CRITICAL
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {r.detail}
                                </div>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: th.faint,
                                fontFamily: "monospace",
                                marginTop: 5,
                              }}
                            >
                              ↳ {r.ref}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "contraengine" &&
              (() => {
                const data =
                  CONTRADICTION_ENGINE_DATA[caseId] ||
                  CONTRADICTION_ENGINE_DATA.juul;
                const sec = contraSection;
                const sectionBtn = (k, emoji, label, count, color) => (
                  <button
                    key={k}
                    onClick={() => setContraSection(k)}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? color : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                    {count !== undefined && count > 0 && (
                      <span
                        style={{
                          fontSize: 8,
                          padding: "1px 6px",
                          background:
                            sec === k ? "rgba(255,255,255,0.25)" : color + "22",
                          color: sec === k ? "#fff" : color,
                          borderRadius: 8,
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
                const SEV_C = {
                  critical: "#ef4444",
                  high: "#f59e0b",
                  medium: "#3b82f6",
                  low: "#6b7280",
                };
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* Engine Status Header */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#7f1d1d,#991b1b)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 14,
                          right: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: data.scanStatus.running
                              ? "#10b981"
                              : "#6b7280",
                            boxShadow: data.scanStatus.running
                              ? "0 0 0 3px rgba(16,185,129,0.3)"
                              : "none",
                            animation: data.scanStatus.running
                              ? "pulse 1.5s infinite"
                              : "none",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#fef2f2",
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                          }}
                        >
                          {data.scanStatus.running
                            ? "ENGINE RUNNING"
                            : "PAUSED"}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 7,
                          color: "#fecaca",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        CONTRADICTION ENGINE · LIVE DETECTION
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 8px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Real-Time Record Analysis
                      </h1>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          flexWrap: "wrap",
                          fontSize: 9,
                          color: "#fecaca",
                          fontFamily: "monospace",
                        }}
                      >
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.scanStatus.entriesScanned.toLocaleString()}
                          </span>{" "}
                          entries scanned
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.scanStatus.docketsWatched}
                          </span>{" "}
                          docket{data.scanStatus.docketsWatched > 1 ? "s" : ""}{" "}
                          watched
                        </span>
                        <span>
                          <span style={{ color: "#fbbf24", fontWeight: 700 }}>
                            {data.scanStatus.detectedToday}
                          </span>{" "}
                          contradictions today
                        </span>
                        <span>
                          last scan:{" "}
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.scanStatus.lastScan}
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background:
                          contraPersp === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : contraPersp === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${contraPersp === "plaintiff" ? "#dc262655" : contraPersp === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: contraPersp === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: contraPersp === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {contraPersp === "plaintiff"
                            ? "🔴 Plaintiff Counsel View - contradictions favoring plaintiff"
                            : contraPersp === "defendant"
                              ? "🔵 Defendant Counsel View - contradictions favoring defendant"
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              contraPersp === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {contraPersp === "plaintiff"
                            ? "Live alerts framed for plaintiff use · cross-case opportunities for plaintiff · timeline gaps in defendant's record"
                            : contraPersp === "defendant"
                              ? "Live alerts framed for defendant use · cross-case opportunities for defendant · timeline gaps in plaintiff's record"
                              : "Toggle to filter contradictions by which side benefits"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setContraPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: contraPersp === v ? 700 : 500,
                              background:
                                contraPersp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                contraPersp === "all"
                                  ? contraPersp === v
                                    ? th.text
                                    : th.muted
                                  : contraPersp === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                contraPersp === v
                                  ? contraPersp === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${contraPersp === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Section selector */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn(
                        "live",
                        "⚡",
                        "Live Alerts",
                        data.liveAlerts.length,
                        "#ef4444",
                      )}
                      {sectionBtn(
                        "cross",
                        "🔀",
                        "Cross-Case",
                        data.crossCase.length,
                        "#a78bfa",
                      )}
                      {sectionBtn(
                        "timeline",
                        "📅",
                        "Timeline Inconsistencies",
                        data.timeline.length,
                        "#f59e0b",
                      )}
                    </div>

                    {/* LIVE ALERTS */}
                    {sec === "live" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#ef444410",
                            borderLeft: "2px solid #ef4444",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#ef4444", fontWeight: 700 }}>
                            ⚡ Live contradiction detection.
                          </span>{" "}
                          When a new docket entry is uploaded, the AI compares
                          it against the entire existing record and flags
                          contradictions within 60 seconds. Alerts shown newest
                          first.
                        </div>
                        {data.liveAlerts.length === 0 ? (
                          <div
                            style={{
                              padding: "30px",
                              textAlign: "center",
                              color: th.faint,
                              background: th.card,
                              borderRadius: 8,
                              border: `1px dashed ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              🔍
                            </div>
                            <div style={{ fontSize: 11, color: th.muted }}>
                              No live alerts · scanning continues
                            </div>
                          </div>
                        ) : (
                          data.liveAlerts.map((a) => {
                            const c = SEV_C[a.severity];
                            return (
                              <div
                                key={a.id}
                                style={{
                                  background: th.card,
                                  borderRadius: 9,
                                  border: `1px solid ${th.border}`,
                                  borderLeft: `4px solid ${c}`,
                                  padding: "14px 16px",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 10,
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        flexWrap: "wrap",
                                        marginBottom: 4,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: c + "22",
                                          color: c,
                                          border: `1px solid ${c}55`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {a.severity}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: th.bg,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontWeight: 600,
                                          fontFamily: "monospace",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {a.contradictionType}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 8,
                                          color: th.faint,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        · detected {a.time}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.dim,
                                        fontFamily: "monospace",
                                        fontWeight: 700,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {a.source}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {a.sourceDesc}
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: th.text,
                                    marginBottom: 9,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {a.summary}
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                    marginBottom: 9,
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "9px 11px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: `1px solid ${th.border}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: c,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      ↪ NEW FILING
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: th.dim,
                                        fontWeight: 700,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {a.recordA.ref}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      "{a.recordA.text}"
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      padding: "9px 11px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: `1px solid ${th.border}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: c,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      ↩ EXISTING RECORD
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: th.dim,
                                        fontWeight: 700,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {a.recordB.ref}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      "{a.recordB.text}"
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: c + "10",
                                    borderLeft: `3px solid ${c}`,
                                    borderRadius: "0 5px 5px 0",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: c,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    → RECOMMENDED ACTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                      marginBottom: 3,
                                    }}
                                  >
                                    {a.action}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    impact:{" "}
                                    <span
                                      style={{
                                        color: c,
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {a.impact}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* CROSS-CASE */}
                    {sec === "cross" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#a78bfa10",
                            borderLeft: "2px solid #a78bfa",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#a78bfa", fontWeight: 700 }}>
                            🔀 Cross-case contradiction detection.
                          </span>{" "}
                          The same defendants are fighting multiple proceedings
                          simultaneously. Positions taken in one can contradict
                          positions in the other. The engine watches both
                          dockets and flags inconsistencies.
                        </div>
                        {data.crossCase.length === 0 ? (
                          <div
                            style={{
                              padding: "30px",
                              textAlign: "center",
                              color: th.faint,
                              background: th.card,
                              borderRadius: 8,
                              border: `1px dashed ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              🔀
                            </div>
                            <div style={{ fontSize: 11, color: th.muted }}>
                              No cross-case watchlist for this matter
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: th.faint,
                                marginTop: 5,
                              }}
                            >
                              Cross-case detection activates when a party is
                              litigating in multiple proceedings simultaneously
                            </div>
                          </div>
                        ) : (
                          data.crossCase.map((c) => {
                            const sc = SEV_C[c.severity];
                            return (
                              <div
                                key={c.id}
                                style={{
                                  background: th.card,
                                  borderRadius: 9,
                                  border: `1px solid ${th.border}`,
                                  borderLeft: `4px solid ${sc}`,
                                  padding: "14px 16px",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    flexWrap: "wrap",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: sc + "22",
                                      color: sc,
                                      border: `1px solid ${sc}55`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {c.severity}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: "#a78bfa18",
                                      color: "#a78bfa",
                                      border: "1px solid #a78bfa44",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    CROSS-CASE
                                  </span>
                                  {c.docketsConflicting &&
                                    c.docketsConflicting.map((d, i) => (
                                      <span
                                        key={i}
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: th.bg,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {d}
                                      </span>
                                    ))}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: th.text,
                                    marginBottom: 9,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {c.summary}
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                    marginBottom: 9,
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "9px 11px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: "1px solid #ef444433",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#ef4444",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      📂 CASE A POSITION
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: th.dim,
                                        fontWeight: 700,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {c.caseA.ref}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      "{c.caseA.text}"
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      padding: "9px 11px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: "1px solid #3b82f633",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#3b82f6",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      📂 CASE B POSITION
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: th.dim,
                                        fontWeight: 700,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {c.caseB.ref}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      "{c.caseB.text}"
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: "#a78bfa10",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    🧠 ANALYSIS
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {c.analysis}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: sc + "10",
                                    borderLeft: `3px solid ${sc}`,
                                    borderRadius: "0 5px 5px 0",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: sc,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    → ACTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {c.action}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* TIMELINE */}
                    {sec === "timeline" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 10,
                            padding: "8px 12px",
                            background: "#f59e0b10",
                            borderLeft: "2px solid #f59e0b",
                            borderRadius: "0 4px 4px 0",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                            📅 Timeline contradiction detection.
                          </span>{" "}
                          Compares dates - when a party claims something
                          happened before something else but the record shows
                          the reverse. Sequence inconsistencies create both
                          procedural and substantive opportunities.
                        </div>
                        {data.timeline.length === 0 ? (
                          <div
                            style={{
                              padding: "30px",
                              textAlign: "center",
                              color: th.faint,
                              background: th.card,
                              borderRadius: 8,
                              border: `1px dashed ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              📅
                            </div>
                            <div style={{ fontSize: 11, color: th.muted }}>
                              No timeline inconsistencies detected
                            </div>
                          </div>
                        ) : (
                          data.timeline.map((t) => {
                            const sc = SEV_C[t.severity];
                            return (
                              <div
                                key={t.id}
                                style={{
                                  background: th.card,
                                  borderRadius: 9,
                                  border: `1px solid ${th.border}`,
                                  borderLeft: `4px solid ${sc}`,
                                  padding: "14px 16px",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    flexWrap: "wrap",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: sc + "22",
                                      color: sc,
                                      border: `1px solid ${sc}55`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {t.severity}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: "#f59e0b18",
                                      color: "#f59e0b",
                                      border: "1px solid #f59e0b44",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    📅 TIMELINE
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: th.text,
                                    marginBottom: 11,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {t.summary}
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                    marginBottom: 9,
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: "1px solid #ef444433",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#ef4444",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 6,
                                      }}
                                    >
                                      ❌ CLAIMED SEQUENCE
                                    </div>
                                    {t.claimedSequence.map((s, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          gap: 8,
                                          padding: "3px 0",
                                          fontSize: 9,
                                          alignItems: "baseline",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            minWidth: 42,
                                          }}
                                        >
                                          {i + 1}.
                                        </span>
                                        <div style={{ flex: 1 }}>
                                          <span
                                            style={{
                                              color: th.dim,
                                              fontFamily: "monospace",
                                              fontWeight: 700,
                                              fontSize: 8,
                                            }}
                                          >
                                            {s.date}
                                          </span>
                                          <div
                                            style={{
                                              color: th.muted,
                                              marginTop: 1,
                                            }}
                                          >
                                            {s.event}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderRadius: 6,
                                      border: "1px solid #10b98133",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 6,
                                      }}
                                    >
                                      ✓ ACTUAL RECORD
                                    </div>
                                    {t.actualSequence.map((s, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          gap: 8,
                                          padding: "3px 0",
                                          fontSize: 9,
                                          alignItems: "baseline",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: "#10b981",
                                            fontWeight: 700,
                                            minWidth: 42,
                                          }}
                                        >
                                          {i + 1}.
                                        </span>
                                        <div style={{ flex: 1 }}>
                                          <span
                                            style={{
                                              color: th.dim,
                                              fontFamily: "monospace",
                                              fontWeight: 700,
                                              fontSize: 8,
                                            }}
                                          >
                                            {s.date}
                                          </span>
                                          <div
                                            style={{
                                              color: th.muted,
                                              marginTop: 1,
                                            }}
                                          >
                                            {s.event}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: "#a78bfa10",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    🧠 ANALYSIS
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {t.analysis}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: sc + "10",
                                    borderLeft: `3px solid ${sc}`,
                                    borderRadius: "0 5px 5px 0",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 8,
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: sc,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 4,
                                        }}
                                      >
                                        → ACTION
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {t.action}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.faint,
                                        fontFamily: "monospace",
                                        fontStyle: "italic",
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        paddingTop: 2,
                                      }}
                                    >
                                      cite: {t.cite}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "jury" &&
              (() => {
                const data = JURY_INTEL_DATA[caseId] || JURY_INTEL_DATA.juul;
                const sec = jurySection;
                const persp = juryPersp;
                const sectionBtn = (k, emoji, label, disabled) => (
                  <button
                    key={k}
                    onClick={() => {
                      if (!disabled) setJurySection(k);
                    }}
                    disabled={disabled}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#dc2626" : "transparent",
                      color:
                        sec === k ? "#fff" : disabled ? th.faint : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: disabled ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                      opacity: disabled ? 0.4 : 1,
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
                const isActive = persp !== "all";
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : dark
                        ? "linear-gradient(135deg,#0f172a,#1e293b)"
                        : "linear-gradient(135deg,#f1f5f9,#e2e8f0)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : th.border;
                const perspTitleColor = isActive ? "#fff" : th.text;
                const perspSubColor = isActive
                  ? "rgba(255,255,255,0.85)"
                  : th.muted;
                const archByLean = (a) =>
                  persp === "all"
                    ? true
                    : persp === "plaintiff"
                      ? a.lean === "plaintiff" ||
                        a.lean === "plaintiff-leaning" ||
                        a.lean === "mixed" ||
                        a.lean === "unclear"
                      : a.lean === "defense" ||
                        a.lean === "defense-leaning" ||
                        a.lean === "mixed" ||
                        a.lean === "unclear";
                const themeByPersp = (t) =>
                  persp === "all" ? true : t.sponsor === persp;
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#7f1d1d,#991b1b)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 14,
                          right: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: data.jurySeated ? "#10b981" : "#f59e0b",
                            boxShadow: data.jurySeated
                              ? "0 0 0 3px rgba(16,185,129,0.3)"
                              : "0 0 0 3px rgba(245,158,11,0.3)",
                            animation: data.jurySeated
                              ? "pulse 1.5s infinite"
                              : "none",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#fef2f2",
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                          }}
                        >
                          {data.jurySeated ? "JURY SEATED" : "PRE-TRIAL"}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 7,
                          color: "#fecaca",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        JURY INTELLIGENCE ·{" "}
                        {data.jurySeated ? "LIVE TRIAL" : "PREP MODE"}
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 8px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {data.caseTitle}
                      </h1>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          flexWrap: "wrap",
                          fontSize: 9,
                          color: "#fecaca",
                          fontFamily: "monospace",
                        }}
                      >
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.trialDate}
                          </span>
                        </span>
                        <span>
                          panel size:{" "}
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.panelSize || "TBD"}
                          </span>
                        </span>
                        <span>
                          voir dire bank:{" "}
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.voirDire.length}
                          </span>{" "}
                          categories
                        </span>
                        <span>
                          archetypes:{" "}
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.archetypes.length}
                          </span>
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#fecaca",
                          lineHeight: 1.5,
                          marginTop: 10,
                          fontStyle: "italic",
                        }}
                      >
                        {data.theory}
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: perspTitleColor,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 Plaintiff Counsel View - pick the panel that finds for plaintiff"
                            : persp === "defendant"
                              ? "🔵 Defendant Counsel View - pick the panel that finds for defendant"
                              : "All Jury Intelligence"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: perspSubColor,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Strike strategy, voir dire signals, theme calibration from plaintiff's side"
                            : persp === "defendant"
                              ? "Strike strategy, voir dire signals, theme calibration from defendant's side"
                              : "Toggle to filter archetype strikes, theme landings, and voir dire signals by side"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setJuryPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SECTION SELECTOR */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn("voir", "🗣️", "Voir Dire Bank", false)}
                      {sectionBtn("archetype", "👥", "Juror Archetypes", false)}
                    </div>

                    {/* VOIR DIRE BANK */}
                    {sec === "voir" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 12,
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #dc2626",
                            borderRadius: "0 6px 6px 0",
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              color: "#dc2626",
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          >
                            🗣️ Case-specific voir dire questions.
                          </span>{" "}
                          Built from this case's theory, not a generic template.
                          Each question shows the rationale, plus how plaintiff
                          and defendant counsel should read each likely answer.
                          Toggle the perspective banner to focus on one side's
                          signal interpretation.
                        </div>
                        {data.voirDire.map((cat) => {
                          const wColor =
                            cat.weight === "critical"
                              ? "#ef4444"
                              : cat.weight === "high"
                                ? "#f59e0b"
                                : "#3b82f6";
                          return (
                            <div
                              key={cat.id}
                              style={{
                                background: th.card,
                                borderRadius: 9,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${wColor}`,
                                padding: "14px 16px",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 10,
                                  paddingBottom: 8,
                                  borderBottom: `1px solid ${th.border}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: th.text,
                                    letterSpacing: "-0.2px",
                                  }}
                                >
                                  {cat.category}
                                </span>
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 7px",
                                    background: wColor + "22",
                                    color: wColor,
                                    border: `1px solid ${wColor}55`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {cat.weight}
                                </span>
                                <span
                                  style={{
                                    fontSize: 8,
                                    color: th.faint,
                                    fontFamily: "monospace",
                                    marginLeft: "auto",
                                  }}
                                >
                                  {cat.questions.length} question
                                  {cat.questions.length > 1 ? "s" : ""}
                                </span>
                              </div>
                              {cat.questions.map((q, qi) => (
                                <div
                                  key={qi}
                                  style={{
                                    padding: "10px 0",
                                    borderBottom:
                                      qi < cat.questions.length - 1
                                        ? `1px dashed ${th.border}`
                                        : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: th.text,
                                      marginBottom: 6,
                                      lineHeight: 1.5,
                                      paddingLeft: 8,
                                      borderLeft: `2px solid ${wColor}66`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: wColor,
                                        fontWeight: 700,
                                        marginRight: 6,
                                        fontSize: 9,
                                      }}
                                    >
                                      Q{qi + 1}.
                                    </span>
                                    "{q.q}"
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      marginBottom: 6,
                                      paddingLeft: 14,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {q.rationale}
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        persp === "all" ? "1fr 1fr" : "1fr",
                                      gap: 8,
                                      paddingLeft: 14,
                                    }}
                                  >
                                    {(persp === "all" ||
                                      persp === "plaintiff") && (
                                      <div
                                        style={{
                                          padding: "8px 11px",
                                          background: "#ef444410",
                                          borderLeft: "3px solid #ef4444",
                                          borderRadius: "0 5px 5px 0",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          🔴 PLAINTIFF SIGNAL
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 9,
                                            color: th.text,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {q.plaintiffSignal}
                                        </div>
                                      </div>
                                    )}
                                    {(persp === "all" ||
                                      persp === "defendant") && (
                                      <div
                                        style={{
                                          padding: "8px 11px",
                                          background: "#3b82f610",
                                          borderLeft: "3px solid #3b82f6",
                                          borderRadius: "0 5px 5px 0",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#3b82f6",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          🔵 DEFENDANT SIGNAL
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 9,
                                            color: th.text,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {q.defendantSignal}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ARCHETYPES */}
                    {sec === "archetype" && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            marginBottom: 12,
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #dc2626",
                            borderRadius: "0 6px 6px 0",
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              color: "#dc2626",
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          >
                            👥 Juror archetypes for this case type.
                          </span>{" "}
                          Each archetype has its own strike strategy depending
                          on which side you represent. Filter by perspective to
                          see your strike calculus first; resonate/alienate
                          themes for opening and closing.
                        </div>
                        {data.archetypes.filter(archByLean).map((a) => {
                          const lc = a.leanColor;
                          return (
                            <div
                              key={a.id}
                              style={{
                                background: th.card,
                                borderRadius: 10,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${lc}`,
                                padding: "14px 16px",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 12,
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 32,
                                    lineHeight: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  {a.icon}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      flexWrap: "wrap",
                                      marginBottom: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {a.label}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 7,
                                        padding: "2px 7px",
                                        background: lc + "22",
                                        color: lc,
                                        border: `1px solid ${lc}55`,
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.4,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {a.lean.replace("-", " ")}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {a.description}
                                  </div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    persp === "all" ? "1fr 1fr" : "1fr",
                                  gap: 8,
                                  marginBottom: 10,
                                }}
                              >
                                {(persp === "all" || persp === "plaintiff") && (
                                  <div
                                    style={{
                                      padding: "9px 12px",
                                      background: "#ef444410",
                                      borderLeft: "3px solid #ef4444",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#ef4444",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      🔴 PLAINTIFF STRIKE STRATEGY
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {a.plaintiffStrike}
                                    </div>
                                  </div>
                                )}
                                {(persp === "all" || persp === "defendant") && (
                                  <div
                                    style={{
                                      padding: "9px 12px",
                                      background: "#3b82f610",
                                      borderLeft: "3px solid #3b82f6",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#3b82f6",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      🔵 DEFENDANT STRIKE STRATEGY
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {a.defendantStrike}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  padding: "8px 11px",
                                  background: dark ? "#0f172a" : "#fafafa",
                                  borderRadius: 5,
                                  border: `1px solid ${th.border}`,
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: th.faint,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    marginBottom: 4,
                                  }}
                                >
                                  🔍 IDENTIFY VIA VOIR DIRE
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {a.identifyVoirDire.map((vid) => {
                                    const cat = data.voirDire.find(
                                      (c) => c.id === vid,
                                    );
                                    return (
                                      cat && (
                                        <span
                                          key={vid}
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 7px",
                                            background: th.card,
                                            color: th.dim,
                                            border: `1px solid ${th.border}`,
                                            borderRadius: 8,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {cat.category}
                                        </span>
                                      )
                                    );
                                  })}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    persp === "all" ? "1fr 1fr" : "1fr",
                                  gap: 6,
                                }}
                              >
                                {(persp === "all" || persp === "plaintiff") && (
                                  <>
                                    <div
                                      style={{
                                        padding: "7px 10px",
                                        background: "#10b98110",
                                        borderLeft: "2px solid #10b981",
                                        borderRadius: "0 4px 4px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        {persp === "all"
                                          ? "🔴 PLAINTIFF · "
                                          : ""}
                                        ✓ RESONATES
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {a.plaintiffResonate}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "7px 10px",
                                        background: "#ef444410",
                                        borderLeft: "2px solid #ef4444",
                                        borderRadius: "0 4px 4px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#ef4444",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        {persp === "all"
                                          ? "🔴 PLAINTIFF · "
                                          : ""}
                                        ✗ ALIENATES
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {a.plaintiffAlienate}
                                      </div>
                                    </div>
                                  </>
                                )}
                                {(persp === "all" || persp === "defendant") && (
                                  <>
                                    <div
                                      style={{
                                        padding: "7px 10px",
                                        background: "#10b98110",
                                        borderLeft: "2px solid #10b981",
                                        borderRadius: "0 4px 4px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        {persp === "all"
                                          ? "🔵 DEFENDANT · "
                                          : ""}
                                        ✓ RESONATES
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {a.defendantResonate}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "7px 10px",
                                        background: "#ef444410",
                                        borderLeft: "2px solid #ef4444",
                                        borderRadius: "0 4px 4px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#ef4444",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        {persp === "all"
                                          ? "🔵 DEFENDANT · "
                                          : ""}
                                        ✗ ALIENATES
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {a.defendantAlienate}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {data.archetypes.filter(archByLean).length === 0 && (
                          <div
                            style={{
                              padding: "30px",
                              textAlign: "center",
                              color: th.faint,
                              background: th.card,
                              borderRadius: 8,
                              border: `1px dashed ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              👥
                            </div>
                            <div style={{ fontSize: 11, color: th.muted }}>
                              No archetypes match this perspective filter
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "experts" &&
              (() => {
                const data =
                  EXPERT_BATTLEFIELD_DATA[caseId] ||
                  EXPERT_BATTLEFIELD_DATA.juul;
                const persp = expertsPersp;
                const isActive = persp !== "all";
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : dark
                        ? "linear-gradient(135deg,#0f172a,#1e293b)"
                        : "linear-gradient(135deg,#f1f5f9,#e2e8f0)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : th.border;
                const DAUBERT_LABEL = {
                  survived: "✓ Survived Daubert",
                  partial: "⚠️ Partial Exclusion",
                  pending: "⏱️ Pending Ruling",
                  supported: "✓ Supported (no challenge)",
                  filed: "📋 Filed (no challenge)",
                  unretained: "⚠️ Not Retained",
                };
                const LEV_LABEL = {
                  balanced: "BALANCED",
                  plaintiff: "PLAINTIFF FAVORED",
                  "plaintiff-strong": "PLAINTIFF STRONG",
                  defendant: "DEFENDANT FAVORED",
                  "defendant-strong": "DEFENDANT STRONG",
                  open: "OPEN - Daubert pending",
                };
                const LEV_COLOR = {
                  balanced: "#a78bfa",
                  plaintiff: "#ef4444",
                  "plaintiff-strong": "#dc2626",
                  defendant: "#3b82f6",
                  "defendant-strong": "#1e3a8a",
                  open: "#f59e0b",
                };
                const toggleExp = (issueId, side) => {
                  const k = issueId + ":" + side;
                  setExpertsExpanded((p) => ({ ...p, [k]: !p[k] }));
                };
                const isExp = (issueId, side) =>
                  !!expertsExpanded[issueId + ":" + side];
                return (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 14,
                          right: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background:
                              data.status === "trial-in-progress"
                                ? "#fbbf24"
                                : data.status === "markman-pending"
                                  ? "#f59e0b"
                                  : "#94a3b8",
                            boxShadow: `0 0 0 3px rgba(${data.status === "trial-in-progress" ? "251,191,36" : data.status === "markman-pending" ? "245,158,11" : "148,163,184"},0.3)`,
                            animation:
                              data.status === "trial-in-progress"
                                ? "pulse 1.5s infinite"
                                : "none",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#ecfdf5",
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                          }}
                        >
                          {data.statusLabel}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 7,
                          color: "#d1fae5",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        EXPERT BATTLEFIELD · HEAD-TO-HEAD ANALYSIS
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 8px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {data.caseTitle}
                      </h1>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          flexWrap: "wrap",
                          fontSize: 9,
                          color: "#d1fae5",
                          fontFamily: "monospace",
                        }}
                      >
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.issues.length}
                          </span>{" "}
                          contested issue{data.issues.length > 1 ? "s" : ""}
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {
                              data.issues.filter(
                                (i) =>
                                  i.plaintiffExpert.daubertStatus ===
                                    "survived" ||
                                  i.plaintiffExpert.daubertStatus ===
                                    "partial" ||
                                  i.plaintiffExpert.daubertStatus ===
                                    "supported",
                              ).length
                            }
                          </span>{" "}
                          plaintiff experts active
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {
                              data.issues.filter(
                                (i) =>
                                  i.defendantExpert.daubertStatus ===
                                    "survived" ||
                                  i.defendantExpert.daubertStatus ===
                                    "supported" ||
                                  i.defendantExpert.daubertStatus === "filed",
                              ).length
                            }
                          </span>{" "}
                          defendant experts active
                        </span>
                        {data.issues.filter(
                          (i) =>
                            i.plaintiffExpert.daubertStatus === "unretained" ||
                            i.plaintiffExpert.daubertStatus === "pending",
                        ).length > 0 && (
                          <span style={{ color: "#fef3c7", fontWeight: 700 }}>
                            ⚠️{" "}
                            {
                              data.issues.filter(
                                (i) =>
                                  i.plaintiffExpert.daubertStatus ===
                                  "unretained",
                              ).length
                            }{" "}
                            unretained ·{" "}
                            {
                              data.issues.filter(
                                (i) =>
                                  i.plaintiffExpert.daubertStatus === "pending",
                              ).length
                            }{" "}
                            pending
                          </span>
                        )}
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#fff" : th.text,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 Plaintiff Counsel View - your experts vs theirs"
                            : persp === "defendant"
                              ? "🔵 Defendant Counsel View - your experts vs theirs"
                              : "All Experts · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: isActive
                              ? "rgba(255,255,255,0.85)"
                              : th.muted,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Your expert vulnerabilities · prep for cross · plan redirect · their cross-angles to defend against"
                            : persp === "defendant"
                              ? "Your expert vulnerabilities · prep for cross · plan redirect · their cross-angles to defend against"
                              : "Toggle to see one side's expert vulnerabilities and prep prominently"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setExpertsPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* COUNTDOWN / GAP NOTE */}
                    {data.countdown && (
                      <div
                        style={{
                          background: "#f59e0b10",
                          border: "2px solid #f59e0b66",
                          borderRadius: 10,
                          padding: "12px 16px",
                          marginBottom: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                        }}
                      >
                        <div
                          style={{
                            flexShrink: 0,
                            padding: "6px 12px",
                            background: "#f59e0b15",
                            borderRadius: 6,
                            border: "1px solid #f59e0b44",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 800,
                              fontFamily: "monospace",
                              color: "#f59e0b",
                              lineHeight: 1,
                            }}
                          >
                            {data.countdown.days}d
                          </div>
                          <div
                            style={{
                              fontSize: 7,
                              color: th.faint,
                              fontFamily: "monospace",
                              letterSpacing: 0.5,
                              marginTop: 2,
                            }}
                          >
                            UNTIL DEADLINE
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#f59e0b",
                              marginBottom: 3,
                            }}
                          >
                            ⚠️ {data.countdown.event} · {data.countdown.date}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.55,
                            }}
                          >
                            {data.gapNote}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ISSUE MATRIX */}
                    {data.issues.map((issue, ii) => {
                      const lev = issue.battlegroundScore.leverage;
                      const totalStr =
                        issue.battlegroundScore.plaintiffStrength +
                        issue.battlegroundScore.defendantStrength;
                      const pPct =
                        totalStr > 0
                          ? Math.round(
                              (issue.battlegroundScore.plaintiffStrength /
                                totalStr) *
                                100,
                            )
                          : 50;
                      const dPct = 100 - pPct;
                      const showPlaintiff =
                        persp === "all" || persp === "plaintiff";
                      const showDefendant =
                        persp === "all" || persp === "defendant";
                      const ePlaintiff = issue.plaintiffExpert;
                      const eDefendant = issue.defendantExpert;
                      return (
                        <div
                          key={issue.id}
                          style={{
                            background: th.card,
                            borderRadius: 11,
                            border: `1px solid ${th.border}`,
                            marginBottom: 14,
                            overflow: "hidden",
                          }}
                        >
                          {/* Issue header */}
                          <div
                            style={{
                              padding: "14px 18px",
                              background: dark ? "#0f172a" : "#fafafa",
                              borderBottom: `1px solid ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                                marginBottom: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 7,
                                  padding: "3px 9px",
                                  background: issue.importanceColor + "22",
                                  color: issue.importanceColor,
                                  border: `1px solid ${issue.importanceColor}55`,
                                  borderRadius: 8,
                                  fontWeight: 700,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.4,
                                  textTransform: "uppercase",
                                }}
                              >
                                {issue.importance}
                              </span>
                              <span
                                style={{
                                  fontSize: 7,
                                  padding: "3px 9px",
                                  background: LEV_COLOR[lev] + "22",
                                  color: LEV_COLOR[lev],
                                  border: `1px solid ${LEV_COLOR[lev]}55`,
                                  borderRadius: 8,
                                  fontWeight: 700,
                                  fontFamily: "monospace",
                                  letterSpacing: 0.4,
                                }}
                              >
                                {LEV_LABEL[lev]}
                              </span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: th.text,
                                  letterSpacing: "-0.2px",
                                }}
                              >
                                {issue.label}
                              </span>
                            </div>
                            {/* Battleground bar */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 8,
                                  color: "#ef4444",
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  minWidth: 30,
                                  textAlign: "right",
                                }}
                              >
                                P {issue.battlegroundScore.plaintiffStrength}
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  overflow: "hidden",
                                  display: "flex",
                                  border: `1px solid ${th.border}`,
                                }}
                              >
                                <div
                                  style={{
                                    width: pPct + "%",
                                    background:
                                      "linear-gradient(to right,#7f1d1d,#ef4444)",
                                    transition: "width 0.3s",
                                  }}
                                />
                                <div
                                  style={{
                                    width: dPct + "%",
                                    background:
                                      "linear-gradient(to left,#1e3a8a,#3b82f6)",
                                    transition: "width 0.3s",
                                  }}
                                />
                              </div>
                              <span
                                style={{
                                  fontSize: 8,
                                  color: "#3b82f6",
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  minWidth: 30,
                                }}
                              >
                                D {issue.battlegroundScore.defendantStrength}
                              </span>
                            </div>
                          </div>

                          {/* Two-column expert matrix */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                showPlaintiff && showDefendant
                                  ? "1fr 1fr"
                                  : "1fr",
                              gap: 0,
                            }}
                          >
                            {showPlaintiff &&
                              (() => {
                                const e = ePlaintiff;
                                const dC = e.daubertColor;
                                const expanded = isExp(issue.id, "plaintiff");
                                return (
                                  <div
                                    style={{
                                      padding: "14px 16px",
                                      borderRight: showDefendant
                                        ? `1px solid ${th.border}`
                                        : "none",
                                      borderBottom: `1px solid ${th.border}`,
                                      background:
                                        isActive && persp === "plaintiff"
                                          ? dark
                                            ? "rgba(239,68,68,0.05)"
                                            : "rgba(239,68,68,0.03)"
                                          : "transparent",
                                      opacity:
                                        isActive && persp === "defendant"
                                          ? 0.45
                                          : 1,
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: "#ef444418",
                                          color: "#ef4444",
                                          border: "1px solid #ef444444",
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        🔴 PLAINTIFF EXPERT
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 2,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {e.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontStyle: "italic",
                                        marginBottom: 8,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {e.credentials}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        lineHeight: 1.6,
                                        marginBottom: 9,
                                        paddingLeft: 8,
                                        borderLeft: "2px solid #ef444466",
                                      }}
                                    >
                                      {e.coreOpinion}
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 8,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 8px",
                                          background: dC + "18",
                                          color: dC,
                                          border: `1px solid ${dC}44`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        {DAUBERT_LABEL[e.daubertStatus]}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 8px",
                                          background:
                                            e.vulnerabilityScore <= 4
                                              ? "#10b98118"
                                              : e.vulnerabilityScore <= 6
                                                ? "#f59e0b18"
                                                : "#ef444418",
                                          color:
                                            e.vulnerabilityScore <= 4
                                              ? "#10b981"
                                              : e.vulnerabilityScore <= 6
                                                ? "#f59e0b"
                                                : "#ef4444",
                                          border: `1px solid ${e.vulnerabilityScore <= 4 ? "#10b98144" : e.vulnerabilityScore <= 6 ? "#f59e0b44" : "#ef444444"}`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        VULNERABILITY {e.vulnerabilityScore}/10
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.dim,
                                        fontFamily: "monospace",
                                        lineHeight: 1.4,
                                        marginBottom: 8,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {e.daubertRuling}
                                    </div>
                                    <button
                                      onClick={() =>
                                        toggleExp(issue.id, "plaintiff")
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "6px 10px",
                                        fontSize: 9,
                                        background: expanded
                                          ? "#ef444418"
                                          : "transparent",
                                        color: expanded ? "#ef4444" : th.muted,
                                        border: `1px solid ${expanded ? "#ef444444" : th.border}`,
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {expanded ? "▼ Hide" : "▶ Show"}{" "}
                                      cross-angles & redirect prep
                                    </button>
                                    {expanded && (
                                      <div
                                        style={{
                                          marginTop: 10,
                                          padding: "10px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderRadius: 6,
                                          border: `1px solid ${th.border}`,
                                        }}
                                      >
                                        {e.expanded.admitted &&
                                          e.expanded.admitted.length > 0 && (
                                            <div style={{ marginBottom: 9 }}>
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  color: "#10b981",
                                                  fontWeight: 700,
                                                  letterSpacing: 0.5,
                                                  fontFamily: "monospace",
                                                  marginBottom: 5,
                                                }}
                                              >
                                                ✓ ADMITTED OPINIONS
                                              </div>
                                              {e.expanded.admitted.map(
                                                (a, ai) => (
                                                  <div
                                                    key={ai}
                                                    style={{
                                                      fontSize: 9,
                                                      color: th.text,
                                                      padding: "3px 0 3px 10px",
                                                      borderLeft:
                                                        "2px solid #10b98166",
                                                      marginBottom: 3,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {a}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        {e.expanded.excluded &&
                                          e.expanded.excluded.length > 0 && (
                                            <div style={{ marginBottom: 9 }}>
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  color: "#ef4444",
                                                  fontWeight: 700,
                                                  letterSpacing: 0.5,
                                                  fontFamily: "monospace",
                                                  marginBottom: 5,
                                                }}
                                              >
                                                ✗ EXCLUDED OPINIONS
                                              </div>
                                              {e.expanded.excluded.map(
                                                (x, xi) => (
                                                  <div
                                                    key={xi}
                                                    style={{
                                                      fontSize: 9,
                                                      color: th.text,
                                                      padding: "3px 0 3px 10px",
                                                      borderLeft:
                                                        "2px solid #ef444466",
                                                      marginBottom: 3,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {x}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        {e.expanded.ruling && (
                                          <div
                                            style={{
                                              marginBottom: 9,
                                              padding: "7px 10px",
                                              background: dark
                                                ? "#1e293b"
                                                : "#fff",
                                              borderRadius: 5,
                                              border: `1px dashed ${th.border}`,
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                color: th.faint,
                                                fontWeight: 700,
                                                letterSpacing: 0.5,
                                                fontFamily: "monospace",
                                                marginBottom: 4,
                                              }}
                                            >
                                              ⚖️ DAUBERT RULING
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              {e.expanded.ruling}
                                            </div>
                                          </div>
                                        )}
                                        <div style={{ marginBottom: 9 }}>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              color: "#3b82f6",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              fontFamily: "monospace",
                                              marginBottom: 5,
                                            }}
                                          >
                                            🔵 DEFENSE CROSS-EXAM ANGLES
                                          </div>
                                          {e.expanded.crossAngles.map(
                                            (c, ci) => (
                                              <div
                                                key={ci}
                                                style={{
                                                  fontSize: 9,
                                                  color: th.text,
                                                  padding: "4px 0 4px 12px",
                                                  borderLeft:
                                                    "2px solid #3b82f666",
                                                  marginBottom: 4,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {c}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                        <div>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              color: "#10b981",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              fontFamily: "monospace",
                                              marginBottom: 5,
                                            }}
                                          >
                                            ↩️ REDIRECT PREP
                                          </div>
                                          {e.expanded.redirectPrep.map(
                                            (r, ri) => (
                                              <div
                                                key={ri}
                                                style={{
                                                  fontSize: 9,
                                                  color: th.text,
                                                  padding: "4px 0 4px 12px",
                                                  borderLeft:
                                                    "2px solid #10b98166",
                                                  marginBottom: 4,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {r}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            {showDefendant &&
                              (() => {
                                const e = eDefendant;
                                const dC = e.daubertColor;
                                const expanded = isExp(issue.id, "defendant");
                                return (
                                  <div
                                    style={{
                                      padding: "14px 16px",
                                      borderBottom: `1px solid ${th.border}`,
                                      background:
                                        isActive && persp === "defendant"
                                          ? dark
                                            ? "rgba(59,130,246,0.05)"
                                            : "rgba(59,130,246,0.03)"
                                          : "transparent",
                                      opacity:
                                        isActive && persp === "plaintiff"
                                          ? 0.45
                                          : 1,
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: "#3b82f618",
                                          color: "#3b82f6",
                                          border: "1px solid #3b82f644",
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        🔵 DEFENDANT EXPERT
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 2,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {e.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontStyle: "italic",
                                        marginBottom: 8,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {e.credentials}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.text,
                                        lineHeight: 1.6,
                                        marginBottom: 9,
                                        paddingLeft: 8,
                                        borderLeft: "2px solid #3b82f666",
                                      }}
                                    >
                                      {e.coreOpinion}
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 8,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 8px",
                                          background: dC + "18",
                                          color: dC,
                                          border: `1px solid ${dC}44`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        {DAUBERT_LABEL[e.daubertStatus]}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 8px",
                                          background:
                                            e.vulnerabilityScore <= 4
                                              ? "#10b98118"
                                              : e.vulnerabilityScore <= 6
                                                ? "#f59e0b18"
                                                : "#ef444418",
                                          color:
                                            e.vulnerabilityScore <= 4
                                              ? "#10b981"
                                              : e.vulnerabilityScore <= 6
                                                ? "#f59e0b"
                                                : "#ef4444",
                                          border: `1px solid ${e.vulnerabilityScore <= 4 ? "#10b98144" : e.vulnerabilityScore <= 6 ? "#f59e0b44" : "#ef444444"}`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        VULNERABILITY {e.vulnerabilityScore}/10
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.dim,
                                        fontFamily: "monospace",
                                        lineHeight: 1.4,
                                        marginBottom: 8,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {e.daubertRuling}
                                    </div>
                                    <button
                                      onClick={() =>
                                        toggleExp(issue.id, "defendant")
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "6px 10px",
                                        fontSize: 9,
                                        background: expanded
                                          ? "#3b82f618"
                                          : "transparent",
                                        color: expanded ? "#3b82f6" : th.muted,
                                        border: `1px solid ${expanded ? "#3b82f644" : th.border}`,
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {expanded ? "▼ Hide" : "▶ Show"}{" "}
                                      cross-angles & redirect prep
                                    </button>
                                    {expanded && (
                                      <div
                                        style={{
                                          marginTop: 10,
                                          padding: "10px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderRadius: 6,
                                          border: `1px solid ${th.border}`,
                                        }}
                                      >
                                        {e.expanded.admitted &&
                                          e.expanded.admitted.length > 0 && (
                                            <div style={{ marginBottom: 9 }}>
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  color: "#10b981",
                                                  fontWeight: 700,
                                                  letterSpacing: 0.5,
                                                  fontFamily: "monospace",
                                                  marginBottom: 5,
                                                }}
                                              >
                                                ✓ ADMITTED OPINIONS
                                              </div>
                                              {e.expanded.admitted.map(
                                                (a, ai) => (
                                                  <div
                                                    key={ai}
                                                    style={{
                                                      fontSize: 9,
                                                      color: th.text,
                                                      padding: "3px 0 3px 10px",
                                                      borderLeft:
                                                        "2px solid #10b98166",
                                                      marginBottom: 3,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {a}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        {e.expanded.excluded &&
                                          e.expanded.excluded.length > 0 && (
                                            <div style={{ marginBottom: 9 }}>
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  color: "#ef4444",
                                                  fontWeight: 700,
                                                  letterSpacing: 0.5,
                                                  fontFamily: "monospace",
                                                  marginBottom: 5,
                                                }}
                                              >
                                                ✗ EXCLUDED OPINIONS
                                              </div>
                                              {e.expanded.excluded.map(
                                                (x, xi) => (
                                                  <div
                                                    key={xi}
                                                    style={{
                                                      fontSize: 9,
                                                      color: th.text,
                                                      padding: "3px 0 3px 10px",
                                                      borderLeft:
                                                        "2px solid #ef444466",
                                                      marginBottom: 3,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {x}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        {e.expanded.ruling && (
                                          <div
                                            style={{
                                              marginBottom: 9,
                                              padding: "7px 10px",
                                              background: dark
                                                ? "#1e293b"
                                                : "#fff",
                                              borderRadius: 5,
                                              border: `1px dashed ${th.border}`,
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                color: th.faint,
                                                fontWeight: 700,
                                                letterSpacing: 0.5,
                                                fontFamily: "monospace",
                                                marginBottom: 4,
                                              }}
                                            >
                                              ⚖️ DAUBERT RULING
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              {e.expanded.ruling}
                                            </div>
                                          </div>
                                        )}
                                        <div style={{ marginBottom: 9 }}>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              color: "#ef4444",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              fontFamily: "monospace",
                                              marginBottom: 5,
                                            }}
                                          >
                                            🔴 PLAINTIFF CROSS-EXAM ANGLES
                                          </div>
                                          {e.expanded.crossAngles.map(
                                            (c, ci) => (
                                              <div
                                                key={ci}
                                                style={{
                                                  fontSize: 9,
                                                  color: th.text,
                                                  padding: "4px 0 4px 12px",
                                                  borderLeft:
                                                    "2px solid #ef444466",
                                                  marginBottom: 4,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {c}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                        <div>
                                          <div
                                            style={{
                                              fontSize: 7,
                                              color: "#10b981",
                                              fontWeight: 700,
                                              letterSpacing: 0.5,
                                              fontFamily: "monospace",
                                              marginBottom: 5,
                                            }}
                                          >
                                            ↩️ REDIRECT PREP
                                          </div>
                                          {e.expanded.redirectPrep.map(
                                            (r, ri) => (
                                              <div
                                                key={ri}
                                                style={{
                                                  fontSize: 9,
                                                  color: th.text,
                                                  padding: "4px 0 4px 12px",
                                                  borderLeft:
                                                    "2px solid #10b98166",
                                                  marginBottom: 4,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {r}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                          </div>

                          {/* AI Head-to-Head Assessment */}
                          <div
                            style={{
                              padding: "14px 18px",
                              background: dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#faf5ff,#f3e8ff)",
                              borderTop: `1px solid ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#a78bfa",
                                letterSpacing: 1.2,
                                fontFamily: "monospace",
                                marginBottom: 6,
                              }}
                            >
                              ✨ AI HEAD-TO-HEAD ASSESSMENT
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.65,
                                marginBottom: 10,
                              }}
                            >
                              {issue.aiAssessment}
                            </div>
                            <div
                              style={{
                                padding: "9px 12px",
                                background: dark
                                  ? "rgba(167,139,250,0.08)"
                                  : "rgba(167,139,250,0.06)",
                                borderLeft: "3px solid #a78bfa",
                                borderRadius: "0 5px 5px 0",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  color: "#a78bfa",
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  fontFamily: "monospace",
                                  marginBottom: 4,
                                }}
                              >
                                📋 JURY INSTRUCTION BATTLE
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.text,
                                  lineHeight: 1.55,
                                }}
                              >
                                {issue.juryInstructionBattle}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            {tab === "client" &&
              (() => {
                const data = CLIENT_COMM_DATA[caseId] || CLIENT_COMM_DATA.juul;
                const persp = clientPersp;
                const isActive = persp !== "all";
                const sec = clientSection;
                const fmt = (n) => {
                  if (!n) return "$0";
                  const a = Math.abs(n);
                  if (a >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
                  if (a >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
                  if (a >= 1e3) return "$" + (n / 1e3).toFixed(0) + "K";
                  return "$" + Math.round(n).toLocaleString();
                };
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : dark
                        ? "linear-gradient(135deg,#0f172a,#1e293b)"
                        : "linear-gradient(135deg,#f1f5f9,#e2e8f0)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : th.border;
                const sectionBtn = (k, emoji, label) => (
                  <button
                    key={k}
                    onClick={() => setClientSection(k)}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#5b8def" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
                const activeSide =
                  persp === "defendant" ? "defendant" : "plaintiff";
                const activeName = data.clientNames[activeSide];
                const sideColor =
                  activeSide === "plaintiff" ? "#ef4444" : "#3b82f6";
                return (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#5b8def,#3b82f6)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#dbeafe",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        CLIENT COMMUNICATION HUB · STATUS · BUDGET · DECISIONS
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {data.matterName}
                      </h1>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#dbeafe",
                          lineHeight: 1.5,
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>Client:</span>{" "}
                        {persp === "all" ? (
                          <>
                            <span style={{ color: "#fef2f2" }}>
                              {data.clientNames.plaintiff}
                            </span>{" "}
                            <span style={{ opacity: 0.6 }}>vs.</span>{" "}
                            <span style={{ color: "#dbeafe" }}>
                              {data.clientNames.defendant}
                            </span>
                          </>
                        ) : (
                          activeName
                        )}
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#fff" : th.text,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 Plaintiff Counsel - reporting to " +
                              data.clientNames.plaintiff
                            : persp === "defendant"
                              ? "🔵 Defendant Counsel - reporting to " +
                                data.clientNames.defendant
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: isActive
                              ? "rgba(255,255,255,0.85)"
                              : th.muted,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Plaintiff status report · plaintiff budget · plaintiff decision log"
                            : persp === "defendant"
                              ? "Defendant status report · defense budget · defense decision log"
                              : "Toggle to see one side's client communications"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setClientPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* STATUS REPORT */}
                    {(() => {
                      const sidesToShow =
                        persp === "all"
                          ? ["plaintiff", "defendant"]
                          : [activeSide];
                      return (
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              color: th.muted,
                              marginBottom: 12,
                              padding: "10px 14px",
                              background: dark ? "#1e293b" : "#f8fafc",
                              borderLeft: "3px solid #5b8def",
                              borderRadius: "0 6px 6px 0",
                              lineHeight: 1.55,
                            }}
                          >
                            <span
                              style={{
                                color: "#5b8def",
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            >
                              📰 AI-generated status report.
                            </span>{" "}
                            Plain-English client communication built from the
                            case record. No litigation jargon - written for the
                            client to actually read.
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              marginBottom: 12,
                              padding: "10px 14px",
                              background: th.card,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                color: th.faint,
                                fontWeight: 700,
                                fontFamily: "monospace",
                                letterSpacing: 0.5,
                              }}
                            >
                              📅 SUMMARY FREQUENCY
                            </span>
                            <select
                              value={clientFreq}
                              onChange={(e) => setClientFreq(e.target.value)}
                              style={{
                                fontSize: 11,
                                padding: "6px 10px",
                                background: th.bg,
                                color: th.text,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="ytd">Year-to-Date</option>
                            </select>
                            <span
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                fontStyle: "italic",
                              }}
                            >
                              {clientFreq === "weekly"
                                ? "Reports the past 7 days of case activity"
                                : clientFreq === "monthly"
                                  ? "Reports the past 30 days · best for pre-trial cadence"
                                  : clientFreq === "quarterly"
                                    ? "Reports the past 90 days · best for dormant matters"
                                    : "Year-to-date · January 1 through today"}
                            </span>
                          </div>
                          {sidesToShow.map((side) => {
                            const sd = data.statusReports[side];
                            const sc =
                              side === "plaintiff" ? "#ef4444" : "#3b82f6";
                            return (
                              <div
                                key={side}
                                style={{
                                  background: th.card,
                                  borderRadius: 10,
                                  border: `1px solid ${th.border}`,
                                  borderLeft: `4px solid ${sc}`,
                                  marginBottom: 14,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "12px 16px",
                                    background: dark ? "#0f172a" : "#fafafa",
                                    borderBottom: `1px solid ${th.border}`,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                    gap: 8,
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 3,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: sc + "22",
                                          color: sc,
                                          border: `1px solid ${sc}55`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {side === "plaintiff"
                                          ? "🔴 PLAINTIFF"
                                          : "🔵 DEFENDANT"}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: th.bg,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontFamily: "monospace",
                                          fontWeight: 600,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {clientFreq === "ytd"
                                          ? "YEAR TO DATE"
                                          : clientFreq.toUpperCase()}{" "}
                                        · {sd.latest.reportingPeriod}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                      }}
                                    >
                                      To: {data.clientNames[side]}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      Date: {sd.latest.date}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 5 }}>
                                    <button
                                      onClick={() => {
                                        const txt = `STATUS REPORT - ${data.matterName}\nClient: ${data.clientNames[side]}\nDate: ${sd.latest.date}\nReporting Period: ${sd.latest.reportingPeriod}\n\nWHAT HAPPENED\n${sd.latest.whatHappened}\n\nWHAT IT MEANS\n${sd.latest.whatItMeans}\n\nNEXT MILESTONE\n${sd.latest.nextMilestone}\n\nDECISIONS NEEDED\n${sd.latest.decisionsNeeded}`;
                                        navigator.clipboard &&
                                          navigator.clipboard.writeText(txt);
                                        toast(
                                          "Status report copied",
                                          "success",
                                        );
                                      }}
                                      style={{
                                        fontSize: 8,
                                        padding: "5px 10px",
                                        background: sc + "15",
                                        color: sc,
                                        border: `1px solid ${sc}44`,
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                    >
                                      📋 Copy
                                    </button>
                                  </div>
                                </div>
                                <div style={{ padding: "14px 16px" }}>
                                  <div
                                    style={{
                                      marginBottom: 11,
                                      padding: "10px 12px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      borderLeft: `3px solid ${sc}`,
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: sc,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      📋 WHAT HAPPENED
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.65,
                                      }}
                                    >
                                      {sd.latest.whatHappened}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      marginBottom: 11,
                                      padding: "10px 12px",
                                      background: "#a78bfa10",
                                      borderLeft: "3px solid #a78bfa",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#a78bfa",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      🧠 WHAT IT MEANS
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.65,
                                      }}
                                    >
                                      {sd.latest.whatItMeans}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      marginBottom: 11,
                                      padding: "10px 12px",
                                      background: "#10b98110",
                                      borderLeft: "3px solid #10b981",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      📅 NEXT MILESTONE
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.65,
                                      }}
                                    >
                                      {sd.latest.nextMilestone}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      background: "#f59e0b10",
                                      borderLeft: "3px solid #f59e0b",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#f59e0b",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      ⚡ DECISIONS NEEDED
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.65,
                                      }}
                                    >
                                      {sd.latest.decisionsNeeded}
                                    </div>
                                  </div>
                                  {sd.history && sd.history.length > 0 && (
                                    <div
                                      style={{
                                        marginTop: 14,
                                        paddingTop: 11,
                                        borderTop: `1px dashed ${th.border}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 6,
                                        }}
                                      >
                                        📚 PRIOR REPORTS
                                      </div>
                                      {sd.history.map((h, hi) => (
                                        <div
                                          key={hi}
                                          style={{
                                            display: "flex",
                                            gap: 8,
                                            padding: "4px 0",
                                            fontSize: 9,
                                            color: th.muted,
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontFamily: "monospace",
                                              color: th.dim,
                                              fontWeight: 600,
                                              minWidth: 90,
                                            }}
                                          >
                                            {h.date}
                                          </span>
                                          <span>{h.summary}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            {tab === "stratmap" &&
              (() => {
                const data =
                  STRATEGIC_MAP_DATA[caseId] || STRATEGIC_MAP_DATA.juul;
                const sec = stratmapSection;
                const layer = stratmapLayer;
                const gapType = stratmapGapType;
                const cellSel = stratmapCellSel;
                const cellColor = (s) => {
                  if (s === 0)
                    return {
                      bg: dark ? "#1e293b" : "#f1f5f9",
                      fg: th.faint,
                      border: dark ? "#334155" : "#cbd5e1",
                    };
                  if (s <= 2)
                    return { bg: "#7f1d1d", fg: "#fff", border: "#991b1b" };
                  if (s <= 3)
                    return { bg: "#dc2626", fg: "#fff", border: "#b91c1c" };
                  if (s <= 5)
                    return { bg: "#f59e0b", fg: "#fff", border: "#d97706" };
                  if (s <= 7)
                    return { bg: "#84cc16", fg: "#fff", border: "#65a30d" };
                  return { bg: "#10b981", fg: "#fff", border: "#059669" };
                };
                const sectionBtn = (k, emoji, label) => (
                  <button
                    key={k}
                    onClick={() => setStratmapSection(k)}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#10b981" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
                const layerBtn = (k, emoji, label) => (
                  <button
                    key={k}
                    onClick={() => {
                      setStratmapLayer(k);
                      setStratmapCellSel(null);
                    }}
                    style={{
                      padding: "7px 12px",
                      fontSize: 10,
                      fontWeight: layer === k ? 700 : 500,
                      background: layer === k ? th.text : "transparent",
                      color: layer === k ? th.bg : th.muted,
                      border: `1px solid ${layer === k ? th.text : th.border}`,
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
                const gapBtn = (k, emoji, label, color) => {
                  const list = data.gaps[k] || [];
                  return (
                    <button
                      key={k}
                      onClick={() => setStratmapGapType(k)}
                      style={{
                        padding: "8px 14px",
                        fontSize: 10,
                        fontWeight: gapType === k ? 700 : 500,
                        background: gapType === k ? color : "transparent",
                        color: gapType === k ? "#fff" : th.muted,
                        border: `1px solid ${gapType === k ? color : th.border}`,
                        borderRadius: 6,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>{emoji}</span>
                      <span>{label}</span>
                      <span
                        style={{
                          fontSize: 8,
                          padding: "1px 6px",
                          background:
                            gapType === k
                              ? "rgba(255,255,255,0.25)"
                              : color + "22",
                          color: gapType === k ? "#fff" : color,
                          borderRadius: 8,
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      >
                        {list.length}
                      </span>
                    </button>
                  );
                };
                const layerData =
                  layer === "layer1"
                    ? data.layer1
                    : layer === "layer2"
                      ? data.layer2
                      : layer === "layer3"
                        ? data.layer3
                        : data.layer4;
                const isGrid = layer !== "layer4";
                const isTimeline = layer === "layer4";
                return (
                  <div
                    style={{
                      maxWidth: 1200,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#d1fae5",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        STRATEGIC MAP · CASE STATE VISUALIZATION + GAP ANALYSIS
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {data.caseTitle}
                      </h1>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#d1fae5",
                          lineHeight: 1.5,
                        }}
                      >
                        Heat Map (4 layers) + Evidence Gap Analysis (4 gap
                        types) · merged for unified strategic view
                      </div>
                    </div>

                    {/* SECTION SELECTOR */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn("heatmap", "🌡️", "Heat Map")}
                      {sectionBtn("gaps", "🧩", "Evidence Gaps")}
                    </div>

                    {/* HEAT MAP SECTION */}
                    {sec === "heatmap" && (
                      <div>
                        {/* Layer selector */}
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            marginBottom: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          {layerBtn("layer1", "📊", "Evidence Intensity")}
                          {layerBtn("layer2", "⚠️", "Vulnerability")}
                          {layerBtn("layer3", "👥", "Witness Coverage")}
                          {layerBtn("layer4", "📅", "Timeline Density")}
                        </div>
                        {/* Layer description */}
                        <div
                          style={{
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #10b981",
                            borderRadius: "0 6px 6px 0",
                            marginBottom: 12,
                            fontSize: 10,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#10b981",
                              fontSize: 11,
                            }}
                          >
                            {layerData.title}.
                          </span>{" "}
                          {layerData.description}
                        </div>

                        {/* Grid layers (1, 2, 3) */}
                        {isGrid && (
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 11,
                              border: `1px solid ${th.border}`,
                              padding: "14px",
                              marginBottom: 12,
                              overflowX: "auto",
                            }}
                          >
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "separate",
                                borderSpacing: 4,
                                minWidth: 600,
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      textAlign: "left",
                                      fontSize: 8,
                                      fontWeight: 600,
                                      color: th.faint,
                                      padding: "6px 8px",
                                      fontFamily: "monospace",
                                      letterSpacing: 0.5,
                                      minWidth: 140,
                                    }}
                                  >
                                    {layer === "layer3"
                                      ? "FACT TO PROVE"
                                      : "CLAIM"}
                                  </th>
                                  {layerData.cols.map((c) => (
                                    <th
                                      key={c.id}
                                      style={{
                                        textAlign: "center",
                                        fontSize: 8,
                                        fontWeight: 700,
                                        color: th.muted,
                                        padding: "6px 4px",
                                        fontFamily: "monospace",
                                        letterSpacing: 0.3,
                                        minWidth: 90,
                                      }}
                                    >
                                      {c.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {layerData.rows.map((row, ri) => (
                                  <tr key={row.id}>
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: th.text,
                                        padding: "8px 10px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fafafa",
                                        borderRadius: 5,
                                        maxWidth: 160,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {row.label}
                                    </td>
                                    {layerData.cols.map((col, ci) => {
                                      const cell = layerData.grid[ri][ci];
                                      const c = cellColor(cell.s);
                                      const isSel =
                                        cellSel &&
                                        cellSel.row === ri &&
                                        cellSel.col === ci;
                                      return (
                                        <td
                                          key={col.id}
                                          onClick={() =>
                                            setStratmapCellSel({
                                              row: ri,
                                              col: ci,
                                            })
                                          }
                                          style={{
                                            padding: 0,
                                            cursor: "pointer",
                                          }}
                                        >
                                          <div
                                            style={{
                                              background: c.bg,
                                              color: c.fg,
                                              border: `2px solid ${isSel ? th.text : c.border}`,
                                              borderRadius: 6,
                                              padding: "10px 8px",
                                              textAlign: "center",
                                              transition: "all 0.15s",
                                              transform: isSel
                                                ? "scale(1.05)"
                                                : "scale(1)",
                                              boxShadow: isSel
                                                ? `0 4px 12px ${c.bg}66`
                                                : "none",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 14,
                                                fontWeight: 800,
                                                fontFamily: "monospace",
                                                lineHeight: 1,
                                              }}
                                            >
                                              {cell.s}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontWeight: 600,
                                                marginTop: 3,
                                                opacity: 0.95,
                                                letterSpacing: 0.3,
                                              }}
                                            >
                                              {cell.l}
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {/* Legend */}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                marginTop: 12,
                                paddingTop: 10,
                                borderTop: `1px dashed ${th.border}`,
                                fontSize: 8,
                                color: th.faint,
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                }}
                              >
                                SCALE:
                              </span>
                              {[
                                [0, "None"],
                                [2, "Critical"],
                                [3, "Weak"],
                                [5, "Mixed"],
                                [7, "Strong"],
                                [9, "Pivotal"],
                              ].map(([s, l]) => {
                                const c = cellColor(s);
                                return (
                                  <span
                                    key={s}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: 3,
                                        background: c.bg,
                                        border: `1px solid ${c.border}`,
                                      }}
                                    />
                                    <span style={{ fontFamily: "monospace" }}>
                                      {s}/10 {l}
                                    </span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Cell detail box */}
                        {isGrid &&
                          cellSel &&
                          (() => {
                            const cell =
                              layerData.grid[cellSel.row][cellSel.col];
                            const row = layerData.rows[cellSel.row];
                            const col = layerData.cols[cellSel.col];
                            const c = cellColor(cell.s);
                            return (
                              <div
                                style={{
                                  background: th.card,
                                  borderRadius: 10,
                                  border: `1px solid ${th.border}`,
                                  borderLeft: `4px solid ${c.bg}`,
                                  padding: "14px 16px",
                                  marginBottom: 12,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 6,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: c.bg,
                                      color: "#fff",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    {cell.s}/10 · {cell.l}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: th.text,
                                    }}
                                  >
                                    {row.label}
                                  </span>
                                  <span
                                    style={{ fontSize: 10, color: th.faint }}
                                  >
                                    ×
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: th.muted,
                                    }}
                                  >
                                    {col.label}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    padding: "10px 12px",
                                    background: dark ? "#0f172a" : "#fafafa",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    ✨ AI STRATEGIC ASSESSMENT
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {cell.d}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 6,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      setStratmapSection("gaps");
                                      toast(
                                        "Switched to Evidence Gaps",
                                        "success",
                                      );
                                    }}
                                    style={{
                                      fontSize: 9,
                                      padding: "5px 10px",
                                      background: "#10b98115",
                                      color: "#10b981",
                                      border: "1px solid #10b98144",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    🧩 Send to Evidence Gaps
                                  </button>
                                  <button
                                    onClick={() =>
                                      toast(
                                        "Cell flagged for follow-up",
                                        "success",
                                      )
                                    }
                                    style={{
                                      fontSize: 9,
                                      padding: "5px 10px",
                                      background: "#f59e0b15",
                                      color: "#f59e0b",
                                      border: "1px solid #f59e0b44",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    🚩 Flag for follow-up
                                  </button>
                                  <button
                                    onClick={() =>
                                      toast("Cell note added", "success")
                                    }
                                    style={{
                                      fontSize: 9,
                                      padding: "5px 10px",
                                      background: "#3b82f615",
                                      color: "#3b82f6",
                                      border: "1px solid #3b82f644",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    📝 Add note
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        {isGrid && !cellSel && (
                          <div
                            style={{
                              padding: "12px 16px",
                              background: th.surface,
                              border: `1px dashed ${th.border}`,
                              borderRadius: 8,
                              fontSize: 10,
                              color: th.faint,
                              textAlign: "center",
                              fontStyle: "italic",
                            }}
                          >
                            👆 Tap any cell to see the AI strategic assessment,
                            send to Evidence Gaps, flag for follow-up, or add a
                            note.
                          </div>
                        )}

                        {/* Timeline density layer */}
                        {isTimeline && (
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 11,
                              border: `1px solid ${th.border}`,
                              padding: "16px 18px",
                            }}
                          >
                            {layerData.periods.map((p, pi) => {
                              const c =
                                p.density >= 8
                                  ? "#10b981"
                                  : p.density >= 6
                                    ? "#84cc16"
                                    : p.density >= 4
                                      ? "#f59e0b"
                                      : p.density >= 2
                                        ? "#dc2626"
                                        : "#7f1d1d";
                              return (
                                <div key={pi} style={{ marginBottom: 12 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: 5,
                                      gap: 10,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: th.text,
                                      }}
                                    >
                                      {p.label}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 9,
                                        color: c,
                                        fontFamily: "monospace",
                                        fontWeight: 700,
                                      }}
                                    >
                                      DENSITY {p.density}/10
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      height: 18,
                                      background: dark ? "#0f172a" : "#f1f5f9",
                                      borderRadius: 4,
                                      overflow: "hidden",
                                      position: "relative",
                                      marginBottom: 5,
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        width: p.density * 10 + "%",
                                        background: `linear-gradient(to right,${c}88,${c})`,
                                        borderRadius: 4,
                                        transition: "width 0.4s",
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color:
                                          p.density >= 4 ? "#fff" : th.text,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.3,
                                        textShadow:
                                          p.density >= 4
                                            ? "0 1px 2px rgba(0,0,0,0.4)"
                                            : "none",
                                      }}
                                    >
                                      {p.density >= 8
                                        ? "VERY DENSE"
                                        : p.density >= 6
                                          ? "DENSE"
                                          : p.density >= 4
                                            ? "MODERATE"
                                            : p.density >= 2
                                              ? "SPARSE"
                                              : "VERY SPARSE"}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                      paddingLeft: 10,
                                      borderLeft: `2px solid ${c}66`,
                                    }}
                                  >
                                    {p.note}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* GAPS SECTION */}
                    {sec === "gaps" && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginBottom: 12,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            onClick={async () => {
                              toast("Generating gap analysis...", "info");
                              await new Promise((r) => setTimeout(r, 1000));
                              toast("Gap analysis updated", "success");
                            }}
                            style={{
                              padding: "9px 16px",
                              fontSize: 11,
                              fontWeight: 600,
                              background:
                                "linear-gradient(135deg,#a78bfa,#7c3aed)",
                              color: "#fff",
                              border: "none",
                              borderRadius: 7,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            ✨ Generate Gap Analysis
                          </button>
                          <span
                            style={{
                              fontSize: 9,
                              color: th.faint,
                              fontStyle: "italic",
                            }}
                          >
                            AI reads case theories + record + legal elements →
                            comprehensive gap list ranked by severity
                          </span>
                        </div>
                        {/* Gap type selector */}
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            marginBottom: 14,
                            flexWrap: "wrap",
                          }}
                        >
                          {gapBtn("closable", "🔧", "Closable", "#10b981")}
                          {gapBtn("unclosable", "🚫", "Unclosable", "#6b7280")}
                          {gapBtn("contested", "⚔️", "Contested", "#f59e0b")}
                          {gapBtn("strategic", "🎯", "Strategic", "#3b82f6")}
                        </div>
                        {/* Gap type description */}
                        <div
                          style={{
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: `3px solid ${gapType === "closable" ? "#10b981" : gapType === "unclosable" ? "#6b7280" : gapType === "contested" ? "#f59e0b" : "#3b82f6"}`,
                            borderRadius: "0 6px 6px 0",
                            marginBottom: 12,
                            fontSize: 10,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          {gapType === "closable" ? (
                            <>
                              <span
                                style={{
                                  color: "#10b981",
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}
                              >
                                🔧 Closable.
                              </span>{" "}
                              Evidence that doesn't exist in the record yet but
                              could be obtained before discovery cutoff. Each
                              card has a deadline and a Create Task button.
                            </>
                          ) : gapType === "unclosable" ? (
                            <>
                              <span
                                style={{
                                  color: "#6b7280",
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}
                              >
                                🚫 Unclosable.
                              </span>{" "}
                              Evidence that can't be obtained - witness
                              unavailable, document destroyed, time period
                              predates preservation. Strategic implications and
                              authorities for working without it.
                            </>
                          ) : gapType === "contested" ? (
                            <>
                              <span
                                style={{
                                  color: "#f59e0b",
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}
                              >
                                ⚔️ Contested.
                              </span>{" "}
                              Evidence one side claims exists and the other
                              denies. Each card shows party positions and the
                              disputed factual question for the jury.
                            </>
                          ) : (
                            <>
                              <span
                                style={{
                                  color: "#3b82f6",
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}
                              >
                                🎯 Strategic.
                              </span>{" "}
                              Evidence that exists but hasn't been developed or
                              emphasized. Each card shows the inconsistency or
                              underdevelopment and the action to address it.
                            </>
                          )}
                        </div>

                        {/* Gap cards */}
                        {(data.gaps[gapType] || []).map((g, gi) => {
                          const sevC =
                            g.severity === "critical"
                              ? "#dc2626"
                              : g.severity === "high"
                                ? "#f59e0b"
                                : g.severity === "medium"
                                  ? "#3b82f6"
                                  : "#6b7280";
                          return (
                            <div
                              key={gi}
                              style={{
                                background: th.card,
                                borderRadius: 10,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${sevC}`,
                                padding: "14px 16px",
                                marginBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 8px",
                                    background: sevC + "22",
                                    color: sevC,
                                    border: `1px solid ${sevC}55`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {g.severity}
                                </span>
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: th.text,
                                    letterSpacing: "-0.2px",
                                    flex: 1,
                                    minWidth: 200,
                                  }}
                                >
                                  {g.gap}
                                </span>
                              </div>
                              <div
                                style={{
                                  padding: "8px 11px",
                                  background: dark ? "#0f172a" : "#fafafa",
                                  borderLeft: `3px solid ${sevC}66`,
                                  borderRadius: "0 5px 5px 0",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: th.faint,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    marginBottom: 3,
                                  }}
                                >
                                  WHY IT MATTERS
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {g.why}
                                </div>
                              </div>
                              {g.required && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#10b98110",
                                    borderLeft: "3px solid #10b981",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#10b981",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    REQUIRED TO CLOSE
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.required}
                                  </div>
                                </div>
                              )}
                              {g.strategicResponse && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#a78bfa10",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    STRATEGIC RESPONSE
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.strategicResponse}
                                  </div>
                                </div>
                              )}
                              {g.partyPositions && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#f59e0b10",
                                    borderLeft: "3px solid #f59e0b",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#f59e0b",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    PARTY POSITIONS
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.partyPositions}
                                  </div>
                                </div>
                              )}
                              {g.juryQuestion && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#3b82f610",
                                    borderLeft: "3px solid #3b82f6",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#3b82f6",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    JURY QUESTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.juryQuestion}
                                  </div>
                                </div>
                              )}
                              {g.juryInstruction && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#a78bfa10",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    📋 JURY INSTRUCTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.juryInstruction}
                                  </div>
                                </div>
                              )}
                              {g.action && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#10b98110",
                                    borderLeft: "3px solid #10b981",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#10b981",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    ACTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.action}
                                  </div>
                                </div>
                              )}
                              {g.impact && (
                                <div
                                  style={{
                                    padding: "8px 11px",
                                    background: "#3b82f610",
                                    borderLeft: "3px solid #3b82f6",
                                    borderRadius: "0 5px 5px 0",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#3b82f6",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    IMPACT
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {g.impact}
                                  </div>
                                </div>
                              )}
                              {g.authorities && (
                                <div
                                  style={{
                                    padding: "7px 11px",
                                    background: dark ? "#1e293b" : "#f1f5f9",
                                    borderRadius: 5,
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: th.faint,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 3,
                                    }}
                                  >
                                    📚 AUTHORITIES
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {g.authorities}
                                  </div>
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  flexWrap: "wrap",
                                  marginTop: 8,
                                  paddingTop: 8,
                                  borderTop: `1px dashed ${th.border}`,
                                  fontSize: 8,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                }}
                              >
                                {g.leadtime && (
                                  <span>
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: th.muted,
                                      }}
                                    >
                                      LEAD TIME:
                                    </span>{" "}
                                    {g.leadtime}
                                  </span>
                                )}
                                {g.deadline && (
                                  <span>
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: th.muted,
                                      }}
                                    >
                                      DEADLINE:
                                    </span>{" "}
                                    {g.deadline}
                                  </span>
                                )}
                              </div>
                              {gapType === "closable" && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 6,
                                    marginTop: 8,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      toast(
                                        "Task created in War Room Countdown",
                                        "success",
                                      )
                                    }
                                    style={{
                                      fontSize: 9,
                                      padding: "6px 12px",
                                      background: "#10b981",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      fontWeight: 700,
                                    }}
                                  >
                                    ✓ Create Task
                                  </button>
                                  <button
                                    onClick={() =>
                                      toast(
                                        "Marked closed - heat map updated",
                                        "success",
                                      )
                                    }
                                    style={{
                                      fontSize: 9,
                                      padding: "6px 12px",
                                      background: "transparent",
                                      color: th.muted,
                                      border: `1px solid ${th.border}`,
                                      borderRadius: 5,
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Mark Closed
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {(data.gaps[gapType] || []).length === 0 && (
                          <div
                            style={{
                              padding: "30px 20px",
                              background: th.card,
                              border: `1px dashed ${th.border}`,
                              borderRadius: 10,
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              ✨
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: th.muted,
                              }}
                            >
                              No {gapType} gaps recorded
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "appellate" &&
              (() => {
                const data =
                  APPELLATE_RISK_DATA[caseId] || APPELLATE_RISK_DATA.juul;
                const persp = appellatePersp;
                const isActive = persp !== "all";
                const sec = appellateSection;
                const filteredRulings = data.rulings.filter((r) => {
                  if (persp === "all") return true;
                  if (persp === "plaintiff")
                    return (
                      r.adverseTo === "Plaintiff" ||
                      r.favoredParty === "Defendant" ||
                      r.adverseTo === "pending" ||
                      r.favoredParty === "open" ||
                      r.favoredParty === "neutral"
                    );
                  if (persp === "defendant")
                    return (
                      r.adverseTo === "Defendant" ||
                      r.favoredParty === "Plaintiff" ||
                      r.adverseTo === "pending" ||
                      r.favoredParty === "open" ||
                      r.favoredParty === "neutral"
                    );
                  return true;
                });
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : dark
                        ? "linear-gradient(135deg,#0f172a,#1e293b)"
                        : "linear-gradient(135deg,#f1f5f9,#e2e8f0)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : th.border;
                const sectionBtn = (k, emoji, label, count) => (
                  <button
                    key={k}
                    onClick={() => setAppellateSection(k)}
                    style={{
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#7c3aed" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                    {count > 0 && (
                      <span
                        style={{
                          fontSize: 8,
                          padding: "1px 6px",
                          background:
                            sec === k ? "rgba(255,255,255,0.25)" : "#7c3aed22",
                          color: sec === k ? "#fff" : "#7c3aed",
                          borderRadius: 8,
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
                const TYPE_C = {
                  MSJ: "#ef4444",
                  PI: "#dc2626",
                  Daubert: "#f59e0b",
                  "Class Cert": "#10b981",
                  Markman: "#a78bfa",
                  "§101": "#7c3aed",
                  Evidentiary: "#3b82f6",
                  Discovery: "#6b7280",
                  Sanctions: "#94a3b8",
                  Procedural: "#64748b",
                };
                const URG_C = {
                  critical: "#dc2626",
                  high: "#ef4444",
                  medium: "#f59e0b",
                  low: "#6b7280",
                };
                const STD_C = {
                  "De novo": "#10b981",
                  "de novo": "#10b981",
                  "Abuse of discretion": "#f59e0b",
                  "abuse of discretion": "#f59e0b",
                  "Clear error": "#3b82f6",
                };
                return (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#7c3aed,#5b21b6)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#e9d5ff",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        APPELLATE RISK REGISTER · PRESERVE THE RECORD · TRACK
                        REVERSAL EXPOSURE
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {data.caseTitle}
                      </h1>
                      <div
                        style={{
                          display: "flex",
                          gap: 18,
                          flexWrap: "wrap",
                          fontSize: 9,
                          color: "#e9d5ff",
                          fontFamily: "monospace",
                        }}
                      >
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.rulings.length}
                          </span>{" "}
                          rulings tracked
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {
                              data.rulings.filter(
                                (r) =>
                                  r.preservation === "WAIVED" ||
                                  r.preservation === "pending",
                              ).length
                            }
                          </span>{" "}
                          preservation issues
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {data.pendingAlerts.length}
                          </span>{" "}
                          live alerts
                        </span>
                        <span>
                          <span style={{ color: "#fff", fontWeight: 700 }}>
                            {
                              data.rulings.filter(
                                (r) =>
                                  r.urgency === "critical" ||
                                  r.urgency === "high" ||
                                  (r.urgency &&
                                    r.urgency.includes("preservation")),
                              ).length
                            }
                          </span>{" "}
                          high-urgency entries
                        </span>
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#fff" : th.text,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 Plaintiff Counsel - rulings adverse to plaintiff and pending preservation"
                            : persp === "defendant"
                              ? "🔵 Defendant Counsel - rulings adverse to defendant and pending preservation"
                              : "All Rulings · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: isActive
                              ? "rgba(255,255,255,0.85)"
                              : th.muted,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Standards of review · preservation status · reversal likelihood · live preservation alerts from plaintiff vantage"
                            : persp === "defendant"
                              ? "Standards of review · preservation status · reversal likelihood · live preservation alerts from defendant vantage"
                              : "All adverse rulings, standards of review, preservation status, reversal likelihood"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setAppellatePersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SECTION SELECTOR */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn(
                        "rulings",
                        "⚖️",
                        "Rulings",
                        filteredRulings.length,
                      )}
                      {sectionBtn(
                        "alerts",
                        "⚡",
                        "Live Preservation Alerts",
                        data.pendingAlerts.length,
                      )}
                    </div>

                    {/* RULINGS SECTION */}
                    {sec === "rulings" && (
                      <div>
                        <div
                          style={{
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #7c3aed",
                            borderRadius: "0 6px 6px 0",
                            marginBottom: 12,
                            fontSize: 10,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#7c3aed",
                              fontSize: 11,
                            }}
                          >
                            ⚖️ Adverse rulings tracker.
                          </span>{" "}
                          Every significant ruling - orders, SJ decisions,
                          Daubert rulings, MIL decisions - organized by
                          appellate risk level. Each entry shows the ruling, the
                          standard of review, whether the issue was properly
                          preserved, and an AI assessment of the likelihood of
                          reversal.
                        </div>
                        {filteredRulings.length === 0 && (
                          <div
                            style={{
                              padding: "30px 20px",
                              background: th.card,
                              border: `1px dashed ${th.border}`,
                              borderRadius: 10,
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 30,
                                opacity: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              ⚖️
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: th.muted,
                              }}
                            >
                              No rulings match this perspective filter
                            </div>
                          </div>
                        )}
                        {filteredRulings.map((r, ri) => {
                          const tC = TYPE_C[r.type] || "#6b7280";
                          const isWaived = r.preservation === "WAIVED";
                          const isPending = r.preservation === "pending";
                          const sc = isWaived
                            ? "#dc2626"
                            : isPending
                              ? "#f59e0b"
                              : "#10b981";
                          const urgC =
                            URG_C[r.urgency && r.urgency.split(" ")[0]] ||
                            "#6b7280";
                          return (
                            <div
                              key={r.id}
                              style={{
                                background: th.card,
                                borderRadius: 11,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${tC}`,
                                padding: "14px 16px",
                                marginBottom: 12,
                              }}
                            >
                              {/* Top row: type, urgency, date */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 8,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 8px",
                                    background: tC + "22",
                                    color: tC,
                                    border: `1px solid ${tC}55`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {r.type}
                                </span>
                                {r.urgency && r.urgency !== "low" && (
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 8px",
                                      background: urgC + "22",
                                      color: urgC,
                                      border: `1px solid ${urgC}55`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    ⚡ {r.urgency}
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: th.dim,
                                    padding: "2px 7px",
                                    background: th.bg,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 8,
                                  }}
                                >
                                  {r.date}
                                </span>
                                {isWaived && (
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 8px",
                                      background: "#dc262622",
                                      color: "#dc2626",
                                      border: "1px solid #dc262655",
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    ⚠️ WAIVED
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 8px",
                                    background:
                                      r.adverseTo === "Plaintiff"
                                        ? "#ef444422"
                                        : r.adverseTo === "Defendant"
                                          ? "#3b82f622"
                                          : r.adverseTo === "pending"
                                            ? "#a78bfa22"
                                            : "#6b728022",
                                    color:
                                      r.adverseTo === "Plaintiff"
                                        ? "#ef4444"
                                        : r.adverseTo === "Defendant"
                                          ? "#3b82f6"
                                          : r.adverseTo === "pending"
                                            ? "#a78bfa"
                                            : "#6b7280",
                                    border: `1px solid ${r.adverseTo === "Plaintiff" ? "#ef444455" : r.adverseTo === "Defendant" ? "#3b82f655" : r.adverseTo === "pending" ? "#a78bfa55" : "#6b728055"}`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {r.adverseTo === "pending"
                                    ? "PENDING"
                                    : r.adverseTo === "both-affirmed"
                                      ? "NEUTRAL"
                                      : r.adverseTo === "third-party"
                                        ? "3RD PARTY"
                                        : r.adverseTo === "none"
                                          ? "NEUTRAL"
                                          : "ADVERSE TO " +
                                            r.adverseTo.toUpperCase()}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: th.text,
                                  marginBottom: 5,
                                  letterSpacing: "-0.2px",
                                }}
                              >
                                {r.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 9,
                                  fontStyle: "italic",
                                }}
                              >
                                {r.court}
                                {r.dockets && r.dockets.length > 0
                                  ? " · " + r.dockets.join(" · ")
                                  : ""}
                              </div>
                              <div
                                style={{
                                  padding: "9px 12px",
                                  background: dark ? "#0f172a" : "#fafafa",
                                  borderLeft: `3px solid ${tC}66`,
                                  borderRadius: "0 5px 5px 0",
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: th.faint,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    marginBottom: 4,
                                  }}
                                >
                                  SUMMARY
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {r.summary}
                                </div>
                              </div>
                              {/* Two-column: Standard of review + Preservation */}
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit,minmax(220px,1fr))",
                                  gap: 8,
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: "#10b98110",
                                    borderLeft: "3px solid #10b981",
                                    borderRadius: "0 5px 5px 0",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#10b981",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    📏 STANDARD OF REVIEW
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {r.standardOfReview}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "9px 11px",
                                    background: sc + "15",
                                    borderLeft: `3px solid ${sc}`,
                                    borderRadius: "0 5px 5px 0",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: sc,
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {isWaived
                                      ? "⚠️ PRESERVATION - WAIVED"
                                      : isPending
                                        ? "⏱️ PRESERVATION - PENDING"
                                        : "✓ PRESERVATION"}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {r.preservationDetail}
                                  </div>
                                </div>
                              </div>
                              {/* AI Assessment + Reversal */}
                              <div
                                style={{
                                  padding: "10px 12px",
                                  background: "#a78bfa10",
                                  borderLeft: "3px solid #a78bfa",
                                  borderRadius: "0 5px 5px 0",
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 5,
                                    flexWrap: "wrap",
                                    gap: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    ✨ AI REVERSAL ASSESSMENT
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 7,
                                        color: th.faint,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      REVERSAL LIKELIHOOD:
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color:
                                          r.reversalLikelihood >= 50
                                            ? "#ef4444"
                                            : r.reversalLikelihood >= 30
                                              ? "#f59e0b"
                                              : "#10b981",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {r.reversalLikelihood}%
                                    </span>
                                    <div
                                      style={{
                                        width: 60,
                                        height: 5,
                                        borderRadius: 3,
                                        background: th.border,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "100%",
                                          width: r.reversalLikelihood + "%",
                                          background:
                                            r.reversalLikelihood >= 50
                                              ? "#ef4444"
                                              : r.reversalLikelihood >= 30
                                                ? "#f59e0b"
                                                : "#10b981",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {r.reversalAssessment}
                                </div>
                              </div>
                              {/* Bottom row: appealability, circuit, precedent */}
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit,minmax(200px,1fr))",
                                  gap: 6,
                                  fontSize: 8,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "6px 9px",
                                    background: dark ? "#0f172a" : "#f1f5f9",
                                    borderRadius: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      color: th.faint,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.3,
                                      marginBottom: 2,
                                    }}
                                  >
                                    APPEALABILITY
                                  </div>
                                  <div
                                    style={{ color: th.muted, lineHeight: 1.4 }}
                                  >
                                    {r.appealability}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "6px 9px",
                                    background: dark ? "#0f172a" : "#f1f5f9",
                                    borderRadius: 4,
                                  }}
                                >
                                  <div
                                    style={{
                                      color: th.faint,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.3,
                                      marginBottom: 2,
                                    }}
                                  >
                                    CIRCUIT · SPLIT
                                  </div>
                                  <div
                                    style={{ color: th.muted, lineHeight: 1.4 }}
                                  >
                                    {r.circuit} · {r.circuitSplit}
                                  </div>
                                </div>
                              </div>
                              {r.relevantPrecedent && (
                                <div
                                  style={{
                                    marginTop: 6,
                                    padding: "6px 9px",
                                    background: dark ? "#0f172a" : "#f1f5f9",
                                    borderRadius: 4,
                                    fontSize: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      color: th.faint,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.3,
                                      marginBottom: 2,
                                    }}
                                  >
                                    📚 RELEVANT PRECEDENT
                                  </div>
                                  <div
                                    style={{
                                      color: th.muted,
                                      lineHeight: 1.5,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {r.relevantPrecedent}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* PRESERVATION ALERTS SECTION */}
                    {sec === "alerts" && (
                      <div>
                        <div
                          style={{
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #ef4444",
                            borderRadius: "0 6px 6px 0",
                            marginBottom: 12,
                            fontSize: 10,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#ef4444",
                              fontSize: 11,
                            }}
                          >
                            ⚡ Live preservation alerts.
                          </span>{" "}
                          Real-time prompts when an evidentiary ruling goes
                          against you. The difference between a preserved error
                          and a waived one is often a single sentence said on
                          the record. These alerts deliver that sentence in real
                          time through the War Room.
                        </div>
                        {data.pendingAlerts.map((a, ai) => {
                          const uC = URG_C[a.urgency] || "#6b7280";
                          return (
                            <div
                              key={a.id}
                              style={{
                                background: th.card,
                                borderRadius: 11,
                                border: `1px solid ${th.border}`,
                                borderLeft: `4px solid ${uC}`,
                                padding: "14px 16px",
                                marginBottom: 11,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 7,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 8px",
                                    background: uC + "22",
                                    color: uC,
                                    border: `1px solid ${uC}55`,
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  ⚡ {a.urgency}
                                </span>
                                <span
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 8px",
                                    background: "#7c3aed22",
                                    color: "#7c3aed",
                                    border: "1px solid #7c3aed55",
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {a.type.replace(/-/g, " ")}
                                </span>
                                <span
                                  style={{
                                    fontSize: 8,
                                    color: th.dim,
                                    fontFamily: "monospace",
                                    fontWeight: 600,
                                    marginLeft: "auto",
                                  }}
                                >
                                  {a.time}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: th.text,
                                  marginBottom: 8,
                                  letterSpacing: "-0.2px",
                                }}
                              >
                                {a.ruling}
                              </div>
                              <div
                                style={{
                                  padding: "10px 12px",
                                  background: dark ? "#0f172a" : "#fefce8",
                                  border: "2px dashed #f59e0b66",
                                  borderRadius: 6,
                                  marginBottom: 9,
                                  position: "relative",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: "#d97706",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    marginBottom: 5,
                                  }}
                                >
                                  📜 SUGGESTED ON-THE-RECORD LANGUAGE
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.65,
                                    fontFamily: "Georgia, serif",
                                    fontStyle: "italic",
                                  }}
                                >
                                  "{a.suggestedLanguage}"
                                </div>
                                <button
                                  onClick={() => {
                                    navigator.clipboard &&
                                      navigator.clipboard.writeText(
                                        a.suggestedLanguage,
                                      );
                                    toast(
                                      "Preservation language copied to clipboard",
                                      "success",
                                    );
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    fontSize: 8,
                                    padding: "3px 9px",
                                    background: "#d97706",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                              <div
                                style={{
                                  padding: "8px 11px",
                                  background: "#10b98110",
                                  borderLeft: "3px solid #10b981",
                                  borderRadius: "0 5px 5px 0",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: "#10b981",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    fontFamily: "monospace",
                                    marginBottom: 3,
                                  }}
                                >
                                  ✓ ACTION REQUIRED
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {a.actionRequired}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "playbook" &&
              (() => {
                const data = PLAYBOOK_DATA[caseId] || PLAYBOOK_DATA.juul;
                const persp = playbookPersp;
                const isActive = persp !== "all";
                const sec = playbookSection;
                const perspData =
                  persp === "defendant" ? data.defendant : data.plaintiff;
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : "linear-gradient(135deg,#7c2d12,#991b1b)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : "#dc262655";
                const sectionBtn = (k, emoji, label) => (
                  <button
                    key={k}
                    onClick={() => setPlaybookSection(k)}
                    style={{
                      padding: "9px 13px",
                      fontSize: 10,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#dc2626" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </button>
                );
                return (
                  <div
                    style={{
                      maxWidth: 1200,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#dc2626,#7f1d1d)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#fecaca",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        OPPONENT PLAYBOOK · MODEL THE OPPOSITION'S
                        DECISION-MAKING · NOT THEIR ARGUMENTS
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Opponent Playbook
                      </h1>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#fecaca",
                          lineHeight: 1.5,
                        }}
                      >
                        {persp === "defendant"
                          ? "You are defense counsel. The opponent is plaintiff."
                          : persp === "plaintiff"
                            ? "You are plaintiff counsel. The opponent is defense."
                            : "Select a perspective to load the opponent's playbook from your vantage."}
                        {isActive && " · " + (perspData.opponentName || "")}
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          YOUR PERSPECTIVE → OPPONENT'S PLAYBOOK
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#fff" : th.text,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 You are plaintiff - modeling the defense"
                            : persp === "defendant"
                              ? "🔵 You are defense - modeling the plaintiff"
                              : "⚖️ Both sides - plaintiff & defendant playbooks"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: isActive
                              ? "rgba(255,255,255,0.85)"
                              : th.muted,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Their strongest case · their decision tree · their resource tells · their witness sequencing · their pressure points"
                            : persp === "defendant"
                              ? "Their strongest case · their decision tree · their resource tells · their witness sequencing · their pressure points"
                              : "This tab models opposing counsel's strategic decision-making - sequencing, resource allocation, tells, sequencing, pressure points."}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff View"],
                          ["defendant", "🔵 Defendant View"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setPlaybookPersp(v)}
                            style={{
                              padding: "7px 13px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SECTION SELECTOR - always visible */}
                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        marginBottom: 14,
                        padding: 5,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        overflowX: "auto",
                      }}
                    >
                      {sectionBtn("strongest", "📜", "Their Strongest Case")}
                      {sectionBtn("decisions", "🌳", "Their Decision Tree")}
                      {sectionBtn("tells", "🔍", "Their Resource Tells")}
                      {sectionBtn(
                        "witnesses",
                        "👥",
                        "Their Witness Sequencing",
                      )}
                      {sectionBtn("pressure", "⚡", "Their Pressure Points")}
                    </div>

                    {(() => {
                      const sidesToRender =
                        persp === "all"
                          ? [
                              [
                                "plaintiff",
                                data.plaintiff,
                                "#ef4444",
                                "🔴 PLAINTIFF VIEW - modeling the defense",
                              ],
                              [
                                "defendant",
                                data.defendant,
                                "#3b82f6",
                                "🔵 DEFENDANT VIEW - modeling the plaintiff",
                              ],
                            ]
                          : [
                              [
                                persp,
                                perspData,
                                persp === "plaintiff" ? "#ef4444" : "#3b82f6",
                                persp === "plaintiff"
                                  ? "🔴 YOUR VIEW (PLAINTIFF) - modeling the defense"
                                  : "🔵 YOUR VIEW (DEFENDANT) - modeling the plaintiff",
                              ],
                            ];
                      return sidesToRender.map(
                        ([sideKey, perspData, sideC, sideLabel], sIdx) => (
                          <div
                            key={sideKey}
                            style={{
                              marginTop: persp === "all" && sIdx > 0 ? 22 : 0,
                              paddingTop: persp === "all" && sIdx > 0 ? 14 : 0,
                              borderTop:
                                persp === "all" && sIdx > 0
                                  ? `2px dashed ${th.border}`
                                  : "none",
                            }}
                          >
                            {persp === "all" && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 10,
                                  padding: "6px 12px",
                                  background: sideC + "15",
                                  border: `1px solid ${sideC}55`,
                                  borderRadius: 7,
                                  borderLeft: `4px solid ${sideC}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: sideC,
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                  }}
                                >
                                  {sideLabel}
                                </span>
                              </div>
                            )}

                            {/* SECTION 1: STRONGEST CASE */}
                            {sec === "strongest" && perspData.strongestCase && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #dc2626",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      fontSize: 11,
                                    }}
                                  >
                                    📜 Their Strongest Case.
                                  </span>{" "}
                                  The AI writes the opponent's brief in its
                                  strongest form - what they would argue if they
                                  were the best lawyers in the world with this
                                  set of facts. In their voice. Their evidence
                                  selection. Their framing. Seeing it written
                                  this way is more useful than a list of
                                  counterarguments.
                                </div>
                                <div
                                  style={{
                                    background: th.card,
                                    borderRadius: 11,
                                    border: `1px solid ${th.border}`,
                                    borderLeft: "4px solid #dc2626",
                                    padding: "16px 20px",
                                    marginBottom: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: "#dc2626",
                                      fontFamily: "monospace",
                                      letterSpacing: 1,
                                      marginBottom: 6,
                                      fontWeight: 700,
                                    }}
                                  >
                                    OPPONENT'S BEST BRIEF - WRITTEN IN THEIR
                                    VOICE
                                  </div>
                                  <h3
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      color: th.text,
                                      margin: "0 0 12px",
                                      letterSpacing: "-0.2px",
                                    }}
                                  >
                                    {perspData.strongestCase.title}
                                  </h3>
                                  <div
                                    style={{
                                      padding: "14px 18px",
                                      background: dark ? "#0f172a" : "#fefefe",
                                      border: `1px solid ${th.border}`,
                                      borderRadius: 7,
                                      fontFamily: "Georgia, serif",
                                      fontSize: 11,
                                      color: th.text,
                                      lineHeight: 1.75,
                                      fontStyle: "italic",
                                      marginBottom: 14,
                                    }}
                                  >
                                    {perspData.strongestCase.opening}
                                  </div>
                                  <div
                                    style={{
                                      padding: "10px 13px",
                                      background: "#dc262610",
                                      borderLeft: "3px solid #dc2626",
                                      borderRadius: "0 5px 5px 0",
                                      marginBottom: 10,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#dc2626",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      📎 THEIR EVIDENCE SELECTION
                                    </div>
                                    {perspData.strongestCase.evidenceSelection.map(
                                      (e, ei) => (
                                        <div
                                          key={ei}
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                            marginBottom: 3,
                                            paddingLeft: 8,
                                            position: "relative",
                                          }}
                                        >
                                          <span
                                            style={{
                                              position: "absolute",
                                              left: 0,
                                              color: "#dc2626",
                                            }}
                                          >
                                            ▸
                                          </span>
                                          {e}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      padding: "10px 13px",
                                      background: "#a78bfa10",
                                      borderLeft: "3px solid #a78bfa",
                                      borderRadius: "0 5px 5px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#a78bfa",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "monospace",
                                        marginBottom: 5,
                                      }}
                                    >
                                      🎯 THEIR FRAMING STRATEGY
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {perspData.strongestCase.framing}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* SECTION 2: DECISION TREE */}
                            {sec === "decisions" && perspData.decisionTree && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #dc2626",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      fontSize: 11,
                                    }}
                                  >
                                    🌳 Their Decision Tree.
                                  </span>{" "}
                                  The strategic decisions the opponent faces and
                                  how they're likely to make each one. Each
                                  branch shows the AI's probability estimate and
                                  the case events that would shift those
                                  probabilities.
                                </div>
                                {perspData.decisionTree.map((d, di) => (
                                  <div
                                    key={di}
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #dc2626",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 8,
                                        flexWrap: "wrap",
                                        gap: 6,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: th.text,
                                          letterSpacing: "-0.2px",
                                          flex: 1,
                                          minWidth: 200,
                                        }}
                                      >
                                        {d.decision}
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 7,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            color: th.faint,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.5,
                                            fontWeight: 700,
                                          }}
                                        >
                                          LEANING
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 9,
                                            padding: "3px 9px",
                                            background: "#dc262622",
                                            color: "#dc2626",
                                            border: "1px solid #dc262655",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {d.leaning}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 9,
                                        marginBottom: 9,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                          fontWeight: 700,
                                        }}
                                      >
                                        AI PROBABILITY:
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 800,
                                          color:
                                            d.probability >= 70
                                              ? "#dc2626"
                                              : d.probability >= 50
                                                ? "#f59e0b"
                                                : "#10b981",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {d.probability}%
                                      </span>
                                      <div
                                        style={{
                                          flex: 1,
                                          height: 6,
                                          borderRadius: 3,
                                          background: th.border,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          style={{
                                            height: "100%",
                                            width: d.probability + "%",
                                            background:
                                              d.probability >= 70
                                                ? "#dc2626"
                                                : d.probability >= 50
                                                  ? "#f59e0b"
                                                  : "#10b981",
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fafafa",
                                        borderLeft: "3px solid #dc262666",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 4,
                                        }}
                                      >
                                        RATIONALE
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {d.rationale}
                                      </div>
                                    </div>
                                    {d.shifters && d.shifters.length > 0 && (
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#f59e0b10",
                                          borderLeft: "3px solid #f59e0b",
                                          borderRadius: "0 5px 5px 0",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#f59e0b",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 4,
                                          }}
                                        >
                                          EVENTS THAT WOULD SHIFT THIS
                                        </div>
                                        {d.shifters.map((s, si) => (
                                          <div
                                            key={si}
                                            style={{
                                              fontSize: 9,
                                              color: th.muted,
                                              lineHeight: 1.55,
                                              marginBottom: 2,
                                              paddingLeft: 8,
                                              position: "relative",
                                            }}
                                          >
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: 0,
                                                color: "#f59e0b",
                                              }}
                                            >
                                              ↗
                                            </span>
                                            {s}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SECTION 3: RESOURCE TELLS */}
                            {sec === "tells" && perspData.resourceTells && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #dc2626",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      fontSize: 11,
                                    }}
                                  >
                                    🔍 Their Resource Tells.
                                  </span>{" "}
                                  Which motions did they file that were clearly
                                  strategic (designed to impose cost or delay)
                                  versus substantive (genuinely contested legal
                                  questions)? Which patterns reveal litigation
                                  style and resource allocation? Each tell
                                  becomes intelligence about how to apply
                                  pressure.
                                </div>
                                {perspData.resourceTells.map((t, ti) => (
                                  <div
                                    key={ti}
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #dc2626",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#dc2626",
                                        fontFamily: "monospace",
                                        letterSpacing: 1,
                                        marginBottom: 5,
                                        fontWeight: 700,
                                      }}
                                    >
                                      OBSERVATION
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 8,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {t.observation}
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fafafa",
                                        borderLeft: "3px solid #dc262666",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        PATTERN
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.55,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {t.pattern}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#a78bfa10",
                                        borderLeft: "3px solid #a78bfa",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#a78bfa",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✨ AI INTERPRETATION
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {t.interpretation}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#10b98110",
                                        borderLeft: "3px solid #10b981",
                                        borderRadius: "0 5px 5px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✓ STRATEGIC IMPLICATION
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {t.implications}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SECTION 4: WITNESS SEQUENCING */}
                            {sec === "witnesses" &&
                              perspData.witnessSequencing && (
                                <div>
                                  <div
                                    style={{
                                      padding: "10px 14px",
                                      background: dark ? "#1e293b" : "#f8fafc",
                                      borderLeft: "3px solid #dc2626",
                                      borderRadius: "0 6px 6px 0",
                                      marginBottom: 12,
                                      fontSize: 10,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: "#dc2626",
                                        fontSize: 11,
                                      }}
                                    >
                                      👥 Their Witness Sequencing Model.
                                    </span>{" "}
                                    Based on the joint witness list and trial
                                    schedule, how is the opponent likely to
                                    order their witnesses and why? Seeing the
                                    expected sequence lets you plan
                                    cross-examination order to maximize
                                    contradictions between witnesses.
                                  </div>
                                  <div
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #dc2626",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#dc2626",
                                        fontFamily: "monospace",
                                        letterSpacing: 1,
                                        marginBottom: 8,
                                        fontWeight: 700,
                                      }}
                                    >
                                      PREDICTED CALLING ORDER
                                    </div>
                                    <div style={{ position: "relative" }}>
                                      {perspData.witnessSequencing.predictedOrder.map(
                                        (w, wi) => (
                                          <div
                                            key={wi}
                                            style={{
                                              display: "flex",
                                              gap: 12,
                                              marginBottom:
                                                wi <
                                                perspData.witnessSequencing
                                                  .predictedOrder.length -
                                                  1
                                                  ? 14
                                                  : 0,
                                              position: "relative",
                                            }}
                                          >
                                            {wi <
                                              perspData.witnessSequencing
                                                .predictedOrder.length -
                                                1 && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  left: 14,
                                                  top: 30,
                                                  bottom: -12,
                                                  width: 2,
                                                  background: dark
                                                    ? "#334155"
                                                    : "#e2e8f0",
                                                }}
                                              />
                                            )}
                                            <div
                                              style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: "50%",
                                                background: "#dc2626",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 12,
                                                fontWeight: 800,
                                                flexShrink: 0,
                                                zIndex: 1,
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              {w.rank}
                                            </div>
                                            <div
                                              style={{
                                                flex: 1,
                                                padding: "9px 12px",
                                                background: dark
                                                  ? "#0f172a"
                                                  : "#fafafa",
                                                border: `1px solid ${th.border}`,
                                                borderRadius: 6,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: 11,
                                                  fontWeight: 700,
                                                  color: th.text,
                                                  marginBottom: 4,
                                                  letterSpacing: "-0.1px",
                                                }}
                                              >
                                                {w.witness}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: 9,
                                                  color: th.muted,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                {w.rationale}
                                              </div>
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      background: th.card,
                                      borderRadius: 10,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #10b981",
                                      padding: "12px 14px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#10b981",
                                        fontFamily: "monospace",
                                        letterSpacing: 1,
                                        marginBottom: 5,
                                        fontWeight: 700,
                                      }}
                                    >
                                      ✓ YOUR CROSS-EXAM STRATEGY
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {
                                        perspData.witnessSequencing
                                          .crossExamStrategy
                                      }
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* SECTION 5: PRESSURE POINTS */}
                            {sec === "pressure" && perspData.pressurePoints && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #dc2626",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      fontSize: 11,
                                    }}
                                  >
                                    ⚡ Their Pressure Points.
                                  </span>{" "}
                                  What events in the litigation would most
                                  change the opponent's calculus? Each pressure
                                  point is a lever - if it occurs, it moves
                                  them. The attorney can sequence their own
                                  strategic moves to maximize multi-front
                                  pressure.
                                </div>
                                {perspData.pressurePoints.map((p, pi) => (
                                  <div
                                    key={pi}
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #f59e0b",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        marginBottom: 8,
                                        gap: 10,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <div style={{ flex: 1, minWidth: 200 }}>
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#f59e0b",
                                            fontFamily: "monospace",
                                            letterSpacing: 1,
                                            marginBottom: 4,
                                            fontWeight: 700,
                                          }}
                                        >
                                          EVENT
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: th.text,
                                            letterSpacing: "-0.2px",
                                          }}
                                        >
                                          {p.event}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 7,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            color: th.faint,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.5,
                                            fontWeight: 700,
                                          }}
                                        >
                                          LIKELIHOOD
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 800,
                                            color:
                                              p.likelihood >= 60
                                                ? "#dc2626"
                                                : p.likelihood >= 40
                                                  ? "#f59e0b"
                                                  : "#10b981",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {p.likelihood}%
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#f59e0b10",
                                        borderLeft: "3px solid #f59e0b",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#f59e0b",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        IMPACT ON OPPONENT'S CALCULUS
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {p.impact}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#10b98110",
                                        borderLeft: "3px solid #10b981",
                                        borderRadius: "0 5px 5px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✓ YOUR PRE-POSITIONED MOVE
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {p.ourMove}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ),
                      );
                    })()}
                  </div>
                );
              })()}
            {tab === "traps" &&
              (() => {
                const data = PROC_TRAPS_DATA[caseId] || PROC_TRAPS_DATA.juul;
                const persp = trapsPersp;
                const isActive = persp !== "all";
                const sec = trapsSection;
                const perspData =
                  persp === "defendant" ? data.defendant : data.plaintiff;
                const perspBg =
                  persp === "plaintiff"
                    ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                    : persp === "defendant"
                      ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                      : dark
                        ? "linear-gradient(135deg,#0f172a,#1e293b)"
                        : "linear-gradient(135deg,#f1f5f9,#e2e8f0)";
                const perspBorder =
                  persp === "plaintiff"
                    ? "#dc262655"
                    : persp === "defendant"
                      ? "#3b82f655"
                      : th.border;
                const SEV_C = {
                  critical: "#dc2626",
                  high: "#ef4444",
                  medium: "#f59e0b",
                  low: "#6b7280",
                };
                const sectionBtn = (k, emoji, label, count) => (
                  <button
                    key={k}
                    onClick={() => setTrapsSection(k)}
                    style={{
                      padding: "9px 13px",
                      fontSize: 10,
                      fontWeight: sec === k ? 700 : 500,
                      background: sec === k ? "#f59e0b" : "transparent",
                      color: sec === k ? "#fff" : th.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                    {count > 0 && (
                      <span
                        style={{
                          fontSize: 8,
                          padding: "1px 6px",
                          background:
                            sec === k ? "rgba(255,255,255,0.25)" : "#f59e0b22",
                          color: sec === k ? "#fff" : "#f59e0b",
                          borderRadius: 8,
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
                return (
                  <div
                    style={{
                      maxWidth: 1200,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      style={{
                        background: "linear-gradient(135deg,#f59e0b,#b45309)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#fef3c7",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        PROCEDURAL TRAPS · WATCH THE DOCKET FOR TRAPS BEING SET
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Procedural Traps
                      </h1>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#fef3c7",
                          lineHeight: 1.5,
                        }}
                      >
                        Real-time procedural intelligence · active trap
                        monitoring · waiver risk alerts · deadline triggers ·
                        counsel patterns · standing-order conflicts
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background: perspBg,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${perspBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: isActive ? "#fff" : th.faint,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isActive ? "#fff" : th.text,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {persp === "plaintiff"
                            ? "🔴 Plaintiff Counsel - your procedural intelligence"
                            : persp === "defendant"
                              ? "🔵 Defendant Counsel - your procedural intelligence"
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: isActive
                              ? "rgba(255,255,255,0.85)"
                              : th.muted,
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {persp === "plaintiff"
                            ? "Active trap monitoring · waiver alerts · deadline triggers · opposing counsel patterns · standing order conflicts from plaintiff vantage"
                            : persp === "defendant"
                              ? "Active trap monitoring · waiver alerts · deadline triggers · opposing counsel patterns · standing order conflicts from defendant vantage"
                              : "Toggle to load procedural intelligence from one side's vantage"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setTrapsPersp(v)}
                            style={{
                              padding: "7px 13px",
                              fontSize: 10,
                              fontWeight: persp === v ? 700 : 500,
                              background:
                                persp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color: !isActive
                                ? persp === v
                                  ? th.text
                                  : th.muted
                                : persp === v
                                  ? "#fff"
                                  : "rgba(255,255,255,0.7)",
                              border:
                                persp === v
                                  ? !isActive
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${!isActive ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SECTION SELECTOR - always visible. Counts merge plaintiff + defendant on "all" */}
                    {(() => {
                      const cnt = (k) =>
                        persp === "all"
                          ? (data.plaintiff[k] || []).length +
                            (data.defendant[k] || []).length
                          : (perspData[k] || []).length;
                      return (
                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            marginBottom: 14,
                            padding: 5,
                            background: th.surface,
                            borderRadius: 9,
                            border: `1px solid ${th.border}`,
                            overflowX: "auto",
                          }}
                        >
                          {sectionBtn(
                            "monitor",
                            "🎯",
                            "Active Trap Monitoring",
                            cnt("activeMonitoring"),
                          )}
                          {sectionBtn(
                            "waivers",
                            "🚨",
                            "Waiver Risk Alerts",
                            cnt("waiverRisks"),
                          )}
                          {sectionBtn(
                            "deadlines",
                            "⏰",
                            "Deadline Traps",
                            cnt("deadlineTraps"),
                          )}
                          {sectionBtn(
                            "counsel",
                            "🔍",
                            "Counsel Patterns",
                            cnt("counselPatterns"),
                          )}
                          {sectionBtn(
                            "orders",
                            "📜",
                            "Standing Order Conflicts",
                            cnt("standingOrders"),
                          )}
                        </div>
                      );
                    })()}

                    {(() => {
                      const sidesToRender =
                        persp === "all"
                          ? [
                              [
                                "plaintiff",
                                data.plaintiff,
                                "#ef4444",
                                "🔴 PLAINTIFF VIEW - traps facing plaintiff & opposing-counsel patterns",
                              ],
                              [
                                "defendant",
                                data.defendant,
                                "#3b82f6",
                                "🔵 DEFENDANT VIEW - traps facing defendant & opposing-counsel patterns",
                              ],
                            ]
                          : [
                              [
                                persp,
                                perspData,
                                persp === "plaintiff" ? "#ef4444" : "#3b82f6",
                                persp === "plaintiff"
                                  ? "🔴 YOUR VIEW (PLAINTIFF)"
                                  : "🔵 YOUR VIEW (DEFENDANT)",
                              ],
                            ];
                      return sidesToRender.map(
                        ([sideKey, perspData, sideC, sideLabel], sIdx) => (
                          <div
                            key={sideKey}
                            style={{
                              marginTop: persp === "all" && sIdx > 0 ? 22 : 0,
                              paddingTop: persp === "all" && sIdx > 0 ? 14 : 0,
                              borderTop:
                                persp === "all" && sIdx > 0
                                  ? `2px dashed ${th.border}`
                                  : "none",
                            }}
                          >
                            {persp === "all" && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 10,
                                  padding: "6px 12px",
                                  background: sideC + "15",
                                  border: `1px solid ${sideC}55`,
                                  borderRadius: 7,
                                  borderLeft: `4px solid ${sideC}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: sideC,
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                  }}
                                >
                                  {sideLabel}
                                </span>
                              </div>
                            )}

                            {/* SECTION 1: ACTIVE TRAP MONITORING */}
                            {sec === "monitor" && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #f59e0b",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#f59e0b",
                                      fontSize: 11,
                                    }}
                                  >
                                    🎯 Active Trap Monitoring.
                                  </span>{" "}
                                  The AI reads every filing by opposing counsel
                                  and flags procedural moves that may be setting
                                  up a trap. Each entry: observation · analysis
                                  · warning · reference.
                                </div>
                                {(perspData.activeMonitoring || []).map((t) => {
                                  const sevC = SEV_C[t.severity] || "#6b7280";
                                  return (
                                    <div
                                      key={t.id}
                                      style={{
                                        background: th.card,
                                        borderRadius: 11,
                                        border: `1px solid ${th.border}`,
                                        borderLeft: `4px solid ${sevC}`,
                                        padding: "14px 16px",
                                        marginBottom: 11,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          flexWrap: "wrap",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 8px",
                                            background: sevC + "22",
                                            color: sevC,
                                            border: `1px solid ${sevC}55`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {t.severity}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 8,
                                            color: th.dim,
                                            fontFamily: "monospace",
                                            fontWeight: 600,
                                            marginLeft: "auto",
                                          }}
                                        >
                                          {t.time}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: th.text,
                                          marginBottom: 8,
                                          letterSpacing: "-0.2px",
                                        }}
                                      >
                                        {t.observation}
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderLeft: "3px solid #a78bfa",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 7,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#a78bfa",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          ✨ AI ANALYSIS
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {t.analysis}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#f59e0b15",
                                          borderLeft: "3px solid #f59e0b",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 7,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#f59e0b",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          ⚠️ WARNING / RECOMMENDED RESPONSE
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {t.warning}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          color: th.faint,
                                          fontFamily: "monospace",
                                          fontStyle: "italic",
                                          marginTop: 5,
                                        }}
                                      >
                                        📎 {t.ref}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* SECTION 2: WAIVER RISK ALERTS */}
                            {sec === "waivers" && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #dc2626",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      fontSize: 11,
                                    }}
                                  >
                                    🚨 Waiver Risk Alerts.
                                  </span>{" "}
                                  The most dangerous procedural trap is the one
                                  that triggers silently - an argument waived by
                                  not being raised, an affirmative defense
                                  forfeited by not being pleaded, an appellate
                                  issue lost by not making an offer of proof.
                                </div>
                                {(perspData.waiverRisks || []).map((w) => {
                                  const sevC = SEV_C[w.severity] || "#6b7280";
                                  return (
                                    <div
                                      key={w.id}
                                      style={{
                                        background: th.card,
                                        borderRadius: 11,
                                        border: `1px solid ${th.border}`,
                                        borderLeft: `4px solid ${sevC}`,
                                        padding: "14px 16px",
                                        marginBottom: 11,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          flexWrap: "wrap",
                                          marginBottom: 7,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 8px",
                                            background: sevC + "22",
                                            color: sevC,
                                            border: `1px solid ${sevC}55`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {w.severity}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 8,
                                            color: th.dim,
                                            fontFamily: "monospace",
                                            fontWeight: 600,
                                            marginLeft: "auto",
                                          }}
                                        >
                                          {w.time}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: th.text,
                                          marginBottom: 8,
                                          letterSpacing: "-0.2px",
                                        }}
                                      >
                                        {w.ruling}
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderLeft: "3px solid #6b728066",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: th.faint,
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          SITUATION
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {w.situation}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          padding: "10px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fefce8",
                                          border: "2px dashed #f59e0b66",
                                          borderRadius: 6,
                                          marginBottom: 8,
                                          position: "relative",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#d97706",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 5,
                                          }}
                                        >
                                          📜 REQUIRED LANGUAGE TO PRESERVE
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.65,
                                            fontFamily: "Georgia, serif",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          "{w.requiredLanguage}"
                                        </div>
                                        <button
                                          onClick={() => {
                                            navigator.clipboard &&
                                              navigator.clipboard.writeText(
                                                w.requiredLanguage,
                                              );
                                            toast(
                                              "Preservation language copied",
                                              "success",
                                            );
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            fontSize: 8,
                                            padding: "3px 9px",
                                            background: "#d97706",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📋 Copy
                                        </button>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#dc262610",
                                          borderLeft: "3px solid #dc2626",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 6,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#dc2626",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          ⚠️ CONSEQUENCE OF FAILURE
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {w.consequence}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          color: th.faint,
                                          fontFamily: "monospace",
                                          fontStyle: "italic",
                                          marginTop: 5,
                                        }}
                                      >
                                        📚 {w.authority}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* SECTION 3: DEADLINE TRAPS */}
                            {sec === "deadlines" && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #ef4444",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#ef4444",
                                      fontSize: 11,
                                    }}
                                  >
                                    ⏰ Deadline Traps.
                                  </span>{" "}
                                  Deadlines that derive from triggers rather
                                  than being stated in the scheduling order.
                                  These are the deadlines buried in CMC minute
                                  entries, standing orders, and scheduling order
                                  paragraphs that nobody calendars.
                                </div>
                                {(perspData.deadlineTraps || []).map((d) => {
                                  const sevC = SEV_C[d.severity] || "#6b7280";
                                  return (
                                    <div
                                      key={d.id}
                                      style={{
                                        background: th.card,
                                        borderRadius: 11,
                                        border: `1px solid ${th.border}`,
                                        borderLeft: `4px solid ${sevC}`,
                                        padding: "14px 16px",
                                        marginBottom: 11,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          flexWrap: "wrap",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 8px",
                                            background: sevC + "22",
                                            color: sevC,
                                            border: `1px solid ${sevC}55`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {d.severity}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 8,
                                            color: "#ef4444",
                                            fontFamily: "monospace",
                                            fontWeight: 700,
                                            marginLeft: "auto",
                                          }}
                                        >
                                          ⏰ {d.time}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#ef444415",
                                          borderLeft: "3px solid #ef4444",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          TRIGGER
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: th.text,
                                            marginBottom: 5,
                                          }}
                                        >
                                          {d.trigger}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                            marginTop: 7,
                                          }}
                                        >
                                          DEADLINE
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {d.deadline}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: dark
                                            ? "#0f172a"
                                            : "#fafafa",
                                          borderLeft: "3px solid #6b728066",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 7,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: th.faint,
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          SOURCE
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 9,
                                            color: th.muted,
                                            lineHeight: 1.55,
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {d.source}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#a78bfa10",
                                          borderLeft: "3px solid #a78bfa",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 7,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#a78bfa",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          ✨ AI ALERT
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {d.aiAlert}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          padding: "9px 12px",
                                          background: "#10b98110",
                                          borderLeft: "3px solid #10b981",
                                          borderRadius: "0 5px 5px 0",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#10b981",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 3,
                                          }}
                                        >
                                          ✓ ACTION ITEM
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: th.text,
                                            lineHeight: 1.55,
                                          }}
                                        >
                                          {d.actionItem}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* SECTION 4: COUNSEL PATTERNS */}
                            {sec === "counsel" && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #3b82f6",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#3b82f6",
                                      fontSize: 11,
                                    }}
                                  >
                                    🔍 Opposing Counsel Procedural Patterns.
                                  </span>{" "}
                                  Has opposing counsel consistently filed things
                                  late? Filed early to limit response time?
                                  Moved for extensions on the eve of deadlines
                                  to impose cost? Patterns become intelligence
                                  about how to respond to the next maneuver.
                                </div>
                                {(perspData.counselPatterns || []).map((c) => (
                                  <div
                                    key={c.id}
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #3b82f6",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#3b82f6",
                                        fontFamily: "monospace",
                                        letterSpacing: 1,
                                        marginBottom: 5,
                                        fontWeight: 700,
                                      }}
                                    >
                                      OPPOSING COUNSEL
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 8,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {c.counselName}
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fafafa",
                                        borderLeft: "3px solid #3b82f666",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        OBSERVED PATTERN
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {c.pattern}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#a78bfa10",
                                        borderLeft: "3px solid #a78bfa",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#a78bfa",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✨ AI INTERPRETATION
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {c.interpretation}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#10b98110",
                                        borderLeft: "3px solid #10b981",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✓ YOUR MOVE
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {c.ourMove}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "7px 11px",
                                        background: dark
                                          ? "#1e293b"
                                          : "#f1f5f9",
                                        borderRadius: 5,
                                        fontSize: 9,
                                      }}
                                    >
                                      <div
                                        style={{
                                          color: th.faint,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                          marginBottom: 2,
                                        }}
                                      >
                                        👨‍⚖️ JUDGE'S RESPONSE TO PATTERN
                                      </div>
                                      <div
                                        style={{
                                          color: th.muted,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {c.judgeResponse}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SECTION 5: STANDING ORDER CONFLICTS */}
                            {sec === "orders" && (
                              <div>
                                <div
                                  style={{
                                    padding: "10px 14px",
                                    background: dark ? "#1e293b" : "#f8fafc",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 12,
                                    fontSize: 10,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#a78bfa",
                                      fontSize: 11,
                                    }}
                                  >
                                    📜 Standing Order Conflicts.
                                  </span>{" "}
                                  Every judge has standing orders that create
                                  traps for attorneys who don't know them. The
                                  AI cross-references every upcoming filing
                                  against standing-order requirements and flags
                                  conflicts before the filing is made.
                                </div>
                                {(perspData.standingOrders || []).map((o) => (
                                  <div
                                    key={o.id}
                                    style={{
                                      background: th.card,
                                      borderRadius: 11,
                                      border: `1px solid ${th.border}`,
                                      borderLeft: "4px solid #a78bfa",
                                      padding: "14px 16px",
                                      marginBottom: 11,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: "#a78bfa",
                                        fontFamily: "monospace",
                                        letterSpacing: 1,
                                        marginBottom: 5,
                                        fontWeight: 700,
                                      }}
                                    >
                                      STANDING ORDER
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 8,
                                        letterSpacing: "-0.2px",
                                      }}
                                    >
                                      {o.order}
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fafafa",
                                        borderLeft: "3px solid #a78bfa66",
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        REQUIREMENT
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {o.requirement}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: dark
                                          ? "#1e293b"
                                          : "#f1f5f9",
                                        borderRadius: 5,
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        RECENT FILING
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {o.recentFiling}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: o.conflict.startsWith(
                                          "None",
                                        )
                                          ? "#10b98110"
                                          : "#ef444415",
                                        borderLeft: `3px solid ${o.conflict.startsWith("None") ? "#10b981" : "#ef4444"}`,
                                        borderRadius: "0 5px 5px 0",
                                        marginBottom: 7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: o.conflict.startsWith("None")
                                            ? "#10b981"
                                            : "#ef4444",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        {o.conflict.startsWith("None")
                                          ? "✓ COMPLIANCE"
                                          : "⚠️ CONFLICT FLAG"}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {o.conflict}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "9px 12px",
                                        background: "#10b98110",
                                        borderLeft: "3px solid #10b981",
                                        borderRadius: "0 5px 5px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#10b981",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ✓ RECOMMENDATION
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {o.recommendation}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ),
                      );
                    })()}
                  </div>
                );
              })()}
            {tab === "theories" &&
              (() => {
                const theories = THEORIES_DATA[caseId] || THEORIES_DATA.juul;
                const battle = BATTLE_ISSUES[caseId] || BATTLE_ISSUES.juul;
                const persp = theoriesPersp;
                const sidesToRender =
                  persp === "all"
                    ? [
                        [
                          "plaintiff",
                          theories.plaintiff,
                          "#ef4444",
                          "🔴 PLAINTIFF THEORIES - your strategic positions",
                        ],
                        [
                          "defendant",
                          theories.defendant,
                          "#3b82f6",
                          "🔵 DEFENDANT THEORIES - your strategic defenses",
                        ],
                      ]
                    : [
                        [
                          persp,
                          theories[persp],
                          persp === "plaintiff" ? "#ef4444" : "#3b82f6",
                          persp === "plaintiff"
                            ? "🔴 PLAINTIFF THEORIES - your strategic positions"
                            : "🔵 DEFENDANT THEORIES - your strategic defenses",
                        ],
                      ];
                const tlOf = (t) => {
                  const s = t.elements.map((e) => e.status);
                  return s.includes("red")
                    ? "red"
                    : s.includes("amber")
                      ? "amber"
                      : "green";
                };
                const tlC = {
                  red: "#ef4444",
                  amber: "#f59e0b",
                  green: "#10b981",
                };
                const scoreC = (s) =>
                  s >= 70 ? "#10b981" : s >= 50 ? "#f59e0b" : "#ef4444";
                const ScoreBar = ({ label, value }) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        width: 90,
                        color: th.muted,
                        fontFamily: "monospace",
                        letterSpacing: 0.3,
                      }}
                    >
                      {label}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 5,
                        background: th.surface,
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: value + "%",
                          height: "100%",
                          background: scoreC(value),
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 9,
                        width: 25,
                        textAlign: "right",
                        color: th.text,
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                );
                return (
                  <div
                    style={{
                      maxWidth: 1200,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg,#7c3aed,#5b21b6)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#e9d5ff",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        THEORIES OF THE CASE · LIVE STRATEGIC ASSESSMENT
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Theories of the Case
                      </h1>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 10, color: "#e9d5ff" }}>
                          {theories.plaintiff.length} plaintiff theories ·{" "}
                          {theories.defendant.length} defendant theories ·{" "}
                          {battle.length} disputed issues
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            color: "#c4b5fd",
                            fontFamily: "monospace",
                            padding: "2px 8px",
                            background: "#ffffff15",
                            borderRadius: 10,
                          }}
                        >
                          SCORES UPDATE LIVE FROM EVIDENCE + DOCKET
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 14,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          fontWeight: 600,
                          letterSpacing: 1,
                          fontFamily: "monospace",
                          marginRight: 4,
                        }}
                      >
                        VIEW
                      </span>
                      {[
                        ["dashboard", "📊", "Dashboard"],
                        ["battle", "⚔️", "Battle View"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => setTheoryViewMode(k)}
                          style={{
                            padding: "7px 13px",
                            fontSize: 10,
                            fontWeight: theoryViewMode === k ? 700 : 500,
                            background:
                              theoryViewMode === k ? "#7c3aed" : th.surface,
                            color: theoryViewMode === k ? "#fff" : th.muted,
                            border: `1px solid ${theoryViewMode === k ? "#7c3aed" : th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                        </button>
                      ))}
                      <div style={{ flex: 1 }} />
                      <span
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          fontWeight: 600,
                          letterSpacing: 1,
                          fontFamily: "monospace",
                          marginRight: 4,
                        }}
                      >
                        PERSPECTIVE
                      </span>
                      {[
                        ["all", "⚖️", "Both"],
                        ["plaintiff", "🔴", "Plaintiff"],
                        ["defendant", "🔵", "Defendant"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => {
                            setTheoriesPersp(k);
                            setTheoryDetailId(null);
                          }}
                          style={{
                            padding: "7px 13px",
                            fontSize: 10,
                            fontWeight: persp === k ? 700 : 500,
                            background:
                              persp === k
                                ? k === "plaintiff"
                                  ? "#ef4444"
                                  : k === "defendant"
                                    ? "#3b82f6"
                                    : "#a78bfa"
                                : th.surface,
                            color: persp === k ? "#fff" : th.muted,
                            border: `1px solid ${persp === k ? "transparent" : th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                        </button>
                      ))}
                    </div>
                    {theoryViewMode === "dashboard" &&
                      sidesToRender.map(
                        ([sideKey, theoryList, sideC, sideLabel], sIdx) => (
                          <div
                            key={sideKey}
                            style={{
                              marginTop: persp === "all" && sIdx > 0 ? 20 : 0,
                              paddingTop: persp === "all" && sIdx > 0 ? 14 : 0,
                              borderTop:
                                persp === "all" && sIdx > 0
                                  ? `2px dashed ${th.border}`
                                  : "none",
                            }}
                          >
                            {persp === "all" && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 12,
                                  padding: "7px 13px",
                                  background: sideC + "15",
                                  border: `1px solid ${sideC}55`,
                                  borderRadius: 7,
                                  borderLeft: `4px solid ${sideC}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: sideC,
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                  }}
                                >
                                  {sideLabel}
                                </span>
                              </div>
                            )}
                            {theoryList.map((t, ti) => {
                              const tl = tlOf(t);
                              const isOpen =
                                theoryDetailId === sideKey + "_" + t.id;
                              return (
                                <div
                                  key={t.id}
                                  style={{
                                    background: th.card,
                                    borderRadius: 11,
                                    border: `1px solid ${th.border}`,
                                    borderLeft: `4px solid ${sideC}`,
                                    marginBottom: 11,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    onClick={() =>
                                      setTheoryDetailId(
                                        isOpen ? null : sideKey + "_" + t.id,
                                      )
                                    }
                                    style={{
                                      padding: "14px 18px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 14,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <div
                                      style={{
                                        flexShrink: 0,
                                        width: 54,
                                        height: 54,
                                        borderRadius: 10,
                                        background: scoreC(t.score) + "18",
                                        border: `2px solid ${scoreC(t.score)}`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 18,
                                          fontWeight: 800,
                                          color: scoreC(t.score),
                                          lineHeight: 1,
                                        }}
                                      >
                                        {t.score}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 6,
                                          color: scoreC(t.score),
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        /100
                                      </span>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 7,
                                          marginBottom: 4,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <h3
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            margin: 0,
                                            color: th.text,
                                            letterSpacing: "-0.2px",
                                          }}
                                        >
                                          {t.name}
                                        </h3>
                                        <span
                                          title={"Worst element status: " + tl}
                                          style={{
                                            display: "inline-block",
                                            width: 11,
                                            height: 11,
                                            borderRadius: "50%",
                                            background: tlC[tl],
                                            boxShadow: `0 0 0 2px ${tlC[tl]}33`,
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: 7,
                                            color: scoreC(t.score),
                                            background: scoreC(t.score) + "18",
                                            padding: "2px 7px",
                                            borderRadius: 8,
                                            fontFamily: "monospace",
                                            fontWeight: 700,
                                            letterSpacing: 0.3,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {t.score >= 70
                                            ? "strong"
                                            : t.score >= 50
                                              ? "moderate"
                                              : "weak"}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {t.deps.text.split(".")[0]}.
                                      </div>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 18,
                                        color: th.muted,
                                        opacity: 0.7,
                                        transition: "transform 0.2s",
                                        transform: isOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                      }}
                                    >
                                      ▾
                                    </span>
                                  </div>
                                  {!isOpen && (
                                    <div
                                      style={{
                                        padding: "0 18px 14px",
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit,minmax(150px,1fr))",
                                        gap: 14,
                                      }}
                                    >
                                      <div>
                                        <ScoreBar
                                          label="EVIDENCE"
                                          value={t.dim.evid}
                                        />
                                        <ScoreBar
                                          label="CASELAW"
                                          value={t.dim.caselaw}
                                        />
                                      </div>
                                      <div>
                                        <ScoreBar
                                          label="EXPERTS"
                                          value={t.dim.experts}
                                        />
                                        <ScoreBar
                                          label="MOTION VULN"
                                          value={t.dim.mvuln}
                                        />
                                      </div>
                                      <div>
                                        <ScoreBar
                                          label="DAMAGES Q"
                                          value={t.dim.damages}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {isOpen && (
                                    <div
                                      style={{
                                        borderTop: `1px solid ${th.border}`,
                                        background: dark
                                          ? "#0a0f1c"
                                          : "#fafbfc",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: 1,
                                          padding: 8,
                                          background: th.surface,
                                          borderBottom: `1px solid ${th.border}`,
                                          overflowX: "auto",
                                        }}
                                      >
                                        {[
                                          [
                                            "elements",
                                            "🎯",
                                            "Element Proof Board",
                                          ],
                                          ["deps", "🔗", "Dependency Chain"],
                                          ["narrative", "📖", "Jury Narrative"],
                                          ["vuln", "🛡️", "Vulnerability Radar"],
                                          [
                                            "timeline",
                                            "📈",
                                            "Strength Timeline",
                                          ],
                                          ["jinst", "⚖️", "Jury Instruction"],
                                        ]
                                          .concat(
                                            caseId === "neuro" &&
                                              t.id === "direct"
                                              ? [
                                                  [
                                                    "election",
                                                    "🎯",
                                                    "Theory Election",
                                                  ],
                                                ]
                                              : [],
                                          )
                                          .map(([k, e, l]) => (
                                            <button
                                              key={k}
                                              onClick={() => setTheorySubTab(k)}
                                              style={{
                                                padding: "6px 12px",
                                                fontSize: 9,
                                                fontWeight:
                                                  theorySubTab === k
                                                    ? 700
                                                    : 500,
                                                background:
                                                  theorySubTab === k
                                                    ? "#7c3aed"
                                                    : "transparent",
                                                color:
                                                  theorySubTab === k
                                                    ? "#fff"
                                                    : th.muted,
                                                border: "none",
                                                borderRadius: 5,
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                              }}
                                            >
                                              <span>{e}</span>
                                              <span>{l}</span>
                                            </button>
                                          ))}
                                      </div>
                                      <div style={{ padding: "16px 20px" }}>
                                        {theorySubTab === "elements" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              Each legal element of the theory
                                              mapped to evidence, color-coded by
                                              proof status. Tap any red element
                                              to see what evidence would move it
                                              to amber or green.
                                            </div>
                                            {t.elements.map((el, ei) => {
                                              const elC = tlC[el.status];
                                              return (
                                                <div
                                                  key={ei}
                                                  style={{
                                                    background: th.card,
                                                    border: `1px solid ${th.border}`,
                                                    borderLeft: `4px solid ${elC}`,
                                                    borderRadius: 8,
                                                    padding: "12px 14px",
                                                    marginBottom: 8,
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 8,
                                                      marginBottom: 6,
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        display: "inline-block",
                                                        width: 9,
                                                        height: 9,
                                                        borderRadius: "50%",
                                                        background: elC,
                                                        flexShrink: 0,
                                                      }}
                                                    />
                                                    <span
                                                      style={{
                                                        fontSize: 7,
                                                        fontFamily: "monospace",
                                                        color: elC,
                                                        letterSpacing: 1,
                                                        fontWeight: 700,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Element {ei + 1} ·{" "}
                                                      {el.status}
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: 12,
                                                      fontWeight: 700,
                                                      color: th.text,
                                                      marginBottom: 5,
                                                    }}
                                                  >
                                                    {el.name}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: 9,
                                                      color: th.muted,
                                                      lineHeight: 1.55,
                                                      marginBottom:
                                                        el.ev.length > 0
                                                          ? 9
                                                          : 0,
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    {el.explainer}
                                                  </div>
                                                  {el.ev.length > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 3,
                                                      }}
                                                    >
                                                      {el.ev.map((e, evi) => (
                                                        <div
                                                          key={evi}
                                                          style={{
                                                            display: "flex",
                                                            gap: 7,
                                                            padding: "5px 9px",
                                                            background:
                                                              e.supportive
                                                                ? "#10b98108"
                                                                : "#ef444408",
                                                            borderLeft: `2px solid ${e.supportive ? "#10b981" : "#ef4444"}55`,
                                                            borderRadius:
                                                              "0 4px 4px 0",
                                                            fontSize: 9,
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              color:
                                                                e.supportive
                                                                  ? "#10b981"
                                                                  : "#ef4444",
                                                              fontFamily:
                                                                "monospace",
                                                              fontSize: 7,
                                                              fontWeight: 700,
                                                              flexShrink: 0,
                                                              minWidth: 78,
                                                              letterSpacing: 0.3,
                                                            }}
                                                          >
                                                            {e.id}
                                                          </span>
                                                          <span
                                                            style={{
                                                              color: th.text,
                                                              lineHeight: 1.5,
                                                              flex: 1,
                                                            }}
                                                          >
                                                            {e.label}
                                                          </span>
                                                          {e.caveat && (
                                                            <span
                                                              style={{
                                                                fontSize: 7,
                                                                color:
                                                                  "#f59e0b",
                                                                fontStyle:
                                                                  "italic",
                                                                fontFamily:
                                                                  "monospace",
                                                              }}
                                                            >
                                                              caveat: {e.caveat}
                                                            </span>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {el.status === "red" && (
                                                    <div
                                                      style={{
                                                        marginTop: 10,
                                                        padding: "7px 11px",
                                                        background: "#ef444415",
                                                        border:
                                                          "1px dashed #ef444455",
                                                        borderRadius: 5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 7,
                                                      }}
                                                    >
                                                      <span
                                                        style={{ fontSize: 11 }}
                                                      >
                                                        🔗
                                                      </span>
                                                      <span
                                                        style={{
                                                          fontSize: 9,
                                                          color: "#ef4444",
                                                          fontWeight: 600,
                                                        }}
                                                      >
                                                        Linked to Evidence Gaps
                                                        tab - task created
                                                        automatically
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {theorySubTab === "deps" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              How this theory relates to others
                                              strategically - what survives if
                                              it fails, and what falls with it.
                                            </div>
                                            <div
                                              style={{
                                                background: th.card,
                                                border: `1px solid ${th.border}`,
                                                borderRadius: 9,
                                                padding: "16px 20px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 8,
                                                  marginBottom: 10,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    color: "#a78bfa",
                                                    background: "#a78bfa15",
                                                    padding: "2px 8px",
                                                    borderRadius: 8,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.5,
                                                    textTransform: "uppercase",
                                                  }}
                                                >
                                                  {t.deps.role}
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: th.text,
                                                  }}
                                                >
                                                  {t.name}
                                                </span>
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: 11,
                                                  color: th.text,
                                                  lineHeight: 1.7,
                                                  marginBottom: 14,
                                                }}
                                              >
                                                {t.deps.text}
                                              </div>
                                              <div
                                                style={{
                                                  padding: "11px 14px",
                                                  background: dark
                                                    ? "#1e1b4b"
                                                    : "#f5f3ff",
                                                  borderLeft:
                                                    "3px solid #a78bfa",
                                                  borderRadius: "0 6px 6px 0",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: 7,
                                                    color: "#7c3aed",
                                                    fontFamily: "monospace",
                                                    fontWeight: 700,
                                                    letterSpacing: 1,
                                                    marginBottom: 5,
                                                  }}
                                                >
                                                  STRATEGIC IMPLICATION
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 10,
                                                    color: th.text,
                                                    lineHeight: 1.6,
                                                  }}
                                                >
                                                  {t.deps.role === "primary"
                                                    ? "Lead theory - concentrate resources here. Fallback theories defined."
                                                    : t.deps.role ===
                                                        "primary-fragile"
                                                      ? "Lead theory but vulnerable. Fallbacks viable - case doesn't collapse if this theory fails."
                                                      : t.deps.role ===
                                                          "durable"
                                                        ? "Most durable theory. Survives independent of other theories. Defensible at SJ."
                                                        : t.deps.role ===
                                                            "survivor"
                                                          ? "Most resilient. Independent of other theories. Should always be preserved."
                                                          : t.deps.role ===
                                                              "backup"
                                                            ? "Backup theory. Activate only if primary theory fails."
                                                            : t.deps.role ===
                                                                "narrowest"
                                                              ? "Narrowest fallback. High legal-novelty risk but streamlined path if admitted."
                                                              : t.deps.role ===
                                                                  "narrow"
                                                                ? "Narrow theory. Effective only against specific defendants/circumstances."
                                                                : t.deps
                                                                      .role ===
                                                                    "enhancement"
                                                                  ? "Damages multiplier. Doesn't affect liability - only damages amount."
                                                                  : t.deps
                                                                        .role ===
                                                                      "alternative"
                                                                    ? "Alternative path to liability. Independent of other theories."
                                                                    : t.deps
                                                                          .role ===
                                                                        "narrowing"
                                                                      ? "Narrowing defense. Reduces scope rather than eliminating."
                                                                      : "Strategic role under analysis."}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {theorySubTab === "narrative" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              Toggle between formal legal
                                              language (for briefs) and
                                              plain-English jury narrative (for
                                              opening/closing).
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: 5,
                                                marginBottom: 12,
                                                padding: 4,
                                                background: th.surface,
                                                borderRadius: 6,
                                                width: "fit-content",
                                              }}
                                            >
                                              {[
                                                [
                                                  "legal",
                                                  "📜",
                                                  "Legal Element Form",
                                                ],
                                                [
                                                  "jury",
                                                  "💬",
                                                  "Jury Narrative",
                                                ],
                                              ].map(([k, e, l]) => (
                                                <button
                                                  key={k}
                                                  onClick={() =>
                                                    setTheoryNarrMode(k)
                                                  }
                                                  style={{
                                                    padding: "6px 12px",
                                                    fontSize: 9,
                                                    fontWeight:
                                                      theoryNarrMode === k
                                                        ? 700
                                                        : 500,
                                                    background:
                                                      theoryNarrMode === k
                                                        ? "#a78bfa"
                                                        : "transparent",
                                                    color:
                                                      theoryNarrMode === k
                                                        ? "#fff"
                                                        : th.muted,
                                                    border: "none",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                  }}
                                                >
                                                  <span>{e}</span>
                                                  <span>{l}</span>
                                                </button>
                                              ))}
                                            </div>
                                            <div
                                              style={{
                                                background: th.card,
                                                border: `1px solid ${th.border}`,
                                                borderLeft: `4px solid ${theoryNarrMode === "legal" ? "#3b82f6" : "#10b981"}`,
                                                borderRadius: 9,
                                                padding: "16px 22px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  fontFamily: "monospace",
                                                  color:
                                                    theoryNarrMode === "legal"
                                                      ? "#3b82f6"
                                                      : "#10b981",
                                                  letterSpacing: 1.2,
                                                  fontWeight: 700,
                                                  marginBottom: 8,
                                                  textTransform: "uppercase",
                                                }}
                                              >
                                                {theoryNarrMode === "legal"
                                                  ? "FORMAL LEGAL - for briefs, motions, summary judgment"
                                                  : "JURY NARRATIVE - for opening / closing / framing"}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize:
                                                    theoryNarrMode === "legal"
                                                      ? 11
                                                      : 13,
                                                  color: th.text,
                                                  lineHeight: 1.75,
                                                  fontFamily:
                                                    theoryNarrMode === "jury"
                                                      ? "Georgia, serif"
                                                      : "inherit",
                                                  fontStyle:
                                                    theoryNarrMode === "jury"
                                                      ? "italic"
                                                      : "normal",
                                                }}
                                              >
                                                {t.narrative[theoryNarrMode]}
                                              </div>
                                            </div>
                                            {theoryNarrMode === "jury" && (
                                              <div
                                                style={{
                                                  marginTop: 10,
                                                  padding: "9px 12px",
                                                  background: "#10b98110",
                                                  borderLeft:
                                                    "3px solid #10b981",
                                                  borderRadius: "0 5px 5px 0",
                                                  fontSize: 9,
                                                  color: th.muted,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    color: "#10b981",
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  📌 Strategic frame.
                                                </span>{" "}
                                                This narrative tells the
                                                attorney which facts matter most
                                                for this theory and how to
                                                present them. Feeds into the War
                                                Room opening-statement builder.
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {theorySubTab === "vuln" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              Five vulnerability dimensions
                                              plotted as a radar. Wider area =
                                              more exposure. Asymmetric shapes
                                              flag single points of failure.
                                            </div>
                                            <div
                                              style={{
                                                background: th.card,
                                                border: `1px solid ${th.border}`,
                                                borderRadius: 9,
                                                padding: "20px",
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: 24,
                                                alignItems: "center",
                                              }}
                                            >
                                              {(() => {
                                                const v = t.vuln;
                                                const axes = [
                                                  ["SOL", "sol"],
                                                  ["Preempt", "preempt"],
                                                  ["Causation", "causation"],
                                                  ["Expert Dep", "expertDep"],
                                                  ["Damages Q", "damages"],
                                                ];
                                                const cx = 120,
                                                  cy = 120,
                                                  r = 90;
                                                const pts = axes.map(
                                                  ([, k], i) => {
                                                    const angle =
                                                      (i / 5) * 2 * Math.PI -
                                                      Math.PI / 2;
                                                    const val = v[k];
                                                    const px =
                                                      cx +
                                                      Math.cos(angle) *
                                                        r *
                                                        (val / 100);
                                                    const py =
                                                      cy +
                                                      Math.sin(angle) *
                                                        r *
                                                        (val / 100);
                                                    return {
                                                      px,
                                                      py,
                                                      val,
                                                      angle,
                                                      k,
                                                    };
                                                  },
                                                );
                                                const polyPts = pts
                                                  .map((p) => `${p.px},${p.py}`)
                                                  .join(" ");
                                                const gridLevels = [
                                                  25, 50, 75, 100,
                                                ];
                                                return (
                                                  <svg
                                                    width="240"
                                                    height="240"
                                                    viewBox="0 0 240 240"
                                                    style={{
                                                      margin: "0 auto",
                                                      display: "block",
                                                    }}
                                                  >
                                                    {gridLevels.map((lvl) => {
                                                      const gp = axes
                                                        .map((_, i) => {
                                                          const a =
                                                            (i / 5) *
                                                              2 *
                                                              Math.PI -
                                                            Math.PI / 2;
                                                          return `${cx + Math.cos(a) * r * (lvl / 100)},${cy + Math.sin(a) * r * (lvl / 100)}`;
                                                        })
                                                        .join(" ");
                                                      return (
                                                        <polygon
                                                          key={lvl}
                                                          points={gp}
                                                          fill="none"
                                                          stroke={th.border}
                                                          strokeWidth="1"
                                                          strokeDasharray={
                                                            lvl === 100
                                                              ? ""
                                                              : "2,3"
                                                          }
                                                          opacity="0.5"
                                                        />
                                                      );
                                                    })}
                                                    {axes.map((_, i) => {
                                                      const a =
                                                        (i / 5) * 2 * Math.PI -
                                                        Math.PI / 2;
                                                      return (
                                                        <line
                                                          key={i}
                                                          x1={cx}
                                                          y1={cy}
                                                          x2={
                                                            cx + Math.cos(a) * r
                                                          }
                                                          y2={
                                                            cy + Math.sin(a) * r
                                                          }
                                                          stroke={th.border}
                                                          strokeWidth="1"
                                                          opacity="0.4"
                                                        />
                                                      );
                                                    })}
                                                    <polygon
                                                      points={polyPts}
                                                      fill="#ef444425"
                                                      stroke="#ef4444"
                                                      strokeWidth="2"
                                                    />
                                                    {pts.map((p, i) => (
                                                      <circle
                                                        key={i}
                                                        cx={p.px}
                                                        cy={p.py}
                                                        r="4"
                                                        fill={
                                                          p.val >= 70
                                                            ? "#dc2626"
                                                            : p.val >= 50
                                                              ? "#f59e0b"
                                                              : "#10b981"
                                                        }
                                                        stroke="#fff"
                                                        strokeWidth="1.5"
                                                      />
                                                    ))}
                                                    {axes.map(([lbl], i) => {
                                                      const a =
                                                        (i / 5) * 2 * Math.PI -
                                                        Math.PI / 2;
                                                      const lx =
                                                        cx +
                                                        Math.cos(a) * (r + 18);
                                                      const ly =
                                                        cy +
                                                        Math.sin(a) * (r + 18);
                                                      return (
                                                        <text
                                                          key={i}
                                                          x={lx}
                                                          y={ly}
                                                          textAnchor="middle"
                                                          dominantBaseline="middle"
                                                          fontSize="9"
                                                          fontFamily="system-ui"
                                                          fontWeight="600"
                                                          fill={th.text}
                                                        >
                                                          {lbl}
                                                        </text>
                                                      );
                                                    })}
                                                  </svg>
                                                );
                                              })()}
                                              <div
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 5,
                                                }}
                                              >
                                                {[
                                                  [
                                                    "SOL Risk",
                                                    "sol",
                                                    "Statute of limitations exposure",
                                                  ],
                                                  [
                                                    "Preemption",
                                                    "preempt",
                                                    "Federal/state preemption exposure",
                                                  ],
                                                  [
                                                    "Causation Difficulty",
                                                    "causation",
                                                    "Difficulty of proving causation",
                                                  ],
                                                  [
                                                    "Expert Dependency",
                                                    "expertDep",
                                                    "Reliance on single expert",
                                                  ],
                                                  [
                                                    "Damages Quantifiability",
                                                    "damages",
                                                    "Difficulty quantifying damages",
                                                  ],
                                                ].map(([lbl, k, desc]) => {
                                                  const v = t.vuln[k];
                                                  const c =
                                                    v >= 70
                                                      ? "#dc2626"
                                                      : v >= 50
                                                        ? "#f59e0b"
                                                        : "#10b981";
                                                  return (
                                                    <div
                                                      key={k}
                                                      style={{
                                                        padding: "7px 11px",
                                                        background: th.surface,
                                                        borderRadius: 6,
                                                        borderLeft: `3px solid ${c}`,
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          justifyContent:
                                                            "space-between",
                                                          alignItems: "center",
                                                          marginBottom: 2,
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            fontSize: 9,
                                                            color: th.text,
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          {lbl}
                                                        </span>
                                                        <span
                                                          style={{
                                                            fontSize: 11,
                                                            color: c,
                                                            fontWeight: 800,
                                                            fontFamily:
                                                              "monospace",
                                                          }}
                                                        >
                                                          {v}
                                                        </span>
                                                      </div>
                                                      <div
                                                        style={{
                                                          fontSize: 8,
                                                          color: th.muted,
                                                        }}
                                                      >
                                                        {desc}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                            {(() => {
                                              const maxK = Object.entries(
                                                t.vuln,
                                              ).reduce((a, b) =>
                                                a[1] > b[1] ? a : b,
                                              );
                                              const [mk, mv] = maxK;
                                              if (mv >= 75) {
                                                const lbls = {
                                                  sol: "Statute of Limitations",
                                                  preempt: "Preemption",
                                                  causation: "Causation",
                                                  expertDep:
                                                    "Expert Dependency",
                                                  damages:
                                                    "Damages Quantifiability",
                                                };
                                                return (
                                                  <div
                                                    style={{
                                                      marginTop: 10,
                                                      padding: "9px 13px",
                                                      background: "#dc262615",
                                                      border:
                                                        "1px dashed #dc262655",
                                                      borderRadius: 6,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 8,
                                                    }}
                                                  >
                                                    <span
                                                      style={{ fontSize: 14 }}
                                                    >
                                                      ⚠️
                                                    </span>
                                                    <span
                                                      style={{
                                                        fontSize: 10,
                                                        color: "#dc2626",
                                                        fontWeight: 600,
                                                        lineHeight: 1.5,
                                                      }}
                                                    >
                                                      Single point of failure
                                                      flagged:{" "}
                                                      <strong>
                                                        {lbls[mk]}
                                                      </strong>{" "}
                                                      ({mv}/100). Mitigate
                                                      before trial.
                                                    </span>
                                                  </div>
                                                );
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        )}
                                        {theorySubTab === "timeline" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              Strength score over time.
                                              Auto-populated from docket events.
                                              Tells the litigation arc - which
                                              events changed the case, which
                                              were noise.
                                            </div>
                                            <div
                                              style={{
                                                background: th.card,
                                                border: `1px solid ${th.border}`,
                                                borderRadius: 9,
                                                padding: "16px 20px",
                                              }}
                                            >
                                              {(() => {
                                                const events = t.timeline;
                                                const w = 600,
                                                  h = 200,
                                                  pl = 40,
                                                  pr = 20,
                                                  pt = 20,
                                                  pb = 40;
                                                const iw = w - pl - pr,
                                                  ih = h - pt - pb;
                                                const xStep =
                                                  events.length > 1
                                                    ? iw / (events.length - 1)
                                                    : 0;
                                                const points = events.map(
                                                  (e, i) => ({
                                                    x: pl + i * xStep,
                                                    y:
                                                      pt +
                                                      ih -
                                                      (e.score / 100) * ih,
                                                    ...e,
                                                  }),
                                                );
                                                const path = points
                                                  .map(
                                                    (p, i) =>
                                                      `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`,
                                                  )
                                                  .join(" ");
                                                return (
                                                  <svg
                                                    viewBox={`0 0 ${w} ${h}`}
                                                    style={{
                                                      width: "100%",
                                                      maxHeight: 240,
                                                      display: "block",
                                                    }}
                                                  >
                                                    {[0, 25, 50, 75, 100].map(
                                                      (g) => {
                                                        const y =
                                                          pt +
                                                          ih -
                                                          (g / 100) * ih;
                                                        return (
                                                          <g key={g}>
                                                            <line
                                                              x1={pl}
                                                              y1={y}
                                                              x2={w - pr}
                                                              y2={y}
                                                              stroke={th.border}
                                                              strokeWidth="1"
                                                              strokeDasharray={
                                                                g === 0 ||
                                                                g === 100
                                                                  ? ""
                                                                  : "2,3"
                                                              }
                                                              opacity="0.5"
                                                            />
                                                            <text
                                                              x={pl - 6}
                                                              y={y + 3}
                                                              textAnchor="end"
                                                              fontSize="8"
                                                              fill={th.muted}
                                                              fontFamily="monospace"
                                                            >
                                                              {g}
                                                            </text>
                                                          </g>
                                                        );
                                                      },
                                                    )}
                                                    <line
                                                      x1={pl}
                                                      y1={
                                                        pt +
                                                        ih -
                                                        (70 / 100) * ih
                                                      }
                                                      x2={w - pr}
                                                      y2={
                                                        pt +
                                                        ih -
                                                        (70 / 100) * ih
                                                      }
                                                      stroke="#10b98155"
                                                      strokeWidth="1"
                                                      strokeDasharray="3,3"
                                                    />
                                                    <line
                                                      x1={pl}
                                                      y1={
                                                        pt +
                                                        ih -
                                                        (50 / 100) * ih
                                                      }
                                                      x2={w - pr}
                                                      y2={
                                                        pt +
                                                        ih -
                                                        (50 / 100) * ih
                                                      }
                                                      stroke="#f59e0b55"
                                                      strokeWidth="1"
                                                      strokeDasharray="3,3"
                                                    />
                                                    <path
                                                      d={path}
                                                      fill="none"
                                                      stroke="#7c3aed"
                                                      strokeWidth="2.5"
                                                    />
                                                    <path
                                                      d={
                                                        path +
                                                        ` L ${points[points.length - 1].x} ${pt + ih} L ${points[0].x} ${pt + ih} Z`
                                                      }
                                                      fill="#7c3aed18"
                                                    />
                                                    {points.map((p, i) => {
                                                      const c =
                                                        p.score >= 70
                                                          ? "#10b981"
                                                          : p.score >= 50
                                                            ? "#f59e0b"
                                                            : "#ef4444";
                                                      return (
                                                        <g key={i}>
                                                          <circle
                                                            cx={p.x}
                                                            cy={p.y}
                                                            r="5"
                                                            fill={c}
                                                            stroke="#fff"
                                                            strokeWidth="2"
                                                          />
                                                          <text
                                                            x={p.x}
                                                            y={h - pb + 18}
                                                            textAnchor="middle"
                                                            fontSize="7"
                                                            fill={th.muted}
                                                            fontFamily="monospace"
                                                          >
                                                            {p.date}
                                                          </text>
                                                          <text
                                                            x={p.x}
                                                            y={p.y - 12}
                                                            textAnchor="middle"
                                                            fontSize="9"
                                                            fontWeight="700"
                                                            fill={c}
                                                            fontFamily="monospace"
                                                          >
                                                            {p.score}
                                                          </text>
                                                        </g>
                                                      );
                                                    })}
                                                  </svg>
                                                );
                                              })()}
                                              <div
                                                style={{
                                                  marginTop: 14,
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 4,
                                                  maxHeight: 120,
                                                  overflowY: "auto",
                                                }}
                                              >
                                                {t.timeline.map((e, i) => {
                                                  const c =
                                                    e.score >= 70
                                                      ? "#10b981"
                                                      : e.score >= 50
                                                        ? "#f59e0b"
                                                        : "#ef4444";
                                                  return (
                                                    <div
                                                      key={i}
                                                      style={{
                                                        display: "flex",
                                                        gap: 8,
                                                        padding: "4px 8px",
                                                        background:
                                                          i % 2 === 0
                                                            ? th.surface
                                                            : "transparent",
                                                        borderRadius: 4,
                                                        fontSize: 9,
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          color: th.muted,
                                                          fontFamily:
                                                            "monospace",
                                                          minWidth: 80,
                                                          fontSize: 8,
                                                        }}
                                                      >
                                                        {e.date}
                                                      </span>
                                                      <span
                                                        style={{
                                                          display:
                                                            "inline-block",
                                                          width: 8,
                                                          height: 8,
                                                          borderRadius: "50%",
                                                          background: c,
                                                          flexShrink: 0,
                                                        }}
                                                      />
                                                      <span
                                                        style={{
                                                          color: c,
                                                          fontFamily:
                                                            "monospace",
                                                          fontWeight: 700,
                                                          minWidth: 24,
                                                          fontSize: 9,
                                                        }}
                                                      >
                                                        {e.score}
                                                      </span>
                                                      <span
                                                        style={{
                                                          color: th.text,
                                                          flex: 1,
                                                          lineHeight: 1.5,
                                                        }}
                                                      >
                                                        {e.event}
                                                      </span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {theorySubTab === "jinst" && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                marginBottom: 11,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              Jury instruction language with
                                              each phrase linked to the evidence
                                              that establishes it.
                                            </div>
                                            <div
                                              style={{
                                                background: th.card,
                                                border: `1px solid ${th.border}`,
                                                borderRadius: 9,
                                                padding: "16px 20px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  fontFamily: "monospace",
                                                  color: t.jinst.source
                                                    .toLowerCase()
                                                    .includes("special")
                                                    ? "#f59e0b"
                                                    : "#3b82f6",
                                                  letterSpacing: 1.2,
                                                  fontWeight: 700,
                                                  marginBottom: 8,
                                                  textTransform: "uppercase",
                                                }}
                                              >
                                                SOURCE · {t.jinst.source}
                                              </div>
                                              {t.jinst.source
                                                .toLowerCase()
                                                .includes("special") && (
                                                <div
                                                  style={{
                                                    padding: "8px 12px",
                                                    background: "#f59e0b15",
                                                    border:
                                                      "1px dashed #f59e0b55",
                                                    borderRadius: 6,
                                                    marginBottom: 12,
                                                    fontSize: 9,
                                                    color: "#f59e0b",
                                                    lineHeight: 1.55,
                                                  }}
                                                >
                                                  <span
                                                    style={{ fontWeight: 700 }}
                                                  >
                                                    ⚠️ Special instruction
                                                    required.
                                                  </span>{" "}
                                                  No model jury instruction
                                                  available for this theory.
                                                  Draft below is a starting
                                                  point for the
                                                  disputed-instruction battle.
                                                </div>
                                              )}
                                              <div
                                                style={{
                                                  padding: "14px 18px",
                                                  background: dark
                                                    ? "#0f172a"
                                                    : "#fefefe",
                                                  border: `1px solid ${th.border}`,
                                                  borderRadius: 6,
                                                  fontFamily: "Georgia, serif",
                                                  fontSize: 11,
                                                  color: th.text,
                                                  lineHeight: 1.85,
                                                  marginBottom: 14,
                                                }}
                                              >
                                                {t.jinst.model}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: 7,
                                                  fontFamily: "monospace",
                                                  color: th.muted,
                                                  letterSpacing: 1,
                                                  fontWeight: 700,
                                                  marginBottom: 7,
                                                }}
                                              >
                                                📎 LINKED EVIDENCE PER PHRASE
                                              </div>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 4,
                                                }}
                                              >
                                                {t.jinst.links.map((lk, li) => (
                                                  <div
                                                    key={li}
                                                    style={{
                                                      padding: "6px 11px",
                                                      background: th.surface,
                                                      borderLeft:
                                                        "3px solid #3b82f6",
                                                      borderRadius:
                                                        "0 5px 5px 0",
                                                      fontSize: 9,
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        color: "#3b82f6",
                                                        fontWeight: 700,
                                                        fontStyle: "italic",
                                                        marginBottom: 2,
                                                      }}
                                                    >
                                                      "{lk.phrase}"
                                                    </div>
                                                    <div
                                                      style={{
                                                        color: th.text,
                                                        lineHeight: 1.55,
                                                      }}
                                                    >
                                                      → {lk.ev}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {theorySubTab === "election" &&
                                          caseId === "neuro" &&
                                          t.id === "direct" && (
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: 9,
                                                  color: th.muted,
                                                  marginBottom: 11,
                                                  lineHeight: 1.55,
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                Plaintiff must elect between
                                                alternative damages theories.
                                                Comparison shows expected
                                                recovery, elements established,
                                                and recommended election.
                                              </div>
                                              <div
                                                style={{
                                                  background:
                                                    "linear-gradient(135deg,#10b98115,#3b82f615)",
                                                  border: "1px solid #10b98155",
                                                  borderRadius: 9,
                                                  padding: "14px 18px",
                                                  marginBottom: 14,
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    color: "#10b981",
                                                    letterSpacing: 1,
                                                    fontWeight: 700,
                                                    marginBottom: 5,
                                                  }}
                                                >
                                                  RECOMMENDATION
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    color: th.text,
                                                    marginBottom: 7,
                                                  }}
                                                >
                                                  {NEURO_ELECTION.decision}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 10,
                                                    color: th.muted,
                                                    lineHeight: 1.65,
                                                  }}
                                                >
                                                  {
                                                    NEURO_ELECTION.recommendation
                                                  }
                                                </div>
                                              </div>
                                              <div
                                                style={{
                                                  display: "grid",
                                                  gridTemplateColumns:
                                                    "1fr 1fr",
                                                  gap: 12,
                                                }}
                                              >
                                                {NEURO_ELECTION.alternatives.map(
                                                  (alt, ai) => {
                                                    const isRec =
                                                      alt.verdict ===
                                                      "recommended";
                                                    return (
                                                      <div
                                                        key={ai}
                                                        style={{
                                                          background: th.card,
                                                          border: `2px solid ${isRec ? "#10b981" : "#ef444455"}`,
                                                          borderRadius: 9,
                                                          padding: "14px 16px",
                                                          position: "relative",
                                                        }}
                                                      >
                                                        {isRec && (
                                                          <div
                                                            style={{
                                                              position:
                                                                "absolute",
                                                              top: -9,
                                                              left: 14,
                                                              background:
                                                                "#10b981",
                                                              color: "#fff",
                                                              fontSize: 7,
                                                              fontFamily:
                                                                "monospace",
                                                              letterSpacing: 1,
                                                              fontWeight: 700,
                                                              padding:
                                                                "2px 8px",
                                                              borderRadius: 8,
                                                            }}
                                                          >
                                                            ✓ RECOMMENDED
                                                          </div>
                                                        )}
                                                        {!isRec && (
                                                          <div
                                                            style={{
                                                              position:
                                                                "absolute",
                                                              top: -9,
                                                              left: 14,
                                                              background:
                                                                "#ef4444",
                                                              color: "#fff",
                                                              fontSize: 7,
                                                              fontFamily:
                                                                "monospace",
                                                              letterSpacing: 1,
                                                              fontWeight: 700,
                                                              padding:
                                                                "2px 8px",
                                                              borderRadius: 8,
                                                            }}
                                                          >
                                                            ✗ DECLINED
                                                          </div>
                                                        )}
                                                        <h4
                                                          style={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            margin: "4px 0 9px",
                                                            color: th.text,
                                                          }}
                                                        >
                                                          {alt.name}
                                                        </h4>
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            flexDirection:
                                                              "column",
                                                            gap: 7,
                                                            fontSize: 9,
                                                            color: th.text,
                                                          }}
                                                        >
                                                          <div>
                                                            <span
                                                              style={{
                                                                color: th.muted,
                                                                fontFamily:
                                                                  "monospace",
                                                                fontSize: 7,
                                                                letterSpacing: 0.5,
                                                                fontWeight: 700,
                                                                display:
                                                                  "block",
                                                                marginBottom: 2,
                                                              }}
                                                            >
                                                              EXPECTED RECOVERY
                                                            </span>
                                                            <span
                                                              style={{
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color: isRec
                                                                  ? "#10b981"
                                                                  : "#ef4444",
                                                              }}
                                                            >
                                                              {
                                                                alt.expectedRecovery
                                                              }
                                                            </span>
                                                          </div>
                                                          <div>
                                                            <span
                                                              style={{
                                                                color: th.muted,
                                                                fontFamily:
                                                                  "monospace",
                                                                fontSize: 7,
                                                                letterSpacing: 0.5,
                                                                fontWeight: 700,
                                                                display:
                                                                  "block",
                                                                marginBottom: 2,
                                                              }}
                                                            >
                                                              BASIS
                                                            </span>
                                                            {alt.basis}
                                                          </div>
                                                          <div>
                                                            <span
                                                              style={{
                                                                color: th.muted,
                                                                fontFamily:
                                                                  "monospace",
                                                                fontSize: 7,
                                                                letterSpacing: 0.5,
                                                                fontWeight: 700,
                                                                display:
                                                                  "block",
                                                                marginBottom: 2,
                                                              }}
                                                            >
                                                              ESTABLISHED
                                                            </span>
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#10b981",
                                                              }}
                                                            >
                                                              {alt.established}
                                                            </span>
                                                          </div>
                                                          <div>
                                                            <span
                                                              style={{
                                                                color: th.muted,
                                                                fontFamily:
                                                                  "monospace",
                                                                fontSize: 7,
                                                                letterSpacing: 0.5,
                                                                fontWeight: 700,
                                                                display:
                                                                  "block",
                                                                marginBottom: 2,
                                                              }}
                                                            >
                                                              MISSING
                                                            </span>
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#ef4444",
                                                              }}
                                                            >
                                                              {alt.missing}
                                                            </span>
                                                          </div>
                                                          <div>
                                                            <span
                                                              style={{
                                                                color: th.muted,
                                                                fontFamily:
                                                                  "monospace",
                                                                fontSize: 7,
                                                                letterSpacing: 0.5,
                                                                fontWeight: 700,
                                                                display:
                                                                  "block",
                                                                marginBottom: 2,
                                                              }}
                                                            >
                                                              RISK
                                                            </span>
                                                            {alt.risk}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ),
                      )}
                    {theoryViewMode === "battle" && (
                      <div>
                        <div
                          style={{
                            padding: "10px 14px",
                            background: dark ? "#1e293b" : "#f8fafc",
                            borderLeft: "3px solid #7c3aed",
                            borderRadius: "0 6px 6px 0",
                            marginBottom: 12,
                            fontSize: 10,
                            color: th.muted,
                            lineHeight: 1.55,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#7c3aed",
                              fontSize: 11,
                            }}
                          >
                            ⚔️ Battle View.
                          </span>{" "}
                          Disputed legal issues, organized by issue rather than
                          by theory. Each card shows plaintiff's position,
                          defendant's position, and the AI's assessment of who
                          currently has the stronger position.
                        </div>
                        {battle.map((b, bi) => {
                          const winnerC =
                            b.winner === "plaintiff"
                              ? "#ef4444"
                              : b.winner === "defendant"
                                ? "#3b82f6"
                                : "#a78bfa";
                          return (
                            <div
                              key={b.id}
                              style={{
                                background: th.card,
                                borderRadius: 11,
                                border: `1px solid ${th.border}`,
                                marginBottom: 14,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  padding: "13px 18px",
                                  borderBottom: `1px solid ${th.border}`,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: "#7c3aed",
                                    letterSpacing: 1.2,
                                    fontWeight: 700,
                                  }}
                                >
                                  ISSUE {bi + 1}
                                </span>
                                <h3
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    margin: 0,
                                    color: th.text,
                                    flex: 1,
                                  }}
                                >
                                  {b.issue}
                                </h3>
                                <span
                                  style={{
                                    fontSize: 8,
                                    fontFamily: "monospace",
                                    color: winnerC,
                                    background: winnerC + "18",
                                    border: `1px solid ${winnerC}55`,
                                    padding: "3px 9px",
                                    borderRadius: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {b.winner === "contested"
                                    ? "contested"
                                    : b.winner + " leading"}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 0,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "14px 18px",
                                    background: "#ef444408",
                                    borderRight: `1px solid ${th.border}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#ef4444",
                                      letterSpacing: 1,
                                      fontWeight: 700,
                                      marginBottom: 7,
                                    }}
                                  >
                                    🔴 PLAINTIFF
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: th.text,
                                      lineHeight: 1.7,
                                      marginBottom: 10,
                                    }}
                                  >
                                    {b.plaintiff.position}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: th.muted,
                                      fontWeight: 600,
                                      letterSpacing: 0.5,
                                      marginBottom: 4,
                                    }}
                                  >
                                    CITATIONS
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    {b.plaintiff.cites.map((c, ci) => (
                                      <span
                                        key={ci}
                                        style={{
                                          fontSize: 8,
                                          color: "#ef4444",
                                          fontFamily: "monospace",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        · {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "14px 18px",
                                    background: "#3b82f608",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#3b82f6",
                                      letterSpacing: 1,
                                      fontWeight: 700,
                                      marginBottom: 7,
                                    }}
                                  >
                                    🔵 DEFENDANT
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: th.text,
                                      lineHeight: 1.7,
                                      marginBottom: 10,
                                    }}
                                  >
                                    {b.defendant.position}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: th.muted,
                                      fontWeight: 600,
                                      letterSpacing: 0.5,
                                      marginBottom: 4,
                                    }}
                                  >
                                    CITATIONS
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    {b.defendant.cites.map((c, ci) => (
                                      <span
                                        key={ci}
                                        style={{
                                          fontSize: 8,
                                          color: "#3b82f6",
                                          fontFamily: "monospace",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        · {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div
                                style={{
                                  padding: "13px 18px",
                                  borderTop: `1px solid ${th.border}`,
                                  background: dark ? "#1e1b4b" : "#f5f3ff",
                                  borderLeft: `4px solid ${winnerC}`,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: winnerC,
                                    letterSpacing: 1.2,
                                    fontWeight: 700,
                                    marginBottom: 6,
                                  }}
                                >
                                  🤖 AI ASSESSMENT
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: th.text,
                                    lineHeight: 1.7,
                                  }}
                                >
                                  {b.ai}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "witnesses" &&
              (() => {
                const data = WITNESSES_INTEL[caseId] || WITNESSES_INTEL.juul;
                const persp = witIntelPersp;
                const sidesToRender =
                  persp === "all"
                    ? [
                        [
                          "plaintiff",
                          data.plaintiff,
                          "#ef4444",
                          "🔴 PLAINTIFF WITNESSES",
                        ],
                        [
                          "defendant",
                          data.defendant,
                          "#3b82f6",
                          "🔵 DEFENDANT WITNESSES",
                        ],
                      ]
                    : [
                        [
                          persp,
                          data[persp],
                          persp === "plaintiff" ? "#ef4444" : "#3b82f6",
                          persp === "plaintiff"
                            ? "🔴 PLAINTIFF WITNESSES"
                            : "🔵 DEFENDANT WITNESSES",
                        ],
                      ];
                const allW = [
                  ...data.plaintiff.map((w) => ({ ...w, _side: "plaintiff" })),
                  ...data.defendant.map((w) => ({ ...w, _side: "defendant" })),
                ];
                const sel = selectedWitId
                  ? allW.find((w) => w.id === selectedWitId)
                  : null;
                const credLabels = {
                  likability: "Jury Likability",
                  consistency: "Internal Consistency",
                  exposure: "Prior Statement Exposure",
                  authority: "Subject Authority",
                  advocacy: "Advocacy Risk",
                };
                const isHazard = (k, v) =>
                  k === "exposure" || k === "advocacy" ? v >= 70 : false;
                const flagC = (k, v) => {
                  if (isHazard(k, v))
                    return v >= 85
                      ? "#dc2626"
                      : v >= 70
                        ? "#f59e0b"
                        : "#10b981";
                  return v >= 70 ? "#10b981" : v >= 50 ? "#f59e0b" : "#ef4444";
                };
                const theoriesData =
                  THEORIES_DATA[caseId] || THEORIES_DATA.juul;
                const allTheories = [
                  ...theoriesData.plaintiff.map((t) => ({
                    ...t,
                    _side: "plaintiff",
                  })),
                  ...theoriesData.defendant.map((t) => ({
                    ...t,
                    _side: "defendant",
                  })),
                ];

                return (
                  <div
                    style={{
                      maxWidth: 1400,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg,#0891b2,#0e7490)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#cffafe",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        WITNESSES · STRATEGIC INSTRUMENTS · NOT A CONTACT SHEET
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Witnesses
                      </h1>
                      <div style={{ fontSize: 10, color: "#cffafe" }}>
                        {data.plaintiff.length} plaintiff ·{" "}
                        {data.defendant.length} defendant · relationship map ·
                        proof matrix · sequencing · per-witness credibility
                        radar + testimony prediction + depo deep-dive + expert
                        mode
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 14,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          fontWeight: 600,
                          letterSpacing: 1,
                          fontFamily: "monospace",
                          marginRight: 4,
                        }}
                      >
                        VIEW
                      </span>
                      {[
                        ["map", "🕸️", "Relationship Map"],
                        ["list", "📋", "Witness List"],
                        ["matrix", "🎯", "Proof Matrix"],
                        ["seq", "📅", "Trial Sequencing"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => {
                            setWitViewMode(k);
                            setSelectedWitId(null);
                          }}
                          style={{
                            padding: "7px 13px",
                            fontSize: 10,
                            fontWeight: witViewMode === k ? 700 : 500,
                            background:
                              witViewMode === k ? "#0891b2" : th.surface,
                            color: witViewMode === k ? "#fff" : th.muted,
                            border: `1px solid ${witViewMode === k ? "#0891b2" : th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                        </button>
                      ))}
                      <div style={{ flex: 1 }} />
                      <span
                        style={{
                          fontSize: 9,
                          color: th.muted,
                          fontWeight: 600,
                          letterSpacing: 1,
                          fontFamily: "monospace",
                          marginRight: 4,
                        }}
                      >
                        PERSPECTIVE
                      </span>
                      {[
                        ["all", "⚖️", "Both"],
                        ["plaintiff", "🔴", "Plaintiff"],
                        ["defendant", "🔵", "Defendant"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => {
                            setWitIntelPersp(k);
                            setSelectedWitId(null);
                          }}
                          style={{
                            padding: "7px 13px",
                            fontSize: 10,
                            fontWeight: persp === k ? 700 : 500,
                            background:
                              persp === k
                                ? k === "plaintiff"
                                  ? "#ef4444"
                                  : k === "defendant"
                                    ? "#3b82f6"
                                    : "#a78bfa"
                                : th.surface,
                            color: persp === k ? "#fff" : th.muted,
                            border: `1px solid ${persp === k ? "transparent" : th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                        </button>
                      ))}
                    </div>

                    {witViewMode === "map" &&
                      (() => {
                        const visible =
                          persp === "all"
                            ? allW
                            : allW.filter((w) => w._side === persp);
                        const W = 900;
                        const COLS = 4;
                        const ROW_PITCH = 170;
                        const rows = Math.max(1, Math.ceil(visible.length / COLS));
                        const H = 180 + (rows - 1) * ROW_PITCH;
                        // fixed 4-column grid: filtered witnesses reflow left-to-right with no gaps
                        const positions = visible.map((w, i) => {
                          const col = i % COLS,
                            row = Math.floor(i / COLS);
                          return {
                            ...w,
                            cx: 80 + col * ((W - 160) / (COLS - 1)),
                            cy: 90 + row * ROW_PITCH,
                          };
                        });
                        const links = [];
                        visible.forEach((w) => {
                          (w.relations || []).forEach((r) => {
                            const t = positions.find((p) => p.id === r.with);
                            const f = positions.find((p) => p.id === w.id);
                            if (t && f)
                              links.push({
                                from: f,
                                to: t,
                                type: r.type,
                                note: r.note,
                              });
                          });
                        });
                        const linkC = {
                          corroborates: "#10b981",
                          contradicts: "#dc2626",
                          exposes: "#f59e0b",
                          "exposed-by": "#f59e0b",
                          foundational: "#3b82f6",
                          "opposing-expert": "#a78bfa",
                        };
                        return (
                          <div
                            style={{
                              background: th.card,
                              border: `1px solid ${th.border}`,
                              borderRadius: 10,
                              padding: "14px",
                              marginBottom: 12,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 10,
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  color: th.muted,
                                  fontStyle: "italic",
                                  lineHeight: 1.55,
                                  maxWidth: 600,
                                }}
                              >
                                Network of corroboration, contradiction,
                                foundational, and impeachment relationships
                                across all witnesses. Tap any node to drill into
                                the witness profile.
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  fontSize: 8,
                                  fontFamily: "monospace",
                                  color: th.muted,
                                  flexWrap: "wrap",
                                }}
                              >
                                {[
                                  ["corroborates", "#10b981"],
                                  ["contradicts", "#dc2626"],
                                  ["exposes", "#f59e0b"],
                                  ["foundational", "#3b82f6"],
                                  ["opposing-expert", "#a78bfa"],
                                ].map(([k, c]) => (
                                  <span
                                    key={k}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 14,
                                        height: 2,
                                        background: c,
                                        display: "inline-block",
                                      }}
                                    />
                                    {k}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <svg
                              viewBox={`0 0 ${W} ${H}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: 520,
                                display: "block",
                              }}
                            >
                              {links.map((l, i) => {
                                const dx = l.to.cx - l.from.cx,
                                  dy = l.to.cy - l.from.cy,
                                  len = Math.sqrt(dx * dx + dy * dy);
                                const ux = dx / len,
                                  uy = dy / len;
                                return (
                                  <line
                                    key={i}
                                    x1={l.from.cx + ux * 36}
                                    y1={l.from.cy + uy * 36}
                                    x2={l.to.cx - ux * 36}
                                    y2={l.to.cy - uy * 36}
                                    stroke={linkC[l.type] || "#999"}
                                    strokeWidth="1.5"
                                    strokeDasharray={
                                      l.type === "contradicts" ? "4,3" : ""
                                    }
                                    opacity="0.7"
                                  >
                                    <title>
                                      {l.type}: {l.note}
                                    </title>
                                  </line>
                                );
                              })}
                              {positions.map((w) => {
                                const sideC =
                                  w._side === "plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6";
                                const isPending =
                                  w.depo &&
                                  w.depo.status &&
                                  w.depo.status
                                    .toLowerCase()
                                    .includes("not yet");
                                const flagged =
                                  (w.logistics &&
                                    w.logistics.flags &&
                                    w.logistics.flags.length > 0) ||
                                  isPending;
                                return (
                                  <g
                                    key={w.id}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setSelectedWitId(w.id);
                                      setWitSubTab("profile");
                                    }}
                                  >
                                    <circle
                                      cx={w.cx}
                                      cy={w.cy}
                                      r="34"
                                      fill={th.card}
                                      stroke={isPending ? "#dc2626" : sideC}
                                      strokeWidth={isPending ? "2.5" : "2"}
                                      strokeDasharray={isPending ? "4,3" : ""}
                                    />
                                    {flagged && (
                                      <circle
                                        cx={w.cx + 24}
                                        cy={w.cy - 24}
                                        r="7"
                                        fill="#dc2626"
                                      >
                                        <title>
                                          {isPending
                                            ? "Pending - critical"
                                            : (w.logistics.flags || []).join(
                                                ", ",
                                              )}
                                        </title>
                                      </circle>
                                    )}
                                    <text
                                      x={w.cx}
                                      y={w.cy + 5}
                                      textAnchor="middle"
                                      fontSize="20"
                                      fontFamily="system-ui"
                                    >
                                      {w.photo || "👤"}
                                    </text>
                                    <text
                                      x={w.cx}
                                      y={w.cy + 50}
                                      textAnchor="middle"
                                      fontSize="10"
                                      fontFamily="system-ui"
                                      fontWeight="700"
                                      fill={th.text}
                                    >
                                      {w.name.length > 22
                                        ? w.name.slice(0, 20) + "…"
                                        : w.name}
                                    </text>
                                    <text
                                      x={w.cx}
                                      y={w.cy + 62}
                                      textAnchor="middle"
                                      fontSize="8"
                                      fontFamily="monospace"
                                      fill={th.muted}
                                    >
                                      {w.role.length > 32
                                        ? w.role.slice(0, 30) + "…"
                                        : w.role}
                                    </text>
                                  </g>
                                );
                              })}
                            </svg>
                          </div>
                        );
                      })()}

                    {witViewMode === "list" &&
                      sidesToRender.map(
                        ([sideKey, witList, sideC, sideLabel], sIdx) => (
                          <div
                            key={sideKey}
                            style={{
                              marginTop: persp === "all" && sIdx > 0 ? 20 : 0,
                              paddingTop: persp === "all" && sIdx > 0 ? 14 : 0,
                              borderTop:
                                persp === "all" && sIdx > 0
                                  ? `2px dashed ${th.border}`
                                  : "none",
                            }}
                          >
                            {persp === "all" && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 12,
                                  padding: "7px 13px",
                                  background: sideC + "15",
                                  border: `1px solid ${sideC}55`,
                                  borderRadius: 7,
                                  borderLeft: `4px solid ${sideC}`,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: sideC,
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                  }}
                                >
                                  {sideLabel}
                                </span>
                              </div>
                            )}
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit,minmax(280px,1fr))",
                                gap: 10,
                              }}
                            >
                              {witList.map((w) => {
                                const isOpen = selectedWitId === w.id;
                                const isPending =
                                  w.depo &&
                                  w.depo.status &&
                                  w.depo.status
                                    .toLowerCase()
                                    .includes("not yet");
                                return (
                                  <div
                                    key={w.id}
                                    onClick={(e) => {
                                      if (e.target.closest(".wit-add-asset"))
                                        return;
                                      setSelectedWitId(isOpen ? null : w.id);
                                      setWitSubTab("profile");
                                    }}
                                    style={{
                                      background: th.card,
                                      borderRadius: 9,
                                      border: `1px solid ${isPending ? "#dc262655" : th.border}`,
                                      borderLeft: `4px solid ${sideC}`,
                                      padding: "12px 14px",
                                      cursor: "pointer",
                                      position: "relative",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 9,
                                        marginBottom: 6,
                                      }}
                                    >
                                      <span
                                        style={{ fontSize: 24, flexShrink: 0 }}
                                      >
                                        {w.photo || "👤"}
                                      </span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                          style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: th.text,
                                            letterSpacing: "-0.2px",
                                          }}
                                        >
                                          {w.name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            fontFamily: "monospace",
                                            color: th.muted,
                                          }}
                                        >
                                          {w.role}
                                        </div>
                                      </div>
                                      {w.type === "expert" && (
                                        <span
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: "#a78bfa",
                                            background: "#a78bfa18",
                                            padding: "2px 7px",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                          }}
                                        >
                                          EXPERT
                                        </span>
                                      )}
                                      {isPending && (
                                        <span
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: "#dc2626",
                                            background: "#dc262618",
                                            padding: "2px 7px",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                          }}
                                        >
                                          ⚠ PENDING
                                        </span>
                                      )}
                                    </div>
                                    {!isPending && (
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 1fr",
                                          gap: 4,
                                          marginTop: 8,
                                        }}
                                      >
                                        {Object.entries(w.cred).map(
                                          ([k, v]) => {
                                            const c = flagC(k, v);
                                            return (
                                              <div
                                                key={k}
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 6,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 7,
                                                    color: th.muted,
                                                    fontFamily: "monospace",
                                                    letterSpacing: 0.3,
                                                    minWidth: 74,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {credLabels[k]
                                                    .split(" ")[0]
                                                    .toUpperCase()}
                                                </span>
                                                <div
                                                  style={{
                                                    flex: 1,
                                                    height: 3,
                                                    background: th.surface,
                                                    borderRadius: 2,
                                                    overflow: "hidden",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      width: v + "%",
                                                      height: "100%",
                                                      background: c,
                                                    }}
                                                  />
                                                </div>
                                                <span
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    fontWeight: 700,
                                                    color: c,
                                                    minWidth: 18,
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  {v}
                                                </span>
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    )}
                                    {w.story && (
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          lineHeight: 1.55,
                                          marginTop: 8,
                                          fontStyle: "italic",
                                          borderTop: `1px solid ${th.border}`,
                                          paddingTop: 7,
                                        }}
                                      >
                                        {w.story.slice(0, 140)}
                                        {w.story.length > 140 ? "…" : ""}
                                      </div>
                                    )}
                                    <button
                                      className="wit-add-asset"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setUploadAssoc({
                                          wit: w.name,
                                          loc: "",
                                        });
                                        setAssetUploadDraft({
                                          title: "",
                                          desc: "",
                                          date: "",
                                          classification: "pin",
                                          exhibit: "",
                                          type: "depo",
                                        });
                                        setShowUpload(true);
                                      }}
                                      title={"Upload asset for " + w.name}
                                      style={{
                                        marginTop: 9,
                                        padding: "5px 10px",
                                        fontSize: 9,
                                        fontWeight: 600,
                                        background: th.surface,
                                        color: "#7c3aed",
                                        border: `1px solid ${th.border}`,
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                      }}
                                    >
                                      ＋ Upload Asset
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ),
                      )}

                    {witViewMode === "matrix" &&
                      (() => {
                        const witnessesShown =
                          persp === "all"
                            ? allW
                            : allW.filter((w) => w._side === persp);
                        const rows = [];
                        allTheories.forEach((t) => {
                          t.elements.forEach((el, ei) => {
                            rows.push({
                              theory: t.name,
                              theorySide: t._side,
                              theoryShort: t.short,
                              element: el.name,
                              elemIdx: ei,
                              theoryId: t.id,
                            });
                          });
                        });
                        const cellLabel = {
                          green: "✓",
                          amber: "~",
                          red: "✗",
                          gray: "·",
                        };
                        const cellC = {
                          green: "#10b981",
                          amber: "#f59e0b",
                          red: "#dc2626",
                          gray: "#9ca3af",
                        };
                        return (
                          <div
                            style={{
                              background: th.card,
                              border: `1px solid ${th.border}`,
                              borderRadius: 10,
                              overflow: "auto",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 14px",
                                borderBottom: `1px solid ${th.border}`,
                                fontSize: 10,
                                color: th.muted,
                                fontStyle: "italic",
                                lineHeight: 1.55,
                              }}
                            >
                              Every witness mapped against every theory element
                              they can establish or contradict. Cells:{" "}
                              <span
                                style={{ color: "#10b981", fontWeight: 700 }}
                              >
                                green
                              </span>{" "}
                              = establishes;{" "}
                              <span
                                style={{ color: "#f59e0b", fontWeight: 700 }}
                              >
                                amber
                              </span>{" "}
                              = partial;{" "}
                              <span
                                style={{ color: "#dc2626", fontWeight: 700 }}
                              >
                                red
                              </span>{" "}
                              = contradicts; gray = no testimony. Tap any
                              colored cell to see witness's specific
                              contribution.
                            </div>
                            <div style={{ overflow: "auto" }}>
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  fontSize: 9,
                                }}
                              >
                                <thead>
                                  <tr style={{ background: th.surface }}>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        textAlign: "left",
                                        fontFamily: "monospace",
                                        letterSpacing: 0.5,
                                        fontSize: 8,
                                        color: th.muted,
                                        position: "sticky",
                                        left: 0,
                                        background: th.surface,
                                        minWidth: 240,
                                        borderRight: `1px solid ${th.border}`,
                                        borderBottom: `1px solid ${th.border}`,
                                      }}
                                    >
                                      THEORY · ELEMENT
                                    </th>
                                    {witnessesShown.map((w) => (
                                      <th
                                        key={w.id}
                                        style={{
                                          padding: "8px 5px",
                                          textAlign: "center",
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                          fontSize: 7,
                                          color:
                                            w._side === "plaintiff"
                                              ? "#ef4444"
                                              : "#3b82f6",
                                          borderBottom: `1px solid ${th.border}`,
                                          minWidth: 65,
                                          maxWidth: 80,
                                          fontWeight: 700,
                                        }}
                                      >
                                        <div
                                          style={{
                                            transform: "rotate(-45deg)",
                                            transformOrigin: "center",
                                            whiteSpace: "nowrap",
                                            fontSize: 7,
                                            padding: "4px 0",
                                          }}
                                        >
                                          {w.name.split(" ").slice(-1)[0]}
                                        </div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((r, ri) => {
                                    const isPlaintiff =
                                      r.theorySide === "plaintiff";
                                    return (
                                      <tr
                                        key={ri}
                                        style={{
                                          borderTop: `1px solid ${th.border}`,
                                          background:
                                            ri % 2 === 0
                                              ? "transparent"
                                              : th.surface + "55",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "6px 10px",
                                            position: "sticky",
                                            left: 0,
                                            background:
                                              ri % 2 === 0
                                                ? th.card
                                                : th.surface,
                                            borderRight: `1px solid ${th.border}`,
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 8,
                                              fontFamily: "monospace",
                                              color: isPlaintiff
                                                ? "#ef4444"
                                                : "#3b82f6",
                                              letterSpacing: 0.5,
                                              fontWeight: 700,
                                              marginBottom: 1,
                                            }}
                                          >
                                            {r.theoryShort || r.theory}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            Elem {r.elemIdx + 1}:{" "}
                                            {r.element.slice(0, 38)}
                                            {r.element.length > 38 ? "…" : ""}
                                          </div>
                                        </td>
                                        {witnessesShown.map((w) => {
                                          const map = (w.elements || []).find(
                                            (m) =>
                                              m.theoryId === r.theoryId &&
                                              m.elemIdx === r.elemIdx,
                                          );
                                          const status = map
                                            ? map.status
                                            : "gray";
                                          return (
                                            <td
                                              key={w.id}
                                              title={
                                                map ? map.note : "No testimony"
                                              }
                                              style={{
                                                padding: "6px 5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  display: "inline-block",
                                                  width: 22,
                                                  height: 22,
                                                  borderRadius: 6,
                                                  background:
                                                    cellC[status] +
                                                    (status === "gray"
                                                      ? "22"
                                                      : "22"),
                                                  border: `1px solid ${cellC[status]}55`,
                                                  color: cellC[status],
                                                  fontWeight: 700,
                                                  fontSize: 11,
                                                  lineHeight: "22px",
                                                }}
                                              >
                                                {cellLabel[status]}
                                              </span>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                    {witViewMode === "seq" &&
                      (() => {
                        const sideToShow =
                          persp === "all" ? "plaintiff" : persp;
                        const witList = data[sideToShow];
                        const orderKey = caseId + "_" + sideToShow;
                        const currentOrder =
                          seqOrder[orderKey] || witList.map((w) => w.id);
                        const orderedWits = currentOrder
                          .map((id) => witList.find((w) => w.id === id))
                          .filter(Boolean);
                        const aiNotes = {
                          chid_plaintiff:
                            "AI Note: CC-2 first establishes the internal account-control mechanics before CC-3 corroborates with the $95K round-trip; Employee 1 closes with the grand-jury-subpoena trigger. Watch Bruton exposure if either cooperator's out-of-court statements implicate Chidambaran without live testimony.",
                          naqvi_plaintiff:
                            "AI Note: CC-2 first establishes the internal account-control mechanics before CC-3 corroborates with the $95K round-trip; Employee 1 closes with the grand-jury-subpoena trigger. Sequencing away from Naqvi's role helps the government's differentiated-culpability framing.",
                          chid_defendant:
                            "AI Note: Lead with Olivier - the SPAC president who bought the assets and stayed on as interim CEO - before Gerety, so the jury hears the diligence-and-good-faith narrative before the post-report employment context.",
                          naqvi_defendant:
                            "AI Note: Lead with Olivier's good-faith diligence narrative before Gerety's post-report WhatsApp production, reinforcing that Naqvi's cooperation started well before any subpoena.",
                          juul_plaintiff:
                            "AI Note: Halpern-Felsher should appear before Dorn - establishing the 75% awareness gap before the abatement expert quantifies remediation gives the jury emotional foundation to accept the $78.8M number. ⚠️ Halpern-Felsher has Sep 30 academic conflict - if jury selection runs full Sep 28, she conflicts with Day 2.",
                          musk_plaintiff:
                            "AI Note: Currently in trial. Days 1-2 complete. Brockman next (Day 3). If YGR's ruling on §17510.8 comes during Brockman's exam, request brief recess to assess impact on remaining direct outline. Wazzan's testimony Day 4-5 should anchor between Brockman's PX163 cross and damages framing.",
                          neuro_plaintiff:
                            "AI Note: Markman pending - sequencing depends on claim construction outcome. Alfaro 30(b)(6) cross before Liu's direct may help by establishing manufacturing-origin contradictions before plaintiff opens its damages case.",
                          juul_defendant:
                            "AI Note: JLI 30(b)(6) appears multiple times - segment by topic to avoid overload. Lead with FDA-compliance framing before plaintiff's marketing-doc cross sequence.",
                          musk_defendant:
                            "AI Note: Brockman is the highest-stakes witness. Prepare for journal-entry confrontation last in cross. Dudney FIFO testimony Day 4-5 anchors the tracing battle.",
                          neuro_defendant:
                            "AI Note: Adams' Markman testimony is foundational - sequence first. Alfaro 30(b)(6) needs prep on manufacturing-origin contradictions.",
                        };
                        return (
                          <div
                            style={{
                              background: th.card,
                              border: `1px solid ${th.border}`,
                              borderRadius: 10,
                              padding: "14px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 14,
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: th.text,
                                }}
                              >
                                Trial Witness Order - {" "}
                                {sideToShow === "plaintiff"
                                  ? "🔴 Plaintiff"
                                  : sideToShow === "defendant"
                                    ? "🔵 Defendant"
                                    : ""}{" "}
                                side
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  fontFamily: "monospace",
                                }}
                              >
                                ↑↓ to reorder · AI analyzes sequence
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "10px 14px",
                                background: dark ? "#1e3a5f" : "#dbeafe",
                                border: "1px solid #3b82f655",
                                borderLeft: "3px solid #3b82f6",
                                borderRadius: "0 6px 6px 0",
                                marginBottom: 12,
                                fontSize: 10,
                                color: th.text,
                                lineHeight: 1.6,
                              }}
                            >
                              <span style={{ fontSize: 11 }}>🤖</span>{" "}
                              {aiNotes[orderKey] || aiNotes.juul_plaintiff}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 7,
                              }}
                            >
                              {orderedWits.map((w, wi) => {
                                const flagged =
                                  (w.logistics &&
                                    w.logistics.flags &&
                                    w.logistics.flags.length > 0) ||
                                  (w.depo &&
                                    w.depo.status &&
                                    w.depo.status
                                      .toLowerCase()
                                      .includes("not yet"));
                                const sideC =
                                  w._side === "plaintiff" ||
                                  sideToShow === "plaintiff"
                                    ? "#ef4444"
                                    : "#3b82f6";
                                return (
                                  <div
                                    key={w.id}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      padding: "11px 13px",
                                      background: th.surface,
                                      borderRadius: 7,
                                      border: `1px solid ${flagged ? "#dc262655" : th.border}`,
                                      borderLeft: `4px solid ${sideC}`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 800,
                                        color: sideC,
                                        fontFamily: "monospace",
                                        minWidth: 24,
                                      }}
                                    >
                                      {wi + 1}
                                    </span>
                                    <span style={{ fontSize: 18 }}>
                                      {w.photo || "👤"}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 700,
                                          color: th.text,
                                        }}
                                      >
                                        {w.name}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 8,
                                          fontFamily: "monospace",
                                          color: th.muted,
                                        }}
                                      >
                                        {w.role}
                                      </div>
                                    </div>
                                    {flagged &&
                                      (w.logistics.flags || []).map((f) => (
                                        <span
                                          key={f}
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: "#dc2626",
                                            background: "#dc262618",
                                            padding: "2px 7px",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                          }}
                                        >
                                          ⚠ {f.slice(0, 28)}
                                        </span>
                                      ))}
                                    <div style={{ display: "flex", gap: 3 }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newOrder = [...currentOrder];
                                          if (wi > 0) {
                                            [newOrder[wi - 1], newOrder[wi]] = [
                                              newOrder[wi],
                                              newOrder[wi - 1],
                                            ];
                                            setSeqOrder((p) => ({
                                              ...p,
                                              [orderKey]: newOrder,
                                            }));
                                          }
                                        }}
                                        disabled={wi === 0}
                                        style={{
                                          width: 24,
                                          height: 24,
                                          fontSize: 11,
                                          background:
                                            wi === 0 ? "transparent" : th.card,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 4,
                                          cursor:
                                            wi === 0 ? "default" : "pointer",
                                          color: wi === 0 ? th.faint : th.dim,
                                          opacity: wi === 0 ? 0.5 : 1,
                                        }}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newOrder = [...currentOrder];
                                          if (wi < newOrder.length - 1) {
                                            [newOrder[wi], newOrder[wi + 1]] = [
                                              newOrder[wi + 1],
                                              newOrder[wi],
                                            ];
                                            setSeqOrder((p) => ({
                                              ...p,
                                              [orderKey]: newOrder,
                                            }));
                                          }
                                        }}
                                        disabled={wi === orderedWits.length - 1}
                                        style={{
                                          width: 24,
                                          height: 24,
                                          fontSize: 11,
                                          background:
                                            wi === orderedWits.length - 1
                                              ? "transparent"
                                              : th.card,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 4,
                                          cursor:
                                            wi === orderedWits.length - 1
                                              ? "default"
                                              : "pointer",
                                          color:
                                            wi === orderedWits.length - 1
                                              ? th.faint
                                              : th.dim,
                                          opacity:
                                            wi === orderedWits.length - 1
                                              ? 0.5
                                              : 1,
                                        }}
                                      >
                                        ↓
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                    {sel && (
                      <div
                        style={{
                          position: "fixed",
                          top: 0,
                          right: 0,
                          width: 540,
                          height: "100vh",
                          background: th.card,
                          borderLeft: `1px solid ${th.border}`,
                          boxShadow: "-4px 0 24px rgba(0,0,0,0.25)",
                          zIndex: 100,
                          overflow: "auto",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            padding: "14px 18px",
                            borderBottom: `1px solid ${th.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 11,
                            flexShrink: 0,
                            background:
                              sel._side === "plaintiff"
                                ? "#ef444408"
                                : "#3b82f608",
                          }}
                        >
                          <span style={{ fontSize: 28 }}>
                            {sel.photo || "👤"}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: th.text,
                                letterSpacing: "-0.3px",
                              }}
                            >
                              {sel.name}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                fontFamily: "monospace",
                                color: th.muted,
                              }}
                            >
                              {sel.role}
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color:
                                sel._side === "plaintiff"
                                  ? "#ef4444"
                                  : "#3b82f6",
                              background:
                                (sel._side === "plaintiff"
                                  ? "#ef4444"
                                  : "#3b82f6") + "18",
                              padding: "3px 9px",
                              borderRadius: 8,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                            }}
                          >
                            {sel._side}
                          </span>
                          <button
                            onClick={() => setSelectedWitId(null)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: th.muted,
                              cursor: "pointer",
                              fontSize: 18,
                              marginLeft: 6,
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 1,
                            padding: 7,
                            background: th.surface,
                            borderBottom: `1px solid ${th.border}`,
                            overflowX: "auto",
                            flexShrink: 0,
                          }}
                        >
                          {[
                            ["profile", "👤", "Profile"],
                            ["pred", "🎬", "Prediction"],
                            ["depo", "📋", "Depo Deep-Dive"],
                            ["theory", "🎯", "Theories"],
                            ["rel", "🕸️", "Relations"],
                            ["log", "📅", "Logistics"],
                            ["prep", "📝", "Prep"],
                          ]
                            .concat(
                              sel.type === "expert"
                                ? [["exp", "🎓", "Expert Mode"]]
                                : [],
                            )
                            .map(([k, e, l]) => (
                              <button
                                key={k}
                                onClick={() => setWitSubTab(k)}
                                style={{
                                  padding: "6px 10px",
                                  fontSize: 9,
                                  fontWeight: witSubTab === k ? 700 : 500,
                                  background:
                                    witSubTab === k ? "#0891b2" : "transparent",
                                  color: witSubTab === k ? "#fff" : th.muted,
                                  border: "none",
                                  borderRadius: 5,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <span>{e}</span>
                                <span>{l}</span>
                              </button>
                            ))}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            overflow: "auto",
                            padding: "14px 18px",
                          }}
                        >
                          {witSubTab === "profile" && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Five-dimensional credibility radar. Asymmetric
                                shapes flag specific risks: high exposure + high
                                advocacy = visibly partisan witness with prior
                                contradictions.
                              </div>
                              {Object.values(sel.cred).reduce(
                                (a, b) => a + b,
                                0,
                              ) > 0 ? (
                                <>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 18,
                                      alignItems: "center",
                                      marginBottom: 14,
                                    }}
                                  >
                                    {(() => {
                                      const cr = sel.cred;
                                      const axes = [
                                        ["Likability", "likability"],
                                        ["Consistency", "consistency"],
                                        ["Exposure", "exposure"],
                                        ["Authority", "authority"],
                                        ["Advocacy", "advocacy"],
                                      ];
                                      const cx = 110,
                                        cy = 110,
                                        r = 80;
                                      const pts = axes.map(([, k], i) => {
                                        const a =
                                          (i / 5) * 2 * Math.PI - Math.PI / 2;
                                        const v = cr[k];
                                        return {
                                          px: cx + Math.cos(a) * r * (v / 100),
                                          py: cy + Math.sin(a) * r * (v / 100),
                                          v,
                                          a,
                                          k,
                                        };
                                      });
                                      const polyPts = pts
                                        .map((p) => `${p.px},${p.py}`)
                                        .join(" ");
                                      return (
                                        <svg
                                          width="220"
                                          height="220"
                                          viewBox="0 0 220 220"
                                          style={{
                                            margin: "0 auto",
                                            display: "block",
                                          }}
                                        >
                                          {[25, 50, 75, 100].map((lvl) => {
                                            const gp = axes
                                              .map((_, i) => {
                                                const a =
                                                  (i / 5) * 2 * Math.PI -
                                                  Math.PI / 2;
                                                return `${cx + Math.cos(a) * r * (lvl / 100)},${cy + Math.sin(a) * r * (lvl / 100)}`;
                                              })
                                              .join(" ");
                                            return (
                                              <polygon
                                                key={lvl}
                                                points={gp}
                                                fill="none"
                                                stroke={th.border}
                                                strokeWidth="1"
                                                strokeDasharray={
                                                  lvl === 100 ? "" : "2,3"
                                                }
                                                opacity="0.5"
                                              />
                                            );
                                          })}
                                          {axes.map((_, i) => {
                                            const a =
                                              (i / 5) * 2 * Math.PI -
                                              Math.PI / 2;
                                            return (
                                              <line
                                                key={i}
                                                x1={cx}
                                                y1={cy}
                                                x2={cx + Math.cos(a) * r}
                                                y2={cy + Math.sin(a) * r}
                                                stroke={th.border}
                                                strokeWidth="1"
                                                opacity="0.4"
                                              />
                                            );
                                          })}
                                          <polygon
                                            points={polyPts}
                                            fill="#0891b225"
                                            stroke="#0891b2"
                                            strokeWidth="2"
                                          />
                                          {pts.map((p, i) => (
                                            <circle
                                              key={i}
                                              cx={p.px}
                                              cy={p.py}
                                              r="4"
                                              fill={flagC(p.k, p.v)}
                                              stroke="#fff"
                                              strokeWidth="1.5"
                                            />
                                          ))}
                                          {axes.map(([lbl], i) => {
                                            const a =
                                              (i / 5) * 2 * Math.PI -
                                              Math.PI / 2;
                                            const lx =
                                              cx + Math.cos(a) * (r + 18);
                                            const ly =
                                              cy + Math.sin(a) * (r + 18);
                                            return (
                                              <text
                                                key={i}
                                                x={lx}
                                                y={ly}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fontSize="9"
                                                fontFamily="system-ui"
                                                fontWeight="600"
                                                fill={th.text}
                                              >
                                                {lbl}
                                              </text>
                                            );
                                          })}
                                        </svg>
                                      );
                                    })()}
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 5,
                                      }}
                                    >
                                      {Object.entries(sel.cred).map(
                                        ([k, v]) => {
                                          const c = flagC(k, v);
                                          return (
                                            <div
                                              key={k}
                                              style={{
                                                padding: "6px 10px",
                                                background: th.surface,
                                                borderRadius: 5,
                                                borderLeft: `3px solid ${c}`,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 9,
                                                    color: th.text,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {credLabels[k]}
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: 11,
                                                    color: c,
                                                    fontWeight: 800,
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {v}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                  {sel.story && (
                                    <div
                                      style={{
                                        padding: "12px 14px",
                                        background: dark
                                          ? "#0f172a"
                                          : "#fefce8",
                                        borderLeft: "3px solid #eab308",
                                        borderRadius: "0 6px 6px 0",
                                        fontSize: 11,
                                        color: th.text,
                                        lineHeight: 1.7,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#a16207",
                                          fontFamily: "monospace",
                                          letterSpacing: 1.2,
                                          fontWeight: 700,
                                          marginBottom: 5,
                                        }}
                                      >
                                        📌 STRATEGIC PROFILE
                                      </div>
                                      {sel.story}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: th.muted,
                                    fontSize: 11,
                                    background: "#dc262615",
                                    border: "1px dashed #dc262655",
                                    borderRadius: 8,
                                  }}
                                >
                                  ⚠️ No deposition taken. Credibility profile
                                  cannot be assessed without testimony record.
                                </div>
                              )}
                            </div>
                          )}
                          {witSubTab === "pred" && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Predicted testimony arc - direct, cross, and
                                redirect. Updates in real-time during trial as
                                deviations from prediction are flagged.
                              </div>
                              {[
                                ["direct", "🎤", "DIRECT", "#10b981"],
                                ["cross", "⚔️", "CROSS", "#dc2626"],
                                ["redirect", "🔄", "REDIRECT", "#3b82f6"],
                              ].map(([k, e, l, c]) =>
                                sel.prediction &&
                                sel.prediction[k] &&
                                sel.prediction[k] !==
                                  "N/A - not yet deposed" ? (
                                  <div
                                    key={k}
                                    style={{
                                      background: th.surface,
                                      borderRadius: 7,
                                      borderLeft: `4px solid ${c}`,
                                      padding: "11px 14px",
                                      marginBottom: 9,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: c,
                                        letterSpacing: 1.2,
                                        fontWeight: 700,
                                        marginBottom: 6,
                                      }}
                                    >
                                      {e} {l}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: th.text,
                                        lineHeight: 1.7,
                                      }}
                                    >
                                      {sel.prediction[k]}
                                    </div>
                                  </div>
                                ) : null,
                              )}
                            </div>
                          )}
                          {witSubTab === "depo" && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Structured analysis of deposition record. Topics
                                covered, topics NOT covered, key admissions,
                                gaps, and auto-generated action items.
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 11,
                                  padding: "7px 11px",
                                  background: sel.depo.status.includes(
                                    "NOT YET",
                                  )
                                    ? "#dc262615"
                                    : "#10b98115",
                                  border: `1px solid ${sel.depo.status.includes("NOT YET") ? "#dc262655" : "#10b98155"}`,
                                  borderRadius: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: sel.depo.status.includes("NOT YET")
                                      ? "#dc2626"
                                      : "#10b981",
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                  }}
                                >
                                  STATUS: {sel.depo.status}
                                </span>
                                {sel.depo.date && (
                                  <span
                                    style={{
                                      fontSize: 8,
                                      fontFamily: "monospace",
                                      color: th.muted,
                                    }}
                                  >
                                    · {sel.depo.date}
                                  </span>
                                )}
                              </div>
                              {sel.depo.topics &&
                                sel.depo.topics.length > 0 && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      ✓ TOPICS COVERED
                                    </div>
                                    {sel.depo.topics.map((t, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "4px 9px",
                                          background: "#10b98108",
                                          borderLeft: "2px solid #10b98155",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                        }}
                                      >
                                        {t}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {sel.depo.notTopics &&
                                sel.depo.notTopics.length > 0 && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#dc2626",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      ✗ TOPICS NOT COVERED
                                    </div>
                                    {sel.depo.notTopics.map((t, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "4px 9px",
                                          background: "#dc262608",
                                          borderLeft: "2px solid #dc262655",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                        }}
                                      >
                                        {t}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {sel.depo.admissions &&
                                sel.depo.admissions.length > 0 && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: th.muted,
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      📌 KEY ADMISSIONS
                                    </div>
                                    {sel.depo.admissions.map((a, i) => {
                                      const c =
                                        a.impact === "red"
                                          ? "#dc2626"
                                          : a.impact === "green"
                                            ? "#10b981"
                                            : a.impact === "amber"
                                              ? "#f59e0b"
                                              : "#6b7280";
                                      return (
                                        <div
                                          key={i}
                                          style={{
                                            padding: "7px 11px",
                                            background: c + "08",
                                            borderLeft: `3px solid ${c}`,
                                            borderRadius: "0 5px 5px 0",
                                            marginBottom: 5,
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: c,
                                              fontWeight: 700,
                                              marginBottom: 3,
                                            }}
                                          >
                                            {a.cite}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 10,
                                              color: th.text,
                                              lineHeight: 1.55,
                                            }}
                                          >
                                            {a.text}
                                          </div>
                                          {a.linkedToImpBank && (
                                            <div
                                              style={{
                                                fontSize: 7,
                                                color: "#a78bfa",
                                                marginTop: 4,
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              🔗 Linked to Impeachment Bank
                                            </div>
                                          )}
                                          {a.update && (
                                            <div
                                              style={{
                                                fontSize: 7,
                                                color: "#dc2626",
                                                marginTop: 4,
                                                fontFamily: "monospace",
                                                fontWeight: 700,
                                              }}
                                            >
                                              ⚡ {a.update}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              {sel.depo.gaps && sel.depo.gaps.length > 0 && (
                                <div style={{ marginBottom: 10 }}>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#f59e0b",
                                      fontWeight: 700,
                                      letterSpacing: 1,
                                      marginBottom: 6,
                                    }}
                                  >
                                    ⚠ GAPS IDENTIFIED
                                  </div>
                                  {sel.depo.gaps.map((g, i) => (
                                    <div
                                      key={i}
                                      style={{
                                        fontSize: 9,
                                        padding: "5px 11px",
                                        background: "#f59e0b08",
                                        border: "1px dashed #f59e0b55",
                                        borderRadius: 5,
                                        marginBottom: 4,
                                        color: th.text,
                                        lineHeight: 1.55,
                                      }}
                                    >
                                      {g}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {sel.depo.actions &&
                                sel.depo.actions.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#a78bfa",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      🎯 AUTO-GENERATED ACTIONS
                                    </div>
                                    {sel.depo.actions.map((a, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          gap: 7,
                                          padding: "7px 11px",
                                          background: dark
                                            ? "#1e1b4b"
                                            : "#f5f3ff",
                                          borderLeft: "3px solid #a78bfa",
                                          borderRadius: "0 5px 5px 0",
                                          marginBottom: 4,
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        <span
                                          style={{
                                            color: "#a78bfa",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                          }}
                                        >
                                          ▸
                                        </span>
                                        {a}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          )}
                          {witSubTab === "theory" && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Theory elements this witness establishes,
                                partially supports, or contradicts.
                                Cross-referenced with Theories tab.
                              </div>
                              {(sel.elements || []).map((m, i) => {
                                const t = allTheories.find(
                                  (t) => t.id === m.theoryId,
                                );
                                if (!t) return null;
                                const el = t.elements[m.elemIdx];
                                const c =
                                  m.status === "green"
                                    ? "#10b981"
                                    : m.status === "amber"
                                      ? "#f59e0b"
                                      : m.status === "red"
                                        ? "#dc2626"
                                        : "#6b7280";
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      padding: "10px 13px",
                                      background: th.surface,
                                      borderLeft: `3px solid ${c}`,
                                      borderRadius: "0 6px 6px 0",
                                      marginBottom: 7,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 5,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color: c,
                                          fontWeight: 700,
                                          letterSpacing: 1,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {m.status}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color:
                                            t._side === "plaintiff"
                                              ? "#ef4444"
                                              : "#3b82f6",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {t._side.toUpperCase()}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {t.name}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        marginBottom: 4,
                                      }}
                                    >
                                      Element {m.elemIdx + 1}:{" "}
                                      {el ? el.name : ""}
                                    </div>
                                    {m.note && (
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          lineHeight: 1.55,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {m.note}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(!sel.elements || sel.elements.length === 0) && (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: th.muted,
                                    fontSize: 10,
                                  }}
                                >
                                  No theory elements mapped yet.
                                </div>
                              )}
                            </div>
                          )}
                          {witSubTab === "rel" && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Relationships to other witnesses - 
                                corroboration, contradiction, foundational, or
                                impeachment.
                              </div>
                              {(sel.relations || []).map((r, i) => {
                                const other = allW.find((w) => w.id === r.with);
                                if (!other) return null;
                                const c =
                                  {
                                    corroborates: "#10b981",
                                    contradicts: "#dc2626",
                                    exposes: "#f59e0b",
                                    "exposed-by": "#f59e0b",
                                    foundational: "#3b82f6",
                                    "opposing-expert": "#a78bfa",
                                  }[r.type] || "#999";
                                return (
                                  <div
                                    key={i}
                                    onClick={() => setSelectedWitId(other.id)}
                                    style={{
                                      padding: "10px 13px",
                                      background: th.surface,
                                      borderLeft: `3px solid ${c}`,
                                      borderRadius: "0 6px 6px 0",
                                      marginBottom: 7,
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 5,
                                      }}
                                    >
                                      <span style={{ fontSize: 14 }}>
                                        {other.photo || "👤"}
                                      </span>
                                      <div style={{ flex: 1 }}>
                                        <span
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: th.text,
                                          }}
                                        >
                                          {other.name}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            color: c,
                                            fontWeight: 700,
                                            letterSpacing: 1,
                                            textTransform: "uppercase",
                                            marginLeft: 6,
                                          }}
                                        >
                                          {r.type}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        lineHeight: 1.55,
                                      }}
                                    >
                                      {r.note}
                                    </div>
                                  </div>
                                );
                              })}
                              {(!sel.relations ||
                                sel.relations.length === 0) && (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: th.muted,
                                    fontSize: 10,
                                  }}
                                >
                                  No witness relationships mapped.
                                </div>
                              )}
                            </div>
                          )}
                          {witSubTab === "log" && sel.logistics && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Availability constraints, location, and
                                logistical risks. Flags surface BEFORE they
                                become crises.
                              </div>
                              <div
                                style={{
                                  padding: "11px 13px",
                                  background: th.surface,
                                  borderRadius: 6,
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: th.muted,
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    marginBottom: 4,
                                  }}
                                >
                                  AVAILABILITY
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: th.text,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {sel.logistics.availability}
                                </div>
                              </div>
                              <div
                                style={{
                                  padding: "11px 13px",
                                  background: th.surface,
                                  borderRadius: 6,
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: th.muted,
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    marginBottom: 4,
                                  }}
                                >
                                  LOCATION
                                </div>
                                <div style={{ fontSize: 10, color: th.text }}>
                                  {sel.logistics.location}
                                </div>
                              </div>
                              {sel.logistics.flags &&
                                sel.logistics.flags.length > 0 && (
                                  <div
                                    style={{
                                      padding: "11px 13px",
                                      background: "#dc262615",
                                      border: "1px dashed #dc262655",
                                      borderRadius: 6,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#dc2626",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 5,
                                      }}
                                    >
                                      ⚠ FLAGS
                                    </div>
                                    {sel.logistics.flags.map((f, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          color: th.text,
                                          padding: "3px 0",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        · {f}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          )}
                          {witSubTab === "prep" && sel.prep && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Witness preparation tracker - sessions
                                completed, topics covered, topics still to
                                address.
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 8,
                                  marginBottom: 11,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "11px 13px",
                                    background: dark ? "#0f172a" : "#f0f9ff",
                                    borderRadius: 6,
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 24,
                                      fontWeight: 800,
                                      color: "#0891b2",
                                      lineHeight: 1,
                                    }}
                                  >
                                    {sel.prep.sessions}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                      letterSpacing: 1,
                                      marginTop: 3,
                                    }}
                                  >
                                    SESSIONS COMPLETE
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "11px 13px",
                                    background: dark ? "#0f172a" : "#fef3c7",
                                    borderRadius: 6,
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 24,
                                      fontWeight: 800,
                                      color: "#f59e0b",
                                      lineHeight: 1,
                                    }}
                                  >
                                    {(sel.prep.uncovered || []).length}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                      letterSpacing: 1,
                                      marginTop: 3,
                                    }}
                                  >
                                    TOPICS REMAINING
                                  </div>
                                </div>
                              </div>
                              {sel.prep.covered &&
                                sel.prep.covered.length > 0 && (
                                  <div style={{ marginBottom: 8 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      ✓ COVERED
                                    </div>
                                    {sel.prep.covered.map((c, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "4px 10px",
                                          background: "#10b98108",
                                          borderLeft: "2px solid #10b98155",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                        }}
                                      >
                                        {c}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {sel.prep.uncovered &&
                                sel.prep.uncovered.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#f59e0b",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      ⚠ STILL TO COVER
                                    </div>
                                    {sel.prep.uncovered.map((c, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "4px 10px",
                                          background: "#f59e0b08",
                                          borderLeft: "2px solid #f59e0b55",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                        }}
                                      >
                                        {c}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          )}
                          {witSubTab === "exp" && sel.expert && (
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 11,
                                  lineHeight: 1.55,
                                  fontStyle: "italic",
                                }}
                              >
                                Expert-specific intelligence - Daubert history,
                                methodology attack surface, redirect script,
                                prior testimony.
                              </div>
                              <div
                                style={{
                                  padding: "11px 13px",
                                  background:
                                    sel.expert.daubert.status.includes(
                                      "excluded",
                                    )
                                      ? "#f59e0b15"
                                      : "#10b98115",
                                  border: `1px solid ${sel.expert.daubert.status.includes("excluded") ? "#f59e0b55" : "#10b98155"}`,
                                  borderLeft: `4px solid ${sel.expert.daubert.status.includes("excluded") ? "#f59e0b" : "#10b981"}`,
                                  borderRadius: "0 6px 6px 0",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: sel.expert.daubert.status.includes(
                                      "excluded",
                                    )
                                      ? "#f59e0b"
                                      : "#10b981",
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    marginBottom: 5,
                                  }}
                                >
                                  🎓 DAUBERT STATUS
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: th.text,
                                    marginBottom: 6,
                                  }}
                                >
                                  {sel.expert.daubert.status}
                                </div>
                                {sel.expert.daubert.rulings.map((r, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.6,
                                      marginTop: 5,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        color: th.text,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {r.date}:
                                    </span>{" "}
                                    {r.ruling}
                                  </div>
                                ))}
                              </div>
                              {sel.expert.attackSurface &&
                                sel.expert.attackSurface.length > 0 && (
                                  <div style={{ marginBottom: 9 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#dc2626",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      ⚔️ METHODOLOGY ATTACK SURFACE
                                    </div>
                                    {sel.expert.attackSurface.map((a, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "5px 11px",
                                          background: "#dc262608",
                                          borderLeft: "2px solid #dc262655",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {a}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {sel.expert.opposing && (
                                <div
                                  style={{
                                    padding: "10px 13px",
                                    background: dark ? "#1e1b4b" : "#f5f3ff",
                                    borderLeft: "3px solid #a78bfa",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 9,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                      letterSpacing: 1,
                                      marginBottom: 5,
                                    }}
                                  >
                                    🥊 OPPOSING-EXPERT MATCHUP
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {sel.expert.opposing}
                                  </div>
                                </div>
                              )}
                              {sel.expert.redirect &&
                                sel.expert.redirect.length > 0 && (
                                  <div style={{ marginBottom: 9 }}>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#10b981",
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      🔄 REDIRECT SCRIPT
                                    </div>
                                    {sel.expert.redirect.map((r, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 9,
                                          padding: "5px 11px",
                                          background: "#10b98108",
                                          borderLeft: "2px solid #10b98155",
                                          borderRadius: "0 4px 4px 0",
                                          marginBottom: 3,
                                          color: th.text,
                                          lineHeight: 1.55,
                                        }}
                                      >
                                        {r}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {sel.expert.juryInstr && (
                                <div
                                  style={{
                                    padding: "10px 13px",
                                    background: th.surface,
                                    borderLeft: "3px solid #3b82f6",
                                    borderRadius: "0 6px 6px 0",
                                    marginBottom: 9,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 7,
                                      fontFamily: "monospace",
                                      color: "#3b82f6",
                                      fontWeight: 700,
                                      letterSpacing: 1,
                                      marginBottom: 5,
                                    }}
                                  >
                                    ⚖️ JURY INSTRUCTION
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.text,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {sel.expert.juryInstr}
                                  </div>
                                </div>
                              )}
                              {sel.expert.priorTestimony &&
                                sel.expert.priorTestimony.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: th.muted,
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        marginBottom: 6,
                                      }}
                                    >
                                      📚 PRIOR TESTIMONY
                                    </div>
                                    {sel.expert.priorTestimony.map((p, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          padding: "7px 11px",
                                          background: th.surface,
                                          borderRadius: 5,
                                          marginBottom: 4,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 9,
                                            fontWeight: 700,
                                            color: th.text,
                                          }}
                                        >
                                          {p.case}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.muted,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          · {p.topic}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: "#10b981",
                                            fontFamily: "monospace",
                                            marginTop: 2,
                                          }}
                                        >
                                          · {p.outcome}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "locations" &&
              (() => {
                const data = LOCATIONS_INTEL[caseId] || LOCATIONS_INTEL.juul;
                const allPins = [];
                (data.urgent || []).forEach((u) =>
                  allPins.push({ ...u, _kind: "urgent", _section: "Urgent" }),
                );
                (data.sections || []).forEach((s) => {
                  (s.pins || []).forEach((p) =>
                    allPins.push({
                      ...p,
                      _kind: "pin",
                      _section: s.title,
                      _sectionIcon: s.icon,
                    }),
                  );
                  (s.supplyChain || []).forEach((p) =>
                    allPins.push({
                      ...p,
                      _kind: "supply",
                      _section: s.title,
                      _sectionIcon: s.icon,
                    }),
                  );
                });
                const mapPin = locMapPinId
                  ? allPins.find((p) => (p.name || p.title) === locMapPinId)
                  : allPins[0] || null;
                const openInGoogle = (pin) => {
                  const q = encodeURIComponent(
                    pin.name || pin.title || pin.coords || "",
                  );
                  window.open(
                    "https://www.google.com/maps/search/?api=1&query=" + q,
                    "_blank",
                  );
                };
                const handleAddAssetForLoc = (pinName) => {
                  setUploadAssoc({ wit: "", loc: pinName });
                  setAssetUploadDraft({
                    title: "",
                    desc: "",
                    date: "",
                    classification: "foundation",
                    exhibit: "",
                    type: "photo",
                  });
                  setShowUpload(true);
                };
                return (
                  <div
                    style={{
                      maxWidth: 1200,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg,#ec4899,#be185d)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 7,
                          color: "#fce7f3",
                          fontFamily: "monospace",
                          letterSpacing: 1.5,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        LOCATIONS · GEOGRAPHIC INTELLIGENCE
                      </div>
                      <h1
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                          margin: "0 0 6px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Locations
                      </h1>
                      <div style={{ fontSize: 10, color: "#fce7f3" }}>
                        {allPins.length} pins · {data.sections.length} section
                        {data.sections.length === 1 ? "" : "s"} ·{" "}
                        {data.urgent.length} urgent flag
                        {data.urgent.length === 1 ? "" : "s"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 5,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                        width: "fit-content",
                      }}
                    >
                      {[
                        ["list", "📋", "List View"],
                        ["map", "🗺️", "Map View"],
                      ].map(([k, e, l]) => (
                        <button
                          key={k}
                          onClick={() => setLocView(k)}
                          style={{
                            padding: "7px 14px",
                            fontSize: 10,
                            fontWeight: locView === k ? 700 : 500,
                            background:
                              locView === k ? "#ec4899" : "transparent",
                            color: locView === k ? "#fff" : th.muted,
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>{e}</span>
                          <span>{l}</span>
                        </button>
                      ))}
                    </div>

                    {locView === "map" && (
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 11,
                          border: `1px solid ${th.border}`,
                          padding: 14,
                          marginBottom: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "280px 1fr",
                            gap: 12,
                            height: "calc(100vh - 320px)",
                            minHeight: 520,
                          }}
                        >
                          <div
                            style={{
                              background: th.surface,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              overflow: "auto",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 13px",
                                borderBottom: `1px solid ${th.border}`,
                                position: "sticky",
                                top: 0,
                                background: th.surface,
                                zIndex: 1,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: th.text,
                                  marginBottom: 2,
                                }}
                              >
                                📍 All Pins ({allPins.length})
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  fontFamily: "monospace",
                                }}
                              >
                                Tap to center · auto-plotted on map
                              </div>
                            </div>
                            {allPins.map((p, pi) => {
                              const isSel =
                                mapPin &&
                                (mapPin.name || mapPin.title) ===
                                  (p.name || p.title);
                              const isUrgent = p._kind === "urgent";
                              const isSupply = p._kind === "supply";
                              const sideC = isUrgent
                                ? "#dc2626"
                                : isSupply
                                  ? p.authStatus === "green"
                                    ? "#10b981"
                                    : p.authStatus === "amber"
                                      ? "#f59e0b"
                                      : "#dc2626"
                                  : "#ec4899";
                              return (
                                <div
                                  key={pi}
                                  onClick={() =>
                                    setLocMapPinId(p.name || p.title)
                                  }
                                  style={{
                                    padding: "9px 12px",
                                    cursor: "pointer",
                                    background: isSel
                                      ? dark
                                        ? "#3a1d4f"
                                        : "#fce7f3"
                                      : "transparent",
                                    borderBottom: `1px solid ${th.border}`,
                                    borderLeft: `3px solid ${sideC}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 5,
                                      marginBottom: 3,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {p._sectionIcon && (
                                      <span style={{ fontSize: 10 }}>
                                        {p._sectionIcon}
                                      </span>
                                    )}
                                    {isUrgent && (
                                      <span style={{ fontSize: 10 }}>🚨</span>
                                    )}
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: th.text,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {p.name || p.title}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: th.muted,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {p._section}
                                  </div>
                                  {p.coords && (
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: th.faint,
                                        fontFamily: "monospace",
                                        marginTop: 2,
                                      }}
                                    >
                                      📍{" "}
                                      {p.coords.length > 32
                                        ? p.coords.slice(0, 30) + "…"
                                        : p.coords}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div
                            style={{
                              background: th.bg,
                              borderRadius: 8,
                              border: `1px solid ${th.border}`,
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {mapPin ? (
                              <>
                                <iframe
                                  key={mapPin.name || mapPin.title}
                                  title="map"
                                  src={
                                    "https://www.google.com/maps?q=" +
                                    encodeURIComponent(
                                      mapPin.coords &&
                                        !mapPin.coords.match(/[°NSEW]/)
                                        ? mapPin.coords
                                        : mapPin.name || mapPin.title,
                                    ) +
                                    "&output=embed&z=12"
                                  }
                                  style={{
                                    flex: 1,
                                    border: "none",
                                    width: "100%",
                                    minHeight: 360,
                                  }}
                                  loading="lazy"
                                ></iframe>
                                <div
                                  style={{
                                    padding: "11px 14px",
                                    borderTop: `1px solid ${th.border}`,
                                    background: th.surface,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 9,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 160 }}>
                                    <div
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: th.text,
                                      }}
                                    >
                                      {mapPin.name || mapPin.title}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {mapPin.coords || "-"}
                                    </div>
                                    {mapPin.dkts && mapPin.dkts.length > 0 && (
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: 4,
                                          marginTop: 5,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {mapPin.dkts.map((d, di) => (
                                          <span
                                            key={di}
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#f59e0b",
                                              background: "#f59e0b15",
                                              border: "1px solid #f59e0b33",
                                              padding: "2px 6px",
                                              borderRadius: 4,
                                              fontWeight: 600,
                                            }}
                                          >
                                            {d}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleAddAssetForLoc(
                                        mapPin.name || mapPin.title,
                                      )
                                    }
                                    style={{
                                      padding: "6px 11px",
                                      fontSize: 9,
                                      fontWeight: 600,
                                      background: "#7c3aed",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                    }}
                                  >
                                    ＋ Upload Asset Here
                                  </button>
                                  <button
                                    onClick={() => openInGoogle(mapPin)}
                                    style={{
                                      padding: "6px 11px",
                                      fontSize: 9,
                                      fontWeight: 600,
                                      background: "#1e40af",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 5,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Open in Google Maps ↗
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: th.muted,
                                  fontSize: 11,
                                }}
                              >
                                No pins to plot
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {locView === "list" && (
                      <>
                        {data.urgent.map((u, i) => (
                          <div
                            key={i}
                            style={{
                              background:
                                "linear-gradient(135deg,#dc2626,#991b1b)",
                              borderRadius: 12,
                              padding: "18px 22px",
                              marginBottom: 14,
                              boxShadow: "0 4px 16px rgba(220,38,38,0.3)",
                              animation: "pulse 2s ease-in-out infinite",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontSize: 24 }}>🚨</span>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: "#fee2e2",
                                    fontFamily: "monospace",
                                    letterSpacing: 1.5,
                                    fontWeight: 700,
                                    marginBottom: 3,
                                  }}
                                >
                                  URGENT - ACTION REQUIRED
                                </div>
                                <div
                                  style={{
                                    fontSize: 15,
                                    fontWeight: 800,
                                    color: "#fff",
                                    letterSpacing: "-0.3px",
                                  }}
                                >
                                  {u.title}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: "#fff",
                                    background: "#ffffff22",
                                    padding: "5px 11px",
                                    borderRadius: 7,
                                    fontWeight: 700,
                                    border: "1px solid #ffffff44",
                                  }}
                                >
                                  {u.countdown}
                                </span>
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    color: "#fff",
                                    background: "#ffffff22",
                                    padding: "5px 11px",
                                    borderRadius: 7,
                                    fontWeight: 700,
                                    border: "1px solid #ffffff44",
                                  }}
                                >
                                  {u.leadTime}
                                </span>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: "#fee2e2",
                                lineHeight: 1.65,
                                marginBottom: 12,
                                padding: "10px 14px",
                                background: "#00000033",
                                borderRadius: 7,
                                border: "1px solid #ffffff22",
                              }}
                            >
                              {u.summary}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                fontFamily: "monospace",
                                color: "#fef2f2",
                                fontWeight: 700,
                                letterSpacing: 1.2,
                                marginBottom: 6,
                              }}
                            >
                              📋 STEPS REQUIRED
                            </div>
                            <ol
                              style={{
                                margin: "0 0 12px",
                                paddingLeft: 24,
                                color: "#fee2e2",
                                fontSize: 10,
                                lineHeight: 1.7,
                              }}
                            >
                              {u.steps.map((s, si) => (
                                <li key={si} style={{ marginBottom: 3 }}>
                                  {s}
                                </li>
                              ))}
                            </ol>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "10px 14px",
                                background: "#00000033",
                                borderRadius: 7,
                                border: "1px solid #ffffff22",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 8,
                                  fontFamily: "monospace",
                                  color: "#fee2e2",
                                  fontWeight: 600,
                                }}
                              >
                                📍 {u.coords}
                              </span>
                              <div style={{ flex: 1 }} />
                              <button
                                onClick={() => {
                                  toast(
                                    "✓ Task added to War Room countdown checklist",
                                    "success",
                                  );
                                }}
                                style={{
                                  padding: "7px 14px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: "#fff",
                                  color: "#dc2626",
                                  border: "none",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                }}
                              >
                                ＋ Create War Room Task
                              </button>
                            </div>
                          </div>
                        ))}

                        {data.sections.map((s, si) => {
                          const isOpen = locExpanded[s.id] !== false;
                          return (
                            <div
                              key={s.id}
                              style={{
                                background: th.card,
                                borderRadius: 11,
                                border: `1px solid ${th.border}`,
                                marginBottom: 11,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                onClick={() =>
                                  setLocExpanded((p) => ({
                                    ...p,
                                    [s.id]: !isOpen,
                                  }))
                                }
                                style={{
                                  padding: "14px 18px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 11,
                                  cursor: "pointer",
                                  background: dark ? "#0a0f1c" : "#fafbfc",
                                }}
                              >
                                <span style={{ fontSize: 22 }}>{s.icon}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <h3
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      margin: "0 0 3px",
                                      color: th.text,
                                      letterSpacing: "-0.2px",
                                    }}
                                  >
                                    {s.title}
                                  </h3>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {s.intro.slice(0, 160)}
                                    {s.intro.length > 160 ? "…" : ""}
                                  </div>
                                </div>
                                <span
                                  style={{
                                    fontSize: 18,
                                    color: th.muted,
                                    opacity: 0.7,
                                    transition: "transform 0.2s",
                                    transform: isOpen
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  }}
                                >
                                  ▾
                                </span>
                              </div>
                              {isOpen && (
                                <div
                                  style={{
                                    padding: "14px 20px",
                                    borderTop: `1px solid ${th.border}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: th.muted,
                                      lineHeight: 1.65,
                                      fontStyle: "italic",
                                      marginBottom: 14,
                                      paddingBottom: 12,
                                      borderBottom: `1px dashed ${th.border}`,
                                    }}
                                  >
                                    {s.intro}
                                  </div>

                                  {s.supplyChain && (
                                    <div style={{ marginBottom: 14 }}>
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "repeat(auto-fit,minmax(260px,1fr))",
                                          gap: 10,
                                          position: "relative",
                                        }}
                                      >
                                        {s.supplyChain.map((p, pi) => {
                                          const aC =
                                            p.authStatus === "green"
                                              ? "#10b981"
                                              : p.authStatus === "amber"
                                                ? "#f59e0b"
                                                : "#dc2626";
                                          return (
                                            <div
                                              key={pi}
                                              style={{
                                                background: th.surface,
                                                borderRadius: 9,
                                                border: `2px solid ${aC}55`,
                                                borderLeft: `5px solid ${aC}`,
                                                padding: "12px 14px",
                                                position: "relative",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 7,
                                                  marginBottom: 6,
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    color: aC,
                                                    background: aC + "18",
                                                    padding: "2px 7px",
                                                    borderRadius: 8,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.8,
                                                  }}
                                                >
                                                  STEP {p.step}
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: 7,
                                                    fontFamily: "monospace",
                                                    color: aC,
                                                    background: aC + "18",
                                                    padding: "2px 7px",
                                                    borderRadius: 8,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.8,
                                                    textTransform: "uppercase",
                                                  }}
                                                >
                                                  {p.authStatus === "green"
                                                    ? "✓ AUTH"
                                                    : "⚠ " +
                                                      p.authStatus.toUpperCase()}
                                                </span>
                                              </div>
                                              <h4
                                                style={{
                                                  fontSize: 12,
                                                  fontWeight: 700,
                                                  margin: "0 0 4px",
                                                  color: th.text,
                                                }}
                                              >
                                                📍 {p.name}
                                              </h4>
                                              <div
                                                style={{
                                                  fontSize: 8,
                                                  color: th.muted,
                                                  fontFamily: "monospace",
                                                  marginBottom: 6,
                                                }}
                                              >
                                                {p.coords}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: 10,
                                                  fontWeight: 700,
                                                  color: th.text,
                                                  padding: "5px 9px",
                                                  background: dark
                                                    ? "#0f172a"
                                                    : "#fff8e1",
                                                  borderLeft: `2px solid ${aC}`,
                                                  borderRadius: "0 4px 4px 0",
                                                  marginBottom: 7,
                                                }}
                                              >
                                                {p.label}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: 9,
                                                  color: th.muted,
                                                  lineHeight: 1.55,
                                                  marginBottom: 8,
                                                }}
                                              >
                                                {p.authNote}
                                              </div>
                                              <div
                                                style={{
                                                  padding: "6px 10px",
                                                  background: "#dc262615",
                                                  borderLeft:
                                                    "2px dashed #dc262655",
                                                  borderRadius: "0 4px 4px 0",
                                                  fontSize: 9,
                                                  color: th.text,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    color: "#dc2626",
                                                    fontWeight: 700,
                                                    marginRight: 4,
                                                  }}
                                                >
                                                  📌 TASK:
                                                </span>
                                                {p.task}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      {s.fraudGap && (
                                        <div
                                          style={{
                                            marginTop: 11,
                                            padding: "11px 14px",
                                            background: dark
                                              ? "#1a0a0a"
                                              : "#fff1f2",
                                            border: "1px dashed #dc262666",
                                            borderRadius: 7,
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: 7,
                                              fontFamily: "monospace",
                                              color: "#dc2626",
                                              letterSpacing: 1.2,
                                              fontWeight: 700,
                                              marginBottom: 5,
                                            }}
                                          >
                                            ⚖️ FRAUD GAP
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 10,
                                              color: th.text,
                                              lineHeight: 1.65,
                                            }}
                                          >
                                            {s.fraudGap}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {s.pins &&
                                    s.pins.map((p, pi) => (
                                      <div
                                        key={pi}
                                        style={{
                                          background: th.surface,
                                          borderRadius: 9,
                                          border: `1px solid ${th.border}`,
                                          padding: "12px 16px",
                                          marginBottom: 8,
                                          borderLeft: p.live
                                            ? "4px solid #ef4444"
                                            : "4px solid #ec4899",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 7,
                                            marginBottom: 6,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <span style={{ fontSize: 14 }}>
                                            📍
                                          </span>
                                          <h4
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 700,
                                              margin: 0,
                                              color: th.text,
                                              flex: 1,
                                              minWidth: 0,
                                            }}
                                          >
                                            {p.name}
                                          </h4>
                                          {p.live && (
                                            <span
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#ef4444",
                                                background: "#ef444418",
                                                padding: "2px 8px",
                                                borderRadius: 8,
                                                fontWeight: 700,
                                                letterSpacing: 0.8,
                                                animation:
                                                  "pulse 1.5s ease-in-out infinite",
                                              }}
                                            >
                                              ● LIVE TRIAL
                                            </span>
                                          )}
                                          {p.coords && (
                                            <span
                                              style={{
                                                fontSize: 8,
                                                color: th.muted,
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              {p.coords}
                                            </span>
                                          )}
                                        </div>
                                        {p.dkts && p.dkts.length > 0 && (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 4,
                                              marginBottom: 8,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {p.dkts.map((dk, dki) => (
                                              <span
                                                key={dki}
                                                style={{
                                                  fontSize: 7,
                                                  fontFamily: "monospace",
                                                  color: "#f59e0b",
                                                  background: "#f59e0b15",
                                                  border: "1px solid #f59e0b33",
                                                  padding: "2px 7px",
                                                  borderRadius: 4,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {dk}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: 5,
                                            marginBottom: 8,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <button
                                            onClick={() =>
                                              handleAddAssetForLoc(p.name)
                                            }
                                            style={{
                                              padding: "4px 9px",
                                              fontSize: 8,
                                              fontWeight: 600,
                                              background: "#7c3aed",
                                              color: "#fff",
                                              border: "none",
                                              borderRadius: 4,
                                              cursor: "pointer",
                                            }}
                                          >
                                            ＋ Upload Asset
                                          </button>
                                          <button
                                            onClick={() => {
                                              setLocView("map");
                                              setLocMapPinId(p.name);
                                            }}
                                            style={{
                                              padding: "4px 9px",
                                              fontSize: 8,
                                              fontWeight: 600,
                                              background: "#ec4899",
                                              color: "#fff",
                                              border: "none",
                                              borderRadius: 4,
                                              cursor: "pointer",
                                            }}
                                          >
                                            🗺️ View on Map
                                          </button>
                                          <button
                                            onClick={() => openInGoogle(p)}
                                            style={{
                                              padding: "4px 9px",
                                              fontSize: 8,
                                              fontWeight: 600,
                                              background: th.card,
                                              color: th.muted,
                                              border: `1px solid ${th.border}`,
                                              borderRadius: 4,
                                              cursor: "pointer",
                                            }}
                                          >
                                            Open in Google Maps ↗
                                          </button>
                                        </div>
                                        {p.neighborhood && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.muted,
                                              fontFamily: "monospace",
                                              marginBottom: 5,
                                            }}
                                          >
                                            Neighborhood: {p.neighborhood}
                                          </div>
                                        )}
                                        {p.remediation && (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 10,
                                              marginBottom: 7,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            <div
                                              style={{
                                                padding: "5px 10px",
                                                background: "#10b98115",
                                                border: "1px solid #10b98155",
                                                borderRadius: 5,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: 7,
                                                  fontFamily: "monospace",
                                                  color: "#10b981",
                                                  fontWeight: 700,
                                                }}
                                              >
                                                REMEDIATION
                                              </span>
                                              <div
                                                style={{
                                                  fontSize: 11,
                                                  fontWeight: 700,
                                                  color: th.text,
                                                }}
                                              >
                                                {p.remediation}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {p.findings && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.55,
                                              marginBottom: 6,
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: th.muted,
                                                fontWeight: 700,
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                letterSpacing: 0.8,
                                                display: "block",
                                                marginBottom: 2,
                                              }}
                                            >
                                              FINDINGS
                                            </span>
                                            {p.findings}
                                          </div>
                                        )}
                                        {p.retail && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.55,
                                              marginBottom: 6,
                                              padding: "6px 9px",
                                              background: "#3b82f608",
                                              borderLeft: "2px solid #3b82f655",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "#3b82f6",
                                                fontWeight: 700,
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                letterSpacing: 0.8,
                                                display: "block",
                                                marginBottom: 2,
                                              }}
                                            >
                                              RETAIL CONTEXT
                                            </span>
                                            {p.retail}
                                          </div>
                                        )}
                                        {p.solTimeline && (
                                          <div style={{ marginTop: 9 }}>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: th.muted,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              📅 SOL TIMELINE
                                            </div>
                                            {p.solTimeline.map((e, ei) => {
                                              const c =
                                                e.impact === "red"
                                                  ? "#dc2626"
                                                  : e.impact === "green"
                                                    ? "#10b981"
                                                    : "#6b7280";
                                              return (
                                                <div
                                                  key={ei}
                                                  style={{
                                                    display: "flex",
                                                    gap: 10,
                                                    padding: "4px 8px",
                                                    background:
                                                      ei % 2 === 0
                                                        ? dark
                                                          ? "#0a0f1c"
                                                          : "#fafafa"
                                                        : "transparent",
                                                    borderRadius: 4,
                                                    fontSize: 9,
                                                    alignItems: "center",
                                                    marginBottom: 2,
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      color: th.muted,
                                                      fontFamily: "monospace",
                                                      minWidth: 90,
                                                      fontSize: 8,
                                                    }}
                                                  >
                                                    {e.date}
                                                  </span>
                                                  <span
                                                    style={{
                                                      display: "inline-block",
                                                      width: 8,
                                                      height: 8,
                                                      borderRadius: "50%",
                                                      background: c,
                                                      flexShrink: 0,
                                                    }}
                                                  />
                                                  <span
                                                    style={{
                                                      color: th.text,
                                                      flex: 1,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {e.event}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {p.remandVenue && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              padding: "8px 11px",
                                              background: "#f59e0b15",
                                              borderLeft: "2px solid #f59e0b",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#f59e0b",
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 3,
                                              }}
                                            >
                                              REMAND VENUE
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 10,
                                                color: th.text,
                                                fontWeight: 600,
                                                marginBottom: 3,
                                              }}
                                            >
                                              {p.remandVenue}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.muted,
                                                lineHeight: 1.55,
                                              }}
                                            >
                                              {p.remandImplication}
                                            </div>
                                          </div>
                                        )}
                                        {p.timeline && (
                                          <div style={{ marginTop: 8 }}>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: th.muted,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              📅 TIMELINE
                                            </div>
                                            {p.timeline.map((e, ei) => {
                                              const c =
                                                e.impact === "red"
                                                  ? "#dc2626"
                                                  : e.impact === "green"
                                                    ? "#10b981"
                                                    : "#6b7280";
                                              return (
                                                <div
                                                  key={ei}
                                                  style={{
                                                    display: "flex",
                                                    gap: 10,
                                                    padding: "4px 8px",
                                                    fontSize: 9,
                                                    alignItems: "center",
                                                    marginBottom: 2,
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      color: th.muted,
                                                      fontFamily: "monospace",
                                                      minWidth: 90,
                                                      fontSize: 8,
                                                    }}
                                                  >
                                                    {e.date}
                                                  </span>
                                                  <span
                                                    style={{
                                                      display: "inline-block",
                                                      width: 8,
                                                      height: 8,
                                                      borderRadius: "50%",
                                                      background: c,
                                                      flexShrink: 0,
                                                    }}
                                                  />
                                                  <span
                                                    style={{
                                                      color: th.text,
                                                      flex: 1,
                                                      lineHeight: 1.5,
                                                    }}
                                                  >
                                                    {e.event}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {p.consequence && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              padding: "8px 11px",
                                              background: "#dc262615",
                                              borderLeft: "2px solid #dc2626",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#dc2626",
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 3,
                                              }}
                                            >
                                              STRATEGIC CONSEQUENCE
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {p.consequence}
                                            </div>
                                          </div>
                                        )}
                                        {p.constraints && (
                                          <div style={{ marginTop: 8 }}>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: th.muted,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              📜 ACTIVE CONSTRAINTS
                                            </div>
                                            {p.constraints.map((c, ci) => (
                                              <div
                                                key={ci}
                                                style={{
                                                  fontSize: 9,
                                                  padding: "4px 9px",
                                                  background: "#3b82f608",
                                                  borderLeft:
                                                    "2px solid #3b82f655",
                                                  borderRadius: "0 4px 4px 0",
                                                  marginBottom: 3,
                                                  color: th.text,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                · {c}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {p.schedule && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              padding: "10px 13px",
                                              background: "#ef444415",
                                              borderLeft: "3px solid #ef4444",
                                              borderRadius: "0 6px 6px 0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#ef4444",
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              📅 TODAY · LIVE TRIAL
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                                marginBottom: 3,
                                              }}
                                            >
                                              <strong>Sessions:</strong>{" "}
                                              {p.schedule.today}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                                marginBottom: 3,
                                              }}
                                            >
                                              <strong>Pending:</strong>{" "}
                                              {p.schedule.pending}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                              }}
                                            >
                                              <strong>Next witness:</strong>{" "}
                                              {p.schedule.nextWitness}
                                            </div>
                                          </div>
                                        )}
                                        {p.flags && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              display: "flex",
                                              gap: 5,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {p.flags.map((f, fi) => (
                                              <span
                                                key={fi}
                                                style={{
                                                  fontSize: 8,
                                                  fontFamily: "monospace",
                                                  color: "#f59e0b",
                                                  background: "#f59e0b15",
                                                  border: "1px solid #f59e0b55",
                                                  padding: "3px 8px",
                                                  borderRadius: 5,
                                                }}
                                              >
                                                ⚠ {f}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {p.contradictions && (
                                          <div style={{ marginTop: 8 }}>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#dc2626",
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              ⚠ DOCUMENTED CONTRADICTIONS
                                            </div>
                                            {p.contradictions.map((c, ci) => (
                                              <div
                                                key={ci}
                                                style={{
                                                  fontSize: 9,
                                                  padding: "4px 9px",
                                                  background: "#dc262608",
                                                  borderLeft:
                                                    "2px solid #dc262655",
                                                  borderRadius: "0 4px 4px 0",
                                                  marginBottom: 3,
                                                  color: th.text,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                · {c}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {p.jurisdictionStatus && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              padding: "8px 11px",
                                              background: "#a78bfa15",
                                              borderLeft: "2px solid #a78bfa",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: "#a78bfa",
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 3,
                                              }}
                                            >
                                              JURISDICTION STATUS
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 9,
                                                color: th.text,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {p.jurisdictionStatus}
                                            </div>
                                          </div>
                                        )}
                                        {p.significance && (
                                          <div style={{ marginTop: 8 }}>
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: th.muted,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                                marginBottom: 5,
                                              }}
                                            >
                                              SIGNIFICANCE
                                            </div>
                                            {p.significance.map((c, ci) => (
                                              <div
                                                key={ci}
                                                style={{
                                                  fontSize: 9,
                                                  padding: "4px 9px",
                                                  background: dark
                                                    ? "#0f172a"
                                                    : "#f8fafc",
                                                  borderLeft: `2px solid ${th.border}`,
                                                  borderRadius: "0 4px 4px 0",
                                                  marginBottom: 3,
                                                  color: th.text,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                · {c}
                                              </div>
                                            ))}
                                            {p.authNote && (
                                              <div
                                                style={{
                                                  marginTop: 5,
                                                  padding: "6px 10px",
                                                  background:
                                                    p.authStatus === "green"
                                                      ? "#10b98115"
                                                      : "#f59e0b15",
                                                  borderRadius: 5,
                                                  fontSize: 9,
                                                  color: th.text,
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    color:
                                                      p.authStatus === "green"
                                                        ? "#10b981"
                                                        : "#f59e0b",
                                                    fontWeight: 700,
                                                    marginRight: 5,
                                                  }}
                                                >
                                                  {p.authStatus === "green"
                                                    ? "✓"
                                                    : "⚠"}
                                                </span>
                                                {p.authNote}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {p.details && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.55,
                                              marginBottom: 6,
                                              marginTop: 6,
                                            }}
                                          >
                                            {p.details}
                                          </div>
                                        )}
                                        {p.antitrust && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.55,
                                              marginTop: 5,
                                              padding: "6px 10px",
                                              background: "#3b82f608",
                                              borderLeft: "2px solid #3b82f655",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "#3b82f6",
                                                fontWeight: 700,
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                letterSpacing: 0.8,
                                                display: "block",
                                                marginBottom: 2,
                                              }}
                                            >
                                              ANTITRUST EXPOSURE
                                            </span>
                                            {p.antitrust}
                                          </div>
                                        )}
                                        {p.settlement && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.text,
                                              lineHeight: 1.55,
                                              marginTop: 5,
                                              padding: "6px 10px",
                                              background: "#10b98108",
                                              borderLeft: "2px solid #10b98155",
                                              borderRadius: "0 4px 4px 0",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "#10b981",
                                                fontWeight: 700,
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                letterSpacing: 0.8,
                                                display: "block",
                                                marginBottom: 2,
                                              }}
                                            >
                                              SETTLEMENT POSTURE
                                            </span>
                                            {p.settlement}
                                          </div>
                                        )}
                                        {p.counsel && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.muted,
                                              lineHeight: 1.55,
                                              marginTop: 5,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            {p.counsel}
                                          </div>
                                        )}
                                      </div>
                                    ))}

                                  {s.analysis && (
                                    <div
                                      style={{
                                        marginTop: 12,
                                        padding: "12px 14px",
                                        background: dark
                                          ? "#1e1b4b"
                                          : "#f5f3ff",
                                        borderLeft: "3px solid #a78bfa",
                                        borderRadius: "0 6px 6px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "monospace",
                                          color: "#a78bfa",
                                          fontWeight: 700,
                                          letterSpacing: 1.2,
                                          marginBottom: 5,
                                        }}
                                      >
                                        📊 GEOGRAPHIC ANALYSIS
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: th.text,
                                          lineHeight: 1.7,
                                        }}
                                      >
                                        {s.analysis}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })()}
            {tab === "deps" &&
              (() => {
                const deps = DEPOSITION_DATA[caseId] || [];
                const outstanding = OUTSTANDING_DEPOSITIONS[caseId] || [];
                const STATUS_STAGES = [
                  { k: "raw", l: "Raw Transcript", emoji: "📄" },
                  { k: "quotes", l: "Quotes Used", emoji: "💬" },
                  { k: "admissions", l: "Key Admissions", emoji: "⚡" },
                ];
                const stageIdx = {
                  raw: 0,
                  quotes: 1,
                  excerpts: 1,
                  admissions: 2,
                  impeachment: 2,
                  designated: 2,
                };
                const filtered = deps.filter((d) => {
                  if (depSearch) {
                    const q = depSearch.toLowerCase();
                    const hay = (
                      d.witness +
                      " " +
                      d.role +
                      " " +
                      (d.keyAdmissions || [])
                        .map((a) => a.quote + a.why)
                        .join(" ")
                    ).toLowerCase();
                    if (!hay.includes(q)) return false;
                  }
                  if (depStatusFilter !== "all" && d.status !== depStatusFilter)
                    return false;
                  if (
                    depPartyFilter !== "all" &&
                    d.party.toLowerCase() !== depPartyFilter
                  )
                    return false;
                  return true;
                });
                const counts = {
                  total: deps.length,
                  designated: deps.filter(
                    (d) =>
                      d.status === "admissions" ||
                      d.status === "designated" ||
                      d.status === "impeachment",
                  ).length,
                  withImp: deps.filter(
                    (d) => d.bankLinks && d.bankLinks.length > 0,
                  ).length,
                  outstanding: outstanding.length,
                  critical: outstanding.filter((o) => o.urgency === "critical")
                    .length,
                };
                const URG_C = {
                  critical: "#ef4444",
                  high: "#f59e0b",
                  medium: "#3b82f6",
                };
                const goToBank = (id) => {
                  setTab("impeach");
                  toast(`Opening Impeachment Bank · ${id}`, "success");
                };
                const goToWar = () => {
                  setTab("warroom");
                  toast("Opening War Room", "success");
                };
                const caseTitle = isIle
                  ? caseId === "chid"
                    ? "USA v. Chidambaran"
                    : "USA v. Naqvi"
                  : caseId === "juul"
                    ? "Seliavski v. JUUL MDL"
                    : caseId === "musk"
                      ? "Musk v. Altman"
                      : "Neurocentria v. Mando";
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#3b82f6",
                              background: "#3b82f618",
                              border: "1px solid #3b82f633",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            📋 DEPOSITIONS
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            The connective tissue of the trial graph
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Deposition Tracker
                        </h1>
                        <div
                          style={{
                            fontSize: 10,
                            color: th.muted,
                            fontFamily: "monospace",
                          }}
                        >
                          <span style={{ color: th.text, fontWeight: 700 }}>
                            {counts.total} taken
                          </span>{" "}
                          ·{" "}
                          <span style={{ color: "#10b981" }}>
                            {counts.designated} key admissions
                          </span>{" "}
                          ·{" "}
                          <span style={{ color: "#a78bfa" }}>
                            {counts.withImp} with impeachment
                          </span>
                          {counts.outstanding > 0 && (
                            <>
                              {" "}
                              ·{" "}
                              <span
                                style={{
                                  color:
                                    counts.critical > 0 ? "#ef4444" : "#f59e0b",
                                  fontWeight: 700,
                                }}
                              >
                                {counts.outstanding} OUTSTANDING
                                {counts.critical > 0 &&
                                  ` · ${counts.critical} CRITICAL`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background:
                          depPartyFilter === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : depPartyFilter === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${depPartyFilter === "plaintiff" ? "#dc262655" : depPartyFilter === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: depPartyFilter === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: depPartyFilter === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {depPartyFilter === "plaintiff"
                            ? "🔴 Plaintiff Witness Depositions"
                            : depPartyFilter === "defendant"
                              ? "🔵 Defendant Witness Depositions"
                              : "All Depositions"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              depPartyFilter === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {depPartyFilter === "plaintiff"
                            ? "Defense view: cross-exam targets · prep your witnesses for these attacks"
                            : depPartyFilter === "defendant"
                              ? "Plaintiff view: cross-exam targets · key admissions to mine"
                              : "Filter to see how each side's counsel sees the deposition record"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setDepPartyFilter(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: depPartyFilter === v ? 700 : 500,
                              background:
                                depPartyFilter === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                depPartyFilter === "all"
                                  ? depPartyFilter === v
                                    ? th.text
                                    : th.muted
                                  : depPartyFilter === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                depPartyFilter === v
                                  ? depPartyFilter === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${depPartyFilter === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* OUTSTANDING DEPOSITIONS - filtered by perspective */}
                    {(() => {
                      const filteredOutstanding = outstanding.filter(
                        (o) =>
                          depPartyFilter === "all" ||
                          (o.party && o.party.toLowerCase() === depPartyFilter),
                      );
                      const fCritical = filteredOutstanding.filter(
                        (o) => o.urgency === "critical",
                      ).length;
                      return filteredOutstanding.length > 0 ? (
                        <div
                          style={{
                            background:
                              fCritical > 0
                                ? dark
                                  ? "#1f0a0a"
                                  : "#fef2f2"
                                : dark
                                  ? "#1a1410"
                                  : "#fffbeb",
                            border: `2px solid ${fCritical > 0 ? "#ef4444" : "#f59e0b"}66`,
                            borderRadius: 10,
                            padding: "14px 16px",
                            marginBottom: 14,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 10,
                            }}
                          >
                            <span style={{ fontSize: 14 }}>
                              {fCritical > 0 ? "🚨" : "⚠️"}
                            </span>
                            <div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: fCritical > 0 ? "#ef4444" : "#f59e0b",
                                  letterSpacing: "-0.1px",
                                }}
                              >
                                Outstanding Depositions (
                                {filteredOutstanding.length})
                                {depPartyFilter !== "all" && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      fontWeight: 500,
                                      marginLeft: 8,
                                    }}
                                  >
                                    ·{" "}
                                    {depPartyFilter === "plaintiff"
                                      ? "plaintiff witnesses"
                                      : "defendant witnesses"}{" "}
                                    only
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 9, color: th.muted }}>
                                Discovery cutoff intelligence · lead-time aware
                                {depPartyFilter === "plaintiff"
                                  ? " · plaintiff witnesses defense will take"
                                  : depPartyFilter === "defendant"
                                    ? " · defendant witnesses plaintiff will take"
                                    : ""}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {filteredOutstanding.map((o, i) => {
                              const c = URG_C[o.urgency];
                              const partyC =
                                o.party === "Plaintiff"
                                  ? "#ef4444"
                                  : o.party === "Defendant"
                                    ? "#3b82f6"
                                    : "#a78bfa";
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: "12px 14px",
                                    background: th.card,
                                    borderLeft: `4px solid ${c}`,
                                    borderRadius: "0 7px 7px 0",
                                    border: `1px solid ${th.border}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 10,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          flexWrap: "wrap",
                                          marginBottom: 3,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: th.text,
                                          }}
                                        >
                                          {o.witness}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 7px",
                                            background: c + "22",
                                            color: c,
                                            border: `1px solid ${c}55`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {o.urgency}
                                        </span>
                                        {o.party && (
                                          <span
                                            style={{
                                              fontSize: 7,
                                              padding: "2px 7px",
                                              background: partyC + "18",
                                              color: partyC,
                                              border: `1px solid ${partyC}44`,
                                              borderRadius: 8,
                                              fontWeight: 700,
                                              fontFamily: "monospace",
                                              letterSpacing: 0.4,
                                            }}
                                          >
                                            {o.party === "Plaintiff"
                                              ? "🔴 "
                                              : "🔵 "}
                                            {o.party.toUpperCase()} WITNESS
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          fontStyle: "italic",
                                          marginBottom: 5,
                                        }}
                                      >
                                        {o.role}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        textAlign: "right",
                                        flexShrink: 0,
                                        padding: "6px 10px",
                                        background: c + "15",
                                        borderRadius: 6,
                                        border: `1px solid ${c}44`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 18,
                                          fontWeight: 800,
                                          fontFamily: "monospace",
                                          color: c,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {o.daysToDeadline}d
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: th.faint,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.5,
                                          marginTop: 2,
                                        }}
                                      >
                                        {o.deadlineEvent.toUpperCase()}
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: th.dim,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        fontSize: 7,
                                        letterSpacing: 0.5,
                                        textTransform: "uppercase",
                                        display: "block",
                                        marginBottom: 3,
                                      }}
                                    >
                                      Why it matters
                                    </span>
                                    {o.why}
                                  </div>
                                  <div
                                    style={{
                                      padding: "8px 10px",
                                      background: c + "10",
                                      borderLeft: `2px solid ${c}`,
                                      borderRadius: "0 4px 4px 0",
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.55,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: c,
                                        fontWeight: 700,
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.5,
                                        display: "block",
                                        marginBottom: 3,
                                      }}
                                    >
                                      ⏱ COMPLICATIONS / LEAD TIME
                                    </span>
                                    {o.complications}
                                  </div>
                                  <div
                                    style={{
                                      padding: "7px 10px",
                                      background: dark ? "#0f172a" : "#fafafa",
                                      border: `1px dashed ${c}66`,
                                      borderRadius: 5,
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: c,
                                        fontWeight: 700,
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        letterSpacing: 0.5,
                                        marginRight: 6,
                                      }}
                                    >
                                      → ACTION
                                    </span>
                                    {o.action}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : outstanding.length > 0 && depPartyFilter !== "all" ? (
                        <div
                          style={{
                            padding: "14px 16px",
                            marginBottom: 14,
                            background: th.card,
                            border: `1px dashed ${th.border}`,
                            borderRadius: 10,
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              color: th.faint,
                              marginBottom: 4,
                            }}
                          >
                            No outstanding{" "}
                            {depPartyFilter === "plaintiff"
                              ? "plaintiff"
                              : "defendant"}{" "}
                            witnesses
                          </div>
                          <div style={{ fontSize: 8, color: th.muted }}>
                            {outstanding.length} total outstanding · toggle
                            perspective to "All" to see them
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* FILTERS */}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 8,
                        border: `1px solid ${th.border}`,
                        padding: "10px 12px",
                        marginBottom: 12,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 14, color: th.faint }}>
                          🔍
                        </span>
                        <input
                          value={depSearch}
                          onChange={(e) => setDepSearch(e.target.value)}
                          placeholder="Search witness, role, admission text…"
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 11,
                            background: th.bg,
                            border: `1px solid ${th.border}`,
                            borderRadius: 5,
                            color: th.text,
                            outline: "none",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            fontFamily: "monospace",
                          }}
                        >
                          STATUS:
                        </span>
                        <div style={{ display: "flex", gap: 3 }}>
                          {[
                            ["all", "All"],
                            ...STATUS_STAGES.map((s) => [
                              s.k,
                              s.emoji + " " + s.l,
                            ]),
                          ].map(([v, l]) => (
                            <button
                              key={v}
                              onClick={() => setDepStatusFilter(v)}
                              style={{
                                padding: "3px 8px",
                                fontSize: 8,
                                background:
                                  depStatusFilter === v
                                    ? "#3b82f6"
                                    : "transparent",
                                color:
                                  depStatusFilter === v ? "#fff" : th.muted,
                                border: `1px solid ${depStatusFilter === v ? "#3b82f6" : th.border}`,
                                borderRadius: 4,
                                cursor: "pointer",
                                fontWeight: depStatusFilter === v ? 700 : 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* DEPOSITION CARDS */}
                    {filtered.length === 0 ? (
                      <div
                        style={{
                          padding: "40px 20px",
                          textAlign: "center",
                          color: th.faint,
                          background: th.card,
                          border: `1px solid ${th.border}`,
                          borderRadius: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 30,
                            opacity: 0.4,
                            marginBottom: 8,
                          }}
                        >
                          📋
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: th.muted,
                          }}
                        >
                          No depositions match
                        </div>
                      </div>
                    ) : (
                      filtered.map((d) => {
                        const stage = stageIdx[d.status];
                        const isExp = depExpanded[d.id];
                        const partyC =
                          d.party === "Plaintiff"
                            ? "#ef4444"
                            : d.party === "Defendant"
                              ? "#3b82f6"
                              : "#a78bfa";
                        return (
                          <div
                            key={d.id}
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${th.border}`,
                              borderLeft: `4px solid ${partyC}`,
                              padding: "14px 16px",
                              marginBottom: 10,
                            }}
                          >
                            {/* Card header */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 10,
                                marginBottom: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    flexWrap: "wrap",
                                    marginBottom: 4,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      color: th.text,
                                    }}
                                  >
                                    {d.witness}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 7,
                                      padding: "2px 7px",
                                      background: partyC + "18",
                                      color: partyC,
                                      border: `1px solid ${partyC}44`,
                                      borderRadius: 8,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    {d.party.toUpperCase()} WITNESS
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    fontFamily: "system-ui",
                                    marginBottom: 4,
                                  }}
                                >
                                  {d.role}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 14,
                                    fontSize: 9,
                                    color: th.muted,
                                    flexWrap: "wrap",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  <span>📅 {d.date}</span>
                                  <span>⏱ {d.duration}</span>
                                  <span>📄 {d.pages}p</span>
                                  <span>↗ Taken by {d.takenBy}</span>
                                </div>
                              </div>
                            </div>

                            {/* Status progress */}
                            <div
                              style={{
                                padding: "10px 12px",
                                background: dark ? "#0f172a" : "#f8fafc",
                                borderRadius: 7,
                                marginBottom: isExp ? 12 : 0,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  fontFamily: "monospace",
                                  marginBottom: 7,
                                }}
                              >
                                STATUS PIPELINE
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0,
                                }}
                              >
                                {STATUS_STAGES.map((s, i) => {
                                  const done = i <= stage;
                                  const isCurrent = i === stage;
                                  return (
                                    <React.Fragment key={s.k}>
                                      <div
                                        style={{
                                          flex: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: "50%",
                                            background: done
                                              ? "#10b981"
                                              : th.border,
                                            color: done ? "#fff" : th.faint,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            boxShadow: isCurrent
                                              ? "0 0 0 3px #10b98144"
                                              : "none",
                                            border: isCurrent
                                              ? "2px solid #10b981"
                                              : "none",
                                          }}
                                        >
                                          {done ? "✓" : i + 1}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 7,
                                            fontWeight: 700,
                                            color: done ? "#10b981" : th.faint,
                                            letterSpacing: 0.3,
                                            fontFamily: "monospace",
                                            marginTop: 5,
                                            textAlign: "center",
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {s.l.toUpperCase()}
                                        </div>
                                      </div>
                                      {i < STATUS_STAGES.length - 1 && (
                                        <div
                                          style={{
                                            flex: 0.4,
                                            height: 2,
                                            background:
                                              i < stage ? "#10b981" : th.border,
                                            marginBottom: 18,
                                          }}
                                        />
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>

                            <button
                              onClick={() =>
                                setDepExpanded((p) => ({
                                  ...p,
                                  [d.id]: !p[d.id],
                                }))
                              }
                              style={{
                                marginTop: isExp ? 0 : 10,
                                width: "100%",
                                padding: "7px",
                                fontSize: 9,
                                fontWeight: 600,
                                background: isExp ? "#3b82f618" : th.bg,
                                color: isExp ? "#3b82f6" : th.text,
                                border: `1px solid ${isExp ? "#3b82f644" : th.border}`,
                                borderRadius: 5,
                                cursor: "pointer",
                              }}
                            >
                              {isExp
                                ? "▾ Hide details"
                                : "▸ Show key admissions, designations, links"}
                            </button>

                            {/* Expanded sections */}
                            {isExp && (
                              <div
                                style={{
                                  marginTop: 12,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 10,
                                  animation: "fadeIn 0.3s ease",
                                }}
                              >
                                {/* Section A - Key Admissions */}
                                <div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      color: "#f59e0b",
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 6,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    ⚡ Key Admissions ({d.keyAdmissions.length})
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 5,
                                    }}
                                  >
                                    {d.keyAdmissions.map((a, i) => {
                                      const sc =
                                        a.supports === "Plaintiff"
                                          ? "#ef4444"
                                          : a.supports === "Defendant"
                                            ? "#3b82f6"
                                            : "#a78bfa";
                                      return (
                                        <div
                                          key={i}
                                          style={{
                                            padding: "10px 12px",
                                            background: th.bg,
                                            borderLeft: `3px solid ${sc}`,
                                            borderRadius: "0 6px 6px 0",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "baseline",
                                              gap: 8,
                                              marginBottom: 4,
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: 8,
                                                fontFamily: "monospace",
                                                color: sc,
                                                fontWeight: 700,
                                                letterSpacing: 0.4,
                                              }}
                                            >
                                              {a.cite}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 7,
                                                padding: "1px 6px",
                                                background: sc + "18",
                                                color: sc,
                                                border: `1px solid ${sc}44`,
                                                borderRadius: 8,
                                                fontWeight: 700,
                                                fontFamily: "monospace",
                                                letterSpacing: 0.4,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              SUPPORTS{" "}
                                              {a.supports.toUpperCase()}
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 10,
                                              color: th.text,
                                              fontStyle: "italic",
                                              lineHeight: 1.5,
                                              marginBottom: 4,
                                            }}
                                          >
                                            "{a.quote}"
                                          </div>
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: th.muted,
                                              lineHeight: 1.5,
                                              marginBottom: 3,
                                            }}
                                          >
                                            {a.why}
                                          </div>
                                          {a.linkTheory && (
                                            <div
                                              style={{
                                                fontSize: 7,
                                                fontFamily: "monospace",
                                                color: th.faint,
                                                letterSpacing: 0.3,
                                              }}
                                            >
                                              ↳ theory:{" "}
                                              <span
                                                style={{
                                                  color: sc,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {a.linkTheory}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Section B - Designation Tool */}
                                <div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      color: "#a78bfa",
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 6,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    📑 Deposition Designation
                                  </div>
                                  <div
                                    style={{
                                      padding: "11px 13px",
                                      background: th.bg,
                                      borderRadius: 6,
                                      border: `1px solid ${th.border}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: th.text,
                                        }}
                                      >
                                        {d.designation.useCase}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: d.designation.complete
                                            ? "#10b98118"
                                            : "#f59e0b18",
                                          color: d.designation.complete
                                            ? "#10b981"
                                            : "#f59e0b",
                                          border: `1px solid ${d.designation.complete ? "#10b98144" : "#f59e0b44"}`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {d.designation.complete
                                          ? "✓ FINAL"
                                          : "⏳ IN PROGRESS"}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 8,
                                        marginBottom: 8,
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "8px 10px",
                                          background: dark
                                            ? "#1c0a0a"
                                            : "#fef2f2",
                                          borderRadius: 5,
                                          border: "1px solid #ef444433",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 4,
                                          }}
                                        >
                                          🔴 PLAINTIFF DESIGNATIONS
                                        </div>
                                        {d.designation.plaintiffRanges.length >
                                        0 ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 4,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {d.designation.plaintiffRanges.map(
                                              (r, i) => (
                                                <span
                                                  key={i}
                                                  style={{
                                                    fontSize: 9,
                                                    fontFamily: "monospace",
                                                    color: "#ef4444",
                                                    fontWeight: 700,
                                                    padding: "2px 6px",
                                                    background: "#ef444415",
                                                    borderRadius: 4,
                                                  }}
                                                >
                                                  p.{r[0]}-{r[1]}
                                                </span>
                                              ),
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: 8,
                                              color: th.faint,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            None designated
                                          </div>
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          padding: "8px 10px",
                                          background: dark
                                            ? "#0a141c"
                                            : "#eff6ff",
                                          borderRadius: 5,
                                          border: "1px solid #3b82f633",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 7,
                                            color: "#3b82f6",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "monospace",
                                            marginBottom: 4,
                                          }}
                                        >
                                          🔵 DEFENDANT DESIGNATIONS
                                        </div>
                                        {d.designation.defendantRanges.length >
                                        0 ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 4,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {d.designation.defendantRanges.map(
                                              (r, i) => (
                                                <span
                                                  key={i}
                                                  style={{
                                                    fontSize: 9,
                                                    fontFamily: "monospace",
                                                    color: "#3b82f6",
                                                    fontWeight: 700,
                                                    padding: "2px 6px",
                                                    background: "#3b82f615",
                                                    borderRadius: 4,
                                                  }}
                                                >
                                                  p.{r[0]}-{r[1]}
                                                </span>
                                              ),
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: 8,
                                              color: th.faint,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            None designated
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: "7px 10px",
                                        background: "#a78bfa10",
                                        borderLeft: "2px solid #a78bfa",
                                        borderRadius: "0 4px 4px 0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 7,
                                          color: "#a78bfa",
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "monospace",
                                          marginBottom: 3,
                                        }}
                                      >
                                        ⚖️ FRCP 32(a)(6) STATUS
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {d.designation.rule32Status}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Section C - Cross-Exam Prep Integration */}
                                <div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      color: "#10b981",
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      marginBottom: 6,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    🔗 Trial Prep Integration
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "10px 12px",
                                        background: th.bg,
                                        borderRadius: 6,
                                        border: `1px solid ${th.border}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: 6,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: th.text,
                                          }}
                                        >
                                          Impeachment Bank
                                        </div>
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "1px 6px",
                                            background:
                                              d.bankLinks.length > 0
                                                ? "#ef444418"
                                                : th.surface,
                                            color:
                                              d.bankLinks.length > 0
                                                ? "#ef4444"
                                                : th.faint,
                                            border: `1px solid ${d.bankLinks.length > 0 ? "#ef444444" : th.border}`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {d.bankLinks.length} seq
                                        </span>
                                      </div>
                                      {d.bankLinks.length > 0 ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 3,
                                          }}
                                        >
                                          {d.bankLinks.map((bl, i) => (
                                            <button
                                              key={i}
                                              onClick={() => goToBank(bl)}
                                              style={{
                                                textAlign: "left",
                                                padding: "5px 8px",
                                                fontSize: 8,
                                                background: dark
                                                  ? "#0f172a"
                                                  : "#fef2f2",
                                                color: "#ef4444",
                                                border: "1px solid #ef444433",
                                                borderRadius: 4,
                                                cursor: "pointer",
                                                fontFamily: "monospace",
                                                fontWeight: 600,
                                              }}
                                            >
                                              → {bl.replace(/^[a-z]+-/, "")}
                                            </button>
                                          ))}
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.faint,
                                            fontStyle: "italic",
                                          }}
                                        >
                                          No sequences built yet
                                        </div>
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        padding: "10px 12px",
                                        background: th.bg,
                                        borderRadius: 6,
                                        border: `1px solid ${th.border}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: 6,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: th.text,
                                          }}
                                        >
                                          War Room
                                        </div>
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "1px 6px",
                                            background:
                                              d.warLinks.length > 0
                                                ? "#dc262618"
                                                : th.surface,
                                            color:
                                              d.warLinks.length > 0
                                                ? "#dc2626"
                                                : th.faint,
                                            border: `1px solid ${d.warLinks.length > 0 ? "#dc262644" : th.border}`,
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {d.warLinks.length > 0
                                            ? "connected"
                                            : "not linked"}
                                        </span>
                                      </div>
                                      {d.warLinks.length > 0 ? (
                                        <button
                                          onClick={goToWar}
                                          style={{
                                            width: "100%",
                                            padding: "6px 8px",
                                            fontSize: 9,
                                            background: "#dc262618",
                                            color: "#dc2626",
                                            border: "1px solid #dc262644",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            fontWeight: 700,
                                          }}
                                        >
                                          → Open War Room profile
                                        </button>
                                      ) : (
                                        <div
                                          style={{
                                            fontSize: 8,
                                            color: th.faint,
                                            fontStyle: "italic",
                                          }}
                                        >
                                          Witness not yet on War Room queue
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })()}
            {tab === "settleintel" &&
              (() => {
                const data = SETTLEMENT_DATA[caseId] || SETTLEMENT_DATA.juul;
                const fmt = (n) => {
                  if (!n) return "$0";
                  const a = Math.abs(n);
                  if (a >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
                  if (a >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
                  if (a >= 1e3) return "$" + (n / 1e3).toFixed(0) + "K";
                  return "$" + Math.round(n).toLocaleString();
                };
                const TYPE_C = {
                  settlement: "#10b981",
                  approval: "#a78bfa",
                  demand: "#ef4444",
                  offer: "#3b82f6",
                  docket: "#6b7280",
                  ruling: "#f59e0b",
                  movement: "#f59e0b",
                  licensing: "#10b981",
                  decline: "#a78bfa",
                  "civil enforcement": "#a78bfa",
                  restitution: "#10b981",
                };
                const sortedLedger = [
                  ...data.ledger,
                  ...(settleNewEntries[caseId] || []),
                ].sort((a, b) => new Date(a.ts) - new Date(b.ts));
                const compFiltered = data.comparables
                  .filter((c) => {
                    if (
                      settleCompTrack !== "all" &&
                      c.track !== settleCompTrack
                    )
                      return false;
                    if (
                      settleCompSide !== "all" &&
                      c.side.toLowerCase() !== settleCompSide
                    )
                      return false;
                    if (
                      settleCompType !== "all" &&
                      c.type.toLowerCase() !== settleCompType
                    )
                      return false;
                    return true;
                  })
                  .sort((a, b) => b.analogy - a.analogy);
                const tracks = [
                  ...new Set(data.comparables.map((c) => c.track)),
                ];
                const types = [...new Set(data.comparables.map((c) => c.type))];
                const bIn = settleBatnaInputs[caseId] || {};
                const get = (side, k, def) =>
                  bIn[side]?.[k] !== undefined
                    ? bIn[side][k]
                    : data.batna[side][k + "Default"] !== undefined
                      ? data.batna[side][k + "Default"]
                      : data.batna[side][k];
                const setB = (side, k, v) =>
                  setSettleBatnaInputs((p) => ({
                    ...p,
                    [caseId]: {
                      ...(p[caseId] || {}),
                      [side]: { ...((p[caseId] || {})[side] || {}), [k]: v },
                    },
                  }));
                const pFloor =
                  get("plaintiff", "probWeighted", 0) -
                  get("plaintiff", "trialCost", 0);
                const dCeiling =
                  get("defendant", "riskAssessment", 0) +
                  (data.batna.twoFront.active
                    ? get("defendant", "trialCost", 0)
                    : 0) -
                  (data.batna.twoFront.active ? 0 : 0);
                const zoneExists = dCeiling > pFloor;
                const midpoint = zoneExists ? (pFloor + dCeiling) / 2 : 0;
                const addEntry = () => {
                  if (!settleNewEntry.label || !settleNewEntry.amount) return;
                  const ent = {
                    ...settleNewEntry,
                    ts:
                      settleNewEntry.date ||
                      new Date().toISOString().slice(0, 10),
                    amount: Number(settleNewEntry.amount),
                    date: new Date(
                      settleNewEntry.date || new Date(),
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    }),
                    custom: true,
                  };
                  setSettleNewEntries((p) => ({
                    ...p,
                    [caseId]: [...(p[caseId] || []), ent],
                  }));
                  setSettleNewEntry({
                    label: "",
                    amount: "",
                    type: "demand",
                    side: "Plaintiff",
                    date: "",
                    linkedTo: "",
                    note: "",
                  });
                  setSettleShowAdd(false);
                  toast("Entry added to ledger", "success");
                };
                const caseTitle = isIle
                  ? caseId === "chid"
                    ? "USA v. Chidambaran"
                    : "USA v. Naqvi"
                  : caseId === "juul"
                    ? "Seliavski v. JUUL MDL"
                    : caseId === "musk"
                      ? "Musk v. Altman"
                      : "Neurocentria v. Mando";
                return (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#10b981",
                              background: "#10b98118",
                              border: "1px solid #10b98133",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            🤝 SETTLEMENT INTEL
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            Negotiation modeling · case-grounded
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Settlement Intelligence
                        </h1>
                        <div style={{ fontSize: 9, color: th.muted }}>
                          {caseTitle} · what's it worth to settle today, and
                          where will it land?
                        </div>
                      </div>
                    </div>

                    {/* CONTEXT BANNER */}
                    <div
                      style={{
                        background: "#a78bfa10",
                        border: "1px solid #a78bfa44",
                        borderLeft: "3px solid #a78bfa",
                        borderRadius: 8,
                        padding: "10px 14px",
                        marginBottom: 14,
                        fontSize: 10,
                        color: th.muted,
                        lineHeight: 1.6,
                        fontStyle: "italic",
                      }}
                    >
                      {data.context}
                    </div>

                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background:
                          settlePersp === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : settlePersp === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${settlePersp === "plaintiff" ? "#dc262655" : settlePersp === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: settlePersp === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: settlePersp === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {settlePersp === "plaintiff"
                            ? "🔴 Plaintiff Counsel View - your floor, your demands, your comparable verdicts"
                            : settlePersp === "defendant"
                              ? "🔵 Defendant Counsel View - your ceiling, your offers, your comparable settlements"
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              settlePersp === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {settlePersp === "plaintiff"
                            ? "BATNA highlighted from plaintiff's vantage · ledger filtered to plaintiff entries"
                            : settlePersp === "defendant"
                              ? "BATNA highlighted from defendant's vantage · ledger filtered to defendant entries"
                              : "Toggle to filter ledger and highlight BATNA box for one side"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setSettlePersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: settlePersp === v ? 700 : 500,
                              background:
                                settlePersp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                settlePersp === "all"
                                  ? settlePersp === v
                                    ? th.text
                                    : th.muted
                                  : settlePersp === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                settlePersp === v
                                  ? settlePersp === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${settlePersp === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* SECTION 1 - BATNA CALCULATOR */}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 10,
                        border: `2px solid ${zoneExists ? "#10b98166" : "#ef444466"}`,
                        padding: "14px 16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: 12,
                          flexWrap: "wrap",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 7,
                              color: "#a78bfa",
                              fontFamily: "monospace",
                              letterSpacing: 1,
                              marginBottom: 2,
                              fontWeight: 700,
                            }}
                          >
                            SECTION 1 OF 3
                          </div>
                          <h2
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              margin: "0 0 2px",
                              letterSpacing: "-0.2px",
                            }}
                          >
                            🎯 BATNA Calculator
                          </h2>
                          <div style={{ fontSize: 9, color: th.muted }}>
                            Best Alternative to a Negotiated Agreement ·
                            settlement zone analysis
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 8,
                            padding: "4px 10px",
                            background: zoneExists ? "#10b98118" : "#ef444418",
                            color: zoneExists ? "#10b981" : "#ef4444",
                            border: `1px solid ${zoneExists ? "#10b98166" : "#ef444466"}`,
                            borderRadius: 8,
                            fontWeight: 700,
                            fontFamily: "monospace",
                            letterSpacing: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {zoneExists
                            ? "✓ ZONE EXISTS · DEAL POSSIBLE"
                            : "✗ NO ZONE · TRIAL TRACK"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 14,
                          marginBottom: 14,
                        }}
                      >
                        {/* Plaintiff inputs */}
                        <div
                          style={{
                            padding: "12px 14px",
                            background: dark ? "#0f172a" : "#fef2f2",
                            border: "1px solid #ef444433",
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: "#ef4444",
                              letterSpacing: 0.5,
                              fontFamily: "monospace",
                              marginBottom: 9,
                            }}
                          >
                            🔴 PLAINTIFF
                          </div>
                          {[
                            {
                              k: "trialCost",
                              l: "Trial cost (estimated)",
                              max: 50e6,
                              min: 0,
                              step: 100000,
                            },
                            {
                              k: "probWeighted",
                              l: "Prob-weighted trial outcome",
                              max: caseId === "musk" ? 20e9 : 100e6,
                              min: 0,
                              step: caseId === "musk" ? 100e6 : 500000,
                            },
                          ].map((s) => {
                            const v = get("plaintiff", s.k, 0);
                            return (
                              <div key={s.k} style={{ marginBottom: 10 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    marginBottom: 4,
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 9, color: th.muted }}
                                  >
                                    {s.l}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      color: "#ef4444",
                                    }}
                                  >
                                    {fmt(v)}
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={s.min}
                                  max={s.max}
                                  step={s.step}
                                  value={v}
                                  onChange={(e) =>
                                    setB("plaintiff", s.k, +e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    height: 4,
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    background: `linear-gradient(to right,#ef4444 ${((v - s.min) / (s.max - s.min)) * 100}%,${th.border} ${((v - s.min) / (s.max - s.min)) * 100}%)`,
                                    borderRadius: 2,
                                    outline: "none",
                                    cursor: "pointer",
                                    accentColor: "#ef4444",
                                  }}
                                />
                              </div>
                            );
                          })}
                          <div
                            style={{
                              padding: "8px 10px",
                              background: dark ? "#1a1a1a" : "#fff",
                              borderRadius: 5,
                              marginTop: 8,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                fontFamily: "monospace",
                                letterSpacing: 0.5,
                                marginBottom: 3,
                                fontWeight: 700,
                              }}
                            >
                              PLAINTIFF FLOOR
                            </div>
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                fontFamily: "monospace",
                                color: "#ef4444",
                              }}
                            >
                              {fmt(pFloor)}
                            </div>
                            <div
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                marginTop: 3,
                                fontStyle: "italic",
                              }}
                            >
                              = prob-weighted outcome − trial cost
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: th.muted,
                              marginTop: 7,
                              lineHeight: 1.55,
                              fontStyle: "italic",
                            }}
                          >
                            {data.batna.plaintiff.note}
                          </div>
                        </div>

                        {/* Defendant inputs */}
                        <div
                          style={{
                            padding: "12px 14px",
                            background: dark ? "#0f172a" : "#eff6ff",
                            border: "1px solid #3b82f633",
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: "#3b82f6",
                              letterSpacing: 0.5,
                              fontFamily: "monospace",
                              marginBottom: 9,
                            }}
                          >
                            🔵 DEFENDANT
                          </div>
                          {[
                            {
                              k: "trialCost",
                              l: "Trial cost (estimated)",
                              max: caseId === "juul" ? 80e6 : 50e6,
                              min: 0,
                              step: caseId === "juul" ? 500000 : 100000,
                            },
                            {
                              k: "riskAssessment",
                              l: "Risk assessment (expected loss)",
                              max: caseId === "musk" ? 20e9 : 200e6,
                              min: 0,
                              step: caseId === "musk" ? 100e6 : 1000000,
                            },
                          ].map((s) => {
                            const v = get("defendant", s.k, 0);
                            return (
                              <div key={s.k} style={{ marginBottom: 10 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    marginBottom: 4,
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 9, color: th.muted }}
                                  >
                                    {s.l}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      color: "#3b82f6",
                                    }}
                                  >
                                    {fmt(v)}
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={s.min}
                                  max={s.max}
                                  step={s.step}
                                  value={v}
                                  onChange={(e) =>
                                    setB("defendant", s.k, +e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    height: 4,
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    background: `linear-gradient(to right,#3b82f6 ${((v - s.min) / (s.max - s.min)) * 100}%,${th.border} ${((v - s.min) / (s.max - s.min)) * 100}%)`,
                                    borderRadius: 2,
                                    outline: "none",
                                    cursor: "pointer",
                                    accentColor: "#3b82f6",
                                  }}
                                />
                              </div>
                            );
                          })}
                          <div
                            style={{
                              padding: "8px 10px",
                              background: dark ? "#1a1a1a" : "#fff",
                              borderRadius: 5,
                              marginTop: 8,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                fontFamily: "monospace",
                                letterSpacing: 0.5,
                                marginBottom: 3,
                                fontWeight: 700,
                              }}
                            >
                              DEFENDANT CEILING
                            </div>
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                fontFamily: "monospace",
                                color: "#3b82f6",
                              }}
                            >
                              {fmt(dCeiling)}
                            </div>
                            <div
                              style={{
                                fontSize: 7,
                                color: th.faint,
                                marginTop: 3,
                                fontStyle: "italic",
                              }}
                            >
                              = risk + trial cost{" "}
                              {data.batna.twoFront.active
                                ? "(two-front penalty applied)"
                                : ""}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: th.muted,
                              marginTop: 7,
                              lineHeight: 1.55,
                              fontStyle: "italic",
                            }}
                          >
                            {data.batna.defendant.note}
                          </div>
                        </div>
                      </div>

                      {/* Settlement Zone Visualization */}
                      {zoneExists ? (
                        <div
                          style={{
                            padding: "14px 16px",
                            background: dark ? "#0f172a" : "#f0fdf4",
                            border: "1px solid #10b98144",
                            borderRadius: 8,
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 12,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#10b981",
                                letterSpacing: 0.5,
                                fontFamily: "monospace",
                              }}
                            >
                              SETTLEMENT ZONE
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.muted,
                                fontFamily: "monospace",
                              }}
                            >
                              midpoint{" "}
                              <span
                                style={{ fontWeight: 700, color: "#10b981" }}
                              >
                                {fmt(midpoint)}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              position: "relative",
                              height: 60,
                              marginBottom: 8,
                            }}
                          >
                            {/* track */}
                            <div
                              style={{
                                position: "absolute",
                                top: 24,
                                left: 0,
                                right: 0,
                                height: 12,
                                background: th.border,
                                borderRadius: 6,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  bottom: 0,
                                  left: `${(pFloor / dCeiling) * 70 + 5}%`,
                                  width: `${(1 - pFloor / dCeiling) * 70}%`,
                                  background:
                                    "linear-gradient(to right,#10b981,#059669)",
                                  borderRadius: 6,
                                }}
                              />
                            </div>
                            {/* plaintiff floor marker */}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: `${(pFloor / dCeiling) * 70 + 5}%`,
                                transform: "translateX(-50%)",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: "#ef4444",
                                  fontWeight: 700,
                                  marginBottom: 2,
                                }}
                              >
                                P FLOOR
                              </div>
                              <div
                                style={{
                                  width: 2,
                                  height: 24,
                                  background: "#ef4444",
                                  margin: "0 auto",
                                }}
                              />
                              <div
                                style={{
                                  fontSize: 9,
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  color: "#ef4444",
                                  marginTop: 2,
                                }}
                              >
                                {fmt(pFloor)}
                              </div>
                            </div>
                            {/* defendant ceiling marker */}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: "75%",
                                transform: "translateX(-50%)",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: "#3b82f6",
                                  fontWeight: 700,
                                  marginBottom: 2,
                                }}
                              >
                                D CEILING
                              </div>
                              <div
                                style={{
                                  width: 2,
                                  height: 24,
                                  background: "#3b82f6",
                                  margin: "0 auto",
                                }}
                              />
                              <div
                                style={{
                                  fontSize: 9,
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  color: "#3b82f6",
                                  marginTop: 2,
                                }}
                              >
                                {fmt(dCeiling)}
                              </div>
                            </div>
                            {/* midpoint marker */}
                            <div
                              style={{
                                position: "absolute",
                                top: 18,
                                left: `${((pFloor + dCeiling) / 2 / dCeiling) * 70 + 5}%`,
                                transform: "translateX(-50%)",
                              }}
                            >
                              <div
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  background: "#10b981",
                                  boxShadow:
                                    "0 0 0 3px #fff,0 0 0 4px #10b98155",
                                  border: "2px solid #fff",
                                }}
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.55,
                              marginTop: 14,
                            }}
                          >
                            {data.batna.analysis}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: "14px 16px",
                            background: dark ? "#0f172a" : "#fef2f2",
                            border: "1px solid #ef444444",
                            borderRadius: 8,
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#ef4444",
                              letterSpacing: 0.5,
                              fontFamily: "monospace",
                              marginBottom: 6,
                            }}
                          >
                            NO SETTLEMENT ZONE - TRIAL TRACK
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.55,
                            }}
                          >
                            Plaintiff floor (
                            <span
                              style={{
                                color: "#ef4444",
                                fontFamily: "monospace",
                                fontWeight: 700,
                              }}
                            >
                              {fmt(pFloor)}
                            </span>
                            ) exceeds defendant ceiling (
                            <span
                              style={{
                                color: "#3b82f6",
                                fontFamily: "monospace",
                                fontWeight: 700,
                              }}
                            >
                              {fmt(dCeiling)}
                            </span>
                            ). Either liability assessments are irreconcilable
                            or trial-cost asymmetry is locking sides into trial.
                            Recalibrate sliders or wait for next material event.
                          </div>
                        </div>
                      )}

                      {/* Two-Front War indicator */}
                      {data.batna.twoFront.active && (
                        <div
                          style={{
                            padding: "10px 12px",
                            background: "#f59e0b10",
                            border: "1px solid #f59e0b44",
                            borderLeft: "3px solid #f59e0b",
                            borderRadius: 6,
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: "#f59e0b",
                              letterSpacing: 0.5,
                              fontFamily: "monospace",
                              marginBottom: 4,
                            }}
                          >
                            ⚡ TWO-FRONT WAR PREMIUM ·{" "}
                            {fmt(data.batna.twoFront.premium)}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              lineHeight: 1.55,
                              marginBottom: 3,
                            }}
                          >
                            {data.batna.twoFront.description}
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: th.faint,
                              fontStyle: "italic",
                              lineHeight: 1.5,
                            }}
                          >
                            {data.batna.twoFront.note}
                          </div>
                        </div>
                      )}

                      {/* AI insight */}
                      <div
                        style={{
                          padding: "10px 12px",
                          background: "#a78bfa10",
                          borderLeft: "3px solid #a78bfa",
                          borderRadius: "0 6px 6px 0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 8,
                            fontWeight: 700,
                            color: "#a78bfa",
                            letterSpacing: 0.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                          }}
                        >
                          ✨ AI INSIGHT - TIMING & STRATEGY
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.text,
                            lineHeight: 1.6,
                          }}
                        >
                          {data.batna.ai}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 2 - DEMAND/OFFER LEDGER */}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 10,
                        border: `1px solid ${th.border}`,
                        padding: "14px 16px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 14,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 7,
                              color: "#10b981",
                              fontFamily: "monospace",
                              letterSpacing: 1,
                              marginBottom: 2,
                              fontWeight: 700,
                            }}
                          >
                            SECTION 2 OF 3
                          </div>
                          <h2
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              margin: "0 0 2px",
                              letterSpacing: "-0.2px",
                            }}
                          >
                            📒 Demand / Offer Ledger
                          </h2>
                          <div style={{ fontSize: 9, color: th.muted }}>
                            {sortedLedger.length} entries · linked to docket
                          </div>
                        </div>
                        <button
                          onClick={() => setSettleShowAdd(true)}
                          style={{
                            padding: "6px 13px",
                            fontSize: 10,
                            fontWeight: 700,
                            background: "#10b981",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          + Add Entry
                        </button>
                      </div>
                      <div style={{ position: "relative", paddingLeft: 24 }}>
                        <div
                          style={{
                            position: "absolute",
                            left: 6,
                            top: 6,
                            bottom: 6,
                            width: 2,
                            background: `linear-gradient(to bottom,${th.border},${th.border}88)`,
                            borderRadius: 1,
                          }}
                        />
                        {sortedLedger.map((e, i) => {
                          const c = TYPE_C[e.type] || th.muted;
                          const isAnchor = e.isAnchor;
                          const isMilestone = e.isMilestone;
                          return (
                            <div
                              key={i}
                              style={{
                                position: "relative",
                                marginBottom: 10,
                                paddingTop: 2,
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: -22,
                                  top: 6,
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  background: c,
                                  boxShadow: `0 0 0 3px ${dark ? th.bg : "#fff"},0 0 0 4px ${c}55`,
                                  border: isAnchor ? "2px solid #fff" : "none",
                                }}
                              />
                              <div
                                style={{
                                  padding: "10px 14px",
                                  background: isAnchor ? c + "10" : th.bg,
                                  borderRadius: 7,
                                  border: isAnchor
                                    ? `1px solid ${c}55`
                                    : `1px solid ${th.border}`,
                                  borderLeft: `3px solid ${c}`,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 10,
                                    marginBottom: 5,
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        flexWrap: "wrap",
                                        marginBottom: 3,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          color: c,
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {e.date}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: c + "22",
                                          color: c,
                                          border: `1px solid ${c}55`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {e.type}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          color: th.muted,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        · {e.side}
                                      </span>
                                      {isAnchor && (
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 6px",
                                            background: "#a78bfa18",
                                            color: "#a78bfa",
                                            border: "1px solid #a78bfa44",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          ⚓ ANCHOR
                                        </span>
                                      )}
                                      {isMilestone && (
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 6px",
                                            background: "#f59e0b18",
                                            color: "#f59e0b",
                                            border: "1px solid #f59e0b44",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          ● MILESTONE
                                        </span>
                                      )}
                                      {e.custom && (
                                        <span
                                          style={{
                                            fontSize: 7,
                                            padding: "2px 6px",
                                            background: "#3b82f618",
                                            color: "#3b82f6",
                                            border: "1px solid #3b82f644",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          YOU
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: th.text,
                                        marginBottom: 3,
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {e.label}
                                    </div>
                                    {e.note && (
                                      <div
                                        style={{
                                          fontSize: 9,
                                          color: th.muted,
                                          fontStyle: "italic",
                                          lineHeight: 1.5,
                                          marginBottom: 4,
                                        }}
                                      >
                                        {e.note}
                                      </div>
                                    )}
                                    {e.linkedTo && (
                                      <div
                                        style={{
                                          fontSize: 8,
                                          fontFamily: "monospace",
                                          color: th.faint,
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        ↳ {e.linkedTo}
                                      </div>
                                    )}
                                  </div>
                                  {e.amount > 0 && (
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        color: c,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {fmt(e.amount)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* SECTION 3 - COMPARABLES */}
                    <div
                      style={{
                        background: th.card,
                        borderRadius: 10,
                        border: `1px solid ${th.border}`,
                        padding: "14px 16px",
                        marginBottom: 14,
                      }}
                    >
                      <div style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: "#3b82f6",
                            fontFamily: "monospace",
                            letterSpacing: 1,
                            marginBottom: 2,
                            fontWeight: 700,
                          }}
                        >
                          SECTION 3 OF 3
                        </div>
                        <h2
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            margin: "0 0 2px",
                            letterSpacing: "-0.2px",
                          }}
                        >
                          ⚖️ Comparable Verdicts & Settlements
                        </h2>
                        <div style={{ fontSize: 9, color: th.muted }}>
                          {compFiltered.length} of {data.comparables.length}{" "}
                          comparables · AI-curated by case theory and
                          jurisdiction
                        </div>
                      </div>
                      {/* Filters */}
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                          marginBottom: 12,
                          padding: "8px 10px",
                          background: th.bg,
                          borderRadius: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 8,
                            fontWeight: 700,
                            color: th.faint,
                            letterSpacing: 0.5,
                            fontFamily: "monospace",
                          }}
                        >
                          FILTER:
                        </span>
                        <div style={{ display: "flex", gap: 3 }}>
                          <button
                            onClick={() => setSettleCompTrack("all")}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              background:
                                settleCompTrack === "all"
                                  ? "#3b82f6"
                                  : "transparent",
                              color:
                                settleCompTrack === "all" ? "#fff" : th.muted,
                              border: `1px solid ${settleCompTrack === "all" ? "#3b82f6" : th.border}`,
                              borderRadius: 4,
                              cursor: "pointer",
                              fontWeight: settleCompTrack === "all" ? 700 : 500,
                            }}
                          >
                            All tracks
                          </button>
                          {tracks.map((t) => (
                            <button
                              key={t}
                              onClick={() => setSettleCompTrack(t)}
                              style={{
                                padding: "3px 8px",
                                fontSize: 8,
                                background:
                                  settleCompTrack === t
                                    ? "#3b82f6"
                                    : "transparent",
                                color:
                                  settleCompTrack === t ? "#fff" : th.muted,
                                border: `1px solid ${settleCompTrack === t ? "#3b82f6" : th.border}`,
                                borderRadius: 4,
                                cursor: "pointer",
                                fontWeight: settleCompTrack === t ? 700 : 500,
                                textTransform: "capitalize",
                              }}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                        <span
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            fontFamily: "monospace",
                          }}
                        >
                          ·
                        </span>
                        <div style={{ display: "flex", gap: 3 }}>
                          {[
                            ["all", "All sides"],
                            ["plaintiff", "🔴 Plaintiff"],
                            ["defendant", "🔵 Defendant"],
                          ].map(([v, l]) => (
                            <button
                              key={v}
                              onClick={() => setSettleCompSide(v)}
                              style={{
                                padding: "3px 8px",
                                fontSize: 8,
                                background:
                                  settleCompSide === v
                                    ? "#10b981"
                                    : "transparent",
                                color: settleCompSide === v ? "#fff" : th.muted,
                                border: `1px solid ${settleCompSide === v ? "#10b981" : th.border}`,
                                borderRadius: 4,
                                cursor: "pointer",
                                fontWeight: settleCompSide === v ? 700 : 500,
                              }}
                            >
                              {l}
                            </button>
                          ))}
                        </div>
                        <span
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            fontFamily: "monospace",
                          }}
                        >
                          ·
                        </span>
                        <div style={{ display: "flex", gap: 3 }}>
                          <button
                            onClick={() => setSettleCompType("all")}
                            style={{
                              padding: "3px 8px",
                              fontSize: 8,
                              background:
                                settleCompType === "all"
                                  ? "#a78bfa"
                                  : "transparent",
                              color:
                                settleCompType === "all" ? "#fff" : th.muted,
                              border: `1px solid ${settleCompType === "all" ? "#a78bfa" : th.border}`,
                              borderRadius: 4,
                              cursor: "pointer",
                              fontWeight: settleCompType === "all" ? 700 : 500,
                            }}
                          >
                            All types
                          </button>
                          {types.map((t) => (
                            <button
                              key={t}
                              onClick={() => setSettleCompType(t)}
                              style={{
                                padding: "3px 8px",
                                fontSize: 8,
                                background:
                                  settleCompType === t
                                    ? "#a78bfa"
                                    : "transparent",
                                color: settleCompType === t ? "#fff" : th.muted,
                                border: `1px solid ${settleCompType === t ? "#a78bfa" : th.border}`,
                                borderRadius: 4,
                                cursor: "pointer",
                                fontWeight: settleCompType === t ? 700 : 500,
                                textTransform: "capitalize",
                              }}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      {compFiltered.length === 0 ? (
                        <div
                          style={{
                            padding: "24px",
                            textAlign: "center",
                            color: th.faint,
                            fontSize: 10,
                          }}
                        >
                          No comparables match · adjust filters
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 7,
                          }}
                        >
                          {compFiltered.map((c) => {
                            const sc =
                              c.side === "Plaintiff" ? "#ef4444" : "#3b82f6";
                            return (
                              <div
                                key={c.id}
                                style={{
                                  padding: "11px 13px",
                                  background: th.bg,
                                  borderRadius: 7,
                                  borderLeft: `3px solid ${sc}`,
                                  border: `1px solid ${th.border}`,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 10,
                                    marginBottom: 6,
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                        flexWrap: "wrap",
                                        marginBottom: 3,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: sc + "18",
                                          color: sc,
                                          border: `1px solid ${sc}44`,
                                          borderRadius: 8,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {c.side.toUpperCase()}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: th.surface,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontWeight: 600,
                                          fontFamily: "monospace",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {c.type}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 7,
                                          padding: "2px 7px",
                                          background: th.surface,
                                          color: th.muted,
                                          border: `1px solid ${th.border}`,
                                          borderRadius: 8,
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {c.year}
                                      </span>
                                      <span
                                        style={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "center",
                                          marginLeft: "auto",
                                        }}
                                      >
                                        {Array.from({ length: 10 }).map(
                                          (_, i) => (
                                            <span
                                              key={i}
                                              style={{
                                                width: 5,
                                                height: 8,
                                                background:
                                                  i < c.analogy
                                                    ? c.analogy >= 8
                                                      ? "#10b981"
                                                      : c.analogy >= 5
                                                        ? "#f59e0b"
                                                        : "#ef4444"
                                                    : th.border,
                                                borderRadius: 1,
                                              }}
                                            />
                                          ),
                                        )}
                                        <span
                                          style={{
                                            fontSize: 7,
                                            color: th.faint,
                                            marginLeft: 4,
                                            fontFamily: "monospace",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {c.analogy}/10
                                        </span>
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: th.text,
                                        marginBottom: 2,
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {c.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 8,
                                        color: th.muted,
                                        fontFamily: "monospace",
                                        marginBottom: 4,
                                      }}
                                    >
                                      {c.court} · {c.theory}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: th.muted,
                                        marginBottom: 5,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: th.dim,
                                          fontWeight: 700,
                                          fontFamily: "monospace",
                                          fontSize: 7,
                                          letterSpacing: 0.5,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        OUTCOME:{" "}
                                      </span>
                                      {c.outcome}
                                    </div>
                                  </div>
                                  {c.amount > 0 && (
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        color: sc,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {fmt(c.amount)}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.text,
                                    padding: "7px 10px",
                                    background: dark ? "#0f172a" : "#fafaf8",
                                    borderLeft: `2px solid ${sc}`,
                                    borderRadius: "0 4px 4px 0",
                                    lineHeight: 1.55,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: sc,
                                      fontWeight: 700,
                                      fontSize: 7,
                                      letterSpacing: 0.5,
                                      fontFamily: "monospace",
                                      display: "block",
                                      marginBottom: 3,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    AI ANALYSIS - supports{" "}
                                    {c.side.toUpperCase()}
                                  </span>
                                  {c.ai}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Add Entry Modal */}
                    {settleShowAdd && (
                      <div
                        style={{
                          position: "fixed",
                          inset: 0,
                          background: "rgba(0,0,0,.65)",
                          zIndex: 500,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => setSettleShowAdd(false)}
                      >
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: th.surface,
                            border: `1px solid ${th.border}`,
                            borderRadius: 12,
                            padding: "24px 28px",
                            width: 480,
                            boxShadow: "0 16px 48px rgba(0,0,0,.4)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              marginBottom: 14,
                            }}
                          >
                            + Add Ledger Entry
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                              marginBottom: 10,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.dim,
                                  marginBottom: 4,
                                  fontWeight: 600,
                                }}
                              >
                                TYPE
                              </div>
                              <select
                                value={settleNewEntry.type}
                                onChange={(e) =>
                                  setSettleNewEntry((p) => ({
                                    ...p,
                                    type: e.target.value,
                                  }))
                                }
                                style={{
                                  width: "100%",
                                  padding: "7px 9px",
                                  fontSize: 10,
                                  background: th.bg,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 5,
                                  color: th.text,
                                  outline: "none",
                                }}
                              >
                                <option value="demand">
                                  Demand (plaintiff)
                                </option>
                                <option value="offer">Offer (defendant)</option>
                                <option value="movement">Movement</option>
                                <option value="settlement">Settlement</option>
                                <option value="docket">Docket event</option>
                              </select>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.dim,
                                  marginBottom: 4,
                                  fontWeight: 600,
                                }}
                              >
                                SIDE
                              </div>
                              <select
                                value={settleNewEntry.side}
                                onChange={(e) =>
                                  setSettleNewEntry((p) => ({
                                    ...p,
                                    side: e.target.value,
                                  }))
                                }
                                style={{
                                  width: "100%",
                                  padding: "7px 9px",
                                  fontSize: 10,
                                  background: th.bg,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 5,
                                  color: th.text,
                                  outline: "none",
                                }}
                              >
                                <option value="Plaintiff">Plaintiff</option>
                                <option value="Defendant">Defendant</option>
                                <option value="Court">Court</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.dim,
                                marginBottom: 4,
                                fontWeight: 600,
                              }}
                            >
                              LABEL
                            </div>
                            <input
                              value={settleNewEntry.label}
                              onChange={(e) =>
                                setSettleNewEntry((p) => ({
                                  ...p,
                                  label: e.target.value,
                                }))
                              }
                              placeholder="e.g. Counter-offer at $1.5M after MSJ ruling"
                              style={{
                                width: "100%",
                                padding: "7px 9px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                color: th.text,
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                              marginBottom: 10,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.dim,
                                  marginBottom: 4,
                                  fontWeight: 600,
                                }}
                              >
                                AMOUNT (USD)
                              </div>
                              <input
                                type="number"
                                value={settleNewEntry.amount}
                                onChange={(e) =>
                                  setSettleNewEntry((p) => ({
                                    ...p,
                                    amount: e.target.value,
                                  }))
                                }
                                placeholder="0"
                                style={{
                                  width: "100%",
                                  padding: "7px 9px",
                                  fontSize: 10,
                                  background: th.bg,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 5,
                                  color: th.text,
                                  outline: "none",
                                  boxSizing: "border-box",
                                  fontFamily: "monospace",
                                }}
                              />
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.dim,
                                  marginBottom: 4,
                                  fontWeight: 600,
                                }}
                              >
                                DATE
                              </div>
                              <input
                                type="date"
                                value={settleNewEntry.date}
                                onChange={(e) =>
                                  setSettleNewEntry((p) => ({
                                    ...p,
                                    date: e.target.value,
                                  }))
                                }
                                style={{
                                  width: "100%",
                                  padding: "7px 9px",
                                  fontSize: 10,
                                  background: th.bg,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 5,
                                  color: th.text,
                                  outline: "none",
                                  boxSizing: "border-box",
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.dim,
                                marginBottom: 4,
                                fontWeight: 600,
                              }}
                            >
                              LINKED TO (Dkt # or event)
                            </div>
                            <input
                              value={settleNewEntry.linkedTo}
                              onChange={(e) =>
                                setSettleNewEntry((p) => ({
                                  ...p,
                                  linkedTo: e.target.value,
                                }))
                              }
                              placeholder="e.g. Dkt.4341 - MSJ ruling"
                              style={{
                                width: "100%",
                                padding: "7px 9px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                color: th.text,
                                outline: "none",
                                boxSizing: "border-box",
                                fontFamily: "monospace",
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: 14 }}>
                            <div
                              style={{
                                fontSize: 9,
                                color: th.dim,
                                marginBottom: 4,
                                fontWeight: 600,
                              }}
                            >
                              NOTE - which Damages Model slider justified the
                              move?
                            </div>
                            <textarea
                              value={settleNewEntry.note}
                              onChange={(e) =>
                                setSettleNewEntry((p) => ({
                                  ...p,
                                  note: e.target.value,
                                }))
                              }
                              rows={2}
                              placeholder="e.g. SJ ruling on Pak shifted plaintiff damages expectation downward · Sonner risk acknowledged"
                              style={{
                                width: "100%",
                                padding: "7px 9px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 5,
                                color: th.text,
                                outline: "none",
                                boxSizing: "border-box",
                                fontFamily: "inherit",
                                resize: "vertical",
                                lineHeight: 1.5,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => setSettleShowAdd(false)}
                              style={{
                                padding: "7px 14px",
                                fontSize: 10,
                                background: "transparent",
                                color: th.muted,
                                border: `1px solid ${th.border}`,
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={addEntry}
                              disabled={!settleNewEntry.label}
                              style={{
                                padding: "7px 16px",
                                fontSize: 10,
                                fontWeight: 700,
                                background: settleNewEntry.label
                                  ? "#10b981"
                                  : th.border,
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                cursor: settleNewEntry.label
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Add to Ledger
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "damages" &&
              (() => {
                const tracks = DAMAGES_TRACKS[caseId] || DAMAGES_TRACKS.juul;
                const tIdx = Math.min(damTrack, tracks.length - 1);
                const track = tracks[tIdx];
                const sl = damSliders[track.id] || {};
                const tg = damToggles[track.id] || {};
                const result = computeDamages(track, sl, tg);
                const setS = (sid, v) =>
                  setDamSliders((p) => ({
                    ...p,
                    [track.id]: { ...(p[track.id] || {}), [sid]: v },
                  }));
                const setT = (tid, v) =>
                  setDamToggles((p) => ({
                    ...p,
                    [track.id]: { ...(p[track.id] || {}), [tid]: v },
                  }));
                const sliderVal = (s) =>
                  (sl[s.id] !== undefined ? sl[s.id] : s.def) / (s.div || 1);
                const toggleVal = (t) => tg[t.id] || t.def;
                const apply = (scen) => {
                  if (scen.sliders)
                    setDamSliders((p) => ({
                      ...p,
                      [track.id]: { ...(p[track.id] || {}), ...scen.sliders },
                    }));
                  if (scen.toggles)
                    setDamToggles((p) => ({
                      ...p,
                      [track.id]: { ...(p[track.id] || {}), ...scen.toggles },
                    }));
                  toast(`Applied: ${scen.name || "scenario"}`, "success");
                };
                const saveScen = (name) => {
                  const tot = result.total;
                  const snap = {
                    name,
                    sliders: { ...sl },
                    toggles: { ...tg },
                    total: tot,
                    date: new Date().toLocaleDateString(),
                  };
                  setDamScenarios((p) => ({
                    ...p,
                    [track.id]: [...(p[track.id] || []), snap],
                  }));
                  setDamShowSave(false);
                  setDamSavedName("");
                  toast(`Saved: ${name}`, "success");
                };
                const reset = () => {
                  setDamSliders((p) => ({ ...p, [track.id]: {} }));
                  setDamToggles((p) => ({ ...p, [track.id]: {} }));
                  toast("Reset to defaults", "success");
                };
                const suggestAI = async () => {
                  setDamAILoading(true);
                  setDamAIScenarios([]);
                  try {
                    const result = await aiCall(
                      {
                        model: "claude-sonnet-4-20250514",
                        max_tokens: 1500,
                        system:
                          "You are a senior litigation strategist analyzing damages exposure. Return only valid JSON in the exact format requested.",
                        messages: [
                          {
                            role: "user",
                            content: `Case: ${isIle ? (caseId === "chid" ? "USA v. Chidambaran" : "USA v. Naqvi") : caseId === "juul" ? "Seliavski v. JUUL MDL" : caseId === "musk" ? "Musk v. Altman" : "Neurocentria v. Mando"}. Track: ${track.title}. Current model produces $${result.total.toLocaleString()}. Suggest 3 case-specific inflection scenarios as JSON array: [{"name":"...","desc":"..."}]. Be specific to this case's facts.`,
                          },
                        ],
                      },
                      () => JSON.stringify(damagesDemoScenarios(track)),
                    );
                    try {
                      const parsed =
                        typeof result.text === "string"
                          ? JSON.parse(result.text)
                          : result.text;
                      setDamAIScenarios(
                        Array.isArray(parsed)
                          ? parsed
                          : damagesDemoScenarios(track),
                      );
                    } catch {
                      setDamAIScenarios(damagesDemoScenarios(track));
                    }
                  } catch {
                    setDamAIScenarios(damagesDemoScenarios(track));
                  }
                  setDamAILoading(false);
                };
                const saved = damScenarios[track.id] || [];
                const caseTitle = isIle
                  ? caseId === "chid"
                    ? "USA v. Chidambaran"
                    : "USA v. Naqvi"
                  : caseId === "juul"
                    ? "Seliavski v. JUUL MDL"
                    : caseId === "musk"
                      ? "Musk v. Altman"
                      : "Neurocentria v. Mando";
                return (
                  <div
                    style={{
                      maxWidth: 1180,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    {/* PERSPECTIVE BANNER */}
                    <div
                      style={{
                        background:
                          damPersp === "plaintiff"
                            ? "linear-gradient(135deg,#7f1d1d,#991b1b)"
                            : damPersp === "defendant"
                              ? "linear-gradient(135deg,#1e3a8a,#1e40af)"
                              : dark
                                ? "linear-gradient(135deg,#0f172a,#1e293b)"
                                : "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 12,
                        border: `1px solid ${damPersp === "plaintiff" ? "#dc262655" : damPersp === "defendant" ? "#3b82f655" : th.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div
                          style={{
                            fontSize: 7,
                            color: damPersp === "all" ? th.faint : "#fff",
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontFamily: "monospace",
                            marginBottom: 4,
                            opacity: 0.85,
                          }}
                        >
                          STRATEGIC PERSPECTIVE
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: damPersp === "all" ? th.text : "#fff",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {damPersp === "plaintiff"
                            ? "🔴 Plaintiff Damages Model - your affirmative claims"
                            : damPersp === "defendant"
                              ? "🔵 Defendant Damages Model - counterclaims, offsets, fee recovery"
                              : "All · Both Sides"}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color:
                              damPersp === "all"
                                ? th.muted
                                : "rgba(255,255,255,0.85)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {damPersp === "plaintiff"
                            ? "Standard plaintiff exposure tracks: SFUSD nuisance, antitrust, charitable trust, patent royalty"
                            : damPersp === "defendant"
                              ? "Defendant counterclaim tracks: invalidity, §285 fees, SOL offset, Sonner reduction (where available)"
                              : "Toggle to see plaintiff's affirmative damages or defendant's counterclaim model"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {[
                          ["all", "All"],
                          ["plaintiff", "🔴 Plaintiff"],
                          ["defendant", "🔵 Defendant"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            onClick={() => setDamPersp(v)}
                            style={{
                              padding: "7px 14px",
                              fontSize: 10,
                              fontWeight: damPersp === v ? 700 : 500,
                              background:
                                damPersp === v
                                  ? "rgba(255,255,255,0.18)"
                                  : "transparent",
                              color:
                                damPersp === "all"
                                  ? damPersp === v
                                    ? th.text
                                    : th.muted
                                  : damPersp === v
                                    ? "#fff"
                                    : "rgba(255,255,255,0.7)",
                              border:
                                damPersp === v
                                  ? damPersp === "all"
                                    ? `1px solid ${th.text}`
                                    : "1px solid #fff"
                                  : `1px solid ${damPersp === "all" ? th.border : "rgba(255,255,255,0.25)"}`,
                              borderRadius: 6,
                              cursor: "pointer",
                              letterSpacing: 0.3,
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    {damPersp === "defendant" && caseId !== "neuro" && (
                      <div
                        style={{
                          padding: "30px 20px",
                          marginBottom: 14,
                          background: th.card,
                          border: `1px dashed ${th.border}`,
                          borderRadius: 10,
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 30,
                            opacity: 0.4,
                            marginBottom: 8,
                          }}
                        >
                          🔵
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: th.muted,
                            marginBottom: 5,
                          }}
                        >
                          No affirmative defendant damages model
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.faint,
                            lineHeight: 1.5,
                            maxWidth: 520,
                            margin: "0 auto",
                          }}
                        >
                          {caseId === "juul"
                            ? "Defendant has no affirmative damages claim. Defense focus is liability reduction (Sonner-bar on past costs) and SJ-narrowing of the case. Toggle to All or Plaintiff to see the affirmative damages tracks."
                            : "Defendant has no affirmative damages claim. Defense focus is SOL bar reduction and constructive-trust valuation challenge. Toggle to All or Plaintiff to see the affirmative damages tracks."}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 14,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#10b981",
                              background: "#10b98118",
                              border: "1px solid #10b98133",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            💰 EXPOSURE MODELING
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            Live · interactive · scenario-saveable
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Damages Model
                        </h1>
                        <div style={{ fontSize: 9, color: th.muted }}>
                          {caseTitle} · model the case, save scenarios, brief
                          the client
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5 }}>
                        {saved.length > 0 && (
                          <button
                            onClick={() => setDamCompare((c) => !c)}
                            style={{
                              padding: "6px 12px",
                              fontSize: 9,
                              fontWeight: 600,
                              background: damCompare ? "#1e40af" : th.surface,
                              color: damCompare ? "#fff" : th.dim,
                              border: `1px solid ${damCompare ? "#1e40af" : th.border}`,
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            📊 Compare ({saved.length})
                          </button>
                        )}
                        <button
                          onClick={reset}
                          style={{
                            padding: "6px 12px",
                            fontSize: 9,
                            background: "transparent",
                            color: th.dim,
                            border: `1px solid ${th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          ↻ Reset defaults
                        </button>
                      </div>
                    </div>

                    {/* Track selector */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 14,
                        padding: 6,
                        background: th.surface,
                        borderRadius: 9,
                        border: `1px solid ${th.border}`,
                      }}
                    >
                      {tracks.map((t, i) => (
                        <button
                          key={t.id}
                          onClick={() => {
                            setDamTrack(i);
                            setDamAIScenarios([]);
                          }}
                          style={{
                            flex: 1,
                            textAlign: "left",
                            padding: "10px 14px",
                            fontSize: 10,
                            fontWeight: i === tIdx ? 700 : 500,
                            background: i === tIdx ? "#10b981" : "transparent",
                            color: i === tIdx ? "#fff" : th.muted,
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            position: "relative",
                          }}
                        >
                          {t.urgent && (
                            <span
                              style={{
                                position: "absolute",
                                top: -6,
                                right: -4,
                                fontSize: 6,
                                padding: "2px 6px",
                                background: "#ef4444",
                                color: "#fff",
                                borderRadius: 8,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                boxShadow: "0 2px 6px #ef444466",
                                fontFamily: "system-ui",
                              }}
                            >
                              URGENT
                            </span>
                          )}
                          <div>{t.title.split(" - ")[0]}</div>
                          <div
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              fontWeight: 500,
                              opacity: 0.8,
                              marginTop: 2,
                            }}
                          >
                            {t.title.split(" - ")[1] || ""}
                          </div>
                        </button>
                      ))}
                    </div>

                    {damCompare && saved.length > 0 ? (
                      <div
                        style={{
                          background: th.card,
                          borderRadius: 10,
                          border: `1px solid ${th.border}`,
                          padding: "14px 16px",
                          marginBottom: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 10,
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 700 }}>
                            📊 Saved Scenarios - {track.title}
                          </div>
                          <button
                            onClick={() => setDamCompare(false)}
                            style={{
                              fontSize: 8,
                              padding: "3px 9px",
                              background: "transparent",
                              color: th.dim,
                              border: `1px solid ${th.border}`,
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          >
                            ✕ Close
                          </button>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${Math.min(saved.length, 3)},1fr)`,
                            gap: 10,
                          }}
                        >
                          {saved.map((s, i) => (
                            <div
                              key={i}
                              style={{
                                background: th.bg,
                                borderRadius: 8,
                                border: `1px solid ${th.border}`,
                                padding: "12px 14px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: th.text,
                                  marginBottom: 3,
                                }}
                              >
                                {s.name}
                              </div>
                              <div
                                style={{
                                  fontSize: 7,
                                  color: th.faint,
                                  fontFamily: "monospace",
                                  marginBottom: 8,
                                }}
                              >
                                {s.date}
                              </div>
                              <div
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#10b981",
                                  fontFamily: "monospace",
                                  marginBottom: 8,
                                }}
                              >
                                {fmtUSD(s.total)}
                              </div>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button
                                  onClick={() => {
                                    setDamSliders((p) => ({
                                      ...p,
                                      [track.id]: s.sliders,
                                    }));
                                    setDamToggles((p) => ({
                                      ...p,
                                      [track.id]: s.toggles,
                                    }));
                                    setDamCompare(false);
                                    toast(`Loaded: ${s.name}`, "success");
                                  }}
                                  style={{
                                    flex: 1,
                                    fontSize: 8,
                                    padding: "4px",
                                    background: "#1e40af",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  Load
                                </button>
                                <button
                                  onClick={() =>
                                    setDamScenarios((p) => ({
                                      ...p,
                                      [track.id]: (p[track.id] || []).filter(
                                        (_, j) => j !== i,
                                      ),
                                    }))
                                  }
                                  style={{
                                    fontSize: 8,
                                    padding: "4px 8px",
                                    background: "transparent",
                                    color: "#ef4444",
                                    border: "1px solid #ef444433",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            marginTop: 10,
                            fontStyle: "italic",
                            lineHeight: 1.5,
                          }}
                        >
                          💡 Save current as "JLI best case", "Plaintiff best
                          case", "Settlement target" to bring a structured
                          exposure range to your client call.
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 360px",
                          gap: 14,
                        }}
                      >
                        {/* LEFT - sliders + toggles */}
                        <div>
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 10,
                              border: `1px solid ${th.border}`,
                              padding: "14px 18px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 6,
                              }}
                            >
                              <div>
                                <h2
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    margin: "0 0 2px",
                                    letterSpacing: "-0.2px",
                                  }}
                                >
                                  {track.title}
                                </h2>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {track.anchor}
                                </div>
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color:
                                    track.mode === "ev" ? "#a78bfa" : "#3b82f6",
                                  background:
                                    track.mode === "ev"
                                      ? "#a78bfa18"
                                      : "#3b82f618",
                                  border: `1px solid ${track.mode === "ev" ? "#a78bfa33" : "#3b82f633"}`,
                                  padding: "2px 7px",
                                  borderRadius: 10,
                                  fontWeight: 600,
                                  letterSpacing: 0.4,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {track.mode === "ev"
                                  ? "RISK-ADJUSTED EV"
                                  : "TOTAL EXPOSURE"}
                              </span>
                            </div>
                          </div>

                          {track.sliders.map((s, i) => {
                            const v = sliderVal(s);
                            const pct =
                              ((v * (s.div || 1) - s.min) / (s.max - s.min)) *
                              100;
                            const displayV =
                              s.suffix === "$"
                                ? "$" + v.toLocaleString()
                                : s.suffix === "M"
                                  ? "$" + v.toFixed(0) + "M"
                                  : s.suffix === "B"
                                    ? "$" + v.toFixed(0) + "B"
                                    : s.suffix === "x" || s.suffix === "×"
                                      ? v.toFixed(2) + s.suffix
                                      : v.toFixed(s.div ? 2 : 0) + s.suffix;
                            return (
                              <div
                                key={s.id}
                                style={{
                                  background: th.card,
                                  borderRadius: 10,
                                  border: `1px solid ${th.border}`,
                                  padding: "14px 18px",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    marginBottom: 8,
                                    gap: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      flex: 1,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: th.text,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {s.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      color: "#10b981",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {displayV}
                                  </div>
                                </div>
                                <input
                                  type="range"
                                  min={s.min}
                                  max={s.max}
                                  value={
                                    sl[s.id] !== undefined ? sl[s.id] : s.def
                                  }
                                  onChange={(e) => setS(s.id, +e.target.value)}
                                  style={{
                                    width: "100%",
                                    height: 5,
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    background: `linear-gradient(to right,#10b981 ${pct}%,${th.border} ${pct}%)`,
                                    borderRadius: 3,
                                    outline: "none",
                                    cursor: "pointer",
                                    accentColor: "#10b981",
                                    marginBottom: 6,
                                  }}
                                />
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.faint,
                                    fontFamily: "monospace",
                                    marginBottom: 7,
                                    paddingLeft: 2,
                                    letterSpacing: 0.2,
                                  }}
                                >
                                  ⚓ {s.anchor}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    lineHeight: 1.55,
                                    padding: "7px 10px",
                                    background: "#a78bfa10",
                                    borderLeft: "2px solid #a78bfa",
                                    borderRadius: "0 4px 4px 0",
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "#a78bfa",
                                      fontWeight: 700,
                                    }}
                                  >
                                    AI:{" "}
                                  </span>
                                  {s.ai}
                                </div>
                              </div>
                            );
                          })}

                          {track.toggles.map((t, i) => {
                            const cur = toggleVal(t);
                            return (
                              <div
                                key={t.id}
                                style={{
                                  background: th.card,
                                  borderRadius: 10,
                                  border: `2px solid ${cur === t.options[0].val ? "#10b98144" : "#ef444444"}`,
                                  padding: "14px 18px",
                                  marginBottom: 9,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: th.text,
                                    marginBottom: 9,
                                    letterSpacing: "-0.1px",
                                  }}
                                >
                                  ⚖️ {t.label}
                                </div>
                                <div style={{ display: "flex", gap: 7 }}>
                                  {t.options.map((o) => {
                                    const isSel = cur === o.val;
                                    const isPos = o.label.startsWith("✓");
                                    const isWarn = o.label.startsWith("⚠");
                                    const c = isPos
                                      ? "#10b981"
                                      : isWarn
                                        ? "#f59e0b"
                                        : "#ef4444";
                                    return (
                                      <button
                                        key={o.val}
                                        onClick={() => setT(t.id, o.val)}
                                        style={{
                                          flex: 1,
                                          padding: "10px 12px",
                                          textAlign: "left",
                                          fontSize: 10,
                                          fontWeight: isSel ? 700 : 500,
                                          background: isSel ? c : th.bg,
                                          color: isSel ? "#fff" : th.muted,
                                          border: `2px solid ${isSel ? c : th.border}`,
                                          borderRadius: 7,
                                          cursor: "pointer",
                                          transition: "all 0.15s",
                                        }}
                                      >
                                        <div style={{ marginBottom: 2 }}>
                                          {o.label}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: 7,
                                            fontFamily: "monospace",
                                            fontWeight: 500,
                                            opacity: 0.8,
                                          }}
                                        >
                                          {o.desc}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}

                          {/* AI Suggest + Save buttons */}
                          <div
                            style={{ display: "flex", gap: 8, marginTop: 14 }}
                          >
                            <button
                              onClick={suggestAI}
                              disabled={damAILoading}
                              style={{
                                flex: 1,
                                padding: "10px 14px",
                                fontSize: 10,
                                fontWeight: 700,
                                background: damAILoading
                                  ? "#a78bfa66"
                                  : "linear-gradient(135deg,#1e40af,#a78bfa)",
                                color: "#fff",
                                border: "none",
                                borderRadius: 7,
                                cursor: damAILoading
                                  ? "not-allowed"
                                  : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                              }}
                            >
                              {damAILoading ? (
                                <>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 11,
                                      height: 11,
                                      border: "2px solid #fff",
                                      borderTopColor: "transparent",
                                      borderRadius: "50%",
                                      animation: "spin 0.8s linear infinite",
                                    }}
                                  />
                                  Analyzing case…
                                </>
                              ) : (
                                <>✨ AI: Suggest Key What-Ifs</>
                              )}
                            </button>
                            <button
                              onClick={() => setDamShowSave(true)}
                              style={{
                                flex: 1,
                                padding: "10px 14px",
                                fontSize: 10,
                                fontWeight: 700,
                                background: th.surface,
                                color: th.text,
                                border: `1px solid ${th.border}`,
                                borderRadius: 7,
                                cursor: "pointer",
                              }}
                            >
                              💾 Save as Scenario
                            </button>
                          </div>

                          {/* AI scenarios */}
                          {damAIScenarios.length > 0 && (
                            <div
                              style={{
                                marginTop: 12,
                                background: "#a78bfa08",
                                border: "1px solid #a78bfa44",
                                borderLeft: "3px solid #a78bfa",
                                borderRadius: 8,
                                padding: "12px 14px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 10,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#a78bfa",
                                  }}
                                >
                                  ✨ {damAIScenarios.length} Inflection
                                  Scenarios
                                </div>
                                <button
                                  onClick={() => setDamAIScenarios([])}
                                  style={{
                                    fontSize: 7,
                                    padding: "2px 6px",
                                    background: "transparent",
                                    color: th.faint,
                                    border: `1px solid ${th.border}`,
                                    borderRadius: 3,
                                    cursor: "pointer",
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                              {damAIScenarios.map((s, i) => (
                                <div
                                  key={i}
                                  style={{
                                    padding: "10px 12px",
                                    marginBottom: 6,
                                    background: th.card,
                                    borderRadius: 6,
                                    border: `1px solid ${th.border}`,
                                    animation: `slideIn 0.4s ease ${i * 0.1}s both`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 10,
                                      marginBottom: 4,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: th.text,
                                        flex: 1,
                                      }}
                                    >
                                      {s.name}
                                    </div>
                                    {s.apply && (
                                      <button
                                        onClick={() => apply(s)}
                                        style={{
                                          fontSize: 8,
                                          padding: "3px 9px",
                                          background: "#a78bfa",
                                          color: "#fff",
                                          border: "none",
                                          borderRadius: 4,
                                          cursor: "pointer",
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                          flexShrink: 0,
                                        }}
                                      >
                                        Apply →
                                      </button>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.muted,
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {s.desc}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* RIGHT - live result panel (sticky) */}
                        <div>
                          <div
                            style={{
                              position: "sticky",
                              top: "70px",
                              background: `linear-gradient(135deg,${dark ? "#0f172a" : "#f8fafc"},${dark ? "#1e293b" : "#e2e8f0"})`,
                              borderRadius: 12,
                              border: `1px solid ${th.border}`,
                              padding: "18px 20px",
                              boxShadow: dark
                                ? "0 8px 24px rgba(0,0,0,0.4)"
                                : "0 8px 24px rgba(0,0,0,0.06)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                fontFamily: "monospace",
                                color:
                                  track.mode === "ev" ? "#a78bfa" : "#3b82f6",
                                letterSpacing: 1.5,
                                marginBottom: 6,
                                fontWeight: 700,
                                textTransform: "uppercase",
                              }}
                            >
                              {track.mode === "ev"
                                ? "Risk-Adjusted Expected Value"
                                : "Total Exposure"}
                            </div>
                            <div
                              style={{
                                fontSize: 34,
                                fontWeight: 800,
                                fontFamily: "monospace",
                                color: result.total > 0 ? "#10b981" : th.muted,
                                letterSpacing: "-1px",
                                marginBottom: 4,
                                lineHeight: 1.1,
                                wordBreak: "break-word",
                              }}
                            >
                              {fmtUSD(result.total)}
                            </div>
                            {result.probability !== undefined && (
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.muted,
                                  marginBottom: 12,
                                  fontFamily: "monospace",
                                }}
                              >
                                P(liability) ={" "}
                                {(result.probability * 100).toFixed(1)}%
                              </div>
                            )}
                            <div
                              style={{
                                height: 1,
                                background: th.border,
                                margin: "14px 0",
                              }}
                            />
                            <div
                              style={{
                                fontSize: 8,
                                color: th.dim,
                                letterSpacing: 1,
                                fontFamily: "monospace",
                                marginBottom: 9,
                                fontWeight: 700,
                                textTransform: "uppercase",
                              }}
                            >
                              Breakdown
                            </div>
                            {result.lines.map((line, i) => (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  padding: "7px 0",
                                  borderBottom:
                                    i < result.lines.length - 1
                                      ? `1px dashed ${th.border}`
                                      : "none",
                                  gap: 8,
                                  opacity: line.isZero ? 0.5 : 1,
                                  transition: "opacity 0.3s",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    fontSize: 9,
                                    color: line.isHeavy ? th.text : th.muted,
                                    fontWeight: line.isHeavy ? 700 : 500,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {line.label}
                                  {line.note && (
                                    <div
                                      style={{
                                        fontSize: 7,
                                        color: line.note.includes("Barred")
                                          ? "#ef4444"
                                          : th.faint,
                                        fontStyle: "italic",
                                        marginTop: 2,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      ↳ {line.note}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: line.isHeavy ? 700 : 600,
                                    fontFamily: "monospace",
                                    color: line.isReduction
                                      ? "#ef4444"
                                      : line.isHeavy
                                        ? th.text
                                        : th.text,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {fmtUSD(line.value)}
                                </div>
                              </div>
                            ))}
                            <div
                              style={{
                                height: 1,
                                background: th.border,
                                margin: "14px 0 10px",
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 9,
                                  color: th.dim,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  fontFamily: "monospace",
                                }}
                              >
                                TOTAL
                              </div>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  fontFamily: "monospace",
                                  color: "#10b981",
                                }}
                              >
                                {fmtUSD(result.total)}
                              </div>
                            </div>
                            {saved.length > 0 && (
                              <div
                                style={{
                                  marginTop: 14,
                                  paddingTop: 14,
                                  borderTop: `1px solid ${th.border}`,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.dim,
                                    letterSpacing: 1,
                                    fontFamily: "monospace",
                                    marginBottom: 8,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Saved scenarios ({saved.length})
                                </div>
                                {saved.slice(0, 3).map((s, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "4px 0",
                                      fontSize: 8,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: th.muted,
                                        flex: 1,
                                        marginRight: 8,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {s.name}
                                    </span>
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        color: "#10b981",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {fmtUSD(s.total)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Save scenario modal */}
                    {damShowSave && (
                      <div
                        style={{
                          position: "fixed",
                          inset: 0,
                          background: "rgba(0,0,0,.65)",
                          zIndex: 500,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => setDamShowSave(false)}
                      >
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: th.surface,
                            border: `1px solid ${th.border}`,
                            borderRadius: 12,
                            padding: "24px 28px",
                            width: 420,
                            boxShadow: "0 16px 48px rgba(0,0,0,.4)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              marginBottom: 6,
                            }}
                          >
                            💾 Save Scenario
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: th.muted,
                              marginBottom: 16,
                              lineHeight: 1.5,
                            }}
                          >
                            Save the current slider configuration with a name.
                            You can compare scenarios side-by-side later - 
                            useful for client briefings.
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              marginBottom: 6,
                              color: th.dim,
                            }}
                          >
                            SCENARIO NAME
                          </div>
                          <input
                            value={damSavedName}
                            onChange={(e) => setDamSavedName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && damSavedName.trim())
                                saveScen(damSavedName.trim());
                            }}
                            placeholder="e.g. JLI best case · Plaintiff best case · Settlement target"
                            autoFocus
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              fontSize: 11,
                              background: th.bg,
                              border: `1px solid ${th.border}`,
                              borderRadius: 6,
                              color: th.text,
                              outline: "none",
                              boxSizing: "border-box",
                              marginBottom: 14,
                            }}
                          />
                          <div
                            style={{
                              padding: "10px 12px",
                              background: th.card,
                              borderRadius: 6,
                              marginBottom: 16,
                              border: `1px solid ${th.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                color: th.dim,
                                marginBottom: 3,
                                fontFamily: "monospace",
                              }}
                            >
                              CURRENT TOTAL
                            </div>
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#10b981",
                                fontFamily: "monospace",
                              }}
                            >
                              {fmtUSD(result.total)}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                setDamShowSave(false);
                                setDamSavedName("");
                              }}
                              style={{
                                padding: "7px 14px",
                                fontSize: 10,
                                background: "transparent",
                                color: th.muted,
                                border: `1px solid ${th.border}`,
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() =>
                                damSavedName.trim() &&
                                saveScen(damSavedName.trim())
                              }
                              disabled={!damSavedName.trim()}
                              style={{
                                padding: "7px 16px",
                                fontSize: 10,
                                fontWeight: 700,
                                background: damSavedName.trim()
                                  ? "#10b981"
                                  : th.border,
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                cursor: damSavedName.trim()
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Save Scenario
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            {tab === "demos" &&
              (() => {
                const list =
                  DEMONSTRATIVES_DATA[caseId] || DEMONSTRATIVES_DATA.juul;
                const sel = Math.min(demoSelected, list.length - 1);
                const cur = list[sel];
                const variant = demoVariants[cur.id] || 0;
                const desc =
                  demoDescs[cur.id] !== undefined
                    ? demoDescs[cur.id]
                    : cur.description;
                const regen = () => {
                  setDemoLoading(true);
                  setTimeout(() => {
                    setDemoVariants((p) => ({
                      ...p,
                      [cur.id]: ((p[cur.id] || 0) + 1) % cur.variants,
                    }));
                    setDemoLoading(false);
                    toast("Demonstrative regenerated · new visual", "success");
                  }, 1100);
                };
                const resetDesc = () => {
                  setDemoDescs((p) => {
                    const n = { ...p };
                    delete n[cur.id];
                    return n;
                  });
                  toast("Description reset", "success");
                };
                const isEdited =
                  demoDescs[cur.id] !== undefined &&
                  demoDescs[cur.id] !== cur.description;
                return (
                  <div
                    style={{
                      maxWidth: 1100,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 14,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#10b981",
                              background: "#10b98118",
                              border: "1px solid #10b98133",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            📊 TRIAL VISUALS
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            AI-generated · attorney-editable
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Demonstratives
                        </h1>
                        <div style={{ fontSize: 9, color: th.muted }}>
                          Infographics that explain complex issues to juries,
                          judges, and clients · Edit the description and
                          regenerate to iterate
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        gap: 14,
                      }}
                    >
                      {/* List of demonstratives */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: th.dim,
                            letterSpacing: 0.8,
                            textTransform: "uppercase",
                            marginBottom: 4,
                            padding: "0 4px",
                          }}
                        >
                          This Case ({list.length})
                        </div>
                        {list.map((d, i) => {
                          const isSel = i === sel;
                          const isE =
                            demoDescs[d.id] !== undefined &&
                            demoDescs[d.id] !== d.description;
                          return (
                            <button
                              key={d.id}
                              onClick={() => {
                                setDemoSelected(i);
                                setDemoEditMode(false);
                              }}
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontSize: 10,
                                background: isSel ? "#10b98118" : th.card,
                                color: isSel ? th.text : th.muted,
                                border: `1px solid ${isSel ? "#10b98166" : th.border}`,
                                borderLeft: isSel
                                  ? "3px solid #10b981"
                                  : `1px solid ${th.border}`,
                                borderRadius: 6,
                                cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 6,
                                  marginBottom: 3,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "monospace",
                                    color: isSel ? "#10b981" : th.faint,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  FIG. {i + 1}
                                </span>
                                {isE && (
                                  <span
                                    style={{
                                      fontSize: 6,
                                      color: "#f59e0b",
                                      background: "#f59e0b18",
                                      padding: "1px 5px",
                                      borderRadius: 4,
                                      fontWeight: 700,
                                      letterSpacing: 0.4,
                                    }}
                                  >
                                    EDITED
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: isSel ? th.text : th.muted,
                                  lineHeight: 1.35,
                                }}
                              >
                                {d.title}
                              </div>
                            </button>
                          );
                        })}
                        <div
                          style={{
                            fontSize: 8,
                            color: th.faint,
                            padding: "8px 4px",
                            lineHeight: 1.5,
                            fontStyle: "italic",
                          }}
                        >
                          💡 Each demonstrative has multiple visual variants.
                          Click "Regenerate" to cycle.
                        </div>
                      </div>

                      {/* Selected demonstrative - large preview */}
                      <div>
                        <div
                          style={{
                            background: th.card,
                            borderRadius: 10,
                            border: `1px solid ${th.border}`,
                            padding: "14px 16px",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 10,
                              marginBottom: 8,
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: "#10b981",
                                  letterSpacing: 1,
                                  marginBottom: 3,
                                  fontWeight: 700,
                                }}
                              >
                                FIGURE {sel + 1} OF {list.length} · VARIANT{" "}
                                {variant + 1} OF {cur.variants}
                              </div>
                              <h2
                                style={{
                                  fontSize: 15,
                                  fontWeight: 700,
                                  margin: "0 0 3px",
                                  lineHeight: 1.3,
                                }}
                              >
                                {cur.title}
                              </h2>
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.muted,
                                  fontFamily: "monospace",
                                }}
                              >
                                Use for: {cur.useFor}
                              </div>
                            </div>
                            <button
                              onClick={regen}
                              disabled={demoLoading}
                              style={{
                                padding: "7px 14px",
                                fontSize: 10,
                                fontWeight: 700,
                                background: demoLoading
                                  ? th.surface
                                  : "linear-gradient(135deg,#10b981,#059669)",
                                color: demoLoading ? th.faint : "#fff",
                                border: "none",
                                borderRadius: 6,
                                cursor: demoLoading ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              {demoLoading ? (
                                <>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 10,
                                      height: 10,
                                      border: "2px solid currentColor",
                                      borderTopColor: "transparent",
                                      borderRadius: "50%",
                                      animation: "spin 0.8s linear infinite",
                                    }}
                                  />
                                  Regenerating
                                </>
                              ) : (
                                <>↻ Regenerate</>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* SVG preview */}
                        <div
                          style={{
                            background: dark ? "#0f0f10" : "#fafaf8",
                            borderRadius: 10,
                            border: `1px solid ${th.border}`,
                            padding: "18px",
                            marginBottom: 10,
                            opacity: demoLoading ? 0.4 : 1,
                            transition: "opacity 0.3s",
                            position: "relative",
                          }}
                        >
                          {demoLoading && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            >
                              <div
                                style={{
                                  padding: "10px 16px",
                                  background: th.card,
                                  border: `1px solid ${th.border}`,
                                  borderRadius: 8,
                                  fontSize: 10,
                                  color: th.muted,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 10,
                                    height: 10,
                                    border: "2px solid #10b981",
                                    borderTopColor: "transparent",
                                    borderRadius: "50%",
                                    animation: "spin 0.8s linear infinite",
                                  }}
                                />
                                Generating new visualization…
                              </div>
                            </div>
                          )}
                          <div
                            key={cur.id + "-" + variant}
                            style={{ animation: "fadeIn 0.5s ease" }}
                          >
                            {renderDemoSVG(cur.id, variant, th, dark)}
                          </div>
                        </div>

                        {/* Editable description */}
                        <div
                          style={{
                            background: th.card,
                            borderRadius: 10,
                            border: `1px solid ${th.border}`,
                            padding: "14px 16px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: th.text,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              📝 Description{" "}
                              {isEdited && (
                                <span
                                  style={{
                                    fontSize: 6,
                                    color: "#f59e0b",
                                    background: "#f59e0b18",
                                    border: "1px solid #f59e0b33",
                                    padding: "1px 6px",
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  EDITED BY YOU
                                </span>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 5 }}>
                              {isEdited && (
                                <button
                                  onClick={resetDesc}
                                  style={{
                                    fontSize: 8,
                                    padding: "3px 8px",
                                    background: "transparent",
                                    color: "#ef4444",
                                    border: "1px solid #ef444433",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                  }}
                                >
                                  Reset
                                </button>
                              )}
                              <button
                                onClick={() => setDemoEditMode((m) => !m)}
                                style={{
                                  fontSize: 8,
                                  padding: "3px 8px",
                                  background: demoEditMode
                                    ? "#f59e0b"
                                    : th.surface,
                                  color: demoEditMode ? "#fff" : th.dim,
                                  border: `1px solid ${demoEditMode ? "#f59e0b" : th.border}`,
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                {demoEditMode ? "Done" : "Edit"}
                              </button>
                            </div>
                          </div>
                          {demoEditMode ? (
                            <textarea
                              value={desc}
                              onChange={(e) =>
                                setDemoDescs((p) => ({
                                  ...p,
                                  [cur.id]: e.target.value,
                                }))
                              }
                              rows={6}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: 10,
                                background: th.bg,
                                border: `1px solid ${th.border}`,
                                borderRadius: 6,
                                color: th.text,
                                outline: "none",
                                resize: "vertical",
                                fontFamily: "inherit",
                                lineHeight: 1.6,
                                boxSizing: "border-box",
                              }}
                            />
                          ) : (
                            <p
                              style={{
                                fontSize: 10,
                                color: th.muted,
                                lineHeight: 1.7,
                                margin: 0,
                              }}
                            >
                              {desc}
                            </p>
                          )}
                          {demoEditMode && (
                            <div
                              style={{
                                fontSize: 8,
                                color: th.faint,
                                marginTop: 6,
                                fontStyle: "italic",
                              }}
                            >
                              💡 Tip: Edit the description, then click
                              Regenerate to produce a new visual variant aligned
                              with your edits.
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 10,
                            padding: "0 4px",
                            fontSize: 8,
                            color: th.faint,
                          }}
                        >
                          <span>
                            Click ↻ Regenerate to cycle visual variants · Edit
                            text inline
                          </span>
                          <span style={{ fontFamily: "monospace" }}>
                            Export coming soon: PNG · PDF · trial-ready
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            {tab === "learn" &&
              (() => {
                const story = (LEARN_STORIES[caseId] || LEARN_STORIES.juul)[
                  learnPersp
                ];
                const totalP = story.length;
                const curP = Math.min(learnPage, totalP - 1);
                const page = story[curP];
                const persOpts = [
                  ["plaintiff", "⚖️ Plaintiff", "#ef4444"],
                  ["defendant", "🛡️ Defendant", "#3b82f6"],
                  ["judge", "👨‍⚖️ Judge", "#a78bfa"],
                ];
                const persColor = persOpts.find((p) => p[0] === learnPersp)[2];
                const speak = (t) => {
                  const mp3Url = `/audio/learn/${caseId}/${learnPersp}/${curP}.mp3`;
                  // request token: a later speak()/stopSpeak() invalidates any probe or play() still in flight
                  const token = (window.__learnReq = (window.__learnReq || 0) + 1);
                  const stale = () => token !== window.__learnReq;
                  if (window.speechSynthesis) window.speechSynthesis.cancel();
                  if (window.__learnAudio) {
                    try { window.__learnAudio.pause(); } catch (_) {}
                    window.__learnAudio = null;
                  }
                  setLearnPlaying(true);
                  const tts = () => {
                    if (stale()) return;
                    window.__learnAudio = null;
                    if (!window.speechSynthesis) {
                      toast("Speech not supported in this browser", "warn");
                      setLearnPlaying(false);
                      return;
                    }
                    const u = new SpeechSynthesisUtterance(t);
                    u.rate = 1.1;
                    u.pitch = 1.0;
                    u.onend = () => { if (!stale()) setLearnPlaying(false); };
                    u.onerror = () => { if (!stale()) setLearnPlaying(false); };
                    window.speechSynthesis.speak(u);
                    setLearnPlaying(true);
                  };
                  // pre-generated narration when present; the CRA dev server proxies HEAD and 404s missing files,
                  // so probe with a ranged GET and check the content-type before handing it to <audio>
                  fetch(mp3Url, { method: "GET", headers: { Range: "bytes=0-0" } })
                    .then((r) => {
                      if (stale()) return;
                      const ct = (r.headers.get("content-type") || "").toLowerCase();
                      if (!r.ok || !ct.includes("audio")) throw new Error("no mp3");
                      const audio = new Audio(mp3Url);
                      audio.playbackRate = 1.05;
                      window.__learnAudio = audio;
                      audio.onended = () => {
                        if (window.__learnAudio === audio) window.__learnAudio = null;
                        if (!stale()) setLearnPlaying(false);
                      };
                      audio.onerror = () => { if (!stale()) tts(); };
                      return audio.play().catch((err) => {
                        // pause() during start-up rejects with AbortError - that is a stop, not a failure
                        if (stale() || (err && err.name === "AbortError")) return;
                        tts();
                      });
                    })
                    .catch(() => tts());
                };
                const stopSpeak = () => {
                  window.__learnReq = (window.__learnReq || 0) + 1;
                  if (window.speechSynthesis) window.speechSynthesis.cancel();
                  if (window.__learnAudio) {
                    try { window.__learnAudio.pause(); } catch (_) {}
                    window.__learnAudio = null;
                  }
                  setLearnPlaying(false);
                };
                const goPage = (d) => {
                  stopSpeak();
                  setLearnFlipDir(d);
                  const target = Math.max(0, Math.min(totalP - 1, curP + d));
                  setLearnPage(target);
                };
                const switchPersp = (p) => {
                  stopSpeak();
                  setLearnPersp(p);
                  setLearnPage(0);
                  setLearnFlipDir(0);
                };
                const caseTitle = isIle
                  ? caseId === "chid"
                    ? "USA v. Chidambaran"
                    : "USA v. Naqvi"
                  : caseId === "juul"
                    ? "Seliavski v. JUUL Labs"
                    : caseId === "musk"
                      ? "Musk v. Altman et al."
                      : "Neurocentria v. Mando";
                const paperBg = dark
                  ? "linear-gradient(135deg,#1c1d22 0%,#16171b 100%)"
                  : "linear-gradient(135deg,#fdfaf2 0%,#f5efe0 100%)";
                const paperText = dark ? "#e6e3d8" : "#2a2520";
                const paperMuted = dark ? "#a8a497" : "#5a544a";
                return (
                  <div
                    style={{
                      maxWidth: 980,
                      margin: "0 auto",
                      padding: "16px 16px 60px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 14,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: "#a78bfa",
                              background: "#a78bfa18",
                              border: "1px solid #a78bfa33",
                              padding: "2px 7px",
                              borderRadius: 10,
                              letterSpacing: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            📖 ONBOARDING
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontFamily: "monospace",
                              color: th.faint,
                            }}
                          >
                            For new associates
                          </span>
                        </div>
                        <h1
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            margin: "0 0 2px",
                          }}
                        >
                          Learn the Case
                        </h1>
                        <div style={{ fontSize: 9, color: th.muted }}>
                          {caseTitle} · {totalP}-chapter storybook · read or
                          listen
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          padding: 3,
                          background: th.surface,
                          borderRadius: 8,
                          border: `1px solid ${th.border}`,
                        }}
                      >
                        {persOpts.map(([k, l, c]) => (
                          <button
                            key={k}
                            onClick={() => switchPersp(k)}
                            style={{
                              padding: "6px 12px",
                              fontSize: 9,
                              fontWeight: learnPersp === k ? 700 : 500,
                              background: learnPersp === k ? c : "transparent",
                              color: learnPersp === k ? "#fff" : th.muted,
                              border: "none",
                              borderRadius: 5,
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Book */}
                    <div
                      style={{
                        position: "relative",
                        perspective: "1500px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        key={learnPersp + "-" + curP}
                        style={{
                          background: paperBg,
                          borderRadius: 10,
                          boxShadow: dark
                            ? "0 12px 40px rgba(0,0,0,0.5),inset 0 0 0 1px rgba(255,255,255,0.04)"
                            : "0 12px 40px rgba(0,0,0,0.18),inset 0 0 0 1px rgba(0,0,0,0.04)",
                          padding: "38px 56px 30px",
                          minHeight: 380,
                          position: "relative",
                          animation:
                            learnFlipDir > 0
                              ? "pageFlipNext 0.55s ease"
                              : learnFlipDir < 0
                                ? "pageFlipPrev 0.55s ease"
                                : "none",
                          transformStyle: "preserve-3d",
                          fontFamily: "Georgia,'Times New Roman',serif",
                        }}
                      >
                        {/* Spine shadow */}
                        <div
                          style={{
                            position: "absolute",
                            top: 14,
                            bottom: 14,
                            left: 0,
                            width: 18,
                            background: dark
                              ? "linear-gradient(to right,rgba(0,0,0,0.3),transparent)"
                              : "linear-gradient(to right,rgba(0,0,0,0.08),transparent)",
                            pointerEvents: "none",
                            borderRadius: "10px 0 0 10px",
                          }}
                        />
                        {/* Page corner */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 0,
                            height: 0,
                            borderTop: `28px solid ${dark ? "#2a2b30" : "#ebe2cf"}`,
                            borderLeft: "28px solid transparent",
                            pointerEvents: "none",
                            borderRadius: "0 10px 0 0",
                            opacity: 0.7,
                          }}
                        />
                        <div
                          style={{
                            fontSize: 7,
                            fontFamily: "monospace",
                            color: persColor,
                            letterSpacing: 1.5,
                            marginBottom: 14,
                            textTransform: "uppercase",
                            fontWeight: 600,
                          }}
                        >
                          {learnPersp === "plaintiff"
                            ? "Plaintiff's Perspective"
                            : learnPersp === "defendant"
                              ? "Defendant's Perspective"
                              : "Judge's Perspective"}{" "}
                          · {caseTitle}
                        </div>
                        <h2
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: paperText,
                            margin: "0 0 22px",
                            lineHeight: 1.25,
                            letterSpacing: "-0.3px",
                            fontFamily: "Georgia,'Times New Roman',serif",
                          }}
                        >
                          {page.title}
                        </h2>
                        <div
                          style={{
                            height: 1,
                            background: `linear-gradient(to right,${persColor}66,${persColor}11)`,
                            marginBottom: 22,
                          }}
                        />
                        <p
                          style={{
                            fontSize: 13,
                            color: paperText,
                            lineHeight: 1.85,
                            margin: 0,
                            textAlign: "justify",
                            fontFamily: "Georgia,'Times New Roman',serif",
                          }}
                        >
                          {page.text}
                        </p>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 18,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: 9,
                            color: paperMuted,
                            fontFamily: "Georgia,'Times New Roman',serif",
                            fontStyle: "italic",
                          }}
                        >
 - {curP + 1} - 
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 18,
                            right: 24,
                            fontSize: 7,
                            fontFamily: "monospace",
                            color: paperMuted,
                            letterSpacing: 0.5,
                          }}
                        >
                          page {curP + 1} / {totalP}
                        </div>
                      </div>
                    </div>

                    {/* Controls */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 14,
                        padding: "10px 14px",
                        background: th.card,
                        borderRadius: 8,
                        border: `1px solid ${th.border}`,
                      }}
                    >
                      <button
                        onClick={() => goPage(-1)}
                        disabled={curP === 0}
                        style={{
                          padding: "7px 16px",
                          fontSize: 10,
                          fontWeight: 600,
                          background: curP === 0 ? th.surface : th.bg,
                          color: curP === 0 ? th.faint : th.text,
                          border: `1px solid ${th.border}`,
                          borderRadius: 6,
                          cursor: curP === 0 ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        ← Previous
                      </button>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {!learnPlaying ? (
                          <button
                            onClick={() => speak(page.title + ". " + page.text)}
                            style={{
                              padding: "8px 18px",
                              fontSize: 11,
                              fontWeight: 700,
                              background: `linear-gradient(135deg,${persColor},${persColor}cc)`,
                              color: "#fff",
                              border: "none",
                              borderRadius: 7,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow: `0 2px 8px ${persColor}55`,
                            }}
                          >
                            ▶ Listen
                          </button>
                        ) : (
                          <button
                            onClick={stopSpeak}
                            style={{
                              padding: "8px 18px",
                              fontSize: 11,
                              fontWeight: 700,
                              background: "#ef4444",
                              color: "#fff",
                              border: "none",
                              borderRadius: 7,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow: "0 2px 8px #ef444455",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                background: "#fff",
                                animation: "pulse 0.8s infinite",
                              }}
                            />
                            ⏹ Stop
                          </button>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 9,
                            color: th.muted,
                            fontFamily: "monospace",
                          }}
                        >
                          {Array.from({ length: totalP }).map((_, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                stopSpeak();
                                setLearnFlipDir(i > curP ? 1 : -1);
                                setLearnPage(i);
                              }}
                              style={{
                                width: i === curP ? 16 : 6,
                                height: 6,
                                borderRadius: 3,
                                background:
                                  i === curP
                                    ? persColor
                                    : i < curP
                                      ? persColor + "66"
                                      : th.border,
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                            />
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => goPage(1)}
                        disabled={curP === totalP - 1}
                        style={{
                          padding: "7px 16px",
                          fontSize: 10,
                          fontWeight: 600,
                          background: curP === totalP - 1 ? th.surface : th.bg,
                          color: curP === totalP - 1 ? th.faint : th.text,
                          border: `1px solid ${th.border}`,
                          borderRadius: 6,
                          cursor:
                            curP === totalP - 1 ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        Next →
                      </button>
                    </div>

                    <div
                      style={{
                        marginTop: 14,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 8,
                        color: th.faint,
                        padding: "0 4px",
                      }}
                    >
                      <span>
                        💡 Tip: switch perspectives at the top to read how each
                        side sees the same case
                      </span>
                      <span style={{ fontFamily: "monospace" }}>
                        Narrated audio · falls back to your browser's text-to-speech if a chapter has no recording
                      </span>
                    </div>
                  </div>
                );
              })()}
            {tab === "depo" && (
              <div
                style={{
                  maxWidth: 900,
                  margin: "0 auto",
                  padding: "16px 16px 60px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <h1
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        margin: "0 0 2px",
                      }}
                    >
                      {isIle
                        ? "🎙️ USA v. Chidambaran & Naqvi - Witness Interview"
                        : isJuul
                          ? "🎙️ Seliavski v. JUUL Labs - Live Deposition"
                          : caseId === "musk"
                            ? "🎙️ Musk v. Altman - Live Deposition"
                            : "🎙️ Neurocentria v. Mando - Live Deposition"}
                    </h1>
                    <div style={{ fontSize: 9, color: th.muted }}>
                      {isJuul
                        ? "JUUL MDL · 3:19-md-02913-WHO · N.D. Cal."
                        : "C.D. Cal. · 2:25-cv-05871"}
                      {isRecording ? " · 🔴 Live" : ""}
                    </div>
                  </div>
                  {!isRecording ? (
                    <span />
                  ) : (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: "#ef4444",
                          animation: "pulse 1.2s infinite",
                        }}
                      ></div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#ef4444",
                          }}
                        >
                          {recordingLabel || "Recording…"}
                        </div>
                        <div style={{ fontSize: 8, color: th.dim }}>
                          Live · click Stop to save
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (window._depoMR) {
                            window._depoMR.stop();
                          }
                          if (window._depoStream) {
                            window._depoStream
                              .getTracks()
                              .forEach((t) => t.stop());
                          }
                          setIsRecording(false);
                        }}
                        style={{
                          padding: "6px 14px",
                          fontSize: 10,
                          fontWeight: 700,
                          background: "#ef444418",
                          color: "#ef4444",
                          border: "1px solid #ef444433",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        ⏹ Stop + Save
                      </button>
                    </div>
                  )}
                </div>
                {isRecording && (
                  <div
                    style={{
                      background: "#ef444410",
                      border: "1px solid #ef444433",
                      borderRadius: 8,
                      padding: "8px 14px",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#ef4444",
                        animation: "pulse 1.2s infinite",
                        flexShrink: 0,
                      }}
                    ></div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: "#ef4444",
                        }}
                      >
                        {recordingLabel}
                      </div>
                      <div style={{ fontSize: 8, color: th.dim }}>
                        Deposition recording
                        {recordWitness ? " · Witness: " + recordWitness : ""} ·
                        Started {new Date().toLocaleTimeString()}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (window._depoMR) window._depoMR.stop();
                        if (window._depoStream)
                          window._depoStream
                            .getTracks()
                            .forEach((t) => t.stop());
                        setIsRecording(false);
                        if (simTimer) {
                          clearInterval(simTimer);
                          setSimTimer(null);
                        }
                        setSimTick(0);
                      }}
                      style={{
                        fontSize: 8,
                        background: "#ef444418",
                        color: "#ef4444",
                        border: "1px solid #ef444433",
                        borderRadius: 4,
                        padding: "3px 8px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      ⏹ Stop
                    </button>
                  </div>
                )}
                {showRecordSetup && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,.65)",
                      zIndex: 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={cancelRecordSetup}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: th.surface,
                        border: `1px solid ${th.border}`,
                        borderRadius: 12,
                        padding: "24px 28px",
                        width: 400,
                        boxShadow: "0 16px 48px rgba(0,0,0,.4)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background:
                              "linear-gradient(135deg,#ef4444,#dc2626)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                          }}
                        >
                          🎧
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>
                          Start Live Deposition
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: th.muted,
                          marginBottom: 18,
                          lineHeight: 1.5,
                        }}
                      >
                        Real-time transcript with contradiction detection,
                        suggested questions, and exhibit prompts.
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            marginBottom: 6,
                            color: th.dim,
                          }}
                        >
                          WITNESS
                        </div>
                        <select
                          value={recordWitness}
                          onChange={(e) => setRecordWitness(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: 10,
                            background: th.bg,
                            border: `1px solid ${th.border}`,
                            borderRadius: 6,
                            color: th.text,
                            outline: "none",
                          }}
                        >
                          <option value="">Select witness…</option>
                          {(isJuul
                            ? [
                                "James Monsees (Co-Founder)",
                                "Adam Bowen (Co-Founder)",
                                "Kevin Burns (Former CEO)",
                                "Julia Seliavski (Plaintiff)",
                                "Nicholas Pritzker (Director)",
                                "Hoyoung Huh (Director)",
                                "Riaz Valani (Director)",
                                "Altria Group Rep.",
                                "FDA Representative",
                              ]
                            : isIle
                              ? [
                                  "Tom Olivier (SPAC President)",
                                  "Bonnie Gerety",
                                  "Co-Conspirator 2 (proffer)",
                                  "Co-Conspirator 3 (proffer)",
                                  "Employee 1",
                                  "Case Agent (FBI)",
                                ]
                              : caseId === "musk"
                                ? [
                                    "Sam Altman",
                                    "Greg Brockman",
                                    "Kevin Scott (Microsoft)",
                                    "Elon Musk",
                                    "Reid Hoffman",
                                  ]
                                : [
                                    "Dr. Guosong Liu",
                                    "Dr. Fei Mao",
                                    "Wei Zhang",
                                    "Kappa Engineer Rep.",
                                  ]
                          ).map((w) => (
                            <option key={w} value={w}>
                              {w}
                            </option>
                          ))}
                          {customWits.map((w) => (
                            <option key={w.id} value={w.name}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            marginBottom: 6,
                            color: th.dim,
                          }}
                        >
                          LABEL / NOTES{" "}
                          <span style={{ fontWeight: 400, color: th.faint }}>
                            (optional)
                          </span>
                        </div>
                        <input
                          value={recordNote}
                          onChange={(e) => setRecordNote(e.target.value)}
                          placeholder="e.g. Vol. 2 - cross examination"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: 10,
                            background: th.bg,
                            border: `1px solid ${th.border}`,
                            borderRadius: 6,
                            color: th.text,
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            marginBottom: 6,
                            color: th.dim,
                          }}
                        >
                          📤 UPLOAD EXISTING AUDIO{" "}
                          <span style={{ fontWeight: 400, color: th.faint }}>
                            (optional - or record live)
                          </span>
                        </div>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 12px",
                            background: th.card,
                            border: `1px solid ${th.border}`,
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 9,
                            color: th.muted,
                          }}
                        >
                          <span style={{ fontSize: 14 }}>🎵</span> Click to
                          attach audio file (.mp3, .wav, .m4a, .webm)
                          <input
                            type="file"
                            accept=".mp3,.wav,.m4a,.webm,.ogg"
                            style={{ display: "none" }}
                          />
                        </label>
                      </div>
                      <button
                        onClick={() => {
                          const label =
                            "Depo" +
                            (recordWitness ? " - " + recordWitness : "") +
                            (recordNote ? " (" + recordNote + ")" : "");
                          setRecordingLabel(label);
                          recordReturnTabRef.current = null; setRecordType("depo");
                          setShowRecordSetup(false);
                          setIsRecording(true);
                          setSimTick(0);
                          const _t = setInterval(
                            () => setSimTick((n) => n + 1),
                            2200,
                          );
                          setSimTimer(_t);
                          toast(
                            `Recording started · ${recordWitness || "witness"}`,
                            "success",
                          );
                          if (
                            navigator.mediaDevices &&
                            navigator.mediaDevices.getUserMedia
                          ) {
                            navigator.mediaDevices
                              .getUserMedia({ audio: true })
                              .then((stream) => {
                                try {
                                  const mr = new MediaRecorder(stream);
                                  window._depoStream = stream;
                                  window._depoMR = mr;
                                  const chunks = [];
                                  mr.ondataavailable = (e) =>
                                    chunks.push(e.data);
                                  mr.onstop = () => {
                                    const blob = new Blob(chunks, {
                                      type: "audio/webm",
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download =
                                      label
                                        .replace(/[^a-z0-9]/gi, "-")
                                        .toLowerCase() + ".webm";
                                    a.click();
                                  };
                                  mr.start();
                                } catch (err) {
                                  console.warn(
                                    "MediaRecorder failed - simulation continues",
                                    err,
                                  );
                                }
                              })
                              .catch(() => {
                                toast(
                                  "Demo mode - simulation only (mic unavailable)",
                                  "warn",
                                );
                              });
                          } else {
                            toast(
                              "Demo mode - simulation only (no mic API)",
                              "warn",
                            );
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "10px",
                          fontSize: 11,
                          fontWeight: 700,
                          background: "linear-gradient(135deg,#ef4444,#dc2626)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        🔴 Start Recording
                      </button>
                      <button
                        onClick={cancelRecordSetup}
                        style={{
                          width: "100%",
                          padding: "6px",
                          fontSize: 9,
                          background: "transparent",
                          color: th.muted,
                          border: "none",
                          cursor: "pointer",
                          marginTop: 8,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      background: th.card,
                      borderRadius: 8,
                      border: `1px solid ${isRecording ? "#ef444433" : th.border}`,
                      padding: "14px 18px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700 }}>
                        {isRecording
                          ? {
                              depo: "🔴 Live Transcript",
                              attorney: "💬 Case Counsel AI",
                              interview: "🎙️ Witness Interview",
                              hearing: "⚖️ Hearing Notes",
                              call: "📋 Call Notes",
                            }[recordType] || "Live"
                          : "📝 Live Transcript"}
                      </div>
                      {isRecording ? (
                        <div
                          style={{
                            fontSize: 8,
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "#ef4444",
                              animation: "pulse 0.8s infinite",
                              display: "inline-block",
                            }}
                          ></span>
                          REC {Math.floor((simTick * 2.2) / 60)}:
                          {String(Math.round((simTick * 2.2) % 60)).padStart(
                            2,
                            "0",
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setRecordType("depo");
                            if (!recordWitness) {
                              setRecordWitness(
                                isIle
                                  ? caseId === "chid"
                                    ? "Co-Conspirator 2 (proffer)"
                                    : "Tom Olivier (SPAC President)"
                                  : isJuul
                                    ? "James Monsees (Co-Founder)"
                                    : caseId === "musk"
                                      ? "Sam Altman"
                                      : "Dr. Guosong Liu",
                              );
                            }
                            setShowRecordSetup(true);
                          }}
                          style={{
                            padding: "5px 14px",
                            fontSize: 9,
                            fontWeight: 700,
                            background: "#1e40af",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          🔴 Record
                        </button>
                      )}
                    </div>
                    {false && (
                      <div
                        style={{
                          background: th.bg,
                          borderRadius: 6,
                          padding: "20px 16px",
                          minHeight: 200,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          textAlign: "center",
                        }}
                      >
                        <div style={{ fontSize: 28, marginBottom: 4 }}>🎙️</div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: th.text,
                          }}
                        >
                          Ready to Record
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.muted,
                            lineHeight: 1.5,
                            maxWidth: 240,
                          }}
                        >
                          Tap 🔴 Record to choose a recording type and start
                          your session
                        </div>
                        <button
                          onClick={() => setShowRecordSetup(true)}
                          style={{
                            marginTop: 8,
                            padding: "8px 20px",
                            fontSize: 10,
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg,#ef4444,#dc2626)",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        >
                          🔴 Start Recording
                        </button>
                      </div>
                    )}
                    {tab === "depo" &&
                      recordType === "depo" &&
                      (() => {
                        const MAX_TICK = 12;
                        const dispTick = isRecording
                          ? Math.min(simTick, MAX_TICK)
                          : 0;
                        const lines = isJuul
                          ? [
                              {
                                tick: 1,
                                s: "Q",
                                c: "#3b82f6",
                                txt: "Mr. Monsees, you co-founded JUUL Labs in 2015?",
                              },
                              {
                                tick: 2,
                                s: "A",
                                c: "#10b981",
                                txt: "Yes, Adam Bowen and I founded the company.",
                              },
                              {
                                tick: 3,
                                s: "Q",
                                c: "#3b82f6",
                                txt: "The device delivers nicotine at 59mg/mL - correct?",
                              },
                              {
                                tick: 4,
                                s: "A",
                                c: "#10b981",
                                txt: "The product was designed for adult smokers switching from cigarettes.",
                              },
                              {
                                tick: 6,
                                s: "Q",
                                c: "#3b82f6",
                                txt: "Were you aware of the JUUL School outreach program at California high schools?",
                              },
                              {
                                tick: 7,
                                s: "A",
                                c: "#10b981",
                                txt: "I was not directly involved in field marketing operations.",
                              },
                              {
                                tick: 9,
                                s: "Q",
                                c: "#3b82f6",
                                txt: "Did you personally approve marketing materials before release?",
                              },
                              {
                                tick: 10,
                                s: "A",
                                c: "#10b981",
                                txt: "I do not recall specific approval of youth-targeted content.",
                              },
                            ]
                          : isIle
                            ? caseId === "chid"
                              ? [
                                  {
                                    tick: 1,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "You controlled the Entity 1 accounts from 2021 through 2024?",
                                  },
                                  {
                                    tick: 2,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "I had signature authority, yes.",
                                  },
                                  {
                                    tick: 3,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Every transfer in the $145 million the government charts moved through accounts you controlled?",
                                  },
                                  {
                                    tick: 4,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Most of them. I executed transfers when instructed.",
                                  },
                                  {
                                    tick: 5,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Instructed in writing?",
                                  },
                                  {
                                    tick: 6,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Sometimes. Often by phone or in person.",
                                  },
                                  {
                                    tick: 7,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "So for those, the only record of who instructed you is your memory?",
                                  },
                                  {
                                    tick: 8,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "My recollection, yes.",
                                  },
                                  {
                                    tick: 9,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "You are testifying under a cooperation agreement, and your sentence turns on a motion only the government can make?",
                                  },
                                  {
                                    tick: 10,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "I hope for a reduction, yes.",
                                  },
                                  {
                                    tick: 11,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "The September 2024 deletions ran through whose account?",
                                  },
                                  {
                                    tick: 12,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Mine. I told the government what I remember about who asked.",
                                  },
                                ]
                              : [
                                  {
                                    tick: 1,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Mr. Olivier, what was your role in the 2024 transaction?",
                                  },
                                  {
                                    tick: 2,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "President of the SPAC. I led the diligence - about eighteen months end to end.",
                                  },
                                  {
                                    tick: 3,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "What did the data room show about Entity 1?",
                                  },
                                  {
                                    tick: 4,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "A master channel-partner agreement and receivables schedules. The auditor's confirmations tied to those schedules.",
                                  },
                                  {
                                    tick: 5,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Were you shown a December 2023 revenue breakdown by customer?",
                                  },
                                  {
                                    tick: 6,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Summary schedules, yes. Customer-level fabrication - no. If $227 million was fake, it did not read that way in what I reviewed.",
                                  },
                                  {
                                    tick: 7,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "After the August 2024 short report, what did Mr. Naqvi say to you?",
                                  },
                                  {
                                    tick: 8,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "That he wanted the numbers re-verified and the lenders kept current. He never asked me to shade anything.",
                                  },
                                  {
                                    tick: 9,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "You later bought the company's assets out of bankruptcy?",
                                  },
                                  {
                                    tick: 10,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Through AAI Solutions, yes. And I kept working with Farhan. In my dealings he was straight with me.",
                                  },
                                  {
                                    tick: 11,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Did anyone ever ask you to delete or withhold documents?",
                                  },
                                  {
                                    tick: 12,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Never. Not Farhan, not anyone.",
                                  },
                                ]
                            : caseId === "musk"
                              ? [
                                  {
                                    tick: 1,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Mr. Altman, did you draft the May 2015 founding email describing OpenAI as a non-profit AI lab?",
                                  },
                                  {
                                    tick: 2,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Yes, I sent the email. It described our research mission.",
                                  },
                                  {
                                    tick: 3,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Did you tell Mr. Musk that 'tech belongs to the world' between 2015 and 2017?",
                                  },
                                  {
                                    tick: 4,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "I always characterized OpenAI as a research organization, not a charity.",
                                  },
                                  {
                                    tick: 6,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Did you communicate with Microsoft about donor representations prior to the 2019 investment?",
                                  },
                                  {
                                    tick: 7,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Microsoft did its own due diligence on donor representations.",
                                  },
                                  {
                                    tick: 9,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Are you aware of Brockman's journal entry calling the LP 'wrong to steal non-profit from Elon'?",
                                  },
                                  {
                                    tick: 10,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Brockman's journal was personal speculation. It was not an admission.",
                                  },
                                ]
                              : [
                                  {
                                    tick: 1,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Dr. Liu, when did you first synthesize magnesium L-threonate?",
                                  },
                                  {
                                    tick: 2,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "I personally synthesized the compound first, in 1999 at MIT.",
                                  },
                                  {
                                    tick: 3,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Was Dr. Fei Mao involved in the synthesis?",
                                  },
                                  {
                                    tick: 4,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "Mao performed the actual synthesis under my direction.",
                                  },
                                  {
                                    tick: 6,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Were you aware of CN1200366A - the 1997 Chinese patent - at filing?",
                                  },
                                  {
                                    tick: 7,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "I did not know of any prior art in 1997. I believed our work was novel.",
                                  },
                                  {
                                    tick: 9,
                                    s: "Q",
                                    c: "#3b82f6",
                                    txt: "Does the spec's reference to 'in vivo formation' admit natural formation?",
                                  },
                                  {
                                    tick: 10,
                                    s: "A",
                                    c: "#10b981",
                                    txt: "The specification does not admit natural formation. It describes our manufactured compound.",
                                  },
                                ];
                        const visible = lines.filter((e) => e.tick <= dispTick);
                        return (
                          <div
                            style={{
                              background: th.bg,
                              borderRadius: 6,
                              padding: 12,
                              fontFamily: "monospace",
                              fontSize: 9,
                              lineHeight: 1.9,
                              minHeight: 200,
                              maxHeight: 340,
                              overflowY: "auto",
                            }}
                          >
                            {visible.length === 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: 180,
                                  gap: 10,
                                  color: th.faint,
                                  textAlign: "center",
                                  padding: "20px",
                                }}
                              >
                                <div style={{ fontSize: 32, opacity: 0.4 }}>
                                  🎙️
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: th.muted,
                                    fontFamily: "system-ui,sans-serif",
                                  }}
                                >
                                  Press 🔴 Record to begin live deposition
                                </div>
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: th.faint,
                                    fontFamily: "system-ui,sans-serif",
                                    lineHeight: 1.55,
                                    maxWidth: 280,
                                  }}
                                >
                                  Transcript builds line-by-line. Suggested
                                  questions, exhibits, and inconsistencies
                                  appear as the witness answers.
                                </div>
                              </div>
                            ) : (
                              visible.map((e, i) => {
                                const isLast =
                                  i === visible.length - 1 &&
                                  e.tick === dispTick;
                                return (
                                  <div
                                    key={e.tick}
                                    style={{
                                      marginBottom: 5,
                                      animation: isLast
                                        ? "slideIn 0.5s ease"
                                        : "none",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: e.c,
                                        fontWeight: 700,
                                        marginRight: 6,
                                      }}
                                    >
                                      {e.s}:
                                    </span>
                                    <span style={{ color: th.text }}>
                                      {e.txt}
                                    </span>
                                    {isLast && isRecording && (
                                      <span
                                        style={{
                                          display: "inline-block",
                                          fontSize: 6,
                                          color: "#10b981",
                                          background: "#10b98118",
                                          border: "1px solid #10b98144",
                                          padding: "1px 5px",
                                          borderRadius: 6,
                                          marginLeft: 6,
                                          fontWeight: 700,
                                          letterSpacing: 0.4,
                                          verticalAlign: "middle",
                                          fontFamily: "system-ui,sans-serif",
                                          animation: "pulse 1s 3",
                                        }}
                                      >
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                            {isRecording &&
                              visible.length > 0 &&
                              dispTick < MAX_TICK && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginTop: 8,
                                    color: "#ef4444",
                                    fontSize: 8,
                                    fontFamily: "system-ui,sans-serif",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 5,
                                      height: 5,
                                      borderRadius: "50%",
                                      background: "#ef4444",
                                      animation: "pulse 0.8s infinite",
                                    }}
                                  />
                                  <span style={{ opacity: 0.85 }}>
                                    Listening
                                    <span
                                      style={{
                                        display: "inline-block",
                                        animation: "pulse 1.5s infinite",
                                      }}
                                    >
                                      …
                                    </span>
                                  </span>
                                </div>
                              )}
                          </div>
                        );
                      })()}
                    {tab === "depo" && recordType === "bounce" && (
                      <div
                        style={{
                          background: th.bg,
                          borderRadius: 6,
                          padding: 12,
                          fontSize: 9,
                          lineHeight: 1.7,
                          minHeight: 200,
                          maxHeight: 340,
                          overflowY: "auto",
                        }}
                      >
                        {[
                          {
                            t: 0,
                            who: "you",
                            txt: "I'm thinking through remand strategy post-SJ. If Orrick loses on causation, Seliavski goes to Virginia.",
                          },
                          {
                            t: 1,
                            who: "ai",
                            txt: "Virginia is actually favorable for plaintiff - no punitive caps like California. Flag to client.",
                          },
                          {
                            t: 2,
                            who: "you",
                            txt: "Meta production deadline is May 11.",
                          },
                          {
                            t: 3,
                            who: "ai",
                            txt: "Dkt.4497 moved it to May 8. If Meta misses it you have a clean FRCP 37 motion - draft it and have it ready May 9.",
                          },
                          {
                            t: 4,
                            who: "you",
                            txt: "Monsees depo went well. He denied knowing about JUUL School.",
                          },
                          {
                            t: 5,
                            who: "ai",
                            txt: "Contradicted by Dkt.3447 SOF ¶47 - he was on the distribution list for youth metrics. That's your impeachment exhibit. Pair with Buzz campaign emails.",
                          },
                          {
                            t: 6,
                            who: "you",
                            txt: "I'm worried about the damages expert timeline.",
                          },
                          {
                            t: 7,
                            who: "ai",
                            txt: "Seliavski has no medical causation expert. No expert by remand = SJ almost certain in Virginia. Is there a Daubert deadline in the schedule?",
                          },
                        ]
                          .filter((e) => e.t <= (isRecording ? simTick % 9 : 4))
                          .map((e, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                gap: 8,
                                marginBottom: 10,
                                alignItems: "flex-start",
                              }}
                            >
                              {e.who === "ai" ? (
                                <div
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    background:
                                      "linear-gradient(135deg,#1e40af,#a78bfa)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 8,
                                    fontWeight: 700,
                                    color: "#fff",
                                    flexShrink: 0,
                                  }}
                                >
                                  AI
                                </div>
                              ) : (
                                <div
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    background: th.surface,
                                    border: `1px solid ${th.border}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 9,
                                    flexShrink: 0,
                                  }}
                                >
                                  👤
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontWeight: 600,
                                    color: e.who === "ai" ? "#a78bfa" : th.dim,
                                    marginBottom: 2,
                                  }}
                                >
                                  {e.who === "ai"
                                    ? "🧠 Case Counsel AI"
                                    : "You"}
                                </div>
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: th.text,
                                    lineHeight: 1.5,
                                    background:
                                      e.who === "ai"
                                        ? "#a78bfa0a"
                                        : "transparent",
                                    padding: e.who === "ai" ? "6px 8px" : 0,
                                    borderRadius: e.who === "ai" ? 6 : 0,
                                  }}
                                >
                                  {e.txt}
                                </div>
                              </div>
                            </div>
                          ))}
                        <div
                          style={{
                            fontSize: 8,
                            color: "#a78bfa",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          💬 Case Counsel AI is listening…
                        </div>
                      </div>
                    )}
                    {tab === "depo" && recordType === "interview" && (
                      <div
                        style={{
                          background: th.bg,
                          borderRadius: 6,
                          padding: 12,
                          fontSize: 9,
                          minHeight: 200,
                          maxHeight: 340,
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            background: th.card,
                            borderRadius: 6,
                            padding: "8px 12px",
                            marginBottom: 10,
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            border: "1px solid #3b82f633",
                          }}
                        >
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg,#3b82f6,#1e40af)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              flexShrink: 0,
                            }}
                          >
                            👤
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: th.text,
                              }}
                            >
                              {recordWitness || "Witness"}
                            </div>
                            <div style={{ fontSize: 8, color: th.dim }}>
                              Interview ·{" "}
                              {isIle
                                ? "USA v. Chidambaran & Naqvi"
                                : isJuul
                                  ? "JUUL MDL"
                                  : caseId === "musk"
                                    ? "Musk v. Altman"
                                    : "Neurocentria v. Mando"}
                            </div>
                          </div>
                        </div>
                        {[
                          {
                            t: 0,
                            ts: "00:00",
                            who: "Atty",
                            f: false,
                            txt: "Please state your full name and role.",
                          },
                          {
                            t: 1,
                            ts: "00:14",
                            who: "Witness",
                            f: false,
                            txt: isJuul
                              ? "Julia Seliavski. I am the named plaintiff."
                              : "Dr. Guosong Liu, named inventor.",
                          },
                          {
                            t: 2,
                            ts: "00:31",
                            who: "AI",
                            f: "admission",
                            txt: isJuul
                              ? "⭐ KEY ADMISSION: Identity confirmed on record."
                              : "⭐ KEY FACT: Named inventor confirmed.",
                          },
                          {
                            t: 3,
                            ts: "01:02",
                            who: "Atty",
                            f: false,
                            txt: isJuul
                              ? "When did you first use JUUL products?"
                              : "Were you first to synthesize magnesium L-threonate?",
                          },
                          {
                            t: 4,
                            ts: "01:18",
                            who: "Witness",
                            f: false,
                            txt: isJuul
                              ? "July 2017. I was 16 at the time."
                              : "No. Dr. Mao performed the synthesis.",
                          },
                          {
                            t: 5,
                            ts: "01:35",
                            who: "AI",
                            f: "flag",
                            txt: isJuul
                              ? "⚡ AGE FLAG: 16 at first use - key for youth exposure damages."
                              : "⚡ CONTRADICTION: Patent names Liu as sole inventor. See Ex.4.",
                          },
                          {
                            t: 6,
                            ts: "02:10",
                            who: "Atty",
                            f: false,
                            txt: isJuul
                              ? "Did anyone say JUUL was safe or not addictive?"
                              : "Did you know of prior art before filing?",
                          },
                          {
                            t: 7,
                            ts: "02:28",
                            who: "Witness",
                            f: false,
                            txt: isJuul
                              ? "A friend said it was just flavored vapor."
                              : "I believed our work was novel.",
                          },
                        ]
                          .filter((e) => e.t <= (isRecording ? simTick % 9 : 4))
                          .map((e, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                gap: 8,
                                marginBottom: 6,
                                alignItems: "flex-start",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.faint,
                                  flexShrink: 0,
                                  width: 34,
                                }}
                              >
                                {e.ts}
                              </div>
                              {e.who === "AI" ? (
                                <div
                                  style={{
                                    flex: 1,
                                    background:
                                      e.f === "admission"
                                        ? "#f59e0b15"
                                        : "#ef444415",
                                    border:
                                      "1px solid " +
                                      (e.f === "admission"
                                        ? "#f59e0b33"
                                        : "#ef444433"),
                                    borderRadius: 4,
                                    padding: "4px 8px",
                                    fontSize: 8,
                                    color:
                                      e.f === "admission"
                                        ? "#f59e0b"
                                        : "#ef4444",
                                  }}
                                >
                                  {e.txt}
                                </div>
                              ) : (
                                <div style={{ flex: 1 }}>
                                  <span
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 700,
                                      color:
                                        e.who === "Atty" ? "#3b82f6" : th.text,
                                      marginRight: 5,
                                    }}
                                  >
                                    {e.who}:
                                  </span>
                                  <span style={{ fontSize: 9, color: th.text }}>
                                    {e.txt}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                    {tab === "depo" && recordType === "hearing" && (
                      <div
                        style={{
                          background: th.bg,
                          borderRadius: 6,
                          padding: 12,
                          fontSize: 9,
                          minHeight: 200,
                          maxHeight: 340,
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg,#0f172a,#1e293b)",
                            borderRadius: 6,
                            padding: "10px 14px",
                            marginBottom: 10,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#f8fafc",
                              }}
                            >
                              {isJuul
                                ? "Judge William H. Orrick · Courtroom 2"
                                : "Judge Aenlle-Rocha · Courtroom 540"}
                            </div>
                            <div style={{ fontSize: 8, color: "#94a3b8" }}>
                              {isJuul
                                ? "N.D. Cal. · 450 Golden Gate Ave SF"
                                : "C.D. Cal. · 350 W 1st St LA"}
                            </div>
                          </div>
                        </div>
                        {[
                          {
                            t: 0,
                            who: "COURT",
                            c: "#f59e0b",
                            f: false,
                            txt: isJuul
                              ? "This is the SJ hearing on causation. Defense, proceed."
                              : "This is the MTD hearing. Defense, proceed.",
                          },
                          {
                            t: 1,
                            who: "DEF.",
                            c: "#3b82f6",
                            f: false,
                            txt: isJuul
                              ? "Your Honor, plaintiff has no causation expert."
                              : "The complaint fails to identify any infringement.",
                          },
                          {
                            t: 2,
                            who: "AI",
                            c: "#a78bfa",
                            f: true,
                            txt: isJuul
                              ? "⚡ SIGNAL: Watch for Rule 56(d) - plaintiff may argue discovery needed."
                              : "⚡ FLAG: Prepare literal infringement + doctrine of equivalents.",
                          },
                          {
                            t: 3,
                            who: "COURT",
                            c: "#f59e0b",
                            f: false,
                            txt: isJuul
                              ? "What is plaintiff's position on experts?"
                              : "How do you respond to claim construction?",
                          },
                          {
                            t: 4,
                            who: "PL.",
                            c: "#10b981",
                            f: false,
                            txt: isJuul
                              ? "We have identified an expert and will disclose post-remand."
                              : "Column 4, lines 12-18 supports our construction.",
                          },
                          {
                            t: 5,
                            who: "AI",
                            c: "#a78bfa",
                            f: true,
                            txt: isJuul
                              ? "⚡ KEY: Post-remand expert disclosure - judge may condition ruling."
                              : "⚡ CITE: Phillips v. AWH (Fed. Cir. 2005).",
                          },
                          {
                            t: 6,
                            who: "COURT",
                            c: "#f59e0b",
                            f: false,
                            txt: isJuul
                              ? "Under submission. Ruling within 30 days."
                              : "Brief recess.",
                          },
                        ]
                          .filter((e) => e.t <= (isRecording ? simTick % 8 : 4))
                          .map((e, i) => (
                            <div
                              key={i}
                              style={{
                                marginBottom: 8,
                                padding: e.f ? "4px 8px" : 0,
                                background: e.f ? "#a78bfa12" : "transparent",
                                borderLeft: e.f ? "2px solid #a78bfa" : "none",
                                borderRadius: e.f ? 4 : 0,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 700,
                                  color: e.c,
                                  marginBottom: 2,
                                }}
                              >
                                {e.who}
                              </div>
                              <div
                                style={{
                                  fontSize: 9,
                                  color: e.f ? "#c4b5fd" : th.text,
                                  lineHeight: 1.5,
                                }}
                              >
                                {e.txt}
                              </div>
                            </div>
                          ))}
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 8,
                            color: "#f59e0b",
                          }}
                        >
                          ⚖️ AI flagging judicial signals
                        </div>
                      </div>
                    )}
                    {tab === "depo" && recordType === "call" && (
                      <div
                        style={{
                          background: th.bg,
                          borderRadius: 6,
                          padding: 12,
                          fontSize: 9,
                          minHeight: 200,
                          maxHeight: 340,
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 10,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: th.text,
                              }}
                            >
                              {recordNote || "Strategy Call"}
                            </div>
                            <div style={{ fontSize: 8, color: th.dim }}>
                              {isIle
                                ? "1:26-cr-00097-NGG · Pretrial"
                                : isJuul
                                  ? "JUUL MDL · Seliavski PI"
                                  : caseId === "musk"
                                    ? "Musk v. Altman · Trial"
                                    : "Neurocentria v. Mando"}{" "}
                              · {new Date().toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 6,
                              padding: "8px 10px",
                              border: "1px solid #3b82f633",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#3b82f6",
                                marginBottom: 6,
                              }}
                            >
                              📝 Auto-Notes
                            </div>
                            {[
                              { t: 0, txt: "Remand strategy post-SJ" },
                              { t: 1, txt: "Seliavski: no causation expert" },
                              { t: 2, txt: "Meta deadline May 8" },
                              {
                                t: 3,
                                txt: "Monsees: Dkt.3447 ¶47 impeachment",
                              },
                              {
                                t: 4,
                                txt: "Virginia favorable for PI plaintiff",
                              },
                              { t: 5, txt: "NMD amendment before remand" },
                            ]
                              .filter(
                                (e) => e.t <= (isRecording ? simTick % 7 : 4),
                              )
                              .map((e, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    gap: 5,
                                    marginBottom: 4,
                                    fontSize: 8,
                                    color: th.muted,
                                  }}
                                >
                                  <span style={{ color: "#3b82f6" }}>·</span>
                                  {e.txt}
                                </div>
                              ))}
                          </div>
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 6,
                              padding: "8px 10px",
                              border: "1px solid #10b98133",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#10b981",
                                marginBottom: 6,
                              }}
                            >
                              ✅ Action Items
                            </div>
                            {[
                              {
                                t: 1,
                                txt: "Retain causation expert",
                                own: "Lead",
                              },
                              {
                                t: 2,
                                txt: "Draft FRCP 37 - ready May 9",
                                own: "Assoc.",
                              },
                              {
                                t: 3,
                                txt: "Pull Dkt.3447 ¶47 binder",
                                own: "Para.",
                              },
                              {
                                t: 4,
                                txt: "Calendar CBF motion Q4",
                                own: "Billing",
                              },
                              {
                                t: 5,
                                txt: "Review Dkt.4094 for remand",
                                own: "Lead",
                              },
                            ]
                              .filter(
                                (e) => e.t <= (isRecording ? simTick % 7 : 4),
                              )
                              .map((e, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    gap: 6,
                                    marginBottom: 5,
                                    fontSize: 8,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <span style={{ fontSize: 9, flexShrink: 0 }}>
                                    ☐
                                  </span>
                                  <div>
                                    <div style={{ color: th.text }}>
                                      {e.txt}
                                    </div>
                                    <div
                                      style={{ color: th.faint, fontSize: 7 }}
                                    >
                                      {e.own}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div style={{ fontSize: 8, color: "#a78bfa" }}>
                          🤖 AI extracting action items in real time
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    {(() => {
                      const MAX_TICK = 12;
                      const dispTick = isRecording
                        ? Math.min(simTick, MAX_TICK)
                        : 0;
                      const isNew = (t) =>
                        isRecording && t > 0 && dispTick - t <= 2;
                      const depoData = isJuul
                        ? {
                            witness: "James Monsees",
                            witnessRole: "Co-Founder, JUUL Labs",
                            questions: [
                              {
                                tick: 0,
                                text: "Were you on the distribution list for Q3 2018 youth-cohort engagement metrics? (Dkt.3447 ¶47)",
                              },
                              {
                                tick: 0,
                                text: "Did you personally approve the 'Buzz' marketing campaign launched April 2018?",
                              },
                              {
                                tick: 0,
                                text: "When did you first learn JUUL pods contained 59mg/mL nicotine - higher than any competitor?",
                              },
                              {
                                tick: 5,
                                text: "Follow-up: What was the median age of your 'novice' user cohort, and how was 'novice' measured?",
                              },
                              {
                                tick: 8,
                                text: "Follow-up: Then who WAS responsible for field marketing in 2018, and who reported to you about it?",
                              },
                              {
                                tick: 11,
                                text: "Follow-up: Show witness PX-Buzz-12 - does this campaign approval refresh your recollection?",
                              },
                            ],
                            exhibits: [
                              {
                                tick: 0,
                                id: "PX-3447",
                                label:
                                  "Dkt.3447 SOF ¶47 - Youth metrics distribution list",
                                why: "Names Monsees on the recipient list",
                              },
                              {
                                tick: 0,
                                id: "PX-2891",
                                label:
                                  "Sept 2018 board deck (unsealed Dkt.3393)",
                                why: "Lists JUUL School outreach by school",
                              },
                              {
                                tick: 5,
                                id: "PX-FDA-MDO",
                                label: "FDA MDO June 2022 (since stayed)",
                                why: "Cited 59mg/mL - impeach 'designed for adult smokers'",
                              },
                              {
                                tick: 8,
                                id: "PX-Buzz-12",
                                label:
                                  "April 2018 'Buzz' campaign approval chain",
                                why: "Monsees' email signature on approval - impeaches 'not in field marketing'",
                              },
                              {
                                tick: 11,
                                id: "PX-INSTA-04",
                                label:
                                  "Instagram targeting parameters (Meta production May 8, 2026)",
                                why: "Cross on youth-targeted content denial",
                              },
                            ],
                            thisDepo: [
                              {
                                tick: 5,
                                at: "00:14:22",
                                said: '"Designed for adult smokers switching from cigarettes."',
                                vs: "At 00:11:05 he described the target as 'novice nicotine users.' Adult smokers are by definition not novice.",
                              },
                              {
                                tick: 8,
                                at: "00:18:55",
                                said: '"Not directly involved in field marketing operations."',
                                vs: "At 00:02:31 he stated he 'reviewed all major marketing initiatives' through 2018.",
                              },
                              {
                                tick: 11,
                                at: "00:24:40",
                                said: '"I do not recall specific approval of youth-targeted content."',
                                vs: "At 00:09:12 he said he 'personally signed off on every campaign before launch.'",
                              },
                            ],
                            caseRecord: [
                              {
                                tick: 5,
                                at: "00:14:22",
                                said: '"Designed for adult smokers"',
                                contradiction:
                                  "April 2018 'Buzz' campaign emails (PX-Buzz-12) describe target as 'youth-adjacent novice users.'",
                              },
                              {
                                tick: 8,
                                at: "00:18:55",
                                said: '"Not directly in field marketing"',
                                contradiction:
                                  "Dkt.3447 ¶47 places Monsees on the youth-metrics distribution list (Q3 2018).",
                              },
                              {
                                tick: 11,
                                at: "00:24:40",
                                said: '"No recall of youth content approval"',
                                contradiction:
                                  "Sept 2018 board deck (Dkt.3393) lists JUUL School by school district - Monsees attended the meeting.",
                              },
                            ],
                          }
                        : isIle
                          ? caseId === "chid"
                            ? {
                                witness: "Co-Conspirator 2 (proffer)",
                                witnessRole:
                                  "Former iLearning VP - Entity 1 signatory - cooperating witness",
                                questions: [
                                  {
                                    tick: 0,
                                    text: "Lock account control first: sole signature authority on every Entity 1 account, 2021 through 2024.",
                                  },
                                  {
                                    tick: 0,
                                    text: "Writings versus memory: force the split early - which instructions exist on paper?",
                                  },
                                  {
                                    tick: 4,
                                    text: "He executed every charged transfer. Establish agency: his hands, his logins, his approvals.",
                                  },
                                  {
                                    tick: 7,
                                    text: "For phone-only directives: exact words, date, who else was present, any contemporaneous note. Lock the absence.",
                                  },
                                  {
                                    tick: 9,
                                    text: "5K1.1 mechanics: the motion belongs to the government alone - build the bias-instruction record.",
                                  },
                                  {
                                    tick: 11,
                                    text: "Deletions ran through his account, in September 2024. Pin the chronology against any post-subpoena directive he attributes to the CEO.",
                                  },
                                ],
                                exhibits: [
                                  {
                                    tick: 0,
                                    id: "S-1 ¶27",
                                    label:
                                      "$145M round-trip chart - his accounts at the center",
                                    why: "Every wire is his hand; agency cuts both ways",
                                  },
                                  {
                                    tick: 4,
                                    id: "S-1 ¶¶45, 56",
                                    label: "Alleged deletion directives",
                                    why: "Pin each to a date, a channel, and a writing - or to memory alone",
                                  },
                                  {
                                    tick: 9,
                                    id: "Cooperation Agreement",
                                    label: "3500 material - proffer plus agreement",
                                    why: "Sentence exposure and the government-only motion; bias foundation",
                                  },
                                  {
                                    tick: 11,
                                    id: "Committee Forensic Scope",
                                    label: "Deletion attribution methodology",
                                    why: "Whose device, whose account, what metadata - before his narrative fills the gaps",
                                  },
                                ],
                                thisDepo: [
                                  {
                                    tick: 8,
                                    at: "00:16:40",
                                    said: "“My recollection, yes.”",
                                    vs: "At 00:10:12 he said instructions were sometimes written. Demand every writing; anything unproduced becomes a 3500/Brady letter by Friday.",
                                  },
                                  {
                                    tick: 12,
                                    at: "00:24:55",
                                    said: "“What I remember about who asked.”",
                                    vs: "Memory is the entire 11s case. He conceded phone-only directives and no contemporaneous notes - lock it before redirect rehabilitates.",
                                  },
                                ],
                                caseRecord: [
                                  {
                                    tick: 4,
                                    at: "00:09:05",
                                    said: "“I executed transfers when instructed.”",
                                    contradiction:
                                      "S-1 ¶27 puts the full $145M through accounts he alone controlled. The instructed framing is his shield and the defense’s sword.",
                                  },
                                  {
                                    tick: 10,
                                    at: "00:21:15",
                                    said: "“I hope for a reduction.”",
                                    contradiction:
                                      "USSG §5K1.1 - only the government moves. Queue the cooperating-witness bias instruction and the sentencing-exposure math.",
                                  },
                                ],
                              }
                            : {
                                witness: "Tom Olivier (SPAC President)",
                                witnessRole:
                                  "SPAC President / Interim CEO / AAI Solutions - defense-friendly",
                                questions: [
                                  {
                                    tick: 0,
                                    text: "Lock the diligence scope first: duration, data room contents, who ran confirmations.",
                                  },
                                  {
                                    tick: 0,
                                    text: "Establish the auditor channel: confirmations ran auditor-to-customer, not through the SPAC.",
                                  },
                                  {
                                    tick: 4,
                                    text: "Pin Entity 1 paper: master agreement plus receivables schedules - what more would a reasonable diligence see?",
                                  },
                                  {
                                    tick: 6,
                                    text: "Follow-up: would $227M of customer-level fabrication have been visible in what he reviewed? Get the no on the record.",
                                  },
                                  {
                                    tick: 8,
                                    text: "Post-report conduct: re-verify and keep lenders current - the anti-concealment instruction, from Naqvi himself.",
                                  },
                                  {
                                    tick: 11,
                                    text: "Close on deletions: nobody ever asked him to delete or withhold. Clean, short, done.",
                                  },
                                ],
                                exhibits: [
                                  {
                                    tick: 0,
                                    id: "Dkt.17-Ex.1",
                                    label:
                                      "Play-ball chain (full) - produced by Naqvi",
                                    why: "Completeness shield before any fragment surfaces",
                                  },
                                  {
                                    tick: 4,
                                    id: "S-1 ¶34",
                                    label:
                                      "Dec 17 2023 diligence email - fake revenue by customer",
                                    why: "Test what the diligence layer could actually see",
                                  },
                                  {
                                    tick: 8,
                                    id: "Dkt.17 at 8-9",
                                    label:
                                      "Committee forensic finding - deletions appear to be spam",
                                    why: "Kill obstruction theming before the government opens it",
                                  },
                                  {
                                    tick: 10,
                                    id: "Ch.7 Sale Order",
                                    label: "AAI Solutions asset purchase",
                                    why: "The buyer’s verdict - the most informed party kept working with Naqvi",
                                  },
                                ],
                                thisDepo: [
                                  {
                                    tick: 6,
                                    at: "00:13:10",
                                    said: "“Customer-level fabrication - no.”",
                                    vs: "At 00:03:40 he called the diligence comprehensive. The government will bridge comprehensive to should-have-seen. Prep the scope answer: confirmations ran auditor-to-customer, outside the SPAC lane.",
                                  },
                                  {
                                    tick: 10,
                                    at: "00:22:05",
                                    said: "“He was straight with me.”",
                                    vs: "Opinion drift - expect an FRE 602/701 objection. Anchor to specific dealings (purchase negotiation, transition services) before the characterization.",
                                  },
                                ],
                                caseRecord: [
                                  {
                                    tick: 4,
                                    at: "00:08:45",
                                    said: "“Confirmations tied to those schedules.”",
                                    contradiction:
                                      "S-1 ¶28: Lender A found no trace of Entity 2 online. Cross will ask what a reasonable review surfaces - have the auditor-lane answer ready.",
                                  },
                                  {
                                    tick: 8,
                                    at: "00:17:30",
                                    said: "“Keep the lenders current.”",
                                    contradiction:
                                      "¶¶46-48 allege staged post-report transactions. The Oct 11 Lender B approval texts (Count TEN) are the answer: the lender saw and approved.",
                                  },
                                ],
                              }
                          : caseId === "musk"
                            ? {
                                witness: "Sam Altman",
                                witnessRole: "CEO, OpenAI",
                                questions: [
                                  {
                                    tick: 0,
                                    text: "Did you draft or send the May 2015 founding email describing OpenAI as a non-profit AI lab? (PX-003)",
                                  },
                                  {
                                    tick: 0,
                                    text: "Did you tell Mr. Musk 'tech belongs to the world' between 2015 and 2017?",
                                  },
                                  {
                                    tick: 0,
                                    text: "Why did you tell Musk in March 2019 to 'tell them I'm supportive'? (DX-862)",
                                  },
                                  {
                                    tick: 5,
                                    text: "Follow-up: Your May 2015 email used 'belongs to the world' - is that not charity language?",
                                  },
                                  {
                                    tick: 8,
                                    text: "Follow-up: Did Microsoft ask you to characterize donor terms, and what did you tell them?",
                                  },
                                  {
                                    tick: 11,
                                    text: "Follow-up: Was Brockman's journal entry written before or after litigation was contemplated?",
                                  },
                                ],
                                exhibits: [
                                  {
                                    tick: 0,
                                    id: "PX-003",
                                    label:
                                      "Altman founding email (May 2015) - 'tech belongs to the world'",
                                    why: "Charitable mission representation",
                                  },
                                  {
                                    tick: 0,
                                    id: "DX-862",
                                    label:
                                      "'Tell them I'm supportive' (Mar 2019) [defense]",
                                    why: "Anticipate acquiescence defense",
                                  },
                                  {
                                    tick: 5,
                                    id: "PX-003-q",
                                    label:
                                      "PX-003 - exact quote 'belongs to the world'",
                                    why: "Direct refutation of 'not a charity' claim",
                                  },
                                  {
                                    tick: 8,
                                    id: "PX-230",
                                    label:
                                      "Kevin Scott (MS CTO) email March 2018",
                                    why: "MS CTO questioned donor awareness 16 months pre-investment",
                                  },
                                  {
                                    tick: 11,
                                    id: "PX-163",
                                    label:
                                      "Brockman journal entry - full text with date stamp",
                                    why: "Contemporaneous, pre-litigation, FRE 801(d)(2)(D) admissible",
                                  },
                                ],
                                thisDepo: [
                                  {
                                    tick: 5,
                                    at: "00:09:42",
                                    said: '"I always characterized OpenAI as a research organization, not a charity."',
                                    vs: "At 00:04:18 he confirmed sending the May 2015 founding email which used 'tech belongs to the world' - that is charity language.",
                                  },
                                  {
                                    tick: 8,
                                    at: "00:17:30",
                                    said: '"Microsoft did its own due diligence on donor representations."',
                                    vs: "At 00:13:55 he said OpenAI 'provided full transparency' to Microsoft. Both can't be true.",
                                  },
                                  {
                                    tick: 11,
                                    at: "00:22:11",
                                    said: '"Brockman journal was personal speculation."',
                                    vs: "At 00:21:00 he agreed Brockman wrote the entry contemporaneously, before any litigation was contemplated.",
                                  },
                                ],
                                caseRecord: [
                                  {
                                    tick: 5,
                                    at: "00:09:42",
                                    said: '"Not a charity - research organization"',
                                    contradiction:
                                      "PX-003 (May 2015): Altman's own email said 'tech belongs to the world.' OpenAI's IRS Form 1023 sought 501(c)(3) status.",
                                  },
                                  {
                                    tick: 8,
                                    at: "00:17:30",
                                    said: '"Microsoft did its own due diligence"',
                                    contradiction:
                                      "PX-230 (Mar 2018): Kevin Scott (MS CTO) wrote internally questioning whether donors were aware - 16 months pre-investment, suggesting MS lacked clarity from OpenAI.",
                                  },
                                  {
                                    tick: 11,
                                    at: "00:22:11",
                                    said: '"Brockman journal was speculation"',
                                    contradiction:
                                      "PX-163 was contemporaneous, written before any litigation, by the co-founder - admissible against the entity under FRE 801(d)(2)(D).",
                                  },
                                ],
                              }
                            : {
                                witness: "Dr. Guosong Liu",
                                witnessRole:
                                  "Named Co-Inventor (with Mao), '061 Patent",
                                questions: [
                                  {
                                    tick: 0,
                                    text: "Dr. Liu, when did you first synthesize magnesium L-threonate?",
                                  },
                                  {
                                    tick: 0,
                                    text: "Were you aware of CN1200366A (1997 Chinese patent) at filing?",
                                  },
                                  {
                                    tick: 0,
                                    text: "Did MIT's Technology Licensing Office disclose any prior art?",
                                  },
                                  {
                                    tick: 5,
                                    text: "Follow-up: Dr. Mao's 2014 YouTube credits Slutsky for the discovery in your MIT lab. Why isn't she a named inventor?",
                                  },
                                  {
                                    tick: 8,
                                    text: "Follow-up: Have you reviewed your MIT lab's literature search records from 1999?",
                                  },
                                  {
                                    tick: 11,
                                    text: "Follow-up: Read column 4, lines 12-18 aloud - does it not describe in vivo formation?",
                                  },
                                ],
                                exhibits: [
                                  {
                                    tick: 0,
                                    id: "Ex.4",
                                    label: "CN1200366A (1997 Chinese Patent)",
                                    why: "Anticipates Claim 1",
                                  },
                                  {
                                    tick: 0,
                                    id: "Ex.5",
                                    label:
                                      "Liu Deposition Transcript (Feb 17, 2026)",
                                    why: "Cross with prior testimony",
                                  },
                                  {
                                    tick: 5,
                                    id: "Ex.6",
                                    label: "Mao YouTube Interview (2014)",
                                    why: "Mao describes discovery using fluorescent tool he invented 15 years earlier - Slutsky omission",
                                  },
                                  {
                                    tick: 8,
                                    id: "Ex.7",
                                    label: "MIT Provisional Prosecution File",
                                    why: "TLO-disclosed prior art search history",
                                  },
                                  {
                                    tick: 11,
                                    id: "Ex.8",
                                    label:
                                      "'061 Patent Specification, col.4 lines 12-18",
                                    why: "Read aloud - explicit in vivo formation language",
                                  },
                                ],
                                thisDepo: [
                                  {
                                    tick: 5,
                                    at: "00:11:20",
                                    said: '"I personally synthesized the compound first."',
                                    vs: "Just now at 00:14:55 he confirmed 'Mao performed the actual synthesis under my direction.'",
                                  },
                                  {
                                    tick: 8,
                                    at: "00:19:42",
                                    said: '"I did not know of any prior art in 1997."',
                                    vs: "At 00:08:30 he said his MIT lab 'reviewed all relevant Chinese patents during background research.'",
                                  },
                                  {
                                    tick: 11,
                                    at: "00:25:08",
                                    said: '"Specification does not admit natural formation."',
                                    vs: "Patent col.4 lines 12-18 explicitly states 'in vivo formation' as a property of the claimed compound.",
                                  },
                                ],
                                caseRecord: [
                                  {
                                    tick: 5,
                                    at: "00:11:20",
                                    said: '"Personally synthesized first"',
                                    contradiction:
                                      "Mao YouTube interview (Ex.6, 2014): Mao describes 'accidental discovery' using a fluorescent tool he invented 15 years earlier. Mao's Feb 18, 2026 depo credits Slutsky for the discovery - yet Slutsky is not named on '061. § 102(f) / § 256 issue.",
                                  },
                                  {
                                    tick: 8,
                                    at: "00:19:42",
                                    said: '"Did not know of prior Chinese patents"',
                                    contradiction:
                                      "CN1200366A (Ex.4, 1997) discloses magnesium L-threonate. MIT TLO records (Ex.7) show this was reviewed during background research.",
                                  },
                                  {
                                    tick: 11,
                                    at: "00:25:08",
                                    said: '"No natural formation admission"',
                                    contradiction:
                                      "'061 col.4 lines 12-18 explicitly describes 'in vivo formation' - direct § 101 ineligibility admission.",
                                  },
                                ],
                              };
                      const fQ = depoData.questions.filter(
                        (q) => q.tick <= dispTick,
                      );
                      const fE = depoData.exhibits.filter(
                        (e) => e.tick <= dispTick,
                      );
                      const fT = depoData.thisDepo.filter(
                        (c) => c.tick <= dispTick,
                      );
                      const fR = depoData.caseRecord.filter(
                        (c) => c.tick <= dispTick,
                      );
                      return (
                        <>
                          {/* Suggested Questions */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: "1px solid #3b82f633",
                              borderLeft: "3px solid #3b82f6",
                              padding: "12px 14px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#3b82f6",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                📋 Suggested Questions to Ask
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.faint,
                                }}
                              >
                                {fQ.length}/{depoData.questions.length} ·{" "}
                                {depoData.witness}
                              </span>
                            </div>
                            {fQ.map((q, i) => {
                              const ne = isNew(q.tick);
                              return (
                                <div
                                  key={"q" + q.tick + "-" + i}
                                  style={{
                                    fontSize: 9,
                                    color: th.muted,
                                    padding: "5px 28px 5px 8px",
                                    borderLeft: "2px solid #3b82f6",
                                    marginBottom: 4,
                                    cursor: "pointer",
                                    lineHeight: 1.5,
                                    background: ne ? "#3b82f618" : "#3b82f608",
                                    borderRadius: "0 4px 4px 0",
                                    animation: "slideIn 0.45s ease",
                                    transition: "background 0.5s",
                                    position: "relative",
                                  }}
                                >
                                  {ne && (
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        fontSize: 6,
                                        color: "#3b82f6",
                                        background: "#3b82f628",
                                        border: "1px solid #3b82f6",
                                        padding: "1px 5px",
                                        borderRadius: 6,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        fontFamily: "system-ui,sans-serif",
                                        animation: "pulse 1s infinite",
                                      }}
                                    >
                                      NEW
                                    </span>
                                  )}
                                  {q.text}
                                </div>
                              );
                            })}
                          </div>

                          {/* Exhibits to Show */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: "1px solid #f59e0b33",
                              borderLeft: "3px solid #f59e0b",
                              padding: "12px 14px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#f59e0b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                📎 Exhibits to Show Deponent
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.faint,
                                }}
                              >
                                {fE.length}/{depoData.exhibits.length} ready
                              </span>
                            </div>
                            {fE.map((e, i) => {
                              const ne = isNew(e.tick);
                              return (
                                <div
                                  key={"e" + e.tick + "-" + i}
                                  style={{
                                    padding: "6px 8px",
                                    marginBottom: 5,
                                    background: ne ? "#f59e0b18" : "#f59e0b08",
                                    borderRadius: 5,
                                    borderLeft: "2px solid #f59e0b",
                                    animation: "slideIn 0.45s ease",
                                    transition: "background 0.5s",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      marginBottom: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 8,
                                        fontFamily: "monospace",
                                        color: "#f59e0b",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {e.id}
                                    </span>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 4,
                                        alignItems: "center",
                                      }}
                                    >
                                      {ne && (
                                        <span
                                          style={{
                                            fontSize: 6,
                                            color: "#f59e0b",
                                            background: "#f59e0b28",
                                            border: "1px solid #f59e0b",
                                            padding: "1px 5px",
                                            borderRadius: 6,
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            fontFamily: "system-ui,sans-serif",
                                            animation: "pulse 1s infinite",
                                          }}
                                        >
                                          NEW
                                        </span>
                                      )}
                                      <button
                                        style={{
                                          fontSize: 7,
                                          padding: "1px 6px",
                                          background: "#f59e0b18",
                                          color: "#f59e0b",
                                          border: "1px solid #f59e0b33",
                                          borderRadius: 3,
                                          cursor: "pointer",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Mark
                                      </button>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      lineHeight: 1.4,
                                      marginBottom: 2,
                                    }}
                                  >
                                    {e.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 7,
                                      color: th.faint,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    ↳ {e.why}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Inconsistencies in this deposition */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: "1px solid #ef444433",
                              borderLeft: "3px solid #ef4444",
                              padding: "12px 14px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#ef4444",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                ⚡ Inconsistencies - This Deposition
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.faint,
                                }}
                              >
                                {fT.length}/{depoData.thisDepo.length} flagged
                              </span>
                            </div>
                            {fT.length === 0 && (
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.faint,
                                  fontStyle: "italic",
                                  textAlign: "center",
                                  padding: "14px 8px",
                                  lineHeight: 1.55,
                                }}
                              >
                                {isRecording
                                  ? "Listening for self-contradictions in the witness's testimony…"
                                  : "Self-contradictions will be flagged in real-time as the witness answers."}
                              </div>
                            )}
                            {fT.map((c, i) => {
                              const ne = isNew(c.tick);
                              return (
                                <div
                                  key={"t" + c.tick + "-" + i}
                                  style={{
                                    padding: "7px 9px",
                                    marginBottom: 5,
                                    background: ne ? "#ef444415" : "#ef444408",
                                    borderRadius: 5,
                                    borderLeft: "2px solid #ef4444",
                                    animation: "slideIn 0.45s ease",
                                    transition: "background 0.5s",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      marginBottom: 3,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#ef4444",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ⏱ {c.at}
                                    </span>
                                    {ne && (
                                      <span
                                        style={{
                                          fontSize: 6,
                                          color: "#ef4444",
                                          background: "#ef444428",
                                          border: "1px solid #ef4444",
                                          padding: "1px 5px",
                                          borderRadius: 6,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "system-ui,sans-serif",
                                          animation: "pulse 1s infinite",
                                        }}
                                      >
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      marginBottom: 4,
                                      fontStyle: "italic",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {c.said}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      lineHeight: 1.45,
                                      paddingTop: 4,
                                      borderTop: `1px dashed ${th.border}`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#ef4444",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ↳ But:{" "}
                                    </span>
                                    {c.vs}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Inconsistencies with case record */}
                          <div
                            style={{
                              background: th.card,
                              borderRadius: 8,
                              border: "1px solid #a78bfa33",
                              borderLeft: "3px solid #a78bfa",
                              padding: "12px 14px",
                              marginBottom: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#a78bfa",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                🔍 Inconsistencies - Case Record
                              </div>
                              <span
                                style={{
                                  fontSize: 7,
                                  fontFamily: "monospace",
                                  color: th.faint,
                                }}
                              >
                                {fR.length}/{depoData.caseRecord.length} flagged
                              </span>
                            </div>
                            {fR.length === 0 && (
                              <div
                                style={{
                                  fontSize: 8,
                                  color: th.faint,
                                  fontStyle: "italic",
                                  textAlign: "center",
                                  padding: "14px 8px",
                                  lineHeight: 1.55,
                                }}
                              >
                                {isRecording
                                  ? "Cross-checking testimony against the docket and prior records…"
                                  : "Cross-references with the docket will appear as the witness contradicts the record."}
                              </div>
                            )}
                            {fR.map((c, i) => {
                              const ne = isNew(c.tick);
                              return (
                                <div
                                  key={"r" + c.tick + "-" + i}
                                  style={{
                                    padding: "7px 9px",
                                    marginBottom: 5,
                                    background: ne ? "#a78bfa15" : "#a78bfa08",
                                    borderRadius: 5,
                                    borderLeft: "2px solid #a78bfa",
                                    animation: "slideIn 0.45s ease",
                                    transition: "background 0.5s",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      marginBottom: 3,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "monospace",
                                        color: "#a78bfa",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ⏱ {c.at}
                                    </span>
                                    {ne && (
                                      <span
                                        style={{
                                          fontSize: 6,
                                          color: "#a78bfa",
                                          background: "#a78bfa28",
                                          border: "1px solid #a78bfa",
                                          padding: "1px 5px",
                                          borderRadius: 6,
                                          fontWeight: 700,
                                          letterSpacing: 0.5,
                                          fontFamily: "system-ui,sans-serif",
                                          animation: "pulse 1s infinite",
                                        }}
                                      >
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: th.text,
                                      marginBottom: 4,
                                      fontStyle: "italic",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {c.said}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: th.muted,
                                      lineHeight: 1.45,
                                      paddingTop: 4,
                                      borderTop: `1px dashed ${th.border}`,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#a78bfa",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ↳ Record:{" "}
                                    </span>
                                    {c.contradiction}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* SPLIT PANEL */}
          {panel && (
            <div
              style={{
                width: 380,
                minWidth: 380,
                background: th.card,
                borderLeft: `1px solid ${th.border}`,
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 52px)",
                position: "sticky",
                top: 0,
                animation: "panelSlide 0.2s ease",
                boxShadow: dark
                  ? "-4px 0 20px rgba(0,0,0,0.2)"
                  : "-4px 0 20px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  padding: "12px 14px",
                  borderBottom: `1px solid ${th.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  background: th.surface,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 8,
                      fontFamily: "monospace",
                      color: th.faint,
                      letterSpacing: 0.5,
                      marginBottom: 2,
                    }}
                  >
                    📄 DOCUMENT · {panel.ref}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {panel.title}
                  </div>
                </div>
                <button
                  onClick={() => setPanel(null)}
                  title="Close (Esc)"
                  style={{
                    background: th.bg,
                    border: `1px solid ${th.border}`,
                    color: th.dim,
                    width: 24,
                    height: 24,
                    borderRadius: 5,
                    cursor: "pointer",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = th.text;
                    e.currentTarget.style.background = th.surface;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = th.dim;
                    e.currentTarget.style.background = th.bg;
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 6,
                    padding: "18px 16px",
                    minHeight: 180,
                    boxShadow: "0 1px 3px rgba(0,0,0,.12)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 7,
                      fontFamily: "serif",
                      color: "#999",
                      textAlign: "center",
                      marginBottom: 8,
                      letterSpacing: 0.5,
                    }}
                  >
                    UNITED STATES DISTRICT COURT
                    <br />
                    {isIle ? "EASTERN DISTRICT OF NEW YORK" : caseId === "neuro" ? "CENTRAL DISTRICT OF CALIFORNIA" : "NORTHERN DISTRICT OF CALIFORNIA"}
                  </div>
                  <div
                    style={{
                      fontSize: 7,
                      fontFamily: "monospace",
                      color: "#ccc",
                      textAlign: "center",
                      marginBottom: 10,
                    }}
                  >
                    {isIle ? "1:26-cr-00097-NGG" : isJuul ? "3:19-md-02913-WHO" : caseId === "musk" ? "4:24-cv-04722-YGR" : "2:25-cv-05871-FLA-MAR"} · {panel.ref}
                  </div>
                  <div
                    style={{ borderBottom: "1px solid #eee", marginBottom: 10 }}
                  />
                  {panel.loading ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 200,
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          border: "3px solid #1e40af33",
                          borderTopColor: "#1e40af",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      <div
                        style={{
                          fontSize: 10,
                          color: "#1e40af",
                          fontWeight: 600,
                        }}
                      >
                        Expanding theory...
                      </div>
                      <div
                        style={{
                          fontSize: 8,
                          color: "#6b7280",
                          textAlign: "center",
                          maxWidth: 260,
                          lineHeight: 1.5,
                        }}
                      >
                        AI is writing a detailed ~1000-word expansion of
                        this theory with citations and legal analysis.
                      </div>
                      <div
                        style={{ fontSize: 8, color: "#9ca3af", marginTop: 4 }}
                      >
                        This usually takes 10–20 seconds
                      </div>
                    </div>
                  ) : (
                    <div
                      className="cb-md cb-doc"
                      style={{ fontSize: 10, lineHeight: 1.65, color: "#1a1a1a", fontFamily: "serif", margin: 0 }}
                      dangerouslySetInnerHTML={{ __html: mdToHtml(panel.content) }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {tab === "assets" && aSel && !panel && (
            <div
              style={{
                width: 360,
                minWidth: 360,
                background: th.card,
                borderLeft: `1px solid ${th.border}`,
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 52px)",
                position: "sticky",
                top: 0,
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  borderBottom: `1px solid ${th.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600 }}>
                  {aSel.title}
                </div>
                <button
                  onClick={() => setASel(null)}
                  style={{
                    background: th.surface,
                    border: "none",
                    color: th.dim,
                    width: 18,
                    height: 18,
                    borderRadius: 3,
                    cursor: "pointer",
                    fontSize: 11,
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
                <DocC a={aSel} />
                <div style={{ fontSize: 10, color: th.muted, marginTop: 6 }}>
                  {aSel.desc}
                </div>
                <div
                  style={{
                    fontSize: 7,
                    fontFamily: "monospace",
                    color: th.faint,
                    marginTop: 3,
                  }}
                >
                  {aSel.date} · {aSel.pages}pp
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {Modals}
      {showAddTL && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowAddTL(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: th.surface,
              border: `1px solid ${th.border}`,
              borderRadius: 10,
              padding: 20,
              width: 440,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              ＋ Add Timeline Event
            </div>
            <div style={{ fontSize: 9, fontWeight: 600, marginBottom: 4 }}>
              Type
            </div>
            <div
              style={{ display: "flex", gap: 4, marginBottom: 10 }}
              id="tl-type"
            >
              {[
                ["factual", "🔴 Factual", "#ef4444"],
                ["procedural", "🔵 Procedural", "#3b82f6"],
                ["scheduling", "🟢 Scheduling", "#10b981"],
              ].map(([k, l, c]) => (
                <button
                  key={k}
                  data-type={k}
                  onClick={(e) => {
                    e.currentTarget.parentElement
                      .querySelectorAll("button")
                      .forEach((b) => (b.style.background = th.bg));
                    e.currentTarget.style.background = `${c}20`;
                    e.currentTarget.dataset.selected = "true";
                  }}
                  style={{
                    padding: "5px 10px",
                    fontSize: 9,
                    background: th.bg,
                    color: c,
                    border: `1px solid ${c}`,
                    borderRadius: 4,
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
            <input
              id="tl-date"
              placeholder="Date * (e.g. Mar 3, 2026)"
              style={{
                width: "100%",
                padding: 8,
                fontSize: 11,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                boxSizing: "border-box",
                outline: "none",
                marginBottom: 6,
              }}
            />
            <input
              id="tl-title"
              placeholder="Title *"
              style={{
                width: "100%",
                padding: 8,
                fontSize: 11,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                boxSizing: "border-box",
                outline: "none",
                marginBottom: 6,
              }}
            />
            <textarea
              id="tl-desc"
              placeholder="Description"
              rows={2}
              style={{
                width: "100%",
                padding: 8,
                fontSize: 11,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                boxSizing: "border-box",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                marginBottom: 6,
              }}
            />
            <div style={{ fontSize: 9, fontWeight: 600, marginBottom: 4 }}>
              Ground Truth (supporting asset)
            </div>
            <select
              id="tl-asset"
              style={{
                width: "100%",
                padding: 7,
                fontSize: 10,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 4,
                color: th.text,
                outline: "none",
                marginBottom: 6,
              }}
            >
              <option value="">None - no attachment</option>
              {aA.map((a) => (
                <option key={a.id} value={a.title}>
                  {a.title} ({a.cat})
                </option>
              ))}
            </select>
            <div
              style={{
                border: `2px dashed ${th.border}`,
                borderRadius: 6,
                padding: 10,
                textAlign: "center",
                marginBottom: 10,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 14 }}>📂</span>
              <div style={{ fontSize: 8, color: th.dim }}>
                Or drag & drop a new document as ground truth
              </div>
            </div>
            <div
              style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => setShowAddTL(false)}
                style={{
                  padding: "5px 14px",
                  fontSize: 10,
                  background: th.border,
                  color: th.muted,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const d = document.getElementById("tl-date")?.value,
                    t = document.getElementById("tl-title")?.value,
                    desc = document.getElementById("tl-desc")?.value,
                    asset = document.getElementById("tl-asset")?.value,
                    typeBtn = document.querySelector(
                      "#tl-type [data-selected=true]",
                    ),
                    type = typeBtn?.dataset?.type || "factual";
                  if (d && t) {
                    setCustomTL((p) => [
                      ...p,
                      {
                        date: d,
                        label: t,
                        detail: desc || "",
                        ref: asset || "User",
                        type,
                        ecf:
                          (desc || t) +
                          (asset ? `\n\nGround Truth: ${asset}` : ""),
                      },
                    ]);
                    setShowAddTL(false);
                  }
                }}
                style={{
                  padding: "5px 14px",
                  fontSize: 10,
                  background: "#1e40af",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Add to Timeline
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`button:hover{opacity:.85}input[type="range"]::-webkit-slider-thumb{-webkit-appearance:none;width:12px;height:12px;border-radius:50%;background:#fff;box-shadow:0 0 4px rgba(0,0,0,.4);cursor:pointer}*::-webkit-scrollbar{width:6px;height:6px}*::-webkit-scrollbar-track{background:transparent}*::-webkit-scrollbar-thumb{background:${th.border};border-radius:3px}*::-webkit-scrollbar-thumb:hover{background:${th.dim}}@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}@keyframes slideIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes spin{to{transform:rotate(360deg)}}@keyframes panelSlide{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}@keyframes toastUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}@keyframes pageFlipNext{0%{opacity:0;transform:translateX(40px) rotateY(15deg)}50%{opacity:0.5}100%{opacity:1;transform:translateX(0) rotateY(0)}}@keyframes pageFlipPrev{0%{opacity:0;transform:translateX(-40px) rotateY(-15deg)}50%{opacity:0.5}100%{opacity:1;transform:translateX(0) rotateY(0)}}::selection{background:rgba(59,130,246,0.25)}kbd{font-family:monospace}.cb-md h2,.cb-md h3,.cb-md h4,.cb-md h5{margin:12px 0 5px;line-height:1.3;font-weight:700}.cb-md h2{font-size:1.35em}.cb-md h3{font-size:1.2em}.cb-md h4{font-size:1.1em}.cb-md h5{font-size:1em;text-transform:uppercase;letter-spacing:.4px}.cb-md p{margin:0 0 8px}.cb-md ul,.cb-md ol{margin:0 0 8px 18px;padding:0}.cb-md li{margin:2px 0}.cb-md hr{border:0;border-top:1px solid rgba(120,120,120,.35);margin:10px 0}.cb-md code{background:rgba(120,120,120,.18);padding:1px 5px;border-radius:3px;font-size:.9em}.cb-md>*:first-child{margin-top:0}.cb-md>*:last-child{margin-bottom:0}.cb-doc h2{text-align:center;font-size:1.3em;margin:14px 0 8px}`}</style>
      {/* TOAST RENDERER */}
      {toasts.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              style={{
                background:
                  t.kind === "success"
                    ? "#10b981"
                    : t.kind === "warn"
                      ? "#f59e0b"
                      : t.kind === "error"
                        ? "#ef4444"
                        : th.surface,
                color: ["success", "warn", "error"].includes(t.kind) ? "#fff" : th.text,
                border: ["success", "warn", "error"].includes(t.kind) ? "none" : `1px solid ${th.border}`,
                borderRadius: 8,
                padding: "10px 16px",
                fontSize: 11,
                fontWeight: 500,
                minWidth: 200,
                maxWidth: 340,
                boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                animation: "toastUp 0.3s ease",
                pointerEvents: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 14 }}>
                {t.kind === "success"
                  ? "✓"
                  : t.kind === "warn"
                    ? "⚠"
                    : t.kind === "error"
                      ? "✕"
                      : "ℹ"}
              </span>
              <span>{t.text}</span>
            </div>
          ))}
        </div>
      )}
      {confirmRemove && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            zIndex: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setConfirmRemove(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: th.surface,
              border: `1px solid ${th.border}`,
              borderRadius: 10,
              padding: "20px 24px",
              width: 320,
              boxShadow: "0 8px 32px rgba(0,0,0,.4)",
            }}
          >
            <div style={{ fontSize: 14, marginBottom: 8 }}>🗑️</div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: th.text,
                marginBottom: 6,
              }}
            >
              Remove this item?
            </div>
            <div
              style={{
                fontSize: 10,
                color: th.muted,
                marginBottom: 18,
                lineHeight: 1.5,
              }}
            >
              Remove <strong>{confirmRemove.name}</strong> from this view?
              Built-in items can be restored by pressing Reset.
            </div>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => setConfirmRemove(null)}
                style={{
                  padding: "6px 14px",
                  fontSize: 10,
                  background: th.border,
                  color: th.muted,
                  border: "none",
                  borderRadius: 5,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmRemove.action();
                  setConfirmRemove(null);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 10,
                  fontWeight: 700,
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 5,
                  cursor: "pointer",
                }}
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}
      {showMarketplace && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.7)",
            zIndex: 500,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
          }}
          onClick={() => setShowMarketplace(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 420,
              height: "100vh",
              background: th.nav,
              borderLeft: `1px solid ${th.border}`,
              overflow: "auto",
              padding: 0,
            }}
          >
            <div
              style={{
                padding: "16px 20px 12px",
                borderBottom: `1px solid ${th.border}`,
                position: "sticky",
                top: 0,
                background: th.nav,
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    🛒 Marketplace
                  </div>
                  <div style={{ fontSize: 8, color: th.dim, marginTop: 1 }}>
                    Manage your views · Browse extensions
                  </div>
                </div>
                <button
                  onClick={() => setShowMarketplace(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: th.dim,
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ padding: "14px 20px" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  marginBottom: 10,
                  color: th.text,
                }}
              >
                My Views - Standard Features
              </div>
              <div style={{ fontSize: 8, color: th.muted, marginBottom: 10 }}>
                Toggle any view on or off for your session. These are always
                available to re-enable.
              </div>
              {[
                ["learn", "📖", "Learn the Case"],
                ["timelines", "📅", "Timelines"],
                ["assets", "📁", "Assets"],
                ["chat", "💬", "Case AI Chat"],
                ["warroom", "⚔️", "War Room"],
                ["jury", "⚖️", "Jury Intelligence"],
                ["damages", "💰", "Damages Model"],
                ["settleintel", "🤝", "Settlement Intel"],
                ["deps", "📋", "Depositions"],
                ["experts", "🎓", "Expert Battlefield"],
                ["stratmap", "🌡️", "Strategic Map"],
                ["playbook", "🎯", "Opponent Playbook"],
                ["contraengine", "🔍", "Contradiction Engine"],
                ["impeach", "🎯", "Impeachment Bank"],
                ["appellate", "⚖️", "Appellate Risk"],
                ["traps", "⚠️", "Procedural Traps"],
                ["client", "💼", "Client Hub"],
                ["judgeintel", "👨‍⚖️", "Judge Intel"],
                ["attorneys", "👥", "Attorneys & Counsel"],
                ["demos", "📊", "Demonstratives"],
                ...RB_PAGES.filter(
                  (r) =>
                    ![
                      "heatmap",
                      "gaps",
                      "contradictions",
                      "witnesses",
                      "judge",
                      "settlement",
                      "locations",
                      "playbook",
                      "traps",
                    ].includes(r[0]),
                ),
              ].map(([id, icon, lbl]) => (
                <div
                  key={id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "7px 10px",
                    background: th.surface,
                    borderRadius: 6,
                    marginBottom: 5,
                    border: `1px solid ${th.border}`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ fontSize: 14 }}>{icon}</span>
                    <span style={{ fontSize: 9, color: th.text }}>{lbl}</span>
                  </div>
                  <button
                    onClick={() =>
                      setEnabledTabs((prev) => {
                        const all = [
                          "learn",
                          "timelines",
                          "assets",
                          "chat",
                          "warroom",
                          "jury",
                          "damages",
                          "settleintel",
                          "deps",
                          "experts",
                          "stratmap",
                          "playbook",
                          "contraengine",
                          "impeach",
                          "appellate",
                          "traps",
                          "client",
                          "judgeintel",
                          "attorneys",
                          "demos",
                          ...RB_PAGES.map((r) => r[0]),
                        ];
                        const cur = prev || all;
                        return cur.includes(id)
                          ? cur.filter((x) => x !== id)
                          : [...cur, id];
                      })
                    }
                    style={{
                      width: 36,
                      height: 20,
                      borderRadius: 10,
                      border: "none",
                      cursor: "pointer",
                      background:
                        !enabledTabs || enabledTabs.includes(id)
                          ? "#10b981"
                          : "#334155",
                      position: "relative",
                      transition: "background 0.2s",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left:
                          !enabledTabs || enabledTabs.includes(id)
                            ? "18px"
                            : "2px",
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left 0.2s",
                        display: "block",
                      }}
                    />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setEnabledTabs(null)}
                style={{
                  width: "100%",
                  padding: "6px",
                  fontSize: 8,
                  background: "transparent",
                  color: th.dim,
                  border: `1px solid ${th.border}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  marginTop: 6,
                }}
              >
                Reset to defaults (show all)
              </button>
            </div>
            <div
              style={{ height: 1, background: th.border, margin: "0 20px" }}
            />
            <div style={{ padding: "14px 20px" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  marginBottom: 4,
                  color: th.text,
                }}
              >
                Extension Marketplace
              </div>
              <div style={{ fontSize: 8, color: th.muted, marginBottom: 12 }}>
                Built by developers on the CaseBinder API. Install in one click.
              </div>
              {[
                {
                  icon: "🤝",
                  name: "Settlement Calculator",
                  author: "Lex Machina",
                  desc: "Predicts settlement ranges based on similar MDL outcomes and judge history.",
                  badge: "4.9 ⭐",
                  tags: ["MDL", "Settlement"],
                },
                {
                  icon: "📊",
                  name: "Damages Modeler",
                  author: "Cornerstone Research",
                  desc: "Builds economic damages models from case facts and expert reports.",
                  badge: "4.8 ⭐",
                  tags: ["Damages", "Expert"],
                },
                {
                  icon: "📡",
                  name: "State Court Sync",
                  author: "CourtLink",
                  desc: "Extends live docket sync to all 50 state court systems.",
                  badge: "4.9 ⭐",
                  tags: ["Docket", "State"],
                },
                {
                  icon: "🔐",
                  name: "Conflict Checker",
                  author: "Thomson Reuters",
                  desc: "Checks new matters against firm client database in real time.",
                  badge: "4.7 ⭐",
                  tags: ["Ethics", "Compliance"],
                },
                {
                  icon: "🌍",
                  name: "Expert Witness Finder",
                  author: "Bloomberg Law",
                  desc: "Searches expert databases, flags prior testimony and conflicts of interest.",
                  badge: "4.6 ⭐",
                  tags: ["Expert", "Discovery"],
                },
                {
                  icon: "📈",
                  name: "Litigation Analytics",
                  author: "Docket Alarm",
                  desc: "Historical win rates, motion outcomes, and judge analytics for any court.",
                  badge: "4.8 ⭐",
                  tags: ["Analytics", "Judge"],
                },
                {
                  icon: "🔗",
                  name: "E-Discovery Bridge",
                  author: "Relativity",
                  desc: "Connects CaseBinder evidence gaps to Relativity review platform.",
                  badge: "4.5 ⭐",
                  tags: ["E-Discovery"],
                },
                {
                  icon: "🤖",
                  name: "Deposition AI",
                  author: "Veritone",
                  desc: "Auto-generates deposition summaries and contradiction flags from transcripts.",
                  badge: "4.7 ⭐",
                  tags: ["Deposition", "AI"],
                },
                {
                  icon: "🏛️",
                  name: "Court Calendar Sync",
                  author: "Law Toolbar",
                  desc: "Syncs all deadlines and hearings to Outlook and Google Calendar.",
                  badge: "4.8 ⭐",
                  tags: ["Calendar", "Deadlines"],
                },
                {
                  icon: "🛡️",
                  name: "Privilege Screener",
                  author: "Epiq Systems",
                  desc: "AI-powered privilege review to flag documents before production.",
                  badge: "4.6 ⭐",
                  tags: ["Privilege", "Review"],
                },
              ].map((app, i) => (
                <div
                  key={i}
                  style={{
                    background: th.surface,
                    borderRadius: 8,
                    border: `1px solid ${th.border}`,
                    padding: "12px 14px",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <span style={{ fontSize: 16 }}>{app.icon}</span>
                      <div>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            color: th.text,
                          }}
                        >
                          {app.name}
                        </div>
                        <div style={{ fontSize: 7, color: th.dim }}>
                          {app.author}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setInstalledApps((p) =>
                          p.includes(i) ? p.filter((x) => x !== i) : [...p, i],
                        )
                      }
                      style={{
                        fontSize: 7,
                        padding: "3px 10px",
                        background: installedApps.includes(i)
                          ? "#10b98118"
                          : "#1e40af",
                        color: installedApps.includes(i) ? "#10b981" : "#fff",
                        border: `1px solid ${installedApps.includes(i) ? "#10b98133" : "#1e40af"}`,
                        borderRadius: 4,
                        cursor: "pointer",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {installedApps.includes(i) ? "✓ Installed" : "Install"}
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: 8,
                      color: th.muted,
                      lineHeight: 1.4,
                      marginBottom: 6,
                    }}
                  >
                    {app.desc}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {app.tags.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontSize: 6,
                          color: "#3b82f6",
                          background: "#3b82f618",
                          padding: "1px 5px",
                          borderRadius: 8,
                          fontFamily: "monospace",
                        }}
                      >
                        {t}
                      </span>
                    ))}
                    <span
                      style={{ fontSize: 7, color: th.dim, marginLeft: "auto" }}
                    >
                      {app.badge}
                    </span>
                  </div>
                </div>
              ))}
              <div
                style={{
                  background: "linear-gradient(135deg,#1e40af18,#a78bfa18)",
                  borderRadius: 8,
                  border: "1px solid #a78bfa33",
                  padding: "12px 14px",
                  textAlign: "center",
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 12, marginBottom: 4 }}>🛠️</div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: th.text,
                    marginBottom: 4,
                  }}
                >
                  Build your own extension
                </div>
                <div
                  style={{
                    fontSize: 8,
                    color: th.muted,
                    marginBottom: 8,
                    lineHeight: 1.4,
                  }}
                >
                  Use the CaseBinder API to create custom views, data
                  integrations, and AI features. Publish to the marketplace or
                  keep private.
                </div>
                <button
                  onClick={() => setPage("devdocs")}
                  style={{
                    padding: "5px 14px",
                    fontSize: 8,
                    background: "#a78bfa",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  View Developer Docs →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SM({ th, onClose, onSel }) {
  const [sTab, setSTab] = useState("court");
  const [q, setQ] = useState("");
  const [ct, setCt] = useState("pacer");
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 50);
  }, [sTab, ct]);
  const dists = [
    "Select...",
    "California Central",
    "California Northern",
    "California Southern",
    "New York Southern",
    "Texas Northern",
    "Texas Eastern",
    "Delaware",
    "New Jersey",
    "Illinois Northern",
  ];
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.65)",
        zIndex: 300,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        backdropFilter: "blur(4px)",
        animation: "fadeIn 0.15s ease",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: th.surface,
          border: `1px solid ${th.border}`,
          borderRadius: 14,
          padding: 20,
          width: 540,
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
          animation: "slideIn 0.2s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>🔍</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Search</div>
              <div style={{ fontSize: 10, color: th.muted }}>
                Pull a case from PACER or any state court
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close (Esc)"
            style={{
              background: th.card,
              border: `1px solid ${th.border}`,
              color: th.dim,
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          {[
            ["pacer", "🏛️ Federal"],
            ["ca", "🐻 CA"],
            ["tx", "⭐ TX"],
            ["ny", "🗽 NY"],
            ["fl", "🌴 FL"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setCt(k)}
              style={{
                padding: "6px 11px",
                fontSize: 11,
                fontWeight: 500,
                background: ct === k ? "#1e40af" : th.card,
                color: ct === k ? "#fff" : th.dim,
                border: `1px solid ${ct === k ? "#1e40af" : th.border}`,
                borderRadius: 7,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {l}
            </button>
          ))}
        </div>
        {ct === "pacer" ? (
          <div>
            <div style={{ display: "flex", gap: 0, marginBottom: 0 }}>
              {[
                ["court", "By Court"],
                ["name", "By Name"],
                ["case", "By Case #"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setSTab(k)}
                  style={{
                    padding: "8px 14px",
                    fontSize: 11,
                    fontWeight: sTab === k ? 600 : 400,
                    color: sTab === k ? "#a78bfa" : th.dim,
                    background: sTab === k ? th.card : "transparent",
                    border: `1px solid ${sTab === k ? "#a78bfa44" : th.border}`,
                    borderBottom: sTab === k ? "none" : undefined,
                    borderRadius: "7px 7px 0 0",
                    cursor: "pointer",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
            <div
              style={{
                background: th.card,
                borderRadius: "0 8px 8px 8px",
                border: `1px solid ${th.border}`,
                borderTop: "none",
                padding: 16,
              }}
            >
              {sTab === "court" && (
                <select
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 12,
                    background: th.bg,
                    border: `1px solid ${th.border}`,
                    borderRadius: 7,
                    color: th.text,
                    outline: "none",
                    marginBottom: 12,
                  }}
                >
                  {dists.map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              )}
              {sTab !== "court" && (
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSel();
                  }}
                  placeholder={
                    sTab === "name"
                      ? "e.g. Apple, Tesla, Smith v. Jones…"
                      : "e.g. 4:24-cv-04722"
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 12,
                    background: th.bg,
                    border: `1px solid ${th.border}`,
                    borderRadius: 7,
                    color: th.text,
                    outline: "none",
                    boxSizing: "border-box",
                    marginBottom: 12,
                  }}
                />
              )}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  onClick={onSel}
                  style={{
                    padding: "9px 20px",
                    fontSize: 12,
                    background: "#1e40af",
                    color: "#fff",
                    border: "none",
                    borderRadius: 7,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Pull Case
                </button>
                <span
                  style={{
                    fontSize: 10,
                    color: th.faint,
                    fontFamily: "monospace",
                  }}
                >
                  Press Enter to search · Esc to close
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: th.card,
              borderRadius: 8,
              border: `1px solid ${th.border}`,
              padding: 16,
            }}
          >
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSel();
              }}
              placeholder="Case number or party name…"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 12,
                background: th.bg,
                border: `1px solid ${th.border}`,
                borderRadius: 7,
                color: th.text,
                outline: "none",
                boxSizing: "border-box",
                marginBottom: 12,
              }}
            />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                onClick={onSel}
                style={{
                  padding: "9px 20px",
                  fontSize: 12,
                  background: "#1e40af",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Pull Case
              </button>
              <span
                style={{
                  fontSize: 10,
                  color: th.faint,
                  fontFamily: "monospace",
                }}
              >
                Press Enter to search · Esc to close
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SetM({
  th,
  dark,
  setDark,
  aiModel,
  setAiModel,
  onClose,
  onPage,
  openMarketplace,
  emailIntegrations,
  setEmailIntegrations,
  toast,
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: th.surface,
          border: `1px solid ${th.border}`,
          borderRadius: 10,
          padding: 24,
          width: 480,
          maxHeight: "85vh",
          overflow: "auto",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
          ⚙️ Settings
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 6,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Theme
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => setDark(true)}
              style={{
                padding: "7px 18px",
                fontSize: 13,
                background: dark ? "#1e40af" : th.border,
                color: dark ? "#fff" : th.muted,
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
                fontWeight: dark ? 600 : 400,
              }}
            >
              🌙 Dark
            </button>
            <button
              onClick={() => setDark(false)}
              style={{
                padding: "7px 18px",
                fontSize: 13,
                background: !dark ? "#1e40af" : th.border,
                color: !dark ? "#fff" : th.muted,
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
                fontWeight: !dark ? 600 : 400,
              }}
            >
              ☀️ Light
            </button>
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 6,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            AI Model
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              ["multi", "🔀 Multi-Model"],
              ["gpt", "ChatGPT"],
              ["claude", "Claude"],
              ["gemini", "Gemini"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setAiModel(k)}
                style={{
                  padding: "7px 16px",
                  fontSize: 13,
                  background: aiModel === k ? "#1e40af" : th.card,
                  color: aiModel === k ? "#fff" : th.dim,
                  border: `1px solid ${aiModel === k ? "#1e40af" : th.border}`,
                  borderRadius: 5,
                  cursor: "pointer",
                  fontWeight: aiModel === k ? 600 : 400,
                }}
              >
                {l}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: th.faint, marginTop: 5 }}>
            Multi-Model combines GPT, Claude, and Gemini for strongest output.
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Email Integration
          </div>
          <div
            style={{
              fontSize: 11,
              color: th.faint,
              marginBottom: 10,
              lineHeight: 1.5,
            }}
          >
            Connect to sync attorney communications. Powers the Communication
            Log, deadline extraction, and obligation tracking in the Attorneys
            tab.
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            {(() => {
              const gm = emailIntegrations && emailIntegrations.gmail;
              const out = emailIntegrations && emailIntegrations.outlook;
              return [
                <div
                  key="gm"
                  style={{
                    background: th.card,
                    borderRadius: 8,
                    border: `2px solid ${gm ? "#10b98155" : th.border}`,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>📧</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: th.text,
                        }}
                      >
                        Gmail
                      </div>
                      <div
                        style={{
                          fontSize: 8,
                          color: th.dim,
                          fontFamily: "monospace",
                        }}
                      >
                        {gm ? "● Connected" : "Not connected"}
                      </div>
                    </div>
                  </div>
                  {gm ? (
                    <>
                      <div
                        style={{
                          padding: "7px 10px",
                          background: dark ? "#0f172a" : "#f0fdf4",
                          borderRadius: 5,
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: th.text,
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          {gm.email}
                        </div>
                        <div
                          style={{
                            fontSize: 8,
                            color: th.muted,
                            fontFamily: "monospace",
                          }}
                        >
                          Last sync · {gm.lastSync}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button
                          onClick={() => {
                            setEmailIntegrations((p) => ({
                              ...p,
                              gmail: { ...p.gmail, lastSync: "just now" },
                            }));
                            toast &&
                              toast("✓ Re-authenticated Gmail", "success");
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 9,
                            background: th.surface,
                            color: th.muted,
                            border: `1px solid ${th.border}`,
                            borderRadius: 5,
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Re-auth
                        </button>
                        <button
                          onClick={() => {
                            setEmailIntegrations((p) => ({
                              ...p,
                              gmail: null,
                            }));
                            toast && toast("Gmail disconnected", "warn");
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 9,
                            background: "#dc262615",
                            color: "#dc2626",
                            border: "1px solid #dc262655",
                            borderRadius: 5,
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Disconnect
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setEmailIntegrations((p) => ({
                          ...p,
                          gmail: {
                            email: "raj@patentvc.com",
                            lastSync: "just now",
                          },
                        }));
                        toast &&
                          toast("✓ Gmail connected (demo OAuth)", "success");
                      }}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        background: "#ea4335",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      Connect Gmail
                    </button>
                  )}
                </div>,
                <div
                  key="out"
                  style={{
                    background: th.card,
                    borderRadius: 8,
                    border: `2px solid ${out ? "#10b98155" : th.border}`,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>📨</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: th.text,
                        }}
                      >
                        Outlook
                      </div>
                      <div
                        style={{
                          fontSize: 8,
                          color: th.dim,
                          fontFamily: "monospace",
                        }}
                      >
                        {out ? "● Connected" : "Not connected"}
                      </div>
                    </div>
                  </div>
                  {out ? (
                    <>
                      <div
                        style={{
                          padding: "7px 10px",
                          background: dark ? "#0f172a" : "#eff6ff",
                          borderRadius: 5,
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: th.text,
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          {out.email}
                        </div>
                        <div
                          style={{
                            fontSize: 8,
                            color: th.muted,
                            fontFamily: "monospace",
                          }}
                        >
                          Last sync · {out.lastSync}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button
                          onClick={() => {
                            setEmailIntegrations((p) => ({
                              ...p,
                              outlook: { ...p.outlook, lastSync: "just now" },
                            }));
                            toast &&
                              toast("✓ Re-authenticated Outlook", "success");
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 9,
                            background: th.surface,
                            color: th.muted,
                            border: `1px solid ${th.border}`,
                            borderRadius: 5,
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Re-auth
                        </button>
                        <button
                          onClick={() => {
                            setEmailIntegrations((p) => ({
                              ...p,
                              outlook: null,
                            }));
                            toast && toast("Outlook disconnected", "warn");
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            fontSize: 9,
                            background: "#dc262615",
                            color: "#dc2626",
                            border: "1px solid #dc262655",
                            borderRadius: 5,
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Disconnect
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setEmailIntegrations((p) => ({
                          ...p,
                          outlook: {
                            email: "raj@patentvc.com",
                            lastSync: "just now",
                          },
                        }));
                        toast &&
                          toast("✓ Outlook connected (demo OAuth)", "success");
                      }}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        background: "#0078d4",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                      }}
                    >
                      Connect Outlook
                    </button>
                  )}
                </div>,
              ];
            })()}
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Integrations
          </div>
          {[
            { n: "PACER", i: "🏛️", s: true },
            { n: "Harvey.ai", i: "🤖", s: false },
            { n: "Clio", i: "📂", s: false },
            { n: "Google Calendar", i: "📅", s: true },
            { n: "Zoom", i: "🎥", s: false },
            { n: "Westlaw", i: "📚", s: false },
            { n: "Lexis+", i: "📖", s: false },
          ].map((ig) => (
            <div
              key={ig.n}
              style={{
                background: th.card,
                borderRadius: 6,
                border: `1px solid ${th.border}`,
                padding: "10px 12px",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{ig.i}</span>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{ig.n}</span>
                  {ig.s && (
                    <span
                      style={{ fontSize: 11, color: "#10b981", marginLeft: 6 }}
                    >
                      ● Connected
                    </span>
                  )}
                </div>
              </div>
              {ig.s ? (
                <button
                  style={{
                    fontSize: 11,
                    color: th.dim,
                    background: "none",
                    border: `1px solid ${th.border}`,
                    borderRadius: 4,
                    padding: "4px 10px",
                    cursor: "pointer",
                  }}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  style={{
                    fontSize: 12,
                    background: "#1e40af",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "5px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            Your Plan
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
            }}
          >
            {[
              [
                "Pilot",
                "$10K",
                "per matter / mo",
                false,
                "Up to 5 active matters",
              ],
              [
                "Practice Group",
                "$3,500",
                "per attorney / mo",
                true,
                "Unlimited matters · billed annually",
              ],
              [
                "Firm-Wide",
                "Custom",
                "volume pricing",
                false,
                "Multi-year deal · SOC 2 + SLA",
              ],
            ].map(([n, p, unit, c, sub]) => (
              <div
                key={n}
                style={{
                  background: c ? "#1e40af12" : th.bg,
                  border: `1px solid ${c ? "#1e40af" : th.border}`,
                  borderRadius: 6,
                  padding: 12,
                  textAlign: "center",
                  position: "relative",
                }}
              >
                {c && (
                  <div
                    style={{
                      position: "absolute",
                      top: -8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#f59e0b",
                      color: "#1a1a1a",
                      fontSize: 7,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 8,
                      fontFamily: "monospace",
                      whiteSpace: "nowrap",
                    }}
                  >
                    CURRENT
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700 }}>{n}</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: c ? "#1e40af" : th.text,
                    margin: "3px 0 1px",
                  }}
                >
                  {p}
                </div>
                <div style={{ fontSize: 8, color: th.faint, marginBottom: 4 }}>
                  {unit}
                </div>
                <div
                  style={{
                    fontSize: 7,
                    color: th.muted,
                    minHeight: 18,
                    marginBottom: 6,
                    lineHeight: 1.4,
                  }}
                >
                  {sub}
                </div>
                <button
                  onClick={() => {
                    onClose && onClose();
                    onPage && onPage("sales");
                  }}
                  style={{
                    padding: "5px 10px",
                    fontSize: 10,
                    background: c ? th.border : "#1e40af",
                    color: c ? th.dim : "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "100%",
                  }}
                >
                  {c ? "Active Plan" : "Contact Sales"}
                </button>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 10,
              padding: "8px 12px",
              background: dark ? "#0f172a" : "#f8fafc",
              borderRadius: 6,
              border: `1px solid ${th.border}`,
              fontSize: 9,
              color: th.muted,
              lineHeight: 1.5,
            }}
          >
            💡 Pricing reflects the new model.{" "}
            {[
              ["Contact us", "contact"],
              ["Privacy", "privacy"],
              ["Terms", "terms"],
              ["Support", "support"],
            ].flatMap(([l, p], i) => [
              i > 0 && <span key={"s" + i}> · </span>,
              <button
                key={p}
                onClick={() => {
                  onClose && onClose();
                  onPage && onPage(p);
                }}
                style={{
                  color: "#3b82f6",
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 9,
                }}
              >
                {l}
              </button>,
            ])}
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: th.muted,
              textTransform: "uppercase",
              letterSpacing: ".05em",
            }}
          >
            🛒 Marketplace & My Views
          </div>
          <div
            style={{
              background: th.card,
              borderRadius: 8,
              border: `1px solid ${th.border}`,
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: th.muted,
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              Manage which tabs appear in your left nav, install developer
              extensions, or build your own on the CaseBinder API.
            </div>
            <button
              onClick={openMarketplace}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: 13,
                fontWeight: 600,
                background: "linear-gradient(135deg,#1e40af,#10b981)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              🛒 Open Marketplace & My Views
            </button>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "7px 18px",
              fontSize: 12,
              background: th.border,
              color: th.muted,
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
